import { useState, useCallback, useEffect, useRef, useMemo, Fragment } from "react";
import { supabase } from "./supabase.js";
import MobileApp from "./MobileApp.jsx";
import { createSampleTour } from "./sampleTour.js";
import { TOK } from "./tokens.js";
import { Button, EmptyState } from "./ui.jsx";
import { fmtPhone } from "./tokens.js";
import ProfileEditor from "./ProfileEditor.jsx";
import ChatPanel from "./ChatPanel.jsx";

// ── Seed Venues ────────────────────────────────────────────────────────────
const SEED_VENUES = {
  v1: { id: "v1", name: "9:30 Club", addressStreet: "815 V St NW", city: "Washington", addressState: "DC", addressZip: "20001", addressCountry: "", capacity: 1200, ages: "18+", gaSeated: "GA", website: "https://www.930.com", promoterName: "Jamie Okafor", promoterEmail: "jamie@930club.com", promoterPhone: "202-265-0930", promoterCompany: "IMP", loadIn: "Dock on V St, double doors", parking: "Street + paid lot on 8th", houseGear: "L-Acoustics K2, 4×Crown amps, Midas Pro2", notes: "" },
  v2: { id: "v2", name: "The Sinclair", addressStreet: "52 Church St", city: "Cambridge", addressState: "MA", addressZip: "02138", addressCountry: "", capacity: 525, ages: "18+", gaSeated: "GA", website: "https://sinclaircambridge.com", promoterName: "Priya Mehta", promoterEmail: "priya@sinclaircambridge.com", promoterPhone: "617-547-5200", promoterCompany: "", loadIn: "Rear alley off Palmer St", parking: "Harvard Square garages nearby", houseGear: "d&b audiotechnik, Allen & Heath SQ7", notes: "" },
  v3: { id: "v3", name: "Thalia Hall", addressStreet: "1807 S Allport St", city: "Chicago", addressState: "IL", addressZip: "60608", addressCountry: "", capacity: 1600, ages: "21+", gaSeated: "GA Floor / Seated Balcony", website: "https://thaliahallchicago.com", promoterName: "Devon Walsh", promoterEmail: "devon@thaliahall.com", promoterPhone: "312-526-3851", promoterCompany: "", loadIn: "Stage door on Allport", parking: "Venue lot behind building", houseGear: "Meyer Sound LEOPARD, Yamaha CL5", notes: "" },
  v4: { id: "v4", name: "Bowery Ballroom", addressStreet: "6 Delancey St", city: "New York", addressState: "NY", addressZip: "10002", addressCountry: "", capacity: 575, ages: "21+", gaSeated: "GA", website: "https://www.boweryballroom.com", promoterName: "Tara Noel", promoterEmail: "tara@boweryballroom.com", promoterPhone: "212-533-2111", promoterCompany: "Bowery Presents", loadIn: "Delancey St entrance, freight elevator", parking: "No venue parking, street + Essex Market garage", houseGear: "Martin Audio MLA Mini, Soundcraft Vi1", notes: "" },
  v5: { id: "v5", name: "Rickshaw Stop", addressStreet: "155 Fell St", city: "San Francisco", addressState: "CA", addressZip: "94102", addressCountry: "", capacity: 400, ages: "All Ages", gaSeated: "GA", website: "https://www.rickshawstop.com", promoterName: "Miguel Torres", promoterEmail: "miguel@rickshawstop.com", promoterPhone: "415-861-5000", promoterCompany: "", loadIn: "Fell St double doors", parking: "Street parking, Civic Center BART nearby", houseGear: "QSC K Series, Behringer X32", notes: "" },
};

// ── Team Helpers ────────────────────────────────────────────────────────────

async function ensureTeam(userId, userEmail) {
  const { data: memberships } = await supabase
    .from("team_members").select("team_id, role").eq("user_id", userId).limit(1);
  const membership = memberships?.[0] || null;
  if (membership) return membership.team_id;

  // Generate ID client-side to avoid RLS chicken-and-egg:
  // if we insert then select, teams RLS blocks the select because
  // the user isn't in team_members yet.
  const teamId = crypto.randomUUID();
  const { error: teamErr } = await supabase
    .from("teams").insert({ id: teamId, name: "My Team" });
  if (teamErr) { console.error("[ADVANCD] ensureTeam failed:", teamErr); return null; }

  const { error: memberErr } = await supabase.from("team_members").insert({
    team_id: teamId, user_id: userId, role: "owner", email: userEmail || ""
  });
  if (memberErr) { console.error("[ADVANCD] ensureTeam member insert failed:", memberErr); return null; }

  // Migrate ALL data owned by this user to the new team_id.
  // We intentionally do NOT filter by is("team_id", null) here — if ensureTeam is
  // creating a brand-new team it means the user has no team membership, so we want
  // to sweep in all their records regardless of what team_id they previously had.
  try {
    await Promise.all([
      supabase.from("artists").update({ team_id: teamId }).eq("user_id", userId),
      supabase.from("venues").update({ team_id: teamId }).eq("user_id", userId),
      supabase.from("contacts").update({ team_id: teamId }).eq("user_id", userId),
      supabase.from("tours").update({ team_id: teamId }).eq("user_id", userId),
      supabase.from("shows").update({ team_id: teamId }).eq("user_id", userId),
    ]);
  } catch (e) { console.error("[ADVANCD] ensureTeam migration failed:", e); }
  return teamId;
}

// ── Org Helpers (V-0: venue / festival accounts) ─────────────────────────────
async function ensureOrg(userId, orgType) {
  // Check for an existing membership first
  const { data: memberships } = await supabase
    .from("organization_members").select("org_id, role").eq("user_id", userId).limit(1);
  if (memberships?.[0]) return memberships[0].org_id;

  // Create the org + membership in one go (gen_random_uuid server-side)
  const orgId = crypto.randomUUID();
  const defaultName = orgType === "venue" ? "My Venue" : orgType === "festival" ? "My Festival" : "My Organization";
  const { error: orgErr } = await supabase
    .from("organizations").insert({ id: orgId, type: orgType, name: defaultName });
  if (orgErr) { console.error("[ADVANCD] ensureOrg failed:", orgErr); return null; }

  const { error: memErr } = await supabase
    .from("organization_members").insert({ org_id: orgId, user_id: userId, role: "owner" });
  if (memErr) { console.error("[ADVANCD] ensureOrg member insert failed:", memErr); return null; }

  return orgId;
}

async function joinTeamByCode(userId, userEmail, code) {
  const { data: invite } = await supabase
    .from("team_invites").select("team_id, expires_at")
    .eq("code", code.toUpperCase().trim()).maybeSingle();
  if (!invite) throw new Error("Invalid invite code");
  if (new Date(invite.expires_at) < new Date()) throw new Error("Invite code has expired");

  // Leave current team if any
  const { error: delErr } = await supabase.from("team_members").delete().eq("user_id", userId);
  if (delErr) throw new Error("Failed to leave current team: " + delErr.message);

  // Join new team
  const { error: upsErr } = await supabase.from("team_members").upsert({
    team_id: invite.team_id, user_id: userId, role: "member", email: userEmail || ""
  }, { onConflict: "team_id,user_id" });
  if (upsErr) throw new Error("Failed to join team: " + upsErr.message);

  // Migrate any owned data to the new team (non-blocking — log but don't fail)
  try {
    await Promise.all([
      supabase.from("artists").update({ team_id: invite.team_id }).eq("user_id", userId),
      supabase.from("venues").update({ team_id: invite.team_id }).eq("user_id", userId),
      supabase.from("contacts").update({ team_id: invite.team_id }).eq("user_id", userId),
      supabase.from("tours").update({ team_id: invite.team_id }).eq("user_id", userId),
      supabase.from("shows").update({ team_id: invite.team_id }).eq("user_id", userId),
    ]);
  } catch (e) { console.error("[ADVANCD] joinTeamByCode migration failed:", e); }
  return invite.team_id;
}

async function loadTeamMembers(teamId) {
  const { data } = await supabase
    .from("team_members").select("user_id, role, email, joined_at").eq("team_id", teamId);
  return data || [];
}

async function createTeamInvite(teamId, userId) {
  const { data, error } = await supabase
    .from("team_invites").insert({ team_id: teamId, created_by: userId }).select().single();
  if (error) throw error;
  return data;
}

// ── Band Member Module Definitions ────────────────────────────────────────
// Each entry defines a section that can appear on a band/crew member's Today screen.
// TMs always see everything — modules only gate the band/crew restricted view.
const MODULE_DEFS = [
  { type: "welcome",    label: "Welcome Note",      description: "AI-generated daily greeting personalised with name, city, and day context" },
  { type: "schedule",   label: "Today's Schedule",  description: "Run of show — customise which time slots each member sees" },
  { type: "bus_call",   label: "Bus Call",           description: "Departure time for the bus" },
  { type: "hotel",      label: "Hotel",              description: "Check-in/out times and address" },
  { type: "weather",    label: "Weather",            description: "Local forecast for the day" },
  { type: "flights",    label: "Flights",            description: "Flight details for the day" },
  { type: "transport",  label: "Ground Transport",   description: "Transfers and car services" },
  { type: "notes",      label: "Day Notes",          description: "General notes from the TM" },
  { type: "local_recs", label: "Local Recs",         description: "City recommendations by category — coffee, food, bars, and more" },
];

// Sub-fields for the Schedule module — category-level toggles for a member's Today screen.
// "load_ins off" hides ALL load-in rows regardless of how many acts are on the bill.
const SCHEDULE_FIELDS = [
  { id: "venue_access", label: "Venue Access",     description: "General venue access / load-in time" },
  { id: "load_ins",     label: "Load-in Times",    description: "Load-in time for each act on the bill" },
  { id: "soundchecks",  label: "Soundcheck Times", description: "Soundcheck time for each act" },
  { id: "doors",        label: "Doors",            description: "When doors open to the public" },
  { id: "set_times",    label: "Set Times",        description: "Set time for each act on the bill" },
  { id: "changeovers",  label: "Changeovers",      description: "Changeover time between sets" },
  { id: "curfew",       label: "Curfew",           description: "Hard curfew for the venue" },
];

// Default config for the schedule sub-fields (all on)
const DEFAULT_SCHEDULE_CONFIG = Object.fromEntries(SCHEDULE_FIELDS.map(f => [f.id, true]));

// Default bill for a new show — 3 acts in chronological playing order
const DEFAULT_BILL = [
  { id: "b0", name: "1st of 3",       load: "", sc: "", set: "", setEnd: "" },
  { id: "b1", name: "Direct Support", load: "", sc: "", set: "", setEnd: "" },
  { id: "b2", name: "Headliner",      load: "", sc: "", set: "", setEnd: "" },
];

const DEFAULT_MEMBER_MODULES = MODULE_DEFS.map((m, i) => ({
  type: m.type, enabled: true, order: i,
  config: m.type === "schedule" ? { ...DEFAULT_SCHEDULE_CONFIG } : {},
}));

// ── Module sub-field definitions ──────────────────────────────────────────
const HOTEL_FIELDS = [
  { id: "checkin",      label: "Check-in Time",    description: "When check-in starts" },
  { id: "checkout",     label: "Check-out Time",   description: "Checkout deadline" },
  { id: "address",      label: "Address & Map",    description: "Hotel address with Maps link" },
  { id: "phone",        label: "Hotel Phone",      description: "Front desk number" },
  { id: "confirmation", label: "Confirmation",     description: "Booking confirmation code" },
];
const FLIGHT_FIELDS = [
  { id: "flight_number",  label: "Flight Number",   description: "Airline and flight number" },
  { id: "departure",      label: "Departure",       description: "Departure time and airport" },
  { id: "arrival",        label: "Arrival",         description: "Arrival time and airport" },
  { id: "terminal_gate",  label: "Terminal / Gate", description: "Terminal and gate info" },
  { id: "confirmation",   label: "Confirmation",    description: "Booking reference code" },
];
const TRANSPORT_FIELDS = [
  { id: "pickup_time",     label: "Pickup Time",     description: "When the vehicle arrives" },
  { id: "pickup_location", label: "Pickup Location", description: "Where to meet the driver" },
  { id: "driver",          label: "Driver Details",  description: "Driver name and contact" },
  { id: "dropoff",         label: "Drop-off",        description: "Final destination" },
  { id: "confirmation",    label: "Confirmation",    description: "Booking reference number" },
];
const LOCAL_REC_CATS = [
  { id: "coffee",     label: "Coffee"         },
  { id: "food",       label: "Food"           },
  { id: "brunch",     label: "Brunch"         },
  { id: "bars",       label: "Bars"           },
  { id: "barbershop", label: "Barber / Salon" },
  { id: "gym",        label: "Gym"            },
  { id: "music",      label: "Record Stores"  },
  { id: "massage",    label: "Massage"        },
  { id: "dispensary", label: "Dispensary"     },
  { id: "other",      label: "Other"          },
];
const DEFAULT_LOCAL_REC_CONFIG = Object.fromEntries(LOCAL_REC_CATS.map(c => [c.id, true]));

// ── Checklist Template ─────────────────────────────────────────────────────
const CHECKLIST_TEMPLATE = [
  { id: "loadIn",      label: "Load-in / Soundcheck Times",  fields: ["Load-in time", "Soundcheck time", "Doors time", "Set time", "Curfew"] },
  { id: "hospitality", label: "Hospitality / Rider",          fields: ["Catering", "Buyout amount", "Drinks", "Dietary restrictions noted"] },
  { id: "backline",    label: "Backline & Gear",              fields: ["Drum kit confirmed", "Amps confirmed", "Piano / keys", "Backline contact"] },
  { id: "parking",     label: "Parking / Load-in Logistics",  fields: ["Parking passes", "Load-in instructions sent", "Parking contact"] },
  { id: "settlement",  label: "Settlement / Deal Terms",      fields: ["Deal memo signed", "Guarantee confirmed", "Settlement contact", "Payment method"] },
  { id: "guestList",   label: "Guest List / Tickets",         fields: ["GL cap confirmed", "Will call instructions", "Comps count"] },
  { id: "merch",       label: "Merch Terms",                  fields: ["Merch split %", "Table location", "Merch rep on-site", "Cash float"] },
  { id: "press",       label: "Press / Photo Pit",            fields: ["Photo pass issued", "3-song rule confirmed", "Press list submitted"] },
  { id: "greenRoom",   label: "Green Room",                   fields: ["Green room location", "Wi-Fi credentials", "Private bathroom confirmed"] },
];

const buildChecklist = (enabledCatIds, riderNotes) => {
  const template = enabledCatIds
    ? CHECKLIST_TEMPLATE.filter(c => enabledCatIds.includes(c.id))
    : CHECKLIST_TEMPLATE;
  const cl = {};
  template.forEach(cat => {
    cl[cat.id] = { complete: false, notes: cat.id === "hospitality" && riderNotes ? riderNotes : "", fields: {} };
    cat.fields.forEach(f => { cl[cat.id].fields[f] = ""; });
  });
  return cl;
};

const tourDateRange = (t) => {
  if (!t.shows.length) return "";
  const dates = [...t.shows].map(s => s.date).sort();
  const d1 = new Date(dates[0] + "T12:00:00");
  const d2 = new Date(dates[dates.length - 1] + "T12:00:00");
  const M = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  if (dates.length === 1) return `${M[d1.getMonth()]} ${d1.getDate()}, ${d1.getFullYear()}`;
  const [m1, m2] = [M[d1.getMonth()], M[d2.getMonth()]];
  const [yr1, yr2] = [d1.getFullYear(), d2.getFullYear()];
  if (yr1 === yr2 && m1 === m2) return `${m1} ${d1.getDate()}–${d2.getDate()}, ${yr1}`;
  if (yr1 === yr2) return `${m1} ${d1.getDate()} – ${m2} ${d2.getDate()}, ${yr1}`;
  return `${m1} ${d1.getDate()}, ${yr1} – ${m2} ${d2.getDate()}, ${yr2}`;
};

// ── Seed Artist Data ───────────────────────────────────────────────────────
const SEED_DATA = {
  artists: [
    {
      id: "a1", name: "Liv Greene", genre: "Folk / Americana",
      tours: [{
        id: "t1", name: "Midwest & East Spring Tour 2026", year: "2026",
        shows: [
          { id: "s1", date: "2026-04-12", venueId: "v1", status: "confirmed", notes: "", checklist: { ...buildChecklist(), loadIn: { complete: true, notes: "Load-in 3pm, SC 5pm", fields: { "Load-in time": "3:00 PM", "Soundcheck time": "5:00 PM", "Doors time": "7:00 PM", "Set time": "8:30 PM", "Curfew": "11:00 PM" } }, guestList: { complete: true, notes: "", fields: { "GL cap confirmed": "20", "Will call instructions": "ID at box office", "Comps count": "6" } } } },
          { id: "s2", date: "2026-04-14", venueId: "v2", status: "advancing", notes: "", checklist: buildChecklist() },
          { id: "s3", date: "2026-04-17", venueId: "v4", status: "unstarted", notes: "", checklist: buildChecklist() },
        ]
      }]
    },
    {
      id: "a2", name: "Lyle Brewer", genre: "Indie / Guitar",
      tours: [{
        id: "t2", name: "West Coast Run 2026", year: "2026",
        shows: [
          { id: "s4", date: "2026-05-02", venueId: "v5", status: "advancing", notes: "", checklist: buildChecklist() },
          { id: "s5", date: "2026-05-05", venueId: "v3", status: "unstarted", notes: "", checklist: buildChecklist() },
        ]
      }]
    },
    {
      id: "a3", name: "The Ballroom Thieves", genre: "Folk / Indie",
      tours: [{
        id: "t3", name: "Summer Headline Tour 2026", year: "2026",
        shows: [
          { id: "s6", date: "2026-06-10", venueId: "v3", status: "unstarted", notes: "", checklist: buildChecklist() },
          { id: "s7", date: "2026-06-12", venueId: "v1", status: "unstarted", notes: "", checklist: buildChecklist() },
          { id: "s8", date: "2026-06-15", venueId: "v4", status: "confirmed", notes: "", checklist: { ...buildChecklist(), loadIn: { complete: true, notes: "", fields: { "Load-in time": "4:00 PM", "Soundcheck time": "6:00 PM", "Doors time": "8:00 PM", "Set time": "9:00 PM", "Curfew": "12:00 AM" } } } },
        ]
      }]
    }
  ]
};

// ── localStorage ───────────────────────────────────────────────────────────
const LS_DATA   = "advance_v2_data";
const LS_VENUES = "advance_v2_venues";

function loadStorage() {
  try {
    const d = localStorage.getItem(LS_DATA);
    const v = localStorage.getItem(LS_VENUES);
    return { data: d ? JSON.parse(d) : SEED_DATA, venues: v ? JSON.parse(v) : SEED_VENUES };
  } catch { return { data: SEED_DATA, venues: SEED_VENUES }; }
}

// supabase client is imported from ./supabase.js

// ── Google Maps ──────────────────────────────────────────────────────────
const GMAP_KEY = import.meta.env.VITE_GOOGLE_MAPS_KEY || "";

// ── Helpers ────────────────────────────────────────────────────────────────
const statusConfig = {
  unstarted: { label: "Unstarted", color: "#555",    bg: "#1a1a1a", dot: "#555" },
  advancing: { label: "Advancing", color: "var(--accent)", bg: "#2a1f00", dot: "var(--accent)" },
  confirmed: { label: "Confirmed", color: "var(--success)", bg: "#0a2016", dot: "var(--success)" },
  dayOf:     { label: "Day-Of",    color: "#60b8ff", bg: "#001a2e", dot: "#60b8ff" },
};

const fmtDate = (d) => new Date(d + "T12:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" });

// EM-1: canonical advance email subject —
// ADVANCE: [Artist] | [mm.dd.yyyy] | [Venue] | [City, State]
const advanceSubject = (artistName, isoDate, venueName, city, state) => {
  const [y, m, d] = String(isoDate || "").split("-");
  const dt  = y && m && d ? `${m}.${d}.${y}` : (isoDate || "");
  const loc = [city, state].filter(Boolean).join(", ");
  const head = ("ADVANCE: " + (artistName || "")).trim();
  return [head, dt, venueName, loc].filter(Boolean).join(" | ");
};

// ── Feature flag: venue/festival org linking UI (V-1 + ORG-1) ──────────────
// Hidden until venue/festival accounts actually exist on the platform — the
// "Show Code" strip and "⊕ Link Venue / Festival" search confused TMs when
// every search came back empty. Flip to true to bring both back; all the
// underlying code (event_links, lookup RPC, VenueDashboard) remains live.
const ORG_LINKING_UI = false;

// EM-1: convert the plain-text AI draft into email-safe HTML for mailbox drafts.
// The raw portal URL is replaced with a styled button banner (table-based — the
// only thing that renders reliably across Outlook/Gmail/Apple Mail). If the
// TM edited the URL out entirely, the banner is appended before the signoff
// area instead, so the recipient always gets a working portal entry point.
const draftBodyToHtml = (text, portalUrl, captionLabel) => {
  const esc    = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const toHtml = (s) => esc(s).replace(/\n/g, "<br/>");
  const banner = portalUrl ? (
    `<table role="presentation" cellpadding="0" cellspacing="0" border="0" align="center" style="margin:22px auto;">` +
      `<tr><td bgcolor="#f0a500" style="background:#f0a500;border-radius:8px;text-align:center;">` +
        `<a href="${portalUrl}" target="_blank" style="display:inline-block;padding:13px 36px;font-family:Helvetica,Arial,sans-serif;font-size:14px;font-weight:bold;letter-spacing:1.5px;color:#1a1200;text-decoration:none;text-transform:uppercase;">Open the Advance Portal</a>` +
      `</td></tr>` +
      (captionLabel ? `<tr><td style="padding-top:8px;text-align:center;font-family:Helvetica,Arial,sans-serif;font-size:11px;letter-spacing:0.5px;color:#999;">${esc(captionLabel)}</td></tr>` : "") +
    `</table>`
  ) : "";
  let html;
  if (portalUrl && text.includes(portalUrl)) {
    // Trim newlines at the seams — the banner brings its own margins, so
    // leftover line breaks around the old URL line would double the spacing
    const parts = text.split(portalUrl);
    html = parts.map((p, i) => {
      let t = p;
      if (i > 0) t = t.replace(/^\n+/, "");                 // after banner
      if (i < parts.length - 1) t = t.replace(/\n+$/, "");  // before banner
      return toHtml(t);
    }).join(banner);
  } else {
    html = toHtml(text) + banner;
  }
  return `<div style="font-family:Helvetica,Arial,sans-serif;font-size:14px;line-height:1.65;color:#222222;max-width:640px;">${html}</div>`;
};
const timeAgo = (iso) => {
  if (!iso) return null;
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1)   return "just now";
  if (mins < 60)  return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs  < 24)  return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7)   return `${days}d ago`;
  const wks = Math.floor(days / 7);
  return `${wks}w ago`;
};
const completionPct = (cl) => { const cats = Object.values(cl); return Math.round(cats.filter(c => c.complete).length / cats.length * 100); };
const uid = () => Date.now().toString(36) + Math.random().toString(36).slice(2, 5);

// Auto-detect a file label from its filename
// Labels available when uploading a document to an artist profile
const ARTIST_DOC_LABELS = [
  "Stage Plot", "Input List", "Technical Rider", "Hospitality Rider",
  "Backline Spec", "Setlist Template", "Press Kit", "Logo / Artwork", "Other",
];

const PRESS_CATEGORIES = ["Photos", "Bios", "Admats", "Fonts", "Promo Copy", "Other"];

// Tour document categories (replaces dedicated rider_file / w9 columns)
const TOUR_DOC_CATEGORIES = [
  { type: "tech_rider",         label: "Tech Rider",         accept: ".pdf,.doc,.docx,.txt,.pages" },
  { type: "hospitality_rider",  label: "Hospitality Rider",  accept: ".pdf,.doc,.docx,.txt,.pages" },
  { type: "w9",                 label: "W9 / Tax Doc",       accept: ".pdf,.jpg,.jpeg,.png" },
  { type: "stage_plot",         label: "Stage Plot",         accept: ".pdf,.jpg,.jpeg,.png,.png,.ai,.eps,.zip" },
  { type: "input_list",         label: "Input List",         accept: ".pdf,.doc,.docx,.xls,.xlsx,.txt" },
  { type: "other",              label: "Other",              accept: ".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.zip,.ai,.eps" },
];

function PressKitIcon({ cat, size = 11, color = "currentColor" }) {
  const s = { width: size, height: size, flexShrink: 0, display: "inline-block", verticalAlign: "middle" };
  const p = { fill: "none", stroke: color, strokeWidth: 1.4, strokeLinecap: "round", strokeLinejoin: "round" };
  switch (cat) {
    case "Photos":
      return (
        <svg viewBox="0 0 16 16" {...p} style={s}>
          <rect x="1.5" y="4" width="13" height="10.5" rx="1.5"/>
          <circle cx="8" cy="9.5" r="2.5"/>
          <path d="M5.5 4L6.5 2H9.5L10.5 4"/>
          <circle cx="12.5" cy="6.5" r="0.6" fill={color} stroke="none"/>
        </svg>
      );
    case "Bios":
      return (
        <svg viewBox="0 0 16 16" {...p} style={s}>
          <path d="M3.5 1.5H9.5L12.5 4.5V14.5H3.5Z"/>
          <path d="M9.5 1.5V4.5H12.5"/>
          <line x1="5.5" y1="7" x2="10.5" y2="7"/>
          <line x1="5.5" y1="9.5" x2="10.5" y2="9.5"/>
          <line x1="5.5" y1="12" x2="8.5" y2="12"/>
        </svg>
      );
    case "Admats":
      return (
        <svg viewBox="0 0 16 16" {...p} style={s}>
          <rect x="1.5" y="1.5" width="8.5" height="8.5" rx="1"/>
          <rect x="6" y="6" width="8.5" height="8.5" rx="1"/>
        </svg>
      );
    case "Fonts":
      return (
        <svg viewBox="0 0 16 16" fill={color} style={s}>
          <path d="M2 3.5h12v1.5H9.5v8.5h-3V5H2V3.5z"/>
        </svg>
      );
    case "Promo Copy":
      return (
        <svg viewBox="0 0 16 16" {...p} style={s}>
          <path d="M10.5 2.5L13.5 5.5L5.5 13.5H2.5V10.5Z"/>
          <line x1="8.5" y1="4.5" x2="11.5" y2="7.5"/>
          <line x1="2" y1="14.5" x2="14" y2="14.5"/>
        </svg>
      );
    default: // Other
      return (
        <svg viewBox="0 0 16 16" {...p} style={s}>
          <path d="M3.5 1.5H9.5L12.5 4.5V14.5H3.5Z"/>
          <path d="M9.5 1.5V4.5H12.5"/>
          <line x1="5.5" y1="8" x2="10.5" y2="8"/>
          <line x1="5.5" y1="10.5" x2="9" y2="10.5"/>
        </svg>
      );
  }
}
const PRESS_CATEGORY_ACCEPT = {
  Photos:       ".jpg,.jpeg,.png,.gif,.webp,.tiff,.psd,.ai,.eps,.zip",
  Bios:         ".pdf,.doc,.docx,.txt,.rtf",
  Admats:       ".pdf,.jpg,.jpeg,.png,.ai,.eps,.psd,.zip,.tiff",
  Fonts:        ".ttf,.otf,.woff,.woff2,.zip",
  "Promo Copy": ".pdf,.doc,.docx,.txt",
  Other:        ".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.zip,.ai,.eps,.psd",
};

const detectFileLabel = (filename) => {
  const n = filename.toLowerCase().replace(/[\-_.]/g, " ");
  if (/stage.?plot|stageplot|sp\s?\d/.test(n))              return "Stage Plot";
  if (/input.?list|channel.?list|patch.?list|inputs/.test(n)) return "Input List";
  if (/tech.?rider|technical.?rider|prod.?rider/.test(n))   return "Tech Rider";
  if (/hosp|hospitality|catering/.test(n))                  return "Hospitality Rider";
  if (/set.?list|setlist/.test(n))                          return "Setlist";
  if (/press.?kit|epk|\bepk\b|bio/.test(n))                 return "Press Kit";
  if (/contract|deal.?memo/.test(n))                        return "Contract";
  if (/\bw.?9\b|tax.?form/.test(n))                         return "W9";
  return "Other";
};

// ── Advance portal status (derived from token + promoter data) ─────────────
const advanceStatus = (show) => {
  if (!show?.advanceToken) return { label: "NOT SENT", color: "#3a3a3a", bg: "#141414" };
  const d = show.advanceData || {};
  const dataKeys = Object.keys(d).filter(k => !k.startsWith("_"));
  if (dataKeys.length === 0) return { label: "SENT", color: "#6a8faf", bg: "#0c1520" };
  if (d._submitted) return { label: "CONFIRMED", color: "var(--success)", bg: "#0a1a0a" };
  const allConfirmed = dataKeys.length > 0 && dataKeys.every(k => d[k]?._confirmed);
  if (allConfirmed) return { label: "CONFIRMED", color: "var(--success)", bg: "#0a1a0a" };
  return { label: "IN PROGRESS", color: "var(--accent)", bg: "#1a1000" };
};

// ── Artist color palette ───────────────────────────────────────────────────
const ARTIST_COLORS = [
  { pill: "#b09cbf", bg: "#19102a", text: "#ddd4e8" }, // lavender
  { pill: "#8faabc", bg: "#0f1a26", text: "#c8d8e4" }, // slate blue
  { pill: "#9db89d", bg: "#101e10", text: "#ccdacc" }, // sage
  { pill: "#c4a882", bg: "#261e0e", text: "#e4d4bc" }, // sand
  { pill: "#bc8f8f", bg: "#261010", text: "#e4cccc" }, // rose
  { pill: "#80aaa8", bg: "#0e201e", text: "#bcd8d4" }, // teal
];
const artistColor = (idx) => ARTIST_COLORS[idx % ARTIST_COLORS.length];

// ── Calendar helpers ───────────────────────────────────────────────────────
function calDays(year, month) {
  const first = new Date(year, month, 1).getDay(); // 0=Sun
  const total = new Date(year, month + 1, 0).getDate();
  return { first, total };
}
function isoDate(year, month, day) {
  return `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

// ── PDF Export ─────────────────────────────────────────────────────────────
function exportPDF(artist, show, venue) {
  const pct = completionPct(show.checklist);
  const sc  = statusConfig[show.status] || statusConfig.unstarted;
  const rows = CHECKLIST_TEMPLATE.filter(cat => show.checklist[cat.id]).map(cat => {
    const cd = show.checklist[cat.id];
    const fRows = cat.fields.map(f =>
      `<tr><td style="padding:3px 8px 3px 28px;color:#666;font-size:12px;">${f}</td><td style="padding:3px 8px;font-size:12px;color:#222;">${cd.fields[f] || "—"}</td></tr>`
    ).join("");
    const nRow = cd.notes ? `<tr><td colspan="2" style="padding:2px 8px 8px 28px;color:#888;font-size:11px;font-style:italic;">↳ ${cd.notes}</td></tr>` : "";
    return `<tr style="background:#f7f7f7;"><td colspan="2" style="padding:8px 8px;font-weight:600;font-size:14px;border-top:2px solid #eee;">
      <span style="display:inline-block;width:11px;height:11px;border-radius:4px;margin-right:8px;vertical-align:middle;background:${cd.complete ? "#f0a500" : "#ddd"};"></span>${cat.label}
    </td></tr>${fRows}${nRow}`;
  }).join("");

  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/>
<title>Advance — ${venue.name} ${fmtDate(show.date)}</title>
<style>
  body{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;margin:0;padding:32px 40px;color:#111;background:#fff;}
  h1{font-size:28px;letter-spacing:3px;text-transform:uppercase;margin:0 0 2px;}
  .meta{color:#999;font-size:11px;letter-spacing:2px;text-transform:uppercase;margin-bottom:24px;}
  .top{display:grid;grid-template-columns:1fr 1fr;gap:28px;padding-bottom:20px;border-bottom:2px solid #111;margin-bottom:24px;}
  .lbl{font-size:11px;letter-spacing:2px;text-transform:uppercase;color:#bbb;margin-bottom:4px;}
  .val{font-size:14px;color:#111;margin-bottom:6px;}
  .badge{display:inline-block;padding:3px 10px;border-radius:16px;font-size:11px;font-weight:600;background:#f0f0f0;color:#444;letter-spacing:1px;}
  .bar{height:5px;background:#eee;border-radius:8px;overflow:hidden;margin-top:5px;}
  .fill{height:100%;background:#f0a500;width:${pct}%;}
  table{width:100%;border-collapse:collapse;}
  .footer{margin-top:28px;padding-top:10px;border-top:1px solid #eee;font-size:11px;color:#ccc;text-align:center;letter-spacing:2px;}
</style></head><body>
  <h1>${venue.name}</h1>
  <div class="meta">${artist.name} · ${fmtDate(show.date)} · Advance Sheet</div>
  <div class="top">
    <div>
      <div class="lbl">Venue</div><div class="val" style="font-size:16px;font-weight:600;">${venue.name}</div>
      <div class="val">${[venue.city, venue.addressState].filter(Boolean).join(", ")}${venue.capacity ? ` · Cap. ${Number(venue.capacity).toLocaleString()}` : ""}${venue.ages ? ` · ${venue.ages}` : ""}</div>
      <div class="val" style="color:#888;font-size:12px;">${fmtVenueAddress(venue) || ""}</div>
      <div class="val" style="color:#888;font-size:12px;">${venue.loadIn || ""}</div>
    </div>
    <div>
      <div class="lbl">Promoter</div>
      <div class="val">${venue.promoterName || "—"}${venue.promoterCompany ? ` · ${venue.promoterCompany}` : ""}</div>
      <div class="val" style="color:#888;font-size:12px;">${venue.promoterEmail || ""}</div>
      <div class="val" style="color:#888;font-size:12px;">${venue.promoterPhone || ""}</div>
      <br/>
      <div class="lbl">Status</div><div class="badge">${sc.label}</div>
      <div style="margin-top:10px;"><div class="lbl">${pct}% Advanced</div><div class="bar"><div class="fill"></div></div></div>
    </div>
  </div>
  <table>${rows}</table>
  ${show.stagePlotUrl ? `<div style="margin-top:24px;padding-top:16px;border-top:2px solid #eee;">
    <div style="font-size:11px;letter-spacing:2px;text-transform:uppercase;color:#bbb;margin-bottom:8px;">Stage Plot</div>
    <a href="${show.stagePlotUrl}" style="font-size:14px;color:#f0a500;">${show.stagePlotUrl}</a>
    <div style="font-size:11px;color:#aaa;margin-top:4px;">Open link to view interactive stage plot</div>
  </div>` : ""}
  <div class="footer">ADVANCD · LR Artists · Generated ${new Date().toLocaleDateString()}</div>
</body></html>`;

  const blob = new Blob([html], { type: "text/html" });
  const url  = URL.createObjectURL(blob);
  const win  = window.open(url, "_blank");
  if (win) setTimeout(() => { win.print(); URL.revokeObjectURL(url); }, 700);
}

// ── Bulk Import Parser ─────────────────────────────────────────────────────
async function parseRoutingWithAI(rawText, venueList) {
  const venueNames = venueList.map(v => `${v.name} (${v.city}) [id:${v.id}]`).join("\n");
  const prompt = `You are a tour manager's assistant parsing booking confirmations, deal memos, and routing sheets into structured show data. Extract every show from the text below.

Known venues in our CRM (match against these if possible):
${venueNames || "None yet"}

For each show found, return a JSON object with these fields:

SHOW BASICS
- "date": ISO format YYYY-MM-DD — infer the most likely upcoming year if not specified
- "venueName": venue name exactly as written in the text
- "venueAddress": street address of the venue if mentioned (e.g. "123 Main St"), otherwise null
- "venueState": state or province abbreviation if mentioned (e.g. "NY", "CA", "TX"), otherwise null
- "venueZip": zip or postal code if mentioned, otherwise null
- "city": city name if mentioned
- "ages": age restriction if mentioned (e.g. "21+", "18+", "All Ages"), otherwise null
- "venueCapacity": venue capacity as a number if mentioned, otherwise null
- "matchedVenueId": the id field (e.g. "v_rec...") of the best matching CRM venue, or null if no confident match
- "confidence": "high", "medium", or "low" for the venue match

DEAL TERMS
- "guarantee": dollar amount as a number if mentioned (e.g. 5000), otherwise null
- "dealTerms": full deal structure as a string (e.g. "$5,000 flat guarantee" or "$2,500 vs 80% NBOR after $1,500" or "$3,000 + 10% of net ticket sales over breakeven"), otherwise null
- "isConfirmed": true if the show is definitively booked/confirmed, false if it is a hold, tentative, or unclear
- "specialTerms": any special terms, rain dates, or other caveats (excluding radius clause, which is captured separately), otherwise null
- "radiusDistance": if a radius clause is mentioned, the distance as a string including unit (e.g. "90 miles", "60 miles"), otherwise null
- "radiusDuration": if a radius clause is mentioned, the duration as a string (e.g. "90 days before and after", "60 days prior"), otherwise null
- "depositAmount": deposit amount as a number if mentioned, otherwise null
- "depositDue": deposit due date as YYYY-MM-DD if mentioned, otherwise null

CONTACTS
- "promoterName": promoter or buyer name if mentioned, otherwise null
- "promoterEmail": promoter email if mentioned, otherwise null
- "promoterPhone": promoter phone if mentioned, otherwise null
- "marketingContact": marketing contact as "Name <email>" string if mentioned, otherwise null
- "advanceContact": advance contact as "Name <email>" string if a separate person is listed, otherwise null

RUN OF SHOW
- "loadIn": load-in time if mentioned (e.g. "1:00 PM"), otherwise null
- "soundcheck": soundcheck time if mentioned, otherwise null
- "doorsTime": doors time if mentioned (e.g. "12:00 PM"), otherwise null
- "setTime": artist set time if mentioned (e.g. "5:30 PM"), otherwise null
- "setLength": set length in minutes as a number if mentioned (e.g. 60), otherwise null
- "curfew": curfew or end time if mentioned, otherwise null

HOSPITALITY
- "catering": catering description or budget if mentioned (e.g. "$200 buyout" or "hot meal for 6"), otherwise null
- "buyout": buyout dollar amount as a number if mentioned, otherwise null

MERCH
- "merchSplit": merch deal as a string (e.g. "Hard: 100% / Soft: 100%" or "85/15"), otherwise null
- "merchNotes": any merch comments or instructions (e.g. "artist runs table"), otherwise null

GUEST LIST
- "glCap": guest list cap as a number if mentioned, otherwise null
- "willCall": will call or ticket instructions if mentioned (e.g. "free to public", "ID at box office"), otherwise null

- "notes": any remaining relevant info not captured above, otherwise null

Return ONLY a valid JSON array, no markdown fences, no explanation.

Text to parse:
${rawText}`;

  const res = await fetch("/api/anthropic", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      model: "claude-sonnet-4-6", max_tokens: 4000,
      messages: [{ role: "user", content: prompt }]
    })
  });
  if (!res.ok) throw new Error(`API error ${res.status}`);
  const d = await res.json();
  const text = d.content?.[0]?.text || "[]";
  try { return JSON.parse(text.replace(/```json|```/g, "").trim()); }
  catch { return []; }
}

// ── Rec Parser — extracts place recommendations from unstructured text ────────
async function parseRecsWithAI(text, cityHint, stateHint) {
  const cityCtx = [cityHint, stateHint].filter(Boolean).join(", ") || null;
  const prompt = `You are extracting place recommendations from an informal text message, email, or note. Extract every distinct place being recommended.
${cityCtx ? `\nCity context (apply to any rec that doesn't specify its own location): ${cityCtx}` : ""}

Valid categories (pick the best fit):
- food    (restaurants, diners, food trucks, markets)
- drink   (bars, cocktail lounges, breweries, wineries)
- coffee  (cafes, coffee shops, tea houses)
- music   (live music venues, record stores, jazz clubs)
- art     (galleries, museums, theatres, cinemas)
- outdoors (parks, hikes, beaches, scenic spots, gardens)
- shop    (boutiques, bookshops, vintage stores, markets)
- other   (anything else worth visiting)

For each recommended place return a JSON object with:
- "name": the place name (string, required)
- "category": one of the categories above (string, required)
- "note": a short description from the text — max 100 chars — capturing why it's good (string or null)
- "city": city name (string — use context city if not mentioned, or null)
- "state": US/Canada state or province abbreviation if applicable (string or null)
- "url": a website or Google Maps URL if mentioned (string or null)

Return ONLY a valid JSON array with no markdown fences or explanation. If no recommendations are found, return [].

Text to parse:
${text}`;

  const res = await fetch("/api/anthropic", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      model: "claude-haiku-4-5-20251001",
      max_tokens: 2000,
      messages: [{ role: "user", content: prompt }],
    }),
  });
  if (!res.ok) throw new Error(`API error ${res.status}`);
  const d = await res.json();
  const raw = d.content?.[0]?.text || "[]";
  try { return JSON.parse(raw.replace(/```json|```/g, "").trim()); }
  catch { return []; }
}

// ── Guest List Bulk Parser ────────────────────────────────────────────────
async function parseGuestListWithAI(text) {
  const prompt = `You are extracting guest list entries from a message, email, or forwarded list sent by a promoter or band member.

For each guest entry return a JSON object with:
- "firstName": first name (string, required)
- "lastName": last name (string or null)
- "tickets": number of tickets/+1s (integer, default 1 if not mentioned)
- "access": array of access level strings from ["GA","AA","Family","Media","VIP","Backstage"] — pick what fits, default ["GA"] if unspecified
- "note": any extra context from the text (string or null)

Rules:
- A line like "John Smith +2" means firstName=John, lastName=Smith, tickets=2
- A line like "Maria — VIP" means access=["VIP"]
- "+1" or "and guest" means tickets=2
- Ignore header/footer lines that aren't names (e.g. "Guest list for tonight:", "Thanks")
- If the same name appears with different access levels, merge into one entry

Return ONLY a valid JSON array with no markdown fences or explanation. If no guests are found, return [].

Text to parse:
${text}`;

  const res = await fetch("/api/anthropic", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      model: "claude-haiku-4-5-20251001",
      max_tokens: 2000,
      messages: [{ role: "user", content: prompt }],
    }),
  });
  if (!res.ok) throw new Error(`API error ${res.status}`);
  const d = await res.json();
  const raw = d.content?.[0]?.text || "[]";
  try { return JSON.parse(raw.replace(/```json|```/g, "").trim()); }
  catch { return []; }
}

// ── Itinerary Parser ──────────────────────────────────────────────────────
async function parseItineraryWithAI(text, crewList) {
  const roster = crewList.length
    ? crewList.map(c => `- ${c.name} (${c.role || c.travel_group})`).join("\n")
    : "None yet";

  const prompt = `You are a tour manager's assistant. Parse the travel itinerary below and extract structured travel data.

Touring party:
${roster}

Return a JSON array where each element is a calendar date that has travel data. Only include dates that have actual flights, a hotel, or ground transport.

Schema for each element:
{
  "date": "YYYY-MM-DD",
  "hotel": {
    "name": string or null,
    "address": string or null,
    "check_in": "HH:MM 24h" or null,
    "check_out": "HH:MM 24h" or null,
    "confirmation": string or null,
    "phone": string or null
  },
  "passengers": [
    {
      "name": "match to touring party list above where possible",
      "flights": [
        {
          "airline_code": "2-letter IATA code e.g. AA",
          "flight_number": "digits only e.g. 1234",
          "airline": "full airline name",
          "origin": "IATA airport code",
          "destination": "IATA airport code",
          "origin_city": "departure city",
          "destination_city": "arrival city",
          "scheduled_dep": "YYYY-MM-DDTHH:MM:00",
          "scheduled_arr": "YYYY-MM-DDTHH:MM:00",
          "booking_ref": string or null,
          "seat": string or null,
          "cabin_class": "Economy|Premium Economy|Business|First" or null
        }
      ]
    }
  ],
  "ground_transport": [
    {
      "type": "car|van|rideshare|train|other",
      "pickup_time": "HH:MM 24h" or null,
      "pickup_location": string or null,
      "dropoff_location": string or null,
      "company": string or null,
      "driver_name": string or null,
      "driver_phone": string or null,
      "confirmation_number": string or null,
      "passenger_notes": string or null
    }
  ]
}

Rules:
- If a hotel covers multiple nights, include it on each night's date entry
- If multiple passengers share a flight, create a separate passenger entry for each
- Times are local — no timezone conversion
- Return ONLY a valid JSON array. No markdown fences, no explanation.

Itinerary:
${text}`;

  const res = await fetch("/api/anthropic", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      model: "claude-sonnet-4-6", max_tokens: 6000,
      messages: [{ role: "user", content: prompt }]
    })
  });
  if (!res.ok) throw new Error(`API error ${res.status}`);
  const d = await res.json();
  const raw = d.content?.[0]?.text || "[]";
  try { return JSON.parse(raw.replace(/```json|```/g, "").trim()); }
  catch { return []; }
}

// ── Day-Sheet Generator ────────────────────────────────────────────────────
function exportGuestList(artist, show, venue, entries) {
  const date = show.date ? new Date(show.date + "T12:00:00").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" }) : "";
  const ACCESS_COLORS = { "AAA": "var(--accent)", "VIP": "#8a6faf", "GA": "var(--success)", "Family": "var(--success)", "AA": "var(--accent)", "Media": "#6a8faf", "Backstage": "#aaa" };
  const rows = entries.map((g, i) => {
    const accessArr = Array.isArray(g.access) ? g.access : (g.access ? [g.access] : ["GA"]);
    const accessBadges = accessArr.map(a => `<span style="font-size:11px;font-weight:700;letter-spacing:0.5px;color:${ACCESS_COLORS[a] || "#555"};background:#f5f5f5;padding:2px 7px;border-radius:4px;margin-right:3px;">${a}</span>`).join("");
    const tix = Number(g.tickets) || 1;
    return `
    <tr style="border-bottom:1px solid #eee;${i % 2 === 0 ? "background:#fafafa;" : ""}">
      <td style="padding:8px 10px;font-weight:600;">${i + 1}</td>
      <td style="padding:8px 10px;">${g.lastName || "—"}</td>
      <td style="padding:8px 10px;">${g.firstName || "—"}</td>
      <td style="padding:8px 10px;color:#555;">${g.email || "—"}</td>
      <td style="padding:8px 10px;text-align:center;font-weight:600;">${tix}</td>
      <td style="padding:8px 10px;">${accessBadges}</td>
      <td style="padding:8px 10px;color:#555;font-size:11px;">${g.note || ""}</td>
      <td style="padding:8px 10px;text-align:center;">${g.confirmed ? "✓" : ""}</td>
    </tr>`;
  }).join("");
  const totalTickets = entries.reduce((sum, g) => sum + (Number(g.tickets) || 1), 0);
  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Guest List</title>
    <style>*{box-sizing:border-box;margin:0;padding:0;}body{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:14px;color:#111;padding:36px;}
    h1{font-size:20px;font-weight:700;margin-bottom:4px;}
    .meta{font-size:11px;color:#888;margin-bottom:24px;}
    table{width:100%;border-collapse:collapse;border:1px solid #ddd;}
    th{background:#111;color:#fff;padding:8px 10px;text-align:left;font-size:11px;letter-spacing:1px;text-transform:uppercase;}
    @media print{body{padding:18px;}}</style></head><body>
    <h1>Guest List${artist?.name ? " — " + artist.name : ""}</h1>
    <div class="meta">${[venue?.name, [venue?.city, venue?.addressState].filter(Boolean).join(", "), date].filter(Boolean).join(" · ")} · ${entries.length} guest${entries.length !== 1 ? "s" : ""} · ${totalTickets} ticket${totalTickets !== 1 ? "s" : ""}</div>
    <table><thead><tr><th>#</th><th>Last Name</th><th>First Name</th><th>Email</th><th>Tickets</th><th>Access</th><th>Note</th><th>✓</th></tr></thead>
    <tbody>${rows}</tbody></table></body></html>`;
  const w = window.open("", "_blank");
  w.document.write(html);
  w.document.close();
  w.focus();
  setTimeout(() => w.print(), 400);
}

function exportDaySheet(artist, tour, show, venue) {
  const cl = show.checklist;
  const li = cl.loadIn?.fields    || {};
  const ho = cl.hospitality?.fields || {};
  const pk = cl.parking?.fields   || {};
  const gr = cl.greenRoom?.fields  || {};
  const me = cl.merch?.fields      || {};

  const row = (label, val) => val
    ? `<tr><td style="padding:5px 0;color:#888;font-size:12px;width:140px;vertical-align:top;">${label}</td><td style="padding:5px 0;font-size:14px;color:#111;">${val}</td></tr>`
    : "";

  const section = (title, icon, rows) => {
    const content = rows.filter(Boolean).join("");
    if (!content) return "";
    return `
      <div style="margin-bottom:24px;">
        <div style="font-size:11px;letter-spacing:3px;text-transform:uppercase;color:#bbb;margin-bottom:10px;display:flex;align-items:center;gap:8px;">
          <span>${icon}</span><span>${title}</span>
        </div>
        <table style="width:100%;border-collapse:collapse;">${content}</table>
      </div>`;
  };

  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/>
<title>Day Sheet — ${venue.name} ${fmtDate(show.date)}</title>
<style>
  *{box-sizing:border-box;margin:0;padding:0;}
  body{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;background:#fff;color:#111;padding:0;}
  .cover{background:#111;color:#fff;padding:36px 44px 28px;}
  .cover-label{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;letter-spacing:3px;color:#555;text-transform:uppercase;margin-bottom:10px;}
  .cover-artist{font-size:40px;font-weight:600;letter-spacing:-0.5px;margin-bottom:4px;}
  .cover-venue{font-size:20px;color:#aaa;font-weight:300;}
  .cover-date{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:14px;color:#f0a500;margin-top:8px;}
  .body{padding:32px 44px;}
  .times-grid{display:grid;grid-template-columns:repeat(5,1fr);gap:0;margin-bottom:28px;border:1px solid #eee;border-radius:8px;overflow:hidden;}
  .time-cell{padding:14px 16px;border-right:1px solid #eee;}
  .time-cell:last-child{border-right:none;}
  .time-lbl{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;letter-spacing:2px;text-transform:uppercase;color:#bbb;margin-bottom:5px;}
  .time-val{font-size:20px;font-weight:600;color:#111;}
  .time-val.empty{color:#ddd;font-weight:300;font-size:14px;}
  .divider{border:none;border-top:1px solid #f0f0f0;margin:20px 0;}
  @media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact;}.cover{background:#111!important;color:#fff!important;}}
</style></head>
<body>
  <div class="cover">
    <div class="cover-label">Day Sheet · ${tour.name}</div>
    <div class="cover-artist">${artist.name}</div>
    <div class="cover-venue">${venue.name} · ${[venue.city, venue.addressState].filter(Boolean).join(", ")}</div>
    <div class="cover-date">${fmtDate(show.date)}</div>
  </div>
  <div class="body">

    <div class="times-grid">
      <div class="time-cell"><div class="time-lbl">Load-in</div><div class="time-val ${!li["Load-in time"] ? "empty" : ""}">${li["Load-in time"] || "TBD"}</div></div>
      <div class="time-cell"><div class="time-lbl">Soundcheck</div><div class="time-val ${!li["Soundcheck time"] ? "empty" : ""}">${li["Soundcheck time"] || "TBD"}</div></div>
      <div class="time-cell"><div class="time-lbl">Doors</div><div class="time-val ${!li["Doors time"] ? "empty" : ""}">${li["Doors time"] || "TBD"}</div></div>
      <div class="time-cell"><div class="time-lbl">Set Time</div><div class="time-val ${!li["Set time"] ? "empty" : ""}">${li["Set time"] || "TBD"}</div></div>
      <div class="time-cell"><div class="time-lbl">Curfew</div><div class="time-val ${!li["Curfew"] ? "empty" : ""}">${li["Curfew"] || "TBD"}</div></div>
    </div>

    ${section("Venue & Contacts", "📍", [
      row("Address",    fmtVenueAddress(venue)),
      row("Load-in",    venue.loadIn),
      row("Ages",       venue.ages),
      row("Setup",      venue.gaSeated),
      row("Promoter",   venue.promoterName ? `${venue.promoterName}${venue.promoterCompany ? " · " + venue.promoterCompany : ""}${venue.promoterEmail ? " · " + venue.promoterEmail : ""}${venue.promoterPhone ? " · " + venue.promoterPhone : ""}` : null),
    ])}

    ${section("Parking", "🚐", [
      row("Parking",    venue.parking),
      row("Passes",     pk["Parking passes"]),
      row("Contact",    pk["Parking contact"]),
    ])}

    ${section("Hospitality", "🍽", [
      row("Catering",   ho["Catering"]),
      row("Buyout",     ho["Buyout amount"]),
      row("Drinks",     ho["Drinks"]),
      row("Dietary",    ho["Dietary restrictions noted"]),
      cl.hospitality?.notes ? row("Notes", cl.hospitality.notes) : "",
    ])}

    ${section("Green Room", "🔑", [
      row("Location",   gr["Green room location"]),
      row("Wi-Fi",      gr["Wi-Fi credentials"]),
      row("Bathroom",   gr["Private bathroom confirmed"]),
      cl.greenRoom?.notes ? row("Notes", cl.greenRoom.notes) : "",
    ])}

    ${section("Merch", "🛍", [
      row("Split",      me["Merch split %"]),
      row("Table",      me["Table location"]),
      row("Rep on-site",me["Merch rep on-site"]),
      row("Cash float", me["Cash float"]),
    ])}

    ${show.stagePlotUrl ? `
    <div style="margin-bottom:24px;">
      <div style="font-size:11px;letter-spacing:3px;text-transform:uppercase;color:#bbb;margin-bottom:10px;display:flex;align-items:center;gap:8px;">
        <span>🎸</span><span>Stage Plot</span>
      </div>
      <a href="${show.stagePlotUrl}" style="font-size:14px;color:#111;font-weight:600;display:block;margin-bottom:4px;">${show.stagePlotUrl}</a>
      <div style="font-size:11px;color:#aaa;">Open link to view full interactive stage plot in PLOTR</div>
    </div>` : ""}

  </div>
</body></html>`;

  const blob = new Blob([html], { type: "text/html" });
  const url  = URL.createObjectURL(blob);
  const win  = window.open(url, "_blank");
  if (win) setTimeout(() => { win.print(); URL.revokeObjectURL(url); }, 700);
}

// ── AI Email ───────────────────────────────────────────────────────────────
async function draftAdvanceEmail(artist, show, venue, userProfile, advanceToken) {
  const tmName   = userProfile?.name   || "[Your Name]";
  const tmAgency = userProfile?.agency || "[Your Agency]";
  const signoff  = `${tmName} | Tour Manager for ${artist.name}${tmAgency ? `, ${tmAgency}` : ""}`;
  const mgmtLine    = artist.mgmt?.name    ? `Management: ${artist.mgmt.name}${artist.mgmt.email ? ` <${artist.mgmt.email}>` : ""}` : "";
  const bookingLine = artist.booking?.agent ? `Booking: ${artist.booking.agent}${artist.booking.agency ? ` @ ${artist.booking.agency}` : ""}${artist.booking.email ? ` <${artist.booking.email}>` : ""}` : "";
  const portalUrl = `${window.location.origin}/advance/${advanceToken}`;

  const res = await fetch("/api/anthropic", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      model: "claude-sonnet-4-6", max_tokens: 800,
      messages: [{ role: "user", content: `You are a professional tour manager drafting an initial advance email to a venue promoter.
Artist: ${artist.name}
Show Date: ${fmtDate(show.date)}
Venue: ${venue.name}, ${[venue.city, venue.addressState].filter(Boolean).join(", ")}${venue.capacity ? ` (cap. ${venue.capacity})` : ""}
Promoter: ${venue.promoterName || ""}${venue.promoterCompany ? ` (${venue.promoterCompany})` : ""}
${mgmtLine}
${bookingLine}
${show.notes ? `Show notes: ${show.notes}` : ""}
Advance portal link: ${portalUrl}

Write a short, warm, professional advance intro email (~100 words). Introduce yourself as TM, mention the show, and direct the promoter to fill out the advance portal at the link. Include the portal link exactly once, on its own line by itself with no label or punctuation around it. Mention that it covers all logistics (run of show, hospitality, backline, parking, guest list, merch, settlement) and that you've pre-filled anything you already know. Keep it friendly and to the point. Do NOT enumerate the advance topics in the email — the portal handles that. Sign off as "${signoff}". Email body only, no subject line.` }]
    })
  });
  const d = await res.json();
  return d.content?.[0]?.text || "Failed to generate.";
}

// ── FT-1: Financials constants ─────────────────────────────────────────────
const EXPENSE_CATEGORIES = [
  { id: "accommodation", label: "Accommodation",    color: "var(--success)" },
  { id: "travel",        label: "Travel",           color: "var(--accent)" },
  { id: "per_diems",     label: "Per Diems",        color: "#a78bfa" },
  { id: "production",    label: "Production",       color: "#38bdf8" },
  { id: "marketing",     label: "Marketing",        color: "#fb923c" },
  { id: "crew",          label: "Crew / Wages",     color: "#e879f9" },
  { id: "catering",      label: "Catering / Rider", color: "#4ade80" },
  { id: "merch_costs",   label: "Merch / Print",    color: "#818cf8" },
  { id: "misc",          label: "Miscellaneous",    color: "#94a3b8" },
];

const CURRENCIES = [
  { code: "USD", symbol: "$",  label: "USD – US Dollar" },
  { code: "EUR", symbol: "€",  label: "EUR – Euro" },
  { code: "GBP", symbol: "£",  label: "GBP – British Pound" },
  { code: "CAD", symbol: "CA$",label: "CAD – Canadian Dollar" },
  { code: "AUD", symbol: "A$", label: "AUD – Australian Dollar" },
  { code: "JPY", symbol: "¥",  label: "JPY – Japanese Yen" },
  { code: "MXN", symbol: "MX$",label: "MXN – Mexican Peso" },
  { code: "NZD", symbol: "NZ$",label: "NZD – New Zealand Dollar" },
  { code: "SEK", symbol: "kr", label: "SEK – Swedish Krona" },
  { code: "NOK", symbol: "kr", label: "NOK – Norwegian Krone" },
  { code: "DKK", symbol: "kr", label: "DKK – Danish Krone" },
  { code: "CHF", symbol: "Fr", label: "CHF – Swiss Franc" },
  { code: "BRL", symbol: "R$", label: "BRL – Brazilian Real" },
];

function getCurrencySymbol(code) {
  return CURRENCIES.find(c => c.code === code)?.symbol || code;
}

function fmtMoney(amount, currency = "USD") {
  const sym = getCurrencySymbol(currency);
  const num = Number(amount) || 0;
  return `${sym}${num.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// ── FT-1 AI helpers ────────────────────────────────────────────────────────
async function getTourFinancialInsight({ tour, shows, expenses, budget, venues: vMap }) {
  const todayStr = new Date().toISOString().slice(0, 10);
  const totalContracted = shows.reduce((sum, s) => {
    const g = Number((s.settlement?.guarantee || "0").replace(/[^0-9.]/g, ""));
    return sum + (isNaN(g) ? 0 : g);
  }, 0);
  const totalDepositsReceived = shows.reduce((sum, s) => {
    if (!s.settlement?.depositReceived) return sum;
    const d = Number((s.settlement?.deposit || "0").replace(/[^0-9.]/g, ""));
    return sum + (isNaN(d) ? 0 : d);
  }, 0);
  const totalSettled = shows.filter(s => s.settlement?.settled).reduce((sum, s) => {
    const g = Number((s.settlement?.guarantee || "0").replace(/[^0-9.]/g, ""));
    return sum + (isNaN(g) ? 0 : g);
  }, 0);
  const totalExpenses = expenses.filter(e => e.currency === "USD").reduce((sum, e) => sum + Number(e.amount), 0);
  const outstandingShows = shows.filter(s => s.date < todayStr && !s.settlement?.settled).length;
  const upcomingShows = shows.filter(s => s.date >= todayStr).length;
  const budgetTotal = budget.filter(b => b.currency === "USD").reduce((sum, b) => sum + Number(b.budgeted_amount), 0);

  const expByCat = {};
  expenses.forEach(e => {
    if (!expByCat[e.category]) expByCat[e.category] = 0;
    expByCat[e.category] += Number(e.amount);
  });
  const budByCat = {};
  budget.forEach(b => { budByCat[b.category] = Number(b.budgeted_amount); });
  const overBudgetCats = EXPENSE_CATEGORIES
    .filter(c => expByCat[c.id] > 0 && budByCat[c.id] > 0 && expByCat[c.id] > budByCat[c.id])
    .map(c => `${c.label} (spent ${fmtMoney(expByCat[c.id])} vs budget ${fmtMoney(budByCat[c.id])})`);

  const prompt = `You are a financial advisor for a touring music act. Provide a concise, plain-English financial health summary (3-4 sentences max) for this tour.

Tour: "${tour.name}"
Shows: ${shows.length} total, ${upcomingShows} upcoming, ${outstandingShows} outstanding settlements
Total contracted (guarantees): ${fmtMoney(totalContracted)}
Deposits received: ${fmtMoney(totalDepositsReceived)}
Total settled: ${fmtMoney(totalSettled)}
Total expenses logged (USD): ${fmtMoney(totalExpenses)}
Total budget (USD): ${fmtMoney(budgetTotal)}
${overBudgetCats.length > 0 ? `Over-budget categories: ${overBudgetCats.join("; ")}` : "No categories currently over budget."}

Write a short, direct summary covering: income vs expenses, any concerning patterns, and one actionable recommendation. No headers. Plain text only.`;

  const res = await fetch("/api/anthropic", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      model: "claude-haiku-4-5-20251001", max_tokens: 400,
      messages: [{ role: "user", content: prompt }]
    })
  });
  if (!res.ok) throw new Error(`API error ${res.status}`);
  const d = await res.json();
  return d.content?.[0]?.text || "Unable to generate insight.";
}

async function getBudgetSuggestions({ tourName, showCount, tourDays: dayCount, crewCount }) {
  const prompt = `You are a tour manager building an expense budget for a music tour.

Tour: "${tourName}"
Shows: ${showCount}
Tour days: ${dayCount}
Crew/band on the road: ${crewCount}

Return a JSON array of budget suggestions. Each item: { "category": string (one of: accommodation, travel, per_diems, production, marketing, crew, catering, merch_costs, misc), "amount": number (USD), "notes": string (1 sentence explaining the estimate) }.
Cover all relevant categories. Be realistic for a mid-level touring act. Return ONLY valid JSON, no markdown.`;

  const res = await fetch("/api/anthropic", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      model: "claude-haiku-4-5-20251001", max_tokens: 1000,
      messages: [{ role: "user", content: prompt }]
    })
  });
  if (!res.ok) throw new Error(`API error ${res.status}`);
  const d = await res.json();
  const text = d.content?.[0]?.text || "[]";
  try { return JSON.parse(text.replace(/```json|```/g, "").trim()); }
  catch { return []; }
}

// ── Styles ─────────────────────────────────────────────────────────────────
const S = `
  *{box-sizing:border-box;margin:0;padding:0;}
  input,textarea,select,button{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;}
  body{background:#0d0d0d;}
  ::-webkit-scrollbar{width:4px;}
  ::-webkit-scrollbar-track{background:#111;}
  ::-webkit-scrollbar-thumb{background:#333;border-radius:4px;}
  .app{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;background:#0d0d0d;color:#e8e8e8;height:100vh;overflow:hidden;display:flex;}

  /* Sidebar */
  .sb{width:260px;min-width:260px;background:#111;border-right:1px solid #1e1e1e;display:flex;flex-direction:column;height:100vh;position:sticky;top:0;overflow:hidden;transition:width 0.2s ease,min-width 0.2s ease;}
  .sb.collapsed{width:40px;min-width:40px;overflow:hidden;}
  .sb.collapsed .sb-inner{display:none;}
  .sb-inner{display:flex;flex-direction:column;height:100%;overflow:hidden;}
  .sb-content{flex:1;overflow-y:auto;min-height:0;}
  .sb-collapse-btn{position:absolute;top:18px;right:-12px;width:22px;height:22px;background:#1e1e1e;border:1px solid #2a2a2a;border-radius:50%;cursor:pointer;display:flex;align-items:center;justify-content:center;font-size:11px;color:#666;z-index:10;transition:color 0.15s;}
  .sb-collapse-btn:hover{color:#ccc;}
  .sb-expand-btn{display:flex;align-items:center;justify-content:center;height:100%;cursor:pointer;color:#555;font-size:14px;writing-mode:vertical-lr;}
  .sb-expand-btn:hover{color:#aaa;}
  .sb-logo{padding:22px 20px 14px;border-bottom:1px solid #1e1e1e;display:flex;align-items:flex-end;justify-content:space-between;}
  .wordmark{font-family:var(--font-display);font-weight:700;font-size:28px;letter-spacing:0.5px;background:linear-gradient(90deg,#f0a500,#ffcc55);-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;}
  .tagline{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;color:#444;letter-spacing:1px;margin-top:2px;}
  .saved{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;color:var(--success);letter-spacing:1px;opacity:0;transition:opacity 0.4s;}
  .saved.on{opacity:1;}
  .sb-sec{padding:10px 0 4px;}
  .sb-nav{padding:8px 16px 8px 18px;cursor:pointer;font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:12px;color:#555;display:flex;align-items:center;gap:9px;transition:all 0.15s;border-left:2px solid transparent;letter-spacing:0.2px;}
  .sb-nav:hover{color:#bbb;background:#181818;}
  .sb-nav.on{color:var(--accent);border-left-color:var(--accent);background:#1a1500;}
  .sb-nav svg{flex-shrink:0;opacity:0.62;transition:opacity 0.15s;}
  .sb-nav:hover svg{opacity:0.85;}
  .sb-nav.on svg{opacity:1;}

  /* Main */
  .main{flex:1;overflow-y:auto;min-width:0;scrollbar-gutter:stable;}

  /* Page header */
  .ph{padding:30px 36px 22px;border-bottom:1px solid #1a1a1a;display:flex;align-items:flex-end;justify-content:space-between;gap:16px;}
  .ph-title{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-weight:700;font-size:40px;letter-spacing:-0.5px;color:#f0f0f0;line-height:1;text-align:left;}
  .ph-sub{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;color:#555;margin-top:5px;letter-spacing:1px;text-align:left;}
  .stats{display:flex;gap:24px;}
  .stat-n{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-weight:700;font-size:28px;color:var(--accent);line-height:1;text-align:center;}
  .stat-l{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;color:#555;letter-spacing:1px;margin-top:2px;text-align:center;}

  /* Tour name input */
  .tour-inp{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-weight:700;font-size:40px;letter-spacing:-0.5px;color:#f0f0f0;background:transparent;border:none;border-bottom:2px solid transparent;outline:none;width:100%;line-height:1;transition:border-color 0.15s;}
  .tour-inp:hover{border-bottom-color:#2a2a2a;}
  .tour-inp:focus{border-bottom-color:var(--accent);}

  /* Shows grid */
  .sg{padding:26px 36px;}
  .sg-hdr,.s-row{display:grid;grid-template-columns:100px 3fr 140px 60px 160px;gap:12px;padding:0 16px;}
  .sg-hdr{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;letter-spacing:2px;color:#444;text-transform:uppercase;padding-bottom:10px;border-bottom:1px solid #1a1a1a;margin-bottom:4px;}
  .s-row{align-items:center;padding-top:13px;padding-bottom:13px;cursor:pointer;border-radius:8px;border:1px solid transparent;transition:all 0.15s;margin-bottom:3px;}
  .s-row:hover{background:#141414;border-color:#222;}
  .s-row.sel{background:#161616;border-color:#2a2a2a;}
  /* Past shows: faded but fully accessible — hover or select restores.
     Past shows still needing settlement fade less so SETTLE UP stays legible. */
  .s-row.past{opacity:0.45;}
  .s-row.past.unsettled{opacity:0.75;}
  .s-row.past:hover,.s-row.past.sel{opacity:1;}
  .s-date{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;color:#888;}
  .s-vname{font-weight:500;font-size:14px;color:#e0e0e0;}
  .s-city{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;color:#555;margin-top:2px;}
  .s-badge{display:inline-flex;align-items:center;gap:6px;padding:4px 10px;border-radius:16px;font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;}
  .s-dot{width:6px;height:6px;border-radius:50%;flex-shrink:0;}
  .pb-wrap{background:#1a1a1a;border-radius:8px;height:4px;overflow:hidden;}
  .pb-fill{height:100%;border-radius:8px;background:linear-gradient(90deg,#f0a500,#ffcc55);transition:width 0.4s;}
  .pb-lbl{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;color:#555;margin-top:4px;}
  .s-acts{display:flex;gap:6px;align-items:center;}
  @keyframes toastIn{from{transform:translateY(-10px);opacity:0;}to{transform:translateY(0);opacity:1;}}
  @keyframes undoShrink{from{width:100%;}to{width:0%;}}
  .save-toast{position:fixed;top:20px;right:20px;z-index:9000;background:#1a0808;border:1px solid #5a1a1a;border-radius:8px;padding:12px 18px;font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:12px;color:var(--danger);box-shadow:0 4px 24px rgba(0,0,0,0.5);display:flex;align-items:center;gap:10px;animation:toastIn 0.25s ease;}
  .app.light .save-toast{background:#fce8e8;border-color:#f5c5c5;color:#c0392b;box-shadow:0 4px 24px rgba(0,0,0,0.12);}
  .day-of-bar{background:#081508;border-bottom:1px solid #1a3a1a;padding:10px 20px;}
  .day-of-item{display:flex;flex-direction:column;gap:3px;}
  .day-of-k{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;color:#2a5a2a;letter-spacing:1.5px;text-transform:uppercase;}
  .day-of-v{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:14px;color:#ccc;}
  .app.light .day-of-bar{background:#f0faf0;border-bottom-color:#c8e6c9;}
  .app.light .day-of-k{color:#5a8a5a;}
  .app.light .day-of-v{color:#1a1917;}
  .calc-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:8px;}
  .calc-cell{background:#0d0d0d;border:1px solid #1e1e1e;border-radius:8px;padding:9px 12px;}
  .calc-k{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;color:#444;letter-spacing:1px;margin-bottom:4px;}
  .calc-v{font-size:14px;font-weight:600;color:#ccc;}
  .app.light .calc-cell{background:#e4e0da;border-color:#d0ccc6;}
  .app.light .calc-k{color:#8a8784;}
  .app.light .calc-v{color:#1a1917;}
  .abtn{background:#1a1a1a;border:1px solid #272727;color:#aaa;padding:6px 13px;border-radius:4px;font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;cursor:pointer;transition:all 0.15s;white-space:nowrap;}
  .abtn:hover{background:#222;color:var(--accent);border-color:var(--accent);}
  .ibtn{background:none;border:none;color:#333;cursor:pointer;padding:4px 6px;border-radius:4px;font-size:14px;transition:all 0.15s;line-height:1;}
  .ibtn:hover{color:#c84b4b;background:#1a0808;}
  .ibtn.ed:hover{color:var(--accent);background:#1a1500;}

  /* Detail panel */
  .dp{border-top:1px solid #1a1a1a;background:#0f0f0f;}
  .dp-hdr{padding:22px 36px 0;display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:12px;}
  .dp-title{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-weight:700;font-size:20px;letter-spacing:0;color:#f0f0f0;}
  .dp-meta{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;color:#555;margin-top:4px;}
  .dp-body{padding:24px 40px 48px;}
  .xbtn{background:none;border:none;color:#555;cursor:pointer;font-size:20px;padding:4px;transition:color 0.15s;}
  .xbtn:hover{color:#aaa;}
  /* Detail tabs */
  .dp-tabs{display:flex;align-items:flex-end;gap:2px;padding:18px 36px 0;border-bottom:1px solid #1a1a1a;margin-top:16px;}
  .dp-tab{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;letter-spacing:1.5px;text-transform:uppercase;padding:8px 18px;cursor:pointer;border:none;background:none;color:#444;border-bottom:2px solid transparent;margin-bottom:-1px;transition:all 0.15s;}
  .dp-tab:hover{color:#888;}
  .dp-tab.on{color:var(--accent);border-bottom-color:var(--accent);}
  /* Advance tab full-width layout */
  .adv-wrap{padding:24px 40px 48px;}
  .adv-ctrl{display:flex;align-items:center;gap:10px;margin-bottom:20px;flex-wrap:wrap;}
  .adv-ctrl-left{display:flex;align-items:center;gap:10px;flex:1;}
  .adv-link-bar{display:flex;align-items:center;gap:8px;padding:8px 14px;background:#141414;border:1px solid #1e1e1e;border-radius:8px;margin-bottom:18px;}
  .adv-link-url{flex:1;font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;color:#444;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
  .adv-hint{font-size:12px;color:#444;line-height:1.5;margin-bottom:18px;}
  .adv-grid{display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:10px;align-items:start;}
  .adv-full{display:flex;flex-direction:column;gap:10px;}
  .adv-3col{display:grid;grid-template-columns:1fr 1fr 1fr;gap:10px;margin-bottom:10px;}
  .adv-sections{display:flex;flex-direction:column;gap:10px;margin-bottom:10px;}
  /* Flag button: hidden until the section header is hovered (stays visible
     while flagged or while the note editor is open) — cuts header noise */
  .adv-sec-hdr-row .adv-flag-slot{opacity:0;transition:opacity 0.15s;}
  .adv-sec-hdr-row:hover .adv-flag-slot,.adv-sec-hdr-row .adv-flag-slot.on{opacity:1;}

  /* Venue card */
  .vc{background:#141414;border:1px solid #1e1e1e;border-radius:8px;padding:16px;margin-bottom:18px;}
  .vc-lbl{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;letter-spacing:2px;color:#555;text-transform:uppercase;margin-bottom:10px;}
  .vc-name{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-weight:700;font-size:20px;letter-spacing:0;color:var(--accent);}
  .vc-grid{display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-top:10px;}
  .vf-k{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;color:#444;letter-spacing:1px;text-transform:uppercase;}
  .vf-v{font-size:12px;color:#bbb;margin-top:2px;}

  /* Checklist */
  .cl{display:flex;flex-direction:column;gap:5px;}
  .cl-cat{border:1px solid #1e1e1e;border-radius:8px;overflow:hidden;}
  .cl-hdr{display:flex;align-items:center;gap:10px;padding:10px 14px;cursor:pointer;background:#141414;transition:background 0.15s;user-select:none;}
  .cl-hdr:hover{background:#181818;}
  .chk{width:18px;height:18px;border-radius:4px;border:2px solid #333;display:flex;align-items:center;justify-content:center;flex-shrink:0;transition:all 0.15s;cursor:pointer;}
  .chk.dn{background:var(--accent);border-color:var(--accent);}
  .chk svg{opacity:0;transition:opacity 0.15s;}
  .chk.dn svg{opacity:1;}
  .cl-lbl{font-size:14px;font-weight:500;color:#ccc;flex:1;}
  .chev{color:#444;font-size:12px;transition:transform 0.2s;}
  .chev.op{transform:rotate(90deg);}
  .cl-flds{padding:10px 14px 12px;background:#111;display:flex;flex-direction:column;gap:7px;border-top:1px solid #1a1a1a;}
  .f-row{display:flex;align-items:center;gap:10px;}
  .f-lbl{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;color:#555;width:130px;flex-shrink:0;}
  .f-inp{flex:1;background:#0d0d0d;border:1px solid #1e1e1e;border-radius:4px;padding:5px 10px;font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:12px;color:#ccc;outline:none;transition:border-color 0.15s;}
  .f-inp:focus{border-color:var(--accent);}
  .f-inp::placeholder{color:#333;}
  /* Setlist song cell — reads as plain text, reveals an editable field on hover/focus */
  .sl-inp{width:100%;box-sizing:border-box;padding:5px 7px;border:1px solid transparent;border-radius:4px;font-size:12px;font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;color:var(--text);background:transparent;outline:none;min-width:0;transition:background 0.12s,border-color 0.12s;}
  .sl-inp:hover{border-color:var(--field-border);background:var(--panel-alt);}
  .sl-inp:focus{border-color:var(--accent);background:var(--panel-alt);}
  .sl-inp::placeholder{color:var(--text-vfaint);}
  .cl-notes{margin-top:5px;}
  .cl-notes textarea{width:100%;background:#0d0d0d;border:1px solid #1e1e1e;border-radius:4px;padding:7px 10px;font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:12px;color:#bbb;outline:none;resize:none;transition:border-color 0.15s;}
  .cl-notes textarea:focus{border-color:var(--accent);}

  /* Email / export panel */
  .ep{display:flex;flex-direction:column;gap:12px;}
  .ec{background:#141414;border:1px solid #1e1e1e;border-radius:8px;overflow:hidden;}
  .ec-hdr{padding:13px 16px;border-bottom:1px solid #1a1a1a;display:flex;align-items:center;justify-content:space-between;}
  .ec-ttl{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;letter-spacing:2px;color:#555;text-transform:uppercase;}
  .draft-btn{background:var(--accent);color:#0d0d0d;border:none;padding:7px 15px;border-radius:4px;font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;letter-spacing:1px;cursor:pointer;font-weight:700;transition:background 0.15s;display:flex;align-items:center;gap:6px;}
  .draft-btn:hover{background:#ffcc55;}
  .draft-btn:disabled{background:#2a2a2a;color:#555;cursor:not-allowed;}
  .e-to{padding:11px 16px;border-bottom:1px solid #1a1a1a;font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;color:#666;}
  .e-to span{color:#aaa;}
  .e-body{padding:14px 16px;min-height:170px;font-size:14px;color:#bbb;line-height:1.65;white-space:pre-wrap;font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;}
  .e-ph{color:#333;font-style:italic;}
  .e-load{display:flex;align-items:center;gap:10px;color:#555;font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;}
  .spin{width:14px;height:14px;border:2px solid #333;border-top-color:var(--accent);border-radius:50%;animation:spin 0.8s linear infinite;}
  @keyframes spin{to{transform:rotate(360deg);}}
  @keyframes pulse{0%,100%{opacity:0.3;}50%{opacity:1;}}
  @keyframes skeletonShimmer{0%{background-position:100% 50%;}100%{background-position:0 50%;}}
  /* Mobile: pressed-state feedback for tappable rows/cards. filter (not background)
     so it shows over inline-styled backgrounds; transient, theme-agnostic. */
  .mob-press{transition:filter 0.1s ease;}
  .mob-press:active{filter:brightness(0.94);}
  .tm-flds-wrap{display:grid;grid-template-rows:0fr;transition:grid-template-rows 0.3s cubic-bezier(0.4,0,0.2,1);}
  .tm-flds-wrap.open{grid-template-rows:1fr;}
  .tm-flds-inner{overflow:hidden;}
  /* Advance section field layout — left-aligns inputs and gives them real width */
  .adv-flds{text-align:left;}
  .adv-flds .f-inp{width:100%;box-sizing:border-box;}
  .adv-flds textarea.f-inp{display:block;}
  .adv-fld-grid{display:grid;grid-template-columns:1fr 1fr;gap:12px 16px;align-items:start;}
  .e-btns{display:flex;gap:8px;padding:0 16px 13px;}
  .sm-btn{background:none;border:1px solid #222;color:#555;padding:6px 12px;border-radius:4px;font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;cursor:pointer;transition:all 0.15s;}
  .sm-btn:hover{color:#aaa;border-color:#444;}
  .pdf-btn{background:none;border:1px solid #222;color:#555;padding:6px 12px;border-radius:4px;font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;cursor:pointer;transition:all 0.15s;}
  .pdf-btn:hover{color:var(--accent);border-color:var(--accent);}
  /* PLOTR card */
  .plotr-url-row{display:flex;gap:8px;padding:12px 16px;border-bottom:1px solid #1a1a1a;align-items:center;}
  .plotr-url-inp{flex:1;background:#0d0d0d;border:1px solid #1e1e1e;border-radius:4px;padding:7px 10px;font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;color:#aaa;outline:none;transition:border-color 0.15s;}
  .plotr-url-inp:focus{border-color:var(--accent);}
  .plotr-url-inp::placeholder{color:#333;}
  .plotr-open-btn{background:none;border:1px solid #272727;color:#666;padding:6px 10px;border-radius:4px;font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;cursor:pointer;transition:all 0.15s;white-space:nowrap;}
  .plotr-open-btn:hover{color:var(--accent);border-color:var(--accent);}
  .plotr-preview{border:none;width:100%;border-radius:0 0 8px 8px;background:#0a0a0a;display:block;}
  .plotr-empty{padding:12px 16px 14px;color:#555;font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:12px;line-height:1.6;}

  /* Status pills */
  .st-sel{display:flex;gap:6px;flex-wrap:wrap;}
  .st-opt{padding:5px 12px;border-radius:16px;border:1px solid #222;font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;cursor:pointer;transition:all 0.15s;color:#555;background:transparent;}
  .st-opt:hover{border-color:#444;color:#aaa;}
  .st-opt.on{color:#0d0d0d;border-color:transparent;font-weight:700;}

  /* Overview */
  .ov-grid{padding:26px 36px;display:flex;flex-direction:column;gap:6px;}
  .ac{background:#131313;border:1px solid #1e1e1e;border-radius:8px;padding:0;cursor:pointer;transition:all 0.15s;position:relative;overflow:hidden;}
  .ac:hover{border-color:#3a2800;background:#161000;}
  .ac-name{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-weight:700;font-size:20px;letter-spacing:0;color:#f0f0f0;}
  .ac-genre{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;color:#555;margin-bottom:13px;}
  .ac-stats{display:flex;gap:20px;margin-top:12px;margin-bottom:13px;}
  .ac-n{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-weight:700;font-size:20px;color:var(--accent);}
  .ac-l{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;color:#444;letter-spacing:1px;}
  .ac-bar{background:#1a1a1a;border-radius:8px;height:3px;overflow:hidden;}
  .ac-fill{height:100%;border-radius:8px;background:linear-gradient(90deg,#f0a500,#ffcc55);}

  /* Venues */
  .vcm-grid{padding:26px 36px;display:grid;grid-template-columns:repeat(auto-fill,minmax(300px,1fr));gap:14px;}
  .vcm-card{background:#131313;border:1px solid #1e1e1e;border-radius:8px;padding:16px;transition:all 0.15s;}
  .vcm-card:hover{border-color:#272727;background:#161616;}
  .vcm-name{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-weight:700;font-size:20px;letter-spacing:0;color:var(--accent);}
  .vcm-city{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;color:#555;margin-bottom:12px;}
  .vcm-rows{display:flex;flex-direction:column;gap:5px;}
  .vcm-r{display:flex;gap:8px;}
  .vcm-k{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;color:#444;width:76px;flex-shrink:0;padding-top:1px;}
  .vcm-v{font-size:12px;color:#bbb;line-height:1.4;}
  .vcm-acts{display:flex;gap:8px;margin-top:12px;padding-top:12px;border-top:1px solid #1a1a1a;}
  .vcm-card-editable{cursor:pointer;position:relative;}
  .vcm-card-editable:hover{border-color:#2a2a2a;background:#161616;}
  .card-edit-hint{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;color:#444;letter-spacing:1px;transition:color 0.15s;user-select:none;}
  .vcm-card-editable:hover .card-edit-hint{color:var(--accent);}
  .av-card-hdr{display:flex;justify-content:space-between;align-items:center;padding-bottom:8px;margin-bottom:10px;border-bottom:1px solid #1e1e1e;}
  .av-cat{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;letter-spacing:1.5px;color:#666;text-transform:uppercase;font-weight:600;}
  .av-co{font-size:14px;font-weight:600;color:#e0e0e0;margin-bottom:2px;line-height:1.3;}
  .av-sub{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;color:#555;margin-bottom:6px;}
  .av-sec-lbl{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;letter-spacing:2.5px;color:#555;text-transform:uppercase;font-weight:500;margin-bottom:12px;padding-bottom:8px;border-bottom:1px solid #1a1a1a;}
  .av-tour-row{background:#131313;border:1px solid #1e1e1e;border-radius:8px;padding:14px 16px;cursor:pointer;display:flex;align-items:flex-start;justify-content:space-between;gap:12px;transition:all 0.15s;margin-bottom:6px;}
  .av-tour-row:hover{border-color:#3a2800;background:#161000;}
  .av-tour-name{font-size:14px;font-weight:600;color:#e0e0e0;margin-bottom:4px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;text-align:left;}
  .av-tour-meta{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;color:#555;display:flex;gap:10px;flex-wrap:wrap;}

  /* CRM search bar */
  .crm-bar{padding:16px 36px 0;display:flex;gap:10px;align-items:center;}
  .crm-inp{flex:1;background:#131313;border:1px solid #222;border-radius:8px;padding:9px 14px;font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:12px;color:#ccc;outline:none;transition:border-color 0.15s;}
  .crm-inp:focus{border-color:var(--accent);}
  .crm-inp::placeholder{color:#3a3a3a;}
  .crm-sel{background:#131313;border:1px solid #222;border-radius:8px;padding:9px 12px;font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;color:#888;outline:none;cursor:pointer;}
  .crm-count{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;color:#444;white-space:nowrap;}

  /* CRM list rows */
  .crm-list{padding:14px 36px 36px;text-align:left;}
  .crm-hdr{display:grid;gap:10px;padding:0 14px 8px;border-bottom:1px solid #1a1a1a;margin-bottom:4px;font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;letter-spacing:2px;color:#444;text-transform:uppercase;text-align:left;}
  .crm-row{display:grid;gap:10px;padding:11px 14px;border-radius:8px;border:1px solid transparent;cursor:pointer;transition:all 0.13s;align-items:center;margin-bottom:2px;text-align:left;}
  .crm-row:hover{background:#141414;border-color:#222;}
  .crm-row-name{font-size:14px;font-weight:500;color:#e0e0e0;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;text-align:left;}
  .crm-row-sub{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;color:#555;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;text-align:left;}
  .crm-row-tag{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;color:#666;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;text-align:left;}
  .crm-row-email{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;color:#555;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;text-align:left;}
  .crm-row-acts{display:flex;gap:6px;justify-content:flex-end;}

  /* Empty */
  .empty{padding:56px 36px;text-align:center;color:#333;font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:12px;}

  /* Artist card edit button */
  .ac-edit-btn{position:absolute;bottom:8px;right:10px;background:none;border:none;color:#333;cursor:pointer;font-size:14px;padding:4px 6px;border-radius:4px;transition:all 0.15s;opacity:0;}
  .ac:hover .ac-edit-btn{opacity:1;}
  .ac-edit-btn:hover{color:var(--accent);background:#1a1500;}

  /* Edit artist modal extras */
  .mo-sec-hdr{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;letter-spacing:2px;color:#444;text-transform:uppercase;padding:14px 0 8px;border-top:1px solid #1e1e1e;margin-top:4px;}
  .mem-item{background:#0d0d0d;border:1px solid #1e1e1e;border-radius:4px;padding:10px 12px;margin-bottom:8px;}
  .mem-rm{background:none;border:none;color:#444;cursor:pointer;font-size:14px;padding:4px 6px;border-radius:4px;transition:all 0.15s;line-height:1;align-self:flex-end;margin-bottom:1px;}
  .mem-rm:hover{color:#c84b4b;}
  /* Show modal venue dropdown */
  .mo-drop{position:absolute;top:100%;left:0;right:0;background:#1a1a1a;border:1px solid #333;border-radius:8px;z-index:100;max-height:260px;overflow-y:auto;margin-top:2px;}
  .mo-drop-lbl{padding:5px 12px 3px;font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;color:#444;letter-spacing:2px;text-transform:uppercase;}
  .mo-drop-lbl.dir{color:var(--success);}
  .mo-drop-item{padding:8px 12px;cursor:pointer;font-size:14px;color:#ccc;border-bottom:1px solid #1e1e1e;}
  .mo-drop-item:hover{background:#2a2a2a;}
  .mo-drop-item.add{color:var(--accent);}
  .mo-lodging-div{border-top:1px solid #1e1e1e;margin:10px 0 2px;padding:10px 0 0;}
  .mo-lodging-lbl{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;color:#444;letter-spacing:2px;text-transform:uppercase;margin-bottom:10px;}

  /* Copy button */
  .copy-btn{background:none;border:none;color:#3a3a3a;cursor:pointer;font-size:11px;padding:1px 5px;border-radius:4px;transition:color 0.15s;font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;flex-shrink:0;}
  .copy-btn:hover{color:var(--accent);}
  .copy-btn.ok{color:var(--success);}

  /* Sidebar search */
  .sb-search{width:100%;background:#0d0d0d;border:1px solid #1e1e1e;border-radius:4px;padding:6px 10px;font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;color:#ccc;outline:none;transition:border-color 0.15s;box-sizing:border-box;}
  .sb-search:focus{border-color:var(--accent);}
  .sb-search::placeholder{color:#2a2a2a;}
  .s-hit{padding:6px 20px;cursor:pointer;display:flex;align-items:center;gap:8px;transition:background 0.12s;}
  .s-hit:hover{background:#161616;}
  .s-hit-type{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;letter-spacing:0.8px;color:#444;text-transform:uppercase;width:46px;flex-shrink:0;}
  .s-hit-name{font-size:12px;color:#ccc;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
  .s-hit-sub{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;color:#555;margin-top:1px;}

  /* Flag button */
  .flag-btn{background:none;border:none;color:#2a2a2a;cursor:pointer;font-size:14px;padding:2px 5px;border-radius:4px;transition:color 0.15s;line-height:1;flex-shrink:0;}
  .flag-btn:hover{color:var(--accent);}
  .flag-btn.on{color:var(--accent);}
  .s-row.flagged{background:#17120a;}
  .s-row.next-show{border-left:3px solid var(--accent);}

  /* Filter pills */
  .fp{padding:4px 11px;border-radius:16px;border:1px solid #1e1e1e;font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;cursor:pointer;transition:all 0.15s;color:#555;background:transparent;}
  .fp:hover{border-color:#333;color:#aaa;}
  .fp.on{background:var(--accent);color:#0d0d0d;border-color:var(--accent);font-weight:700;}

  /* Show notes */
  .show-notes-ta{width:100%;background:#0d0d0d;border:1px solid #1a1a1a;border-radius:4px;padding:8px 10px;font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:12px;color:#bbb;outline:none;resize:none;transition:border-color 0.15s;line-height:1.6;box-sizing:border-box;}
  .show-notes-ta:focus{border-color:var(--accent);}
  .show-notes-ta::placeholder{color:#2a2a2a;}

  /* Profile page */
  .prf-inp{width:100%;background:#1a1a1a;border:1px solid #1e1e1e;border-radius:4px;padding:7px 10px;font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:14px;color:#ddd;outline:none;transition:border-color 0.15s;}
  .prf-inp:focus{border-color:var(--accent);}
  .prf-inp::placeholder{color:#2a2a2a;}
  .prf-lbl{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;letter-spacing:1.5px;color:#444;text-transform:uppercase;margin-bottom:5px;}
  .prf-tour-row{background:#131313;border:1px solid #1e1e1e;border-radius:8px;padding:14px 18px;cursor:pointer;display:flex;align-items:center;justify-content:space-between;transition:all 0.15s;margin-bottom:8px;}
  .prf-tour-row:hover{border-color:#2a2a2a;background:#161616;}

  /* Modals */
  .ov{position:fixed;inset:0;background:rgba(0,0,0,0.8);z-index:200;display:flex;align-items:center;justify-content:center;backdrop-filter:blur(3px);}
  .mo{background:#141414;border:1px solid #272727;border-radius:8px;padding:28px;width:500px;max-width:94vw;max-height:90vh;overflow-y:auto;box-shadow:0 24px 60px rgba(0,0,0,0.7);}
  .mo-title{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-weight:700;font-size:20px;letter-spacing:0;color:#f0f0f0;margin-bottom:3px;}
  .mo-sub{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;color:#555;margin-bottom:20px;letter-spacing:1px;}
  .mo-fld{margin-bottom:13px;}
  .mo-lbl{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;color:#555;letter-spacing:1.5px;text-transform:uppercase;margin-bottom:6px;}
  .mo-section-lbl{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;letter-spacing:2px;color:#444;text-transform:uppercase;border-bottom:1px solid #1e1e1e;padding-bottom:6px;margin-bottom:12px;}
  .mo-inp{width:100%;background:#0d0d0d;border:1px solid #272727;border-radius:4px;padding:9px 12px;font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:14px;color:#ddd;outline:none;transition:border-color 0.15s;}
  .mo-inp:focus{border-color:var(--accent);}
  .mo-inp::placeholder{color:#333;}
  .mo-sel{width:100%;background:#0d0d0d;border:1px solid #272727;border-radius:4px;padding:9px 12px;font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:14px;color:#ddd;outline:none;cursor:pointer;appearance:none;transition:border-color 0.15s;}
  .mo-sel:focus{border-color:var(--accent);}
  .mo-row{display:grid;grid-template-columns:1fr 1fr;gap:12px;}
  .mo-acts{display:flex;gap:10px;justify-content:flex-end;margin-top:22px;padding-top:14px;border-top:1px solid #1e1e1e;}
  .btn-p{background:var(--accent);color:#0d0d0d;border:none;padding:9px 20px;border-radius:4px;font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;letter-spacing:1px;font-weight:700;cursor:pointer;transition:background 0.15s;}
  .btn-p:hover{background:#ffcc55;}
  .btn-p:disabled{background:#2a2a2a;color:#555;cursor:not-allowed;}
  .btn-g{background:none;border:1px solid #272727;color:#666;padding:9px 16px;border-radius:4px;font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;cursor:pointer;transition:all 0.15s;}
  .btn-g:hover{border-color:#444;color:#aaa;}
  .btn-d{background:none;border:1px solid #3a1515;color:#c84b4b;padding:9px 16px;border-radius:4px;font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;cursor:pointer;transition:all 0.15s;}
  .btn-d:hover{background:#1a0808;border-color:#c84b4b;}
  .add-p{background:var(--accent);border:none;color:#0d0d0d;padding:7px 14px;border-radius:4px;font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;letter-spacing:1px;font-weight:700;cursor:pointer;transition:background 0.15s;}
  .add-p:hover{background:#ffcc55;}
  .add-g{background:none;border:1px solid #272727;color:#666;padding:7px 14px;border-radius:4px;font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;letter-spacing:1px;cursor:pointer;transition:all 0.15s;}
  .add-g:hover{border-color:var(--accent);color:var(--accent);}

  /* ── Light Mode ──────────────────────────────────────────────────────────── */
  .app.light{background:#f0ede8;color:#1a1917;}
  body:has(.app.light){background:#f0ede8;}
  .app.light .sb{background:#e8e4de;border-right-color:#d0ccc6;}
  .app.light .sb-collapse-btn{background:#dedad4;border-color:#ccc8c2;color:#888;}
  .app.light .sb-collapse-btn:hover{color:#1a1917;}
  .app.light .sb-expand-btn{color:#9a9794;}
  .app.light .sb-logo{border-bottom-color:#d0ccc6;}

  .app.light .tagline{color:#9a9794;}
  .app.light .saved{color:#1a9e4a;}
  .app.light .sb-nav svg{opacity:0.45;}
  .app.light .sb-nav:hover svg{opacity:0.75;}
  .app.light .sb-nav.on svg{opacity:1;}
  .app.light .sb-nav{color:#6a6764;}
  .app.light .sb-nav:hover{color:#1a1917;background:#dedad4;}
  .app.light .sb-nav.on{color:#c47f00;border-left-color:#c47f00;background:#f5e8cc;}
  /* Mini divider within nav */
  .sb-mini-hr{border:none;border-top:1px solid #232323;margin:6px 16px;}
  .app.light .sb-mini-hr{border-top-color:#d0ccc6;}
  /* Directory filter tab pill */
  .sb-dir-tab-wrap{display:flex;border-radius:4px;overflow:hidden;border:1px solid #272727;flex-shrink:0;}
  .app.light .sb-dir-tab-wrap{border-color:#ccc8c2;}
  .sb-dir-tab{background:none;border:none;font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;letter-spacing:0.5px;cursor:pointer;padding:5px 12px;color:#666;transition:all 0.15s;}
  .sb-dir-tab.on{color:var(--accent);background:#1a1500;}
  .app.light .sb-dir-tab{color:#8a8784;}
  .app.light .sb-dir-tab.on{color:#c47f00;background:#f5e8cc;}
  .app.light .sb-search{background:#dedad4;border-color:#ccc8c2;color:#1a1917;}
  .app.light .sb-search::placeholder{color:#aaa9a6;}
  .app.light .sb-search:focus{border-color:#c47f00;}
  .app.light .s-hit:hover{background:#dedad4;}
  .app.light .s-hit-type{color:#9a9794;}
  .app.light .s-hit-name{color:#1a1917;}
  .app.light .s-hit-sub{color:#8a8784;}
  .app.light .main{background:#f0ede8;}
  .app.light .ph{border-bottom-color:#d0ccc6;}
  .app.light .ph-title{color:#1a1917;}
  .app.light .ph-sub{color:#8a8784;}
  .app.light .stat-n{color:#c47f00;}
  .app.light .stat-l{color:#8a8784;}
  .app.light .tour-inp{color:#1a1917;}
  .app.light .tour-inp:hover{border-bottom-color:#ccc8c2;}
  .app.light .tour-inp:focus{border-bottom-color:#c47f00;}
  .app.light .sg-hdr{color:#8a8784;border-bottom-color:#d0ccc6;}
  .app.light .s-row:hover{background:#e8e4de;border-color:#d0ccc6;}
  .app.light .s-row.sel{background:#e4e0da;border-color:#ccc8c2;}
  .app.light .s-row.flagged{background:#f5e8cc;}
  .app.light .s-row.next-show{border-left:3px solid #c47f00;}
  .app.light .s-date{color:#6a6764;}
  .app.light .s-vname{color:#1a1917;}
  .app.light .s-city{color:#8a8784;}
  .app.light .pb-wrap{background:#d8d4ce;}
  .app.light .pb-lbl{color:#8a8784;}
  .app.light .abtn{background:#e4e0da;border-color:#ccc8c2;color:#4a4744;}
  .app.light .abtn:hover{background:#dedad4;color:#c47f00;border-color:#c47f00;}
  .app.light .ibtn{color:#aaa9a6;}
  .app.light .ibtn:hover{color:#c84b4b;background:#fce8e8;}
  .app.light .ibtn.ed:hover{color:#c47f00;background:#f5e8cc;}
  .app.light .dp{border-top-color:#d0ccc6;background:#ede9e3;}
  .app.light .dp-title{color:#1a1917;}
  .app.light .dp-meta{color:#8a8784;}
  .app.light .dp-tabs{border-bottom-color:#d0ccc6;}
  .app.light .dp-tab{color:#aaa8a4;}
  .app.light .dp-tab:hover{color:#5a5754;}
  .app.light .dp-tab.on{color:#c47f00;border-bottom-color:#c47f00;}
  .app.light .adv-link-bar{background:#e8e4de;border-color:#d0ccc6;}
  .app.light .adv-link-url{color:#9a9794;}
  .app.light .adv-hint{color:#9a9794;}
  .app.light .xbtn{color:#8a8784;}
  .app.light .xbtn:hover{color:#1a1917;}
  .app.light .vc{background:#e8e4de;border-color:#d0ccc6;}
  .app.light .vc-lbl{color:#8a8784;}
  .app.light .vc-name{color:#c47f00;}
  .app.light .vf-k{color:#8a8784;}
  .app.light .vf-v{color:#3a3734;}
  .app.light .ec{background:#e8e4de;border-color:#d0ccc6;}
  .app.light .ec-hdr{border-bottom-color:#d0ccc6;}
  .app.light .ec-ttl{color:#8a8784;}
  .app.light .draft-btn{background:#c47f00;color:#fff;}
  .app.light .draft-btn:hover{background:#d4880a;}
  .app.light .draft-btn:disabled{background:#d8d4ce;color:#aaa9a6;}
  .app.light .e-to{border-bottom-color:#d0ccc6;color:#6a6764;}
  .app.light .e-to span{color:#3a3734;}
  .app.light .e-body{color:#3a3734;}
  .app.light .e-ph{color:#aaa9a6;}
  .app.light .sm-btn{border-color:#d0ccc6;color:#8a8784;}
  .app.light .sm-btn:hover{color:#1a1917;border-color:#aaa9a6;}
  .app.light .pdf-btn{border-color:#d0ccc6;color:#8a8784;}
  .app.light .pdf-btn:hover{color:#c47f00;border-color:#c47f00;}
  .app.light .plotr-url-inp{background:#e4e0da;border-color:#d0ccc6;color:#3a3734;}
  .app.light .plotr-url-inp:focus{border-color:#c47f00;}
  .app.light .plotr-url-inp::placeholder{color:#aaa9a6;}
  .app.light .plotr-open-btn{border-color:#d0ccc6;color:#6a6764;}
  .app.light .plotr-open-btn:hover{color:#c47f00;border-color:#c47f00;}
  .app.light .plotr-empty{color:#aaa9a6;}
  .app.light .st-opt{border-color:#d0ccc6;color:#8a8784;}
  .app.light .st-opt:hover{border-color:#aaa9a6;color:#1a1917;}
  .app.light .ac{background:#e8e4de;border-color:#d0ccc6;}
  .app.light .ac:hover{border-color:#c47f00;background:#f5ead8;}
  .app.light .ac-name{color:#1a1917;}
  .app.light .ac-genre{color:#8a8784;}
  .app.light .ac-n{color:#c47f00;}
  .app.light .ac-l{color:#8a8784;}
  .app.light .ac-bar{background:#d0ccc6;}
  .app.light .ac-edit-btn{color:#aaa9a6;}
  .app.light .ac-edit-btn:hover{color:#c47f00;background:#f5e8cc;}
  .app.light .vcm-card{background:#e8e4de;border-color:#d0ccc6;}
  .app.light .vcm-card:hover{border-color:#ccc8c2;background:#e2dfd9;}
  .app.light .vcm-name{color:#c47f00;}
  .app.light .vcm-city{color:#8a8784;}
  .app.light .vcm-k{color:#8a8784;}
  .app.light .vcm-v{color:#3a3734;}
  .app.light .vcm-acts{border-top-color:#d0ccc6;}
  .app.light .card-edit-hint{color:#aaa;}
  .app.light .av-card-hdr{border-bottom-color:#d0ccc6;}
  .app.light .av-cat{color:#777;}
  .app.light .av-co{color:#1a1917;}
  .app.light .av-sub{color:#6a6764;}
  .app.light .av-sec-lbl{color:#6a6764;border-bottom-color:#d0ccc6;}
  .app.light .av-tour-row{background:#e8e4de;border-color:#d0ccc6;}
  .app.light .av-tour-row:hover{border-color:#c47f00;background:#ede9e1;}
  .app.light .av-tour-name{color:#1a1917;}
  .app.light .av-tour-meta{color:#6a6764;}
  .app.light .crm-bar{background:#f0ede8;}
  .app.light .crm-inp{background:#e8e4de;border-color:#d0ccc6;color:#1a1917;}
  .app.light .crm-inp:focus{border-color:#c47f00;}
  .app.light .crm-inp::placeholder{color:#aaa9a6;}
  .app.light .crm-sel{background:#e8e4de;border-color:#d0ccc6;color:#6a6764;}
  .app.light .crm-count{color:#8a8784;}
  .app.light .crm-hdr{border-bottom-color:#d0ccc6;color:#8a8784;}
  .app.light .crm-row:hover{background:#e4e0da;border-color:#d0ccc6;}
  .app.light .crm-row-name{color:#1a1917;}
  .app.light .crm-row-sub{color:#8a8784;}
  .app.light .crm-row-tag{color:#6a6764;}
  .app.light .crm-row-email{color:#8a8784;}
  .app.light .fp{border-color:#d0ccc6;color:#8a8784;}
  .app.light .fp:hover{border-color:#aaa9a6;color:#1a1917;}
  .app.light .fp.on{background:#c47f00;color:#fff;border-color:#c47f00;}
  .app.light .flag-btn{color:#d0ccc6;}
  .app.light .flag-btn:hover{color:#c47f00;}
  .app.light .flag-btn.on{color:#c47f00;}
  .app.light .show-notes-ta{background:#e8e4de;border-color:#d0ccc6;color:#3a3734;}
  .app.light .show-notes-ta:focus{border-color:#c47f00;}
  .app.light .show-notes-ta::placeholder{color:#aaa9a6;}
  .app.light .f-inp{background:#e4e0da;border-color:#d0ccc6;color:#1a1917;}
  .app.light .f-inp:focus{border-color:#c47f00;}
  .app.light .f-inp::placeholder{color:#aaa9a6;}
  .app.light .prf-inp{background:#e4e0da;border-color:#d0ccc6;color:#1a1917;}
  .app.light .prf-inp:focus{border-color:#c47f00;}
  .app.light .prf-inp::placeholder{color:#aaa9a6;}
  .app.light .prf-lbl{color:#8a8784;}
  .app.light .prf-tour-row{background:#e8e4de;border-color:#d0ccc6;}
  .app.light .prf-tour-row:hover{border-color:#ccc8c2;background:#e2dfd9;}
  .app.light .ov{background:rgba(0,0,0,0.5);}
  .app.light .mo{background:#ede9e3;border-color:#d0ccc6;box-shadow:0 24px 60px rgba(0,0,0,0.2);}
  .app.light .mo-title{color:#1a1917;}
  .app.light .mo-sub{color:#8a8784;}
  .app.light .mo-lbl{color:#8a8784;}
  .app.light .mo-section-lbl{color:#9a9794;border-bottom-color:#d0ccc6;}
  .app.light .mo-inp{background:#e4e0da;border-color:#d0ccc6;color:#1a1917;}
  .app.light .mo-inp:focus{border-color:#c47f00;}
  .app.light .mo-inp::placeholder{color:#aaa9a6;}
  .app.light .mo-sel{background:#e4e0da;border-color:#d0ccc6;color:#1a1917;}
  .app.light .mo-sel:focus{border-color:#c47f00;}
  .app.light .mo-acts{border-top-color:#d0ccc6;}
  .app.light .btn-p{background:#c47f00;color:#fff;}
  .app.light .btn-p:hover{background:#d4880a;}
  .app.light .btn-p:disabled{background:#d8d4ce;color:#aaa9a6;}
  .app.light .btn-g{border-color:#d0ccc6;color:#6a6764;}
  .app.light .btn-g:hover{border-color:#aaa9a6;color:#1a1917;}
  .app.light .btn-d{border-color:#f5c5c5;color:#c84b4b;}
  .app.light .btn-d:hover{background:#fce8e8;border-color:#c84b4b;}
  .app.light .add-p{background:#c47f00;color:#fff;}
  .app.light .add-p:hover{background:#d4880a;}
  .app.light .add-g{border-color:#d0ccc6;color:#6a6764;}
  .app.light .add-g:hover{border-color:#c47f00;color:#c47f00;}
  .app.light .mo-sec-hdr{color:#8a8784;border-top-color:#d0ccc6;}
  .app.light .mem-item{background:#e4e0da;border-color:#d0ccc6;}
  .app.light .copy-btn{color:#aaa9a6;}
  .app.light .copy-btn:hover{color:#c47f00;}
  .app.light .copy-btn.ok{color:#1a9e4a;}
  .app.light .empty{color:#aaa9a6;}
  .app.light ::-webkit-scrollbar-track{background:#e8e4de;}
  .app.light ::-webkit-scrollbar-thumb{background:#ccc8c2;}
  /* Show modal dropdown — light */
  .app.light .mo-drop{background:#e8e4de;border-color:#d0ccc6;}
  .app.light .mo-drop-lbl{color:#9a9794;}
  .app.light .mo-drop-item{color:#1a1917;border-bottom-color:#d0ccc6;}
  .app.light .mo-drop-item:hover{background:#dedad4;}
  .app.light .mo-lodging-div{border-top-color:#d0ccc6;}
  .app.light .mo-lodging-lbl{color:#9a9794;}
  /* Calendar — light */
  .app.light .cal-month{color:#1a1917;}
  .app.light .cal-nav-btn{border-color:#d0ccc6;color:#6a6764;}
  .app.light .cal-nav-btn:hover{border-color:#c47f00;color:#c47f00;}
  .app.light .cal-today-btn{border-color:#d0ccc6;color:#6a6764;}
  .app.light .cal-today-btn:hover{border-color:#c47f00;color:#c47f00;}
  .app.light .cal-filter{border-color:#d0ccc6;color:#8a8784;}
  .app.light .cal-filter:hover{border-color:#aaa9a6;color:#3a3734;}
  .app.light .cal-grid{border-color:#d0ccc6;}
  .app.light .cal-dow{border-color:#d0ccc6;background:#e4e0da;color:#8a8784;}
  .app.light .cal-cell{border-color:#d0ccc6;background:#ede9e3;}
  .app.light .cal-cell.other-month{background:#e8e4de;}
  .app.light .cal-cell.today{background:#fdf3dc;}
  .app.light .cal-cell:hover{background:#e4e0da;}
  .app.light .cal-day-num{color:#8a8784;}
  .app.light .cal-cell.today .cal-day-num{color:#c47f00;}
  .app.light .cal-more{color:#8a8784;}
  .app.light .cal-more:hover{color:#3a3734;}
  .app.light .cal-legend-item{color:#8a8784;}
  .app.light .cal-popover{background:#e8e4de;border-color:#d0ccc6;box-shadow:0 12px 40px rgba(0,0,0,0.15);}
  .app.light .cal-pop-title{color:#1a1917;}
  .app.light .cal-pop-date{color:#8a8784;}
  .app.light .cal-pop-row{color:#3a3734;}
  .app.light .cal-pop-key{color:#8a8784;}

  /* ── ContactField ── */
  .cf-lbl{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;letter-spacing:1.5px;color:#444;text-transform:uppercase;margin-bottom:3px;}
  .app.light .cf-lbl{color:#8a8784;}
  .cf-val{font-size:14px;color:#ccc;flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
  .app.light .cf-val{color:#1a1917;}

  /* ── EditArtistModal checklist ── */
  .mo-checklist-label{display:flex;align-items:center;gap:9px;cursor:pointer;padding:7px 10px;border-radius:8px;background:#0d0d0d;border:1px solid #1e1e1e;transition:all 0.12s;}
  .mo-checklist-label.checked{background:#1a1a00;border-color:#3a3000;}
  .app.light .mo-checklist-label{background:#e4e0da;border-color:#d0ccc6;}
  .app.light .mo-checklist-label.checked{background:#fdf3dc;border-color:#e8cc80;}
  .mo-checklist-span{font-size:12px;color:#555;}
  .mo-checklist-label.checked .mo-checklist-span{color:#ccc;}
  .app.light .mo-checklist-span{color:#8a8784;}
  .app.light .mo-checklist-label.checked .mo-checklist-span{color:#1a1917;}
  .mo-hint{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;color:#444;margin-bottom:12px;}
  .app.light .mo-hint{color:#8a8784;}

  /* ── BulkImportModal ── */
  .bulk-row{background:#1a1a1a;border:1px solid #272727;border-radius:8px;padding:10px 14px;transition:all 0.15s;}
  .bulk-row.skip{background:#111;border-color:#1a1a1a;opacity:0.4;}
  .app.light .bulk-row{background:#e4e0da;border-color:#d0ccc6;}
  .app.light .bulk-row.skip{background:#dedad4;border-color:#ccc8c2;}
  .bulk-date{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;color:var(--accent);}
  .app.light .bulk-date{color:#c47f00;}
  .bulk-venue{font-size:14px;font-weight:500;color:#ddd;}
  .app.light .bulk-venue{color:#1a1917;}
  .bulk-city{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;color:#555;}
  .app.light .bulk-city{color:#8a8784;}
  .bulk-detail{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;color:#888;}
  .app.light .bulk-detail{color:#8a8784;}
  .bulk-match-lbl{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;color:#444;letter-spacing:1px;}
  .app.light .bulk-match-lbl{color:#8a8784;}
  .bulk-skip-btn{background:none;border:1px solid #333;border-radius:4px;color:#555;font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;padding:3px 8px;cursor:pointer;}
  .app.light .bulk-skip-btn{border-color:#d0ccc6;color:#8a8784;}
  .bulk-status-lbl{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;color:#555;margin-top:10px;}
  .app.light .bulk-status-lbl{color:#8a8784;}
  .bulk-badge-ai{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;background:#1a1500;border:1px solid #3a3000;color:var(--accent);border-radius:4px;padding:2px 7px;letter-spacing:1px;}
  .app.light .bulk-badge-ai{background:#fdf3dc;border-color:#e8cc80;color:#c47f00;}
  .bulk-badge-basic{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;background:#141414;border:1px solid #2a2a2a;color:#555;border-radius:4px;padding:2px 7px;letter-spacing:1px;}
  .app.light .bulk-badge-basic{background:#e4e0da;border-color:#ccc8c2;color:#8a8784;}
  .bulk-row-count{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;color:#555;letter-spacing:1px;}
  .app.light .bulk-row-count{color:#8a8784;}

  /* ── Delete confirm box ── */
  .mo-del-confirm{background:#1a0a0a;border:1px solid #5a1a1a;border-radius:8px;padding:14px 16px;}
  .app.light .mo-del-confirm{background:#fce8e8;border-color:#f5c5c5;}

  /* ── DocUploadRow ── */
  .mo-upload-file{display:flex;align-items:center;gap:10px;background:#0d0d0d;border:1px solid #272727;border-radius:8px;padding:10px 14px;}
  .app.light .mo-upload-file{background:#e4e0da;border-color:#d0ccc6;}
  .mo-upload-label{display:inline-flex;align-items:center;gap:8px;background:#0d0d0d;border:1px dashed #333;border-radius:8px;padding:10px 16px;cursor:pointer;font-size:12px;color:#666;}
  .app.light .mo-upload-label{background:#e4e0da;border-color:#bbb9b5;color:#8a8784;}

  /* ── DataImportModal ── */
  .mo-import-drop{border:2px dashed #2a2a2a;border-radius:8px;padding:52px 32px;text-align:center;cursor:pointer;background:#0d0d0d;transition:all 0.15s;margin:8px 0 18px;}
  .mo-import-drop.drag-over{border-color:var(--accent);background:#1a1200;}
  .app.light .mo-import-drop{background:#e4e0da;border-color:#bbb9b5;}
  .app.light .mo-import-drop.drag-over{border-color:#c47f00;background:#fdf3dc;}
  .mo-import-drop-icon{font-size:40px;margin-bottom:12px;color:#555;}
  .mo-import-drop.drag-over .mo-import-drop-icon{color:var(--accent);}
  .app.light .mo-import-drop-icon{color:#aaa9a6;}
  .app.light .mo-import-drop.drag-over .mo-import-drop-icon{color:#c47f00;}
  .mo-import-drop-title{font-size:16px;color:#ccc;font-weight:500;margin-bottom:8px;}
  .app.light .mo-import-drop-title{color:#1a1917;}
  .mo-import-drop-sub{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;color:#444;letter-spacing:1px;}
  .app.light .mo-import-drop-sub{color:#8a8784;}
  .mo-import-divider{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;color:#444;text-align:center;margin-bottom:12px;letter-spacing:1px;}
  .app.light .mo-import-divider{color:#aaa9a6;}
  .mo-import-hint{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;color:#3a3a3a;line-height:1.8;margin-top:14px;}
  .app.light .mo-import-hint{color:#8a8784;}
  .mo-map-hdr{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;color:#444;letter-spacing:1.5px;text-transform:uppercase;padding:0 6px;margin-bottom:6px;display:grid;grid-template-columns:1fr 130px 1fr;gap:8px;}
  .app.light .mo-map-hdr span{color:#8a8784;}
  .mo-map-row{display:grid;grid-template-columns:1fr 130px 1fr;gap:8px;align-items:center;padding:7px 8px;background:#0d0d0d;border-radius:8px;}
  .app.light .mo-map-row{background:#e4e0da;}
  .mo-map-col{font-size:12px;color:#ccc;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
  .app.light .mo-map-col{color:#1a1917;}
  .mo-map-sample{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;color:#555;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
  .app.light .mo-map-sample{color:#8a8784;}
  .mo-import-type-lbl{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;color:#555;}
  .app.light .mo-import-type-lbl{color:#8a8784;}
  .mo-preview-row{display:flex;align-items:center;gap:12px;padding:10px 0;border-bottom:1px solid #1a1a1a;}
  .app.light .mo-preview-row{border-bottom-color:#d0ccc6;}
  .mo-preview-name{font-size:14px;font-weight:500;color:#e0e0e0;}
  .app.light .mo-preview-name{color:#1a1917;}
  .mo-preview-sub{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;color:#555;margin-top:2px;}
  .app.light .mo-preview-sub{color:#8a8784;}
  .mo-preview-more{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;color:#444;padding:10px 0;}
  .app.light .mo-preview-more{color:#8a8784;}
`;

// ── Venue form fields config ───────────────────────────────────────────────
const VF = [
  { k: "name",            l: "Venue Name *",       r: true  },
  { k: "addressStreet",   l: "Street Address",      r: false },
  { k: "city",            l: "City",                r: false },
  { k: "addressState",    l: "State / Province",    r: false },
  { k: "addressZip",      l: "Zip / Postal Code",   r: false },
  { k: "addressCountry",  l: "Country",             r: false },
  { k: "capacity",        l: "Capacity",            r: false },
  { k: "ages",            l: "Ages",                r: false },
  { k: "gaSeated",        l: "GA / Seated Setup",   r: false },
  { k: "website",         l: "Website",             r: false },
  { k: "promoterName",    l: "Promoter Name",       r: false },
  { k: "promoterEmail",   l: "Promoter Email",      r: false },
  { k: "promoterCompany", l: "Promoter Company",    r: false },
  { k: "promoterPhone",   l: "Promoter Phone",      r: false },
  { k: "loadIn",          l: "Load-in Info",        r: false },
  { k: "parking",         l: "Parking",             r: false },
  { k: "houseGear",       l: "House Gear",          r: false },
  { k: "greenRooms",      l: "Green Rooms",         r: false },
  { k: "bathrooms",       l: "Bathrooms",           r: false },
  { k: "merchSplit",      l: "Merch Split",         r: false },
  { k: "notes",           l: "Internal Notes",      r: false },
];
// helper: build a full display address from structured fields
const fmtVenueAddress = (v) => {
  const usVariants = ["usa","us","united states","u.s.","u.s.a."];
  const line2 = [v.city, v.addressState, v.addressZip].filter(Boolean).join(", ");
  const country = v.addressCountry && !usVariants.includes(v.addressCountry.toLowerCase()) ? v.addressCountry : "";
  return [v.addressStreet, line2, country].filter(Boolean).join(", ");
};

const socialHref = (platform, val) => {
  if (!val) return "";
  const v = val.trim();
  if (platform === "website")    return v.startsWith("http") ? v : `https://${v}`;
  if (platform === "instagram")  return `https://instagram.com/${v.replace(/^@/, "")}`;
  if (platform === "twitter")    return `https://x.com/${v.replace(/^@/, "")}`;
  if (platform === "tiktok")     return `https://tiktok.com/@${v.replace(/^@/, "")}`;
  if (platform === "spotify")    return v.startsWith("http") ? v : `https://open.spotify.com/artist/${v}`;
  if (platform === "appleMusic") return v.startsWith("http") ? v : `https://music.apple.com/${v}`;
  return v;
};

const SOCIAL_SVG_PATHS = {
  website:    <><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></>,
  instagram:  <><rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></>,
  twitter:    <><path d="M4 4l16 16M20 4L4 20" strokeWidth="2.5" strokeLinecap="round"/></>,
  tiktok:     <><path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5"/></>,
  spotify:    <><circle cx="12" cy="12" r="10"/><path d="M8 13.5c2.67-.9 5.33-.9 8 0"/><path d="M7.5 10.5c3.17-1.17 6.33-1.17 9.5 0"/><path d="M8.5 7.5c2.5-.83 5-.83 7.5 0"/></>,
  appleMusic: <><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></>,
};

// ── Modals ─────────────────────────────────────────────────────────────────
function ArtistModal({ onSave, onClose }) {
  const [name, setName] = useState("");
  return (
    <div className="ov" onClick={onClose}>
      <div className="mo" onClick={e => e.stopPropagation()}>
        <div className="mo-title">Add Artist</div>
        <div className="mo-sub">New roster entry</div>
        <div className="mo-fld"><div className="mo-lbl">Artist Name</div>
          <input className="mo-inp" placeholder="e.g. Fruit Bats" value={name} onChange={e => setName(e.target.value)} autoFocus /></div>
        <div className="mo-acts">
          <button className="btn-g" onClick={onClose}>Cancel</button>
          <button className="btn-p" disabled={!name.trim()} onClick={() => onSave({ name: name.trim() })}>Add Artist</button>
        </div>
      </div>
    </div>
  );
}

function DocUploadRow({ label, fileUrl, fileName, uploading, uploadErr, onUpload, onRemove, inputId, accept }) {
  const [dragging, setDragging] = useState(false);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    // Synthesise a change event shape that onUpload expects
    onUpload({ target: { files: [file] } });
  };

  return (
    <div style={{ marginBottom: 14 }}>
      <div className="mo-lbl" style={{ marginBottom: 8 }}>{label}</div>
      {fileUrl ? (
        <div className="mo-upload-file">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, color: "var(--text-mut)" }}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/></svg>
          <a href={fileUrl} target="_blank" rel="noreferrer"
            style={{ flex: 1, fontSize: 12, color: "var(--accent)", textDecoration: "none", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {fileName || "View Document"}
          </a>
          <button className="btn-g" style={{ padding: "4px 10px", fontSize: 11 }} onClick={onRemove}>Remove</button>
        </div>
      ) : (
        <div>
          <input type="file" id={inputId} style={{ display: "none" }} accept={accept} onChange={onUpload} />
          <label htmlFor={inputId}
            onDragOver={e => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={handleDrop}>
            <div className="mo-upload-label" style={dragging ? { borderColor: "var(--accent)", background: "rgba(240,165,0,0.07)" } : {}}>
              {uploading
                ? <><span className="spin" /> Uploading...</>
                : dragging
                  ? <>Drop to upload</>
                  : <>⬆ Upload {label} <span style={{ opacity: 0.6 }}>— or drag &amp; drop (PDF, Word, etc. — max 10 MB)</span></>}
            </div>
          </label>
          {uploadErr && <div style={{ marginTop: 6, fontSize: 11, color: "var(--danger)", fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif" }}>{uploadErr}</div>}
        </div>
      )}
    </div>
  );
}

// ── Edit Artist Section Modal ───────────────────────────────────────────────
// Focused per-card editor: mgmt | booking | label | members | online
function EditArtistSectionModal({ artist, section, onSave, onClose }) {
  const [mgmt,    setMgmt]    = useState(artist?.mgmt    || { company: "", name: "", email: "", phone: "" });
  const [booking, setBooking] = useState(artist?.booking || { agency: "", agent: "", email: "", phone: "" });
  const [label,   setLabel]   = useState(artist?.label   || { name: "", contact: "", email: "", phone: "" });
  const [members, setMembers] = useState(artist?.members || []);
  const [website, setWebsite] = useState(artist?.website || "");
  const [socials, setSocials] = useState(artist?.socials || { instagram: "", twitter: "", spotify: "", tiktok: "", appleMusic: "" });

  // Rider notes state (used only when section === "rider")
  const [rider, setRider] = useState(artist?.rider || "");

  const addMember    = () => setMembers(p => [...p, { id: uid(), name: "", role: "", email: "", phone: "" }]);
  const removeMember = (id) => setMembers(p => p.filter(m => m.id !== id));
  const updMember    = (id, field, val) => setMembers(p => p.map(m => m.id === id ? { ...m, [field]: val } : m));

  const handleSave = () => {
    if (section === "mgmt")    onSave({ mgmt });
    if (section === "booking") onSave({ booking });
    if (section === "label")   onSave({ label });
    if (section === "members") onSave({ members });
    if (section === "online")  onSave({ website, socials });
    if (section === "rider")   onSave({ rider });
  };

  const titles = { mgmt: "Management", booking: "Booking", label: "Label", members: "Band Members", online: "Online", rider: "Hospitality Rider" };

  return (
    <div className="ov" onClick={onClose}>
      <div className="mo" style={{ width: 560 }} onClick={e => e.stopPropagation()}>
        <div className="mo-title">Edit {titles[section]}</div>
        <div className="mo-sub">{artist?.name?.toUpperCase()}</div>

        {section === "mgmt" && (<>
          <div className="mo-row">
            <div className="mo-fld"><div className="mo-lbl">Management Company</div>
              <input className="mo-inp" value={mgmt.company || ""} onChange={e => setMgmt(p => ({ ...p, company: e.target.value }))} placeholder="e.g. Full Stop Management" autoFocus /></div>
            <div className="mo-fld"><div className="mo-lbl">Manager Name</div>
              <input className="mo-inp" value={mgmt.name} onChange={e => setMgmt(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Sarah Goldberg" /></div>
          </div>
          <div className="mo-row">
            <div className="mo-fld"><div className="mo-lbl">Email</div>
              <input className="mo-inp" value={mgmt.email} onChange={e => setMgmt(p => ({ ...p, email: e.target.value }))} placeholder="sarah@mgmt.com" /></div>
            <div className="mo-fld"><div className="mo-lbl">Phone</div>
              <PhoneInput className="mo-inp" value={mgmt.phone||""} onChange={v => setMgmt(p => ({ ...p, phone: v }))} /></div>
          </div>
        </>)}

        {section === "booking" && (<>
          <div className="mo-row">
            <div className="mo-fld"><div className="mo-lbl">Booking Agency</div>
              <input className="mo-inp" value={booking.agency} onChange={e => setBooking(p => ({ ...p, agency: e.target.value }))} placeholder="e.g. CAA" autoFocus /></div>
            <div className="mo-fld"><div className="mo-lbl">Agent Name</div>
              <input className="mo-inp" value={booking.agent} onChange={e => setBooking(p => ({ ...p, agent: e.target.value }))} placeholder="e.g. Tom Parker" /></div>
          </div>
          <div className="mo-row">
            <div className="mo-fld"><div className="mo-lbl">Email</div>
              <input className="mo-inp" value={booking.email} onChange={e => setBooking(p => ({ ...p, email: e.target.value }))} placeholder="tom@caa.com" /></div>
            <div className="mo-fld"><div className="mo-lbl">Phone</div>
              <PhoneInput className="mo-inp" value={booking.phone||""} onChange={v => setBooking(p => ({ ...p, phone: v }))} /></div>
          </div>
        </>)}

        {section === "label" && (<>
          <div className="mo-row">
            <div className="mo-fld"><div className="mo-lbl">Label Name</div>
              <input className="mo-inp" value={label.name} onChange={e => setLabel(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Sub Pop" autoFocus /></div>
            <div className="mo-fld"><div className="mo-lbl">Label Contact</div>
              <input className="mo-inp" value={label.contact} onChange={e => setLabel(p => ({ ...p, contact: e.target.value }))} placeholder="e.g. Jamie Lee" /></div>
          </div>
          <div className="mo-row">
            <div className="mo-fld"><div className="mo-lbl">Email</div>
              <input className="mo-inp" value={label.email} onChange={e => setLabel(p => ({ ...p, email: e.target.value }))} placeholder="jamie@subpop.com" /></div>
            <div className="mo-fld"><div className="mo-lbl">Phone</div>
              <PhoneInput className="mo-inp" value={label.phone||""} onChange={v => setLabel(p => ({ ...p, phone: v }))} /></div>
          </div>
        </>)}

        {section === "members" && (<>
          {members.map(m => (
            <div key={m.id} className="mem-item">
              <div className="mo-row" style={{ marginBottom: 8 }}>
                <div className="mo-fld" style={{ marginBottom: 0 }}><div className="mo-lbl">Name</div>
                  <input className="mo-inp" value={m.name} onChange={e => updMember(m.id, "name", e.target.value)} placeholder="e.g. Alex Kim" /></div>
                <div className="mo-fld" style={{ marginBottom: 0 }}><div className="mo-lbl">Role</div>
                  <input className="mo-inp" value={m.role} onChange={e => updMember(m.id, "role", e.target.value)} placeholder="e.g. Drums" /></div>
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
                <div className="mo-fld" style={{ flex: 1, marginBottom: 0 }}><div className="mo-lbl">Email</div>
                  <input className="mo-inp" value={m.email} onChange={e => updMember(m.id, "email", e.target.value)} placeholder="alex@band.com" /></div>
                <div className="mo-fld" style={{ flex: 1, marginBottom: 0 }}><div className="mo-lbl">Phone</div>
                  <PhoneInput className="mo-inp" value={m.phone||""} onChange={v => updMember(m.id, "phone", v)} /></div>
                <button className="mem-rm" onClick={() => removeMember(m.id)} title="Remove">✕</button>
              </div>
            </div>
          ))}
          <button className="btn-g" style={{ marginTop: 10, fontSize: 12 }} onClick={addMember}>+ Add Member</button>
        </>)}

        {section === "online" && (<>
          <div className="mo-fld"><div className="mo-lbl">Website</div>
            <input className="mo-inp" value={website} onChange={e => setWebsite(e.target.value)} placeholder="https://yourband.com" autoFocus /></div>
          {[
            { key: "instagram",  label: "Instagram",   placeholder: "@handle or username" },
            { key: "twitter",    label: "X / Twitter",  placeholder: "@handle" },
            { key: "tiktok",     label: "TikTok",       placeholder: "@handle" },
            { key: "spotify",    label: "Spotify",      placeholder: "Artist URL or ID" },
            { key: "appleMusic", label: "Apple Music",  placeholder: "Artist URL" },
          ].map(s => (
            <div key={s.key} className="mo-fld">
              <div className="mo-lbl">{s.label}</div>
              <input className="mo-inp" value={socials[s.key] || ""} placeholder={s.placeholder}
                onChange={e => setSocials(p => ({ ...p, [s.key]: e.target.value }))} />
            </div>
          ))}
        </>)}

        {section === "rider" && (<>
          <div className="mo-fld">
            <div className="mo-lbl">Rider Notes</div>
            <textarea className="mo-inp" style={{ minHeight: 90, resize: "vertical" }} value={rider}
              onChange={e => setRider(e.target.value)}
              placeholder="Dietary requirements, dressing room setup, hospitality requests…" autoFocus />
          </div>
          <p style={{ fontSize: 11, color: "var(--text-mut)", margin: "10px 0 0" }}>
            To upload rider documents, use the Tour Documents card in the Documents tab.
          </p>
        </>)}

        <div className="mo-acts">
          <button className="btn-g" onClick={onClose}>Cancel</button>
          <button className="btn-p" onClick={handleSave}>Save</button>
        </div>
      </div>
    </div>
  );
}

function EditArtistModal({ artist, onSave, onClose }) {
  const [name,      setName]      = useState(artist?.name      || "");
  const [colorIdx,  setColorIdx]  = useState(artist?.colorIdx  ?? null);
  const [homeCity,  setHomeCity]  = useState(artist?.homeCity  || "");
  const [homeState, setHomeState] = useState(artist?.homeState || "");
  const [mgmt,    setMgmt]    = useState(artist?.mgmt    || { company: "", name: "", email: "", phone: "" });
  const [booking, setBooking] = useState(artist?.booking || { agency: "", agent: "", email: "", phone: "" });
  const [label,   setLabel]   = useState(artist?.label   || { name: "", contact: "", email: "", phone: "" });
  const [members, setMembers] = useState(artist?.members || []);
  const [website, setWebsite] = useState(artist?.website || "");
  const [socials, setSocials] = useState(artist?.socials || { instagram: "", twitter: "", spotify: "", tiktok: "", appleMusic: "" });
  const [rider,          setRider]          = useState(artist?.rider || "");
  const [confirmDel, setConfirmDel] = useState(false);
  const allCatIds = CHECKLIST_TEMPLATE.map(c => c.id);
  const [checklistCats,     setChecklistCats]     = useState(artist?.checklistCats || allCatIds);
  const [lodgingPrefs,      setLodgingPrefs]      = useState(artist?.lodging_preferences || { preferred_chains: [], preferred_room_types: [], notes: "" });
  const toggleLodgingChain = (c) => setLodgingPrefs(p => ({ ...p, preferred_chains: p.preferred_chains.includes(c) ? p.preferred_chains.filter(x => x !== c) : [...p.preferred_chains, c] }));
  const toggleLodgingRoom  = (r) => setLodgingPrefs(p => ({ ...p, preferred_room_types: p.preferred_room_types.includes(r) ? p.preferred_room_types.filter(x => x !== r) : [...p.preferred_room_types, r] }));

  const addMember    = () => setMembers(p => [...p, { id: uid(), name: "", role: "", email: "", phone: "" }]);
  const removeMember = (id) => setMembers(p => p.filter(m => m.id !== id));
  const updMember    = (id, field, val) => setMembers(p => p.map(m => m.id === id ? { ...m, [field]: val } : m));
  const toggleCat    = (id) => setChecklistCats(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);

  const handleSave = () => onSave({ name: name.trim(), colorIdx, homeCity: homeCity.trim(), homeState: homeState.trim(), mgmt, booking, label, members, website, socials, rider, checklistCats, lodging_preferences: lodgingPrefs });

  return (
    <div className="ov" onClick={onClose}>
      <div className="mo" style={{ width: 620 }} onClick={e => e.stopPropagation()}>
        <div className="mo-title">Edit Artist</div>
        <div className="mo-sub">{artist?.name?.toUpperCase()}</div>

        {/* Name */}
        <div className="mo-fld">
          <div className="mo-lbl">Artist Name</div>
          <input className="mo-inp" value={name} onChange={e => setName(e.target.value)} autoFocus />
        </div>

        {/* Color */}
        <div className="mo-fld">
          <div className="mo-lbl">Color Identity</div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            {ARTIST_COLORS.map((c, i) => {
              const isSelected = colorIdx === i;
              return (
                <button
                  key={i}
                  onClick={() => setColorIdx(isSelected ? null : i)}
                  title={["Lavender","Slate","Sage","Sand","Rose","Teal"][i]}
                  style={{
                    width: 28, height: 28, borderRadius: 8, border: isSelected ? `2px solid ${c.pill}` : "2px solid transparent",
                    background: c.bg, cursor: "pointer", position: "relative", flexShrink: 0,
                    boxShadow: isSelected ? `0 0 0 2px ${c.pill}55` : "none", transition: "all 0.15s",
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                  <div style={{ width: 14, height: 14, borderRadius: 4, background: c.pill, opacity: isSelected ? 1 : 0.7 }} />
                </button>
              );
            })}
            {colorIdx !== null && (
              <button
                onClick={() => setColorIdx(null)}
                style={{ fontFamily: "'Helvetica Neue',Helvetica,Arial,sans-serif", fontSize: 11, color: "var(--text-mut)", background: "none", border: "1px solid #333", borderRadius: 4, padding: "4px 10px", cursor: "pointer" }}>
                Auto
              </button>
            )}
            <span style={{ fontFamily: "'Helvetica Neue',Helvetica,Arial,sans-serif", fontSize: 11, color: "var(--text-mut)", marginLeft: 4 }}>
              {colorIdx !== null ? `${["Lavender","Slate","Sage","Sand","Rose","Teal"][colorIdx]} selected` : "Auto-assigned by position"}
            </span>
          </div>
        </div>

        {/* Home Base */}
        <div className="mo-sec-hdr">Home Base</div>
        <div className="mo-row">
          <div className="mo-fld"><div className="mo-lbl">Home City</div>
            <input className="mo-inp" value={homeCity} onChange={e => setHomeCity(e.target.value)} placeholder="e.g. Chicago" /></div>
          <div className="mo-fld"><div className="mo-lbl">Home State</div>
            <input className="mo-inp" value={homeState} onChange={e => setHomeState(e.target.value)} placeholder="e.g. IL" /></div>
        </div>

        {/* Management */}
        <div className="mo-sec-hdr">Management</div>
        <div className="mo-row">
          <div className="mo-fld"><div className="mo-lbl">Management Company</div>
            <input className="mo-inp" value={mgmt.company || ""} onChange={e => setMgmt(p => ({ ...p, company: e.target.value }))} placeholder="e.g. Full Stop Management" /></div>
          <div className="mo-fld"><div className="mo-lbl">Manager Name</div>
            <input className="mo-inp" value={mgmt.name} onChange={e => setMgmt(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Sarah Goldberg" /></div>
        </div>
        <div className="mo-row">
          <div className="mo-fld"><div className="mo-lbl">Manager Email</div>
            <input className="mo-inp" value={mgmt.email} onChange={e => setMgmt(p => ({ ...p, email: e.target.value }))} placeholder="sarah@mgmt.com" /></div>
          <div className="mo-fld"><div className="mo-lbl">Manager Phone</div>
            <PhoneInput className="mo-inp" value={mgmt.phone||""} onChange={v => setMgmt(p => ({ ...p, phone: v }))} />
          </div>
        </div>

        {/* Booking */}
        <div className="mo-sec-hdr">Booking</div>
        <div className="mo-row">
          <div className="mo-fld"><div className="mo-lbl">Booking Agency</div>
            <input className="mo-inp" value={booking.agency} onChange={e => setBooking(p => ({ ...p, agency: e.target.value }))} placeholder="e.g. CAA" /></div>
          <div className="mo-fld"><div className="mo-lbl">Agent Name</div>
            <input className="mo-inp" value={booking.agent} onChange={e => setBooking(p => ({ ...p, agent: e.target.value }))} placeholder="e.g. Tom Parker" /></div>
        </div>
        <div className="mo-row">
          <div className="mo-fld"><div className="mo-lbl">Agent Email</div>
            <input className="mo-inp" value={booking.email} onChange={e => setBooking(p => ({ ...p, email: e.target.value }))} placeholder="tom@caa.com" /></div>
          <div className="mo-fld"><div className="mo-lbl">Agent Phone</div>
            <PhoneInput className="mo-inp" value={booking.phone||""} onChange={v => setBooking(p => ({ ...p, phone: v }))} /></div>
        </div>

        {/* Label */}
        <div className="mo-sec-hdr">Label</div>
        <div className="mo-row">
          <div className="mo-fld"><div className="mo-lbl">Label Name</div>
            <input className="mo-inp" value={label.name} onChange={e => setLabel(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Sub Pop" /></div>
          <div className="mo-fld"><div className="mo-lbl">Label Contact</div>
            <input className="mo-inp" value={label.contact} onChange={e => setLabel(p => ({ ...p, contact: e.target.value }))} placeholder="e.g. Jamie Lee" /></div>
        </div>
        <div className="mo-row">
          <div className="mo-fld"><div className="mo-lbl">Label Email</div>
            <input className="mo-inp" value={label.email} onChange={e => setLabel(p => ({ ...p, email: e.target.value }))} placeholder="jamie@subpop.com" /></div>
          <div className="mo-fld"><div className="mo-lbl">Label Phone</div>
            <PhoneInput className="mo-inp" value={label.phone||""} onChange={v => setLabel(p => ({ ...p, phone: v }))} /></div>
        </div>

        {/* Band Members */}
        <div className="mo-sec-hdr">Band Members</div>
        {members.map(m => (
          <div key={m.id} className="mem-item">
            <div className="mo-row" style={{ marginBottom: 8 }}>
              <div className="mo-fld" style={{ marginBottom: 0 }}><div className="mo-lbl">Name</div>
                <input className="mo-inp" value={m.name} onChange={e => updMember(m.id, "name", e.target.value)} placeholder="e.g. Alex Kim" /></div>
              <div className="mo-fld" style={{ marginBottom: 0 }}><div className="mo-lbl">Role</div>
                <input className="mo-inp" value={m.role} onChange={e => updMember(m.id, "role", e.target.value)} placeholder="e.g. Drums" /></div>
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
              <div className="mo-fld" style={{ flex: 1, marginBottom: 0 }}><div className="mo-lbl">Email (optional)</div>
                <input className="mo-inp" value={m.email} onChange={e => updMember(m.id, "email", e.target.value)} placeholder="alex@band.com" /></div>
              <div className="mo-fld" style={{ flex: 1, marginBottom: 0 }}><div className="mo-lbl">Phone (optional)</div>
                <PhoneInput className="mo-inp" value={m.phone||""} onChange={v => updMember(m.id, "phone", v)} /></div>
              <button className="mem-rm" onClick={() => removeMember(m.id)} title="Remove member">✕</button>
            </div>
          </div>
        ))}
        <button className="add-g" style={{ marginBottom: 4 }} onClick={addMember}>+ Add Member</button>

        {/* Online */}
        <div className="mo-sec-hdr">Online</div>
        <div className="mo-fld"><div className="mo-lbl">Website</div>
          <input className="mo-inp" value={website} onChange={e => setWebsite(e.target.value)} placeholder="https://artistname.com" /></div>
        <div className="mo-row">
          <div className="mo-fld"><div className="mo-lbl">Instagram</div>
            <input className="mo-inp" value={socials.instagram} onChange={e => setSocials(p => ({ ...p, instagram: e.target.value }))} placeholder="@handle" /></div>
          <div className="mo-fld"><div className="mo-lbl">Twitter / X</div>
            <input className="mo-inp" value={socials.twitter} onChange={e => setSocials(p => ({ ...p, twitter: e.target.value }))} placeholder="@handle" /></div>
        </div>
        <div className="mo-row">
          <div className="mo-fld"><div className="mo-lbl">Spotify</div>
            <input className="mo-inp" value={socials.spotify} onChange={e => setSocials(p => ({ ...p, spotify: e.target.value }))} placeholder="open.spotify.com/artist/..." /></div>
          <div className="mo-fld"><div className="mo-lbl">TikTok</div>
            <input className="mo-inp" value={socials.tiktok} onChange={e => setSocials(p => ({ ...p, tiktok: e.target.value }))} placeholder="@handle" /></div>
        </div>
        <div className="mo-fld" style={{ maxWidth: "calc(50% - 6px)" }}>
          <div className="mo-lbl">Apple Music</div>
          <input className="mo-inp" value={socials.appleMusic} onChange={e => setSocials(p => ({ ...p, appleMusic: e.target.value }))} placeholder="music.apple.com/artist/..." />
        </div>

        {/* Hospitality Rider */}
        <div className="mo-sec-hdr">Hospitality Rider</div>
        <div className="mo-fld">
          <div className="mo-lbl">Rider Notes <span style={{ opacity: 0.6, textTransform: "none", letterSpacing: 0 }}>— type key requirements or attach a file in the Documents tab</span></div>
          <textarea className="mo-inp" rows={4} style={{ resize: "vertical" }} placeholder="e.g. 2 dressing rooms, hot meal for 5, specific beverages..."
            value={rider} onChange={e => setRider(e.target.value)} />
        </div>

        {/* Checklist Template */}
        <div className="mo-sec-hdr">Advance Checklist</div>
        <div className="mo-hint">
          Choose which sections appear on new shows for this artist.
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
          {CHECKLIST_TEMPLATE.map(cat => (
            <label key={cat.id} className={`mo-checklist-label${checklistCats.includes(cat.id) ? " checked" : ""}`}>
              <input type="checkbox" checked={checklistCats.includes(cat.id)} onChange={() => toggleCat(cat.id)}
                style={{ accentColor: "var(--accent)", width: 13, height: 13, cursor: "pointer" }} />
              <span className="mo-checklist-span">{cat.label}</span>
            </label>
          ))}
        </div>

        {/* Lodging Preferences */}
        <div className="mo-sec-hdr">Lodging Preferences</div>
        <div className="mo-hint">Used to filter hotel recommendations on tour days. Fill in once per artist.</div>
        <div className="mo-fld">
          <div className="mo-lbl">Preferred Chains</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {HOTEL_CHAINS.map(c => {
              const on = lodgingPrefs.preferred_chains.includes(c);
              return (
                <button key={c} onClick={() => toggleLodgingChain(c)}
                  style={{ fontFamily: "'Helvetica Neue',Helvetica,Arial,sans-serif", fontSize: 11, padding: "4px 10px", borderRadius: 16, cursor: "pointer", border: `1px solid ${on ? "var(--accent)" : "#2a2a2a"}`, background: on ? "var(--accent)" : "transparent", color: on ? "#0d0d0d" : "#888", fontWeight: on ? 700 : 400 }}>
                  {c}
                </button>
              );
            })}
          </div>
        </div>
        <div className="mo-fld">
          <div className="mo-lbl">Preferred Room Types</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {ROOM_TYPES.map(r => {
              const on = lodgingPrefs.preferred_room_types.includes(r);
              return (
                <button key={r} onClick={() => toggleLodgingRoom(r)}
                  style={{ fontFamily: "'Helvetica Neue',Helvetica,Arial,sans-serif", fontSize: 11, padding: "4px 10px", borderRadius: 16, cursor: "pointer", border: `1px solid ${on ? "var(--accent)" : "#2a2a2a"}`, background: on ? "var(--accent)" : "transparent", color: on ? "#0d0d0d" : "#888", fontWeight: on ? 700 : 400 }}>
                  {r}
                </button>
              );
            })}
          </div>
        </div>
        <div className="mo-fld">
          <div className="mo-lbl">Lodging Notes</div>
          <textarea className="mo-inp" rows={2} style={{ resize: "vertical" }}
            placeholder="e.g. No motels, minimum 3-star, must have gym…"
            value={lodgingPrefs.notes || ""}
            onChange={e => setLodgingPrefs(p => ({ ...p, notes: e.target.value }))} />
        </div>

        {/* Actions */}
        <div className="mo-acts">
          {confirmDel ? (
            <>
              <span style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: "#c84b4b", marginRight: "auto" }}>Delete {artist?.name}?</span>
              <button className="btn-d" onClick={() => onSave(null)}>Yes, Delete</button>
              <button className="btn-g" onClick={() => setConfirmDel(false)}>Cancel</button>
            </>
          ) : (
            <>
              <button className="btn-d" onClick={() => setConfirmDel(true)}>Delete Artist</button>
              <button className="btn-g" onClick={onClose}>Cancel</button>
              <button className="btn-p" disabled={!name.trim()} onClick={handleSave}>Save Changes</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── PlaceSearchInput ──────────────────────────────────────────────────────────
// Reusable Google Places autocomplete input.
// Props:
//   query / onQueryChange — controlled input value
//   onPlacePicked({ name, placeId, city, state, mapsUrl }) — fired when user selects a prediction
//   onClear — called when user edits after a place is confirmed
//   confirmed — bool, shows ✓ badge when true
//   UI, location (city bias string e.g. "Nashville, TN")
function PlaceSearchInput({ query, onQueryChange, onPlacePicked, onClear, confirmed, UI, location, placeholder, autoFocus }) {
  const FF = "'Helvetica Neue', Helvetica, Arial, sans-serif";
  const [predictions, setPredictions] = useState([]);
  const [searching,   setSearching]   = useState(false);
  const [fetchingDetails, setFetchingDetails] = useState(false);
  const debounceRef = useRef(null);
  const acSvcRef    = useRef(null);
  const detailsSvcRef = useRef(null);

  useEffect(() => {
    loadGoogleMaps().then(maps => {
      acSvcRef.current      = new maps.places.AutocompleteService();
      detailsSvcRef.current = new maps.places.PlacesService(document.createElement("div"));
    }).catch(() => {});
  }, []);

  const handleChange = (e) => {
    const val = e.target.value;
    onQueryChange(val);
    if (confirmed) onClear?.();
    clearTimeout(debounceRef.current);
    if (!val.trim()) { setPredictions([]); return; }
    debounceRef.current = setTimeout(() => {
      if (!acSvcRef.current) return;
      setSearching(true);
      const biasedInput = location ? `${val} ${location}` : val;
      acSvcRef.current.getPlacePredictions({ input: biasedInput, types: ["establishment"] }, (results, status) => {
        setSearching(false);
        setPredictions(status === "OK" && results ? results.slice(0, 5) : []);
      });
    }, 280);
  };

  const handlePick = (pred) => {
    const name = pred.structured_formatting?.main_text || pred.description;
    onQueryChange(name);
    setPredictions([]);
    if (!detailsSvcRef.current) {
      onPlacePicked({ name, placeId: pred.place_id, city: "", state: "", mapsUrl: `https://www.google.com/maps/place/?q=place_id:${pred.place_id}` });
      return;
    }
    setFetchingDetails(true);
    detailsSvcRef.current.getDetails(
      { placeId: pred.place_id, fields: ["address_components", "name", "url"] },
      (place, status) => {
        setFetchingDetails(false);
        let city = "", state = "";
        if (status === "OK" && place?.address_components) {
          city  = place.address_components.find(c => c.types.includes("locality"))?.long_name || "";
          state = place.address_components.find(c => c.types.includes("administrative_area_level_1"))?.short_name || "";
        }
        onPlacePicked({
          name,
          placeId: pred.place_id,
          city,
          state,
          mapsUrl: `https://www.google.com/maps/place/?q=place_id:${pred.place_id}`,
        });
      }
    );
  };

  const inputStyle = {
    fontFamily: FF, fontSize: 14, color: UI.text,
    background: UI.rowBg, border: `1px solid ${confirmed ? "var(--success)" : UI.panelBdr}`,
    borderRadius: 8, padding: "8px 11px", width: "100%", boxSizing: "border-box",
    outline: "none", transition: "border-color 0.15s",
  };

  return (
    <div style={{ position: "relative" }}>
      <div style={{ position: "relative" }}>
        <input
          value={query}
          onChange={handleChange}
          placeholder={placeholder || "Search for a place…"}
          style={{ ...inputStyle, paddingRight: (searching || fetchingDetails) ? 30 : 11 }}
          autoFocus={autoFocus}
          autoComplete="off"
        />
        {(searching || fetchingDetails) && (
          <span style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", fontFamily: FF, fontSize: 11, color: UI.textVFnt }}>…</span>
        )}
        {confirmed && !searching && !fetchingDetails && (
          <span style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", fontFamily: FF, fontSize: 11, color: "var(--success)", letterSpacing: "0.3px" }}>✓ Maps</span>
        )}
      </div>
      {predictions.length > 0 && (
        <div style={{
          position: "absolute", top: "100%", left: 0, right: 0, zIndex: 999, marginTop: 3,
          background: UI.bg, border: `1px solid ${UI.panelBdr}`, borderRadius: 8,
          boxShadow: "0 6px 20px rgba(0,0,0,0.4)", overflow: "hidden",
        }}>
          {predictions.map((pred, i) => (
            <button key={pred.place_id} onMouseDown={() => handlePick(pred)}
              style={{
                width: "100%", textAlign: "left", background: "none", border: "none",
                borderTop: i > 0 ? `1px solid ${UI.panelBdr}` : "none",
                padding: "8px 12px", cursor: "pointer", display: "flex", flexDirection: "column", gap: 2,
              }}>
              <span style={{ fontFamily: FF, fontSize: 12, color: UI.text, fontWeight: 500 }}>
                {pred.structured_formatting?.main_text || pred.description}
              </span>
              {pred.structured_formatting?.secondary_text && (
                <span style={{ fontFamily: FF, fontSize: 11, color: UI.textMut }}>
                  {pred.structured_formatting.secondary_text}
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Inline form for submitting a community recommendation on a Tour Mode day card.
// Props: onSave(rec), onCancel, UI (theme tokens), FF (font family string)
// LocalRecForm — community recommendation form with Google Places Autocomplete
// Props: onSave(rec), onCancel(), UI, FF, location (e.g. "Nashville, TN" for bias)
function LocalRecForm({ onSave, onCancel, UI, FF, location }) {
  const CATS = [
    { id: "food",        label: "Food"        },
    { id: "bars",        label: "Bars"        },
    { id: "brunch",      label: "Brunch"      },
    { id: "coffee",      label: "Coffee"      },
    { id: "catering",    label: "Catering"    },
    { id: "barbershop",  label: "Barbershop"  },
    { id: "laundromat",  label: "Laundromat"  },
    { id: "music",       label: "Music Store" },
    { id: "tattoo",      label: "Tattoo"      },
    { id: "clothing",    label: "Clothing"    },
    { id: "convenience", label: "Convenience" },
    { id: "gym",         label: "Gym"         },
    { id: "dentist",     label: "Dentist"     },
    { id: "dispensary",  label: "Dispensary"  },
    { id: "hospital",    label: "Hospital"    },
    { id: "pharmacy",    label: "Pharmacy"    },
    { id: "massage",     label: "Massage"     },
    { id: "chiro",       label: "Chiro"       },
    { id: "acupuncture", label: "Acupuncture" },
    { id: "other",       label: "Other"       },
  ];

  const font = FF || "'Helvetica Neue', Helvetica, Arial, sans-serif";

  // Query typed in the search box
  const [query,       setQuery]       = useState("");
  // Google Places predictions [ { place_id, description, structured_formatting } ]
  const [predictions, setPredictions] = useState([]);
  const [searching,   setSearching]   = useState(false);
  // Once a prediction is chosen, lock in name + place_id
  const [chosenName,    setChosenName]    = useState("");
  const [chosenPlaceId, setChosenPlaceId] = useState(null);  // Google place_id
  const [cat,  setCat]    = useState("food");
  const [note, setNote]   = useState("");
  const [err,  setErr]    = useState("");
  // Debounce ref
  const debounceRef = useRef(null);
  const svcRef      = useRef(null); // AutocompleteService instance

  // Initialise AutocompleteService once Maps is ready
  useEffect(() => {
    loadGoogleMaps().then(maps => {
      svcRef.current = new maps.places.AutocompleteService();
    }).catch(() => {});
  }, []);

  const handleQueryChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    // Clear any previously confirmed selection if user edits the text
    setChosenName("");
    setChosenPlaceId(null);
    if (err) setErr("");

    clearTimeout(debounceRef.current);
    if (!val.trim()) { setPredictions([]); return; }

    debounceRef.current = setTimeout(() => {
      if (!svcRef.current) return;
      setSearching(true);
      // Bias results toward the current city by appending location to the query
      const biasedInput = location ? `${val} ${location}` : val;
      svcRef.current.getPlacePredictions({ input: biasedInput, types: ["establishment"] }, (results, status) => {
        setSearching(false);
        if (status === "OK" && results) {
          setPredictions(results.slice(0, 5));
        } else {
          setPredictions([]);
        }
      });
    }, 280);
  };

  const handlePickPrediction = (pred) => {
    setChosenName(pred.structured_formatting?.main_text || pred.description);
    setChosenPlaceId(pred.place_id);
    setQuery(pred.structured_formatting?.main_text || pred.description);
    setPredictions([]);
  };

  const handleSave = () => {
    const finalName = chosenName || query.trim();
    if (!finalName) { setErr("Search for a place above."); return; }
    // Build the URL: direct Google Maps place link if we have a place_id, otherwise blank
    const url = chosenPlaceId
      ? `https://www.google.com/maps/place/?q=place_id:${chosenPlaceId}`
      : "";
    onSave({
      id:       "rec" + Math.random().toString(36).slice(2),
      name:     finalName,
      cat,
      note:     note.trim(),
      url,
      ts:       new Date().toISOString(),
    });
  };

  const inputStyle = {
    fontFamily: font, fontSize: 12, color: "var(--text)",
    background: "#1e1e1e", border: "1px solid #333",
    borderRadius: 4, padding: "5px 9px", width: "100%", boxSizing: "border-box", outline: "none",
  };

  return (
    <div style={{
      background: "var(--row-hover)", border: "1px solid #2e2e2e",
      borderRadius: 8, padding: "12px 14px",
      display: "flex", flexDirection: "column", gap: 9,
    }}>
      <div style={{ fontFamily: font, fontSize: 11, letterSpacing: 1.5, color: "var(--text-mid)", marginBottom: 2 }}>
        SHARE A RECOMMENDATION
      </div>

      {/* Place search with autocomplete */}
      <div style={{ display: "flex", flexDirection: "column", gap: 4, position: "relative" }}>
        <label style={{ fontFamily: font, fontSize: 11, color: "var(--text-mid)" }}>
          Search for a place *
          {chosenPlaceId && (
            <span style={{ color: "var(--success)", marginLeft: 6, fontSize: 11 }}>✓ Linked to Google Maps</span>
          )}
        </label>
        <div style={{ position: "relative" }}>
          <input
            value={query}
            onChange={handleQueryChange}
            placeholder="Start typing a name…"
            style={{ ...inputStyle, paddingRight: searching ? 28 : 9 }}
            autoFocus
            autoComplete="off"
          />
          {searching && (
            <span style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", fontFamily: font, fontSize: 11, color: "var(--text-mut)" }}>…</span>
          )}
        </div>
        {/* Autocomplete dropdown */}
        {predictions.length > 0 && (
          <div style={{
            position: "absolute", top: "100%", left: 0, right: 0, zIndex: 999,
            background: "#1e1e1e", border: "1px solid #333", borderRadius: 4,
            boxShadow: "0 4px 12px rgba(0,0,0,0.5)", overflow: "hidden", marginTop: 2,
          }}>
            {predictions.map((pred, i) => (
              <button key={pred.place_id} onMouseDown={() => handlePickPrediction(pred)}
                style={{
                  width: "100%", textAlign: "left", background: "none", border: "none",
                  borderTop: i > 0 ? "1px solid #2a2a2a" : "none",
                  padding: "7px 10px", cursor: "pointer", display: "flex", flexDirection: "column", gap: 1,
                }}>
                <span style={{ fontFamily: font, fontSize: 12, color: "var(--text)", fontWeight: 500 }}>
                  {pred.structured_formatting?.main_text || pred.description}
                </span>
                {pred.structured_formatting?.secondary_text && (
                  <span style={{ fontFamily: font, fontSize: 11, color: "var(--text-mut)" }}>
                    {pred.structured_formatting.secondary_text}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}
        {err && <span style={{ fontFamily: font, fontSize: 11, color: "#e05c5c" }}>{err}</span>}
      </div>

      {/* Category chips */}
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <label style={{ fontFamily: font, fontSize: 11, color: "var(--text-mid)" }}>Category</label>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
          {CATS.map(c => (
            <button key={c.id} onClick={() => setCat(c.id)} style={{
              fontFamily: font, fontSize: 11, padding: "4px 10px", borderRadius: 8, cursor: "pointer", lineHeight: 1.4,
              border: `1px solid ${cat === c.id ? "var(--success)" : "#2e2e2e"}`,
              background: cat === c.id ? "#0a1a0a" : "none",
              color: cat === c.id ? "var(--success)" : "#888",
            }}>
              {c.label}
            </button>
          ))}
        </div>
      </div>

      {/* Note */}
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <label style={{ fontFamily: font, fontSize: 11, color: "var(--text-mid)" }}>Note <span style={{ opacity: 0.5 }}>(optional)</span></label>
        <input
          value={note}
          onChange={e => setNote(e.target.value)}
          placeholder="e.g. Ask for the back room, cash only…"
          style={inputStyle}
        />
      </div>

      {/* Actions */}
      <div style={{ display: "flex", gap: 8, marginTop: 2 }}>
        <button onClick={handleSave} style={{
          fontFamily: font, fontSize: 11, fontWeight: 600, letterSpacing: 0.5,
          color: "var(--success)", background: "#0a1a0a",
          border: "1px solid #1e3a1e", borderRadius: 4, padding: "6px 16px", cursor: "pointer",
        }}>
          Save
        </button>
        <button onClick={onCancel} style={{
          fontFamily: font, fontSize: 11, color: UI?.textMut || "#888",
          background: "none", border: `1px solid ${UI?.panelBdr || "#2a2a2a"}`,
          borderRadius: 4, padding: "6px 14px", cursor: "pointer",
        }}>
          Cancel
        </button>
      </div>
    </div>
  );
}

function CopyBtn({ text }) {
  const [ok, setOk] = useState(false);
  if (!text) return null;
  return (
    <button className={`copy-btn${ok ? " ok" : ""}`} title="Copy"
      onClick={e => { e.stopPropagation(); navigator.clipboard.writeText(text); setOk(true); setTimeout(() => setOk(false), 1500); }}>
      {ok ? "✓" : "⎘"}
    </button>
  );
}

// Stacked label + value row used in narrow contact cards
function ContactField({ label, value, link }) {
  if (!value) return null;
  // Phone fields render in the app-standard xxx.xxx.xxxx regardless of source
  const display = /phone/i.test(label || "") ? fmtPhone(value) : value;
  return (
    <div style={{ marginBottom: 7 }}>
      <div className="cf-lbl">{label}</div>
      <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
        {link
          ? <a href={value} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: "var(--accent)", textDecoration: "none", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{value}</a>
          : <span className="cf-val">{display}</span>
        }
        <CopyBtn text={value} />
      </div>
    </div>
  );
}

function TourModal({ artistName, existing, onSave, onClose, templates }) {
  const [name,       setName]       = useState(existing?.name || "");
  const [year,       setYear]       = useState(existing?.year || new Date().getFullYear().toString());
  const [templateId, setTemplateId] = useState("");
  const hasTemplates = !existing && templates?.length > 0;
  const selectedTpl  = hasTemplates ? templates.find(t => t.id === templateId) : null;
  return (
    <div className="ov" onClick={onClose}>
      <div className="mo" onClick={e => e.stopPropagation()}>
        <div className="mo-title">{existing ? "Edit Tour" : "New Tour"}</div>
        <div className="mo-sub">{artistName?.toUpperCase()}</div>
        <div className="mo-fld"><div className="mo-lbl">Tour Name</div>
          <input className="mo-inp" placeholder="e.g. Spring East Coast 2026" value={name} onChange={e => setName(e.target.value)} autoFocus /></div>
        <div className="mo-fld"><div className="mo-lbl">Year</div>
          <input className="mo-inp" placeholder="2026" value={year} onChange={e => setYear(e.target.value)} /></div>
        {hasTemplates && (
          <div className="mo-fld">
            <div className="mo-lbl">Start from template</div>
            <select className="mo-inp" value={templateId} onChange={e => setTemplateId(e.target.value)}>
              <option value="">Blank tour</option>
              {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            {selectedTpl && (
              <div style={{ marginTop: 6, fontSize: 11, color: "var(--text-mut)", lineHeight: 1.5 }}>
                {selectedTpl.roster_profile_ids?.length > 0
                  ? `✦ Portal config + ${selectedTpl.roster_profile_ids.length} roster member${selectedTpl.roster_profile_ids.length !== 1 ? "s" : ""} will be applied`
                  : "✦ Portal config will be applied"}
              </div>
            )}
          </div>
        )}
        <div className="mo-acts">
          <button className="btn-g" onClick={onClose}>Cancel</button>
          <button className="btn-p" disabled={!name.trim()} onClick={() => onSave({ name: name.trim(), year, templateId: templateId || null })}>
            {existing ? "Save Changes" : "Create Tour"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ShowModal({ tourName, existing, defaultDate, venues, onSave, onClose, onAddVenue, onAddVenueFromDir }) {
  const vl = Object.values(venues).sort((a, b) => a.name.localeCompare(b.name));
  const [date,        setDate]        = useState(existing?.date   || defaultDate || "");
  const [vid,         setVid]         = useState(existing?.venueId || "");
  const [st,          setSt]          = useState(existing?.status   || "unstarted");
  const [tz,          setTz]          = useState(existing?.timezone || "");
  const [confirmDel,  setConfirmDel]  = useState(false);
  const [newVenueCity,  setNewVenueCity]  = useState("");
  const [newVenueState, setNewVenueState] = useState("");
  const [isNewVenue,    setIsNewVenue]    = useState(false);
  const existingVenue = existing?.venueId ? (venues[existing.venueId] || null) : null;
  // Fix: include state in venue display so it reads "Venue — City, ST" not "Venue — City"
  const [venueSearch, setVenueSearch] = useState(existingVenue
    ? `${existingVenue.name}${existingVenue.city ? " — " + [existingVenue.city, existingVenue.addressState].filter(Boolean).join(", ") : ""}`
    : "");
  const [showDrop, setShowDrop] = useState(false);
  const [dirResults, setDirResults] = useState([]);
  const [dirSearching, setDirSearching] = useState(false);
  const dirTimer = useRef(null);
  const [addrSuggestions, setAddrSuggestions] = useState([]);
  const addrTimer = useRef(null);
  // Uncontrolled refs for lodging fields — avoids controlled-input flicker caused
  // by parent re-renders when data changes (Realtime echo, saveNormalized flush, etc.)
  const hotelNameRef = useRef(null);
  const hotelAddrRef = useRef(null);

  const fetchAddrSuggestions = (q) => {
    clearTimeout(addrTimer.current);
    if (!q.trim() || q.trim().length < 3) { setAddrSuggestions([]); return; }
    addrTimer.current = setTimeout(() => {
      try {
        if (!window.google?.maps?.places) return;
        const svc = new window.google.maps.places.AutocompleteService();
        svc.getPlacePredictions({ input: q, types: ["geocode"] }, (preds, status) => {
          if (status === "OK" && preds) {
            setAddrSuggestions(preds.map(p => p.description));
          } else {
            setAddrSuggestions([]);
          }
        });
      } catch { setAddrSuggestions([]); }
    }, 300);
  };

  const filtered = venueSearch.trim().length > 0
    ? vl.filter(v =>
        v.name.toLowerCase().includes(venueSearch.toLowerCase()) ||
        (v.city || "").toLowerCase().includes(venueSearch.toLowerCase())
      ).slice(0, 15)
    : vl.slice(0, 15);

  const runDirSearch = (q) => {
    clearTimeout(dirTimer.current);
    if (!q.trim()) { setDirResults([]); return; }
    dirTimer.current = setTimeout(async () => {
      setDirSearching(true);
      const { data } = await supabase
        .from("public_venues")
        .select("id,name,city,address_state,capacity,kind")
        .ilike("name", `%${q.trim()}%`)
        .order("name")
        .limit(12);
      // Filter out venues already in the user's list
      const localNames = new Set(vl.map(v => v.name.toLowerCase()));
      setDirResults((data || []).filter(pv => !localNames.has(pv.name.toLowerCase())));
      setDirSearching(false);
    }, 300);
  };

  const selectVenue = (v) => {
    setVid(v.id);
    setVenueSearch(`${v.name}${v.city ? ` — ${[v.city, v.addressState].filter(Boolean).join(", ")}` : ""}`);
    setShowDrop(false);
    setDirResults([]);
    setIsNewVenue(false);
    setNewVenueCity("");
    setNewVenueState("");
  };

  return (
    <div className="ov" onClick={onClose}>
      <div className="mo" onClick={e => e.stopPropagation()}>
        <div className="mo-title">{existing ? "Edit Show" : "Add Show"}</div>
        <div className="mo-sub">{tourName?.toUpperCase()}</div>
        <div className="mo-fld"><div className="mo-lbl">Date</div>
          <input type="date" className="mo-inp" value={date} onChange={e => setDate(e.target.value)} autoFocus /></div>
        <div className="mo-fld" style={{ position: "relative" }}>
          <div className="mo-lbl">Venue</div>
          <input
            className="mo-inp"
            value={venueSearch}
            onChange={e => { setVenueSearch(e.target.value); setVid(""); setShowDrop(true); runDirSearch(e.target.value); }}
            onFocus={() => setShowDrop(true)}
            onBlur={() => setTimeout(() => { setShowDrop(false); }, 200)}
            placeholder="Search your venues or the directory…"
          />
          {showDrop && (filtered.length > 0 || dirResults.length > 0 || venueSearch.trim().length > 0) && (
            <div className="mo-drop">
              {filtered.length > 0 && (
                <>
                  {dirResults.length > 0 && <div className="mo-drop-lbl">My Venues</div>}
                  {filtered.map(v => (
                    <div key={v.id} className="mo-drop-item" onMouseDown={() => selectVenue(v)}>
                      {v.name} <span style={{ opacity: 0.5 }}>— {[v.city, v.addressState].filter(Boolean).join(", ")}</span>
                    </div>
                  ))}
                </>
              )}
              {dirSearching && <div className="mo-drop-item" style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, opacity: 0.6 }}>Searching directory…</div>}
              {!dirSearching && dirResults.length > 0 && (
                <>
                  <div className="mo-drop-lbl dir" style={{ borderTop: filtered.length > 0 ? "1px solid inherit" : "none" }}>From Directory</div>
                  {dirResults.map(pv => (
                    <div key={pv.id} className="mo-drop-item" onMouseDown={() => { const v = onAddVenueFromDir(pv); selectVenue(v); }}>
                      {pv.name} <span style={{ opacity: 0.5 }}>— {[pv.city, pv.address_state].filter(Boolean).join(", ")}</span>
                      <span style={{ float: "right", fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: "var(--success)", marginTop: 2 }}>+ add</span>
                    </div>
                  ))}
                </>
              )}
              {venueSearch.trim().length > 0 && (
                <div className="mo-drop-item add" onMouseDown={() => { setIsNewVenue(true); setShowDrop(false); }}>
                  + Add &ldquo;{venueSearch.trim()}&rdquo; as new venue
                </div>
              )}
            </div>
          )}
        </div>
        {isNewVenue && (
          <div style={{ display: "flex", gap: 8 }}>
            <div className="mo-fld" style={{ flex: 1 }}>
              <div className="mo-lbl">City</div>
              <input className="mo-inp" placeholder="e.g. Nashville" value={newVenueCity} onChange={e => setNewVenueCity(e.target.value)} autoFocus />
            </div>
            <div className="mo-fld" style={{ flex: 1 }}>
              <div className="mo-lbl">State / Province</div>
              <input className="mo-inp" placeholder="e.g. TN" value={newVenueState} onChange={e => setNewVenueState(e.target.value)} />
            </div>
          </div>
        )}
        <div style={{ display: "flex", gap: 10 }}>
          <div className="mo-fld" style={{ flex: 1 }}><div className="mo-lbl">Status</div>
            <select className="mo-sel" value={st} onChange={e => setSt(e.target.value)}>
              {Object.entries(statusConfig).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select></div>
          <div className="mo-fld" style={{ flex: 1 }}><div className="mo-lbl">Timezone</div>
            <select className="mo-sel" value={tz} onChange={e => setTz(e.target.value)}>
              <option value="">—</option>
              <option value="ET">ET — Eastern</option>
              <option value="CT">CT — Central</option>
              <option value="MT">MT — Mountain</option>
              <option value="PT">PT — Pacific</option>
              <option value="AK">AK — Alaska</option>
              <option value="HI">HI — Hawaii</option>
              <option value="GMT">GMT</option>
              <option value="CET">CET — Central European</option>
              <option value="AEST">AEST — Australian Eastern</option>
            </select></div>
        </div>
        <div className="mo-lodging-div">
          <div className="mo-lodging-lbl">Lodging</div>
          <div className="mo-fld"><div className="mo-lbl">Hotel</div>
            {/* Uncontrolled — defaultValue so parent re-renders can't overwrite typing */}
            <input className="mo-inp" placeholder="Hotel name…"
              defaultValue={existing?.lodging?.name || ""}
              ref={hotelNameRef} />
          </div>
          <div className="mo-fld"><div className="mo-lbl">Address</div>
            {/* datalist avoids the overflow:auto clipping that broke the custom dropdown */}
            <input className="mo-inp" placeholder="Full address for routing…"
              defaultValue={existing?.lodging?.address || ""}
              ref={hotelAddrRef}
              list="hotel-addr-suggestions"
              onChange={e => fetchAddrSuggestions(e.target.value)} />
            <datalist id="hotel-addr-suggestions">
              {addrSuggestions.map((s, i) => <option key={i} value={s} />)}
            </datalist>
          </div>
        </div>
        <div className="mo-acts">
          {existing && !confirmDel && <button className="btn-d" onClick={() => setConfirmDel(true)}>Delete Show</button>}
          {existing && confirmDel && <>
            <span style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: "#c84b4b", marginRight: "auto" }}>Delete this show?</span>
            <button className="btn-g" onClick={() => setConfirmDel(false)}>Cancel</button>
            <button className="btn-d" onClick={() => onSave(null)}>Yes, Delete</button>
          </>}
          {!confirmDel && <button className="btn-g" onClick={onClose}>Cancel</button>}
          {!confirmDel && <button className="btn-p" disabled={!date || (!vid && !isNewVenue)} onClick={() => {
            let venueId = vid;
            if (isNewVenue && venueSearch.trim()) {
              const v = onAddVenue(venueSearch.trim(), newVenueCity, newVenueState);
              venueId = v.id;
            }
            const lodgingName = (hotelNameRef.current?.value || "").trim();
            const lodgingAddr = (hotelAddrRef.current?.value || "").trim();
            onSave({ date, venueId, status: st, timezone: tz, lodging: { name: lodgingName, address: lodgingAddr } });
          }}>
            {existing ? "Save Changes" : "Add Show"}
          </button>}
        </div>
      </div>
    </div>
  );
}

// ── ShowReportTMForm ──────────────────────────────────────────────────────
// Extracted from the Report tab IIFE to satisfy Rules of Hooks.
// key={selS} on the call site resets state when the selected show changes.
function ShowReportTMForm({ showId, tourId, showReports, profiles, saveShowReport, show, venues, fmtDate, UI, lightMode }) {
  const FF = "'Helvetica Neue', Helvetica, Arial, sans-serif";
  const T  = lightMode ? "light" : "dark";
  const [tmForm,   setTmForm]   = useState(() => {
    const ex = (showReports || []).find(r => r.show_id === showId && r.role_category === 'tm');
    return ex?.responses || {};
  });
  const [tmSaving, setTmSaving] = useState(false);
  const [tmSaved,  setTmSaved]  = useState(false);

  const rpts     = (showReports || []).filter(r => r.show_id === showId);
  const myTm     = rpts.find(r => r.role_category === 'tm');
  const bandRpts = rpts.filter(r => r.role_category === 'band');
  const crewRpts = rpts.filter(r => r.role_category === 'crew');
  const ratings  = rpts.map(r => r.responses?.overall_rating).filter(Boolean);
  const avgRating = ratings.length
    ? (ratings.reduce((a, b) => a + Number(b), 0) / ratings.length).toFixed(1)
    : null;

  const starBtns = (label, key) => (
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
      <div style={{ fontFamily: FF, fontSize: 11, color: UI.textMut, width: 170, flexShrink: 0 }}>{label}</div>
      <div style={{ display: "flex", gap: 4 }}>
        {[1,2,3,4,5].map(n => (
          <button key={n} onClick={() => setTmForm(f => ({ ...f, [key]: f[key] === n ? null : n }))}
            style={{ width: 22, height: 22, borderRadius: 4, border: "none", cursor: "pointer",
              background: (tmForm[key] || 0) >= n ? UI.amber : (T === "dark" ? "#2a2a2a" : "#eee"),
              color: (tmForm[key] || 0) >= n ? "#fff" : UI.textMut, fontFamily: FF, fontSize: 11, fontWeight: 700 }}>
            {n}
          </button>
        ))}
      </div>
    </div>
  );
  const pillBtns = (label, key, opts) => (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontFamily: FF, fontSize: 11, color: UI.textMut, marginBottom: 4 }}>{label}</div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {opts.map(o => (
          <button key={o} onClick={() => setTmForm(f => ({ ...f, [key]: f[key] === o ? null : o }))}
            style={{ fontFamily: FF, fontSize: 11, padding: "3px 10px", borderRadius: 12,
              border: `1px solid ${tmForm[key] === o ? UI.amber : UI.bdr}`,
              background: tmForm[key] === o ? UI.amber + "22" : "transparent",
              color: tmForm[key] === o ? UI.amber : UI.textMut, cursor: "pointer" }}>
            {o}
          </button>
        ))}
      </div>
    </div>
  );
  const txtArea = (label, key, ph) => (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontFamily: FF, fontSize: 11, color: UI.textMut, marginBottom: 4 }}>{label}</div>
      <textarea value={tmForm[key] || ""} onChange={e => setTmForm(f => ({ ...f, [key]: e.target.value }))}
        placeholder={ph || ""}
        style={{ width: "100%", minHeight: 54, fontFamily: FF, fontSize: 12, color: UI.text,
          background: T === "dark" ? "#1a1a1a" : "#f8f8f8", border: `1px solid ${UI.bdr}`,
          borderRadius: 8, padding: "6px 8px", resize: "vertical", boxSizing: "border-box" }} />
    </div>
  );

  return (
    <div className="dp-body">
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
        <div>
          <div style={{ fontFamily: FF, fontSize: 14, fontWeight: 700, color: UI.text }}>Post-Show Report</div>
          {avgRating
            ? <div style={{ fontFamily: FF, fontSize: 11, color: UI.textMut, marginTop: 2 }}>Avg {avgRating}/5 · {rpts.length} response{rpts.length !== 1 ? "s" : ""}</div>
            : <div style={{ fontFamily: FF, fontSize: 11, color: UI.textVFnt, marginTop: 2 }}>No member reports yet</div>}
        </div>
        {rpts.length > 0 && (
          <button onClick={() => {
            if (!show) return;
            const v = (venues || {})[show.venueId] || {};
            const lines = [`Post-Show Report: ${v.name || "Unknown Venue"} – ${fmtDate ? fmtDate(show.date) : show.date}`, ""];
            if (avgRating) lines.push(`Avg rating: ${avgRating}/5 (${rpts.length} response${rpts.length !== 1 ? "s" : ""})`);
            lines.push("");
            if (myTm?.responses) {
              const rs = myTm.responses;
              lines.push("TM Notes:");
              if (rs.promoter_rating)      lines.push(`  Promoter communication: ${rs.promoter_rating}/5`);
              if (rs.deal_settled)         lines.push(`  Deal settled: ${rs.deal_settled}`);
              if (rs.deal_notes)           lines.push(`  Settlement notes: ${rs.deal_notes}`);
              if (rs.venue_recommendation) lines.push(`  Venue recommendation: ${rs.venue_recommendation}`);
              if (rs.hospitality_notes)    lines.push(`  Hospitality notes: ${rs.hospitality_notes}`);
              lines.push("");
            }
            bandRpts.forEach((r, i) => {
              const p  = (profiles || []).find(px => px.id === r.profile_id);
              const rs = r.responses || {};
              lines.push(`Band – ${p?.name || `Member ${i+1}`}:`);
              if (rs.overall_rating) lines.push(`  Show rating: ${rs.overall_rating}/5`);
              if (rs.monitor_mix)    lines.push(`  Monitor mix: ${rs.monitor_mix}`);
              if (rs.crowd_energy)   lines.push(`  Crowd energy: ${rs.crowd_energy}`);
              if (rs.went_wrong)     lines.push(`  Went wrong: ${rs.went_wrong}`);
              if (rs.went_well)      lines.push(`  Went well: ${rs.went_well}`);
              if (rs.play_again)     lines.push(`  Play here again: ${rs.play_again}`);
              if (rs.notes)          lines.push(`  Notes: ${rs.notes}`);
              lines.push("");
            });
            crewRpts.forEach((r, i) => {
              const p  = (profiles || []).find(px => px.id === r.profile_id);
              const rs = r.responses || {};
              lines.push(`Crew – ${p?.name || `Member ${i+1}`}:`);
              if (rs.overall_rating)    lines.push(`  Venue rating: ${rs.overall_rating}/5`);
              if (rs.load_in_ease)      lines.push(`  Load-in ease: ${rs.load_in_ease}`);
              if (rs.staff_helpfulness) lines.push(`  Staff: ${rs.staff_helpfulness}`);
              if (rs.issues)            lines.push(`  Issues: ${rs.issues}`);
              if (rs.notes)             lines.push(`  Notes: ${rs.notes}`);
              lines.push("");
            });
            const subj = encodeURIComponent(`Post-Show Report: ${v.name || "Show"} – ${fmtDate ? fmtDate(show.date) : show.date}`);
            const body = encodeURIComponent(lines.join("\n"));
            window.open(`mailto:?subject=${subj}&body=${body}`);
          }}
            style={{ fontFamily: FF, fontSize: 11, padding: "5px 12px", borderRadius: 8,
              border: `1px solid ${UI.bdr}`, background: "none", color: UI.textMut, cursor: "pointer",
              display: "inline-flex", alignItems: "center", gap: 5 }}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
            Send to Manager
          </button>
        )}
      </div>

      {/* TM form */}
      <div style={{ marginBottom: 20, padding: "14px 16px", background: T === "dark" ? "#141414" : "#f4f4f4", borderRadius: 8, border: `1px solid ${UI.bdr}` }}>
        <div style={{ fontFamily: FF, fontSize: 11, fontWeight: 700, color: UI.amber, letterSpacing: 1.2, marginBottom: 12, textTransform: "uppercase" }}>Your Notes (TM)</div>
        {starBtns("Promoter communication", "promoter_rating")}
        {pillBtns("Deal settled as expected?", "deal_settled", ["Yes", "No", "Partially"])}
        {txtArea("Settlement notes", "deal_notes", "Any discrepancies from the deal?")}
        {pillBtns("Venue recommendation", "venue_recommendation", ["Recommend", "Neutral", "Avoid"])}
        {txtArea("Hospitality vs. advance accuracy", "hospitality_notes", "Was what was promised delivered?")}
        {txtArea("Private notes (not shared)", "venue_notes", "Internal notes for future reference…")}
        <button onClick={async () => {
          if (!show) return;
          setTmSaving(true);
          const ok = await saveShowReport({ showId: show.id, tourId, profileId: null, roleCategory: "tm", responses: tmForm });
          setTmSaving(false);
          if (ok) { setTmSaved(true); setTimeout(() => setTmSaved(false), 2500); }
        }} disabled={tmSaving}
          style={{ fontFamily: FF, fontSize: 11, padding: "5px 14px", borderRadius: 8,
            background: tmSaved ? "var(--success)" : UI.amber, color: "#fff", border: "none", cursor: "pointer", fontWeight: 600, marginTop: 4 }}>
          {tmSaving ? "Saving…" : tmSaved ? "Saved" : myTm ? "Update Notes" : "Save Notes"}
        </button>
      </div>

      {/* Band reports */}
      {bandRpts.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontFamily: FF, fontSize: 11, fontWeight: 700, color: UI.textVFnt, letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 8 }}>Band ({bandRpts.length})</div>
          {bandRpts.map(r => {
            const p  = (profiles || []).find(px => px.id === r.profile_id);
            const rs = r.responses || {};
            return (
              <div key={r.id} style={{ marginBottom: 10, padding: "12px 14px", background: T === "dark" ? "#141414" : "#f4f4f4", borderRadius: 8, border: `1px solid ${UI.bdr}` }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                  <div style={{ fontFamily: FF, fontSize: 12, fontWeight: 700, color: UI.text }}>{p?.name || "Band Member"}</div>
                  {rs.overall_rating && <div style={{ fontFamily: FF, fontSize: 11, color: UI.amber, fontWeight: 700 }}>{rs.overall_rating}/5</div>}
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "3px 12px", marginBottom: 4 }}>
                  {rs.monitor_mix  && <div style={{ fontFamily: FF, fontSize: 11 }}><span style={{ color: UI.textMut }}>Monitor: </span><span style={{ color: UI.text }}>{rs.monitor_mix}</span></div>}
                  {rs.crowd_energy && <div style={{ fontFamily: FF, fontSize: 11 }}><span style={{ color: UI.textMut }}>Crowd: </span><span style={{ color: UI.text }}>{rs.crowd_energy}</span></div>}
                  {rs.play_again   && <div style={{ fontFamily: FF, fontSize: 11 }}><span style={{ color: UI.textMut }}>Play again: </span><span style={{ color: UI.text }}>{rs.play_again}</span></div>}
                </div>
                {rs.went_wrong && <div style={{ fontFamily: FF, fontSize: 11, marginTop: 4 }}><span style={{ color: UI.textMut }}>Went wrong: </span><span style={{ color: "#c44" }}>{rs.went_wrong}</span></div>}
                {rs.went_well  && <div style={{ fontFamily: FF, fontSize: 11, marginTop: 3 }}><span style={{ color: UI.textMut }}>Went well: </span><span style={{ color: "#3a7a3a" }}>{rs.went_well}</span></div>}
                {rs.notes      && <div style={{ fontFamily: FF, fontSize: 11, color: UI.textMut, marginTop: 3, fontStyle: "italic" }}>{rs.notes}</div>}
              </div>
            );
          })}
        </div>
      )}

      {/* Crew reports */}
      {crewRpts.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontFamily: FF, fontSize: 11, fontWeight: 700, color: UI.textVFnt, letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 8 }}>Crew ({crewRpts.length})</div>
          {crewRpts.map(r => {
            const p  = (profiles || []).find(px => px.id === r.profile_id);
            const rs = r.responses || {};
            return (
              <div key={r.id} style={{ marginBottom: 10, padding: "12px 14px", background: T === "dark" ? "#141414" : "#f4f4f4", borderRadius: 8, border: `1px solid ${UI.bdr}` }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                  <div style={{ fontFamily: FF, fontSize: 12, fontWeight: 700, color: UI.text }}>{p?.name || "Crew Member"}</div>
                  {rs.overall_rating && <div style={{ fontFamily: FF, fontSize: 11, color: UI.amber, fontWeight: 700 }}>{rs.overall_rating}/5</div>}
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "3px 12px", marginBottom: 4 }}>
                  {rs.load_in_ease      && <div style={{ fontFamily: FF, fontSize: 11 }}><span style={{ color: UI.textMut }}>Load-in: </span><span style={{ color: UI.text }}>{rs.load_in_ease}</span></div>}
                  {rs.staff_helpfulness && <div style={{ fontFamily: FF, fontSize: 11 }}><span style={{ color: UI.textMut }}>Staff: </span><span style={{ color: UI.text }}>{rs.staff_helpfulness}</span></div>}
                </div>
                {rs.issues && <div style={{ fontFamily: FF, fontSize: 11, marginTop: 4 }}><span style={{ color: UI.textMut }}>Issues: </span><span style={{ color: "#c44" }}>{rs.issues}</span></div>}
                {rs.notes  && <div style={{ fontFamily: FF, fontSize: 11, color: UI.textMut, marginTop: 3, fontStyle: "italic" }}>{rs.notes}</div>}
              </div>
            );
          })}
        </div>
      )}

      {/* Empty state */}
      {rpts.length === 0 && (
        <div style={{ textAlign: "center", padding: "32px 0", opacity: 0.35 }}>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ display: "block", margin: "0 auto 8px" }}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
          <div style={{ fontFamily: FF, fontSize: 12, color: UI.textVFnt }}>No reports yet.<br />Band and crew submit from mobile after the show.</div>
        </div>
      )}
    </div>
  );
}

// ── PhoneInput ────────────────────────────────────────────────────────────
// Renders an editable country-code box (+1 by default) + xxx.xxx.xxxx local.
// Stores the combined value as "+1 555.867.5309" (or "" if empty).
function PhoneInput({ value = "", onChange, className = "", inputStyle = {}, placeholder = "555.867.5309" }) {
  const fmtLocal = (raw) => {
    const d = (raw || "").replace(/\D/g, "").slice(0, 10);
    if (!d) return "";
    if (d.length <= 3) return d;
    if (d.length <= 6) return `${d.slice(0,3)}.${d.slice(3)}`;
    return `${d.slice(0,3)}.${d.slice(3,6)}.${d.slice(6)}`;
  };
  const parse = (v) => {
    const s = (v || "").trim();
    const m = s.match(/^(\+\d{1,4})\s*(.*)/);
    if (m) return { cc: m[1], local: fmtLocal(m[2]) };
    return { cc: "+1", local: fmtLocal(s) };
  };
  const [cc,    setCc]    = useState(() => parse(value).cc);
  const [local, setLocal] = useState(() => parse(value).local);
  const emit = (newCc, newLocal) => onChange(newLocal ? `${newCc} ${newLocal}` : "");
  return (
    <div style={{ display: "flex", gap: 4 }}>
      <input
        className={className}
        value={cc}
        onChange={e => { setCc(e.target.value); emit(e.target.value, local); }}
        style={{ ...inputStyle, width: 40, minWidth: 40, flexShrink: 0, textAlign: "center" }}
        placeholder="+1"
        maxLength={6}
      />
      <input
        className={className}
        value={local}
        onChange={e => { const fmt = fmtLocal(e.target.value); setLocal(fmt); emit(cc, fmt); }}
        style={{ ...inputStyle, flex: 1, width: "auto", minWidth: 0 }}
        placeholder={placeholder}
      />
    </div>
  );
}

// ── Contact Modal ──────────────────────────────────────────────────────────
const CONTACT_ROLES = ["", "Promoter", "Production Manager", "Tour Manager", "Press / Publicist", "Label Rep", "Venue Manager", "Agent", "Manager", "Vendor / Supplier", "Other"];

function ContactModal({ existing, onSave, onClose }) {
  const blank = { name: "", company: "", role: "", email: "", phone: "", notes: "" };
  const [form, setForm] = useState(existing ? { ...existing } : blank);
  const [confirmDel, setConfirmDel] = useState(false);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const inp = (k, label, opts = {}) => (
    <div className="mo-fld">
      <div className="mo-lbl">{label}</div>
      {opts.textarea
        ? <textarea className="mo-inp" rows={3} style={{ resize: "vertical" }} value={form[k]||""} onChange={e => set(k, e.target.value)} />
        : opts.select
          ? <select className="mo-sel" value={form[k]||""} onChange={e => set(k, e.target.value)}>
              {CONTACT_ROLES.map(r => <option key={r} value={r}>{r || "— Select role —"}</option>)}
            </select>
          : <input className="mo-inp" value={form[k]||""} onChange={e => set(k, e.target.value)} autoFocus={opts.focus} />}
    </div>
  );
  return (
    <div className="ov" onClick={onClose}>
      <div className="mo" onClick={e => e.stopPropagation()}>
        <div className="mo-title">{existing ? "Edit Contact" : "Add Contact"}</div>
        <div className="mo-sub">Global Address Book</div>
        {inp("name", "Name *", { focus: true })}
        <div className="mo-row">{inp("company", "Company / Organization")}{inp("role", "Role", { select: true })}</div>
        <div className="mo-row">{inp("email", "Email")}<div className="mo-fld"><div className="mo-lbl">Phone</div><PhoneInput className="mo-inp" value={form.phone||""} onChange={v => set("phone", v)} /></div></div>
        <div className="mo-section-lbl" style={{ marginTop: 14 }}>Notes</div>
        {inp("notes", "Notes", { textarea: true })}

        {existing && !confirmDel && (
          <div style={{ marginTop: 18 }}>
            <button className="btn-d" onClick={() => setConfirmDel(true)}>Delete Contact</button>
          </div>
        )}
        {existing && confirmDel && (
          <div className="mo-del-confirm" style={{ marginTop: 18 }}>
            <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: "var(--danger)", marginBottom: 12 }}>Delete {form.name}? You can undo this for a few seconds afterward.</div>
            <div style={{ display: "flex", gap: 8 }}>
              <button className="btn-g" onClick={() => setConfirmDel(false)}>Cancel</button>
              <button className="btn-d" onClick={() => onSave(null)}>Yes, Delete</button>
            </div>
          </div>
        )}
        <div className="mo-acts">
          <button className="btn-g" onClick={onClose}>Cancel</button>
          <button className="btn-p" disabled={!form.name?.trim()} onClick={() => onSave(form)}>
            {existing ? "Save Changes" : "Add Contact"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Contact bulk parser ───────────────────────────────────────────────────────
async function parseContactsWithAI(text) {
  const prompt = `You are extracting contact entries from pasted text, a CSV, or a list. Each contact should have:
- "name": full name (string, required)
- "company": company or organization (string or null)
- "role": job title or role (string or null)
- "email": email address (string or null)
- "phone": phone number (string or null)
- "notes": any other relevant info (string or null)

Return ONLY a valid JSON array with no markdown fences or explanation. If no contacts are found, return [].

Text to parse:
${text}`;
  const res = await fetch("/api/anthropic", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ model: "claude-haiku-4-5-20251001", max_tokens: 2000, messages: [{ role: "user", content: prompt }] }),
  });
  if (!res.ok) throw new Error(`API error ${res.status}`);
  const d = await res.json();
  const raw = d.content?.[0]?.text || "[]";
  try { return JSON.parse(raw.replace(/```json|```/g, "").trim()); }
  catch { return []; }
}

// ── Setlist bulk import — AI parse of a pasted song list ─────────────────────
async function parseSetlistWithAI(text) {
  const prompt = `You are extracting a band's setlist from pasted text. Each line is usually one song, but formats vary. For each song extract:
- "title": the song title (string, required)
- "song_key": musical key if present, e.g. "C", "Am", "F#" (string or null)
- "tempo": tempo in BPM as a number if present (number or null)
- "tuning": guitar/instrument tuning if present, e.g. "Standard", "Drop D", "Eb" (string or null)
- "notes": any other info such as segues, intro cues, or "acoustic" (string or null)

Ignore set headers like "SET 1", "Encore", track numbering (e.g. "1.", "01 -"), and blank lines. Keep the songs in the order given.

Return ONLY a valid JSON array with no markdown fences or explanation. If no songs are found, return [].

Text to parse:
${text}`;
  const res = await fetch("/api/anthropic", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ model: "claude-haiku-4-5-20251001", max_tokens: 3000, messages: [{ role: "user", content: prompt }] }),
  });
  if (!res.ok) throw new Error(`API error ${res.status}`);
  const d = await res.json();
  const raw = d.content?.[0]?.text || "[]";
  try { return JSON.parse(raw.replace(/```json|```/g, "").trim()); }
  catch { return []; }
}

// ── Setlist Import Modal — paste/drop a whole song list ──────────────────────
function SetlistImportModal({ onSave, onClose }) {
  const FF = "'Helvetica Neue', Helvetica, Arial, sans-serif";
  const [text,       setText]       = useState("");
  const [loading,    setLoading]    = useState(false);
  const [parsed,     setParsed]     = useState(null);
  const [parseError, setParseError] = useState("");
  const [dragging,   setDragging]   = useState(false);
  const fileRef = useRef(null);

  const handleFile = async (file) => {
    setParseError("");
    const name = file.name.toLowerCase();
    try {
      let content = "";
      if (name.endsWith(".csv") || name.endsWith(".tsv") || name.endsWith(".txt")) {
        content = await file.text();
      } else if (name.endsWith(".xlsx") || name.endsWith(".xls")) {
        const XLSX = (await import("xlsx")).default;
        const wb = XLSX.read(await file.arrayBuffer(), { type: "array" });
        const ws = wb.Sheets[wb.SheetNames[0]];
        content = XLSX.utils.sheet_to_csv(ws);
      } else { setParseError("Unsupported format. Use CSV, TSV, TXT, or Excel."); return; }
      setText(content);
      await doParse(content);
    } catch (e) { setParseError("Could not read file: " + e.message); }
  };

  const doParse = async (src) => {
    const input = src ?? text;
    if (!input.trim()) return;
    setLoading(true); setParseError(""); setParsed(null);
    try {
      const results = await parseSetlistWithAI(input);
      if (!results.length) setParseError("No songs found. Try a different format.");
      else setParsed(results.map((s, i) => ({ ...s, _id: i, _remove: false })));
    } catch { setParseError("Parsing failed. Please try again."); }
    setLoading(false);
  };

  const keep = parsed ? parsed.filter(s => !s._remove && (s.title || "").trim()) : [];

  return (
    <div className="ov" onClick={onClose}>
      <div className="mo" style={{ width: 640, maxWidth: "94vw", height: 540, display: "flex", flexDirection: "column" }} onClick={e => e.stopPropagation()}>
        <div className="mo-title">Import Setlist</div>
        <div className="mo-sub">PASTE OR DROP A SONG LIST</div>

        {!parsed ? (
          <div style={{ display: "flex", flexDirection: "column", flex: 1, marginTop: 14 }}>
            <div style={{ position: "relative" }}
              onDragOver={e => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={e => { e.preventDefault(); setDragging(false); const f = e.dataTransfer.files[0]; if (f) handleFile(f); }}>
              <textarea className="mo-inp" rows={9}
                autoFocus
                value={text}
                onChange={e => setText(e.target.value)}
                placeholder={"Paste your setlist — any format works:\n\nOpener — C — 120\nSecond Song (Drop D)\nBig Single, key of G, 92 bpm\nEncore: Closer — segue into outro\n\nOr drop a TXT / CSV / Excel file anywhere on this box."}
                style={{ resize: "vertical", lineHeight: 1.6 }}
              />
              {dragging && (
                <div style={{ position: "absolute", inset: 0, background: "#f0a50018", border: "2px solid var(--accent)", borderRadius: 4, display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
                  <span style={{ fontFamily: FF, fontSize: 14, color: "var(--accent)" }}>Drop file to import</span>
                </div>
              )}
            </div>
            <input ref={fileRef} type="file" accept=".csv,.tsv,.txt,.xlsx,.xls" style={{ display: "none" }} onChange={e => { if (e.target.files[0]) handleFile(e.target.files[0]); }} />
            {parseError && <div style={{ fontFamily: FF, fontSize: 11, color: "#c84b4b", marginTop: 8 }}>{parseError}</div>}
            <div className="mo-acts" style={{ marginTop: "auto" }}>
              <button className="btn-g" onClick={() => fileRef.current?.click()}>Upload File</button>
              <button className="btn-g" onClick={onClose}>Cancel</button>
              <button className="btn-p" disabled={!text.trim() || loading} onClick={() => doParse()}>
                {loading ? "Parsing…" : "✦ Parse Setlist"}
              </button>
            </div>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", flex: 1, marginTop: 14 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
              <span style={{ fontFamily: FF, fontSize: 11, color: "var(--accent)", fontWeight: 600 }}>{keep.length} song{keep.length !== 1 ? "s" : ""} found — review before adding</span>
              <button onClick={() => { setParsed(null); setParseError(""); }} style={{ fontFamily: FF, fontSize: 11, color: "var(--text-mut)", background: "none", border: "1px solid var(--panel-border)", borderRadius: 4, padding: "2px 10px", cursor: "pointer" }}>← Back</button>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "20px 1.6fr 52px 52px 90px 20px", gap: 6, padding: "0 2px 6px", fontFamily: FF, fontSize: 11, letterSpacing: 1, color: "var(--text-mid)", textTransform: "uppercase" }}>
              <span>#</span><span>Title</span><span>Key</span><span>BPM</span><span>Tuning</span><span></span>
            </div>
            <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: 4 }}>
              {parsed.map((s, i) => (
                <div key={s._id} style={{ display: "grid", gridTemplateColumns: "20px 1.6fr 52px 52px 90px 20px", gap: 6, alignItems: "center", opacity: s._remove ? 0.35 : 1 }}>
                  <span style={{ fontFamily: FF, fontSize: 11, color: "var(--text-mut)", textAlign: "right" }}>{i + 1}</span>
                  <input className="f-inp" placeholder="Song title *" value={s.title || ""} onChange={ev => setParsed(p => p.map((x, j) => j === i ? { ...x, title: ev.target.value } : x))} />
                  <input className="f-inp" placeholder="C" value={s.song_key || ""} onChange={ev => setParsed(p => p.map((x, j) => j === i ? { ...x, song_key: ev.target.value } : x))} />
                  <input className="f-inp" placeholder="120" value={s.tempo ?? ""} onChange={ev => setParsed(p => p.map((x, j) => j === i ? { ...x, tempo: ev.target.value === "" ? null : Number(ev.target.value) } : x))} />
                  <input className="f-inp" placeholder="Standard" value={s.tuning || ""} onChange={ev => setParsed(p => p.map((x, j) => j === i ? { ...x, tuning: ev.target.value } : x))} />
                  <button onClick={() => setParsed(p => p.map((x, j) => j === i ? { ...x, _remove: !x._remove } : x))} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-mut)", fontSize: 14, padding: 0 }}>{s._remove ? "↩" : "×"}</button>
                </div>
              ))}
            </div>
            <div className="mo-acts" style={{ marginTop: "auto" }}>
              <button className="btn-g" onClick={onClose}>Cancel</button>
              <button className="btn-p" disabled={!keep.length} onClick={() => {
                onSave(keep.map(s => ({ title: s.title.trim(), song_key: s.song_key || "", tempo: (s.tempo === "" || s.tempo == null) ? null : Number(s.tempo), tuning: s.tuning || "", notes: s.notes || "" })));
                onClose();
              }}>
                Add {keep.length} Song{keep.length !== 1 ? "s" : ""}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Add Contacts Modal — Multiple (paste/drop) + Single tab ──────────────────
function AddContactsModal({ onSave, onClose }) {
  const FF = "'Helvetica Neue', Helvetica, Arial, sans-serif";
  const [tab, setTab] = useState("import");

  // ── Import state ──
  const [text,       setText]       = useState("");
  const [loading,    setLoading]    = useState(false);
  const [parsed,     setParsed]     = useState(null);
  const [parseError, setParseError] = useState("");
  const [dragging,   setDragging]   = useState(false);
  const fileRef = useRef(null);

  const handleFile = async (file) => {
    setParseError("");
    const name = file.name.toLowerCase();
    try {
      let content = "";
      if (name.endsWith(".csv") || name.endsWith(".tsv") || name.endsWith(".txt")) {
        content = await file.text();
      } else if (name.endsWith(".xlsx") || name.endsWith(".xls")) {
        const XLSX = (await import("xlsx")).default;
        const wb = XLSX.read(await file.arrayBuffer(), { type: "array" });
        const ws = wb.Sheets[wb.SheetNames[0]];
        content = XLSX.utils.sheet_to_csv(ws);
      } else { setParseError("Unsupported format. Use CSV, TSV, TXT, or Excel."); return; }
      setText(content);
      await doParse(content);
    } catch (e) { setParseError("Could not read file: " + e.message); }
  };

  const doParse = async (src) => {
    const input = src ?? text;
    if (!input.trim()) return;
    setLoading(true); setParseError(""); setParsed(null);
    try {
      const results = await parseContactsWithAI(input);
      if (!results.length) setParseError("No contacts found. Try a different format.");
      else setParsed(results.map((c, i) => ({ ...c, _id: i, _remove: false })));
    } catch { setParseError("Parsing failed. Please try again."); }
    setLoading(false);
  };

  // ── Single contact state ──
  const blank = { name: "", company: "", role: "", email: "", phone: "", notes: "" };
  const [form, setForm] = useState(blank);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const tabStyle = (id) => ({
    fontFamily: FF, fontSize: 11, padding: "7px 18px", cursor: "pointer",
    background: "none", border: "none", borderBottom: `1px solid ${tab === id ? "var(--accent)" : "transparent"}`,
    color: tab === id ? "var(--accent)" : "#666", fontWeight: tab === id ? 700 : 400,
    marginBottom: -1, transition: "color 0.12s, border-color 0.12s",
  });

  return (
    <div className="ov" onClick={onClose}>
      <div className="mo" style={{ width: 600, maxWidth: "94vw", height: 520, display: "flex", flexDirection: "column" }} onClick={e => e.stopPropagation()}>
        <div className="mo-title">Add Contacts</div>
        <div className="mo-sub">GLOBAL ADDRESS BOOK</div>

        {/* Tab bar */}
        <div style={{ display: "flex", borderBottom: "1px solid #333", marginBottom: 20, marginTop: -4 }}>
          {[["import", "Multiple Contacts"], ["single", "Single Contact"]].map(([id, label]) => (
            <button key={id} style={tabStyle(id)} onClick={() => setTab(id)}>
              <span style={{ display: "block", height: 0, overflow: "hidden", fontWeight: 700, visibility: "hidden" }} aria-hidden="true">{label}</span>
              {label}
            </button>
          ))}
        </div>

        {/* ── Multiple tab ── */}
        {tab === "import" && (
          <div style={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden" }}>
            {!parsed ? (
              <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
                {/* Drop zone + textarea */}
                <div style={{ position: "relative" }}
                  onDragOver={e => { e.preventDefault(); setDragging(true); }}
                  onDragLeave={() => setDragging(false)}
                  onDrop={e => { e.preventDefault(); setDragging(false); const f = e.dataTransfer.files[0]; if (f) handleFile(f); }}>
                  <textarea className="mo-inp" rows={8}
                    autoFocus
                    value={text}
                    onChange={e => setText(e.target.value)}
                    placeholder={"Paste contacts here — any format works:\n\nJane Smith, Booking Agent, CAA, jane@caa.com, 615-555-0100\nBob Jones | Promoter | bob@venue.com\n\nOr drop a CSV / Excel file anywhere on this box."}
                    style={{ resize: "vertical", lineHeight: 1.6 }}
                  />
                  {dragging && (
                    <div style={{ position: "absolute", inset: 0, background: "#f0a50018", border: "2px solid var(--accent)", borderRadius: 4, display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
                      <span style={{ fontFamily: FF, fontSize: 14, color: "var(--accent)" }}>Drop file to import</span>
                    </div>
                  )}
                </div>
                <input ref={fileRef} type="file" accept=".csv,.tsv,.txt,.xlsx,.xls" style={{ display: "none" }} onChange={e => { if (e.target.files[0]) handleFile(e.target.files[0]); }} />
                {parseError && <div style={{ fontFamily: FF, fontSize: 11, color: "#c84b4b", marginTop: 8 }}>{parseError}</div>}
                <div className="mo-acts" style={{ marginTop: "auto" }}>
                  <button className="btn-g" onClick={() => fileRef.current?.click()}>Upload File</button>
                  <button className="btn-g" onClick={onClose}>Cancel</button>
                  <button className="btn-p" disabled={!text.trim() || loading} onClick={() => doParse()}>
                    {loading ? "Parsing…" : "✦ Parse Contacts"}
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
                  <span style={{ fontFamily: FF, fontSize: 11, color: "var(--accent)", fontWeight: 600 }}>{parsed.filter(c => !c._remove).length} contacts found — review before saving</span>
                  <button onClick={() => { setParsed(null); setParseError(""); }} style={{ fontFamily: FF, fontSize: 11, color: "var(--text-mut)", background: "none", border: "1px solid var(--panel-border)", borderRadius: 4, padding: "2px 10px", cursor: "pointer" }}>← Back</button>
                </div>
                <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: 5 }}>
                  {parsed.map((c, i) => (
                    <div key={c._id} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 20px", gap: 5, opacity: c._remove ? 0.35 : 1 }}>
                      <input className="f-inp" placeholder="Name *" value={c.name || ""} onChange={ev => setParsed(p => p.map((x, j) => j === i ? { ...x, name: ev.target.value } : x))} />
                      <input className="f-inp" placeholder="Company" value={c.company || ""} onChange={ev => setParsed(p => p.map((x, j) => j === i ? { ...x, company: ev.target.value } : x))} />
                      <input className="f-inp" placeholder="Email" value={c.email || ""} onChange={ev => setParsed(p => p.map((x, j) => j === i ? { ...x, email: ev.target.value } : x))} />
                      <button onClick={() => setParsed(p => p.map((x, j) => j === i ? { ...x, _remove: !x._remove } : x))} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-mut)", fontSize: 14, padding: 0 }}>{c._remove ? "↩" : "×"}</button>
                    </div>
                  ))}
                </div>
                <div className="mo-acts" style={{ marginTop: "auto" }}>
                  <button className="btn-g" onClick={onClose}>Cancel</button>
                  <button className="btn-p" disabled={!parsed.some(c => !c._remove && c.name?.trim())} onClick={() => {
                    parsed.filter(c => !c._remove && c.name?.trim()).forEach(c => onSave({ name: c.name, company: c.company || "", role: c.role || "", email: c.email || "", phone: c.phone || "", notes: c.notes || "" }));
                    onClose();
                  }}>
                    Save {parsed.filter(c => !c._remove && c.name?.trim()).length} Contact{parsed.filter(c => !c._remove && c.name?.trim()).length !== 1 ? "s" : ""}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Single Contact tab ── */}
        {tab === "single" && (
          <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
            <div style={{ flex: 1, overflowY: "auto" }}>
              <div className="mo-fld"><div className="mo-lbl">Name *</div><input className="mo-inp" value={form.name} onChange={e => set("name", e.target.value)} autoFocus /></div>
              <div className="mo-row">
                <div className="mo-fld"><div className="mo-lbl">Company</div><input className="mo-inp" value={form.company} onChange={e => set("company", e.target.value)} /></div>
                <div className="mo-fld"><div className="mo-lbl">Role</div>
                  <select className="mo-sel" value={form.role} onChange={e => set("role", e.target.value)}>
                    {CONTACT_ROLES.map(r => <option key={r} value={r}>{r || "— Select role —"}</option>)}
                  </select>
                </div>
              </div>
              <div className="mo-row">
                <div className="mo-fld"><div className="mo-lbl">Email</div><input className="mo-inp" value={form.email} onChange={e => set("email", e.target.value)} /></div>
                <div className="mo-fld"><div className="mo-lbl">Phone</div><PhoneInput className="mo-inp" value={form.phone} onChange={v => set("phone", v)} /></div>
              </div>
              <div className="mo-fld"><div className="mo-lbl">Notes</div><textarea className="mo-inp" rows={2} style={{ resize: "vertical" }} value={form.notes} onChange={e => set("notes", e.target.value)} /></div>
            </div>
            <div className="mo-acts">
              <button className="btn-g" onClick={onClose}>Cancel</button>
              <button className="btn-p" disabled={!form.name?.trim()} onClick={() => { onSave(form); onClose(); }}>Add Contact</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Add Venue/Festival Modal — Single tab + Multiple (CSV import) tab ────────
// ── Venue/Festival bulk parser ────────────────────────────────────────────────
async function parseVenuesWithAI(text, isFestival) {
  const type = isFestival ? "festival" : "venue";
  const prompt = `You are extracting ${type} entries from pasted text, a CSV, or a list. Each entry should have:
- "name": ${type} name (string, required)
- "city": city (string or null)
- "addressState": state or province abbreviation (string or null)
- "addressStreet": street address (string or null)
- "capacity": capacity as a number (integer or null)
- "promoterName": primary contact / promoter name (string or null)
- "promoterEmail": promoter email (string or null)
- "promoterPhone": promoter phone (string or null)
- "website": website URL (string or null)

Return ONLY a valid JSON array with no markdown fences or explanation. If no entries are found, return [].

Text to parse:
${text}`;
  const res = await fetch("/api/anthropic", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ model: "claude-haiku-4-5-20251001", max_tokens: 2000, messages: [{ role: "user", content: prompt }] }),
  });
  if (!res.ok) throw new Error(`API error ${res.status}`);
  const d = await res.json();
  const raw = d.content?.[0]?.text || "[]";
  try { return JSON.parse(raw.replace(/```json|```/g, "").trim()); }
  catch { return []; }
}

// ── Add Venue/Festival Modal — Multiple (paste/drop) + Single tab ─────────────
function AddVenueModal({ kind = "venue", onSave, onClose }) {
  const FF = "'Helvetica Neue', Helvetica, Arial, sans-serif";
  const [tab, setTab] = useState("import");
  const isFestival = kind === "festival";
  const label = isFestival ? "Festival" : "Venue";

  // ── Single form state ──
  const [sForm, setSForm] = useState({ name: "", city: "", addressState: "", capacity: "", promoterName: "", promoterEmail: "", promoterPhone: "" });
  const sSet = (k, v) => setSForm(p => ({ ...p, [k]: v }));

  // ── Import state ──
  const [text,       setText]       = useState("");
  const [loading,    setLoading]    = useState(false);
  const [parsed,     setParsed]     = useState(null);
  const [parseError, setParseError] = useState("");
  const [dragging,   setDragging]   = useState(false);
  const fileRef = useRef(null);

  const handleFile = async (file) => {
    setParseError("");
    const name = file.name.toLowerCase();
    try {
      let content = "";
      if (name.endsWith(".csv") || name.endsWith(".tsv") || name.endsWith(".txt")) {
        content = await file.text();
      } else if (name.endsWith(".xlsx") || name.endsWith(".xls")) {
        const XLSX = (await import("xlsx")).default;
        const wb = XLSX.read(await file.arrayBuffer(), { type: "array" });
        const ws = wb.Sheets[wb.SheetNames[0]];
        content = XLSX.utils.sheet_to_csv(ws);
      } else { setParseError("Unsupported format. Use CSV, TSV, TXT, or Excel."); return; }
      setText(content);
      await doParse(content);
    } catch (e) { setParseError("Could not read file: " + e.message); }
  };

  const doParse = async (src) => {
    const input = src ?? text;
    if (!input.trim()) return;
    setLoading(true); setParseError(""); setParsed(null);
    try {
      const results = await parseVenuesWithAI(input, isFestival);
      if (!results.length) setParseError("No entries found. Try a different format.");
      else setParsed(results.map((v, i) => ({ ...v, _id: i, _remove: false })));
    } catch { setParseError("Parsing failed. Please try again."); }
    setLoading(false);
  };

  const tabStyle = (id) => ({
    fontFamily: FF, fontSize: 11, padding: "7px 18px", cursor: "pointer",
    background: "none", border: "none", borderBottom: `1px solid ${tab === id ? "var(--accent)" : "transparent"}`,
    color: tab === id ? "var(--accent)" : "#666", fontWeight: tab === id ? 700 : 400,
    marginBottom: -1, transition: "color 0.12s, border-color 0.12s",
  });

  return (
    <div className="ov" onClick={onClose}>
      <div className="mo" style={{ width: 600, maxWidth: "94vw", height: 520, display: "flex", flexDirection: "column" }} onClick={e => e.stopPropagation()}>
        <div className="mo-title">Add {label}</div>
        <div className="mo-sub">{label.toUpperCase()}S</div>

        {/* Tab bar */}
        <div style={{ display: "flex", borderBottom: "1px solid #333", marginBottom: 20, marginTop: -4 }}>
          {[["import", `Multiple ${label}s`], ["single", `Single ${label}`]].map(([id, lbl]) => (
            <button key={id} style={tabStyle(id)} onClick={() => setTab(id)}>
              <span style={{ display: "block", height: 0, overflow: "hidden", fontWeight: 700, visibility: "hidden" }} aria-hidden="true">{lbl}</span>
              {lbl}
            </button>
          ))}
        </div>

        {/* ── Multiple tab ── */}
        {tab === "import" && (
          <div style={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden" }}>
            {!parsed ? (
              <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
                <div style={{ position: "relative" }}
                  onDragOver={e => { e.preventDefault(); setDragging(true); }}
                  onDragLeave={() => setDragging(false)}
                  onDrop={e => { e.preventDefault(); setDragging(false); const f = e.dataTransfer.files[0]; if (f) handleFile(f); }}>
                  <textarea className="mo-inp" rows={8} autoFocus
                    value={text}
                    onChange={e => setText(e.target.value)}
                    placeholder={`Paste ${label.toLowerCase()}s here — any format:\n\nThe Ryman Auditorium, Nashville, TN, 2362\nBowery Ballroom | New York, NY | 575\n\nOr drop a CSV / Excel file anywhere on this box.`}
                    style={{ resize: "vertical", lineHeight: 1.6 }}
                  />
                  {dragging && (
                    <div style={{ position: "absolute", inset: 0, background: "#f0a50018", border: "2px solid var(--accent)", borderRadius: 4, display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
                      <span style={{ fontFamily: FF, fontSize: 14, color: "var(--accent)" }}>Drop file to import</span>
                    </div>
                  )}
                </div>
                <input ref={fileRef} type="file" accept=".csv,.tsv,.txt,.xlsx,.xls" style={{ display: "none" }} onChange={e => { if (e.target.files[0]) handleFile(e.target.files[0]); }} />
                {parseError && <div style={{ fontFamily: FF, fontSize: 11, color: "#c84b4b", marginTop: 8 }}>{parseError}</div>}
                <div className="mo-acts" style={{ marginTop: "auto" }}>
                  <button className="btn-g" onClick={() => fileRef.current?.click()}>Upload File</button>
                  <button className="btn-g" onClick={onClose}>Cancel</button>
                  <button className="btn-p" disabled={!text.trim() || loading} onClick={() => doParse()}>
                    {loading ? "Parsing…" : "✦ Parse"}
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
                  <span style={{ fontFamily: FF, fontSize: 11, color: "var(--accent)", fontWeight: 600 }}>{parsed.filter(v => !v._remove).length} {label.toLowerCase()}s found — review before saving</span>
                  <button onClick={() => { setParsed(null); setParseError(""); }} style={{ fontFamily: FF, fontSize: 11, color: "var(--text-mut)", background: "none", border: "1px solid var(--panel-border)", borderRadius: 4, padding: "2px 10px", cursor: "pointer" }}>← Back</button>
                </div>
                <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: 5 }}>
                  {parsed.map((v, i) => (
                    <div key={v._id} style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 20px", gap: 5, opacity: v._remove ? 0.35 : 1 }}>
                      <input className="f-inp" placeholder="Name *" value={v.name || ""} onChange={ev => setParsed(p => p.map((x, j) => j === i ? { ...x, name: ev.target.value } : x))} />
                      <input className="f-inp" placeholder="City" value={v.city || ""} onChange={ev => setParsed(p => p.map((x, j) => j === i ? { ...x, city: ev.target.value } : x))} />
                      <input className="f-inp" placeholder="State" value={v.addressState || ""} onChange={ev => setParsed(p => p.map((x, j) => j === i ? { ...x, addressState: ev.target.value } : x))} />
                      <button onClick={() => setParsed(p => p.map((x, j) => j === i ? { ...x, _remove: !x._remove } : x))} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-mut)", fontSize: 14, padding: 0 }}>{v._remove ? "↩" : "×"}</button>
                    </div>
                  ))}
                </div>
                <div className="mo-acts" style={{ marginTop: "auto" }}>
                  <button className="btn-g" onClick={onClose}>Cancel</button>
                  <button className="btn-p" disabled={!parsed.some(v => !v._remove && v.name?.trim())} onClick={() => {
                    parsed.filter(v => !v._remove && v.name?.trim()).forEach(v => {
                      const { _id, _remove, ...rest } = v;
                      onSave({ ...rest, id: "v_" + uid(), kind });
                    });
                    onClose();
                  }}>
                    Save {parsed.filter(v => !v._remove && v.name?.trim()).length} {label}{parsed.filter(v => !v._remove && v.name?.trim()).length !== 1 ? "s" : ""}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Single tab — embedded form ── */}
        {tab === "single" && (
          <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
            <div style={{ flex: 1, overflowY: "auto" }}>
              <div className="mo-fld"><div className="mo-lbl">Name *</div><input className="mo-inp" autoFocus value={sForm.name} onChange={e => sSet("name", e.target.value)} placeholder={`e.g. ${isFestival ? "Bonnaroo Music & Arts Festival" : "The Ryman Auditorium"}`} /></div>
              <div style={{ display: "flex", gap: 8 }}>
                <div className="mo-fld" style={{ flex: 2 }}><div className="mo-lbl">City</div><input className="mo-inp" value={sForm.city} onChange={e => sSet("city", e.target.value)} placeholder="Nashville" /></div>
                <div className="mo-fld" style={{ flex: 1 }}><div className="mo-lbl">State</div><input className="mo-inp" value={sForm.addressState} onChange={e => sSet("addressState", e.target.value)} placeholder="TN" /></div>
                <div className="mo-fld" style={{ flex: 1 }}><div className="mo-lbl">Capacity</div><input className="mo-inp" value={sForm.capacity} onChange={e => sSet("capacity", e.target.value)} placeholder="2362" /></div>
              </div>
              <div className="mo-fld"><div className="mo-lbl">Promoter / Contact Name</div><input className="mo-inp" value={sForm.promoterName} onChange={e => sSet("promoterName", e.target.value)} /></div>
              <div style={{ display: "flex", gap: 8 }}>
                <div className="mo-fld" style={{ flex: 1 }}><div className="mo-lbl">Promoter Email</div><input className="mo-inp" value={sForm.promoterEmail} onChange={e => sSet("promoterEmail", e.target.value)} /></div>
                <div className="mo-fld" style={{ flex: 1 }}><div className="mo-lbl">Promoter Phone</div><input className="mo-inp" value={sForm.promoterPhone} onChange={e => sSet("promoterPhone", e.target.value)} /></div>
              </div>
            </div>
            <div className="mo-acts">
              <button className="btn-g" onClick={onClose}>Cancel</button>
              <button className="btn-p" disabled={!sForm.name?.trim()} onClick={() => {
                onSave({ ...sForm, id: "v_" + uid(), kind });
                onClose();
              }}>Add {label}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function VenueModal({ existing, kind: kindProp = "venue", contacts = [], onSave, onClose }) {
  const blank = Object.fromEntries(VF.map(f => [f.k, ""]));
  const [form, setForm] = useState(existing ? { ...existing, linkedContacts: existing.linkedContacts || [] } : { ...blank, kind: kindProp, linkedContacts: [] });
  const isFestival = (form.kind || kindProp) === "festival";
  const [confirmDel, setConfirmDel] = useState(false);
  const [addingLC, setAddingLC] = useState(false);
  const [lcPick, setLcPick] = useState({ contactId: "", role: "" });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const addLinkedContact = () => {
    if (!lcPick.contactId) return;
    set("linkedContacts", [...(form.linkedContacts || []), { ...lcPick }]);
    setLcPick({ contactId: "", role: "" });
    setAddingLC(false);
  };
  const removeLinkedContact = (idx) => set("linkedContacts", (form.linkedContacts || []).filter((_, i) => i !== idx));
  const FF = "'Helvetica Neue', Helvetica, Arial, sans-serif";
  const valid = form.name?.trim();
  const lbl = k => VF.find(f => f.k === k)?.l;
  const inp = (k, opts = {}) => (
    <div className="mo-fld">
      <div className="mo-lbl">{lbl(k)}</div>
      {opts.textarea
        ? <textarea className="mo-inp" rows={3} style={{ resize: "vertical" }} value={form[k]||""} onChange={e => set(k, e.target.value)} />
        : opts.select
          ? <select className="mo-sel" value={form[k]||""} onChange={e => set(k, e.target.value)}>
              {opts.options.map(o => <option key={o} value={o}>{o || "—"}</option>)}
            </select>
          : <input className="mo-inp" value={form[k]||""} onChange={e => set(k, e.target.value)} autoFocus={opts.focus} />}
    </div>
  );
  return (
    <div className="ov" onClick={onClose}>
      <div className="mo" onClick={e => e.stopPropagation()} style={{ maxHeight: "88vh", overflowY: "auto" }}>
        <div className="mo-title">{existing ? (isFestival ? "Edit Festival" : "Edit Venue") : (isFestival ? "Add Festival" : "Add Venue")}</div>
        <div className="mo-sub">{isFestival ? "Festivals" : "Venues"}</div>

        {/* Venue / Festival */}
        <div className="mo-section-lbl">{isFestival ? "Festival" : "Venue"}</div>
        {inp("name", { focus: true })}
        {inp("addressStreet")}
        <div className="mo-row">{inp("city")}{inp("addressState")}</div>
        <div className="mo-row">{inp("addressZip")}{inp("addressCountry")}</div>
        <div className="mo-row">
          {inp("capacity")}
          {inp("ages", { select: true, options: ["", "All Ages", "16+", "18+", "21+"] })}
        </div>
        {inp("gaSeated", { select: true, options: ["", "GA", "Seated", "GA Floor / Seated Balcony", "Mixed GA & Seated"] })}
        {inp("website")}

        {/* Promoter */}
        <div className="mo-section-lbl" style={{ marginTop: 18 }}>Promoter</div>
        {inp("promoterName")}
        {inp("promoterCompany")}
        <div className="mo-row">{inp("promoterEmail")}<div className="mo-fld"><div className="mo-lbl">{lbl("promoterPhone")}</div><PhoneInput className="mo-inp" value={form.promoterPhone||""} onChange={v => set("promoterPhone", v)} /></div></div>

        {/* Logistics */}
        <div className="mo-section-lbl" style={{ marginTop: 18 }}>Logistics</div>
        {inp("loadIn")}
        {inp("parking")}
        {inp("houseGear")}
        <div className="mo-row">{inp("greenRooms")}{inp("bathrooms")}</div>
        {inp("merchSplit", { select: true, options: ["", "0% / House keeps all", "10%", "15%", "20%", "25%", "30%"] })}

        {/* Linked Contacts */}
        {contacts.length > 0 && (<>
          <div className="mo-section-lbl" style={{ marginTop: 18 }}>Linked Contacts</div>
          {(form.linkedContacts || []).map((lc, i) => {
            const c = contacts.find(x => x.id === lc.contactId);
            if (!c) return null;
            return (
              <div key={lc.contactId} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "7px 10px", background: "var(--row-alt)", border: "1px solid var(--panel-border)", borderRadius: 8, marginBottom: 6 }}>
                <div>
                  <span style={{ fontFamily: FF, fontSize: 12, fontWeight: 600, color: "var(--text)" }}>{c.name}</span>
                  {lc.role && <span style={{ fontFamily: FF, fontSize: 11, color: "var(--accent)", marginLeft: 8, letterSpacing: "0.5px" }}>{lc.role}</span>}
                  {c.email && <span style={{ fontFamily: FF, fontSize: 11, color: "var(--text-mut)", marginLeft: 8 }}>{c.email}</span>}
                </div>
                <button onClick={() => removeLinkedContact(i)} style={{ background: "none", border: "none", color: "var(--text-mut)", cursor: "pointer", fontSize: 14, padding: "0 4px" }}>×</button>
              </div>
            );
          })}
          {addingLC ? (
            <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 4 }}>
              <select className="mo-sel" style={{ flex: 2, fontSize: 12 }} value={lcPick.contactId} onChange={e => setLcPick(p => ({ ...p, contactId: e.target.value }))}>
                <option value="">— Pick a contact —</option>
                {contacts.map(c => <option key={c.id} value={c.id}>{c.name}{c.company ? ` · ${c.company}` : ""}</option>)}
              </select>
              <select className="mo-sel" style={{ flex: 1, fontSize: 12 }} value={lcPick.role} onChange={e => setLcPick(p => ({ ...p, role: e.target.value }))}>
                {CONTACT_ROLES.map(r => <option key={r} value={r}>{r || "— Role —"}</option>)}
              </select>
              <button className="btn-p" style={{ padding: "5px 12px", fontSize: 11, flexShrink: 0 }} onClick={addLinkedContact}>Add</button>
              <button className="btn-g" style={{ padding: "5px 10px", fontSize: 11, flexShrink: 0 }} onClick={() => setAddingLC(false)}>✕</button>
            </div>
          ) : (
            <button className="btn-g" style={{ fontSize: 11, padding: "5px 12px", marginTop: 4 }} onClick={() => setAddingLC(true)}>+ Link Contact</button>
          )}
        </>)}

        {/* Notes */}
        <div className="mo-section-lbl" style={{ marginTop: 18 }}>Internal Notes</div>
        {inp("notes", { textarea: true })}

        {/* Delete */}
        {existing && !confirmDel && (
          <div style={{ marginTop: 22 }}>
            <button className="btn-d" onClick={() => setConfirmDel(true)}>{isFestival ? "Delete Festival" : "Delete Venue"}</button>
          </div>
        )}
        {existing && confirmDel && (
          <div className="mo-del-confirm" style={{ marginTop: 22 }}>
            <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: "var(--danger)", marginBottom: 12 }}>
              Delete {form.name}? You can undo this for a few seconds afterward.
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button className="btn-g" onClick={() => setConfirmDel(false)}>Cancel</button>
              <button className="btn-d" onClick={() => onSave(null)}>Yes, Delete</button>
            </div>
          </div>
        )}

        <div className="mo-acts">
          <button className="btn-g" onClick={onClose}>Cancel</button>
          <button className="btn-p" disabled={!valid} onClick={() => onSave({ ...form, kind: form.kind || kindProp || "venue" })}>
            {existing ? "Save Changes" : (isFestival ? "Add Festival" : "Add Venue")}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── PLOTR Stage Plot Card ──────────────────────────────────────────────────
function PlotCard({ show, onUrlChange, lightMode }) {
  const [open,    setOpen]    = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft,   setDraft]   = useState(show.stagePlotUrl || "");

  const hasUrl = !!show.stagePlotUrl;
  const amber  = lightMode ? "#c47f00" : "var(--accent)";
  const green  = lightMode ? "#1a9e4a" : "var(--success)";
  const muted  = lightMode ? "#6a6764" : "#555";
  const faint  = lightMode ? "#8a8784" : "#666";
  const redTxt = lightMode ? "#b03030" : "#c84b4b";
  const redBdr = lightMode ? "#d0a0a0" : "#3a1515";

  const save = () => {
    onUrlChange(draft.trim());
    setEditing(false);
  };

  const clear = () => {
    setDraft("");
    onUrlChange("");
    setOpen(false);
  };

  return (
    <div className="ec">
      <div className="ec-hdr">
        <span className="ec-ttl" style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg> Stage Plot
          {hasUrl && <span style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: green, letterSpacing: 1 }}>● linked</span>}
        </span>
        <div style={{ display: "flex", gap: 6 }}>
          {hasUrl && (
            <button className="plotr-open-btn" onClick={() => setOpen(o => !o)}>
              {open ? "Hide" : "Preview"}
            </button>
          )}
          <button className="plotr-open-btn" onClick={() => { setEditing(e => !e); setDraft(show.stagePlotUrl || ""); }}>
            {editing ? "Cancel" : hasUrl ? "Edit" : "Link Plot"}
          </button>
        </div>
      </div>

      {editing && (
        <div className="plotr-url-row">
          <input
            className="plotr-url-inp"
            placeholder="Paste PLOTR share URL…"
            value={draft}
            onChange={e => setDraft(e.target.value)}
            onKeyDown={e => e.key === "Enter" && save()}
            autoFocus
          />
          <button className="plotr-open-btn" onClick={save} style={{ color: amber, borderColor: amber }}>Save</button>
          {hasUrl && <button className="plotr-open-btn" onClick={clear} style={{ color: redTxt, borderColor: redBdr }}>Remove</button>}
        </div>
      )}

      {!editing && !hasUrl && (
        <div className="plotr-empty">
          No stage plot linked. Paste a PLOTR share URL here to include it in advance exports.
        </div>
      )}

      {!editing && hasUrl && !open && (
        <div style={{ padding: "10px 16px 12px", fontSize: 12, color: muted, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: faint, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 260 }}>
            {show.stagePlotUrl}
          </span>
          <a href={show.stagePlotUrl} target="_blank" rel="noreferrer"
            style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: amber, textDecoration: "none", flexShrink: 0, marginLeft: 8 }}>
            Open ↗
          </a>
        </div>
      )}

      {!editing && hasUrl && open && (
        <iframe
          className="plotr-preview"
          src={show.stagePlotUrl}
          height={380}
          title="Stage Plot"
          sandbox="allow-scripts allow-same-origin"
        />
      )}
    </div>
  );
}

// ── Calendar View ──────────────────────────────────────────────────────────
const CAL_STYLES = `
  .cal-wrap { padding: 0 36px 36px; }
  .cal-controls { display: flex; align-items: center; justify-content: space-between; padding: 24px 0 20px; }
  .cal-month { font-family: 'Helvetica Neue',Helvetica,Arial,sans-serif; font-weight: 700; font-size: 28px; letter-spacing: 2px; color: #f0f0f0; }
  .cal-nav { display: flex; gap: 6px; align-items: center; }
  .cal-nav-btn { background: none; border: 1px solid #272727; color: #666; width: 32px; height: 32px; border-radius: 4px; font-size: 16px; cursor: pointer; transition: all 0.15s; display: flex; align-items: center; justify-content: center; }
  .cal-nav-btn:hover { border-color: var(--accent); color: var(--accent); }
  .cal-today-btn { background: none; border: 1px solid #272727; color: #666; padding: 0 12px; height: 32px; border-radius: 4px; font-family: 'Helvetica Neue',Helvetica,Arial,sans-serif; font-size: 11px; letter-spacing: 1px; cursor: pointer; transition: all 0.15s; }
  .cal-today-btn:hover { border-color: var(--accent); color: var(--accent); }
  .cal-filters { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
  .cal-filter { padding: 4px 12px; border-radius: 16px; border: 1px solid #222; font-family: 'Helvetica Neue',Helvetica,Arial,sans-serif; font-size: 11px; cursor: pointer; transition: all 0.15s; color: #555; background: transparent; white-space: nowrap; }
  .cal-filter:hover { border-color: #444; color: #aaa; }
  .cal-filter.on { border-color: transparent; color: #0d0d0d; font-weight: 700; }
  .cal-grid { display: grid; grid-template-columns: repeat(7, 1fr); border-left: 1px solid #1a1a1a; border-top: 1px solid #1a1a1a; border-radius: 8px; overflow: hidden; }
  .cal-dow { border-right: 1px solid #1a1a1a; border-bottom: 1px solid #1a1a1a; padding: 8px 10px; font-family: 'Helvetica Neue',Helvetica,Arial,sans-serif; font-size: 11px; letter-spacing: 2px; color: #444; text-transform: uppercase; background: #111; text-align: center; }
  .cal-cell { border-right: 1px solid #1a1a1a; border-bottom: 1px solid #1a1a1a; min-height: 108px; padding: 8px; background: #0f0f0f; transition: background 0.12s; vertical-align: top; }
  .cal-cell.other-month { background: #0a0a0a; }
  .cal-cell.today { background: #141008; }
  .cal-cell:hover { background: #141414; }
  .cal-day-num { font-family: 'Helvetica Neue',Helvetica,Arial,sans-serif; font-size: 11px; color: #444; margin-bottom: 5px; }
  .cal-cell.today .cal-day-num { color: var(--accent); font-weight: 700; }
  .cal-pill { display: flex; align-items: center; gap: 5px; padding: 3px 7px; border-radius: 4px; margin-bottom: 3px; cursor: pointer; transition: opacity 0.15s; font-size: 11px; line-height: 1.3; overflow: hidden; }
  .cal-pill:hover { opacity: 0.8; }
  .cal-pill-dot { width: 5px; height: 5px; border-radius: 50%; flex-shrink: 0; }
  .cal-pill-text { overflow: hidden; white-space: nowrap; text-overflow: ellipsis; font-weight: 500; }
  .cal-pill-pct { font-family: 'Helvetica Neue',Helvetica,Arial,sans-serif; font-size: 11px; opacity: 0.7; flex-shrink: 0; }
  .cal-more { font-family: 'Helvetica Neue',Helvetica,Arial,sans-serif; font-size: 11px; color: #444; padding: 2px 4px; cursor: pointer; }
  .cal-more:hover { color: #aaa; }
  .cal-legend { display: flex; gap: 16px; padding: 16px 0 0; flex-wrap: wrap; }
  .cal-legend-item { display: flex; align-items: center; gap: 6px; font-family: 'Helvetica Neue',Helvetica,Arial,sans-serif; font-size: 11px; color: #555; }
  .cal-legend-dot { width: 10px; height: 10px; border-radius: 4px; }

  .cal-popover { position: fixed; z-index: 300; background: #1a1a1a; border: 1px solid #2a2a2a; border-radius: 8px; padding: 14px 16px; width: 240px; box-shadow: 0 12px 40px rgba(0,0,0,0.6); pointer-events: none; }
  .cal-pop-title { font-family: 'Helvetica Neue',Helvetica,Arial,sans-serif; font-weight: 700; font-size: 16px; letter-spacing: 1px; color: #f0f0f0; margin-bottom: 2px; }
  .cal-pop-date { font-family: 'Helvetica Neue',Helvetica,Arial,sans-serif; font-size: 11px; color: #555; margin-bottom: 10px; }
  .cal-pop-row { display: flex; justify-content: space-between; font-size: 12px; color: #bbb; margin-bottom: 4px; }
  .cal-pop-key { font-family: 'Helvetica Neue',Helvetica,Arial,sans-serif; font-size: 11px; color: #555; }
`;

function CalendarView({ data, venues, onGoToShow, onAddShow, lightMode }) {
  const SC = lightMode ? {
    unstarted: { label: "Unstarted", color: "#6a6764", bg: "#e4e0da", dot: "#aaa9a6" },
    advancing: { label: "Advancing", color: "#8a4a00", bg: "#f5e8cc", dot: "#c47f00" },
    confirmed: { label: "Confirmed", color: "#1a6a3a", bg: "#d8f0e4", dot: "#1a9e4a" },
    dayOf:     { label: "Day-Of",    color: "#1a4a7a", bg: "#d8ebf5", dot: "#4a90c0" },
  } : statusConfig;
  const advStatus = (show) => {
    const base = advanceStatus(show);
    if (!lightMode) return base;
    const lm = { "NOT SENT": ["#9a9794","#e4e0da"], "SENT": ["#1a4a7a","#d8ebf5"], "IN PROGRESS": ["#8a4a00","#f5e8cc"], "CONFIRMED": ["#1a6a3a","#d8f0e4"] };
    const [color, bg] = lm[base.label] || [base.color, base.bg];
    return { ...base, color, bg };
  };
  const today = new Date();
  const [year,      setYear]      = useState(today.getFullYear());
  const [month,     setMonth]     = useState(today.getMonth());
  const [filterAid, setFilterAid] = useState("all");
  const [hovered,   setHovered]   = useState(null); // { show, artist, venue, x, y }

  const prevMonth = () => { if (month === 0) { setMonth(11); setYear(y => y - 1); } else setMonth(m => m - 1); };
  const nextMonth = () => { if (month === 11) { setMonth(0); setYear(y => y + 1); } else setMonth(m => m + 1); };
  const goToday   = () => { setYear(today.getFullYear()); setMonth(today.getMonth()); };

  // Build flat show index keyed by ISO date — memoized so it only rebuilds on data/filter change
  const showsByDate = useMemo(() => {
    const map = {};
    data.artists.forEach((a, ai) => {
      if (filterAid !== "all" && a.id !== filterAid) return;
      const color = artistColor(a.colorIdx ?? ai);
      a.tours.forEach(t => {
        t.shows.forEach(s => {
          if (!map[s.date]) map[s.date] = [];
          map[s.date].push({ show: s, artist: a, tour: t, color });
        });
      });
    });
    return map;
  }, [data.artists, filterAid]);

  const { first, total } = calDays(year, month);
  const prevTotal = new Date(year, month, 0).getDate();
  const cells = [];

  // Leading empty cells from prev month
  for (let i = 0; i < first; i++) {
    cells.push({ day: prevTotal - first + 1 + i, current: false, iso: null });
  }
  // Current month days
  for (let d = 1; d <= total; d++) {
    cells.push({ day: d, current: true, iso: isoDate(year, month, d) });
  }
  // Trailing cells
  const trailing = 7 - (cells.length % 7 === 0 ? 7 : cells.length % 7);
  for (let i = 1; i <= trailing; i++) {
    cells.push({ day: i, current: false, iso: null });
  }

  const todayIso = isoDate(today.getFullYear(), today.getMonth(), today.getDate());
  const monthName = new Date(year, month, 1).toLocaleDateString("en-US", { month: "long", year: "numeric" });

  return (
    <>
      <style>{CAL_STYLES}</style>
      <div className="cal-wrap">
        <div className="cal-controls">
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <div className="cal-month">{monthName}</div>
            <div className="cal-nav">
              <button className="cal-nav-btn" onClick={prevMonth}>‹</button>
              <button className="cal-today-btn" onClick={goToday}>Today</button>
              <button className="cal-nav-btn" onClick={nextMonth}>›</button>
            </div>
          </div>
          <div className="cal-filters">
            <button className={`cal-filter ${filterAid === "all" ? "on" : ""}`}
              style={filterAid === "all" ? { background: "var(--accent)", color: "#0d0d0d" } : {}}
              onClick={() => setFilterAid("all")}>All Artists</button>
            {data.artists.map((a, ai) => {
              const c = artistColor(a.colorIdx ?? ai);
              return (
                <button key={a.id}
                  className={`cal-filter ${filterAid === a.id ? "on" : ""}`}
                  style={filterAid === a.id ? { background: c.pill, color: "#0d0d0d" } : {}}
                  onClick={() => setFilterAid(filterAid === a.id ? "all" : a.id)}>
                  {a.name}
                </button>
              );
            })}
          </div>
        </div>

        <div className="cal-grid">
          {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map(d => (
            <div key={d} className="cal-dow">{d}</div>
          ))}
          {cells.map((cell, i) => {
            const entries = cell.iso ? (showsByDate[cell.iso] || []) : [];
            const visible = entries.slice(0, 3);
            const overflow = entries.length - 3;
            const isToday = cell.iso === todayIso;
            return (
              <div key={i} className={`cal-cell ${!cell.current ? "other-month" : ""} ${isToday ? "today" : ""}`}
                onClick={() => { if (cell.iso && entries.length === 0 && onAddShow) onAddShow(cell.iso); }}>
                <div className="cal-day-num">{cell.day}</div>
                {visible.map(({ show, artist, tour, color }) => {
                  const v = venues[show.venueId];
                  const sc = SC[show.status] || SC.unstarted;
                  const adv = advStatus(show);
                  return (
                    <div key={show.id} className="cal-pill"
                      style={{ background: lightMode ? `${color.pill}33` : color.bg }}
                      onClick={() => onGoToShow(artist.id, tour.id, show.id)}
                      onMouseEnter={e => {
                        const rect = e.currentTarget.getBoundingClientRect();
                        setHovered({ show, artist, tour, venue: v, color, sc, adv, x: rect.right + 8, y: rect.top });
                      }}
                      onMouseLeave={() => setHovered(null)}>
                      <span className="cal-pill-dot" style={{ background: sc.dot }} />
                      <span className="cal-pill-text" style={{ color: lightMode ? "#3a3734" : color.text }}>{v?.name || "?"}</span>
                      <span className="cal-pill-pct" style={{ color: adv.color }}>{adv.label === "NOT SENT" ? "—" : adv.label === "CONFIRMED" ? "✓" : adv.label === "SENT" ? "→" : "…"}</span>
                    </div>
                  );
                })}
                {overflow > 0 && (
                  <div className="cal-more">+{overflow} more</div>
                )}
              </div>
            );
          })}
        </div>

        {/* Empty state — shown when the filtered view has no shows this month */}
        {(() => {
          const hasShows = Object.keys(showsByDate).some(iso => {
            const d = new Date(iso + "T12:00:00");
            return d.getFullYear() === year && d.getMonth() === month;
          });
          if (hasShows) return null;
          const artist = filterAid !== "all" ? data.artists.find(a => a.id === filterAid) : null;
          return (
            <div style={{ textAlign: "center", padding: "28px 0 12px", fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 12, color: "var(--text-faint)" }}>
              {artist ? `No shows for ${artist.name} in ${new Date(year, month).toLocaleDateString("en-US", { month: "long", year: "numeric" })}` : `No shows this month — click any date to add one`}
            </div>
          );
        })()}
        <div className="cal-legend">
          {data.artists.map((a, ai) => {
            const c = artistColor(a.colorIdx ?? ai);
            if (filterAid !== "all" && a.id !== filterAid) return null;
            const showCount = a.tours.flatMap(t => t.shows).filter(s => {
              const d = new Date(s.date + "T12:00:00");
              return d.getFullYear() === year && d.getMonth() === month;
            }).length;
            if (!showCount) return null;
            return (
              <div key={a.id} className="cal-legend-item">
                <div className="cal-legend-dot" style={{ background: c.pill }} />
                {a.name} · {showCount} show{showCount !== 1 ? "s" : ""}
              </div>
            );
          })}
        </div>
      </div>

      {/* Hover popover */}
      {hovered && (
        <div className="cal-popover" style={{
          left: Math.min(hovered.x, window.innerWidth - 256),
          top: Math.max(8, Math.min(hovered.y, window.innerHeight - 180)),
        }}>
          <div className="cal-pop-title">{hovered.venue?.name || "Unknown Venue"}</div>
          <div className="cal-pop-date">{fmtDate(hovered.show.date)}</div>
          <div className="cal-pop-row"><span className="cal-pop-key">Artist</span><span>{hovered.artist.name}</span></div>
          <div className="cal-pop-row"><span className="cal-pop-key">Tour</span><span style={{ fontSize: 11 }}>{hovered.tour.name}</span></div>
          <div className="cal-pop-row"><span className="cal-pop-key">Status</span>
            <span style={{ color: hovered.sc.color }}>{hovered.sc.label}</span></div>
          <div className="cal-pop-row"><span className="cal-pop-key">Advance</span>
            <span style={{ color: hovered.adv.color }}>{hovered.adv.label}</span></div>
          <div style={{ marginTop: 8, fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: "var(--text-faint)", letterSpacing: 1 }}>CLICK TO ADVANCE →</div>
        </div>
      )}
    </>
  );
}

// ── Data Import (Venues / Contacts) ────────────────────────────────────────
const VENUE_FIELDS = [
  { key: "name",           label: "Venue Name" },
  { key: "city",           label: "City" },
  { key: "addressState",   label: "State" },
  { key: "addressStreet",  label: "Street Address" },
  { key: "addressZip",     label: "ZIP Code" },
  { key: "addressCountry", label: "Country" },
  { key: "capacity",       label: "Capacity" },
  { key: "ages",           label: "Ages" },
  { key: "gaSeated",       label: "GA / Seated" },
  { key: "website",        label: "Website" },
  { key: "promoterName",   label: "Promoter Name" },
  { key: "promoterEmail",  label: "Promoter Email" },
  { key: "promoterCompany",label: "Promoter Company" },
  { key: "promoterPhone",  label: "Promoter Phone" },
  { key: "loadIn",         label: "Load-in Info" },
  { key: "houseGear",      label: "House Gear" },
  { key: "greenRooms",     label: "Green Rooms" },
  { key: "notes",          label: "Notes" },
];
const CONTACT_FIELDS = [
  { key: "name",    label: "Name" },
  { key: "email",   label: "Email" },
  { key: "phone",   label: "Phone" },
  { key: "company", label: "Company" },
  { key: "role",    label: "Role / Title" },
  { key: "notes",   label: "Notes" },
];
const VENUE_PATTERNS = {
  name:            [/venue.?name/i, /^name$/i, /^venue$/i],
  city:            [/^city$/i, /venue.?city/i, /^town$/i, /^market$/i],
  addressState:    [/^state$/i, /^st$/i, /venue.?st/i, /^province$/i],
  addressStreet:   [/street/i, /^address/i, /^addr/i],
  addressZip:      [/zip/i, /postal/i],
  addressCountry:  [/country/i],
  capacity:        [/^cap$/i, /^cap\b/i, /capacity/i],
  ages:            [/^ages$/i, /age.?restrict/i, /age.?policy/i],
  gaSeated:        [/ga.?\//i, /ga.*seat/i, /^setup$/i, /set.?up/i],
  website:         [/website/i, /^url$/i, /^web$/i, /^site$/i],
  promoterName:    [/promoter.?name/i, /^promoter$/i, /^buyer$/i, /contact.?name/i],
  promoterEmail:   [/promoter.?email/i, /^email$/i, /^e.?mail$/i],
  promoterCompany: [/promoter.?company/i, /^company$/i, /^organization$/i, /^org$/i],
  promoterPhone:   [/promoter.?phone/i, /^phone$/i, /^tel/i, /telephone/i],
  loadIn:          [/load.?in/i],
  houseGear:       [/house.?gear/i, /^gear$/i, /pa system/i],
  greenRooms:      [/green.?room/i, /backstage/i, /dressing/i],
  notes:           [/^notes$/i, /^comments$/i, /additional/i],
};
const CONTACT_PATTERNS = {
  name:    [/^name$/i, /full.?name/i, /contact.?name/i, /person/i],
  email:   [/^email$/i, /^e.?mail$/i, /email.?address/i],
  phone:   [/^phone$/i, /^tel/i, /mobile/i, /cell/i],
  company: [/^company$/i, /organization/i, /^org$/i, /employer/i, /venue/i],
  role:    [/^role$/i, /^title$/i, /^position$/i, /job.?title/i, /^type$/i],
  notes:   [/^notes$/i, /^comments$/i, /additional/i],
};

function parseDelimited(text) {
  const lines = text.trim().split(/\r?\n/).filter(l => l.trim());
  if (!lines.length) return { headers: [], rows: [] };
  const delim = lines[0].includes("\t") ? "\t" : ",";
  const parseRow = (line) => {
    if (delim === "\t") return line.split("\t").map(c => c.trim());
    const result = []; let inQ = false, cur = "";
    for (let i = 0; i < line.length; i++) {
      const c = line[i];
      if (c === '"') { inQ = !inQ; }
      else if (c === "," && !inQ) { result.push(cur.trim()); cur = ""; }
      else { cur += c; }
    }
    result.push(cur.trim());
    return result;
  };
  const headers = parseRow(lines[0]).map(h => h.replace(/^"|"$/g, "").trim());
  const rows = lines.slice(1)
    .map(l => { const cells = parseRow(l); return headers.map((_, i) => (cells[i] || "").replace(/^"|"$/g, "").trim()); })
    .filter(r => r.some(c => c));
  return { headers, rows };
}

function detectImportType(headers) {
  const h = headers.join(" ").toLowerCase();
  const vScore = /capacity|cap\b|venue.?name|load.?in|house.?gear|green.?room|ga.?seat/.test(h) ? 2 : 0;
  const cScore = /\brole\b|\btitle\b|\bposition\b|job.?title/.test(h) ? 2 : 0;
  if (vScore > cScore) return "venues";
  if (cScore > vScore) return "contacts";
  return "venues"; // default
}

function autoMapCols(headers, type) {
  const patterns = type === "venues" ? VENUE_PATTERNS : CONTACT_PATTERNS;
  const result = {}; const used = new Set();
  headers.forEach((h, i) => {
    for (const [field, regexes] of Object.entries(patterns)) {
      if (used.has(field)) continue;
      if (regexes.some(r => r.test(h.trim()))) { result[i] = field; used.add(field); break; }
    }
    if (result[i] === undefined) result[i] = "_skip";
  });
  return result;
}

function DataImportModal({ onClose, onImportVenues, onImportContacts }) {
  const [step,       setStep]       = useState("drop");
  const [parsed,     setParsed]     = useState(null);
  const [targetType, setTargetType] = useState("venues");
  const [mappings,   setMappings]   = useState({});
  const [dragging,   setDragging]   = useState(false);
  const [err,        setErr]        = useState("");
  const [importing,  setImporting]  = useState(false);
  const [doneCount,  setDoneCount]  = useState(0);
  const fileRef = useRef(null);

  const applyParsed = (result) => {
    const type = detectImportType(result.headers);
    setTargetType(type);
    setMappings(autoMapCols(result.headers, type));
    setParsed(result);
    setStep("map");
  };

  const handleFile = async (file) => {
    setErr("");
    const name = file.name.toLowerCase();
    try {
      if (name.endsWith(".csv") || name.endsWith(".tsv") || name.endsWith(".txt")) {
        applyParsed(parseDelimited(await file.text()));
      } else if (name.endsWith(".xlsx") || name.endsWith(".xls")) {
        const XLSX = (await import("xlsx")).default;
        const wb = XLSX.read(await file.arrayBuffer(), { type: "array" });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const raw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
        const headers = (raw[0] || []).map(String);
        const rows = raw.slice(1).filter(r => r.some(c => String(c).trim()))
          .map(r => headers.map((_, i) => String(r[i] ?? "").trim()));
        applyParsed({ headers, rows });
      } else {
        setErr("Unsupported format. Use CSV, TSV, or Excel (.xlsx).");
      }
    } catch (e) { setErr("Could not parse file: " + e.message); }
  };

  const handleTextPaste = (text) => {
    if (!text.trim() || !text.includes("\n")) return;
    setErr("");
    applyParsed(parseDelimited(text));
  };

  const doImport = () => {
    setImporting(true);
    const { rows } = parsed;
    const get = (row, field) => {
      const idx = Object.entries(mappings).find(([_, f]) => f === field)?.[0];
      return idx != null ? row[idx]?.trim() || "" : "";
    };
    if (targetType === "venues") {
      const items = rows.map(row => {
        const obj = { id: "v_imp_" + uid() };
        Object.entries(mappings).forEach(([i, f]) => { if (f !== "_skip") obj[f] = row[i]?.trim() || ""; });
        return obj;
      }).filter(v => v.name);
      onImportVenues(items);
      setDoneCount(items.length);
    } else {
      const items = rows.map(row => {
        const obj = { id: "c_imp_" + uid() };
        Object.entries(mappings).forEach(([i, f]) => { if (f !== "_skip") obj[f] = row[i]?.trim() || ""; });
        return obj;
      }).filter(c => c.name);
      onImportContacts(items);
      setDoneCount(items.length);
    }
    setImporting(false);
    setStep("done");
  };

  const nameIdx = Number(Object.entries(mappings).find(([_, f]) => f === "name")?.[0] ?? -1);
  const mappedCount = parsed ? parsed.rows.filter(r => nameIdx >= 0 && r[nameIdx]?.trim()).length : 0;
  const fields = targetType === "venues" ? VENUE_FIELDS : CONTACT_FIELDS;

  return (
    <div className="ov" onClick={onClose}>
      <div className="mo" style={{ width: 700, maxHeight: "88vh", overflowY: "auto" }} onClick={e => e.stopPropagation()}>

        {step === "drop" && (<>
          <div className="mo-title">Import Data</div>
          <div className="mo-sub">Venues · Contacts · Promoters — from any spreadsheet</div>
          <div
            onDragOver={e => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={e => { e.preventDefault(); setDragging(false); const f = e.dataTransfer.files[0]; if (f) handleFile(f); }}
            onClick={() => fileRef.current?.click()}
            className={`mo-import-drop${dragging ? " drag-over" : ""}`}>
            <div className="mo-import-drop-icon">⬆</div>
            <div className="mo-import-drop-title">Drop your file here, or click to browse</div>
            <div className="mo-import-drop-sub">CSV · TSV · EXCEL (.xlsx / .xls)</div>
            <input ref={fileRef} type="file" accept=".csv,.tsv,.txt,.xlsx,.xls" style={{ display: "none" }}
              onChange={e => { const f = e.target.files?.[0]; if (f) handleFile(f); e.target.value = ""; }} />
          </div>
          <div className="mo-import-divider">— OR PASTE SPREADSHEET DATA —</div>
          <textarea className="mo-inp" rows={6}
            placeholder={"Paste rows from Excel, Google Sheets, Airtable, Notion…\nFirst row must be column headers."}
            style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, resize: "vertical" }}
            onBlur={e => handleTextPaste(e.target.value)}
            onChange={e => { const v = e.target.value; if (v.includes("\n") && v.split("\n").length > 2) handleTextPaste(v); }}
          />
          {err && <div style={{ marginTop: 8, fontSize: 12, color: "var(--danger)", fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif" }}>{err}</div>}
          <div className="mo-import-hint">
            Works with exports from Google Sheets, Excel, Airtable, Notion, or any tool that exports rows.<br/>
            The first row must be column headers. ADVANCD will auto-detect the column types.
          </div>
        </>)}

        {step === "map" && parsed && (<>
          <div className="mo-title">Map Columns</div>
          <div className="mo-sub">{parsed.rows.length} rows detected — confirm how columns map to fields</div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
            <span className="mo-import-type-lbl">Import as:</span>
            {["venues", "contacts"].map(t => (
              <button key={t} className={targetType === t ? "btn-p" : "btn-g"}
                style={{ padding: "5px 14px", fontSize: 11 }}
                onClick={() => { setTargetType(t); setMappings(autoMapCols(parsed.headers, t)); }}>
                {t === "venues" ? "⊞ Venues" : "✆ Contacts"}
              </button>
            ))}
          </div>
          <div className="mo-map-hdr">
            <span>Your Column</span><span>Sample Data</span><span>Maps To</span>
          </div>
          <div style={{ maxHeight: 320, overflowY: "auto", display: "flex", flexDirection: "column", gap: 3, marginBottom: 16 }}>
            {parsed.headers.map((h, i) => {
              const sample = parsed.rows.slice(0, 3).map(r => r[i]).filter(Boolean).join(" / ") || "—";
              return (
                <div key={i} className="mo-map-row">
                  <div className="mo-map-col">{h}</div>
                  <div className="mo-map-sample">{sample}</div>
                  <select className="crm-sel" style={{ width: "100%" }} value={mappings[i] || "_skip"}
                    onChange={e => setMappings(p => ({ ...p, [i]: e.target.value }))}>
                    <option value="_skip">— Skip —</option>
                    {fields.map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
                  </select>
                </div>
              );
            })}
          </div>
          <div className="mo-acts">
            <button className="btn-g" onClick={() => setStep("drop")}>← Back</button>
            <button className="btn-p" onClick={() => setStep("preview")} disabled={mappedCount === 0}>
              Preview {mappedCount} Record{mappedCount !== 1 ? "s" : ""} →
            </button>
          </div>
        </>)}

        {step === "preview" && parsed && (<>
          <div className="mo-title">Preview Import</div>
          <div className="mo-sub">{mappedCount} {targetType} ready to add — showing first 8</div>
          <div style={{ marginBottom: 20 }}>
            {(() => {
              const emailIdx = Number(Object.entries(mappings).find(([_, f]) => f === "promoterEmail" || f === "email")?.[0] ?? -1);
              const subIdx   = Number(Object.entries(mappings).find(([_, f]) => f === "city" || f === "company")?.[0] ?? -1);
              const sub2Idx  = Number(Object.entries(mappings).find(([_, f]) => f === "addressState" || f === "role")?.[0] ?? -1);
              return parsed.rows.filter(r => nameIdx >= 0 && r[nameIdx]?.trim()).slice(0, 8).map((row, i) => (
                <div key={i} className="mo-preview-row">
                  <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 14, color: "var(--accent)", width: 22, textAlign: "center", flexShrink: 0 }}>{i + 1}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="mo-preview-name">{row[nameIdx]}</div>
                    <div className="mo-preview-sub">
                      {[subIdx >= 0 && row[subIdx], sub2Idx >= 0 && row[sub2Idx], emailIdx >= 0 && row[emailIdx]].filter(Boolean).join(" · ")}
                    </div>
                  </div>
                </div>
              ));
            })()}
            {parsed.rows.filter(r => nameIdx >= 0 && r[nameIdx]?.trim()).length > 8 && (
              <div className="mo-preview-more">
                + {parsed.rows.filter(r => nameIdx >= 0 && r[nameIdx]?.trim()).length - 8} more…
              </div>
            )}
          </div>
          <div className="mo-acts">
            <button className="btn-g" onClick={() => setStep("map")}>← Adjust Mapping</button>
            <button className="btn-p" onClick={doImport} disabled={importing}>
              {importing ? "Importing…" : `Import ${mappedCount} ${targetType === "venues" ? "Venue" : "Contact"}${mappedCount !== 1 ? "s" : ""}`}
            </button>
          </div>
        </>)}

        {step === "done" && (<>
          <div className="mo-title" style={{ color: "var(--success)" }}>✓ Done</div>
          <div className="mo-sub" style={{ marginBottom: 24 }}>
            {doneCount} {targetType} added to your {targetType === "venues" ? "Venues" : "Contacts"}.
          </div>
          <div className="mo-acts">
            <button className="btn-g" onClick={() => { setParsed(null); setStep("drop"); }}>Import More</button>
            <button className="btn-p" onClick={onClose}>Close</button>
          </div>
        </>)}

      </div>
    </div>
  );
}

// ── Local Routing Parser (no AI needed) ────────────────────────────────────
function parseRoutingLocal(text, venueList) {
  const MONTH_MAP = {
    jan:1,january:1,feb:2,february:2,mar:3,march:3,apr:4,april:4,
    may:5,jun:6,june:6,jul:7,july:7,aug:8,august:8,
    sep:9,september:9,oct:10,october:10,nov:11,november:11,dec:12,december:12,
  };
  const curYear = new Date().getFullYear();

  const toISO = (mo, dy, yr) => {
    const y = yr ? (String(yr).length === 2 ? 2000 + Number(yr) : Number(yr)) : curYear;
    return `${y}-${String(mo).padStart(2,"0")}-${String(dy).padStart(2,"0")}`;
  };

  const MON = "jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|jun(?:e)?|jul(?:y)?|aug(?:ust)?|sep(?:tember)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?";

  const tryDate = (line) => {
    let m;
    // "April 12" / "Apr 12" / "April 12th, 2026"
    m = line.match(new RegExp(`\\b(${MON})\\.?\\s+(\\d{1,2})(?:st|nd|rd|th)?(?:\\s*[,.]?\\s*(\\d{4}))?\\b`, "i"));
    if (m) { const mo = MONTH_MAP[m[1].toLowerCase().replace(/\.$/, "").slice(0,3)]; if (mo) return { iso: toISO(mo, m[2], m[3]), raw: m[0] }; }
    // "12 April" / "12th April 2026"
    m = line.match(new RegExp(`\\b(\\d{1,2})(?:st|nd|rd|th)?\\s+(${MON})\\.?(?:\\s+(\\d{4}))?\\b`, "i"));
    if (m) { const mo = MONTH_MAP[m[2].toLowerCase().replace(/\.$/, "").slice(0,3)]; if (mo) return { iso: toISO(mo, m[1], m[3]), raw: m[0] }; }
    // "2026-04-12" ISO
    m = line.match(/\b(20\d{2})[-\/](\d{2})[-\/](\d{2})\b/);
    if (m) return { iso: `${m[1]}-${m[2]}-${m[3]}`, raw: m[0] };
    // "4/12" "04/12" "4/12/26" "4-12-26"
    m = line.match(/\b(\d{1,2})[\/\-](\d{1,2})(?:[\/\-](\d{2,4}))?\b/);
    if (m && Number(m[1]) <= 12 && Number(m[2]) <= 31) return { iso: toISO(m[1], m[2], m[3]), raw: m[0] };
    return null;
  };

  const norm = s => s.toLowerCase().replace(/[^a-z0-9\s]/g, " ").replace(/\s+/g, " ").trim();

  const matchVenue = (name, city) => {
    if (!name || !venueList.length) return { id: null, confidence: "low" };
    const vn = norm(name);
    // Exact
    let hit = venueList.find(v => norm(v.name) === vn);
    if (hit) return { id: hit.id, confidence: "high" };
    // Substring
    hit = venueList.find(v => { const cn = norm(v.name); return cn.includes(vn) || vn.includes(cn); });
    if (hit) {
      const cityBoost = city && hit.city && norm(hit.city).includes(norm(city));
      return { id: hit.id, confidence: cityBoost ? "high" : "medium" };
    }
    // Word overlap
    const words = vn.split(" ").filter(w => w.length > 2);
    if (words.length) {
      const scored = venueList
        .map(v => ({ v, s: words.filter(w => norm(v.name).includes(w)).length / words.length }))
        .filter(x => x.s >= 0.5).sort((a, b) => b.s - a.s);
      if (scored.length) return { id: scored[0].v.id, confidence: scored[0].s >= 0.8 ? "medium" : "low" };
    }
    return { id: null, confidence: "low" };
  };

  const results = [];
  for (const rawLine of text.split(/\r?\n/)) {
    const line = rawLine.trim();
    if (line.length < 5) continue;
    // Skip obvious header rows
    if (/^(date|venue|city|show|routing|tour|artist)\b/i.test(line)) continue;

    const dated = tryDate(line);
    if (!dated) continue;

    // Strip the date from the line to get the venue/city portion
    let rest = line.replace(dated.raw, "").replace(/^[\s\-|—–:·•]+/, "").trim();
    if (!rest) continue;

    // Split venue from city — try strong separators first, then trailing comma
    let venueName = rest, city = "";
    const strongSep = rest.match(/^(.+?)\s{0,2}[|—–]\s{0,2}(.+)$/);
    if (strongSep) {
      venueName = strongSep[1].trim();
      city = strongSep[2].replace(/\b[A-Z]{2}\b\s*$/, "").trim();
    } else {
      // Last comma = city boundary, but only if remainder looks like a city
      const ci = rest.lastIndexOf(",");
      if (ci > 0 && ci < rest.length - 2) {
        const candidate = rest.slice(ci + 1).trim();
        // Accept if short enough to be a city/state phrase
        if (candidate.length < 40) { venueName = rest.slice(0, ci).trim(); city = candidate.replace(/\b[A-Z]{2}\b\s*$/, "").trim(); }
      }
    }

    const { id: matchedVenueId, confidence } = matchVenue(venueName, city);
    results.push({ date: dated.iso, venueName, city, matchedVenueId, confidence });
  }
  return results;
}

// ── Team Modal ─────────────────────────────────────────────────────────────
function TeamModal({ user, teamId, teamName, teamMembers, onMembersChange, onTeamNameChange, onClose, lightMode }) {
  const [tab,         setTab]         = useState("members"); // "members" | "join"
  const [inviteCode,  setInviteCode]  = useState(null);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteCopied, setInviteCopied] = useState(false);
  const [joinCode,    setJoinCode]    = useState("");
  const [joinLoading, setJoinLoading] = useState(false);
  const [joinError,   setJoinError]   = useState("");
  const [joinSuccess, setJoinSuccess] = useState(false);
  const [tName,       setTName]       = useState(teamName);
  const [nameSaved,   setNameSaved]   = useState(false);

  const myRole = teamMembers.find(m => m.user_id === user?.id)?.role || "member";
  const isOwner = myRole === "owner";

  const saveTeamName = async () => {
    if (!tName.trim() || !teamId) return;
    const { error } = await supabase.from("teams").update({ name: tName.trim() }).eq("id", teamId);
    if (error) { console.error("[ADVANCD] saveTeamName failed:", error); return; }
    onTeamNameChange?.(tName.trim());
    setNameSaved(true); setTimeout(() => setNameSaved(false), 2000);
  };

  const generateInvite = async () => {
    setInviteLoading(true);
    try {
      const inv = await createTeamInvite(teamId, user.id);
      setInviteCode(inv.code);
    } catch { /* ignore */ }
    setInviteLoading(false);
  };

  const copyCode = () => {
    navigator.clipboard.writeText(inviteCode);
    setInviteCopied(true); setTimeout(() => setInviteCopied(false), 2000);
  };

  const doJoin = async () => {
    if (!joinCode.trim()) return;
    setJoinLoading(true); setJoinError("");
    try {
      await joinTeamByCode(user.id, user.email, joinCode);
      setJoinSuccess(true);
      // Reload after brief delay so user sees success message
      setTimeout(() => window.location.reload(), 1500);
    } catch (err) {
      setJoinError(err.message);
    }
    setJoinLoading(false);
  };

  const T = lightMode
    ? { lbl: "#8a8784", inp: "#e0dbd4", inpBorder: "#ccc8c2", inpText: "#1a1917", divider: "#d0ccc6", memberDiv: "#dedad4", avatar: "#d8d4ce", avatarBorder: "#c8c4be", avatarText: "#8a8784", roleText: "#aaa8a4", youBg: "#ede9e3", youBorder: "#d0ccc6", youText: "#8a8784", codeBg: "#ede9e3", codeBorder: "#d0ccc6", mutedText: "#8a8784", tabActive: "#c47f00", tabInactive: "#aaa8a4", btnBg: "#dedad4", btnBorder: "#c8c4be", saveBtnBg: "#fdf3dc", saveBtnBorder: "#f0d080", saveBtnText: "#c47f00", invBtnBg: "#fdf3dc", invBtnBorder: "#f0d080", invBtnText: "#c47f00", accent: "#c47f00", accentText: "#fff", joinBtn: "#c47f00" }
    : { lbl: "#444", inp: "#0d0d0d", inpBorder: "#222", inpText: "#ccc", divider: "#1a1a1a", memberDiv: "#141414", avatar: "#1a1a1a", avatarBorder: "#2a2a2a", avatarText: "#555", roleText: "#444", youBg: "#1a1a00", youBorder: "#2a2a00", youText: "#3a3a00", codeBg: "#0d0d0d", codeBorder: "#2a2a2a", mutedText: "#555", tabActive: "var(--accent)", tabInactive: "#555", btnBg: "#1a1a00", btnBorder: "#3a3000", saveBtnBg: "#1a1a00", saveBtnBorder: "#3a3000", saveBtnText: "var(--accent)", invBtnBg: "#1a1a00", invBtnBorder: "#3a3000", invBtnText: "var(--accent)", accent: "var(--accent)", accentText: "#0d0d0d", joinBtn: "var(--accent)" };

  const lbl = { fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, letterSpacing: 2, color: T.lbl, textTransform: "uppercase", marginBottom: 8 };
  const inp = { background: T.inp, border: `1px solid ${T.inpBorder}`, borderRadius: 8, padding: "9px 12px", fontSize: 14, color: T.inpText, fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", width: "100%", outline: "none", boxSizing: "border-box" };

  return (
    <div className="ov" onClick={onClose}>
      <div className="mo" style={{ width: 500 }} onClick={e => e.stopPropagation()}>
        <div className="mo-hdr"><span className="mo-ttl">Team Settings</span><button className="mo-x" onClick={onClose}>✕</button></div>

        {/* Team name */}
        <div style={{ padding: "20px 24px 0" }}>
          <div style={lbl}>Team Name</div>
          <div style={{ display: "flex", gap: 8 }}>
            <input style={{ ...inp, flex: 1 }} value={tName} onChange={e => setTName(e.target.value)}
              onKeyDown={e => e.key === "Enter" && saveTeamName()} />
            <button onClick={saveTeamName}
              style={{ background: T.saveBtnBg, border: `1px solid ${T.saveBtnBorder}`, color: T.saveBtnText, borderRadius: 8, padding: "8px 14px", fontSize: 11, cursor: "pointer", fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", whiteSpace: "nowrap" }}>
              {nameSaved ? "✓ Saved" : "Save"}
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", gap: 0, padding: "18px 24px 0", borderBottom: `1px solid ${T.divider}`, marginTop: 18 }}>
          {[["members", "Members"], ["join", "Join Another Team"]].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)}
              style={{ background: "none", border: "none", borderBottom: `2px solid ${tab === k ? T.tabActive : "transparent"}`, color: tab === k ? T.tabActive : T.tabInactive, fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, letterSpacing: 1, padding: "0 0 12px", marginRight: 20, cursor: "pointer", transition: "all 0.15s" }}>
              {l}
            </button>
          ))}
        </div>

        <div style={{ padding: "20px 24px 24px" }}>
          {tab === "members" && (
            <>
              {/* Members list */}
              <div style={lbl}>Current Members</div>
              <div style={{ marginBottom: 20 }}>
                {teamMembers.map(m => (
                  <div key={m.user_id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 0", borderBottom: `1px solid ${T.memberDiv}` }}>
                    <div style={{ width: 28, height: 28, borderRadius: "50%", background: T.avatar, border: `1px solid ${T.avatarBorder}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, color: T.avatarText, flexShrink: 0 }}>
                      {(m.email || "?")[0].toUpperCase()}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, color: T.inpText, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.email || m.user_id.slice(0, 8) + "..."}</div>
                      <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: T.roleText, marginTop: 1 }}>{m.role.toUpperCase()}</div>
                    </div>
                    {m.user_id === user?.id && <span style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: T.youText, background: T.youBg, border: `1px solid ${T.youBorder}`, borderRadius: 4, padding: "2px 6px" }}>YOU</span>}
                  </div>
                ))}
              </div>

              {/* Invite code section (owners only) */}
              {isOwner && (
                <>
                  <div style={lbl}>Invite a Team Member</div>
                  {!inviteCode ? (
                    <button onClick={generateInvite} disabled={inviteLoading}
                      style={{ background: T.invBtnBg, border: `1px solid ${T.invBtnBorder}`, color: T.invBtnText, borderRadius: 8, padding: "9px 16px", fontSize: 12, cursor: "pointer", fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", letterSpacing: 1 }}>
                      {inviteLoading ? "..." : "⊕ Generate Invite Code"}
                    </button>
                  ) : (
                    <div style={{ background: T.codeBg, border: `1px solid ${T.codeBorder}`, borderRadius: 8, padding: "14px 16px" }}>
                      <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 20, color: T.accent, letterSpacing: 6, textAlign: "center", marginBottom: 10 }}>{inviteCode}</div>
                      <div style={{ fontSize: 11, color: T.mutedText, textAlign: "center", marginBottom: 12 }}>Share this code with your teammate. Expires in 30 days.</div>
                      <div style={{ display: "flex", gap: 8 }}>
                        <button onClick={copyCode} style={{ flex: 1, background: T.invBtnBg, border: `1px solid ${T.invBtnBorder}`, color: T.invBtnText, borderRadius: 8, padding: "8px", fontSize: 11, cursor: "pointer", fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif" }}>
                          {inviteCopied ? "✓ Copied!" : "Copy Code"}
                        </button>
                        <button onClick={() => setInviteCode(null)} style={{ background: "none", border: `1px solid ${T.codeBorder}`, color: T.mutedText, borderRadius: 8, padding: "8px 12px", fontSize: 11, cursor: "pointer", fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif" }}>
                          New Code
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {tab === "join" && (
            <>
              <div style={lbl}>Enter Invite Code</div>
              <div style={{ fontSize: 12, color: T.mutedText, marginBottom: 14, lineHeight: 1.6 }}>
                If a colleague has invited you to their team, enter their invite code below. Your current data will move to the new team.
              </div>
              <input style={{ ...inp, marginBottom: 10, textTransform: "uppercase", letterSpacing: 4, fontSize: 16, textAlign: "center" }}
                placeholder="XXXXXXXX" value={joinCode} maxLength={8}
                onChange={e => { setJoinCode(e.target.value.toUpperCase()); setJoinError(""); }} />
              {joinError && <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: "var(--danger)", marginBottom: 8 }}>{joinError}</div>}
              {joinSuccess && <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: "var(--success)", marginBottom: 8 }}>✓ Joined! Reloading...</div>}
              <button onClick={doJoin} disabled={joinLoading || !joinCode.trim() || joinSuccess}
                style={{ width: "100%", background: T.joinBtn, border: "none", color: lightMode ? "#fff" : "#0d0d0d", borderRadius: 8, padding: "10px", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", letterSpacing: 1, opacity: (!joinCode.trim() || joinSuccess) ? 0.4 : 1 }}>
                {joinLoading ? "Joining..." : "Join Team"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Bulk Import Modal ──────────────────────────────────────────────────────
// ── Add Shows Modal (combines single-show form + bulk import) ───────────────
function AddShowsModal({ tourName, venues, onSave, onImport, onClose, onAddVenue, onAddVenueFromDir, defaultDate }) {
  const FF = "'Helvetica Neue', Helvetica, Arial, sans-serif";
  const [tab, setTab] = useState("import");

  // ── Single show state ──
  const vl = Object.values(venues).sort((a, b) => a.name.localeCompare(b.name));
  const [date,          setDate]          = useState(defaultDate || "");
  const [vid,           setVid]           = useState("");
  const [venueSearch,   setVenueSearch]   = useState("");
  const [showDrop,      setShowDrop]      = useState(false);
  const [dirResults,    setDirResults]    = useState([]);
  const [dirSearching,  setDirSearching]  = useState(false);
  const [isNewVenue,    setIsNewVenue]    = useState(false);
  const [newVenueCity,  setNewVenueCity]  = useState("");
  const [newVenueState, setNewVenueState] = useState("");
  const dirTimer  = useRef(null);

  const filtered = venueSearch.trim().length > 0
    ? vl.filter(v => v.name.toLowerCase().includes(venueSearch.toLowerCase()) || (v.city || "").toLowerCase().includes(venueSearch.toLowerCase())).slice(0, 15)
    : vl.slice(0, 15);

  const runDirSearch = (q) => {
    clearTimeout(dirTimer.current);
    if (!q.trim()) { setDirResults([]); return; }
    dirTimer.current = setTimeout(async () => {
      setDirSearching(true);
      const { data } = await supabase.from("public_venues").select("id,name,city,address_state,capacity,kind").ilike("name", `%${q.trim()}%`).order("name").limit(12);
      const localNames = new Set(vl.map(v => v.name.toLowerCase()));
      setDirResults((data || []).filter(pv => !localNames.has(pv.name.toLowerCase())));
      setDirSearching(false);
    }, 300);
  };

  const selectVenue = (v) => {
    setVid(v.id);
    setVenueSearch(`${v.name}${v.city ? ` — ${[v.city, v.addressState].filter(Boolean).join(", ")}` : ""}`);
    setShowDrop(false); setDirResults([]); setIsNewVenue(false); setNewVenueCity(""); setNewVenueState("");
  };

  // ── Import state ──
  const [text,       setText]       = useState("");
  const [loading,    setLoading]    = useState(false);
  const [parsed,     setParsed]     = useState(null);
  const [parseError, setParseError] = useState("");
  const [parserUsed, setParserUsed] = useState(null);
  const venueList = Object.values(venues);

  const doParse = async () => {
    if (!text.trim()) return;
    setLoading(true); setParseError(""); setParsed(null); setParserUsed(null);
    try {
      const results = await parseRoutingWithAI(text, venueList);
      if (!results.length) setParseError("No shows found — try pasting the full email or routing sheet.");
      else { setParsed(results); setParserUsed("ai"); }
    } catch {
      const results = parseRoutingLocal(text, venueList);
      if (!results.length) setParseError("No dates found. Make sure each line has a date (e.g. April 12, 4/12) followed by venue and city.");
      else { setParsed(results); setParserUsed("local"); }
    }
    setLoading(false);
  };

  const toggleRow  = (i) => setParsed(p => p.map((r, idx) => idx === i ? { ...r, _skip: !r._skip } : r));
  const setVenueMatch = (i, v) => setParsed(p => p.map((r, idx) => idx === i ? { ...r, matchedVenueId: v } : r));
  const readyCount = parsed ? parsed.filter(r => !r._skip && (r.matchedVenueId || r.venueName)).length : 0;

  const tabStyle = (id) => ({
    fontFamily: FF, fontSize: 11, padding: "7px 18px", cursor: "pointer",
    background: "none", border: "none", borderBottom: `1px solid ${tab === id ? "var(--accent)" : "transparent"}`,
    color: tab === id ? "var(--accent)" : "#666", fontWeight: tab === id ? 700 : 400,
    marginBottom: -1, transition: "color 0.12s, border-color 0.12s",
    position: "relative",
  });

  return (
    <div className="ov" onClick={onClose}>
      <div className="mo" style={{ width: 600, maxWidth: "94vw", height: "min(640px, 90vh)", display: "flex", flexDirection: "column" }} onClick={e => e.stopPropagation()}>
        <div className="mo-title">Add Shows</div>
        <div className="mo-sub">{tourName?.toUpperCase()}</div>

        {/* Tab bar */}
        <div style={{ display: "flex", borderBottom: "1px solid #333", marginBottom: 20, marginTop: -4 }}>
          {[["import", "Multiple Shows"], ["single", "Single Show"]].map(([id, label]) => (
            <button key={id} style={tabStyle(id)} onClick={() => setTab(id)}>
              {/* Invisible bold copy reserves the bold width so layout never shifts */}
              <span style={{ display: "block", height: 0, overflow: "hidden", fontWeight: 700, visibility: "hidden" }} aria-hidden="true">{label}</span>
              {label}
            </button>
          ))}
        </div>

        {/* ── Single Show tab ── */}
        {tab === "single" && (
          <div style={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden" }}>
            <div className="mo-fld">
              <div className="mo-lbl">Date</div>
              <input type="date" className="mo-inp" value={date} onChange={e => setDate(e.target.value)} autoFocus />
            </div>
            <div className="mo-fld" style={{ position: "relative" }}>
              <div className="mo-lbl">Venue</div>
              <input className="mo-inp" value={venueSearch}
                onChange={e => { setVenueSearch(e.target.value); setVid(""); setShowDrop(true); runDirSearch(e.target.value); }}
                onFocus={() => setShowDrop(true)}
                onBlur={() => setTimeout(() => setShowDrop(false), 200)}
                placeholder="Search your venues or the directory…" />
              {showDrop && (filtered.length > 0 || dirResults.length > 0 || venueSearch.trim().length > 0) && (
                <div className="mo-drop">
                  {filtered.length > 0 && (
                    <>
                      {dirResults.length > 0 && <div className="mo-drop-lbl">My Venues</div>}
                      {filtered.map(v => (
                        <div key={v.id} className="mo-drop-item" onMouseDown={() => selectVenue(v)}>
                          {v.name} <span style={{ opacity: 0.5 }}>— {[v.city, v.addressState].filter(Boolean).join(", ")}</span>
                        </div>
                      ))}
                    </>
                  )}
                  {dirSearching && <div className="mo-drop-item" style={{ fontFamily: FF, fontSize: 11, opacity: 0.6 }}>Searching directory…</div>}
                  {!dirSearching && dirResults.length > 0 && (
                    <>
                      <div className="mo-drop-lbl dir" style={{ borderTop: filtered.length > 0 ? "1px solid inherit" : "none" }}>From Directory</div>
                      {dirResults.map(pv => (
                        <div key={pv.id} className="mo-drop-item" onMouseDown={() => { const v = onAddVenueFromDir(pv); selectVenue(v); }}>
                          {pv.name} <span style={{ opacity: 0.5 }}>— {[pv.city, pv.address_state].filter(Boolean).join(", ")}</span>
                          <span style={{ float: "right", fontFamily: FF, fontSize: 11, color: "var(--success)", marginTop: 2 }}>+ add</span>
                        </div>
                      ))}
                    </>
                  )}
                  {venueSearch.trim().length > 0 && (
                    <div className="mo-drop-item add" onMouseDown={() => { setIsNewVenue(true); setShowDrop(false); }}>
                      + Add &ldquo;{venueSearch.trim()}&rdquo; as new venue
                    </div>
                  )}
                </div>
              )}
            </div>
            {isNewVenue && (
              <div style={{ display: "flex", gap: 8 }}>
                <div className="mo-fld" style={{ flex: 1 }}>
                  <div className="mo-lbl">City</div>
                  <input className="mo-inp" placeholder="e.g. Nashville" value={newVenueCity} onChange={e => setNewVenueCity(e.target.value)} autoFocus />
                </div>
                <div className="mo-fld" style={{ flex: 1 }}>
                  <div className="mo-lbl">State / Province</div>
                  <input className="mo-inp" placeholder="e.g. TN" value={newVenueState} onChange={e => setNewVenueState(e.target.value)} />
                </div>
              </div>
            )}
            <div className="mo-acts" style={{ marginTop: "auto" }}>
              <button className="btn-g" onClick={onClose}>Cancel</button>
              <button className="btn-p" disabled={!date || (!vid && !isNewVenue)} onClick={() => {
                let venueId = vid;
                if (isNewVenue && venueSearch.trim()) {
                  const v = onAddVenue(venueSearch.trim(), newVenueCity, newVenueState);
                  venueId = v.id;
                }
                onSave({ date, venueId, status: "unstarted" });
              }}>Add Show</button>
            </div>
          </div>
        )}

        {/* ── Import tab ── */}
        {tab === "import" && (
          <div style={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden" }}>
            {!parsed ? (
              <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
                <div className="mo-fld">
                  <div className="mo-lbl">Paste routing — email, list, spreadsheet, anything</div>
                  <textarea className="mo-inp" rows={8}
                    placeholder={`Paste your routing — one show per line. Any of these formats work:\n\nApril 12 - 9:30 Club, Washington DC\nApr 14 | The Sinclair | Cambridge MA\n4/17 - Bowery Ballroom, New York NY\n\nOr paste a full booking email — any line with a date and venue will be detected.`}
                    style={{ resize: "vertical", lineHeight: 1.6 }}
                    value={text} onChange={e => setText(e.target.value)} autoFocus />
                </div>
                {parseError && <div style={{ color: "#c84b4b", fontFamily: FF, fontSize: 11, marginBottom: 12 }}>{parseError}</div>}
                <div className="mo-acts" style={{ marginTop: "auto" }}>
                  <button className="btn-g" onClick={onClose}>Cancel</button>
                  <button className="btn-p" disabled={!text.trim() || loading} onClick={doParse}>
                    {loading
                      ? <span style={{ display: "flex", alignItems: "center", gap: 8 }}><span style={{ width: 12, height: 12, border: "2px solid #0d0d0d", borderTopColor: "transparent", borderRadius: "50%", display: "inline-block", animation: "spin 0.8s linear infinite" }} />Parsing...</span>
                      : "✦ Parse Routing"}
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
                  <span className="bulk-row-count">{parsed.length} SHOWS FOUND · REVIEW & CONFIRM</span>
                  {parserUsed === "ai"    && <span className="bulk-badge-ai">✦ AI</span>}
                  {parserUsed === "local" && <span className="bulk-badge-basic">BASIC PARSE · dates & venues only</span>}
                </div>
                {/* flex:1 + minHeight:0 (not a fixed maxHeight) so the list shrinks to fit
                    and scrolls internally — footer buttons always stay reachable */}
                <div style={{ display: "flex", flexDirection: "column", gap: 6, flex: 1, minHeight: 0, overflowY: "auto" }}>
                  {parsed.map((r, i) => (
                    <div key={i} className={`bulk-row${r._skip ? " skip" : ""}`}>
                      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10 }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                            <span className="bulk-date">{r.date}</span>
                            <span className="bulk-city">·</span>
                            <span className="bulk-venue">{r.venueName}</span>
                            {r.city && <span className="bulk-city">{r.city}</span>}
                          </div>
                          {(r.dealTerms || r.guarantee || r.promoterName || r.loadIn || r.setTime || r.notes) && (
                            <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 12px", marginBottom: 8 }}>
                              {r.guarantee    && <span style={{ fontFamily: FF, fontSize: 11, color: "var(--success)" }}>${Number(r.guarantee).toLocaleString()} guarantee</span>}
                              {r.dealTerms    && <span className="bulk-detail">{r.dealTerms}</span>}
                              {r.promoterName && <span className="bulk-detail">{r.promoterName}</span>}
                              {r.loadIn       && <span className="bulk-detail">Load-in {r.loadIn}</span>}
                              {r.setTime      && <span className="bulk-detail">Set {r.setTime}</span>}
                              {r.notes        && <span className="bulk-city" style={{ fontStyle: "italic" }}>{r.notes}</span>}
                            </div>
                          )}
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <span className="bulk-match-lbl">MATCH:</span>
                            <select className="mo-sel" style={{ padding: "3px 8px", fontSize: 11, flex: 1 }}
                              value={r.matchedVenueId || ""}
                              onChange={e => setVenueMatch(i, e.target.value || null)}>
                              <option value="">— Add as new venue stub —</option>
                              {venueList.map(v => <option key={v.id} value={v.id}>{v.name} · {v.city}</option>)}
                            </select>
                            {r.matchedVenueId && r.confidence === "high" && <span style={{ fontFamily: FF, fontSize: 11, color: "var(--success)" }}>✓ matched</span>}
                            {r.matchedVenueId && r.confidence !== "high" && <span style={{ fontFamily: FF, fontSize: 11, color: "var(--accent)" }}>⚠ review</span>}
                          </div>
                        </div>
                        <button className="bulk-skip-btn" onClick={() => toggleRow(i)}>{r._skip ? "Include" : "Skip"}</button>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="bulk-status-lbl" style={{ marginTop: 10 }}>
                  Unmatched venues will be added as stubs — you can fill in their details later in the Venues.
                </div>
                <div className="mo-acts" style={{ marginTop: "auto" }}>
                  <button className="btn-g" onClick={() => { setParsed(null); setParseError(""); }}>← Re-paste</button>
                  <button className="btn-g" onClick={onClose}>Cancel</button>
                  <button className="btn-p" disabled={readyCount === 0} onClick={() => onImport(parsed.filter(r => !r._skip && (r.matchedVenueId || r.venueName)))}>
                    Import {readyCount} Show{readyCount !== 1 ? "s" : ""}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function BulkImportModal({ tourName, venues, onImport, onClose }) {
  const [text,       setText]       = useState("");
  const [loading,    setLoading]    = useState(false);
  const [parsed,     setParsed]     = useState(null);
  const [error,      setError]      = useState("");
  const [parserUsed, setParserUsed] = useState(null); // "ai" | "local"
  const venueList = Object.values(venues);

  const doParse = async () => {
    if (!text.trim()) return;
    setLoading(true); setError(""); setParsed(null); setParserUsed(null);
    try {
      const results = await parseRoutingWithAI(text, venueList);
      if (!results.length) setError("No shows found — try pasting the full email or routing sheet.");
      else { setParsed(results); setParserUsed("ai"); }
    } catch {
      const results = parseRoutingLocal(text, venueList);
      if (!results.length) setError("No dates found. Make sure each line has a date (e.g. April 12, 4/12) followed by venue and city.");
      else { setParsed(results); setParserUsed("local"); }
    }
    setLoading(false);
  };

  const toggleRow = (i) => setParsed(p => p.map((r, idx) => idx === i ? { ...r, _skip: !r._skip } : r));
  const setVenueMatch = (i, vid) => setParsed(p => p.map((r, idx) => idx === i ? { ...r, matchedVenueId: vid } : r));

  const doImport = () => {
    // Include any non-skipped show that has either a matched venue OR a parsed venue name
    const toAdd = parsed.filter(r => !r._skip && (r.matchedVenueId || r.venueName));
    onImport(toAdd);
  };

  const readyCount = parsed ? parsed.filter(r => !r._skip && (r.matchedVenueId || r.venueName)).length : 0;

  return (
    <div className="ov" onClick={onClose}>
      <div className="mo" style={{ width: 580 }} onClick={e => e.stopPropagation()}>
        <div className="mo-title">Bulk Import Shows</div>
        <div className="mo-sub">{tourName?.toUpperCase()}</div>

        {!parsed ? (
          <>
            <div className="mo-fld">
              <div className="mo-lbl">Paste routing — email, list, spreadsheet, anything</div>
              <textarea className="mo-inp" rows={8} placeholder={`Paste your routing — one show per line. Any of these formats work:\n\nApril 12 - 9:30 Club, Washington DC\nApr 14 | The Sinclair | Cambridge MA\n4/17 - Bowery Ballroom, New York NY\n2026-05-02 - Thalia Hall, Chicago IL\n\nOr paste a full booking email — any line with a date and venue will be detected.`}
                style={{ resize: "vertical", lineHeight: 1.6 }}
                value={text} onChange={e => setText(e.target.value)} autoFocus />
            </div>
            {error && <div style={{ color: "#c84b4b", fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, marginBottom: 12 }}>{error}</div>}
            <div className="mo-acts">
              <button className="btn-g" onClick={onClose}>Cancel</button>
              <button className="btn-p" disabled={!text.trim() || loading} onClick={doParse}>
                {loading
                  ? <span style={{ display:"flex", alignItems:"center", gap:8 }}><span style={{ width:12, height:12, border:"2px solid #0d0d0d", borderTopColor:"transparent", borderRadius:"50%", display:"inline-block", animation:"spin 0.8s linear infinite" }} />Parsing...</span>
                  : "✦ Parse Routing"}
              </button>
            </div>
          </>
        ) : (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
              <span className="bulk-row-count">{parsed.length} SHOWS FOUND · REVIEW & CONFIRM</span>
              {parserUsed === "ai" && <span className="bulk-badge-ai">✦ AI</span>}
              {parserUsed === "local" && <span className="bulk-badge-basic">BASIC PARSE · dates & venues only</span>}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 340, overflowY: "auto" }}>
              {parsed.map((r, i) => (
                <div key={i} className={`bulk-row${r._skip ? " skip" : ""}`}>
                  <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                        <span className="bulk-date">{r.date}</span>
                        <span className="bulk-city">·</span>
                        <span className="bulk-venue">{r.venueName}</span>
                        {r.city && <span className="bulk-city">{r.city}</span>}
                      </div>
                      {/* Extra parsed fields */}
                      {(r.dealTerms || r.guarantee || r.promoterName || r.loadIn || r.setTime || r.notes) && (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 12px", marginBottom: 8 }}>
                          {r.guarantee    && <span style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: "var(--success)" }}>${Number(r.guarantee).toLocaleString()} guarantee</span>}
                          {r.dealTerms    && <span className="bulk-detail">{r.dealTerms}</span>}
                          {r.promoterName && <span className="bulk-detail">{r.promoterName}</span>}
                          {r.loadIn       && <span className="bulk-detail">Load-in {r.loadIn}</span>}
                          {r.setTime      && <span className="bulk-detail">Set {r.setTime}</span>}
                          {r.notes        && <span className="bulk-city" style={{ fontStyle: "italic" }}>{r.notes}</span>}
                        </div>
                      )}
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span className="bulk-match-lbl">MATCH:</span>
                        <select className="mo-sel" style={{ padding: "3px 8px", fontSize: 11, flex: 1 }}
                          value={r.matchedVenueId || ""}
                          onChange={e => setVenueMatch(i, e.target.value || null)}>
                          <option value="">— Add as new venue stub —</option>
                          {venueList.map(v => <option key={v.id} value={v.id}>{v.name} · {v.city}</option>)}
                        </select>
                        {r.matchedVenueId && r.confidence === "high" &&
                          <span style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: "var(--success)" }}>✓ matched</span>}
                        {r.matchedVenueId && r.confidence !== "high" &&
                          <span style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: "var(--accent)" }}>⚠ review</span>}
                      </div>
                    </div>
                    <button className="bulk-skip-btn" onClick={() => toggleRow(i)}>{r._skip ? "Include" : "Skip"}</button>
                  </div>
                </div>
              ))}
            </div>
            <div className="bulk-status-lbl" style={{ marginTop: 10 }}>
              Unmatched venues will be added as stubs — you can fill in their details later in the Venues.
            </div>
            <div className="mo-acts">
              <button className="btn-g" onClick={() => { setParsed(null); setError(""); }}>← Re-paste</button>
              <button className="btn-g" onClick={onClose}>Cancel</button>
              <button className="btn-p" disabled={readyCount === 0} onClick={doImport}>
                Import {readyCount} Show{readyCount !== 1 ? "s" : ""}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Venue Profile Tab ─────────────────────────────────────────────────────────
// V-2: Venue fills in their permanent house inventory (stored in organizations.profile JSONB)
function VenueProfileTab({ orgData, onUpdateOrg, T, FF, typeLabel }) {
  const [profile, setProfile] = useState(orgData?.profile || {});
  const [saving,  setSaving]  = useState(false);
  const [saved,   setSaved]   = useState(false);
  const [orgName, setOrgName] = useState(orgData?.name || "");
  const [editingName, setEditingName] = useState(false);
  const [savingName,  setSavingName]  = useState(false);

  const set = (k, v) => setProfile(p => ({ ...p, [k]: v }));

  const saveProfile = async () => {
    setSaving(true);
    await supabase.from("organizations").update({ profile }).eq("id", orgData.id);
    if (onUpdateOrg) onUpdateOrg({ ...orgData, profile });
    setSaving(false); setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const saveOrgName = async () => {
    if (!orgName.trim()) return;
    setSavingName(true);
    await supabase.from("organizations").update({ name: orgName.trim() }).eq("id", orgData.id);
    if (onUpdateOrg) onUpdateOrg({ ...orgData, name: orgName.trim() });
    setSavingName(false); setEditingName(false);
  };

  const inp = (label, key, placeholder = "") => (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontFamily: FF, fontSize: 11, color: T.muted, letterSpacing: 0.5, marginBottom: 4 }}>{label}</div>
      <input value={profile[key] || ""} onChange={e => set(key, e.target.value)} placeholder={placeholder}
        style={{ width: "100%", fontFamily: FF, fontSize: 12, padding: "7px 10px", background: T.inp, border: `1px solid ${T.inpBorder}`, borderRadius: 4, color: T.text, outline: "none", boxSizing: "border-box" }} />
    </div>
  );
  const ta = (label, key, placeholder = "") => (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontFamily: FF, fontSize: 11, color: T.muted, letterSpacing: 0.5, marginBottom: 4 }}>{label}</div>
      <textarea value={profile[key] || ""} onChange={e => set(key, e.target.value)} placeholder={placeholder} rows={3}
        style={{ width: "100%", fontFamily: FF, fontSize: 12, padding: "7px 10px", background: T.inp, border: `1px solid ${T.inpBorder}`, borderRadius: 4, color: T.text, outline: "none", boxSizing: "border-box", resize: "vertical" }} />
    </div>
  );

  return (
    <div style={{ maxWidth: 600 }}>
      {/* Org name */}
      <div style={{ marginBottom: 28 }}>
        <div style={{ fontFamily: FF, fontSize: 11, color: T.muted, letterSpacing: 1, marginBottom: 8 }}>{typeLabel.toUpperCase()} NAME</div>
        {editingName ? (
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input autoFocus value={orgName} onChange={e => setOrgName(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") saveOrgName(); if (e.key === "Escape") setEditingName(false); }}
              style={{ flex: 1, fontFamily: FF, fontSize: 20, fontWeight: 700, color: T.text, background: T.inp, border: `1px solid ${T.inpBorder}`, borderRadius: 8, padding: "6px 12px", outline: "none" }} />
            <button onClick={saveOrgName} disabled={savingName} style={{ padding: "7px 16px", background: T.btn, color: T.btnText, border: "none", borderRadius: 8, fontFamily: FF, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>{savingName ? "..." : "Save"}</button>
            <button onClick={() => setEditingName(false)} style={{ padding: "7px 14px", background: "none", color: T.muted, border: `1px solid ${T.border}`, borderRadius: 8, fontFamily: FF, fontSize: 12, cursor: "pointer" }}>Cancel</button>
          </div>
        ) : (
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ fontFamily: FF, fontSize: 20, fontWeight: 700 }}>{orgData?.name || "Unnamed"}</div>
            <button onClick={() => setEditingName(true)} style={{ fontFamily: FF, fontSize: 11, color: T.muted, background: "none", border: `1px solid ${T.border}`, borderRadius: 4, padding: "3px 8px", cursor: "pointer" }}>Edit</button>
          </div>
        )}
      </div>

      {/* Basic info */}
      <div style={{ fontFamily: FF, fontSize: 11, fontWeight: 700, letterSpacing: 1, color: T.muted, marginBottom: 12, textTransform: "uppercase" }}>Venue Info</div>
      {inp("Address", "address", "123 Main St, City, State ZIP")}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div>{inp("Capacity", "capacity", "e.g. 500")}</div>
        <div>{inp("Phone", "phone", "+1 555.000.0000")}</div>
      </div>
      {inp("Website", "website", "https://...")}

      {/* House technical spec */}
      <div style={{ fontFamily: FF, fontSize: 11, fontWeight: 700, letterSpacing: 1, color: T.muted, margin: "20px 0 12px", textTransform: "uppercase" }}>House Technical Spec</div>
      <div style={{ fontFamily: FF, fontSize: 11, color: T.muted, marginBottom: 12, lineHeight: 1.5 }}>
        This information is shown to tour managers when their show is linked to your venue — so they know what's in house before sending their technical rider.
      </div>
      {inp("PA System", "pa_system", "e.g. L-Acoustics K2, 24 tops + 12 subs")}
      {inp("FOH Console", "foh_console", "e.g. Avid S6L, Yamaha CL5")}
      {inp("Monitor Console", "mon_console", "e.g. Yamaha PM5D, DiGiCo SD10")}
      {inp("Backline", "backline", "e.g. drums available, no backline guitars")}
      {inp("Lighting", "lighting", "e.g. GrandMA2, LED wash + spot package")}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div>{inp("Stage Width", "stage_width", "e.g. 40ft")}</div>
        <div>{inp("Stage Depth", "stage_depth", "e.g. 30ft")}</div>
      </div>
      {inp("Load-in Door", "load_in_door", "e.g. 10ft wide × 12ft tall, stage left, dock level")}
      {inp("Power", "power", "e.g. 3-phase 400A available stage left")}
      {ta("Additional Notes", "tech_notes", "Anything else TMs should know about the house rig...")}

      <button onClick={saveProfile} disabled={saving}
        style={{ fontFamily: FF, fontSize: 12, fontWeight: 700, padding: "9px 24px", background: T.btn, color: T.btnText, border: "none", borderRadius: 8, cursor: "pointer", marginTop: 4, letterSpacing: 0.5 }}>
        {saving ? "Saving..." : saved ? "Saved ✓" : "Save Profile"}
      </button>
    </div>
  );
}

// ── Venue Advance Panel ───────────────────────────────────────────────────────
// V-2: Venue user's view of a show advance — TM sections (read-only) + venue-fillable sections
const VENUE_ADVANCE_SECTIONS = [
  { id: "venue_contacts",  label: "Venue Contacts",   fields: [
    { key: "production_manager", label: "Production Manager", type: "text" },
    { key: "prod_mgr_phone",     label: "Phone",              type: "text" },
    { key: "prod_mgr_email",     label: "Email",              type: "text" },
    { key: "foh_engineer",       label: "FOH Engineer",       type: "text" },
    { key: "foh_phone",          label: "FOH Phone",          type: "text" },
    { key: "stage_manager",      label: "Stage Manager",      type: "text" },
    { key: "stage_mgr_phone",    label: "Stage Manager Phone", type: "text" },
  ]},
  { id: "venue_load_in",   label: "Load-in & Access",  fields: [
    { key: "load_in_time",    label: "Load-in Time",         type: "text" },
    { key: "doors_time",      label: "Doors Time",           type: "text" },
    { key: "load_in_address", label: "Load-in Door Address", type: "text" },
    { key: "parking_info",    label: "Parking & Access",     type: "textarea" },
    { key: "security_notes",  label: "Security / Check-in",  type: "textarea" },
  ]},
  { id: "venue_technical", label: "Stage & Technical", fields: [
    { key: "pa_notes",       label: "PA / Sound System",     type: "textarea" },
    { key: "console_notes",  label: "FOH Console",           type: "text" },
    { key: "monitor_notes",  label: "Monitor Console",       type: "text" },
    { key: "backline_notes", label: "Backline Available",    type: "textarea" },
    { key: "stage_notes",    label: "Stage Notes",           type: "textarea" },
  ]},
  { id: "venue_hospitality", label: "Hospitality",   fields: [
    { key: "dressing_rooms", label: "Dressing Rooms",        type: "textarea" },
    { key: "catering_notes", label: "Catering",              type: "textarea" },
    { key: "parking_passes", label: "Parking Passes",        type: "text" },
    { key: "wifi",           label: "WiFi",                  type: "text" },
  ]},
  { id: "venue_settlement", label: "Settlement",     fields: [
    { key: "settlement_contact", label: "Settlement Contact", type: "text" },
    { key: "settlement_phone",   label: "Phone",              type: "text" },
    { key: "settlement_email",   label: "Email",              type: "text" },
    { key: "payment_method",     label: "Payment Method",     type: "text" },
  ]},
];

const FESTIVAL_ADVANCE_SECTIONS = [
  { id: "fest_schedule",    label: "Schedule",          fields: [
    { key: "confirmed_set_time",   label: "Confirmed Set Time",   type: "text" },
    { key: "stage_name",           label: "Stage",                type: "text" },
    { key: "changeover_before",    label: "Changeover Before",    type: "text" },
    { key: "changeover_after",     label: "Changeover After",     type: "text" },
    { key: "schedule_notes",       label: "Schedule Notes",       type: "textarea" },
  ]},
  { id: "fest_production",  label: "Production",        fields: [
    { key: "pa_system",     label: "PA / Sound System",   type: "textarea" },
    { key: "foh_console",   label: "FOH Console",         type: "text" },
    { key: "monitors",      label: "Monitor System",      type: "text" },
    { key: "backline",      label: "Backline Provided",   type: "textarea" },
    { key: "local_crew",    label: "Local Crew",          type: "textarea" },
    { key: "stage_dims",    label: "Stage Dimensions",    type: "text" },
  ]},
  { id: "fest_logistics",   label: "Logistics",         fields: [
    { key: "load_in_time",  label: "Artist Load-in Time", type: "text" },
    { key: "load_in_access",label: "Load-in Access",      type: "textarea" },
    { key: "parking",       label: "Parking",             type: "textarea" },
    { key: "credentials",   label: "Credential Pickup",   type: "textarea" },
    { key: "wristbands",    label: "Wristband Count",     type: "text" },
  ]},
  { id: "fest_hospitality", label: "Hospitality",       fields: [
    { key: "dressing_room", label: "Dressing Room",       type: "textarea" },
    { key: "catering",      label: "Catering",            type: "textarea" },
    { key: "buyout",        label: "Buyout Amount",       type: "text" },
    { key: "wifi",          label: "WiFi",                type: "text" },
  ]},
  { id: "fest_contacts",    label: "Festival Contacts", fields: [
    { key: "production_mgr",    label: "Production Manager",  type: "text" },
    { key: "prod_phone",        label: "Phone",               type: "text" },
    { key: "prod_email",        label: "Email",               type: "text" },
    { key: "stage_manager",     label: "Stage Manager",       type: "text" },
    { key: "stage_mgr_phone",   label: "Phone",               type: "text" },
    { key: "foh_engineer",      label: "FOH Engineer",        type: "text" },
    { key: "foh_phone",         label: "Phone",               type: "text" },
  ]},
];

// ── AdvanceMessageThread ─────────────────────────────────────────────────────
// Self-contained per-event_link message thread. Used in TM advance tab,
// VenueAdvancePanel, and FestivalDashboard. Loads its own data + Realtime.
function AdvanceMessageThread({ linkId, senderType, senderName, T, FF }) {
  const [msgs,    setMsgs]    = useState([]);
  const [draft,   setDraft]   = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!linkId) return;
    let chan;
    (async () => {
      setLoading(true);
      const { data } = await supabase
        .from('advance_messages')
        .select('id, sender_type, sender_name, body, created_at')
        .eq('event_link_id', linkId)
        .order('created_at', { ascending: true });
      setMsgs(data || []);
      setLoading(false);
      chan = supabase
        .channel(`adv-msgs-${linkId}`)
        .on('postgres_changes', {
          event: 'INSERT', schema: 'public', table: 'advance_messages',
          filter: `event_link_id=eq.${linkId}`,
        }, payload => {
          setMsgs(prev => prev.some(m => m.id === payload.new.id) ? prev : [...prev, payload.new]);
        })
        .subscribe();
    })();
    return () => { if (chan) supabase.removeChannel(chan); };
  }, [linkId]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [msgs]);

  const send = async () => {
    const body = draft.trim();
    if (!body || sending) return;
    setSending(true);
    const id = Date.now().toString(36) + Math.random().toString(36).slice(2);
    const { data: authData } = await supabase.auth.getUser();
    const newMsg = {
      id, event_link_id: linkId,
      sender_type: senderType,
      sender_name: senderName || authData?.user?.email || senderType,
      body,
      created_at: new Date().toISOString(),
    };
    setMsgs(prev => [...prev, newMsg]);
    setDraft('');
    await supabase.from('advance_messages').insert(newMsg);
    setSending(false);
  };

  const _FF = FF || "'Helvetica Neue', Helvetica, Arial, sans-serif";
  const isOwn = (type) => type === senderType;
  const fmtTime = (ts) => new Date(ts).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  const roleLabel = (type) => type === 'tm' ? 'Tour Manager' : type === 'festival' ? 'Festival' : 'Venue';

  return (
    <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 16, marginTop: 8 }}>
      <div style={{ fontFamily: _FF, fontSize: 11, fontWeight: 700, letterSpacing: 1, color: T.muted, marginBottom: 10, textTransform: 'uppercase' }}>
        Messages
      </div>
      {loading ? (
        <div style={{ fontFamily: _FF, fontSize: 12, color: T.muted }}>Loading…</div>
      ) : (
        <>
          <div ref={scrollRef} style={{ maxHeight: 200, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 10, paddingRight: 4 }}>
            {msgs.length === 0 && (
              <div style={{ fontFamily: _FF, fontSize: 12, color: T.muted, textAlign: 'center', padding: '16px 0', opacity: 0.7 }}>No messages yet.</div>
            )}
            {msgs.map(msg => (
              <div key={msg.id} style={{ alignSelf: isOwn(msg.sender_type) ? 'flex-end' : 'flex-start', maxWidth: '78%' }}>
                <div style={{ fontFamily: _FF, fontSize: 11, color: T.muted, marginBottom: 2, textAlign: isOwn(msg.sender_type) ? 'right' : 'left', letterSpacing: 0.3 }}>
                  {roleLabel(msg.sender_type)}{msg.sender_name ? ` · ${msg.sender_name}` : ''} · {fmtTime(msg.created_at)}
                </div>
                <div style={{ fontFamily: _FF, fontSize: 12, color: T.text,
                  background: isOwn(msg.sender_type) ? (T.btn + '22') : T.card,
                  border: `1px solid ${isOwn(msg.sender_type) ? (T.btn + '44') : T.border}`,
                  borderRadius: isOwn(msg.sender_type) ? '10px 10px 2px 10px' : '10px 10px 10px 2px',
                  padding: '8px 12px', lineHeight: 1.5 }}>
                  {msg.body}
                </div>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <input
              value={draft}
              onChange={e => setDraft(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }}}
              placeholder="Message…"
              style={{ flex: 1, fontFamily: _FF, fontSize: 12, padding: '8px 12px',
                background: T.inp || T.card, border: `1px solid ${T.inpBorder || T.border}`,
                borderRadius: 8, color: T.text, outline: 'none' }}
            />
            <button onClick={send} disabled={!draft.trim() || sending}
              style={{ fontFamily: _FF, fontSize: 12, fontWeight: 700, padding: '8px 14px',
                background: draft.trim() ? T.btn : 'transparent',
                color: draft.trim() ? T.btnText : T.muted,
                border: `1px solid ${draft.trim() ? T.btn : T.border}`,
                borderRadius: 8, cursor: draft.trim() && !sending ? 'pointer' : 'default', transition: 'background 0.15s' }}>
              {sending ? '…' : 'Send'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function VenueAdvancePanel({ token, linkId, lightMode }) {
  const isLight = lightMode;
  const T = isLight
    ? { page: "#f0ede8", card: "#e8e4de", border: "#d0ccc6", inp: "#e0dbd4", inpBorder: "#ccc8c2", text: "#1a1917", muted: "#8a8784", sub: "#9a9794", btn: "#c47f00", btnText: "#fff", amber: "#c47f00", green: "#2a7d4f", secBg: "#e8e4de", secBdr: "#d0ccc6", red: "#c0392b" }
    : { page: "#0d0d0d", card: "#141414", border: "var(--panel-border)",    inp: "#1a1a1a", inpBorder: "#2a2a2a", text: "#fff",    muted: "#555",    sub: "#444",    btn: "var(--accent)", btnText: "#0d0d0d", amber: "var(--accent)", green: "var(--success)", secBg: "#141414", secBdr: "#222", red: "#f87171" };
  const FF = "'Helvetica Neue', Helvetica, Arial, sans-serif";

  const [advanceData,    setAdvanceData]    = useState(null);  // TM's advance data
  const [venueData,      setVenueData]      = useState({});    // venue-filled field data
  const [openSecs,       setOpenSecs]       = useState({ venue_contacts: true }); // default first sec open
  const [saving,         setSaving]         = useState({});
  const [saved,          setSaved]          = useState({});
  const [loading,        setLoading]        = useState(true);
  const [tmSettled,      setTmSettled]      = useState(false);
  const [venueSettled,   setVenueSettled]   = useState(false);
  const [settleNotes,    setSettleNotes]    = useState("");
  const [settleNoteEdit, setSettleNoteEdit] = useState(false);
  const [confirmingSettle, setConfirmingSettle] = useState(false);

  // Load TM advance data via token + venue data from event_links
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      // TM advance data via existing API
      try {
        const res = await fetch(`/api/advance?token=${token}`);
        if (res.ok) { const d = await res.json(); setAdvanceData(d); }
      } catch {}
      // Venue data from event_links (including settlement confirmation state)
      if (linkId) {
        const { data: link } = await supabase.from("event_links")
          .select("venue_advance_data, tm_settlement_confirmed, venue_settlement_confirmed, settlement_notes")
          .eq("id", linkId).single();
        if (link) {
          if (link.venue_advance_data) setVenueData(link.venue_advance_data);
          setTmSettled(!!link.tm_settlement_confirmed);
          setVenueSettled(!!link.venue_settlement_confirmed);
          setSettleNotes(link.settlement_notes || "");
        }
      }
      setLoading(false);
    };
    load();
  }, [token, linkId]);

  const toggleSec = (id) => setOpenSecs(p => ({ ...p, [id]: !p[id] }));

  const updateField = (secId, key, val) => {
    setVenueData(p => ({ ...p, [secId]: { ...(p[secId] || {}), [key]: val } }));
  };

  const saveSection = async (secId) => {
    if (!linkId) return;
    setSaving(p => ({ ...p, [secId]: true }));
    await supabase.from("event_links")
      .update({ venue_advance_data: venueData })
      .eq("id", linkId);
    setSaving(p => ({ ...p, [secId]: false }));
    setSaved(p => ({ ...p, [secId]: true }));
    setTimeout(() => setSaved(p => ({ ...p, [secId]: false })), 2000);
  };

  const confirmVenueSettlement = async () => {
    if (!linkId) return;
    setConfirmingSettle(true);
    const now = new Date().toISOString();
    const updates = { venue_settlement_confirmed: true };
    // If TM already confirmed too, stamp the bilateral confirmation time
    if (tmSettled) updates.settlement_confirmed_at = now;
    await supabase.from("event_links").update(updates).eq("id", linkId);
    setVenueSettled(true);
    setConfirmingSettle(false);
  };

  const saveSettleNotes = async () => {
    if (!linkId) return;
    await supabase.from("event_links").update({ settlement_notes: settleNotes }).eq("id", linkId);
    setSettleNoteEdit(false);
  };

  if (loading) return (
    <div style={{ padding: 40, fontFamily: FF, fontSize: 12, color: T.muted, textAlign: "center" }}>Loading advance...</div>
  );

  const show     = advanceData?.show     || {};
  const tour     = advanceData?.tour     || {};
  const venue    = advanceData?.venue    || {};
  const artist   = advanceData?.artist   || {};
  const tmData   = advanceData?.tmData   || {};

  return (
    <div style={{ minHeight: "100vh", background: T.page, fontFamily: FF, color: T.text }}>
      <div style={{ maxWidth: 740, margin: "0 auto", padding: "32px 24px" }}>

        {/* Show header */}
        <div style={{ marginBottom: 28 }}>
          <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 4 }}>{artist.name || "Show Advance"}</div>
          <div style={{ fontSize: 14, color: T.muted }}>
            {show.date ? new Date(show.date + "T12:00:00").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" }) : ""}
            {venue.name ? ` · ${venue.name}` : ""}
          </div>
        </div>

        {/* TM Advance sections (read-only) */}
        <div style={{ marginBottom: 8, fontFamily: FF, fontSize: 11, fontWeight: 700, letterSpacing: 1, color: T.muted, textTransform: "uppercase" }}>Advance from Tour Manager</div>
        {Object.keys(tmData).length === 0 && (
          <div style={{ padding: "14px 18px", background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, fontFamily: FF, fontSize: 12, color: T.muted, marginBottom: 20 }}>
            No advance data submitted yet.
          </div>
        )}
        {Object.entries(tmData).filter(([k]) => !k.startsWith("_")).map(([secId, secData]) => (
          typeof secData === "object" && secData !== null ? (
            <div key={secId} style={{ marginBottom: 8, background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, overflow: "hidden" }}>
              <div style={{ padding: "10px 16px", fontFamily: FF, fontSize: 11, fontWeight: 700, letterSpacing: 0.5, color: T.text, textTransform: "uppercase" }}>{secId.replace(/_/g, " ")}</div>
              <div style={{ padding: "0 16px 12px" }}>
                {Object.entries(secData).filter(([k]) => !k.startsWith("_")).map(([fk, fv]) => fv ? (
                  <div key={fk} style={{ display: "flex", gap: 12, marginBottom: 6 }}>
                    <div style={{ fontFamily: FF, fontSize: 11, color: T.muted, minWidth: 120, textTransform: "capitalize" }}>{fk.replace(/_/g, " ")}</div>
                    <div style={{ fontFamily: FF, fontSize: 12, color: T.text }}>{String(fv)}</div>
                  </div>
                ) : null)}
              </div>
            </div>
          ) : null
        ))}

        {/* Venue-side sections (editable) */}
        <div style={{ marginTop: 28, marginBottom: 8, fontFamily: FF, fontSize: 11, fontWeight: 700, letterSpacing: 1, color: T.muted, textTransform: "uppercase" }}>Your Information for the Tour Manager</div>
        {VENUE_ADVANCE_SECTIONS.map(sec => {
          const isOpen = !!openSecs[sec.id];
          const secData = venueData[sec.id] || {};
          const filled  = sec.fields.filter(f => secData[f.key]).length;
          return (
            <div key={sec.id} style={{ marginBottom: 8, background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, overflow: "hidden" }}>
              <div onClick={() => toggleSec(sec.id)}
                style={{ padding: "12px 16px", display: "flex", alignItems: "center", cursor: "pointer", userSelect: "none" }}>
                <span style={{ fontFamily: FF, fontSize: 12, fontWeight: 700 }}>{sec.label}</span>
                {filled > 0 && <span style={{ fontFamily: FF, fontSize: 11, color: T.green, marginLeft: 8 }}>✓ {filled} filled</span>}
                <span style={{ marginLeft: "auto", fontFamily: FF, fontSize: 14, color: T.muted }}>{isOpen ? "▲" : "▼"}</span>
              </div>
              {isOpen && (
                <div style={{ padding: "0 16px 16px", borderTop: `1px solid ${T.border}` }}>
                  <div style={{ paddingTop: 14 }}>
                    {sec.fields.map(f => (
                      <div key={f.key} style={{ marginBottom: 12 }}>
                        <div style={{ fontFamily: FF, fontSize: 11, color: T.muted, letterSpacing: 0.5, marginBottom: 4 }}>{f.label}</div>
                        {f.type === "textarea" ? (
                          <textarea value={secData[f.key] || ""} onChange={e => updateField(sec.id, f.key, e.target.value)} rows={3}
                            style={{ width: "100%", fontFamily: FF, fontSize: 12, padding: "7px 10px", background: T.inp, border: `1px solid ${T.inpBorder}`, borderRadius: 4, color: T.text, outline: "none", boxSizing: "border-box", resize: "vertical" }} />
                        ) : (
                          <input type="text" value={secData[f.key] || ""} onChange={e => updateField(sec.id, f.key, e.target.value)}
                            style={{ width: "100%", fontFamily: FF, fontSize: 12, padding: "7px 10px", background: T.inp, border: `1px solid ${T.inpBorder}`, borderRadius: 4, color: T.text, outline: "none", boxSizing: "border-box" }} />
                        )}
                      </div>
                    ))}
                    <button onClick={() => saveSection(sec.id)} disabled={saving[sec.id]}
                      style={{ fontFamily: FF, fontSize: 11, fontWeight: 700, padding: "7px 18px", background: T.btn, color: T.btnText, border: "none", borderRadius: 4, cursor: "pointer" }}>
                      {saving[sec.id] ? "Saving..." : saved[sec.id] ? "Saved ✓" : "Save Section"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {/* V-3: Settlement Sign-off ─────────────────────────────────────── */}
        <div style={{ marginTop: 32, marginBottom: 40 }}>
          <div style={{ marginBottom: 8, fontFamily: FF, fontSize: 11, fontWeight: 700, letterSpacing: 1, color: T.muted, textTransform: "uppercase" }}>Settlement Sign-off</div>

          {/* TM deal terms — read-only (from show.settlement JSONB on the DB row) */}
          {(() => {
            const st = show?.settlement || {};
            const hasDealTerms = st.guarantee || st.dealType || st.paymentMethod || st.vsTerms;
            return hasDealTerms ? (
              <div style={{ marginBottom: 10, padding: "12px 16px", background: T.card, border: `1px solid ${T.border}`, borderRadius: 8 }}>
                <div style={{ fontFamily: FF, fontSize: 11, fontWeight: 700, color: T.muted, letterSpacing: 0.5, marginBottom: 10, textTransform: "uppercase" }}>Deal Terms from Tour Manager</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 20px" }}>
                  {st.guarantee && (
                    <div>
                      <div style={{ fontFamily: FF, fontSize: 11, color: T.muted, letterSpacing: 0.5, marginBottom: 2 }}>GUARANTEE</div>
                      <div style={{ fontFamily: FF, fontSize: 14, fontWeight: 700, color: T.amber }}>{st.guarantee}</div>
                    </div>
                  )}
                  {st.dealType && (
                    <div>
                      <div style={{ fontFamily: FF, fontSize: 11, color: T.muted, letterSpacing: 0.5, marginBottom: 2 }}>DEAL TYPE</div>
                      <div style={{ fontFamily: FF, fontSize: 14, color: T.text }}>{st.dealType}</div>
                    </div>
                  )}
                  {st.vsTerms && (
                    <div style={{ gridColumn: "span 2" }}>
                      <div style={{ fontFamily: FF, fontSize: 11, color: T.muted, letterSpacing: 0.5, marginBottom: 2 }}>VS TERMS</div>
                      <div style={{ fontFamily: FF, fontSize: 12, color: T.text }}>{st.vsTerms}</div>
                    </div>
                  )}
                  {st.paymentMethod && (
                    <div>
                      <div style={{ fontFamily: FF, fontSize: 11, color: T.muted, letterSpacing: 0.5, marginBottom: 2 }}>PAYMENT METHOD</div>
                      <div style={{ fontFamily: FF, fontSize: 12, color: T.text }}>{st.paymentMethod}</div>
                    </div>
                  )}
                  {st.deposit && (
                    <div>
                      <div style={{ fontFamily: FF, fontSize: 11, color: T.muted, letterSpacing: 0.5, marginBottom: 2 }}>DEPOSIT</div>
                      <div style={{ fontFamily: FF, fontSize: 12, color: T.text }}>
                        {st.deposit}{st.depositReceived ? <span style={{ color: T.green, marginLeft: 6 }}>✓ Received</span> : null}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div style={{ marginBottom: 10, padding: "10px 14px", background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, fontFamily: FF, fontSize: 12, color: T.muted }}>
                Deal terms not yet added by tour manager.
              </div>
            );
          })()}

          {/* Confirmation status row */}
          <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
            <div style={{ flex: 1, padding: "10px 14px", background: T.card, border: `1px solid ${tmSettled ? T.green : T.border}`, borderRadius: 8, display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontFamily: FF, fontSize: 11, fontWeight: 700, color: T.muted, letterSpacing: 0.5 }}>TOUR MANAGER</span>
              <span style={{ marginLeft: "auto", fontFamily: FF, fontSize: 11, color: tmSettled ? T.green : T.muted }}>
                {tmSettled ? "✓ Confirmed" : "Pending…"}
              </span>
            </div>
            <div style={{ flex: 1, padding: "10px 14px", background: T.card, border: `1px solid ${venueSettled ? T.green : T.border}`, borderRadius: 8, display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontFamily: FF, fontSize: 11, fontWeight: 700, color: T.muted, letterSpacing: 0.5 }}>VENUE</span>
              <span style={{ marginLeft: "auto", fontFamily: FF, fontSize: 11, color: venueSettled ? T.green : T.muted }}>
                {venueSettled ? "✓ Confirmed" : "Pending…"}
              </span>
            </div>
          </div>

          {/* Both confirmed banner */}
          {tmSettled && venueSettled && (
            <div style={{ marginBottom: 12, padding: "12px 16px", background: isLight ? "#e8f5ee" : "#0d1f14", border: `1px solid ${T.green}`, borderRadius: 8, display: "flex", alignItems: "center", gap: 10 }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={T.green} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><path d="M22 4 12 14.01l-3-3"/></svg>
              <div>
                <div style={{ fontFamily: FF, fontSize: 12, fontWeight: 700, color: T.green }}>Settlement Confirmed by Both Parties</div>
                <div style={{ fontFamily: FF, fontSize: 11, color: T.muted, marginTop: 2 }}>Both the tour manager and venue have signed off on the settlement.</div>
              </div>
            </div>
          )}

          {/* Venue confirm button */}
          {!venueSettled && (
            <button
              onClick={confirmVenueSettlement}
              disabled={confirmingSettle}
              style={{ fontFamily: FF, fontSize: 12, fontWeight: 700, padding: "10px 24px", background: T.btn, color: T.btnText, border: "none", borderRadius: 8, cursor: "pointer", display: "block", width: "100%", marginBottom: 12 }}>
              {confirmingSettle ? "Confirming…" : "✓ Confirm Settlement"}
            </button>
          )}

          {/* Settlement notes */}
          <div>
            <div style={{ fontFamily: FF, fontSize: 11, color: T.muted, letterSpacing: 0.5, marginBottom: 6, display: "flex", alignItems: "center", gap: 8 }}>
              SETTLEMENT NOTES
              {!settleNoteEdit && (
                <button onClick={() => setSettleNoteEdit(true)}
                  style={{ fontFamily: FF, fontSize: 11, color: T.btn, background: "none", border: "none", cursor: "pointer", padding: 0 }}>
                  {settleNotes ? "Edit" : "+ Add note"}
                </button>
              )}
            </div>
            {settleNoteEdit ? (
              <div>
                <textarea
                  value={settleNotes}
                  onChange={e => setSettleNotes(e.target.value)}
                  rows={3}
                  placeholder="Add any settlement notes visible to both parties…"
                  style={{ width: "100%", fontFamily: FF, fontSize: 12, padding: "8px 10px", background: T.inp, border: `1px solid ${T.inpBorder}`, borderRadius: 4, color: T.text, outline: "none", boxSizing: "border-box", resize: "vertical" }}
                />
                <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
                  <button onClick={saveSettleNotes}
                    style={{ fontFamily: FF, fontSize: 11, fontWeight: 700, padding: "6px 16px", background: T.btn, color: T.btnText, border: "none", borderRadius: 4, cursor: "pointer" }}>
                    Save Note
                  </button>
                  <button onClick={() => setSettleNoteEdit(false)}
                    style={{ fontFamily: FF, fontSize: 11, padding: "6px 12px", background: "none", color: T.muted, border: `1px solid ${T.border}`, borderRadius: 4, cursor: "pointer" }}>
                    Cancel
                  </button>
                </div>
              </div>
            ) : settleNotes ? (
              <div style={{ padding: "8px 12px", background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, fontFamily: FF, fontSize: 12, color: T.text, whiteSpace: "pre-wrap" }}>
                {settleNotes}
              </div>
            ) : null}
          </div>

          {/* Message thread with TM */}
          {linkId && (
            <AdvanceMessageThread
              linkId={linkId}
              senderType="venue"
              senderName=""
              T={T}
              FF={FF}
            />
          )}
        </div>

      </div>
    </div>
  );
}

// ── VenueShowCard ─────────────────────────────────────────────────────────────
// Reusable show card used in both Shows tab and History tab of VenueDashboard
function VenueShowCard({ link, T, FF, onViewAdvance, isPast = false }) {
  const m = link.metadata || {};
  const tmSettled   = !!link.tm_settlement_confirmed;
  const venSettled  = !!link.venue_settlement_confirmed;
  const bothSettled = tmSettled && venSettled;
  const anySettled  = tmSettled || venSettled;

  const settleBorderColor = bothSettled ? T.green : anySettled ? T.amber : T.border;

  return (
    <div style={{ background: T.card, border: `1px solid ${bothSettled ? T.green : T.border}`, borderRadius: 8, marginBottom: 10, overflow: "hidden", opacity: (isPast && !bothSettled) ? 0.85 : 1 }}>
      {/* Main row */}
      <div style={{ padding: "14px 20px", display: "flex", alignItems: "center", gap: 16 }}>
        {/* Date block */}
        <div style={{ minWidth: 56, textAlign: "center" }}>
          <div style={{ fontFamily: FF, fontSize: 11, color: T.muted, letterSpacing: 0.5 }}>
            {m.show_date ? new Date(m.show_date + "T12:00:00").toLocaleDateString("en-US", { month: "short" }) : "—"}
          </div>
          <div style={{ fontFamily: FF, fontSize: 20, fontWeight: 700, lineHeight: 1 }}>
            {m.show_date ? new Date(m.show_date + "T12:00:00").getDate() : "—"}
          </div>
          <div style={{ fontFamily: FF, fontSize: 11, color: T.muted }}>
            {m.show_date ? new Date(m.show_date + "T12:00:00").getFullYear() : ""}
          </div>
        </div>
        {/* Show info */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: FF, fontSize: 14, fontWeight: 700, marginBottom: 3 }}>{m.artist_name || "Artist"}</div>
          <div style={{ fontFamily: FF, fontSize: 11, color: T.muted }}>{m.tour_name || ""}</div>
        </div>
        {/* Right side: settlement badge + advance button */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
          {/* Settlement status chip */}
          {isPast && (
            <div style={{
              padding: "3px 10px", borderRadius: 16, fontFamily: FF, fontSize: 11, fontWeight: 700, letterSpacing: 0.5,
              background: bothSettled ? (T.green === "#3ecf6e" ? "#0d1f14" : "#e8f5ee") : anySettled ? (T.amber === "#f0a500" ? "#1a1200" : "#fff8e6") : "transparent",
              color: bothSettled ? T.green : anySettled ? T.amber : T.muted,
              border: `1px solid ${bothSettled ? T.green : anySettled ? T.amber : T.border}`,
            }}>
              {bothSettled ? "✓ Settled" : tmSettled ? "TM ✓" : venSettled ? "Venue ✓" : "Unsettled"}
            </div>
          )}
          {m.advance_token ? (
            <button
              onClick={() => onViewAdvance({ token: m.advance_token, linkId: link.id, artistName: m.artist_name || "Show" })}
              style={{ fontFamily: FF, fontSize: 11, fontWeight: 700, padding: "7px 14px", background: T.btn, color: T.btnText, border: "none", borderRadius: 8, cursor: "pointer", letterSpacing: 0.5 }}>
              {isPast ? "View Details" : "View Advance"}
            </button>
          ) : (
            <span style={{ fontFamily: FF, fontSize: 11, color: T.muted }}>No advance</span>
          )}
        </div>
      </div>
      {/* Settlement strip — shown on past shows when not both settled */}
      {isPast && !bothSettled && (
        <div style={{ padding: "6px 20px 8px", borderTop: `1px solid ${T.border}`, display: "flex", alignItems: "center", gap: 12, background: T.page }}>
          <span style={{ fontFamily: FF, fontSize: 11, color: T.muted, letterSpacing: 1, textTransform: "uppercase" }}>Settlement outstanding</span>
          <span style={{ fontFamily: FF, fontSize: 11, color: tmSettled ? T.green : T.muted }}>
            TM {tmSettled ? "✓" : "○"}
          </span>
          <span style={{ fontFamily: FF, fontSize: 11, color: venSettled ? T.green : T.muted }}>
            Venue {venSettled ? "✓" : "○"}
          </span>
          {m.advance_token && (
            <button
              onClick={() => onViewAdvance({ token: m.advance_token, linkId: link.id, artistName: m.artist_name || "Show" })}
              style={{ fontFamily: FF, fontSize: 11, fontWeight: 700, padding: "3px 10px", background: "none", color: T.btn, border: `1px solid ${T.btn}`, borderRadius: 4, cursor: "pointer", marginLeft: "auto" }}>
              Confirm Settlement →
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ── Festival Dashboard ───────────────────────────────────────────────────────
// F-0 + F-1: Profile, stages, artist roster, scheduling grid
// ── Runner View (F-5c) ───────────────────────────────────────────────────────
// Accessible via /run/:token (public) or by an org member with role='runner'
function RunnerView({ token, orgId, orgData, user, lightMode: lmProp }) {
  const isLight = lmProp !== undefined ? lmProp : localStorage.getItem("lightMode") !== "false";
  const T = isLight
    ? { page: "#f0ede8", card: "#e8e4de", border: "#d0ccc6", inp: "#e0dbd4", inpBorder: "#ccc8c2", text: "#1a1917", muted: "#8a8784", sub: "#9a9794", btn: "#c47f00", btnText: "#fff", amber: "#c47f00", green: "#2a7d4f", red: "#c0392b" }
    : { page: "#0d0d0d", card: "#141414", border: "var(--panel-border)",    inp: "#1a1a1a", inpBorder: "#2a2a2a", text: "#fff",    muted: "#555",    sub: "#444",    btn: "var(--accent)", btnText: "#0d0d0d", amber: "var(--accent)", green: "var(--success)", red: "#f87171" };
  const FF = "'Helvetica Neue', Helvetica, Arial, sans-serif";

  const [runs,    setRuns]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [orgName, setOrgName] = useState(orgData?.name || "");
  const [error,   setError]   = useState(null);

  // Token-based load (public share link)
  useEffect(() => {
    if (!token) return;
    supabase.from("festival_runs")
      .select("*, organizations(name)")
      .eq("day_token", token)
      .order("time_minutes", { ascending: true, nullsFirst: false })
      .then(({ data, error: err }) => {
        if (err) { setError("Could not load run sheet."); setLoading(false); return; }
        setRuns(data || []);
        if (data?.[0]?.organizations?.name) setOrgName(data[0].organizations.name);
        setLoading(false);
      });
  }, [token]);

  // Account-based load (runner role — show today's runs)
  useEffect(() => {
    if (token || !orgId) return;
    const { dates_start } = orgData?.profile || {};
    const todayIdx = (() => {
      if (!dates_start) return 0;
      const diff = Math.round((new Date() - new Date(dates_start + "T12:00:00")) / 86400000);
      return Math.max(0, diff);
    })();
    supabase.from("festival_runs")
      .select("*").eq("org_id", orgId).eq("day_index", todayIdx)
      .order("time_minutes", { ascending: true, nullsFirst: false })
      .then(({ data }) => { setRuns(data || []); setOrgName(orgData?.name || ""); setLoading(false); });
  }, [token, orgId, orgData]);

  const fmtRunTime = (m) => {
    if (m == null) return "–";
    const h = Math.floor(m / 60) % 24, mn = m % 60, ampm = h >= 12 ? "PM" : "AM";
    return `${h % 12 || 12}${mn ? `:${String(mn).padStart(2,"0")}` : ""} ${ampm}`;
  };

  const cycleStatus = async (run) => {
    const CYCLE = { pending: "in_progress", in_progress: "completed", completed: "pending" };
    const next = CYCLE[run.status] || "in_progress";
    setRuns(prev => prev.map(r => r.id === run.id ? { ...r, status: next } : r));
    await supabase.from("festival_runs").update({ status: next }).eq("id", run.id);
  };

  const STATUS_CONFIG = {
    pending:     { label: "Pending",     bg: "#88888818", color: "#888",    dot: "#888" },
    in_progress: { label: "In Progress", bg: "#f0a50018", color: "var(--accent)", dot: "var(--accent)" },
    completed:   { label: "Done",        bg: "#3ecf6e18", color: "var(--success)", dot: "var(--success)" },
  };

  const dayDate = (() => {
    if (!runs.length) return null;
    const { dates_start } = orgData?.profile || {};
    if (!dates_start && !token) return null;
    // For token access, we don't know the date — derive from day_index if possible
    return null; // display handled below
  })();

  if (loading) return (
    <div style={{ minHeight: "100vh", background: T.page, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: FF }}>
      <div style={{ fontSize: 14, color: T.muted }}>Loading run sheet…</div>
    </div>
  );

  if (error) return (
    <div style={{ minHeight: "100vh", background: T.page, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: FF, padding: 24 }}>
      <div style={{ textAlign: "center" }}>
        <div style={{ fontSize: 14, color: T.text, marginBottom: 8 }}>Run sheet not found</div>
        <div style={{ fontSize: 12, color: T.muted }}>{error}</div>
      </div>
    </div>
  );

  const pendingCount = runs.filter(r => r.status === "pending").length;
  const doneCount = runs.filter(r => r.status === "completed").length;

  return (
    <div style={{ minHeight: "100vh", background: T.page, fontFamily: FF, color: T.text, maxWidth: 480, margin: "0 auto" }}>
      {/* Header */}
      <div style={{ background: T.card, borderBottom: `1px solid ${T.border}`, padding: "16px 20px 14px" }}>
        <div style={{ fontSize: 11, color: T.btn, letterSpacing: 2, fontWeight: 700, marginBottom: 3 }}>
          {orgName ? orgName.toUpperCase() : "ADVANCD"}
        </div>
        <div style={{ fontSize: 20, fontWeight: 800, letterSpacing: -0.5 }}>Run Sheet</div>
        <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
          <span style={{ fontSize: 11, color: T.muted }}>{runs.length} run{runs.length !== 1 ? "s" : ""}</span>
          {doneCount > 0 && <span style={{ fontSize: 11, color: T.green }}>{doneCount} done</span>}
          {pendingCount > 0 && <span style={{ fontSize: 11, color: T.muted }}>{pendingCount} pending</span>}
        </div>
      </div>

      {/* Run cards */}
      <div style={{ padding: "12px 16px", display: "flex", flexDirection: "column", gap: 10 }}>
        {runs.length === 0 ? (
          <div style={{ textAlign: "center", padding: "48px 16px" }}>
            <div style={{ fontSize: 14, color: T.muted }}>No runs on this sheet</div>
            <div style={{ fontSize: 11, color: T.sub, marginTop: 4 }}>Check back later or contact your coordinator.</div>
          </div>
        ) : (
          runs.map(run => {
            const sc = STATUS_CONFIG[run.status] || STATUS_CONFIG.pending;
            const isDone = run.status === "completed";
            return (
              <div key={run.id}
                style={{ background: T.card, border: `1px solid ${isDone ? T.border : sc.dot + "44"}`, borderRadius: 12, padding: "16px 16px 14px", opacity: isDone ? 0.65 : 1 }}>
                {/* Time + status row */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                  <div style={{ fontSize: 20, fontWeight: 800, color: isDone ? T.muted : T.text, letterSpacing: -0.5 }}>
                    {fmtRunTime(run.time_minutes)}
                  </div>
                  <button onClick={() => cycleStatus(run)}
                    style={{ fontFamily: FF, fontSize: 11, fontWeight: 700, padding: "5px 14px", borderRadius: 16,
                      background: sc.bg, color: sc.color, border: `1px solid ${sc.dot}44`, cursor: "pointer" }}>
                    {sc.label}
                  </button>
                </div>

                {/* Name */}
                <div style={{ fontSize: 16, fontWeight: 700, color: isDone ? T.muted : T.text, textDecoration: isDone ? "line-through" : "none", marginBottom: 6 }}>
                  {run.pickup_name}
                </div>

                {/* Route */}
                {(run.pickup_location || run.dropoff_location) && (
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                    {run.pickup_location && (
                      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width={12} height={12} style={{ color: T.green, flexShrink: 0 }}>
                          <circle cx="12" cy="10" r="3"/><path d="M12 2a8 8 0 00-8 8c0 5.25 8 14 8 14s8-8.75 8-14a8 8 0 00-8-8z"/>
                        </svg>
                        <span style={{ fontSize: 12, color: T.muted }}>{run.pickup_location}</span>
                      </div>
                    )}
                    {run.pickup_location && run.dropoff_location && (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width={10} height={10} style={{ color: T.sub, flexShrink: 0 }}>
                        <polyline points="9 18 15 12 9 6"/>
                      </svg>
                    )}
                    {run.dropoff_location && (
                      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width={12} height={12} style={{ color: T.amber, flexShrink: 0 }}>
                          <circle cx="12" cy="10" r="3"/><path d="M12 2a8 8 0 00-8 8c0 5.25 8 14 8 14s8-8.75 8-14a8 8 0 00-8-8z"/>
                        </svg>
                        <span style={{ fontSize: 12, color: T.muted }}>{run.dropoff_location}</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Runner + notes */}
                {run.runner_name && (
                  <div style={{ fontSize: 11, color: T.sub, marginBottom: 4 }}>Runner: {run.runner_name}</div>
                )}
                {run.notes && (
                  <div style={{ fontSize: 12, color: T.muted, marginTop: 4, padding: "7px 10px", background: `${T.btn}0a`, borderRadius: 8, borderLeft: `2px solid ${T.btn}44` }}>
                    {run.notes}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Footer */}
      <div style={{ padding: "8px 20px 32px", textAlign: "center" }}>
        <div style={{ fontSize: 11, color: T.sub, letterSpacing: 1 }}>ADVANCD · TAP STATUS TO UPDATE</div>
      </div>
    </div>
  );
}

function FestivalDashboard({ orgData, user, onUpdateOrg }) {
  const isLight = localStorage.getItem("lightMode") !== "false";
  const T = isLight
    ? { page: "#f0ede8", card: "#e8e4de", border: "#d0ccc6", inp: "#e0dbd4", inpBorder: "#ccc8c2", text: "#1a1917", muted: "#8a8784", sub: "#9a9794", btn: "#c47f00", btnText: "#fff", rowHov: "#dedad4", amber: "#c47f00", green: "#2a7d4f", red: "#c0392b", cardAlt: "#dedad4" }
    : { page: "#0d0d0d", card: "#141414", border: "var(--panel-border)",    inp: "#1a1a1a", inpBorder: "#2a2a2a", text: "#fff",    muted: "#555",    sub: "#444",    btn: "var(--accent)", btnText: "#0d0d0d", rowHov: "#1e1e1e",  amber: "var(--accent)", green: "var(--success)", red: "#f87171", cardAlt: "#1a1a1a" };
  const FF = "'Helvetica Neue', Helvetica, Arial, sans-serif";

  const [tab,            setTab]            = useState("artists");  // 'artists' | 'stages' | 'schedule' | 'profile'
  const [stages,         setStages]         = useState([]);
  const [stagesLoading,  setStagesLoading]  = useState(true);
  const [realtimeStatus, setRealtimeStatus] = useState("connecting");

  // Stage modal state
  const [stageModal,     setStageModal]     = useState(null); // null | { mode:'add'|'edit', stage:{...} }
  const [stageName,      setStageName]      = useState("");
  const [stageCapacity,  setStageCapacity]  = useState("");
  const [stageColor,     setStageColor]     = useState("#f0a500");
  const [stageSaving,    setStageSaving]    = useState(false);

  // Profile editing
  const [profileEdit,    setProfileEdit]    = useState(false);
  const [profileDraft,   setProfileDraft]   = useState({});
  const [profileSaving,  setProfileSaving]  = useState(false);

  // F-1: Artist roster + scheduling
  const [festivalArtists, setFestivalArtists] = useState([]);
  const [artistsLoading,  setArtistsLoading]  = useState(true);
  const [orgMembers,      setOrgMembers]      = useState([]);
  const [docView,         setDocView]         = useState(false); // false = list, true = matrix
  const [selectedDay,     setSelectedDay]     = useState(0);
  const [statusFilter,    setStatusFilter]    = useState("all");
  // Artist modal form state
  const [artistModal,  setArtistModal]  = useState(null); // null | { mode:'add'|'edit'|'schedule', artist?, stageId?, startMins? }
  const [aName,        setAName]        = useState("");
  const [aStageId,     setAStageId]     = useState("");
  const [aDayIndex,    setADayIndex]    = useState(0);
  const [aStartMins,   setAStartMins]   = useState(null);
  const [aDuration,    setADuration]    = useState(60);
  const [aStatus,      setAStatus]      = useState("unconfirmed");
  const [aEmail,       setAEmail]       = useState("");
  const [aNotes,       setANotes]       = useState("");
  const [artistSaving, setArtistSaving] = useState(false);
  // F-2: Advancing tab filters + inline notes
  const [advDay,         setAdvDay]         = useState("all");
  const [advStage,       setAdvStage]       = useState("all");
  const [advDocFilter,   setAdvDocFilter]   = useState("all"); // "all" | doc key (show only artists missing that doc)
  const [advLiaison,     setAdvLiaison]     = useState("all");
  const [editingNotesId, setEditingNotesId] = useState(null);
  const [notesDraft,     setNotesDraft]     = useState("");
  // F-3: Liaison assignment
  const [aLiaisonId,     setALiaisonId]     = useState(""); // artist modal
  const [myName,         setMyName]         = useState(""); // current user's display name (Profile tab)
  // F-4: Shows tab (event_links)
  const [festEventLinks,         setFestEventLinks]         = useState([]);
  const [festEventLinksLoading,  setFestEventLinksLoading]  = useState(true);
  const [festShowsSubtab,        setFestShowsSubtab]        = useState("shows"); // "shows" | "requests"
  const [expandedLinkId,         setExpandedLinkId]         = useState(null);
  const [festLinkAdvData,        setFestLinkAdvData]        = useState({}); // linkId → { secId → { key: val } }
  const [festLinkDrafts,         setFestLinkDrafts]         = useState({}); // linkId+secId → field drafts
  const [festLinkSaving,         setFestLinkSaving]         = useState({}); // linkId+secId → bool
  const [festLinkSaved,          setFestLinkSaved]          = useState({}); // linkId+secId → bool
  const [festLinkTmData,         setFestLinkTmData]         = useState({}); // linkId → TM advance data
  const [festLinkTmLoading,      setFestLinkTmLoading]      = useState({}); // linkId → bool
  const [festLinkOpenSecs,       setFestLinkOpenSecs]       = useState({}); // linkId+secId → bool
  // F-5d: Festival section flags on TM advance sections
  const [festFlagNoteOpen,       setFestFlagNoteOpen]       = useState({}); // "linkId:secId" → bool
  const [festFlagInput,          setFestFlagInput]          = useState({}); // "linkId:secId" → draft note string
  // ORG-1: Enter a show code
  const [festCodeInput,          setFestCodeInput]          = useState("");
  const [festCodeLookup,         setFestCodeLookup]         = useState(null); // { loading, result, error }
  // F-5: Credentials
  const [credentials,     setCredentials]     = useState([]);
  const [credLoading,     setCredLoading]     = useState(true);
  const [credModal,       setCredModal]       = useState(null); // null | { mode:'add'|'edit', cred? }
  const [credTypeFilter,  setCredTypeFilter]  = useState("all");
  const [credName,        setCredName]        = useState("");
  const [credType,        setCredType]        = useState("artist");
  const [credCompany,     setCredCompany]     = useState("");
  const [credEmail,       setCredEmail]       = useState("");
  const [credNotes,       setCredNotes]       = useState("");
  const [credSaving,      setCredSaving]      = useState(false);
  // F-5b: Vendors
  const [vendors,           setVendors]           = useState([]);
  const [vendorsLoading,    setVendorsLoading]    = useState(true);
  const [vendorModal,       setVendorModal]       = useState(null); // null | { mode:'add'|'edit', vendor? }
  const [vendorTypeFilter,  setVendorTypeFilter]  = useState("all");
  const [vName,             setVName]             = useState("");
  const [vType,             setVType]             = useState("catering");
  const [vContactName,      setVContactName]      = useState("");
  const [vContactEmail,     setVContactEmail]     = useState("");
  const [vContactPhone,     setVContactPhone]     = useState("");
  const [vStatus,           setVStatus]           = useState("pending");
  const [vDays,             setVDays]             = useState([]); // array of day indices
  const [vContractStatus,   setVContractStatus]   = useState("none");
  const [vNotes,            setVNotes]            = useState("");
  const [vendorSaving,      setVendorSaving]      = useState(false);
  // F-5c: Runs
  const [runs,           setRuns]           = useState([]);
  const [runsLoading,    setRunsLoading]    = useState(true);
  const [runDayIndex,    setRunDayIndex]    = useState(0);
  const [runModal,       setRunModal]       = useState(null); // null | { mode:'add'|'edit', run? }
  const [rPickupName,    setRPickupName]    = useState("");
  const [rPickupLoc,     setRPickupLoc]     = useState("");
  const [rDropoffLoc,    setRDropoffLoc]    = useState("");
  const [rRunnerName,    setRRunnerName]    = useState("");
  const [rTimeMins,      setRTimeMins]      = useState(null);
  const [rNotes,         setRNotes]         = useState("");
  const [runSaving,      setRunSaving]      = useState(false);
  const [dayTokens,      setDayTokens]      = useState({}); // dayIndex → token string
  const [copiedToken,    setCopiedToken]    = useState(null);
  // Drag-to-reschedule
  const [dragging,   setDragging]   = useState(null); // { artistId, origStart, origStageId, currentStart, currentStageId, duration, offsetY }
  const dragRef      = useRef(null);  // live copy of dragging for event handlers (avoids stale closure)
  const columnRefs   = useRef({});    // stageId → column body DOM element
  const wasDragRef   = useRef(false); // suppresses the click that fires after mouseup

  // ── Load stages ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!orgData?.id) return;
    supabase.from("festival_stages")
      .select("*")
      .eq("org_id", orgData.id)
      .order("stage_order", { ascending: true })
      .then(({ data }) => { setStages(data || []); setStagesLoading(false); });
  }, [orgData?.id]);

  // ── Realtime for festival_stages ────────────────────────────────────────────
  useEffect(() => {
    if (!orgData?.id) return;
    setRealtimeStatus("connecting");
    const channel = supabase
      .channel(`festival-${orgData.id}`)
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "festival_stages",
        filter: `org_id=eq.${orgData.id}` }, payload => {
        setStages(prev => prev.find(s => s.id === payload.new.id) ? prev : [...prev, payload.new]
          .sort((a, b) => a.stage_order - b.stage_order));
      })
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "festival_stages",
        filter: `org_id=eq.${orgData.id}` }, payload => {
        setStages(prev => prev.map(s => s.id === payload.new.id ? { ...s, ...payload.new } : s));
      })
      .on("postgres_changes", { event: "DELETE", schema: "public", table: "festival_stages",
        filter: `org_id=eq.${orgData.id}` }, payload => {
        setStages(prev => prev.filter(s => s.id !== payload.old.id));
      })
      .subscribe(status => {
        if (status === "SUBSCRIBED")    setRealtimeStatus("connected");
        if (status === "CLOSED")        setRealtimeStatus("disconnected");
        if (status === "CHANNEL_ERROR") setRealtimeStatus("disconnected");
      });
    return () => { supabase.removeChannel(channel); };
  }, [orgData?.id]);

  // ── Load festival artists ────────────────────────────────────────────────────
  useEffect(() => {
    if (!orgData?.id) return;
    supabase.from("festival_artists")
      .select("*").eq("org_id", orgData.id)
      .order("day_index", { ascending: true })
      .order("set_start", { ascending: true, nullsFirst: false })
      .then(({ data }) => { setFestivalArtists(data || []); setArtistsLoading(false); });
  }, [orgData?.id]);

  // ── Load event_links for this festival org (F-4) ────────────────────────────
  useEffect(() => {
    if (!orgData?.id) return;
    supabase.from("event_links").select("*").eq("org_id", orgData.id)
      .order("created_at", { ascending: false })
      .then(({ data }) => {
        const links = data || [];
        setFestEventLinks(links);
        setFestEventLinksLoading(false);
        // Seed advance data from DB
        const advMap = {};
        links.forEach(l => { if (l.venue_advance_data) advMap[l.id] = l.venue_advance_data; });
        setFestLinkAdvData(advMap);
      });
  }, [orgData?.id]);

  // ── Realtime for event_links (festival org) ──────────────────────────────────
  useEffect(() => {
    if (!orgData?.id) return;
    const ch = supabase.channel(`festival-links-${orgData.id}`)
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "event_links",
        filter: `org_id=eq.${orgData.id}` }, p => {
        setFestEventLinks(prev => prev.find(l => l.id === p.new.id) ? prev : [p.new, ...prev]);
      })
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "event_links",
        filter: `org_id=eq.${orgData.id}` }, p => {
        setFestEventLinks(prev => prev.map(l => l.id === p.new.id ? { ...l, ...p.new } : l));
        if (p.new.venue_advance_data) {
          setFestLinkAdvData(prev => ({ ...prev, [p.new.id]: p.new.venue_advance_data }));
        }
      })
      .on("postgres_changes", { event: "DELETE", schema: "public", table: "event_links",
        filter: `org_id=eq.${orgData.id}` }, p => {
        setFestEventLinks(prev => prev.filter(l => l.id !== p.old.id));
      })
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [orgData?.id]);

  // ── Load org members (for liaison display) ───────────────────────────────────
  useEffect(() => {
    if (!orgData?.id) return;
    supabase.from("organization_members").select("id, role, user_id, name").eq("org_id", orgData.id)
      .then(({ data }) => {
        setOrgMembers(data || []);
        // Seed current user's name into the myName field
        const me = (data || []).find(m => m.user_id === user?.id);
        if (me?.name) setMyName(me.name);
      });
  }, [orgData?.id]);

  // ── Realtime for festival_artists ────────────────────────────────────────────
  useEffect(() => {
    if (!orgData?.id) return;
    const ch = supabase.channel(`festival-artists-${orgData.id}`)
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "festival_artists",
        filter: `org_id=eq.${orgData.id}` }, p => {
        setFestivalArtists(prev => prev.find(a => a.id === p.new.id) ? prev : [...prev, p.new]
          .sort((a, b) => (a.day_index - b.day_index) || ((a.set_start ?? 9999) - (b.set_start ?? 9999))));
      })
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "festival_artists",
        filter: `org_id=eq.${orgData.id}` }, p => {
        setFestivalArtists(prev => prev.map(a => a.id === p.new.id ? { ...a, ...p.new } : a));
      })
      .on("postgres_changes", { event: "DELETE", schema: "public", table: "festival_artists",
        filter: `org_id=eq.${orgData.id}` }, p => {
        setFestivalArtists(prev => prev.filter(a => a.id !== p.old.id));
      })
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [orgData?.id]);

  // ── F-5: Load credentials ────────────────────────────────────────────────────
  useEffect(() => {
    if (!orgData?.id) return;
    supabase.from("festival_credentials")
      .select("*").eq("org_id", orgData.id)
      .order("type", { ascending: true })
      .order("name", { ascending: true })
      .then(({ data }) => { setCredentials(data || []); setCredLoading(false); });
  }, [orgData?.id]);

  // ── F-5b: Load vendors ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!orgData?.id) return;
    supabase.from("festival_vendors")
      .select("*").eq("org_id", orgData.id)
      .order("type", { ascending: true })
      .order("name", { ascending: true })
      .then(({ data }) => { setVendors(data || []); setVendorsLoading(false); });
  }, [orgData?.id]);

  // ── F-5c: Load runs ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!orgData?.id) return;
    supabase.from("festival_runs")
      .select("*").eq("org_id", orgData.id)
      .order("day_index", { ascending: true })
      .order("time_minutes", { ascending: true, nullsFirst: false })
      .then(({ data }) => {
        setRuns(data || []);
        setRunsLoading(false);
        // Reconstruct dayTokens map from existing runs
        const tokens = {};
        (data || []).forEach(r => { if (r.day_token) tokens[r.day_index] = r.day_token; });
        setDayTokens(tokens);
      });
  }, [orgData?.id]);

  // ── Drag-to-reschedule: document-level mouse tracking ───────────────────────
  useEffect(() => {
    // Grid constants (duplicated as locals so the empty-dep effect doesn't go stale)
    const SH = 40, SM = 30, GS = 600, GE = 1560;
    const TS = (GE - GS) / SM; // total slots

    const onMouseMove = (e) => {
      const drag = dragRef.current;
      if (!drag) return;

      // Which stage column is the mouse currently over?
      let hoveredStageId = drag.currentStageId;
      for (const [sid, el] of Object.entries(columnRefs.current)) {
        if (!el) continue;
        const r = el.getBoundingClientRect();
        if (e.clientX >= r.left && e.clientX <= r.right) { hoveredStageId = sid; break; }
      }

      // Compute snapped start time from Y inside the target column
      const colEl = columnRefs.current[hoveredStageId] || columnRefs.current[drag.origStageId];
      if (!colEl) return;
      const r     = colEl.getBoundingClientRect();
      const rawTop = (e.clientY - r.top) - drag.offsetY;
      const maxSlot = TS - Math.ceil(drag.duration / SM);
      const slot    = Math.max(0, Math.min(maxSlot, Math.round(rawTop / SH)));
      const newStart = GS + slot * SM;

      const next = { ...drag, currentStart: newStart, currentStageId: hoveredStageId };
      dragRef.current = next;
      setDragging(d => d ? { ...d, currentStart: newStart, currentStageId: hoveredStageId } : null);
    };

    const onMouseUp = async () => {
      const drag = dragRef.current;
      if (!drag) return;
      dragRef.current = null;

      const moved = drag.currentStart !== drag.origStart || drag.currentStageId !== drag.origStageId;
      if (moved) {
        wasDragRef.current = true; // suppress the upcoming click event
        const updates = { set_start: drag.currentStart, stage_id: drag.currentStageId };
        setFestivalArtists(prev => prev.map(a => a.id === drag.artistId ? { ...a, ...updates } : a));
        await supabase.from("festival_artists").update(updates).eq("id", drag.artistId);
      }
      setDragging(null);
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
    return () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };
  }, []); // empty — uses refs only, constants are local

  // ── Stage CRUD ───────────────────────────────────────────────────────────────
  const openAddStage = () => {
    setStageName(""); setStageCapacity(""); setStageColor("#f0a500");
    setStageModal({ mode: "add" });
  };
  const openEditStage = (stage) => {
    setStageName(stage.name); setStageCapacity(stage.capacity ? String(stage.capacity) : ""); setStageColor(stage.color || "#f0a500");
    setStageModal({ mode: "edit", stage });
  };
  const saveStage = async () => {
    if (!stageName.trim()) return;
    setStageSaving(true);
    if (stageModal.mode === "add") {
      const payload = {
        id: Date.now().toString(36) + Math.random().toString(36).slice(2, 6),
        org_id: orgData.id,
        name: stageName.trim(),
        capacity: stageCapacity ? parseInt(stageCapacity, 10) : null,
        color: stageColor,
        stage_order: stages.length,
        tech_spec: {},
      };
      const { data, error } = await supabase.from("festival_stages").insert(payload).select().single();
      if (!error && data) setStages(prev => [...prev, data]);
    } else {
      const updates = { name: stageName.trim(), capacity: stageCapacity ? parseInt(stageCapacity, 10) : null, color: stageColor };
      const { data, error } = await supabase.from("festival_stages").update(updates).eq("id", stageModal.stage.id).select().single();
      if (!error && data) setStages(prev => prev.map(s => s.id === data.id ? data : s));
    }
    setStageSaving(false);
    setStageModal(null);
  };
  const deleteStage = async (stage) => {
    if (!window.confirm(`Delete stage "${stage.name}"? This cannot be undone.`)) return;
    await supabase.from("festival_stages").delete().eq("id", stage.id);
    setStages(prev => prev.filter(s => s.id !== stage.id));
  };
  const moveStage = async (idx, dir) => {
    const arr = [...stages];
    const swapIdx = idx + dir;
    if (swapIdx < 0 || swapIdx >= arr.length) return;
    [arr[idx], arr[swapIdx]] = [arr[swapIdx], arr[idx]];
    const updated = arr.map((s, i) => ({ ...s, stage_order: i }));
    setStages(updated);
    // persist order
    await Promise.all(updated.map(s => supabase.from("festival_stages").update({ stage_order: s.stage_order }).eq("id", s.id)));
  };

  // ── Profile save ─────────────────────────────────────────────────────────────
  const openProfileEdit = () => {
    setProfileDraft({
      name: orgData?.name || "",
      dates_start: orgData?.profile?.dates_start || "",
      dates_end:   orgData?.profile?.dates_end   || "",
      location:    orgData?.profile?.location    || "",
      capacity:    orgData?.profile?.capacity    || "",
      website:     orgData?.profile?.website     || "",
      tagline:     orgData?.profile?.tagline     || "",
    });
    setProfileEdit(true);
  };
  const saveProfile = async () => {
    setProfileSaving(true);
    const { name, ...profileFields } = profileDraft;
    const updates = { name: name.trim() };
    // Merge into existing profile JSONB
    const mergedProfile = { ...(orgData?.profile || {}), ...profileFields };
    updates.profile = mergedProfile;
    await supabase.from("organizations").update(updates).eq("id", orgData.id);
    if (onUpdateOrg) onUpdateOrg({ ...orgData, name: name.trim(), profile: mergedProfile });
    setProfileSaving(false);
    setProfileEdit(false);
  };

  // ── Artist CRUD ──────────────────────────────────────────────────────────────
  const openAddArtist = (prefill = {}) => {
    setAName(""); setAStageId(prefill.stageId || ""); setADayIndex(prefill.dayIndex ?? selectedDay);
    setAStartMins(prefill.startMins ?? null); setADuration(60); setAStatus("unconfirmed");
    setAEmail(""); setANotes(""); setALiaisonId("");
    setArtistModal({ mode: prefill.stageId ? "schedule" : "add" });
  };
  const openEditArtist = (artist) => {
    setAName(artist.artist_name); setAStageId(artist.stage_id || ""); setADayIndex(artist.day_index ?? 0);
    setAStartMins(artist.set_start ?? null); setADuration(artist.set_duration || 60); setAStatus(artist.status);
    setAEmail(artist.contact_email || ""); setANotes(artist.notes || ""); setALiaisonId(artist.liaison_id || "");
    setArtistModal({ mode: "edit", artist });
  };
  const saveArtist = async () => {
    if (!aName.trim()) return;
    setArtistSaving(true);
    const payload = {
      org_id: orgData.id, artist_name: aName.trim(),
      stage_id: aStageId || null, day_index: aDayIndex,
      set_start: aStartMins, set_duration: aDuration,
      status: aStatus, contact_email: aEmail || null,
      notes: aNotes || null, liaison_id: aLiaisonId || null,
      documents: artistModal?.artist?.documents || {},
    };
    if (artistModal?.mode === "edit" && artistModal.artist?.id) {
      const { data, error } = await supabase.from("festival_artists")
        .update(payload).eq("id", artistModal.artist.id).select().single();
      if (!error && data) setFestivalArtists(prev => prev.map(a => a.id === data.id ? data : a));
    } else {
      const id = Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
      const { data, error } = await supabase.from("festival_artists")
        .insert({ id, ...payload }).select().single();
      if (!error && data) setFestivalArtists(prev => [...prev, data]);
    }
    setArtistSaving(false);
    setArtistModal(null);
  };
  const deleteArtist = async (artist) => {
    if (!window.confirm(`Remove "${artist.artist_name}" from the lineup?`)) return;
    await supabase.from("festival_artists").delete().eq("id", artist.id);
    setFestivalArtists(prev => prev.filter(a => a.id !== artist.id));
  };
  const cycleDocStatus = async (artist, docKey) => {
    const CYCLE = { undefined: "received", missing: "received", received: "flagged", flagged: "missing" };
    const next = CYCLE[artist.documents?.[docKey]] || "received";
    const docs = { ...(artist.documents || {}), [docKey]: next };
    setFestivalArtists(prev => prev.map(a => a.id === artist.id ? { ...a, documents: docs } : a));
    await supabase.from("festival_artists").update({ documents: docs }).eq("id", artist.id);
  };

  const cycleArtistStatus = async (artist) => {
    const CYCLE = { unconfirmed: "advancing", advancing: "confirmed", confirmed: "cancelled", cancelled: "unconfirmed" };
    const next = CYCLE[artist.status] || "advancing";
    setFestivalArtists(prev => prev.map(a => a.id === artist.id ? { ...a, status: next } : a));
    await supabase.from("festival_artists").update({ status: next }).eq("id", artist.id);
  };

  const saveNotes = async (artistId, notes) => {
    setFestivalArtists(prev => prev.map(a => a.id === artistId ? { ...a, notes } : a));
    setEditingNotesId(null);
    await supabase.from("festival_artists").update({ notes }).eq("id", artistId);
  };

  const saveLiaisonName = async (name) => {
    const trimmed = name.trim();
    setOrgMembers(prev => prev.map(m => m.user_id === user?.id ? { ...m, name: trimmed } : m));
    await supabase.from("organization_members")
      .update({ name: trimmed })
      .eq("org_id", orgData.id)
      .eq("user_id", user?.id);
  };

  // F-4 helpers
  const confirmFestLink = async (linkId) => {
    await supabase.from("event_links").update({ status: "confirmed" }).eq("id", linkId);
    setFestEventLinks(prev => prev.map(l => l.id === linkId ? { ...l, status: "confirmed" } : l));
  };
  const declineFestLink = async (linkId) => {
    await supabase.from("event_links").delete().eq("id", linkId);
    setFestEventLinks(prev => prev.filter(l => l.id !== linkId));
  };
  // ORG-1: Festival enters a show code to request a link
  const lookupAndRequestFestLink = async () => {
    const code = festCodeInput.trim().toUpperCase();
    if (!code || !orgData?.id) return;
    setFestCodeLookup({ loading: true, result: null, error: null });
    const { data, error } = await supabase.rpc("lookup_show_by_link_code", { p_code: code });
    if (error || !data) {
      setFestCodeLookup({ loading: false, result: null, error: "No show found for that code." });
      return;
    }
    const linkId = Date.now().toString(36) + Math.random().toString(36).slice(2);
    const newLink = {
      id: linkId, show_id: data.show_id, org_id: orgData.id,
      org_role: "promoter", status: "pending", initiated_by_org: true,
      metadata: {
        show_date: data.show_date, venue_name: data.venue_name || "",
        artist_name: data.artist_name || "", tour_name: data.tour_name || "",
        advance_token: data.advance_token || null, org_type: "festival",
      },
    };
    const { error: insErr } = await supabase.from("event_links").insert(newLink);
    if (insErr) {
      setFestCodeLookup({ loading: false, result: null, error: "Could not send request. The show may already be linked." });
      return;
    }
    setFestEventLinks(prev => [newLink, ...prev]);
    setFestCodeLookup({ loading: false, result: data, error: null });
    setFestCodeInput("");
    setFestShowsSubtab("requests");
  };
  const loadFestTmData = async (linkId, token) => {
    if (festLinkTmData[linkId] || festLinkTmLoading[linkId]) return;
    setFestLinkTmLoading(prev => ({ ...prev, [linkId]: true }));
    try {
      const res = await fetch(`/api/advance?token=${token}`);
      if (res.ok) {
        const d = await res.json();
        setFestLinkTmData(prev => ({ ...prev, [linkId]: d }));
      }
    } catch {}
    setFestLinkTmLoading(prev => ({ ...prev, [linkId]: false }));
  };
  const saveFestLinkSection = async (linkId, secId, fields) => {
    const key = `${linkId}:${secId}`;
    setFestLinkSaving(prev => ({ ...prev, [key]: true }));
    const mergedAdv = { ...(festLinkAdvData[linkId] || {}), [secId]: fields };
    const { error } = await supabase.from("event_links")
      .update({ venue_advance_data: mergedAdv }).eq("id", linkId);
    if (!error) {
      setFestLinkAdvData(prev => ({ ...prev, [linkId]: mergedAdv }));
      setFestLinkSaved(prev => ({ ...prev, [key]: true }));
      setTimeout(() => setFestLinkSaved(prev => ({ ...prev, [key]: false })), 2000);
    }
    setFestLinkSaving(prev => ({ ...prev, [key]: false }));
  };

  // ── F-5d: Festival flags on TM advance sections ──────────────────────────────
  const writeFestTmFlag = async (linkId, secId, flagObj) => {
    const link = festEventLinks.find(l => l.id === linkId);
    if (!link) return;
    const existing = link.venue_section_status || {};
    const tmFlags = { ...(existing.tm_section_flags || {}) };
    if (flagObj === null) { delete tmFlags[secId]; }
    else { tmFlags[secId] = flagObj; }
    const updated = { ...existing, tm_section_flags: tmFlags };
    const { error } = await supabase.from("event_links").update({ venue_section_status: updated }).eq("id", linkId);
    if (!error) setFestEventLinks(prev => prev.map(l => l.id === linkId ? { ...l, venue_section_status: updated } : l));
  };
  const flagTmSection    = (linkId, secId, note) => writeFestTmFlag(linkId, secId, { status: "flagged",   note: note || "" });
  const confirmTmSection = (linkId, secId)       => writeFestTmFlag(linkId, secId, { status: "confirmed", note: "" });
  const unflagTmSection  = (linkId, secId)       => writeFestTmFlag(linkId, secId, null);

  // ── F-5: Credential CRUD ─────────────────────────────────────────────────────
  const openAddCred = () => {
    setCredName(""); setCredType("artist"); setCredCompany(""); setCredEmail(""); setCredNotes("");
    setCredModal({ mode: "add" });
  };
  const openEditCred = (cred) => {
    setCredName(cred.name); setCredType(cred.type); setCredCompany(cred.company || "");
    setCredEmail(cred.email || ""); setCredNotes(cred.notes || "");
    setCredModal({ mode: "edit", cred });
  };
  const saveCred = async () => {
    if (!credName.trim()) return;
    setCredSaving(true);
    const payload = {
      org_id: orgData.id, name: credName.trim(), type: credType,
      company: credCompany.trim() || null, email: credEmail.trim() || null,
      notes: credNotes.trim() || null,
    };
    if (credModal?.mode === "edit" && credModal.cred?.id) {
      const { data, error } = await supabase.from("festival_credentials")
        .update(payload).eq("id", credModal.cred.id).select().single();
      if (!error && data) setCredentials(prev => prev.map(c => c.id === data.id ? data : c));
    } else {
      const id = Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
      const { data, error } = await supabase.from("festival_credentials")
        .insert({ id, ...payload, issued: false }).select().single();
      if (!error && data) setCredentials(prev => [...prev, data]
        .sort((a, b) => a.type.localeCompare(b.type) || a.name.localeCompare(b.name)));
    }
    setCredSaving(false);
    setCredModal(null);
  };
  const deleteCred = async (id) => {
    setCredentials(prev => prev.filter(c => c.id !== id));
    await supabase.from("festival_credentials").delete().eq("id", id);
  };
  const toggleIssued = async (cred) => {
    const next = !cred.issued;
    const updates = { issued: next, issued_at: next ? new Date().toISOString() : null };
    setCredentials(prev => prev.map(c => c.id === cred.id ? { ...c, ...updates } : c));
    await supabase.from("festival_credentials").update(updates).eq("id", cred.id);
  };
  const importFromArtists = async () => {
    const existing = new Set(credentials.map(c => c.name.toLowerCase()));
    const toImport = festivalArtists
      .filter(a => a.status !== "cancelled" && !existing.has(a.artist_name.toLowerCase()))
      .map(a => ({
        id: Date.now().toString(36) + Math.random().toString(36).slice(2, 6) + Math.random().toString(36).slice(2, 4),
        org_id: orgData.id, name: a.artist_name, type: "artist",
        email: a.contact_email || null, company: null, issued: false, notes: null,
      }));
    if (toImport.length === 0) return;
    const { data, error } = await supabase.from("festival_credentials").insert(toImport).select();
    if (!error && data) setCredentials(prev =>
      [...prev, ...data].sort((a, b) => a.type.localeCompare(b.type) || a.name.localeCompare(b.name)));
  };

  // ── F-5b: Vendor CRUD ────────────────────────────────────────────────────────
  const openAddVendor = () => {
    setVName(""); setVType("catering"); setVContactName(""); setVContactEmail("");
    setVContactPhone(""); setVStatus("pending"); setVDays([]); setVContractStatus("none"); setVNotes("");
    setVendorModal({ mode: "add" });
  };
  const openEditVendor = (v) => {
    setVName(v.name); setVType(v.type); setVContactName(v.contact_name || "");
    setVContactEmail(v.contact_email || ""); setVContactPhone(v.contact_phone || "");
    setVStatus(v.status); setVDays(v.day_assignments || []); setVContractStatus(v.contract_status || "none");
    setVNotes(v.notes || "");
    setVendorModal({ mode: "edit", vendor: v });
  };
  const saveVendor = async () => {
    if (!vName.trim()) return;
    setVendorSaving(true);
    const payload = {
      org_id: orgData.id, name: vName.trim(), type: vType,
      contact_name: vContactName.trim() || null, contact_email: vContactEmail.trim() || null,
      contact_phone: vContactPhone.trim() || null, status: vStatus,
      day_assignments: vDays, contract_status: vContractStatus,
      notes: vNotes.trim() || null,
    };
    if (vendorModal?.mode === "edit" && vendorModal.vendor?.id) {
      const { data, error } = await supabase.from("festival_vendors")
        .update(payload).eq("id", vendorModal.vendor.id).select().single();
      if (!error && data) setVendors(prev => prev.map(v => v.id === data.id ? data : v));
    } else {
      const id = Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
      const { data, error } = await supabase.from("festival_vendors")
        .insert({ id, ...payload }).select().single();
      if (!error && data) setVendors(prev => [...prev, data]
        .sort((a, b) => a.type.localeCompare(b.type) || a.name.localeCompare(b.name)));
    }
    setVendorSaving(false);
    setVendorModal(null);
  };
  const deleteVendor = async (id) => {
    setVendors(prev => prev.filter(v => v.id !== id));
    await supabase.from("festival_vendors").delete().eq("id", id);
  };
  const cycleVendorStatus = async (vendor) => {
    const CYCLE = { pending: "confirmed", confirmed: "cancelled", cancelled: "pending" };
    const next = CYCLE[vendor.status] || "pending";
    setVendors(prev => prev.map(v => v.id === vendor.id ? { ...v, status: next } : v));
    await supabase.from("festival_vendors").update({ status: next }).eq("id", vendor.id);
  };

  // ── F-5c: Runs CRUD ──────────────────────────────────────────────────────────
  const fmtMinsToTime = (m) => {
    if (m == null) return "";
    const h = Math.floor(m / 60) % 24, mn = m % 60;
    return `${String(h).padStart(2, "0")}:${String(mn).padStart(2, "0")}`;
  };
  const parseTimeToMins = (str) => {
    if (!str) return null;
    const [h, m] = str.split(":").map(Number);
    return isNaN(h) ? null : h * 60 + (m || 0);
  };
  const openAddRun = () => {
    setRPickupName(""); setRPickupLoc(""); setRDropoffLoc(""); setRRunnerName("");
    setRTimeMins(null); setRNotes("");
    setRunModal({ mode: "add" });
  };
  const openEditRun = (r) => {
    setRPickupName(r.pickup_name); setRPickupLoc(r.pickup_location || "");
    setRDropoffLoc(r.dropoff_location || ""); setRRunnerName(r.runner_name || "");
    setRTimeMins(r.time_minutes ?? null); setRNotes(r.notes || "");
    setRunModal({ mode: "edit", run: r });
  };
  const saveRun = async () => {
    if (!rPickupName.trim()) return;
    setRunSaving(true);
    const payload = {
      org_id: orgData.id, day_index: runDayIndex, time_minutes: rTimeMins,
      pickup_name: rPickupName.trim(), pickup_location: rPickupLoc.trim() || null,
      dropoff_location: rDropoffLoc.trim() || null, runner_name: rRunnerName.trim() || null,
      status: "pending", notes: rNotes.trim() || null,
      day_token: dayTokens[runDayIndex] || null,
    };
    if (runModal?.mode === "edit" && runModal.run?.id) {
      const { status: _, ...updatePayload } = payload; // don't reset status on edit
      const { data, error } = await supabase.from("festival_runs")
        .update({ ...updatePayload, status: runModal.run.status }).eq("id", runModal.run.id).select().single();
      if (!error && data) setRuns(prev => prev.map(r => r.id === data.id ? data : r));
    } else {
      const id = Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
      const { data, error } = await supabase.from("festival_runs")
        .insert({ id, ...payload }).select().single();
      if (!error && data) setRuns(prev => [...prev, data]
        .sort((a, b) => a.day_index - b.day_index || (a.time_minutes ?? 9999) - (b.time_minutes ?? 9999)));
    }
    setRunSaving(false);
    setRunModal(null);
  };
  const deleteRun = async (id) => {
    setRuns(prev => prev.filter(r => r.id !== id));
    await supabase.from("festival_runs").delete().eq("id", id);
  };
  const generateDayToken = async (dayIdx) => {
    const token = crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
    setDayTokens(prev => ({ ...prev, [dayIdx]: token }));
    // Set token on all existing runs for this day
    await supabase.from("festival_runs").update({ day_token: token })
      .eq("org_id", orgData.id).eq("day_index", dayIdx);
    setRuns(prev => prev.map(r => r.day_index === dayIdx ? { ...r, day_token: token } : r));
    return token;
  };
  const copyDayLink = async (dayIdx) => {
    let token = dayTokens[dayIdx];
    if (!token) token = await generateDayToken(dayIdx);
    const url = `${window.location.origin}/run/${token}`;
    navigator.clipboard.writeText(url).catch(() => {});
    setCopiedToken(dayIdx);
    setTimeout(() => setCopiedToken(null), 2500);
  };

  const handleBlockMouseDown = (e, artist, stageId) => {
    e.preventDefault();
    e.stopPropagation();
    const colEl = columnRefs.current[stageId];
    if (!colEl) return;
    const r       = colEl.getBoundingClientRect();
    const blockTop = artist.set_start != null ? (artist.set_start - 600) / 30 * 40 : 0;
    const offsetY  = (e.clientY - r.top) - blockTop; // where within the block the user grabbed
    const drag = {
      artistId: artist.id, origStart: artist.set_start, origStageId: stageId,
      currentStart: artist.set_start, currentStageId: stageId,
      duration: artist.set_duration || 60, offsetY,
    };
    dragRef.current = drag;
    setDragging(drag);
  };

  // ── Helpers ──────────────────────────────────────────────────────────────────
  const fmtGridTime = (mins) => {
    if (mins == null) return "–";
    const h = Math.floor(mins / 60) % 24;
    const m = mins % 60;
    const ampm = h >= 12 ? "PM" : "AM";
    const disp = h % 12 || 12;
    const suffix = mins >= 1440 ? "+" : "";
    return `${disp}${m ? `:${String(m).padStart(2, "0")}` : ""}${ampm}${suffix}`;
  };
  const fmtTimeRange = (start, dur) => {
    if (start == null) return "Unscheduled";
    return `${fmtGridTime(start)} – ${fmtGridTime(start + (dur || 60))}`;
  };
  // Detect conflicts: returns Set of artist IDs that overlap with another on the same stage + day
  const conflictIds = (() => {
    const ids = new Set();
    const byStageDay = {};
    festivalArtists.forEach(a => {
      if (a.set_start == null || !a.stage_id) return;
      const key = `${a.stage_id}:${a.day_index}`;
      if (!byStageDay[key]) byStageDay[key] = [];
      byStageDay[key].push(a);
    });
    Object.values(byStageDay).forEach(group => {
      for (let i = 0; i < group.length; i++) {
        for (let j = i + 1; j < group.length; j++) {
          const a = group[i], b = group[j];
          const aEnd = a.set_start + (a.set_duration || 60);
          const bEnd = b.set_start + (b.set_duration || 60);
          if (a.set_start < bEnd && b.set_start < aEnd) { ids.add(a.id); ids.add(b.id); }
        }
      }
    });
    return ids;
  })();

  const FESTIVAL_DOCS = ["rider", "stage_plot", "input_list", "w9", "photo_bio"];
  const DOC_LABELS    = { rider: "Rider", stage_plot: "Stage Plot", input_list: "Input List", w9: "W9", photo_bio: "Photo/Bio" };
  const DOC_COLOR     = (s) => s === "received" ? "var(--success)" : s === "flagged" ? "var(--accent)" : "#444";
  const STATUS_COLORS = { confirmed: "var(--success)", advancing: "#60b4f0", unconfirmed: "#888", cancelled: "#f87171" };
  const CRED_COLORS   = { artist: "var(--accent)", crew: "#60b4f0", press: "#e06fdf", vip: "var(--success)", production: "#f07060", vendor: "#50d0c0" };
  const VENDOR_TYPES  = ["catering", "infrastructure", "security", "staging", "lighting", "sound", "medical", "transport", "waste", "other"];
  const VENDOR_COLORS = { catering: "#f07060", infrastructure: "#8898aa", security: "#5b5bd0", staging: "#50d0c0", lighting: "#f0e060", sound: "#60b4f0", medical: "#f87171", transport: "#a080f0", waste: "#7a9a7a", other: "#888" };
  const CONTRACT_LABELS = { none: "No contract", requested: "Requested", received: "Received", signed: "Signed" };
  const CONTRACT_COLORS = { none: "#555", requested: "var(--accent)", received: "#60b4f0", signed: "var(--success)" };
  const VENDOR_STATUS_COLORS = { pending: "#888", confirmed: "var(--success)", cancelled: "#f87171" };

  // Festival days derived from profile
  const festivalDays = (() => {
    const { dates_start, dates_end } = orgData?.profile || {};
    if (dates_start && dates_end) {
      const diff = Math.round((new Date(dates_end + "T12:00:00") - new Date(dates_start + "T12:00:00")) / 86400000) + 1;
      return Math.max(1, Math.min(diff, 7));
    }
    return 1;
  })();
  const dayLabel = (i) => {
    const s = orgData?.profile?.dates_start;
    if (s) {
      const d = new Date(s + "T12:00:00");
      d.setDate(d.getDate() + i);
      return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
    }
    return `Day ${i + 1}`;
  };

  // Grid constants
  const GRID_START = 600;  // 10:00 AM in minutes
  const GRID_END   = 1560; // 2:00 AM (+1 day) in minutes
  const SLOT_MINS  = 30;
  const SLOT_H     = 40;   // px per 30-min slot
  const TOTAL_SLOTS = (GRID_END - GRID_START) / SLOT_MINS;
  const TOTAL_H     = TOTAL_SLOTS * SLOT_H;

  const STAGE_COLORS = ["#f0a500","#3ecf6e","#60b4f0","#e06fdf","#f07060","#50d0c0","#a080f0","#f0e060"];

  return (
    <div style={{ minHeight: "100vh", background: T.page, fontFamily: FF, color: T.text }}>
      {/* Top bar */}
      <div style={{ borderBottom: `1px solid ${T.border}`, padding: "0 32px", display: "flex", alignItems: "center", height: 52, background: T.card }}>
        <div style={{ fontSize: 20, letterSpacing: 3, color: T.btn, fontWeight: 700 }}>ADVANCD</div>
        <div style={{ marginLeft: 12, fontSize: 11, color: T.muted, letterSpacing: 1, paddingTop: 2 }}>FESTIVAL</div>
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
            <span style={{ display: "inline-block", width: 7, height: 7, borderRadius: "50%",
              background: realtimeStatus === "connected" ? T.green : realtimeStatus === "connecting" ? T.amber : "#666" }} />
            <span style={{ fontSize: 11, color: realtimeStatus === "connected" ? T.green : T.muted, letterSpacing: 0.5 }}>
              {realtimeStatus === "connected" ? "Live" : realtimeStatus === "connecting" ? "Connecting…" : "Offline"}
            </span>
          </div>
          <span style={{ fontSize: 12, color: T.muted }}>{orgData?.name}</span>
          <button onClick={() => supabase.auth.signOut()} style={{ fontSize: 11, color: T.muted, background: "none", border: "none", cursor: "pointer", letterSpacing: 1 }}>SIGN OUT</button>
        </div>
      </div>

      {/* Tab bar */}
      <div style={{ borderBottom: `1px solid ${T.border}`, padding: "0 32px", display: "flex", gap: 0, background: T.card }}>
        {[
          { id: "artists",   label: `Artists${festivalArtists.length ? ` (${festivalArtists.length})` : ""}` },
          { id: "advancing", label: "Advancing" },
          { id: "shows",     label: `Shows${festEventLinks.filter(l => l.status === "confirmed").length ? ` (${festEventLinks.filter(l => l.status === "confirmed").length})` : ""}${festEventLinks.filter(l => l.status === "pending").length ? ` · ${festEventLinks.filter(l => l.status === "pending").length} req` : ""}` },
          { id: "runs",      label: `Runs${runs.length ? ` (${runs.length})` : ""}` },
          { id: "vendors",   label: `Vendors${vendors.length ? ` (${vendors.filter(v => v.status === "confirmed").length}/${vendors.length})` : ""}` },
          { id: "stages",    label: `Stages${stages.length ? ` (${stages.length})` : ""}` },
          { id: "schedule",     label: "Schedule" },
          { id: "credentials", label: `Credentials${credentials.length ? ` (${credentials.filter(c => c.issued).length}/${credentials.length})` : ""}` },
          { id: "profile",     label: "Profile" },
        ].map(({ id, label }) => (
          <button key={id} onClick={() => setTab(id)}
            style={{ fontFamily: FF, fontSize: 12, fontWeight: tab === id ? 700 : 400, padding: "12px 18px",
              background: "none", border: "none", borderBottom: `2px solid ${tab === id ? T.btn : "transparent"}`,
              color: tab === id ? T.text : T.muted, cursor: "pointer", letterSpacing: 0.5 }}>
            {label}
          </button>
        ))}
      </div>

      <div style={{ maxWidth: 900, margin: "0 auto", padding: "32px 24px" }}>

        {/* ── ARTISTS TAB ── */}
        {tab === "artists" && (
          <div>
            {/* Header row */}
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 16, fontWeight: 700 }}>Lineup{festivalArtists.length ? ` (${festivalArtists.length})` : ""}</div>
                <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>Manage artists, scheduling, and document collection.</div>
              </div>
              <button onClick={() => openAddArtist()}
                style={{ fontFamily: FF, fontSize: 12, fontWeight: 700, padding: "8px 16px", background: T.btn, color: T.btnText, border: "none", borderRadius: 8, cursor: "pointer" }}>
                + Add Artist
              </button>
            </div>

            {/* Status filter pills */}
            <div style={{ display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap" }}>
              {["all","confirmed","advancing","unconfirmed"].map(s => (
                <button key={s} onClick={() => setStatusFilter(s)}
                  style={{ fontFamily: FF, fontSize: 11, padding: "4px 12px", borderRadius: 16,
                    background: statusFilter === s ? T.btn : "none",
                    color: statusFilter === s ? T.btnText : T.muted,
                    border: `1px solid ${statusFilter === s ? T.btn : T.border}`, cursor: "pointer" }}>
                  {s === "all" ? "All" : s.charAt(0).toUpperCase() + s.slice(1)}
                </button>
              ))}
            </div>

            {artistsLoading && <div style={{ fontSize: 12, color: T.muted }}>Loading...</div>}

            {/* ── LIST VIEW ── */}
            {!artistsLoading && (() => {
              const filtered = festivalArtists.filter(a => statusFilter === "all" || a.status === statusFilter);
              if (filtered.length === 0) return (
                <div style={{ textAlign: "center", padding: "60px 0", border: `1px dashed ${T.border}`, borderRadius: 12 }}>
                  <div style={{ opacity: 0.25, color: T.text, marginBottom: 8 }}><svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2a3 3 0 0 1 3 3v7a3 3 0 0 1-6 0V5a3 3 0 0 1 3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="22"/><line x1="8" y1="22" x2="16" y2="22"/></svg></div>
                  <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>
                    {festivalArtists.length === 0 ? "No artists yet" : "No artists match this filter"}
                  </div>
                  {festivalArtists.length === 0 && (
                    <div style={{ fontSize: 12, color: T.muted }}>Click "+ Add Artist" to start building your lineup.</div>
                  )}
                </div>
              );
              return filtered.map(artist => {
                const stage = stages.find(s => s.id === artist.stage_id);
                const isConflict = conflictIds.has(artist.id);
                return (
                  <div key={artist.id} style={{
                    background: T.card, border: `1px solid ${isConflict ? T.red : T.border}`,
                    borderLeft: `4px solid ${stage?.color || T.border}`,
                    borderRadius: 8, padding: "14px 16px", marginBottom: 8,
                    display: "flex", alignItems: "flex-start", gap: 12
                  }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
                        <div style={{ fontSize: 14, fontWeight: 700 }}>{artist.artist_name}</div>
                        {isConflict && <span style={{ fontSize: 11, color: T.red, border: `1px solid ${T.red}`, borderRadius: 4, padding: "1px 5px" }}>CONFLICT</span>}
                        <span style={{ fontSize: 11, color: STATUS_COLORS[artist.status] || T.muted,
                          border: `1px solid ${STATUS_COLORS[artist.status] || T.border}`,
                          borderRadius: 8, padding: "1px 7px" }}>
                          {(artist.status || "unconfirmed").toUpperCase()}
                        </span>
                        {artist.liaison_id && (() => {
                          const liaison = orgMembers.find(m => m.id === artist.liaison_id);
                          return liaison?.name ? (
                            <span style={{ fontSize: 11, color: T.muted, background: T.cardAlt, borderRadius: 8, padding: "1px 7px" }}>
                              👤 {liaison.name}
                            </span>
                          ) : null;
                        })()}
                      </div>
                      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                        <span style={{ fontSize: 11, color: T.muted }}>
                          {festivalDays > 1 ? `${dayLabel(artist.day_index || 0)} · ` : ""}
                          {fmtTimeRange(artist.set_start, artist.set_duration)}
                        </span>
                        {stage && (
                          <span style={{ fontSize: 11, display: "flex", alignItems: "center", gap: 4 }}>
                            <span style={{ width: 7, height: 7, borderRadius: "50%", background: stage.color, display: "inline-block" }} />
                            {stage.name}
                          </span>
                        )}
                        {artist.contact_email && <span style={{ fontSize: 11, color: T.muted }}>{artist.contact_email}</span>}
                      </div>
                      {/* Doc dots */}
                      <div style={{ display: "flex", gap: 5, marginTop: 8 }}>
                        {FESTIVAL_DOCS.map(dk => (
                          <button key={dk} title={`${DOC_LABELS[dk]}: ${artist.documents?.[dk] || "missing"}`}
                            onClick={() => cycleDocStatus(artist, dk)}
                            style={{ width: 9, height: 9, borderRadius: "50%", border: "none", padding: 0, cursor: "pointer",
                              background: DOC_COLOR(artist.documents?.[dk]) }} />
                        ))}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                      <button onClick={() => openEditArtist(artist)}
                        style={{ fontFamily: FF, fontSize: 11, padding: "5px 12px", background: "none", border: `1px solid ${T.border}`, borderRadius: 4, color: T.text, cursor: "pointer" }}>Edit</button>
                      <button onClick={() => deleteArtist(artist)}
                        style={{ fontFamily: FF, fontSize: 11, padding: "5px 10px", background: "none", border: `1px solid ${T.border}`, borderRadius: 4, color: T.red, cursor: "pointer" }}>✕</button>
                    </div>
                  </div>
                );
              });
            })()}

          </div>
        )}

        {/* ── ADVANCING TAB ── */}
        {tab === "advancing" && (() => {
          // Filters
          const namedLiaisons = orgMembers.filter(m => m.name);
          const filtered = festivalArtists.filter(a => {
            if (advDay !== "all" && a.day_index !== Number(advDay)) return false;
            if (advStage !== "all" && a.stage_id !== advStage) return false;
            if (advLiaison !== "all") {
              if (advLiaison === "unassigned") { if (a.liaison_id) return false; }
              else if (a.liaison_id !== advLiaison) return false;
            }
            if (advDocFilter !== "all") {
              const s = a.documents?.[advDocFilter];
              if (s === "received") return false; // hide received when filtering for outstanding
            }
            return true;
          });

          // Completion stats per doc type across ALL artists
          const total = festivalArtists.length;
          const docStats = FESTIVAL_DOCS.reduce((acc, dk) => {
            acc[dk] = festivalArtists.filter(a => a.documents?.[dk] === "received").length;
            return acc;
          }, {});

          return (
            <div>
              {/* Header */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 10 }}>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 700 }}>Advancing</div>
                  <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>Track document collection and artist status across the lineup.</div>
                </div>
                {festivalArtists.length > 0 && (
                  <div style={{ fontSize: 11, color: T.muted }}>
                    {festivalArtists.filter(a => a.status === "confirmed").length} confirmed · {festivalArtists.filter(a => a.status === "advancing").length} advancing · {festivalArtists.filter(a => a.status === "unconfirmed").length} unconfirmed
                  </div>
                )}
              </div>

              {/* Completion summary strip */}
              {total > 0 && (
                <div style={{ display: "grid", gridTemplateColumns: `repeat(${FESTIVAL_DOCS.length}, 1fr)`, gap: 10, marginBottom: 20 }}>
                  {FESTIVAL_DOCS.map(dk => {
                    const count = docStats[dk];
                    const pct = total > 0 ? Math.round((count / total) * 100) : 0;
                    const flagged = festivalArtists.filter(a => a.documents?.[dk] === "flagged").length;
                    return (
                      <button key={dk} onClick={() => setAdvDocFilter(advDocFilter === dk ? "all" : dk)}
                        style={{ background: advDocFilter === dk ? `${T.btn}18` : T.card, border: `1px solid ${advDocFilter === dk ? T.btn : T.border}`, borderRadius: 8, padding: "10px 12px", cursor: "pointer", textAlign: "left" }}>
                        <div style={{ fontFamily: FF, fontSize: 11, color: T.muted, letterSpacing: 0.8, textTransform: "uppercase", marginBottom: 6 }}>{DOC_LABELS[dk]}</div>
                        <div style={{ fontFamily: FF, fontSize: 20, fontWeight: 700, color: pct === 100 ? T.green : T.text, marginBottom: 5 }}>{count}<span style={{ fontSize: 11, fontWeight: 400, color: T.muted }}>/{total}</span></div>
                        {/* Progress bar */}
                        <div style={{ height: 3, background: T.border, borderRadius: 4, overflow: "hidden" }}>
                          <div style={{ height: "100%", width: `${pct}%`, background: pct === 100 ? T.green : T.btn, borderRadius: 4, transition: "width 0.3s" }} />
                        </div>
                        {flagged > 0 && <div style={{ fontFamily: FF, fontSize: 11, color: T.amber, marginTop: 5 }}>⚑ {flagged} flagged</div>}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Day + Stage filters */}
              {(festivalDays > 1 || stages.length > 0) && (
                <div style={{ display: "flex", gap: 16, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
                  {festivalDays > 1 && (
                    <div style={{ display: "flex", gap: 5, alignItems: "center" }}>
                      <span style={{ fontFamily: FF, fontSize: 11, color: T.muted, letterSpacing: 0.8, textTransform: "uppercase" }}>Day</span>
                      {["all", ...Array.from({ length: festivalDays }, (_, i) => String(i))].map(d => (
                        <button key={d} onClick={() => setAdvDay(d)}
                          style={{ fontFamily: FF, fontSize: 11, padding: "3px 10px", borderRadius: 16,
                            background: advDay === d ? T.btn : "none", color: advDay === d ? T.btnText : T.muted,
                            border: `1px solid ${advDay === d ? T.btn : T.border}`, cursor: "pointer" }}>
                          {d === "all" ? "All" : dayLabel(Number(d))}
                        </button>
                      ))}
                    </div>
                  )}
                  {stages.length > 0 && (
                    <div style={{ display: "flex", gap: 5, alignItems: "center" }}>
                      <span style={{ fontFamily: FF, fontSize: 11, color: T.muted, letterSpacing: 0.8, textTransform: "uppercase" }}>Stage</span>
                      {["all", ...stages.map(s => s.id)].map(sid => {
                        const stg = stages.find(s => s.id === sid);
                        return (
                          <button key={sid} onClick={() => setAdvStage(sid)}
                            style={{ fontFamily: FF, fontSize: 11, padding: "3px 10px", borderRadius: 16, display: "flex", alignItems: "center", gap: 4,
                              background: advStage === sid ? T.btn : "none", color: advStage === sid ? T.btnText : T.muted,
                              border: `1px solid ${advStage === sid ? T.btn : T.border}`, cursor: "pointer" }}>
                            {stg && <span style={{ width: 6, height: 6, borderRadius: "50%", background: stg.color, display: "inline-block" }} />}
                            {sid === "all" ? "All" : stg?.name}
                          </button>
                        );
                      })}
                    </div>
                  )}
                  {namedLiaisons.length > 0 && (
                    <div style={{ display: "flex", gap: 5, alignItems: "center" }}>
                      <span style={{ fontFamily: FF, fontSize: 11, color: T.muted, letterSpacing: 0.8, textTransform: "uppercase" }}>Liaison</span>
                      {["all", "unassigned", ...namedLiaisons.map(m => m.id)].map(lid => {
                        const member = namedLiaisons.find(m => m.id === lid);
                        const label = lid === "all" ? "All" : lid === "unassigned" ? "Unassigned" : member?.name || lid;
                        const isMe = member?.user_id === user?.id;
                        return (
                          <button key={lid} onClick={() => setAdvLiaison(lid)}
                            style={{ fontFamily: FF, fontSize: 11, padding: "3px 10px", borderRadius: 16,
                              background: advLiaison === lid ? T.btn : "none",
                              color: advLiaison === lid ? T.btnText : T.muted,
                              border: `1px solid ${advLiaison === lid ? T.btn : T.border}`, cursor: "pointer" }}>
                            {label}{isMe ? " ★" : ""}
                          </button>
                        );
                      })}
                    </div>
                  )}
                  {(advDay !== "all" || advStage !== "all" || advDocFilter !== "all" || advLiaison !== "all") && (
                    <button onClick={() => { setAdvDay("all"); setAdvStage("all"); setAdvDocFilter("all"); setAdvLiaison("all"); }}
                      style={{ fontFamily: FF, fontSize: 11, color: T.muted, background: "none", border: "none", cursor: "pointer", textDecoration: "underline" }}>
                      Clear filters
                    </button>
                  )}
                </div>
              )}

              {/* Active doc-filter banner */}
              {advDocFilter !== "all" && (
                <div style={{ fontFamily: FF, fontSize: 11, color: T.amber, background: `${T.amber}12`, border: `1px solid ${T.amber}40`, borderRadius: 8, padding: "6px 12px", marginBottom: 12 }}>
                  Showing artists with <strong>{DOC_LABELS[advDocFilter]}</strong> outstanding — {filtered.length} of {festivalArtists.length}
                </div>
              )}

              {/* Matrix table */}
              {festivalArtists.length === 0 ? (
                <div style={{ textAlign: "center", padding: "60px 0", border: `1px dashed ${T.border}`, borderRadius: 12 }}>
                  <div style={{ fontSize: 11, color: T.muted }}>Add artists in the Artists tab to start tracking documents.</div>
                </div>
              ) : (
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: FF }}>
                    <thead>
                      <tr style={{ borderBottom: `2px solid ${T.border}` }}>
                        <th style={{ textAlign: "left", padding: "8px 10px", fontSize: 11, color: T.muted, letterSpacing: 0.8, fontWeight: 600, whiteSpace: "nowrap" }}>ARTIST</th>
                        {festivalDays > 1 && <th style={{ textAlign: "left", padding: "8px 8px", fontSize: 11, color: T.muted, letterSpacing: 0.8, fontWeight: 600 }}>DAY</th>}
                        <th style={{ textAlign: "left", padding: "8px 8px", fontSize: 11, color: T.muted, letterSpacing: 0.8, fontWeight: 600 }}>STAGE</th>
                        <th style={{ textAlign: "center", padding: "8px 8px", fontSize: 11, color: T.muted, letterSpacing: 0.8, fontWeight: 600 }}>STATUS</th>
                        {FESTIVAL_DOCS.map(dk => (
                          <th key={dk} style={{ textAlign: "center", padding: "8px 8px", fontSize: 11, color: advDocFilter === dk ? T.btn : T.muted, letterSpacing: 0.8, fontWeight: 600, whiteSpace: "nowrap" }}>
                            {DOC_LABELS[dk].toUpperCase()}
                          </th>
                        ))}
                        <th style={{ textAlign: "left", padding: "8px 10px", fontSize: 11, color: T.muted, letterSpacing: 0.8, fontWeight: 600 }}>CONTACT</th>
                        <th style={{ textAlign: "left", padding: "8px 10px", fontSize: 11, color: T.muted, letterSpacing: 0.8, fontWeight: 600 }}>LIAISON</th>
                        <th style={{ textAlign: "left", padding: "8px 10px", fontSize: 11, color: T.muted, letterSpacing: 0.8, fontWeight: 600 }}>NOTES</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.length === 0 && (
                        <tr><td colSpan={5 + FESTIVAL_DOCS.length} style={{ padding: "32px", textAlign: "center", fontSize: 12, color: T.muted }}>No artists match the current filters.</td></tr>
                      )}
                      {filtered.map(artist => {
                        const stage = stages.find(s => s.id === artist.stage_id);
                        const isEditing = editingNotesId === artist.id;
                        return (
                          <tr key={artist.id} style={{ borderBottom: `1px solid ${T.border}` }}
                            onMouseEnter={e => e.currentTarget.style.background = T.rowHov}
                            onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                            {/* Artist name */}
                            <td style={{ padding: "10px 10px", fontSize: 14, fontWeight: 600, whiteSpace: "nowrap" }}>
                              {artist.artist_name}
                              {conflictIds.has(artist.id) && <span style={{ fontSize: 11, color: T.red, border: `1px solid ${T.red}`, borderRadius: 4, padding: "1px 4px", marginLeft: 6 }}>CONFLICT</span>}
                            </td>
                            {/* Day */}
                            {festivalDays > 1 && (
                              <td style={{ padding: "10px 8px", fontSize: 11, color: T.muted, whiteSpace: "nowrap" }}>{dayLabel(artist.day_index || 0)}</td>
                            )}
                            {/* Stage */}
                            <td style={{ padding: "10px 8px" }}>
                              {stage ? (
                                <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                  <span style={{ width: 6, height: 6, borderRadius: "50%", background: stage.color, flexShrink: 0 }} />
                                  <span style={{ fontSize: 11 }}>{stage.name}</span>
                                </span>
                              ) : <span style={{ fontSize: 11, color: T.sub, fontStyle: "italic" }}>TBD</span>}
                            </td>
                            {/* Status — clickable to cycle */}
                            <td style={{ padding: "10px 8px", textAlign: "center" }}>
                              <button onClick={() => cycleArtistStatus(artist)}
                                title="Click to advance status"
                                style={{ fontFamily: FF, fontSize: 11, color: STATUS_COLORS[artist.status] || T.muted,
                                  border: `1px solid ${STATUS_COLORS[artist.status] || T.border}`,
                                  borderRadius: 8, padding: "2px 8px", background: "none", cursor: "pointer", whiteSpace: "nowrap" }}>
                                {(artist.status || "unconfirmed").toUpperCase()}
                              </button>
                            </td>
                            {/* Doc cells — clickable chips */}
                            {FESTIVAL_DOCS.map(dk => {
                              const s = artist.documents?.[dk] || "missing";
                              const bg = s === "received" ? `${T.green}18` : s === "flagged" ? `${T.amber}18` : T.card;
                              const col = s === "received" ? T.green : s === "flagged" ? T.amber : T.sub;
                              const bdr = s === "received" ? `${T.green}50` : s === "flagged" ? `${T.amber}50` : T.border;
                              return (
                                <td key={dk} style={{ padding: "10px 8px", textAlign: "center" }}>
                                  <button onClick={() => cycleDocStatus(artist, dk)}
                                    title={`Click to cycle: ${DOC_LABELS[dk]} — currently ${s}`}
                                    style={{ fontFamily: FF, fontSize: 11, padding: "3px 8px", borderRadius: 4,
                                      background: bg, color: col, border: `1px solid ${bdr}`, cursor: "pointer", whiteSpace: "nowrap" }}>
                                    {s === "received" ? "✓" : s === "flagged" ? "⚑" : "–"}
                                  </button>
                                </td>
                              );
                            })}
                            {/* Contact */}
                            <td style={{ padding: "10px 10px", maxWidth: 160 }}>
                              {artist.contact_email ? (
                                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                  <a href={`mailto:${artist.contact_email}`}
                                    style={{ fontSize: 11, color: T.muted, textDecoration: "none", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 130 }}
                                    onMouseEnter={e => e.currentTarget.style.color = T.btn}
                                    onMouseLeave={e => e.currentTarget.style.color = T.muted}
                                    title={artist.contact_email}>
                                    {artist.contact_email}
                                  </a>
                                </div>
                              ) : (
                                <button onClick={() => openEditArtist(artist)}
                                  style={{ fontFamily: FF, fontSize: 11, color: T.sub, background: "none", border: "none", cursor: "pointer", fontStyle: "italic" }}>
                                  + add email
                                </button>
                              )}
                            </td>
                            {/* Liaison */}
                            <td style={{ padding: "10px 10px", whiteSpace: "nowrap" }}>
                              {(() => {
                                const liaison = orgMembers.find(m => m.id === artist.liaison_id);
                                if (liaison?.name) {
                                  return (
                                    <span style={{ fontFamily: FF, fontSize: 11, color: T.muted }}>
                                      {liaison.name}{liaison.user_id === user?.id ? " ★" : ""}
                                    </span>
                                  );
                                }
                                return (
                                  <button onClick={() => openEditArtist(artist)}
                                    style={{ fontFamily: FF, fontSize: 11, color: T.sub, background: "none", border: "none", cursor: "pointer", fontStyle: "italic" }}>
                                    assign
                                  </button>
                                );
                              })()}
                            </td>
                            {/* Notes — inline editable */}
                            <td style={{ padding: "8px 10px", minWidth: 160 }}>
                              {isEditing ? (
                                <textarea
                                  autoFocus
                                  value={notesDraft}
                                  onChange={e => setNotesDraft(e.target.value)}
                                  onBlur={() => saveNotes(artist.id, notesDraft)}
                                  onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); saveNotes(artist.id, notesDraft); } if (e.key === "Escape") setEditingNotesId(null); }}
                                  style={{ fontFamily: FF, fontSize: 11, width: "100%", minHeight: 48, background: T.inp, border: `1px solid ${T.btn}`, borderRadius: 4, padding: "4px 6px", color: T.text, resize: "vertical" }}
                                />
                              ) : (
                                <button onClick={() => { setEditingNotesId(artist.id); setNotesDraft(artist.notes || ""); }}
                                  style={{ fontFamily: FF, fontSize: 11, color: artist.notes ? T.text : T.sub, background: "none", border: "none", cursor: "pointer", textAlign: "left", width: "100%", padding: 0, fontStyle: artist.notes ? "normal" : "italic", lineHeight: 1.5, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                                  {artist.notes || "+ add note"}
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Legend */}
              {festivalArtists.length > 0 && (
                <div style={{ display: "flex", gap: 14, marginTop: 14, alignItems: "center" }}>
                  {[["#3ecf6e","✓ Received"],["#f0a500","⚑ Flagged"],["#888","– Missing"]].map(([c,l]) => (
                    <div key={l} style={{ display: "flex", alignItems: "center", gap: 5 }}>
                      <span style={{ fontSize: 11, color: c }}>{l}</span>
                    </div>
                  ))}
                  <span style={{ fontSize: 11, color: T.sub, marginLeft: 4 }}>· Click any chip to cycle · Click status badge to advance · Click note to edit</span>
                </div>
              )}
            </div>
          );
        })()}

        {/* ── SHOWS TAB (F-4) ── */}
        {tab === "shows" && (
          <div>
            {/* Sub-tabs */}
            <div style={{ display: "flex", gap: 0, borderBottom: `1px solid ${T.border}`, marginBottom: 24 }}>
              {[
                { id: "shows",    label: `Confirmed${festEventLinks.filter(l => l.status === "confirmed").length ? ` (${festEventLinks.filter(l => l.status === "confirmed").length})` : ""}` },
                { id: "requests", label: `Requests${festEventLinks.filter(l => l.status === "pending").length ? ` (${festEventLinks.filter(l => l.status === "pending").length})` : ""}` },
              ].map(st => (
                <button key={st.id} onClick={() => setFestShowsSubtab(st.id)}
                  style={{ fontFamily: FF, fontSize: 12, fontWeight: festShowsSubtab === st.id ? 700 : 400,
                    padding: "10px 16px", background: "none", border: "none",
                    borderBottom: `2px solid ${festShowsSubtab === st.id ? T.btn : "transparent"}`,
                    color: festShowsSubtab === st.id ? T.text : T.muted, cursor: "pointer" }}>
                  {st.label}
                </button>
              ))}
            </div>

            {/* REQUESTS sub-tab */}
            {festShowsSubtab === "requests" && (
              <div>
                {/* ORG-1: Enter a show code */}
                <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, padding: "16px 20px", marginBottom: 20 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, color: T.muted, marginBottom: 8, textTransform: "uppercase" }}>Have a Show Code?</div>
                  <div style={{ fontSize: 12, color: T.muted, marginBottom: 12 }}>
                    If a tour manager shared a 6-character code, enter it here to request a link.
                  </div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <input
                      value={festCodeInput}
                      onChange={e => setFestCodeInput(e.target.value.toUpperCase().slice(0, 6))}
                      onKeyDown={e => { if (e.key === "Enter") lookupAndRequestFestLink(); }}
                      placeholder="ABC123"
                      maxLength={6}
                      style={{ fontFamily: "monospace", fontSize: 16, letterSpacing: 4, fontWeight: 700,
                        width: 120, padding: "8px 14px", background: T.bg, border: `1px solid ${T.border}`,
                        borderRadius: 8, color: T.btn, outline: "none", textTransform: "uppercase" }}
                    />
                    <button
                      onClick={lookupAndRequestFestLink}
                      disabled={festCodeInput.length < 6 || festCodeLookup?.loading}
                      style={{ fontFamily: FF, fontSize: 12, fontWeight: 700, padding: "8px 18px",
                        background: festCodeInput.length === 6 ? T.btn : "transparent",
                        color: festCodeInput.length === 6 ? T.btnText : T.muted,
                        border: `1px solid ${festCodeInput.length === 6 ? T.btn : T.border}`,
                        borderRadius: 8, cursor: festCodeInput.length === 6 && !festCodeLookup?.loading ? "pointer" : "default" }}>
                      {festCodeLookup?.loading ? "Looking up…" : "Request Link"}
                    </button>
                  </div>
                  {festCodeLookup?.error && <div style={{ fontFamily: FF, fontSize: 11, color: T.red, marginTop: 8 }}>{festCodeLookup.error}</div>}
                  {festCodeLookup?.result && (
                    <div style={{ fontFamily: FF, fontSize: 11, color: T.green, marginTop: 8 }}>
                      ✓ Request sent for {festCodeLookup.result.artist_name || "the show"} · {festCodeLookup.result.show_date || "—"}
                    </div>
                  )}
                </div>

                {festEventLinksLoading && <div style={{ color: T.muted, fontSize: 14 }}>Loading…</div>}
                {!festEventLinksLoading && festEventLinks.filter(l => l.status === "pending").length === 0 && (
                  <div style={{ color: T.muted, fontSize: 14, padding: "32px 0", textAlign: "center" }}>No pending link requests.</div>
                )}
                {festEventLinks.filter(l => l.status === "pending").map(link => (
                  <div key={link.id} style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, padding: "16px 20px", marginBottom: 10, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 14 }}>{link.metadata?.artist_name || "Unknown Artist"}</div>
                      <div style={{ fontSize: 12, color: T.muted, marginTop: 2 }}>
                        {link.metadata?.show_date || "—"} · {link.metadata?.venue_name || link.metadata?.tour_name || "—"}
                      </div>
                      <div style={{ fontSize: 11, color: T.muted, marginTop: 4 }}>Linked by Tour Manager</div>
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button onClick={() => confirmFestLink(link.id)}
                        style={{ fontFamily: FF, fontSize: 12, fontWeight: 600, padding: "7px 14px",
                          background: T.btn, color: "#fff", border: "none", borderRadius: 8, cursor: "pointer" }}>
                        ✓ Confirm
                      </button>
                      <button onClick={() => declineFestLink(link.id)}
                        style={{ fontFamily: FF, fontSize: 12, padding: "7px 14px",
                          background: "none", color: T.muted, border: `1px solid ${T.border}`, borderRadius: 8, cursor: "pointer" }}>
                        Decline
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* CONFIRMED SHOWS sub-tab */}
            {festShowsSubtab === "shows" && (
              <div>
                {festEventLinksLoading && <div style={{ color: T.muted, fontSize: 14 }}>Loading…</div>}
                {!festEventLinksLoading && festEventLinks.filter(l => l.status === "confirmed").length === 0 && (
                  <div style={{ color: T.muted, fontSize: 14, padding: "32px 0", textAlign: "center" }}>
                    No confirmed shows yet. Confirm link requests to get started.
                  </div>
                )}
                {festEventLinks.filter(l => l.status === "confirmed").sort((a, b) => {
                  const da = a.metadata?.show_date || "";
                  const db = b.metadata?.show_date || "";
                  return da < db ? -1 : da > db ? 1 : 0;
                }).map(link => {
                  const isExpanded = expandedLinkId === link.id;
                  const advData    = festLinkAdvData[link.id] || {};
                  const tmData     = festLinkTmData[link.id];
                  const tmLoading  = festLinkTmLoading[link.id];
                  const openSecs   = festLinkOpenSecs[link.id] || {};

                  return (
                    <div key={link.id} style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, marginBottom: 12, overflow: "hidden" }}>
                      {/* Card header — always visible */}
                      <div onClick={() => {
                        if (!isExpanded && link.metadata?.advance_token) {
                          loadFestTmData(link.id, link.metadata.advance_token);
                        }
                        setExpandedLinkId(isExpanded ? null : link.id);
                      }} style={{ padding: "16px 20px", cursor: "pointer", display: "flex", alignItems: "center", gap: 14 }}>
                        {/* Date block */}
                        <div style={{ minWidth: 44, textAlign: "center", background: T.bg, borderRadius: 8, padding: "6px 8px" }}>
                          <div style={{ fontSize: 20, fontWeight: 800, lineHeight: 1 }}>
                            {link.metadata?.show_date ? new Date(link.metadata.show_date + "T12:00:00").getDate() : "—"}
                          </div>
                          <div style={{ fontSize: 11, color: T.muted, letterSpacing: 0.5, textTransform: "uppercase" }}>
                            {link.metadata?.show_date ? new Date(link.metadata.show_date + "T12:00:00").toLocaleString("en-US", { month: "short" }) : ""}
                          </div>
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 700, fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {link.metadata?.artist_name || "Unknown Artist"}
                          </div>
                          <div style={{ fontSize: 12, color: T.muted, marginTop: 2 }}>
                            {link.metadata?.tour_name || "—"}
                          </div>
                        </div>
                        <div style={{ fontSize: 14, color: T.muted, userSelect: "none" }}>{isExpanded ? "▲" : "▼"}</div>
                      </div>

                      {/* Expanded panel */}
                      {isExpanded && (
                        <div style={{ borderTop: `1px solid ${T.border}` }}>

                          {/* ── TM Advance Data (read-only) ── */}
                          <div style={{ padding: "16px 20px", borderBottom: `1px solid ${T.border}` }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: T.muted, letterSpacing: 1, marginBottom: 12 }}>ADVANCE FROM TOUR MANAGER</div>
                            {tmLoading && <div style={{ fontSize: 12, color: T.muted }}>Loading advance data…</div>}
                            {!tmLoading && !tmData && (
                              <div style={{ fontSize: 12, color: T.muted }}>No advance data available yet.</div>
                            )}
                            {!tmLoading && tmData && (() => {
                              // Render each TM advance section read-only + festival can flag/confirm each
                              const sections = tmData.sections || [];
                              if (sections.length === 0) return <div style={{ fontSize: 12, color: T.muted }}>No advance sections filled in yet.</div>;
                              const tmFlagMap = link.venue_section_status?.tm_section_flags || {};
                              return sections.map(sec => {
                                const fields = sec.fields || [];
                                const hasContent = fields.some(f => f.value);
                                if (!hasContent) return null;
                                const secFlag   = tmFlagMap[sec.id] || {};
                                const isFlagged  = secFlag.status === "flagged";
                                const isConfirmed = secFlag.status === "confirmed";
                                const flagKey   = `${link.id}:${sec.id}`;
                                const noteOpen  = !!festFlagNoteOpen[flagKey];
                                return (
                                  <div key={sec.id} style={{ marginBottom: 10, border: `1px solid ${isFlagged ? "#f0a50044" : T.border}`, borderRadius: 8, overflow: "hidden" }}>
                                    {/* Section header with flag controls */}
                                    <div style={{ padding: "8px 12px", display: "flex", alignItems: "center", gap: 8, background: isFlagged ? "#f0a50010" : T.bg }}>
                                      <span style={{ fontSize: 11, fontWeight: 700, color: isFlagged ? "var(--accent)" : T.muted, letterSpacing: 0.8, textTransform: "uppercase", flex: 1 }}>{sec.label}</span>
                                      {isConfirmed && !isFlagged && <span style={{ fontSize: 11, background: T.green + "22", color: T.green, borderRadius: 8, padding: "1px 8px", fontWeight: 600 }}>✓ Confirmed</span>}
                                      {isFlagged && <span style={{ fontSize: 11, color: "var(--accent)", fontWeight: 600 }}>⚑ Flagged</span>}
                                      {isFlagged ? (
                                        <>
                                          <button onClick={() => unflagTmSection(link.id, sec.id)}
                                            style={{ fontFamily: FF, fontSize: 11, color: T.muted, background: "none", border: `1px solid ${T.border}`, borderRadius: 4, padding: "2px 8px", cursor: "pointer" }}>
                                            Unflag
                                          </button>
                                          <button onClick={() => confirmTmSection(link.id, sec.id)}
                                            style={{ fontFamily: FF, fontSize: 11, fontWeight: 700, color: "#0d0d0d", background: T.green, border: "none", borderRadius: 4, padding: "2px 8px", cursor: "pointer" }}>
                                            ✓ Confirm
                                          </button>
                                        </>
                                      ) : (
                                        <>
                                          {!isConfirmed && (
                                            <button onClick={() => setFestFlagNoteOpen(prev => ({ ...prev, [flagKey]: !prev[flagKey] }))}
                                              style={{ fontFamily: FF, fontSize: 11, color: "var(--accent)", background: "none", border: `1px solid #f0a50044`, borderRadius: 4, padding: "2px 8px", cursor: "pointer", opacity: noteOpen ? 1 : 0.7 }}>
                                              ⚑ Flag
                                            </button>
                                          )}
                                          <button onClick={() => { confirmTmSection(link.id, sec.id); setFestFlagNoteOpen(prev => ({ ...prev, [flagKey]: false })); }}
                                            style={{ fontFamily: FF, fontSize: 11, color: T.green, background: "none", border: `1px solid ${T.green + "44"}`, borderRadius: 4, padding: "2px 8px", cursor: "pointer" }}>
                                            ✓ {isConfirmed ? "Confirmed" : "Confirm"}
                                          </button>
                                        </>
                                      )}
                                    </div>
                                    {/* Inline flag note editor */}
                                    {noteOpen && !isFlagged && (
                                      <div style={{ padding: "8px 12px", background: "#f0a50010", borderTop: `1px solid #f0a50033`, display: "flex", gap: 8, alignItems: "center" }}>
                                        <span style={{ fontSize: 11, color: "var(--accent)", fontWeight: 700, flexShrink: 0 }}>⚑ Note to TM:</span>
                                        <input
                                          value={festFlagInput[flagKey] || ""}
                                          onChange={e => setFestFlagInput(prev => ({ ...prev, [flagKey]: e.target.value }))}
                                          placeholder="e.g. Please update the stage plot"
                                          onKeyDown={e => {
                                            if (e.key === "Enter") { flagTmSection(link.id, sec.id, festFlagInput[flagKey] || ""); setFestFlagNoteOpen(prev => ({ ...prev, [flagKey]: false })); }
                                            if (e.key === "Escape") setFestFlagNoteOpen(prev => ({ ...prev, [flagKey]: false }));
                                          }}
                                          autoFocus
                                          style={{ flex: 1, background: T.bg, border: `1px solid ${T.border}`, borderRadius: 4, color: T.text, fontFamily: FF, fontSize: 12, padding: "5px 8px", outline: "none" }}
                                        />
                                        <button onClick={() => { flagTmSection(link.id, sec.id, festFlagInput[flagKey] || ""); setFestFlagNoteOpen(prev => ({ ...prev, [flagKey]: false })); }}
                                          style={{ fontFamily: FF, fontSize: 11, fontWeight: 700, color: "#0d0d0d", background: "var(--accent)", border: "none", borderRadius: 4, padding: "4px 10px", cursor: "pointer" }}>
                                          Send
                                        </button>
                                      </div>
                                    )}
                                    {/* Existing flag note display */}
                                    {isFlagged && secFlag.note && (
                                      <div style={{ padding: "6px 12px", background: "#f0a50010", borderTop: `1px solid #f0a50033`, display: "flex", gap: 8, alignItems: "flex-start" }}>
                                        <span style={{ fontSize: 11, color: "var(--accent)", fontWeight: 700, flexShrink: 0, paddingTop: 1 }}>⚑ Note:</span>
                                        <span style={{ fontSize: 12, color: "var(--accent)" }}>{secFlag.note}</span>
                                      </div>
                                    )}
                                    {/* Section fields */}
                                    <div style={{ padding: "10px 12px" }}>
                                      {fields.filter(f => f.value).map(f => (
                                        <div key={f.key} style={{ display: "flex", gap: 8, marginBottom: 4, fontSize: 14 }}>
                                          <span style={{ color: T.muted, minWidth: 140, flexShrink: 0 }}>{f.label}:</span>
                                          <span style={{ whiteSpace: "pre-wrap" }}>{f.value}</span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                );
                              });
                            })()}
                          </div>

                          {/* ── Message thread with TM ── */}
                          <div style={{ padding: "0 20px 16px" }}>
                            <AdvanceMessageThread
                              linkId={link.id}
                              senderType="festival"
                              senderName=""
                              T={T}
                              FF={FF}
                            />
                          </div>

                          {/* ── Festival Advance Sections (editable) ── */}
                          <div style={{ padding: "16px 20px" }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: T.muted, letterSpacing: 1, marginBottom: 14 }}>FESTIVAL INFO FOR ARTIST</div>
                            {FESTIVAL_ADVANCE_SECTIONS.map(sec => {
                              const isOpen   = !!openSecs[sec.id];
                              const secData  = advData[sec.id] || {};
                              const drafts   = (festLinkDrafts[link.id] || {})[sec.id] || {};
                              const saveKey  = `${link.id}:${sec.id}`;
                              const saving   = !!festLinkSaving[saveKey];
                              const saved    = !!festLinkSaved[saveKey];
                              const filled   = sec.fields.some(f => secData[f.key]);

                              return (
                                <div key={sec.id} style={{ border: `1px solid ${T.border}`, borderRadius: 8, marginBottom: 8, overflow: "hidden" }}>
                                  {/* Accordion header */}
                                  <div onClick={() => setFestLinkOpenSecs(prev => ({
                                    ...prev, [link.id]: { ...(prev[link.id] || {}), [sec.id]: !isOpen }
                                  }))} style={{ padding: "11px 14px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between", background: isOpen ? T.bg : "transparent" }}>
                                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                      <span style={{ fontSize: 14, fontWeight: 600 }}>{sec.label}</span>
                                      {filled && !isOpen && <span style={{ fontSize: 11, background: T.green + "22", color: T.green, borderRadius: 8, padding: "1px 7px", fontWeight: 600 }}>Filled</span>}
                                    </div>
                                    <span style={{ fontSize: 11, color: T.muted }}>{isOpen ? "▲" : "▼"}</span>
                                  </div>

                                  {/* Accordion body */}
                                  {isOpen && (
                                    <div style={{ padding: "14px 16px", borderTop: `1px solid ${T.border}` }}>
                                      {sec.fields.map(field => {
                                        const val = drafts[field.key] !== undefined ? drafts[field.key] : (secData[field.key] || "");
                                        const setDraft = (v) => setFestLinkDrafts(prev => ({
                                          ...prev,
                                          [link.id]: { ...(prev[link.id] || {}), [sec.id]: { ...((prev[link.id] || {})[sec.id] || {}), [field.key]: v }
                                          }
                                        }));
                                        return (
                                          <div key={field.key} style={{ marginBottom: 12 }}>
                                            <div style={{ fontSize: 11, color: T.muted, marginBottom: 4, fontWeight: 600 }}>{field.label}</div>
                                            {field.type === "textarea" ? (
                                              <textarea value={val} onChange={e => setDraft(e.target.value)}
                                                rows={3}
                                                style={{ width: "100%", background: T.bg, border: `1px solid ${T.border}`, borderRadius: 8,
                                                  color: T.text, fontFamily: FF, fontSize: 14, padding: "8px 10px", resize: "vertical", boxSizing: "border-box" }} />
                                            ) : (
                                              <input type="text" value={val} onChange={e => setDraft(e.target.value)}
                                                style={{ width: "100%", background: T.bg, border: `1px solid ${T.border}`, borderRadius: 8,
                                                  color: T.text, fontFamily: FF, fontSize: 14, padding: "8px 10px", boxSizing: "border-box" }} />
                                            )}
                                          </div>
                                        );
                                      })}
                                      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 4 }}>
                                        <button onClick={() => {
                                          const merged = { ...secData, ...(drafts) };
                                          saveFestLinkSection(link.id, sec.id, merged);
                                          // clear drafts for this section
                                          setFestLinkDrafts(prev => ({
                                            ...prev,
                                            [link.id]: { ...(prev[link.id] || {}), [sec.id]: {} }
                                          }));
                                        }} disabled={saving}
                                          style={{ fontFamily: FF, fontSize: 12, fontWeight: 600, padding: "7px 16px",
                                            background: saved ? T.green : T.btn, color: "#fff", border: "none", borderRadius: 8, cursor: saving ? "default" : "pointer",
                                            opacity: saving ? 0.7 : 1, transition: "background 0.3s" }}>
                                          {saving ? "Saving…" : saved ? "✓ Saved" : "Save"}
                                        </button>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ── STAGES TAB ── */}
        {tab === "stages" && (
          <div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 3 }}>Stages</div>
                <div style={{ fontSize: 11, color: T.muted }}>Add the stages your festival uses. These become the columns of your scheduling grid.</div>
              </div>
              <button onClick={openAddStage}
                style={{ fontFamily: FF, fontSize: 12, fontWeight: 700, padding: "8px 18px", background: T.btn, color: T.btnText, border: "none", borderRadius: 8, cursor: "pointer" }}>
                + Add Stage
              </button>
            </div>

            {stagesLoading && <div style={{ fontSize: 12, color: T.muted }}>Loading...</div>}
            {!stagesLoading && stages.length === 0 && (
              <div style={{ textAlign: "center", padding: "60px 0", border: `1px dashed ${T.border}`, borderRadius: 12 }}>
                <div style={{ opacity: 0.25, color: T.text, marginBottom: 10 }}><svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/></svg></div>
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>No stages yet</div>
                <div style={{ fontSize: 12, color: T.muted }}>Add your main stage, second stage, etc.</div>
              </div>
            )}

            {stages.map((stage, idx) => (
              <div key={stage.id} style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, padding: "16px 18px", marginBottom: 8, display: "flex", alignItems: "center", gap: 14 }}>
                {/* Color swatch */}
                <div style={{ width: 14, height: 36, borderRadius: 4, background: stage.color || T.btn, flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 700 }}>{stage.name}</div>
                  {stage.capacity && <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>Capacity: {stage.capacity.toLocaleString()}</div>}
                </div>
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  <button onClick={() => moveStage(idx, -1)} disabled={idx === 0}
                    style={{ fontFamily: FF, fontSize: 14, padding: "4px 8px", background: "none", border: `1px solid ${T.border}`, borderRadius: 4, color: idx === 0 ? T.muted : T.text, cursor: idx === 0 ? "default" : "pointer", opacity: idx === 0 ? 0.3 : 1 }}>↑</button>
                  <button onClick={() => moveStage(idx, 1)} disabled={idx === stages.length - 1}
                    style={{ fontFamily: FF, fontSize: 14, padding: "4px 8px", background: "none", border: `1px solid ${T.border}`, borderRadius: 4, color: idx === stages.length - 1 ? T.muted : T.text, cursor: idx === stages.length - 1 ? "default" : "pointer", opacity: idx === stages.length - 1 ? 0.3 : 1 }}>↓</button>
                  <button onClick={() => openEditStage(stage)}
                    style={{ fontFamily: FF, fontSize: 11, padding: "5px 12px", background: "none", border: `1px solid ${T.border}`, borderRadius: 4, color: T.text, cursor: "pointer" }}>Edit</button>
                  <button onClick={() => deleteStage(stage)}
                    style={{ fontFamily: FF, fontSize: 11, padding: "5px 10px", background: "none", border: `1px solid ${T.border}`, borderRadius: 4, color: T.red, cursor: "pointer" }}>✕</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── SCHEDULE TAB ── */}
        {tab === "schedule" && (
          <div>
            {/* Header */}
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 16, fontWeight: 700 }}>Schedule</div>
                <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>Click any empty slot to schedule an artist.</div>
              </div>
              {conflictIds.size > 0 && (
                <div style={{ fontSize: 11, color: T.red, border: `1px solid ${T.red}`, borderRadius: 8, padding: "5px 10px" }}>
                  ⚠ {conflictIds.size} conflict{conflictIds.size > 1 ? "s" : ""} detected
                </div>
              )}
            </div>

            {stages.length === 0 ? (
              <div style={{ textAlign: "center", padding: "60px 0", border: `1px dashed ${T.border}`, borderRadius: 12 }}>
                <div style={{ opacity: 0.25, color: T.text, marginBottom: 8 }}><svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/></svg></div>
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>No stages configured</div>
                <div style={{ fontSize: 12, color: T.muted }}>Add stages in the Stages tab first — they become the columns of the schedule.</div>
                <button onClick={() => setTab("stages")}
                  style={{ fontFamily: FF, fontSize: 12, marginTop: 14, padding: "7px 18px", background: T.btn, color: T.btnText, border: "none", borderRadius: 8, cursor: "pointer" }}>
                  Go to Stages
                </button>
              </div>
            ) : (
              <>
                {/* Day selector */}
                {festivalDays > 1 && (
                  <div style={{ display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap" }}>
                    {Array.from({ length: festivalDays }, (_, i) => (
                      <button key={i} onClick={() => setSelectedDay(i)}
                        style={{ fontFamily: FF, fontSize: 12, padding: "6px 16px", borderRadius: 16, cursor: "pointer",
                          background: selectedDay === i ? T.btn : "none",
                          color: selectedDay === i ? T.btnText : T.muted,
                          border: `1px solid ${selectedDay === i ? T.btn : T.border}`,
                          fontWeight: selectedDay === i ? 700 : 400 }}>
                        {dayLabel(i)}
                      </button>
                    ))}
                  </div>
                )}

                {/* Stage legend */}
                <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
                  {stages.map(s => (
                    <div key={s.id} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: T.muted }}>
                      <span style={{ width: 10, height: 10, borderRadius: 4, background: s.color, display: "inline-block" }} />
                      {s.name}
                    </div>
                  ))}
                </div>

                {/* The Grid */}
                <div style={{ overflowX: "auto", borderRadius: 8, border: `1px solid ${T.border}`, cursor: dragging ? "grabbing" : "default" }}>
                  <div style={{ display: "flex", minWidth: stages.length * 160 + 64 }}>
                    {/* Time column */}
                    <div style={{ width: 64, flexShrink: 0 }}>
                      {/* Header spacer */}
                      <div style={{ height: 40, borderBottom: `1px solid ${T.border}`, background: T.card }} />
                      {/* Time labels */}
                      <div style={{ position: "relative", height: TOTAL_H }}>
                        {Array.from({ length: TOTAL_SLOTS }, (_, i) => {
                          const mins = GRID_START + i * SLOT_MINS;
                          const isHour = mins % 60 === 0;
                          return (
                            <div key={i} style={{ position: "absolute", top: i * SLOT_H, left: 0, width: "100%", height: SLOT_H,
                              display: "flex", alignItems: "center", justifyContent: "flex-end", paddingRight: 8,
                              borderBottom: `1px solid ${T.border}`, background: T.card }}>
                              {isHour && (
                                <span style={{ fontSize: 11, color: T.muted, fontFamily: FF }}>
                                  {fmtGridTime(mins)}
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Stage columns */}
                    {stages.map(stage => {
                      const stageArtists = festivalArtists.filter(a =>
                        a.stage_id === stage.id && a.day_index === selectedDay && a.set_start != null
                      );
                      // Track occupied slots for empty-cell click
                      const occupiedSlots = new Set();
                      stageArtists.forEach(a => {
                        const startSlot = Math.floor((a.set_start - GRID_START) / SLOT_MINS);
                        const endSlot = startSlot + Math.ceil((a.set_duration || 60) / SLOT_MINS);
                        for (let s = startSlot; s < endSlot; s++) occupiedSlots.add(s);
                      });

                      return (
                        <div key={stage.id} style={{ flex: 1, minWidth: 160, borderLeft: `1px solid ${T.border}` }}>
                          {/* Stage header */}
                          <div style={{ height: 40, borderBottom: `1px solid ${T.border}`, background: T.card,
                            display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                            <span style={{ width: 8, height: 8, borderRadius: "50%", background: stage.color, display: "inline-block" }} />
                            <span style={{ fontSize: 12, fontWeight: 700, fontFamily: FF }}>{stage.name}</span>
                          </div>

                          {/* Slot column body */}
                          <div ref={el => { columnRefs.current[stage.id] = el; }}
                            style={{ position: "relative", height: TOTAL_H, background: T.page }}>
                            {/* Empty slot click targets + grid lines */}
                            {Array.from({ length: TOTAL_SLOTS }, (_, i) => {
                              const mins = GRID_START + i * SLOT_MINS;
                              const isHour = mins % 60 === 0;
                              const occupied = occupiedSlots.has(i);
                              return (
                                <div key={i}
                                  onClick={() => !dragging && !occupied && openAddArtist({ stageId: stage.id, startMins: mins, dayIndex: selectedDay })}
                                  style={{ position: "absolute", top: i * SLOT_H, left: 0, right: 0, height: SLOT_H,
                                    borderBottom: `1px solid ${isHour ? T.border : (isLight ? "rgba(0,0,0,0.06)" : "rgba(255,255,255,0.04)")}`,
                                    cursor: dragging ? "grabbing" : occupied ? "default" : "pointer",
                                    background: "transparent",
                                    transition: "background 0.1s",
                                  }}
                                  onMouseEnter={e => { if (!dragging && !occupied) e.currentTarget.style.background = isLight ? "rgba(0,0,0,0.04)" : "rgba(255,255,255,0.04)"; }}
                                  onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
                                />
                              );
                            })}

                            {/* Artist blocks — drag-aware rendering ─────────── */}
                            {(() => {
                              // Blocks to render in this column:
                              // 1. All artists natively in this stage (excluding the one being dragged away)
                              // 2. The dragged artist if it's currently hovering over this column
                              const blocks = [
                                ...stageArtists
                                  .filter(a => !dragging || a.id !== dragging.artistId)
                                  .map(a => ({ artist: a, startMins: a.set_start, isDragging: false })),
                                ...(dragging?.currentStageId === stage.id
                                  ? [{ artist: festivalArtists.find(a => a.id === dragging.artistId), startMins: dragging.currentStart, isDragging: true }].filter(x => x.artist)
                                  : []),
                              ];
                              return blocks.map(({ artist, startMins, isDragging: isBeingDragged }) => {
                                const topPx    = (startMins - GRID_START) / SLOT_MINS * SLOT_H;
                                const heightPx = Math.max((artist.set_duration || 60) / SLOT_MINS * SLOT_H, SLOT_H);
                                const isConflict = conflictIds.has(artist.id) && !isBeingDragged;
                                return (
                                  <div key={`${artist.id}${isBeingDragged ? "-drag" : ""}`}
                                    onMouseDown={e => !isBeingDragged && handleBlockMouseDown(e, artist, stage.id)}
                                    onClick={() => {
                                      if (wasDragRef.current) { wasDragRef.current = false; return; }
                                      openEditArtist(artist);
                                    }}
                                    style={{
                                      position: "absolute", left: 3, right: 3, top: topPx + 2, height: heightPx - 4,
                                      background: stage.color + (isLight ? "33" : "22"),
                                      border: `2px solid ${isConflict ? T.red : stage.color}`,
                                      borderRadius: 8,
                                      cursor: isBeingDragged ? "grabbing" : "grab",
                                      overflow: "hidden", padding: "4px 7px",
                                      zIndex: isBeingDragged ? 20 : 1,
                                      opacity: isBeingDragged ? 0.88 : 1,
                                      boxShadow: isBeingDragged ? "0 8px 24px rgba(0,0,0,0.4)" : "none",
                                      transform: isBeingDragged ? "scale(1.025)" : "none",
                                      transition: isBeingDragged ? "none" : "box-shadow 0.15s, opacity 0.15s",
                                      userSelect: "none",
                                      pointerEvents: isBeingDragged ? "none" : "auto",
                                    }}>
                                    <div style={{ fontSize: 12, fontWeight: 700, fontFamily: FF, color: T.text,
                                      whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                      {artist.artist_name}
                                    </div>
                                    {heightPx >= 60 && (
                                      <div style={{ fontSize: 11, color: T.muted, fontFamily: FF }}>
                                        {fmtTimeRange(startMins, artist.set_duration)}
                                      </div>
                                    )}
                                    {isConflict && heightPx >= 50 && (
                                      <div style={{ fontSize: 11, color: T.red, fontFamily: FF }}>⚠ CONFLICT</div>
                                    )}
                                  </div>
                                );
                              });
                            })()}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div style={{ fontSize: 11, color: T.sub, marginTop: 8, fontFamily: FF }}>
                  Grid shows 10:00 AM – 2:00 AM. Click any empty slot to schedule an artist; click an existing block to edit.
                </div>
              </>
            )}
          </div>
        )}

        {/* ── RUNS TAB (F-5c) ── */}
        {tab === "runs" && (() => {
          const dayRuns = runs.filter(r => r.day_index === runDayIndex)
            .sort((a, b) => (a.time_minutes ?? 9999) - (b.time_minutes ?? 9999));
          const RUN_STATUS = { pending: { label: "Pending", color: "var(--text-mut)" }, in_progress: { label: "In Progress", color: "var(--accent)" }, completed: { label: "Done", color: "var(--success)" } };
          const fmtRunTime = (m) => {
            if (m == null) return "–";
            const h = Math.floor(m / 60) % 24, mn = m % 60, ampm = h >= 12 ? "PM" : "AM";
            return `${h % 12 || 12}${mn ? `:${String(mn).padStart(2,"0")}` : ""}${ampm}`;
          };
          return (
            <div>
              {/* Header */}
              <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 20, flexWrap: "wrap" }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 16, fontWeight: 700 }}>Run Sheet{runs.length ? ` (${runs.length} total)` : ""}</div>
                  <div style={{ fontSize: 11, color: T.muted, marginTop: 3 }}>Artist pickups, dropoffs, and logistics runs. Share a day link with runners — no account needed.</div>
                </div>
                <button onClick={openAddRun}
                  style={{ fontFamily: FF, fontSize: 12, fontWeight: 700, padding: "7px 16px", background: T.btn, color: T.btnText, border: "none", borderRadius: 8, cursor: "pointer" }}>
                  + Add Run
                </button>
              </div>

              {/* Day tabs */}
              <div style={{ display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap" }}>
                {Array.from({ length: festivalDays }, (_, i) => i).map(di => (
                  <button key={di} onClick={() => setRunDayIndex(di)}
                    style={{ fontFamily: FF, fontSize: 11, padding: "6px 14px", borderRadius: 8,
                      background: runDayIndex === di ? T.btn : T.card,
                      color: runDayIndex === di ? T.btnText : T.muted,
                      border: `1px solid ${runDayIndex === di ? T.btn : T.border}`,
                      cursor: "pointer", fontWeight: runDayIndex === di ? 700 : 400 }}>
                    {dayLabel(di)}
                    {runs.filter(r => r.day_index === di).length > 0 && (
                      <span style={{ marginLeft: 5, opacity: 0.7 }}>({runs.filter(r => r.day_index === di).length})</span>
                    )}
                  </button>
                ))}
              </div>

              {/* Day link banner */}
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16, padding: "10px 14px", background: T.card, border: `1px solid ${T.border}`, borderRadius: 8 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  {dayTokens[runDayIndex] ? (
                    <div style={{ fontSize: 11, color: T.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {`${window.location.origin}/run/${dayTokens[runDayIndex]}`}
                    </div>
                  ) : (
                    <div style={{ fontSize: 11, color: T.sub }}>No share link yet for {dayLabel(runDayIndex)}</div>
                  )}
                </div>
                <button onClick={() => copyDayLink(runDayIndex)}
                  style={{ fontFamily: FF, fontSize: 11, fontWeight: 700, padding: "6px 14px", background: copiedToken === runDayIndex ? T.green : "none",
                    color: copiedToken === runDayIndex ? "#fff" : T.muted, border: `1px solid ${copiedToken === runDayIndex ? T.green : T.border}`, borderRadius: 8, cursor: "pointer", flexShrink: 0, display: "flex", alignItems: "center", gap: 5 }}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width={12} height={12}>
                    <rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/>
                  </svg>
                  {copiedToken === runDayIndex ? "Copied!" : dayTokens[runDayIndex] ? "Copy Link" : "Generate Link"}
                </button>
              </div>

              {/* Run list */}
              {runsLoading ? (
                <div style={{ color: T.muted, fontSize: 14 }}>Loading…</div>
              ) : dayRuns.length === 0 ? (
                <div style={{ textAlign: "center", padding: "48px 24px" }}>
                  <div style={{ opacity: 0.2, marginBottom: 12, display: "flex", justifyContent: "center" }}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width={36} height={36}>
                      <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                    </svg>
                  </div>
                  <div style={{ fontSize: 14, color: T.muted }}>No runs for {dayLabel(runDayIndex)}</div>
                  <div style={{ fontSize: 11, color: T.sub, marginTop: 4 }}>Add pickups and dropoffs for this day.</div>
                </div>
              ) : (
                <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, overflow: "hidden" }}>
                  {dayRuns.map((run, i) => (
                    <div key={run.id} style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "13px 16px",
                      borderBottom: i < dayRuns.length - 1 ? `1px solid ${T.border}` : "none" }}>
                      {/* Time */}
                      <div style={{ width: 52, flexShrink: 0, paddingTop: 1 }}>
                        <div style={{ fontSize: 14, fontWeight: 700, color: T.text }}>{fmtRunTime(run.time_minutes)}</div>
                      </div>
                      {/* Main info */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 14, fontWeight: 700, color: T.text }}>{run.pickup_name}</div>
                        {(run.pickup_location || run.dropoff_location) && (
                          <div style={{ fontSize: 11, color: T.muted, marginTop: 3, display: "flex", alignItems: "center", gap: 5 }}>
                            {run.pickup_location && <span>{run.pickup_location}</span>}
                            {run.pickup_location && run.dropoff_location && (
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width={10} height={10}>
                                <polyline points="9 18 15 12 9 6"/>
                              </svg>
                            )}
                            {run.dropoff_location && <span>{run.dropoff_location}</span>}
                          </div>
                        )}
                        {run.runner_name && <div style={{ fontSize: 11, color: T.muted, marginTop: 3 }}>Runner: {run.runner_name}</div>}
                        {run.notes && <div style={{ fontSize: 11, color: T.sub, marginTop: 3, fontStyle: "italic" }}>{run.notes}</div>}
                      </div>
                      {/* Status chip */}
                      <span style={{ fontSize: 11, padding: "3px 9px", borderRadius: 16, fontWeight: 700, flexShrink: 0,
                        background: `${(RUN_STATUS[run.status] || RUN_STATUS.pending).color}18`,
                        color: (RUN_STATUS[run.status] || RUN_STATUS.pending).color }}>
                        {(RUN_STATUS[run.status] || RUN_STATUS.pending).label}
                      </span>
                      {/* Actions */}
                      <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
                        <button onClick={() => openEditRun(run)}
                          style={{ fontFamily: FF, fontSize: 11, padding: "4px 10px", background: "none", border: `1px solid ${T.border}`, borderRadius: 4, color: T.muted, cursor: "pointer" }}>Edit</button>
                        <button onClick={() => deleteRun(run.id)}
                          style={{ fontFamily: FF, fontSize: 11, padding: "4px 10px", background: "none", border: `1px solid ${T.border}`, borderRadius: 4, color: T.red, cursor: "pointer" }}>Remove</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })()}

        {/* ── VENDORS TAB (F-5b) ── */}
        {tab === "vendors" && (() => {
          const filtered = vendors.filter(v => vendorTypeFilter === "all" || v.type === vendorTypeFilter);
          const confirmed = vendors.filter(v => v.status === "confirmed").length;
          const byType = {};
          vendors.forEach(v => {
            if (!byType[v.type]) byType[v.type] = { total: 0, confirmed: 0 };
            byType[v.type].total++;
            if (v.status === "confirmed") byType[v.type].confirmed++;
          });
          return (
            <div>
              {/* Header */}
              <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 20, flexWrap: "wrap" }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 16, fontWeight: 700 }}>Vendors & Contractors{vendors.length ? ` (${vendors.length})` : ""}</div>
                  <div style={{ fontSize: 11, color: T.muted, marginTop: 3 }}>Manage catering, production, security, and other contractors separately from your artist roster.</div>
                </div>
                <button onClick={openAddVendor}
                  style={{ fontFamily: FF, fontSize: 12, fontWeight: 700, padding: "7px 16px", background: T.btn, color: T.btnText, border: "none", borderRadius: 8, cursor: "pointer" }}>
                  + Add Vendor
                </button>
              </div>

              {/* Stats strip */}
              {vendors.length > 0 && (
                <div style={{ display: "flex", gap: 10, marginBottom: 20, flexWrap: "wrap" }}>
                  <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, padding: "10px 16px", minWidth: 80 }}>
                    <div style={{ fontSize: 20, fontWeight: 800, color: confirmed === vendors.length && vendors.length > 0 ? T.green : T.text, lineHeight: 1 }}>{confirmed}/{vendors.length}</div>
                    <div style={{ fontSize: 11, color: T.muted, marginTop: 4, letterSpacing: 0.8 }}>CONFIRMED</div>
                  </div>
                  {Object.entries(byType).map(([type, { total, confirmed: c }]) => (
                    <div key={type} onClick={() => setVendorTypeFilter(vendorTypeFilter === type ? "all" : type)}
                      style={{ background: vendorTypeFilter === type ? `${VENDOR_COLORS[type] || "#888"}15` : T.card, border: `1px solid ${vendorTypeFilter === type ? (VENDOR_COLORS[type] || "#888") : T.border}`, borderRadius: 8, padding: "10px 16px", minWidth: 60, cursor: "pointer" }}>
                      <div style={{ fontSize: 16, fontWeight: 700, color: c === total ? T.green : T.text, lineHeight: 1 }}>{c}/{total}</div>
                      <div style={{ fontSize: 11, color: VENDOR_COLORS[type] || T.muted, marginTop: 4, letterSpacing: 0.8, fontWeight: 700 }}>{type.toUpperCase()}</div>
                    </div>
                  ))}
                </div>
              )}

              {/* Type filter pills */}
              {vendors.length > 0 && (
                <div style={{ display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap" }}>
                  {["all", ...VENDOR_TYPES].map(t => {
                    const count = t === "all" ? vendors.length : vendors.filter(v => v.type === t).length;
                    if (t !== "all" && count === 0) return null;
                    return (
                      <button key={t} onClick={() => setVendorTypeFilter(t)}
                        style={{ fontFamily: FF, fontSize: 11, padding: "5px 12px", borderRadius: 16,
                          background: vendorTypeFilter === t ? (t === "all" ? T.btn : (VENDOR_COLORS[t] || T.btn)) : "none",
                          color: vendorTypeFilter === t ? (t === "all" ? T.btnText : "#fff") : T.muted,
                          border: `1px solid ${vendorTypeFilter === t ? (t === "all" ? T.btn : (VENDOR_COLORS[t] || T.btn)) : T.border}`,
                          cursor: "pointer", fontWeight: vendorTypeFilter === t ? 700 : 400, letterSpacing: 0.3 }}>
                        {t === "all" ? "All" : t.charAt(0).toUpperCase() + t.slice(1)}{count > 0 ? ` (${count})` : ""}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Vendor list */}
              {vendorsLoading ? (
                <div style={{ color: T.muted, fontSize: 14 }}>Loading…</div>
              ) : filtered.length === 0 ? (
                <div style={{ textAlign: "center", padding: "52px 24px" }}>
                  <div style={{ opacity: 0.2, marginBottom: 12, display: "flex", justifyContent: "center" }}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width={36} height={36}>
                      <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>
                    </svg>
                  </div>
                  <div style={{ fontSize: 14, color: T.muted }}>No vendors yet</div>
                  <div style={{ fontSize: 11, color: T.sub, marginTop: 4 }}>Add catering, production, security, and other contractors here.</div>
                </div>
              ) : (
                <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, overflow: "hidden" }}>
                  {filtered.map((v, i) => (
                    <div key={v.id}
                      style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "13px 16px",
                        borderBottom: i < filtered.length - 1 ? `1px solid ${T.border}` : "none",
                        background: v.status === "cancelled" ? `${T.red}06` : "transparent" }}>
                      {/* Status dot + click to cycle */}
                      <button onClick={() => cycleVendorStatus(v)} title={`Status: ${v.status} — click to cycle`}
                        style={{ marginTop: 2, width: 10, height: 10, borderRadius: "50%", flexShrink: 0, padding: 0, border: "none", cursor: "pointer", background: VENDOR_STATUS_COLORS[v.status] || "#888" }} />
                      {/* Main info */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                          <div style={{ fontSize: 14, fontWeight: 700, color: v.status === "cancelled" ? T.muted : T.text }}>
                            {v.name}
                          </div>
                          <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 16, letterSpacing: 0.5, fontWeight: 700,
                            background: `${VENDOR_COLORS[v.type] || "#888"}18`, color: VENDOR_COLORS[v.type] || T.muted }}>
                            {v.type.toUpperCase()}
                          </span>
                          {/* Contract status pill */}
                          <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 16, letterSpacing: 0.3,
                            background: `${CONTRACT_COLORS[v.contract_status] || "#888"}18`,
                            color: CONTRACT_COLORS[v.contract_status] || T.muted }}>
                            {CONTRACT_LABELS[v.contract_status] || "No contract"}
                          </span>
                        </div>
                        {/* Contact row */}
                        {(v.contact_name || v.contact_email || v.contact_phone) && (
                          <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 4, flexWrap: "wrap" }}>
                            {v.contact_name && <span style={{ fontSize: 11, color: T.muted }}>{v.contact_name}</span>}
                            {v.contact_email && (
                              <a href={`mailto:${v.contact_email}`} style={{ fontSize: 11, color: T.muted, textDecoration: "none", display: "flex", alignItems: "center", gap: 3 }}>
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width={11} height={11}>
                                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/>
                                </svg>
                                {v.contact_email}
                              </a>
                            )}
                            {v.contact_phone && (
                              <a href={`tel:${v.contact_phone}`} style={{ fontSize: 11, color: T.muted, textDecoration: "none", display: "flex", alignItems: "center", gap: 3 }}>
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width={11} height={11}>
                                  <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 11 19.79 19.79 0 01.01 2.22 2 2 0 012 0h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 14.92v2z"/>
                                </svg>
                                {v.contact_phone}
                              </a>
                            )}
                          </div>
                        )}
                        {/* Day assignments */}
                        {v.day_assignments && v.day_assignments.length > 0 && (
                          <div style={{ display: "flex", gap: 4, marginTop: 5, flexWrap: "wrap" }}>
                            {v.day_assignments.sort((a, b) => a - b).map(di => (
                              <span key={di} style={{ fontSize: 11, padding: "2px 7px", borderRadius: 4, background: T.cardAlt, color: T.muted }}>
                                {dayLabel(di)}
                              </span>
                            ))}
                          </div>
                        )}
                        {/* Notes */}
                        {v.notes && <div style={{ fontSize: 11, color: T.sub, marginTop: 4, fontStyle: "italic" }}>{v.notes}</div>}
                      </div>
                      {/* Actions */}
                      <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
                        <button onClick={() => openEditVendor(v)}
                          style={{ fontFamily: FF, fontSize: 11, padding: "4px 10px", background: "none", border: `1px solid ${T.border}`, borderRadius: 4, color: T.muted, cursor: "pointer" }}>
                          Edit
                        </button>
                        <button onClick={() => deleteVendor(v.id)}
                          style={{ fontFamily: FF, fontSize: 11, padding: "4px 10px", background: "none", border: `1px solid ${T.border}`, borderRadius: 4, color: T.red, cursor: "pointer" }}>
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })()}

        {/* ── CREDENTIALS TAB (F-5) ── */}
        {tab === "credentials" && (() => {
          const filtered = credentials.filter(c => credTypeFilter === "all" || c.type === credTypeFilter);
          const issuedCount = credentials.filter(c => c.issued).length;
          const byType = {};
          credentials.forEach(c => {
            if (!byType[c.type]) byType[c.type] = { total: 0, issued: 0 };
            byType[c.type].total++;
            if (c.issued) byType[c.type].issued++;
          });
          return (
            <div>
              {/* Header */}
              <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 20, flexWrap: "wrap" }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 16, fontWeight: 700 }}>Credentials{credentials.length ? ` (${credentials.length})` : ""}</div>
                  <div style={{ fontSize: 11, color: T.muted, marginTop: 3 }}>Track credential issuance for artists, crew, press, and vendors.</div>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  {festivalArtists.filter(a => a.status !== "cancelled" && !credentials.find(c => c.name.toLowerCase() === a.artist_name.toLowerCase())).length > 0 && (
                    <button onClick={importFromArtists}
                      style={{ fontFamily: FF, fontSize: 12, padding: "7px 14px", background: "none", border: `1px solid ${T.border}`, borderRadius: 8, color: T.text, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width={12} height={12}>
                        <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                      </svg>
                      Import Artists
                    </button>
                  )}
                  <button onClick={openAddCred}
                    style={{ fontFamily: FF, fontSize: 12, fontWeight: 700, padding: "7px 16px", background: T.btn, color: T.btnText, border: "none", borderRadius: 8, cursor: "pointer" }}>
                    + Add
                  </button>
                </div>
              </div>

              {/* Stats strip */}
              {credentials.length > 0 && (
                <div style={{ display: "flex", gap: 10, marginBottom: 20, flexWrap: "wrap" }}>
                  <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, padding: "10px 16px", minWidth: 80 }}>
                    <div style={{ fontSize: 20, fontWeight: 800, color: issuedCount === credentials.length && credentials.length > 0 ? T.green : T.text, lineHeight: 1 }}>{issuedCount}/{credentials.length}</div>
                    <div style={{ fontSize: 11, color: T.muted, marginTop: 4, letterSpacing: 0.8 }}>TOTAL ISSUED</div>
                  </div>
                  {Object.entries(byType).map(([type, { total, issued }]) => (
                    <div key={type} onClick={() => setCredTypeFilter(credTypeFilter === type ? "all" : type)}
                      style={{ background: credTypeFilter === type ? `${CRED_COLORS[type] || T.btn}15` : T.card, border: `1px solid ${credTypeFilter === type ? (CRED_COLORS[type] || T.btn) : T.border}`, borderRadius: 8, padding: "10px 16px", minWidth: 64, cursor: "pointer" }}>
                      <div style={{ fontSize: 16, fontWeight: 700, color: issued === total ? T.green : T.text, lineHeight: 1 }}>{issued}/{total}</div>
                      <div style={{ fontSize: 11, color: CRED_COLORS[type] || T.muted, marginTop: 4, letterSpacing: 0.8, fontWeight: 700 }}>{type.toUpperCase()}</div>
                    </div>
                  ))}
                </div>
              )}

              {/* Type filter pills */}
              {credentials.length > 0 && (
                <div style={{ display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap" }}>
                  {["all", "artist", "crew", "press", "vip", "production", "vendor"].map(t => {
                    const count = t === "all" ? credentials.length : credentials.filter(c => c.type === t).length;
                    if (t !== "all" && count === 0) return null;
                    return (
                      <button key={t} onClick={() => setCredTypeFilter(t)}
                        style={{ fontFamily: FF, fontSize: 11, padding: "5px 12px", borderRadius: 16,
                          background: credTypeFilter === t ? (t === "all" ? T.btn : (CRED_COLORS[t] || T.btn)) : "none",
                          color: credTypeFilter === t ? (t === "all" ? T.btnText : "#fff") : T.muted,
                          border: `1px solid ${credTypeFilter === t ? (t === "all" ? T.btn : (CRED_COLORS[t] || T.btn)) : T.border}`,
                          cursor: "pointer", fontWeight: credTypeFilter === t ? 700 : 400, letterSpacing: 0.3 }}>
                        {t === "all" ? "All" : t.charAt(0).toUpperCase() + t.slice(1)}{count > 0 ? ` (${count})` : ""}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* List */}
              {credLoading ? (
                <div style={{ color: T.muted, fontSize: 14 }}>Loading…</div>
              ) : filtered.length === 0 ? (
                <div style={{ textAlign: "center", padding: "52px 24px" }}>
                  <div style={{ opacity: 0.2, marginBottom: 12, display: "flex", justifyContent: "center" }}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width={36} height={36}>
                      <rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/><line x1="6" y1="15" x2="10" y2="15"/>
                    </svg>
                  </div>
                  <div style={{ fontSize: 14, color: T.muted }}>No credentials yet</div>
                  <div style={{ fontSize: 11, color: T.sub, marginTop: 4 }}>Add them manually or use Import Artists to pull from your lineup.</div>
                </div>
              ) : (
                <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, overflow: "hidden" }}>
                  {filtered.map((cred, i) => (
                    <div key={cred.id}
                      style={{ display: "flex", alignItems: "center", gap: 12, padding: "11px 16px",
                        borderBottom: i < filtered.length - 1 ? `1px solid ${T.border}` : "none",
                        background: cred.issued ? `${T.green}0a` : "transparent",
                        transition: "background 0.2s" }}>
                      {/* Issued checkbox */}
                      <button onClick={() => toggleIssued(cred)} title={cred.issued ? "Mark not issued" : "Mark as issued"}
                        style={{ width: 18, height: 18, borderRadius: 4, flexShrink: 0, padding: 0, cursor: "pointer",
                          border: `1.5px solid ${cred.issued ? T.green : T.sub}`,
                          background: cred.issued ? T.green : "none",
                          display: "flex", alignItems: "center", justifyContent: "center" }}>
                        {cred.issued && (
                          <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" width={10} height={10}>
                            <polyline points="20 6 9 17 4 12"/>
                          </svg>
                        )}
                      </button>
                      {/* Name + company */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 14, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                          color: cred.issued ? T.muted : T.text, textDecoration: cred.issued ? "line-through" : "none" }}>
                          {cred.name}
                        </div>
                        {cred.company && <div style={{ fontSize: 11, color: T.muted, marginTop: 1 }}>{cred.company}</div>}
                      </div>
                      {/* Type chip */}
                      <span style={{ fontSize: 11, padding: "3px 9px", borderRadius: 16, letterSpacing: 0.5, fontWeight: 700, flexShrink: 0,
                        background: `${CRED_COLORS[cred.type] || T.btn}18`, color: CRED_COLORS[cred.type] || T.btn }}>
                        {cred.type.toUpperCase()}
                      </span>
                      {/* Issued-at time */}
                      {cred.issued && cred.issued_at && (
                        <span style={{ fontSize: 11, color: T.green, flexShrink: 0 }}>
                          {new Date(cred.issued_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                        </span>
                      )}
                      {/* Email link */}
                      {cred.email && (
                        <a href={`mailto:${cred.email}`} title={cred.email} style={{ color: T.muted, display: "flex", flexShrink: 0 }}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width={13} height={13}>
                            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/>
                          </svg>
                        </a>
                      )}
                      {/* Notes indicator */}
                      {cred.notes && (
                        <span title={cred.notes} style={{ color: T.muted, display: "flex", flexShrink: 0 }}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width={12} height={12}>
                            <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
                          </svg>
                        </span>
                      )}
                      {/* Actions */}
                      <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
                        <button onClick={() => openEditCred(cred)}
                          style={{ fontFamily: FF, fontSize: 11, padding: "4px 10px", background: "none", border: `1px solid ${T.border}`, borderRadius: 4, color: T.muted, cursor: "pointer" }}>
                          Edit
                        </button>
                        <button onClick={() => deleteCred(cred.id)}
                          style={{ fontFamily: FF, fontSize: 11, padding: "4px 10px", background: "none", border: `1px solid ${T.border}`, borderRadius: 4, color: T.red, cursor: "pointer" }}>
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })()}

        {/* ── PROFILE TAB ── */}
        {tab === "profile" && (
          <div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 3 }}>Festival Profile</div>
                <div style={{ fontSize: 11, color: T.muted }}>Basic info about your festival. Visible to tour managers when you link to their shows.</div>
              </div>
              {!profileEdit && (
                <button onClick={openProfileEdit}
                  style={{ fontFamily: FF, fontSize: 12, padding: "7px 16px", background: "none", border: `1px solid ${T.border}`, borderRadius: 8, color: T.text, cursor: "pointer" }}>
                  Edit
                </button>
              )}
            </div>

            {/* Your display name — used as liaison identifier */}
            {(() => {
              const me = orgMembers.find(m => m.user_id === user?.id);
              return (
                <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, padding: "16px 20px", marginBottom: 16 }}>
                  <div style={{ fontSize: 11, color: T.muted, letterSpacing: 0.8, marginBottom: 10 }}>YOUR DISPLAY NAME</div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <input
                      value={myName}
                      onChange={e => setMyName(e.target.value)}
                      onKeyDown={e => { if (e.key === "Enter") saveLiaisonName(myName); }}
                      placeholder="e.g. Jamie Lee"
                      style={{ fontFamily: FF, fontSize: 14, flex: 1, padding: "7px 11px", background: T.inp, border: `1px solid ${T.inpBorder}`, borderRadius: 8, color: T.text, outline: "none" }}
                    />
                    <button onClick={() => saveLiaisonName(myName)} disabled={!myName.trim() || myName.trim() === me?.name}
                      style={{ fontFamily: FF, fontSize: 12, fontWeight: 700, padding: "7px 16px", background: T.btn, color: T.btnText, border: "none", borderRadius: 8, cursor: "pointer", opacity: (!myName.trim() || myName.trim() === me?.name) ? 0.5 : 1 }}>
                      Save
                    </button>
                  </div>
                  <div style={{ fontSize: 11, color: T.sub, marginTop: 6 }}>This name appears in the Advancing tab when you're assigned as a liaison.</div>
                  {orgMembers.filter(m => m.name && m.user_id !== user?.id).length > 0 && (
                    <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${T.border}` }}>
                      <div style={{ fontSize: 11, color: T.muted, letterSpacing: 0.8, marginBottom: 6 }}>TEAM</div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                        {orgMembers.filter(m => m.name && m.user_id !== user?.id).map(m => (
                          <span key={m.id} style={{ fontFamily: FF, fontSize: 11, color: T.muted, background: T.cardAlt, borderRadius: 16, padding: "3px 10px" }}>{m.name}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            {!profileEdit ? (
              <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, padding: "20px 22px" }}>
                {[
                  { label: "Festival Name", value: orgData?.name },
                  { label: "Tagline",       value: orgData?.profile?.tagline },
                  { label: "Dates",         value: orgData?.profile?.dates_start && orgData?.profile?.dates_end
                      ? `${orgData.profile.dates_start} → ${orgData.profile.dates_end}`
                      : orgData?.profile?.dates_start || null },
                  { label: "Location",      value: orgData?.profile?.location },
                  { label: "Capacity",      value: orgData?.profile?.capacity ? parseInt(orgData.profile.capacity).toLocaleString() : null },
                  { label: "Website",       value: orgData?.profile?.website },
                ].map(({ label, value }) => (
                  <div key={label} style={{ display: "flex", gap: 16, padding: "9px 0", borderBottom: `1px solid ${T.border}` }}>
                    <div style={{ width: 140, fontSize: 11, color: T.muted, flexShrink: 0, paddingTop: 1, letterSpacing: 0.5 }}>{label.toUpperCase()}</div>
                    <div style={{ fontSize: 14, color: value ? T.text : T.sub }}>{value || <span style={{ fontStyle: "italic" }}>Not set</span>}</div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, padding: "20px 22px" }}>
                {[
                  { key: "name",        label: "Festival Name", placeholder: "e.g. Sunset Fields" },
                  { key: "tagline",     label: "Tagline",       placeholder: "e.g. Three days, three stages" },
                  { key: "dates_start", label: "Start Date",    placeholder: "YYYY-MM-DD" },
                  { key: "dates_end",   label: "End Date",      placeholder: "YYYY-MM-DD" },
                  { key: "location",    label: "Location",      placeholder: "City, State / Country" },
                  { key: "capacity",    label: "Capacity",      placeholder: "e.g. 15000" },
                  { key: "website",     label: "Website",       placeholder: "https://..." },
                ].map(({ key, label, placeholder }) => (
                  <div key={key} style={{ marginBottom: 14 }}>
                    <div style={{ fontSize: 11, color: T.muted, letterSpacing: 1, marginBottom: 5 }}>{label.toUpperCase()}</div>
                    <input
                      value={profileDraft[key] || ""}
                      onChange={e => setProfileDraft(d => ({ ...d, [key]: e.target.value }))}
                      placeholder={placeholder}
                      style={{ fontFamily: FF, fontSize: 14, width: "100%", padding: "8px 11px", background: T.inp,
                        border: `1px solid ${T.inpBorder}`, borderRadius: 8, color: T.text, boxSizing: "border-box",
                        outline: "none" }}
                    />
                  </div>
                ))}
                <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
                  <button onClick={saveProfile} disabled={profileSaving}
                    style={{ fontFamily: FF, fontSize: 12, fontWeight: 700, padding: "8px 20px", background: T.btn, color: T.btnText, border: "none", borderRadius: 8, cursor: "pointer" }}>
                    {profileSaving ? "Saving…" : "Save"}
                  </button>
                  <button onClick={() => setProfileEdit(false)}
                    style={{ fontFamily: FF, fontSize: 12, padding: "8px 16px", background: "none", border: `1px solid ${T.border}`, borderRadius: 8, color: T.muted, cursor: "pointer" }}>
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Stage Modal ─────────────────────────────────────────────────────── */}
      {stageModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 12, padding: "28px 28px 24px", width: 380, fontFamily: FF }}>
            <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 20 }}>
              {stageModal.mode === "add" ? "Add Stage" : `Edit "${stageModal.stage?.name}"`}
            </div>

            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, color: T.muted, letterSpacing: 1, marginBottom: 5 }}>STAGE NAME *</div>
              <input value={stageName} onChange={e => setStageName(e.target.value)}
                placeholder="e.g. Main Stage"
                autoFocus
                style={{ fontFamily: FF, fontSize: 14, width: "100%", padding: "8px 11px", background: T.inp, border: `1px solid ${T.inpBorder}`, borderRadius: 8, color: T.text, boxSizing: "border-box", outline: "none" }} />
            </div>

            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, color: T.muted, letterSpacing: 1, marginBottom: 5 }}>CAPACITY (optional)</div>
              <input value={stageCapacity} onChange={e => setStageCapacity(e.target.value)}
                placeholder="e.g. 5000" type="number"
                style={{ fontFamily: FF, fontSize: 14, width: "100%", padding: "8px 11px", background: T.inp, border: `1px solid ${T.inpBorder}`, borderRadius: 8, color: T.text, boxSizing: "border-box", outline: "none" }} />
            </div>

            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 11, color: T.muted, letterSpacing: 1, marginBottom: 8 }}>COLOR</div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {STAGE_COLORS.map(c => (
                  <button key={c} onClick={() => setStageColor(c)}
                    style={{ width: 28, height: 28, borderRadius: 8, background: c, border: stageColor === c ? `3px solid ${T.text}` : `2px solid transparent`, cursor: "pointer", padding: 0 }} />
                ))}
              </div>
              {/* Also show a color preview swatch */}
              <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{ width: 10, height: 28, borderRadius: 4, background: stageColor }} />
                <span style={{ fontSize: 11, color: T.muted }}>{stageName || "Stage name"}</span>
              </div>
            </div>

            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={saveStage} disabled={stageSaving || !stageName.trim()}
                style={{ fontFamily: FF, flex: 1, fontSize: 12, fontWeight: 700, padding: "9px", background: stageName.trim() ? T.btn : T.border, color: stageName.trim() ? T.btnText : T.muted, border: "none", borderRadius: 8, cursor: stageName.trim() ? "pointer" : "default" }}>
                {stageSaving ? "Saving…" : stageModal.mode === "add" ? "Add Stage" : "Save Changes"}
              </button>
              <button onClick={() => setStageModal(null)}
                style={{ fontFamily: FF, fontSize: 12, padding: "9px 16px", background: "none", border: `1px solid ${T.border}`, borderRadius: 8, color: T.muted, cursor: "pointer" }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Run Modal (F-5c) ────────────────────────────────────────────────── */}
      {runModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}
          onClick={e => e.target === e.currentTarget && setRunModal(null)}>
          <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 12, padding: "28px 28px 24px", width: 420, maxHeight: "85vh", overflowY: "auto", fontFamily: FF }}>
            <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 20 }}>
              {runModal.mode === "edit" ? `Edit Run — ${runModal.run?.pickup_name}` : "Add Run"}
            </div>

            {/* Pickup name */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, color: T.muted, letterSpacing: 1, marginBottom: 5 }}>WHO / WHAT *</div>
              <input value={rPickupName} onChange={e => setRPickupName(e.target.value)} placeholder="e.g. The Midnight, VIP Guest, Equipment" autoFocus
                style={{ fontFamily: FF, fontSize: 14, width: "100%", padding: "8px 11px", background: T.inp, border: `1px solid ${T.inpBorder}`, borderRadius: 8, color: T.text, boxSizing: "border-box", outline: "none" }} />
            </div>

            {/* Time + Runner row */}
            <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, color: T.muted, letterSpacing: 1, marginBottom: 5 }}>PICKUP TIME</div>
                <input type="time" value={fmtMinsToTime(rTimeMins)} onChange={e => setRTimeMins(parseTimeToMins(e.target.value))}
                  style={{ fontFamily: FF, fontSize: 14, width: "100%", padding: "8px 11px", background: T.inp, border: `1px solid ${T.inpBorder}`, borderRadius: 8, color: T.text, boxSizing: "border-box", outline: "none" }} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, color: T.muted, letterSpacing: 1, marginBottom: 5 }}>ASSIGNED RUNNER</div>
                <input value={rRunnerName} onChange={e => setRRunnerName(e.target.value)} placeholder="Runner name (optional)"
                  style={{ fontFamily: FF, fontSize: 14, width: "100%", padding: "8px 11px", background: T.inp, border: `1px solid ${T.inpBorder}`, borderRadius: 8, color: T.text, boxSizing: "border-box", outline: "none" }} />
              </div>
            </div>

            {/* From / To */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, color: T.muted, letterSpacing: 1, marginBottom: 5 }}>PICKUP LOCATION</div>
              <input value={rPickupLoc} onChange={e => setRPickupLoc(e.target.value)} placeholder="e.g. Fairmont Hotel Lobby"
                style={{ fontFamily: FF, fontSize: 14, width: "100%", padding: "8px 11px", background: T.inp, border: `1px solid ${T.inpBorder}`, borderRadius: 8, color: T.text, boxSizing: "border-box", outline: "none" }} />
            </div>
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, color: T.muted, letterSpacing: 1, marginBottom: 5 }}>DROPOFF LOCATION</div>
              <input value={rDropoffLoc} onChange={e => setRDropoffLoc(e.target.value)} placeholder="e.g. Main Stage Artist Entrance"
                style={{ fontFamily: FF, fontSize: 14, width: "100%", padding: "8px 11px", background: T.inp, border: `1px solid ${T.inpBorder}`, borderRadius: 8, color: T.text, boxSizing: "border-box", outline: "none" }} />
            </div>

            {/* Notes */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 11, color: T.muted, letterSpacing: 1, marginBottom: 5 }}>NOTES</div>
              <textarea value={rNotes} onChange={e => setRNotes(e.target.value)} placeholder="Special requirements, contact number, vehicle info…" rows={2}
                style={{ fontFamily: FF, fontSize: 14, width: "100%", padding: "8px 11px", background: T.inp, border: `1px solid ${T.inpBorder}`, borderRadius: 8, color: T.text, boxSizing: "border-box", outline: "none", resize: "vertical" }} />
            </div>

            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={saveRun} disabled={runSaving || !rPickupName.trim()}
                style={{ fontFamily: FF, flex: 1, fontSize: 12, fontWeight: 700, padding: "9px", background: rPickupName.trim() ? T.btn : T.border, color: rPickupName.trim() ? T.btnText : T.muted, border: "none", borderRadius: 8, cursor: rPickupName.trim() ? "pointer" : "default" }}>
                {runSaving ? "Saving…" : runModal.mode === "add" ? "Add Run" : "Save Changes"}
              </button>
              <button onClick={() => setRunModal(null)}
                style={{ fontFamily: FF, fontSize: 12, padding: "9px 16px", background: "none", border: `1px solid ${T.border}`, borderRadius: 8, color: T.muted, cursor: "pointer" }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Vendor Modal (F-5b) ─────────────────────────────────────────────── */}
      {vendorModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}
          onClick={e => e.target === e.currentTarget && setVendorModal(null)}>
          <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 12, padding: "28px 28px 24px", width: 460, maxHeight: "88vh", overflowY: "auto", fontFamily: FF }}>
            <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 20 }}>
              {vendorModal.mode === "edit" ? `Edit — ${vendorModal.vendor?.name}` : "Add Vendor / Contractor"}
            </div>

            {/* Name */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, color: T.muted, letterSpacing: 1, marginBottom: 5 }}>VENDOR NAME *</div>
              <input value={vName} onChange={e => setVName(e.target.value)} placeholder="e.g. Levy Restaurants" autoFocus
                style={{ fontFamily: FF, fontSize: 14, width: "100%", padding: "8px 11px", background: T.inp, border: `1px solid ${T.inpBorder}`, borderRadius: 8, color: T.text, boxSizing: "border-box", outline: "none" }} />
            </div>

            {/* Type */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, color: T.muted, letterSpacing: 1, marginBottom: 8 }}>TYPE</div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {VENDOR_TYPES.map(t => (
                  <button key={t} onClick={() => setVType(t)}
                    style={{ fontFamily: FF, fontSize: 11, padding: "5px 11px", borderRadius: 16,
                      background: vType === t ? (VENDOR_COLORS[t] || T.btn) : "none",
                      color: vType === t ? "#fff" : T.muted,
                      border: `1px solid ${vType === t ? (VENDOR_COLORS[t] || T.btn) : T.border}`,
                      cursor: "pointer", fontWeight: vType === t ? 700 : 400 }}>
                    {t.charAt(0).toUpperCase() + t.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            {/* Status */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, color: T.muted, letterSpacing: 1, marginBottom: 8 }}>STATUS</div>
              <div style={{ display: "flex", gap: 6 }}>
                {["pending", "confirmed", "cancelled"].map(s => (
                  <button key={s} onClick={() => setVStatus(s)}
                    style={{ fontFamily: FF, fontSize: 11, padding: "5px 13px", borderRadius: 16,
                      background: vStatus === s ? (VENDOR_STATUS_COLORS[s] || T.btn) : "none",
                      color: vStatus === s ? "#fff" : T.muted,
                      border: `1px solid ${vStatus === s ? (VENDOR_STATUS_COLORS[s] || T.btn) : T.border}`,
                      cursor: "pointer", fontWeight: vStatus === s ? 700 : 400 }}>
                    {s.charAt(0).toUpperCase() + s.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            {/* Contract status */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, color: T.muted, letterSpacing: 1, marginBottom: 8 }}>CONTRACT</div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {Object.entries(CONTRACT_LABELS).map(([key, label]) => (
                  <button key={key} onClick={() => setVContractStatus(key)}
                    style={{ fontFamily: FF, fontSize: 11, padding: "5px 11px", borderRadius: 16,
                      background: vContractStatus === key ? `${CONTRACT_COLORS[key]}22` : "none",
                      color: vContractStatus === key ? CONTRACT_COLORS[key] : T.muted,
                      border: `1px solid ${vContractStatus === key ? CONTRACT_COLORS[key] : T.border}`,
                      cursor: "pointer", fontWeight: vContractStatus === key ? 700 : 400 }}>
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Contact */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, color: T.muted, letterSpacing: 1, marginBottom: 5 }}>CONTACT NAME</div>
              <input value={vContactName} onChange={e => setVContactName(e.target.value)} placeholder="e.g. Sarah Johnson"
                style={{ fontFamily: FF, fontSize: 14, width: "100%", padding: "8px 11px", background: T.inp, border: `1px solid ${T.inpBorder}`, borderRadius: 8, color: T.text, boxSizing: "border-box", outline: "none" }} />
            </div>
            <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, color: T.muted, letterSpacing: 1, marginBottom: 5 }}>EMAIL</div>
                <input value={vContactEmail} onChange={e => setVContactEmail(e.target.value)} placeholder="vendor@example.com" type="email"
                  style={{ fontFamily: FF, fontSize: 14, width: "100%", padding: "8px 11px", background: T.inp, border: `1px solid ${T.inpBorder}`, borderRadius: 8, color: T.text, boxSizing: "border-box", outline: "none" }} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, color: T.muted, letterSpacing: 1, marginBottom: 5 }}>PHONE</div>
                <input value={vContactPhone} onChange={e => setVContactPhone(e.target.value)} placeholder="+1 555 000 0000"
                  style={{ fontFamily: FF, fontSize: 14, width: "100%", padding: "8px 11px", background: T.inp, border: `1px solid ${T.inpBorder}`, borderRadius: 8, color: T.text, boxSizing: "border-box", outline: "none" }} />
              </div>
            </div>

            {/* Day assignments */}
            {festivalDays > 1 && (
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 11, color: T.muted, letterSpacing: 1, marginBottom: 8 }}>ON SITE — WHICH DAYS?</div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {Array.from({ length: festivalDays }, (_, i) => i).map(di => {
                    const active = vDays.includes(di);
                    return (
                      <button key={di} onClick={() => setVDays(prev => active ? prev.filter(d => d !== di) : [...prev, di])}
                        style={{ fontFamily: FF, fontSize: 11, padding: "5px 11px", borderRadius: 8,
                          background: active ? T.btn : "none", color: active ? T.btnText : T.muted,
                          border: `1px solid ${active ? T.btn : T.border}`, cursor: "pointer", fontWeight: active ? 700 : 400 }}>
                        {dayLabel(di)}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Notes */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 11, color: T.muted, letterSpacing: 1, marginBottom: 5 }}>NOTES</div>
              <textarea value={vNotes} onChange={e => setVNotes(e.target.value)} placeholder="Setup times, special requirements, access needs…" rows={2}
                style={{ fontFamily: FF, fontSize: 14, width: "100%", padding: "8px 11px", background: T.inp, border: `1px solid ${T.inpBorder}`, borderRadius: 8, color: T.text, boxSizing: "border-box", outline: "none", resize: "vertical" }} />
            </div>

            {/* Actions */}
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={saveVendor} disabled={vendorSaving || !vName.trim()}
                style={{ fontFamily: FF, flex: 1, fontSize: 12, fontWeight: 700, padding: "9px", background: vName.trim() ? T.btn : T.border, color: vName.trim() ? T.btnText : T.muted, border: "none", borderRadius: 8, cursor: vName.trim() ? "pointer" : "default" }}>
                {vendorSaving ? "Saving…" : vendorModal.mode === "add" ? "Add Vendor" : "Save Changes"}
              </button>
              <button onClick={() => setVendorModal(null)}
                style={{ fontFamily: FF, fontSize: 12, padding: "9px 16px", background: "none", border: `1px solid ${T.border}`, borderRadius: 8, color: T.muted, cursor: "pointer" }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Credential Modal (F-5) ──────────────────────────────────────────── */}
      {credModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}
          onClick={e => e.target === e.currentTarget && setCredModal(null)}>
          <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 12, padding: "28px 28px 24px", width: 400, maxHeight: "85vh", overflowY: "auto", fontFamily: FF }}>
            <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 20 }}>
              {credModal.mode === "edit" ? `Edit — ${credModal.cred?.name}` : "Add Credential"}
            </div>

            {/* Name */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, color: T.muted, letterSpacing: 1, marginBottom: 5 }}>NAME *</div>
              <input value={credName} onChange={e => setCredName(e.target.value)} placeholder="e.g. The Midnight" autoFocus
                style={{ fontFamily: FF, fontSize: 14, width: "100%", padding: "8px 11px", background: T.inp, border: `1px solid ${T.inpBorder}`, borderRadius: 8, color: T.text, boxSizing: "border-box", outline: "none" }} />
            </div>

            {/* Type */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, color: T.muted, letterSpacing: 1, marginBottom: 8 }}>CREDENTIAL TYPE</div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {["artist", "crew", "press", "vip", "production", "vendor"].map(t => (
                  <button key={t} onClick={() => setCredType(t)}
                    style={{ fontFamily: FF, fontSize: 11, padding: "5px 12px", borderRadius: 16,
                      background: credType === t ? (CRED_COLORS[t] || T.btn) : "none",
                      color: credType === t ? "#fff" : T.muted,
                      border: `1px solid ${credType === t ? (CRED_COLORS[t] || T.btn) : T.border}`,
                      cursor: "pointer", fontWeight: credType === t ? 700 : 400 }}>
                    {t.charAt(0).toUpperCase() + t.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            {/* Company */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, color: T.muted, letterSpacing: 1, marginBottom: 5 }}>COMPANY / BAND</div>
              <input value={credCompany} onChange={e => setCredCompany(e.target.value)} placeholder="e.g. Interscope Records"
                style={{ fontFamily: FF, fontSize: 14, width: "100%", padding: "8px 11px", background: T.inp, border: `1px solid ${T.inpBorder}`, borderRadius: 8, color: T.text, boxSizing: "border-box", outline: "none" }} />
            </div>

            {/* Email */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, color: T.muted, letterSpacing: 1, marginBottom: 5 }}>EMAIL</div>
              <input value={credEmail} onChange={e => setCredEmail(e.target.value)} placeholder="contact@example.com" type="email"
                style={{ fontFamily: FF, fontSize: 14, width: "100%", padding: "8px 11px", background: T.inp, border: `1px solid ${T.inpBorder}`, borderRadius: 8, color: T.text, boxSizing: "border-box", outline: "none" }} />
            </div>

            {/* Notes */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 11, color: T.muted, letterSpacing: 1, marginBottom: 5 }}>NOTES</div>
              <textarea value={credNotes} onChange={e => setCredNotes(e.target.value)} placeholder="Any notes about this credential…" rows={2}
                style={{ fontFamily: FF, fontSize: 14, width: "100%", padding: "8px 11px", background: T.inp, border: `1px solid ${T.inpBorder}`, borderRadius: 8, color: T.text, boxSizing: "border-box", outline: "none", resize: "vertical" }} />
            </div>

            {/* Actions */}
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={saveCred} disabled={credSaving || !credName.trim()}
                style={{ fontFamily: FF, flex: 1, fontSize: 12, fontWeight: 700, padding: "9px", background: credName.trim() ? T.btn : T.border, color: credName.trim() ? T.btnText : T.muted, border: "none", borderRadius: 8, cursor: credName.trim() ? "pointer" : "default" }}>
                {credSaving ? "Saving…" : credModal.mode === "add" ? "Add Credential" : "Save Changes"}
              </button>
              <button onClick={() => setCredModal(null)}
                style={{ fontFamily: FF, fontSize: 12, padding: "9px 16px", background: "none", border: `1px solid ${T.border}`, borderRadius: 8, color: T.muted, cursor: "pointer" }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Artist Modal ─────────────────────────────────────────────────────── */}
      {artistModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}
          onClick={e => e.target === e.currentTarget && setArtistModal(null)}>
          <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 12, padding: "28px 28px 24px", width: 440, maxHeight: "85vh", overflowY: "auto", fontFamily: FF }}>
            <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 20 }}>
              {artistModal.mode === "edit" ? `Edit — ${artistModal.artist?.artist_name}` : "Add Artist"}
            </div>

            {/* Name */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, color: T.muted, letterSpacing: 1, marginBottom: 5 }}>ARTIST NAME *</div>
              <input value={aName} onChange={e => setAName(e.target.value)} placeholder="e.g. The Midnight" autoFocus
                style={{ fontFamily: FF, fontSize: 14, width: "100%", padding: "8px 11px", background: T.inp, border: `1px solid ${T.inpBorder}`, borderRadius: 8, color: T.text, boxSizing: "border-box", outline: "none" }} />
            </div>

            {/* Stage + Day row */}
            <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, color: T.muted, letterSpacing: 1, marginBottom: 5 }}>STAGE</div>
                <select value={aStageId} onChange={e => setAStageId(e.target.value)}
                  style={{ fontFamily: FF, fontSize: 14, width: "100%", padding: "8px 10px", background: T.inp, border: `1px solid ${T.inpBorder}`, borderRadius: 8, color: aStageId ? T.text : T.muted, outline: "none" }}>
                  <option value="">— TBD —</option>
                  {stages.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              {festivalDays > 1 && (
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 11, color: T.muted, letterSpacing: 1, marginBottom: 5 }}>DAY</div>
                  <select value={aDayIndex} onChange={e => setADayIndex(parseInt(e.target.value, 10))}
                    style={{ fontFamily: FF, fontSize: 14, width: "100%", padding: "8px 10px", background: T.inp, border: `1px solid ${T.inpBorder}`, borderRadius: 8, color: T.text, outline: "none" }}>
                    {Array.from({ length: festivalDays }, (_, i) => <option key={i} value={i}>{dayLabel(i)}</option>)}
                  </select>
                </div>
              )}
            </div>

            {/* Set time + Duration row */}
            <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, color: T.muted, letterSpacing: 1, marginBottom: 5 }}>SET TIME</div>
                <select value={aStartMins ?? ""} onChange={e => setAStartMins(e.target.value === "" ? null : parseInt(e.target.value, 10))}
                  style={{ fontFamily: FF, fontSize: 14, width: "100%", padding: "8px 10px", background: T.inp, border: `1px solid ${T.inpBorder}`, borderRadius: 8, color: aStartMins != null ? T.text : T.muted, outline: "none" }}>
                  <option value="">— TBD —</option>
                  {Array.from({ length: (GRID_END - GRID_START) / SLOT_MINS }, (_, i) => {
                    const m = GRID_START + i * SLOT_MINS;
                    return <option key={m} value={m}>{fmtGridTime(m)}</option>;
                  })}
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, color: T.muted, letterSpacing: 1, marginBottom: 5 }}>DURATION</div>
                <select value={aDuration} onChange={e => setADuration(parseInt(e.target.value, 10))}
                  style={{ fontFamily: FF, fontSize: 14, width: "100%", padding: "8px 10px", background: T.inp, border: `1px solid ${T.inpBorder}`, borderRadius: 8, color: T.text, outline: "none" }}>
                  {[30,45,60,75,90,105,120,150,180].map(d => <option key={d} value={d}>{d < 60 ? `${d}m` : d === 60 ? "1h" : d % 60 === 0 ? `${d/60}h` : `${Math.floor(d/60)}h ${d%60}m`}</option>)}
                </select>
              </div>
            </div>

            {/* Status */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, color: T.muted, letterSpacing: 1, marginBottom: 5 }}>STATUS</div>
              <div style={{ display: "flex", gap: 6 }}>
                {["unconfirmed","advancing","confirmed","cancelled"].map(s => (
                  <button key={s} onClick={() => setAStatus(s)}
                    style={{ fontFamily: FF, fontSize: 11, padding: "5px 11px", borderRadius: 16, cursor: "pointer",
                      background: aStatus === s ? (STATUS_COLORS[s] || T.btn) : "none",
                      color: aStatus === s ? "#fff" : T.muted,
                      border: `1px solid ${aStatus === s ? (STATUS_COLORS[s] || T.btn) : T.border}` }}>
                    {s.charAt(0).toUpperCase() + s.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            {/* Liaison */}
            {orgMembers.some(m => m.name) && (
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 11, color: T.muted, letterSpacing: 1, marginBottom: 5 }}>LIAISON</div>
                <select value={aLiaisonId} onChange={e => setALiaisonId(e.target.value)}
                  style={{ fontFamily: FF, fontSize: 14, width: "100%", padding: "8px 10px", background: T.inp, border: `1px solid ${T.inpBorder}`, borderRadius: 8, color: aLiaisonId ? T.text : T.muted, outline: "none" }}>
                  <option value="">— Unassigned —</option>
                  {orgMembers.filter(m => m.name).map(m => (
                    <option key={m.id} value={m.id}>{m.name}{m.user_id === user?.id ? " (you)" : ""}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Contact email */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, color: T.muted, letterSpacing: 1, marginBottom: 5 }}>CONTACT EMAIL</div>
              <input value={aEmail} onChange={e => setAEmail(e.target.value)} placeholder="TM or artist contact"
                style={{ fontFamily: FF, fontSize: 14, width: "100%", padding: "8px 11px", background: T.inp, border: `1px solid ${T.inpBorder}`, borderRadius: 8, color: T.text, boxSizing: "border-box", outline: "none" }} />
            </div>

            {/* Notes */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 11, color: T.muted, letterSpacing: 1, marginBottom: 5 }}>NOTES</div>
              <textarea value={aNotes} onChange={e => setANotes(e.target.value)} rows={2} placeholder="Any notes..."
                style={{ fontFamily: FF, fontSize: 14, width: "100%", padding: "8px 11px", background: T.inp, border: `1px solid ${T.inpBorder}`, borderRadius: 8, color: T.text, boxSizing: "border-box", outline: "none", resize: "vertical" }} />
            </div>

            {/* Time preview */}
            {aStartMins != null && (
              <div style={{ marginBottom: 16, padding: "8px 12px", background: T.page, borderRadius: 8, fontSize: 12, color: T.muted }}>
                Set: {fmtTimeRange(aStartMins, aDuration)}
                {aStageId && stages.find(s => s.id === aStageId) && ` · ${stages.find(s => s.id === aStageId).name}`}
              </div>
            )}

            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={saveArtist} disabled={artistSaving || !aName.trim()}
                style={{ fontFamily: FF, flex: 1, fontSize: 12, fontWeight: 700, padding: "9px", background: aName.trim() ? T.btn : T.border, color: aName.trim() ? T.btnText : T.muted, border: "none", borderRadius: 8, cursor: aName.trim() ? "pointer" : "default" }}>
                {artistSaving ? "Saving…" : artistModal.mode === "edit" ? "Save Changes" : "Add to Lineup"}
              </button>
              <button onClick={() => setArtistModal(null)}
                style={{ fontFamily: FF, fontSize: 12, padding: "9px 16px", background: "none", border: `1px solid ${T.border}`, borderRadius: 8, color: T.muted, cursor: "pointer" }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Venue Dashboard ──────────────────────────────────────────────────────────
// V-1: Show calendar + requests + authenticated advance portal
function VenueDashboard({ orgData, orgType, user, onUpdateOrg }) {
  const isLight = localStorage.getItem("lightMode") !== "false";
  const T = isLight
    ? { page: "#f0ede8", card: "#e8e4de", border: "#d0ccc6", inp: "#e0dbd4", inpBorder: "#ccc8c2", text: "#1a1917", muted: "#8a8784", sub: "#9a9794", btn: "#c47f00", btnText: "#fff", rowHov: "#dedad4", amber: "#c47f00", green: "#2a7d4f", red: "#c0392b", blue: "#2860d0" }
    : { page: "#0d0d0d", card: "#141414", border: "var(--panel-border)",    inp: "#1a1a1a", inpBorder: "#2a2a2a", text: "#fff",    muted: "#555",    sub: "#444",    btn: "var(--accent)", btnText: "#0d0d0d", rowHov: "#1e1e1e",  amber: "var(--accent)", green: "var(--success)", red: "#f87171", blue: "#5b8af5" };
  const FF = "'Helvetica Neue', Helvetica, Arial, sans-serif";

  const [tab,            setTab]            = useState("calendar"); // 'calendar' | 'shows' | 'holds' | 'requests' | 'history' | 'profile'
  const [eventLinks,     setEventLinks]     = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [editingName,    setEditingName]    = useState(false);
  const [orgName,        setOrgName]        = useState(orgData?.name || "");
  const [saving,         setSaving]         = useState(false);
  const [activePortal,   setActivePortal]   = useState(null);
  const [realtimeStatus, setRealtimeStatus] = useState("connecting"); // 'connecting' | 'connected' | 'disconnected'
  const [holdModal,      setHoldModal]      = useState(null);         // null | { link, holdNumber, expires }
  const [calMonth,       setCalMonth]       = useState(() => { const n = new Date(); return new Date(n.getFullYear(), n.getMonth(), 1); });
  const [calSelected,    setCalSelected]    = useState(null);         // 'YYYY-MM-DD' | null
  const [maxHoldSlot,    setMaxHoldSlot]    = useState(15);
  const [codeInput,      setCodeInput]      = useState("");           // ORG-1: enter a show code
  const [codeLookup,     setCodeLookup]     = useState(null);         // { loading, result, error }

  // ORG-1: Look up a show by TM-generated link code
  const lookupAndRequestLink = async () => {
    const code = codeInput.trim().toUpperCase();
    if (!code || !orgData?.id) return;
    setCodeLookup({ loading: true, result: null, error: null });
    const { data, error } = await supabase.rpc("lookup_show_by_link_code", { p_code: code });
    if (error || !data) {
      setCodeLookup({ loading: false, result: null, error: "No show found for that code. Check the code and try again." });
      return;
    }
    // Create a pending event_link
    const linkId = Date.now().toString(36) + Math.random().toString(36).slice(2);
    const newLink = {
      id: linkId,
      show_id: data.show_id,
      org_id: orgData.id,
      org_role: "promoter",
      status: "pending",
      initiated_by_org: true,
      metadata: {
        show_date:     data.show_date,
        venue_name:    data.venue_name    || "",
        artist_name:   data.artist_name   || "",
        tour_name:     data.tour_name     || "",
        advance_token: data.advance_token || null,
        org_type:      orgData.type       || "venue",
      },
    };
    const { error: insErr } = await supabase.from("event_links").insert(newLink);
    if (insErr) {
      setCodeLookup({ loading: false, result: null, error: "Could not create link request. The show may already be linked." });
      return;
    }
    setEventLinks(prev => [newLink, ...prev]);
    setCodeLookup({ loading: false, result: data, error: null });
    setCodeInput("");
    setTab("requests");
  };

  // Load all event_links for this org on mount
  useEffect(() => {
    if (!orgData?.id) return;
    supabase.from("event_links").select("*").eq("org_id", orgData.id).order("created_at", { ascending: false })
      .then(({ data }) => { setEventLinks(data || []); setLoading(false); });
  }, [orgData?.id]);

  // V-4: Realtime subscription — keep event_links in sync without manual refresh
  useEffect(() => {
    if (!orgData?.id) return;
    setRealtimeStatus("connecting");
    const channel = supabase
      .channel(`venue-${orgData.id}`)
      .on("postgres_changes", {
        event: "INSERT",
        schema: "public",
        table: "event_links",
        filter: `org_id=eq.${orgData.id}`,
      }, payload => {
        // New link request from a TM — auto-jump to Requests tab if we're not there
        setEventLinks(prev => {
          if (prev.find(l => l.id === payload.new.id)) return prev;
          return [payload.new, ...prev];
        });
      })
      .on("postgres_changes", {
        event: "UPDATE",
        schema: "public",
        table: "event_links",
        filter: `org_id=eq.${orgData.id}`,
      }, payload => {
        // Status change, advance data update, settlement confirmation
        setEventLinks(prev => prev.map(l => l.id === payload.new.id ? { ...l, ...payload.new } : l));
      })
      .on("postgres_changes", {
        event: "DELETE",
        schema: "public",
        table: "event_links",
        filter: `org_id=eq.${orgData.id}`,
      }, payload => {
        setEventLinks(prev => prev.filter(l => l.id !== payload.old.id));
      })
      .subscribe(status => {
        if (status === "SUBSCRIBED")   setRealtimeStatus("connected");
        if (status === "CLOSED")       setRealtimeStatus("disconnected");
        if (status === "CHANNEL_ERROR") setRealtimeStatus("disconnected");
      });

    return () => { supabase.removeChannel(channel); };
  }, [orgData?.id]);

  const todayStr = new Date().toISOString().slice(0, 10);
  const confirmedShows = eventLinks
    .filter(l => l.status === "confirmed" && (!l.metadata?.show_date || l.metadata.show_date >= todayStr))
    .sort((a, b) => (a.metadata?.show_date || "").localeCompare(b.metadata?.show_date || ""));
  const historyShows = eventLinks
    .filter(l => l.status === "confirmed" && l.metadata?.show_date && l.metadata.show_date < todayStr)
    .sort((a, b) => (b.metadata?.show_date || "").localeCompare(a.metadata?.show_date || "")); // newest first
  const pendingRequests = eventLinks.filter(l => l.status === "pending");
  const holdLinks       = eventLinks
    .filter(l => l.status === "hold")
    .sort((a, b) => (a.hold_number || 99) - (b.hold_number || 99));
  const getHoldsOnDate  = (dateStr) => eventLinks.filter(l => l.status === "hold" && l.metadata?.show_date === dateStr);
  const getEventsOnDate = (dateStr) => eventLinks.filter(l => ["confirmed", "hold", "pending"].includes(l.status) && l.metadata?.show_date === dateStr);

  const confirmLink = async (link) => {
    await supabase.from("event_links").update({ status: "confirmed" }).eq("id", link.id);
    setEventLinks(prev => prev.map(l => l.id === link.id ? { ...l, status: "confirmed" } : l));
  };

  const declineLink = async (link) => {
    await supabase.from("event_links").update({ status: "declined" }).eq("id", link.id);
    setEventLinks(prev => prev.map(l => l.id === link.id ? { ...l, status: "declined" } : l));
  };

  const placeHold = async () => {
    if (!holdModal) return;
    const { link, holdNumber, expires } = holdModal;
    const updates = {
      status: "hold",
      hold_number: holdNumber,
      hold_expires_at: expires ? new Date(expires + "T23:59:59").toISOString() : null,
    };
    const { error } = await supabase.from("event_links").update(updates).eq("id", link.id);
    if (!error) {
      setEventLinks(prev => prev.map(l => l.id === link.id ? { ...l, ...updates } : l));
      setHoldModal(null);
    }
  };

  const releaseHold = async (link) => {
    const updates = { status: "pending", hold_number: null, hold_expires_at: null };
    await supabase.from("event_links").update(updates).eq("id", link.id);
    setEventLinks(prev => prev.map(l => l.id === link.id ? { ...l, ...updates } : l));
  };

  const convertHoldToConfirmed = async (link) => {
    const updates = { status: "confirmed", hold_number: null, hold_expires_at: null };
    await supabase.from("event_links").update(updates).eq("id", link.id);
    setEventLinks(prev => prev.map(l => l.id === link.id ? { ...l, ...updates } : l));
  };

  const saveOrgName = async () => {
    if (!orgName.trim() || orgName.trim() === orgData?.name) { setEditingName(false); return; }
    setSaving(true);
    await supabase.from("organizations").update({ name: orgName.trim() }).eq("id", orgData.id);
    if (onUpdateOrg) onUpdateOrg({ ...orgData, name: orgName.trim() });
    setSaving(false);
    setEditingName(false);
  };

  const typeLabel = orgType === "venue" ? "Venue" : orgType === "festival" ? "Festival" : "Organization";

  // If a portal is open, render it full-screen via VenueAdvancePanel
  if (activePortal) {
    return (
      <div>
        <div style={{ height: 44, background: T.card, borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", padding: "0 20px", gap: 12, fontFamily: FF }}>
          <button onClick={() => setActivePortal(null)}
            style={{ fontSize: 12, color: T.muted, background: "none", border: `1px solid ${T.border}`, borderRadius: 4, padding: "4px 12px", cursor: "pointer" }}>
            ← Back to Dashboard
          </button>
          <span style={{ fontSize: 11, color: T.muted, letterSpacing: 1 }}>ADVANCE · {activePortal.artistName}</span>
        </div>
        <VenueAdvancePanel token={activePortal.token} linkId={activePortal.linkId} lightMode={isLight} />
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: T.page, fontFamily: FF, color: T.text }}>
      {/* Top bar */}
      <div style={{ borderBottom: `1px solid ${T.border}`, padding: "0 32px", display: "flex", alignItems: "center", height: 52, background: T.card }}>
        <div style={{ fontSize: 20, letterSpacing: 3, color: T.btn, fontWeight: 700 }}>ADVANCD</div>
        <div style={{ marginLeft: 12, fontSize: 11, color: T.muted, letterSpacing: 1, paddingTop: 2 }}>{typeLabel.toUpperCase()}</div>
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 16 }}>
          {/* V-4: Live badge */}
          <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
            <span style={{
              display: "inline-block", width: 7, height: 7, borderRadius: "50%",
              background: realtimeStatus === "connected" ? T.green : realtimeStatus === "connecting" ? T.amber : "#666",
            }} />
            <span style={{ fontSize: 11, color: realtimeStatus === "connected" ? T.green : T.muted, fontFamily: FF, letterSpacing: 0.5 }}>
              {realtimeStatus === "connected" ? "Live" : realtimeStatus === "connecting" ? "Connecting…" : "Offline"}
            </span>
          </div>
          <span style={{ fontSize: 12, color: T.muted }}>{orgData?.name}</span>
          <button onClick={() => supabase.auth.signOut()} style={{ fontSize: 11, color: T.muted, background: "none", border: "none", cursor: "pointer", letterSpacing: 1 }}>SIGN OUT</button>
        </div>
      </div>

      {/* Tab bar */}
      <div style={{ borderBottom: `1px solid ${T.border}`, padding: "0 32px", display: "flex", gap: 0, background: T.card }}>
        {[
          { id: "calendar", label: "Calendar" },
          { id: "shows",    label: `Shows (${confirmedShows.length})` },
          { id: "holds",    label: `Holds${holdLinks.length ? ` (${holdLinks.length})` : ""}` },
          { id: "requests", label: `Requests${pendingRequests.length ? ` (${pendingRequests.length})` : ""}` },
          { id: "history",  label: `History${historyShows.length ? ` (${historyShows.length})` : ""}` },
          { id: "profile",  label: "Profile" },
        ].map(({ id, label }) => (
          <button key={id} onClick={() => setTab(id)}
            style={{ fontFamily: FF, fontSize: 12, fontWeight: tab === id ? 700 : 400, padding: "12px 18px", background: "none", border: "none", borderBottom: `2px solid ${tab === id ? T.btn : "transparent"}`, color: tab === id ? T.text : T.muted, cursor: "pointer", letterSpacing: 0.5 }}>
            {label}
          </button>
        ))}
      </div>

      <div style={{ maxWidth: 860, margin: "0 auto", padding: "32px 24px" }}>

        {/* CALENDAR TAB */}
        {tab === "calendar" && (() => {
          const year  = calMonth.getFullYear();
          const month = calMonth.getMonth();
          const firstDay    = new Date(year, month, 1).getDay();
          const daysInMonth = new Date(year, month + 1, 0).getDate();
          const totalCells  = Math.ceil((firstDay + daysInMonth) / 7) * 7;
          const todayStr    = new Date().toISOString().slice(0, 10);
          const monthLabel  = calMonth.toLocaleDateString("en-US", { month: "long", year: "numeric" });
          return (
            <div>
              {/* Month navigation */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
                <button onClick={() => setCalMonth(new Date(year, month - 1, 1))}
                  style={{ background: "none", border: `1px solid ${T.border}`, borderRadius: 8, padding: "5px 14px", color: T.text, cursor: "pointer", fontFamily: FF, fontSize: 14, lineHeight: 1 }}>‹</button>
                <div style={{ fontFamily: FF, fontSize: 14, fontWeight: 700, letterSpacing: 0.5 }}>{monthLabel}</div>
                <button onClick={() => setCalMonth(new Date(year, month + 1, 1))}
                  style={{ background: "none", border: `1px solid ${T.border}`, borderRadius: 8, padding: "5px 14px", color: T.text, cursor: "pointer", fontFamily: FF, fontSize: 14, lineHeight: 1 }}>›</button>
              </div>

              {/* Day-of-week headers */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", marginBottom: 2 }}>
                {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(d => (
                  <div key={d} style={{ fontFamily: FF, fontSize: 11, color: T.muted, textAlign: "center", padding: "4px 0", letterSpacing: 1, fontWeight: 600 }}>{d}</div>
                ))}
              </div>

              {/* Calendar grid */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 1, background: T.border, borderRadius: 8, overflow: "hidden", border: `1px solid ${T.border}` }}>
                {Array.from({ length: totalCells }, (_, i) => {
                  const d = i - firstDay + 1;
                  const isValid  = d >= 1 && d <= daysInMonth;
                  const dateStr  = isValid ? `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}` : null;
                  const dayEvents = dateStr ? getEventsOnDate(dateStr) : [];
                  const isToday    = dateStr === todayStr;
                  const isSelected = dateStr === calSelected;
                  const hasEvents  = dayEvents.length > 0;
                  return (
                    <div
                      key={i}
                      onClick={() => dateStr && hasEvents && setCalSelected(isSelected ? null : dateStr)}
                      style={{
                        background: isSelected ? (isLight ? "#e0dbd4" : "#1e1e1e") : T.page,
                        minHeight: 80, padding: "6px 6px 4px",
                        cursor: hasEvents && isValid ? "pointer" : "default",
                        borderBottom: isSelected ? `2px solid ${T.btn}` : "none",
                        transition: "background 0.1s",
                      }}
                    >
                      {isValid && (
                        <>
                          <div style={{
                            fontFamily: FF, fontSize: 11, fontWeight: isToday ? 700 : 400,
                            width: 22, height: 22, borderRadius: "50%",
                            background: isToday ? T.btn : "none",
                            color: isToday ? T.btnText : T.text,
                            display: "flex", alignItems: "center", justifyContent: "center",
                            marginBottom: 3,
                          }}>{d}</div>
                          {dayEvents.slice(0, 2).map(link => {
                            const color = link.status === "confirmed" ? T.green : link.status === "hold" ? T.blue : T.amber;
                            const label = link.status === "hold"
                              ? `H${link.hold_number} ${link.metadata?.artist_name || ""}`.trim()
                              : (link.metadata?.artist_name || "TBD");
                            return (
                              <div key={link.id} style={{
                                fontFamily: FF, fontSize: 11, fontWeight: 600, color: "#fff",
                                background: color, borderRadius: 4, padding: "2px 5px",
                                marginBottom: 2, overflow: "hidden", textOverflow: "ellipsis",
                                whiteSpace: "nowrap", letterSpacing: 0.2,
                              }}>{label}</div>
                            );
                          })}
                          {dayEvents.length > 2 && (
                            <div style={{ fontFamily: FF, fontSize: 11, color: T.muted }}>+{dayEvents.length - 2} more</div>
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Legend */}
              <div style={{ display: "flex", gap: 16, marginTop: 12, padding: "4px 0" }}>
                {[
                  { color: T.green, label: "Confirmed" },
                  { color: T.blue,  label: "Hold" },
                  { color: T.amber, label: "Pending" },
                ].map(({ color, label }) => (
                  <div key={label} style={{ display: "flex", alignItems: "center", gap: 5 }}>
                    <div style={{ width: 8, height: 8, borderRadius: 4, background: color }} />
                    <span style={{ fontFamily: FF, fontSize: 11, color: T.muted }}>{label}</span>
                  </div>
                ))}
              </div>

              {/* Selected day detail panel */}
              {calSelected && (() => {
                const links = getEventsOnDate(calSelected);
                if (!links.length) return null;
                const dateLabel = new Date(calSelected + "T12:00:00").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });
                const sorted = [...links].sort((a, b) => {
                  const ord = { confirmed: 0, hold: 1, pending: 2 };
                  if (ord[a.status] !== ord[b.status]) return ord[a.status] - ord[b.status];
                  return (a.hold_number || 99) - (b.hold_number || 99);
                });
                return (
                  <div style={{ marginTop: 16, background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, padding: "16px 20px" }}>
                    <div style={{ fontFamily: FF, fontSize: 11, fontWeight: 700, letterSpacing: 1, marginBottom: 14, color: T.muted }}>{dateLabel.toUpperCase()}</div>
                    {sorted.map((link, idx) => {
                      const color       = link.status === "confirmed" ? T.green : link.status === "hold" ? T.blue : T.amber;
                      const statusLabel = link.status === "hold" ? `Hold ${link.hold_number}` : link.status === "confirmed" ? "Confirmed" : "Pending";
                      const m           = link.metadata || {};
                      return (
                        <div key={link.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: idx < sorted.length - 1 ? `1px solid ${T.border}` : "none" }}>
                          <div style={{ minWidth: 76, fontFamily: FF, fontSize: 11, fontWeight: 700, color, letterSpacing: 0.5 }}>{statusLabel.toUpperCase()}</div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontFamily: FF, fontSize: 14, fontWeight: 700 }}>{m.artist_name || "Artist"}</div>
                            {m.tour_name && <div style={{ fontFamily: FF, fontSize: 11, color: T.muted }}>{m.tour_name}</div>}
                            {link.hold_expires_at && (
                              <div style={{ fontFamily: FF, fontSize: 11, color: T.muted, marginTop: 1 }}>
                                Expires {new Date(link.hold_expires_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                              </div>
                            )}
                          </div>
                          <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                            {link.status === "hold" && (<>
                              <button onClick={() => convertHoldToConfirmed(link)}
                                style={{ fontFamily: FF, fontSize: 11, fontWeight: 700, padding: "5px 12px", background: T.green, color: "#fff", border: "none", borderRadius: 4, cursor: "pointer" }}>
                                Confirm
                              </button>
                              <button onClick={() => setHoldModal({ link, holdNumber: link.hold_number, expires: link.hold_expires_at ? link.hold_expires_at.slice(0, 10) : "" })}
                                style={{ fontFamily: FF, fontSize: 11, padding: "5px 10px", background: "none", color: T.muted, border: `1px solid ${T.border}`, borderRadius: 4, cursor: "pointer" }}>
                                Edit
                              </button>
                              <button onClick={() => releaseHold(link)}
                                style={{ fontFamily: FF, fontSize: 11, padding: "5px 10px", background: "none", color: T.red, border: `1px solid ${T.border}`, borderRadius: 4, cursor: "pointer" }}>
                                Release
                              </button>
                            </>)}
                            {link.status === "pending" && (<>
                              <button onClick={() => confirmLink(link)}
                                style={{ fontFamily: FF, fontSize: 11, fontWeight: 700, padding: "5px 12px", background: T.btn, color: T.btnText, border: "none", borderRadius: 4, cursor: "pointer" }}>
                                Confirm
                              </button>
                              <button onClick={() => {
                                const takenNums = getHoldsOnDate(calSelected).map(l => l.hold_number);
                                const nextNum = Array.from({ length: 15 }, (_, i) => i + 1).find(n => !takenNums.includes(n)) || (takenNums.length + 1);
                                setHoldModal({ link, holdNumber: nextNum, expires: "" });
                              }} style={{ fontFamily: FF, fontSize: 11, padding: "5px 12px", background: "none", color: T.blue, border: `1px solid ${T.blue}44`, borderRadius: 4, cursor: "pointer" }}>
                                Place Hold
                              </button>
                            </>)}
                            {link.status === "confirmed" && m.advance_token && (
                              <button onClick={() => setActivePortal({ token: m.advance_token, linkId: link.id, artistName: m.artist_name })}
                                style={{ fontFamily: FF, fontSize: 11, padding: "5px 12px", background: "none", color: T.text, border: `1px solid ${T.border}`, borderRadius: 4, cursor: "pointer" }}>
                                Advance
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          );
        })()}

        {/* SHOWS TAB */}
        {tab === "shows" && (
          <div>
            {loading && <div style={{ fontFamily: FF, fontSize: 12, color: T.muted }}>Loading...</div>}
            {!loading && confirmedShows.length === 0 && (
              <div style={{ textAlign: "center", padding: "60px 0" }}>
                <div style={{ opacity: 0.25, color: T.text, marginBottom: 12 }}><svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg></div>
                <div style={{ fontFamily: FF, fontSize: 14, fontWeight: 600, marginBottom: 8 }}>No confirmed shows yet</div>
                <div style={{ fontFamily: FF, fontSize: 12, color: T.muted, lineHeight: 1.6 }}>
                  When a tour manager links one of their shows to your venue account, it will appear here.<br/>
                  Check the Requests tab for pending link requests.
                </div>
              </div>
            )}
            {confirmedShows.map(link => {
              const m = link.metadata || {};
              return (
                <VenueShowCard key={link.id} link={link} T={T} FF={FF} onViewAdvance={setActivePortal} />
              );
            })}
          </div>
        )}

        {/* HOLDS TAB */}
        {tab === "holds" && (
          <div>
            {loading && <div style={{ fontFamily: FF, fontSize: 12, color: T.muted }}>Loading...</div>}
            {!loading && holdLinks.length === 0 && (
              <div style={{ textAlign: "center", padding: "60px 0" }}>
                <div style={{ opacity: 0.25, color: T.text, marginBottom: 12 }}>
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                  </svg>
                </div>
                <div style={{ fontFamily: FF, fontSize: 14, fontWeight: 600, marginBottom: 8 }}>No active holds</div>
                <div style={{ fontFamily: FF, fontSize: 12, color: T.muted, lineHeight: 1.6 }}>
                  Place a hold on a request to reserve a date while you decide.<br/>
                  Holds are numbered so you can manage multiple acts for the same date.
                </div>
              </div>
            )}
            {holdLinks.length > 0 && (() => {
              const byDate = {};
              holdLinks.forEach(link => {
                const d = link.metadata?.show_date || "TBD";
                if (!byDate[d]) byDate[d] = [];
                byDate[d].push(link);
              });
              return Object.keys(byDate).sort().map(dateStr => {
                const dateLabel = dateStr === "TBD"
                  ? "Date TBD"
                  : new Date(dateStr + "T12:00:00").toLocaleDateString("en-US", { weekday: "short", month: "long", day: "numeric", year: "numeric" });
                return (
                  <div key={dateStr} style={{ marginBottom: 24 }}>
                    <div style={{ fontFamily: FF, fontSize: 11, color: T.muted, letterSpacing: 1, fontWeight: 700, marginBottom: 10 }}>{dateLabel.toUpperCase()}</div>
                    {byDate[dateStr].map(link => {
                      const m         = link.metadata || {};
                      const isExpired = link.hold_expires_at && new Date(link.hold_expires_at) < new Date();
                      return (
                        <div key={link.id} style={{ background: T.card, border: `1px solid ${isExpired ? T.red + "55" : T.border}`, borderRadius: 8, padding: "14px 18px", marginBottom: 8 }}>
                          <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
                            {/* Hold number badge */}
                            <div style={{
                              width: 46, height: 46, borderRadius: 8, flexShrink: 0,
                              background: T.blue + "18", border: `1.5px solid ${T.blue}44`,
                              display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                            }}>
                              <div style={{ fontFamily: FF, fontSize: 11, color: T.blue, letterSpacing: 1, fontWeight: 700 }}>HOLD</div>
                              <div style={{ fontFamily: FF, fontSize: 20, color: T.blue, fontWeight: 800, lineHeight: 1.1 }}>{link.hold_number}</div>
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontFamily: FF, fontSize: 14, fontWeight: 700, marginBottom: 2 }}>{m.artist_name || "Artist"}</div>
                              {m.tour_name && <div style={{ fontFamily: FF, fontSize: 11, color: T.muted, marginBottom: 2 }}>{m.tour_name}</div>}
                              {link.hold_expires_at ? (
                                <div style={{ fontFamily: FF, fontSize: 11, color: isExpired ? T.red : T.muted }}>
                                  {isExpired ? "Expired " : "Expires "}{new Date(link.hold_expires_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                                </div>
                              ) : (
                                <div style={{ fontFamily: FF, fontSize: 11, color: T.sub }}>No expiry set</div>
                              )}
                            </div>
                            <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                              <button onClick={() => convertHoldToConfirmed(link)}
                                style={{ fontFamily: FF, fontSize: 11, fontWeight: 700, padding: "7px 14px", background: T.btn, color: T.btnText, border: "none", borderRadius: 8, cursor: "pointer" }}>
                                Confirm
                              </button>
                              <button onClick={() => setHoldModal({ link, holdNumber: link.hold_number, expires: link.hold_expires_at ? link.hold_expires_at.slice(0, 10) : "" })}
                                style={{ fontFamily: FF, fontSize: 11, padding: "7px 10px", background: "none", color: T.muted, border: `1px solid ${T.border}`, borderRadius: 8, cursor: "pointer" }}>
                                Edit
                              </button>
                              <button onClick={() => releaseHold(link)}
                                style={{ fontFamily: FF, fontSize: 11, padding: "7px 10px", background: "none", color: T.red, border: `1px solid ${T.border}`, borderRadius: 8, cursor: "pointer" }}>
                                Release
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              });
            })()}
          </div>
        )}

        {/* REQUESTS TAB */}
        {tab === "requests" && (
          <div>
            {/* ORG-1: Enter a show code */}
            <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, padding: "16px 20px", marginBottom: 20 }}>
              <div style={{ fontFamily: FF, fontSize: 11, fontWeight: 700, letterSpacing: 1, color: T.muted, marginBottom: 8, textTransform: "uppercase" }}>Have a Show Code?</div>
              <div style={{ fontFamily: FF, fontSize: 12, color: T.muted, marginBottom: 12 }}>
                If a tour manager shared a 6-character show code with you, enter it here to request a link.
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input
                  value={codeInput}
                  onChange={e => setCodeInput(e.target.value.toUpperCase().slice(0, 6))}
                  onKeyDown={e => { if (e.key === "Enter") lookupAndRequestLink(); }}
                  placeholder="ABC123"
                  maxLength={6}
                  style={{ fontFamily: "monospace", fontSize: 16, letterSpacing: 4, fontWeight: 700,
                    width: 120, padding: "8px 14px", background: T.inp, border: `1px solid ${T.inpBorder}`,
                    borderRadius: 8, color: T.amber, outline: "none", textTransform: "uppercase" }}
                />
                <button
                  onClick={lookupAndRequestLink}
                  disabled={codeInput.length < 6 || codeLookup?.loading}
                  style={{ fontFamily: FF, fontSize: 12, fontWeight: 700, padding: "8px 18px",
                    background: codeInput.length === 6 ? T.btn : "transparent",
                    color: codeInput.length === 6 ? T.btnText : T.muted,
                    border: `1px solid ${codeInput.length === 6 ? T.btn : T.border}`,
                    borderRadius: 8, cursor: codeInput.length === 6 && !codeLookup?.loading ? "pointer" : "default" }}>
                  {codeLookup?.loading ? "Looking up…" : "Request Link"}
                </button>
              </div>
              {codeLookup?.error && (
                <div style={{ fontFamily: FF, fontSize: 11, color: T.red, marginTop: 8 }}>{codeLookup.error}</div>
              )}
              {codeLookup?.result && (
                <div style={{ fontFamily: FF, fontSize: 11, color: T.green, marginTop: 8 }}>
                  ✓ Link request sent for {codeLookup.result.artist_name || "the show"} on {codeLookup.result.show_date || "—"}. Tour manager will confirm shortly.
                </div>
              )}
            </div>

            {loading && <div style={{ fontFamily: FF, fontSize: 12, color: T.muted }}>Loading...</div>}
            {!loading && pendingRequests.length === 0 && (
              <div style={{ textAlign: "center", padding: "60px 0" }}>
                <div style={{ opacity: 0.25, color: T.text, marginBottom: 12 }}><svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg></div>
                <div style={{ fontFamily: FF, fontSize: 14, fontWeight: 600, marginBottom: 8 }}>No pending requests</div>
                <div style={{ fontFamily: FF, fontSize: 12, color: T.muted }}>
                  When a tour manager sends a link request for a show at your venue, it will appear here.
                </div>
              </div>
            )}
            {pendingRequests.map(link => {
              const m = link.metadata || {};
              const dateStr = m.show_date
                ? new Date(m.show_date + "T12:00:00").toLocaleDateString("en-US", { weekday: "short", month: "long", day: "numeric", year: "numeric" })
                : "Date TBD";
              return (
                <div key={link.id} style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, padding: "18px 20px", marginBottom: 10 }}>
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 16 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontFamily: FF, fontSize: 14, fontWeight: 700, marginBottom: 4 }}>{m.artist_name || "Artist"}</div>
                      <div style={{ fontFamily: FF, fontSize: 12, color: T.muted, marginBottom: 2 }}>{dateStr}</div>
                      {m.tour_name && <div style={{ fontFamily: FF, fontSize: 11, color: T.sub }}>{m.tour_name}</div>}
                      {link.message && <div style={{ fontFamily: FF, fontSize: 11, color: T.muted, marginTop: 8, padding: "6px 10px", background: T.page, borderRadius: 4, fontStyle: "italic" }}>"{link.message}"</div>}
                    </div>
                    <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                      <button
                        onClick={() => confirmLink(link)}
                        style={{ fontFamily: FF, fontSize: 11, fontWeight: 700, padding: "7px 16px", background: T.btn, color: T.btnText, border: "none", borderRadius: 8, cursor: "pointer" }}>
                        Confirm
                      </button>
                      <button
                        onClick={() => {
                          const dateStr   = m.show_date || "";
                          const takenNums = getHoldsOnDate(dateStr).map(l => l.hold_number);
                          const nextNum   = Array.from({ length: 15 }, (_, i) => i + 1).find(n => !takenNums.includes(n)) || (takenNums.length + 1);
                          setHoldModal({ link, holdNumber: nextNum, expires: "" });
                        }}
                        style={{ fontFamily: FF, fontSize: 11, fontWeight: 600, padding: "7px 14px", background: "none", color: T.blue, border: `1px solid ${T.blue}44`, borderRadius: 8, cursor: "pointer" }}>
                        Place Hold
                      </button>
                      <button
                        onClick={() => declineLink(link)}
                        style={{ fontFamily: FF, fontSize: 11, padding: "7px 14px", background: "none", color: T.red, border: `1px solid ${T.border}`, borderRadius: 8, cursor: "pointer" }}>
                        Decline
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* HISTORY TAB — past shows with settlement status */}
        {tab === "history" && (
          <div>
            {loading && <div style={{ fontFamily: FF, fontSize: 12, color: T.muted }}>Loading...</div>}
            {!loading && historyShows.length === 0 && (
              <div style={{ textAlign: "center", padding: "60px 0" }}>
                <div style={{ opacity: 0.25, color: T.text, marginBottom: 12 }}><svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="21 8 21 21 3 21 3 8"/><rect x="1" y="3" width="22" height="5"/><line x1="10" y1="12" x2="14" y2="12"/></svg></div>
                <div style={{ fontFamily: FF, fontSize: 14, fontWeight: 600, marginBottom: 8 }}>No past shows yet</div>
                <div style={{ fontFamily: FF, fontSize: 12, color: T.muted }}>
                  Completed shows will appear here with their settlement status.
                </div>
              </div>
            )}
            {historyShows.length > 0 && (
              <>
                {/* Settlement summary row */}
                {(() => {
                  const bothCount = historyShows.filter(l => l.tm_settlement_confirmed && l.venue_settlement_confirmed).length;
                  const partialCount = historyShows.filter(l => (l.tm_settlement_confirmed || l.venue_settlement_confirmed) && !(l.tm_settlement_confirmed && l.venue_settlement_confirmed)).length;
                  const noneCount = historyShows.filter(l => !l.tm_settlement_confirmed && !l.venue_settlement_confirmed).length;
                  return (
                    <div style={{ display: "flex", gap: 10, marginBottom: 20 }}>
                      <div style={{ flex: 1, padding: "12px 16px", background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, textAlign: "center" }}>
                        <div style={{ fontFamily: FF, fontSize: 20, fontWeight: 700, color: T.green }}>{bothCount}</div>
                        <div style={{ fontFamily: FF, fontSize: 11, color: T.muted, marginTop: 2, letterSpacing: 0.5 }}>FULLY SETTLED</div>
                      </div>
                      <div style={{ flex: 1, padding: "12px 16px", background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, textAlign: "center" }}>
                        <div style={{ fontFamily: FF, fontSize: 20, fontWeight: 700, color: T.amber }}>{partialCount}</div>
                        <div style={{ fontFamily: FF, fontSize: 11, color: T.muted, marginTop: 2, letterSpacing: 0.5 }}>PARTIAL</div>
                      </div>
                      <div style={{ flex: 1, padding: "12px 16px", background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, textAlign: "center" }}>
                        <div style={{ fontFamily: FF, fontSize: 20, fontWeight: 700, color: T.red }}>{noneCount}</div>
                        <div style={{ fontFamily: FF, fontSize: 11, color: T.muted, marginTop: 2, letterSpacing: 0.5 }}>OUTSTANDING</div>
                      </div>
                    </div>
                  );
                })()}
                {historyShows.map(link => (
                  <VenueShowCard key={link.id} link={link} T={T} FF={FF} onViewAdvance={setActivePortal} isPast />
                ))}
              </>
            )}
          </div>
        )}

        {/* PROFILE TAB */}
        {tab === "profile" && (
          <VenueProfileTab orgData={orgData} onUpdateOrg={onUpdateOrg} T={T} FF={FF} typeLabel={typeLabel} />
        )}
      </div>

      {/* HOLD MODAL */}
      {holdModal && (() => {
        const { link, holdNumber, expires } = holdModal;
        const m         = link.metadata || {};
        const dateStr   = m.show_date || "";
        const dateLabel = dateStr
          ? new Date(dateStr + "T12:00:00").toLocaleDateString("en-US", { weekday: "short", month: "long", day: "numeric" })
          : "Date TBD";
        // Which hold slots are already taken on this date (by other links, not this one)
        const takenByOthers = getHoldsOnDate(dateStr).filter(l => l.id !== link.id).map(l => l.hold_number);
        const slots = Array.from({ length: maxHoldSlot }, (_, i) => i + 1);
        const isEditing = link.status === "hold";
        return (
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 }}
            onClick={e => { if (e.target === e.currentTarget) setHoldModal(null); }}>
            <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 12, padding: "28px 28px 24px", width: 440, maxWidth: "90vw", fontFamily: FF }}>
              {/* Header */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>{isEditing ? "Edit Hold" : "Place Hold"}</div>
                <div style={{ fontSize: 14, color: T.text, fontWeight: 600 }}>{m.artist_name || "Artist"}</div>
                <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>{dateLabel}{m.tour_name ? ` · ${m.tour_name}` : ""}</div>
              </div>

              {/* Hold number selector */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 11, color: T.muted, letterSpacing: 1, fontWeight: 700, marginBottom: 10 }}>HOLD POSITION</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {slots.map(n => {
                    const isTaken    = takenByOthers.includes(n);
                    const isSelected = holdNumber === n;
                    return (
                      <button
                        key={n}
                        onClick={() => !isTaken && setHoldModal(prev => ({ ...prev, holdNumber: n }))}
                        style={{
                          width: 40, height: 40, borderRadius: 8, fontFamily: FF,
                          fontSize: 14, fontWeight: 700, cursor: isTaken ? "not-allowed" : "pointer",
                          background: isSelected ? T.blue : isTaken ? T.page : T.inp,
                          color: isSelected ? "#fff" : isTaken ? T.sub : T.text,
                          border: `1.5px solid ${isSelected ? T.blue : isTaken ? T.border : T.inpBorder}`,
                          opacity: isTaken ? 0.5 : 1,
                          position: "relative",
                          transition: "all 0.1s",
                        }}
                        title={isTaken ? "Slot taken" : `Hold ${n}`}
                      >{n}</button>
                    );
                  })}
                  {/* Expand slots button */}
                  <button
                    onClick={() => setMaxHoldSlot(prev => prev + 5)}
                    style={{
                      width: 40, height: 40, borderRadius: 8, fontFamily: FF,
                      fontSize: 20, cursor: "pointer", background: "none",
                      border: `1.5px dashed ${T.border}`, color: T.muted,
                    }}
                    title="Add more slots"
                  >+</button>
                </div>
                {takenByOthers.length > 0 && (
                  <div style={{ fontSize: 11, color: T.muted, marginTop: 8 }}>
                    Dimmed slots already held by another act on this date.
                  </div>
                )}
              </div>

              {/* Expiry date */}
              <div style={{ marginBottom: 24 }}>
                <div style={{ fontSize: 11, color: T.muted, letterSpacing: 1, fontWeight: 700, marginBottom: 8 }}>EXPIRY DATE <span style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>(optional)</span></div>
                <input
                  type="date"
                  value={expires || ""}
                  onChange={e => setHoldModal(prev => ({ ...prev, expires: e.target.value }))}
                  style={{
                    width: "100%", padding: "8px 10px", background: T.inp, border: `1px solid ${T.inpBorder}`,
                    borderRadius: 8, color: T.text, fontSize: 12, fontFamily: FF, boxSizing: "border-box", outline: "none",
                  }}
                />
              </div>

              {/* Actions */}
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <button onClick={() => setHoldModal(null)}
                  style={{ fontFamily: FF, fontSize: 12, padding: "8px 16px", background: "none", color: T.muted, border: `1px solid ${T.border}`, borderRadius: 8, cursor: "pointer" }}>
                  Cancel
                </button>
                <button onClick={placeHold} disabled={!holdNumber}
                  style={{ fontFamily: FF, fontSize: 12, fontWeight: 700, padding: "8px 20px", background: T.blue, color: "#fff", border: "none", borderRadius: 8, cursor: "pointer" }}>
                  {isEditing ? "Update Hold" : `Place Hold ${holdNumber}`}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// ── Auth Screen ─────────────────────────────────────────────────────────────
function AuthScreen() {
  const [mode,     setMode]     = useState("signin");
  const [email,    setEmail]    = useState("");
  const [password, setPassword] = useState("");
  const [error,    setError]    = useState("");
  const [loading,  setLoading]  = useState(false);
  const [message,  setMessage]  = useState("");
  // V-0: account type selector shown on sign-up
  const [accountType, setAccountType] = useState("tm"); // 'tm' | 'venue' | 'festival'

  const handle = async (e) => {
    e.preventDefault();
    setError(""); setMessage(""); setLoading(true);
    if (mode === "signin") {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) setError(error.message);
    } else if (mode === "signup") {
      // V-0: store org_type in user metadata so ensureOrg() can read it on first login
      const signUpOpts = accountType !== "tm"
        ? { options: { data: { org_type: accountType } } }
        : {};
      const { error } = await supabase.auth.signUp({ email, password, ...signUpOpts });
      if (error) setError(error.message);
      else setMessage("Check your email to confirm your account.");
    } else {
      const { error } = await supabase.auth.resetPasswordForEmail(email);
      if (error) setError(error.message);
      else setMessage("Password reset email sent.");
    }
    setLoading(false);
  };

  const isLight = localStorage.getItem("lightMode") !== "false";
  const T = isLight
    ? { page: "#efece6", card: "#fdfbf8", border: "#e4e0d9", inp: "#f4f1ec", inpBorder: "#ddd8d1", text: "#1a1917", muted: "#7a7672", sub: "#a39f99", btn: "#c47f00", btnText: "#fff", btnHover: "#b07200", accent: "#c47f00", ring: "rgba(196,127,0,0.13)", shadow: "0 14px 44px rgba(60,50,30,0.10)", danger: "#c0392b" }
    : { page: "#0d0d0d", card: "#161616", border: "#262626", inp: "#1c1c1c", inpBorder: "#2c2c2c", text: "#fff", muted: "#8f8f8f", sub: "#5f5f5f", btn: "var(--accent)", btnText: "#0d0d0d", btnHover: "#ffb31a", accent: "var(--accent)", ring: "rgba(240,165,0,0.16)", shadow: "0 18px 52px rgba(0,0,0,0.55)", danger: "#f87171" };
  const FF = "'Helvetica Neue', Helvetica, Arial, sans-serif";
  const subtitle = mode === "signin" ? "Sign in to your account" : mode === "signup" ? "Create your account" : "Reset your password";
  const inpStyle = { width: "100%", padding: "11px 13px", background: T.inp, border: `1px solid ${T.inpBorder}`, borderRadius: 8, color: T.text, fontSize: 14, boxSizing: "border-box", outline: "none", fontFamily: FF };
  return (
    <div style={{ minHeight: "100vh", background: T.page, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, fontFamily: FF }}>
      <style>{`
        .auth-inp { transition: border-color .15s ease, box-shadow .15s ease; }
        .auth-inp::placeholder { color: ${T.sub}; }
        .auth-inp:focus { border-color: ${T.accent}; box-shadow: 0 0 0 3px ${T.ring}; }
        .auth-btn { transition: background .15s ease, transform .05s ease; }
        .auth-btn:hover:not(:disabled) { background: ${T.btnHover}; }
        .auth-btn:active:not(:disabled) { transform: translateY(1px); }
        .auth-btn:disabled { opacity: .6; cursor: default; }
        .auth-link { transition: color .15s ease; }
        .auth-link:hover { color: ${T.text}; }
        .auth-pill { transition: all .12s ease; }
        .auth-pill:hover { border-color: ${T.accent} !important; }
      `}</style>
      <div style={{ width: 384, padding: "44px 40px", background: T.card, border: `1px solid ${T.border}`, borderRadius: 16, boxShadow: T.shadow, boxSizing: "border-box" }}>
        {/* Header — centered focal point */}
        <div style={{ textAlign: "center", marginBottom: 26 }}>
          <div style={{ fontFamily: "var(--font-display)", fontSize: 40, lineHeight: 1, letterSpacing: 4, color: T.btn }}>ADVANCD</div>
          <div style={{ fontSize: 11, color: T.sub, letterSpacing: 3, marginTop: 7, fontWeight: 600 }}>TOUR MANAGEMENT</div>
        </div>
        <div style={{ textAlign: "center", fontSize: 16, fontWeight: 600, color: T.text, marginBottom: 24 }}>{subtitle}</div>

        <form onSubmit={handle}>
          {/* V-0: Account type selector — only on sign-up */}
          {mode === "signup" && (
            <div style={{ marginBottom: 18 }}>
              <div style={{ fontSize: 11, color: T.muted, letterSpacing: 1, textAlign: "center", marginBottom: 10, textTransform: "uppercase", fontWeight: 600 }}>I'm signing up as</div>
              <div style={{ display: "flex", gap: 6 }}>
                {[
                  { value: "tm",       label: "Tour Manager" },
                  { value: "venue",    label: "Venue" },
                  { value: "festival", label: "Festival" },
                ].map(({ value, label }) => (
                  <button
                    key={value} type="button" className="auth-pill"
                    onClick={() => setAccountType(value)}
                    style={{
                      flex: 1, padding: "8px 4px", fontSize: 11, fontWeight: 600, borderRadius: 8, cursor: "pointer",
                      border: `1.5px solid ${accountType === value ? T.btn : T.inpBorder}`,
                      background: accountType === value ? T.btn : T.inp,
                      color: accountType === value ? T.btnText : T.muted,
                    }}
                  >{label}</button>
                ))}
              </div>
            </div>
          )}
          <input
            className="auth-inp" type="email" placeholder="Email" value={email} onChange={e => setEmail(e.target.value)} required autoFocus
            autoComplete="email" spellCheck={false}
            style={{ ...inpStyle, marginBottom: 10 }}
          />
          {mode !== "reset" && (
            <input
              className="auth-inp" type="password" placeholder="Password" value={password} onChange={e => setPassword(e.target.value)} required
              autoComplete={mode === "signup" ? "new-password" : "current-password"}
              style={{ ...inpStyle, marginBottom: 16 }}
            />
          )}
          {error   && <div style={{ textAlign: "center", color: T.danger, fontSize: 12, marginBottom: 12 }}>{error}</div>}
          {message && <div style={{ textAlign: "center", color: "var(--success)", fontSize: 12, marginBottom: 12 }}>{message}</div>}
          <button className="auth-btn" type="submit" disabled={loading}
            style={{ width: "100%", padding: "12px", background: T.btn, color: T.btnText, border: "none", borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: FF, letterSpacing: 1.5 }}>
            {loading
              ? (mode === "signin" ? "SIGNING IN…" : mode === "signup" ? "CREATING…" : "SENDING…")
              : mode === "signin" ? "SIGN IN" : mode === "signup" ? "CREATE ACCOUNT" : "SEND RESET EMAIL"}
          </button>
        </form>
        <div style={{ marginTop: 22, fontSize: 12, color: T.muted, display: "flex", flexDirection: "column", gap: 11, textAlign: "center" }}>
          {mode === "signin" && <>
            <span className="auth-link" style={{ cursor: "pointer" }} onClick={() => { setMode("signup"); setError(""); setMessage(""); }}>Don't have an account? <span style={{ color: T.btn, fontWeight: 600 }}>Sign up</span></span>
            <span className="auth-link" style={{ cursor: "pointer", color: T.sub }} onClick={() => { setMode("reset"); setError(""); setMessage(""); }}>Forgot password?</span>
          </>}
          {mode !== "signin" && <span className="auth-link" style={{ cursor: "pointer" }} onClick={() => { setMode("signin"); setError(""); setMessage(""); }}>Back to sign in</span>}
        </div>
        <div style={{ marginTop: 18, paddingTop: 14, borderTop: `1px solid ${T.inpBorder}`, textAlign: "center", fontSize: 11, color: T.sub }}>
          <a href="/privacy" className="auth-link" style={{ color: T.sub, textDecoration: "none" }}>Privacy</a>
          <span style={{ margin: "0 8px" }}>&middot;</span>
          <a href="/terms" className="auth-link" style={{ color: T.sub, textDecoration: "none" }}>Terms</a>
        </div>
      </div>
    </div>
  );
}

// ── Claim Profile Screen ────────────────────────────────────────────────────
// Shown when a user arrives via /claim/<token> but isn't logged in yet.
// After they auth, the parent App useEffect handles the actual claiming.
function ClaimScreen({ token }) {
  const [mode,     setMode]     = useState("signup");
  const [email,    setEmail]    = useState("");
  const [password, setPassword] = useState("");
  const [error,    setError]    = useState("");
  const [loading,  setLoading]  = useState(false);
  const [message,  setMessage]  = useState("");

  const handle = async (e) => {
    e.preventDefault();
    setError(""); setMessage(""); setLoading(true);
    if (mode === "signup") {
      const { error } = await supabase.auth.signUp({ email, password });
      if (error) setError(error.message);
      else setMessage("Check your email to confirm your account, then return to this link.");
    } else {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) setError(error.message);
    }
    setLoading(false);
  };

  const isLight = localStorage.getItem("lightMode") !== "false";
  const T = isLight
    ? { page: "#efece6", card: "#fdfbf8", border: "#e4e0d9", inp: "#f4f1ec", inpBorder: "#ddd8d1", text: "#1a1917", muted: "#7a7672", sub: "#a39f99", btn: "#c47f00", btnText: "#fff", accent: "#c47f00", ring: "rgba(196,127,0,0.13)", shadow: "0 14px 44px rgba(60,50,30,0.10)", danger: "#c0392b" }
    : { page: "#0d0d0d", card: "#161616", border: "#262626", inp: "#1c1c1c", inpBorder: "#2c2c2c", text: "#fff", muted: "#8f8f8f", sub: "#5f5f5f", btn: "var(--accent)", btnText: "#0d0d0d", accent: "var(--accent)", ring: "rgba(240,165,0,0.16)", shadow: "0 18px 52px rgba(0,0,0,0.55)", danger: "#f87171" };
  const FF = "'Helvetica Neue', Helvetica, Arial, sans-serif";
  const inpStyle = { width: "100%", padding: "11px 13px", background: T.inp, border: `1px solid ${T.inpBorder}`, borderRadius: 8, color: T.text, fontSize: 14, boxSizing: "border-box", outline: "none", fontFamily: FF };
  return (
    <div style={{ minHeight: "100vh", background: T.page, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, fontFamily: FF }}>
      <style>{`
        .auth-inp { transition: border-color .15s ease, box-shadow .15s ease; }
        .auth-inp::placeholder { color: ${T.sub}; }
        .auth-inp:focus { border-color: ${T.accent}; box-shadow: 0 0 0 3px ${T.ring}; }
        .auth-btn { transition: background .15s ease, transform .05s ease; }
        .auth-btn:active:not(:disabled) { transform: translateY(1px); }
        .auth-btn:disabled { opacity: .6; cursor: default; }
      `}</style>
      <div style={{ width: 384, padding: "44px 40px", background: T.card, border: `1px solid ${T.border}`, borderRadius: 16, boxShadow: T.shadow, boxSizing: "border-box" }}>
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <div style={{ fontFamily: "var(--font-display)", fontSize: 40, lineHeight: 1, letterSpacing: 4, color: T.btn }}>ADVANCD</div>
          <div style={{ fontSize: 11, color: T.sub, letterSpacing: 3, marginTop: 7, fontWeight: 600 }}>TOUR MANAGEMENT</div>
        </div>

        {/* Invite context banner */}
        <div style={{ background: isLight ? "#fff8e8" : "#1a1200", border: `1px solid ${isLight ? "#f0d080" : "#3a2800"}`, borderRadius: 8, padding: "12px 14px", marginBottom: 24 }}>
          <div style={{ fontFamily: FF, fontSize: 11, fontWeight: 600, color: T.accent, marginBottom: 4 }}>You've been invited</div>
          <div style={{ fontFamily: FF, fontSize: 11, color: T.muted, lineHeight: 1.6 }}>
            Your tour manager has created a profile for you. Sign up or sign in to claim it — once claimed, you control your own network visibility and travel info.
          </div>
        </div>

        <div style={{ textAlign: "center", fontSize: 14, color: T.muted, marginBottom: 20 }}>
          {mode === "signup" ? "Create your account to claim this profile" : "Sign in to claim this profile"}
        </div>
        <form onSubmit={handle}>
          <input
            className="auth-inp" type="email" placeholder="Email" value={email} onChange={e => setEmail(e.target.value)} required autoFocus
            autoComplete="email" spellCheck={false}
            style={{ ...inpStyle, marginBottom: 10 }}
          />
          <input
            className="auth-inp" type="password" placeholder="Password" value={password} onChange={e => setPassword(e.target.value)} required
            autoComplete={mode === "signup" ? "new-password" : "current-password"}
            style={{ ...inpStyle, marginBottom: 16 }}
          />
          {error   && <div style={{ textAlign: "center", color: T.danger, fontSize: 12, marginBottom: 12 }}>{error}</div>}
          {message && <div style={{ textAlign: "center", color: "var(--success)", fontSize: 12, marginBottom: 12 }}>{message}</div>}
          <button className="auth-btn" type="submit" disabled={loading}
            style={{ width: "100%", padding: "12px", background: T.btn, color: T.btnText, border: "none", borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: FF, letterSpacing: 1.5 }}>
            {loading
              ? (mode === "signup" ? "CREATING…" : "SIGNING IN…")
              : mode === "signup" ? "CREATE ACCOUNT & CLAIM" : "SIGN IN & CLAIM"}
          </button>
        </form>
        <div style={{ marginTop: 18, fontSize: 12, color: T.muted, textAlign: "center" }}>
          {mode === "signup"
            ? <span style={{ cursor: "pointer" }} onClick={() => { setMode("signin"); setError(""); setMessage(""); }}>Already have an account? <span style={{ color: T.btn, fontWeight: 600 }}>Sign in</span></span>
            : <span style={{ cursor: "pointer" }} onClick={() => { setMode("signup"); setError(""); setMessage(""); }}>New to ADVANCD? <span style={{ color: T.btn, fontWeight: 600 }}>Sign up</span></span>
          }
        </div>
      </div>
    </div>
  );
}

// ── Claim Status Screen ─────────────────────────────────────────────────────
function ClaimStatusScreen({ claimStatus, lightMode, onContinue, onDismiss }) {
  const FF = "'Helvetica Neue', Helvetica, Arial, sans-serif";
  const T = lightMode
    ? { page: "#f0ede8", card: "#e8e4de", border: "#d0ccc6", text: "#1a1917", muted: "#8a8784", accent: "#c47f00", btn: "#c47f00", btnTxt: "#fff", green: "#166534", greenBg: "#f0fdf4", greenBdr: "#bbf7d0" }
    : { page: "#0d0d0d", card: "#141414", border: "var(--panel-border)",    text: "#fff",    muted: "#555",    accent: "var(--accent)", btn: "var(--accent)", btnTxt: "#0d0d0d", green: "var(--success)", greenBg: "#0a1f0e", greenBdr: "#1a4a1a" };
  return (
    <div style={{ minHeight: "100vh", background: T.page, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: FF }}>
      <div style={{ width: 380, padding: "40px 36px", background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, textAlign: "center" }}>
        <div style={{ fontFamily: "var(--font-display)", fontSize: 28, letterSpacing: 4, color: T.accent, marginBottom: 32 }}>ADVANCD</div>
        {claimStatus === "claiming" && (
          <>
            <style>{`@keyframes claimspin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
            <div style={{ marginBottom: 16, display: "flex", justifyContent: "center", color: T.accent }}>
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ animation: "claimspin 1s linear infinite" }}><path d="M21 12a9 9 0 1 1-2.64-6.36"/></svg>
            </div>
            <div style={{ fontFamily: FF, fontSize: 14, color: T.text, marginBottom: 8 }}>Claiming your profile…</div>
            <div style={{ fontFamily: FF, fontSize: 12, color: T.muted }}>Just a moment.</div>
          </>
        )}
        {claimStatus === "success" && (
          <>
            <div style={{ background: T.greenBg, border: `1px solid ${T.greenBdr}`, borderRadius: 8, padding: "20px 24px", marginBottom: 24 }}>
              <div style={{ marginBottom: 10, display: "flex", justifyContent: "center", color: T.green }}><svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg></div>
              <div style={{ fontFamily: FF, fontSize: 16, fontWeight: 600, color: T.green, marginBottom: 6 }}>Profile Claimed!</div>
              <div style={{ fontFamily: FF, fontSize: 12, color: T.muted, lineHeight: 1.6 }}>
                Your profile is now linked to your account. You can set your network visibility and fill in your travel info.
              </div>
            </div>
            <button style={{ width: "100%", padding: "11px", background: T.btn, color: T.btnTxt, border: "none", borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: FF, letterSpacing: 1 }} onClick={onContinue}>
              GO TO MY PROFILE →
            </button>
          </>
        )}
        {claimStatus === "already_claimed" && (
          <>
            <div style={{ fontFamily: FF, fontSize: 14, color: T.text, marginBottom: 8 }}>This profile has already been claimed.</div>
            <div style={{ fontFamily: FF, fontSize: 12, color: T.muted, marginBottom: 24, lineHeight: 1.6 }}>If that was you, you're all set. If not, contact your tour manager.</div>
            <button style={{ width: "100%", padding: "11px", background: T.btn, color: T.btnTxt, border: "none", borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: FF, letterSpacing: 1 }} onClick={onDismiss}>
              GO TO APP
            </button>
          </>
        )}
        {claimStatus === "error" && (
          <>
            <div style={{ fontFamily: FF, fontSize: 14, color: lightMode ? "#c0392b" : "#f87171", marginBottom: 8 }}>Something went wrong.</div>
            <div style={{ fontFamily: FF, fontSize: 12, color: T.muted, marginBottom: 24, lineHeight: 1.6 }}>This invite link may be invalid or expired. Ask your tour manager to send a new one.</div>
            <button style={{ width: "100%", padding: "11px", background: T.btn, color: T.btnTxt, border: "none", borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: FF, letterSpacing: 1 }} onClick={onDismiss}>
              GO TO APP
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ── Invite Accept Screen ─────────────────────────────────────────────────────
// Shown when a user arrives via /invite/<token>.
// Works pre-auth: shows invite context. After auth, the App useEffect handles acceptance.
function InviteAcceptScreen({ inviteInfo, onAccepted }) {
  const [mode,     setMode]     = useState("signup");
  const [email,    setEmail]    = useState(inviteInfo?.email || "");
  const [password, setPassword] = useState("");
  const [error,    setError]    = useState("");
  const [loading,  setLoading]  = useState(false);
  const [message,  setMessage]  = useState("");

  const isLight = (() => { try { return localStorage.getItem("lightMode") !== "false"; } catch { return true; } })();
  const T = isLight
    ? { page: "#f0ede8", card: "#e8e4de", border: "#d0ccc6", inp: "#e0dbd4", inpBorder: "#ccc8c2", text: "#1a1917", muted: "#8a8784", sub: "#9a9794", btn: "#c47f00", btnText: "#fff", accent: "#c47f00" }
    : { page: "#0d0d0d", card: "#141414", border: "var(--panel-border)",    inp: "#1a1a1a", inpBorder: "#2a2a2a", text: "#fff",    muted: "#555",    sub: "#444",    btn: "var(--accent)", btnText: "#0d0d0d", accent: "var(--accent)" };
  const FF = "'Helvetica Neue', Helvetica, Arial, sans-serif";

  const roleLabel = inviteInfo?.role === "band" ? "Band Member" : "Crew Member";

  const handle = async (e) => {
    e.preventDefault();
    setError(""); setMessage(""); setLoading(true);
    if (mode === "signup") {
      const { error } = await supabase.auth.signUp({ email, password });
      if (error) setError(error.message);
      else setMessage("Check your email to confirm your account. Once confirmed, this invite will be waiting for you.");
    } else {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) setError(error.message);
      // On success, onAuthStateChange fires → App detects inviteToken → calls accept_tour_invite
    }
    setLoading(false);
  };

  return (
    <div style={{ minHeight: "100vh", background: T.page, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: FF, padding: "24px 16px", boxSizing: "border-box" }}>
      <div style={{ width: "100%", maxWidth: 400, padding: "40px 36px", background: T.card, border: `1px solid ${T.border}`, borderRadius: 8 }}>

        {/* Wordmark */}
        <div style={{ fontFamily: "var(--font-display)", fontSize: 28, letterSpacing: 4, color: T.btn, marginBottom: 4, fontWeight: 700 }}>ADVANCD</div>
        <div style={{ fontSize: 11, color: T.sub, letterSpacing: 1.5, marginBottom: 28 }}>TOUR MANAGEMENT</div>

        {/* Invite context */}
        {inviteInfo ? (
          <div style={{ background: isLight ? "#fff8e8" : "#1a1200", border: `1px solid ${isLight ? "#f0d080" : "#3a2800"}`, borderRadius: 8, padding: "14px 16px", marginBottom: 24 }}>
            <div style={{ fontSize: 11, letterSpacing: "2px", textTransform: "uppercase", color: T.accent, marginBottom: 8 }}>You're Invited</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: T.text, marginBottom: 4, lineHeight: 1.2 }}>{inviteInfo.tour_name}</div>
            <div style={{ fontSize: 12, color: T.muted, marginBottom: 2 }}>{inviteInfo.artist_name}</div>
            <div style={{ fontSize: 11, color: T.accent, marginTop: 6, fontWeight: 600 }}>{roleLabel}</div>
          </div>
        ) : (
          <div style={{ background: isLight ? "#f5f0ea" : "#1a1a1a", border: `1px solid ${T.border}`, borderRadius: 8, padding: "12px 14px", marginBottom: 24 }}>
            <div style={{ fontSize: 12, color: T.muted }}>Loading invite details…</div>
          </div>
        )}

        <div style={{ fontSize: 14, color: T.muted, marginBottom: 20 }}>
          {mode === "signup" ? "Create an account to accept this invitation" : "Sign in to accept this invitation"}
        </div>

        <form onSubmit={handle}>
          <input
            type="email" placeholder="Email address" value={email} onChange={e => setEmail(e.target.value)} required
            style={{ width: "100%", padding: "10px 12px", background: T.inp, border: `1px solid ${T.inpBorder}`, borderRadius: 8, color: T.text, fontSize: 14, marginBottom: 10, boxSizing: "border-box", outline: "none" }}
          />
          <input
            type="password" placeholder="Password" value={password} onChange={e => setPassword(e.target.value)} required
            style={{ width: "100%", padding: "10px 12px", background: T.inp, border: `1px solid ${T.inpBorder}`, borderRadius: 8, color: T.text, fontSize: 14, marginBottom: 16, boxSizing: "border-box", outline: "none" }}
          />
          {error   && <div style={{ color: "#f87171", fontSize: 12, marginBottom: 12 }}>{error}</div>}
          {message && <div style={{ color: "var(--success)", fontSize: 12, marginBottom: 12, lineHeight: 1.5 }}>{message}</div>}
          <button type="submit" disabled={loading || !!message}
            style={{ width: "100%", padding: "11px", background: T.btn, color: T.btnText, border: "none", borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: FF, letterSpacing: 1, opacity: message ? 0.5 : 1 }}>
            {loading ? "…" : mode === "signup" ? "CREATE ACCOUNT" : "SIGN IN"}
          </button>
        </form>

        <div style={{ marginTop: 16, fontSize: 12, color: T.muted, textAlign: "center" }}>
          {mode === "signup"
            ? <span style={{ cursor: "pointer", textDecoration: "underline" }} onClick={() => { setMode("signin"); setError(""); setMessage(""); }}>Already have an account? Sign in</span>
            : <span style={{ cursor: "pointer", textDecoration: "underline" }} onClick={() => { setMode("signup"); setError(""); setMessage(""); }}>New to ADVANCD? Create account</span>
          }
        </div>
      </div>
    </div>
  );
}

// ── Normalized DB helpers ──────────────────────────────────────────────────

// Map a DB venue row (snake_case) → app venue object (camelCase)
function dbVenueToApp(v) {
  return {
    id: v.id, name: v.name,
    addressStreet:   v.address_street   || "",
    city:            v.city             || "",
    addressState:    v.address_state    || "",
    addressZip:      v.address_zip      || "",
    addressCountry:  v.address_country  || "",
    capacity:        v.capacity         || "",
    ages:            v.ages             || "",
    gaSeated:        v.ga_seated        || "",
    website:         v.website          || "",
    promoterName:    v.promoter_name    || "",
    promoterEmail:   v.promoter_email   || "",
    promoterCompany: v.promoter_company || "",
    promoterPhone:   v.promoter_phone   || "",
    loadIn:          v.load_in          || "",
    parking:         v.parking          || "",
    houseGear:       v.house_gear       || "",
    greenRooms:      v.green_rooms      || "",
    bathrooms:       v.bathrooms        || "",
    merchSplit:      v.merch_split      || "",
    notes:           v.notes            || "",
    kind:            v.kind             || "venue",
    linkedContacts:  v.linked_contacts  || [],
  };
}

// Map an app venue object → DB row (snake_case)
function appVenueToDB(userId, v) {
  return {
    id: v.id, user_id: userId, name: v.name || "",
    address_street:   v.addressStreet   || "",
    city:             v.city            || "",
    address_state:    v.addressState    || "",
    address_zip:      v.addressZip      || "",
    address_country:  v.addressCountry  || "",
    capacity:         v.capacity ? String(v.capacity) : "",
    ages:             v.ages            || "",
    ga_seated:        v.gaSeated        || "",
    website:          v.website         || "",
    promoter_name:    v.promoterName    || "",
    promoter_email:   v.promoterEmail   || "",
    promoter_company: v.promoterCompany || "",
    promoter_phone:   v.promoterPhone   || "",
    load_in:          v.loadIn          || "",
    parking:          v.parking         || "",
    house_gear:       v.houseGear       || "",
    green_rooms:      v.greenRooms      || "",
    bathrooms:        v.bathrooms       || "",
    merch_split:      v.merchSplit      || "",
    notes:            v.notes           || "",
    kind:             v.kind            || "venue",
    linked_contacts:  v.linkedContacts  || [],
  };
}

// Load all user data from normalized tables and assemble into app state shape
async function loadNormalized(userId, teamId) {
  // Query by team_id OR user_id — catches both migrated records (team_id set)
  // and any records that didn't get migrated yet (team_id still null)
  const byTeam = q => teamId
    ? q.or(`team_id.eq.${teamId},user_id.eq.${userId}`)
    : q.eq("user_id", userId);
  const [profileRes, artistRes, venueRes, tourRes, showRes, contactRes] = await Promise.all([
    supabase.from("profiles").select("*").eq("user_id", userId).maybeSingle(),
    byTeam(supabase.from("artists").select("*")),
    byTeam(supabase.from("venues").select("*")).limit(5000),
    byTeam(supabase.from("tours").select("*")),
    byTeam(supabase.from("shows").select("*")),
    byTeam(supabase.from("contacts").select("*")),
  ]);

  // Log any query failures — non-fatal, fall back to empty
  const resNames = ["profiles", "artists", "venues", "tours", "shows", "contacts"];
  [profileRes, artistRes, venueRes, tourRes, showRes, contactRes].forEach((res, i) => {
    if (res.error) console.error(`[ADVANCD] loadNormalized: ${resNames[i]} query failed:`, res.error);
  });

  const profile     = profileRes.data  || {};
  const artistRows  = artistRes.data   || [];
  const venueRows   = venueRes.data    || [];
  const tourRows    = tourRes.data     || [];
  const showRows    = showRes.data     || [];
  const contactRows = (contactRes.data || []).filter(c => (c.email || "").trim() || (c.phone || "").trim());

  // Group shows by tour
  const showsByTour = {};
  showRows.forEach(s => {
    if (!showsByTour[s.tour_id]) showsByTour[s.tour_id] = [];
    showsByTour[s.tour_id].push({
      id: s.id, venueId: s.venue_id, date: s.date,
      status: s.status || "unstarted", notes: s.notes || "",
      flagged: s.flagged || false,
      checklist: s.checklist || {}, settlement: s.settlement || {},
      advanceToken:          s.advance_token             || null,
      linkCode:              s.link_code                 || null,
      advanceData:           s.advance_data              || {},   // promoter layer
      advanceTmData:         s.advance_tm_data           || {},   // TM suggestions layer
      advanceSectionStatus:  s.advance_section_status    || {},   // AP-1: per-section status
      advanceLastViewedAt:   s.advance_last_viewed_at    || null,
      advanceFinalized:      s.advance_finalized         || false,
      advanceFinalizedAt:    s.advance_finalized_at      || null,
      lodging:               s.lodging                   || {},
      linkedContacts:        s.linked_contacts           || [],
      setlists:              [],                                  // SL-1: hydrated separately via loadShowSetlists
    });
    showsByTour[s.tour_id].sort((a, b) => a.date.localeCompare(b.date));
  });

  // Group tours by artist
  const toursByArtist = {};
  tourRows.forEach(t => {
    if (!toursByArtist[t.artist_id]) toursByArtist[t.artist_id] = [];
    toursByArtist[t.artist_id].push({ id: t.id, name: t.name, year: t.year || "", advanceConfig: t.advance_config || {}, shows: showsByTour[t.id] || [] });
  });

  const artists = artistRows.map(a => ({
    id: a.id, name: a.name,
    mgmt:     a.mgmt     || {}, booking: a.booking || {},
    label:    a.label    || {}, members: a.members  || [],
    website:  a.website  || "", socials: a.socials  || {},
    rider:          a.rider           || "",
    checklistCats:  a.checklist_cats  || null,
    documents:      Array.isArray(a.documents) ? a.documents : [],
    homeCity:       a.home_city  || "",
    homeState:      a.home_state || "",
    colorIdx:       a.color_idx  ?? null,
    tours: toursByArtist[a.id] || [],
  }));

  const venues = {};
  venueRows.forEach(v => { venues[v.id] = dbVenueToApp(v); });

  const contacts = contactRows.map(c => ({
    id: c.id, name: c.name, role: c.role || "",
    email: c.email || "", phone: c.phone || "",
    company: c.company || "", notes: c.notes || "",
  }));

  return {
    data: {
      artists,
      profile: { name: profile.name || "", agency: profile.agency || "", phone: profile.phone || "", website: profile.website || "" },
      contacts,
    },
    venues,
    hasData: artistRows.length > 0 || venueRows.length > 0 || contactRows.length > 0,
  };
}

// ── Load data for invited band/crew members ───────────────────────────────────
// Loads only the specific tour the member was invited to.
// Returns the same shape as loadNormalized so the rest of the app works unchanged.
async function loadMemberData(tourId, artistId, userId) {
  const [tourRes, artistRes, showRes, daysRes, ownProfileRes, networkRes, connRes] = await Promise.all([
    supabase.from("tours").select("*").eq("id", tourId).single(),
    supabase.from("artists").select("*").eq("id", artistId).single(),
    supabase.from("shows").select("*").eq("tour_id", tourId),
    supabase.from("tour_days").select("*").eq("tour_id", tourId).order("date", { ascending: true }),
    // Own people_profile (if they've created one)
    userId ? supabase.from("people_profiles").select("*").eq("user_id", userId).maybeSingle() : Promise.resolve({ data: null }),
    // All visible_to_network profiles
    supabase.from("people_profiles").select("*, teams(name)").eq("visible_to_network", true).order("name"),
    // Member's own connections (keyed by user_id)
    userId ? supabase.from("network_connections").select("profile_id").eq("user_id", userId) : Promise.resolve({ data: [] }),
  ]);

  // PP-1 Phase 3: load member's own tour permissions (if they have a profile)
  let membershipRow = null;
  if (ownProfileRes.data?.id) {
    const { data: mem } = await supabase.from("tour_memberships")
      .select("*")
      .eq("profile_id", ownProfileRes.data.id)
      .eq("tour_id", tourId)
      .maybeSingle();
    membershipRow = mem;
  }

  const tourRow   = tourRes.data;
  const artistRow = artistRes.data;
  const showRows  = showRes.data || [];

  // Load venues for the shows in this tour
  const venueIds = [...new Set(showRows.map(s => s.venue_id).filter(Boolean))];
  const venueRows = venueIds.length
    ? (await supabase.from("venues").select("*").in("id", venueIds)).data || []
    : [];

  // SL-1: load setlists for this tour (band members read; RLS gates non-band members out)
  const setlistRes = await supabase.from("show_setlists").select("*").eq("tour_id", tourId);
  const setlistsByShow = {};
  (setlistRes.data || []).forEach(sl => {
    if (!setlistsByShow[sl.show_id]) setlistsByShow[sl.show_id] = [];
    setlistsByShow[sl.show_id].push(sl);
  });
  Object.values(setlistsByShow).forEach(list => list.sort((a, b) => (a.set_number || 0) - (b.set_number || 0)));

  const shows = showRows.map(s => ({
    id: s.id, venueId: s.venue_id, date: s.date,
    status: s.status || "unstarted", notes: s.notes || "",
    flagged: s.flagged || false,
    checklist: s.checklist || {}, settlement: {},  // settlement hidden for members
    advanceToken:          s.advance_token          || null,
    linkCode:              null,  // link codes not exposed to members
    advanceData:           {},   // hidden for members
    advanceTmData:         s.advance_tm_data        || {},
    advanceSectionStatus:  {},   // hidden for members
    advanceLastViewedAt:   null,
    advanceFinalized:      false,
    advanceFinalizedAt:    null,
    lodging:               s.lodging                || {},
    linkedContacts:        s.linked_contacts        || [],
    setlists:              setlistsByShow[s.id]     || [],   // SL-1: band members get this; RLS empties it for non-band
  })).sort((a, b) => a.date.localeCompare(b.date));

  const tour = tourRow
    ? { id: tourRow.id, name: tourRow.name, year: tourRow.year || "", advanceConfig: tourRow.advance_config || {}, shows }
    : null;

  const artist = artistRow
    ? { id: artistRow.id, name: artistRow.name, mgmt: {}, booking: {}, label: {}, members: [], website: "", socials: {}, rider: "", checklistCats: null, tours: tour ? [tour] : [] }
    : null;

  const venues = {};
  venueRows.forEach(v => { venues[v.id] = dbVenueToApp(v); });

  // Network data for "who's in town"
  const ownProfile      = ownProfileRes.data || null;
  const networkProfiles = (networkRes.data || []).filter(p => p.user_id !== userId);
  const connections     = new Set((connRes.data || []).map(c => c.profile_id));

  return {
    data:     { artists: artist ? [artist] : [], profile: {}, contacts: [] },
    venues,
    tourDays: daysRes.data || [],
    artistId: artistRow?.id,
    tourId:   tourRow?.id,
    ownProfile,
    networkProfiles,
    connections,
    membership: membershipRow, // PP-1 Phase 3: tour role + permissions
  };
}

// Save all user data to normalized tables
// Uses upsert for current records + deletes for removed records
const _saving = { current: false, pendingArgs: null };
async function saveNormalized(userId, teamId, data, venues) {
  if (_saving.current) {
    // Don't silently drop the save — store the latest args so we can
    // flush them once the in-flight save completes
    _saving.pendingArgs = { userId, teamId, data, venues };
    return null;
  }
  _saving.current = true;
  try {
    const artists  = data.artists  || [];
    const contacts = data.contacts || [];
    const profile  = data.profile  || {};
    const tid      = teamId || null;
    // Use team_id for scoping deletes when available, else fall back to user_id
    const scopeQ = q => tid ? q.eq("team_id", tid) : q.eq("user_id", userId);

    // Profile
    await supabase.from("profiles").upsert(
      { user_id: userId, name: profile.name || "", agency: profile.agency || "", phone: profile.phone || "", website: profile.website || "" },
      { onConflict: "user_id" }
    );

    // Artists — upsert, then delete removed
    if (artists.length > 0) {
      await supabase.from("artists").upsert(
        artists.map(a => ({
          id: a.id, user_id: userId, team_id: tid, name: a.name,
          mgmt: a.mgmt || {}, booking: a.booking || {}, label: a.label || {},
          members: a.members || [], website: a.website || "", socials: a.socials || {},
          rider: a.rider || "",
          checklist_cats: a.checklistCats || null,
          documents: a.documents || [],
          home_city:  a.homeCity  || "",
          home_state: a.homeState || "",
          color_idx:  a.colorIdx  ?? null,
        })),
        { onConflict: "id" }
      );
    }
    const artistIds = artists.map(a => a.id);

    // Venues (independent of artists, run in parallel with artists upsert phase)
    const venueRows = Object.values(venues);
    if (venueRows.length > 0) {
      await supabase.from("venues").upsert(venueRows.map(v => ({ ...appVenueToDB(userId, v), team_id: tid })), { onConflict: "id" });
    }
    const venueIds = Object.keys(venues);

    // Contacts (independent, can upsert now)
    if (contacts.length > 0) {
      await supabase.from("contacts").upsert(
        contacts.map(c => ({ id: c.id, user_id: userId, team_id: tid, name: c.name, role: c.role || "", email: c.email || "", phone: c.phone || "", company: c.company || "", notes: c.notes || "" })),
        { onConflict: "id" }
      );
    }
    const contactIds = contacts.map(c => c.id);

    // Tours (depend on artists being upserted above)
    const allTours = artists.flatMap(a =>
      (a.tours || []).map(t => ({ id: t.id, artist_id: a.id, user_id: userId, team_id: tid, name: t.name, year: t.year || "", advance_config: t.advanceConfig || {} }))
    );
    if (allTours.length > 0) await supabase.from("tours").upsert(allTours, { onConflict: "id" });
    const tourIds = allTours.map(t => t.id);

    // Shows (depend on tours being upserted above)
    const allShows = artists.flatMap(a =>
      (a.tours || []).flatMap(t =>
        (t.shows || []).map(s => ({
          id: s.id, tour_id: t.id, artist_id: a.id, user_id: userId, team_id: tid,
          venue_id: s.venueId || null, date: s.date,
          status: s.status || "unstarted", notes: s.notes || "",
          flagged: s.flagged || false,
          checklist: s.checklist || {}, settlement: s.settlement || {},
          advance_token:    s.advanceToken    || null,
          // advance_data and advance_tm_data are NOT written here —
          // they have their own dedicated save path to avoid racing with resetAdvance.
          lodging:          s.lodging        || {},
          linked_contacts:  s.linkedContacts || [],
        }))
      )
    );
    if (allShows.length > 0) {
      const { error: showsErr } = await supabase.from("shows").upsert(allShows, { onConflict: "id" });
      if (showsErr) {
        console.error("[ADVANCD] saveNormalized: shows upsert failed:", showsErr);
        throw showsErr;
      }
    }

    // NOTE: Deletes are NOT handled here — they're handled immediately in each
    // delete action handler (saveShow/deleteTour/saveEditArtist/saveContact/saveVenue).
    // A delete pass here causes a race condition: if this save runs with a stale
    // data snapshot while a newer record was just written directly to DB, it would
    // incorrectly delete that record.

    return true;
  } catch (err) {
    console.error("[ADVANCD] saveNormalized failed:", err);
    return false;
  } finally {
    _saving.current = false;
    // Flush any save that was queued while this one was in-flight
    if (_saving.pendingArgs) {
      const args = _saving.pendingArgs;
      _saving.pendingArgs = null;
      saveNormalized(args.userId, args.teamId, args.data, args.venues);
    }
  }
}

// ── Advance Portal (public-facing, no auth required) ─────────────────────
const ADVANCE_PORTAL_SECTIONS = [
  {
    id: "bus", label: "Bus / Touring Logistics",
    fields: [
      { id: "bus_arrival",  label: "Bus arrival",               type: "time" },
      { id: "shore_power",  label: "Shore power access",        type: "time" },
      { id: "prod_hold",    label: "Production hold",           type: "time" },
      { id: "doors_prod",   label: "Venue doors to production", type: "time" },
      { id: "catering_time",label: "Catering ready",            type: "time" },
      { id: "bus_notes",    label: "Bus / logistics notes",     type: "text" },
    ],
  },
  {
    id: "schedule", label: "Run of Show",
    // Per-act times (load / SC / set) live in the Bill Editor above these fields.
    // These cover venue-level times that are not per-act.
    fields: [
      { id: "venue_access",   label: "Venue Access",               type: "time" },
      { id: "staging_build",  label: "Staging / build",           type: "time" },
      { id: "doors_public",   label: "Doors to public",           type: "time" },
      { id: "vip_access",     label: "VIP access time",           type: "time" },
      { id: "noise_curfew",   label: "Noise curfew",              type: "time" },
      { id: "gr_curfew",      label: "Green room curfew",         type: "time" },
      { id: "curfew",         label: "Hard curfew",               type: "time" },
    ],
  },
  {
    id: "hospitality", label: "Hospitality / Catering",
    fields: [
      { id: "hot_meal",     label: "Venue provides hot meal",                type: "yesno" },
      { id: "vegetarian",   label: "Venue provides vegetarian option",       type: "yesno" },
      { id: "vegan",        label: "Venue provides vegan option",            type: "yesno" },
      { id: "alcohol",      label: "Rider includes alcohol (venue to provide)", type: "yesno" },
      { id: "runner",       label: "Venue provides a runner",                type: "yesno" },
      { id: "buyout_yn",    label: "Venue provides buyout",  type: "yesno" },
      { id: "buyout",       label: "Buy-out amount",        type: "text"  },
      { id: "dietary",      label: "Dietary notes",         type: "text"  },
      { id: "hosp_contact", label: "Catering contact",      type: "text"  },
      { id: "hospo_notes",  label: "Hospitality notes (for band/crew)", type: "text", tmOnly: true },
      { id: "menu_url",     label: "Menu link (Google Drive, PDF, etc.)",    type: "text",  tmOnly: true },
    ],
  },
  {
    id: "backline", label: "Backline",
    fields: [
      { id: "drum_kit",   label: "Drum kit (venue-provided)",    type: "yesno" },
      { id: "bass_amp",   label: "Bass amp (venue-provided)",    type: "yesno" },
      { id: "guitar_amp", label: "Guitar amp (venue-provided)",  type: "yesno" },
      { id: "keys_di",    label: "Keys / DI (venue-provided)",   type: "yesno" },
      { id: "piano",      label: "Piano available (venue-provided)", type: "yesno" },
      { id: "bl_notes",   label: "Backline notes",    type: "text"  },
    ],
  },
  {
    id: "parking", label: "Parking",
    fields: [
      { id: "passes",       label: "Parking passes #",       type: "text"  },
      { id: "loading_zone", label: "Loading zone location",  type: "text"  },
      { id: "bus_parking",  label: "Bus / trailer parking",  type: "text"  },
      { id: "secured_lot",  label: "Secured lot available",  type: "yesno" },
      { id: "vehicle",      label: "Touring vehicle / bus",  type: "text"  },
      { id: "park_notes",   label: "Parking notes",          type: "text"  },
    ],
  },
  {
    id: "guestList", label: "Guest List",
    fields: [
      { id: "gl_cap",      label: "GL cap",                type: "text" },
      { id: "comps",       label: "Comps #",               type: "text" },
      { id: "will_call",   label: "Will call instructions", type: "text" },
      { id: "gl_contact",  label: "GL contact",            type: "text" },
    ],
  },
  {
    id: "tickets", label: "Tickets / Box Office",
    fields: [
      { id: "capacity",     label: "Venue capacity",          type: "text"  },
      { id: "tickets_sold", label: "Tickets sold to date",    type: "text"  },
      { id: "on_sale_date", label: "On-sale date",            type: "text"  },
      { id: "sellout",      label: "Sold out",                type: "yesno" },
      { id: "ticket_link",  label: "Ticketing page URL",      type: "text"  },
      { id: "ticket_notes", label: "Notes",                   type: "text"  },
    ],
  },
  {
    id: "merch", label: "Merch",
    fields: [
      { id: "merch_split_hard", label: "Hard merch split",  type: "percent" },
      { id: "merch_split_soft", label: "Soft merch split",  type: "percent" },
      { id: "table_loc",    label: "Table location",             type: "text"  },
      { id: "seller_prov",  label: "Venue provides merch seller", type: "yesno" },
      { id: "cc_reader",    label: "Venue has card reader",       type: "yesno" },
      { id: "merch_grids",  label: "Merch grids (venue-provided)", type: "yesno" },
      { id: "merch_power",  label: "Power at merch table",        type: "yesno" },
      { id: "merch_lights", label: "Lighting at merch table",     type: "yesno" },
      { id: "merch_notes",  label: "Merch notes",                type: "text"  },
    ],
  },
  {
    id: "production", label: "Production",
    fields: [
      { id: "stage_size",  label: "Stage dimensions",             type: "text"    },
      { id: "pa_system",   label: "PA system",                    type: "text"    },
      { id: "lighting",    label: "Lighting rig",                 type: "text"    },
      { id: "foh_provided",  label: "Venue provides FOH engineer",        type: "yesno"   },
      { id: "mon_eng",       label: "Venue provides monitor engineer",    type: "yesno"   },
      { id: "light_op",      label: "Venue provides lighting operator",   type: "yesno"   },
      { id: "prod_notes",    label: "Production notes",             type: "text"    },
    ],
  },
  {
    id: "greenRoom", label: "Green Room / Dressing Room",
    fields: [
      { id: "rooms",        label: "# of rooms",                 type: "text"  },
      { id: "shower",       label: "Shower available",           type: "yesno" },
      { id: "private_bath", label: "Private bathroom backstage", type: "yesno" },
      { id: "mirror",       label: "Mirror in green room",       type: "yesno" },
      { id: "towels",       label: "Towels provided",            type: "yesno" },
      { id: "iron",         label: "Iron / ironing board",       type: "yesno" },
      { id: "laundry",      label: "Laundry available",          type: "yesno" },
      { id: "gr_access",    label: "Access instructions",        type: "text"  },
      { id: "gr_layout",    label: "Green room layout / vibe",   type: "text"  },
      { id: "wifi_networks", label: "Wi-Fi Networks", type: "wifi_networks" },
    ],
  },
  {
    id: "settlement", label: "Settlement",
    fields: [
      { id: "s_name",  label: "Contact name",   type: "text" },
      { id: "s_email", label: "Contact email",  type: "text" },
      { id: "s_phone", label: "Contact phone",  type: "text" },
      { id: "box_ofc", label: "Box office #",   type: "text" },
    ],
  },
  {
    id: "lodging", label: "Lodging",
    fields: [
      { id: "lod_incl",   label: "Venue provides lodging", type: "yesno" },
      { id: "hotel_name", label: "Hotel / property",       type: "text"  },
      { id: "hotel_addr", label: "Address",                type: "text"  },
      { id: "hotel_rate", label: "Rate / discount code",   type: "text"  },
      { id: "checkin",    label: "Check-in",               type: "time"  },
      { id: "checkout",   label: "Check-out",              type: "time"  },
      { id: "rooms",      label: "Rooms confirmed",        type: "text"  },
      { id: "hotel_con",  label: "Hotel contact",          type: "text"  },
      { id: "lod_notes",  label: "Lodging notes",          type: "text"  },
    ],
  },
  {
    id: "venueCrew", label: "Venue Crew",
    fields: [
      { id: "vc_dos",   label: "Day of Show contact",   type: "contact" },
      { id: "vc_foh",   label: "FOH engineer",           type: "contact" },
      { id: "vc_mon",   label: "Monitor engineer",       type: "contact" },
      { id: "vc_ld",    label: "Lighting director",      type: "contact" },
      { id: "vc_pm",    label: "Production manager",     type: "contact" },
      { id: "vc_sm",    label: "Stage manager",          type: "contact" },
      { id: "vc_sec",   label: "Security contact",       type: "contact" },
      { id: "vc_notes", label: "Venue crew notes",       type: "text"    },
    ],
  },
  {
    id: "security", label: "Security",
    fields: [
      { id: "barricade_type",  label: "Barricade type",          type: "text"    },
      { id: "sec_count",       label: "Security personnel #",    type: "text"    },
      { id: "sec_contact",     label: "Head of security",        type: "contact" },
      { id: "sec_notes",       label: "Security notes / POI",    type: "text"    },
    ],
  },
  {
    id: "groundTransportAdv", label: "Ground Transport",
    fields: [
      { id: "gt_provided",      label: "Provided by venue",      type: "yesno" },
      { id: "gt_type",          label: "Vehicle type",           type: "text"  },
      { id: "gt_driver_name",   label: "Driver name",            type: "text"  },
      { id: "gt_driver_phone",  label: "Driver phone",           type: "text"  },
      { id: "gt_driver_email",  label: "Driver email",           type: "text"  },
      { id: "gt_pickup_time",   label: "Pickup time",            type: "time"  },
      { id: "gt_pickup_loc",    label: "Pickup location",        type: "text"  },
      { id: "gt_dropoff_time",  label: "Drop-off time",          type: "time"  },
      { id: "gt_dropoff_loc",   label: "Drop-off location",      type: "text"  },
      { id: "gt_route",         label: "Route",                  type: "text"  },
      { id: "gt_post_pickup",   label: "Post-show pickup time",  type: "time"  },
      { id: "gt_post_dropoff",  label: "Post-show drop-off loc", type: "text"  },
    ],
  },
  {
    id: "vip", label: "VIP",
    fields: [
      { id: "vip_access_time",  label: "VIP early access time",  type: "time"    },
      { id: "vip_fee",          label: "VIP venue fee",          type: "text"    },
      { id: "vip_contact",      label: "VIP coordinator",        type: "contact" },
      { id: "vip_notes",        label: "VIP notes",              type: "text"    },
    ],
  },
];

// ── Desktop Invite Modal ─────────────────────────────────────────────────────
function DesktopInviteModal({ tourId, artistId, teamId, tourName, artistName, inviterEmail, onClose, UI, lightMode }) {
  const [name,    setName]    = useState("");
  const [email,   setEmail]   = useState("");
  const [role,    setRole]    = useState("crew");
  const [loading, setLoading] = useState(false);
  const [done,    setDone]    = useState(null); // null | { inviteUrl }
  const [error,   setError]   = useState("");

  const FF = "'Helvetica Neue', Helvetica, Arial, sans-serif";

  const handleSend = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const { data: row, error: insErr } = await supabase
        .from("tour_members")
        .insert({ team_id: teamId, tour_id: tourId, artist_id: artistId, email: email.trim(), name: name.trim() || null, role })
        .select("invite_token")
        .single();
      if (insErr) throw new Error(insErr.message);

      const inviteUrl = `${window.location.origin}/invite/${row.invite_token}`;

      // Fire-and-forget email — works once RESEND_API_KEY is set in Vercel
      fetch("/api/invite", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), name: name.trim(), role, inviteToken: row.invite_token, tourName, artistName, inviterName: inviterEmail }),
      }).catch(() => {});

      try { await navigator.clipboard.writeText(inviteUrl); } catch {}
      setDone({ inviteUrl });
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  const overlay = { position: "fixed", inset: 0, zIndex: 600, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 };
  const card    = { width: "100%", maxWidth: 420, background: UI.panelBg, border: `1px solid ${UI.panelBdr}`, borderRadius: 8, overflow: "hidden" };

  return (
    <div style={overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={card}>
        {/* Header */}
        <div style={{ padding: "18px 22px 14px", borderBottom: `1px solid ${UI.panelBdr}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontFamily: FF, fontSize: 14, fontWeight: 700, color: UI.text }}>Invite to Tour</div>
            {tourName && <div style={{ fontFamily: FF, fontSize: 11, color: UI.textMut, marginTop: 2 }}>{artistName} · {tourName}</div>}
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: UI.textMut, fontSize: 20, cursor: "pointer", lineHeight: 1 }}>✕</button>
        </div>

        <div style={{ padding: "20px 22px 22px" }}>
          {done ? (
            /* ── Success ── */
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 28, marginBottom: 10, color: UI.green }}>✓</div>
              <div style={{ fontFamily: FF, fontSize: 14, fontWeight: 600, color: UI.text, marginBottom: 6 }}>Invite sent to {email}</div>
              <div style={{ fontFamily: FF, fontSize: 11, color: UI.textMut, marginBottom: 16, lineHeight: 1.6 }}>
                An email is on its way. The invite link has also been copied to your clipboard.
              </div>
              <div style={{ fontFamily: FF, fontSize: 11, color: UI.textFnt, background: UI.panelAlt, padding: "8px 10px", borderRadius: 4, wordBreak: "break-all", textAlign: "left", marginBottom: 16 }}>
                {done.inviteUrl}
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={() => { setDone(null); setName(""); setEmail(""); setRole("crew"); }}
                  style={{ flex: 1, padding: "8px", background: UI.rowBg, border: `1px solid ${UI.panelBdr}`, borderRadius: 4, fontFamily: FF, fontSize: 11, color: UI.textMut, cursor: "pointer" }}>
                  Invite Another
                </button>
                <button onClick={onClose}
                  style={{ flex: 1, padding: "8px", background: UI.amber, border: "none", borderRadius: 4, fontFamily: FF, fontSize: 11, fontWeight: 700, color: "#000", cursor: "pointer" }}>
                  Done
                </button>
              </div>
            </div>
          ) : (
            /* ── Form ── */
            <form onSubmit={handleSend}>
              {/* Role toggle */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontFamily: FF, fontSize: 11, letterSpacing: "2px", color: UI.textFnt, marginBottom: 7 }}>ROLE</div>
                <div style={{ display: "flex", gap: 6 }}>
                  {["band", "crew"].map(r => (
                    <button key={r} type="button" onClick={() => setRole(r)}
                      style={{ flex: 1, padding: "7px 0", background: role === r ? UI.amdBg : UI.rowBg, border: `1px solid ${role === r ? UI.amdBdr : UI.panelBdr}`, borderRadius: 4, cursor: "pointer", fontFamily: FF, fontSize: 11, fontWeight: role === r ? 700 : 400, color: role === r ? UI.amber : UI.textMut }}>
                      {r === "band" ? "Band Member" : "Crew Member"}
                    </button>
                  ))}
                </div>
              </div>

              {/* Name */}
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontFamily: FF, fontSize: 11, letterSpacing: "2px", color: UI.textFnt, marginBottom: 5 }}>NAME</div>
                <input type="text" placeholder="Full name" value={name} onChange={e => setName(e.target.value)}
                  style={{ width: "100%", padding: "8px 10px", background: UI.panelAlt, border: `1px solid ${UI.fieldBdr}`, borderRadius: 4, color: UI.text, fontFamily: FF, fontSize: 12, outline: "none", boxSizing: "border-box" }} />
              </div>

              {/* Email */}
              <div style={{ marginBottom: 18 }}>
                <div style={{ fontFamily: FF, fontSize: 11, letterSpacing: "2px", color: UI.textFnt, marginBottom: 5 }}>EMAIL</div>
                <input type="email" placeholder="email@example.com" value={email} onChange={e => setEmail(e.target.value)} required
                  style={{ width: "100%", padding: "8px 10px", background: UI.panelAlt, border: `1px solid ${UI.fieldBdr}`, borderRadius: 4, color: UI.text, fontFamily: FF, fontSize: 12, outline: "none", boxSizing: "border-box" }} />
              </div>

              {error && <div style={{ fontFamily: FF, fontSize: 11, color: "#f87171", marginBottom: 12 }}>{error}</div>}

              <div style={{ display: "flex", gap: 8 }}>
                <button type="button" onClick={onClose}
                  style={{ flex: 1, padding: "8px", background: "none", border: `1px solid ${UI.panelBdr}`, borderRadius: 4, fontFamily: FF, fontSize: 11, color: UI.textMut, cursor: "pointer" }}>
                  Cancel
                </button>
                <button type="submit" disabled={loading || !email.trim()}
                  style={{ flex: 2, padding: "8px", background: UI.amber, border: "none", borderRadius: 4, fontFamily: FF, fontSize: 11, fontWeight: 700, color: "#000", cursor: "pointer", opacity: loading || !email.trim() ? 0.5 : 1 }}>
                  {loading ? "Sending…" : "Send Invite"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Module SVG Icons ────────────────────────────────────────────────────────
function ModuleIcon({ type, size = 16, color }) {
  const s = { width: size, height: size, display: "block", flexShrink: 0 };
  const c = color;
  switch (type) {
    case "schedule": return (
      <svg style={s} viewBox="0 0 16 16" fill="none" stroke={c} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="8" cy="8" r="6" />
        <path d="M8 4.5v3.8l2.3 2.3" />
      </svg>
    );
    case "bus_call": return (
      <svg style={s} viewBox="0 0 16 16" fill="none" stroke={c} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
        <rect x="1.5" y="3.5" width="13" height="8" rx="1.5" />
        <path d="M1.5 7.5h13M4.5 11.5v1.5M11.5 11.5v1.5" />
        <circle cx="4.5" cy="9.5" r="0.9" fill={c} stroke="none" />
        <circle cx="11.5" cy="9.5" r="0.9" fill={c} stroke="none" />
        <path d="M5 3.5L6.5 1.5h3L11 3.5" />
      </svg>
    );
    case "hotel": return (
      <svg style={s} viewBox="0 0 16 16" fill="none" stroke={c} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
        <path d="M2 14V4.5a1 1 0 0 1 .6-.9l5-2a1 1 0 0 1 .8 0l5 2a1 1 0 0 1 .6.9V14" />
        <path d="M2 14h12M6 14v-3.5h4V14" />
        <rect x="5" y="6" width="2" height="2" rx="0.3" />
        <rect x="9" y="6" width="2" height="2" rx="0.3" />
      </svg>
    );
    case "weather": return (
      <svg style={s} viewBox="0 0 16 16" fill="none" stroke={c} strokeWidth="1.4" strokeLinecap="round">
        <circle cx="8" cy="6" r="2.2" />
        <path d="M8 1.5v1M8 9v1M12 6h1M3 6h1M10.8 3.2l-.7.7M5.9 8.1l-.7.7M10.8 8.8l-.7-.7M5.9 3.9l-.7-.7" />
        <path d="M5 13.5a2 2 0 0 1 0-4h.5a3.5 3.5 0 0 1 6.8 1A1.8 1.8 0 0 1 11 13.5H5z" />
      </svg>
    );
    case "flights": return (
      <svg style={s} viewBox="0 0 16 16" fill="none" stroke={c} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
        <path d="M1.5 11L4 9.5l7.5-3.8L14 4a1.4 1.4 0 0 0-2-2l-1.5 2.5L3 8 1.5 9v1.5L3 11z" />
        <path d="M2 13.5h12" />
        <path d="M5.5 9.5l1 2.5" />
      </svg>
    );
    case "transport": return (
      <svg style={s} viewBox="0 0 16 16" fill="none" stroke={c} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
        <path d="M1 9.5h14M2.5 5.5h11l1 4H1.5l1-4z" />
        <path d="M4 5.5L5 3h6l1 2.5" />
        <circle cx="4.5" cy="12" r="1.5" />
        <circle cx="11.5" cy="12" r="1.5" />
        <path d="M6 12h5" />
      </svg>
    );
    case "notes": return (
      <svg style={s} viewBox="0 0 16 16" fill="none" stroke={c} strokeWidth="1.4" strokeLinecap="round">
        <rect x="3" y="1.5" width="10" height="13" rx="1.5" />
        <path d="M6 5.5h4M6 8.5h4M6 11.5h2.5" />
      </svg>
    );
    case "local_recs": return (
      <svg style={s} viewBox="0 0 16 16" fill="none" stroke={c} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
        <path d="M8 1.5a5 5 0 0 1 5 5c0 3.5-5 8.5-5 8.5S3 10 3 6.5a5 5 0 0 1 5-5z" />
        <circle cx="8" cy="6.5" r="1.8" />
      </svg>
    );
    case "welcome": return (
      <svg style={s} viewBox="0 0 16 16" fill="none" stroke={c} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
        <path d="M8 2.5v1.8" />
        <path d="M12.5 4l-1.3 1.3" />
        <path d="M3.5 4l1.3 1.3" />
        <path d="M2 9h1.5" />
        <path d="M12.5 9H14" />
        <path d="M5 12.5c0-1.7 1.3-3 3-3s3 1.3 3 3" />
        <path d="M3.5 9a4.5 4.5 0 1 1 9 0" />
      </svg>
    );
    default: return null;
  }
}

// ── Desktop Module Config Modal ──────────────────────────────────────────────
// Lets a TM configure which modules appear on an invited band/crew member's
// Today screen. Changes are saved to tour_members.modules in Supabase.
function DesktopModuleConfigModal({ member, onClose, onSaved, UI, lightMode }) {
  const FF = "'Helvetica Neue', Helvetica, Arial, sans-serif";
  const subBg = lightMode ? "#f4f2ef" : "#111";

  const baseModules = member.modules?.length
    ? member.modules
    : MODULE_DEFS.map((m, i) => ({ type: m.type, enabled: true, order: i, config: m.type === "schedule" ? { ...DEFAULT_SCHEDULE_CONFIG } : {} }));

  const [modules, setModules] = useState(() =>
    MODULE_DEFS.map((d, i) => {
      const saved = baseModules.find(m => m.type === d.type);
      const base  = saved ? { ...saved } : { type: d.type, enabled: true, order: i, config: {} };
      if (d.type === "schedule")   base.config = { ...DEFAULT_SCHEDULE_CONFIG,   ...(base.config || {}) };
      if (d.type === "hotel")      base.config = { checkin: true, checkout: true, address: true, phone: true, confirmation: true, ...(base.config || {}) };
      if (d.type === "flights")    base.config = { flight_number: true, departure: true, arrival: true, terminal_gate: true, confirmation: true, ...(base.config || {}) };
      if (d.type === "transport")  base.config = { pickup_time: true, pickup_location: true, driver: true, dropoff: true, confirmation: true, ...(base.config || {}) };
      if (d.type === "local_recs") base.config = { ...DEFAULT_LOCAL_REC_CONFIG, ...(base.config || {}) };
      return base;
    }).sort((a, b) => a.order - b.order)
  );

  const [saving,       setSaving]       = useState(false);
  const [saved,        setSavedToast]   = useState(false);
  const [expanded,     setExpanded]     = useState(null);
  const [dragOverIdx,  setDragOverIdx]  = useState(null);

  // Dirty-state tracking
  const initialSnapshot = useRef(JSON.stringify(modules));
  const dragSrcIdx      = useRef(null);
  const isDirty = JSON.stringify(modules) !== initialSnapshot.current;

  const toggle = (type) => {
    setModules(prev => prev.map(m => m.type === type ? { ...m, enabled: !m.enabled } : m));
    if (!modules.find(m => m.type === type)?.enabled) setExpanded(null);
  };

  const toggleSubField = (moduleType, fieldId, defaultsObj) => {
    setModules(prev => prev.map(m => {
      if (m.type !== moduleType) return m;
      const current = (m.config || {})[fieldId] !== false;
      return { ...m, config: { ...defaultsObj, ...m.config, [fieldId]: !current } };
    }));
  };

  // Drag-and-drop reorder
  const onDragStart = (idx) => { dragSrcIdx.current = idx; };
  const onDragOver  = (e, idx) => { e.preventDefault(); setDragOverIdx(idx); };
  const onDragEnd   = () => { setDragOverIdx(null); dragSrcIdx.current = null; };
  const onDrop      = (targetIdx) => {
    const src = dragSrcIdx.current;
    if (src === null || src === targetIdx) { setDragOverIdx(null); return; }
    setModules(prev => {
      const n = [...prev];
      const [item] = n.splice(src, 1);
      n.splice(targetIdx, 0, item);
      return n.map((m, i) => ({ ...m, order: i }));
    });
    setDragOverIdx(null);
  };

  const save = async () => {
    setSaving(true);
    const withOrder = modules.map((m, i) => ({ ...m, order: i }));
    const { error } = await supabase.from("tour_members").update({ modules: withOrder }).eq("id", member.id);
    setSaving(false);
    if (!error) {
      initialSnapshot.current = JSON.stringify(withOrder); // reset dirty state
      setSavedToast(true);
      setTimeout(() => { setSavedToast(false); onSaved(member.id, withOrder); }, 900);
    } else {
      console.error("[ADVANCD] save modules failed:", error);
    }
  };

  const roleLabel = member.role === "band" ? "Band Member" : "Crew Member";

  // Sub-field config: maps module type → { fields, defaults }
  const SUB_CONFIGS = {
    schedule:   { fields: SCHEDULE_FIELDS,   defaults: DEFAULT_SCHEDULE_CONFIG },
    hotel:      { fields: HOTEL_FIELDS,      defaults: { checkin: true, checkout: true, address: true, phone: true, confirmation: true } },
    flights:    { fields: FLIGHT_FIELDS,     defaults: { flight_number: true, departure: true, arrival: true, terminal_gate: true, confirmation: true } },
    transport:  { fields: TRANSPORT_FIELDS,  defaults: { pickup_time: true, pickup_location: true, driver: true, dropoff: true, confirmation: true } },
  };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 6000, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div style={{ width: "100%", maxWidth: 520, background: UI.panelBg, border: `1px solid ${UI.panelBdr}`, borderRadius: 12, boxShadow: "0 24px 60px rgba(0,0,0,0.55)", display: "flex", flexDirection: "column", maxHeight: "88vh" }}>

        {/* Header */}
        <div style={{ padding: "18px 20px 14px", borderBottom: `1px solid ${UI.panelBdr}`, display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexShrink: 0 }}>
          <div>
            <div style={{ fontFamily: FF, fontSize: 11, letterSpacing: "2.5px", textTransform: "uppercase", color: UI.amber, marginBottom: 5 }}>Briefing Modules</div>
            <div style={{ fontFamily: FF, fontSize: 16, fontWeight: 700, color: UI.text }}>{member.name || member.email}</div>
            <div style={{ fontFamily: FF, fontSize: 11, color: UI.textMut, marginTop: 2 }}>{roleLabel} · {member.email}</div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: UI.textMut, fontSize: 20, cursor: "pointer", lineHeight: 1, padding: 2 }}>✕</button>
        </div>

        {/* Module list */}
        <div style={{ flex: 1, overflowY: "auto", padding: "6px 0" }}>
          <div style={{ padding: "6px 20px 10px", fontFamily: FF, fontSize: 11, color: UI.textFnt, letterSpacing: "0.3px" }}>
            Toggle modules on or off, drag to reorder. The member sees sections in this order on their Today screen.
          </div>

          {modules.map((m, idx) => {
            const def = MODULE_DEFS.find(d => d.type === m.type);
            if (!def) return null;
            const subCfg       = SUB_CONFIGS[m.type];
            const hasSubFields = !!subCfg || m.type === "local_recs";
            const conf         = m.config || {};
            const isExpanded   = expanded === m.type;
            const iconColor    = m.enabled ? UI.amber : UI.textFnt;

            // Sub-field enabled count label
            let subLabel = "";
            if (subCfg && m.enabled) {
              const onCount = subCfg.fields.filter(f => conf[f.id] !== false).length;
              subLabel = `${onCount}/${subCfg.fields.length} fields`;
            } else if (m.type === "local_recs" && m.enabled) {
              const onCount = LOCAL_REC_CATS.filter(c => conf[c.id] !== false).length;
              subLabel = `${onCount}/${LOCAL_REC_CATS.length} categories`;
            }

            return (
              <div key={m.type}
                draggable
                onDragStart={() => onDragStart(idx)}
                onDragOver={e => onDragOver(e, idx)}
                onDragEnd={onDragEnd}
                onDrop={() => onDrop(idx)}
                style={{ borderTop: dragOverIdx === idx && dragSrcIdx.current !== idx ? `2px solid ${UI.amber}` : "2px solid transparent", transition: "border-color 0.1s" }}>
                {/* ── Module row ── */}
                <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 20px", borderBottom: isExpanded ? "none" : `1px solid ${UI.panelBdr}`, opacity: m.enabled ? 1 : 0.45, transition: "opacity 0.15s" }}>
                  {/* Drag grip */}
                  <div style={{ flexShrink: 0, cursor: "grab", padding: "4px 3px", opacity: 0.35 }} title="Drag to reorder">
                    <svg width="10" height="14" viewBox="0 0 10 14" fill="none">
                      <circle cx="3" cy="2.5" r="1.2" fill={UI.textFnt}/>
                      <circle cx="7" cy="2.5" r="1.2" fill={UI.textFnt}/>
                      <circle cx="3" cy="7"   r="1.2" fill={UI.textFnt}/>
                      <circle cx="7" cy="7"   r="1.2" fill={UI.textFnt}/>
                      <circle cx="3" cy="11.5" r="1.2" fill={UI.textFnt}/>
                      <circle cx="7" cy="11.5" r="1.2" fill={UI.textFnt}/>
                    </svg>
                  </div>

                  {/* SVG Icon */}
                  <ModuleIcon type={m.type} size={16} color={iconColor} />

                  {/* Label + expand trigger */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontFamily: FF, fontSize: 12, fontWeight: 600, color: UI.text }}>{def.label}</div>
                    {hasSubFields && m.enabled ? (
                      <button onClick={() => setExpanded(isExpanded ? null : m.type)} style={{ background: "none", border: "none", padding: 0, cursor: "pointer", display: "flex", alignItems: "center", gap: 5, marginTop: 2 }}>
                        <span style={{ fontFamily: FF, fontSize: 11, color: UI.amber }}>{isExpanded ? "▲" : "▼"} Customise</span>
                        {subLabel ? <span style={{ fontFamily: FF, fontSize: 11, color: UI.textFnt }}>({subLabel})</span> : null}
                      </button>
                    ) : (
                      <div style={{ fontFamily: FF, fontSize: 11, color: UI.textFnt, marginTop: 1 }}>{def.description}</div>
                    )}
                  </div>

                  {/* Sliding toggle */}
                  <div
                    onClick={() => { toggle(m.type); setExpanded(null); }}
                    style={{ position: "relative", width: 42, height: 24, borderRadius: 12, background: m.enabled ? "#4caf50" : UI.amber, cursor: "pointer", transition: "background 0.2s", flexShrink: 0 }}>
                    <div style={{ position: "absolute", width: 18, height: 18, borderRadius: "50%", background: "#fff", top: 3, left: m.enabled ? 21 : 3, transition: "left 0.2s", boxShadow: "0 1px 4px rgba(0,0,0,0.35)" }} />
                  </div>
                </div>

                {/* ── Sub-fields panel (shared pattern for schedule / hotel / flights / transport) ── */}
                {isExpanded && m.enabled && subCfg && (
                  <div style={{ background: subBg, borderBottom: `1px solid ${UI.panelBdr}`, padding: "4px 0 8px" }}>
                    {subCfg.fields.map(f => {
                      const on = conf[f.id] !== false;
                      return (
                        <div key={f.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "7px 20px 7px 50px" }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontFamily: FF, fontSize: 11, fontWeight: 600, color: on ? UI.text : UI.textFnt }}>{f.label}</div>
                            <div style={{ fontFamily: FF, fontSize: 11, color: UI.textVFnt, marginTop: 1 }}>{f.description}</div>
                          </div>
                          <div
                            onClick={() => toggleSubField(m.type, f.id, subCfg.defaults)}
                            style={{ position: "relative", width: 36, height: 20, borderRadius: 8, background: on ? "#4caf50" : UI.amber, cursor: "pointer", transition: "background 0.2s", flexShrink: 0 }}>
                            <div style={{ position: "absolute", width: 14, height: 14, borderRadius: "50%", background: "#fff", top: 3, left: on ? 19 : 3, transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.3)" }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* ── Local Recs: category chips ── */}
                {isExpanded && m.enabled && m.type === "local_recs" && (
                  <div style={{ background: subBg, borderBottom: `1px solid ${UI.panelBdr}`, padding: "10px 20px 12px 50px" }}>
                    <div style={{ fontFamily: FF, fontSize: 11, color: UI.textFnt, letterSpacing: "0.4px", marginBottom: 8 }}>
                      Show recs from these categories on the member's Today screen
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                      {LOCAL_REC_CATS.map(cat => {
                        const on = conf[cat.id] !== false;
                        return (
                          <button
                            key={cat.id}
                            onClick={() => toggleSubField("local_recs", cat.id, DEFAULT_LOCAL_REC_CONFIG)}
                            style={{ fontFamily: FF, fontSize: 11, fontWeight: on ? 600 : 400, color: on ? "#fff" : UI.textFnt, background: on ? "#4caf50" : UI.rowBg, border: `1px solid ${on ? "#4caf50" : UI.panelBdr}`, borderRadius: 16, padding: "4px 12px", cursor: "pointer", transition: "all 0.15s" }}>
                            {cat.label}
                          </button>
                        );
                      })}
                    </div>
                    <div style={{ fontFamily: FF, fontSize: 11, color: UI.textVFnt, marginTop: 10, letterSpacing: "0.3px" }}>
                      Recs are pulled from city data added by the team. Members see only their enabled categories.
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div style={{ padding: "14px 20px", borderTop: `1px solid ${UI.panelBdr}`, display: "flex", alignItems: "center", gap: 8, justifyContent: "flex-end", flexShrink: 0 }}>
          {saved && <span style={{ fontFamily: FF, fontSize: 11, color: UI.green, marginRight: "auto", letterSpacing: "0.3px" }}>✓ Saved</span>}
          {!saved && isDirty && <span style={{ fontFamily: FF, fontSize: 11, color: UI.textFnt, marginRight: "auto", letterSpacing: "0.3px" }}>Unsaved changes</span>}
          <button onClick={onClose} style={{ fontFamily: FF, fontSize: 11, color: isDirty ? UI.text : UI.textMut, background: "none", border: `1px solid ${isDirty ? UI.panelBdr : UI.panelBdr}`, borderRadius: 4, padding: "7px 18px", cursor: "pointer" }}>
            {isDirty ? "Discard" : "Close"}
          </button>
          <button
            onClick={save}
            disabled={saving || saved || !isDirty}
            style={{ fontFamily: FF, fontSize: 11, fontWeight: 700, color: isDirty ? "#000" : UI.textFnt, background: isDirty ? UI.amber : UI.panelAlt, border: `1px solid ${isDirty ? UI.amdBdr : UI.panelBdr}`, borderRadius: 4, padding: "7px 22px", cursor: isDirty ? "pointer" : "default", opacity: (saving || saved) ? 0.6 : 1, transition: "all 0.2s" }}>
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Bill Editor — inline in the Run of Show advance section ──────────────────
// TM builds the full nightly bill: each act's name, load-in, soundcheck, set time,
// plus changeover times between consecutive acts.
// Stored in advanceTmData.schedule.bill (array) and .changeovers (array).
// ── 12-hour time handling (ROS + advance time fields) ───────────────────────
// Times are stored as canonical "H:MM AM/PM" strings. Legacy values parse:
// 24h ("17:00") converts to 5:00 PM; bare "7:00" keeps the 24h reading (7 AM,
// matching every existing parser) but the toggle makes the ambiguity visible
// and one tap fixes it. parseDisplayTime (below, hoisted) reads both formats.
function splitTime12(v) {
  if (!v || typeof v !== "string") return null;
  const s = v.trim();
  // "5:30 PM", "5pm", "5:30p", "5 p.m."
  let m = s.match(/^(\d{1,2})(?::(\d{2}))?\s*([ap])\.?\s*m?\.?$/i);
  if (m) {
    let h = parseInt(m[1], 10);
    if (h === 0) h = 12;
    if (h > 12) h -= 12;
    return { t: `${h}:${m[2] || "00"}`, ap: m[3].toUpperCase() === "P" ? "PM" : "AM" };
  }
  // "17:00", "7:00" — 24h / ambiguous bare time
  m = s.match(/^(\d{1,2}):(\d{2})$/);
  if (m) {
    let h = parseInt(m[1], 10);
    const ap = h >= 12 ? "PM" : "AM";
    if (h === 0) h = 12;
    if (h > 12) h -= 12;
    return { t: `${h}:${m[2]}`, ap };
  }
  // bare "7"
  m = s.match(/^(\d{1,2})$/);
  if (m) {
    let h = parseInt(m[1], 10);
    const ap = h >= 12 ? "PM" : "AM";
    if (h === 0) h = 12;
    if (h > 12) h -= 12;
    return { t: `${h}:00`, ap };
  }
  return null;
}
// Semantic time equality across formats ("17:00" === "5:00 PM").
function timeEq(a, b) {
  return parseDisplayTime(a || "") === parseDisplayTime(b || "");
}

// Time input with an explicit AM/PM toggle. Commits canonical "H:MM AM/PM" on
// blur / Enter / toggle click. `defaultAp` applies when the field is empty —
// PM by default, because nearly every ROS time is a PM time.
function TimeInput12({ value, onChange, readOnly = false, className = "", inputStyle = {}, toggleStyle = {}, placeholder = "0:00", defaultAp = "PM" }) {
  const [focus, setFocus] = useState(false);
  const [draft, setDraft] = useState("");
  const parts = splitTime12(value);
  const ap = parts?.ap || defaultAp;
  const text = focus ? draft : (parts?.t ?? (value || ""));

  const commitText = (raw) => {
    const s = String(raw).trim();
    if (!s) { onChange(""); return; }
    // Typed am/pm wins over the toggle; 24h converts; else toggle applies.
    const p = splitTime12(s) && /[ap]/i.test(s) ? splitTime12(s)
      : (splitTime12(`${s} ${ap}`) || splitTime12(s));
    if (p) onChange(`${p.t} ${p.ap}`);
    else onChange(s); // unparseable — keep as typed rather than eating input
  };

  return (
    <span style={{ display: "inline-flex", width: "100%", minWidth: 0, alignItems: "stretch" }}>
      <input
        className={className}
        value={text}
        readOnly={readOnly}
        placeholder={placeholder}
        onFocus={(e) => { setFocus(true); setDraft(parts?.t ?? (value || "")); e.target.select(); }}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => { setFocus(false); commitText(draft); }}
        onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
        style={{ ...inputStyle, flex: 1, minWidth: 0, width: "auto", borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
      />
      <button
        type="button"
        tabIndex={-1}
        disabled={readOnly}
        title={readOnly ? undefined : `Switch to ${ap === "PM" ? "AM" : "PM"}`}
        onMouseDown={(e) => e.preventDefault() /* don't steal focus mid-typing */}
        onClick={() => {
          if (readOnly) return;
          const next = ap === "PM" ? "AM" : "PM";
          const src = focus ? draft : (parts?.t || value || "");
          const p = splitTime12(String(src).trim());
          if (p) { onChange(`${p.t} ${next}`); if (focus) setDraft(p.t); }
        }}
        style={{
          flexShrink: 0, width: 26, padding: 0, lineHeight: 1,
          fontSize: 11, fontWeight: 700, letterSpacing: 0.3,
          borderTopLeftRadius: 0, borderBottomLeftRadius: 0, borderLeft: "none",
          cursor: readOnly ? "default" : "pointer",
          ...toggleStyle,
        }}>
        {ap}
      </button>
    </span>
  );
}

function BillEditorInline({ schedule, onUpdate, UI, lightMode }) {
  const FF = "'Helvetica Neue', Helvetica, Arial, sans-serif";
  const bill = Array.isArray(schedule?.bill) && schedule.bill.length ? schedule.bill : DEFAULT_BILL;
  const cos  = Array.isArray(schedule?.changeovers) ? schedule.changeovers : [];

  const updBand = (idx, field, val) => {
    const next = bill.map((b, i) => i === idx ? { ...b, [field]: val } : b);
    onUpdate({ bill: next, changeovers: cos });
  };
  const updCoField = (idx, field, val) => {
    const next = [...cos];
    while (next.length <= idx) next.push({ time: "" });
    next[idx] = { ...next[idx], [field]: val };
    onUpdate({ bill, changeovers: next });
  };
  const addBand = () => {
    const newAct  = { id: "b" + Date.now(), name: "Support Act", load: "", sc: "", set: "", setEnd: "" };
    const newBill = [...bill.slice(0, -1), newAct, bill[bill.length - 1]];
    const newCos  = Array(newBill.length - 1).fill(null).map((_, i) => cos[i] || { time: "" });
    onUpdate({ bill: newBill, changeovers: newCos });
  };
  const removeBand = (idx) => {
    if (bill.length <= 1) return;
    const newBill = bill.filter((_, i) => i !== idx);
    const newCos  = Array(Math.max(0, newBill.length - 1)).fill(null).map((_, i) => {
      const orig = i >= idx ? i + 1 : i;
      return cos[orig] || { time: "" };
    });
    onUpdate({ bill: newBill, changeovers: newCos });
  };

  const inp = (val, onChange) => (
    <TimeInput12 value={val || ""} onChange={onChange}
      className="f-inp"
      inputStyle={{ textAlign: "center", padding: "4px 2px" }}
      toggleStyle={{ fontFamily: FF, background: "var(--panel-alt)", color: "var(--text-mut)", border: "1px solid var(--field-border, var(--panel-border))", borderRadius: "0 6px 6px 0" }}
    />
  );

  return (
    <div>
      {/* Column headers */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 88px 88px 88px 88px 22px", gap: 5, marginBottom: 5 }}>
        <div style={{ fontFamily: FF, fontSize: 11, letterSpacing: "1px", color: UI.textFnt, paddingLeft: 2 }}>ACT</div>
        <div style={{ fontFamily: FF, fontSize: 11, letterSpacing: "1px", color: UI.textFnt, textAlign: "center" }}>LOAD-IN</div>
        <div style={{ fontFamily: FF, fontSize: 11, letterSpacing: "1px", color: UI.textFnt, textAlign: "center" }}>S/CHECK</div>
        <div style={{ fontFamily: FF, fontSize: 11, letterSpacing: "1px", color: UI.textFnt, textAlign: "center" }}>SET</div>
        <div style={{ fontFamily: FF, fontSize: 11, letterSpacing: "1px", color: UI.textFnt, textAlign: "center" }}>SET END</div>
        <div />
      </div>

      {bill.map((band, i) => {
        const co   = cos[i] || {};
        const isIM = !!co.isIntermission;
        const dur  = setDurMins(band.set, band.setEnd);
        return (
          <Fragment key={band.id || i}>
            {/* Act row */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 88px 88px 88px 88px 22px", gap: 5, marginBottom: dur ? 2 : 4 }}>
              <input className="f-inp" placeholder={`Act ${i + 1} name`} value={band.name}
                onChange={e => updBand(i, "name", e.target.value)} style={{ padding: "4px 6px" }} />
              {inp(band.load,   v => updBand(i, "load",   v))}
              {inp(band.sc,     v => updBand(i, "sc",     v))}
              {inp(band.set,    v => updBand(i, "set",    v))}
              {inp(band.setEnd, v => updBand(i, "setEnd", v))}
              <button onClick={() => removeBand(i)} disabled={bill.length <= 1}
                title="Remove act"
                style={{ background: "none", border: "none", cursor: bill.length <= 1 ? "default" : "pointer", color: bill.length <= 1 ? UI.panelBdr : UI.textFnt, fontSize: 12, lineHeight: 1, padding: 0 }}>✕</button>
            </div>
            {/* Computed duration badge */}
            {dur && (
              <div style={{ fontFamily: FF, fontSize: 11, color: UI.amber, marginBottom: 4, paddingLeft: 2, letterSpacing: 0.5 }}>
                {band.name} set: <strong>{fmtDur(dur)}</strong>
              </div>
            )}

            {/* Changeover / Intermission row between consecutive acts */}
            {i < bill.length - 1 && (() => {
              const coDur = setDurMins(co.time, co.timeEnd);
              return (
                <>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 88px 88px 88px 88px 22px", gap: 5, marginBottom: coDur ? 2 : 6 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 5, paddingLeft: 4 }}>
                      <div style={{ flex: 1, height: 1, background: isIM ? UI.amdBdr : UI.panelBdr }} />
                      {/* Toggle: click label to switch type */}
                      <button
                        onClick={() => updCoField(i, "isIntermission", !isIM)}
                        title={isIM ? "Switch to Changeover" : "Switch to Intermission"}
                        style={{ fontFamily: FF, fontSize: 11, letterSpacing: "1px", color: isIM ? UI.amber : UI.textFnt, background: "none", border: `1px solid ${isIM ? UI.amdBdr : UI.panelBdr}`, borderRadius: 4, padding: "1px 5px", cursor: "pointer", flexShrink: 0 }}>
                        {isIM ? "INTERMISSION" : "CHANGEOVER"}
                      </button>
                    </div>
                    <div />
                    <div />
                    {/* Start time */}
                    {inp(co.time || "", v => updCoField(i, "time", v))}
                    {/* End time */}
                    {inp(co.timeEnd || "", v => updCoField(i, "timeEnd", v))}
                  </div>
                  {/* Computed duration badge */}
                  {coDur && (
                    <div style={{ fontFamily: FF, fontSize: 11, color: UI.amber, marginBottom: 6, paddingLeft: 2, letterSpacing: 0.5 }}>
                      {isIM ? "Intermission" : "Changeover"}: <strong>{fmtDur(coDur)}</strong>
                    </div>
                  )}
                </>
              );
            })()}
          </Fragment>
        );
      })}

      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6 }}>
        <button onClick={addBand}
          style={{ fontFamily: FF, fontSize: 11, color: UI.green, background: "none", border: `1px dashed ${UI.ckOnBdr}`, borderRadius: 4, padding: "4px 12px", cursor: "pointer" }}>
          + Add Act
        </button>
        <span style={{ fontFamily: FF, fontSize: 11, color: UI.textVFnt }}>
          Click CHANGEOVER to mark as INTERMISSION
        </span>
      </div>
    </div>
  );
}

// ── Promoter-facing Run of Show editor (advance portal) ─────────────────────
// Mirrors the TM's BillEditorInline layout inside the portal. The TM's bill is
// the baseline; promoter edits store a full proposed copy in
// advance_data.schedule.bill / .changeovers — advance_tm_data is never touched.
// Cells that differ from the TM version highlight amber (the portal's standard
// "amended" language). Promoters can adjust act names, times, and changeovers,
// but cannot add or remove acts — that stays with the TM.
function PortalBillEditor({ tmSchedule, proSchedule, locked, P, onUpdate }) {
  const FF = "'Helvetica Neue', Helvetica, Arial, sans-serif";
  const tmBill = Array.isArray(tmSchedule?.bill) ? tmSchedule.bill : [];
  const tmCos  = Array.isArray(tmSchedule?.changeovers) ? tmSchedule.changeovers : [];
  const hasProposal = Array.isArray(proSchedule?.bill) && proSchedule.bill.length > 0;
  const bill = hasProposal ? proSchedule.bill : tmBill;
  const cos  = hasProposal && Array.isArray(proSchedule?.changeovers) ? proSchedule.changeovers : tmCos;
  if (bill.length === 0) return null; // TM hasn't built a bill — nothing to show

  const commit  = (nb, nc) => onUpdate({ bill: nb, changeovers: nc });
  const updBand = (idx, field, val) =>
    commit(bill.map((b, i) => (i === idx ? { ...b, [field]: val } : b)), cos);
  const updCo = (idx, field, val) => {
    const next = [...cos];
    while (next.length <= idx) next.push({ time: "" });
    next[idx] = { ...next[idx], [field]: val };
    commit(bill, next);
  };
  const tmActFor = (band, i) => tmBill.find((b) => b.id && b.id === band.id) || tmBill[i] || {};

  const lblSt = { fontFamily: FF, fontSize: 11, letterSpacing: "1px", color: P.textFnt, textAlign: "center" };
  const baseInp = (changed) => ({
    width: "100%", boxSizing: "border-box",
    padding: "5px 4px", fontFamily: FF, fontSize: 12, borderRadius: 6, outline: "none",
    background: changed ? P.amd : P.inpBg, color: P.text,
    border: `1px solid ${changed ? P.amdBdr : P.inpBdr}`,
    opacity: locked ? 0.7 : 1,
  });
  const cell = (val, tmVal, onChange, opts = {}) => {
    if (opts.text) {
      const changed = hasProposal && (val || "") !== (tmVal || "");
      return (
        <input
          value={val ?? ""} readOnly={locked}
          placeholder={opts.ph || ""}
          onChange={(e) => !locked && onChange(e.target.value)}
          style={{ ...baseInp(changed), textAlign: "left", padding: "5px 6px" }} />
      );
    }
    // Time cell — AM/PM toggle, canonical "H:MM AM/PM" storage, format-aware diff
    const changed = hasProposal && !timeEq(val, tmVal);
    return (
      <TimeInput12
        value={val ?? ""} readOnly={locked}
        onChange={(v) => !locked && onChange(v)}
        inputStyle={{ ...baseInp(changed), textAlign: "center" }}
        toggleStyle={{
          fontFamily: FF, background: P.inpBg, color: P.textMut,
          border: `1px solid ${changed ? P.amdBdr : P.inpBdr}`,
          borderRadius: "0 6px 6px 0", opacity: locked ? 0.7 : 1,
        }}
      />
    );
  };

  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
        <div style={{ fontFamily: FF, fontSize: 11, letterSpacing: "1.5px", textTransform: "uppercase", color: P.textFnt }}>Run of Show</div>
        {!locked && hasProposal && (
          <button onClick={() => commit([], [])}
            title="Discard your proposed changes and go back to the TM's schedule"
            style={{ fontFamily: FF, fontSize: 11, color: P.textMut, background: "none", border: `1px solid ${P.bdr}`, borderRadius: 4, padding: "2px 8px", cursor: "pointer" }}>
            Reset to TM schedule
          </button>
        )}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 88px 88px 88px 88px", gap: 5, marginBottom: 5 }}>
        <div style={{ ...lblSt, textAlign: "left", paddingLeft: 2 }}>ACT</div>
        <div style={lblSt}>LOAD-IN</div>
        <div style={lblSt}>S/CHECK</div>
        <div style={lblSt}>SET</div>
        <div style={lblSt}>SET END</div>
      </div>
      {bill.map((band, i) => {
        const tmB  = tmActFor(band, i);
        const co   = cos[i] || {};
        const tmCo = tmCos[i] || {};
        const isIM = !!co.isIntermission;
        return (
          <Fragment key={band.id || i}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 88px 88px 88px 88px", gap: 5, marginBottom: 4 }}>
              {cell(band.name,   tmB.name,   (v) => updBand(i, "name", v),   { text: true, ph: `Act ${i + 1}` })}
              {cell(band.load,   tmB.load,   (v) => updBand(i, "load", v))}
              {cell(band.sc,     tmB.sc,     (v) => updBand(i, "sc", v))}
              {cell(band.set,    tmB.set,    (v) => updBand(i, "set", v))}
              {cell(band.setEnd, tmB.setEnd, (v) => updBand(i, "setEnd", v))}
            </div>
            {i < bill.length - 1 && (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 88px 88px 88px 88px", gap: 5, marginBottom: 6 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 5, paddingLeft: 4 }}>
                  <div style={{ flex: 1, height: 1, background: isIM ? P.amdBdr : P.bdr }} />
                  <button
                    onClick={() => !locked && updCo(i, "isIntermission", !isIM)}
                    title={isIM ? "Switch to Changeover" : "Switch to Intermission"}
                    style={{ fontFamily: FF, fontSize: 11, letterSpacing: "1px", color: isIM ? P.amber : P.textFnt, background: "none", border: `1px solid ${isIM ? P.amdBdr : P.bdr}`, borderRadius: 4, padding: "1px 5px", cursor: locked ? "default" : "pointer", flexShrink: 0 }}>
                    {isIM ? "INTERMISSION" : "CHANGEOVER"}
                  </button>
                </div>
                <div />
                <div />
                {cell(co.time || "",    tmCo.time,    (v) => updCo(i, "time", v))}
                {cell(co.timeEnd || "", tmCo.timeEnd, (v) => updCo(i, "timeEnd", v))}
              </div>
            )}
          </Fragment>
        );
      })}
      <div style={{ fontFamily: FF, fontSize: 11, color: P.textVFnt, marginTop: 4, lineHeight: 1.5 }}>
        {locked
          ? "Responses are locked."
          : "Edit times to propose changes — edits highlight amber and go back to the tour manager. Need an act added or removed? Leave a note in Additional Notes."}
      </div>
    </div>
  );
}

function AdvancePortal({ token, lightMode }) {
  const [status,         setStatus]         = useState("loading");
  const [advData,        setAdvData]        = useState({});
  const [info,           setInfo]           = useState(null);
  const [saved,          setSaved]          = useState(false);
  const [saveFailed,     setSaveFailed]     = useState(false);
  const _secKey = `adv_secs_${token}`;
  const [openSecs, setOpenSecs] = useState(() => {
    try { return JSON.parse(localStorage.getItem(_secKey) || "{}"); } catch { return {}; }
  });
  const [proFileUploading, setProFileUploading] = useState(false);
  const [proFileLabel,   setProFileLabel]   = useState("Venue Specs");
  // AP-1: per-section status { [secId]: { status, note, updated_at } }
  const [sectionStatus, setSectionStatus] = useState({});
  const apTimer = useRef(null);

  useEffect(() => {
    fetch(`/api/advance?token=${token}`)
      .then(r => r.json())
      .then(d => {
        if (d.error) { setStatus("error"); return; }
        setInfo(d);
        const data = d.show?.advance_data || {};
        setAdvData(data);
        // AP-1: load per-section status
        const secStat = d.sectionStatus || {};
        setSectionStatus(secStat);
        // Only derive initial open state if nothing is saved in localStorage for this token
        const hasSavedState = (() => { try { return !!localStorage.getItem(_secKey); } catch { return false; } })();
        const enabled = d.tour?.advance_config?.sections || ADVANCE_PORTAL_SECTIONS.map(s => s.id);
        if (!hasSavedState) {
          // First visit: open sections that have TM-prefilled data or are flagged by TM
          const initial = {};
          ADVANCE_PORTAL_SECTIONS.forEach(s => {
            if (!enabled.includes(s.id)) return;
            const hasTmData = !!(data[s.id] && Object.values(data[s.id]).some(v => v !== false && v !== "" && v !== undefined));
            const isFlagged = secStat[s.id]?.status === "flagged";
            initial[s.id] = hasTmData || isFlagged;
          });
          setOpenSecs(initial);
          try { localStorage.setItem(_secKey, JSON.stringify(initial)); } catch {}
        } else {
          // Returning visit: force-open any newly-flagged sections so promoter sees the flag
          setOpenSecs(prev => {
            const next = { ...prev };
            ADVANCE_PORTAL_SECTIONS.forEach(s => {
              if (enabled.includes(s.id) && secStat[s.id]?.status === "flagged") next[s.id] = true;
            });
            return next;
          });
        }
        setStatus("ready");
      })
      .catch(() => setStatus("error"));
  }, [token]);

  const patchAdvance = (patch) => {
    if (apTimer.current) clearTimeout(apTimer.current);
    setSaved(false);
    apTimer.current = setTimeout(async () => {
      try {
        const res = await fetch(`/api/advance?token=${token}`, {
          method: "PATCH", headers: { "Content-Type": "application/json" },
          body: JSON.stringify(patch),
        });
        // Previously this showed "Saved ✓" unconditionally — a rejected write
        // (404/500) looked identical to success, so a promoter could fill the
        // whole advance while nothing persisted. Surface failures loudly.
        if (!res.ok) throw new Error(`Server rejected save (${res.status})`);
        setSaveFailed(false);
        setSaved(true);
        setTimeout(() => setSaved(false), 2500);
      } catch (err) {
        console.error('[ADVANCD] patchAdvance failed:', err);
        setSaveFailed(true);
      }
    }, 900);
  };

  const logProActivity = (label) => {
    const entry = { id: "log" + Math.random().toString(36).slice(2), ts: new Date().toISOString(), actor: "promoter", label };
    setAdvData(p => {
      const next = { ...p, _activity: [...(p._activity || []), entry] };
      patchAdvance({ _activity: next._activity });
      return next;
    });
  };

  const saveField = (secId, fieldId, value) => {
    const next = { ...advData, [secId]: { ...(advData[secId] || {}), [fieldId]: value } };
    setAdvData(next);
    patchAdvance({ [secId]: next[secId] });
  };


  const toggleSec = (id) => setOpenSecs(p => {
    const next = { ...p, [id]: !p[id] };
    try { localStorage.setItem(_secKey, JSON.stringify(next)); } catch {}
    return next;
  });

  const saveComment = (secId, fieldId, text) => {
    const sec = ADVANCE_PORTAL_SECTIONS.find(s => s.id === secId);
    const fld = sec?.fields.find(f => f.id === fieldId);
    setAdvData(p => {
      const secCurrent = p[secId] || {};
      const comments = { ...(secCurrent._comments || {}), [fieldId]: { text: text.trim(), ts: new Date().toISOString() } };
      const secNext = { ...secCurrent, _comments: comments };
      patchAdvance({ [secId]: secNext });
      return { ...p, [secId]: secNext };
    });
    logProActivity(`Added note on ${sec?.label || secId} / ${fld?.label || fieldId}`);
  };
  const clearComment = (secId, fieldId) => {
    setAdvData(p => {
      const secCurrent = { ...(p[secId] || {}) };
      const comments = { ...(secCurrent._comments || {}) };
      delete comments[fieldId];
      if (Object.keys(comments).length) secCurrent._comments = comments;
      else delete secCurrent._comments;
      patchAdvance({ [secId]: secCurrent });
      return { ...p, [secId]: secCurrent };
    });
  };

  // count filled fields in a section (promoter value OR TM suggestion)
  const secCount = (sec) => {
    const sd   = advData[sec.id]  || {};
    const tmSd = tmData[sec.id] || {};
    return sec.fields.filter(f => {
      const v = sd[f.id] !== undefined ? sd[f.id] : tmSd[f.id];
      if (f.type === "yesno")        return v === true || v === false;
      if (f.type === "wifi_networks") return Array.isArray(v) && v.some(n => n.network);
      if (f.type === "contact") { const c = (typeof v === "object" && v) ? v : {}; return !!(c.name || c.phone || c.email); }
      return v && String(v).trim();
    }).length;
  };

  // ── Colour palette (light vs dark) ────────────────────────────────────────
  const P = lightMode ? {
    bg:"#f5f2ec",hdrBg:"#ece8e2",heroBdr:"#d8d4ce",secBg:"#e8e4de",secHov:"#e2dfd9",
    fldsTop:"#d8d4ce",inpBg:"#e4e0da",inpBdr:"#ccc8c2",inpFocus:"#c47f00",
    ckBg:"#ede9e3",ckBdr:"#d0ccc6",ckBdrHov:"#c0bdb8",ckBoxBg:"#dedad4",ckBoxBdr:"#ccc8c2",
    ckOn:"#c47f00",tmPre:"#e8ede3",tmPreBdr:"#ccdac4",amd:"#f5e8cc",amdBdr:"#d4880a",
    notBg:"#e8f0e0",notBdr:"#a8c890",notClr:"#3a6a20",
    amber:"#c47f00",green:"#1a9e4a",text:"#1a1917",textMid:"#4a4744",
    textMut:"#6a6764",textFnt:"#9a9794",textVFnt:"#c0bdb8",bdr:"#d0ccc6",bdrSub:"#d8d4ce",
    svgTk:"#fff",foot:"#aaa9a6",logoClr:"#c47f00",savedClr:"#1a9e4a",
    tmBadgeBg:"#ddf0d8",tmBadgeBdr:"#a0d090",tmBadgeClr:"#2a6a2a",
    amdBadgeBg:"#fff0cc",amdBadgeBdr:"#d4880a",amdBadgeClr:"#8a4a00",
    hasAmdBdr:"#d4880a",confBdr:"#a8c890",confHdr:"#e4ede0",
    notedBtn:"#3a6a2a",notedBtnBg:"#e8f0e0",notedBtnBdr:"#a8c890",
    notedHov:"#1a6a1a",notedHovBdr:"var(--success)",notedHovBg:"#d8eed8",
    notedClr:"#1a9e4a",tmFileBg:"#e8ede3",tmFileBdr:"#a8c890",
    proFileBg:"#e8e4de",proFileBdr:"#d0ccc6",uploadBdr:"#ccc8c2",
    ynYesBg:"#e8f0e0",ynYesBdr:"#1a9e4a",ynYesClr:"#1a6a2a",
    ynNoBg:"#f5e8cc",ynNoBdr:"#c44a4a",ynNoClr:"#8a2020",
  } : {
    bg:"#0d0d0d",hdrBg:"#111",heroBdr:"#1a1a1a",secBg:"#111",secHov:"#161616",
    fldsTop:"#1a1a1a",inpBg:"#161616",inpBdr:"#252525",inpFocus:"var(--accent)",
    ckBg:"#0f0f0f",ckBdr:"#1e1e1e",ckBdrHov:"#2a2a2a",ckBoxBg:"#161616",ckBoxBdr:"#333",
    ckOn:"var(--accent)",tmPre:"#0e140e",tmPreBdr:"#1e2a1e",amd:"#120b00",amdBdr:"#c45c00",
    notBg:"#141a0f",notBdr:"#253320",notClr:"#7aad4a",
    amber:"var(--accent)",green:"var(--success)",text:"#e0e0e0",textMid:"#bbb",
    textMut:"#666",textFnt:"#444",textVFnt:"#2a2a2a",bdr:"#1e1e1e",bdrSub:"#1a1a1a",
    svgTk:"#0d0d0d",foot:"#2a2a2a",logoClr:"var(--accent)",savedClr:"var(--success)",
    tmBadgeBg:"#0e1a0e",tmBadgeBdr:"#1a3a1a",tmBadgeClr:"#2a4a2a",
    amdBadgeBg:"#1a0e00",amdBadgeBdr:"#3a2000",amdBadgeClr:"var(--accent)",
    hasAmdBdr:"#3a2200",confBdr:"#1a3a1a",confHdr:"#0a140a",
    notedBtn:"#2a5a2a",notedBtnBg:"#0a1a0a",notedBtnBdr:"#1a3a1a",
    notedHov:"var(--success)",notedHovBdr:"var(--success)",notedHovBg:"#0d1f0d",
    notedClr:"var(--success)",tmFileBg:"#0e140e",tmFileBdr:"#1a3a1a",
    proFileBg:"#111",proFileBdr:"#1e1e1e",uploadBdr:"#2a2a2a",
    ynYesBg:"#0e1a0e",ynYesBdr:"var(--success)",ynYesClr:"var(--success)",
    ynNoBg:"#1e1200",ynNoBdr:"var(--danger)",ynNoClr:"var(--danger)",
  };

  if (status === "loading") return (
    <div style={{ minHeight: "100vh", background: P.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 12, color: P.textMut, letterSpacing: 2 }}>LOADING...</div>
    </div>
  );
  if (status === "error") return (
    <div style={{ minHeight: "100vh", background: P.bg, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 14 }}>
      <div style={{ fontFamily: "var(--font-display)", fontSize: 40, letterSpacing: 4, color: P.amber }}>ADVANCD</div>
      <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 12, color: P.textMut }}>Advance link not found or expired.</div>
    </div>
  );

  const { show, tour, venue, artist } = info || {};
  const tmData = info?.tmData || {};  // TM suggestions layer (read-only for promoter)

  // Convert a 24h time string ("15:30") to 12h format ("3:30 PM") for promoter display.
  // Leaves values that are already in 12h format (contain "am"/"pm") unchanged.
  const to12h = (t) => {
    if (!t || typeof t !== "string") return t;
    if (/[ap]m/i.test(t)) return t;                    // already 12h — leave alone
    const m = t.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
    if (!m) return t;                                   // unrecognised format — leave alone
    let h = parseInt(m[1], 10);
    const min = m[2];
    const suffix = h >= 12 ? "PM" : "AM";
    if (h === 0) h = 12;
    else if (h > 12) h -= 12;
    return `${h}:${min} ${suffix}`;
  };

  // Guard against partial API response (e.g. venue deleted from CRM after portal was sent)
  if (!show || !tour || !artist) return (
    <div style={{ minHeight: "100vh", background: P.bg, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 14 }}>
      <div style={{ fontFamily: "var(--font-display)", fontSize: 40, letterSpacing: 4, color: P.amber }}>ADVANCD</div>
      <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 12, color: P.textMut }}>This advance is temporarily unavailable. Contact your tour manager.</div>
    </div>
  );

  // Only show sections + fields the TM has enabled for this tour
  const enabledSecIds = tour?.advance_config?.sections || ADVANCE_PORTAL_SECTIONS.map(s => s.id);
  const visibleSections = ADVANCE_PORTAL_SECTIONS
    .filter(s => enabledSecIds.includes(s.id))
    .map(s => {
      const enabledFlds = tour?.advance_config?.fields?.[s.id];
      // tmOnly fields are TM-internal (band/crew notes) — never shown to the promoter portal
      const portalFields = s.fields.filter(f => !f.tmOnly);
      if (!enabledFlds) return { ...s, fields: portalFields };
      // Only show fields explicitly enabled by the TM (filter out any that no longer exist in schema)
      // Do NOT auto-include fields missing from the saved list — those are ones the TM disabled
      return { ...s, fields: portalFields.filter(f => enabledFlds.includes(f.id)) };
    });

  // Overall progress across all visible sections
  const totalFields  = visibleSections.reduce((acc, sec) => acc + sec.fields.length, 0);
  const filledFields = visibleSections.reduce((acc, sec) => acc + secCount(sec), 0);
  const progress     = totalFields > 0 ? Math.round((filledFields / totalFields) * 100) : 0;

  // Submit advance (marks as done; form becomes read-only but can be re-opened)
  const submitAdvance = async () => {
    const ts = new Date().toISOString();
    logProActivity("Submitted advance");
    const patch = { _submitted: true, _submittedAt: ts };
    setAdvData(p => ({ ...p, _submitted: true, _submittedAt: ts }));
    try {
      await fetch(`/api/advance?token=${token}`, {
        method: "PATCH", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch),
      });
    } catch (err) { console.error('[ADVANCD] submitAdvance patch failed:', err); }
  };

  const reopenAdvance = async () => {
    logProActivity("Re-opened advance for editing");
    const patch = { _submitted: false };
    setAdvData(p => ({ ...p, _submitted: false }));
    try {
      await fetch(`/api/advance?token=${token}`, {
        method: "PATCH", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch),
      });
    } catch (err) { console.error('[ADVANCD] reopenAdvance patch failed:', err); }
  };

  // Confirm an entire section as "Noted" (promoter has reviewed, no changes needed)
  const confirmSection = (secId) => {
    const sec = visibleSections.find(s => s.id === secId);
    if (!sec) return;
    // Copy TM values into promoter layer for all fields in this section, mark section confirmed
    const tmSec = tmData[secId] || {};
    const patch = { [secId]: { ...tmSec, _confirmed: true } };
    setAdvData(p => ({ ...p, [secId]: { ...(p[secId] || {}), ...tmSec, _confirmed: true } }));
    patchAdvance(patch);
    logProActivity(`Confirmed ${sec.label}`);
  };

  // AP-1: Submit section status to TM (marks as "submitted" in advance_section_status)
  // Also calls confirmSection for backward compat (locks the section fields locally)
  const submitSection = (secId) => {
    const sec = visibleSections.find(s => s.id === secId);
    if (!sec) return;
    const ts = new Date().toISOString();
    const statusUpdate = { status: "submitted", updated_at: ts };
    setSectionStatus(prev => ({ ...prev, [secId]: { ...(prev[secId] || {}), ...statusUpdate } }));
    patchAdvance({ _section_status: { [secId]: statusUpdate } });
    confirmSection(secId);
    logProActivity(`Submitted ${sec.label}`);
  };

  const uploadPromoterFile = async (file, label) => {
    if (!file) return;
    setProFileUploading(true);
    try {
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
      // Get a signed upload URL from the server
      const sigRes = await fetch(`/api/advance?token=${token}&action=sign&filename=${encodeURIComponent(safeName)}`);
      const { signedUrl, path, publicUrl, error: sigErr } = await sigRes.json();
      if (sigErr) throw new Error(sigErr);
      // Upload directly to Supabase Storage via signed URL
      await fetch(signedUrl, { method: "PUT", body: file, headers: { "Content-Type": file.type || "application/octet-stream" } });
      const newFile = { id: crypto.randomUUID(), name: file.name, label, url: publicUrl, path };
      const newFiles = [...(advData._files || []), newFile];
      setAdvData(p => ({ ...p, _files: newFiles }));
      patchAdvance({ _files: newFiles });
    } catch (e) { console.error("Promoter file upload failed:", e); }
    setProFileUploading(false);
  };

  const removePromoterFile = (fileId) => {
    const newFiles = (advData._files || []).filter(f => f.id !== fileId);
    setAdvData(p => ({ ...p, _files: newFiles }));
    patchAdvance({ _files: newFiles });
  };

  // ── Promoter assistant (portal chat) ────────────────────────────────────────
  // Fresh snapshot of ONLY what the promoter can see: visible sections/fields
  // (tmOnly fields are already filtered out of visibleSections), TM suggestions,
  // their current answers, and section status. This is the entire universe the
  // assistant knows — it never queries the database.
  const getPortalData = () => ({
    meta: { artist: artist?.name || null, venue: venue?.name || null, city: venue?.city || null, date: show?.date || null },
    submitted: !!advData._submitted,
    sections: visibleSections.map(sec => ({
      id: sec.id,
      label: sec.label,
      status: (sectionStatus[sec.id] || {}).status || ((advData[sec.id] || {})._confirmed ? "confirmed" : "pending"),
      fields: sec.fields.map(f => {
        const clip = (v) => typeof v === "string" ? v.slice(0, 300) : v;
        return {
          id: f.id, label: f.label, type: f.type,
          ...(tmData[sec.id]?.[f.id] !== undefined && tmData[sec.id]?.[f.id] !== "" ? { tm: clip(tmData[sec.id][f.id]) } : {}),
          ...(advData[sec.id]?.[f.id] !== undefined && advData[sec.id]?.[f.id] !== "" ? { value: clip(advData[sec.id][f.id]) } : {}),
        };
      }),
    })),
  });

  // Apply confirmed field updates through the portal's normal save path —
  // one combined PATCH (patchAdvance debounces; per-section calls would race).
  const applyPortalUpdates = (updates) => {
    const bySec = {};
    for (const u of updates) (bySec[u.section] = bySec[u.section] || {})[u.field] = u.value;
    const combined = {};
    for (const [secId, flds] of Object.entries(bySec)) {
      combined[secId] = { ...(advData[secId] || {}), ...flds };
    }
    setAdvData(p => {
      const next = { ...p };
      for (const [secId, flds] of Object.entries(bySec)) {
        next[secId] = { ...(next[secId] || {}), ...flds };
      }
      return next;
    });
    patchAdvance(combined);
  };

  const portalChatAction = async (action) => {
    try {
      if (advData._submitted) return { ok: false, message: "The advance is locked — use 'Re-open for editing' first." };
      switch (action.tool) {
        case "update_fields": {
          const updates = action.input.updates || [];
          applyPortalUpdates(updates);
          logProActivity(`Updated ${updates.length} field${updates.length === 1 ? "" : "s"} via assistant`);
          return { ok: true, message: `${updates.length} field${updates.length === 1 ? "" : "s"} updated.` };
        }
        case "confirm_sections": {
          const ids = action.input.section_ids || [];
          for (const id of ids) submitSection(id);
          return { ok: true, message: `${ids.length} section${ids.length === 1 ? "" : "s"} confirmed — the tour manager will see them as reviewed.` };
        }
        default:
          return { ok: false, message: `Unknown action: ${action.tool}` };
      }
    } catch (e) {
      return { ok: false, message: e.message || "Action failed" };
    }
  };

  return (
    <div style={{ minHeight: "100vh", background: P.bg, color: P.text, fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif" }}>
      <style>{`
        *{box-sizing:border-box;margin:0;padding:0;}
        .ap-hdr{background:${P.hdrBg};border-bottom:1px solid ${P.bdr};padding:16px 28px;display:flex;align-items:center;justify-content:space-between;}
        .ap-logo{font-family:var(--font-display);font-weight:700;font-size:20px;letter-spacing:4px;color:${P.logoClr};}
        .ap-saved{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;color:${P.savedClr};letter-spacing:1px;transition:opacity 0.4s;}
        .ap-hero{padding:22px 28px 20px;border-bottom:1px solid ${P.heroBdr};}
        .ap-artist{font-size:20px;font-weight:700;color:${P.text};margin-bottom:4px;}
        .ap-meta{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;color:${P.textMut};letter-spacing:0.3px;}
        .ap-body{max-width:720px;margin:0 auto;padding:22px 28px;display:flex;flex-direction:column;gap:10px;}
        .ap-notice{background:${P.notBg};border:1px solid ${P.notBdr};border-radius:8px;padding:10px 14px;font-size:12px;color:${P.notClr};line-height:1.55;}
        .ap-sec{background:${P.secBg};border:1px solid ${P.bdr};border-radius:8px;overflow:hidden;}
        .ap-sec-hdr{padding:12px 16px;display:flex;align-items:center;cursor:pointer;user-select:none;gap:10px;transition:background 0.15s;}
        .ap-sec-hdr:hover{background:${P.secHov};}
        .ap-sec-title{font-size:11px;font-weight:600;color:${P.textMid};text-transform:uppercase;letter-spacing:1.5px;font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;}
        .ap-sec-meta{display:flex;align-items:center;gap:8px;margin-left:auto;}
        .ap-sec-count{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;color:${P.textFnt};letter-spacing:0.5px;}
        .ap-sec-count.has{color:${P.green};}
        .ap-chev{font-size:11px;color:${P.textFnt};transition:transform 0.28s cubic-bezier(0.4,0,0.2,1);line-height:1;flex-shrink:0;}
        .ap-chev.open{transform:rotate(90deg);}
        .ap-flds-wrap{display:grid;grid-template-rows:0fr;transition:grid-template-rows 0.3s cubic-bezier(0.4,0,0.2,1);}
        .ap-flds-wrap.open{grid-template-rows:1fr;}
        .ap-flds-inner{overflow:hidden;}
        .ap-flds{padding:14px 16px 16px;display:flex;flex-direction:column;gap:10px;border-top:1px solid ${P.fldsTop};}
        .ap-time-grid{display:grid;grid-template-columns:1fr 1fr;gap:8px;}
        .ap-check-grid{display:grid;grid-template-columns:1fr 1fr;gap:6px;}
        .ap-yn-grid{display:grid;grid-template-columns:1fr 1fr;gap:6px;}
        .ap-yn-item{display:flex;flex-direction:column;gap:5px;padding:7px 10px;background:${P.ckBg};border:1px solid ${P.ckBdr};border-radius:8px;}
        .ap-yn-item.tm-pre{border-color:${P.tmPreBdr};background:${P.tmPre};}
        .ap-yn-item.amended{border-color:${P.amdBdr};background:${P.amd};}
        .ap-yn-lbl{font-size:11px;color:${P.textMut};font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;letter-spacing:0.3px;display:flex;align-items:center;gap:4px;}
        .ap-yn-lbl.amend-lbl{color:${P.amber};}
        .ap-yn-btns{display:flex;gap:4px;}
        .ap-yn-btn{flex:1;padding:5px 0;border-radius:4px;border:1px solid ${P.ckBdr};background:transparent;color:${P.textMut};font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;letter-spacing:0.5px;cursor:pointer;transition:all 0.12s;}
        .ap-yn-btn.yes.on{background:${P.ynYesBg};border-color:${P.ynYesBdr};color:${P.ynYesClr};font-weight:600;}
        .ap-yn-btn.no.on{background:${P.ynNoBg};border-color:${P.ynNoBdr};color:${P.ynNoClr};font-weight:600;}
        .ap-thread{margin-top:6px;display:flex;flex-direction:column;gap:4px;}
        .ap-thread-msg{display:flex;align-items:flex-start;gap:7px;padding:5px 8px;border-radius:4px;line-height:1.45;}
        .ap-thread-msg.tm{background:${P.tmPre};border:1px solid ${P.tmPreBdr};}
        .ap-thread-msg.pro{background:${P.amd};border:1px solid ${P.amdBdr};}
        .ap-thread-from{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;font-weight:700;letter-spacing:0.8px;text-transform:uppercase;flex-shrink:0;padding-top:1px;}
        .ap-thread-msg.tm .ap-thread-from{color:${P.green};}
        .ap-thread-msg.pro .ap-thread-from{color:${P.amber};}
        .ap-thread-text{flex:1;font-size:11px;color:${P.textMid};}
        .ap-thread-clear{background:none;border:none;color:${P.textFnt};cursor:pointer;font-size:11px;padding:0;margin-left:auto;flex-shrink:0;line-height:1;}
        .ap-thread-inp{width:100%;box-sizing:border-box;background:${P.inpBg};border:1px solid ${P.ckBdr};border-radius:4px;padding:5px 8px;color:${P.textMut};font-size:11px;font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;outline:none;transition:border-color 0.15s;}
        .ap-thread-inp:focus{border-color:${P.inpFocus};}
        .ap-thread-inp::placeholder{color:${P.textVFnt};}
        .ap-row{display:flex;flex-direction:column;gap:4px;}
        .ap-lbl{font-size:11px;color:${P.textMut};font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;letter-spacing:0.3px;}
        .ap-inp{width:100%;background:${P.inpBg};border:1px solid ${P.inpBdr};border-radius:8px;padding:7px 10px;color:${P.text};font-size:14px;font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;outline:none;transition:border-color 0.15s;}
        .ap-inp:focus{border-color:${P.inpFocus};}
        .ap-ta{resize:vertical;min-height:68px;}
        .ap-ck-row{display:flex;align-items:center;gap:8px;padding:6px 10px;background:${P.ckBg};border:1px solid ${P.ckBdr};border-radius:8px;cursor:pointer;}
        .ap-ck-row:hover{border-color:${P.ckBdrHov};}
        .ap-ck-box{width:16px;height:16px;border-radius:4px;border:1.5px solid ${P.ckBoxBdr};background:${P.ckBoxBg};display:flex;align-items:center;justify-content:center;flex-shrink:0;transition:all 0.12s;}
        .ap-ck-box.on{background:${P.ckOn};border-color:${P.ckOn};}
        .ap-ck-lbl{font-size:12px;color:${P.textMut};}
        .ap-ck-lbl.on{color:${P.text};}
        .ap-inp.tm-pre{border-color:${P.tmPreBdr};background:${P.tmPre};}
        .ap-inp.amended{border-color:${P.amdBdr};color:${P.amber};background:${P.amd};}
        .ap-inp.amended:focus{border-color:${P.amber};}
        .ap-ck-row.tm-pre{border-color:${P.tmPreBdr};background:${P.tmPre};}
        .ap-ck-row.amended{border-color:${P.amdBdr};background:${P.amd};}
        .ap-ck-box.amend-box{background:${P.amdBdr};border-color:${P.amdBdr};}
        .ap-lbl.amend-lbl{color:${P.amber};}
        .ap-tm-badge{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;color:${P.tmBadgeClr};background:${P.tmBadgeBg};border:1px solid ${P.tmBadgeBdr};border-radius:4px;padding:1px 4px;margin-left:5px;letter-spacing:0.5px;}
        .ap-amend-badge{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;color:${P.amdBadgeClr};background:${P.amdBadgeBg};border:1px solid ${P.amdBadgeBdr};border-radius:4px;padding:1px 5px;margin-left:5px;letter-spacing:0.5px;}
        .ap-ck-lbl.amend-lbl{color:${P.amber};}
        .ap-sec.has-amend{border-color:${P.hasAmdBdr};}
        .ap-sec.confirmed{border-color:${P.confBdr};}
        .ap-sec.confirmed .ap-sec-hdr{background:${P.confHdr};}
        .ap-noted{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;color:${P.notedClr};letter-spacing:0.5px;}
        .ap-amended-badge{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;color:${P.amber};background:${P.amdBadgeBg};border:1px solid ${P.amdBadgeBdr};border-radius:4px;padding:1px 6px;letter-spacing:0.3px;}
        .ap-noted-btn{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;color:${P.notedBtn};background:${P.notedBtnBg};border:1px solid ${P.notedBtnBdr};border-radius:4px;padding:3px 8px;cursor:pointer;letter-spacing:0.5px;transition:all 0.12s;}
        .ap-noted-btn:hover{color:${P.notedHov};border-color:${P.notedHovBdr};background:${P.notedHovBg};}
        .ap-foot{text-align:center;padding:28px 16px;font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;color:${P.foot};letter-spacing:1px;}
        .ap-sec.flagged{border-color:${P.amdBdr};}
        .ap-sec.flagged .ap-sec-hdr{background:${P.amd};}
        .ap-flag-banner{background:${P.amd};border:1px solid ${P.amdBdr};border-radius:8px;padding:8px 12px;display:flex;align-items:flex-start;gap:8px;margin-bottom:4px;}
        .ap-flag-from{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;font-weight:700;letter-spacing:0.8px;text-transform:uppercase;color:${P.amber};flex-shrink:0;padding-top:1px;}
        .ap-flag-text{font-size:12px;color:${P.amber};line-height:1.5;flex:1;}
        .ap-flag-badge{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;color:${P.amber};background:${P.amdBadgeBg};border:1px solid ${P.amdBadgeBdr};border-radius:4px;padding:1px 6px;letter-spacing:0.5px;font-weight:700;}
        .ap-submitted-badge{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;color:${P.green};letter-spacing:0.5px;}
        .ap-progress-wrap{background:${P.secBg};border:1px solid ${P.bdr};border-radius:8px;padding:12px 16px;display:flex;flex-direction:column;gap:6px;}
        .ap-progress-row{display:flex;align-items:center;justify-content:space-between;}
        .ap-progress-lbl{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;color:${P.textMut};letter-spacing:0.5px;}
        .ap-progress-pct{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;font-weight:500;color:${P.green};}
        .ap-progress-track{height:5px;background:${P.inpBg};border-radius:4px;overflow:hidden;}
        .ap-progress-fill{height:100%;border-radius:4px;background:${P.green};transition:width 0.4s ease;}
        .ap-submit-wrap{display:flex;flex-direction:column;align-items:center;gap:10px;padding:4px 0 8px;}
        .ap-submit-btn{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;letter-spacing:1.5px;color:${P.bg};background:${P.green};border:none;border-radius:8px;padding:12px 32px;cursor:pointer;transition:opacity 0.15s;font-weight:600;}
        .ap-submit-btn:hover{opacity:0.85;}
        .ap-submit-btn:disabled{opacity:0.5;cursor:default;}
        .ap-submit-banner{background:${P.notBg};border:1px solid ${P.notBdr};border-radius:8px;padding:11px 16px;font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;color:${P.notClr};letter-spacing:0.3px;text-align:center;}
        .ap-subhdr{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:1.2px;padding:8px 0 4px;margin-top:6px;border-top:1px solid ${P.bdrSub};color:${P.textFnt};}
        .ap-subhdr:first-child{margin-top:0;border-top:none;padding-top:0;}
        .ap-subhdr.arranged{color:${P.green};}
        .ap-subhdr.needed{color:${P.amber};}
        @media(max-width:600px){.ap-body,.ap-hero{padding:16px;}.ap-hdr{padding:14px 16px;}.ap-time-grid,.ap-check-grid{grid-template-columns:1fr;}}
      `}</style>

      <div className="ap-hdr">
        <div className="ap-logo">ADVANCD</div>
        <div className="ap-saved" style={{ opacity: saved ? 1 : 0 }}>✓ SAVED</div>
      </div>

      {/* Promoter assistant — token-scoped chat over this advance only */}
      <ChatPanel
        portalToken={token}
        getPortalData={getPortalData}
        onExecuteAction={portalChatAction}
        mobile={typeof window !== "undefined" && window.innerWidth < 768}
        context={{ tourName: tour?.name || null, artistName: artist?.name || null }}
        suggestions={[
          "What do you still need from me?",
          "Confirm all sections",
          "What time is load-in?",
          "What does the band need for hospitality?",
        ]}
      />

      {/* Save-failure banner — sticky so it can't be missed mid-form */}
      {saveFailed && (
        <div style={{
          position: "sticky", top: 0, zIndex: 50,
          background: P.ynNoBg, borderBottom: `1px solid ${P.ynNoBdr}`,
          padding: "10px 20px", display: "flex", alignItems: "center", gap: 10,
          fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
        }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={P.ynNoClr} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
            <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
          <div style={{ fontSize: 12, color: P.ynNoClr }}>
            <strong>Your changes are not saving.</strong> Check your connection and keep this page open — recent edits will retry when you type again. If this persists, contact the tour manager for a fresh link.
          </div>
        </div>
      )}

      <div className="ap-hero">
        <div className="ap-artist">{artist?.name || "Show Advance"}</div>
        <div className="ap-meta">
          {[show?.date ? fmtDate(show.date) : null, venue?.name,
            venue?.address_street,
            [venue?.city, venue?.address_state].filter(Boolean).join(", ")]
            .filter(Boolean).join(" · ")}
        </div>
        <div style={{ marginTop: 14, fontSize: 14, color: P.textMid, lineHeight: 1.65, maxWidth: 560, textAlign: "center", margin: "14px auto 0" }}>
          Thanks for working with us on this advance. We've pre-filled everything we have on our end — please review, confirm, or update any details that differ. Sections marked <span style={{ color: P.green, fontWeight: 600 }}>✦ From us</span> are our suggestions; anything under <span style={{ color: P.amber, fontWeight: 600 }}>✎ We need from you</span> is a question we still need answered.
        </div>
      </div>

      <div className="ap-body">
        {advData._submitted ? (
          <div className="ap-submit-banner">
            <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>✓ Advance submitted</div>
            {advData._submittedAt && (
              <div style={{ fontSize: 11, opacity: 0.75 }}>
                Submitted {new Date(advData._submittedAt).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })} at {new Date(advData._submittedAt).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}
              </div>
            )}
            <div style={{ fontSize: 11, marginTop: 6, opacity: 0.7 }}>Your responses are locked. Need to make a change? <button onClick={reopenAdvance} style={{ background: "none", border: "none", color: "inherit", textDecoration: "underline", cursor: "pointer", padding: 0, fontSize: "inherit", fontFamily: "inherit" }}>Re-open for editing</button></div>
          </div>
        ) : (
          <div className="ap-notice">
            Your responses save automatically and are instantly visible to the tour manager. Once you've reviewed a section — whether or not you've made changes — hit <strong>Noted ✓</strong> to confirm it. This is how the tour manager knows each section is done on your end and is the primary way the advance moves forward. Leave a comment on any field if something needs context or a heads up.
          </div>
        )}

        {totalFields > 0 && (
          <div className="ap-progress-wrap">
            <div className="ap-progress-row">
              <span className="ap-progress-lbl">COMPLETION</span>
              <span className="ap-progress-pct">{filledFields}/{totalFields} fields · {progress}%</span>
            </div>
            <div className="ap-progress-track">
              <div className="ap-progress-fill" style={{ width: `${progress}%` }} />
            </div>
          </div>
        )}

        {/* Touring Party — per-show override if set, else people_profiles (PP-1), else legacy crewManifest */}
        {(() => {
          const showOverride = show?.advance_tm_data?._showCrew?.entries;
          const crew = Array.isArray(showOverride) ? showOverride : (info?.tourParty?.length ? info.tourParty : (tour?.advance_config?.crewManifest || []));
          if (!crew.length) return null;
          const isOpen = !!openSecs["_crew"];
          const locked = !!advData._submitted;
          const crewConfirmed = !!(advData._crew?._confirmed);
          const confirmCrew = () => {
            setAdvData(p => ({ ...p, _crew: { ...(p._crew || {}), _confirmed: true } }));
            patchAdvance({ _crew: { ...(advData._crew || {}), _confirmed: true } });
            logProActivity("Confirmed Touring Party");
          };
          return (
            <div className={`ap-sec ${crewConfirmed ? "confirmed" : ""}`}>
              <div className="ap-sec-hdr" onClick={() => toggleSec("_crew")}>
                <span className={`ap-chev ${isOpen ? "open" : ""}`}>▶</span>
                <span className="ap-sec-title">Touring Party</span>
                <div className="ap-sec-meta">
                  {crewConfirmed
                    ? <span className="ap-noted">✓ Noted</span>
                    : <span className="ap-sec-count has">{crew.length} member{crew.length !== 1 ? "s" : ""}</span>}
                  {!crewConfirmed && !locked && (
                    <button className="ap-noted-btn" onClick={e => { e.stopPropagation(); confirmCrew(); }}
                      title="Confirm you've received the touring party list">
                      Noted ✓
                    </button>
                  )}
                </div>
              </div>
              <div className={`ap-flds-wrap ${isOpen ? "open" : ""}`}>
                <div className="ap-flds-inner">
                  <div className="ap-flds" style={{ gap: 0, padding: "10px 16px 14px" }}>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 0, marginBottom: 6 }}>
                      {["Name", "Role", "Phone", "Email"].map(h => (
                        <span key={h} style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: P.textFnt, letterSpacing: 0.8, textTransform: "uppercase" }}>{h}</span>
                      ))}
                    </div>
                    {crew.map((p, i) => (
                      <div key={p.id || i} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 4, padding: "6px 0", borderTop: `1px solid ${P.bdrSub}` }}>
                        <span style={{ fontSize: 12, color: P.text, fontWeight: 500 }}>{p.name || "—"}</span>
                        <span style={{ fontSize: 11, color: P.textMut }}>{p.role || "—"}</span>
                        <span style={{ fontSize: 11, color: P.textMut }}>{p.phone || "—"}</span>
                        <a href={`mailto:${p.email}`} style={{ fontSize: 11, color: p.email ? P.amber : P.textMut, textDecoration: "none" }}>{p.email || "—"}</a>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

        {visibleSections.map(sec => {
          const locked    = !!advData._submitted;
          const isOpen    = !!openSecs[sec.id];
          const sd        = advData[sec.id] || {};
          const tmSd      = tmData[sec.id]  || {};
          const confirmed = !!sd._confirmed;

          // Field-type buckets
          const timeFields    = sec.fields.filter(f => f.type === "time");
          const ynFields      = sec.fields.filter(f => f.type === "yesno");
          const textFields    = sec.fields.filter(f => f.type === "text");
          const contactFields = sec.fields.filter(f => f.type === "contact");

          // Count fields the promoter has touched (amended from TM or entered fresh)
          const cnt = secCount(sec);

          // Any field the promoter changed from TM suggestion?
          const hasAmend = sec.fields.some(f => {
            const tv = tmSd[f.id]; const pv = sd[f.id];
            if (f.type === "contact") {
              const tmC = (typeof tv === "object" && tv) ? tv : {};
              const proC = (typeof pv === "object" && pv) ? pv : {};
              return ["name","phone","email"].some(sub => proC[sub] !== undefined && proC[sub] !== "" && proC[sub] !== tmC[sub]);
            }
            if (f.type === "time") return pv !== undefined && pv !== "" && pv !== null && !timeEq(pv, tv);
            return pv !== undefined && pv !== "" && pv !== null && pv !== tv;
          });

          // Render a single field — shows TM suggestion style + amber amendment style
          const renderField = (f) => {
            const rawTmVal = tmSd[f.id];
            // For hospitality dietary and parking vehicle fields, fall back to tour-level
            // defaults so promoter always sees them pre-filled from the TM's tour setup
            const tmVal = (!rawTmVal && (
              (sec.id === "hospitality" && f.id === "dietary") ||
              (sec.id === "parking" && f.id === "vehicle")
            ))
              ? (sec.id === "hospitality"
                  ? (tour?.advance_config?.dietaryNotes || rawTmVal)
                  : (tour?.advance_config?.vehicleNotes || rawTmVal))
              : rawTmVal;
            const proVal  = sd[f.id];
            const hasTm   = tmVal !== undefined && tmVal !== "" && tmVal !== false;
            const amended = proVal !== undefined && proVal !== null && proVal !== "" &&
              (f.type === "time" ? !timeEq(proVal, tmVal) : proVal !== tmVal);

            // Thread — appears on amended fields or when either side has left a note
            const tmNote  = (tmSd._comments || {})[f.id];
            const proNote = (sd._comments   || {})[f.id];
            const showThread = amended || tmNote || proNote;
            const thread = showThread ? (
              <div className="ap-thread">
                {tmNote && (
                  <div className="ap-thread-msg tm">
                    <span className="ap-thread-from">TM</span>
                    <span className="ap-thread-text">{tmNote.text}</span>
                  </div>
                )}
                {proNote ? (
                  <div className="ap-thread-msg pro">
                    <span className="ap-thread-from">You</span>
                    <span className="ap-thread-text">{proNote.text}</span>
                    {!locked && <button className="ap-thread-clear" title="Remove note" onClick={() => clearComment(sec.id, f.id)}>✕</button>}
                  </div>
                ) : !locked ? (
                  <input className="ap-thread-inp" placeholder="Add context…"
                    onBlur={e => { if (e.target.value.trim()) { saveComment(sec.id, f.id, e.target.value.trim()); e.target.value = ""; } }}
                    onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); e.target.blur(); } }} />
                ) : null}
              </div>
            ) : null;

            if (f.type === "yesno") {
              const hasTmYN = tmVal === true || tmVal === false;
              const displayVal = proVal !== undefined ? proVal : (hasTmYN ? tmVal : undefined);
              return (
                <div key={f.id} className={`ap-yn-item ${amended ? "amended" : hasTmYN && !amended ? "tm-pre" : ""}`}>
                  <div className={`ap-yn-lbl ${amended ? "amend-lbl" : ""}`}>
                    {f.label}
                    {hasTmYN && !amended && <span className="ap-tm-badge">✦ TM</span>}
                    {amended && <span className="ap-amend-badge">↩ amended</span>}
                  </div>
                  <div className="ap-yn-btns">
                    <button className={`ap-yn-btn yes ${displayVal === true ? "on" : ""}`}
                      onClick={() => !locked && saveField(sec.id, f.id, true)}
                      style={ locked ? { cursor: "default", opacity: 0.7 } : {} }>Yes</button>
                    <button className={`ap-yn-btn no ${displayVal === false ? "on" : ""}`}
                      onClick={() => !locked && saveField(sec.id, f.id, false)}
                      style={ locked ? { cursor: "default", opacity: 0.7 } : {} }>No</button>
                  </div>
                  {thread}
                </div>
              );
            }

            // Contact field (name + phone + email)
            if (f.type === "contact") {
              const tmC  = (typeof tmVal  === "object" && tmVal)  ? tmVal  : {};
              const proC = (typeof proVal === "object" && proVal) ? proVal : {};
              const hasTmC   = !!(tmC.name || tmC.phone || tmC.email);
              const subAmd   = sub => proC[sub] !== undefined && proC[sub] !== "" && proC[sub] !== tmC[sub];
              const anyAmd   = subAmd("name") || subAmd("phone") || subAmd("email");
              const getVal   = sub => proC[sub] !== undefined ? proC[sub] : (tmC[sub] || "");
              const saveSub  = (sub, val) => {
                const cur = (typeof sd[f.id] === "object" && sd[f.id]) ? sd[f.id] : {};
                saveField(sec.id, f.id, { ...cur, [sub]: val });
              };
              return (
                <Fragment key={f.id}>
                  <div className="ap-row">
                    <div className={`ap-lbl ${anyAmd ? "amend-lbl" : ""}`}>
                      {f.label}
                      {hasTmC && !anyAmd && <span className="ap-tm-badge">✦ TM</span>}
                      {anyAmd && <span className="ap-amend-badge">↩ amended</span>}
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 5 }}>
                      {[["name","Name"],["phone","Phone"],["email","Email"]].map(([sub, ph]) => (
                        <input key={sub}
                          className={`ap-inp ${subAmd(sub) ? "amended" : hasTmC && tmC[sub] ? "tm-pre" : ""}`}
                          placeholder={tmC[sub] || ph}
                          value={getVal(sub)}
                          readOnly={locked}
                          onChange={e => !locked && saveSub(sub, e.target.value)}
                        />
                      ))}
                    </div>
                  </div>
                  {thread}
                </Fragment>
              );
            }

            // Percent field — numeric input with auto-% suffix
            if (f.type === "percent") {
              const rawPct     = proVal !== undefined && proVal !== "" ? proVal : (tmVal || "");
              const numericPct = String(rawPct).replace(/%/g, "").trim();
              return (
                <Fragment key={f.id}>
                  <div className="ap-row">
                    <div className={`ap-lbl ${amended ? "amend-lbl" : ""}`}>
                      {f.label}
                      {hasTm && !amended && <span className="ap-tm-badge">✦ TM</span>}
                      {amended && (
                        <span className="ap-amend-badge">↩ amended
                          {tmVal ? <span style={{ color: P.textMut }}> · TM: {String(tmVal).replace(/%/g,"").trim()}%</span> : null}
                        </span>
                      )}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 0, position: "relative" }}>
                      <input
                        className={`ap-inp ${amended ? "amended" : hasTm && !amended ? "tm-pre" : ""}`}
                        placeholder={hasTm ? (String(tmVal).replace(/%/g,"").trim() || "0") : "0"}
                        value={numericPct}
                        readOnly={locked}
                        inputMode="decimal"
                        style={{ paddingRight: 22, width: "100%", boxSizing: "border-box" }}
                        onChange={e => {
                          const v = e.target.value.replace(/[^0-9.]/g, "");
                          !locked && saveField(sec.id, f.id, v ? `${v}%` : "");
                        }}
                      />
                      <span style={{ position: "absolute", right: 8, fontSize: 14, color: P.textMut, pointerEvents: "none", userSelect: "none" }}>%</span>
                    </div>
                  </div>
                  {thread}
                </Fragment>
              );
            }

            // Wi-Fi networks — array of { network, password } pairs
            if (f.type === "wifi_networks") {
              const rawNets = sd[f.id] !== undefined ? sd[f.id] : (tmSd[f.id] || []);
              const nets = Array.isArray(rawNets) ? rawNets : [];
              const hasTmNets = Array.isArray(tmSd[f.id]) && tmSd[f.id].some(n => n.network);
              const updateNets = (newNets) => saveField(sec.id, f.id, newNets);
              return (
                <Fragment key={f.id}>
                  <div style={{ marginBottom: 16 }}>
                    <div className="ap-lbl" style={{ marginBottom: 8 }}>
                      {f.label}
                      {hasTmNets && !nets.length && <span className="ap-tm-badge">✦ TM</span>}
                    </div>
                    {nets.map((net, i) => (
                      <div key={i} style={{ display: "flex", gap: 8, marginBottom: 8, alignItems: "center" }}>
                        <input
                          className="ap-inp"
                          placeholder="Network name"
                          value={net.network || ""}
                          readOnly={locked}
                          style={{ flex: 1 }}
                          onChange={e => { if (!locked) updateNets(nets.map((n, j) => j === i ? { ...n, network: e.target.value } : n)); }}
                        />
                        <input
                          className="ap-inp"
                          placeholder="Password"
                          value={net.password || ""}
                          readOnly={locked}
                          style={{ flex: 1 }}
                          onChange={e => { if (!locked) updateNets(nets.map((n, j) => j === i ? { ...n, password: e.target.value } : n)); }}
                        />
                        {!locked && (
                          <button
                            onClick={() => updateNets(nets.filter((_, j) => j !== i))}
                            style={{ background: "none", border: "none", cursor: "pointer", color: P.textMut, padding: "0 4px", fontSize: 16, lineHeight: 1, flexShrink: 0 }}
                            title="Remove network">
                            ✕
                          </button>
                        )}
                      </div>
                    ))}
                    {nets.length === 0 && hasTmNets && (
                      <div style={{ fontSize: 11, color: P.textMut, marginBottom: 8, fontStyle: "italic" }}>
                        TM has pre-filled networks — edit below to confirm or update.
                      </div>
                    )}
                    {!locked && (
                      <button
                        onClick={() => updateNets([...nets, { network: "", password: "" }])}
                        style={{ fontFamily: "'Helvetica Neue',Helvetica,Arial,sans-serif", fontSize: 11, color: P.amber, background: "none", border: `1px solid ${P.bdr}`, borderRadius: 8, padding: "5px 12px", cursor: "pointer", letterSpacing: 0.3 }}>
                        + Add network
                      </button>
                    )}
                  </div>
                  {thread}
                </Fragment>
              );
            }

            // Text / time field
            const rawDisplayVal = proVal !== undefined && proVal !== "" ? proVal : (tmVal || "");
            const displayVal    = f.type === "time" ? to12h(rawDisplayVal) : rawDisplayVal;
            const tmValDisplay  = f.type === "time" ? to12h(tmVal) : tmVal;
            // Long-form text fields (notes, instructions, descriptions) get a textarea
            const isLongText = f.type === "text" && (
              f.id.endsWith("_notes") ||
              ["dietary","will_call","loading_zone","bus_parking","gr_access","gr_layout","gt_route"].includes(f.id)
            );
            return (
              <Fragment key={f.id}>
                <div className="ap-row">
                  <div className={`ap-lbl ${amended ? "amend-lbl" : ""}`}>
                    {f.label}
                    {hasTm && !amended && <span className="ap-tm-badge">✦ TM</span>}
                    {amended && (
                      <span className="ap-amend-badge">↩ amended
                        {tmVal ? <span style={{ color: P.textMut }}> · TM: {tmValDisplay}</span> : null}
                      </span>
                    )}
                  </div>
                  {isLongText ? (
                    <textarea
                      className={`ap-inp ${amended ? "amended" : hasTm && !amended ? "tm-pre" : ""}`}
                      placeholder={hasTm ? tmValDisplay : "—"}
                      value={displayVal}
                      readOnly={locked}
                      rows={2}
                      style={{ resize: "vertical", lineHeight: 1.5 }}
                      onChange={e => !locked && saveField(sec.id, f.id, e.target.value)}
                    />
                  ) : f.type === "time" ? (
                    <TimeInput12
                      value={displayVal}
                      readOnly={locked}
                      onChange={v => !locked && saveField(sec.id, f.id, v)}
                      className={`ap-inp ${amended ? "amended" : hasTm && !amended ? "tm-pre" : ""}`}
                      placeholder={hasTm ? tmValDisplay : "3:00"}
                      toggleStyle={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", background: P.inpBg, color: P.textMut, border: `1px solid ${P.inpBdr}`, borderRadius: "0 6px 6px 0", opacity: locked ? 0.7 : 1 }}
                    />
                  ) : (
                    <input
                      className={`ap-inp ${amended ? "amended" : hasTm && !amended ? "tm-pre" : ""}`}
                      placeholder={hasTm ? tmValDisplay : "—"}
                      value={displayVal}
                      readOnly={locked}
                      onChange={e => !locked && saveField(sec.id, f.id, e.target.value)}
                    />
                  )}
                </div>
                {thread}
              </Fragment>
            );
          };

          // Split fields into TM-pre-filled ("From us") vs. blank ("We need from you")
          const hasTmForField = (f) => {
            const tv = tmSd[f.id];
            if (f.type === 'yesno') return tv === true || tv === false;
            if (f.type === 'wifi_networks') return Array.isArray(tv) && tv.some(n => n.network);
            if (f.type === 'contact') {
              const c = (typeof tv === 'object' && tv) ? tv : {};
              return !!(c.name || c.phone || c.email);
            }
            // Tour-level fallbacks for hospitality dietary + parking vehicle
            if ((sec.id === 'hospitality' && f.id === 'dietary') || (sec.id === 'parking' && f.id === 'vehicle')) {
              const fallback = sec.id === 'hospitality'
                ? tour?.advance_config?.dietaryNotes
                : tour?.advance_config?.vehicleNotes;
              return !!(tv !== undefined && tv !== '' && tv !== null) || !!fallback;
            }
            return tv !== undefined && tv !== '' && tv !== null;
          };
          const arrangedFields = sec.fields.filter(hasTmForField);
          const neededFields   = sec.fields.filter(f => !hasTmForField(f));
          const showSubhdrs    = arrangedFields.length > 0 && neededFields.length > 0;

          const renderFieldGroup = (fields) => {
            const tF = fields.filter(f => f.type === "time");
            const yF = fields.filter(f => f.type === "yesno");
            const cF = fields.filter(f => f.type === "contact");
            const xF = fields.filter(f => f.type === "text" || f.type === "percent");
            const wF = fields.filter(f => f.type === "wifi_networks");
            return (
              <Fragment>
                {tF.length > 0 && <div className="ap-time-grid">{tF.map(renderField)}</div>}
                {yF.length > 0 && <div className="ap-yn-grid">{yF.map(renderField)}</div>}
                {cF.map(renderField)}
                {xF.map(renderField)}
                {wF.map(renderField)}
              </Fragment>
            );
          };

          // AP-1: per-section status from TM
          const secStat      = sectionStatus[sec.id] || {};
          const isFlagged    = secStat.status === "flagged";
          const isSubmitted  = secStat.status === "submitted";
          const isTmConfirmed = secStat.status === "confirmed";
          const isDone       = confirmed || isTmConfirmed;

          return (
            <div key={sec.id} className={`ap-sec ${hasAmend ? "has-amend" : isDone ? "confirmed" : isFlagged ? "flagged" : ""}`}>
              <div className="ap-sec-hdr" onClick={() => toggleSec(sec.id)}>
                <span className={`ap-chev ${isOpen ? "open" : ""}`}>▶</span>
                <span className="ap-sec-title">{sec.label}</span>
                <div className="ap-sec-meta">
                  {isDone && !hasAmend && <span className="ap-noted">✓ Noted</span>}
                  {isFlagged && <span className="ap-flag-badge">⚑ Needs attention</span>}
                  {isSubmitted && !isFlagged && !isDone && <span className="ap-submitted-badge">✓ Submitted</span>}
                  {hasAmend  && <span className="ap-amended-badge">↩ amended</span>}
                  {!isDone && !hasAmend && !isSubmitted && !isFlagged && (
                    <span className={`ap-sec-count ${cnt > 0 ? "has" : ""}`}>{cnt}/{sec.fields.length}</span>
                  )}
                  {!isDone && !locked && (
                    <button className="ap-noted-btn" onClick={e => { e.stopPropagation(); submitSection(sec.id); }}
                      title="Confirm this section — sends a 'Noted' reply to your tour manager">
                      {isFlagged ? "Resolved ✓" : "Noted ✓"}
                    </button>
                  )}
                </div>
              </div>
              <div className={`ap-flds-wrap ${isOpen ? "open" : ""}`}>
                <div className="ap-flds-inner">
                  <div className="ap-flds">
                    {/* AP-1: TM flag banner — shown when TM has flagged this section */}
                    {isFlagged && secStat.note && (
                      <div className="ap-flag-banner">
                        <span className="ap-flag-from">⚑ TM Note</span>
                        <span className="ap-flag-text">{secStat.note}</span>
                      </div>
                    )}
                    {isFlagged && !secStat.note && (
                      <div className="ap-flag-banner">
                        <span className="ap-flag-from">⚑ TM Note</span>
                        <span className="ap-flag-text">Your tour manager needs you to review this section.</span>
                      </div>
                    )}
                    {/* Run of Show — full per-act bill (load-in / soundcheck / set / set end)
                        + changeovers, mirroring the TM view. Promoter edits save a proposed
                        copy to advance_data.schedule; the TM sees a diff and can apply it. */}
                    {sec.id === "schedule" && (
                      <PortalBillEditor
                        tmSchedule={tmSd}
                        proSchedule={sd}
                        locked={locked}
                        P={P}
                        onUpdate={(update) => {
                          setAdvData(p => {
                            const secNext = { ...(p.schedule || {}), ...update };
                            patchAdvance({ schedule: secNext });
                            return { ...p, schedule: secNext };
                          });
                        }}
                      />
                    )}
                    {showSubhdrs ? (
                      <Fragment>
                        <div className="ap-subhdr arranged">✦ From us — review or amend</div>
                        {renderFieldGroup(arrangedFields)}
                        <div className="ap-subhdr needed">✎ We need from you</div>
                        {renderFieldGroup(neededFields)}
                      </Fragment>
                    ) : arrangedFields.length > 0
                      ? renderFieldGroup(arrangedFields)
                      : renderFieldGroup(neededFields)
                    }
                  </div>
                </div>
              </div>
            </div>
          );
        })}

        <div className="ap-sec">
          <div className="ap-sec-hdr" onClick={() => toggleSec("_notes")}>
            <span className={`ap-chev ${openSecs._notes ? "open" : ""}`}>▶</span>
            <span className="ap-sec-title">Additional Notes</span>
            <div className="ap-sec-meta">
              <span className={`ap-sec-count ${advData._notes ? "has" : ""}`}>{advData._notes ? "✓" : "—"}</span>
            </div>
          </div>
          <div className={`ap-flds-wrap ${openSecs._notes ? "open" : ""}`}>
            <div className="ap-flds-inner">
              <div className="ap-flds">
                <textarea className="ap-inp ap-ta" rows={4}
                  placeholder="Anything else the tour manager should know..."
                  value={advData._notes || ""}
                  onChange={e => {
                    setAdvData(p => ({ ...p, _notes: e.target.value }));
                    patchAdvance({ _notes: e.target.value });
                  }} />
              </div>
            </div>
          </div>
        </div>

        {/* Documents — Tour files + TM files + Promoter uploads */}
        {(() => {
          const tourFiles = (tour?.advance_config?.files || []);
          const tmFiles   = (info?.tmData?._files || []);
          const proFiles  = (advData._files || []);
          const totalFiles = tourFiles.length + tmFiles.length + proFiles.length;
          return (
            <div className="ap-sec">
              <div className="ap-sec-hdr" onClick={() => toggleSec("_docs")}>
                <span className={`ap-chev ${openSecs._docs ? "open" : ""}`}>▶</span>
                <span className="ap-sec-title">Documents</span>
                <div className="ap-sec-meta">
                  <span className={`ap-sec-count ${totalFiles > 0 ? "has" : ""}`}>{totalFiles > 0 ? `${totalFiles} file${totalFiles !== 1 ? "s" : ""}` : "—"}</span>
                </div>
              </div>
              <div className={`ap-flds-wrap ${openSecs._docs ? "open" : ""}`}>
              <div className="ap-flds-inner">
              <div className="ap-flds" style={{ gap: 14 }}>

                  {/* Tour-wide files (read-only for promoter) — split into tour docs + press kit */}
                  {(tourFiles.length > 0 || tmFiles.length > 0) && (() => {
                    const _PRESS_CATS = ["Photos", "Bios", "Admats", "Fonts", "Promo Copy"];
                    const allTmFiles  = [...tourFiles, ...tmFiles];
                    const tourDocFiles  = allTmFiles.filter(f => !_PRESS_CATS.includes(f.label));
                    const pressKitFiles = allTmFiles.filter(f => _PRESS_CATS.includes(f.label));
                    const FF = "'Helvetica Neue', Helvetica, Arial, sans-serif";
                    const renderTmFile = (f) => (
                      <a key={f.id || f.name} href={f.url} target="_blank" rel="noreferrer"
                        style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", background: P.tmFileBg, border: `1px solid ${P.tmFileBdr}`, borderRadius: 8, textDecoration: "none" }}>
                        <span style={{ fontFamily: FF, fontSize: 11, color: P.green, background: P.confHdr, border: `1px solid ${P.confBdr}`, borderRadius: 4, padding: "2px 6px", flexShrink: 0 }}>{f.label || "File"}</span>
                        <span style={{ flex: 1, fontSize: 12, color: P.textMid, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.name}</span>
                        <span style={{ fontSize: 11, color: P.green, flexShrink: 0 }}>↓</span>
                      </a>
                    );
                    return (
                      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                        {tourDocFiles.length > 0 && (
                          <div>
                            <div style={{ fontFamily: FF, fontSize: 11, color: P.textFnt, letterSpacing: 1, marginBottom: 8 }}>TOUR DOCUMENTS</div>
                            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>{tourDocFiles.map(renderTmFile)}</div>
                          </div>
                        )}
                        {pressKitFiles.length > 0 && (
                          <div>
                            <div style={{ fontFamily: FF, fontSize: 11, color: P.textFnt, letterSpacing: 1, marginBottom: 8 }}>PRESS KIT</div>
                            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>{pressKitFiles.map(renderTmFile)}</div>
                          </div>
                        )}
                      </div>
                    );
                  })()}


                  {/* Promoter file uploads */}
                  <div>
                    <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: P.textFnt, letterSpacing: 1, marginBottom: 8 }}>YOUR UPLOADS</div>
                    {proFiles.length > 0 && (
                      <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 10 }}>
                        {proFiles.map(f => (
                          <div key={f.id || f.name} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", background: P.proFileBg, border: `1px solid ${P.proFileBdr}`, borderRadius: 8 }}>
                            <span style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: P.textMut, background: P.inpBg, border: `1px solid ${P.inpBdr}`, borderRadius: 4, padding: "2px 6px", flexShrink: 0 }}>{f.label || "File"}</span>
                            <a href={f.url} target="_blank" rel="noreferrer" style={{ flex: 1, fontSize: 12, color: P.textMut, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textDecoration: "none" }}>{f.name}</a>
                            <button onClick={() => removePromoterFile(f.id)} style={{ background: "none", border: "none", color: P.textFnt, cursor: "pointer", fontSize: 14, lineHeight: 1, padding: "0 2px", flexShrink: 0 }}>✕</button>
                          </div>
                        ))}
                      </div>
                    )}
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <select value={proFileLabel} onChange={e => setProFileLabel(e.target.value)}
                        className="ap-inp" style={{ flex: "0 0 auto", width: "auto", fontSize: 12, padding: "7px 10px" }}>
                        {["Venue Specs","Parking Map","Restaurant / Catering Menu","Production Specs","Stage Plot","Tech Rider","Other"].map(l => <option key={l} value={l}>{l}</option>)}
                      </select>
                      <label style={{ flex: 1, minWidth: 120, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: "8px 14px", background: proFileUploading ? P.inpBg : P.proFileBg, border: `1px dashed ${P.uploadBdr}`, borderRadius: 8, cursor: proFileUploading ? "default" : "pointer", fontSize: 12, color: P.textMut }}>
                        {proFileUploading ? "Uploading…" : "⬆ Upload File"}
                        <input type="file" style={{ display: "none" }} disabled={proFileUploading}
                          onChange={e => { if (e.target.files[0]) { const lbl = detectFileLabel(e.target.files[0].name); setProFileLabel(lbl); uploadPromoterFile(e.target.files[0], lbl); e.target.value = ""; } }} />
                      </label>
                    </div>
                  </div>

                </div>
              </div>
              </div>
            </div>
          );
        })()}
      </div>

        <div className="ap-submit-wrap">
          {advData._submitted ? (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
              <span style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 14, color: P.green, fontWeight: 600 }}>✓ Advance submitted</span>
              <button className="ap-submit-btn" style={{ background: "none", border: `1px solid ${P.textVFnt}`, color: P.textFnt, fontSize: 11, padding: "8px 20px" }} onClick={reopenAdvance}>
                Re-open for editing
              </button>
            </div>
          ) : (
            <>
              <button className="ap-submit-btn" onClick={submitAdvance} disabled={progress === 0}>
                Submit Advance →
              </button>
              <span style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: P.textFnt }}>
                {progress === 0 ? "Fill in at least one field to submit" : "Your data is already auto-saved — this notifies the TM you're done."}
              </span>
            </>
          )}
        </div>

      <div className="ap-foot">POWERED BY ADVANCD</div>
    </div>
  );
}

// ── Batch Send Advances Modal ─────────────────────────────────────────────
function BatchAdvanceModal({ state, onEditDraft, onToggleSkip, onClose, lightMode, emailConn, batchDraftState, onSaveDrafts, artistName }) {
  const { stage, items, draftingIdx } = state;
  const ready = items.filter(it => !it.skipped && it.draft).length;
  const total = items.length;
  const progress = stage === "drafting" ? Math.round((draftingIdx / total) * 100) : 100;
  const M = lightMode ? {
    rowBg: "#ede9e3", rowBdr: "#d0ccc6", rowBdrSkip: "#e8e4de",
    text: "#1a1917", textMut: "#6a6764", textFnt: "#9a9794",
    fieldBg: "#f8f5f0", fieldBdr: "#d0ccc6", fieldTxt: "#1a1917",
    trackBg: "#d0ccc6", amber: "#c47f00", amdBdr: "#d4880a",
    skipTxt: "#8a8784", skipBdr: "#ccc8c2",
    draftingTxt: "#8a8784", queuedTxt: "#ccc8c2",
    footTxt: "#8a8784",
  } : {
    rowBg: "#111", rowBdr: "#272727", rowBdrSkip: "#1a1a1a",
    text: "#e0e0e0", textMut: "#555", textFnt: "#444",
    fieldBg: "#0d0d0d", fieldBdr: "#1e1e1e", fieldTxt: "#ccc",
    trackBg: "#1e1e1e", amber: "var(--accent)", amdBdr: "#3a2800",
    skipTxt: "#444", skipBdr: "#272727",
    draftingTxt: "#555", queuedTxt: "#2a2a2a",
    footTxt: "#555",
  };
  return (
    <div className="ov" onClick={onClose}>
      <div className="mo" style={{ width: 740, maxWidth: "94vw" }} onClick={e => e.stopPropagation()}>
        <div className="mo-title">Batch Send Advances</div>
        <div className="mo-sub">
          {stage === "drafting"
            ? `DRAFTING ${Math.min(draftingIdx + 1, total)} OF ${total} — PLEASE WAIT…`
            : `${total} SHOW${total !== 1 ? "S" : ""} · REVIEW DRAFTS AND SEND`}
        </div>
        {stage === "drafting" && (
          <div style={{ marginBottom: 20 }}>
            <div style={{ height: 3, background: M.trackBg, borderRadius: 4, overflow: "hidden", marginBottom: 8 }}>
              <div style={{ height: "100%", background: M.amber, width: `${progress}%`, transition: "width 0.4s ease" }} />
            </div>
            <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: M.draftingTxt }}>
              Generating advance emails — this may take a moment for larger tours.
            </div>
          </div>
        )}
        <div style={{ display: "flex", flexDirection: "column", gap: 12, maxHeight: "55vh", overflowY: "auto" }}>
          {items.map((item, i) => (
            <div key={item.showId} style={{
              background: M.rowBg, border: `1px solid ${item.skipped ? M.rowBdrSkip : M.rowBdr}`,
              borderRadius: 8, padding: "14px 16px", opacity: item.skipped ? 0.4 : 1, transition: "opacity 0.2s"
            }}>
              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 8, gap: 12 }}>
                <div>
                  <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 14, fontWeight: 600, color: M.text }}>
                    {fmtDate(item.date)} · {item.venueName}
                  </div>
                  <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: M.textMut, marginTop: 2 }}>
                    To: {item.email}
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                  {item.draft && stage === "review" && !item.skipped && (
                    <a href={`mailto:${item.email}?subject=${encodeURIComponent(advanceSubject(artistName, item.date, item.venueName, item.city, item.state))}&body=${encodeURIComponent(item.draft)}`}
                      style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: M.amber, textDecoration: "none", padding: "4px 10px", border: `1px solid ${M.amdBdr}`, borderRadius: 4, whiteSpace: "nowrap" }}>
                      Open in Email ↗
                    </a>
                  )}
                  <button onClick={() => onToggleSkip(i)} style={{
                    fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, letterSpacing: "0.5px",
                    color: item.skipped ? M.amber : M.skipTxt, background: "none",
                    border: `1px solid ${M.skipBdr}`, borderRadius: 4, padding: "3px 8px", cursor: "pointer"
                  }}>{item.skipped ? "INCLUDE" : "SKIP"}</button>
                </div>
              </div>
              {stage === "drafting" && i === draftingIdx && !item.draft && (
                <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 12, color: M.draftingTxt }}>Drafting…</div>
              )}
              {stage === "drafting" && i > draftingIdx && !item.draft && (
                <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 12, color: M.queuedTxt }}>Queued</div>
              )}
              {item.draft && (
                <textarea value={item.draft} onChange={e => onEditDraft(i, e.target.value)}
                  disabled={item.skipped} rows={6} style={{
                    width: "100%", boxSizing: "border-box", background: M.fieldBg, border: `1px solid ${M.fieldBdr}`,
                    borderRadius: 8, padding: "10px 12px", color: M.fieldTxt, resize: "vertical",
                    fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 12, lineHeight: 1.55
                  }} />
              )}
            </div>
          ))}
        </div>
        <div className="mo-acts" style={{ marginTop: 18, flexWrap: "wrap", gap: 8 }}>
          <button className="btn-g" onClick={onClose}>Close</button>
          {/* EM-1: bulk save to mailbox drafts */}
          {stage === "review" && ready > 0 && [["microsoft", "Outlook"], ["google", "Gmail"]].map(([prov, label]) => {
            if (!emailConn?.[prov]?.connected) return null;
            const bs = batchDraftState?.provider === prov ? batchDraftState : null;
            return (
              <button key={prov} className="btn-p"
                onClick={() => onSaveDrafts(prov)}
                disabled={!!batchDraftState?.saving}
                style={{ fontSize: 11, opacity: batchDraftState?.saving ? 0.6 : 1 }}>
                {bs?.saving
                  ? `Saving ${bs.done + bs.failed} / ${ready}…`
                  : bs && !bs.saving
                    ? `✓ ${bs.done} in ${label} Drafts${bs.failed ? ` · ${bs.failed} failed` : ""}`
                    : `Save ${ready} to ${label} Drafts`}
              </button>
            );
          })}
          {stage === "review" && ready > 0 && (
            <span style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: M.footTxt }}>
              {emailConn?.microsoft?.connected || emailConn?.google?.connected
                ? `${ready} email${ready !== 1 ? "s" : ""} ready`
                : `${ready} email${ready !== 1 ? "s" : ""} ready — click "Open in Email" for each, or connect your mailbox in any show's Email tab to save all drafts at once`}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Crew Member Modal ──────────────────────────────────────────────────────
const TRAVEL_GROUPS = ["band", "crew", "tm", "other"];
const TRAVEL_GROUP_LABELS = { band: "Band", crew: "Crew", tm: "Tour Manager", other: "Other" };

// ── Profile Role Categories ────────────────────────────────────────────────
const PROFILE_ROLE_CATS = ["tm", "band", "crew", "management", "production", "other"];

// ── Tour Role Presets (PP-1 Phase 3) ──────────────────────────────────────
// Each role defines default permissions for what a member sees on mobile.
// TM can override per-person after assigning a role.
const TOUR_ROLE_PRESETS = [
  { id: "tm",         label: "Tour Manager",     can_see_guarantee: true,  can_see_settlement: true,  can_see_merch: true,  can_see_contacts: true,  can_see_advance: true,  can_edit_setlist: true  },
  { id: "management", label: "Management",       can_see_guarantee: true,  can_see_settlement: true,  can_see_merch: true,  can_see_contacts: true,  can_see_advance: true,  can_edit_setlist: true  },
  { id: "band",       label: "Band Member",      can_see_guarantee: true,  can_see_settlement: false, can_see_merch: false, can_see_contacts: true,  can_see_advance: true,  can_edit_setlist: true  },
  { id: "crew",       label: "Crew",             can_see_guarantee: false, can_see_settlement: false, can_see_merch: false, can_see_contacts: true,  can_see_advance: true,  can_edit_setlist: false },
  { id: "session",    label: "Session Musician", can_see_guarantee: false, can_see_settlement: false, can_see_merch: false, can_see_contacts: false, can_see_advance: false, can_edit_setlist: false },
  { id: "production", label: "Production",       can_see_guarantee: false, can_see_settlement: false, can_see_merch: false, can_see_contacts: true,  can_see_advance: true,  can_edit_setlist: false },
];
const PROFILE_ROLE_LABELS = { tm: "Tour Manager", band: "Band", crew: "Crew", management: "Management", production: "Production", other: "Other" };
const PROFILE_ROLE_COLORS = {
  band:       "#b09cbf", // lavender
  tm:         "#80aaa8", // teal
  management: "#c4a882", // sand
  crew:       "#8faabc", // slate blue
  production: "#9db89d", // sage
  session:    "#bc8f8f", // rose
  other:      "#9a9490", // warm grey
};

// ── Google Maps Script Loader ─────────────────────────────────────────────
function loadGoogleMaps() {
  if (!GMAP_KEY) return Promise.reject(new Error("no_key"));
  if (window.__gmapsPromise) return window.__gmapsPromise;
  window.__gmapsPromise = new Promise((resolve, reject) => {
    if (window.google?.maps?.Map) { resolve(window.google.maps); return; }
    const cb = "__gmapsReady" + Date.now();
    window[cb] = () => { resolve(window.google.maps); delete window[cb]; };
    const s = document.createElement("script");
    s.src = `https://maps.googleapis.com/maps/api/js?key=${GMAP_KEY}&libraries=places&callback=${cb}`;
    s.async = true;
    s.onerror = () => { reject(new Error("load_failed")); window.__gmapsPromise = null; };
    document.head.appendChild(s);
  });
  return window.__gmapsPromise;
}

// Light elegant map style — clean, muted palette, road-focused
const LIGHT_MAP_STYLE = [
  { elementType: "geometry",            stylers: [{ color: "#f5f2ee" }] },
  { elementType: "labels.text.fill",    stylers: [{ color: "#5a5550" }] },
  { elementType: "labels.text.stroke",  stylers: [{ color: "#f5f2ee" }] },
  { featureType: "administrative",      elementType: "geometry", stylers: [{ color: "#dedad4" }] },
  { featureType: "administrative.country", elementType: "labels.text.fill", stylers: [{ color: "#9a9490" }] },
  { featureType: "administrative.locality", elementType: "labels.text.fill", stylers: [{ color: "#5a5550" }] },
  { featureType: "poi",                 elementType: "labels", stylers: [{ visibility: "off" }] },
  { featureType: "poi.park",            elementType: "geometry", stylers: [{ color: "#ddeedd" }] },
  { featureType: "poi.park",            elementType: "labels.text.fill", stylers: [{ color: "#9aaa9a" }] },
  { featureType: "road",                elementType: "geometry",        stylers: [{ color: "#ffffff" }] },
  { featureType: "road",                elementType: "geometry.stroke", stylers: [{ color: "#e8e4de" }] },
  { featureType: "road",                elementType: "labels.text.fill", stylers: [{ color: "#8a8078" }] },
  { featureType: "road.highway",        elementType: "geometry",        stylers: [{ color: "#fae8c0" }] },
  { featureType: "road.highway",        elementType: "geometry.stroke", stylers: [{ color: "#f0d080" }] },
  { featureType: "road.highway",        elementType: "labels.text.fill", stylers: [{ color: "#706050" }] },
  { featureType: "road.arterial",       elementType: "labels", stylers: [{ visibility: "off" }] },
  { featureType: "road.local",          elementType: "labels", stylers: [{ visibility: "off" }] },
  { featureType: "transit.line",        elementType: "geometry",        stylers: [{ color: "#e0dcd6" }] },
  { featureType: "transit.station",     elementType: "geometry",        stylers: [{ color: "#e8e4de" }] },
  { featureType: "water",               elementType: "geometry",        stylers: [{ color: "#c8dde8" }] },
  { featureType: "water",               elementType: "labels.text.fill", stylers: [{ color: "#8ab0c8" }] },
];

const ROUTE_COLORS = ["#f0a500", "#6a8faf", "#3ecf6e", "#c06faf"];

// Parses time strings like "3:00 PM", "15:00", "3pm", "3:30PM" → Date (today at that time)
function parseArrivalTime(str) {
  if (!str) return null;
  const s = str.trim();
  // 12-hour: "3pm", "3:30 PM", "3:30pm"
  const m12 = s.match(/^(\d{1,2})(?::(\d{2}))?\s*(am|pm)$/i);
  if (m12) {
    let h = parseInt(m12[1], 10);
    const min = parseInt(m12[2] || "0", 10);
    const ap  = m12[3].toLowerCase();
    if (ap === "pm" && h !== 12) h += 12;
    if (ap === "am" && h === 12) h = 0;
    const d = new Date(); d.setHours(h, min, 0, 0); return d;
  }
  // 24-hour: "15:00", "9:30"
  const m24 = s.match(/^(\d{1,2}):(\d{2})$/);
  if (m24) {
    const d = new Date(); d.setHours(parseInt(m24[1], 10), parseInt(m24[2], 10), 0, 0); return d;
  }
  return null;
}

// ── TravelMap component ───────────────────────────────────────────────────
function TravelMap({ origin, destination, originLabel, destLabel, arrivalTarget, arrivalLabel, darkMode, onRouteReady }) {
  const mapRef     = useRef(null);
  const mapObjRef  = useRef(null);
  const renderers  = useRef([]);
  const trafficRef = useRef(null);

  const [routes,        setRoutes]        = useState([]);
  const [selectedRoute, setSelectedRoute] = useState(0);
  const [status,        setStatus]        = useState("loading"); // loading | ready | error | no_key | no_route
  const [errMsg,        setErrMsg]        = useState("");
  const [mapLoaded,     setMapLoaded]     = useState(false);
  const [suggestedDep,  setSuggestedDep]  = useState(null); // Date | null

  // Load map on mount
  useEffect(() => {
    if (!origin || !destination) return;
    setStatus("loading");
    setRoutes([]);
    setSelectedRoute(0);
    setSuggestedDep(null);

    loadGoogleMaps().then(maps => {
      if (!mapRef.current) return;
      setMapLoaded(true);

      // Init map once
      if (!mapObjRef.current) {
        mapObjRef.current = new maps.Map(mapRef.current, {
          zoom: 6,
          center: { lat: 39.5, lng: -98.35 },
          styles: LIGHT_MAP_STYLE,
          disableDefaultUI: false,
          zoomControl: true,
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: true,
          gestureHandling: "cooperative",
        });
        trafficRef.current = new maps.TrafficLayer();
        trafficRef.current.setMap(mapObjRef.current);
      }

      // Trigger resize so map fills its container even if it was hidden when mounted
      requestAnimationFrame(() => {
        if (mapObjRef.current) {
          maps.event.trigger(mapObjRef.current, 'resize');
        }
      });

      // Clear old renderers
      renderers.current.forEach(r => r.setMap(null));
      renderers.current = [];

      const svc = new maps.DirectionsService();
      svc.route({
        origin,
        destination,
        travelMode: maps.TravelMode.DRIVING,
        provideRouteAlternatives: true,
        drivingOptions: {
          departureTime: new Date(),
          trafficModel: maps.TrafficModel.BEST_GUESS,
        },
      }, (result, st) => {
        if (st !== "OK") {
          setStatus(st === "ZERO_RESULTS" ? "no_route" : "error");
          setErrMsg(st);
          return;
        }

        const rts = result.routes;
        setRoutes(rts);
        setStatus("ready");

        // Extract route waypoints for hotel search
        if (onRouteReady) {
          const leg        = rts[0]?.legs?.[0];
          const steps      = leg?.steps || [];
          const totalDist  = leg?.distance?.value  || 0;
          const totalDur   = leg?.duration_in_traffic?.value || leg?.duration?.value || 0;
          const toLL = loc => ({ lat: loc.lat(), lng: loc.lng() });

          const wps = [];
          // Start
          wps.push({ label: originLabel || "Start", address: leg?.start_address || origin, location: toLL(leg.start_location) });

          // Add midpoints for drives over 90 minutes
          if (totalDur > 90 * 60 && steps.length > 1) {
            const thirds = totalDur > 4 * 3600 ? [1/3, 2/3] : [1/2]; // 2 midpoints for 4hr+ drives
            let cumDist = 0, cumDur = 0;
            let threshIdx = 0;
            for (const step of steps) {
              cumDist += step.distance.value;
              cumDur  += step.duration.value;
              if (threshIdx < thirds.length && cumDur >= totalDur * thirds[threshIdx]) {
                const hrs = (cumDur / 3600).toFixed(1).replace(/\.0$/, "");
                wps.push({ label: `~${hrs}hr mark`, address: null, location: toLL(step.end_location) });
                threshIdx++;
              }
            }
          }

          // End
          wps.push({ label: destLabel || "Destination", address: leg?.end_address || destination, location: toLL(leg.end_location) });
          onRouteReady(wps);
        }

        // Suggested departure = arrival target − drive time (with traffic)
        if (arrivalTarget) {
          const arrivalDate = parseArrivalTime(arrivalTarget);
          const driveSecs   = rts[0]?.legs?.[0]?.duration_in_traffic?.value
                           || rts[0]?.legs?.[0]?.duration?.value || 0;
          if (arrivalDate && driveSecs > 0) {
            setSuggestedDep(new Date(arrivalDate.getTime() - driveSecs * 1000));
          }
        }

        // Create a renderer per route; only primary is visible
        rts.forEach((_, i) => {
          const renderer = new maps.DirectionsRenderer({
            directions: result,
            routeIndex: i,
            suppressMarkers: i !== 0,
            polylineOptions: {
              strokeColor: ROUTE_COLORS[i % ROUTE_COLORS.length],
              strokeOpacity: i === 0 ? 0.9 : 0.35,
              strokeWeight: i === 0 ? 5 : 3,
            },
            suppressInfoWindows: true,
            preserveViewport: i !== 0,
          });
          renderer.setMap(mapObjRef.current);
          renderers.current.push(renderer);
        });
      });
    }).catch(err => {
      setStatus(err.message === "no_key" ? "no_key" : "error");
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [origin, destination]);

  // Switch active route
  const selectRoute = (idx) => {
    setSelectedRoute(idx);
    renderers.current.forEach((r, i) => {
      const isSelected = i === idx;
      r.setOptions({
        polylineOptions: {
          strokeColor: ROUTE_COLORS[i % ROUTE_COLORS.length],
          strokeOpacity: isSelected ? 0.9 : 0.25,
          strokeWeight: isSelected ? 5 : 3,
        },
        suppressMarkers: !isSelected,
      });
    });
    // Fit bounds of selected route
    if (mapObjRef.current && routes[idx]) {
      mapObjRef.current.fitBounds(routes[idx].bounds);
    }
  };

  const FF = "'Helvetica Neue', Helvetica, Arial, sans-serif";
  const bg      = "#f5f2ee";
  const border  = "#e0dcd6";
  const text    = "#3a3530";
  const muted   = "#9a9490";
  const panelBg = "#ffffff";

  const selectedLeg = routes[selectedRoute]?.legs?.[0];
  const driveTime = selectedLeg?.duration_in_traffic?.text || selectedLeg?.duration?.text;
  const distance  = selectedLeg?.distance?.text;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 0, borderRadius: 8, overflow: "hidden", border: `1px solid ${border}` }}>

      {/* Header bar — origin → destination */}
      <div style={{ background: panelBg, padding: "10px 14px", display: "flex", alignItems: "center", gap: 8, borderBottom: `1px solid ${border}` }}>
        <div style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 6, overflow: "hidden" }}>
          <span style={{ fontFamily: FF, fontSize: 11, color: muted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {originLabel || origin}
          </span>
          <span style={{ fontFamily: FF, fontSize: 11, color: muted, flexShrink: 0 }}>→</span>
          <span style={{ fontFamily: FF, fontSize: 11, color: text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {destLabel || destination}
          </span>
        </div>
        {status === "ready" && driveTime && (
          <div style={{ display: "flex", gap: 12, flexShrink: 0 }}>
            <span style={{ fontFamily: FF, fontSize: 11, color: "#b07a00", fontWeight: 600 }}>{driveTime}</span>
            {distance && <span style={{ fontFamily: FF, fontSize: 11, color: muted }}>{distance}</span>}
          </div>
        )}
      </div>

      {/* Route selector — only shown when alternatives exist */}
      {status === "ready" && routes.length > 1 && (
        <div style={{ background: panelBg, padding: "8px 14px", display: "flex", gap: 6, flexWrap: "wrap", borderBottom: `1px solid ${border}` }}>
          {routes.map((r, i) => {
            const leg = r.legs?.[0];
            const t = leg?.duration_in_traffic?.text || leg?.duration?.text || "";
            const isSel = i === selectedRoute;
            return (
              <button key={i}
                onClick={() => selectRoute(i)}
                style={{
                  fontFamily: FF, fontSize: 11, cursor: "pointer", borderRadius: 4,
                  padding: "4px 10px", letterSpacing: 0.3,
                  background: isSel ? "var(--accent)" : "transparent",
                  color: isSel ? "#0d0d0d" : muted,
                  border: `1px solid ${isSel ? "var(--accent)" : border}`,
                  fontWeight: isSel ? 700 : 400,
                }}>
                {r.summary} · {t}
              </button>
            );
          })}
          <span style={{ fontFamily: FF, fontSize: 11, color: muted, alignSelf: "center", marginLeft: "auto" }}>
            🚦 Live traffic
          </span>
        </div>
      )}

      {/* Suggested departure banner */}
      {status === "ready" && suggestedDep && (
        <div style={{ background: "#fffbf0", borderBottom: `1px solid #f0d898`, padding: "10px 14px", display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, letterSpacing: 1.5, color: "#a08040", textTransform: "uppercase" }}>Suggested Departure</div>
            <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 20, fontWeight: 700, color: "#7a5800", letterSpacing: -0.5 }}>
              {suggestedDep.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}
            </div>
          </div>
          <div style={{ width: 1, height: 32, background: "#f0d898", flexShrink: 0 }} />
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, letterSpacing: 1.5, color: "#a08040", textTransform: "uppercase" }}>{arrivalLabel || "Arrival"}</div>
            <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 14, color: "#5a4010" }}>{arrivalTarget}</div>
          </div>
          <div style={{ marginLeft: "auto", fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: "#a08040", textAlign: "right" }}>
            Based on current traffic
          </div>
        </div>
      )}

      {/* Map */}
      <div style={{ position: "relative", height: 320, background: bg }}>
        <div ref={mapRef} style={{ width: "100%", height: "320px", display: "block" }} />

        {/* Overlay states */}
        {status === "loading" && (
          <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: bg }}>
            <span style={{ fontFamily: FF, fontSize: 12, color: muted }}>Loading route…</span>
          </div>
        )}
        {status === "no_key" && (
          <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: bg, flexDirection: "column", gap: 8 }}>
            <span style={{ fontFamily: FF, fontSize: 14, color: text }}>Google Maps not configured</span>
            <span style={{ fontFamily: FF, fontSize: 11, color: muted }}>Add VITE_GOOGLE_MAPS_KEY to your .env</span>
          </div>
        )}
        {status === "no_route" && (
          <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: bg, flexDirection: "column", gap: 8 }}>
            <span style={{ fontFamily: FF, fontSize: 14, color: text }}>No route found</span>
            <span style={{ fontFamily: FF, fontSize: 11, color: muted }}>Check that origin and destination addresses are valid</span>
          </div>
        )}
        {status === "error" && (
          <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: bg }}>
            <span style={{ fontFamily: FF, fontSize: 12, color: muted }}>Maps error: {errMsg}</span>
          </div>
        )}
      </div>

      {/* Footer — live traffic note + steps count */}
      {status === "ready" && routes.length === 1 && (
        <div style={{ background: panelBg, padding: "7px 14px", borderTop: `1px solid ${border}`, display: "flex", justifyContent: "space-between" }}>
          <span style={{ fontFamily: FF, fontSize: 11, color: muted }}>🚦 Live traffic · {routes[0].summary}</span>
          {selectedLeg?.steps && <span style={{ fontFamily: FF, fontSize: 11, color: muted }}>{selectedLeg.steps.length} turns</span>}
        </div>
      )}
    </div>
  );
}

// ── HotelRecommendations ──────────────────────────────────────────────────
function HotelRecommendations({ destination, routeWaypoints, lodgingPrefs, onAddLodging }) {
  const [results,      setResults]      = useState([]);
  const [status,       setStatus]       = useState("idle"); // idle | loading | done | error
  const [activeChains, setActiveChains] = useState(lodgingPrefs?.preferred_chains || []);
  // Selected waypoint index — default to last (destination)
  const [selectedWp,   setSelectedWp]   = useState(null);
  const FF = "'Helvetica Neue', Helvetica, Arial, sans-serif";

  const preferredChains = lodgingPrefs?.preferred_chains || [];

  // Determine which waypoints to show. Use routeWaypoints if available, else single destination.
  const waypoints = routeWaypoints?.length > 0 ? routeWaypoints : (destination ? [{ label: destination.split(",")[0], address: destination, location: null }] : []);
  // Active waypoint: use selectedWp index or default to last (destination end)
  const activeWpIdx = selectedWp !== null ? selectedWp : waypoints.length - 1;
  const activeWp    = waypoints[activeWpIdx];

  const doSearch = (wp) => {
    if (!wp) return;
    setStatus("loading");
    setResults([]);

    loadGoogleMaps().then(maps => {
      const svc = new maps.places.PlacesService(document.createElement("div"));

      const runSearch = (location) => {
        svc.nearbySearch({ location, radius: 10000, type: "lodging" }, (places, plStatus) => {
          if (plStatus !== "OK" && plStatus !== "ZERO_RESULTS") { setStatus("error"); return; }
          const all = places || [];
          const filtered = activeChains.length > 0
            ? all.filter(p => activeChains.some(c => p.name.toLowerCase().includes(c.toLowerCase())))
            : all;
          setResults((filtered.length > 0 ? filtered : all).slice(0, 12));
          setStatus("done");
        });
      };

      if (wp.location) {
        // Already have coordinates from the route — use directly, no geocoding needed
        runSearch(new maps.LatLng(wp.location.lat, wp.location.lng));
      } else if (wp.address) {
        new maps.Geocoder().geocode({ address: wp.address }, (res, st) => {
          if (st !== "OK" || !res?.[0]) { setStatus("error"); return; }
          runSearch(res[0].geometry.location);
        });
      } else {
        setStatus("error");
      }
    }).catch(() => setStatus("error"));
  };

  const toggleChain = (chain) => {
    setActiveChains(prev =>
      prev.includes(chain) ? prev.filter(c => c !== chain) : [...prev, chain]
    );
  };

  const priceLabel = (level) => level ? "$".repeat(level) : null;
  const ratingStars = (rating) => {
    if (!rating) return null;
    const full = Math.round(rating);
    return "★".repeat(full) + "☆".repeat(Math.max(0, 5 - full));
  };

  // Detect which chain a place name matches
  const matchedChain = (name) =>
    HOTEL_CHAINS.find(c => name.toLowerCase().includes(c.toLowerCase())) || null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>

      {/* Chain filter chips — show preferred chains + ability to expand */}
      {preferredChains.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
          <span style={{ fontFamily: FF, fontSize: 11, color: "#9a9490", letterSpacing: 1, textTransform: "uppercase", marginRight: 4 }}>Filter</span>
          {preferredChains.map(c => {
            const on = activeChains.includes(c);
            return (
              <button key={c} onClick={() => toggleChain(c)}
                style={{ fontFamily: FF, fontSize: 11, padding: "3px 10px", borderRadius: 16, cursor: "pointer",
                  border: `1px solid ${on ? "#b07a00" : "#e0dcd6"}`,
                  background: on ? "#fff8e8" : "#fff",
                  color: on ? "#7a5800" : "#9a9490",
                  fontWeight: on ? 600 : 400 }}>
                {c}
              </button>
            );
          })}
        </div>
      )}

      {/* Route stop selector — only shown when there are multiple waypoints */}
      {waypoints.length > 1 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <span style={{ fontFamily: FF, fontSize: 11, color: "#9a9490", letterSpacing: 1, textTransform: "uppercase" }}>Search near</span>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {waypoints.map((wp, i) => {
              const isSel = i === activeWpIdx;
              return (
                <button key={wp.label} onClick={() => { setSelectedWp(i); setStatus("idle"); setResults([]); }}
                  style={{ fontFamily: FF, fontSize: 11, padding: "5px 14px", borderRadius: 16, cursor: "pointer",
                    border: `1px solid ${isSel ? "#b07a00" : "#e0dcd6"}`,
                    background: isSel ? "#fff8e8" : "#fff",
                    color: isSel ? "#7a5800" : "#9a9490",
                    fontWeight: isSel ? 700 : 400 }}>
                  {wp.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Search button */}
      {status === "idle" && activeWp && (
        <button onClick={() => doSearch(activeWp)}
          style={{ fontFamily: FF, fontSize: 11, padding: "8px 16px", borderRadius: 8, cursor: "pointer",
            background: "#fff8e8", border: "1px solid #f0d898", color: "#7a5800", fontWeight: 600, alignSelf: "flex-start" }}>
          Search Hotels Near {activeWp.label}
        </button>
      )}

      {status === "loading" && (
        <div style={{ fontFamily: FF, fontSize: 12, color: "#9a9490" }}>Searching nearby hotels…</div>
      )}

      {status === "error" && (
        <div style={{ fontFamily: FF, fontSize: 12, color: "#c84b4b" }}>Search failed — check your Maps API key has Places enabled.</div>
      )}

      {status === "done" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
          {/* Results header + re-search */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
            <span style={{ fontFamily: FF, fontSize: 11, color: "#9a9490" }}>
              {results.length} {results.length === 1 ? "result" : "results"}
              {activeChains.length > 0 && preferredChains.length > 0 ? " matching preferences" : " nearby"}
            </span>
            <button onClick={() => doSearch(activeWp)}
              style={{ fontFamily: FF, fontSize: 11, background: "none", border: "none", color: "#b07a00", cursor: "pointer", textDecoration: "underline" }}>
              Refresh
            </button>
          </div>

          {results.length === 0 ? (
            <div style={{ fontFamily: FF, fontSize: 12, color: "#9a9490" }}>No matching hotels found. Try removing chain filters.</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {results.map((place, i) => {
                const chain   = matchedChain(place.name);
                const price   = priceLabel(place.price_level);
                const stars   = ratingStars(place.rating);
                const address = place.vicinity || "";
                return (
                  <div key={place.place_id || i}
                    style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "10px 12px",
                      background: "#ffffff", border: "1px solid #e8e4de", borderRadius: 8 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
                        <span style={{ fontFamily: FF, fontSize: 12, fontWeight: 600, color: "#3a3530" }}>{place.name}</span>
                        {chain && (
                          <span style={{ fontFamily: FF, fontSize: 11, color: "#b07a00", background: "#fff8e8",
                            border: "1px solid #f0d898", borderRadius: 8, padding: "1px 7px", letterSpacing: 0.5 }}>
                            {chain}
                          </span>
                        )}
                      </div>
                      <div style={{ display: "flex", gap: 10, marginTop: 3, flexWrap: "wrap" }}>
                        {stars && <span style={{ fontFamily: FF, fontSize: 11, color: "#b07a00", letterSpacing: -0.5 }}>{stars} {place.rating?.toFixed(1)}</span>}
                        {price && <span style={{ fontFamily: FF, fontSize: 11, color: "#9a9490" }}>{price}</span>}
                        {address && <span style={{ fontFamily: FF, fontSize: 11, color: "#9a9490", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{address}</span>}
                      </div>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 5, flexShrink: 0 }}>
                      <button
                        onClick={() => onAddLodging({ name: place.name, address, chain: chain || "", type: "hotel" })}
                        style={{ fontFamily: FF, fontSize: 11, padding: "4px 12px", borderRadius: 4, cursor: "pointer",
                          background: "#fff8e8", border: "1px solid #f0d898", color: "#7a5800", fontWeight: 600 }}>
                        + Add
                      </button>
                      <a
                        href={`https://www.google.com/travel/hotels?q=${encodeURIComponent(place.name + " " + address)}`}
                        target="_blank" rel="noreferrer"
                        onClick={e => e.stopPropagation()}
                        style={{ fontFamily: FF, fontSize: 11, padding: "4px 12px", borderRadius: 4, cursor: "pointer", textAlign: "center", textDecoration: "none",
                          background: "transparent", border: "1px solid #e0dcd6", color: "#9a9490" }}>
                        Rates ↗
                      </a>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const HOTEL_CHAINS  = ["Marriott","Sheraton","Westin","W Hotels","Autograph Collection","Hilton","DoubleTree","Hampton Inn","Embassy Suites","Curio Collection","Hyatt","Park Hyatt","Grand Hyatt","Andaz","Aloft","IHG","Holiday Inn","Crowne Plaza","Kimpton","Best Western","Wyndham","Courtyard","Residence Inn","AC Hotels","Tribute Portfolio","Other"];
const ROOM_TYPES    = ["King","Queen","2 Queens","Double Double","Suite","Junior Suite","Executive Suite","Presidential Suite","Studio","Accessible King","Accessible Queen","Connecting Rooms"];
const LODGING_TYPES = ["hotel","airbnb","home"];
const LODGING_TYPE_LABELS = { hotel: "Hotel", airbnb: "Airbnb", home: "Home" };

const VEHICLE_TYPES       = ["bus","van","sprinter","truck"];
const VEHICLE_TYPE_LABELS = { bus: "Bus", van: "Van", sprinter: "Sprinter", truck: "Truck" };
const TRANSPORT_TYPES       = ["car","van","rideshare","train","other"];
const TRANSPORT_TYPE_LABELS = { car: "Car Service", van: "Van", rideshare: "Rideshare", train: "Train", other: "Other" };
function LodgingModal({ existing, tourParty, onSave, onClose, artistHomeCity, artistHomeState }) {
  const blank = { type: "hotel", name: "", address: "", chain: "", check_in: "", check_out: "", confirmation: "", phone: "", contact: "", personnel_ids: [], rooms: [], notes: "" };
  const [form, setForm] = useState(existing ? { ...blank, ...existing } : blank);
  // When switching to Home type, pre-fill city/state from artist home base
  const handleTypeChange = (t) => {
    if (t === "home") {
      setForm(p => ({
        ...p, type: "home",
        name: "Home",
        address: [artistHomeCity, artistHomeState].filter(Boolean).join(", ") || p.address,
      }));
    } else {
      setForm(p => ({ ...p, type: t, name: p.type === "home" ? "" : p.name }));
    }
  };
  const [confirmDel, setConfirmDel] = useState(false);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  // Room block helpers
  const addRoom    = () => setForm(p => ({ ...p, rooms: [...p.rooms, { type: "King", count: 1 }] }));
  const removeRoom = (i) => setForm(p => ({ ...p, rooms: p.rooms.filter((_, ri) => ri !== i) }));
  const updRoom    = (i, k, v) => setForm(p => ({ ...p, rooms: p.rooms.map((r, ri) => ri === i ? { ...r, [k]: v } : r) }));

  // Personnel chip toggle
  const togglePerson = (id) => {
    const cur = form.personnel_ids || [];
    set("personnel_ids", cur.includes(id) ? cur.filter(x => x !== id) : [...cur, id]);
  };

  const FF = "'Helvetica Neue', Helvetica, Arial, sans-serif";
  const inp = (field, label, ph = "") => (
    <div>
      <div style={{ fontFamily: FF, fontSize: 11, color: "var(--text-mut)", marginBottom: 4, letterSpacing: 1, textTransform: "uppercase" }}>{label}</div>
      <input className="mo-inp" value={form[field]} placeholder={ph || label}
        onChange={e => set(field, e.target.value)} />
    </div>
  );

  return (
    <div className="ov" onClick={onClose}>
      <div className="mo" style={{ width: 560, maxHeight: "88vh", display: "flex", flexDirection: "column" }} onClick={e => e.stopPropagation()}>
        <div className="mo-hdr">
          <span className="mo-title">{existing ? "Edit Lodging" : "Add Lodging"}</span>
          <button className="mo-x" onClick={onClose}>✕</button>
        </div>
        <div className="mo-body" style={{ overflowY: "auto", flex: 1 }}>

          {/* Type toggle */}
          <div className="mo-fld">
            <div className="mo-lbl">Type</div>
            <div style={{ display: "flex", gap: 6 }}>
              {LODGING_TYPES.map(t => (
                <button key={t} onClick={() => handleTypeChange(t)}
                  style={{ fontFamily: FF, fontSize: 11, padding: "5px 16px", borderRadius: 4, cursor: "pointer", border: `1px solid ${form.type === t ? "var(--accent)" : "#2a2a2a"}`, background: form.type === t ? "var(--accent)" : "transparent", color: form.type === t ? "#0d0d0d" : "#888", fontWeight: form.type === t ? 700 : 400 }}>
                  {LODGING_TYPE_LABELS[t]}
                </button>
              ))}
            </div>
          </div>

          {/* Home type — simplified: just city/state + notes */}
          {form.type === "home" && (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                {inp("address", "City, State", "e.g. Chicago, IL")}
              </div>
              {(!artistHomeCity && !artistHomeState) && (
                <div style={{ fontFamily: FF, fontSize: 11, color: "var(--text-mut)", marginTop: 4 }}>
                  Add a Home Base city/state to the artist profile to pre-fill this automatically.
                </div>
              )}
            </>
          )}

          {/* Core fields — hotel/airbnb only */}
          {form.type !== "home" && <>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              {inp("name", form.type === "airbnb" ? "Property Name / Listing" : "Hotel Name")}
              {form.type === "hotel" && (
                <div>
                  <div style={{ fontFamily: FF, fontSize: 11, color: "var(--text-mut)", marginBottom: 4, letterSpacing: 1, textTransform: "uppercase" }}>Chain / Brand</div>
                  <select className="mo-inp" value={form.chain} onChange={e => set("chain", e.target.value)}>
                    <option value="">Select chain…</option>
                    {HOTEL_CHAINS.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              )}
              {form.type === "airbnb" && inp("address", "Address / Area")}
            </div>
            {form.type === "hotel" && <div style={{ marginTop: 10 }}>{inp("address", "Address")}</div>}

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 10 }}>
              {inp("check_in",    "Check-in",       "e.g. 3:00 PM")}
              {inp("check_out",   "Check-out",      "e.g. 11:00 AM")}
              {inp("confirmation","Confirmation #")}
              <div className="mo-fld"><div className="mo-lbl">Phone</div><PhoneInput className="mo-inp" value={form.phone||""} onChange={v => set("phone", v)} /></div>
              {inp("contact",     "Contact Name")}
            </div>

            {/* Room block */}
            <div style={{ marginTop: 14 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                <div className="mo-lbl" style={{ margin: 0 }}>Rooms</div>
                <button onClick={addRoom} style={{ fontFamily: FF, fontSize: 11, color: "var(--accent)", background: "none", border: "1px solid var(--panel-border)", borderRadius: 4, padding: "3px 10px", cursor: "pointer" }}>+ Add Room Type</button>
              </div>
              {form.rooms.length === 0 ? (
                <div style={{ fontFamily: FF, fontSize: 11, color: "var(--text-faint)" }}>No rooms added yet.</div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {form.rooms.map((r, i) => (
                    <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 80px 28px", gap: 8, alignItems: "center" }}>
                      <select className="mo-inp" value={r.type} onChange={e => updRoom(i, "type", e.target.value)}>
                        {ROOM_TYPES.map(rt => <option key={rt} value={rt}>{rt}</option>)}
                      </select>
                      <input className="mo-inp" type="number" min={1} value={r.count}
                        onChange={e => updRoom(i, "count", Math.max(1, parseInt(e.target.value) || 1))}
                        placeholder="Qty" />
                      <button onClick={() => removeRoom(i)} style={{ background: "none", border: "none", color: "var(--text-mut)", cursor: "pointer", fontSize: 14, padding: 0 }}>✕</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>}

          {/* Personnel */}
          {tourParty?.length > 0 && (
            <div style={{ marginTop: 14 }}>
              <div className="mo-lbl">Assign Personnel</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {tourParty.map(c => {
                  const on = (form.personnel_ids || []).includes(c.id);
                  return (
                    <button key={c.id} onClick={() => togglePerson(c.id)}
                      style={{ fontFamily: FF, fontSize: 11, padding: "4px 12px", borderRadius: 16, cursor: "pointer", border: `1px solid ${on ? "var(--accent)" : "#2a2a2a"}`, background: on ? "var(--accent)" : "transparent", color: on ? "#0d0d0d" : "#888", fontWeight: on ? 700 : 400 }}>
                      {c.name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Notes */}
          <div style={{ marginTop: 14 }}>
            <div className="mo-lbl">Notes</div>
            <textarea className="mo-inp" rows={2} style={{ resize: "vertical" }} value={form.notes}
              placeholder="Any special requests, notes…" onChange={e => set("notes", e.target.value)} />
          </div>

          {/* Airbnb link helper */}
          {form.type === "airbnb" && form.address && (
            <div style={{ marginTop: 10, padding: "8px 12px", background: "var(--panel-alt)", borderRadius: 8, display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontFamily: FF, fontSize: 11, color: "var(--text-mut)", flex: 1 }}>Search Airbnb near this location</span>
              <a href={`https://www.airbnb.com/s/${encodeURIComponent(form.address)}/homes`} target="_blank" rel="noreferrer"
                style={{ fontFamily: FF, fontSize: 11, color: "var(--accent)", textDecoration: "none", border: "1px solid #2a1f00", borderRadius: 4, padding: "3px 10px" }}>
                Open Airbnb ↗
              </a>
            </div>
          )}
        </div>

        <div className="mo-acts">
          {existing && !confirmDel && (
            <button className="btn-del" onClick={() => setConfirmDel(true)}>Delete</button>
          )}
          {confirmDel && (
            <div style={{ display: "flex", gap: 8 }}>
              <button className="btn-del" onClick={() => onSave({ ...form, _delete: true })}>Confirm Delete</button>
              <button className="abtn" onClick={() => setConfirmDel(false)}>Cancel</button>
            </div>
          )}
          <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
            <button className="abtn" onClick={onClose}>Cancel</button>
            <button className="btn-p" disabled={!form.name.trim()} onClick={() => onSave(form)}>
              {existing ? "Save" : "Add Lodging"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Profile Modal ─────────────────────────────────────────────────────────
function ProfileModal({ existing, existingPrivate, allTours, artistId, onSave, onClose, lightMode }) {
  const FF = "'Helvetica Neue', Helvetica, Arial, sans-serif";
  const blankPub = { name: "", preferred_name: "", role_category: "crew", role: "", photo_url: "", bio: "", email: "", phone: "", city: "", current_tour_id: "", artist_id: artistId || "", skills: [], instagram: "", notes: "", tour_routing: [], routing_tour_name: "", visible_to_network: false };
  const blankPriv = { passport_number: "", passport_expiry: "", passport_country: "", known_traveler_number: "", tsa_precheck: false, global_entry: false, date_of_birth: "", nationality: "", emergency_contact_name: "", emergency_contact_phone: "", emergency_contact_rel: "", dietary_restrictions: "", medical_notes: "" };

  const [tab, setTab] = useState("profile"); // "profile" | "travel"
  const [form, setForm] = useState(existing ? { ...blankPub, ...existing } : blankPub);
  const [priv, setPriv] = useState(existingPrivate ? { ...blankPriv, ...existingPrivate } : blankPriv);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const [skillInput, setSkillInput] = useState("");
  const [inviteCopied, setInviteCopied] = useState(false);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const setP = (k, v) => setPriv(p => ({ ...p, [k]: v }));

  const bg    = lightMode ? "#f5f2ee" : "#0d0d0d";
  const bdr   = lightMode ? "#d0ccc6" : "#1e1e1e";
  const txt   = lightMode ? "#1a1917" : "#e0e0e0";
  const txtM  = lightMode ? "#6a6764" : "#666";
  const inpBg = lightMode ? "#ffffff" : "#111";
  const tabActiveBg = lightMode ? "#e8e4de" : "#181818";

  const inpStyle = { fontFamily: FF, fontSize: 12, background: inpBg, border: `1px solid ${bdr}`, borderRadius: 4, padding: "7px 10px", color: txt, width: "100%", boxSizing: "border-box", outline: "none" };
  const labelStyle = { fontFamily: FF, fontSize: 11, letterSpacing: "1.5px", textTransform: "uppercase", color: txtM, marginBottom: 4 };

  const renderField = ({ label, fkey, type = "text", opts = {} }) => (
    <div key={fkey} style={{ marginBottom: 14 }}>
      <div style={labelStyle}>{label}</div>
      {type === "textarea"
        ? <textarea rows={3} style={{ ...inpStyle, resize: "vertical" }} value={form[fkey] || ""} onChange={e => set(fkey, e.target.value)} />
        : <input type={type} style={inpStyle} value={form[fkey] || ""} onChange={e => set(fkey, e.target.value)} autoFocus={opts.focus} />
      }
    </div>
  );

  const renderPrivField = ({ label, fkey, type = "text" }) => (
    <div key={fkey} style={{ marginBottom: 14 }}>
      <div style={labelStyle}>{label}</div>
      {type === "textarea"
        ? <textarea rows={3} style={{ ...inpStyle, resize: "vertical" }} value={priv[fkey] || ""} onChange={e => setP(fkey, e.target.value)} />
        : <input type={type} style={inpStyle} value={priv[fkey] || ""} onChange={e => setP(fkey, e.target.value)} />
      }
    </div>
  );

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoUploading(true);
    try {
      const ext = file.name.split(".").pop();
      const path = `profiles/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
      const { error } = await supabase.storage.from("advance-files").upload(path, file, { upsert: true });
      if (!error) {
        const { data: urlData } = supabase.storage.from("advance-files").getPublicUrl(path);
        set("photo_url", urlData.publicUrl);
      }
    } finally { setPhotoUploading(false); }
  };

  const addSkill = () => {
    const s = skillInput.trim();
    if (s && !form.skills.includes(s)) set("skills", [...(form.skills || []), s]);
    setSkillInput("");
  };
  const removeSkill = (s) => set("skills", form.skills.filter(x => x !== s));

  const catColor = PROFILE_ROLE_COLORS[form.role_category] || "#6a8faf";

  return (
    <div className="ov" onClick={onClose}>
      <div className="mo" style={{ width: 580, maxHeight: "90vh", overflow: "hidden", display: "flex", flexDirection: "column", padding: 0 }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ padding: "22px 28px 16px", borderBottom: `1px solid ${bdr}`, flexShrink: 0 }}>
          <div className="mo-title">{existing ? "Edit Profile" : "Create Profile"}</div>
          <div className="mo-sub">PEOPLE · {PROFILE_ROLE_LABELS[form.role_category] || "CREW"}</div>
          {/* Tabs */}
          <div style={{ display: "flex", gap: 4, marginTop: 14 }}>
            {[{ key: "profile", label: "Profile" }, { key: "travel", label: "Travel Info" }].map(t => (
              <button key={t.key} onClick={() => setTab(t.key)} style={{ fontFamily: FF, fontSize: 11, padding: "5px 14px", borderRadius: 4, border: `1px solid ${bdr}`, cursor: "pointer", background: tab === t.key ? tabActiveBg : "transparent", color: tab === t.key ? txt : txtM, fontWeight: tab === t.key ? 600 : 400, transition: "all 0.12s", letterSpacing: "0.3px" }}>
                {t.label}
              </button>
            ))}
            <span style={{ fontFamily: FF, fontSize: 11, color: txtM, marginLeft: "auto", alignSelf: "center", letterSpacing: "0.5px", display: "inline-flex", alignItems: "center", gap: 4 }}>
              {tab === "travel" ? (<><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>TM-ONLY</>) : ""}
            </span>
          </div>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: "20px 28px" }}>

          {tab === "profile" && (
            <>
              {/* Photo + Name row */}
              <div style={{ display: "flex", gap: 18, marginBottom: 20 }}>
                {/* Avatar */}
                <div style={{ flexShrink: 0 }}>
                  <div style={{ width: 72, height: 72, borderRadius: "50%", background: `${catColor}22`, border: `2px solid ${catColor}44`, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", position: "relative" }}>
                    {form.photo_url
                      ? <img src={form.photo_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      : <span style={{ fontFamily: FF, fontSize: 28, fontWeight: 700, color: catColor }}>{(form.name || "?").charAt(0).toUpperCase()}</span>
                    }
                  </div>
                  <label style={{ display: "block", textAlign: "center", marginTop: 6, fontFamily: FF, fontSize: 11, color: txtM, letterSpacing: "0.5px", cursor: "pointer" }}>
                    {photoUploading ? "Uploading…" : "Upload Photo"}
                    <input type="file" accept="image/*" style={{ display: "none" }} onChange={handlePhotoUpload} />
                  </label>
                </div>

                {/* Name fields */}
                <div style={{ flex: 1 }}>
                  {renderField({ label: "Full Name *", fkey: "name", opts: { focus: true } })}
                  {renderField({ label: "Preferred Name / Goes By", fkey: "preferred_name" })}
                </div>
              </div>

              {/* Role Category */}
              <div style={{ marginBottom: 14 }}>
                <div style={labelStyle}>Role Category</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {PROFILE_ROLE_CATS.map(cat => {
                    const col = PROFILE_ROLE_COLORS[cat];
                    const on = form.role_category === cat;
                    return (
                      <button key={cat} onClick={() => set("role_category", cat)} style={{ fontFamily: FF, fontSize: 11, padding: "5px 12px", borderRadius: 4, border: `1px solid ${on ? col : bdr}`, cursor: "pointer", background: on ? `${col}22` : "transparent", color: on ? col : txtM, fontWeight: on ? 700 : 400, transition: "all 0.12s" }}>
                        {PROFILE_ROLE_LABELS[cat]}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Role title */}
              {renderField({ label: "Title / Role (e.g. FOH Engineer, Drummer)", fkey: "role" })}

              {/* Contact row */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }}>
                <div>
                  <div style={labelStyle}>Email</div>
                  <input style={inpStyle} value={form.email || ""} onChange={e => set("email", e.target.value)} />
                </div>
                <div>
                  <div style={labelStyle}>Phone</div>
                  <PhoneInput value={form.phone||""} onChange={v => set("phone", v)} inputStyle={inpStyle} />
                </div>
              </div>

              {/* City / State / Instagram */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 80px 1fr", gap: 14, marginBottom: 14 }}>
                <div>
                  <div style={labelStyle}>City</div>
                  <input style={inpStyle} placeholder="Nashville" value={form.city || ""} onChange={e => set("city", e.target.value)} />
                </div>
                <div>
                  <div style={labelStyle}>State</div>
                  <input style={inpStyle} placeholder="TN" value={form.state || ""} onChange={e => set("state", e.target.value)} maxLength={3} />
                </div>
                <div>
                  <div style={labelStyle}>Instagram</div>
                  <input style={inpStyle} placeholder="@handle" value={form.instagram || ""} onChange={e => set("instagram", e.target.value)} />
                </div>
              </div>

              {/* Bio */}
              {renderField({ label: "Bio", fkey: "bio", type: "textarea" })}

              {/* Skills */}
              <div style={{ marginBottom: 14 }}>
                <div style={labelStyle}>Skills / Tags</div>
                <div style={{ display: "flex", gap: 6, marginBottom: 8, flexWrap: "wrap" }}>
                  {(form.skills || []).map(s => (
                    <span key={s} style={{ fontFamily: FF, fontSize: 11, background: `${catColor}22`, color: catColor, border: `1px solid ${catColor}44`, borderRadius: 16, padding: "3px 10px", display: "flex", alignItems: "center", gap: 5 }}>
                      {s}
                      <span onClick={() => removeSkill(s)} style={{ cursor: "pointer", opacity: 0.6, fontSize: 12, lineHeight: 1 }}>×</span>
                    </span>
                  ))}
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                  <input style={{ ...inpStyle, flex: 1 }} placeholder="e.g. FOH, Lighting, Stage Mgmt" value={skillInput} onChange={e => setSkillInput(e.target.value)} onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addSkill(); } }} />
                  <button className="abtn" onClick={addSkill} style={{ whiteSpace: "nowrap" }}>Add</button>
                </div>
              </div>

              {/* Notes */}
              {renderField({ label: "Notes", fkey: "notes", type: "textarea" })}

              {/* Claim status */}
              <div style={{ marginTop: 4, background: lightMode ? "#f8f5f0" : "#111", border: `1px solid ${bdr}`, borderRadius: 8, padding: "14px 16px" }}>
                {(existing?.user_id || existing?.claimed_at) ? (
                  <>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                      <span style={{ fontFamily: FF, fontSize: 11, letterSpacing: "1.5px", textTransform: "uppercase", background: "#3ecf6e22", color: "var(--success)", border: "1px solid #3ecf6e44", borderRadius: 4, padding: "2px 8px" }}>Profile Claimed</span>
                    </div>
                    <div style={{ fontFamily: FF, fontSize: 11, color: txtM, lineHeight: 1.5 }}>
                      This person has activated their profile and manages their own network settings — visibility, routing, and connections.
                    </div>
                  </>
                ) : (
                  <>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                      <span style={{ fontFamily: FF, fontSize: 11, letterSpacing: "1.5px", textTransform: "uppercase", background: lightMode ? "#e8e4de" : "#1a1a1a", color: txtM, border: `1px solid ${bdr}`, borderRadius: 4, padding: "2px 8px" }}>Unclaimed</span>
                    </div>
                    <div style={{ fontFamily: FF, fontSize: 11, color: txtM, lineHeight: 1.6, marginBottom: 12 }}>
                      Send this person an invite link to activate their profile. Once claimed, they control their own network visibility and connections — you manage the tour logistics.
                    </div>
                    {existing && (
                      <button
                        className="abtn"
                        onClick={async () => {
                          // Generate token if needed (handled by onSave → caller passes back updated profile)
                          const token = existing.invite_token || crypto.randomUUID();
                          const link = `${window.location.origin}/claim/${token}`;
                          await navigator.clipboard.writeText(link);
                          setInviteCopied(true);
                          setTimeout(() => setInviteCopied(false), 2500);
                          // Persist token if not already set
                          if (!existing.invite_token) {
                            await supabase.from('people_profiles').update({ invite_token: token, invite_sent_at: new Date().toISOString() }).eq('id', existing.id);
                          }
                        }}
                        style={{ fontSize: 11 }}
                      >
                        {inviteCopied ? "✓ Link Copied!" : "Copy Invite Link"}
                      </button>
                    )}
                    {!existing && (
                      <div style={{ fontFamily: FF, fontSize: 11, color: txtM, fontStyle: "italic" }}>Save the profile first, then generate an invite link.</div>
                    )}
                  </>
                )}
              </div>
            </>
          )}

          {tab === "travel" && (
            <>
              <div style={{ background: lightMode ? "#fff8e8" : "#1a1200", border: `1px solid ${lightMode ? "#f0d080" : "#3a2800"}`, borderRadius: 8, padding: "10px 14px", marginBottom: 20, fontFamily: FF, fontSize: 11, color: lightMode ? "#7a5500" : "#c0900a", lineHeight: 1.5 }}>
                This information is private and only accessible to Tour Managers on your team. Never share outside your organization.
              </div>

              {/* Passport */}
              <div style={{ marginBottom: 16, paddingBottom: 16, borderBottom: `1px solid ${bdr}` }}>
                <div style={{ fontFamily: FF, fontSize: 11, letterSpacing: "2px", textTransform: "uppercase", color: txtM, marginBottom: 12 }}>Passport</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                  <div><div style={labelStyle}>Passport Number</div><input style={inpStyle} value={priv.passport_number || ""} onChange={e => setP("passport_number", e.target.value)} /></div>
                  <div><div style={labelStyle}>Expiry Date</div><input style={inpStyle} placeholder="MM/YYYY" value={priv.passport_expiry || ""} onChange={e => setP("passport_expiry", e.target.value)} /></div>
                  <div><div style={labelStyle}>Country</div><input style={inpStyle} value={priv.passport_country || ""} onChange={e => setP("passport_country", e.target.value)} /></div>
                </div>
              </div>

              {/* TSA / Known Traveler */}
              <div style={{ marginBottom: 16, paddingBottom: 16, borderBottom: `1px solid ${bdr}` }}>
                <div style={{ fontFamily: FF, fontSize: 11, letterSpacing: "2px", textTransform: "uppercase", color: txtM, marginBottom: 12 }}>Travel Programs</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 12 }}>
                  <div><div style={labelStyle}>Known Traveler #</div><input style={inpStyle} value={priv.known_traveler_number || ""} onChange={e => setP("known_traveler_number", e.target.value)} /></div>
                  <div><div style={labelStyle}>Date of Birth</div><input style={inpStyle} placeholder="MM/DD/YYYY" value={priv.date_of_birth || ""} onChange={e => setP("date_of_birth", e.target.value)} /></div>
                  <div><div style={labelStyle}>Nationality</div><input style={inpStyle} value={priv.nationality || ""} onChange={e => setP("nationality", e.target.value)} /></div>
                </div>
                <div style={{ display: "flex", gap: 20 }}>
                  {[["tsa_precheck", "TSA PreCheck"], ["global_entry", "Global Entry"]].map(([k, lbl]) => (
                    <label key={k} style={{ display: "flex", alignItems: "center", gap: 7, fontFamily: FF, fontSize: 11, color: txt, cursor: "pointer" }}>
                      <input type="checkbox" checked={!!priv[k]} onChange={e => setP(k, e.target.checked)} style={{ width: 14, height: 14 }} />
                      {lbl}
                    </label>
                  ))}
                </div>
              </div>

              {/* Emergency Contact */}
              <div style={{ marginBottom: 16, paddingBottom: 16, borderBottom: `1px solid ${bdr}` }}>
                <div style={{ fontFamily: FF, fontSize: 11, letterSpacing: "2px", textTransform: "uppercase", color: txtM, marginBottom: 12 }}>Emergency Contact</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                  <div><div style={labelStyle}>Name</div><input style={inpStyle} value={priv.emergency_contact_name || ""} onChange={e => setP("emergency_contact_name", e.target.value)} /></div>
                  <div><div style={labelStyle}>Phone</div><PhoneInput value={priv.emergency_contact_phone||""} onChange={v => setP("emergency_contact_phone", v)} inputStyle={inpStyle} /></div>
                  <div><div style={labelStyle}>Relationship</div><input style={inpStyle} placeholder="e.g. Spouse, Parent" value={priv.emergency_contact_rel || ""} onChange={e => setP("emergency_contact_rel", e.target.value)} /></div>
                </div>
              </div>

              {/* Health */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontFamily: FF, fontSize: 11, letterSpacing: "2px", textTransform: "uppercase", color: txtM, marginBottom: 12 }}>Health & Dietary</div>
                {renderPrivField({ label: "Dietary Restrictions / Allergies", fkey: "dietary_restrictions", type: "textarea" })}
                {renderPrivField({ label: "Medical Notes (medications, conditions)", fkey: "medical_notes", type: "textarea" })}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: "14px 28px", borderTop: `1px solid ${bdr}`, flexShrink: 0, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            {existing && !confirmDel && (
              <button className="btn-d" style={{ fontSize: 11 }} onClick={() => setConfirmDel(true)}>Delete Profile</button>
            )}
            {existing && confirmDel && (
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <span style={{ fontFamily: FF, fontSize: 11, color: "var(--danger)" }}>Delete {form.name}?</span>
                <button className="btn-g" style={{ fontSize: 11 }} onClick={() => setConfirmDel(false)}>Cancel</button>
                <button className="btn-d" style={{ fontSize: 11 }} onClick={() => onSave(null, null)}>Yes, Delete</button>
              </div>
            )}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn-g" onClick={onClose}>Cancel</button>
            <button className="btn-p" disabled={!form.name?.trim()} onClick={() => onSave(form, priv)}>
              {existing ? "Save Changes" : "Create Profile"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Itinerary Export Config Modal ─────────────────────────────────────────
const ITINERARY_EXPORT_SECTIONS = [
  { key: "busCall",   label: "Bus Call",         hint: "Departure time for the tour bus" },
  { key: "show",      label: "Show Info",        hint: "Venue, load-in, set time, promoter" },
  { key: "lodging",   label: "Lodging",          hint: "Hotel / Airbnb with confirmation #s" },
  { key: "flights",   label: "Flights",          hint: "Flight numbers, routes, times" },
  { key: "transport", label: "Ground Transport", hint: "Car service, vans, drivers" },
  { key: "weather",   label: "Weather",          hint: "Hi/lo forecast" },
  { key: "notes",     label: "Notes",            hint: "Day-level notes" },
];

function ItineraryExportModal({ UI, onClose, onExport, onShare }) {
  const FF = "'Helvetica Neue', Helvetica, Arial, sans-serif";
  const [checked, setChecked] = useState(() =>
    Object.fromEntries(ITINERARY_EXPORT_SECTIONS.map(s => [s.key, true]))
  );
  const [sharing, setSharing] = useState(false);
  const [shared,  setShared]  = useState(false);
  const toggle = (key) => setChecked(p => ({ ...p, [key]: !p[key] }));
  const allOn  = Object.values(checked).every(Boolean);
  const toggleAll = () => setChecked(Object.fromEntries(ITINERARY_EXPORT_SECTIONS.map(s => [s.key, !allOn])));
  const handleShare = async () => {
    setSharing(true);
    await onShare(checked);
    setSharing(false);
    setShared(true);
    setTimeout(() => setShared(false), 3000);
  };

  return (
    <div onClick={onClose}
      style={{ position: "fixed", inset: 0, zIndex: 2000, background: "rgba(0,0,0,0.6)", backdropFilter: "blur(6px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: UI.bg, borderRadius: 12, width: "100%", maxWidth: 400, border: `1px solid ${UI.panelBdr}`, boxShadow: "0 20px 60px rgba(0,0,0,0.5)", overflow: "hidden" }}>

        {/* Header */}
        <div style={{ padding: "18px 20px 14px", borderBottom: `1px solid ${UI.panelBdr}`, display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontFamily: FF, fontSize: 16, fontWeight: 700, color: UI.text }}>Export Itinerary</div>
            <div style={{ fontFamily: FF, fontSize: 11, color: UI.textMut, marginTop: 3 }}>Choose what to include on each day's page</div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: UI.textMut, cursor: "pointer", fontSize: 20, lineHeight: 1, padding: "0 2px", marginTop: -2 }}>×</button>
        </div>

        {/* Section checkboxes */}
        <div style={{ padding: "12px 20px 8px" }}>
          {/* Select all toggle */}
          <button onClick={toggleAll}
            style={{ fontFamily: FF, fontSize: 11, color: UI.textMut, background: "none", border: "none", cursor: "pointer", padding: "0 0 10px", letterSpacing: 0.3 }}>
            {allOn ? "Deselect all" : "Select all"}
          </button>
          <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {ITINERARY_EXPORT_SECTIONS.map(s => (
              <label key={s.key}
                style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "8px 10px", borderRadius: 8, cursor: "pointer",
                  background: checked[s.key] ? "#3ecf6e08" : "none",
                  border: `1px solid ${checked[s.key] ? "#3ecf6e30" : "transparent"}`,
                  transition: "background 0.1s, border-color 0.1s" }}>
                <input type="checkbox" checked={checked[s.key]} onChange={() => toggle(s.key)}
                  style={{ marginTop: 3, accentColor: "var(--success)", width: 13, height: 13, flexShrink: 0, cursor: "pointer" }} />
                <div>
                  <div style={{ fontFamily: FF, fontSize: 12, fontWeight: 600, color: checked[s.key] ? UI.text : UI.textMut }}>{s.label}</div>
                  <div style={{ fontFamily: FF, fontSize: 11, color: UI.textVFnt, marginTop: 1 }}>{s.hint}</div>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: "12px 20px 18px", borderTop: `1px solid ${UI.panelBdr}`, display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 4, flexWrap: "wrap" }}>
          <button onClick={onClose}
            style={{ fontFamily: FF, fontSize: 11, color: UI.textMut, background: "none", border: `1px solid ${UI.panelBdr}`, borderRadius: 8, padding: "7px 16px", cursor: "pointer" }}>
            Cancel
          </button>
          <button onClick={handleShare} disabled={sharing}
            style={{ fontFamily: FF, fontSize: 11, fontWeight: 600, color: shared ? "#0a1a0a" : UI.text,
              background: shared ? "var(--success)" : UI.panelAlt,
              border: `1px solid ${shared ? "var(--success)" : UI.panelBdr}`,
              borderRadius: 8, padding: "7px 18px", cursor: sharing ? "wait" : "pointer",
              opacity: sharing ? 0.7 : 1, transition: "background 0.2s, border-color 0.2s, color 0.2s",
              display: "flex", alignItems: "center", gap: 6 }}>
            {sharing ? "Generating…" : shared ? "✓ Link Copied!" : <><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: "inline", verticalAlign: "middle", marginRight: 5 }}><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>Copy Share Link</>}
          </button>
          <button onClick={() => { onClose(); onExport(checked); }}
            style={{ fontFamily: FF, fontSize: 11, fontWeight: 600, color: "#0a1a0a", background: "var(--success)", border: "none", borderRadius: 8, padding: "7px 20px", cursor: "pointer" }}>
            Export PDF
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Itinerary Import Modal ────────────────────────────────────────────────
function ItineraryImportModal({ tourParty, tourDays, onConfirm, onClose }) {
  const [step,    setStep]    = useState("paste"); // "paste" | "review"
  const [raw,     setRaw]     = useState("");
  const [parsed,  setParsed]  = useState([]);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");
  const FF = "'Helvetica Neue', Helvetica, Arial, sans-serif";

  const doParse = async () => {
    if (!raw.trim()) return;
    setLoading(true); setError("");
    try {
      const days = await parseItineraryWithAI(raw, tourParty);
      if (!days.length) { setError("Nothing could be parsed — try pasting more of the itinerary text."); setLoading(false); return; }
      // Annotate each day with match status against tourDays
      const annotated = days.map(d => ({
        ...d,
        _matched: tourDays.some(td => td.date === d.date),
      }));
      setParsed(annotated);
      setStep("review");
    } catch (e) {
      setError("Parse failed — check your API connection and try again.");
    }
    setLoading(false);
  };

  // Summary counts
  const flightCount    = parsed.reduce((n, d) => n + (d.passengers || []).reduce((m, p) => m + (p.flights || []).length, 0), 0);
  const hotelCount     = parsed.filter(d => d.hotel?.name).length;
  const transportCount = parsed.reduce((n, d) => n + (d.ground_transport || []).length, 0);
  const unmatchedDates = parsed.filter(d => !d._matched).map(d => d.date);

  const typeColor = { car: "var(--success)", van: "var(--success)", rideshare: "var(--success)", train: "#6a8faf", other: "#6a8faf" };

  return (
    <div className="ov" onClick={onClose}>
      <div className="mo" style={{ width: 640, maxHeight: "85vh", display: "flex", flexDirection: "column" }} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ padding: "20px 24px 14px", borderBottom: "1px solid var(--panel-border)", flexShrink: 0 }}>
          <div className="mo-title" style={{ marginBottom: 2 }}>Import Itinerary</div>
          <div style={{ fontFamily: FF, fontSize: 11, color: "var(--text-mut)", letterSpacing: "0.5px" }}>
            {step === "paste" ? "PASTE TRAVEL AGENT ITINERARY — PDF TEXT, EMAIL, OR GDS FORMAT" : "REVIEW PARSED DATA BEFORE IMPORTING"}
          </div>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: "18px 24px" }}>

          {/* STEP 1 — Paste */}
          {step === "paste" && (
            <>
              <textarea
                style={{ width: "100%", boxSizing: "border-box", height: 320, background: "var(--panel)", border: "1px solid var(--panel-border)", borderRadius: 8, padding: "12px 14px", color: "#ccc", fontFamily: FF, fontSize: 12, lineHeight: 1.6, resize: "vertical" }}
                placeholder={"Paste itinerary text here…\n\nWorks with:\n• Travel agent booking confirmations\n• GDS-formatted itineraries (Amadeus, Sabre, Galileo)\n• Email-format travel summaries\n• PDF text copied from booking documents"}
                value={raw}
                onChange={e => setRaw(e.target.value)}
                autoFocus
              />
              {error && <div style={{ fontFamily: FF, fontSize: 11, color: "var(--danger)", marginTop: 10 }}>{error}</div>}
            </>
          )}

          {/* STEP 2 — Review */}
          {step === "review" && (
            <>
              {/* Summary bar */}
              <div style={{ display: "flex", gap: 16, padding: "10px 14px", background: "#0a1a0a", border: "1px solid #1a3a1a", borderRadius: 8, marginBottom: 16, flexWrap: "wrap" }}>
                <span style={{ fontFamily: FF, fontSize: 11, color: "var(--success)" }}>✓ {parsed.length} day{parsed.length !== 1 ? "s" : ""} parsed</span>
                {flightCount > 0    && <span style={{ fontFamily: FF, fontSize: 11, color: "#6a8faf" }}>· {flightCount} flight{flightCount !== 1 ? "s" : ""}</span>}
                {hotelCount > 0     && <span style={{ fontFamily: FF, fontSize: 11, color: "#ccc" }}>· {hotelCount} hotel night{hotelCount !== 1 ? "s" : ""}</span>}
                {transportCount > 0 && <span style={{ fontFamily: FF, fontSize: 11, color: "var(--success)" }}>· {transportCount} transport entr{transportCount !== 1 ? "ies" : "y"}</span>}
                {unmatchedDates.length > 0 && (
                  <span style={{ fontFamily: FF, fontSize: 11, color: "var(--accent)" }}>· {unmatchedDates.length} date{unmatchedDates.length !== 1 ? "s" : ""} not on tour calendar (will be skipped)</span>
                )}
              </div>

              {/* Day cards */}
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {parsed.map((day, di) => (
                  <div key={di} style={{ background: "var(--row-alt)", border: `1px solid ${day._matched ? "#1e1e1e" : "#3a2800"}`, borderRadius: 8, overflow: "hidden" }}>
                    {/* Day header */}
                    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", background: day._matched ? "#0d0d0d" : "#1a1200", borderBottom: "1px solid var(--panel-border)" }}>
                      <span style={{ fontFamily: FF, fontSize: 12, fontWeight: 700, color: "#ccc" }}>{day.date}</span>
                      {!day._matched && <span style={{ fontFamily: FF, fontSize: 11, letterSpacing: 1, color: "var(--accent)", background: "#1a1200", padding: "2px 7px", borderRadius: 4 }}>NOT ON TOUR — WILL SKIP</span>}
                      {day._matched  && <span style={{ fontFamily: FF, fontSize: 11, letterSpacing: 1, color: "var(--success)" }}>✓ MATCHED TO TOUR DAY</span>}
                    </div>

                    <div style={{ padding: "12px 14px", display: "flex", flexDirection: "column", gap: 10 }}>
                      {/* Hotel */}
                      {day.hotel?.name && (
                        <div>
                          <div style={{ fontFamily: FF, fontSize: 11, letterSpacing: 1.5, color: "var(--text-mut)", marginBottom: 5 }}>HOTEL</div>
                          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                            <span style={{ fontFamily: FF, fontSize: 12, fontWeight: 600, color: "#ccc", display: "flex", alignItems: "center", gap: 5 }}>
                              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 22V8l9-6 9 6v14"/><rect x="9" y="14" width="6" height="8"/><rect x="6" y="10" width="3" height="3"/><rect x="15" y="10" width="3" height="3"/></svg>
                              {day.hotel.name}
                            </span>
                            {day.hotel.address       && <span style={{ fontFamily: FF, fontSize: 11, color: "var(--text-mut)" }}>{day.hotel.address}</span>}
                            {day.hotel.check_in      && <span style={{ fontFamily: FF, fontSize: 11, color: "var(--text-mut)" }}>Check-in {day.hotel.check_in}</span>}
                            {day.hotel.check_out     && <span style={{ fontFamily: FF, fontSize: 11, color: "var(--text-mut)" }}>Check-out {day.hotel.check_out}</span>}
                            {day.hotel.confirmation  && <span style={{ fontFamily: FF, fontSize: 11, color: "var(--text-mut)" }}>#{day.hotel.confirmation}</span>}
                          </div>
                        </div>
                      )}

                      {/* Flights */}
                      {(day.passengers || []).some(p => p.flights?.length > 0) && (
                        <div>
                          <div style={{ fontFamily: FF, fontSize: 11, letterSpacing: 1.5, color: "var(--text-mut)", marginBottom: 5 }}>FLIGHTS</div>
                          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                            {(day.passengers || []).map((pax, pi) =>
                              (pax.flights || []).map((fl, fi) => {
                                const matched = tourParty.find(c =>
                                  c.name.toLowerCase().includes((pax.name || "").toLowerCase().split(" ")[0]) ||
                                  (pax.name || "").toLowerCase().includes(c.name.toLowerCase().split(" ")[0])
                                );
                                return (
                                  <div key={`${pi}-${fi}`} style={{ display: "flex", alignItems: "center", gap: 10, padding: "7px 10px", background: "#0c1520", border: "1px solid #1a3a5a", borderRadius: 4 }}>
                                    <span style={{ fontFamily: FF, fontSize: 11, fontWeight: 700, color: "#6a8faf", minWidth: 65, flexShrink: 0 }}>{fl.airline_code}{fl.flight_number}</span>
                                    <span style={{ fontFamily: FF, fontSize: 11, color: "#ccc" }}>{fl.origin} → {fl.destination}</span>
                                    {fl.scheduled_dep && <span style={{ fontFamily: FF, fontSize: 11, color: "var(--text-mut)" }}>{new Date(fl.scheduled_dep).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}</span>}
                                    {fl.cabin_class   && <span style={{ fontFamily: FF, fontSize: 11, color: "var(--text-faint)" }}>{fl.cabin_class}</span>}
                                    <span style={{ fontFamily: FF, fontSize: 11, marginLeft: "auto", flexShrink: 0 }}>
                                      <span style={{ color: matched ? "var(--success)" : "#555" }}>{pax.name}{matched ? ` ✓` : ""}</span>
                                    </span>
                                  </div>
                                );
                              })
                            )}
                          </div>
                        </div>
                      )}

                      {/* Ground transport */}
                      {(day.ground_transport || []).length > 0 && (
                        <div>
                          <div style={{ fontFamily: FF, fontSize: 11, letterSpacing: 1.5, color: "var(--text-mut)", marginBottom: 5 }}>GROUND TRANSPORT</div>
                          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                            {(day.ground_transport || []).map((tr, ti) => (
                              <div key={ti} style={{ display: "flex", alignItems: "center", gap: 10, padding: "7px 10px", background: "var(--bg)", border: "1px solid var(--panel-border)", borderRadius: 4 }}>
                                <span style={{ fontFamily: FF, fontSize: 11, letterSpacing: 1, color: typeColor[tr.type] || "#555", background: "#0a1a0a", padding: "2px 7px", borderRadius: 4, flexShrink: 0, textTransform: "uppercase" }}>{TRANSPORT_TYPE_LABELS[tr.type] || tr.type}</span>
                                {tr.pickup_time     && <span style={{ fontFamily: FF, fontSize: 11, fontWeight: 600, color: "#ccc", flexShrink: 0 }}>{tr.pickup_time}</span>}
                                <span style={{ fontFamily: FF, fontSize: 11, color: "var(--text-mut)", flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                  {[tr.pickup_location, tr.dropoff_location].filter(Boolean).join(" → ") || tr.company || ""}
                                </span>
                                {tr.confirmation_number && <span style={{ fontFamily: FF, fontSize: 11, color: "var(--text-mut)", flexShrink: 0 }}>#{tr.confirmation_number}</span>}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: "14px 24px", borderTop: "1px solid var(--panel-border)", display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
          {step === "paste" ? (
            <>
              <button style={{ fontFamily: FF, fontSize: 11, background: "none", border: "none", color: "var(--text-mut)", cursor: "pointer", padding: 0 }} onClick={onClose}>Cancel</button>
              <button
                style={{ fontFamily: FF, fontSize: 11, fontWeight: 600, padding: "8px 20px", background: loading ? "#333" : "var(--accent)", color: "#0d0d0d", border: "none", borderRadius: 4, cursor: loading ? "default" : "pointer" }}
                disabled={loading || !raw.trim()}
                onClick={doParse}>
                {loading ? "Parsing…" : "Parse Itinerary →"}
              </button>
            </>
          ) : (
            <>
              <button style={{ fontFamily: FF, fontSize: 11, background: "none", border: "none", color: "var(--text-mut)", cursor: "pointer", padding: 0 }} onClick={() => setStep("paste")}>← Back</button>
              <button
                style={{ fontFamily: FF, fontSize: 11, fontWeight: 600, padding: "8px 20px", background: "var(--success)", color: "#0d0d0d", border: "none", borderRadius: 4, cursor: "pointer" }}
                onClick={() => onConfirm(parsed.filter(d => d._matched))}>
                Import {parsed.filter(d => d._matched).length} Day{parsed.filter(d => d._matched).length !== 1 ? "s" : ""}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Vehicle Modal ─────────────────────────────────────────────────────────
function VehicleModal({ existing, onSave, onClose }) {
  const blank = { label: "", type: "bus", driver_name: "", driver_phone: "", notes: "" };
  const [form, setForm] = useState(existing ? { ...existing } : blank);
  const [confirmDel, setConfirmDel] = useState(false);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const FF = "'Helvetica Neue', Helvetica, Arial, sans-serif";
  const inp = (k, label, opts = {}) => (
    <div className="mo-fld">
      <div className="mo-lbl">{label}</div>
      <input className="mo-inp" value={form[k] || ""} onChange={e => set(k, e.target.value)} autoFocus={opts.focus} />
    </div>
  );
  return (
    <div className="ov" onClick={onClose}>
      <div className="mo" onClick={e => e.stopPropagation()}>
        <div className="mo-title">{existing ? "Edit Vehicle" : "Add Vehicle"}</div>
        <div className="mo-sub">TOUR VEHICLE</div>
        {inp("label", "Vehicle Label *", { focus: true })}
        <div className="mo-fld">
          <div className="mo-lbl">Type</div>
          <div style={{ display: "flex", gap: 6 }}>
            {VEHICLE_TYPES.map(t => (
              <button key={t} onClick={() => set("type", t)}
                style={{ fontFamily: FF, fontSize: 11, padding: "5px 12px", borderRadius: 4, border: "1px solid", cursor: "pointer",
                  background: form.type === t ? "var(--success)" : "none",
                  color: form.type === t ? "#0d0d0d" : "#555",
                  borderColor: form.type === t ? "var(--success)" : "#1e1e1e",
                  fontWeight: form.type === t ? 700 : 400,
                }}>{VEHICLE_TYPE_LABELS[t]}</button>
            ))}
          </div>
        </div>
        <div className="mo-row">{inp("driver_name", "Driver Name")}<div className="mo-fld"><div className="mo-lbl">Driver Phone</div><PhoneInput className="mo-inp" value={form.driver_phone||""} onChange={v => set("driver_phone", v)} /></div></div>
        <div className="mo-fld">
          <div className="mo-lbl">Notes</div>
          <textarea className="mo-inp" rows={2} value={form.notes || ""} onChange={e => set("notes", e.target.value)} style={{ resize: "vertical" }} />
        </div>
        {existing && !confirmDel && (
          <div style={{ marginTop: 18 }}><button className="btn-d" onClick={() => setConfirmDel(true)}>Remove Vehicle</button></div>
        )}
        {existing && confirmDel && (
          <div className="mo-del-confirm" style={{ marginTop: 18 }}>
            <div style={{ fontFamily: FF, fontSize: 11, color: "var(--danger)", marginBottom: 12 }}>Remove {form.label} from this tour?</div>
            <div style={{ display: "flex", gap: 8 }}>
              <button className="btn-g" onClick={() => setConfirmDel(false)}>Cancel</button>
              <button className="btn-d" onClick={() => onSave(null)}>Yes, Remove</button>
            </div>
          </div>
        )}
        <div className="mo-acts">
          <button className="btn-g" onClick={onClose}>Cancel</button>
          <button className="btn-p" disabled={!form.label?.trim()} onClick={() => onSave(form)}>
            {existing ? "Save Changes" : "Add Vehicle"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Ground Transport Modal ─────────────────────────────────────────────────
function GroundTransportModal({ existing, onSave, onClose }) {
  const blank = { type: "car", pickup_time: "", pickup_location: "", dropoff_location: "",
    company: "", driver_name: "", driver_phone: "", confirmation_number: "", passenger_notes: "", notes: "" };
  const [form, setForm] = useState(existing ? { ...existing } : blank);
  const [confirmDel, setConfirmDel] = useState(false);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const FF = "'Helvetica Neue', Helvetica, Arial, sans-serif";
  const inp = (k, label, opts = {}) => (
    <div className="mo-fld">
      <div className="mo-lbl">{label}</div>
      <input className="mo-inp" type={opts.type || "text"} value={form[k] || ""} onChange={e => set(k, e.target.value)} autoFocus={opts.focus} placeholder={opts.placeholder || ""} />
    </div>
  );
  return (
    <div className="ov" onClick={onClose}>
      <div className="mo" style={{ width: 520 }} onClick={e => e.stopPropagation()}>
        <div className="mo-title">{existing ? "Edit Ground Transport" : "Add Ground Transport"}</div>
        <div className="mo-sub">GROUND TRANSPORT</div>
        <div className="mo-fld">
          <div className="mo-lbl">Type</div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {TRANSPORT_TYPES.map(t => (
              <button key={t} onClick={() => set("type", t)}
                style={{ fontFamily: FF, fontSize: 11, padding: "5px 12px", borderRadius: 4, border: "1px solid", cursor: "pointer",
                  background: form.type === t ? "#6a8faf" : "none",
                  color: form.type === t ? "#0d0d0d" : "#555",
                  borderColor: form.type === t ? "#6a8faf" : "#1e1e1e",
                  fontWeight: form.type === t ? 700 : 400,
                }}>{TRANSPORT_TYPE_LABELS[t]}</button>
            ))}
          </div>
        </div>
        <div className="mo-row">
          <div className="mo-fld" style={{ flex: "0 0 130px" }}>
            <div className="mo-lbl">Pickup Time</div>
            <input className="mo-inp" type="time" value={form.pickup_time || ""} onChange={e => set("pickup_time", e.target.value)} />
          </div>
          {inp("company", "Company / Service")}
        </div>
        <div className="mo-row">{inp("pickup_location", "Pickup Location")}{inp("dropoff_location", "Dropoff Location")}</div>
        <div className="mo-row">{inp("driver_name", "Driver Name")}<div className="mo-fld"><div className="mo-lbl">Driver Phone</div><PhoneInput className="mo-inp" value={form.driver_phone||""} onChange={v => set("driver_phone", v)} /></div></div>
        <div className="mo-row">{inp("confirmation_number", "Confirmation #")}{inp("passenger_notes", "Passengers / Notes")}</div>
        {existing && !confirmDel && (
          <div style={{ marginTop: 18 }}><button className="btn-d" onClick={() => setConfirmDel(true)}>Delete</button></div>
        )}
        {existing && confirmDel && (
          <div className="mo-del-confirm" style={{ marginTop: 18 }}>
            <div style={{ fontFamily: FF, fontSize: 11, color: "var(--danger)", marginBottom: 12 }}>Delete this transport entry?</div>
            <div style={{ display: "flex", gap: 8 }}>
              <button className="btn-g" onClick={() => setConfirmDel(false)}>Cancel</button>
              <button className="btn-d" onClick={() => onSave(null)}>Yes, Delete</button>
            </div>
          </div>
        )}
        <div className="mo-acts">
          <button className="btn-g" onClick={onClose}>Cancel</button>
          <button className="btn-p" onClick={() => onSave(form)}>{existing ? "Save Changes" : "Add Transport"}</button>
        </div>
      </div>
    </div>
  );
}

// ── Flight Modal ───────────────────────────────────────────────────────────
function FlightModal({ existing, dayId, tourParty, onSave, onClose }) {
  const blank = { flight_number: "", airline: "", airline_code: "", origin: "", destination: "",
    origin_city: "", destination_city: "", scheduled_dep: "", scheduled_arr: "", booking_ref: "",
    cabin_class: "", passenger_ids: [] };
  const [form, setForm] = useState(existing ? { ...existing, passenger_ids: existing.passenger_ids || [] } : blank);
  const [confirmDel, setConfirmDel] = useState(false);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const FF = "'Helvetica Neue', Helvetica, Arial, sans-serif";
  const inp = (k, label, opts = {}) => (
    <div className="mo-fld">
      <div className="mo-lbl">{label}</div>
      <input className="mo-inp" type={opts.type || "text"} value={form[k] || ""} onChange={e => set(k, e.target.value)} placeholder={opts.placeholder || ""} autoFocus={opts.focus} />
    </div>
  );
  const togglePassenger = (id) => set("passenger_ids",
    form.passenger_ids.includes(id)
      ? form.passenger_ids.filter(x => x !== id)
      : [...form.passenger_ids, id]
  );
  return (
    <div className="ov" onClick={onClose}>
      <div className="mo" style={{ width: 560 }} onClick={e => e.stopPropagation()}>
        <div className="mo-title">{existing ? "Edit Flight" : "Add Flight"}</div>
        <div className="mo-sub">FLIGHT DETAILS</div>
        <div className="mo-row">
          <div className="mo-fld" style={{ flex: "0 0 80px" }}>
            <div className="mo-lbl">IATA Code</div>
            <input className="mo-inp" value={form.airline_code} onChange={e => set("airline_code", e.target.value.toUpperCase())} placeholder="AA" autoFocus maxLength={3} />
          </div>
          <div className="mo-fld" style={{ flex: "0 0 100px" }}>
            <div className="mo-lbl">Flight #</div>
            <input className="mo-inp" value={form.flight_number} onChange={e => set("flight_number", e.target.value.toUpperCase())} placeholder="1234" />
          </div>
          <div className="mo-fld">
            <div className="mo-lbl">Airline</div>
            <input className="mo-inp" value={form.airline} onChange={e => set("airline", e.target.value)} placeholder="American Airlines" />
          </div>
        </div>
        <div className="mo-row">
          <div className="mo-fld">
            <div className="mo-lbl">Origin (IATA)</div>
            <input className="mo-inp" value={form.origin} onChange={e => set("origin", e.target.value.toUpperCase())} placeholder="LAX" maxLength={4} />
          </div>
          <div className="mo-fld">
            <div className="mo-lbl">Destination (IATA)</div>
            <input className="mo-inp" value={form.destination} onChange={e => set("destination", e.target.value.toUpperCase())} placeholder="JFK" maxLength={4} />
          </div>
        </div>
        <div className="mo-row">
          {inp("origin_city",      "Departing City")}
          {inp("destination_city", "Arriving City")}
        </div>
        <div className="mo-row">
          {inp("scheduled_dep", "Departure", { type: "datetime-local" })}
          {inp("scheduled_arr", "Arrival",   { type: "datetime-local" })}
        </div>
        <div className="mo-row">
          {inp("booking_ref",  "Booking Ref / PNR")}
          <div className="mo-fld">
            <div className="mo-lbl">Cabin Class</div>
            <select className="mo-sel" value={form.cabin_class} onChange={e => set("cabin_class", e.target.value)}>
              <option value="">—</option>
              <option>Economy</option><option>Premium Economy</option>
              <option>Business</option><option>First</option>
            </select>
          </div>
        </div>
        {tourParty?.length > 0 && (
          <div className="mo-fld">
            <div className="mo-lbl">Passengers</div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 4 }}>
              {tourParty.map(c => (
                <button key={c.id} onClick={() => togglePassenger(c.id)}
                  style={{ fontFamily: FF, fontSize: 11, padding: "4px 10px", borderRadius: 4, border: "1px solid", cursor: "pointer",
                    background: form.passenger_ids.includes(c.id) ? "#6a8faf" : "none",
                    color: form.passenger_ids.includes(c.id) ? "#0d0d0d" : "#555",
                    borderColor: form.passenger_ids.includes(c.id) ? "#6a8faf" : "#1e1e1e",
                    fontWeight: form.passenger_ids.includes(c.id) ? 700 : 400,
                  }}>{(c.name || "?").split(" ")[0]}</button>
              ))}
            </div>
          </div>
        )}
        {existing && !confirmDel && (
          <div style={{ marginTop: 18 }}><button className="btn-d" onClick={() => setConfirmDel(true)}>Delete Flight</button></div>
        )}
        {existing && confirmDel && (
          <div className="mo-del-confirm" style={{ marginTop: 18 }}>
            <div style={{ fontFamily: FF, fontSize: 11, color: "var(--danger)", marginBottom: 12 }}>Delete this flight?</div>
            <div style={{ display: "flex", gap: 8 }}>
              <button className="btn-g" onClick={() => setConfirmDel(false)}>Cancel</button>
              <button className="btn-d" onClick={() => onSave(null)}>Yes, Delete</button>
            </div>
          </div>
        )}
        <div className="mo-acts">
          <button className="btn-g" onClick={onClose}>Cancel</button>
          <button className="btn-p" disabled={!form.flight_number?.trim() && !form.origin?.trim()} onClick={() => onSave(form)}>
            {existing ? "Save Changes" : "Add Flight"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Batch Deposit Modal ────────────────────────────────────────────────────
const PAYMENT_METHODS = ["", "Check", "Wire Transfer", "ACH", "Cash", "Venmo", "Zelle"];
const DEP_OFFSETS = [
  { label: "15 days before", days: 15 },
  { label: "30 days before", days: 30 },
  { label: "45 days before", days: 45 },
  { label: "60 days before", days: 60 },
  { label: "90 days before", days: 90 },
];
function BatchDepositModal({ shows, venues, onSave, onClose, lightMode }) {
  const M = lightMode ? {
    rowBg: "#ede9e3", rowBdr: "#d0ccc6", hdrBg: "#e2dfd9",
    text: "#1a1917", textMut: "#6a6764", textFnt: "#9a9794",
    fieldBg: "#f8f5f0", fieldBdr: "#d0ccc6",
    amber: "#c47f00", amdBdr: "#d4880a", green: "#1a9e4a",
    applySec: "#f5f0e8", applyBdr: "#d4880a",
  } : {
    rowBg: "#111", rowBdr: "#1e1e1e", hdrBg: "#0d0d0d",
    text: "#e0e0e0", textMut: "#555", textFnt: "#444",
    fieldBg: "#0d0d0d", fieldBdr: "#1e1e1e",
    amber: "var(--accent)", amdBdr: "#3a2800", green: "var(--success)",
    applySec: "#111a00", applyBdr: "#3a2800",
  };
  const inpStyle = { background: M.fieldBg, border: `1px solid ${M.fieldBdr}`, borderRadius: 4, padding: "4px 8px", color: M.text, fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 12, width: "100%", boxSizing: "border-box" };
  const [dateMode, setDateMode] = useState("fixed");   // "fixed" | "offset"
  const [globalDate, setGlobalDate] = useState("");
  const [globalOffset, setGlobalOffset] = useState(30);
  const [globalMethod, setGlobalMethod] = useState("");
  const [globalReceived, setGlobalReceived] = useState(false);
  const sorted = [...shows].sort((a, b) => a.date.localeCompare(b.date));
  const [rows, setRows] = useState(() => sorted.map(s => ({
    id: s.id, date: s.date, venueName: venues[s.venueId]?.name || "TBD",
    depositDue: s.settlement?.depositDue || "",
    paymentMethod: s.settlement?.paymentMethod || "",
    depositReceived: !!s.settlement?.depositReceived,
  })));
  const upd = (id, k, v) => setRows(p => p.map(r => r.id === id ? { ...r, [k]: v } : r));
  const applyGlobal = () => setRows(p => p.map(r => {
    const updated = { ...r };
    if (dateMode === "fixed" && globalDate) updated.depositDue = globalDate;
    if (dateMode === "offset") {
      const d = new Date(r.date + "T12:00:00");
      d.setDate(d.getDate() - globalOffset);
      updated.depositDue = d.toISOString().slice(0, 10);
    }
    if (globalMethod) updated.paymentMethod = globalMethod;
    if (globalReceived) updated.depositReceived = true;
    return updated;
  }));
  const handleSave = () => {
    const overrides = {};
    rows.forEach(r => { overrides[r.id] = { depositDue: r.depositDue, paymentMethod: r.paymentMethod, depositReceived: r.depositReceived }; });
    onSave(overrides);
    onClose();
  };
  const FF = "'Helvetica Neue', Helvetica, Arial, sans-serif";
  return (
    <div className="ov" onClick={onClose}>
      <div className="mo" style={{ width: 800, maxWidth: "96vw" }} onClick={e => e.stopPropagation()}>
        <div className="mo-title">Batch Deposit</div>
        <div className="mo-sub">{shows.length} SHOW{shows.length !== 1 ? "S" : ""} · SET DEPOSIT FIELDS ACROSS THE TOUR</div>
        {/* Global apply section */}
        <div style={{ background: M.applySec, border: `1px solid ${M.applyBdr}`, borderRadius: 8, padding: "14px 16px", marginBottom: 18 }}>
          <div style={{ fontFamily: FF, fontSize: 11, letterSpacing: "1.5px", color: M.amber, marginBottom: 12 }}>APPLY TO ALL SHOWS</div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
            <div>
              <div style={{ fontFamily: FF, fontSize: 11, color: M.textMut, marginBottom: 4 }}>Date Mode</div>
              <select value={dateMode} onChange={e => setDateMode(e.target.value)} style={{ ...inpStyle, width: "auto" }}>
                <option value="fixed">Fixed Date</option>
                <option value="offset">Days Before Show</option>
              </select>
            </div>
            {dateMode === "fixed" ? (
              <div>
                <div style={{ fontFamily: FF, fontSize: 11, color: M.textMut, marginBottom: 4 }}>Deposit Due Date</div>
                <input type="date" value={globalDate} onChange={e => setGlobalDate(e.target.value)} style={{ ...inpStyle, width: 160 }} />
              </div>
            ) : (
              <div>
                <div style={{ fontFamily: FF, fontSize: 11, color: M.textMut, marginBottom: 4 }}>Offset</div>
                <select value={globalOffset} onChange={e => setGlobalOffset(Number(e.target.value))} style={{ ...inpStyle, width: "auto" }}>
                  {DEP_OFFSETS.map(o => <option key={o.days} value={o.days}>{o.label}</option>)}
                </select>
              </div>
            )}
            <div>
              <div style={{ fontFamily: FF, fontSize: 11, color: M.textMut, marginBottom: 4 }}>Payment Method</div>
              <select value={globalMethod} onChange={e => setGlobalMethod(e.target.value)} style={{ ...inpStyle, width: 160 }}>
                {PAYMENT_METHODS.map(m => <option key={m} value={m}>{m || "— no change —"}</option>)}
              </select>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 7, paddingBottom: 2 }}>
              <input type="checkbox" id="glob-recv" style={{ accentColor: M.green }} checked={globalReceived} onChange={e => setGlobalReceived(e.target.checked)} />
              <label htmlFor="glob-recv" style={{ fontFamily: FF, fontSize: 12, color: M.textMut, cursor: "pointer", whiteSpace: "nowrap" }}>Mark received</label>
            </div>
            <button className="btn-p" style={{ padding: "6px 16px", flexShrink: 0, alignSelf: "flex-end" }} onClick={applyGlobal}>Apply to All</button>
          </div>
        </div>
        {/* Per-show rows */}
        <div style={{ display: "flex", flexDirection: "column", gap: 1, maxHeight: "50vh", overflowY: "auto" }}>
          {/* Header */}
          <div style={{ display: "grid", gridTemplateColumns: "90px 1fr 150px 160px 100px", gap: 10, padding: "6px 12px", background: M.hdrBg, borderRadius: 4, marginBottom: 4 }}>
            {["Date", "Venue", "Deposit Due", "Payment Method", "Received"].map(h => (
              <div key={h} style={{ fontFamily: FF, fontSize: 11, letterSpacing: "1px", color: M.textFnt }}>{h.toUpperCase()}</div>
            ))}
          </div>
          {rows.map(r => (
            <div key={r.id} style={{ display: "grid", gridTemplateColumns: "90px 1fr 150px 160px 100px", gap: 10, padding: "8px 12px", background: M.rowBg, border: `1px solid ${M.rowBdr}`, borderRadius: 8, alignItems: "center" }}>
              <div style={{ fontFamily: FF, fontSize: 11, color: M.textMut }}>{r.date}</div>
              <div style={{ fontFamily: FF, fontSize: 12, fontWeight: 600, color: M.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.venueName}</div>
              <input type="date" value={r.depositDue} onChange={e => upd(r.id, "depositDue", e.target.value)} style={{ ...inpStyle }} />
              <select value={r.paymentMethod} onChange={e => upd(r.id, "paymentMethod", e.target.value)} style={{ ...inpStyle }}>
                {PAYMENT_METHODS.map(m => <option key={m} value={m}>{m || "—"}</option>)}
              </select>
              <div style={{ display: "flex", justifyContent: "center" }}>
                <input type="checkbox" style={{ accentColor: M.green }} checked={r.depositReceived} onChange={e => upd(r.id, "depositReceived", e.target.checked)} />
              </div>
            </div>
          ))}
        </div>
        <div className="mo-acts" style={{ marginTop: 18 }}>
          <button className="btn-g" onClick={onClose}>Cancel</button>
          <button className="btn-p" onClick={handleSave}>Save All</button>
        </div>
      </div>
    </div>
  );
}

// ── Advance Links Modal ────────────────────────────────────────────────────
function AdvanceLinksModal({ tour, artist, venues, onClose, onGenerateLink, lightMode }) {
  const FF = "'Helvetica Neue',Helvetica,Arial,sans-serif";
  const M = lightMode ? {
    rowBg: "#ede9e3", rowBdr: "#d0ccc6",
    text: "#1a1917", textMut: "#6a6764", textFnt: "#9a9794",
    filterOnBg: "#d8d4ce", filterOnTxt: "#1a1917", filterOnBdr: "#b8b4ae",
    filterOffTxt: "#8a8784", filterOffBdr: "#d0ccc6",
    btnTxt: "#6a6764", btnBdr: "#ccc8c2",
    green: "#1a9e4a",
    badgeHlBg: "#d8f0e4", badgeHlTxt: "#1a6a3a",
    badgeAmdBg: "#fff3cc", badgeAmdTxt: "#8a5a00",
  } : {
    rowBg: "#111", rowBdr: "#1e1e1e",
    text: "#e0e0e0", textMut: "#555", textFnt: "#444",
    filterOnBg: "#1e1e1e", filterOnTxt: "#e0e0e0", filterOnBdr: "#333",
    filterOffTxt: "#555", filterOffBdr: "#1e1e1e",
    btnTxt: "#888", btnBdr: "#1e1e1e",
    green: "var(--success)",
    badgeHlBg: "#0d2818", badgeHlTxt: "var(--success)",
    badgeAmdBg: "#1e1200", badgeAmdTxt: "var(--accent)",
  };

  const [filter, setFilter] = useState("pending");
  const [copied, setCopied] = useState({});
  const [generating, setGenerating] = useState({});

  const shows = [...(tour?.shows || [])].sort((a, b) => a.date.localeCompare(b.date));
  const pendingCount = shows.filter(s => !s.advanceToken).length;
  const displayed = filter === "pending" ? shows.filter(s => !s.advanceToken) : shows;

  const handleCopy = (show) => {
    const url = `${window.location.origin}/advance/${show.advanceToken}`;
    navigator.clipboard.writeText(url).catch(() => {});
    setCopied(p => ({ ...p, [show.id]: true }));
    setTimeout(() => setCopied(p => ({ ...p, [show.id]: false })), 2000);
  };

  const handleGenerate = async (show) => {
    setGenerating(p => ({ ...p, [show.id]: true }));
    await onGenerateLink(show.id);
    setGenerating(p => ({ ...p, [show.id]: false }));
  };

  const badge = (label, txt, bg) => (
    <span style={{ fontFamily: FF, fontSize: 11, letterSpacing: "1px",
      color: txt, background: bg, padding: "2px 7px", borderRadius: 4, whiteSpace: "nowrap" }}>{label}</span>
  );

  return (
    <div className="ov" onClick={onClose}>
      <div className="mo" style={{ width: 760, maxWidth: "94vw" }} onClick={e => e.stopPropagation()}>
        <div className="mo-title">Advance Links</div>
        <div className="mo-sub">{tour.name} · {pendingCount} SHOW{pendingCount !== 1 ? "S" : ""} WITHOUT A PORTAL LINK</div>
        <div style={{ display: "flex", gap: 6, marginBottom: 18 }}>
          {[["pending", `Pending (${pendingCount})`], ["all", `All Shows (${shows.length})`]].map(([f, label]) => (
            <button key={f} onClick={() => setFilter(f)} style={{
              fontFamily: FF, fontSize: 11, letterSpacing: "1px",
              padding: "4px 12px", borderRadius: 4, cursor: "pointer", border: "1px solid",
              background: filter === f ? M.filterOnBg : "none",
              color: filter === f ? M.filterOnTxt : M.filterOffTxt,
              borderColor: filter === f ? M.filterOnBdr : M.filterOffBdr,
            }}>{label.toUpperCase()}</button>
          ))}
        </div>
        {displayed.length === 0 ? (
          <div style={{ fontFamily: FF, fontSize: 14, color: M.green, padding: "28px 0", textAlign: "center" }}>
            ✓ All shows have advance portal links.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 5, maxHeight: "55vh", overflowY: "auto" }}>
            {displayed.map(s => {
              const v = venues[s.venueId];
              const hasLink = !!s.advanceToken;
              const url = hasLink ? `${window.location.origin}/advance/${s.advanceToken}` : null;
              const isCopied = copied[s.id];
              const isGenerating = generating[s.id];
              return (
                <div key={s.id} style={{ display: "flex", alignItems: "center", gap: 10,
                  padding: "10px 14px", background: M.rowBg, border: `1px solid ${M.rowBdr}`, borderRadius: 8 }}>
                  <div style={{ minWidth: 88, fontFamily: FF, fontSize: 11, color: M.textMut, flexShrink: 0 }}>{s.date}</div>
                  <div style={{ flex: 1, fontFamily: FF, fontSize: 14, fontWeight: 600, color: M.text, textAlign: "left",
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {v?.name || "TBD"}
                    {(v?.city || v?.addressState) && (
                      <span style={{ fontWeight: 400, color: M.textMut, marginLeft: 6 }}>
                        {[v.city, v.addressState].filter(Boolean).join(", ")}
                      </span>
                    )}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                    {hasLink ? badge("LIVE", M.badgeHlTxt, M.badgeHlBg) : badge("NO LINK", M.badgeAmdTxt, M.badgeAmdBg)}
                    {hasLink ? (
                      <>
                        <button onClick={() => handleCopy(s)}
                          style={{ fontFamily: FF, fontSize: 11, lineHeight: 1, color: isCopied ? M.green : M.btnTxt,
                            background: "none", border: `1px solid ${isCopied ? M.green : M.btnBdr}`, borderRadius: 4,
                            padding: "5px 12px", cursor: "pointer", flexShrink: 0, transition: "all 0.15s",
                            display: "flex", alignItems: "center", gap: 4 }}>
                          {isCopied ? "✓ Copied" : "Copy"}
                        </button>
                        <button onClick={() => window.open(url, "_blank")}
                          style={{ fontFamily: FF, fontSize: 11, lineHeight: 1, color: M.btnTxt,
                            background: "none", border: `1px solid ${M.btnBdr}`, borderRadius: 4,
                            padding: "5px 12px", cursor: "pointer", flexShrink: 0,
                            display: "flex", alignItems: "center", gap: 4 }}>
                          Open
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ width: 10, height: 10 }}><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                        </button>
                      </>
                    ) : (
                      <button onClick={() => handleGenerate(s)} disabled={isGenerating}
                        style={{ fontFamily: FF, fontSize: 11, color: M.badgeAmdTxt,
                          background: "none", border: `1px solid ${M.badgeAmdBg}`, borderRadius: 4,
                          padding: "5px 12px", cursor: isGenerating ? "default" : "pointer",
                          flexShrink: 0, opacity: isGenerating ? 0.6 : 1 }}>
                        {isGenerating ? "Generating…" : "+ Generate"}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <div className="mo-acts" style={{ marginTop: 18 }}>
          <button className="btn-g" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ── Tour Health Modal ──────────────────────────────────────────────────────
function TourHealthModal({ tour, artist, venues, onClose, onGoToShow, lightMode }) {
  const todayStr = new Date().toISOString().slice(0, 10);
  const [filter, setFilter] = useState("issues");
  const M = lightMode ? {
    rowBg: "#ede9e3", rowBdr: "#d0ccc6",
    text: "#1a1917", textMut: "#6a6764", textFnt: "#9a9794",
    filterOnBg: "#d8d4ce", filterOnTxt: "#1a1917", filterOnBdr: "#b8b4ae",
    filterOffTxt: "#8a8784", filterOffBdr: "#d0ccc6",
    btnTxt: "#6a6764", btnBdr: "#ccc8c2",
    green: "#1a9e4a",
    badgeHlBg: "#d8f0e4", badgeHlTxt: "#1a6a3a",
    badgeRedBg: "#fdeaea", badgeRedTxt: "#b03030",
    badgeAmdBg: "#fff3cc", badgeAmdTxt: "#8a5a00",
  } : {
    rowBg: "#111", rowBdr: "#1e1e1e",
    text: "#e0e0e0", textMut: "#555", textFnt: "#444",
    filterOnBg: "#1e1e1e", filterOnTxt: "#e0e0e0", filterOnBdr: "#333",
    filterOffTxt: "#555", filterOffBdr: "#1e1e1e",
    btnTxt: "#888", btnBdr: "#1e1e1e",
    green: "var(--success)",
    badgeHlBg: "#0d2818", badgeHlTxt: "var(--success)",
    badgeRedBg: "#1e0d0d", badgeRedTxt: "var(--danger)",
    badgeAmdBg: "#1e1200", badgeAmdTxt: "var(--accent)",
  };
  const rows = tour.shows.map(s => {
    const v = venues[s.venueId];
    const noEmail    = !(s.settlement?.promoterEmail || v?.promoterEmail || "").trim();
    const noToken    = !s.advanceToken;
    const depOverdue = !!(s.settlement?.depositDue && !s.settlement?.depositReceived && s.settlement.depositDue < todayStr);
    const notSettled = s.date < todayStr && !s.settlement?.settled;
    const healthy    = !noEmail && !noToken && !depOverdue && !notSettled;
    return { s, v, noEmail, noToken, depOverdue, notSettled, healthy };
  }).sort((a, b) => a.s.date.localeCompare(b.s.date));
  const issueCount = rows.filter(r => !r.healthy).length;
  const displayed  = filter === "issues" ? rows.filter(r => !r.healthy) : rows;
  const badge = (label, txt, bg) => (
    <span style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, letterSpacing: "1px",
      color: txt, background: bg, padding: "2px 7px", borderRadius: 4, whiteSpace: "nowrap" }}>{label}</span>
  );
  return (
    <div className="ov" onClick={onClose}>
      <div className="mo" style={{ width: 760, maxWidth: "94vw" }} onClick={e => e.stopPropagation()}>
        <div className="mo-title">Tour Health</div>
        <div className="mo-sub">{tour.name} · {issueCount} ISSUE{issueCount !== 1 ? "S" : ""} ACROSS {tour.shows.length} SHOW{tour.shows.length !== 1 ? "S" : ""}</div>
        <div style={{ display: "flex", gap: 6, marginBottom: 18 }}>
          {[["issues", `Issues Only (${issueCount})`], ["all", `All Shows (${tour.shows.length})`]].map(([f, label]) => (
            <button key={f} onClick={() => setFilter(f)} style={{
              fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, letterSpacing: "1px",
              padding: "4px 12px", borderRadius: 4, cursor: "pointer", border: "1px solid",
              background: filter === f ? M.filterOnBg : "none",
              color: filter === f ? M.filterOnTxt : M.filterOffTxt,
              borderColor: filter === f ? M.filterOnBdr : M.filterOffBdr,
            }}>{label.toUpperCase()}</button>
          ))}
        </div>
        {displayed.length === 0 ? (
          <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 14, color: M.green,
            padding: "28px 0", textAlign: "center" }}>
            ✓ All clear — no issues found on this tour.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 5, maxHeight: "55vh", overflowY: "auto" }}>
            {displayed.map(({ s, v, noEmail, noToken, depOverdue, notSettled, healthy }) => (
              <div key={s.id} style={{ display: "flex", alignItems: "center", gap: 10,
                padding: "10px 14px", background: M.rowBg, border: `1px solid ${M.rowBdr}`, borderRadius: 8 }}>
                <div style={{ minWidth: 88, fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
                  fontSize: 11, color: M.textMut, flexShrink: 0 }}>{s.date}</div>
                <div style={{ flex: 1, fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
                  fontSize: 14, fontWeight: 600, color: M.text, textAlign: "left",
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {v?.name || "TBD"}
                  {(v?.city || v?.addressState) ? (
                    <span style={{ fontWeight: 400, color: M.textMut, marginLeft: 6 }}>
                      {[v.city, v.addressState].filter(Boolean).join(", ")}
                    </span>
                  ) : null}
                </div>
                <div style={{ display: "flex", gap: 5, flexShrink: 0, flexWrap: "wrap", justifyContent: "flex-end", maxWidth: 280 }}>
                  {healthy    && badge("HEALTHY",     M.badgeHlTxt,  M.badgeHlBg)}
                  {noEmail    && badge("NO EMAIL",    M.badgeRedTxt, M.badgeRedBg)}
                  {noToken    && badge("NO ADVANCE",  M.badgeAmdTxt, M.badgeAmdBg)}
                  {depOverdue && badge("DEP OVERDUE", M.badgeRedTxt, M.badgeRedBg)}
                  {notSettled && badge("SETTLE UP",   M.badgeRedTxt, M.badgeRedBg)}
                </div>
                <button onClick={() => onGoToShow(s.id)} style={{
                  fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: M.btnTxt,
                  background: "none", border: `1px solid ${M.btnBdr}`, borderRadius: 4,
                  padding: "5px 12px", cursor: "pointer", flexShrink: 0
                }}>Go →</button>
              </div>
            ))}
          </div>
        )}
        <div className="mo-acts" style={{ marginTop: 18 }}>
          <button className="btn-g" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ── MemberPreviewModal ──────────────────────────────────────────────────────
function MemberPreviewModal({ profile, data, venues, profiles, dayLodging, dayFlights, dayTransport, loadDayLodging, loadDayFlights, loadDayTransport, lightMode, onClose, broadcasts = [] }) {
  const [tab,     setTab]     = useState("today");
  const [days,    setDays]    = useState([]);
  const [loading, setLoading] = useState(true);
  const FF = "'Helvetica Neue', Helvetica, Arial, sans-serif";

  const allTours = data.artists.flatMap(a => a.tours);
  const tour     = allTours.find(t => t.id === profile.current_tour_id) || null;
  const artist   = tour ? data.artists.find(a => a.tours.some(t2 => t2.id === tour.id)) : null;

  useEffect(() => {
    if (!tour) { setLoading(false); return; }
    supabase.from("tour_days").select("*").eq("tour_id", tour.id).order("date", { ascending: true })
      .then(({ data: rows }) => { setDays(rows || []); setLoading(false); });
  }, [tour?.id]);

  const todayStr     = new Date().toISOString().slice(0, 10);
  const todayDay     = days.find(d => d.date === todayStr) || null;
  const todayShows   = tour ? tour.shows.filter(s => s.date === todayStr) : [];
  const todayLodging = todayDay ? (dayLodging[todayDay.id]   || null) : null;
  const todayFlights = todayDay ? (dayFlights[todayDay.id]   || [])   : [];
  const todayTransp  = todayDay ? (dayTransport[todayDay.id] || [])   : [];

  useEffect(() => {
    if (!todayDay) return;
    loadDayLodging(todayDay.id);
    loadDayFlights(todayDay.id);
    loadDayTransport(todayDay.id);
  }, [todayDay?.id]);

  const BG   = lightMode ? "#f5f4f1" : "#0d0d0d";
  const TEXT = lightMode ? "#1a1917" : "#e0e0e0";
  const MUT  = lightMode ? "#7a7775" : "#888";
  const AMB  = lightMode ? "#c47f00" : "var(--accent)";
  const BDR  = lightMode ? "#d0ccc6" : "#222";
  const CARD = lightMode ? "#ffffff" : "#111111";
  const NAVBG = lightMode ? "#eeebe6" : "#0a0a0a";

  const Section = ({ label, children }) => (
    <div style={{ marginBottom: 10, background: CARD, border: `1px solid ${BDR}`, borderRadius: 8, padding: "14px 16px" }}>
      <div style={{ fontFamily: FF, fontSize: 11, letterSpacing: 1.5, color: AMB, textTransform: "uppercase", marginBottom: 8 }}>{label}</div>
      {children}
    </div>
  );

  // Inline SVG nav icons matching real MobileApp.jsx NavIcon
  const PrevNavIcon = ({ id, color }) => {
    const s = { width: 20, height: 20, viewBox: "0 0 20 20", fill: "none", stroke: color, strokeWidth: "1.5", strokeLinecap: "round", strokeLinejoin: "round" };
    if (id === "today") return <svg {...s}><circle cx="10" cy="10" r="3.5"/><line x1="10" y1="1.5" x2="10" y2="4"/><line x1="10" y1="16" x2="10" y2="18.5"/><line x1="1.5" y1="10" x2="4" y2="10"/><line x1="16" y1="10" x2="18.5" y2="10"/><line x1="3.7" y1="3.7" x2="5.6" y2="5.6"/><line x1="14.4" y1="14.4" x2="16.3" y2="16.3"/><line x1="16.3" y1="3.7" x2="14.4" y2="5.6"/><line x1="5.6" y1="14.4" x2="3.7" y2="16.3"/></svg>;
    if (id === "shows") return <svg {...s}><rect x="7" y="2" width="6" height="9" rx="3"/><path d="M4 10a6 6 0 0 0 12 0"/><line x1="10" y1="16" x2="10" y2="18.5"/><line x1="7" y1="18.5" x2="13" y2="18.5"/></svg>;
    if (id === "tour") return <svg {...s}><rect x="2" y="4" width="16" height="14" rx="2"/><line x1="2" y1="8.5" x2="18" y2="8.5"/><line x1="6" y1="2" x2="6" y2="6"/><line x1="14" y1="2" x2="14" y2="6"/><line x1="6" y1="13" x2="6" y2="13" stroke={color} strokeWidth="2.5" strokeLinecap="round"/><line x1="10" y1="13" x2="10" y2="13" stroke={color} strokeWidth="2.5" strokeLinecap="round"/><line x1="14" y1="13" x2="14" y2="13" stroke={color} strokeWidth="2.5" strokeLinecap="round"/></svg>;
    if (id === "schedule") return <svg {...s}><rect x="2" y="3" width="16" height="15" rx="2"/><line x1="2" y1="7.5" x2="18" y2="7.5"/><line x1="6" y1="1.5" x2="6" y2="5"/><line x1="14" y1="1.5" x2="14" y2="5"/><line x1="6" y1="11.5" x2="14" y2="11.5"/><line x1="6" y1="15" x2="10" y2="15"/></svg>;
    if (id === "people") return <svg {...s}><circle cx="10" cy="6.5" r="3"/><path d="M3.5 18c0-3.6 2.9-6.5 6.5-6.5s6.5 2.9 6.5 6.5"/></svg>;
    if (id === "more") return <svg {...s} stroke="none"><circle cx="5" cy="10" r="1.5" fill={color}/><circle cx="10" cy="10" r="1.5" fill={color}/><circle cx="15" cy="10" r="1.5" fill={color}/></svg>;
    return null;
  };

  // Helper: parse "HH:MM" or "H:MMam/pm" → minutes since midnight for sorting
  const parseMins = (t) => {
    if (!t) return null;
    const s = t.trim();
    const m12 = s.match(/^(\d{1,2}):(\d{2})\s*(am|pm)$/i);
    if (m12) {
      let h = parseInt(m12[1]); const mn = parseInt(m12[2]); const ap = m12[3].toLowerCase();
      if (ap === "pm" && h !== 12) h += 12;
      if (ap === "am" && h === 12) h = 0;
      return h * 60 + mn;
    }
    const m24 = s.match(/^(\d{1,2}):(\d{2})$/);
    if (m24) return parseInt(m24[1]) * 60 + parseInt(m24[2]);
    return null;
  };

  const fmtMins = (m) => {
    if (m == null) return "";
    const h = Math.floor(m / 60) % 24; const mn = m % 60;
    const ap = h >= 12 ? "pm" : "am"; const h12 = h % 12 || 12;
    return `${h12}:${mn.toString().padStart(2, "0")}${ap}`;
  };

  // Build a flat sorted schedule from a show's advance data (mirrors buildSortedSchedule in MobileApp)
  const buildSortedSched = (show) => {
    if (!show) return [];
    const sc = (show.advanceTmData || {}).schedule || {};
    const items = [];
    const push = (label, timeStr, sub) => {
      const mins = parseMins(timeStr);
      if (mins != null) items.push({ label, time: timeStr, mins, sub: sub || null });
    };
    push("Venue access", sc.venue_access);
    push("Doors", sc.doors_public);
    (sc.bill || []).forEach(act => {
      if (act.load) push(`${act.name || "Act"} · Load-in`, act.load);
      if (act.sc)   push(`${act.name || "Act"} · Soundcheck`, act.sc);
      if (act.set)  push(`${act.name || "Act"} · Set`, act.set);
    });
    (sc.changeovers || []).forEach(co => {
      if (co.time) push(co.label || "Changeover", co.time);
    });
    push("Noise curfew", sc.noise_curfew);
    push("Hard curfew", sc.curfew);
    return items.sort((a, b) => a.mins - b.mins);
  };

  // ── TODAY ──
  const renderToday = () => {
    if (!tour)   return <div style={{ padding: 32, textAlign: "center", fontFamily: FF, fontSize: 12, color: MUT }}>No tour assigned.</div>;
    if (loading) return <div style={{ padding: 32, textAlign: "center", fontFamily: FF, fontSize: 12, color: MUT }}>Loading…</div>;
    const hasContent = todayDay?.bus_call || todayShows.length || todayLodging || todayFlights.length || todayTransp.length || todayDay?.notes;
    const tourBroadcasts = broadcasts.filter(b => b.tour_id === tour?.id);
    return (
      <div style={{ padding: "14px 14px 80px", overflowY: "auto", height: "100%" }}>
        {/* Broadcasts */}
        {tourBroadcasts.length > 0 && (
          <div style={{ margin: "0 0 10px", padding: "12px 14px", background: AMB + "18", border: `1.5px solid ${AMB}55`, borderRadius: 8 }}>
            <div style={{ fontFamily: FF, fontSize: 11, letterSpacing: "1.5px", color: AMB, fontWeight: 700, textTransform: "uppercase", marginBottom: 8 }}>
              Updates from Tour Manager
            </div>
            {tourBroadcasts.map(b => (
              <div key={b.id} style={{ marginBottom: 8, paddingBottom: 8, borderBottom: `1px solid ${AMB}22` }}>
                <div style={{ fontFamily: FF, fontSize: 14, color: TEXT, lineHeight: 1.5 }}>{b.body}</div>
                <div style={{ fontFamily: FF, fontSize: 11, color: MUT, marginTop: 3 }}>{b.sender_name} · {new Date(b.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</div>
              </div>
            ))}
          </div>
        )}
        {/* Bus call */}
        {todayDay?.bus_call && (
          <Section label="Bus Call">
            <div style={{ fontFamily: FF, fontSize: 28, fontWeight: 700, color: TEXT, letterSpacing: "-0.5px" }}>{todayDay.bus_call}</div>
          </Section>
        )}
        {/* Show schedule */}
        {todayShows.map(s => {
          const v = venues[s.venueId];
          const sched = buildSortedSched(s);
          return (
            <Section key={s.id} label="Schedule">
              <div style={{ fontFamily: FF, fontSize: 14, fontWeight: 600, color: TEXT, marginBottom: 4 }}>{v?.name || "Show"}</div>
              {v?.city && <div style={{ fontFamily: FF, fontSize: 11, color: MUT, marginBottom: 8 }}>{v.city}{v.state ? `, ${v.state}` : ""}</div>}
              {sched.length > 0 ? sched.map((it, i) => (
                <div key={i} style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 5 }}>
                  <div style={{ fontFamily: FF, fontSize: 11, color: AMB, minWidth: 44, fontWeight: 600 }}>{it.time}</div>
                  <div style={{ fontFamily: FF, fontSize: 11, color: TEXT }}>{it.label}</div>
                </div>
              )) : <div style={{ fontFamily: FF, fontSize: 11, color: MUT }}>No schedule set yet.</div>}
            </Section>
          );
        })}
        {/* Hotel */}
        {todayLodging && (
          <Section label="Hotel">
            <div style={{ fontFamily: FF, fontSize: 14, fontWeight: 600, color: TEXT }}>{todayLodging.name}</div>
            {todayLodging.address && <div style={{ fontFamily: FF, fontSize: 11, color: MUT, marginTop: 2 }}>{todayLodging.address}</div>}
            {todayLodging.check_in_time && <div style={{ fontFamily: FF, fontSize: 11, color: MUT, marginTop: 4 }}>Check-in {todayLodging.check_in_time}</div>}
            {todayLodging.check_out_time && <div style={{ fontFamily: FF, fontSize: 11, color: MUT }}>Check-out {todayLodging.check_out_time}</div>}
          </Section>
        )}
        {/* Flights */}
        {todayFlights.length > 0 && (
          <Section label="Flights">
            {todayFlights.map((f, i) => (
              <div key={i} style={{ marginBottom: i < todayFlights.length - 1 ? 10 : 0 }}>
                <div style={{ fontFamily: FF, fontSize: 12, fontWeight: 600, color: TEXT }}>{f.airline || ""} {f.flight_number || ""}</div>
                <div style={{ fontFamily: FF, fontSize: 11, color: MUT }}>{f.departure_city} → {f.arrival_city}</div>
                {f.departure_time && <div style={{ fontFamily: FF, fontSize: 11, color: MUT }}>Dep. {f.departure_time}{f.arrival_time ? ` · Arr. ${f.arrival_time}` : ""}</div>}
              </div>
            ))}
          </Section>
        )}
        {/* Transport */}
        {todayTransp.length > 0 && (
          <Section label="Transport">
            {todayTransp.map((t, i) => (
              <div key={i} style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: i < todayTransp.length - 1 ? 6 : 0 }}>
                {t.pickup_time && <span style={{ fontFamily: FF, fontSize: 11, color: AMB, fontWeight: 600, minWidth: 44 }}>{t.pickup_time}</span>}
                <span style={{ fontFamily: FF, fontSize: 11, color: TEXT }}>{t.pickup_location || t.type || "Transport"}</span>
              </div>
            ))}
          </Section>
        )}
        {/* Notes */}
        {todayDay?.notes && (
          <Section label="Notes">
            <div style={{ fontFamily: FF, fontSize: 12, color: TEXT, whiteSpace: "pre-wrap", lineHeight: 1.5 }}>{todayDay.notes}</div>
          </Section>
        )}
        {!hasContent && (
          <div style={{ padding: "40px 20px", textAlign: "center", fontFamily: FF, fontSize: 12, color: MUT }}>
            {todayDay ? "No data for today yet." : "Off day — no tour day entry for today."}
          </div>
        )}
      </div>
    );
  };

  // ── SHOWS ──
  const renderShows = () => {
    if (!tour) return <div style={{ padding: 32, textAlign: "center", fontFamily: FF, fontSize: 12, color: MUT }}>No tour assigned.</div>;
    const sorted = [...tour.shows].sort((a, b) => a.date.localeCompare(b.date));
    return (
      <div style={{ padding: "8px 0 80px", overflowY: "auto", height: "100%" }}>
        <div style={{ padding: "12px 16px 8px", fontFamily: FF, fontSize: 11, letterSpacing: 1.5, color: MUT, textTransform: "uppercase" }}>
          {tour.name} · {sorted.length} show{sorted.length !== 1 ? "s" : ""}
        </div>
        {sorted.map(s => {
          const v = venues[s.venueId];
          const isToday = s.date === todayStr;
          const isPast  = s.date < todayStr;
          const d2 = new Date(s.date + "T12:00:00");
          const dateLabel = d2.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
          return (
            <div key={s.id} style={{
              display: "flex", alignItems: "center", gap: 12,
              padding: "11px 16px",
              borderBottom: `1px solid ${BDR}`,
              background: isToday ? `${AMB}10` : "transparent",
              opacity: isPast ? 0.5 : 1,
            }}>
              <div style={{ width: 36, flexShrink: 0 }}>
                <div style={{ fontFamily: FF, fontSize: 11, color: isToday ? AMB : MUT, letterSpacing: 0.5, textTransform: "uppercase" }}>{dateLabel.split(" ")[0]}</div>
                <div style={{ fontFamily: FF, fontSize: 16, fontWeight: 700, color: isToday ? AMB : TEXT, lineHeight: 1.1 }}>{d2.getDate()}</div>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontFamily: FF, fontSize: 12, fontWeight: 600, color: TEXT, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{v?.name || "TBA"}</div>
                <div style={{ fontFamily: FF, fontSize: 11, color: MUT, marginTop: 1 }}>{v?.city || ""}{ v?.state ? `, ${v.state}` : ""}</div>
              </div>
              {isToday && <span style={{ fontFamily: FF, fontSize: 11, letterSpacing: 1, color: AMB, background: `${AMB}18`, border: `1px solid ${AMB}44`, borderRadius: 4, padding: "2px 6px" }}>TODAY</span>}
              <svg viewBox="0 0 20 20" fill="none" stroke={MUT} strokeWidth="1.5" strokeLinecap="round" style={{ width: 14, height: 14, flexShrink: 0 }}><path d="M7 4l6 6-6 6"/></svg>
            </div>
          );
        })}
        {!sorted.length && <div style={{ padding: 32, textAlign: "center", fontFamily: FF, fontSize: 12, color: MUT }}>No shows on this tour yet.</div>}
      </div>
    );
  };

  // ── TOUR ──
  const renderTour = () => {
    if (!tour)   return <div style={{ padding: 32, textAlign: "center", fontFamily: FF, fontSize: 12, color: MUT }}>No tour assigned.</div>;
    if (loading) return <div style={{ padding: 32, textAlign: "center", fontFamily: FF, fontSize: 12, color: MUT }}>Loading…</div>;
    return (
      <div style={{ padding: "8px 0 80px", overflowY: "auto", height: "100%" }}>
        {days.map(d => {
          const isToday  = d.date === todayStr;
          const showsDay = tour.shows.filter(s => s.date === d.date);
          const d2       = new Date(d.date + "T12:00:00");
          const wday     = d2.toLocaleDateString("en-US", { weekday: "short" }).toUpperCase();
          const mday     = d2.getDate();
          const mname    = d2.toLocaleDateString("en-US", { month: "short" }).toUpperCase();
          return (
            <div key={d.id} style={{
              display: "flex", alignItems: "center", gap: 12,
              padding: "11px 16px",
              borderBottom: `1px solid ${BDR}`,
              background: isToday ? `${AMB}10` : "transparent",
              borderLeft: `3px solid ${isToday ? AMB : "transparent"}`,
            }}>
              {/* Date block */}
              <div style={{ width: 34, flexShrink: 0, textAlign: "center" }}>
                <div style={{ fontFamily: FF, fontSize: 11, color: isToday ? AMB : MUT, letterSpacing: 0.5 }}>{wday}</div>
                <div style={{ fontFamily: FF, fontSize: 16, fontWeight: 700, color: isToday ? AMB : TEXT, lineHeight: 1.1 }}>{mday}</div>
                <div style={{ fontFamily: FF, fontSize: 11, color: MUT, letterSpacing: 0.3 }}>{mname}</div>
              </div>
              {/* Show dot */}
              <div style={{ width: 7, height: 7, borderRadius: "50%", flexShrink: 0, background: showsDay.length ? AMB : BDR }} />
              {/* Content */}
              <div style={{ flex: 1, minWidth: 0 }}>
                {showsDay.map(s => {
                  const v = venues[s.venueId];
                  return <div key={s.id} style={{ fontFamily: FF, fontSize: 11, fontWeight: 600, color: TEXT, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{v?.name || "Show"}{v?.city ? <span style={{ fontWeight: 400, color: MUT }}> · {v.city}</span> : null}</div>;
                })}
                {!showsDay.length && d.type && <div style={{ fontFamily: FF, fontSize: 11, color: MUT, textTransform: "capitalize" }}>{d.type.replace(/_/g, " ")}</div>}
                {!showsDay.length && !d.type && <div style={{ fontFamily: FF, fontSize: 11, color: MUT }}>Day off</div>}
              </div>
              {/* Bus call */}
              {d.bus_call && <div style={{ fontFamily: FF, fontSize: 11, color: AMB, flexShrink: 0 }}>{d.bus_call}</div>}
              {/* Chevron */}
              <svg viewBox="0 0 20 20" fill="none" stroke={MUT} strokeWidth="1.5" strokeLinecap="round" style={{ width: 13, height: 13, flexShrink: 0 }}><path d="M7 4l6 6-6 6"/></svg>
            </div>
          );
        })}
        {!days.length && <div style={{ padding: 32, textAlign: "center", fontFamily: FF, fontSize: 12, color: MUT }}>No tour days added yet.</div>}
      </div>
    );
  };

  // ── SCHEDULE ──
  const renderSchedule = () => {
    if (!tour) return <div style={{ padding: 32, textAlign: "center", fontFamily: FF, fontSize: 12, color: MUT }}>No tour assigned.</div>;
    const todayShow  = todayShows[0] || null;
    const schedItems = buildSortedSched(todayShow);
    const d2 = new Date(todayStr + "T12:00:00");
    const dateLabel = d2.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });
    return (
      <div style={{ padding: "0 0 80px", overflowY: "auto", height: "100%" }}>
        {/* Day header */}
        <div style={{ padding: "12px 16px 10px", borderBottom: `1px solid ${BDR}`, display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontFamily: FF, fontSize: 11, fontWeight: 600, color: TEXT }}>{dateLabel}</span>
          {todayShow && venues[todayShow.venueId] && (
            <span style={{ fontFamily: FF, fontSize: 11, color: AMB, background: `${AMB}18`, border: `1px solid ${AMB}44`, borderRadius: 4, padding: "2px 7px", letterSpacing: 0.5 }}>SHOW</span>
          )}
        </div>
        {schedItems.length > 0 ? (
          <div style={{ padding: "8px 16px" }}>
            {schedItems.map((it, i) => (
              <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "9px 0", borderBottom: i < schedItems.length - 1 ? `1px solid ${BDR}` : "none" }}>
                <div style={{ fontFamily: FF, fontSize: 11, color: AMB, fontWeight: 600, minWidth: 52, flexShrink: 0 }}>{it.time}</div>
                <div style={{ fontFamily: FF, fontSize: 12, color: TEXT }}>{it.label}</div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ padding: "40px 20px", textAlign: "center", fontFamily: FF, fontSize: 12, color: MUT }}>
            {todayDay ? "No schedule set for today." : "No tour day for today."}
          </div>
        )}
      </div>
    );
  };

  // ── PEOPLE ──
  const renderPeople = () => {
    const tourParty = (profiles || []).filter(p => p.current_tour_id === tour?.id);
    return (
      <div style={{ padding: "0 0 80px", overflowY: "auto", height: "100%" }}>
        <div style={{ padding: "12px 16px 8px", fontFamily: FF, fontSize: 11, letterSpacing: 1.5, color: MUT, textTransform: "uppercase" }}>
          Touring Party · {tourParty.length}
        </div>
        {tourParty.map(p => {
          const initials = (p.preferred_name || p.name || "?").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
          return (
            <div key={p.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 16px", borderBottom: `1px solid ${BDR}` }}>
              <div style={{ width: 36, height: 36, borderRadius: "50%", flexShrink: 0, overflow: "hidden", background: lightMode ? "#e0ddd8" : "#222", display: "flex", alignItems: "center", justifyContent: "center" }}>
                {p.photo_url
                  ? <img src={p.photo_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  : <span style={{ fontFamily: FF, fontSize: 12, fontWeight: 700, color: MUT }}>{initials}</span>}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontFamily: FF, fontSize: 12, fontWeight: 600, color: TEXT, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.preferred_name || p.name}</div>
                {p.role && <div style={{ fontFamily: FF, fontSize: 11, color: MUT, marginTop: 1 }}>{p.role}</div>}
              </div>
              {p.phone && (
                <div style={{ display: "flex", gap: 6 }}>
                  <div style={{ width: 28, height: 28, borderRadius: "50%", background: lightMode ? "#e0ddd8" : "#222", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <svg viewBox="0 0 20 20" fill="none" stroke={MUT} strokeWidth="1.5" strokeLinecap="round" style={{ width: 13, height: 13 }}><path d="M4 2h3l1.5 4-2 1.5a12 12 0 0 0 6 6L14 11.5l4 1.5v3a1 1 0 0 1-1 1A17 17 0 0 1 3 3a1 1 0 0 1 1-1z"/></svg>
                  </div>
                </div>
              )}
            </div>
          );
        })}
        {!tourParty.length && (
          <div style={{ padding: "40px 20px", textAlign: "center", fontFamily: FF, fontSize: 12, color: MUT }}>
            No one assigned to this tour yet.
          </div>
        )}
      </div>
    );
  };

  // ── MORE ──
  const renderMore = () => (
    <div style={{ padding: "20px 20px 80px", overflowY: "auto", height: "100%" }}>
      {/* Role / tour context */}
      <div style={{ background: CARD, border: `1px solid ${BDR}`, borderRadius: 8, padding: "14px 16px", marginBottom: 10 }}>
        <div style={{ fontFamily: FF, fontSize: 11, letterSpacing: 1.5, color: AMB, textTransform: "uppercase", marginBottom: 8 }}>Currently Viewing As</div>
        <div style={{ fontFamily: FF, fontSize: 14, fontWeight: 600, color: TEXT }}>{profile.preferred_name || profile.name}</div>
        {profile.role && <div style={{ fontFamily: FF, fontSize: 11, color: MUT, marginTop: 2 }}>{profile.role}</div>}
        {artist && <div style={{ fontFamily: FF, fontSize: 11, color: MUT, marginTop: 2 }}>{artist.name}</div>}
        {tour   && <div style={{ fontFamily: FF, fontSize: 11, color: MUT }}>{tour.name}</div>}
      </div>
      {/* Menu items */}
      {[
        { label: "Switch Tour / Artist" },
        { label: lightMode ? "Switch to Dark Mode" : "Switch to Light Mode" },
      ].map((item, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "13px 16px", background: CARD, border: `1px solid ${BDR}`, borderRadius: 8, marginBottom: 6, cursor: "not-allowed", opacity: 0.5 }}>
          <span style={{ fontFamily: FF, fontSize: 14, color: TEXT }}>{item.label}</span>
          <svg viewBox="0 0 20 20" fill="none" stroke={MUT} strokeWidth="1.5" strokeLinecap="round" style={{ width: 14, height: 14 }}><path d="M7 4l6 6-6 6"/></svg>
        </div>
      ))}
      <div style={{ marginTop: 12, padding: "13px 16px", background: CARD, border: `1px solid ${BDR}`, borderRadius: 8, cursor: "not-allowed", opacity: 0.5 }}>
        <span style={{ fontFamily: FF, fontSize: 14, color: "var(--danger)" }}>Sign Out</span>
      </div>
      <div style={{ marginTop: 24, textAlign: "center", fontFamily: FF, fontSize: 11, letterSpacing: 2, color: MUT, textTransform: "uppercase" }}>Advanced</div>
    </div>
  );

  const PREVIEW_TABS = [
    { id: "today",    label: "Today"    },
    { id: "shows",    label: "Shows"    },
    { id: "tour",     label: "Tour"     },
    { id: "schedule", label: "Schedule" },
    { id: "people",   label: "People"   },
    { id: "more",     label: "More"     },
  ];

  const renderTab = () => {
    switch (tab) {
      case "today":    return renderToday();
      case "shows":    return renderShows();
      case "tour":     return renderTour();
      case "schedule": return renderSchedule();
      case "people":   return renderPeople();
      case "more":     return renderMore();
      default:         return null;
    }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", zIndex: 9500, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={onClose}>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }} onClick={e => e.stopPropagation()}>
        {/* Label row */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", padding: "0 6px" }}>
          <div style={{ fontFamily: FF, fontSize: 12, color: "var(--text-mid)" }}>
            Previewing as <strong style={{ color: "#fff" }}>{profile.name}</strong>
            {tour ? <span style={{ color: "var(--text-mut)" }}> · {tour.name}</span> : <span style={{ color: "var(--text-mut)" }}> · No tour assigned</span>}
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "var(--text-mut)", cursor: "pointer", fontSize: 20, lineHeight: 1, padding: "0 0 0 16px" }}>✕</button>
        </div>
        {/* Phone frame */}
        <div style={{ width: 375, height: 720, background: BG, borderRadius: 999, border: "8px solid #1a1a1a", overflow: "hidden", display: "flex", flexDirection: "column", boxShadow: "0 32px 96px rgba(0,0,0,0.85), inset 0 0 0 1px #2a2a2a" }}>
          {/* Status bar */}
          <div style={{ padding: "14px 24px 6px", display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
            <div style={{ fontFamily: FF, fontSize: 12, fontWeight: 700, color: TEXT }}>9:41</div>
            <div style={{ width: 90, height: 22, background: lightMode ? "#e0ddd8" : "#0a0a0a", borderRadius: 12, flexShrink: 0 }} />
            <div style={{ fontFamily: FF, fontSize: 11, color: MUT }}>●●●</div>
          </div>
          {/* App top bar */}
          <div style={{ padding: "8px 20px 10px", borderBottom: `1px solid ${BDR}`, flexShrink: 0 }}>
            <div style={{ fontFamily: FF, fontSize: 14, fontWeight: 700, color: TEXT, letterSpacing: "-0.2px" }}>
              {artist?.name || "—"}
              <span style={{ fontSize: 11, fontWeight: 400, color: MUT, marginLeft: 8, letterSpacing: 0 }}>ADVANCD</span>
            </div>
            <div style={{ fontFamily: FF, fontSize: 11, color: MUT, marginTop: 1 }}>{tour?.name || "No tour assigned"}</div>
          </div>
          {/* Content */}
          <div style={{ flex: 1, overflow: "hidden", position: "relative" }}>
            {renderTab()}
          </div>
          {/* Bottom nav — 6 tabs matching real app */}
          <div style={{ display: "flex", borderTop: `1px solid ${BDR}`, background: NAVBG, flexShrink: 0, paddingBottom: 6 }}>
            {PREVIEW_TABS.map(t => {
              const active = tab === t.id;
              const col    = active ? AMB : MUT;
              return (
                <button key={t.id} onClick={() => setTab(t.id)} style={{ flex: 1, padding: "9px 0 5px", background: "none", border: "none", cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", gap: 3, borderTop: `2px solid ${active ? AMB : "transparent"}`, marginTop: -1 }}>
                  <PrevNavIcon id={t.id} color={col} />
                  <span style={{ fontFamily: FF, fontSize: 11, letterSpacing: "0.5px", textTransform: "uppercase", color: col, fontWeight: active ? 700 : 400 }}>{t.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── ExpenseModal ────────────────────────────────────────────────────────────
function ExpenseModal({ existing, tourId, teamId, onSave, onClose, lightMode }) {
  const FF = "'Helvetica Neue', Helvetica, Arial, sans-serif";
  const BG  = lightMode ? "#f5f2ed" : "#111";
  const BDR = lightMode ? "#dedad4" : "#222";
  const TEXT = lightMode ? "#1a1814" : "#e8e8e8";
  const MUT  = lightMode ? "#8a8480" : "#666";
  const PANEL = lightMode ? "#eeebe6" : "#161616";

  const [category,    setCategory]    = useState(existing?.category    || "misc");
  const [description, setDescription] = useState(existing?.description || "");
  const [amount,      setAmount]      = useState(existing?.amount      != null ? String(existing.amount) : "");
  const [currency,    setCurrency]    = useState(existing?.currency    || "USD");
  const [date,        setDate]        = useState(existing?.date        || "");
  const [notes,       setNotes]       = useState(existing?.notes       || "");
  const [saving,      setSaving]      = useState(false);

  const inpStyle = { fontFamily: FF, fontSize: 12, padding: "8px 10px", borderRadius: 8, border: `1px solid ${BDR}`, background: lightMode ? "#fff" : "#0d0d0d", color: TEXT, outline: "none", width: "100%" };
  const lblStyle = { fontFamily: FF, fontSize: 11, letterSpacing: 0.5, color: MUT, textTransform: "uppercase", marginBottom: 5, display: "block" };

  const handleSave = async () => {
    if (!description.trim() || !amount) return;
    setSaving(true);
    const rec = {
      ...(existing?.id ? { id: existing.id } : {}),
      tour_id: tourId,
      team_id: teamId,
      category,
      description: description.trim(),
      amount: parseFloat(amount) || 0,
      currency,
      date: date || null,
      notes: notes.trim() || null,
    };
    await onSave(rec);
    setSaving(false);
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 9800, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={onClose}>
      <div style={{ width: 480, background: BG, border: `1px solid ${BDR}`, borderRadius: 12, overflow: "hidden", boxShadow: "0 24px 64px rgba(0,0,0,0.6)" }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ padding: "16px 20px", borderBottom: `1px solid ${BDR}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ fontFamily: FF, fontSize: 14, fontWeight: 700, color: TEXT }}>{existing ? "Edit Expense" : "Add Expense"}</div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: MUT, cursor: "pointer", fontSize: 20, lineHeight: 1 }}>✕</button>
        </div>
        {/* Body */}
        <div style={{ padding: "18px 20px 20px", display: "flex", flexDirection: "column", gap: 14 }}>
          {/* Category */}
          <div>
            <label style={lblStyle}>Category</label>
            <select value={category} onChange={e => setCategory(e.target.value)}
              style={{ ...inpStyle, appearance: "none", cursor: "pointer" }}>
              {EXPENSE_CATEGORIES.map(c => (
                <option key={c.id} value={c.id}>{c.icon} {c.label}</option>
              ))}
            </select>
          </div>
          {/* Description */}
          <div>
            <label style={lblStyle}>Description</label>
            <input style={inpStyle} value={description} onChange={e => setDescription(e.target.value)} placeholder="e.g. Nashville hotel — 2 nights" autoFocus />
          </div>
          {/* Amount + Currency */}
          <div style={{ display: "flex", gap: 10 }}>
            <div style={{ flex: 2 }}>
              <label style={lblStyle}>Amount</label>
              <input style={inpStyle} type="number" min="0" step="0.01" value={amount} onChange={e => setAmount(e.target.value)} placeholder="0.00" />
            </div>
            <div style={{ flex: 1 }}>
              <label style={lblStyle}>Currency</label>
              <select value={currency} onChange={e => setCurrency(e.target.value)}
                style={{ ...inpStyle, appearance: "none", cursor: "pointer" }}>
                {CURRENCIES.map(c => (
                  <option key={c.code} value={c.code}>{c.code}</option>
                ))}
              </select>
            </div>
          </div>
          {/* Date */}
          <div>
            <label style={lblStyle}>Date</label>
            <input style={inpStyle} type="date" value={date} onChange={e => setDate(e.target.value)} />
          </div>
          {/* Notes */}
          <div>
            <label style={lblStyle}>Notes (optional)</label>
            <textarea style={{ ...inpStyle, resize: "vertical", minHeight: 60, lineHeight: 1.5 }} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Any additional context…" />
          </div>
        </div>
        {/* Footer */}
        <div style={{ padding: "12px 20px 16px", borderTop: `1px solid ${BDR}`, display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button onClick={onClose} style={{ fontFamily: FF, fontSize: 12, padding: "8px 16px", borderRadius: 8, border: `1px solid ${BDR}`, background: "none", color: MUT, cursor: "pointer" }}>Cancel</button>
          <button onClick={handleSave} disabled={saving || !description.trim() || !amount}
            style={{ fontFamily: FF, fontSize: 12, fontWeight: 600, padding: "8px 18px", borderRadius: 8, border: "none", background: saving ? MUT : "var(--accent)", color: "#0d0d0d", cursor: saving ? "default" : "pointer", opacity: (!description.trim() || !amount) ? 0.5 : 1 }}>
            {saving ? "Saving…" : existing ? "Save Changes" : "Add Expense"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── MerchModal ─────────────────────────────────────────────────────────────
function MerchModal({ existing, showId, shows, venues, tourId, teamId, onSave, onClose, lightMode }) {
  const FF    = "'Helvetica Neue', Helvetica, Arial, sans-serif";
  const BG    = lightMode ? "#f5f2ed" : "#111";
  const BDR   = lightMode ? "#dedad4" : "#222";
  const TEXT  = lightMode ? "#1a1814" : "#e8e8e8";
  const MUT   = lightMode ? "#8a8480" : "#666";

  const [selShow,   setSelShow]   = useState(existing?.show_id  || showId || shows[0]?.id || "");
  const [gross,     setGross]     = useState(existing?.gross     != null ? String(existing.gross)         : "");
  const [cutPct,    setCutPct]    = useState(existing?.venue_cut_pct != null ? String(existing.venue_cut_pct) : "0");
  const [currency,  setCurrency]  = useState(existing?.currency  || "USD");
  const [notes,     setNotes]     = useState(existing?.notes     || "");
  const [saving,    setSaving]    = useState(false);

  const grossNum = parseFloat(gross) || 0;
  const cutNum   = parseFloat(cutPct) || 0;
  const net      = grossNum * (1 - cutNum / 100);

  const inpStyle = { fontFamily: FF, fontSize: 12, padding: "8px 10px", borderRadius: 8, border: `1px solid ${BDR}`, background: lightMode ? "#fff" : "#0d0d0d", color: TEXT, outline: "none", width: "100%", boxSizing: "border-box" };
  const lblStyle = { fontFamily: FF, fontSize: 11, letterSpacing: 0.5, color: MUT, textTransform: "uppercase", marginBottom: 5, display: "block" };

  const handleSave = async () => {
    if (!selShow || !gross) return;
    setSaving(true);
    const rec = {
      ...(existing?.id ? { id: existing.id } : { id: "m" + Date.now().toString(36) + Math.random().toString(36).slice(2, 6) }),
      tour_id:       tourId,
      show_id:       selShow,
      team_id:       teamId,
      gross:         grossNum,
      venue_cut_pct: cutNum,
      net:           Math.round(net * 100) / 100,
      currency,
      notes:         notes.trim() || "",
    };
    await onSave(rec);
    setSaving(false);
  };

  const showOptions = shows.slice().sort((a, b) => a.date.localeCompare(b.date));

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 9800, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={onClose}>
      <div style={{ width: 460, background: BG, border: `1px solid ${BDR}`, borderRadius: 12, overflow: "hidden", boxShadow: "0 24px 64px rgba(0,0,0,0.6)" }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: "16px 20px", borderBottom: `1px solid ${BDR}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ fontFamily: FF, fontSize: 14, fontWeight: 700, color: TEXT, display: "flex", alignItems: "center", gap: 7 }}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>{existing ? "Edit Merch" : "Log Merch Sales"}</div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: MUT, cursor: "pointer", fontSize: 20, lineHeight: 1 }}>✕</button>
        </div>

        <div style={{ padding: "18px 20px 20px", display: "flex", flexDirection: "column", gap: 14 }}>
          {/* Show selector */}
          <div>
            <label style={lblStyle}>Show</label>
            <select value={selShow} onChange={e => setSelShow(e.target.value)} disabled={!!existing}
              style={{ ...inpStyle, appearance: "none", cursor: "pointer" }}>
              <option value="">— Select a show —</option>
              {showOptions.map(s => {
                const v = venues[s.venueId];
                return <option key={s.id} value={s.id}>{s.date} · {v?.name || "Unknown"}{v?.city ? `, ${v.city}` : ""}</option>;
              })}
            </select>
          </div>

          {/* Gross + Currency */}
          <div style={{ display: "flex", gap: 10 }}>
            <div style={{ flex: 2 }}>
              <label style={lblStyle}>Gross Merch Sales</label>
              <input style={inpStyle} type="number" min="0" step="0.01" value={gross}
                onChange={e => setGross(e.target.value)} placeholder="0.00" autoFocus />
            </div>
            <div style={{ flex: 1 }}>
              <label style={lblStyle}>Currency</label>
              <select value={currency} onChange={e => setCurrency(e.target.value)}
                style={{ ...inpStyle, appearance: "none", cursor: "pointer" }}>
                {CURRENCIES.map(c => <option key={c.code} value={c.code}>{c.code}</option>)}
              </select>
            </div>
          </div>

          {/* Venue cut % */}
          <div>
            <label style={lblStyle}>Venue Cut %</label>
            <input style={inpStyle} type="number" min="0" max="100" step="0.5" value={cutPct}
              onChange={e => setCutPct(e.target.value)} placeholder="0" />
          </div>

          {/* Net preview */}
          {grossNum > 0 && (
            <div style={{ padding: "10px 12px", background: lightMode ? "#eef7f0" : "#0a1a10", border: `1px solid ${lightMode ? "#b0dfc0" : "#1a3a28"}`, borderRadius: 8 }}>
              <div style={{ fontFamily: FF, fontSize: 11, color: lightMode ? "#3a7a5a" : "#3a8a5a", letterSpacing: 0.5, marginBottom: 3 }}>NET TO ARTIST</div>
              <div style={{ fontFamily: FF, fontSize: 20, fontWeight: 700, color: lightMode ? "#1a6a3a" : "var(--success)" }}>
                {CURRENCIES.find(c => c.code === currency)?.symbol || ""}{net.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {currency}
              </div>
              {cutNum > 0 && <div style={{ fontFamily: FF, fontSize: 11, color: MUT, marginTop: 2 }}>
                Gross {CURRENCIES.find(c => c.code === currency)?.symbol || ""}{grossNum.toLocaleString()} − {cutNum}% venue = {(grossNum * cutNum / 100).toLocaleString("en-US", { maximumFractionDigits: 2 })} venue take
              </div>}
            </div>
          )}

          {/* Notes */}
          <div>
            <label style={lblStyle}>Notes (optional)</label>
            <input style={inpStyle} value={notes} onChange={e => setNotes(e.target.value)}
              placeholder="e.g. Strong t-shirt night, ran out of mediums" />
          </div>
        </div>

        <div style={{ padding: "12px 20px 16px", borderTop: `1px solid ${BDR}`, display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button onClick={onClose} style={{ fontFamily: FF, fontSize: 12, padding: "8px 16px", borderRadius: 8, border: `1px solid ${BDR}`, background: "none", color: MUT, cursor: "pointer" }}>Cancel</button>
          <button onClick={handleSave} disabled={saving || !selShow || !gross}
            style={{ fontFamily: FF, fontSize: 12, fontWeight: 600, padding: "8px 18px", borderRadius: 8, border: "none", background: saving ? MUT : "var(--accent)", color: "#0d0d0d", cursor: saving ? "default" : "pointer", opacity: (!selShow || !gross) ? 0.5 : 1 }}>
            {saving ? "Saving…" : existing ? "Save Changes" : "Log Merch"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── SC-1: ScheduleItemModal ───────────────────────────────────────────────
const SCHED_TYPES = [
  { id: "production", label: "Production Call", color: "var(--accent)" },
  { id: "press",      label: "Press / Promo",   color: "#c084fc" },
  { id: "catering",   label: "Catering",         color: "var(--success)" },
  { id: "custom",     label: "Custom",           color: "#6a8faf" },
];
function fmtMins(m) {
  if (m == null) return "";
  const h = Math.floor(m / 60) % 24;
  const mm = m % 60;
  const ampm = h >= 12 ? "PM" : "AM";
  const hh = h % 12 === 0 ? 12 : h % 12;
  return `${hh}:${String(mm).padStart(2, "0")} ${ampm}`;
}
function parseMins(str) {
  if (!str) return null;
  // Accept canonical 12h ("5:00 PM") as well as legacy 24h ("17:00") — the
  // ROS/day-schedule import must keep working now that TimeInput12 stores 12h.
  return parseDisplayTime(String(str).trim());
}
// Parse a display time string like "7:00 PM" or "07:00" to minutes since midnight
function parseDisplayTime(str) {
  if (!str) return null;
  const m = str.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)?$/i);
  if (!m) return null;
  let h = parseInt(m[1]), mm = parseInt(m[2]);
  const ampm = (m[3] || "").toUpperCase();
  if (ampm === "PM" && h !== 12) h += 12;
  if (ampm === "AM" && h === 12) h = 0;
  return h * 60 + mm;
}
// Compute set duration in minutes between two display time strings; handles midnight wrap
function setDurMins(startStr, endStr) {
  const s = parseDisplayTime(startStr), e = parseDisplayTime(endStr);
  if (s == null || e == null) return null;
  let d = e - s;
  if (d <= 0) d += 24 * 60;
  return d;
}
function fmtDur(mins) {
  if (!mins || mins <= 0) return "";
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60), m = mins % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}
function minsToTimeInput(m) {
  if (m == null) return "";
  const h  = Math.floor(m / 60) % 24;
  const mm = m % 60;
  return `${String(h).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
}
function ScheduleItemModal({ existing, dayId, tourParty, onSave, onClose, lightMode }) {
  const FF = "'Helvetica Neue', Helvetica, Arial, sans-serif";
  const BG  = lightMode ? "#fff"     : "#1a1a1a";
  const BDR = lightMode ? "#e0ddd8"  : "#2a2a2a";
  const TXT = lightMode ? "#0d0d0d"  : "#f0ede8";
  const MUT = lightMode ? "#888"     : "#666";
  const INP = { fontFamily: FF, fontSize: 14, color: TXT, background: lightMode ? "#f8f7f5" : "#111",
    border: `1px solid ${BDR}`, borderRadius: 8, padding: "9px 12px", width: "100%", outline: "none", boxSizing: "border-box" };

  const [title,    setTitle]    = useState(existing?.title    || "");
  const [type,     setType]     = useState(existing?.type     || "custom");
  const [time,     setTime]     = useState(existing ? minsToTimeInput(existing.start_minutes) : "");
  const [duration, setDuration] = useState(existing?.duration_minutes ?? 60);
  const [assigned, setAssigned] = useState(existing?.assigned_profile_ids || []);
  const [notes,    setNotes]    = useState(existing?.notes    || "");

  const canSave = title.trim() && time;

  const togglePerson = (id) => setAssigned(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const handleSave = () => {
    const mins = parseMins(time);
    if (!title.trim() || mins == null) return;
    onSave({
      ...(existing || {}),
      tour_day_id:          dayId,
      title:                title.trim(),
      type,
      start_minutes:        mins,
      duration_minutes:     Number(duration),
      assigned_profile_ids: assigned,
      notes:                notes.trim(),
    });
  };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 6000, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
      onClick={onClose}>
      <div style={{ width: "100%", maxWidth: 460, background: BG, border: `1px solid ${BDR}`, borderRadius: 12, boxShadow: "0 20px 50px rgba(0,0,0,0.5)", overflow: "hidden" }}
        onClick={e => e.stopPropagation()}>
        <div style={{ padding: "16px 20px 12px", borderBottom: `1px solid ${BDR}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontFamily: FF, fontSize: 14, fontWeight: 700, color: TXT }}>{existing ? "Edit Event" : "Add Event"}</div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: MUT, fontSize: 20, cursor: "pointer", lineHeight: 1 }}>✕</button>
        </div>
        <div style={{ padding: "16px 20px", display: "flex", flexDirection: "column", gap: 12, maxHeight: "70vh", overflowY: "auto" }}>
          {/* Title */}
          <div>
            <div style={{ fontFamily: FF, fontSize: 11, color: MUT, letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 5 }}>Event Title</div>
            <input style={INP} value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Soundcheck, Press Interview, Catering Opens" autoFocus />
          </div>
          {/* Type */}
          <div>
            <div style={{ fontFamily: FF, fontSize: 11, color: MUT, letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 5 }}>Type</div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {SCHED_TYPES.map(t => (
                <button key={t.id} onClick={() => setType(t.id)}
                  style={{ fontFamily: FF, fontSize: 11, padding: "5px 12px", borderRadius: 16, cursor: "pointer", border: `1px solid ${type === t.id ? t.color : BDR}`,
                    background: type === t.id ? `${t.color}22` : "none", color: type === t.id ? t.color : MUT, transition: "all 0.12s" }}>
                  {t.label}
                </button>
              ))}
            </div>
          </div>
          {/* Time + Duration */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div>
              <div style={{ fontFamily: FF, fontSize: 11, color: MUT, letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 5 }}>Start Time</div>
              <input type="time" style={INP} value={time} onChange={e => setTime(e.target.value)} />
            </div>
            <div>
              <div style={{ fontFamily: FF, fontSize: 11, color: MUT, letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 5 }}>Duration</div>
              <select style={INP} value={duration} onChange={e => setDuration(Number(e.target.value))}>
                {[15, 30, 45, 60, 90, 120, 180, 240].map(d => (
                  <option key={d} value={d}>{d < 60 ? `${d} min` : `${d / 60}h${d % 60 ? ` ${d % 60}m` : ""}`}</option>
                ))}
              </select>
            </div>
          </div>
          {/* Assign people */}
          {tourParty?.length > 0 && (
            <div>
              <div style={{ fontFamily: FF, fontSize: 11, color: MUT, letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 5 }}>
                Assign People <span style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>(leave empty = visible to all)</span>
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {tourParty.map(p => {
                  const on = assigned.includes(p.id);
                  return (
                    <button key={p.id} onClick={() => togglePerson(p.id)}
                      style={{ fontFamily: FF, fontSize: 11, padding: "4px 10px", borderRadius: 16, cursor: "pointer",
                        border: `1px solid ${on ? "var(--accent)" : BDR}`, background: on ? "#f0a50022" : "none",
                        color: on ? "var(--accent)" : MUT, transition: "all 0.12s" }}>
                      {p.name || p.email || "?"}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          {/* Notes */}
          <div>
            <div style={{ fontFamily: FF, fontSize: 11, color: MUT, letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 5 }}>Notes (optional)</div>
            <input style={INP} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Location, contact, details…" />
          </div>
        </div>
        <div style={{ padding: "12px 20px 16px", borderTop: `1px solid ${BDR}`, display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button onClick={onClose} style={{ fontFamily: FF, fontSize: 12, padding: "8px 16px", borderRadius: 8, border: `1px solid ${BDR}`, background: "none", color: MUT, cursor: "pointer" }}>Cancel</button>
          <button onClick={handleSave} disabled={!canSave}
            style={{ fontFamily: FF, fontSize: 12, fontWeight: 600, padding: "8px 18px", borderRadius: 8, border: "none", background: canSave ? "var(--accent)" : MUT, color: "#0d0d0d", cursor: canSave ? "pointer" : "default" }}>
            {existing ? "Save Changes" : "Add Event"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── App ────────────────────────────────────────────────────────────────────
export default function App() {
  const [user,       setUser]       = useState(null);
  const [authReady,  setAuthReady]  = useState(false);
  const [dbLoading,  setDbLoading]  = useState(false);
  const [data,       setData]       = useState({ artists: [], profile: {}, contacts: [] });
  // V-0: org account state (venue / festival users)
  const [orgId,          setOrgId]          = useState(null);
  const [orgType,        setOrgType]        = useState(null);        // 'venue' | 'festival' | null
  const [orgData,        setOrgData]        = useState(null);        // organizations row
  const [orgMemberRole,  setOrgMemberRole]  = useState(null);        // org member role: 'owner'|'admin'|'staff'|'runner'|etc.
  const [venues,     setVenues]     = useState({});
  const [teamId,     setTeamId]     = useState(null);
  const [teamName,   setTeamName]   = useState("My Team");
  const [teamMembers, setTeamMembers] = useState([]);
  const [mTeam,      setMTeam]      = useState(false);
  const saveTimer = useRef(null);

  // ── Auth listener ──
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setAuthReady(true);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, session) => {
      const newUser = session?.user ?? null;
      setUser(prev => {
        // Don't create a new object reference if it's the same user — prevents
        // spurious data reloads on silent JWT token refreshes
        if (prev?.id && newUser?.id && prev.id === newUser.id) return prev;
        return newUser;
      });
    });
    return () => subscription.unsubscribe();
  }, []);

  // ── Load data from normalized tables when user logs in ──
  useEffect(() => {
    if (!user) { setTeamId(null); setTeamMembers([]); setOrgId(null); setOrgType(null); setOrgData(null); return; }
    setDbLoading(true);
    (async () => {
      // V-0: Check if this is a venue/festival org user first.
      // Org users never get the TM or member UI — route them to the org dashboard immediately.
      const { data: orgMemberships } = await supabase
        .from("organization_members").select("org_id, role").eq("user_id", user.id).limit(1);
      if (orgMemberships?.[0]) {
        const oid = orgMemberships[0].org_id;
        setOrgId(oid);
        setOrgMemberRole(orgMemberships[0].role || null);
        const { data: orgRow } = await supabase.from("organizations").select("*").eq("id", oid).single();
        if (orgRow) { setOrgType(orgRow.type); setOrgData(orgRow); }
        setDbLoading(false);
        return; // skip TM + member flows
      }

      // 0. Check if this is an invited band/crew member.
      // TMs take priority — if the user is in team_members they always get the TM view,
      // even if they also ended up in tour_members (e.g. from accidentally opening an invite link).
      const [{ data: memberRows }, { data: tmCheck }] = await Promise.all([
        supabase
          .from("tour_members")
          .select("id, role, tour_id, artist_id, modules")
          .eq("user_id", user.id)
          .eq("invite_status", "accepted")
          .order("created_at", { ascending: false })
          .limit(1),
        supabase
          .from("team_members")
          .select("team_id")
          .eq("user_id", user.id)
          .limit(1),
      ]);

      if (memberRows?.[0] && !tmCheck?.length) {
        const m = memberRows[0];
        setMemberRole(m.role);
        setMemberTourId(m.tour_id);
        setMemberId(m.id);
        // Use saved modules if present, otherwise fall back to defaults
        if (Array.isArray(m.modules) && m.modules.length > 0) setMemberModules(m.modules);
        // Pre-populate from cache so members see data immediately while live fetch runs
        try {
          const cached = sessionStorage.getItem(`cache_member_${m.tour_id}`);
          if (cached) {
            const { tourDays: cachedDays } = JSON.parse(cached);
            if (cachedDays?.length) { setTourDays(cachedDays); setMemberDataStale(true); }
          }
        } catch {}
        try {
          const { data: md, venues: mv, tourDays: td, artistId: aId, tourId: tId,
                  ownProfile: op, networkProfiles: np, connections: nc,
                  membership: mem } =
            await loadMemberData(m.tour_id, m.artist_id, user.id);
          setData(md);
          setVenues(mv);
          if (td?.length) {
            setTourDays(td);
            try { sessionStorage.setItem(`cache_member_${m.tour_id}`, JSON.stringify({ tourDays: td })); } catch {}
          }
          if (aId) setSelA(aId);
          if (tId) setSelT(tId);
          if (op) setMemberOwnProfile(op);
          if (np?.length) setMemberNetworkProfiles(np);
          if (nc?.size) setMemberConnections(nc);
          if (mem) setMemberPermissions(mem); // PP-1 Phase 3
          setMemberDataStale(false);
          // Load broadcasts directly here so they're available on first render
          if (tId) loadBroadcasts(tId);
        } catch (err) {
          console.error("[ADVANCD] loadMemberData failed:", err);
          // Keep showing cached data if live load fails — don't clear stale flag
        }
        setDbLoading(false);
        return; // skip team creation + full data load
      }

      // 1. Check user metadata for org type (set during sign-up for venue/festival users).
      // New org users won't have an organization_members row yet on their very first login.
      const userOrgType = user.user_metadata?.org_type;
      if (userOrgType === "venue" || userOrgType === "festival") {
        const oid = await ensureOrg(user.id, userOrgType);
        setOrgId(oid);
        setOrgType(userOrgType);
        if (oid) {
          const { data: orgRow } = await supabase.from("organizations").select("*").eq("id", oid).single();
          if (orgRow) setOrgData(orgRow);
        }
        setDbLoading(false);
        return; // skip TM flow
      }

      // 2. Ensure user has a team (creates one + migrates data if needed)
      const tid = await ensureTeam(user.id, user.email);
      setTeamId(tid);

      // 2. Load team name + members + profiles
      if (tid) {
        const { data: teamRow } = await supabase.from("teams").select("name").eq("id", tid).single();
        if (teamRow) setTeamName(teamRow.name);
        const members = await loadTeamMembers(tid);
        setTeamMembers(members);
        // Load profiles (non-blocking)
        supabase.from('people_profiles').select('*').eq('team_id', tid).order('name')
          .then(({ data }) => { if (data) setProfiles(data); })
          .catch(err => console.error('[ADVANCD] profiles load failed:', err));
        // TT-1: Load tour templates (non-blocking)
        supabase.from('tour_templates').select('*').eq('team_id', tid).order('created_at')
          .then(({ data }) => { if (data) setTourTemplates(data); })
          .catch(err => console.error('[ADVANCD] tour templates load failed:', err));
        // SL-2: Load setlist templates (non-blocking)
        supabase.from('setlist_templates').select('*').eq('team_id', tid).order('created_at', { ascending: false })
          .then(({ data }) => { if (data) setSetlistTemplates(data); })
          .catch(err => console.error('[ADVANCD] setlist templates load failed:', err));
      }

      // 3. Load all data (scoped to team)
      const { data: loaded, venues: loadedVenues, hasData } = await loadNormalized(user.id, tid);
      if (hasData) {
        setData(loaded);
        setVenues(loadedVenues);
      } else {
        // Check for legacy blob data to migrate
        const { data: oldRow } = await supabase
          .from("user_data")
          .select("artists, venues, profile, contacts")
          .eq("user_id", user.id)
          .single();

        if (oldRow && (oldRow.artists?.length > 0 || Object.keys(oldRow.venues || {}).length > 0)) {
          const migData   = { artists: oldRow.artists || [], profile: oldRow.profile || {}, contacts: oldRow.contacts || [] };
          const migVenues = oldRow.venues || {};
          await saveNormalized(user.id, tid, migData, migVenues);
          setData(migData);
          setVenues(migVenues);
        } else {
          setData({ artists: [], profile: {}, contacts: [] });
          setVenues({});
        }
      }
      setDbLoading(false);
    })();
  }, [user]);
  const [view,   setView]   = useState("overview");
  const [selA,   setSelA]   = useState(() => { try { return localStorage.getItem('adv_selA') || null; } catch { return null; } });
  const [selT,   setSelT]   = useState(() => { try { return localStorage.getItem('adv_selT') || null; } catch { return null; } });
  const [selS,      setSelS]      = useState(null);
  const [detailTab, setDetailTab] = useState("advance");
  // SL-1: which set is active in the Setlist tab (per-show; resets implicitly when selS changes)
  const [activeSetlistSetNum, setActiveSetlistSetNum] = useState(null);
  const [setlistImportOpen, setSetlistImportOpen] = useState(false);
  // SL-1: Export ▾ dropdown visibility (PDF / Word)
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const [cats,      setCats]      = useState({});
  const [glRequests,    setGlRequests]    = useState([]); // guest_list_requests for selS
  const [glBulkOpen,   setGlBulkOpen]   = useState(false);
  // DS-1: Day sheet
  const [dsLinkCopied,    setDsLinkCopied]    = useState(null); // dayId | null
  const [dsGenerating,    setDsGenerating]    = useState(null); // dayId | null
  const [dsInternalFlags, setDsInternalFlags] = useState({}); // { [dayId]: bool }
  const [glBulkText,   setGlBulkText]   = useState("");
  const [glBulkParsing, setGlBulkParsing] = useState(false);
  const [glBulkParsed, setGlBulkParsed] = useState(null); // null | array of parsed entries
  const [eDraft, setEDraft] = useState("");
  const [eLoad,  setELoad]  = useState(false);
  const [copied,    setCopied]    = useState(false);
  const [saved,     setSaved]     = useState(false);
  const [saveError, setSaveError] = useState(false);

  const [mArtist, setMArtist] = useState(false);
  const [mTour,   setMTour]   = useState(null);
  const [mShow,      setMShow]      = useState(null);
  const [mAddShows,  setMAddShows]  = useState(null); // null | { defaultDate?, tab? }
  const [mVenue,     setMVenue]     = useState(null);
  const [mAddVenue,  setMAddVenue]  = useState(null); // null | { kind: "venue"|"festival" }
  const [selVenue,   setSelVenue]   = useState(null);
  const [mContact,    setMContact]    = useState(null); // null | { mode:"add"|"edit", contactId? }
  const [mAddContact, setMAddContact] = useState(false);
  const [mImport,        setMImport]        = useState(false);
  const [mDataImport,    setMDataImport]    = useState(false);
  const [mBatchAdv,      setMBatchAdv]      = useState(null);  // null | { stage:'drafting'|'review', items:[…], draftingIdx }
  const [mTourHealth,    setMTourHealth]    = useState(false);
  const [mAdvLinks,      setMAdvLinks]      = useState(false);   // Advance Links panel
  const [advLinksCopied, setAdvLinksCopied] = useState({});      // { [showId]: true } — flash state
  const [mBatchDeposit,  setMBatchDeposit]  = useState(false);
  const [addingShowContact, setAddingShowContact] = useState(false);
  const [newContactPick,    setNewContactPick]    = useState({ contactId: "", role: "" });
  const [addingShowContactNew, setAddingShowContactNew] = useState(false);
  const [newContactForm,    setNewContactForm]    = useState({ name: "", email: "", phone: "", role: "Promoter" });
  const [mArtistSection, setMArtistSection] = useState(null); // { artistId, section }
  const [mEditArtist, setMEditArtist] = useState(null);
  const [artistDocUploading, setArtistDocUploading] = useState(false);
  const [artistDocUploadErr, setArtistDocUploadErr] = useState("");
  // PLOTR integration: stage_plots per artist
  const [artistStagePlots,   setArtistStagePlots]   = useState({}); // artistId → stage_plot[]
  const [stagePlotsLoading,  setStagePlotsLoading]  = useState({});
  const [artistDocLabel,     setArtistDocLabel]     = useState("Stage Plot");
  const [sbCollapsed, setSbCollapsed] = useState(false);
  const [search,      setSearch]      = useState("");
  const searchRef    = useRef(null);
  const nextShowRef  = useRef(null);
  const exportMenuRef = useRef(null);
  const tourMoreMenuRef = useRef(null);
  const dpMoreMenuRef = useRef(null);
  const [showFilter,  setShowFilter]  = useState("all");
  const [dashFilter,       setDashFilter]       = useState("upcoming");
  const [dashArtistFilter, setDashArtistFilter] = useState("all");
  const [dashDateRange,    setDashDateRange]    = useState("all");
  const [venueSearch,    setVenueSearch]    = useState("");
  const [festivalSearch, setFestivalSearch] = useState("");
  const [selFestival,    setSelFestival]    = useState(null);
  const [contactSearch,  setContactSearch]  = useState("");
  const [contactsShowAll, setContactsShowAll] = useState(false); // perf: render past the 150-row cap on demand
  const [venuesShowAll,   setVenuesShowAll]   = useState(false);
  const [dirModal,   setDirModal]   = useState(null);  // null | 'venue' | 'festival'
  const [dirQuery,   setDirQuery]   = useState("");
  const [dirResults, setDirResults] = useState([]);
  const [dirLoading, setDirLoading] = useState(false);
  // V-1: event_links for current show (showId → array of event_link rows)
  const [showEventLinks,     setShowEventLinks]     = useState({});
  // Advances dashboard: confirmed event_links across all shows
  const [dashEventLinks,     setDashEventLinks]     = useState({});
  const [dashLinksLoaded,    setDashLinksLoaded]    = useState(false);
  const [advDashFilter,      setAdvDashFilter]      = useState({ timeframe: "upcoming", artistId: "all", status: "all" });
  const [venueOrgSearch,     setVenueOrgSearch]     = useState("");  // search input
  const [venueOrgResults,    setVenueOrgResults]    = useState([]); // org search results
  const [venueOrgSearchOpen, setVenueOrgSearchOpen] = useState(false); // search open
  const [venueOrgSearching,  setVenueOrgSearching]  = useState(false);
  const [linkingVenue,       setLinkingVenue]       = useState(false); // creating event_link
  const [linkCodeCopied,     setLinkCodeCopied]     = useState(false); // copy state for show code
  const [linkCodeGenerating, setLinkCodeGenerating] = useState(false);
  const [advanceFinalizing,  setAdvanceFinalizing]  = useState(false);
  const [confirmResetAdv,    setConfirmResetAdv]    = useState(false);

  const [advLinkCopied,  setAdvLinkCopied]  = useState(false);
  const [launchAdvLoading, setLaunchAdvLoading] = useState(false);
  const [launchAdvDone,    setLaunchAdvDone]    = useState(0);
  const [advGenLoading,  setAdvGenLoading]  = useState(false);
  const [tmFileUploading,   setTmFileUploading]   = useState(false);
  const [tmFileLabel,       setTmFileLabel]       = useState("Stage Plot");
  const [tourFileUploading, setTourFileUploading] = useState(false);
  const [tourFileLabel,     setTourFileLabel]     = useState("Tech Rider");
  const [tourSetupTab,      setTourSetupTab]      = useState("sections");
  const [showDayOfPanel,    setShowDayOfPanel]    = useState(true);
  const [undoAction,        setUndoAction]        = useState(null);
  const [activityLog,       setActivityLog]       = useState([]);
  const [activityLogLoading, setActivityLogLoading] = useState(false);
  const [confirmDelTour,    setConfirmDelTour]    = useState(false);
  const [showExportMenu,    setShowExportMenu]    = useState(false);
  useEffect(() => {
    if (!showExportMenu) return;
    const handler = (e) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target)) {
        setShowExportMenu(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showExportMenu]);
  const [tourMoreOpen,      setTourMoreOpen]      = useState(false);
  useEffect(() => {
    if (!tourMoreOpen) return;
    const handler = (e) => {
      if (tourMoreMenuRef.current && !tourMoreMenuRef.current.contains(e.target)) {
        setTourMoreOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [tourMoreOpen]);
  const [dpMoreOpen,        setDpMoreOpen]        = useState(false);
  useEffect(() => {
    if (!dpMoreOpen) return;
    const handler = (e) => {
      if (dpMoreMenuRef.current && !dpMoreMenuRef.current.contains(e.target)) {
        setDpMoreOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [dpMoreOpen]);
  const [advTemplates,      setAdvTemplates]      = useState(() => { try { return JSON.parse(localStorage.getItem(`advTpl_${user?.id}`) || "[]"); } catch { return []; } });
  const [savingTpl,         setSavingTpl]         = useState(false);
  const [newTplName,        setNewTplName]        = useState("");
  // TT-1: Tour Setup Templates (Supabase, per-team)
  const [tourTemplates,     setTourTemplates]     = useState([]);
  const [savingTourTpl,     setSavingTourTpl]     = useState(false);
  const [newTourTplName,    setNewTourTplName]    = useState("");
  const [lightMode,         setLightMode]         = useState(() => {
    try {
      const stored = localStorage.getItem("lightMode");
      if (stored !== null) return stored !== "false";
      // No stored preference — default to device color scheme
      return !window.matchMedia?.("(prefers-color-scheme: dark)").matches;
    } catch { return true; }
  });
  // Step 3: mirror the JS light/dark theme onto <html data-theme> so the CSS
  // colour tokens (--accent, --success, --danger, --accent-2) resolve to the
  // right value. Falls back to the dark defaults if never set.
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", lightMode ? "light" : "dark");
  }, [lightMode]);
  const [isMobile,          setIsMobile]          = useState(() => window.innerWidth < 768);
  const [sampleTourState,   setSampleTourState]   = useState(null); // null | 'loading' | { inviteLinks, claimLinks }

  // ── FT-1: Financials state ──────────────────────────────────────────────
  const [tourFinTab,    setTourFinTab]    = useState("overview"); // "overview"|"income"|"expenses"|"budget"|"merch"
  const [tourExpenses,  setTourExpenses]  = useState([]);         // expense rows for current tour
  const [tourBudget,    setTourBudget]    = useState([]);         // budget rows for current tour
  const [showMerch,     setShowMerch]     = useState([]);         // merch rows for current tour
  const [mMerch,        setMMerch]        = useState(null);       // null | { mode:"add"|"edit", showId?, entry? }
  const [merchDeleteConfirm, setMerchDeleteConfirm] = useState(null); // merch id pending delete
  const [squareConn,    setSquareConn]    = useState(null);       // null | { connected, merchant_id, location_ids, last_synced_at }
  const [squareSyncing, setSquareSyncing] = useState(false);
  // Detect ?square_connected=<tour_id> redirect from Square OAuth callback
  const [squareJustConnected] = useState(() => {
    const p = new URLSearchParams(window.location.search).get("square_connected");
    if (p) window.history.replaceState({}, "", window.location.pathname);
    return p || null;
  });
  // EM-1: email drafts integration (Outlook / Gmail)
  const [emailConn,       setEmailConn]       = useState(null);  // null | { microsoft:{connected,email?}, google:{connected,email?} }
  const [draftSaveState,  setDraftSaveState]  = useState(null);  // null | { provider, status:"saving"|"done"|"error" }
  const [batchDraftState, setBatchDraftState] = useState(null);  // null | { provider, saving, done, failed }
  // Detect ?email_connected=<provider> redirect from the OAuth callback
  const [emailJustConnected] = useState(() => {
    const p = new URLSearchParams(window.location.search).get("email_connected");
    if (p) window.history.replaceState({}, "", window.location.pathname);
    return p || null;
  });
  const [gmailComingSoon, setGmailComingSoon] = useState(false); // EM-1: Gmail not yet live
  const [emailConnecting, setEmailConnecting] = useState(null);  // EM-1: provider currently mid-OAuth-redirect
  const draftSaveInFlightRef = useRef(false);                    // EM-1: hard double-click guard for draft saves
  const [showListOpen,    setShowListOpen]    = useState(true);  // advance mode: tour show list expanded above the open advance
  const [finLoading,    setFinLoading]    = useState(false);
  const [finAiLoading,  setFinAiLoading]  = useState(false);
  const [finAiResponse, setFinAiResponse] = useState("");
  const [mExpense,      setMExpense]      = useState(null);       // null | { mode:"add"|"edit", expense? }
  const [budgetEditing, setBudgetEditing] = useState(false);
  const [budgetDraft,   setBudgetDraft]   = useState({});         // { [category]: { amount, currency } }
  const [budgetAiLoading, setBudgetAiLoading] = useState(false);
  const [expDeleteConfirm, setExpDeleteConfirm] = useState(null); // expense id pending delete

  useEffect(() => { localStorage.setItem("lightMode", lightMode); }, [lightMode]);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  // Dark/light mode toggle: Cmd+\
  useEffect(() => {
    const handler = e => {
      if (e.metaKey && (e.key === "\\" || e.code === "Backslash")) { e.preventDefault(); setLightMode(p => !p); }
      if (e.metaKey && e.key === "k") { e.preventDefault(); searchRef.current?.focus(); searchRef.current?.select(); }
      if (e.key === "Escape" && document.activeElement === searchRef.current) { setSearch(""); searchRef.current?.blur(); }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  // ── Light-mode-aware colour palette (used by TM inline-styled sections) ───
  const UI = lightMode ? {
    // ── Page / shell ───────────────────────────────────────────────────────
    bg:       "#f0ede8",   // app shell / MobileApp shell background
    panelAlt: "#e4e0da",   // secondary panels, input fields, code blocks
    // ── Panels ─────────────────────────────────────────────────────────────
    panelBg:"#e8e4de", panelBdr:"#d0ccc6", subBdr:"#d8d4ce",
    rowBg:"#ede9e3", rowBg2:"#e8e4de", rowHov:"#e2dfd9",
    hdrBdr:"#d0ccc6",
    // ── Text ───────────────────────────────────────────────────────────────
    text:"#1a1917", textMid:"#4a4744",
    textMut:"#5a5753", textFnt:"#9a9794", textVFnt:"#aaa9a6",
    // ── Fields / status ────────────────────────────────────────────────────
    fieldBdr:"#ccc8c2", ckOnBg:"#e8f0d8", ckOnBdr:"#a8c890",
    amdBg:"#fff0cc", amdBdr:"#d4880a", amber:"#c47f00", green:"#1a9e4a",
    // ── Semantic accents / status (step 2 — additive; routed in step 3) ──────
    accent:"#c47f00", accentBg:"#fff0cc", accentBdr:"#d4880a",
    accent2:"#3a6f96", accent2Bg:"#d8ebf5", accent2Bdr:"#a8c4d8",
    success:"#1a9e4a", successBg:"#d8f0e4", successBdr:"#a8d4ba",
    danger:"#c0392b", dangerBg:"#f5dada", dangerBdr:"#d4a0a0",
    warning:"#c47f00", warningBg:"#fff0cc", warningBdr:"#d4880a",
  } : {
    // ── Page / shell ───────────────────────────────────────────────────────
    bg:       "#0d0d0d",
    panelAlt: "#1a1a1a",
    // ── Panels ─────────────────────────────────────────────────────────────
    panelBg:"#0a0a0a", panelBdr:"#1e1e1e", subBdr:"#141414",
    rowBg:"#0d0d0d", rowBg2:"#111", rowHov:"#161616",
    hdrBdr:"#141414",
    // ── Text ───────────────────────────────────────────────────────────────
    text:"#e0e0e0", textMid:"#bbb",
    textMut:"#7d7d7d", textFnt:"#444", textVFnt:"#333",
    // ── Fields / status ────────────────────────────────────────────────────
    fieldBdr:"#1e1e1e", ckOnBg:"#111a00", ckOnBdr:"#2a3a00",
    amdBg:"#1a0f00", amdBdr:"#3a2200", amber:"var(--accent)", green:"var(--success)",
    // ── Semantic accents / status (step 2 — additive; routed in step 3) ──────
    accent:"var(--accent)", accentBg:"#1a0f00", accentBdr:"#3a2200",
    accent2:"#6a8faf", accent2Bg:"#0f1a26", accent2Bdr:"#2a3f52",
    success:"var(--success)", successBg:"#0e1f12", successBdr:"#1e3a26",
    danger:"var(--danger)", dangerBg:"#1f0e0e", dangerBdr:"#3a1a1a",
    warning:"var(--accent)", warningBg:"#1a0f00", warningBdr:"#3a2200",
  };

  // ── Light-mode-aware status configs ───────────────────────────────────────
  const SC = lightMode ? {
    unstarted: { label: "Unstarted", color: "#6a6764", bg: "#e4e0da", dot: "#aaa9a6" },
    advancing: { label: "Advancing", color: "#8a4a00", bg: "#f5e8cc", dot: "#c47f00" },
    confirmed: { label: "Confirmed", color: "#1a6a3a", bg: "#d8f0e4", dot: "#1a9e4a" },
    dayOf:     { label: "Day-Of",    color: "#1a4a7a", bg: "#d8ebf5", dot: "#4a90c0" },
  } : statusConfig;

  // Unified status (2026-06-05): one vocabulary everywhere — the badge always shows
  // BOOKING status (Unstarted / Advancing / Confirmed), matching the header pills and
  // bulk controls. Advance-portal progress (SENT / IN PROGRESS / CONFIRMED) no longer
  // replaces the badge; it renders as a separate small "PORTAL …" chip on list rows.
  // Sending an advance auto-bumps Unstarted → Advancing (see bumpToAdvancing).
  const unifiedStatus = (show) => {
    const sc = SC[show.status] || SC.unstarted;
    return { label: sc.label, color: sc.color, bg: sc.bg, dot: sc.dot };
  };
  const dpRef = useRef(null);
  const tourShowsStripRef = useRef(null); // Tour Shows strip — scroll anchor so the strip (with ‹ › nav) stays visible above the open advance
  const prevSelSRef = useRef(null);       // distinguishes list→show opens (animated collapse) from show→show switches (‹ › nav)

  const artist = data.artists.find(a => a.id === selA);
  const tour   = artist?.tours.find(t => t.id === selT);
  const show   = tour?.shows.find(s => s.id === selS);
  const venue  = show ? venues[show.venueId] : null;
  const allS          = data.artists.flatMap(a => a.tours.flatMap(t => t.shows));
  const filteredShows = tour
    ? (showFilter === "all" ? tour.shows : tour.shows.filter(s => s.status === showFilter))
        .slice().sort((a, b) => a.date.localeCompare(b.date))
    : [];

  // Persist selected artist + tour across refreshes
  useEffect(() => { try { if (selA) localStorage.setItem('adv_selA', selA); else localStorage.removeItem('adv_selA'); } catch {} }, [selA]);
  useEffect(() => { try { if (selT) localStorage.setItem('adv_selT', selT); else localStorage.removeItem('adv_selT'); } catch {} }, [selT]);

  // Reset filter when switching tours
  useEffect(() => setShowFilter("all"), [selT]);
  // Reset financials tab + clear stale data when switching tours
  useEffect(() => {
    setTourFinTab("overview");
    setTourExpenses([]);
    setTourBudget([]);
    setShowMerch([]);
    setSquareConn(null);
    setFinAiResponse("");
    setBudgetEditing(false);
    setBudgetDraft({});
  }, [selT]);
  // Reset artist tab when switching artists
  useEffect(() => setArtistTab("tours"), [selA]);

  // Load Square status whenever the Merch tab is active and we don't have status yet
  useEffect(() => {
    if (tourFinTab === "merch" && selT && squareConn === null) loadSquareStatus(selT);
  }, [tourFinTab, selT]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle Square OAuth callback redirect: ?square_connected=<tour_id>
  useEffect(() => {
    if (!squareJustConnected) return;
    setSelT(squareJustConnected);
    setView("financials");
    setTourFinTab("merch");
    loadSquareStatus(squareJustConnected);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // EM-1: load email connection status once a TM session exists
  useEffect(() => {
    if (teamId && emailConn === null) loadEmailConnStatus();
  }, [teamId]); // eslint-disable-line react-hooks/exhaustive-deps

  // EM-1: handle OAuth callback redirect: ?email_connected=<provider>
  useEffect(() => {
    if (!emailJustConnected) return;
    loadEmailConnStatus();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Advance mode: auto-collapse the show list when an advance is opened,
  // re-expand when the show is deselected
  useEffect(() => { setShowListOpen(!selS); }, [selS]);

  // Auto-scroll to next upcoming show when tour loads
  useEffect(() => {
    if (!selT) return;
    const frame = requestAnimationFrame(() => {
      nextShowRef.current?.scrollIntoView({ block: "center", behavior: "smooth" });
    });
    return () => cancelAnimationFrame(frame);
  }, [selT]);

  // ── Keyboard navigation: ↑↓ between shows in tour view ───────────────────
  // Must be declared AFTER filteredShows to avoid TDZ in the dependency array
  useEffect(() => {
    if (view !== "tour" || !selS || !filteredShows.length) return;
    const handler = e => {
      if (e.key !== "ArrowUp" && e.key !== "ArrowDown") return;
      const tag = e.target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
      e.preventDefault();
      const idx = filteredShows.findIndex(s => s.id === selS);
      if (idx === -1) return;
      const next = e.key === "ArrowDown"
        ? Math.min(idx + 1, filteredShows.length - 1)
        : Math.max(idx - 1, 0);
      if (next !== idx) { setSelS(filteredShows[next].id); setEDraft(""); }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [view, selS, filteredShows]);

  // ── Reminders: computed live from all shows ───────────────────────────────
  const reminders = useMemo(() => {
    const todayStr = new Date().toISOString().slice(0, 10);
    const offset = (days) => { const d = new Date(); d.setDate(d.getDate() + days); return d.toISOString().slice(0, 10); };
    const in7    = offset(7);
    const in14   = offset(14);
    const ago60  = offset(-60);
    const items  = [];
    data.artists.forEach(a => {
      a.tours.forEach(t => {
        t.shows.forEach(s => {
          const v = venues[s.venueId];
          const vName = v?.name || "TBD";
          const daysUntil = Math.ceil((new Date(s.date + "T12:00:00") - new Date()) / 864e5);
          const daysSince = -daysUntil;
          // 1. Deposit due soon (within 7 days, not received)
          if (s.settlement?.depositDue && !s.settlement?.depositReceived
            && s.settlement.depositDue >= todayStr && s.settlement.depositDue <= in7) {
            const dd = Math.ceil((new Date(s.settlement.depositDue + "T12:00:00") - new Date()) / 864e5);
            items.push({ type: "deposit", urgency: dd <= 2 ? "high" : "med",
              label: `Deposit due ${dd === 0 ? "today" : dd === 1 ? "tomorrow" : `in ${dd} days`}`,
              detail: `${a.name} · ${vName} · ${fmtDate(s.date)}`,
              aId: a.id, tId: t.id, sId: s.id });
          }
          // 2. Advance not returned (portal sent, no promoter response, within 14 days)
          if (s.advanceToken && s.date >= todayStr && s.date <= in14) {
            const hasResp = s.advanceData && Object.values(s.advanceData).some(sec =>
              sec && typeof sec === "object" && Object.values(sec).some(v => v !== "" && v !== null && v !== undefined && v !== false));
            if (!hasResp) items.push({ type: "advance", urgency: daysUntil <= 7 ? "high" : "med",
              label: "Advance not returned",
              detail: `${a.name} · ${vName} · ${fmtDate(s.date)} · ${daysUntil === 0 ? "today" : `${daysUntil} days away`}`,
              aId: a.id, tId: t.id, sId: s.id });
          }
          // 3. Not settled (past show, within 60 day lookback)
          if (s.date < todayStr && s.date >= ago60 && !s.settlement?.settled)
            items.push({ type: "settle", urgency: daysSince > 7 ? "high" : "med",
              label: "Not settled",
              detail: `${a.name} · ${vName} · ${fmtDate(s.date)}`,
              aId: a.id, tId: t.id, sId: s.id });
          // 4. Unstarted + upcoming within 7 days
          if (s.status === "unstarted" && s.date >= todayStr && s.date <= in7)
            items.push({ type: "unstarted", urgency: daysUntil <= 2 ? "high" : "med",
              label: `Show unstarted · ${daysUntil === 0 ? "today" : daysUntil === 1 ? "tomorrow" : `${daysUntil} days away`}`,
              detail: `${a.name} · ${vName} · ${fmtDate(s.date)}`,
              aId: a.id, tId: t.id, sId: s.id });
        });
      });
    });
    // High urgency first, then chronological within each group
    return items.sort((a, b) => (a.urgency === b.urgency ? 0 : a.urgency === "high" ? -1 : 1));
  }, [data.artists, venues]);

  // ── Real-time: listen for advance_data + advance_section_status updates ─────
  useEffect(() => {
    if (!selS) return;
    const channel = supabase
      .channel(`advance-${selS}`)
      .on("postgres_changes", {
        event: "UPDATE",
        schema: "public",
        table: "shows",
        filter: `id=eq.${selS}`,
      }, payload => {
        // ⚠ TOAST guard: unchanged large JSONB columns are OMITTED from
        // Realtime payloads (or arrive null). Only adopt values that are
        // actually present — otherwise a trivial row update (e.g. the portal's
        // advance_last_viewed_at ping) wipes promoter data from state.
        const newAdvData       = payload.new?.advance_data;
        const newSecStatus     = payload.new?.advance_section_status;
        const newLastViewed    = payload.new?.advance_last_viewed_at;
        if (newAdvData != null || newSecStatus != null || newLastViewed !== undefined) {
          setData(p => ({ ...p, artists: p.artists.map(a => ({ ...a,
            tours: a.tours.map(t => ({ ...t,
              shows: t.shows.map(s => {
                if (s.id !== selS) return s;
                return {
                  ...s,
                  ...(newAdvData    != null        && { advanceData:          newAdvData }),
                  ...(newSecStatus  != null        && { advanceSectionStatus: newSecStatus }),
                  ...(newLastViewed !== undefined  && { advanceLastViewedAt:  newLastViewed }),
                };
              })
            }))
          })) }));
        }
      })
      .subscribe();
    return () => supabase.removeChannel(channel);
  }, [selS]);

  // Scroll the Tour Shows strip into view when a show is selected — keeps the strip
  // (with its ‹ › navigation) visible above the open advance instead of landing on the
  // panel header. Falls back to the panel if the strip isn't rendered.
  // The strip sits ABOVE the collapsing show list, so its position is stable during the
  // collapse animation — safe to anchor immediately (the old 380ms defer was only needed
  // when the anchor was the panel below the animating region).
  // - list→show open: smooth scroll, fired right away.
  // - show→show switch (‹ › nav): instant — no visible jump-then-correct.
  useEffect(() => {
    const wasShowOpen = !!prevSelSRef.current;
    prevSelSRef.current = selS;
    if (!selS) return;
    const frame = requestAnimationFrame(() =>
      (tourShowsStripRef.current || dpRef.current)?.scrollIntoView({ behavior: wasShowOpen ? "auto" : "smooth", block: "start" })
    );
    return () => cancelAnimationFrame(frame);
  }, [selS]);

  // Load guest list requests for the selected show (TM side)
  useEffect(() => {
    if (!selS) { setGlRequests([]); return; }
    supabase.from("guest_list_requests")
      .select("*")
      .eq("show_id", selS)
      .order("created_at", { ascending: true })
      .then(({ data: rows }) => setGlRequests(rows || []));
  }, [selS]);

  const updGlRequestStatus = async (id, status) => {
    await supabase.from("guest_list_requests").update({ status }).eq("id", id);
    setGlRequests(p => p.map(r => r.id === id ? { ...r, status } : r));
  };

  // ── Global search ──
  const searchQ = search.trim().toLowerCase();
  const searchHits = searchQ.length > 1 ? (() => {
    const hits = [];
    data.artists.forEach(a => {
      if (a.name.toLowerCase().includes(searchQ))
        hits.push({ type: "artist", label: a.name, sub: null, go: () => { setSelA(a.id); setSelT(null); setSelS(null); setView("artist"); setSearch(""); } });
      a.tours.forEach(t => {
        if (t.name.toLowerCase().includes(searchQ))
          hits.push({ type: "tour", label: t.name, sub: a.name, go: () => { goTour(a.id, t.id); setSearch(""); } });
        t.shows.forEach(s => {
          const v = venues[s.venueId];
          const sc = statusConfig[s.status];
          const matchesVenue   = v && (v.name.toLowerCase().includes(searchQ) || (v.city || "").toLowerCase().includes(searchQ));
          const matchesDate    = s.date.includes(searchQ);
          const matchesStatus  = sc && sc.label.toLowerCase().includes(searchQ);
          const matchesContact = (v && ((v.promoterName || "").toLowerCase().includes(searchQ) || (v.promoterEmail || "").toLowerCase().includes(searchQ)))
            || (s.settlement?.promoterName || "").toLowerCase().includes(searchQ)
            || (s.settlement?.promoterEmail || "").toLowerCase().includes(searchQ);
          if (matchesVenue || matchesDate || matchesStatus || matchesContact)
            hits.push({ type: "show", label: v?.name || "?", sub: `${a.name} · ${fmtDate(s.date)}${matchesStatus ? " · " + sc.label : ""}`, go: () => { goToShow(a.id, t.id, s.id); setSearch(""); } });
        });
      });
    });
    Object.values(venues).forEach(v => {
      if ((v.name.toLowerCase().includes(searchQ) || (v.city || "").toLowerCase().includes(searchQ)) && !hits.find(h => h.type === "show" && h.label === v.name))
        hits.push({ type: "venue", label: v.name, sub: v.city, go: () => { setView("venues"); setSelVenue(v.id); setSearch(""); } });
    });
    data.contacts.forEach(c => {
      if (
        (c.name  || "").toLowerCase().includes(searchQ) ||
        (c.company || "").toLowerCase().includes(searchQ) ||
        (c.email || "").toLowerCase().includes(searchQ) ||
        (c.role  || "").toLowerCase().includes(searchQ)
      ) {
        const sub = [c.company, c.role].filter(Boolean).join(" · ") || c.email || null;
        hits.push({ type: "contact", label: c.name || c.email || "?", sub, go: () => { setView("contacts"); setSearch(""); } });
      }
    });
    profiles.forEach(p => {
      if (
        (p.name  || "").toLowerCase().includes(searchQ) ||
        (p.role  || "").toLowerCase().includes(searchQ) ||
        (p.email || "").toLowerCase().includes(searchQ)
      ) {
        hits.push({ type: "person", label: p.name || "?", sub: p.role || null, go: () => { setView("people"); setSearch(""); } });
      }
    });
    return hits.slice(0, 12);
  })() : [];

  // ── Persist to normalized tables (debounced 1.5s) ──
  useEffect(() => {
    if (!user || dbLoading) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      const ok = await saveNormalized(user.id, teamId, data, venues);
      if (ok === false) {
        setSaveError(true);
        setTimeout(() => setSaveError(false), 5000);
      } else if (ok === true) {
        setSaved(true);
        setTimeout(() => setSaved(false), 1600);
      }
      // ok === null means a save was already in progress — silently skip
    }, 1500);
    return () => clearTimeout(saveTimer.current);
  }, [data, venues, user, teamId, dbLoading]);

  // ── Dedicated debounced save for advance_tm_data ──────────────────────────
  // Kept separate from saveNormalized so resetAdvance can never be raced by the
  // bulk flush writing stale advance content back over a reset.
  const advTmSaveTimer   = useRef(null);
  const advResetLock     = useRef(false);  // blocks advance writes for 5s after reset
  const advTmDirtyRef    = useRef(new Set()); // show ids with LOCAL unsaved advance edits
  const advAllDataRef    = useRef(null);      // render-synced `data` for flush-on-switch

  // ── Promoter-activity toast ────────────────────────────────────────────────
  // Set from the Realtime shows handler when promoter-side advance changes land
  // (submit, new activity entries, section submissions). Rendered as an amber
  // toast in the desktop TM view; click-through opens the show's advance tab.
  const [promoterNotice, setPromoterNotice] = useState(null); // { showId, date, kind, ts }
  const [advDraft, setAdvDraft] = useState(null); // DF-3: null | { busy } | { ok, msg }
  const selfAdvDataWriteRef = useRef(0); // TM-side advance_data writes (ROS dismiss) — suppress self-toast
  useEffect(() => {
    if (!promoterNotice) return;
    const t = setTimeout(() => setPromoterNotice(null), 10000);
    return () => clearTimeout(t);
  }, [promoterNotice]);
  const show_for_adv = selS ? data.artists.flatMap(a => a.tours.flatMap(t => t.shows)).find(s => s.id === selS) : null;
  const advTmDataRef = useRef(show_for_adv?.advanceTmData);
  advTmDataRef.current = show_for_adv?.advanceTmData;
  advAllDataRef.current = data;
  useEffect(() => {
    if (!selS || !user || dbLoading) return;
    // Only write when the TM actually edited something this session. Previously
    // this scheduled a write on ANY dep change — including merely opening the
    // show — so a tab holding a stale copy silently overwrote newer DB data
    // (this is what ate bill acts added on another tab/machine).
    if (!advTmDirtyRef.current.has(selS)) return;
    if (advTmSaveTimer.current) clearTimeout(advTmSaveTimer.current);
    advTmSaveTimer.current = setTimeout(() => {
      if (advResetLock.current) return;  // reset in progress — skip
      const tmData = advTmDataRef.current;
      if (tmData === undefined) return;
      const sid = selS;
      supabase.from("shows").update({ advance_tm_data: tmData || {} }).eq("id", sid)
        .then(({ error }) => { if (!error) advTmDirtyRef.current.delete(sid); });
    }, 1200);
    return () => clearTimeout(advTmSaveTimer.current);
  }, [selS, show_for_adv?.advanceTmData, user, dbLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  // Flush a pending advance save when switching away from a show. Previously
  // the debounce cleanup just discarded the timer — edits made <1.2s before
  // deselecting / switching shows were silently lost.
  const prevAdvSelSRef = useRef(null);
  useEffect(() => {
    const prevId = prevAdvSelSRef.current;
    prevAdvSelSRef.current = selS;
    if (!prevId || prevId === selS || !advTmDirtyRef.current.has(prevId)) return;
    if (advResetLock.current) return;
    const prevShow = (advAllDataRef.current?.artists || [])
      .flatMap(a => a.tours).flatMap(t => t.shows).find(s => s.id === prevId);
    if (!prevShow) return;
    supabase.from("shows").update({ advance_tm_data: prevShow.advanceTmData || {} }).eq("id", prevId)
      .then(({ error }) => { if (!error) advTmDirtyRef.current.delete(prevId); });
  }, [selS]);

  // ── Catch-up fetch on tab wake ─────────────────────────────────────────────
  // The Realtime reconnect re-subscribes but does NOT replay events missed
  // while the tab was hidden/asleep — promoter portal submissions made in that
  // window never appeared until a full refresh. On wake, re-pull the open
  // show's advance columns and patch state (advance_tm_data only adopted when
  // there are no local unsaved edits).
  const advCatchUpShowRef = useRef(null);
  advCatchUpShowRef.current = selS;
  useEffect(() => {
    const onWake = async () => {
      if (document.visibilityState !== "visible") return;
      const sid = advCatchUpShowRef.current;
      if (!sid) return;
      const { data: rows } = await supabase
        .from("shows")
        .select("id, advance_data, advance_tm_data, advance_section_status, advance_last_viewed_at")
        .eq("id", sid).limit(1);
      const row = rows && rows[0];
      if (!row || advCatchUpShowRef.current !== sid) return; // show changed while fetching
      setData(p => ({ ...p, artists: p.artists.map(a => ({ ...a,
        tours: a.tours.map(t => ({ ...t,
          shows: t.shows.map(s => {
            if (s.id !== sid) return s;
            return {
              ...s,
              advanceData:          row.advance_data           || {},
              advanceSectionStatus: row.advance_section_status || {},
              advanceLastViewedAt:  row.advance_last_viewed_at || null,
              ...(!advTmDirtyRef.current.has(sid) && { advanceTmData: row.advance_tm_data || {} }),
            };
          })
        }))
      })) }));
    };
    document.addEventListener("visibilitychange", onWake);
    return () => document.removeEventListener("visibilitychange", onWake);
  }, []);

  const goTour = (aId, tId) => { setSelA(aId); setSelT(tId); setSelS(null); setEDraft(""); setView("tour"); };
  const goToShow = (aId, tId, sId) => { setSelA(aId); setSelT(tId); setSelS(sId); setEDraft(""); setView("tour"); };

  // ── Artist CRUD ──
  const saveArtist = ({ name, genre }) => {
    setData(p => ({ ...p, artists: [...p.artists, { id: "a" + uid(), name, genre, tours: [] }] }));
    setMArtist(false);
  };

  const saveEditArtist = (payload) => {
    if (!payload) {
      const deletedArtist = data.artists.find(a => a.id === mEditArtist);
      const aId = mEditArtist;
      const tourIds = deletedArtist?.tours?.map(t => t.id) || [];
      const showIds = deletedArtist?.tours?.flatMap(t => t.shows?.map(s => s.id) || []) || [];
      setData(p => ({ ...p, artists: p.artists.filter(a => a.id !== aId) }));
      if (selA === aId) { setSelA(null); setSelT(null); setSelS(null); setView("overview"); }
      if (undoAction?.timer) clearTimeout(undoAction.timer);
      if (undoAction?.deleteTimer) clearTimeout(undoAction.deleteTimer);
      const deleteTimer = setTimeout(() => {
        if (showIds.length > 0) supabase.from("shows").delete().in("id", showIds);
        if (tourIds.length > 0) supabase.from("tours").delete().in("id", tourIds);
        supabase.from("artists").delete().eq("id", aId);
      }, 30000);
      const timer = setTimeout(() => setUndoAction(null), 30000);
      setUndoAction({
        label: `${deletedArtist?.name || "Artist"} removed from roster`,
        timer, deleteTimer,
        restore: () => {
          clearTimeout(timer);
          clearTimeout(deleteTimer);
          setData(p => ({ ...p, artists: [...p.artists, deletedArtist] }));
          setUndoAction(null);
        },
      });
    } else {
      const aId = mEditArtist;
      setData(p => ({ ...p, artists: p.artists.map(a => a.id !== aId ? a : { ...a, ...payload }) }));
      if (payload.members) syncMembersToRoster(aId, payload.members);
    }
    setMEditArtist(null);
  };

  const saveArtistSection = (payload) => {
    const aId = mArtistSection.artistId;
    setData(p => ({ ...p, artists: p.artists.map(a =>
      a.id === aId ? { ...a, ...payload } : a
    )}));
    setMArtistSection(null);
    if (payload.members) syncMembersToRoster(aId, payload.members);

    // ── Auto-sync mgmt / booking / label contacts to the directory ──
    const candidates = [
      payload.mgmt    && payload.mgmt.email    ? { name: payload.mgmt.name    || "", company: payload.mgmt.company  || "", role: "Management",   email: payload.mgmt.email,    phone: payload.mgmt.phone    || "" } : null,
      payload.booking && payload.booking.email ? { name: payload.booking.agent || "", company: payload.booking.agency || "", role: "Booking Agent", email: payload.booking.email, phone: payload.booking.phone || "" } : null,
      payload.label   && payload.label.email   ? { name: payload.label.contact || "", company: payload.label.name    || "", role: "Label",         email: payload.label.email,   phone: payload.label.phone   || "" } : null,
    ].filter(Boolean);

    if (candidates.length === 0) return;

    setData(p => {
      const existing = p.contacts || [];
      const toAdd = candidates.filter(c => {
        const emailLower = c.email.toLowerCase();
        return !existing.some(e => (e.email || "").toLowerCase() === emailLower);
      });
      if (toAdd.length === 0) return p;
      const newContacts = toAdd.map(c => {
        const id = "c" + uid();
        supabase.from("contacts").insert({ id, user_id: user?.id, team_id: teamId, ...c });
        return { id, ...c };
      });
      return { ...p, contacts: [...existing, ...newContacts] };
    });
  };

  const uploadArtistDoc = async (file, artistId, label, extra = {}) => {
    if (!file || !artistId) return;
    setArtistDocUploading(true);
    setArtistDocUploadErr("");
    try {
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
      const subfolder = extra.section === "press" ? "press" : "docs";
      const path = `artist-docs/${artistId}/${subfolder}/${Date.now()}_${safeName}`;
      const { error } = await supabase.storage.from("advance-files").upload(path, file, { upsert: true });
      if (error) throw error;
      const { data: urlData } = supabase.storage.from("advance-files").getPublicUrl(path);
      if (!urlData?.publicUrl) throw new Error("Could not retrieve public URL");
      const newDoc = { id: uid(), label, fileUrl: urlData.publicUrl, fileName: file.name, filePath: path, ...extra };
      setData(p => ({ ...p, artists: p.artists.map(a =>
        a.id !== artistId ? a : { ...a, documents: [...(a.documents || []), newDoc] }
      )}));
    } catch (e) {
      setArtistDocUploadErr(e.message || "Upload failed");
    }
    setArtistDocUploading(false);
  };

  const removeArtistDoc = async (artistId, docId) => {
    const artist = data.artists.find(a => a.id === artistId);
    if (!artist) return;
    const doc = (artist.documents || []).find(d => d.id === docId);
    if (!doc) return;
    try { await supabase.storage.from("advance-files").remove([doc.filePath]); } catch (err) { /* ignore */ }
    setData(p => ({ ...p, artists: p.artists.map(a =>
      a.id !== artistId ? a : { ...a, documents: (a.documents || []).filter(d => d.id !== docId) }
    )}));
  };

  // ── PLOTR: load stage_plots for an artist ───────────────────────────────────
  const loadArtistStagePlots = async (aId, force = false) => {
    if (!force && (artistStagePlots[aId] || stagePlotsLoading[aId])) return;
    setStagePlotsLoading(prev => ({ ...prev, [aId]: true }));
    const { data: plots } = await supabase.from("stage_plots").select("*")
      .eq("artist_id", aId).order("updated_at", { ascending: false });
    setArtistStagePlots(prev => ({ ...prev, [aId]: plots || [] }));
    setStagePlotsLoading(prev => ({ ...prev, [aId]: false }));
  };

  // ── Tour CRUD ──
  const duplicateTour = () => {
    if (!tour) return;
    const newId = "t" + uid();
    const dup = { ...tour, id: newId, name: tour.name + " (Copy)", shows: tour.shows.map(s => ({ ...s, id: "s" + uid(), status: "unstarted", notes: "", flagged: false, checklist: buildChecklist(artist?.checklistCats, artist?.rider) })) };
    setData(p => ({ ...p, artists: p.artists.map(a => a.id !== selA ? a : { ...a, tours: [...a.tours, dup] }) }));
    setSelT(newId); setSelS(null);
  };

  const saveTour = async ({ name, year, templateId }) => {
    if (mTour.mode === "add") {
      // TT-1: apply template advance_config if selected
      const tpl = templateId ? tourTemplates.find(t => t.id === templateId) : null;
      const nt = { id: "t" + uid(), name, year, shows: [], advanceConfig: tpl ? { ...tpl.advance_config } : {} };
      setData(p => ({ ...p, artists: p.artists.map(a => a.id !== selA ? a : { ...a, tours: [...a.tours, nt] }) }));
      setSelT(nt.id); setSelS(null); setView("tour");
      // TT-1: auto-add roster profiles from template (only those still on this team)
      if (tpl?.roster_profile_ids?.length) {
        for (const pid of tpl.roster_profile_ids) {
          const profile = profiles.find(p => p.id === pid);
          if (profile) await addProfileToTour(pid, nt.id);
        }
      }
    } else {
      setData(p => ({ ...p, artists: p.artists.map(a => a.id !== selA ? a : { ...a, tours: a.tours.map(t => t.id !== selT ? t : { ...t, name, year }) }) }));
    }
    setMTour(null);
  };

  const inlineTourName = v =>
    setData(p => ({ ...p, artists: p.artists.map(a => a.id !== selA ? a : { ...a, tours: a.tours.map(t => t.id !== selT ? t : { ...t, name: v }) }) }));

  const deleteTour = () => {
    const aId = selA, tId = selT;
    const deletedTour = tour;
    const showIds = deletedTour?.shows?.map(s => s.id) || [];
    setSelT(null); setSelS(null); setView("artist");
    setData(p => ({ ...p, artists: p.artists.map(a => a.id !== aId ? a : { ...a, tours: a.tours.filter(t => t.id !== tId) }) }));
    if (undoAction?.timer) clearTimeout(undoAction.timer);
    if (undoAction?.deleteTimer) clearTimeout(undoAction.deleteTimer);
    logActivity("deleted", "tour", tId, deletedTour?.name || "Tour");
    const tourDeleteTimer = setTimeout(() => {
      if (showIds.length > 0) supabase.from("shows").delete().in("id", showIds);
      supabase.from("tours").delete().eq("id", tId);
    }, 30000);
    const timer = setTimeout(() => setUndoAction(null), 30000);
    setUndoAction({
      label: `Tour "${deletedTour?.name || "Tour"}" deleted`,
      timer, deleteTimer: tourDeleteTimer,
      restore: () => {
        clearTimeout(timer);
        clearTimeout(tourDeleteTimer);
        setData(p => ({ ...p, artists: p.artists.map(a => a.id !== aId ? a : { ...a, tours: [...a.tours, deletedTour] }) }));
        setUndoAction(null);
      },
    });
  };

  // ── Show CRUD ──
  const saveShow = (payload) => {
    if (!payload) {
      const deletedShow = tour?.shows.find(s => s.id === mShow.showId);
      const deletedVenue = deletedShow ? venues[deletedShow.venueId] : null;
      const aId = selA, tId = selT, sId = mShow.showId;
      if (selS === sId) { setSelS(null); setEDraft(""); }
      setData(p => ({ ...p, artists: p.artists.map(a => a.id !== aId ? a : { ...a, tours: a.tours.map(t => t.id !== tId ? t : { ...t, shows: t.shows.filter(s => s.id !== sId) }) }) }));
      logActivity("deleted", "show", sId, `${deletedVenue?.name || "Show"} — ${fmtDate(deletedShow?.date || "")}`);
      if (undoAction?.timer) clearTimeout(undoAction.timer);
      if (undoAction?.deleteTimer) clearTimeout(undoAction.deleteTimer);
      const showDeleteTimer = setTimeout(() => { supabase.from("shows").delete().eq("id", sId); }, 30000);
      const timer = setTimeout(() => setUndoAction(null), 30000);
      setUndoAction({
        label: `${deletedVenue?.name || "Show"} on ${fmtDate(deletedShow?.date || "")} deleted`,
        timer, deleteTimer: showDeleteTimer,
        restore: () => {
          clearTimeout(timer);
          clearTimeout(showDeleteTimer);
          setData(p => ({ ...p, artists: p.artists.map(a => a.id !== aId ? a : { ...a, tours: a.tours.map(t => t.id !== tId ? t : { ...t, shows: [...t.shows, deletedShow].sort((a,b) => a.date.localeCompare(b.date)) }) }) }));
          setUndoAction(null);
        },
      });
    } else if (mShow.mode === "add") {
      const ns = { id: "s" + uid(), ...payload, notes: "", checklist: buildChecklist(artist?.checklistCats, artist?.rider) };
      setData(p => ({ ...p, artists: p.artists.map(a => a.id !== selA ? a : { ...a, tours: a.tours.map(t => t.id !== selT ? t : { ...t, shows: [...t.shows, ns].sort((a,b) => a.date.localeCompare(b.date)) }) }) }));
      logActivity("created", "show", ns.id, `${venues[ns.venueId]?.name || "Show"} — ${fmtDate(ns.date)}`);
      // Immediately persist so a refresh can't lose the new show
      supabase.from("shows").upsert({
        id: ns.id, tour_id: selT, artist_id: selA, user_id: user?.id, team_id: teamId,
        venue_id: ns.venueId || null, date: ns.date,
        status: ns.status || "unstarted", notes: "", flagged: false,
        checklist: ns.checklist || {}, settlement: {},
        advance_token: null, advance_data: {}, advance_tm_data: {},
        lodging: ns.lodging || {}, linked_contacts: [],
      }, { onConflict: "id" }).then(({ error }) => {
        if (error) console.error("[ADVANCD] saveShow immediate upsert failed:", error);
      });
    } else {
      setData(p => ({ ...p, artists: p.artists.map(a => a.id !== selA ? a : { ...a, tours: a.tours.map(t => t.id !== selT ? t : { ...t, shows: t.shows.map(s => s.id !== mShow.showId ? s : { ...s, ...payload }).sort((a,b) => a.date.localeCompare(b.date)) }) }) }));
    }
    setMShow(null);
  };

  // ── Bulk Import ──
  const handleBulkImport = (shows) => {
    // First pass: create stub venues for any unmatched show that has a parsed venue name
    const stubMap = {}; // venueName+city → new venue id
    const newVenues = {};
    shows.forEach(r => {
      if (!r.matchedVenueId && r.venueName) {
        const key = `${r.venueName}||${r.city || ""}`;
        if (!stubMap[key]) {
          const vid = "v_stub_" + uid();
          stubMap[key] = vid;
          newVenues[vid] = {
            id: vid, name: r.venueName, city: r.city || "",
            addressStreet: r.venueAddress || "", addressState: r.venueState || "", addressZip: r.venueZip || "", addressCountry: "",
            capacity: "", ages: "", gaSeated: "", website: "",
            promoterName: r.promoterName || "", promoterEmail: r.promoterEmail || "",
            promoterCompany: "", promoterPhone: r.promoterPhone || "",
            loadIn: "", parking: "", houseGear: "", greenRooms: "", bathrooms: "",
            merchSplit: "", notes: "",
          };
        }
        r = { ...r, matchedVenueId: stubMap[key] };
      }
    });
    if (Object.keys(newVenues).length > 0) {
      setVenues(prev => ({ ...prev, ...newVenues }));
    }

    // Backfill missing address + promoter info on matched existing venues
    const venuePatches = {};
    shows.forEach(r => {
      if (!r.matchedVenueId || newVenues[r.matchedVenueId]) return; // skip stubs
      const ev = venues[r.matchedVenueId];
      if (!ev) return;
      const patch = {};
      if (!ev.addressStreet && r.venueAddress)  patch.addressStreet  = r.venueAddress;
      if (!ev.addressState  && r.venueState)    patch.addressState   = r.venueState;
      if (!ev.addressZip    && r.venueZip)      patch.addressZip     = r.venueZip;
      if (!ev.city          && r.city)          patch.city           = r.city;
      if (!ev.promoterName  && r.promoterName)  patch.promoterName   = r.promoterName;
      if (!ev.promoterEmail && r.promoterEmail) patch.promoterEmail  = r.promoterEmail;
      if (!ev.promoterPhone && r.promoterPhone) patch.promoterPhone  = r.promoterPhone;
      if (Object.keys(patch).length > 0) venuePatches[r.matchedVenueId] = patch;
    });
    if (Object.keys(venuePatches).length > 0) {
      setVenues(prev => {
        const next = { ...prev };
        Object.entries(venuePatches).forEach(([vid, patch]) => { next[vid] = { ...next[vid], ...patch }; });
        return next;
      });
      // Persist venue patches to DB
      Object.entries(venuePatches).forEach(([vid, patch]) => {
        const updated = { ...(venues[vid] || {}), ...patch };
        supabase.from("venues").update({ ...Object.fromEntries(Object.entries(patch).map(([k, v]) => [
          k === "addressStreet" ? "address_street" : k === "addressState" ? "address_state" : k === "addressZip" ? "address_zip" :
          k === "promoterName" ? "promoter_name" : k === "promoterEmail" ? "promoter_email" : k === "promoterPhone" ? "promoter_phone" : k,
          v
        ])) }).eq("id", vid).then(({ error }) => { if (error) console.error("[ADVANCD] venue backfill patch failed:", error); });
      });
    }

    // Auto-create promoter contacts from parsed data (deduplicated by email)
    const autoContactMap = {}; // emailKey → { id, isNew, contact }
    shows.forEach(r => {
      if (!r.promoterName) return;
      const emailKey = r.promoterEmail ? r.promoterEmail.toLowerCase() : `__name__${r.promoterName.toLowerCase()}`;
      if (autoContactMap[emailKey]) return; // already handled in this batch
      const existing = r.promoterEmail
        ? data.contacts.find(c => c.email?.toLowerCase() === r.promoterEmail.toLowerCase())
        : data.contacts.find(c => c.name?.toLowerCase() === r.promoterName.toLowerCase() && c.role === "Promoter");
      if (existing) {
        autoContactMap[emailKey] = { id: existing.id, isNew: false };
      } else {
        const cid = "c" + uid();
        autoContactMap[emailKey] = { id: cid, isNew: true, contact: { id: cid, name: r.promoterName, email: r.promoterEmail || "", phone: r.promoterPhone || "", company: "", role: "Promoter", notes: "" } };
      }
    });
    const newAutoContacts = Object.values(autoContactMap).filter(x => x.isNew).map(x => x.contact);

    const newShows = shows.map(r => {
      // Re-resolve stub venue id if needed
      if (!r.matchedVenueId && r.venueName) {
        const key = `${r.venueName}||${r.city || ""}`;
        r = { ...r, matchedVenueId: stubMap[key] || null };
      }
      // Build a notes string from AI-parsed extras not captured in checklist fields
      const noteParts = [];
      if (r.specialTerms)    noteParts.push(`Special terms: ${r.specialTerms}`);
      if (r.depositAmount)   noteParts.push(`Deposit: $${Number(r.depositAmount).toLocaleString()}${r.depositDue ? ` due ${r.depositDue}` : ""}`);
      if (r.marketingContact) noteParts.push(`Marketing: ${r.marketingContact}`);
      if (r.advanceContact)  noteParts.push(`Advance: ${r.advanceContact}`);
      if (r.setLength)       noteParts.push(`Set length: ${r.setLength} min`);
      if (r.notes)           noteParts.push(r.notes);

      const cl = buildChecklist(artist?.checklistCats, artist?.rider);

      // Load-in / Soundcheck
      if (r.loadIn || r.soundcheck || r.doorsTime || r.setTime || r.curfew) {
        cl.loadIn = {
          ...cl.loadIn,
          fields: {
            ...cl.loadIn?.fields,
            ...(r.loadIn     && { "Load-in time": r.loadIn }),
            ...(r.soundcheck && { "Soundcheck time": r.soundcheck }),
            ...(r.doorsTime  && { "Doors time": r.doorsTime }),
            ...(r.setTime    && { "Set time": r.setTime }),
            ...(r.curfew     && { "Curfew": r.curfew }),
          }
        };
      }

      // Settlement / Deal Terms
      if (r.guarantee || r.dealTerms || r.promoterName) {
        cl.settlement = {
          ...cl.settlement,
          fields: {
            ...cl.settlement?.fields,
            ...(r.guarantee    && { "Guarantee confirmed": `$${Number(r.guarantee).toLocaleString()}` }),
            ...(r.dealTerms    && { "Deal memo signed": r.dealTerms }),
            ...(r.promoterName && { "Settlement contact": `${r.promoterName}${r.promoterEmail ? ` <${r.promoterEmail}>` : ""}${r.promoterPhone ? ` · ${fmtPhone(r.promoterPhone)}` : ""}` }),
          }
        };
      }

      // Hospitality / Rider
      if (r.catering || r.buyout) {
        cl.hospitality = {
          ...cl.hospitality,
          fields: {
            ...cl.hospitality?.fields,
            ...(r.catering && { "Catering": r.catering }),
            ...(r.buyout   && { "Buyout amount": `$${Number(r.buyout).toLocaleString()}` }),
          }
        };
      }

      // Merch Terms
      if (r.merchSplit || r.merchNotes) {
        cl.merch = {
          ...cl.merch,
          fields: {
            ...cl.merch?.fields,
            ...(r.merchSplit  && { "Merch split %": r.merchSplit }),
            ...(r.merchNotes  && { "Merch rep on-site": r.merchNotes }),
          }
        };
      }

      // Guest List
      if (r.glCap || r.willCall) {
        cl.guestList = {
          ...cl.guestList,
          fields: {
            ...cl.guestList?.fields,
            ...(r.glCap    && { "GL cap confirmed": String(r.glCap) }),
            ...(r.willCall && { "Will call instructions": r.willCall }),
          }
        };
      }

      // Pre-fill the settlement card fields
      const settlement = {};
      if (r.guarantee)      settlement.guarantee      = `$${Number(r.guarantee).toLocaleString()}`;
      if (r.dealTerms) {
        const dt = r.dealTerms.toLowerCase();
        if (dt.includes("vs") || dt.includes("nbor"))          settlement.dealType = "Vs";
        else if (dt.includes("plus") || /\+\s*\d+%/.test(dt)) settlement.dealType = "Plus";
        else                                                    settlement.dealType = "Flat";
        if (settlement.dealType === "Vs" || settlement.dealType === "Plus") settlement.vsTerms = r.dealTerms;
      }
      if (r.depositAmount)  settlement.deposit        = `$${Number(r.depositAmount).toLocaleString()}`;
      if (r.depositDue)     settlement.depositDue     = r.depositDue;
      if (r.specialTerms)   settlement.notes          = r.specialTerms;
      if (r.promoterName)   settlement.promoterName   = r.promoterName;
      if (r.promoterEmail)  settlement.promoterEmail  = r.promoterEmail;
      if (r.promoterPhone)  settlement.promoterPhone  = r.promoterPhone;
      if (r.radiusDistance || r.radiusDuration) {
        settlement.radiusClause   = true;
        if (r.radiusDistance) settlement.radiusDistance = r.radiusDistance;
        if (r.radiusDuration) settlement.radiusDuration = r.radiusDuration;
      }

      // Pre-fill TM advance portal data from parsed fields
      const advanceTmData = {};
      if (r.loadIn || r.soundcheck || r.doorsTime || r.setTime || r.curfew) {
        // Populate the bill array for the headliner using parsed times
        const hlBill = { id: "b2", name: "Headliner",
          load: r.loadIn    || "",
          sc:   r.soundcheck || "",
          set:  r.setTime   || "",
        };
        advanceTmData.schedule = {
          ...(r.loadIn    && { venue_access: r.loadIn }),
          ...(r.doorsTime && { doors_public: r.doorsTime }),
          ...(r.curfew    && { curfew: r.curfew }),
          bill: [
            { id: "b0", name: "1st of 3",       load: "", sc: "", set: "" },
            { id: "b1", name: "Direct Support", load: "", sc: "", set: "" },
            hlBill,
          ],
          changeovers: [{ time: "" }, { time: "" }],
        };
      }
      if (r.buyout) {
        advanceTmData.hospitality = {
          buyout: `$${Number(r.buyout).toLocaleString()}`,
        };
      }
      if (r.merchSplit || r.merchNotes) {
        advanceTmData.merch = {
          ...(r.merchSplit && { merch_split: r.merchSplit }),
          ...(r.merchNotes && { merch_notes: r.merchNotes }),
        };
      }
      if (r.glCap || r.willCall) {
        advanceTmData.guestList = {
          ...(r.glCap    && { gl_cap: String(r.glCap) }),
          ...(r.willCall && { will_call: r.willCall }),
        };
      }
      // Deal-memo venue facts — stored on the SHOW so the Info tab can prefer
      // event-specific values over the venue CRM (e.g. a 21+ bar show at a
      // venue whose record says All Ages).
      if (r.ages || r.venueCapacity) {
        advanceTmData._dealMemo = {
          ...(r.ages          && { ages: String(r.ages) }),
          ...(r.venueCapacity && { capacity: String(r.venueCapacity) }),
        };
      }

      // Merge matched venue CRM data — fills gaps not covered by parsed deal text
      const crmVenue = r.matchedVenueId ? (venues[r.matchedVenueId] || newVenues[r.matchedVenueId]) : null;
      if (crmVenue) {
        if (crmVenue.loadIn && !advanceTmData.schedule?.venue_access)
          advanceTmData.schedule = { ...(advanceTmData.schedule || {}), venue_access: crmVenue.loadIn };
        if (crmVenue.parking)
          advanceTmData.parking = { ...(advanceTmData.parking || {}), park_notes: crmVenue.parking };
        if (crmVenue.houseGear)
          advanceTmData.production = { ...(advanceTmData.production || {}), prod_notes: crmVenue.houseGear };
        if (crmVenue.greenRooms)
          advanceTmData.greenRoom = { ...(advanceTmData.greenRoom || {}), rooms: crmVenue.greenRooms };
        if (crmVenue.bathrooms)
          advanceTmData.greenRoom = { ...(advanceTmData.greenRoom || {}), shower: crmVenue.bathrooms };
        if (crmVenue.merchSplit && !advanceTmData.merch?.merch_split)
          advanceTmData.merch = { ...(advanceTmData.merch || {}), merch_split: crmVenue.merchSplit };
      }

      // Link auto-created promoter contact to this show
      const promoEmailKey = r.promoterEmail ? r.promoterEmail.toLowerCase() : r.promoterName ? `__name__${r.promoterName.toLowerCase()}` : null;
      const promoContactRef = promoEmailKey ? autoContactMap[promoEmailKey] : null;
      const autoLinkedContacts = promoContactRef ? [{ contactId: promoContactRef.id, role: "Promoter" }] : [];

      return {
        id: "s" + uid(),
        date: r.date,
        venueId: r.matchedVenueId,
        status: r.isConfirmed ? "confirmed" : "unstarted",
        notes: noteParts.join("\n"),
        checklist: cl,
        settlement,
        ...(autoLinkedContacts.length > 0 && { linkedContacts: autoLinkedContacts }),
        ...(Object.keys(advanceTmData).length > 0 && { advanceTmData }),
      };
    });
    setData(p => ({
      ...p,
      contacts: newAutoContacts.length > 0 ? [...(p.contacts || []), ...newAutoContacts] : p.contacts,
      artists: p.artists.map(a => a.id !== selA ? a : {
        ...a, tours: a.tours.map(t => t.id !== selT ? t : {
          ...t, shows: [...t.shows, ...newShows].sort((a, b) => a.date.localeCompare(b.date))
        })
      })
    }));
    // Immediately persist venues + shows so a refresh can't lose imported data
    const venueRows = Object.values(newVenues);
    if (venueRows.length > 0) {
      supabase.from("venues").upsert(
        venueRows.map(v => ({ ...appVenueToDB(user?.id, v), team_id: teamId })),
        { onConflict: "id" }
      );
    }
    if (newShows.length > 0) {
      supabase.from("shows").upsert(
        newShows.map(s => ({
          id: s.id, tour_id: selT, artist_id: selA, user_id: user?.id, team_id: teamId,
          venue_id: s.venueId || null, date: s.date,
          status: s.status || "unstarted", notes: s.notes || "", flagged: false,
          checklist: s.checklist || {}, settlement: s.settlement || {},
          advance_token: null, advance_data: {}, advance_tm_data: s.advanceTmData || {},
          lodging: {}, linked_contacts: s.linkedContacts || [],
        })),
        { onConflict: "id" }
      ).then(({ error }) => {
        if (error) console.error("[ADVANCD] handleBulkImport: immediate show upsert failed:", error);
        else console.log(`[ADVANCD] handleBulkImport: persisted ${newShows.length} show(s) immediately`);
      });
    }
    // Persist auto-created promoter contacts
    if (newAutoContacts.length > 0) {
      supabase.from("contacts").insert(
        newAutoContacts.map(c => ({ id: c.id, user_id: user?.id, team_id: teamId, name: c.name, company: c.company || "", role: c.role || "", email: c.email || "", phone: c.phone || "", notes: c.notes || "" }))
      ).then(({ error }) => { if (error) console.error("[ADVANCD] handleBulkImport: contact insert failed:", error); });
    }
    setMImport(false);
  };

  // ── Data Import handlers ──
  const handleImportVenues = (items) => {
    setVenues(prev => {
      const updated = { ...prev };
      items.forEach(v => { if (v.name) updated[v.id] = v; });
      return updated;
    });
  };
  const handleImportContacts = (items) => {
    setData(prev => ({
      ...prev,
      contacts: [...(prev.contacts || []), ...items.filter(c => c.name)],
    }));
  };

  // ── Venue CRUD ──
  const saveVenue = (payload) => {
    if (!payload) {
      const vId = mVenue.venueId;
      const deletedVenue = venues[vId];
      setVenues(p => { const n = { ...p }; delete n[vId]; return n; });
      logActivity("deleted", "venue", vId, deletedVenue?.name || "Venue");
      if (undoAction?.timer) clearTimeout(undoAction.timer);
      if (undoAction?.deleteTimer) clearTimeout(undoAction.deleteTimer);
      const venueDeleteTimer = setTimeout(() => { supabase.from("venues").delete().eq("id", vId); }, 30000);
      const timer = setTimeout(() => setUndoAction(null), 30000);
      setUndoAction({
        label: `Venue "${deletedVenue?.name || "Venue"}" deleted`,
        timer, deleteTimer: venueDeleteTimer,
        restore: () => {
          clearTimeout(timer);
          clearTimeout(venueDeleteTimer);
          setVenues(p => ({ ...p, [vId]: deletedVenue }));
          setUndoAction(null);
        },
      });
    } else if (mVenue.mode === "add") {
      const id = "v" + uid();
      setVenues(p => ({ ...p, [id]: { id, ...payload } }));
    } else {
      setVenues(p => ({ ...p, [mVenue.venueId]: { id: mVenue.venueId, ...payload } }));
    }
    setMVenue(null);
  };

  // ── Venue Directory ──
  const searchDirectory = async (q, kind) => {
    setDirLoading(true);
    const isSearch = q.trim().length > 0;
    let query = supabase.from("public_venues").select("*").eq("kind", kind).order("name").limit(isSearch ? 100 : 10000);
    if (isSearch) query = query.ilike("name", `%${q.trim()}%`);
    const { data } = await query;
    setDirResults(data || []);
    setDirLoading(false);
  };

  const addFromDirectory = (pv) => {
    const id = "v" + uid();
    const newVenue = {
      id,
      name:           pv.name,
      addressStreet:  pv.address_street  || "",
      city:           pv.city            || "",
      addressState:   pv.address_state   || "",
      addressZip:     pv.address_zip     || "",
      addressCountry: pv.address_country || "",
      capacity:       pv.capacity        || "",
      ages: "", gaSeated: "", website: "",
      promoterName: "", promoterEmail: "", promoterCompany: "", promoterPhone: "",
      loadIn: "", parking: "", houseGear: "", greenRooms: "", bathrooms: "",
      merchSplit: "", notes: "",
      kind: pv.kind || "venue",
    };
    setVenues(p => ({ ...p, [id]: newVenue }));
    setDirModal(null);
    setDirQuery("");
    setDirResults([]);
    setMVenue({ mode: "edit", venueId: id });
  };

  // Silent variant used by ShowModal: adds venue to state + returns it, no modals opened
  const addVenueFromDirForShow = (pv) => {
    const id = "v" + uid();
    const newVenue = {
      id,
      name:           pv.name,
      addressStreet:  pv.address_street  || "",
      city:           pv.city            || "",
      addressState:   pv.address_state   || "",
      addressZip:     pv.address_zip     || "",
      addressCountry: pv.address_country || "",
      capacity:       pv.capacity        || "",
      ages: "", gaSeated: "", website: "",
      promoterName: "", promoterEmail: "", promoterCompany: "", promoterPhone: "",
      loadIn: "", parking: "", houseGear: "", greenRooms: "", bathrooms: "",
      merchSplit: "", notes: "",
      kind: pv.kind || "venue",
    };
    setVenues(p => ({ ...p, [id]: newVenue }));
    return newVenue;
  };

  // Sending an advance moves an Unstarted show to Advancing automatically so booking
  // status tracks reality. Manual pills can still override afterwards. Accepts a single
  // show ID or an array. Persisted by the debounced saveNormalized effect like any setData.
  const bumpToAdvancing = (showIds) => {
    const ids = new Set(Array.isArray(showIds) ? showIds : [showIds]);
    if (!ids.size) return;
    setData(p => ({ ...p, artists: p.artists.map(a => ({ ...a, tours: a.tours.map(t => ({ ...t,
      shows: t.shows.map(s => ids.has(s.id) && (!s.status || s.status === "unstarted") ? { ...s, status: "advancing" } : s),
    })) })) }));
  };

  // ── Generate single advance token (used from AdvanceLinksModal) ──
  const generateAdvanceLink = async (showId) => {
    const token = crypto.randomUUID();
    const { error } = await supabase.from("shows").update({ advance_token: token }).eq("id", showId);
    if (error) { console.error("[ADVANCD] generateAdvanceLink failed", error); return; }
    setData(prev => ({
      ...prev,
      artists: prev.artists.map(a => a.id !== selA ? a : {
        ...a,
        tours: a.tours.map(t => t.id !== selT ? t : {
          ...t,
          shows: t.shows.map(s => s.id === showId ? { ...s, advanceToken: token } : s),
        }),
      }),
    }));
    bumpToAdvancing(showId);
  };

  // ── Launch Advances (bulk token generation for a whole tour) ──
  const launchAllAdvances = async () => {
    if (!tour) return;
    const noToken = tour.shows.filter(s => !s.advanceToken);
    if (!noToken.length || launchAdvLoading) return;
    setLaunchAdvLoading(true);
    const tokenMap = {};
    noToken.forEach(s => { tokenMap[s.id] = crypto.randomUUID(); });
    // Use allSettled so a partial DB failure doesn't block the rest
    const results = await Promise.allSettled(
      Object.entries(tokenMap).map(async ([id, token]) => {
        const { error } = await supabase.from("shows").update({ advance_token: token }).eq("id", id);
        if (error) throw error;
        return id;
      })
    );
    const succeeded = new Set(results.filter(r => r.status === "fulfilled").map(r => r.value));
    const failCount = results.length - succeeded.size;
    if (failCount > 0) console.error(`[ADVANCD] launchAllAdvances: ${failCount} token save(s) failed`);
    if (succeeded.size === 0) { setLaunchAdvLoading(false); return; }
    setData(prev => ({
      ...prev,
      artists: prev.artists.map(a => a.id !== selA ? a : {
        ...a,
        tours: a.tours.map(t => t.id !== selT ? t : {
          ...t,
          shows: t.shows.map(s => succeeded.has(s.id) ? { ...s, advanceToken: tokenMap[s.id] } : s),
        }),
      }),
    }));
    bumpToAdvancing([...succeeded]);
    setLaunchAdvLoading(false);
    setLaunchAdvDone(succeeded.size);
    setTimeout(() => setLaunchAdvDone(0), 5000);
  };

  // ── Contact CRUD ──
  const saveContact = (payload) => {
    if (!payload) {
      const cId = mContact.contactId;
      const deletedContact = data.contacts.find(c => c.id === cId);
      setData(p => ({ ...p, contacts: p.contacts.filter(c => c.id !== cId) }));
      logActivity("deleted", "contact", cId, deletedContact?.name || "Contact");
      if (undoAction?.timer) clearTimeout(undoAction.timer);
      supabase.from("contacts").delete().eq("id", cId);
      const timer = setTimeout(() => setUndoAction(null), 30000);
      setUndoAction({
        label: `Contact "${deletedContact?.name || "Contact"}" deleted`,
        timer,
        restore: () => {
          clearTimeout(timer);
          if (deletedContact) {
            supabase.from("contacts").insert(deletedContact);
            setData(p => ({ ...p, contacts: [...p.contacts, deletedContact] }));
          }
          setUndoAction(null);
        },
      });
    } else if (mContact.mode === "add") {
      const id = "c" + uid();
      setData(p => ({ ...p, contacts: [...(p.contacts || []), { id, ...payload }] }));
    } else {
      setData(p => ({ ...p, contacts: p.contacts.map(c => c.id !== mContact.contactId ? c : { ...c, ...payload }) }));
    }
    setMContact(null);
  };

  const addVenueQuick = (name, city = "", addressState = "") => {
    const id = "v" + uid();
    const venue = Object.fromEntries([["id", id], ...VF.map(f => [f.k, ""])]);
    venue.name = name.trim();
    venue.city = city.trim();
    venue.addressState = addressState.trim();
    setVenues(p => ({ ...p, [id]: venue }));
    return venue;
  };

  // ── Activity log ──
  const logActivity = useCallback((action, entityType, entityId, entityLabel, details = {}) => {
    if (!teamId) return;
    const entry = {
      team_id: teamId,
      user_id: user?.id || null,
      actor_name: data.profile?.name || user?.email || "Unknown",
      action,        // 'created' | 'updated' | 'deleted'
      entity_type: entityType,
      entity_id: entityId || null,
      entity_label: entityLabel || null,
      details,
    };
    // Optimistic local prepend
    const localEntry = { ...entry, id: "log" + uid(), created_at: new Date().toISOString() };
    setActivityLog(prev => [localEntry, ...prev]);
    // Persist to DB
    supabase.from("activity_log").insert(entry).then(() => {});
  }, [teamId, user, data.profile]);

  const loadActivityLog = useCallback(async () => {
    if (!teamId) return;
    setActivityLogLoading(true);
    const { data: rows, error } = await supabase
      .from("activity_log")
      .select("*")
      .eq("team_id", teamId)
      .order("created_at", { ascending: false })
      .limit(200);
    if (!error && rows) setActivityLog(rows);
    setActivityLogLoading(false);
  }, [teamId]);

  // ── COMM-1: Broadcasts — state declared before functions to avoid Rolldown TDZ ──
  const [broadcasts,          setBroadcasts]          = useState([]);
  const [broadcastReads,      setBroadcastReads]      = useState({}); // { [broadcastId]: Set<userId> }
  const [broadcastDayTag,     setBroadcastDayTag]     = useState(""); // "" = unset, "general" = no day, or tour_day_id
  const [broadcastDraft,      setBroadcastDraft]      = useState("");
  const [broadcastAudience,   setBroadcastAudience]   = useState("all"); // "all" | "band" | "crew"
  const [broadcastSending,    setBroadcastSending]    = useState(false);
  const [broadcastCollapsed,  setBroadcastCollapsed]  = useState(true); // collapsed by default — tour view noise reduction (2026-07-03)

  const loadBroadcasts = useCallback(async (tourId) => {
    if (!tourId) return;
    const { data: rows } = await supabase.from("tour_broadcasts").select("*")
      .eq("tour_id", tourId).order("created_at", { ascending: false }).limit(50);
    if (!rows) return;
    setBroadcasts(rows);
    if (!rows.length) return;
    const ids = rows.map(b => b.id);
    const { data: reads } = await supabase.from("broadcast_reads")
      .select("broadcast_id, user_id").in("broadcast_id", ids);
    if (reads) {
      const byId = {};
      reads.forEach(r => {
        if (!byId[r.broadcast_id]) byId[r.broadcast_id] = new Set();
        byId[r.broadcast_id].add(r.user_id);
      });
      setBroadcastReads(byId);
    }
  }, []);

  const saveBroadcast = async () => {
    if (!broadcastDraft.trim() || !selT || !teamId) return;
    setBroadcastSending(true);
    const senderProfile = profiles.find(p => p.user_id === user?.id);
    const newB = {
      id: Date.now().toString(36) + Math.random().toString(36).slice(2),
      tour_id: selT,
      team_id: teamId,
      sender_name: senderProfile?.name || "Tour Manager",
      body: broadcastDraft.trim(),
      audience: broadcastAudience,
      tour_day_id: (broadcastDayTag && broadcastDayTag !== "general") ? broadcastDayTag : null,
      created_at: new Date().toISOString(),
    };
    setBroadcasts(prev => [newB, ...prev]);
    setBroadcastDraft("");
    const { error } = await supabase.from("tour_broadcasts").insert(newB);
    if (error) {
      console.error("[ADVANCD] saveBroadcast error:", error);
      setBroadcasts(prev => prev.filter(b => b.id !== newB.id));
    }
    setBroadcastSending(false);
  };

  const markBroadcastRead = useCallback(async (broadcastId) => {
    if (!user?.id) return;
    if ((broadcastReads[broadcastId] || new Set()).has(user.id)) return;
    setBroadcastReads(prev => {
      const s = prev[broadcastId] ? new Set(prev[broadcastId]) : new Set();
      s.add(user.id);
      return { ...prev, [broadcastId]: s };
    });
    const memberProfile = profiles.find(p => p.user_id === user?.id);
    await supabase.from("broadcast_reads").upsert(
      { broadcast_id: broadcastId, user_id: user.id, profile_id: memberProfile?.id || null },
      { onConflict: "broadcast_id,user_id" }
    );
  }, [user]); // profiles + broadcastReads intentionally omitted — declared later in file (Rolldown TDZ)

  // ── Show helpers ──
  const updShow = useCallback((field, val) =>
    setData(p => ({ ...p, artists: p.artists.map(a => a.id !== selA ? a : { ...a, tours: a.tours.map(t => t.id !== selT ? t : { ...t, shows: t.shows.map(s => s.id !== selS ? s : { ...s, [field]: val }) }) }) })),
  [selA, selT, selS]);

  // Inline status cycle: click badge on any show row to advance status
  const STATUS_CYCLE = ["unstarted", "advancing", "confirmed"];
  const cycleShowStatus = useCallback((aId, tId, sId, currentStatus) => {
    const idx = STATUS_CYCLE.indexOf(currentStatus || "unstarted");
    const next = STATUS_CYCLE[(idx + 1) % STATUS_CYCLE.length];
    setData(p => ({ ...p, artists: p.artists.map(a => a.id !== aId ? a : { ...a, tours: a.tours.map(t => t.id !== tId ? t : { ...t, shows: t.shows.map(s => s.id !== sId ? s : { ...s, status: next }) }) }) }));
  }, []);

  const updCl = useCallback((catId, field, val) =>
    setData(p => ({ ...p, artists: p.artists.map(a => a.id !== selA ? a : { ...a, tours: a.tours.map(t => t.id !== selT ? t : { ...t, shows: t.shows.map(s => {
      if (s.id !== selS) return s;
      const uc = { ...s.checklist[catId] };
      if (field === "__complete") uc.complete = val;
      else if (field === "__notes") uc.notes = val;
      else uc.fields = { ...uc.fields, [field]: val };
      return { ...s, checklist: { ...s.checklist, [catId]: uc } };
    }) }) }) })),
  [selA, selT, selS]);

  const updStagePlot = (url) => updShow("stagePlotUrl", url);
  const updProfile   = (k, v) => setData(p => ({ ...p, profile: { ...p.profile, [k]: v } }));

  // ── TM file uploads (to advance-files Supabase Storage bucket) ────────────
  const uploadTmFile = async (file, label, skipLoadingState = false) => {
    if (!show || !file) return;
    if (!skipLoadingState) setTmFileUploading(true);
    try {
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
      const path = `${show.id}/tm/${Date.now()}_${safeName}`;
      const { error } = await supabase.storage.from("advance-files").upload(path, file, { upsert: true });
      if (error) throw error;
      const { data: urlData } = supabase.storage.from("advance-files").getPublicUrl(path);
      if (!urlData?.publicUrl) throw new Error("Could not retrieve public URL for uploaded file");
      const newFile = { id: uid(), name: file.name, label, url: urlData.publicUrl, path };
      const tmD = show.advanceTmData || {};
      updShow("advanceTmData", { ...tmD, _files: [...(tmD._files || []), newFile] });
    } catch (e) { console.error("TM file upload failed:", e); }
    if (!skipLoadingState) setTmFileUploading(false);
  };

  const uploadTmFiles = async (files) => {
    if (!files.length) return;
    setTmFileUploading(true);
    await Promise.all(files.map(f => uploadTmFile(f, detectFileLabel(f.name), true)));
    setTmFileUploading(false);
  };

  const removeTmFile = async (fileId) => {
    const tmD = show?.advanceTmData || {};
    const file = (tmD._files || []).find(f => f.id === fileId);
    if (!file) return;
    try { await supabase.storage.from("advance-files").remove([file.path]); } catch (err) { console.error('[ADVANCD] removeTmFile storage error:', err); }
    updShow("advanceTmData", { ...tmD, _files: (tmD._files || []).filter(f => f.id !== fileId) });
  };

  // ── Tour-level file upload ────────────────────────────────────────────────
  const uploadTourFile = async (file, label, skipLoadingState = false) => {
    if (!tour || !file) return;
    if (!skipLoadingState) setTourFileUploading(true);
    try {
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
      const path = `tour/${selT}/${Date.now()}_${safeName}`;
      const { error } = await supabase.storage.from("advance-files").upload(path, file, { upsert: true });
      if (error) throw error;
      const { data: urlData } = supabase.storage.from("advance-files").getPublicUrl(path);
      if (!urlData?.publicUrl) throw new Error("Could not retrieve public URL for uploaded file");
      const newFile = { id: uid(), name: file.name, label, url: urlData.publicUrl, path };
      updTourAdvCfg(cfg => ({ ...cfg, files: [...(cfg.files || []), newFile] }));
    } catch (e) { console.error("Tour file upload failed:", e); }
    if (!skipLoadingState) setTourFileUploading(false);
  };

  const uploadTourFiles = async (files) => {
    if (!files.length) return;
    setTourFileUploading(true);
    await Promise.all(files.map(f => uploadTourFile(f, detectFileLabel(f.name), true)));
    setTourFileUploading(false);
  };

  const removeTourFile = async (fileId) => {
    const file = (tour?.advanceConfig?.files || []).find(f => f.id === fileId);
    if (!file) return;
    // Don't delete artist-sourced files from storage — they live in the artist profile
    if (!file._artistSource) {
      try { await supabase.storage.from("advance-files").remove([file.path]); } catch (err) { console.error('[ADVANCD] removeTourFile storage error:', err); }
    }
    updTourAdvCfg(cfg => ({ ...cfg, files: (cfg.files || []).filter(f => f.id !== fileId) }));
  };

  const toggleArtistFile = (sourceKey, fileUrl, fileName, fileLabel) => {
    const existing = (tour?.advanceConfig?.files || []).find(f => f._artistSource === sourceKey);
    if (existing) {
      // Remove it
      updTourAdvCfg(cfg => ({ ...cfg, files: (cfg.files || []).filter(f => f._artistSource !== sourceKey) }));
    } else {
      // Add it (no path — it's not a tour-owned upload)
      const newFile = { id: uid(), name: fileName, label: fileLabel, url: fileUrl, _artistSource: sourceKey };
      updTourAdvCfg(cfg => ({ ...cfg, files: [...(cfg.files || []), newFile] }));
    }
  };

  // ── Crew manifest helpers ─────────────────────────────────────────────────
  const addCrewMember = () =>
    updTourAdvCfg(cfg => ({ ...cfg, crewManifest: [...(cfg.crewManifest || []), { id: uid(), name: "", role: "", phone: "", email: "" }] }));

  const removeCrewMember = (id) =>
    updTourAdvCfg(cfg => ({ ...cfg, crewManifest: (cfg.crewManifest || []).filter(p => p.id !== id) }));

  const updCrewMember = (id, field, value) =>
    updTourAdvCfg(cfg => ({ ...cfg, crewManifest: (cfg.crewManifest || []).map(p => p.id !== id ? p : { ...p, [field]: value }) }));

  // ── Tour-level advance config ─────────────────────────────────────────────
  const ALL_SECTION_IDS = ADVANCE_PORTAL_SECTIONS.map(s => s.id);

  // Helper: update advanceConfig on the current tour
  const updTourAdvCfg = (updater) =>
    setData(p => ({ ...p, artists: p.artists.map(a => a.id !== selA ? a : { ...a,
      tours: a.tours.map(t => t.id !== selT ? t : { ...t, advanceConfig: updater(t.advanceConfig || {}) })
    }) }));

  // Advance setup templates (localStorage, scoped to user)
  const persistTemplates = (tpls) => {
    setAdvTemplates(tpls);
    try { localStorage.setItem(`advTpl_${user?.id}`, JSON.stringify(tpls)); } catch {}
  };
  const saveAdvTemplate = (name) => {
    const cfg = tour?.advanceConfig || {};
    const tpl = {
      id: "tpl_" + Date.now(),
      name: name.trim(),
      savedAt: new Date().toISOString().slice(0, 10),
      sections:     cfg.sections     || ALL_SECTION_IDS,
      fields:       cfg.fields       || {},
      dietaryNotes: cfg.dietaryNotes || "",
      vehicleNotes: cfg.vehicleNotes || "",
    };
    persistTemplates([...advTemplates.filter(t => t.name !== tpl.name), tpl]);
  };
  const [tplLoaded,      setTplLoaded]      = useState(false);
  const [settledToast,   setSettledToast]   = useState(false);
  const [remindersOpen,  setRemindersOpen]  = useState(true);
  const [realtimeStatus, setRealtimeStatus] = useState('disconnected'); // 'connecting' | 'connected' | 'disconnected'
  const realtimeStatusRef = useRef('disconnected');
  useEffect(() => { realtimeStatusRef.current = realtimeStatus; }, [realtimeStatus]);
  const selSRef = useRef(null);
  useEffect(() => { selSRef.current = selS; }, [selS]);

  // V-1: load event_links whenever the selected show changes
  useEffect(() => {
    if (!selS || !teamId) return;
    supabase.from("event_links").select("*").eq("show_id", selS)
      .then(({ data: links }) => {
        if (links) setShowEventLinks(prev => ({ ...prev, [selS]: links }));
      });
  }, [selS, teamId]);

  // Advances dashboard: load all confirmed event_links for all shows
  const loadDashEventLinks = async () => {
    if (dashLinksLoaded) return;
    const allShowIds = (data.shows || []).map(s => s.id);
    if (!allShowIds.length) { setDashLinksLoaded(true); return; }
    const { data: links } = await supabase
      .from("event_links")
      .select("id, show_id, org_id, status, metadata, organizations(id, name, type)")
      .in("show_id", allShowIds);
    if (links) {
      const byShow = {};
      links.forEach(l => { if (!byShow[l.show_id]) byShow[l.show_id] = []; byShow[l.show_id].push(l); });
      setDashEventLinks(byShow);
    }
    setDashLinksLoaded(true);
  };
  const [tourDays,          setTourDays]          = useState([]);
  const [tourMemberships,   setTourMemberships]   = useState([]); // tour_memberships for current tour

  // SL-1: Setlists — one row per set per show (UNIQUE on show_id + set_number)
  const [showSetlists,      setShowSetlists]      = useState([]); // flat array for current tour
  // Ref mirror of showSetlists — read inside debounced setlist write timer callbacks
  // (the setTimeout closure can't see the latest state without it). Synced via useEffect below.
  const showSetlistsRef     = useRef([]);
  // Per-(show,set) debounce timers so rapid typing collapses into a single DB write.
  const setlistWriteTimersRef = useRef(new Map());
  // Keep the ref in sync with the latest committed state. Cheap, no consumers re-render.
  useEffect(() => { showSetlistsRef.current = showSetlists; }, [showSetlists]);
  // SL-2: Named setlist templates, scoped per-team and tagged per-artist
  const [setlistTemplates,  setSetlistTemplates]  = useState([]);
  // SR-1: Show reports — one row per person per show
  const [showReports,    setShowReports]    = useState([]); // flat array for current tour
  // Venue history cache: venueId → { loading, items: [{show, reports}] }
  const [venueHistoryCache, setVenueHistoryCache] = useState({});
  // Template menu UX state (lives at App scope because the Setlist panel renders inside an IIFE)
  const [templatesMenuOpen, setTemplatesMenuOpen] = useState(false);
  const [templateSaveName,  setTemplateSaveName]  = useState("");      // "" = save input closed
  const [templateRenameId,  setTemplateRenameId]  = useState(null);    // id of template being renamed
  const [templateRenameName,setTemplateRenameName]= useState("");
  const [templateApplyConfirm, setTemplateApplyConfirm] = useState(null); // null | { template }
  const [tourDaysLoading,   setTourDaysLoading]   = useState(false);
  const [selectedDayId,     setSelectedDayId]     = useState(null);
  const [logisticsSummary,  setLogisticsSummary]  = useState({}); // dayId → { hasLodging, hasFlights, hasTransport }
  // crew_members state retired (PP-1 Phase 2) — touring party now sourced from people_profiles
  const [mTourInvite,     setMTourInvite]     = useState(false);
  const [tourInvites,     setTourInvites]     = useState([]);    // tour_members rows for current tour (TM view)
  const [mModuleConfig,   setMModuleConfig]   = useState(null);  // null | { member: tourInvite row }
  const [dayFlights,      setDayFlights]      = useState({});   // { [dayId]: [flight,...] }
  const [mFlight,         setMFlight]         = useState(null); // null | {mode:'add'|'edit', dayId, flight?}
  // SC-1: Daily calendar
  const [dayScheduleItems, setDayScheduleItems] = useState([]);  // flat array for current tour
  const [mSchedItem,       setMSchedItem]       = useState(null); // null | {mode:'add'|'edit', dayId, item?}
  const [importingAdvSched, setImportingAdvSched] = useState(null); // dayId being imported, null otherwise
  const [vehicles,        setVehicles]        = useState([]);
  const [mVehicle,        setMVehicle]        = useState(null); // null | {mode:'add'|'edit', vehicle?}
  const [dayTransport,    setDayTransport]    = useState({});   // { [dayId]: [transport,...] }
  const [mTransport,      setMTransport]      = useState(null); // null | {mode:'add'|'edit', dayId, transport?}
  const [daySections,     setDaySections]     = useState({});   // { [dayId]: { flights: bool, transport: bool, lodging: bool } }
  const [dayLodging,          setDayLodging]          = useState({});   // { [dayId]: [lodging,...] }
  const [mLodging,            setMLodging]            = useState(null); // null | {mode, dayId, lodging?}
  const [dayRouteWaypoints,   setDayRouteWaypoints]   = useState({});   // { [dayId]: [{label, address, location:{lat,lng}}] }
  const [dayLocal,     setDayLocal]     = useState({});   // { [dayId]: { [cat]: { status, results, cachedAt } } }
  const [localFilter,  setLocalFilter]  = useState({});   // { [dayId]: category string }
  const [localSort,    setLocalSort]    = useState({});   // { [dayId]: "distance" | "rating" }
  const [localOpenFilter, setLocalOpenFilter] = useState({});   // { [dayId]: ""|"now"|"late" }
  const [localRec,     setLocalRec]     = useState(null); // { dayId, venueId } — inline add-rec form open for this day
  const [cityRecs,     setCityRecs]     = useState({});   // { [city|state]: { status, data: [] } }
  const [myRecs,       setMyRecs]       = useState({ status: "idle", data: [] }); // logged-in user's own recs
  const [myRecsCat,    setMyRecsCat]    = useState("all");   // category filter for My Recs view
  const [myRecsSearch, setMyRecsSearch] = useState("");       // city search filter
  const [myRecsModal,  setMyRecsModal]  = useState(null);    // null | "open"
  const [myRecsTab,    setMyRecsTab]    = useState("multiple"); // "multiple" | "single"
  const [recSaveCounts, setRecSaveCounts] = useState({});    // { [recId]: number }
  const [mySavedRecs,   setMySavedRecs]   = useState(new Set()); // Set of rec IDs saved by me
  const [mySavedLoaded, setMySavedLoaded] = useState(false);
  // Single-add form state
  const [singleRecForm, setSingleRecForm] = useState({ name: "", city: "", state: "", cat: "food", note: "", url: "", placeId: null, confirmed: false });
  const [singleRecSaving, setSingleRecSaving] = useState(false);
  // Bulk import state
  const [bulkText,    setBulkText]    = useState("");
  const [bulkCity,    setBulkCity]    = useState("");
  const [bulkState,   setBulkState]   = useState("");
  const [bulkParsing, setBulkParsing] = useState(false);
  const [bulkParsed,  setBulkParsed]  = useState(null);  // null | parsed rec array (review step)
  const [bulkSaving,  setBulkSaving]  = useState(false);
  const [expandedRec,  setExpandedRec]  = useState(null);    // rec.id currently expanded in rolodex
  const [placeInfoModal,   setPlaceInfoModal]   = useState(null); // { rec } | null — in-app place details overlay
  const [placeInfoDetails, setPlaceInfoDetails] = useState({});  // { [recId]: { status, data } }
  const [localShowMore, setLocalShowMore] = useState({});    // { [dayId]: boolean } — show all Google results
  const [dayWeather,          setDayWeather]          = useState({});   // { [dayId]: { condition, high, low, wind, precip, status } }
  const [profiles,         setProfiles]         = useState([]);
  const [profilePrivate,   setProfilePrivate]   = useState({});   // { [profileId]: privateData }

  // Auto-load private data when the current user has a claimed profile and opens My Profile
  // (must be placed after profiles/profilePrivate/user/view are all declared)
  useEffect(() => {
    if (view !== "profile") return;
    const myProfile = profiles.find(p => p.user_id === user?.id);
    if (myProfile) loadProfilePrivate(myProfile.id);
  }, [view, profiles, user]);

  // ── Realtime: helper to convert a raw DB show row to app shape ────────────
  const dbShowToApp = (s) => ({
    id: s.id, venueId: s.venue_id, date: s.date,
    status: s.status || "unstarted", notes: s.notes || "",
    flagged: s.flagged || false,
    checklist: s.checklist || {}, settlement: s.settlement || {},
    advanceToken:          s.advance_token             || null,
    linkCode:              s.link_code                 || null,
    advanceData:           s.advance_data              || {},
    advanceTmData:         s.advance_tm_data           || {},
    advanceSectionStatus:  s.advance_section_status    || {},
    advanceLastViewedAt:   s.advance_last_viewed_at    || null,
    advanceFinalized:      s.advance_finalized         || false,
    advanceFinalizedAt:    s.advance_finalized_at      || null,
    lodging:               s.lodging                   || {},
    linkedContacts:        s.linked_contacts           || [],
    setlists:              s.setlists                  || [],   // SL-1: hydrated via separate query; default empty
  });

  // ── Realtime: per-table state-patch handlers ───────────────────────────────
  const handleTourDayChange = useCallback((payload) => {
    const { eventType, new: n, old: o } = payload;
    if (eventType === 'INSERT') {
      setTourDays(prev => [...prev, n].sort((a, b) => a.date.localeCompare(b.date)));
    } else if (eventType === 'UPDATE') {
      setTourDays(prev => prev.map(d => d.id === n.id ? n : d));
    } else if (eventType === 'DELETE') {
      setTourDays(prev => prev.filter(d => d.id !== o.id));
    }
  }, []);

  const handleShowChange = useCallback((payload) => {
    const { eventType, new: n, old: o } = payload;

    // Promoter-activity toast: detect promoter-side advance changes on UPDATE.
    // Compares the incoming payload against current state BEFORE it's adopted.
    // Suppressed for ~4s after the TM's own advance_data write (ROS dismiss).
    if (eventType === 'UPDATE') {
      try {
        const advPresent = n.advance_data != null || n.advance_section_status != null;
        const selfRecent = Date.now() - (selfAdvDataWriteRef.current || 0) < 4000;
        if (advPresent && !selfRecent) {
          const prevShow = (advAllDataRef.current?.artists || [])
            .flatMap(a => a.tours).flatMap(t => t.shows).find(s => s.id === n.id);
          if (prevShow) {
            const prevD = prevShow.advanceData || {};
            const newD = n.advance_data != null ? n.advance_data : prevD;
            const submittedNow = newD._submitted === true && prevD._submitted !== true;
            const prevActs = Array.isArray(prevD._activity) ? prevD._activity.length : 0;
            const newActs  = Array.isArray(newD._activity) ? newD._activity.length : 0;
            const countSub = (st) => Object.values(st || {}).filter(x => x?.status === "submitted").length;
            const newSecSub = n.advance_section_status != null &&
              countSub(n.advance_section_status) > countSub(prevShow.advanceSectionStatus);
            if (submittedNow || newActs > prevActs || newSecSub) {
              setPromoterNotice({
                showId: n.id, date: n.date,
                kind: submittedNow ? "submitted the advance" : "updated the advance",
                ts: Date.now(),
              });
            }
          }
        }
      } catch { /* notification is non-critical */ }
    }

    setData(prev => ({
      ...prev,
      artists: prev.artists.map(a => ({
        ...a,
        tours: a.tours.map(t => ({
          ...t,
          shows: eventType === 'INSERT'
            // Only insert into the correct tour, and skip if already present
            // (the TM's own saveShow already added it optimistically — without
            // this guard the show gets added to every tour and then saveNormalized
            // overwrites the DB row with the wrong tour_id on flush)
            ? (t.id === n.tour_id && !t.shows.some(s => s.id === n.id))
              ? [...t.shows, dbShowToApp(n)].sort((a, b) => a.date.localeCompare(b.date))
              : t.shows
            : eventType === 'UPDATE'
            ? t.shows.map(s => {
                if (s.id !== n.id) return s;
                const fromDb = dbShowToApp(n);
                // ⚠ TOAST guard: Realtime UPDATE payloads OMIT large (>~2KB) JSONB
                // columns when they didn't change (Postgres TOAST + replica
                // identity default). dbShowToApp maps a missing column to {} /
                // null — so spreading fromDb blindly wipes promoter advance
                // data from state whenever ANY small update touches the row
                // (e.g. the portal's advance_last_viewed_at ping). For every
                // TOAST-able column: only adopt when actually present in the
                // payload, otherwise keep what state already has.
                const keep = (col, mapped, existing) =>
                  (n[col] === undefined || n[col] === null) ? existing : mapped;
                return {
                  ...s,
                  ...fromDb,
                  advanceData:          keep("advance_data",           fromDb.advanceData,          s.advanceData),
                  advanceSectionStatus: keep("advance_section_status", fromDb.advanceSectionStatus, s.advanceSectionStatus),
                  checklist:            keep("checklist",              fromDb.checklist,            s.checklist),
                  settlement:           keep("settlement",             fromDb.settlement,           s.settlement),
                  lodging:              keep("lodging",                fromDb.lodging,              s.lodging),
                  linkedContacts:       keep("linked_contacts",        fromDb.linkedContacts,       s.linkedContacts),
                  // advanceTmData: keep the local copy ONLY while there are unsaved
                  // local edits (Realtime echoes from our own saves lag local state
                  // and would clobber text the TM typed after saving). When clean,
                  // adopt the DB copy (if present in the payload — TOAST guard).
                  // resetAdvance uses advResetLock to prevent stale writes from re-appearing.
                  advanceTmData: advTmDirtyRef.current.has(s.id)
                    ? s.advanceTmData
                    : keep("advance_tm_data", fromDb.advanceTmData, s.advanceTmData),
                };
              })
            : t.shows.filter(s => s.id !== o.id),
        })),
      })),
    }));
  }, []);

  const handleLodgingChange = useCallback((payload) => {
    const { eventType, new: n, old: o } = payload;
    if (eventType === 'INSERT') {
      setDayLodging(prev => {
        const existing = prev[n.tour_day_id] || [];
        if (existing.some(l => l.id === n.id)) return prev; // already added optimistically — skip
        return { ...prev, [n.tour_day_id]: [...existing, n] };
      });
      setLogisticsSummary(prev => ({ ...prev, [n.tour_day_id]: { ...(prev[n.tour_day_id] || {}), hasLodging: true } }));
    } else if (eventType === 'UPDATE') {
      setDayLodging(prev => ({ ...prev, [n.tour_day_id]: (prev[n.tour_day_id] || []).map(l => l.id === n.id ? n : l) }));
    } else if (eventType === 'DELETE') {
      setDayLodging(prev => {
        const next = { ...prev };
        Object.keys(next).forEach(dayId => { next[dayId] = next[dayId].filter(l => l.id !== o.id); });
        // Update hasLodging based on remaining items for this day
        if (o.tour_day_id) {
          const remaining = next[o.tour_day_id] || [];
          setLogisticsSummary(s => ({ ...s, [o.tour_day_id]: { ...(s[o.tour_day_id] || {}), hasLodging: remaining.length > 0 } }));
        }
        return next;
      });
    }
  }, []);

  const handleFlightChange = useCallback((payload) => {
    const { eventType, new: n, old: o } = payload;
    if (eventType === 'INSERT') {
      setDayFlights(prev => ({ ...prev, [n.tour_day_id]: [...(prev[n.tour_day_id] || []), n] }));
      setLogisticsSummary(prev => ({ ...prev, [n.tour_day_id]: { ...(prev[n.tour_day_id] || {}), hasFlights: true } }));
    } else if (eventType === 'UPDATE') {
      setDayFlights(prev => ({ ...prev, [n.tour_day_id]: (prev[n.tour_day_id] || []).map(f => f.id === n.id ? n : f) }));
    } else if (eventType === 'DELETE') {
      setDayFlights(prev => {
        const next = { ...prev };
        Object.keys(next).forEach(dayId => { next[dayId] = next[dayId].filter(f => f.id !== o.id); });
        return next;
      });
    }
  }, []);

  const handleTransportChange = useCallback((payload) => {
    const { eventType, new: n, old: o } = payload;
    if (eventType === 'INSERT') {
      setDayTransport(prev => ({ ...prev, [n.tour_day_id]: [...(prev[n.tour_day_id] || []), n] }));
      setLogisticsSummary(prev => ({ ...prev, [n.tour_day_id]: { ...(prev[n.tour_day_id] || {}), hasTransport: true } }));
    } else if (eventType === 'UPDATE') {
      setDayTransport(prev => ({ ...prev, [n.tour_day_id]: (prev[n.tour_day_id] || []).map(t => t.id === n.id ? n : t) }));
    } else if (eventType === 'DELETE') {
      setDayTransport(prev => {
        const next = { ...prev };
        Object.keys(next).forEach(dayId => { next[dayId] = next[dayId].filter(t => t.id !== o.id); });
        return next;
      });
    }
  }, []);

  // SL-1: Realtime handler for show_setlists (declared here for lexical-order
  // safety — Rolldown gets strict about forward references inside the realtime
  // useEffect body, so we keep this alongside the other handlers).
  //
  // We compare incoming.updated_at against our local row's updated_at and skip
  // when the incoming event is just an echo of our own write — otherwise the
  // realtime UPDATE would clobber any characters the user has typed locally
  // between the debounced write and its echo arriving back.
  const handleSetlistChange = useCallback((payload) => {
    const { eventType, new: n, old: o } = payload;
    setShowSetlists(prev => {
      if (eventType === 'INSERT') {
        if (prev.some(s => s.id === n.id)) return prev;
        // Replace any matching tmp_ optimistic row first
        const tmpIdx = prev.findIndex(s =>
          s.show_id === n.show_id && s.set_number === n.set_number && String(s.id).startsWith('tmp_'));
        if (tmpIdx >= 0) {
          return prev.map((s, i) => i === tmpIdx ? { ...s, id: n.id, updated_at: n.updated_at } : s);
        }
        return [...prev, n];
      }
      if (eventType === 'UPDATE') {
        const local = prev.find(s => s.id === n.id);
        // Self-echo guard: skip if our local row is at the same or newer updated_at
        if (local?.updated_at && n.updated_at && local.updated_at >= n.updated_at) return prev;
        return prev.map(s => s.id === n.id ? n : s);
      }
      if (eventType === 'DELETE') return prev.filter(s => s.id !== o.id);
      return prev;
    });
  }, []);

  // SL-2: Realtime handler for setlist_templates (declared alongside the others
  // for TDZ safety — the realtime useEffect below references it by name).
  const handleSetlistTemplateChange = useCallback((payload) => {
    const { eventType, new: n, old: o } = payload;
    setSetlistTemplates(prev => {
      if (eventType === 'INSERT') {
        if (prev.some(t => t.id === n.id)) return prev;
        return [n, ...prev];
      }
      if (eventType === 'UPDATE') return prev.map(t => t.id === n.id ? n : t);
      if (eventType === 'DELETE') return prev.filter(t => t.id !== o.id);
      return prev;
    });
  }, []);

  // SR-1: Realtime handler for show_reports
  const handleReportChange = useCallback((payload) => {
    const { eventType, new: n, old: o } = payload;
    setShowReports(prev => {
      if (eventType === 'INSERT') {
        if (prev.some(r => r.id === n.id)) return prev;
        return [...prev, n];
      }
      if (eventType === 'UPDATE') return prev.map(r => r.id === n.id ? n : r);
      if (eventType === 'DELETE') return prev.filter(r => r.id !== o.id);
      return prev;
    });
  }, []);

  // handleCrewMemberChange retired — PP-1 Phase 2

  // TM profiles (people_profiles scoped to team)
  const handleProfileChange = useCallback((payload) => {
    const { eventType, new: n, old: o } = payload;
    if (eventType === 'INSERT') {
      // Guard against duplicating a profile that was already added optimistically by saveProfile
      setProfiles(prev => prev.some(p => p.id === n.id) ? prev : [...prev, n].sort((a, b) => (a.name || '').localeCompare(b.name || '')));
    } else if (eventType === 'UPDATE') {
      setProfiles(prev => prev.map(p => p.id === n.id ? n : p));
    } else if (eventType === 'DELETE') {
      setProfiles(prev => prev.filter(p => p.id !== o.id));
    }
  }, []);

  // Member network profiles (visible_to_network, unscoped — RLS handles auth)
  const handleNetworkProfileChange = useCallback((payload) => {
    const { eventType, new: n, old: o } = payload;
    if (eventType === 'INSERT' && n.visible_to_network) {
      setMemberNetworkProfiles(prev => [...prev, n].sort((a, b) => (a.name || '').localeCompare(b.name || '')));
    } else if (eventType === 'UPDATE') {
      if (n.visible_to_network) {
        setMemberNetworkProfiles(prev => {
          const exists = prev.some(p => p.id === n.id);
          return exists
            ? prev.map(p => p.id === n.id ? n : p)
            : [...prev, n].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        });
      } else {
        setMemberNetworkProfiles(prev => prev.filter(p => p.id !== n.id));
      }
    } else if (eventType === 'DELETE') {
      setMemberNetworkProfiles(prev => prev.filter(p => p.id !== o.id));
    }
  }, []);
  // ── Pull-to-refresh: stable callback passed to MobileApp ─────────────────
  // handleMobileRefreshRef is updated after member state is declared (below),
  // keeping the callback stable (empty deps) while always having fresh values.
  const handleMobileRefreshRef = useRef(null);
  const handleMobileRefresh = useCallback(() => handleMobileRefreshRef.current?.(), []);

  // ── Realtime: TM-side channel (subscribed once teamId resolves) ───────────
  useEffect(() => {
    if (!teamId || memberRole) return; // TMs only — members use their own channel below
    setRealtimeStatus('connecting');
    const channel = supabase
      .channel(`team-${teamId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'tour_days',        filter: `team_id=eq.${teamId}` }, handleTourDayChange)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'shows',            filter: `team_id=eq.${teamId}` }, handleShowChange)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'lodging'                                          }, handleLodgingChange)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'flights'                                         }, handleFlightChange)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'ground_transport'                                }, handleTransportChange)

      .on('postgres_changes', { event: '*', schema: 'public', table: 'people_profiles',  filter: `team_id=eq.${teamId}` }, handleProfileChange)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'show_setlists',    filter: `team_id=eq.${teamId}` }, handleSetlistChange)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'setlist_templates',filter: `team_id=eq.${teamId}` }, handleSetlistTemplateChange)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'show_reports',    filter: `team_id=eq.${teamId}` }, handleReportChange)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'tour_broadcasts', filter: `team_id=eq.${teamId}` }, (payload) => {
        const n = payload.new;
        setBroadcasts(prev => prev.some(b => b.id === n.id) ? prev : [n, ...prev]);
      })
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'broadcast_reads' }, (payload) => {
        const n = payload.new;
        setBroadcastReads(prev => {
          const s = prev[n.broadcast_id] ? new Set(prev[n.broadcast_id]) : new Set();
          s.add(n.user_id);
          return { ...prev, [n.broadcast_id]: s };
        });
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'guest_list_requests', filter: `team_id=eq.${teamId}` }, (payload) => {
        const { eventType, new: n, old: o } = payload;
        // Only update if the event is for the currently open show
        const rowShowId = n?.show_id || o?.show_id;
        if (rowShowId && rowShowId !== selSRef.current) return;
        setGlRequests(prev => {
          if (eventType === 'INSERT') return [...prev, n];
          if (eventType === 'UPDATE') return prev.map(r => r.id === n.id ? n : r);
          if (eventType === 'DELETE') return prev.filter(r => r.id !== o.id);
          return prev;
        });
      })
      .subscribe((status) => {
        if (status === 'SUBSCRIBED')    setRealtimeStatus('connected');
        else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED')
          setRealtimeStatus('disconnected');
      });
    const handleVisibilityChangeTM = () => {
      if (document.visibilityState === 'visible' && realtimeStatusRef.current === 'disconnected') {
        setRealtimeStatus('connecting');
        channel.subscribe();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChangeTM);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChangeTM);
      supabase.removeChannel(channel);
      setRealtimeStatus('disconnected');
    };
  }, [teamId, handleTourDayChange, handleShowChange, handleLodgingChange, handleFlightChange, handleTransportChange, handleProfileChange]);

  const [mProfile,         setMProfile]         = useState(null); // null | { mode:'add'|'edit', profile?, privateData? }
  const [profileSearch,    setProfileSearch]    = useState("");
  const [profCatFilter,    setProfCatFilter]    = useState("all");
  const [peopleTab,        setPeopleTab]        = useState("team"); // "team" | "network"
  const [artistTab,        setArtistTab]        = useState("tours"); // "tours" | "contacts" | "rider"
  const [networkProfiles,  setNetworkProfiles]  = useState([]);
  const [networkLoading,   setNetworkLoading]   = useState(false);
  const [networkLoaded,    setNetworkLoaded]    = useState(false);
  const [networkSearch,    setNetworkSearch]    = useState("");
  const [networkCatFilter, setNetworkCatFilter] = useState("all");
  const [mNetworkProfile,  setMNetworkProfile]  = useState(null); // null | profile object (read-only view)
  const [previewMember,    setPreviewMember]    = useState(null); // null | profile object to preview as
  const [connections,      setConnections]      = useState(new Set()); // Set of profile_ids your team is connected to
  const [networkFilter,    setNetworkFilter]    = useState("all"); // "all" | "connected"
  // Account password change (My Profile → Account Info)
  const [pwOpen,   setPwOpen]   = useState(false);
  const [pwValue,  setPwValue]  = useState("");
  const [pwSaving, setPwSaving] = useState(false);
  const [pwMsg,    setPwMsg]    = useState(null); // null | "saved" | error string
  // My Profile → Share Routing tour selector (was an uncontrolled getElementById select)
  const [myRoutingTourSel, setMyRoutingTourSel] = useState("");
  const [claimToken,       setClaimToken]       = useState(() => {
    const m = window.location.pathname.match(/^\/claim\/([^/]+)/);
    return m ? m[1] : null;
  });
  const [claimStatus,      setClaimStatus]      = useState(null); // null|'claiming'|'success'|'already_claimed'|'error'

  // ── Invite system state ────────────────────────────────────────────────────
  const [inviteToken,      setInviteToken]      = useState(() => {
    const m = window.location.pathname.match(/^\/invite\/([^/]+)/);
    return m ? m[1] : null;
  });
  const [inviteInfo,       setInviteInfo]       = useState(null);   // { tour_name, artist_name, role, email, name }
  const [inviteAccepted,   setInviteAccepted]   = useState(false);
  const [memberRole,           setMemberRole]           = useState(null);   // null (TM) | 'band' | 'crew'
  const [memberTourId,         setMemberTourId]         = useState(null);   // for invited members
  const [memberId,             setMemberId]             = useState(null);   // tour_members.id
  const [memberModules,        setMemberModules]        = useState(DEFAULT_MEMBER_MODULES);
  const [memberPermissions,    setMemberPermissions]    = useState(null);   // tour_memberships row for this member
  const [memberOwnProfile,     setMemberOwnProfile]     = useState(null);   // people_profiles row for this member
  const [memberNetworkProfiles,setMemberNetworkProfiles]= useState([]);     // all visible_to_network profiles
  const [memberConnections,    setMemberConnections]    = useState(new Set()); // Set<profile_id>
  const [memberDataStale,      setMemberDataStale]      = useState(false);   // true while showing cached data

  // ── Auto-sync artist.members → people_profiles when Roster tab is opened ──
  useEffect(() => {
    if (artistTab !== "roster" || !artist || !teamId || !user) return;
    syncMembersToRoster(artist.id, artist.members || []);
  }, [artistTab, artist?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Load stage plots when Documents tab is opened ──
  useEffect(() => {
    if (artistTab !== "rider" || !artist) return;
    loadArtistStagePlots(artist.id, true);
  }, [artistTab, artist?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Keep handleMobileRefreshRef up-to-date with latest member state ────────
  // memberTourId (line ~8779), selA (line ~7639), user (line ~7532) all declared
  // before this point — no Rolldown TDZ risk.
  useEffect(() => {
    handleMobileRefreshRef.current = async () => {
      if (!memberTourId || !user) return;
      try {
        const result = await loadMemberData(memberTourId, selA, user.id);
        if (result?.data)              setData(result.data);
        if (result?.venues)            setVenues(result.venues);
        if (result?.tourDays?.length)  setTourDays(result.tourDays);
        if (result?.ownProfile)        setMemberOwnProfile(result.ownProfile);
        if (result?.networkProfiles?.length) setMemberNetworkProfiles(result.networkProfiles);
        if (result?.connections?.size) setMemberConnections(result.connections);
        if (result?.membership)        setMemberPermissions(result.membership); // PP-1 Phase 3
      } catch (err) { console.error('[ADVANCD] pull-to-refresh failed:', err); }
    };
  }, [memberTourId, selA, user]);

  // Load broadcasts for members when their tour resolves
  useEffect(() => {
    if (!memberTourId || !memberRole) return;
    loadBroadcasts(memberTourId);
  }, [memberTourId, memberRole]);

  // ── Realtime: member-side channel (subscribed once memberTourId resolves) ─
  useEffect(() => {
    if (!memberTourId || !memberRole) return; // band/crew only
    setRealtimeStatus('connecting');
    const channel = supabase
      .channel(`tour-member-${memberTourId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'tour_days',       filter: `tour_id=eq.${memberTourId}` }, handleTourDayChange)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'shows',           filter: `tour_id=eq.${memberTourId}` }, handleShowChange)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'lodging'                                               }, handleLodgingChange)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'flights'                                              }, handleFlightChange)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'ground_transport'                                     }, handleTransportChange)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'people_profiles'                                      }, handleNetworkProfileChange)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'show_setlists',    filter: `tour_id=eq.${memberTourId}` }, handleSetlistChange)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'show_reports',    filter: `tour_id=eq.${memberTourId}` }, handleReportChange)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'tour_broadcasts', filter: `tour_id=eq.${memberTourId}` }, (payload) => {
        const n = payload.new;
        setBroadcasts(prev => prev.some(b => b.id === n.id) ? prev : [n, ...prev]);
      })
      .subscribe((status) => {
        if (status === 'SUBSCRIBED')    setRealtimeStatus('connected');
        else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED')
          setRealtimeStatus('disconnected');
      });
    const handleVisibilityChangeMember = () => {
      if (document.visibilityState === 'visible' && realtimeStatusRef.current === 'disconnected') {
        setRealtimeStatus('connecting');
        channel.subscribe();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChangeMember);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChangeMember);
      supabase.removeChannel(channel);
      setRealtimeStatus('disconnected');
    };
  }, [memberTourId, memberRole, handleTourDayChange, handleShowChange, handleLodgingChange, handleFlightChange, handleTransportChange, handleNetworkProfileChange]);

  // ── Load invite info (pre-auth) ───────────────────────────────────────────
  useEffect(() => {
    if (!inviteToken || inviteInfo) return;
    (async () => {
      const { data, error } = await supabase.rpc('get_invite_info', { p_token: inviteToken });
      if (error) { console.error('[ADVANCD] get_invite_info error:', error); return; }
      if (data?.[0]) setInviteInfo(data[0]);
    })();
  }, [inviteToken]);

  // ── Accept invite once user is authed ─────────────────────────────────────
  useEffect(() => {
    if (!user || !inviteToken || inviteAccepted) return;
    (async () => {
      const { error } = await supabase.rpc('accept_tour_invite', { p_token: inviteToken, p_user_id: user.id });
      if (error) { console.error('[ADVANCD] accept_tour_invite error:', error); return; }
      setInviteAccepted(true);
      // Clean URL
      window.history.replaceState({}, '', '/');
      // Set role from inviteInfo so routing works immediately
      if (inviteInfo?.role) {
        setMemberRole(inviteInfo.role);
        if (inviteInfo.tour_id) setMemberTourId(inviteInfo.tour_id);
      }
    })();
  }, [user, inviteToken, inviteAccepted]);

  // Member role is now detected inside the main data-load useEffect above.
  // No separate effect needed — avoids double-query on login.

  // ── Process profile claim once user is authed ──────────────────────────
  useEffect(() => {
    if (!user || !claimToken || claimStatus) return;
    setClaimStatus('claiming');
    (async () => {
      const { data: result, error } = await supabase.rpc('claim_profile', { p_token: claimToken });
      if (error) {
        console.error('[ADVANCD] claim_profile rpc error:', error);
        setClaimStatus('error');
        return;
      }
      if (result?.error === 'already_claimed') {
        setClaimStatus('already_claimed');
      } else if (result?.error) {
        setClaimStatus('error');
      } else {
        setClaimStatus('success');
        // Clear the /claim/... path from the browser bar
        window.history.replaceState({}, '', '/');
        // Reload profiles so the newly-claimed profile reflects user_id + claimed_at
        if (teamId) {
          const { data: profs } = await supabase.from('people_profiles').select('*').eq('team_id', teamId).order('name');
          if (profs) setProfiles(profs);
        }
      }
    })();
  }, [user, claimToken, claimStatus]);

  const [tourDayFilter,   setTourDayFilter]   = useState('all'); // 'all'|'show'|'travel'|'drive'|'off'
  const [pastDaysOpen,    setPastDaysOpen]    = useState(false); // past tour days collapsed by default (2026-07-03)
  const [mItinerary,       setMItinerary]       = useState(false);
  const [mItineraryExport, setMItineraryExport] = useState(false); // pre-export section picker
  const [itineraryShareUrl, setItineraryShareUrl] = useState(null); // last copied share URL
  const loadAdvTemplate = (tplId) => {
    const tpl = advTemplates.find(t => t.id === tplId);
    if (!tpl) return;
    updTourAdvCfg(cfg => ({
      ...cfg,
      sections:     tpl.sections,
      fields:       tpl.fields,
      dietaryNotes: tpl.dietaryNotes,
      vehicleNotes: tpl.vehicleNotes,
    }));
    setTplLoaded(true);
    setTimeout(() => setTplLoaded(false), 2000);
  };
  const deleteAdvTemplate = (tplId) => persistTemplates(advTemplates.filter(t => t.id !== tplId));

  // TT-1: Tour Setup Templates — Supabase-backed, per-team
  const saveTourTemplate = async (name) => {
    if (!teamId || !selT) return;
    const cfg = tour?.advanceConfig || {};
    const onTourIds = profiles.filter(p => p.current_tour_id === selT).map(p => p.id);
    const row = {
      team_id: teamId,
      name: name.trim(),
      advance_config: {
        sections:     cfg.sections     || ALL_SECTION_IDS,
        fields:       cfg.fields       || {},
        dietaryNotes: cfg.dietaryNotes || "",
        vehicleNotes: cfg.vehicleNotes || "",
        sectionNotes: cfg.sectionNotes || {},
      },
      roster_profile_ids: onTourIds,
    };
    const { data, error } = await supabase.from('tour_templates').insert(row).select().single();
    if (!error && data) setTourTemplates(prev => [...prev, data]);
  };
  const deleteTourTemplate = async (id) => {
    await supabase.from('tour_templates').delete().eq('id', id);
    setTourTemplates(prev => prev.filter(t => t.id !== id));
  };

  const updTourAdvanceSections = (sectionId, enabled) =>
    updTourAdvCfg(cfg => {
      const current = cfg.sections || ALL_SECTION_IDS;
      return { ...cfg, sections: enabled ? [...new Set([...current, sectionId])] : current.filter(id => id !== sectionId) };
    });

  const updTourAdvanceField = (sectionId, fieldId, enabled) =>
    updTourAdvCfg(cfg => {
      const sec = ADVANCE_PORTAL_SECTIONS.find(s => s.id === sectionId);
      const allIds = sec?.fields.map(f => f.id) || [];
      const current = cfg.fields?.[sectionId] ?? allIds; // undefined = all enabled
      const next = enabled ? [...new Set([...current, fieldId])] : current.filter(id => id !== fieldId);
      // If all fields enabled again, remove the key to keep config clean
      const isDefault = next.length === allIds.length && allIds.every(id => next.includes(id));
      const fields = { ...(cfg.fields || {}), [sectionId]: isDefault ? undefined : next };
      if (isDefault) delete fields[sectionId];
      return { ...cfg, fields };
    });

  // Enabled section IDs for the current tour (all if not configured)
  const tourSectionIds = tour?.advanceConfig?.sections || ALL_SECTION_IDS;

  // Returns enabled field IDs for a given section (all fields if not customized)
  const tourEnabledFields = (sectionId) => {
    const sec = ADVANCE_PORTAL_SECTIONS.find(s => s.id === sectionId);
    const allIds = sec?.fields.map(f => f.id) || [];
    const custom = tour?.advanceConfig?.fields?.[sectionId];
    if (!custom) return allIds;
    // Only return fields that are explicitly in the enabled list (and still exist in schema)
    // Note: do NOT auto-include non-custom fields — that would negate any field the TM disabled
    return custom.filter(id => allIds.includes(id));
  };

  // True if any section has field-level customisation
  const hasFieldCustom = !!(tour?.advanceConfig?.fields && Object.keys(tour.advanceConfig.fields).length > 0);

  // ── Advance TM pre-fill helpers (writes to advanceTmData — TM layer only) ──
  const updAdvanceTmField = useCallback((secId, fieldId, value) => {
    advTmDirtyRef.current.add(selS); // mark unsaved local edit → enables the debounced save
    setData(p => ({ ...p, artists: p.artists.map(a => a.id !== selA ? a : { ...a,
      tours: a.tours.map(t => t.id !== selT ? t : { ...t,
        shows: t.shows.map(s => {
          if (s.id !== selS) return s;
          const next = { ...(s.advanceTmData || {}), [secId]: { ...((s.advanceTmData || {})[secId] || {}), [fieldId]: value } };
          return { ...s, advanceTmData: next };
        })
      })
    }) }));
  }, [selA, selT, selS]);

  // Batch-update multiple fields in one section atomically (used by BillEditorInline)
  const updAdvanceTmBatch = useCallback((secId, fields) => {
    advTmDirtyRef.current.add(selS); // mark unsaved local edit → enables the debounced save
    setData(p => ({ ...p, artists: p.artists.map(a => a.id !== selA ? a : { ...a,
      tours: a.tours.map(t => t.id !== selT ? t : { ...t,
        shows: t.shows.map(s => {
          if (s.id !== selS) return s;
          const cur  = (s.advanceTmData || {})[secId] || {};
          const next = { ...(s.advanceTmData || {}), [secId]: { ...cur, ...fields } };
          return { ...s, advanceTmData: next };
        })
      })
    }) }));
  }, [selA, selT, selS]);

  const applyAdvanceToTour = () => {
    if (!show || !tour) return;
    const tmpl = show.advanceTmData || {};
    if (!Object.keys(tmpl).length) return;
    const confirmed = window.confirm(
      `Apply the TM advance template from this show to all ${tour.shows.length - 1} other show(s) on "${tour.name}"?\n\nExisting TM data on other shows will be merged — this show's values take precedence.`
    );
    if (!confirmed) return;
    setData(p => ({ ...p, artists: p.artists.map(a => a.id !== selA ? a : { ...a,
      tours: a.tours.map(t => t.id !== selT ? t : { ...t,
        shows: t.shows.map(s => {
          if (s.id === selS) return s;
          const merged = { ...(s.advanceTmData || {}) };
          Object.keys(tmpl).forEach(secId => {
            merged[secId] = { ...(merged[secId] || {}), ...(tmpl[secId] || {}) };
          });
          return { ...s, advanceTmData: merged };
        })
      })
    }) }));
  };

  // ── Advance Portal: generate/copy link ────────────────────────────────────
  const shareAdvance = async () => {
    if (!show || !selA || !selT || !selS) return;
    setAdvGenLoading(true);
    try {
      let token = show.advanceToken;
      if (!token) {
        // Generate a new unique token and save to DB immediately
        token = crypto.randomUUID();
        const { error } = await supabase
          .from("shows")
          .update({ advance_token: token })
          .eq("id", show.id);
        if (error) { console.error("[ADVANCD] advance token save failed:", error); setAdvGenLoading(false); return; }
        // Update local state
        updShow("advanceToken", token);
        bumpToAdvancing(show.id);
        logActivity("Advance portal link generated", "system");
      }
      const url = `${window.location.origin}/advance/${token}`;
      await navigator.clipboard.writeText(url);
      setAdvLinkCopied(true);
      setTimeout(() => setAdvLinkCopied(false), 3000);
    } catch (e) {
      console.error("[ADVANCD] shareAdvance error:", e);
    }
    setAdvGenLoading(false);
  };

  const toggleFlag = (aId, tId, sId) =>
    setData(p => ({ ...p, artists: p.artists.map(a => a.id !== aId ? a : { ...a, tours: a.tours.map(t => t.id !== tId ? t : { ...t, shows: t.shows.map(s => s.id !== sId ? s : { ...s, flagged: !s.flagged }) }) }) }));

  const doDraft = async (tokenOverride) => {
    const token = tokenOverride || show?.advanceToken;
    if (!artist || !show || !venue || !token) return;
    setELoad(true); setEDraft("");
    try { setEDraft(await draftAdvanceEmail(artist, show, venue, data.profile, token)); }
    catch { setEDraft("Failed to generate — check network."); }
    setELoad(false);
  };

  const sendAdvance = async () => {
    if (!show || !artist || !venue) return;
    let token = show.advanceToken;
    if (!token) {
      setAdvGenLoading(true);
      token = crypto.randomUUID();
      const { error } = await supabase.from("shows").update({ advance_token: token }).eq("id", show.id);
      if (error) { setAdvGenLoading(false); return; }
      updShow("advanceToken", token);
      bumpToAdvancing(show.id);
      setAdvGenLoading(false);
    }
    setDetailTab("email");
    doDraft(token);
  };

  const exportAdvancePdf = () => {
    if (!show || !artist) return;
    const tmD  = show.advanceTmData || {};
    const proD = show.advanceData   || {};
    const FF   = "'Helvetica Neue', Helvetica, Arial, sans-serif";
    const submittedAt = proD._submittedAt
      ? new Date(proD._submittedAt).toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" })
      : null;

    const fmtVal = (f, v) => {
      if (v === undefined || v === null || v === "") return null;
      if (f.type === "yesno") return v === true ? "Yes" : v === false ? "No" : null;
      if (f.type === "contact") {
        const c = (typeof v === "object" && v) ? v : {};
        const parts = [c.name, c.phone, c.email].filter(Boolean);
        return parts.length ? parts.join("  ·  ") : null;
      }
      return String(v).trim() || null;
    };

    const sections = ADVANCE_PORTAL_SECTIONS
      .filter(sec => (tour?.advance_config?.sections || []).includes(sec.id))
      .map(sec => {
        const tmSd  = tmD[sec.id]  || {};
        const proSd = proD[sec.id] || {};
        const enabledFlds = tourEnabledFields(sec.id);
        const fields = sec.fields
          .filter(f => enabledFlds.includes(f.id))
          .map(f => {
            const tmVal  = fmtVal(f, tmSd[f.id]);
            const proVal = fmtVal(f, proSd[f.id]);
            const amended = proVal !== null && proVal !== tmVal;
            const display = proVal ?? tmVal;
            const tmComment  = (tmSd._comments  || {})[f.id];
            const proComment = (proSd._comments || {})[f.id];
            return { label: f.label, display, tmVal, amended, tmComment, proComment };
          })
          .filter(f => f.display !== null);
        return { label: sec.label, fields };
      })
      .filter(sec => sec.fields.length > 0);

    const sectionHtml = sections.map(sec => `
      <div class="sec">
        <div class="sec-title">${sec.label}</div>
        ${sec.fields.map(f => `
          <div class="row ${f.amended ? "amended" : ""}">
            <div class="lbl">${f.label}${f.amended ? ' <span class="amd-badge">AMENDED</span>' : ""}</div>
            <div class="val">${f.display}</div>
            ${f.amended && f.tmVal ? `<div class="tm-note">TM suggested: ${f.tmVal}</div>` : ""}
            ${f.tmComment  ? `<div class="comment tm-comment"><span class="comment-from">TM</span>${f.tmComment.text}</div>` : ""}
            ${f.proComment ? `<div class="comment pro-comment"><span class="comment-from">PROMOTER</span>${f.proComment.text}</div>` : ""}
          </div>
        `).join("")}
      </div>
    `).join("");

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8">
    <title>Advance — ${artist.name} · ${show.date ? fmtDate(show.date) : ""}</title>
    <style>
      *{box-sizing:border-box;margin:0;padding:0;}
      body{font-family:${FF};background:#fff;color:#1a1917;padding:40px;font-size:12px;}
      .header{border-bottom:2px solid #1a1917;padding-bottom:14px;margin-bottom:24px;}
      .header-title{font-size:28px;font-weight:700;letter-spacing:3px;text-transform:uppercase;}
      .header-meta{font-size:11px;color:#6a6764;margin-top:5px;letter-spacing:0.3px;}
      .header-confirmed{display:inline-block;margin-top:8px;font-size:11px;font-weight:700;letter-spacing:1px;text-transform:uppercase;color:#1a6a3a;background:#d8f0e4;border:1px solid #a8d8b8;border-radius:4px;padding:3px 8px;}
      .sec{margin-bottom:22px;break-inside:avoid;}
      .sec-title{font-size:11px;font-weight:700;letter-spacing:1.5px;text-transform:uppercase;color:#6a6764;border-bottom:1px solid #e8e4de;padding-bottom:5px;margin-bottom:8px;}
      .row{padding:5px 0;border-bottom:1px solid #f0ede8;display:flex;flex-direction:column;gap:2px;}
      .row.amended .lbl{color:#c47f00;}
      .lbl{font-size:11px;font-weight:600;color:#9a9794;letter-spacing:0.5px;text-transform:uppercase;display:flex;align-items:center;gap:6px;}
      .val{font-size:12px;color:#1a1917;}
      .amd-badge{font-size:11px;font-weight:700;letter-spacing:0.5px;color:#c47f00;background:#fff0cc;border:1px solid #d4880a;border-radius:4px;padding:1px 5px;}
      .tm-note{font-size:11px;color:#9a9794;font-style:italic;}
      .comment{font-size:11px;margin-top:3px;padding:4px 8px;border-radius:4px;display:flex;gap:6px;}
      .comment-from{font-size:11px;font-weight:700;letter-spacing:0.8px;text-transform:uppercase;flex-shrink:0;padding-top:1px;}
      .tm-comment{background:#e8ede3;border:1px solid #ccdac4;}
      .tm-comment .comment-from{color:#1a6a3a;}
      .pro-comment{background:#fff0cc;border:1px solid #d4880a;}
      .pro-comment .comment-from{color:#c47f00;}
      @media print{body{padding:20px;}@page{margin:18mm;}}
    </style></head><body>
    <div class="header">
      <div class="header-title">${artist.name}</div>
      <div class="header-meta">${[tour?.name, show.date ? fmtDate(show.date) : null, venue?.name, [venue?.city, venue?.addressState].filter(Boolean).join(", ")].filter(Boolean).join("  ·  ")}</div>
      ${submittedAt ? `<div class="header-confirmed">✓ Confirmed by promoter — ${submittedAt}</div>` : ""}
    </div>
    ${sectionHtml}
    </body></html>`;

    const win = window.open("", "_blank");
    if (win) {
      win.document.write(html);
      win.document.close();
      setTimeout(() => win.print(), 400);
    }
  };

  // ── AP-1: TM section status functions ────────────────────────────────────
  // TM can flag a section (with a note) or confirm it once the promoter has resolved it.
  // These write directly to advance_section_status on the shows table.
  const updateAdvanceSectionStatus = async (showId, secId, update) => {
    const show = data.artists.flatMap(a => a.tours.flatMap(t => t.shows)).find(s => s.id === showId);
    if (!show) return;
    const current = show.advanceSectionStatus || {};
    const newStatus = { ...current, [secId]: { ...(current[secId] || {}), ...update, updated_at: new Date().toISOString() } };
    // Optimistic update
    setData(p => ({ ...p, artists: p.artists.map(a => ({ ...a,
      tours: a.tours.map(t => ({ ...t,
        shows: t.shows.map(s => s.id === showId ? { ...s, advanceSectionStatus: newStatus } : s)
      }))
    })) }));
    await supabase.from("shows").update({ advance_section_status: newStatus }).eq("id", showId);
  };

  const flagAdvanceSection = (showId, secId, note) =>
    updateAdvanceSectionStatus(showId, secId, { status: "flagged", note: note || "" });

  const confirmAdvanceSectionByTM = (showId, secId) =>
    updateAdvanceSectionStatus(showId, secId, { status: "confirmed", note: "" });

  const unflagAdvanceSection = (showId, secId) =>
    updateAdvanceSectionStatus(showId, secId, { status: "pending", note: "" });

  // F-5d: TM acknowledges a festival flag on one of their advance sections
  const resolveFestFlag = async (showId, secId) => {
    const links = showEventLinks[showId] || [];
    const festLink = links.find(l => l.status === "confirmed" && l.metadata?.org_type === "festival");
    if (!festLink) return;
    const existing  = festLink.venue_section_status || {};
    const tmFlags   = { ...(existing.tm_section_flags || {}) };
    if (!tmFlags[secId]) return;
    tmFlags[secId]  = { ...tmFlags[secId], status: "confirmed" };
    const updated   = { ...existing, tm_section_flags: tmFlags };
    const { error } = await supabase.from("event_links").update({ venue_section_status: updated }).eq("id", festLink.id);
    if (!error) setShowEventLinks(prev => ({
      ...prev,
      [showId]: (prev[showId] || []).map(l => l.id === festLink.id ? { ...l, venue_section_status: updated } : l)
    }));
  };

  // ── V-1: Venue account linking ────────────────────────────────────────────
  // Search venue orgs by name
  const searchVenueOrgs = async (query) => {
    if (!query.trim()) { setVenueOrgResults([]); return; }
    setVenueOrgSearching(true);
    const { data } = await supabase
      .from("organizations")
      .select("id, name, slug, profile, type")
      .in("type", ["venue", "festival"])
      .ilike("name", `%${query.trim()}%`)
      .limit(8);
    setVenueOrgResults(data || []);
    setVenueOrgSearching(false);
  };

  // Link the current show to a venue or festival org. Auto-generates advance_token if missing.
  const linkShowToVenueOrg = async (orgId, orgName, orgType = "venue") => {
    if (!show || !selS || !artist) return;
    setLinkingVenue(true);
    try {
      // Ensure the show has an advance_token (generate if missing)
      let token = show.advanceToken;
      if (!token) {
        token = crypto.randomUUID();
        const { error: tokErr } = await supabase
          .from("shows").update({ advance_token: token }).eq("id", show.id);
        if (tokErr) throw tokErr;
        updShow("advanceToken", token);
        bumpToAdvancing(show.id);
      }

      const metadata = {
        artist_name:   artist?.name    || "",
        tour_name:     tour?.name      || "",
        show_date:     show.date       || "",
        venue_name:    venue?.name     || orgName,
        advance_token: token,
        org_type:      orgType,
      };

      const { data: newLink, error } = await supabase
        .from("event_links")
        .upsert({ show_id: selS, org_id: orgId, org_role: "promoter", status: "pending", metadata }, { onConflict: "show_id,org_id" })
        .select().single();
      if (error) throw error;

      setShowEventLinks(prev => ({
        ...prev,
        [selS]: [...(prev[selS] || []).filter(l => l.org_id !== orgId), newLink],
      }));
      setVenueOrgSearchOpen(false);
      setVenueOrgSearch("");
      setVenueOrgResults([]);
    } catch (e) {
      console.error("[ADVANCD] linkShowToVenueOrg failed:", e);
    }
    setLinkingVenue(false);
  };

  // Remove a venue link
  const unlinkVenueOrg = async (linkId) => {
    if (!selS) return;
    await supabase.from("event_links").delete().eq("id", linkId);
    setShowEventLinks(prev => ({
      ...prev,
      [selS]: (prev[selS] || []).filter(l => l.id !== linkId),
    }));
  };

  // ORG-1: Generate a short link code for a show so venue/festival can find it
  const generateShowLinkCode = async (showId) => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
    const code = Array.from({ length: 6 }, () => chars[Math.floor(Math.random() * chars.length)]).join("");
    const { error } = await supabase.from("shows").update({ link_code: code }).eq("id", showId);
    if (!error) {
      setData(prev => ({
        ...prev,
        artists: prev.artists.map(a => ({
          ...a,
          tours: a.tours.map(t => ({
            ...t,
            shows: t.shows.map(s => s.id === showId ? { ...s, linkCode: code } : s),
          })),
        })),
      }));
    }
    return error ? null : code;
  };

  // DS-1: Generate (or retrieve) a stable share token for a tour day's day sheet
  const generateDaySheetToken = async (dayId) => {
    const existing = tourDays.find(d => d.id === dayId)?.day_sheet_token;
    if (existing) return existing;
    setDsGenerating(dayId);
    const token = crypto.randomUUID();
    const { error } = await supabase.from("tour_days").update({ day_sheet_token: token }).eq("id", dayId);
    if (!error) {
      setTourDays(prev => prev.map(d => d.id === dayId ? { ...d, day_sheet_token: token } : d));
    }
    setDsGenerating(null);
    return error ? null : token;
  };

  // V-3: TM confirms settlement on their side
  const finalizeAdvance = async (showId, finalized) => {
    // Finalizing an advance implies the show is confirmed — set the status
    // automatically so the TM never has to do it twice. Unlocking drops a
    // confirmed show back to advancing (the status badge stays manually
    // cyclable either way, so the TM keeps full control).
    const showObj = data.artists.flatMap(a => a.tours).flatMap(t => t.shows).find(s => s.id === showId);
    const curStatus = showObj?.status || "unstarted";
    const nextStatus = finalized
      ? "confirmed"
      : (curStatus === "confirmed" ? "advancing" : curStatus);
    const updates = {
      advance_finalized: finalized,
      advance_finalized_at: finalized ? new Date().toISOString() : null,
      ...(nextStatus !== curStatus ? { status: nextStatus } : {}),
    };
    const { error } = await supabase.from("shows").update(updates).eq("id", showId);
    if (error) return;
    setData(prev => ({
      ...prev,
      artists: prev.artists.map(a => ({
        ...a,
        tours: a.tours.map(t => ({
          ...t,
          shows: t.shows.map(s => s.id === showId
            ? { ...s, advanceFinalized: finalized, advanceFinalizedAt: updates.advance_finalized_at, ...(nextStatus !== curStatus ? { status: nextStatus } : {}) }
            : s
          ),
        })),
      })),
    }));
  };

  // ── DF-3: Zero-touch advance draft ─────────────────────────────────────────
  // Fills EMPTY advance sections from, in priority order: (1) the most recent
  // prior show at the same venue — that show's TM layer with the promoter's
  // actual answers overlaid (they answered these exact questions last time),
  // (2) the tour default schedule (materialized bill + venue times), (3) the
  // venue CRM notes (same mapping the deal-memo import uses). Never overwrites
  // anything the TM already entered; schedule bill and guest list stay
  // show-specific and are never copied from prior shows.
  const draftAdvanceForShow = async (targetShow) => {
    if (!targetShow) return { ok: false, msg: "No show." };
    const cur = targetShow.advanceTmData || {};
    const next = { ...cur };
    const isEmptySec = (s) => !next[s] || Object.keys(next[s]).filter(k => !k.startsWith("_")).length === 0;
    const sources = [];

    // 1. Prior show at this venue (any tour, most recent, with advance data)
    const allShowsFlat = data.artists.flatMap(a => a.tours.flatMap(t => t.shows));
    const prior = allShowsFlat
      .filter(s => s.id !== targetShow.id && s.venueId && s.venueId === targetShow.venueId &&
        (Object.keys(s.advanceTmData || {}).length > 0 || Object.keys(s.advanceData || {}).length > 0))
      .sort((a, b) => (b.date || "").localeCompare(a.date || ""))[0];
    if (prior) {
      const COPY_SECS = ["hospitality", "parking", "greenRoom", "backline", "merch", "venueCrew", "bus", "groundTransportAdv", "production", "security", "vip"];
      const pTm = prior.advanceTmData || {};
      const pPro = prior.advanceData || {};
      let copied = 0;
      for (const sec of COPY_SECS) {
        if (!isEmptySec(sec)) continue;
        const merged = { ...(pTm[sec] || {}), ...(pPro[sec] || {}) }; // promoter answers win
        const cleaned = {};
        for (const [k, v] of Object.entries(merged)) {
          if (k.startsWith("_")) continue;
          if (v === undefined || v === null || v === "") continue;
          if (Array.isArray(v) && v.length === 0) continue;
          cleaned[k] = v;
        }
        if (Object.keys(cleaned).length > 0) { next[sec] = cleaned; copied++; }
      }
      if (copied > 0) sources.push(`the ${prior.date} advance at this venue`);
    }

    // 2. Tour default schedule → materialize (display fallback becomes real data)
    const dSched = tour?.advanceConfig?.defaultSchedule;
    if (isEmptySec("schedule") && dSched && (dSched.acts?.length || dSched.doors || dSched.venue_access)) {
      next.schedule = {
        ...(dSched.venue_access && { venue_access: dSched.venue_access }),
        ...(dSched.vip_doors    && { vip_access:   dSched.vip_doors }),
        ...(dSched.doors        && { doors_public: dSched.doors }),
        ...(dSched.noise_curfew && { noise_curfew: dSched.noise_curfew }),
        ...(dSched.gr_curfew    && { gr_curfew:    dSched.gr_curfew }),
        ...(dSched.hard_curfew  && { curfew:       dSched.hard_curfew }),
        bill: (dSched.acts || []).map(a => ({ id: a.id, name: a.name, load: a.load_in || "", sc: a.soundcheck || "", set: a.set_time || "" })),
        changeovers: (dSched.acts || []).slice(0, -1).map(a => ({ time: a.changeover || "" })),
      };
      sources.push("tour defaults");
    }

    // 3. Venue CRM notes → still-empty fields (mirrors the import-path mapping)
    const crmV = venues[targetShow.venueId];
    if (crmV) {
      let usedCrm = false;
      const setIf = (sec, field, val) => {
        if (!val) return;
        if (next[sec]?.[field]) return;
        next[sec] = { ...(next[sec] || {}), [field]: val };
        usedCrm = true;
      };
      setIf("schedule",   "venue_access", (!next.schedule?.venue_access && crmV.loadIn) ? crmV.loadIn : null);
      setIf("parking",    "park_notes",   crmV.parking);
      setIf("production", "prod_notes",   crmV.houseGear);
      setIf("greenRoom",  "rooms",        crmV.greenRooms);
      setIf("merch",      "merch_split",  crmV.merchSplit);
      if (usedCrm) sources.push("the venue record");
    }

    if (sources.length === 0) {
      return { ok: false, msg: "Nothing to draft from — no prior advance here, no tour defaults, no venue notes." };
    }
    next._draftSources = { at: new Date().toISOString(), sources };
    const { error } = await supabase.from("shows").update({ advance_tm_data: next }).eq("id", targetShow.id);
    if (error) return { ok: false, msg: error.message };
    setData(p => ({ ...p, artists: p.artists.map(a => ({ ...a,
      tours: a.tours.map(t => ({ ...t,
        shows: t.shows.map(s => s.id === targetShow.id ? { ...s, advanceTmData: next } : s)
      }))
    })) }));
    return { ok: true, msg: `Drafted from ${sources.join(" + ")}.` };
  };

  const resetAdvance = async (showId) => {
    // Cancel pending save timers and lock advance writes for 5s
    if (saveTimer.current)      { clearTimeout(saveTimer.current);      saveTimer.current      = null; }
    if (advTmSaveTimer.current) { clearTimeout(advTmSaveTimer.current); advTmSaveTimer.current = null; }
    advTmDirtyRef.current.delete(showId); // reset discards local edits — nothing left to flush
    advResetLock.current = true;
    setTimeout(() => { advResetLock.current = false; }, 5000);

    const { error } = await supabase.from("shows").update({
      advance_data:           null,
      advance_tm_data:        null,
      advance_section_status: null,
      advance_last_viewed_at: null,
      advance_finalized:      false,
      advance_finalized_at:   null,
    }).eq("id", showId);
    if (error) return;

    setData(prev => ({
      ...prev,
      artists: prev.artists.map(a => ({
        ...a,
        tours: a.tours.map(t => ({
          ...t,
          shows: t.shows.map(s => s.id === showId
            ? { ...s, advanceData: {}, advanceTmData: {}, advanceSectionStatus: {},
                advanceLastViewedAt: null, advanceFinalized: false, advanceFinalizedAt: null }
            : s
          ),
        })),
      })),
    }));
  };

  const confirmTmSettlement = async (linkId) => {
    if (!selS) return;
    const link = (showEventLinks[selS] || []).find(l => l.id === linkId);
    if (!link) return;
    const updates = { tm_settlement_confirmed: true };
    // If venue already confirmed too, stamp the bilateral time
    if (link.venue_settlement_confirmed) {
      updates.settlement_confirmed_at = new Date().toISOString();
    }
    await supabase.from("event_links").update(updates).eq("id", linkId);
    setShowEventLinks(prev => ({
      ...prev,
      [selS]: (prev[selS] || []).map(l => l.id === linkId ? { ...l, ...updates } : l),
    }));
  };

  // ── Itinerary Export ─────────────────────────────────────────────────────
  const exportTourItinerary = async (sections = {}, opts = {}) => {
    if (!tour || !tourDays.length) return;
    const FF = "'Helvetica Neue', Helvetica, Arial, sans-serif";

    // Batch-fetch all lodging, flights, transport for every day in one round-trip
    const allDayIds = tourDays.map(d => d.id);
    const [{ data: allLodging }, { data: allFlights }, { data: allTransport }] = await Promise.all([
      supabase.from('lodging').select('*').in('tour_day_id', allDayIds).order('created_at'),
      supabase.from('flights').select('*').in('tour_day_id', allDayIds).order('departure_time'),
      supabase.from('ground_transport').select('*').in('tour_day_id', allDayIds).order('pickup_time'),
    ]);

    // Group by day
    const lodgingByDay   = {};
    const flightsByDay   = {};
    const transportByDay = {};
    (allLodging   || []).forEach(r => { (lodgingByDay[r.tour_day_id]   = lodgingByDay[r.tour_day_id]   || []).push(r); });
    (allFlights   || []).forEach(r => { (flightsByDay[r.tour_day_id]   = flightsByDay[r.tour_day_id]   || []).push(r); });
    (allTransport || []).forEach(r => { (transportByDay[r.tour_day_id] = transportByDay[r.tour_day_id] || []).push(r); });

    const fmtTime = (t) => {
      if (!t) return "";
      const [h, m] = t.split(":").map(Number);
      const suffix = h >= 12 ? "PM" : "AM";
      const h12 = h % 12 || 12;
      return `${h12}:${String(m).padStart(2,"0")} ${suffix}`;
    };

    const fmtDate24 = (iso) => {
      if (!iso) return "";
      const d = new Date(iso);
      return d.toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
    };

    const typeColor = { show: "#c47f00", travel: "#3a6abf", drive: "#2a8a6a", off: "#666" };
    const typeLabel = { show: "SHOW", travel: "TRAVEL", drive: "DRIVE", off: "OFF" };

    const dateRange = tourDays.length
      ? `${new Date(tourDays[0].date + "T12:00:00").toLocaleDateString("en-US", { month: "long", day: "numeric" })} – ${new Date(tourDays[tourDays.length - 1].date + "T12:00:00").toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}`
      : "";

    const dayBlocks = tourDays.map((day, idx) => {
      const dayNum  = idx + 1;
      const show    = day.show_id ? tour.shows.find(s => s.id === day.show_id) : null;
      const v       = show ? venues[show.venueId] : null;
      const wx      = dayWeather[day.id];
      const lodging = lodgingByDay[day.id] || [];
      const flights = flightsByDay[day.id] || [];
      const transpt = transportByDay[day.id] || [];
      const color   = typeColor[day.type] || "#666";
      const label   = typeLabel[day.type] || "OFF";
      const dateObj = new Date(day.date + "T12:00:00");
      const dateFmt = dateObj.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
      const city    = (day.type === "show" && v?.city) ? `${v.city}${v.addressState ? ", " + v.addressState : ""}` : (day.city || "");

      const wxHtml = (wx?.status === "ready" && sections.weather !== false)
        ? `<span class="wx">${wx.condition ? wx.condition + " · " : ""}${wx.high}° / ${wx.low}°${wx.precip ? " · " + wx.precip + "% precip" : ""}</span>`
        : "";

      const showHtml = (day.type === "show" && v && sections.show !== false) ? `
        <div class="section">
          <div class="section-label">SHOW</div>
          <div class="info-row"><span class="info-key">Venue</span><span class="info-val">${v.name}</span></div>
          ${[v.addressStreet, v.city, v.addressState].filter(Boolean).length ? `<div class="info-row"><span class="info-key">Address</span><span class="info-val">${[v.addressStreet, v.city, v.addressState].filter(Boolean).join(", ")}</span></div>` : ""}
          ${v.capacity ? `<div class="info-row"><span class="info-key">Capacity</span><span class="info-val">${v.capacity.toLocaleString()}</span></div>` : ""}
          ${show?.advanceTmData?.schedule?.venue_access ? `<div class="info-row"><span class="info-key">Venue Access</span><span class="info-val">${show.advanceTmData.schedule.venue_access}</span></div>` : ""}
          ${(show?.advanceTmData?.schedule?.bill?.find(b => b.name?.toLowerCase().includes("headliner") || show.advanceTmData.schedule.bill.indexOf(b) === show.advanceTmData.schedule.bill.length - 1)?.set) ? `<div class="info-row"><span class="info-key">Set Time</span><span class="info-val">${show.advanceTmData.schedule.bill[show.advanceTmData.schedule.bill.length-1].set}</span></div>` : ""}
          ${v.promoterName ? `<div class="info-row"><span class="info-key">Promoter</span><span class="info-val">${v.promoterName}${v.promoterEmail ? " · " + v.promoterEmail : ""}${v.promoterPhone ? " · " + v.promoterPhone : ""}</span></div>` : ""}
        </div>` : "";

      const busHtml = (day.bus_call && sections.busCall !== false) ? `
        <div class="section">
          <div class="section-label">BUS CALL</div>
          <div class="info-row"><span class="info-key">Departure</span><span class="info-val buscall">${fmtTime(day.bus_call)}</span></div>
        </div>` : "";

      const lodgingHtml = (lodging.length && sections.lodging !== false) ? `
        <div class="section">
          <div class="section-label">LODGING</div>
          ${lodging.map(l => `
            <div class="sub-block">
              <div class="info-row"><span class="info-key">Property</span><span class="info-val">${l.name}${l.chain ? " · " + l.chain : ""} <span class="badge">${l.type === "airbnb" ? "Airbnb" : "Hotel"}</span></span></div>
              ${l.address ? `<div class="info-row"><span class="info-key">Address</span><span class="info-val">${l.address}</span></div>` : ""}
              ${l.check_in || l.check_out ? `<div class="info-row"><span class="info-key">Times</span><span class="info-val">${[l.check_in ? "In " + l.check_in : "", l.check_out ? "Out " + l.check_out : ""].filter(Boolean).join(" · ")}</span></div>` : ""}
              ${l.confirmation ? `<div class="info-row"><span class="info-key">Confirmation</span><span class="info-val conf">${l.confirmation}</span></div>` : ""}
              ${l.phone ? `<div class="info-row"><span class="info-key">Phone</span><span class="info-val">${fmtPhone(l.phone)}</span></div>` : ""}
            </div>`).join("")}
        </div>` : "";

      const flightsHtml = (flights.length && sections.flights !== false) ? `
        <div class="section">
          <div class="section-label">FLIGHTS</div>
          ${flights.map(f => `
            <div class="sub-block">
              <div class="info-row"><span class="info-key">Flight</span><span class="info-val">${f.airline_code || ""}${f.flight_number || ""} — ${f.origin || ""} → ${f.destination || ""}</span></div>
              ${f.departure_time ? `<div class="info-row"><span class="info-key">Departs</span><span class="info-val">${fmtDate24(f.departure_time)}</span></div>` : ""}
              ${f.arrival_time ? `<div class="info-row"><span class="info-key">Arrives</span><span class="info-val">${fmtDate24(f.arrival_time)}</span></div>` : ""}
              ${f.terminal ? `<div class="info-row"><span class="info-key">Terminal</span><span class="info-val">${f.terminal}${f.gate ? " · Gate " + f.gate : ""}</span></div>` : ""}
              ${f.confirmation ? `<div class="info-row"><span class="info-key">Confirmation</span><span class="info-val conf">${f.confirmation}</span></div>` : ""}
            </div>`).join("")}
        </div>` : "";

      const transportHtml = (transpt.length && sections.transport !== false) ? `
        <div class="section">
          <div class="section-label">GROUND TRANSPORT</div>
          ${transpt.map(t => `
            <div class="sub-block">
              <div class="info-row"><span class="info-key">Type</span><span class="info-val">${(t.type || "").replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}${t.company ? " · " + t.company : ""}</span></div>
              ${t.pickup_time ? `<div class="info-row"><span class="info-key">Pickup</span><span class="info-val">${t.pickup_time}</span></div>` : ""}
              ${t.pickup_location ? `<div class="info-row"><span class="info-key">From</span><span class="info-val">${t.pickup_location}</span></div>` : ""}
              ${t.dropoff_location ? `<div class="info-row"><span class="info-key">To</span><span class="info-val">${t.dropoff_location}</span></div>` : ""}
              ${t.driver_name ? `<div class="info-row"><span class="info-key">Driver</span><span class="info-val">${t.driver_name}${t.driver_phone ? " · " + t.driver_phone : ""}</span></div>` : ""}
              ${t.confirmation_number ? `<div class="info-row"><span class="info-key">Confirmation</span><span class="info-val conf">${t.confirmation_number}</span></div>` : ""}
            </div>`).join("")}
        </div>` : "";

      const notesHtml = (day.notes && sections.notes !== false) ? `
        <div class="section">
          <div class="section-label">NOTES</div>
          <div class="notes-text">${day.notes}</div>
        </div>` : "";

      const isEmpty = !showHtml && !busHtml && !lodgingHtml && !flightsHtml && !transportHtml && !notesHtml;
      const pageBreak = idx > 0 ? "page-break-before:always;break-before:page;" : "";

      return `
        <div class="day-block" style="border-left-color:${color};${pageBreak}">
          <div class="day-header">
            <div class="day-num">D${dayNum}</div>
            <div class="day-date">${dateFmt}</div>
            ${city ? `<div class="day-city">${city}</div>` : ""}
            <div class="day-type" style="color:${color};border-color:${color}40;background:${color}10">${label}</div>
            ${wxHtml}
          </div>
          ${isEmpty ? `<div class="empty-day">No details recorded</div>` : ""}
          ${busHtml}${showHtml}${lodgingHtml}${flightsHtml}${transportHtml}${notesHtml}
        </div>`;
    }).join("");

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8">
    <title>Itinerary — ${artist?.name || ""} · ${tour.name || ""}</title>
    <style>
      *{box-sizing:border-box;margin:0;padding:0;}
      body{font-family:${FF};background:#fff;color:#1a1917;padding:28px 36px;font-size:11px;max-width:900px;margin:0 auto;}
      .cover{border-bottom:3px solid #1a1917;padding-bottom:16px;margin-bottom:0;page-break-after:always;break-after:page;}
      .cover-title{font-size:28px;font-weight:800;letter-spacing:4px;text-transform:uppercase;color:#1a1917;}
      .cover-sub{font-size:12px;color:#6a6764;margin-top:6px;letter-spacing:0.5px;}
      .cover-meta{display:flex;gap:20px;margin-top:12px;flex-wrap:wrap;}
      .cover-meta-item{font-size:11px;letter-spacing:1.2px;text-transform:uppercase;color:#9a9794;}
      .cover-meta-item span{color:#1a1917;font-weight:600;}
      .day-block{border-left:4px solid #ccc;padding:16px 18px;background:#faf9f8;min-height:97vh;}
      .day-header{display:flex;align-items:center;gap:10px;margin-bottom:12px;padding-bottom:12px;border-bottom:2px solid #e8e4de;flex-wrap:wrap;}
      .day-num{font-size:11px;font-weight:700;letter-spacing:1.5px;color:#9a9794;min-width:28px;}
      .day-date{font-size:16px;font-weight:700;color:#1a1917;letter-spacing:0.3px;}
      .day-city{font-size:14px;color:#4a4744;flex:1;}
      .day-type{font-size:11px;font-weight:700;letter-spacing:1.5px;padding:3px 9px;border-radius:4px;border:1px solid;flex-shrink:0;}
      .wx{font-size:11px;color:#6a6764;letter-spacing:0.3px;margin-left:auto;}
      .section{margin-top:12px;padding-top:12px;border-top:1px solid #e8e4de;}
      .section-label{font-size:11px;font-weight:700;letter-spacing:1.8px;text-transform:uppercase;color:#9a9794;margin-bottom:8px;}
      .info-row{display:flex;gap:10px;padding:3px 0;align-items:baseline;}
      .info-key{font-size:11px;font-weight:600;letter-spacing:0.5px;text-transform:uppercase;color:#9a9794;min-width:110px;flex-shrink:0;}
      .info-val{font-size:11px;color:#1a1917;line-height:1.4;}
      .buscall{font-size:20px;font-weight:800;color:#c47f00;letter-spacing:1px;}
      .conf{font-family:monospace;font-size:11px;background:#f0ede8;padding:1px 6px;border-radius:4px;}
      .badge{font-size:11px;font-weight:700;letter-spacing:0.8px;text-transform:uppercase;background:#e8e4de;color:#6a6764;padding:1px 5px;border-radius:4px;vertical-align:middle;}
      .sub-block{margin-bottom:8px;padding-bottom:8px;border-bottom:1px dashed #e8e4de;}
      .sub-block:last-child{margin-bottom:0;padding-bottom:0;border-bottom:none;}
      .notes-text{font-size:11px;color:#4a4744;line-height:1.6;font-style:italic;}
      .empty-day{font-size:11px;color:#b0ada8;font-style:italic;padding:4px 0;}
      @media print{
        body{padding:14px 20px;}
        @page{margin:12mm;size:letter;}
        -webkit-print-color-adjust:exact;print-color-adjust:exact;
      }
    </style></head><body>
    <div class="cover">
      <div class="cover-title">${artist?.name || "Tour Itinerary"}</div>
      <div class="cover-sub">${tour.name || ""}${tour.year ? " · " + tour.year : ""}</div>
      <div class="cover-meta">
        <div class="cover-meta-item">Dates <span>${dateRange}</span></div>
        <div class="cover-meta-item">Total Days <span>${tourDays.length}</span></div>
        <div class="cover-meta-item">Shows <span>${tourDays.filter(d => d.type === "show").length}</span></div>
        <div class="cover-meta-item">Generated <span>${new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</span></div>
      </div>
    </div>
    ${dayBlocks}
    </body></html>`;

    const win = window.open("", "_blank");
    if (opts.returnHtml) return html;
    if (win) { win.document.write(html); win.document.close(); setTimeout(() => win.print(), 500); }
  };

  // Generate a shareable link — stores pre-rendered HTML in shared_itineraries, returns URL
  const shareItinerary = async (sections = {}) => {
    const html = await exportTourItinerary(sections, { returnHtml: true });
    if (!html) return null;

    const token = crypto.randomUUID().replace(/-/g, "");
    const { error } = await supabase.from("shared_itineraries").insert({
      token,
      tour_id:      tour?.id     || null,
      artist_name:  artist?.name || null,
      tour_name:    tour?.name   || null,
      sections,
      html_content: html,
      created_by:   user?.id     || null,
      team_id:      teamId       || null,
    });
    if (error) { console.error("[ADVANCD] shareItinerary failed:", error); return null; }

    const url = `${window.location.origin}/api/itinerary?token=${token}`;
    await navigator.clipboard.writeText(url).catch(() => {});
    setItineraryShareUrl(url);
    return url;
  };

  const bulkSetStatus = (status) => {
    const ids = new Set(filteredShows.map(s => s.id));
    setData(p => ({ ...p, artists: p.artists.map(a => a.id !== selA ? a : { ...a,
      tours: a.tours.map(t => t.id !== selT ? t : { ...t,
        shows: t.shows.map(s => ids.has(s.id) ? { ...s, status } : s),
      }),
    })}));
  };

  // ── Tour Mode ────────────────────────────────────────────────────────────
  const isTourMode = tour?.advanceConfig?.mode === 'tour';
  const isFinMode  = tour?.advanceConfig?.mode === 'financials';

  // Auto-load tour days when opening a tour that's already in tour mode
  // (e.g. navigating directly to tour mode without hitting the toggle)
  useEffect(() => {
    if (!isTourMode || !tour?.id) return;
    // Skip if days are already loaded for this tour
    if (tourDays.some(d => d.tour_id === tour.id) || tourDaysLoading) return;
    ensureTourDays(tour);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selT, isTourMode]);

  // Live-reconcile: if the show set changes while we're already in tour mode
  // (e.g. a show added/removed/re-dated without toggling out of tour mode),
  // re-run ensureTourDays so the day grid relinks to the current shows.
  const tourShowSig = isTourMode
    ? (tour?.shows || []).map(s => `${s.id}:${s.date}`).sort().join('|')
    : '';
  useEffect(() => {
    if (!isTourMode || !tour?.id) return;
    // Only reconcile once days for this tour are already loaded — the initial
    // load is handled by the effect above.
    if (!tourDays.some(d => d.tour_id === tour.id)) return;
    if (tourDaysLoading) return;
    ensureTourDays(tour);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tourShowSig]);

  // SL-1: Load setlists whenever a tour is selected, independent of tour mode.
  // The Setlist tab in the show detail pane is reachable without entering tour
  // mode, so we can't rely on ensureTourDays (which only fires in tour mode).
  // Inlined supabase call (rather than calling loadShowSetlists) so this useEffect
  // doesn't forward-reference a const declared much later (Rolldown TDZ safety).
  useEffect(() => {
    if (!teamId || !selT || memberRole) return; // TM only — members use their own flow
    let cancelled = false;
    supabase.from('show_setlists').select('*').eq('tour_id', selT)
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) { console.error('[ADVANCD] auto-load setlists:', error); return; }
        setShowSetlists((data || []).sort((a, b) => (a.set_number || 0) - (b.set_number || 0)));
      });
    return () => { cancelled = true; };
  }, [teamId, selT, memberRole]);

  // SR-1: Load show reports whenever a tour is selected (TM only).
  useEffect(() => {
    if (!teamId || !selT || memberRole) return;
    let cancelled = false;
    supabase.from('show_reports').select('*').eq('tour_id', selT).order('submitted_at', { ascending: true })
      .then(({ data: rData, error }) => {
        if (cancelled) return;
        if (error) { console.error('[ADVANCD] auto-load show reports:', error); return; }
        setShowReports(rData || []);
      });
    return () => { cancelled = true; };
  }, [teamId, selT, memberRole]);

  // Load invited band/crew members for the current tour (TM only)
  useEffect(() => {
    if (!tour?.id || memberRole) return; // skip for band/crew members
    supabase
      .from("tour_members")
      .select("id, name, email, role, invite_status, modules, created_at")
      .eq("tour_id", tour.id)
      .order("created_at", { ascending: true })
      .then(({ data }) => { if (data) setTourInvites(data); })
      .catch(err => console.error("[ADVANCD] tourInvites load failed:", err));
  }, [tour?.id, memberRole]);

  const loadTourDays = async (tourId) => {
    if (!tourId) return;
    setTourDaysLoading(true);
    const { data: rows, error } = await supabase
      .from('tour_days').select('*').eq('tour_id', tourId).order('date', { ascending: true });
    if (!error && rows) {
      setTourDays(rows);
      loadLogisticsSummary(rows.map(r => r.id));
    }
    setTourDaysLoading(false);
  };

  const loadLogisticsSummary = async (dayIds) => {
    if (!dayIds?.length) return;
    const [lodRes, flRes, trRes] = await Promise.all([
      supabase.from('lodging').select('tour_day_id').in('tour_day_id', dayIds),
      supabase.from('flights').select('tour_day_id').in('tour_day_id', dayIds),
      supabase.from('ground_transport').select('tour_day_id').in('tour_day_id', dayIds),
    ]);
    const summary = {};
    dayIds.forEach(id => { summary[id] = { hasLodging: false, hasFlights: false, hasTransport: false }; });
    (lodRes.data || []).forEach(r => { if (summary[r.tour_day_id]) summary[r.tour_day_id].hasLodging   = true; });
    (flRes.data  || []).forEach(r => { if (summary[r.tour_day_id]) summary[r.tour_day_id].hasFlights   = true; });
    (trRes.data  || []).forEach(r => { if (summary[r.tour_day_id]) summary[r.tour_day_id].hasTransport = true; });
    setLogisticsSummary(prev => ({ ...prev, ...summary }));
  };

  const generateTourDays = async (t) => {
    if (!t?.shows?.length) { setTourDaysLoading(false); return; }
    const sorted = [...t.shows].sort((a, b) => a.date.localeCompare(b.date));
    const startDate = sorted[0].date;
    const endDate   = sorted[sorted.length - 1].date;
    const showDates = new Set(sorted.map(s => s.date));
    const showByDate = {};
    sorted.forEach(s => { showByDate[s.date] = s; });
    // Build a row for every calendar day in the range
    const rows = [];
    const cur = new Date(startDate + 'T12:00:00');
    const end = new Date(endDate   + 'T12:00:00');
    while (cur <= end) {
      const iso = cur.toISOString().slice(0, 10);
      const isShow = showDates.has(iso);
      rows.push({
        tour_id: t.id, user_id: user.id, team_id: teamId || null,
        date: iso, type: isShow ? 'show' : 'off',
        show_id: isShow ? showByDate[iso].id : null,
        city:  isShow ? (venues[showByDate[iso].venueId]?.city         || '') : '',
        state: isShow ? (venues[showByDate[iso].venueId]?.addressState || '') : '',
        country: '', hotel_name: '', hotel_address: '',
        hotel_check_in: '', hotel_check_out: '', hotel_confirmation: '',
        hotel_phone: '', hotel_contact: '', bus_call: '', notes: '',
      });
      cur.setDate(cur.getDate() + 1);
    }
    const { data: inserted, error } = await supabase
      .from('tour_days').insert(rows).select();
    if (error) { console.error('[ADVANCD] generateTourDays failed:', error); }
    else if (inserted) {
      const sorted = inserted.sort((a, b) => a.date.localeCompare(b.date));
      setTourDays(sorted);
      loadLogisticsSummary(sorted.map(r => r.id));
    }
    setTourDaysLoading(false);
  };

  // loadCrewMembers retired — PP-1 Phase 2

  const loadProfiles = async (tid) => {
    if (!tid) return;
    const { data, error } = await supabase.from('people_profiles').select('*').eq('team_id', tid).order('name');
    if (!error && data) setProfiles(data);
  };

  // Auto-create people_profiles entries for artist.members that aren't on the roster yet.
  // Called on roster tab open and whenever band members are saved.
  // _rosterSyncing guards against concurrent calls for the same artist creating duplicates.
  const _rosterSyncing = useRef(new Set());
  const syncMembersToRoster = async (artistId, members) => {
    if (!artistId || !teamId || !user) return;
    if (_rosterSyncing.current.has(artistId)) return; // already in flight
    _rosterSyncing.current.add(artistId);
    try {
      // Fetch existing profiles with id + contact fields so we can detect drift
      const { data: existing } = await supabase
        .from('people_profiles').select('id, name, email, phone').eq('artist_id', artistId);
      const existingByName = Object.fromEntries(
        (existing || []).map(p => [(p.name || "").toLowerCase().trim(), p])
      );

      const toCreate = [];
      const toUpdate = [];
      const seenKeys = new Set(); // prevent intra-batch dupes
      for (const m of (members || [])) {
        if (!m.name) continue;
        const key = (m.name || "").toLowerCase().trim();
        if (seenKeys.has(key)) continue;
        seenKeys.add(key);
        const ep = existingByName[key];
        if (!ep) {
          toCreate.push(m);
        } else if ((m.email || "") !== (ep.email || "") || (m.phone || "") !== (ep.phone || "")) {
          // Contact info changed in artist.members — keep profile in sync
          toUpdate.push({ id: ep.id, email: m.email || null, phone: m.phone || null });
        }
      }

      if (toCreate.length === 0 && toUpdate.length === 0) return;

      for (const m of toCreate) {
        await supabase.from('people_profiles').insert({
          team_id: teamId,
          user_id: null,  // null until the member claims the profile via invite
          artist_id: artistId,
          name: m.name,
          preferred_name: m.name,
          role: m.role || "",
          role_category: "band",
          email: m.email || null,
          phone: m.phone || null,
        });
      }
      for (const u of toUpdate) {
        await supabase.from('people_profiles').update({ email: u.email, phone: u.phone }).eq('id', u.id);
      }
      await loadProfiles(teamId);
    } finally {
      _rosterSyncing.current.delete(artistId);
    }
  };

  // ── Tour Memberships (PP-1 Phase 3) ────────────────────────────────────
  const loadTourMemberships = async (tourId) => {
    if (!tourId) return;
    const { data } = await supabase.from('tour_memberships').select('*').eq('tour_id', tourId);
    if (data) setTourMemberships(data);
  };

  const saveTourMembership = async (profileId, updates) => {
    const existing = tourMemberships.find(m => m.profile_id === profileId && m.tour_id === selT);

    // Optimistic update — show the change immediately in the UI
    setTourMemberships(prev => {
      const idx = prev.findIndex(m => m.profile_id === profileId && m.tour_id === selT);
      const optimistic = { ...(idx >= 0 ? prev[idx] : {}), ...updates, profile_id: profileId, tour_id: selT };
      return idx >= 0
        ? prev.map(m => (m.profile_id === profileId && m.tour_id === selT) ? optimistic : m)
        : [...prev, optimistic];
    });

    let result = null;
    if (existing?.id) {
      // Row exists in local state — send only the changed fields (not the full row;
      // sending id/created_at in an update payload causes PostgREST to silently reject)
      const { data, error } = await supabase.from('tour_memberships')
        .update(updates)
        .eq('profile_id', profileId).eq('tour_id', selT)
        .select().single();
      if (error) console.error('[ADVANCD] saveTourMembership update:', error);
      else result = data;
    } else {
      // Not in local state — check the DB before deciding insert vs update
      const { data: dbRow } = await supabase.from('tour_memberships')
        .select('id').eq('profile_id', profileId).eq('tour_id', selT).maybeSingle();
      if (dbRow?.id) {
        const { data, error } = await supabase.from('tour_memberships')
          .update(updates)
          .eq('profile_id', profileId).eq('tour_id', selT)
          .select().single();
        if (error) console.error('[ADVANCD] saveTourMembership update (db-found):', error);
        else result = data;
      } else {
        // No row at all — build a clean insert payload using crew defaults + overrides
        const preset = TOUR_ROLE_PRESETS.find(r => r.id === 'crew');
        const insertPayload = {
          profile_id:         profileId,
          tour_id:            selT,
          team_id:            teamId,
          tour_role:          preset?.id            || 'crew',
          can_see_guarantee:  preset?.can_see_guarantee  ?? false,
          can_see_settlement: preset?.can_see_settlement ?? false,
          can_see_merch:      preset?.can_see_merch       ?? false,
          can_see_contacts:   preset?.can_see_contacts    ?? true,
          can_see_advance:    preset?.can_see_advance     ?? true,
          can_edit_setlist:   preset?.can_edit_setlist    ?? false,  // SL-1
          ...updates,
        };
        const { data, error } = await supabase.from('tour_memberships')
          .insert(insertPayload).select().single();
        if (error) console.error('[ADVANCD] saveTourMembership insert:', error);
        else result = data;
      }
    }

    if (result) {
      // Reconcile optimistic state with the authoritative DB response
      setTourMemberships(prev => {
        const idx = prev.findIndex(m => m.profile_id === profileId && m.tour_id === selT);
        return idx >= 0
          ? prev.map(m => (m.profile_id === profileId && m.tour_id === selT) ? result : m)
          : [...prev, result];
      });
    } else {
      // Write failed — revert by reloading from DB so the UI reflects reality
      console.warn('[ADVANCD] saveTourMembership failed — reverting to DB state');
      loadTourMemberships(selT);
    }
  };

  // ── SL-1: Setlists (one row per set per show) ─────────────────────────────
  const loadShowSetlists = async (tourId) => {
    if (!tourId) return;
    const { data, error } = await supabase.from('show_setlists').select('*').eq('tour_id', tourId);
    if (error) { console.error('[ADVANCD] loadShowSetlists:', error); return; }
    setShowSetlists((data || []).sort((a, b) => (a.set_number || 0) - (b.set_number || 0)));
  };

  // ── SR-1: Show reports ────────────────────────────────────────────────────
  const loadShowReports = async (tourId) => {
    if (!tourId) return;
    const { data, error } = await supabase.from('show_reports').select('*').eq('tour_id', tourId).order('submitted_at', { ascending: true });
    if (error) { console.error('[ADVANCD] loadShowReports:', error); return; }
    setShowReports(data || []);
  };

  // Submit or update a show report. Optimistic insert fires immediately.
  const saveShowReport = async ({ showId, tourId: tId, profileId, roleCategory, responses }) => {
    if (!showId || !tId || !teamId) return false;
    const existing = showReports.find(r => r.show_id === showId && r.profile_id === (profileId || null) && r.role_category === roleCategory);
    if (existing) {
      const updated = { ...existing, responses, submitted_at: new Date().toISOString() };
      setShowReports(prev => prev.map(r => r.id === existing.id ? updated : r));
      const { error } = await supabase.from('show_reports').update({ responses, submitted_at: updated.submitted_at }).eq('id', existing.id);
      if (error) { console.error('[ADVANCD] saveShowReport update:', error); setShowReports(prev => prev.map(r => r.id === existing.id ? existing : r)); return false; }
    } else {
      const id = uid();
      const row = { id, show_id: showId, tour_id: tId, team_id: teamId, profile_id: profileId || null, role_category: roleCategory, responses, submitted_at: new Date().toISOString() };
      setShowReports(prev => [...prev, row]);
      const { error } = await supabase.from('show_reports').insert(row);
      if (error) { console.error('[ADVANCD] saveShowReport insert:', error); setShowReports(prev => prev.filter(r => r.id !== id)); return false; }
      // Best-effort venue CRM attachment
      try {
        const allShows = (data?.artists || []).flatMap(a => (a.tours || []).flatMap(t => t.shows || []));
        const showObj = allShows.find(s => s.id === showId);
        const venueId = showObj?.venueId;
        if (venueId) {
          const venue = venues[venueId];
          const existingVr = Array.isArray(venue?.show_reports) ? venue.show_reports : [];
          const entry = { show_id: showId, show_date: showObj.date, role_category: roleCategory, submitted_at: row.submitted_at };
          const updatedVr = [...existingVr.filter(x => !(x.show_id === showId && x.role_category === roleCategory)), entry];
          await supabase.from('venues').update({ show_reports: updatedVr }).eq('id', venueId);
        }
      } catch (e) { /* non-critical */ }
    }
    return true;
  };

  // Load historical show reports for every past show at a given venue (cross-tour).
  // Results are cached by venueId so repeated tab switches don't re-fetch.
  const loadVenueHistory = async (venueId) => {
    if (!venueId || !teamId) return;
    if (venueHistoryCache[venueId] && !venueHistoryCache[venueId].loading) return; // already loaded
    setVenueHistoryCache(prev => ({ ...prev, [venueId]: { loading: true, items: prev[venueId]?.items || [] } }));
    // Gather all show IDs at this venue across all artists/tours
    const allShows = (data?.artists || []).flatMap(a => a.tours.flatMap(t => t.shows || []));
    const venueShows = allShows.filter(s => s.venueId === venueId).sort((a, b) => b.date.localeCompare(a.date));
    if (!venueShows.length) {
      setVenueHistoryCache(prev => ({ ...prev, [venueId]: { loading: false, items: [] } }));
      return;
    }
    const showIds = venueShows.map(s => s.id);
    const { data: rData, error } = await supabase
      .from('show_reports').select('*').in('show_id', showIds).eq('team_id', teamId)
      .order('submitted_at', { ascending: false });
    if (error) {
      console.error('[ADVANCD] loadVenueHistory:', error);
      setVenueHistoryCache(prev => ({ ...prev, [venueId]: { loading: false, items: [] } }));
      return;
    }
    const reports = rData || [];
    // Build items: one entry per show that has at least one report
    const items = venueShows
      .filter(s => reports.some(r => r.show_id === s.id))
      .map(s => ({ show: s, reports: reports.filter(r => r.show_id === s.id) }));
    setVenueHistoryCache(prev => ({ ...prev, [venueId]: { loading: false, items } }));
  };

  // Trigger venue history load whenever the selected show changes
  useEffect(() => {
    if (!selS || !teamId || memberRole) return;
    const artist_ = data.artists.find(a => a.id === selA);
    const tour_   = artist_?.tours.find(t => t.id === selT);
    const show_   = tour_?.shows.find(s => s.id === selS);
    const vid     = show_?.venueId;
    if (vid) loadVenueHistory(vid);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selS, teamId]);

  // Upsert a full setlist row (the whole songs array goes in one write).
  // payload: { showId, tourId, setNumber, songs }
  //
  // Optimistic local update fires immediately; the DB write is debounced ~300ms
  // per (show, set_number) so rapid typing collapses into a single round-trip.
  // After the DB write returns we DO NOT replace the local songs array — the user
  // may have typed more characters since the write was queued. We only patch id
  // (for inserts) and updated_at so realtime echoes can be deduped.
  const saveShowSetlist = ({ showId, tourId, setNumber, songs }) => {
    if (!showId || !tourId || !teamId) return;
    const key = `${showId}::${setNumber}`;

    // 1. Optimistic local update — immediate
    setShowSetlists(prev => {
      const idx = prev.findIndex(s => s.show_id === showId && s.set_number === setNumber);
      if (idx >= 0) {
        return prev.map(s => (s.show_id === showId && s.set_number === setNumber)
          ? { ...s, songs: songs || [] }
          : s);
      }
      return [...prev, {
        id:         `tmp_${showId}_${setNumber}`,
        show_id:    showId,
        tour_id:    tourId,
        team_id:    teamId,
        set_number: setNumber,
        songs:      songs || [],
        updated_by: user?.id || null,
        updated_at: new Date().toISOString(),
      }];
    });

    // 2. Debounced DB write
    const existingTimer = setlistWriteTimersRef.current.get(key);
    if (existingTimer) clearTimeout(existingTimer);

    const timerId = setTimeout(async () => {
      setlistWriteTimersRef.current.delete(key);
      // Read the latest state — by the time the timer fires, the user may have
      // typed several more characters into the same set.
      const latest = showSetlistsRef.current.find(s => s.show_id === showId && s.set_number === setNumber);
      if (!latest) return; // row may have been deleted between schedule and fire

      const isPersisted = latest.id && !String(latest.id).startsWith('tmp_');

      if (isPersisted) {
        const { data, error } = await supabase.from('show_setlists')
          .update({ songs: latest.songs, updated_by: user?.id || null })
          .eq('id', latest.id).select('id,updated_at').single();
        if (error) { console.error('[ADVANCD] saveShowSetlist update:', error); return; }
        // Only refresh updated_at; do NOT replace songs (user may have typed more since).
        setShowSetlists(prev => prev.map(s => s.id === latest.id ? { ...s, updated_at: data.updated_at } : s));
      } else {
        const insertRow = {
          show_id:    showId,
          tour_id:    tourId,
          team_id:    teamId,
          set_number: setNumber,
          songs:      latest.songs,
          updated_by: user?.id || null,
        };
        const { data, error } = await supabase.from('show_setlists').insert(insertRow).select('id,updated_at').single();
        if (error) {
          console.error('[ADVANCD] saveShowSetlist insert:', error);
          setShowSetlists(prev => prev.filter(s => !(s.show_id === showId && s.set_number === setNumber && String(s.id).startsWith('tmp_'))));
          return;
        }
        // Replace tmp id with real id + updated_at; keep current local songs intact.
        setShowSetlists(prev => prev.map(s =>
          (s.show_id === showId && s.set_number === setNumber && String(s.id).startsWith('tmp_'))
            ? { ...s, id: data.id, updated_at: data.updated_at }
            : s
        ));
      }
    }, 300);

    setlistWriteTimersRef.current.set(key, timerId);
  };

  const deleteShowSetlist = async (setlistId) => {
    if (!setlistId) return;
    const target = showSetlists.find(s => s.id === setlistId);
    setShowSetlists(prev => prev.filter(s => s.id !== setlistId));
    if (target && !String(setlistId).startsWith('tmp_')) {
      const { error } = await supabase.from('show_setlists').delete().eq('id', setlistId);
      if (error) { console.error('[ADVANCD] deleteShowSetlist:', error); loadShowSetlists(selT); }
    }
  };

  // ── SL-2: Setlist templates (named, per-artist) ──────────────────────────
  const loadSetlistTemplates = async (tid) => {
    if (!tid) return;
    const { data, error } = await supabase.from('setlist_templates')
      .select('*').eq('team_id', tid).order('created_at', { ascending: false });
    if (error) { console.error('[ADVANCD] loadSetlistTemplates:', error); return; }
    setSetlistTemplates(data || []);
  };

  // Capture all sets currently on a given show into a new template for an artist.
  const saveSetlistTemplate = async ({ name, artistId, showId }) => {
    if (!name || !artistId || !showId || !teamId) return null;
    // Snapshot the show's sets, sorted by set_number, regenerating song ids so
    // they don't collide with the live show's ids when the template is applied.
    const sets = showSetlists
      .filter(s => s.show_id === showId)
      .sort((a, b) => (a.set_number || 0) - (b.set_number || 0))
      .map(s => ({
        set_number: s.set_number,
        songs: (s.songs || []).map(song => ({
          ...song,
          id: `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 5)}`,
        })),
      }));

    const { data, error } = await supabase.from('setlist_templates').insert({
      team_id:    teamId,
      artist_id:  artistId,
      name:       name.trim(),
      sets,
      created_by: user?.id || null,
    }).select().single();

    if (error) { console.error('[ADVANCD] saveSetlistTemplate:', error); return null; }
    setSetlistTemplates(prev => [data, ...prev]);
    return data;
  };

  const renameSetlistTemplate = async (id, newName) => {
    if (!id || !newName?.trim()) return;
    setSetlistTemplates(prev => prev.map(t => t.id === id ? { ...t, name: newName.trim() } : t));
    const { error } = await supabase.from('setlist_templates')
      .update({ name: newName.trim() }).eq('id', id);
    if (error) { console.error('[ADVANCD] renameSetlistTemplate:', error); loadSetlistTemplates(teamId); }
  };

  const deleteSetlistTemplate = async (id) => {
    if (!id) return;
    setSetlistTemplates(prev => prev.filter(t => t.id !== id));
    const { error } = await supabase.from('setlist_templates').delete().eq('id', id);
    if (error) { console.error('[ADVANCD] deleteSetlistTemplate:', error); loadSetlistTemplates(teamId); }
  };

  // Apply a template's sets to a show. mode is "replace" (delete existing first)
  // or "append" (shift set_numbers past the highest existing one). Bypasses the
  // typing-write debounce — these are direct, immediate writes.
  const applySetlistTemplate = async ({ template, showId, tourId, mode }) => {
    if (!template || !showId || !tourId || !teamId) return;

    const existingForShow = showSetlists.filter(s => s.show_id === showId);

    if (mode === 'replace' && existingForShow.length) {
      // Optimistic local clear, then DB delete in bulk
      setShowSetlists(prev => prev.filter(s => s.show_id !== showId));
      const ids = existingForShow
        .map(s => s.id)
        .filter(id => id && !String(id).startsWith('tmp_'));
      if (ids.length) {
        const { error } = await supabase.from('show_setlists').delete().in('id', ids);
        if (error) console.error('[ADVANCD] applySetlistTemplate delete:', error);
      }
    }

    const offset = mode === 'append'
      ? Math.max(0, ...existingForShow.map(s => s.set_number || 0))
      : 0;

    const sets = Array.isArray(template.sets) ? template.sets : [];
    const inserts = sets.map((ts, i) => ({
      show_id:    showId,
      tour_id:    tourId,
      team_id:    teamId,
      set_number: offset + (ts.set_number || (i + 1)),
      // Regenerate song ids so React keys stay unique even if the template is
      // applied multiple times to the same show.
      songs: (ts.songs || []).map(song => ({
        ...song,
        id: `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 5)}_${i}`,
      })),
      updated_by: user?.id || null,
    }));

    if (!inserts.length) return;

    const { data, error } = await supabase.from('show_setlists').insert(inserts).select();
    if (error) { console.error('[ADVANCD] applySetlistTemplate insert:', error); loadShowSetlists(tourId); return; }

    setShowSetlists(prev => [...prev, ...(data || [])]);
    // Jump the active set to the first newly applied set so the UI reflects it
    if (data?.length) setActiveSetlistSetNum(data[0].set_number);
  };

  // Realtime handler for setlist_templates — declared with the others below for
  // lexical-order safety, but the function body lives here so the related logic
  // is co-located. (See handleSetlistTemplateChange declaration further down.)

  const loadProfilePrivate = async (profileId) => {
    if (!profileId) return null;
    if (profilePrivate[profileId]) return profilePrivate[profileId];
    const { data } = await supabase.from('people_private').select('*').eq('profile_id', profileId).maybeSingle();
    if (data) setProfilePrivate(p => ({ ...p, [profileId]: data }));
    return data || null;
  };

  const loadNetworkProfiles = async () => {
    if (networkLoading) return;
    setNetworkLoading(true);
    const [{ data: profileData }, { data: connData }] = await Promise.all([
      supabase.from('people_profiles').select('*, teams(name)').eq('visible_to_network', true).order('name'),
      supabase.from('network_connections').select('profile_id').eq('team_id', teamId),
    ]);
    if (profileData) setNetworkProfiles(profileData.filter(p => p.team_id !== teamId));
    if (connData)   setConnections(new Set(connData.map(c => c.profile_id)));
    setNetworkLoading(false);
    setNetworkLoaded(true);
  };

  const toggleConnection = async (profileId) => {
    if (!teamId) return;
    const connected = connections.has(profileId);
    if (connected) {
      await supabase.from('network_connections').delete().eq('team_id', teamId).eq('profile_id', profileId);
      setConnections(prev => { const n = new Set(prev); n.delete(profileId); return n; });
    } else {
      await supabase.from('network_connections').insert({ team_id: teamId, profile_id: profileId });
      setConnections(prev => new Set([...prev, profileId]));
    }
  };

  // Toggle a network connection for an invited band/crew member (keyed by user_id, no team_id).
  const toggleMemberConnection = async (profileId) => {
    if (!user?.id) return;
    if (memberConnections.has(profileId)) {
      setMemberConnections(prev => { const n = new Set(prev); n.delete(profileId); return n; });
      await supabase.from('network_connections').delete().eq('user_id', user.id).eq('profile_id', profileId);
    } else {
      setMemberConnections(prev => new Set([...prev, profileId]));
      await supabase.from('network_connections').insert({ user_id: user.id, profile_id: profileId });
    }
  };

  // Upsert the band/crew member's own people_profile row.
  // Keyed on user_id — they have no team_id.
  // Returns { error } or { data }.
  const saveMemberProfile = async (profileData) => {
    if (!user?.id) return { error: "Not logged in" };
    // Upsert by user_id — if a row exists, update it; otherwise insert.
    const existing = memberOwnProfile?.id;
    let result;
    if (existing) {
      // IMPORTANT: never touch team_id on update — a member's own profile may be
      // a CLAIMED TEAM PROFILE (team_id set); forcing team_id: null here would
      // silently strip them off their TM's roster. Partial patches only.
      result = await supabase.from('people_profiles').update({ ...profileData, user_id: user.id }).eq('id', existing).select().single();
    } else {
      // Self-created profile: no team.
      result = await supabase.from('people_profiles').insert({ ...profileData, user_id: user.id, team_id: null }).select().single();
    }
    if (!result.error && result.data) {
      setMemberOwnProfile(result.data);
    }
    return result;
  };

  const saveProfile = async (profileData, privateData) => {
    if (!mProfile) return;
    const mode = mProfile.mode;

    if (profileData === null) {
      // Delete
      const pid = mProfile.profile?.id;
      if (pid) {
        await supabase.from('people_profiles').delete().eq('id', pid);
        setProfiles(p => p.filter(x => x.id !== pid));
        setProfilePrivate(p => { const n = { ...p }; delete n[pid]; return n; });
      }
      setMProfile(null);
      return;
    }

    // Normalize empty strings to null for artist_id
    const normalizeArtistId = (val) => (val && val.trim() ? val.trim() : null);

    const row = {
      ...profileData,
      team_id: teamId,
      user_id: null,  // null until member claims the profile via invite link
      // Preserve artist_id from context (e.g. adding from artist Roster tab) or from the profile itself
      artist_id: normalizeArtistId(profileData.artist_id) || mProfile.context?.artistId || null,
    };
    let profileId = mProfile.profile?.id;

    if (mode === 'add') {
      const { data, error } = await supabase.from('people_profiles').insert(row).select().single();
      if (error) { console.error('[ADVANCD] saveProfile insert:', error); return; }
      profileId = data.id;
      // Guard against Realtime INSERT firing before this response returns (race condition):
      // if the profile is already in state (added by Realtime), update it in place with the
      // authoritative REST response data rather than appending a duplicate.
      setProfiles(p => {
        const exists = p.some(x => x.id === data.id);
        if (exists) return p.map(x => x.id === data.id ? data : x);
        return [...p, data].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      });
      // Auto-add to tour if created from within a tour context (e.g. Crew Roster tab)
      if (mProfile.context?.tourId) {
        await addProfileToTour(profileId, mProfile.context.tourId);
      }
    } else {
      // On edit, normalize artist_id and preserve it from the existing profile if not set
      const updateRow = {
        ...profileData,
        artist_id: normalizeArtistId(profileData.artist_id) ?? (mProfile.profile?.artist_id || null),
      };
      const { data, error } = await supabase.from('people_profiles').update(updateRow).eq('id', profileId).select().single();
      if (error) { console.error('[ADVANCD] saveProfile update:', error); return; }
      setProfiles(p => p.map(x => x.id === profileId ? data : x));
    }

    // Upsert private data
    if (privateData && profileId) {
      const privRow = { ...privateData, profile_id: profileId, team_id: teamId };
      const existing = await supabase.from('people_private').select('id').eq('profile_id', profileId).maybeSingle();
      if (existing?.data?.id) {
        await supabase.from('people_private').update(privateData).eq('profile_id', profileId);
      } else {
        await supabase.from('people_private').insert(privRow);
      }
      setProfilePrivate(p => ({ ...p, [profileId]: { ...(p[profileId] || {}), ...privateData } }));
    }

    setMProfile(null);
  };

  const addProfileToTour = async (profileId, tourId) => {
    const { data, error } = await supabase.from('people_profiles')
      .update({ current_tour_id: tourId })
      .eq('id', profileId).select().single();
    if (!error && data) {
      setProfiles(p => p.map(x => x.id === profileId ? data : x));
      // PP-1 Phase 3: create membership row with role-based permission defaults
      const preset = TOUR_ROLE_PRESETS.find(r => r.id === data.role_category) || TOUR_ROLE_PRESETS.find(r => r.id === 'crew');
      const membershipPayload = {
        profile_id:          profileId,
        tour_id:             tourId,
        team_id:             teamId,
        tour_role:           preset.id,
        can_see_guarantee:   preset.can_see_guarantee,
        can_see_settlement:  preset.can_see_settlement,
        can_see_merch:       preset.can_see_merch,
        can_see_contacts:    preset.can_see_contacts,
        can_see_advance:     preset.can_see_advance,
      };
      // Check if membership row already exists before inserting to avoid conflicts
      const { data: existing } = await supabase.from('tour_memberships')
        .select('id').eq('profile_id', profileId).eq('tour_id', tourId).maybeSingle();
      let mem = null;
      if (existing) {
        // Row exists — update permissions to match preset
        const { data: updated } = await supabase.from('tour_memberships')
          .update(membershipPayload).eq('profile_id', profileId).eq('tour_id', tourId).select().single();
        mem = updated;
      } else {
        const { data: inserted } = await supabase.from('tour_memberships')
          .insert(membershipPayload).select().single();
        mem = inserted;
      }
      if (mem) setTourMemberships(prev => {
        const idx = prev.findIndex(m => m.profile_id === profileId && m.tour_id === tourId);
        return idx >= 0 ? prev.map(m => (m.profile_id === profileId && m.tour_id === tourId) ? mem : m) : [...prev, mem];
      });
    }
  };

  const removeProfileFromTour = async (profileId) => {
    const { data, error } = await supabase.from('people_profiles')
      .update({ current_tour_id: null })
      .eq('id', profileId).select().single();
    if (!error && data) {
      setProfiles(p => p.map(x => x.id === profileId ? data : x));
      // PP-1 Phase 3: remove membership row
      await supabase.from('tour_memberships').delete().eq('profile_id', profileId).eq('tour_id', selT);
      setTourMemberships(prev => prev.filter(m => m.profile_id !== profileId));
    }
  };

  // saveCrewMember retired — PP-1 Phase 2

  const loadDayFlights = async (dayId) => {
    if (!dayId || dayFlights[dayId]) return; // already loaded
    const { data, error } = await supabase.from('flights').select('*').eq('tour_day_id', dayId).order('scheduled_dep');
    if (!error && data) setDayFlights(p => ({ ...p, [dayId]: data }));
  };

  const saveFlight = async (payload) => {
    const { dayId } = mFlight;
    if (!payload) {
      await supabase.from('flights').delete().eq('id', mFlight.flight.id);
      setDayFlights(p => ({ ...p, [dayId]: (p[dayId] || []).filter(f => f.id !== mFlight.flight.id) }));
    } else if (mFlight.mode === 'add') {
      const row = { ...payload, tour_day_id: dayId, user_id: user.id, team_id: teamId || null };
      const { data, error } = await supabase.from('flights').insert(row).select().single();
      if (!error && data) setDayFlights(p => ({ ...p, [dayId]: [...(p[dayId] || []), data] }));
    } else {
      const { data, error } = await supabase.from('flights').update(payload).eq('id', mFlight.flight.id).select().single();
      if (!error && data) setDayFlights(p => ({ ...p, [dayId]: (p[dayId] || []).map(f => f.id === data.id ? data : f) }));
    }
    setMFlight(null);
  };

  const loadVehicles = async (tourId) => {
    if (!tourId) return;
    const { data, error } = await supabase.from('vehicles').select('*').eq('tour_id', tourId).order('created_at');
    if (!error && data) setVehicles(data);
  };

  // SC-1: Daily calendar functions
  const loadDayScheduleItems = async (tourId) => {
    if (!tourId) return;
    const { data } = await supabase.from('day_schedule_items').select('*').eq('tour_id', tourId).order('start_minutes');
    if (data) setDayScheduleItems(data);
  };

  const saveDayScheduleItem = async (payload) => {
    const isEdit = !!(payload.id);
    const item = isEdit ? payload : { id: "sc" + uid(), tour_id: selT, team_id: teamId, ...payload };
    const { data, error } = await supabase.from('day_schedule_items')
      .upsert(item, { onConflict: 'id' }).select().single();
    if (!error && data) {
      setDayScheduleItems(prev =>
        isEdit ? prev.map(x => x.id === data.id ? data : x) : [...prev, data]
      );
    }
    setMSchedItem(null);
  };

  const deleteDayScheduleItem = async (id) => {
    await supabase.from('day_schedule_items').delete().eq('id', id);
    setDayScheduleItems(prev => prev.filter(x => x.id !== id));
  };

  // SC-1+: Import time-tagged advance fields into day_schedule_items for TM curation
  const importAdvanceToSchedule = async (tourDayId, show, day) => {
    if (!tourDayId || !show) return;
    setImportingAdvSched(tourDayId);
    const adTm   = show.advanceTmData || {};
    const sch    = adTm.schedule            || {};
    const bus    = adTm.bus                 || {};
    const gt     = adTm.groundTransportAdv  || {};
    const bill   = Array.isArray(sch.bill)        ? sch.bill        : [];
    const cos    = Array.isArray(sch.changeovers) ? sch.changeovers : [];

    // Existing source_keys for this day — skip any we already imported
    const existingKeys = new Set(
      dayScheduleItems
        .filter(i => i.tour_day_id === tourDayId && i.source_key)
        .map(i => i.source_key)
    );

    const toInsert = [];
    // Times that parse to before 6 AM are treated as next-day (post-midnight) —
    // store as 1440+ so they sort after all evening show times
    const lateNightMins = (m) => (m != null && m < 360) ? m + 1440 : m;
    const addItem = (sourceKey, title, timeStr, type = "production", durationMins = 0) => {
      if (!timeStr || existingKeys.has(sourceKey)) return;
      const m = lateNightMins(parseDisplayTime(timeStr));
      if (m == null) return;
      toInsert.push({
        id: "sc" + uid(),
        tour_day_id: tourDayId,
        tour_id: selT,
        team_id: teamId,
        title,
        type,
        start_minutes: m,
        duration_minutes: durationMins,
        assigned_profile_ids: [],
        notes: "",
        source_key: sourceKey,
      });
    };

    // ── Bus call (tour_day level field) ──────────────────────────────────────
    if (day?.bus_call) addItem("adv_day_bus_call", "Bus Call", day.bus_call, "production");

    // ── Schedule section (venue-level times) ──────────────────────────────────
    addItem("adv_schedule_venue_access",  "Venue Access",               sch.venue_access,  "production");
    addItem("adv_schedule_staging_build", "Staging / Build",            sch.staging_build, "production");
    addItem("adv_schedule_doors_public",  "Doors to Public",            sch.doors_public,  "production");
    addItem("adv_schedule_vip_access",    "VIP Access",                 sch.vip_access,    "production");
    addItem("adv_schedule_noise_curfew",  "Noise Curfew",               sch.noise_curfew,  "production");
    addItem("adv_schedule_gr_curfew",     "Green Room Curfew",          sch.gr_curfew,     "production");
    addItem("adv_schedule_curfew",        "Hard Curfew",                sch.curfew,        "production");

    // ── Bus / logistics section ───────────────────────────────────────────────
    addItem("adv_bus_bus_arrival",  "Bus Arrival",                  bus.bus_arrival,   "production");
    addItem("adv_bus_shore_power",  "Shore Power Access",           bus.shore_power,   "production");
    addItem("adv_bus_prod_hold",    "Production Hold",              bus.prod_hold,     "production");
    addItem("adv_bus_doors_prod",   "Venue Doors to Production",    bus.doors_prod,    "production");
    addItem("adv_bus_catering",     "Catering Ready",               bus.catering_time, "catering");

    // ── Ground transport section ──────────────────────────────────────────────
    addItem("adv_gt_pickup",      "Ground Transport Pickup",    gt.gt_pickup_time,   "production");
    addItem("adv_gt_dropoff",     "Ground Transport Drop-off",  gt.gt_dropoff_time,  "production");
    addItem("adv_gt_post_pickup", "Post-show Pickup",           gt.gt_post_pickup,   "production");

    // ── Bill acts ─────────────────────────────────────────────────────────────
    bill.forEach((act, idx) => {
      if (!act.name) return;
      addItem(`adv_bill_${idx}_load`, `${act.name} · Load-in`,   act.load,   "production");
      addItem(`adv_bill_${idx}_sc`,   `${act.name} · Soundcheck`, act.sc,    "production");
      if (act.set) {
        const dur = setDurMins(act.set, act.setEnd);
        addItem(`adv_bill_${idx}_set`, `${act.name} · Set`, act.set, "production", dur || 0);
      }
    });

    // ── Changeovers ───────────────────────────────────────────────────────────
    cos.forEach((co, idx) => {
      if (!co.time) return;
      const dur = co.timeEnd ? setDurMins(co.time, co.timeEnd) : null;
      addItem(`adv_co_${idx}`, co.label || "Changeover", co.time, "production", dur || 0);
    });

    if (toInsert.length === 0) {
      setImportingAdvSched(null);
      return;
    }

    const { data, error } = await supabase.from('day_schedule_items').insert(toInsert).select();
    if (!error && data) {
      setDayScheduleItems(prev =>
        [...prev, ...data].sort((a, b) => a.start_minutes - b.start_minutes)
      );
    }
    setImportingAdvSched(null);
  };

  const saveVehicle = async (payload) => {
    if (!payload) {
      await supabase.from('vehicles').delete().eq('id', mVehicle.vehicle.id);
      setVehicles(p => p.filter(v => v.id !== mVehicle.vehicle.id));
    } else if (mVehicle.mode === 'add') {
      const row = { ...payload, tour_id: tour.id, user_id: user.id, team_id: teamId || null };
      const { data, error } = await supabase.from('vehicles').insert(row).select().single();
      if (!error && data) setVehicles(p => [...p, data]);
    } else {
      const { data, error } = await supabase.from('vehicles').update(payload).eq('id', mVehicle.vehicle.id).select().single();
      if (!error && data) setVehicles(p => p.map(v => v.id === data.id ? data : v));
    }
    setMVehicle(null);
  };

  const loadDayTransport = async (dayId) => {
    if (!dayId || dayTransport[dayId]) return; // already loaded
    const { data, error } = await supabase.from('ground_transport').select('*').eq('tour_day_id', dayId).order('pickup_time');
    if (!error && data) setDayTransport(p => ({ ...p, [dayId]: data }));
  };

  const saveTransport = async (payload) => {
    const { dayId } = mTransport;
    if (!payload) {
      await supabase.from('ground_transport').delete().eq('id', mTransport.transport.id);
      setDayTransport(p => ({ ...p, [dayId]: (p[dayId] || []).filter(t => t.id !== mTransport.transport.id) }));
    } else if (mTransport.mode === 'add') {
      const row = { ...payload, tour_day_id: dayId, user_id: user.id, team_id: teamId || null };
      const { data, error } = await supabase.from('ground_transport').insert(row).select().single();
      if (!error && data) setDayTransport(p => ({ ...p, [dayId]: [...(p[dayId] || []), data] }));
    } else {
      const { data, error } = await supabase.from('ground_transport').update(payload).eq('id', mTransport.transport.id).select().single();
      if (!error && data) setDayTransport(p => ({ ...p, [dayId]: (p[dayId] || []).map(t => t.id === data.id ? data : t) }));
    }
    setMTransport(null);
  };

  const loadDayWeather = async (dayId, city, date) => {
    if (!city || !date || dayWeather[dayId]) return;
    // OpenWeatherMap free tier only covers ~5 days out
    const today    = new Date(); today.setHours(0,0,0,0);
    const dayDate  = new Date(date + 'T12:00:00');
    const daysOut  = Math.round((dayDate - today) / 86400000);
    if (daysOut > 5) {
      const available = dayDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      setDayWeather(p => ({ ...p, [dayId]: { status: 'future', available } }));
      return;
    }
    if (daysOut < 0) { setDayWeather(p => ({ ...p, [dayId]: { status: 'past' } })); return; }
    setDayWeather(p => ({ ...p, [dayId]: { status: 'loading' } }));
    try {
      const r    = await fetch(`/api/weather?city=${encodeURIComponent(city)}&date=${date}`);
      const data = await r.json();
      if (data.error === 'no_forecast') setDayWeather(p => ({ ...p, [dayId]: { status: 'unavailable' } }));
      else if (data.error)              setDayWeather(p => ({ ...p, [dayId]: { status: 'error', msg: data.error } }));
      else                              setDayWeather(p => ({ ...p, [dayId]: { status: 'ready', ...data } }));
    } catch {
      setDayWeather(p => ({ ...p, [dayId]: { status: 'error' } }));
    }
  };

  const loadDayLodging = async (dayId) => {
    if (!dayId || dayLodging[dayId]) return;
    const { data, error } = await supabase.from('lodging').select('*').eq('tour_day_id', dayId).order('created_at');
    if (!error && data) setDayLodging(p => ({ ...p, [dayId]: data }));
  };

  // ── FT-1: Financials load / save / delete ──────────────────────────────────
  const loadTourFinancials = async (tourId) => {
    if (!tourId || !teamId) return;
    setFinLoading(true);
    const [{ data: expData }, { data: budData }, { data: merchData }] = await Promise.all([
      supabase.from("tour_expenses").select("*").eq("tour_id", tourId).order("date", { ascending: true }),
      supabase.from("tour_budget").select("*").eq("tour_id", tourId),
      supabase.from("show_merch").select("*").eq("tour_id", tourId),
    ]);
    if (expData)  setTourExpenses(expData);
    if (budData)  setTourBudget(budData);
    if (merchData) setShowMerch(merchData);
    setFinLoading(false);
  };

  const saveTourExpense = async (rec) => {
    if (rec.id) {
      const { data, error } = await supabase.from("tour_expenses").update(rec).eq("id", rec.id).select().single();
      if (!error && data) {
        setTourExpenses(prev => prev.map(e => e.id === data.id ? data : e));
      }
    } else {
      const { data, error } = await supabase.from("tour_expenses").insert(rec).select().single();
      if (!error && data) {
        setTourExpenses(prev => [...prev, data]);
      }
    }
    setMExpense(null);
  };

  // ── Chat v2 action registry ─────────────────────────────────────────────────
  // Commits chat-confirmed actions through the SAME functions the UI uses
  // (optimistic updates, Realtime, activity log). The model only proposes;
  // the server only validates. Shared by desktop ChatPanel and mobile (TM).
  const executeChatAction = async (action) => {
    try {
      switch (action.tool) {
        case "set_show_status": {
          const { show_id, status } = action.input;
          const { error } = await supabase.from("shows").update({ status }).eq("id", show_id);
          if (error) throw error;
          setData(p => ({ ...p, artists: p.artists.map(a => ({ ...a,
            tours: a.tours.map(t => ({ ...t,
              shows: t.shows.map(s => s.id === show_id ? { ...s, status } : s)
            }))
          })) }));
          return { ok: true, message: `status set to ${status}.` };
        }
        case "send_broadcast": {
          const { tour_id, tour_day_id, body, audience } = action.input;
          const senderProfile = profiles.find(p => p.user_id === user?.id);
          const newB = {
            id: Date.now().toString(36) + Math.random().toString(36).slice(2),
            tour_id, team_id: teamId,
            sender_name: senderProfile?.name || "Tour Manager",
            body, audience: audience || "all",
            tour_day_id: tour_day_id || null,
            created_at: new Date().toISOString(),
          };
          setBroadcasts(prev => [newB, ...prev]);
          const { error } = await supabase.from("tour_broadcasts").insert(newB);
          if (error) { setBroadcasts(prev => prev.filter(b => b.id !== newB.id)); throw error; }
          return { ok: true, message: `broadcast sent to ${audience || "all"}.` };
        }
        case "flag_advance_section": {
          const { show_id, section_id, note } = action.input;
          flagAdvanceSection(show_id, section_id, note || "");
          return { ok: true, message: "section flagged — the promoter will see it in the portal." };
        }
        case "add_schedule_item": {
          const { tour_id, tour_day_id, title, type, start_minutes, duration_minutes, notes } = action.input;
          await saveDayScheduleItem({
            tour_id, tour_day_id, title, type: type || "custom",
            start_minutes, duration_minutes: duration_minutes || null,
            assigned_profile_ids: [], notes: notes || "",
          });
          return { ok: true, message: "schedule item added." };
        }
        case "record_expense": {
          const i = action.input;
          await saveTourExpense({
            tour_id: i.tour_id, team_id: teamId,
            category: i.category || "misc", description: i.description,
            amount: Number(i.amount) || 0, currency: i.currency || "USD",
            date: i.date || null, notes: i.notes || null,
          });
          return { ok: true, message: `${i.currency || "USD"} ${Number(i.amount).toLocaleString()} expense recorded.` };
        }
        case "set_show_promoter": {
          const { show_id, venue_id, patch } = action.input;
          const showObj = data.artists.flatMap(a => a.tours).flatMap(t => t.shows).find(s => s.id === show_id);
          if (!showObj) throw new Error("Show not found");
          const settlement = { ...(showObj.settlement || {}), ...patch };
          const { error } = await supabase.from("shows").update({ settlement }).eq("id", show_id);
          if (error) throw error;
          setData(p => ({ ...p, artists: p.artists.map(a => ({ ...a,
            tours: a.tours.map(t => ({ ...t,
              shows: t.shows.map(s => s.id === show_id ? { ...s, settlement } : s)
            }))
          })) }));
          if (venue_id) {
            const vPatch = {};
            if (patch.promoterName)  vPatch.promoter_name  = patch.promoterName;
            if (patch.promoterEmail) vPatch.promoter_email = patch.promoterEmail;
            if (patch.promoterPhone) vPatch.promoter_phone = patch.promoterPhone;
            const { error: vErr } = await supabase.from("venues").update(vPatch).eq("id", venue_id);
            if (vErr) return { ok: true, message: "promoter set on the show, but the venue record update failed." };
            setVenues(prev => prev[venue_id]
              ? { ...prev, [venue_id]: { ...prev[venue_id], promoterName: patch.promoterName ?? prev[venue_id].promoterName, promoterEmail: patch.promoterEmail ?? prev[venue_id].promoterEmail, promoterPhone: patch.promoterPhone ?? prev[venue_id].promoterPhone } }
              : prev);
            return { ok: true, message: "promoter set on the show and the venue record." };
          }
          return { ok: true, message: "promoter set on the show." };
        }
        case "record_settlement": {
          const { show_id, patch } = action.input;
          const showObj = data.artists.flatMap(a => a.tours).flatMap(t => t.shows).find(s => s.id === show_id);
          if (!showObj) throw new Error("Show not found");
          const cur = showObj.settlement || {};
          const settlement = {
            ...cur,
            ...patch,
            // Notes append rather than overwrite — settlement notes accumulate
            ...(patch.notes ? { notes: cur.notes ? `${cur.notes}\n${patch.notes}` : patch.notes } : {}),
          };
          const { error } = await supabase.from("shows").update({ settlement }).eq("id", show_id);
          if (error) throw error;
          setData(p => ({ ...p, artists: p.artists.map(a => ({ ...a,
            tours: a.tours.map(t => ({ ...t,
              shows: t.shows.map(s => s.id === show_id ? { ...s, settlement } : s)
            }))
          })) }));
          return { ok: true, message: patch.settled ? "show marked settled." : "settlement details recorded." };
        }
        case "add_guest_list_entries": {
          const { show_id, entries } = action.input;
          const showObj = data.artists.flatMap(a => a.tours).flatMap(t => t.shows).find(s => s.id === show_id);
          if (!showObj) throw new Error("Show not found");
          const newEntries = entries.map(e => ({
            id: crypto.randomUUID(),
            firstName: e.first_name, lastName: e.last_name || "",
            email: e.email || "", tickets: e.tickets || 1,
            access: [], note: e.note || "", confirmed: false,
          }));
          const tmD = { ...(showObj.advanceTmData || {}) };
          const gl = { ...(tmD.guestListSheet || {}) };
          gl.entries = [...(Array.isArray(gl.entries) ? gl.entries : []), ...newEntries];
          tmD.guestListSheet = gl;
          const { error } = await supabase.from("shows").update({ advance_tm_data: tmD }).eq("id", show_id);
          if (error) throw error;
          setData(p => ({ ...p, artists: p.artists.map(a => ({ ...a,
            tours: a.tours.map(t => ({ ...t,
              shows: t.shows.map(s => s.id === show_id ? { ...s, advanceTmData: tmD } : s)
            }))
          })) }));
          return { ok: true, message: `${newEntries.length} guest${newEntries.length === 1 ? "" : "s"} added to the list.` };
        }
        case "add_contacts": {
          const { entries } = action.input;
          const existing = data.contacts || [];
          const fresh = entries.filter(e =>
            !e.email || !existing.some(x => (x.email || "").toLowerCase() === e.email.toLowerCase()));
          if (fresh.length === 0) return { ok: true, message: "all of those contacts already exist (matched by email)." };
          const rows = fresh.map(e => ({
            id: "c" + uid(), user_id: user?.id, team_id: teamId,
            name: e.name, company: e.company || "", role: e.role || "",
            email: e.email || "", phone: e.phone || "", city: e.city || "", notes: e.notes || "",
          }));
          const { error } = await supabase.from("contacts").insert(rows);
          if (error) throw error;
          setData(p => ({ ...p, contacts: [...(p.contacts || []), ...rows] }));
          const skipped = entries.length - fresh.length;
          return { ok: true, message: `${rows.length} contact${rows.length === 1 ? "" : "s"} added${skipped > 0 ? ` (${skipped} already existed)` : ""}.` };
        }
        case "update_venue": {
          const { venue_id, patch_db, patch_app } = action.input;
          const { error } = await supabase.from("venues").update(patch_db).eq("id", venue_id);
          if (error) throw error;
          setVenues(prev => prev[venue_id]
            ? { ...prev, [venue_id]: { ...prev[venue_id], ...patch_app } }
            : prev);
          return { ok: true, message: `venue record updated (${Object.keys(patch_db).length} field${Object.keys(patch_db).length === 1 ? "" : "s"}).` };
        }
        case "update_deal": {
          const { show_id, patch } = action.input;
          const showObj = data.artists.flatMap(a => a.tours).flatMap(t => t.shows).find(s => s.id === show_id);
          if (!showObj) throw new Error("Show not found");
          const settlement = { ...(showObj.settlement || {}), ...patch };
          const { error } = await supabase.from("shows").update({ settlement }).eq("id", show_id);
          if (error) throw error;
          setData(p => ({ ...p, artists: p.artists.map(a => ({ ...a,
            tours: a.tours.map(t => ({ ...t,
              shows: t.shows.map(s => s.id === show_id ? { ...s, settlement } : s)
            }))
          })) }));
          return { ok: true, message: "deal terms updated." };
        }
        case "update_show_notes": {
          const { show_id, notes, mode } = action.input;
          const showObj = data.artists.flatMap(a => a.tours).flatMap(t => t.shows).find(s => s.id === show_id);
          if (!showObj) throw new Error("Show not found");
          const next = mode === "replace" ? notes
            : (showObj.notes ? `${showObj.notes}\n${notes}` : notes);
          const { error } = await supabase.from("shows").update({ notes: next }).eq("id", show_id);
          if (error) throw error;
          setData(p => ({ ...p, artists: p.artists.map(a => ({ ...a,
            tours: a.tours.map(t => ({ ...t,
              shows: t.shows.map(s => s.id === show_id ? { ...s, notes: next } : s)
            }))
          })) }));
          return { ok: true, message: mode === "replace" ? "show notes replaced." : "note added to the show." };
        }
        // update_advance_fields shares the accept_amendments write path —
        // both apply { section, field, value } updates to advance_tm_data.
        case "update_advance_fields":
        case "accept_amendments": {
          const { show_id, updates } = action.input;
          if (!Array.isArray(updates) || updates.length === 0) throw new Error("Nothing to accept");
          const showObj = data.artists.flatMap(a => a.tours).flatMap(t => t.shows).find(s => s.id === show_id);
          if (!showObj) throw new Error("Show not found");
          let tmD = { ...(showObj.advanceTmData || {}) };
          for (const u of updates) {
            tmD = { ...tmD, [u.section]: { ...(tmD[u.section] || {}), [u.field]: u.value } };
          }
          const { error } = await supabase.from("shows").update({ advance_tm_data: tmD }).eq("id", show_id);
          if (error) throw error;
          setData(p => ({ ...p, artists: p.artists.map(a => ({ ...a,
            tours: a.tours.map(t => ({ ...t,
              shows: t.shows.map(s => s.id === show_id ? { ...s, advanceTmData: tmD } : s)
            }))
          })) }));
          return { ok: true, message: `${updates.length} promoter answer${updates.length === 1 ? "" : "s"} accepted into your advance.` };
        }
        default:
          return { ok: false, message: `Unknown action: ${action.tool}` };
      }
    } catch (e) {
      return { ok: false, message: e.message || "Action failed" };
    }
  };

  const deleteTourExpense = async (id) => {
    await supabase.from("tour_expenses").delete().eq("id", id);
    setTourExpenses(prev => prev.filter(e => e.id !== id));
    setExpDeleteConfirm(null);
  };

  // ── FT-1b: Merch save / delete ─────────────────────────────────────────────
  const saveShowMerch = async (rec) => {
    const gross   = parseFloat(rec.gross)         || 0;
    const cutPct  = parseFloat(rec.venue_cut_pct) || 0;
    const net     = parseFloat((gross * (1 - cutPct / 100)).toFixed(2));
    const payload = { ...rec, gross, venue_cut_pct: cutPct, net };

    if (rec.id) {
      const { data, error } = await supabase
        .from("show_merch").update(payload).eq("id", rec.id).select().single();
      if (!error && data) setShowMerch(prev => prev.map(m => m.id === data.id ? data : m));
    } else {
      const { data, error } = await supabase
        .from("show_merch").insert(payload).select().single();
      if (!error && data) setShowMerch(prev => [...prev, data]);
    }
    setMMerch(null);
  };

  const deleteShowMerch = async (id) => {
    await supabase.from("show_merch").delete().eq("id", id);
    setShowMerch(prev => prev.filter(m => m.id !== id));
    setMerchDeleteConfirm(null);
  };

  // ── Square integration helpers ─────────────────────────────────────────────
  const squareAuthHeader = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    return session ? { Authorization: `Bearer ${session.access_token}` } : {};
  };

  const loadSquareStatus = async (tourId) => {
    if (!tourId) return;
    try {
      const headers = await squareAuthHeader();
      const res  = await fetch(`/api/square?action=status&tour_id=${tourId}`, { headers });
      const data = await res.json();
      setSquareConn(data);
    } catch { setSquareConn({ connected: false }); }
  };

  const connectSquare = async () => {
    if (!selT || !teamId) return;
    const headers = await squareAuthHeader();
    const res  = await fetch(`/api/square?action=auth_url&tour_id=${selT}&team_id=${teamId}`, { headers });
    const data = await res.json();
    if (data.url) window.location.href = data.url;
  };

  const syncSquare = async () => {
    if (!selT) return;
    setSquareSyncing(true);
    try {
      const headers = await squareAuthHeader();
      const res  = await fetch(`/api/square?action=sync&tour_id=${selT}`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
      });
      const data = await res.json();
      if (data.synced >= 0) {
        await loadTourFinancials(selT);
        await loadSquareStatus(selT);
      }
    } catch {}
    setSquareSyncing(false);
  };

  const disconnectSquare = async () => {
    if (!selT) return;
    const headers = await squareAuthHeader();
    await fetch(`/api/square?action=disconnect&tour_id=${selT}`, { method: "DELETE", headers });
    setSquareConn({ connected: false });
  };

  // ── EM-1: Email drafts helpers (Outlook / Gmail) ───────────────────────────
  const loadEmailConnStatus = async () => {
    try {
      const headers = await squareAuthHeader();
      const res  = await fetch(`/api/email-drafts?action=status`, { headers });
      const data = await res.json();
      setEmailConn(data?.microsoft ? data : { microsoft: { connected: false }, google: { connected: false } });
    } catch { setEmailConn({ microsoft: { connected: false }, google: { connected: false } }); }
  };

  const connectEmailProvider = async (provider) => {
    // Gmail OAuth isn't live yet (Google app verification pending) — show the
    // coming-soon modal instead of starting a flow that would fail.
    if (provider === "google") { setGmailComingSoon(true); return; }
    if (emailConnecting) return; // already mid-redirect
    setEmailConnecting(provider);
    try {
      const headers = await squareAuthHeader();
      const res  = await fetch(`/api/email-drafts?action=auth_url&provider=${provider}&team_id=${teamId || ""}`, { headers });
      const data = await res.json();
      if (data.url) { window.location.href = data.url; return; } // keep spinner until page unloads
      alert(data.error || "Could not start connection — check server configuration.");
    } catch { alert("Could not reach the server — try again."); }
    setEmailConnecting(null);
  };

  const disconnectEmailProvider = async (provider) => {
    const headers = await squareAuthHeader();
    await fetch(`/api/email-drafts?action=disconnect&provider=${provider}`, { method: "DELETE", headers });
    setEmailConn(prev => ({ ...(prev || {}), [provider]: { connected: false } }));
  };

  // Save the current single-show AI draft into the TM's mailbox drafts folder.
  const saveDraftToProvider = async (provider) => {
    if (!eDraft || !show) return;
    if (draftSaveInFlightRef.current) return; // double-click guard — one draft per click, guaranteed
    draftSaveInFlightRef.current = true;
    setDraftSaveState({ provider, status: "saving" });
    try {
      const headers = await squareAuthHeader();
      const res = await fetch(`/api/email-drafts?action=create_draft`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({
          provider,
          to:      show.settlement?.promoterEmail || venue?.promoterEmail || venue?.email || "",
          subject: advanceSubject(artist.name, show.date, venue?.name, venue?.city, venue?.addressState),
          body:    eDraft,
          html:    draftBodyToHtml(
            eDraft,
            show.advanceToken ? `${window.location.origin}/advance/${show.advanceToken}` : "",
            `${artist.name} · ${venue?.name || ""} · ${fmtDate(show.date)}`
          ),
        }),
      });
      const data = await res.json();
      setDraftSaveState({ provider, status: data.ok ? "done" : "error" });
    } catch { setDraftSaveState({ provider, status: "error" }); }
    draftSaveInFlightRef.current = false;
    setTimeout(() => setDraftSaveState(null), 3000);
  };

  // Bulk: push every non-skipped batch draft into the TM's mailbox.
  const batchSaveDrafts = async (provider) => {
    if (!mBatchAdv) return;
    if (batchDraftState?.saving) return; // double-click guard
    const items = mBatchAdv.items.filter(it => !it.skipped && it.draft);
    if (!items.length) return;
    setBatchDraftState({ provider, saving: true, done: 0, failed: 0 });
    try {
      const headers = await squareAuthHeader();
      const res = await fetch(`/api/email-drafts?action=create_drafts`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({
          provider,
          drafts: items.map(it => ({
            to:      it.email,
            subject: advanceSubject(artist?.name, it.date, it.venueName, it.city, it.state),
            body:    it.draft,
            html:    draftBodyToHtml(
              it.draft,
              it.token ? `${window.location.origin}/advance/${it.token}` : "",
              `${artist?.name || ""} · ${it.venueName} · ${fmtDate(it.date)}`
            ),
          })),
        }),
      });
      const data = await res.json();
      setBatchDraftState({ provider, saving: false, done: data.created || 0, failed: (data.failed || []).length });
    } catch {
      setBatchDraftState({ provider, saving: false, done: 0, failed: items.length });
    }
  };

  const saveTourBudgetRows = async (rows) => {
    // rows: array of { tour_id, team_id, category, budgeted_amount, currency, notes }
    if (!rows.length) return;
    const { data } = await supabase
      .from("tour_budget")
      .upsert(rows, { onConflict: "tour_id,category" })
      .select();
    if (data) setTourBudget(data);
  };

  // ── Category accent colors — shared across Local Discovery & My Recs ──────
  const REC_CAT_COLORS = {
    food:        "var(--accent)",  // amber
    brunch:      "#e8906d",  // coral
    bars:        "#9b6bcf",  // purple
    coffee:      "#b07840",  // brown
    catering:    "#e0b050",  // gold
    tattoo:      "#cf6b6b",  // muted red
    clothing:    "#e0785a",  // terracotta
    convenience: "#7a9fcf",  // slate blue
    barbershop:  "#8b7faf",  // muted violet
    laundromat:  "#5abfcf",  // cyan
    music:       "#c4844a",  // copper
    hospital:    "var(--danger)",  // red
    urgent:      "#e07755",  // orange-red
    pharmacy:    "#5a9fcf",  // blue
    massage:     "#b06bcf",  // lavender
    chiro:       "#5abfaf",  // teal
    acupuncture: "#cfaf50",  // warm gold
    gym:         "#6bbf6b",  // green
    dentist:     "#6b9ebf",  // steel blue
    dispensary:  "var(--success)",  // teal green
    other:       "#888888",  // grey
  };

  // ── Local discovery categories — three groups ────────────────────────────
  const LOCAL_CATS = [
    // Food & Drink
    { id: "food",        label: "Food",         group: "food" },
    { id: "brunch",      label: "Brunch",       group: "food" },
    { id: "bars",        label: "Bars",         group: "food" },
    { id: "coffee",      label: "Coffee",       group: "food" },
    { id: "catering",    label: "Catering",     group: "food" },
    // Specialty
    { id: "barbershop",  label: "Barbershop",   group: "specialty" },
    { id: "laundromat",  label: "Laundromat",   group: "specialty" },
    { id: "music",       label: "Music Store",  group: "specialty" },
    { id: "tattoo",      label: "Tattoo",       group: "specialty" },
    { id: "clothing",    label: "Clothing",     group: "specialty" },
    { id: "convenience", label: "Convenience",  group: "specialty" },
    // Health & Emergency
    { id: "hospital",    label: "Hospital",     group: "health" },
    { id: "urgent",      label: "Urgent",       group: "health" },
    { id: "pharmacy",    label: "Pharmacy",     group: "health" },
    { id: "gym",         label: "Gym",          group: "health" },
    { id: "dentist",     label: "Dentist",      group: "health" },
    { id: "massage",     label: "Massage",      group: "health" },
    { id: "chiro",       label: "Chiro",        group: "health" },
    { id: "acupuncture", label: "Acupuncture",  group: "health" },
    { id: "dispensary",  label: "Dispensary",   group: "health" },
  ];

  // Google Places type/keyword map
  const LOCAL_PLACES_MAP = {
    all:         { type: "restaurant" },
    food:        { type: "restaurant" },
    brunch:      { keyword: "brunch" },
    bars:        { type: "bar" },
    coffee:      { type: "cafe" },
    catering:    { keyword: "catering" },
    barbershop:  { keyword: "barbershop" },
    laundromat:  { type: "laundry" },
    music:       { keyword: "music store instruments" },
    tattoo:      { keyword: "tattoo" },
    clothing:    { keyword: "clothing store sneakers boutique" },
    convenience: { type: "convenience_store" },
    hospital:    { type: "hospital" },
    urgent:      { keyword: "urgent care" },
    pharmacy:    { type: "pharmacy" },
    gym:         { type: "gym" },
    dentist:     { type: "dentist" },
    massage:     { keyword: "massage" },
    chiro:       { keyword: "chiropractor" },
    acupuncture: { keyword: "acupuncture" },
    dispensary:  { keyword: "cannabis dispensary" },
  };

  // openFilter: "" (no filter) | "now" (open right now) | "late" (closes 10pm+)
  const loadDayLocal = async (dayId, location, catOverride, openFilterOverride, dayDate) => {
    if (!dayId || !location) return;
    const cat        = catOverride        !== undefined ? catOverride        : (localFilter[dayId]     || "all");
    const openFilter = openFilterOverride !== undefined ? openFilterOverride : (localOpenFilter[dayId] || "");
    const cacheKey   = `${location}|${cat}|${openFilter}`;
    const existing   = dayLocal[dayId]?.[cacheKey];
    if (existing?.status === "loading" || existing?.status === "ready") return;
    if (!GMAP_KEY) {
      setDayLocal(p => ({ ...p, [dayId]: { ...(p[dayId] || {}), [cacheKey]: { status: "error", results: [], errorMsg: "Google Maps key not configured" } } }));
      return;
    }
    setDayLocal(p => ({ ...p, [dayId]: { ...(p[dayId] || {}), [cacheKey]: { status: "loading", results: [], cachedAt: null } } }));
    try {
      const maps = await loadGoogleMaps();
      const centerLatLng = await new Promise((resolve, reject) => {
        new maps.Geocoder().geocode({ address: location }, (res, status) => {
          if (status === "OK" && res[0]) resolve(res[0].geometry.location);
          else reject(new Error("Location not found: " + location));
        });
      });
      const svc = new maps.places.PlacesService(document.createElement("div"));

      const useOpenNow = openFilter === "now";
      let rawPlaces = [];
      if (cat === "all") {
        const batches = await Promise.all(["restaurant", "bar", "cafe"].map(type =>
          new Promise(resolve => {
            svc.nearbySearch({ location: centerLatLng, radius: 2000, type, ...(useOpenNow ? { openNow: true } : {}) }, (res, st) => {
              resolve(st === "OK" ? res || [] : []);
            });
          })
        ));
        const seen = new Set();
        rawPlaces = batches.flat().filter(p => { if (seen.has(p.place_id)) return false; seen.add(p.place_id); return true; });
      } else {
        const cfg = LOCAL_PLACES_MAP[cat] || { type: "restaurant" };
        rawPlaces = await new Promise((resolve, reject) => {
          svc.nearbySearch({ location: centerLatLng, radius: 2000, ...cfg, ...(useOpenNow ? { openNow: true } : {}) }, (res, st) => {
            if (st === "OK" || st === "ZERO_RESULTS") resolve(res || []);
            else reject(new Error("Places search failed: " + st));
          });
        });
      }

      const toRad = d => d * Math.PI / 180;
      const haversine = (lat1, lng1, lat2, lng2) => {
        const dLat = toRad(lat2 - lat1), dLng = toRad(lng2 - lng1);
        const a = Math.sin(dLat/2)**2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng/2)**2;
        return 6371000 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
      };
      const clat = centerLatLng.lat(), clng = centerLatLng.lng();
      const SKIP_TYPES = new Set(["point_of_interest","establishment","food","health","store","premise","geocode"]);
      // Fetch up to 20 from Google; UI will page at 10 with "Show more"
      const limit = cat === "all" ? 20 : 15;
      const sliced = rawPlaces.slice(0, limit);

      // For "Open Late" filter: fetch getDetails for each place to read opening_hours.periods
      // then filter to places that close at or after 22:00 on the day's day-of-week
      let detailsMap = {}; // place_id → { closingTime: "HHMM"|null, openLate: bool }
      if (openFilter === "late" && sliced.length > 0) {
        const dow = dayDate ? new Date(dayDate + "T12:00:00").getDay() : new Date().getDay();
        const detailResults = await Promise.all(sliced.map(p =>
          new Promise(resolve => {
            svc.getDetails({ placeId: p.place_id, fields: ["opening_hours"] }, (result, status) => {
              if (status !== "OK" || !result?.opening_hours?.periods) {
                resolve({ placeId: p.place_id, openLate: true, closingTime: null }); // unknown = include
                return;
              }
              const periods = result.opening_hours.periods;
              // Find the period that opens on this day-of-week
              let openLate = false;
              let closingTime = null;
              for (const period of periods) {
                if (period.open?.day === dow) {
                  const closeDay  = period.close?.day  ?? period.open.day;
                  const closeTime = period.close?.time ?? "0000";
                  closingTime = closeTime;
                  // Wraps past midnight into next day → definitely open late
                  if (closeDay !== period.open.day) { openLate = true; break; }
                  // Closes at or after 10 pm
                  if (parseInt(closeTime, 10) >= 2200) { openLate = true; break; }
                }
              }
              resolve({ placeId: p.place_id, openLate, closingTime });
            });
          })
        ));
        for (const d of detailResults) detailsMap[d.placeId] = d;
      }

      const businesses = sliced
        .filter(p => openFilter !== "late" || (detailsMap[p.place_id]?.openLate ?? true))
        .map(p => {
          const det = detailsMap[p.place_id];
          return {
            id:          p.place_id,
            name:        p.name,
            rating:      p.rating || 0,
            price:       p.price_level ? "$".repeat(p.price_level) : "",
            is_closed:   p.opening_hours ? !p.opening_hours.open_now : false,
            closingTime: det?.closingTime || null,  // "HHMM" string, null if unknown
            address:     p.vicinity || "",
            distance:    haversine(clat, clng, p.geometry.location.lat(), p.geometry.location.lng()),
            categories:  (p.types || []).filter(t => !SKIP_TYPES.has(t)).slice(0, 2).map(t => t.replace(/_/g, " ")),
            url:         `https://www.google.com/maps/place/?q=place_id:${p.place_id}`,
          };
        })
        .sort((a, b) => a.distance - b.distance);

      setDayLocal(p => ({ ...p, [dayId]: { ...(p[dayId] || {}), [cacheKey]: { status: "ready", results: businesses, cachedAt: new Date().toISOString() } } }));
    } catch (e) {
      setDayLocal(p => ({ ...p, [dayId]: { ...(p[dayId] || {}), [cacheKey]: { status: "error", results: [], cachedAt: null, errorMsg: e.message } } }));
    }
  };

  const refreshDayLocal = (dayId, location, cat, openFilter, dayDate) => {
    const c  = cat        !== undefined ? cat        : "all";
    const of = openFilter !== undefined ? openFilter : "";
    const cacheKey = `${location}|${c}|${of}`;
    setDayLocal(p => ({ ...p, [dayId]: { ...(p[dayId] || {}), [cacheKey]: undefined } }));
    setTimeout(() => loadDayLocal(dayId, location, c, of, dayDate), 50);
  };

  // Normalise city + state into a stable cache key
  const normCityKey = (city, state) =>
    `${(city || "").toLowerCase().trim()}|${(state || "").toLowerCase().trim()}`;

  const loadCommunityRecs = async (city, state) => {
    if (!city) return;
    const key = normCityKey(city, state);
    if (cityRecs[key]?.status === "ready" || cityRecs[key]?.status === "loading") return;
    setCityRecs(p => ({ ...p, [key]: { status: "loading", data: [] } }));
    const { data, error } = await supabase
      .from("community_recs")
      .select("*")
      .ilike("city", city.trim())
      .order("created_at", { ascending: false });
    if (!error) {
      setCityRecs(p => ({ ...p, [key]: { status: "ready", data: data || [] } }));
      // Fetch save counts for these recs in parallel
      const ids = (data || []).map(r => r.id);
      if (ids.length > 0) {
        supabase.from("rec_saves").select("rec_id").in("rec_id", ids).then(({ data: saves }) => {
          if (saves) {
            const counts = {};
            saves.forEach(s => { counts[s.rec_id] = (counts[s.rec_id] || 0) + 1; });
            setRecSaveCounts(prev => ({ ...prev, ...counts }));
          }
        });
      }
      // Load the current user's saved recs once (lazy)
      if (!mySavedLoaded && user) loadMySavedRecs();
    } else {
      setCityRecs(p => ({ ...p, [key]: { status: "error", data: [] } }));
    }
  };

  const addCommunityRec = async (rec, venueId, city, state) => {
    if (!user) return;
    const { data, error } = await supabase.from("community_recs").insert({
      user_id:        user.id,
      venue_id:       venueId || null,
      city:           city    || "",
      state:          state   || "",
      name:           rec.name,
      category:       rec.cat || rec.category || "other",
      note:           rec.note || null,
      url:            rec.url  || null,
      added_by_email: user.email,
    }).select().single();
    if (!error && data) {
      const key = normCityKey(city, state);
      setCityRecs(p => ({
        ...p,
        [key]: { status: "ready", data: [data, ...(p[key]?.data || [])] },
      }));
      // Also prepend to My Recs cache if it's already loaded
      setMyRecs(p => p.status === "ready" ? { ...p, data: [data, ...p.data] } : p);
    }
    return { data, error };
  };

  const removeCommunityRec = async (recId, city, state) => {
    const { error } = await supabase.from("community_recs").delete().eq("id", recId);
    if (!error) {
      const key = normCityKey(city, state);
      setCityRecs(p => ({
        ...p,
        [key]: { ...p[key], data: (p[key]?.data || []).filter(r => r.id !== recId) },
      }));
      // Also remove from My Recs cache if loaded
      setMyRecs(p => ({ ...p, data: p.data.filter(r => r.id !== recId) }));
    }
  };

  const loadMySavedRecs = async () => {
    if (!user || mySavedLoaded) return;
    setMySavedLoaded(true); // prevent double-load
    const { data } = await supabase.from("rec_saves").select("rec_id").eq("user_id", user.id);
    if (data) setMySavedRecs(new Set(data.map(s => s.rec_id)));
  };

  const toggleRecSave = async (rec) => {
    if (!user) return;
    const recId   = rec.id;
    const isSaved = mySavedRecs.has(recId);
    // Optimistic update
    setMySavedRecs(prev => { const s = new Set(prev); isSaved ? s.delete(recId) : s.add(recId); return s; });
    setRecSaveCounts(prev => ({ ...prev, [recId]: Math.max(0, (prev[recId] || 0) + (isSaved ? -1 : 1)) }));
    if (isSaved) {
      await supabase.from("rec_saves").delete().eq("rec_id", recId).eq("user_id", user.id);
    } else {
      await supabase.from("rec_saves").insert({ rec_id: recId, user_id: user.id });
    }
  };

  // Open the in-app place info modal, fetching Google Places details on demand
  const openPlaceInfo = async (rec) => {
    setPlaceInfoModal({ rec });
    const id = rec.id;
    if (placeInfoDetails[id]?.status === "ready" || placeInfoDetails[id]?.status === "loading") return;
    setPlaceInfoDetails(p => ({ ...p, [id]: { status: "loading", data: null } }));
    try {
      const maps = await loadGoogleMaps();
      const svc  = new maps.places.PlacesService(document.createElement("div"));

      const fetchByPlaceId = (placeId) => new Promise(resolve => {
        svc.getDetails({
          placeId,
          fields: ["name","formatted_phone_number","formatted_address","website",
                   "opening_hours","rating","user_ratings_total","price_level",
                   "business_status","url"],
        }, (result, status) => resolve(status === "OK" ? result : null));
      });

      // Prefer the stored place_id deep-link; fall back to findPlaceFromQuery
      const pidMatch = rec.url?.match(/place_id:([^&\s]+)/);
      let details = null;
      if (pidMatch?.[1]) {
        details = await fetchByPlaceId(pidMatch[1]);
      } else {
        const query   = [rec.name, rec.city, rec.state].filter(Boolean).join(", ");
        const found   = await new Promise(resolve =>
          svc.findPlaceFromQuery({ query, fields: ["place_id"] }, (res, st) =>
            resolve(st === "OK" && res?.[0] ? res[0] : null)));
        if (found?.place_id) details = await fetchByPlaceId(found.place_id);
      }
      setPlaceInfoDetails(p => ({ ...p, [id]: { status: details ? "ready" : "not_found", data: details } }));
    } catch (e) {
      setPlaceInfoDetails(p => ({ ...p, [id]: { status: "error", data: null } }));
    }
  };

  // Load all of the current user's own recommendations (for the My Recs view)
  const loadMyRecs = async () => {
    if (!user) return;
    if (myRecs.status === "loading" || myRecs.status === "ready") return;
    setMyRecs({ status: "loading", data: [] });
    const { data, error } = await supabase
      .from("community_recs")
      .select("*")
      .eq("user_id", user.id)
      .order("city")
      .order("category")
      .order("name");
    if (!error) setMyRecs({ status: "ready", data: data || [] });
    else setMyRecs({ status: "error", data: [] });
  };

  const saveLodging = async (payload) => {
    const { dayId } = mLodging;
    if (payload?._delete) {
      const { error } = await supabase.from('lodging').delete().eq('id', mLodging.lodging.id);
      if (!error) {
        setDayLodging(p => {
          const updated = { ...p, [dayId]: (p[dayId] || []).filter(l => l.id !== mLodging.lodging.id) };
          const remaining = updated[dayId] || [];
          setLogisticsSummary(s => ({ ...s, [dayId]: { ...(s[dayId] || {}), hasLodging: remaining.length > 0 } }));
          return updated;
        });
        setMLodging(null);
      } else {
        console.error('[ADVANCD] delete lodging failed:', error);
        setSaveError(true); setTimeout(() => setSaveError(false), 5000);
      }
    } else if (mLodging.mode === 'add') {
      const { _delete, ...clean } = payload;
      const row = { ...clean, tour_day_id: dayId, tour_id: tour.id, user_id: user.id, team_id: teamId || null };
      const { data, error } = await supabase.from('lodging').insert(row).select().single();
      if (!error && data) {
        setDayLodging(p => ({ ...p, [dayId]: [...(p[dayId] || []), data] }));
        setMLodging(null);
      } else {
        console.error('[ADVANCD] insert lodging failed:', error);
        setSaveError(true); setTimeout(() => setSaveError(false), 5000);
      }
    } else {
      const { _delete, ...clean } = payload;
      const { data, error } = await supabase.from('lodging').update(clean).eq('id', mLodging.lodging.id).select().single();
      if (!error && data) {
        setDayLodging(p => ({ ...p, [dayId]: (p[dayId] || []).map(l => l.id === data.id ? data : l) }));
        setMLodging(null);
      } else {
        console.error('[ADVANCD] update lodging failed:', error);
        setSaveError(true); setTimeout(() => setSaveError(false), 5000);
      }
    }
  };

  const handleItineraryConfirm = async (matchedDays) => {
    setMItinerary(false);
    for (const parsedDay of matchedDays) {
      const tourDay = tourDays.find(d => d.date === parsedDay.date);
      if (!tourDay) continue;

      // Update hotel fields on tour_day (only if hotel was parsed)
      if (parsedDay.hotel?.name) {
        const hotelUpdates = {
          hotel_name:         parsedDay.hotel.name         || '',
          hotel_address:      parsedDay.hotel.address      || '',
          hotel_check_in:     parsedDay.hotel.check_in     || '',
          hotel_check_out:    parsedDay.hotel.check_out    || '',
          hotel_confirmation: parsedDay.hotel.confirmation || '',
          hotel_phone:        parsedDay.hotel.phone        || '',
        };
        await saveTourDay(tourDay.id, hotelUpdates);
      }

      // If the day has flights, mark it as a travel day (unless it's already a show day)
      const hasFlights = (parsedDay.passengers || []).some(p => p.flights?.length > 0);
      if (hasFlights && tourDay.type === 'off') {
        await saveTourDay(tourDay.id, { type: 'travel' });
      }

      // Write flights
      for (const pax of (parsedDay.passengers || [])) {
        const tourPartyNow = profiles.filter(p => p.current_tour_id === selT);
        const matchedCrew = tourPartyNow.find(c =>
          c.name.toLowerCase().includes((pax.name || '').toLowerCase().split(' ')[0]) ||
          (pax.name || '').toLowerCase().includes(c.name.toLowerCase().split(' ')[0])
        );
        const passengerIds = matchedCrew ? [matchedCrew.id] : [];
        for (const fl of (pax.flights || [])) {
          const row = { ...fl, tour_day_id: tourDay.id, user_id: user.id, team_id: teamId || null,
            passenger_ids: passengerIds, status: 'scheduled', delay_mins: 0 };
          const { data, error } = await supabase.from('flights').insert(row).select().single();
          if (!error && data) setDayFlights(p => ({ ...p, [tourDay.id]: [...(p[tourDay.id] || []), data] }));
        }
      }

      // Write ground transport
      for (const tr of (parsedDay.ground_transport || [])) {
        const row = { ...tr, tour_day_id: tourDay.id, user_id: user.id, team_id: teamId || null };
        const { data, error } = await supabase.from('ground_transport').insert(row).select().single();
        if (!error && data) setDayTransport(p => ({ ...p, [tourDay.id]: [...(p[tourDay.id] || []), data] }));
      }
    }
  };

  // Shared loader — fetch existing tour_days, patch in any missing dates, generate from scratch if empty.
  // Called both when toggling into tour mode and when opening a tour that's already in tour mode.
  const ensureTourDays = async (tourObj) => {
    if (!tourObj?.id) return;
    setTourDaysLoading(true);

    // Fire auxiliary loaders in parallel but DON'T block tour-day rendering on
    // them — the day grid should appear as soon as tour_days resolves, not after
    // vehicles/memberships/schedule/setlists/reports/broadcasts all finish.
    Promise.all([
      loadVehicles(tourObj.id), loadTourMemberships(tourObj.id),
      loadDayScheduleItems(tourObj.id), loadShowSetlists(tourObj.id),
      loadShowReports(tourObj.id), loadBroadcasts(tourObj.id),
    ]).catch(err => console.error('[ADVANCD] ensureTourDays aux load failed:', err));

    try {
      const { data: existing } = await supabase
        .from('tour_days').select('*').eq('tour_id', tourObj.id).order('date', { ascending: true });

      if (!existing?.length) {
        // No days yet — generate the whole range from the show list.
        await generateTourDays(tourObj);
        return;
      }

      const sorted = [...(tourObj.shows || [])].sort((a, b) => a.date.localeCompare(b.date));
      const showByDate = {};
      sorted.forEach(s => { showByDate[s.date] = s; });
      const validShowIds = new Set(sorted.map(s => s.id));
      let working = [...existing];

      // 1. Insert any calendar days missing from the (possibly expanded) show range.
      if (sorted.length) {
        const startDate = sorted[0].date;
        const endDate   = sorted[sorted.length - 1].date;
        const existingDates = new Set(existing.map(d => d.date));
        const toInsert = [];
        const cur = new Date(startDate + 'T12:00:00');
        const end = new Date(endDate   + 'T12:00:00');
        while (cur <= end) {
          const iso = cur.toISOString().slice(0, 10);
          if (!existingDates.has(iso)) {
            const isShow = !!showByDate[iso];
            toInsert.push({
              tour_id: tourObj.id, user_id: user.id, team_id: teamId || null,
              date: iso, type: isShow ? 'show' : 'off',
              show_id: isShow ? showByDate[iso].id : null,
              city:  isShow ? (venues[showByDate[iso].venueId]?.city         || '') : '',
              state: isShow ? (venues[showByDate[iso].venueId]?.addressState || '') : '',
              country: '', hotel_name: '', hotel_address: '',
              hotel_check_in: '', hotel_check_out: '', hotel_confirmation: '',
              hotel_phone: '', hotel_contact: '', bus_call: '', notes: '',
            });
          }
          cur.setDate(cur.getDate() + 1);
        }
        if (toInsert.length) {
          const { data: patched } = await supabase.from('tour_days').insert(toInsert).select();
          working = [...working, ...(patched || [])];
        }
      }

      // 2. Reconcile existing rows whose show linkage is stale. This is the fix
      //    for "show added in advance mode doesn't appear in tour mode": when a
      //    show is added on a date that was already an off-day, link it here.
      //    Also unlink a day whose show was deleted/moved off it.
      const updates = [];
      working = working.map(d => {
        const showOnDay = showByDate[d.date];
        if (showOnDay) {
          if (d.show_id !== showOnDay.id || d.type !== 'show') {
            const patch = {
              type: 'show', show_id: showOnDay.id,
              city:  venues[showOnDay.venueId]?.city         || d.city  || '',
              state: venues[showOnDay.venueId]?.addressState || d.state || '',
            };
            updates.push({ id: d.id, patch });
            return { ...d, ...patch };
          }
        } else if (d.show_id && !validShowIds.has(d.show_id)) {
          // The show that lived on this day no longer exists (deleted or moved).
          const patch = { type: 'off', show_id: null };
          updates.push({ id: d.id, patch });
          return { ...d, ...patch };
        }
        return d;
      });
      if (updates.length) {
        await Promise.all(updates.map(u =>
          supabase.from('tour_days').update(u.patch).eq('id', u.id)));
      }

      working.sort((a, b) => a.date.localeCompare(b.date));
      setTourDays(working);
      loadLogisticsSummary(working.map(r => r.id));
    } catch (err) {
      console.error('[ADVANCD] ensureTourDays failed:', err);
    } finally {
      // Always release the loading gate — a stuck `true` here silently blocks the
      // auto-load effect from ever retrying (the "had to go back to advance mode
      // and try again" symptom).
      setTourDaysLoading(false);
    }
  };

  const toggleTourMode = async () => {
    const next = isTourMode ? 'advance' : 'tour';
    updTourAdvCfg(cfg => ({ ...cfg, mode: next }));
    if (next === 'tour') {
      await ensureTourDays(tour);
    } else {
      setTourDays([]); setSelectedDayId(null); setDayFlights({});
      setVehicles([]); setDayTransport([]); setTourDayFilter('all'); setPastDaysOpen(false);
    }
  };

  const saveTourDay = async (dayId, updates) => {
    setTourDays(prev => prev.map(d => d.id === dayId ? { ...d, ...updates } : d));
    await supabase.from('tour_days').update(updates).eq('id', dayId);
  };

  // ── Live flight status polling ────────────────────────────────────────────
  const pollFlightStatus = async (flight, dayId) => {
    if (!flight.airline_code || !flight.flight_number) return;
    try {
      const code = flight.airline_code + flight.flight_number;
      const day  = tourDays.find(d => d.id === dayId);
      const qs   = `flight=${encodeURIComponent(code)}${day?.date ? `&date=${day.date}` : ''}`;
      const res  = await fetch(`/api/flights?${qs}`);
      if (!res.ok) return;
      const data = await res.json();
      const updates = {
        status:         data.status,
        delay_mins:     data.delay_mins || 0,
        gate:           data.departure?.gate     || flight.gate     || '',
        terminal:       data.departure?.terminal || flight.terminal || '',
        actual_dep:     data.departure?.actual   || null,
        actual_arr:     data.arrival?.actual     || null,
        last_checked_at: data.last_checked,
      };
      setDayFlights(p => ({ ...p, [dayId]: (p[dayId] || []).map(f => f.id === flight.id ? { ...f, ...updates } : f) }));
      await supabase.from('flights').update(updates).eq('id', flight.id);
    } catch (e) { console.error('[ADVANCD] pollFlightStatus:', e); }
  };

  // Poll when a travel/show day is expanded and has flights — today and tomorrow only
  useEffect(() => {
    if (!selectedDayId || !isTourMode) return;
    const day = tourDays.find(d => d.id === selectedDayId);
    if (!day || (day.type !== 'travel' && day.type !== 'show')) return;
    const today    = new Date().toISOString().slice(0, 10);
    const tomorrow = new Date(Date.now() + 86400000).toISOString().slice(0, 10);
    if (day.date !== today && day.date !== tomorrow) return;
    const flights = dayFlights[selectedDayId] || [];
    if (!flights.length) return;
    const pollAll = () => flights.forEach(f => pollFlightStatus(f, selectedDayId));
    pollAll();
    const timer = setInterval(pollAll, 5 * 60 * 1000); // every 5 min
    return () => clearInterval(timer);
  }, [selectedDayId, (dayFlights[selectedDayId] || []).length, isTourMode]);

  // ── Batch Deposit ───────────────────────────────────────────────────────
  const saveBatchDeposit = (overrides) => {
    setData(prev => ({ ...prev, artists: prev.artists.map(a => a.id !== selA ? a : { ...a,
      tours: a.tours.map(t => t.id !== selT ? t : { ...t,
        shows: t.shows.map(s => {
          const o = overrides[s.id];
          return o ? { ...s, settlement: { ...(s.settlement || {}), ...o } } : s;
        }),
      }),
    })}));
  };

  // ── Batch Send Advances ─────────────────────────────────────────────────
  const batchSendAdvances = async () => {
    if (!tour || !artist) return;
    // Collect shows that have a promoter email (settlement or venue CRM)
    const candidates = tour.shows.filter(s => {
      const v = venues[s.venueId];
      const email = s.settlement?.promoterEmail || v?.promoterEmail || "";
      return email.trim() !== "";
    });
    if (!candidates.length) {
      alert("No shows have a promoter email on file. Add one in the Settlement tab or Venue CRM for each show first.");
      return;
    }
    // Generate tokens for any show that doesn't have one
    const tokenMap = {};
    candidates.filter(s => !s.advanceToken).forEach(s => { tokenMap[s.id] = crypto.randomUUID(); });
    if (Object.keys(tokenMap).length > 0) {
      const results = await Promise.allSettled(
        Object.entries(tokenMap).map(async ([id, token]) => {
          const { error } = await supabase.from("shows").update({ advance_token: token }).eq("id", id);
          if (error) throw error;
          return id;
        })
      );
      const succeeded = new Set(results.filter(r => r.status === "fulfilled").map(r => r.value));
      const failCount = results.length - succeeded.size;
      if (failCount > 0) console.error(`[ADVANCD] batchSendAdvances: ${failCount} token save(s) failed`);
      // Remove failed shows from candidates so we don't draft emails for tokenless shows
      candidates.splice(0, candidates.length, ...candidates.filter(s => !tokenMap[s.id] || succeeded.has(s.id)));
      if (candidates.length === 0) return;
      setData(prev => ({ ...prev, artists: prev.artists.map(a => a.id !== selA ? a : { ...a,
        tours: a.tours.map(t => t.id !== selT ? t : { ...t,
          shows: t.shows.map(s => succeeded.has(s.id) ? { ...s, advanceToken: tokenMap[s.id] } : s),
        }),
      })}));
      bumpToAdvancing([...succeeded]);
    }
    // Build items for the modal
    const items = candidates.map(s => {
      const v = venues[s.venueId];
      const email = s.settlement?.promoterEmail || v?.promoterEmail || "";
      const token = tokenMap[s.id] || s.advanceToken;
      return { showId: s.id, date: s.date, venueName: v?.name || "TBD", city: v?.city || "", state: v?.addressState || "", email, token, draft: "", skipped: false };
    });
    // Open modal in drafting stage
    setMBatchAdv({ stage: "drafting", items, draftingIdx: 0 });
    // Draft emails one by one, updating state as each completes
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      const foundShow = tour.shows.find(s => s.id === item.showId);
      if (!foundShow) continue;
      const showObj = { ...foundShow, advanceToken: item.token };
      const venueObj = venues[showObj?.venueId];
      setMBatchAdv(prev => prev ? { ...prev, draftingIdx: i } : prev);
      let draft = "";
      try { draft = await draftAdvanceEmail(artist, showObj, venueObj, data.profile, item.token); }
      catch { draft = "Failed to generate — check network."; }
      setMBatchAdv(prev => {
        if (!prev) return prev;
        const newItems = [...prev.items];
        newItems[i] = { ...newItems[i], draft };
        return { ...prev, items: newItems };
      });
    }
    setMBatchAdv(prev => prev ? { ...prev, stage: "review", draftingIdx: -1 } : prev);
  };

  const doCopy = () => { navigator.clipboard.writeText(eDraft); setCopied(true); setTimeout(() => setCopied(false), 2000); };
  const doMailto = () => {
    const subject = encodeURIComponent(advanceSubject(artist.name, show.date, venue?.name, venue?.city, venue?.addressState));
    const body = encodeURIComponent(eDraft);
    window.location.href = `mailto:${show.settlement?.promoterEmail || venue.promoterEmail || venue.email || ""}?subject=${subject}&body=${body}`;
  };
  // ── PDF export helpers ──────────────────────────────────────────────────────
  const exportPDF = (art, sh, ven) => {
    const tmD  = sh.advanceTmData || {};
    const proD = sh.advanceData   || {};
    const tourObj = data.artists.flatMap(a => a.tours).find(t => t.shows.some(s => s.id === sh.id));
    const enabledSecIds = tourObj?.advanceConfig?.sections || ADVANCE_PORTAL_SECTIONS.map(s => s.id);
    const visibleSecs = ADVANCE_PORTAL_SECTIONS.filter(s => enabledSecIds.includes(s.id)).map(s => {
      const ef = tourObj?.advanceConfig?.fields?.[s.id];
      return ef ? { ...s, fields: s.fields.filter(f => ef.includes(f.id)) } : s;
    });
    const fieldVal = (secId, fieldId) => {
      const pv = (proD[secId] || {})[fieldId];
      const tv = (tmD[secId]  || {})[fieldId];
      return pv !== undefined && pv !== "" ? pv : tv;
    };
    const fmtContact = (v) => {
      if (!v || typeof v !== "object") return "—";
      const parts = [v.name, v.phone, v.email].filter(Boolean);
      return parts.length ? parts.join(" · ") : "—";
    };
    const secs = visibleSecs.map(sec => {
      const rows = sec.fields.map(f => {
        const v = fieldVal(sec.id, f.id);
        const amended = (proD[sec.id] || {})[f.id] !== undefined && (proD[sec.id] || {})[f.id] !== "" && (proD[sec.id] || {})[f.id] !== (tmD[sec.id] || {})[f.id];
        let display = "—";
        if (f.type === "yesno") display = v === true ? "Yes" : v === false ? "No" : "—";
        else if (f.type === "contact") display = fmtContact(v);
        else display = (v && String(v).trim()) ? String(v) : "—";
        return `<tr><td class="lbl">${f.label}</td><td class="${amended ? "amended" : ""}">${display}</td></tr>`;
      }).join("");
      return `<section><h3>${sec.label}</h3><table>${rows}</table></section>`;
    }).join("");
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Advance Sheet — ${art?.name || ""} @ ${ven?.name || ""}</title>
<style>
  *{box-sizing:border-box;margin:0;padding:0;}
  body{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:14px;color:#111;padding:0;background:#fff;}
  header{padding:28px 40px 20px;border-bottom:2px solid #111;display:flex;align-items:flex-start;justify-content:space-between;}
  .logo{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;letter-spacing:3px;color:#888;font-weight:500;}
  .headline{font-size:20px;font-weight:700;margin-bottom:4px;}
  .meta{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;color:#555;letter-spacing:0.3px;}
  .body{padding:28px 40px;display:flex;flex-direction:column;gap:20px;}
  section{break-inside:avoid;}
  h3{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;letter-spacing:2px;text-transform:uppercase;color:#888;margin-bottom:8px;padding-bottom:5px;border-bottom:1px solid #e0e0e0;}
  table{width:100%;border-collapse:collapse;}
  tr{border-bottom:1px solid #f0f0f0;}
  td{padding:6px 0;vertical-align:top;font-size:12px;}
  td.lbl{width:40%;color:#666;font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;padding-right:16px;}
  td.amended{color:#c47f00;font-style:italic;}
  footer{text-align:center;padding:24px;font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;color:#bbb;letter-spacing:1px;border-top:1px solid #eee;}
  @media print{body{padding:0;}header{padding:20px 32px 16px;}footer{position:fixed;bottom:0;left:0;right:0;}}
</style></head><body>
<header>
  <div>
    <div class="headline">${art?.name || "Artist"} @ ${ven?.name || "Venue"}</div>
    <div class="meta">${[tourObj?.name, sh.date ? fmtDate(sh.date) : "", [ven?.city, ven?.addressState].filter(Boolean).join(", ")].filter(Boolean).join(" · ")}</div>
  </div>
  <div class="logo">ADVANCD</div>
</header>
<div class="body">${secs}</div>
<footer>ADVANCE SHEET · GENERATED ${new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }).toUpperCase()} · LRARTISTS.COM</footer>
<script>window.onload=()=>window.print();</script>
</body></html>`;
    const w = window.open("", "_blank");
    if (w) { w.document.write(html); w.document.close(); }
  };

  const copyRoutingText = (art, tourObj) => {
    const lines = [`${art.name} — ${tourObj.name}`, ""];
    [...(tourObj.shows || [])].sort((a, b) => a.date.localeCompare(b.date)).forEach(s => {
      const v = venues[s.venueId];
      const sc = statusConfig[s.status] || statusConfig.unstarted;
      const tmD = s.advanceTmData || {};
      const tz = s.timezone ? ` ${s.timezone}` : "";
      const loadIn = (tmD.schedule?.venue_access || v?.loadIn || "");
      const setTime = tmD.schedule?.hs_set || "";
      const parts = [
        fmtDate(s.date),
        v?.name || "TBD",
        [v?.city, v?.addressState].filter(Boolean).join(", ") || "—",
        sc.label,
        s.settlement?.guarantee || "—",
        loadIn ? `Load-in: ${loadIn}${tz}` : null,
        setTime ? `Set: ${setTime}${tz}` : null,
      ].filter(Boolean);
      lines.push(parts.join("  |  "));
    });
    navigator.clipboard.writeText(lines.join("\n"));
  };

  const exportRouting = (art, tourObj) => {
    const rows = [...(tourObj.shows || [])].sort((a, b) => a.date.localeCompare(b.date)).map(s => {
      const v = venues[s.venueId];
      const sc = statusConfig[s.status] || statusConfig.unstarted;
      const tmD = s.advanceTmData || {};
      const tz = s.timezone ? ` ${s.timezone}` : "";
      const loadIn = tmD.schedule?.venue_access || v?.loadIn || "";
      const setTime = tmD.schedule?.hs_set || "";
      return `<tr>
        <td>${fmtDate(s.date)}</td>
        <td>${v?.name || "TBD"}</td>
        <td>${[v?.city, v?.addressState].filter(Boolean).join(", ") || "—"}</td>
        <td>${v?.capacity ? Number(v.capacity).toLocaleString() : "—"}</td>
        <td style="color:${sc.color}">${sc.label}</td>
        <td>${s.settlement?.guarantee || "—"}</td>
        <td>${loadIn ? loadIn + tz : "—"}</td>
        <td>${setTime ? setTime + tz : "—"}</td>
      </tr>`;
    }).join("");
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${art.name} · ${tourObj.name}</title>
    <style>*{box-sizing:border-box;margin:0;padding:0;}body{font-family:-apple-system,'Helvetica Neue',sans-serif;font-size:12px;color:#111;padding:32px;}.header{display:flex;align-items:flex-end;justify-content:space-between;margin-bottom:28px;border-bottom:2px solid #111;padding-bottom:16px;}.header-left h1{font-size:20px;font-weight:800;letter-spacing:-0.5px;}.header-left h2{font-size:11px;font-weight:400;color:#777;letter-spacing:1px;text-transform:uppercase;margin-top:4px;}table{width:100%;border-collapse:collapse;}th{font-size:11px;letter-spacing:1.5px;text-transform:uppercase;color:#999;border-bottom:2px solid #eee;padding:6px 10px;text-align:left;}td{padding:9px 10px;border-bottom:1px solid #f0f0f0;vertical-align:top;}tr:last-child td{border-bottom:none;}@media print{body{padding:16px;}@page{margin:0;size:letter;}}</style>
    </head><body>
    <div class="header">
      <div class="header-left"><h1>${art.name}</h1><h2>${tourObj.name} · ${tourObj.shows.length} ${tourObj.shows.length === 1 ? "Show" : "Shows"}</h2></div>
    </div>
    <table><thead><tr><th>Date</th><th>Venue</th><th>City</th><th>Cap.</th><th>Status</th><th>Guarantee</th><th>LOAD</th><th>SET</th></tr></thead>
    <tbody>${rows}</tbody></table>
    <script>window.onload=()=>{window.print();}</script></body></html>`;
    const w = window.open("", "_blank");
    if (w) { w.document.write(html); w.document.close(); }
  };

  const exportRoutingDocx = async (art, tourObj) => {
    const shows = [...(tourObj.shows || [])].sort((a, b) => a.date.localeCompare(b.date)).map(s => {
      const v  = venues[s.venueId];
      const sc = statusConfig[s.status] || statusConfig.unstarted;
      const tz = s.timezone ? ` ${s.timezone}` : "";
      const loadIn  = s.advanceTmData?.schedule?.venue_access || v?.loadIn || "";
      const setTime = s.advanceTmData?.schedule?.hs_set || "";
      return {
        date:      fmtDate(s.date),
        venueName: v?.name || "TBD",
        city:      [v?.city, v?.addressState].filter(Boolean).join(", ") || "",
        cap:       v?.capacity ? Number(v.capacity).toLocaleString() : "",
        status:    sc.label,
        guarantee: s.settlement?.guarantee ? `$${Number(s.settlement.guarantee).toLocaleString()}` : "",
        loadIn:    loadIn ? loadIn + tz : "",
        setTime:   setTime ? setTime + tz : "",
      };
    });
    const res = await fetch("/api/routing-export", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ artistName: art.name, tourName: tourObj.name, shows }),
    });
    if (!res.ok) return;
    const blob = await res.blob();
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href = url; a.download = `${(art.name || "routing").toLowerCase().replace(/\s+/g, "-")}-routing.docx`;
    a.click(); URL.revokeObjectURL(url);
  };

  const exportDaySheet = (art, tourObj, sh, ven) => {
    const tmD  = sh.advanceTmData || {};
    const showCrewOverride = sh?.advanceTmData?._showCrew?.entries;
    const crew = Array.isArray(showCrewOverride) ? showCrewOverride : (tourObj?.advanceConfig?.crewManifest || []);
    const sched = tmD.schedule || {};
    const hosp  = tmD.hospitality || {};
    const prod  = tmD.production  || {};
    const schedFields = ADVANCE_PORTAL_SECTIONS.find(s => s.id === "schedule")?.fields || [];
    const hospFields  = ADVANCE_PORTAL_SECTIONS.find(s => s.id === "hospitality")?.fields || [];
    // Build schedule rows: venue-level times first, then the full bill with intermissions
    const venueTimeRows = schedFields.map(f => {
      const v = sched[f.id]; if (!v || v === "") return "";
      return `<tr><td class="lbl">${f.label}</td><td>${v}</td></tr>`;
    }).filter(Boolean).join("");
    const billRows = (() => {
      const bill = sched.bill;
      const cos  = sched.changeovers || [];
      if (!Array.isArray(bill) || !bill.length) return "";
      return bill.map((act, i) => {
        const co = cos[i] || {};
        const isIM = !!co.isIntermission;
        const actRows = [
          act.load ? `<tr><td class="lbl">${act.name} — Load-in</td><td>${act.load}</td></tr>` : "",
          act.sc   ? `<tr><td class="lbl">${act.name} — S/C</td><td>${act.sc}</td></tr>` : "",
          act.set  ? `<tr><td class="lbl">${act.name} — Set</td><td>${act.set}${setDurMins(act.set, act.setEnd) ? ` <span style="opacity:0.6;font-size:11px">(${fmtDur(setDurMins(act.set, act.setEnd))})</span>` : ""}</td></tr>` : "",
        ].filter(Boolean).join("");
        const coDurMins = setDurMins(co.time, co.timeEnd);
        const coDurStr  = coDurMins ? ` (${fmtDur(coDurMins)})` : (co.duration ? ` (${co.duration})` : "");
        const coRow = (i < bill.length - 1 && (co.time || co.duration))
          ? `<tr style="background:#f8f8f8;"><td class="lbl" style="font-style:italic;">${isIM ? "⏸ Intermission" : "→ Changeover"}${coDurStr}</td><td>${co.time || ""}</td></tr>`
          : "";
        return actRows + coRow;
      }).join("");
    })();
    const schedRows = (venueTimeRows || billRows) ? venueTimeRows + billRows : "";
    const hospRows = hospFields.map(f => {
      const v = hosp[f.id]; if (v === undefined || v === null || v === "") return "";
      return `<tr><td class="lbl">${f.label}</td><td>${f.type === "yesno" ? (v === true ? "Yes" : "No") : v}</td></tr>`;
    }).filter(Boolean).join("");
    const crewRows = crew.map(c => `<tr><td>${c.name || "—"}</td><td>${c.role || "—"}</td><td>${c.phone || "—"}</td><td>${c.email || "—"}</td></tr>`).join("");
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Day Sheet — ${art?.name || ""}</title>
<style>
  *{box-sizing:border-box;margin:0;padding:0;}
  body{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:14px;color:#111;padding:0;}
  header{padding:28px 40px 20px;border-bottom:2px solid #111;display:flex;align-items:flex-start;justify-content:space-between;}
  .logo{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;letter-spacing:3px;color:#888;}
  .headline{font-size:20px;font-weight:700;margin-bottom:4px;}
  .meta{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;color:#555;}
  .body{padding:28px 40px;display:flex;flex-direction:column;gap:20px;}
  section{break-inside:avoid;}
  h3{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;letter-spacing:2px;text-transform:uppercase;color:#888;margin-bottom:8px;padding-bottom:5px;border-bottom:1px solid #e0e0e0;}
  table{width:100%;border-collapse:collapse;}
  tr{border-bottom:1px solid #f0f0f0;}
  td{padding:6px 0;font-size:12px;}
  td.lbl{width:40%;color:#666;font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;padding-right:16px;}
  footer{text-align:center;padding:24px;font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;color:#bbb;border-top:1px solid #eee;}
  @media print{body{padding:0;}header{padding:20px 32px 16px;}}
</style></head><body>
<header>
  <div>
    <div class="headline">${art?.name || "Artist"} — Day Sheet</div>
    <div class="meta">${[sh.date ? fmtDate(sh.date) : "", ven?.name, [ven?.city, ven?.addressState].filter(Boolean).join(", ")].filter(Boolean).join(" · ")}</div>
  </div>
  <div class="logo">ADVANCD</div>
</header>
<div class="body">
  <section><h3>Venue</h3><table>
    <tr><td class="lbl">Venue</td><td>${ven?.name || "—"}</td></tr>
    <tr><td class="lbl">Address</td><td>${[ven?.address, ven?.city, ven?.addressState, ven?.zip].filter(Boolean).join(", ") || "—"}</td></tr>
    <tr><td class="lbl">Load-in</td><td>${ven?.loadIn || "—"}</td></tr>
    <tr><td class="lbl">Parking</td><td>${ven?.parking || "—"}</td></tr>
    <tr><td class="lbl">Capacity</td><td>${ven?.capacity ? Number(ven.capacity).toLocaleString() : "—"}</td></tr>
  </table></section>
  ${schedRows ? `<section><h3>Schedule</h3><table>${schedRows}</table></section>` : ""}
  ${hospRows  ? `<section><h3>Hospitality</h3><table>${hospRows}</table></section>` : ""}
  ${crewRows  ? `<section><h3>Touring Party</h3><table><tr><td><strong>Name</strong></td><td><strong>Role</strong></td><td><strong>Phone</strong></td><td><strong>Email</strong></td></tr>${crewRows}</table></section>` : ""}
</div>
<footer>DAY SHEET · ${art?.name?.toUpperCase() || ""} · LRARTISTS.COM · CONFIDENTIAL</footer>
<script>window.onload=()=>window.print();</script>
</body></html>`;
    const w = window.open("", "_blank");
    if (w) { w.document.write(html); w.document.close(); }
  };

  const togCat = k => setCats(p => ({ ...p, [k]: !p[k] }));

  // ── Advance Portal routing (public, no auth needed) ───────────────────────
  const advMatch = window.location.pathname.match(/^\/advance\/([^/]+)/);
  if (advMatch) return <AdvancePortal token={advMatch[1]} lightMode={lightMode} />;

  // ── Runner View routing (public share link, no auth needed) ──────────────
  const runMatch = window.location.pathname.match(/^\/run\/([^/]+)/);
  if (runMatch) return <RunnerView token={runMatch[1]} lightMode={lightMode} />;

  // ── Auth / loading guards ──────────────────────────────────────────────
  if (!authReady) return (
    <div style={{ minHeight: "100vh", background: lightMode ? "#f0ede8" : "#0d0d0d", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ fontFamily: "var(--font-display)", fontSize: 28, letterSpacing: 4, color: lightMode ? "#c47f00" : "#333" }}>ADVANCD</div>
    </div>
  );

  // ── Invite flow ──────────────────────────────────────────────────────────
  // Show InviteAcceptScreen if arriving via /invite/:token (pre or post-auth)
  if (inviteToken && !inviteAccepted) return <InviteAcceptScreen inviteInfo={inviteInfo} />;

  // ── Claim flow ────────────────────────────────────────────────────────────
  if (claimToken && !user) return <ClaimScreen token={claimToken} />;
  if (claimToken && claimStatus) return (
    <ClaimStatusScreen
      claimStatus={claimStatus}
      lightMode={lightMode}
      onContinue={() => { setClaimToken(null); setClaimStatus(null); setView("profile"); }}
      onDismiss={() => { window.history.replaceState({}, "", "/"); setClaimToken(null); setClaimStatus(null); }}
    />
  );

  if (!user) return <AuthScreen />;
  if (dbLoading) return (
    <div style={{ minHeight: "100vh", background: lightMode ? "#f0ede8" : "#0d0d0d", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 12, color: lightMode ? "#8a8784" : "#555", letterSpacing: 2 }}>LOADING...</div>
    </div>
  );

  // ── V-0: Org routing — venue / festival users get their own dashboard ──────
  if (orgId && orgType === "festival") {
    if (orgMemberRole === "runner") {
      return <RunnerView orgId={orgId} orgData={orgData} user={user} lightMode={lightMode} />;
    }
    return <FestivalDashboard
      orgData={orgData} user={user}
      onUpdateOrg={updated => setOrgData(updated)}
    />;
  }
  if (orgId && orgType === "venue") {
    return <VenueDashboard
      orgData={orgData} orgType={orgType} user={user}
      onUpdateOrg={updated => setOrgData(updated)}
    />;
  }

  // ── Role routing — invited band/crew members get restricted mobile view ───
  if (memberRole === "band" || memberRole === "crew") {
    return (
      <>
        <style>{S}</style>
        <MobileApp
          data={data} tourDays={tourDays} tour={tour} artist={artist}
          venues={venues} teamMembers={teamMembers}
          profiles={profiles} dayWeather={dayWeather} saved={saved}
          lightMode={lightMode} selA={selA} selT={selT} selS={selS}
          setSelA={setSelA} setSelT={setSelT} setSelS={setSelS}
          setLightMode={setLightMode} updShow={updShow}
          memberRole={memberRole} memberModules={memberModules} UI={UI}
          dayLodging={dayLodging} dayFlights={dayFlights} dayTransport={dayTransport}
          loadDayLodging={loadDayLodging} loadDayFlights={loadDayFlights} loadDayTransport={loadDayTransport}
          loadDayWeather={loadDayWeather} user={user}
          memberOwnProfile={memberOwnProfile}
          memberNetworkProfiles={memberNetworkProfiles}
          memberConnections={memberConnections}
          toggleMemberConnection={toggleMemberConnection}
          saveMemberProfile={saveMemberProfile}
          realtimeStatus={realtimeStatus}
          memberDataStale={memberDataStale}
          onRefresh={handleMobileRefresh}
          cityRecs={cityRecs}
          loadCommunityRecs={loadCommunityRecs}
          memberPermissions={memberPermissions}
          dayScheduleItems={dayScheduleItems}
          showSetlists={showSetlists}
          saveShowSetlist={saveShowSetlist}
          deleteShowSetlist={deleteShowSetlist}
          showReports={showReports}
          onSaveShowReport={saveShowReport}
          broadcasts={broadcasts}
          broadcastReads={broadcastReads}
          markBroadcastRead={markBroadcastRead}
          constrainWidth={!isMobile}
        />
      </>
    );
  }

  // ── Render ──────────────────────────────────────────────────────────────
  if (isMobile) {
    return (
      <>
        <style>{S}</style>
        <MobileApp
          data={data}
          tourDays={tourDays}
          tour={tour}
          artist={artist}
          venues={venues}
          teamMembers={teamMembers}
          profiles={profiles}
          dayWeather={dayWeather}
          saved={saved}
          lightMode={lightMode}
          selA={selA}
          selT={selT}
          selS={selS}
          setSelA={setSelA}
          setSelT={setSelT}
          setSelS={setSelS}
          setLightMode={setLightMode}
          updShow={updShow}
          teamId={teamId}
          memberRole={memberRole}
          memberModules={memberModules}
          UI={UI}
          dayLodging={dayLodging}
          dayFlights={dayFlights}
          dayTransport={dayTransport}
          loadDayLodging={loadDayLodging}
          loadDayFlights={loadDayFlights}
          loadDayTransport={loadDayTransport}
          loadDayWeather={loadDayWeather}
          user={user}
          memberOwnProfile={memberOwnProfile}
          memberNetworkProfiles={memberNetworkProfiles}
          memberConnections={memberConnections}
          toggleMemberConnection={toggleMemberConnection}
          saveMemberProfile={saveMemberProfile}
          realtimeStatus={realtimeStatus}
          memberDataStale={memberDataStale}
          onRefresh={handleMobileRefresh}
          cityRecs={cityRecs}
          loadCommunityRecs={loadCommunityRecs}
          memberPermissions={memberPermissions}
          dayScheduleItems={dayScheduleItems}
          showSetlists={showSetlists}
          saveShowSetlist={saveShowSetlist}
          deleteShowSetlist={deleteShowSetlist}
          showReports={showReports}
          onSaveShowReport={saveShowReport}
          broadcasts={broadcasts}
          broadcastReads={broadcastReads}
          markBroadcastRead={markBroadcastRead}
          onChatExecuteAction={executeChatAction}
        />
      </>
    );
  }

  return (
    <>
      <style>{S}</style>
      <div className={`app${lightMode ? " light" : ""}`}>

        {/* Undo toast */}
        {undoAction && (
          <div style={{ position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)", zIndex: 9000, background: "var(--panel-alt)", border: "1px solid var(--panel-border)", borderRadius: 8, overflow: "hidden", boxShadow: "0 4px 24px rgba(0,0,0,0.28)", animation: "toastIn 0.25s ease", fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", minWidth: 320 }}>
            <div style={{ padding: "12px 16px", display: "flex", alignItems: "center", gap: 14 }}>
              <span style={{ fontSize: 12, color: "var(--text-mid)", flex: 1 }}>{undoAction.label}</span>
              <Button variant="primary" size="sm" onClick={undoAction.restore} style={{ padding: "5px 14px", letterSpacing: 0.5, flexShrink: 0 }}>Undo</Button>
              <button onClick={() => { clearTimeout(undoAction.timer); clearTimeout(undoAction.deleteTimer); setUndoAction(null); }} style={{ background: "none", border: "none", color: "var(--text-mut)", cursor: "pointer", fontSize: 16, lineHeight: 1, padding: "0 2px", flexShrink: 0 }}>✕</button>
            </div>
            {/* Countdown bar — shrinks over 30s */}
            <div style={{ height: 2, background: "var(--panel-border)" }}>
              <div style={{ height: "100%", background: "var(--accent)", animation: "undoShrink 30s linear forwards", transformOrigin: "left" }} />
            </div>
          </div>
        )}

        {/* Save error toast */}
        {saveError && (
          <div className="save-toast">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
            <div>
              <div style={{ fontWeight: 600, marginBottom: 2 }}>Save failed</div>
              <div style={{ fontSize: 11, opacity: 0.8 }}>Check your connection — changes may not be saved.</div>
            </div>
          </div>
        )}

        {/* Promoter-activity toast — click to open the show's advance tab */}
        {promoterNotice && (
          <div
            onClick={() => {
              for (const a of data.artists) {
                for (const t of a.tours) {
                  if (t.shows.some(s => s.id === promoterNotice.showId)) {
                    goToShow(a.id, t.id, promoterNotice.showId);
                    setDetailTab("advance");
                    break;
                  }
                }
              }
              setPromoterNotice(null);
            }}
            style={{
              position: "fixed", bottom: 24, left: 24, zIndex: 9000, cursor: "pointer",
              display: "flex", alignItems: "center", gap: 10, maxWidth: 380,
              background: "var(--panel-alt)", border: "1px solid var(--accent)",
              borderRadius: 8, padding: "11px 14px", boxShadow: "0 4px 24px rgba(0,0,0,0.28)",
              animation: "toastIn 0.25s ease", fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
            }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text)" }}>
                Promoter {promoterNotice.kind}
              </div>
              <div style={{ fontSize: 11, color: "var(--text-mut)", marginTop: 1 }}>
                {promoterNotice.date
                  ? new Date(promoterNotice.date + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })
                  : ""} · click to review
              </div>
            </div>
            <button
              onClick={e => { e.stopPropagation(); setPromoterNotice(null); }}
              style={{ background: "none", border: "none", color: "var(--text-mut)", cursor: "pointer", fontSize: 14, lineHeight: 1, padding: "0 2px", flexShrink: 0, marginLeft: 4 }}>
              ✕
            </button>
          </div>
        )}

        {/* AI Assistant (v1: retrieval-only) — floating button + slide-over */}
        <ChatPanel
          context={{
            tourId: selT,
            tourName: tour?.name || null,
            artistName: artist?.name || null,
            dateRange: (() => {
              const dates = (tour?.shows || []).map(s => s.date).filter(Boolean).sort();
              return dates.length > 0 ? `${dates[0]} to ${dates[dates.length - 1]}` : null;
            })(),
            // Screen awareness — lets "this show" / "here" resolve to what the
            // TM is actually looking at right now.
            screen: selS ? {
              showId: selS,
              showDate: show?.date || null,
              venue: venue?.name || null,
              tab: detailTab,
            } : { view },
          }}
          onOpenShow={(showId) => {
            for (const a of data.artists) {
              for (const t of a.tours) {
                if (t.shows.some(s => s.id === showId)) {
                  goToShow(a.id, t.id, showId);
                  setDetailTab("advance");
                  return;
                }
              }
            }
          }}
          onExecuteAction={executeChatAction}
        />

        {/* SIDEBAR */}
        <nav className={`sb${sbCollapsed ? " collapsed" : ""}`} style={{ position: "relative" }}>
          <div title={sbCollapsed ? "Expand sidebar" : "Collapse sidebar"} className="sb-collapse-btn" onClick={() => setSbCollapsed(p => !p)}>
            {sbCollapsed ? "›" : "‹"}
          </div>
          {sbCollapsed ? (
            <div className="sb-expand-btn" onClick={() => setSbCollapsed(false)}>ADVANCD</div>
          ) : (
          <div className="sb-inner">
          <div className="sb-logo">
            <div><div className="wordmark">ADVANCD</div><div className="tagline">Tour Management</div></div>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
              <span className={`saved ${saved ? "on" : ""}`}>✓ saved</span>
              <span style={{
                display: "flex", alignItems: "center", gap: 4,
                fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
                fontSize: 11, letterSpacing: "1px", textTransform: "uppercase",
                color: realtimeStatus === 'connected' ? "#4caf50"
                     : realtimeStatus === 'connecting' ? "var(--accent)"
                     : "#444",
                transition: "color 0.4s",
              }}>
                <span style={{
                  width: 5, height: 5, borderRadius: "50%", flexShrink: 0,
                  background: realtimeStatus === 'connected' ? "#4caf50"
                            : realtimeStatus === 'connecting' ? "var(--accent)"
                            : "#333",
                  boxShadow: realtimeStatus === 'connected' ? "0 0 5px #4caf5088" : "none",
                  transition: "background 0.4s, box-shadow 0.4s",
                }} />
                {realtimeStatus === 'connected' ? "Live"
                  : realtimeStatus === 'connecting' ? "Connecting…"
                  : "Offline"}
              </span>
            </div>
          </div>

          <div className="sb-content">
          <div style={{ padding: "10px 16px 6px" }}>
            <input ref={searchRef} className="sb-search" placeholder="Search… (⌘K)" value={search} onChange={e => setSearch(e.target.value)} />
          </div>

          {searchHits.length > 0 && (
            <div style={{ borderTop: "1px solid #1a1a1a", borderBottom: "1px solid #1a1a1a", paddingBottom: 4 }}>
              {searchHits.map((h, i) => (
                <div key={h.type + '_' + h.label} className="s-hit" onClick={h.go}>
                  <span className="s-hit-type">{h.type}</span>
                  <div>
                    <div className="s-hit-name">{h.label}</div>
                    {h.sub && <div className="s-hit-sub">{h.sub}</div>}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="sb-sec">
            <div className={`sb-nav ${view === "overview" ? "on" : ""}`} onClick={() => { setView("overview"); setSelT(null); }}>
              <svg width="15" height="15" viewBox="0 0 15 15" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><rect x="1.5" y="1.5" width="5" height="5" rx="0.8"/><rect x="8.5" y="1.5" width="5" height="5" rx="0.8"/><rect x="1.5" y="8.5" width="5" height="5" rx="0.8"/><rect x="8.5" y="8.5" width="5" height="5" rx="0.8"/></svg>
              Artists
            </div>
            <div className={`sb-nav ${view === "dashboard" ? "on" : ""}`} onClick={() => setView("dashboard")}>
              <svg width="15" height="15" viewBox="0 0 15 15" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><rect x="3" y="1.5" width="9" height="12" rx="1"/><path d="M5.5 5h4M5.5 7.5h4M5.5 10h2.5"/></svg>
              Advances
            </div>
            <div className={`sb-nav ${view === "calendar" ? "on" : ""}`} onClick={() => setView("calendar")}>
              <svg width="15" height="15" viewBox="0 0 15 15" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><rect x="1.5" y="3" width="12" height="10.5" rx="1"/><path d="M1.5 7h12M5 1.5v3M10 1.5v3"/></svg>
              Calendar
            </div>
            <hr className="sb-mini-hr" />
            <div className={`sb-nav ${(view === "venues" || view === "festivals") ? "on" : ""}`} onClick={() => { setView("venues"); setSelVenue(null); }}>
              <svg width="15" height="15" viewBox="0 0 15 15" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M2 13.5V5.8L7.5 2l5.5 3.8v7.7"/><path d="M1 13.5h13"/><rect x="5.5" y="9" width="4" height="4.5" rx="0.5"/></svg>
              Venues &amp; Festivals
            </div>
            <div className={`sb-nav ${view === "contacts" ? "on" : ""}`} onClick={() => setView("contacts")}>
              <svg width="15" height="15" viewBox="0 0 15 15" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><circle cx="7.5" cy="4.5" r="2.5"/><path d="M2 13.5c0-3 2.5-5 5.5-5s5.5 2 5.5 5"/></svg>
              Industry Contacts
            </div>
            <div className={`sb-nav ${view === "people" ? "on" : ""}`} onClick={() => setView("people")}>
              <svg width="15" height="15" viewBox="0 0 15 15" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><circle cx="5" cy="4" r="2"/><circle cx="10.5" cy="4" r="2"/><path d="M1 13c0-2.2 1.8-3.5 4-3.5s4 1.3 4 3.5"/><path d="M10.5 9.5c1.5 0 3 .9 3 2.5"/></svg>
              Touring Party
            </div>
            <hr className="sb-mini-hr" />
            <div className={`sb-nav ${view === "myrecs" ? "on" : ""}`} onClick={() => { setView("myrecs"); loadMyRecs(); }}>
              <svg width="15" height="15" viewBox="0 0 15 15" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 2h9a1 1 0 0 1 1 1v10.5L7.5 11 2 13.5V3a1 1 0 0 1 1-1z"/><path d="M5.5 6h4M5.5 8.5h2"/></svg>
              My Recs
            </div>
            <div className={`sb-nav ${view === "activity" ? "on" : ""}`} onClick={() => { setView("activity"); loadActivityLog(); }}>
              <svg width="15" height="15" viewBox="0 0 15 15" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M2 4.5h11M2 7.5h8M2 10.5h5"/></svg>
              Activity
            </div>
          </div>

          {/* Context breadcrumb — shows current artist/tour */}
          {artist && (() => {
            const _FF = "'Helvetica Neue', Helvetica, Arial, sans-serif";
            const _aIdx = data.artists.findIndex(a => a.id === artist.id);
            const _c = artistColor(artist.colorIdx ?? (_aIdx < 0 ? 0 : _aIdx));
            const _today = new Date().toISOString().slice(0, 10);
            const _nextShow = tour?.shows
              ?.filter(s => s.date >= _today)
              ?.sort((a, b) => a.date.localeCompare(b.date))[0];
            const _nextVenue = _nextShow ? (venues[_nextShow.venueId] || null) : null;
            return (
              <div style={{ padding: "8px 10px", borderTop: `1px solid ${lightMode ? "#d0ccc6" : "#1a1a1a"}` }}>
                <div style={{
                  borderRadius: 8,
                  background: lightMode ? `${_c.pill}12` : `${_c.pill}0e`,
                  border: `1px solid ${lightMode ? `${_c.pill}35` : `${_c.pill}25`}`,
                  overflow: "hidden",
                }}>
                  {/* Color accent bar */}
                  <div style={{ height: 3, background: `linear-gradient(90deg, ${_c.pill}, ${_c.pill}88)` }} />
                  <div style={{ padding: "9px 11px 10px" }}>
                    {/* Label */}
                    <div style={{ fontFamily: _FF, fontSize: 11, letterSpacing: 1.5, textTransform: "uppercase", color: _c.pill, marginBottom: 5, opacity: 0.75 }}>Current</div>
                    {/* Artist name */}
                    <div
                      onClick={() => { setSelA(artist.id); setSelT(null); setSelS(null); setView("artist"); }}
                      style={{ fontFamily: _FF, fontSize: 14, fontWeight: 700, color: lightMode ? "#1a1917" : "#e8e4de", cursor: "pointer", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginBottom: 2, lineHeight: 1.25, transition: "color 0.15s" }}
                      title={`Go to ${artist.name}`}
                    >{artist.name}</div>
                    {/* Tour name */}
                    {tour && (
                      <div
                        onClick={() => goTour(artist.id, tour.id)}
                        style={{ fontFamily: _FF, fontSize: 11, color: _c.pill, opacity: 0.8, cursor: "pointer", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginBottom: _nextShow ? 7 : 0, transition: "opacity 0.15s" }}
                        title={`Go to ${tour.name}`}
                      >› {tour.name}</div>
                    )}
                    {/* Next show */}
                    {_nextShow && (
                      <div style={{ display: "flex", alignItems: "center", gap: 5, paddingTop: 6, borderTop: `1px solid ${_c.pill}25` }}>
                        <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke={_c.pill} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.6, flexShrink: 0 }}><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                        <span style={{ fontFamily: _FF, fontSize: 11, color: lightMode ? "#5a5754" : "#777", lineHeight: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {new Date(_nextShow.date + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                          {_nextVenue?.city ? ` · ${_nextVenue.city}${_nextVenue.addressState ? `, ${_nextVenue.addressState}` : ""}` : ""}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })()}
          </div>{/* end sb-content */}

          <div style={{ borderTop: `1px solid ${lightMode ? "#d0ccc6" : "#1a1a1a"}` }}>
            {/* Profile warning */}
            {!data.profile?.name && (
              <div onClick={() => setView("profile")} style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: lightMode ? "#8a5500" : "var(--accent)", background: lightMode ? "#fff8e6" : "#1a1200", borderBottom: `1px solid ${lightMode ? "#e8c97a" : "#3a2800"}`, padding: "7px 16px", cursor: "pointer", letterSpacing: 0.3, lineHeight: 1.5 }}>
                ⚠ Set up your profile — your name and agency appear in advance emails.
              </div>
            )}
            {/* User row */}
            <div onClick={() => setView("profile")} title="My Profile" style={{ display: "flex", alignItems: "center", gap: 10, padding: "11px 16px 10px", cursor: "pointer" }}>
              <div style={{ width: 28, height: 28, borderRadius: "50%", background: lightMode ? "#e0dbd4" : "#222", border: `1px solid ${lightMode ? "#ccc8c2" : "#2a2a2a"}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <span style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, fontWeight: 700, color: lightMode ? "#6a6460" : "#666", letterSpacing: 0 }}>
                  {(data.profile?.name || user?.email || "?").charAt(0).toUpperCase()}
                </span>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, fontWeight: 600, color: view === "profile" ? (lightMode ? "#c47f00" : "var(--accent)") : (lightMode ? "#3a3632" : "#aaa"), letterSpacing: 0.2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", transition: "color 0.15s" }}>
                  {data.profile?.name || "My Profile"}
                </div>
                <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: lightMode ? "#9a9794" : "#555", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", letterSpacing: 0.2, marginTop: 1 }}>
                  {user?.email}
                </div>
              </div>
              <button onClick={e => { e.stopPropagation(); setLightMode(p => !p); }} title={`Switch to ${lightMode ? "dark" : "light"} mode (Cmd+\\)`}
                style={{ background: "none", border: "none", color: lightMode ? "#9a9794" : "#555", fontSize: 14, padding: "4px 2px", borderRadius: 4, cursor: "pointer", lineHeight: 1, flexShrink: 0 }}>
                {lightMode ? "☽" : "☀"}
              </button>
            </div>
            {/* Sign out — full-width, clearly separated */}
            <div style={{ padding: "0 12px 12px" }}>
              <button onClick={() => supabase.auth.signOut()}
                style={{ width: "100%", background: "none", border: `1px solid ${lightMode ? "#d0ccc6" : "#2a2a2a"}`, color: lightMode ? "#8a8784" : "#555", fontSize: 11, padding: "7px 0", borderRadius: 4, cursor: "pointer", fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", letterSpacing: "1.2px", textTransform: "uppercase", transition: "border-color 0.15s, color 0.15s" }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = lightMode ? "#c84b4b" : "#5a2020"; e.currentTarget.style.color = "#e05555"; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = lightMode ? "#d0ccc6" : "#2a2a2a"; e.currentTarget.style.color = lightMode ? "#8a8784" : "#555"; }}>
                Sign Out
              </button>
            </div>
          </div>
          </div>
          )}
        </nav>

        {/* MAIN */}
        <div className="main">

          {/* ARTIST PROFILE */}
          {view === "artist" && artist && (
            <>
              <div className="ph" style={{ flexDirection: "column", alignItems: "flex-start", gap: 6 }}>
                <button onClick={() => setView("overview")} style={{ background: "none", border: "none", cursor: "pointer", fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: UI.textMut, letterSpacing: "0.2px", padding: 0, display: "flex", alignItems: "center", gap: 4 }}>
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M7.5 2L3.5 6l4 4"/></svg>
                  Artists
                </button>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
                  <div>
                    <div className="ph-title">{artist.name}</div>
                    <div className="ph-sub">{artist.tours.length} TOUR{artist.tours.length !== 1 ? "S" : ""} · {artist.tours.flatMap(t => t.shows).length} {artist.tours.flatMap(t => t.shows).length === 1 ? "SHOW" : "SHOWS"}</div>
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button className="add-g" onClick={() => setMEditArtist(artist.id)} style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4z"/></svg>Edit Profile</button>
                    <button className="add-p" onClick={() => setMTour({ mode: "add" })}>+ Add Tour</button>
                  </div>
                </div>
              </div>

              {/* Tab bar */}
              <div style={{ display: "flex", borderBottom: `1px solid ${UI.hdrBdr}`, padding: "0 36px", gap: 2 }}>
                {[
                  { id: "tours",    label: "Tours" },
                  { id: "contacts", label: "Contacts" },
                  { id: "roster",   label: "Roster" },
                  { id: "rider",    label: "Documents" },
                ].map(tab => (
                  <button key={tab.id} onClick={() => setArtistTab(tab.id)} style={{
                    fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, letterSpacing: "0.5px",
                    padding: "10px 18px", background: "none", border: "none", cursor: "pointer",
                    color: artistTab === tab.id ? UI.amber : UI.textFnt,
                    fontWeight: artistTab === tab.id ? 700 : 400,
                    borderBottom: `2px solid ${artistTab === tab.id ? UI.amber : "transparent"}`,
                    marginBottom: -1, transition: "all 0.12s",
                  }}>{tab.label}</button>
                ))}
              </div>

              <div style={{ padding: "26px 36px" }}>

                {/* ── TOURS TAB ── */}
                {artistTab === "tours" && (
                  <>
                    {artist.tours.length ? artist.tours.map(t => {
                      const todayStr  = new Date().toISOString().slice(0, 10);
                      const conf      = t.shows.filter(s => s.status === "confirmed").length;
                      const adv       = t.shows.filter(s => s.status === "advancing").length;
                      const sent      = t.shows.filter(s => s.advanceToken).length;
                      const portConf  = t.shows.filter(s => advanceStatus(s).label === "CONFIRMED").length;
                      const nextShow  = [...t.shows].sort((a, b) => a.date.localeCompare(b.date)).find(s => s.date >= todayStr);
                      const nextV     = nextShow ? venues[nextShow.venueId] : null;
                      const dUntil    = nextShow ? Math.ceil((new Date(nextShow.date + "T12:00:00") - new Date()) / 864e5) : null;
                      return (
                        <div key={t.id} className="av-tour-row" onClick={() => goTour(artist.id, t.id)}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div className="av-tour-name">{t.name}</div>
                            <div className="av-tour-meta">
                              <span>{t.shows.length} SHOW{t.shows.length !== 1 ? "S" : ""}</span>
                              {t.shows.length > 0 && <span>{tourDateRange(t)}</span>}
                              {conf > 0 && <span style={{ color: "var(--success)", display: "inline-flex", alignItems: "center", gap: 4 }}><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>{conf} confirmed</span>}
                              {adv  > 0 && <span style={{ color: "var(--accent)", display: "inline-flex", alignItems: "center", gap: 4 }}><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a9 9 0 1 1-2.64-6.36"/><polyline points="21 3 21 9 15 9"/></svg>{adv} advancing</span>}
                            </div>
                            {nextShow && nextV && (
                              <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: UI.textMut, marginTop: 5, display: "flex", alignItems: "center", gap: 6 }}>
                                <span style={{ color: dUntil !== null && dUntil <= 7 ? UI.amber : UI.textFnt, fontWeight: dUntil !== null && dUntil <= 7 ? 600 : 400 }}>
                                  {dUntil === 0 ? "Today" : dUntil === 1 ? "Tomorrow" : fmtDate(nextShow.date)}
                                </span>
                                <span style={{ color: UI.textVFnt }}>·</span>
                                <span>{nextV.name}</span>
                                {nextV.city && <><span style={{ color: UI.textVFnt }}>·</span><span>{nextV.city}</span></>}
                              </div>
                            )}
                          </div>
                          <div style={{ display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
                            <div style={{ textAlign: "right" }}>
                              <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 16, fontWeight: 600, color: portConf > 0 ? "var(--success)" : sent > 0 ? "var(--accent)" : UI.textVFnt, lineHeight: 1 }}>{sent}/{t.shows.length}</div>
                              <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: UI.textVFnt, marginTop: 3, letterSpacing: 0.5 }}>PORTALS</div>
                            </div>
                            <svg width="13" height="13" viewBox="0 0 12 12" fill="none" stroke={UI.textFnt} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M4.5 2l4 4-4 4"/></svg>
                          </div>
                        </div>
                      );
                    }) : (
                      <EmptyState
                        icon={<svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>}
                        title="No tours yet"
                        hint={`Create ${artist.name}'s first tour to start adding shows and advancing.`}
                        action={<Button variant="primary" size="sm" onClick={() => setMTour({ mode: "add" })}>+ Add Tour</Button>}
                      />
                    )}
                  </>
                )}

                {/* ── CONTACTS TAB ── */}
                {artistTab === "contacts" && (
                  <>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14, marginBottom: 14 }}>
                      {[
                        {
                          label: "Management", section: "mgmt",
                          content: (artist.mgmt?.company || artist.mgmt?.name || artist.mgmt?.email || artist.mgmt?.phone) ? (
                            <>
                              {artist.mgmt.company && <div className="av-co">{artist.mgmt.company}</div>}
                              {artist.mgmt.name    && <div className="av-sub">{artist.mgmt.name}</div>}
                              <ContactField label="Email" value={artist.mgmt.email} />
                              <ContactField label="Phone" value={artist.mgmt.phone} />
                            </>
                          ) : null,
                        },
                        {
                          label: "Booking", section: "booking",
                          content: (artist.booking?.agency || artist.booking?.agent || artist.booking?.email) ? (
                            <>
                              {artist.booking.agency && <div className="av-co">{artist.booking.agency}</div>}
                              {artist.booking.agent  && <div className="av-sub">{artist.booking.agent}</div>}
                              <ContactField label="Email" value={artist.booking.email} />
                              <ContactField label="Phone" value={artist.booking.phone} />
                            </>
                          ) : null,
                        },
                        {
                          label: "Label", section: "label",
                          content: (artist.label?.name || artist.label?.contact || artist.label?.email) ? (
                            <>
                              {artist.label.name    && <div className="av-co">{artist.label.name}</div>}
                              {artist.label.contact && <div className="av-sub">{artist.label.contact}</div>}
                              <ContactField label="Email" value={artist.label.email} />
                              <ContactField label="Phone" value={artist.label.phone} />
                            </>
                          ) : null,
                        },
                      ].map(card => (
                        <div key={card.section} className="vcm-card vcm-card-editable" onClick={() => setMArtistSection({ artistId: artist.id, section: card.section })}>
                          <div className="av-card-hdr"><span className="av-cat">{card.label}</span><span className="card-edit-hint" style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4z"/></svg>edit</span></div>
                          {card.content || <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: UI.textVFnt }}>Click to add</div>}
                        </div>
                      ))}
                    </div>
                    <div className="vcm-card vcm-card-editable" style={{ marginBottom: 14 }} onClick={() => setMArtistSection({ artistId: artist.id, section: "members" })}>
                      <div className="av-card-hdr"><span className="av-cat">Band Members</span><span className="card-edit-hint" style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4z"/></svg>edit</span></div>
                      {artist.members?.length ? (
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 12 }}>
                          {artist.members.map((m, i) => (
                            <div key={m.id || i}>
                              <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 4 }}>
                                <span className="av-co" style={{ fontSize: 14, marginBottom: 0 }}>{m.name}</span>
                                {m.role && <span style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: UI.textFnt }}>{m.role}</span>}
                              </div>
                              {m.email && <div className="vcm-r"><span className="vcm-k">Email</span><span className="vcm-v">{m.email}</span><CopyBtn text={m.email} /></div>}
                              {m.phone && <div className="vcm-r"><span className="vcm-k">Phone</span><span className="vcm-v">{fmtPhone(m.phone)}</span><CopyBtn text={fmtPhone(m.phone)} /></div>}
                            </div>
                          ))}
                        </div>
                      ) : <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: UI.textVFnt }}>Click to add band members</div>}
                    </div>
                    {/* Online */}
                    <div className="vcm-card vcm-card-editable" onClick={() => setMArtistSection({ artistId: artist.id, section: "online" })}>
                      <div className="av-card-hdr"><span className="av-cat">Online</span><span className="card-edit-hint" style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4z"/></svg>edit</span></div>
                      {(artist.website || artist.socials?.instagram || artist.socials?.twitter || artist.socials?.spotify || artist.socials?.tiktok || artist.socials?.appleMusic) ? (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }} onClick={e => e.stopPropagation()}>
                          {[
                            { k: "website",    v: artist.website,              label: "Website" },
                            { k: "instagram",  v: artist.socials?.instagram,   label: "Instagram" },
                            { k: "twitter",    v: artist.socials?.twitter,     label: "X / Twitter" },
                            { k: "tiktok",     v: artist.socials?.tiktok,      label: "TikTok" },
                            { k: "spotify",    v: artist.socials?.spotify,     label: "Spotify" },
                            { k: "appleMusic", v: artist.socials?.appleMusic,  label: "Apple Music" },
                          ].filter(s => s.v).map(s => (
                            <a key={s.k} href={socialHref(s.k, s.v)} target="_blank" rel="noreferrer" title={s.label}
                              style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 32, height: 32, borderRadius: 8, background: UI.rowBg, border: `1px solid ${UI.panelBdr}`, color: UI.textFnt, textDecoration: "none" }}
                              onMouseEnter={e => { e.currentTarget.style.color = UI.amber; e.currentTarget.style.borderColor = UI.amdBdr; }}
                              onMouseLeave={e => { e.currentTarget.style.color = UI.textFnt; e.currentTarget.style.borderColor = UI.panelBdr; }}>
                              <svg viewBox="0 0 24 24" width={16} height={16} fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">{SOCIAL_SVG_PATHS[s.k]}</svg>
                            </a>
                          ))}
                        </div>
                      ) : <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: UI.textVFnt }}>Click to add website and social links</div>}
                    </div>
                  </>
                )}

                {/* ── ROSTER TAB ── */}
                {artistTab === "roster" && (() => {
                  const FF4 = "'Helvetica Neue', Helvetica, Arial, sans-serif";
                  const rosterProfiles = profiles.filter(p => p.artist_id === artist.id);
                  const grouped = {};
                  rosterProfiles.forEach(p => {
                    const g = p.role_category || "other";
                    if (!grouped[g]) grouped[g] = [];
                    grouped[g].push(p);
                  });

                  const unlinked = profiles.filter(p => !p.artist_id);

                  return (
                    <>
                      {/* Header row */}
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
                        <div>
                          <div style={{ fontFamily: FF4, fontSize: 14, color: UI.text, fontWeight: 600 }}>Permanent Roster</div>
                          <div style={{ fontFamily: FF4, fontSize: 11, color: UI.textMut, marginTop: 3 }}>
                            {rosterProfiles.length === 0 ? "No members yet" : `${rosterProfiles.length} member${rosterProfiles.length !== 1 ? "s" : ""}`}
                            {" · "}Add band and crew who travel with this artist regularly. Assign them to a tour from here.
                          </div>
                        </div>
                        <button className="add-g" onClick={() => setMProfile({ mode: "add", context: { artistId: artist.id } })}>
                          + Add to Roster
                        </button>
                      </div>

                      {rosterProfiles.length === 0 ? (
                        <EmptyState
                          icon={<svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/></svg>}
                          title="No roster members yet"
                          hint="Add band members and regular crew here — they'll be available to assign to each tour."
                          action={<Button variant="primary" size="sm" onClick={() => setMProfile({ mode: "add", context: { artistId: artist.id } })}>+ Add to Roster</Button>}
                        />
                      ) : (
                        PROFILE_ROLE_CATS.filter(cat => grouped[cat]?.length).map(cat => (
                          <div key={cat} style={{ marginBottom: 22 }}>
                            {/* Category heading */}
                            <div style={{ fontFamily: FF4, fontSize: 11, letterSpacing: "2px", textTransform: "uppercase", color: PROFILE_ROLE_COLORS[cat], marginBottom: 10 }}>
                              {PROFILE_ROLE_LABELS[cat]}
                            </div>
                            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 12 }}>
                              {grouped[cat].map(p => {
                                const onThisTour = tour && p.current_tour_id === tour.id;
                                const onOtherTour = p.current_tour_id && (!tour || p.current_tour_id !== tour.id);
                                const otherTourName = onOtherTour ? (() => {
                                  const t = data.artists.flatMap(a => a.tours).find(t => t.id === p.current_tour_id);
                                  return t?.name || "another tour";
                                })() : null;
                                const catCol = PROFILE_ROLE_COLORS[cat];
                                const initials = (p.preferred_name || p.name || "?").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
                                return (
                                  <div key={p.id} style={{ background: UI.rowBg, border: `1px solid ${UI.panelBdr}`, borderLeft: `3px solid ${catCol}`, borderRadius: 8, padding: "14px 16px", display: "flex", flexDirection: "column", gap: 10 }}>
                                    {/* Name + role */}
                                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                      <div style={{ width: 38, height: 38, borderRadius: "50%", background: `${catCol}18`, border: `1.5px solid ${catCol}45`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, fontWeight: 700, color: catCol, flexShrink: 0, overflow: "hidden" }}>
                                        {p.photo_url ? <img src={p.photo_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : initials}
                                      </div>
                                      <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontFamily: FF4, fontSize: 14, fontWeight: 600, color: UI.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.preferred_name || p.name}</div>
                                        {p.role && <div style={{ fontFamily: FF4, fontSize: 11, color: UI.textMut, marginTop: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.role}</div>}
                                      </div>
                                    </div>

                                    {/* Tour status badge */}
                                    <div>
                                      {onThisTour ? (
                                        <span style={{ fontFamily: FF4, fontSize: 11, letterSpacing: "1px", textTransform: "uppercase", color: UI.green, background: UI.ckOnBg, border: `1px solid ${UI.green}44`, borderRadius: 4, padding: "2px 8px", display: "inline-flex", alignItems: "center", gap: 4 }}>
                                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>On {tour.name}
                                        </span>
                                      ) : onOtherTour ? (
                                        <span style={{ fontFamily: FF4, fontSize: 11, letterSpacing: "1px", textTransform: "uppercase", color: UI.amber, background: UI.amdBg, border: `1px solid ${UI.amber}44`, borderRadius: 4, padding: "2px 8px" }}>
                                          On {otherTourName}
                                        </span>
                                      ) : (
                                        <span style={{ fontFamily: FF4, fontSize: 11, letterSpacing: "1px", textTransform: "uppercase", color: UI.textFnt, background: UI.rowBg, border: `1px solid ${UI.fieldBdr}`, borderRadius: 4, padding: "2px 8px" }}>
                                          Not assigned to a tour
                                        </span>
                                      )}
                                    </div>

                                    {/* Actions row */}
                                    <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
                                      {tour && !onThisTour && (
                                        <button
                                          onClick={() => addProfileToTour(p.id, tour.id)}
                                          style={{ fontFamily: FF4, fontSize: 11, padding: "5px 12px", borderRadius: 4, border: `1px solid ${UI.amber}`, cursor: "pointer", background: `${UI.amber}18`, color: UI.amber, fontWeight: 600, flex: 1, transition: "all 0.12s" }}
                                          onMouseEnter={e => { e.currentTarget.style.background = UI.amber; e.currentTarget.style.color = "var(--accent-fg)"; }}
                                          onMouseLeave={e => { e.currentTarget.style.background = `${UI.amber}18`; e.currentTarget.style.color = UI.amber; }}>
                                          + Add to {tour.name}
                                        </button>
                                      )}
                                      {onThisTour && (
                                        <button
                                          onClick={() => removeProfileFromTour(p.id)}
                                          style={{ fontFamily: FF4, fontSize: 11, padding: "5px 12px", borderRadius: 4, border: `1px solid ${UI.fieldBdr}`, cursor: "pointer", background: "transparent", color: UI.textMut, flex: 1, transition: "all 0.12s" }}
                                          onMouseEnter={e => { e.currentTarget.style.borderColor = "var(--danger)"; e.currentTarget.style.color = "var(--danger)"; }}
                                          onMouseLeave={e => { e.currentTarget.style.borderColor = UI.fieldBdr; e.currentTarget.style.color = UI.textMut; }}>
                                          Remove from Tour
                                        </button>
                                      )}
                                      <button
                                        onClick={async () => {
                                          const privData = await loadProfilePrivate(p.id);
                                          setMProfile({ mode: "edit", profile: p, privateData: privData, context: { artistId: artist.id } });
                                        }}
                                        style={{ fontFamily: FF4, fontSize: 11, padding: "5px 12px", borderRadius: 4, border: `1px solid ${UI.fieldBdr}`, cursor: "pointer", background: "transparent", color: UI.textMut, transition: "all 0.12s" }}
                                        onMouseEnter={e => { e.currentTarget.style.borderColor = UI.textFnt; e.currentTarget.style.color = UI.text; }}
                                        onMouseLeave={e => { e.currentTarget.style.borderColor = UI.fieldBdr; e.currentTarget.style.color = UI.textMut; }}>
                                        <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4z"/></svg>Edit</span>
                                      </button>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ))
                      )}

                      {/* Tour picker — shown when there are roster members and tours exist */}
                      {rosterProfiles.length > 0 && artist.tours.length > 0 && (
                        <div style={{ marginTop: 20, padding: "14px 16px", background: UI.panelAlt, border: `1px solid ${UI.panelBdr}`, borderRadius: 8 }}>
                          <div style={{ fontFamily: FF4, fontSize: 11, letterSpacing: "1px", textTransform: "uppercase", color: UI.textVFnt, marginBottom: 10 }}>
                            Assign to tour
                          </div>
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                            {artist.tours.map(t => {
                              const active = tour?.id === t.id;
                              return (
                                <button
                                  key={t.id}
                                  onClick={() => setSelT(active ? null : t.id)}
                                  style={{ fontFamily: FF4, fontSize: 11, padding: "6px 14px", borderRadius: 999, border: `1px solid ${active ? UI.amber : UI.fieldBdr}`, cursor: "pointer", background: active ? `${UI.amber}20` : "transparent", color: active ? UI.amber : UI.textMut, fontWeight: active ? 700 : 400, transition: "all 0.12s", display: "inline-flex", alignItems: "center", gap: 5 }}
                                  onMouseEnter={e => { if (!active) { e.currentTarget.style.borderColor = UI.textFnt; e.currentTarget.style.color = UI.text; } }}
                                  onMouseLeave={e => { if (!active) { e.currentTarget.style.borderColor = UI.fieldBdr; e.currentTarget.style.color = UI.textMut; } }}>
                                  {active && <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>}{t.name}
                                </button>
                              );
                            })}
                          </div>
                          {!tour && (
                            <div style={{ fontFamily: FF4, fontSize: 11, color: UI.textVFnt, marginTop: 10 }}>
                              Select a tour above to assign or remove roster members from it.
                            </div>
                          )}
                        </div>
                      )}

                      {/* ── Add existing Touring Party profiles to this roster ── */}
                      {unlinked.length > 0 && (
                        <details style={{ marginTop: 28, borderTop: `1px solid ${UI.panelBdr}`, paddingTop: 22 }}>
                          <summary style={{ cursor: "pointer", listStyle: "none", display: "flex", alignItems: "center", gap: 8, userSelect: "none" }}>
                            <span style={{ fontFamily: FF4, fontSize: 11, fontWeight: 600, color: UI.textFnt }}>
                              Add unlinked team profiles to this roster
                            </span>
                            <span style={{ fontFamily: FF4, fontSize: 11, color: UI.textMut, background: UI.rowBg, border: `1px solid ${UI.panelBdr}`, borderRadius: 8, padding: "1px 8px" }}>
                              {unlinked.length}
                            </span>
                          </summary>
                          <div style={{ fontFamily: FF4, fontSize: 11, color: UI.textMut, margin: "8px 0 14px", lineHeight: 1.6 }}>
                            These profiles exist in your team but aren't linked to any artist roster. They may be crew or staff who work across multiple artists — add them here if they belong on this roster.
                          </div>
                          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 10 }}>
                            {unlinked.map(p => {
                              const catCol = PROFILE_ROLE_COLORS[p.role_category] || PROFILE_ROLE_COLORS.other;
                              const initials = (p.preferred_name || p.name || "?").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
                              return (
                                <div key={p.id} style={{ background: UI.rowBg, border: `1px solid ${UI.panelBdr}`, borderRadius: 8, padding: "12px 14px", display: "flex", alignItems: "center", gap: 12 }}>
                                  <div style={{ width: 34, height: 34, borderRadius: "50%", background: `${catCol}18`, border: `1.5px solid ${catCol}45`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 700, color: catCol, flexShrink: 0, overflow: "hidden" }}>
                                    {p.photo_url ? <img src={p.photo_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : initials}
                                  </div>
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontFamily: FF4, fontSize: 12, fontWeight: 600, color: UI.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.preferred_name || p.name}</div>
                                    {p.role && <div style={{ fontFamily: FF4, fontSize: 11, color: UI.textMut, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.role}</div>}
                                  </div>
                                  <button
                                    onClick={async (e) => {
                                      e.currentTarget.disabled = true;
                                      const { error } = await supabase.from('people_profiles')
                                        .update({ artist_id: artist.id })
                                        .eq('id', p.id);
                                      if (!error) await loadProfiles(teamId);
                                      else e.currentTarget.disabled = false;
                                    }}
                                    style={{ fontFamily: FF4, fontSize: 11, padding: "5px 10px", borderRadius: 4, border: `1px solid ${UI.amber}`, cursor: "pointer", background: `${UI.amber}18`, color: UI.amber, fontWeight: 700, whiteSpace: "nowrap", flexShrink: 0, transition: "all 0.12s" }}
                                    onMouseEnter={e => { e.currentTarget.style.background = UI.amber; e.currentTarget.style.color = "var(--accent-fg)"; }}
                                    onMouseLeave={e => { e.currentTarget.style.background = `${UI.amber}18`; e.currentTarget.style.color = UI.amber; }}>
                                    + Add to Roster
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        </details>
                      )}
                    </>
                  );
                })()}

                {/* ── RIDER & FILES TAB ── */}
                {artistTab === "rider" && (
                  <>
                    {/* Rider notes (text only — files go in Tour Documents below) */}
                    <div className="vcm-card vcm-card-editable" style={{ marginBottom: 14 }} onClick={() => setMArtistSection({ artistId: artist.id, section: "rider" })}>
                      <div className="av-card-hdr"><span className="av-cat">Hospitality Rider Notes</span><span className="card-edit-hint" style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4z"/></svg>edit</span></div>
                      {artist.rider
                        ? <div style={{ fontSize: 12, color: UI.textMid, lineHeight: 1.7, whiteSpace: "pre-wrap" }}>{artist.rider}</div>
                        : <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: UI.textVFnt }}>Click to add hospitality notes (dietary, dressing room, etc.)</div>
                      }
                    </div>
                    {/* ── Tour Documents ── */}
                    {(() => {
                      const allDocs = artist.documents || [];
                      const tourDocs = allDocs.filter(d => d.section !== "press");
                      const FF4 = "'Helvetica Neue', Helvetica, Arial, sans-serif";
                      const totalCount = tourDocs.length;
                      return (
                        <div className="vcm-card" onClick={e => e.stopPropagation()}>
                          <div className="av-card-hdr" style={{ marginBottom: 14 }}>
                            <span className="av-cat">Tour Documents</span>
                            {totalCount > 0 && <span style={{ fontFamily: FF4, fontSize: 11, color: UI.green, marginLeft: 8 }}>{totalCount} file{totalCount !== 1 ? "s" : ""}</span>}
                          </div>

                          {TOUR_DOC_CATEGORIES.map(cat => {
                            // Stage Plot category — powered by PLOTR
                            if (cat.type === "stage_plot") {
                              const plots = artistStagePlots[artist.id] || [];
                              const plotrBase = "https://plotr-six.vercel.app";
                              const FF4inner = "'Helvetica Neue', Helvetica, Arial, sans-serif";
                              return (
                                <div key="stage_plot" style={{ marginBottom: 14 }}>
                                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: plots.length > 0 ? 6 : 4 }}>
                                    <span style={{ fontFamily: FF4inner, fontSize: 11, letterSpacing: "1.2px", textTransform: "uppercase", color: UI.textFnt, whiteSpace: "nowrap" }}>
                                      Stage Plots
                                    </span>
                                    <span style={{ flex: 1, height: 1, background: UI.panelBdr }} />
                                    {stagePlotsLoading[artist.id] && <span style={{ fontFamily: FF4inner, fontSize: 11, color: UI.textVFnt }}>Loading…</span>}
                                    {/* Upload a file (PDF, image, etc.) */}
                                    <label style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, padding: "3px 9px", borderRadius: 4, border: `1px solid ${UI.panelBdr}`, background: UI.panelAlt, color: UI.textFnt, cursor: artistDocUploading ? "default" : "pointer", opacity: artistDocUploading ? 0.6 : 1 }}>
                                      {artistDocUploading ? "…" : "+ Upload"}
                                      <input type="file" accept={cat.accept} multiple style={{ display: "none" }} disabled={artistDocUploading}
                                        onChange={async e => {
                                          const files = Array.from(e.target.files || []);
                                          for (const f of files) {
                                            await uploadArtistDoc(f, artist.id, cat.label, { section: "tour", type: cat.type });
                                          }
                                          e.target.value = "";
                                        }} />
                                    </label>
                                    {/* Build interactively in PLOTR */}
                                    <a
                                      href={`${plotrBase}?artistId=${artist.id}`}
                                      target="_blank"
                                      rel="noreferrer"
                                      style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, padding: "3px 9px", borderRadius: 4, border: `1px solid ${UI.panelBdr}`, background: UI.panelAlt, color: UI.amber, cursor: "pointer", textDecoration: "none" }}
                                    >
                                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
                                      </svg>
                                      Build in PLOTR
                                    </a>
                                  </div>
                                  {plots.length > 0 && (
                                    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                                      {plots.map(plot => (
                                        <div key={plot.id} style={{ display: "flex", alignItems: "center", gap: 8, background: UI.rowBg, border: `1px solid ${UI.panelBdr}`, borderRadius: 8, padding: "6px 10px" }}>
                                          <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ fontSize: 12, color: UI.textFnt, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{plot.name}</div>
                                            <div style={{ fontFamily: FF4inner, fontSize: 11, color: UI.textVFnt, marginTop: 1 }}>
                                              Updated {new Date(plot.updated_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                                            </div>
                                          </div>
                                          <a
                                            href={`${plotrBase}?artistId=${artist.id}&plotId=${plot.id}`}
                                            target="_blank"
                                            rel="noreferrer"
                                            style={{ fontFamily: FF4inner, fontSize: 11, color: UI.amber, textDecoration: "none", flexShrink: 0 }}
                                          >
                                            Edit
                                          </a>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                  {!stagePlotsLoading[artist.id] && plots.length === 0 && (
                                    <div style={{ fontFamily: FF4inner, fontSize: 11, color: UI.textVFnt }}>
                                      No stage plots yet. Click "Build in PLOTR" to create one.
                                    </div>
                                  )}
                                </div>
                              );
                            }
                            const catDocs = tourDocs.filter(d => d.type === cat.type || (cat.type === "other" && !TOUR_DOC_CATEGORIES.slice(0,-1).some(c => c.type === d.type)));
                            return (
                              <div key={cat.type} style={{ marginBottom: 14 }}>
                                {/* Category header */}
                                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: catDocs.length > 0 ? 6 : 4 }}>
                                  <span style={{ fontFamily: FF4, fontSize: 11, letterSpacing: "1.2px", textTransform: "uppercase", color: UI.textFnt, whiteSpace: "nowrap" }}>
                                    {cat.label}
                                  </span>
                                  <span style={{ flex: 1, height: 1, background: UI.panelBdr }} />
                                  <label style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, padding: "3px 9px", borderRadius: 4, border: `1px solid ${UI.panelBdr}`, background: UI.panelAlt, color: UI.textFnt, cursor: artistDocUploading ? "default" : "pointer", opacity: artistDocUploading ? 0.6 : 1 }}>
                                    {artistDocUploading ? "…" : "+ Add"}
                                    <input type="file" accept={cat.accept} multiple style={{ display: "none" }} disabled={artistDocUploading}
                                      onChange={async e => {
                                        const files = Array.from(e.target.files || []);
                                        for (const f of files) {
                                          await uploadArtistDoc(f, artist.id, cat.label, { section: "tour", type: cat.type });
                                        }
                                        e.target.value = "";
                                      }} />
                                  </label>
                                </div>
                                {/* Files in this category */}
                                {catDocs.length > 0 && (
                                  <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                                    {catDocs.map(doc => (
                                      <div key={doc.id} style={{ display: "flex", alignItems: "center", gap: 8, background: UI.rowBg, border: `1px solid ${UI.panelBdr}`, borderRadius: 8, padding: "6px 10px" }}>
                                        <a href={doc.fileUrl} target="_blank" rel="noreferrer"
                                          style={{ fontSize: 12, color: UI.textFnt, textDecoration: "none", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                                          onMouseEnter={e => e.currentTarget.style.color = UI.amber}
                                          onMouseLeave={e => e.currentTarget.style.color = UI.textFnt}>
                                          {doc.fileName}
                                        </a>
                                        <button onClick={() => removeArtistDoc(artist.id, doc.id)}
                                          style={{ background: "none", border: "none", cursor: "pointer", color: UI.textVFnt, padding: "0 2px", lineHeight: 1, display: "inline-flex", alignItems: "center" }}
                                          onMouseEnter={e => e.currentTarget.style.color = "var(--danger)"}
                                          onMouseLeave={e => e.currentTarget.style.color = UI.textVFnt}
                                          title="Remove"><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></button>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })}

                          {artistDocUploadErr && <div style={{ fontSize: 11, color: "var(--danger)", marginTop: 6 }}>{artistDocUploadErr}</div>}
                        </div>
                      );
                    })()}

                    {/* ── Press Kit ── */}
                    {(() => {
                      const allDocs = artist.documents || [];
                      const pressDocs = allDocs.filter(d => d.section === "press");
                      const FF4 = "'Helvetica Neue', Helvetica, Arial, sans-serif";
                      return (
                        <div className="vcm-card" onClick={e => e.stopPropagation()} style={{ marginTop: 14 }}>
                          <div className="av-card-hdr" style={{ marginBottom: 14 }}>
                            <span className="av-cat">Press Kit</span>
                            {pressDocs.length > 0 && <span style={{ fontFamily: FF4, fontSize: 11, color: UI.green, marginLeft: 8 }}>{pressDocs.length} file{pressDocs.length !== 1 ? "s" : ""}</span>}
                          </div>

                          {PRESS_CATEGORIES.map(cat => {
                            const catDocs = pressDocs.filter(d => d.category === cat);
                            const catAccept = PRESS_CATEGORY_ACCEPT[cat] || "*";
                            return (
                              <div key={cat} style={{ marginBottom: 14 }}>
                                {/* Category header */}
                                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: catDocs.length > 0 ? 6 : 4 }}>
                                  <span style={{ fontFamily: FF4, fontSize: 11, letterSpacing: "1.2px", textTransform: "uppercase", color: UI.textFnt, display: "flex", alignItems: "center", gap: 5 }}>
                                    <PressKitIcon cat={cat} size={11} color={UI.textFnt} />{cat}
                                  </span>
                                  <span style={{ flex: 1, height: 1, background: UI.panelBdr }} />
                                  <label style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, padding: "3px 9px", borderRadius: 4, border: `1px solid ${UI.panelBdr}`, background: UI.panelAlt, color: UI.textFnt, cursor: artistDocUploading ? "default" : "pointer", opacity: artistDocUploading ? 0.6 : 1 }}>
                                    {artistDocUploading ? "…" : "+ Add"}
                                    <input type="file" accept={catAccept} multiple style={{ display: "none" }} disabled={artistDocUploading}
                                      onChange={async e => {
                                        const files = Array.from(e.target.files || []);
                                        for (const f of files) {
                                          await uploadArtistDoc(f, artist.id, cat, { section: "press", category: cat });
                                        }
                                        e.target.value = "";
                                      }} />
                                  </label>
                                </div>
                                {/* Files in this category */}
                                {catDocs.length > 0 && (
                                  <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                                    {catDocs.map(doc => (
                                      <div key={doc.id} style={{ display: "flex", alignItems: "center", gap: 8, background: UI.rowBg, border: `1px solid ${UI.panelBdr}`, borderRadius: 8, padding: "6px 10px" }}>
                                        <a href={doc.fileUrl} target="_blank" rel="noreferrer"
                                          style={{ fontSize: 12, color: UI.textFnt, textDecoration: "none", flex: 1, minWidth: 0, display: "inline-flex", alignItems: "center", gap: 6 }}
                                          onMouseEnter={e => e.currentTarget.style.color = UI.amber}
                                          onMouseLeave={e => e.currentTarget.style.color = UI.textFnt}>
                                          <span style={{ flexShrink: 0, display: "inline-flex" }}><PressKitIcon cat={cat} size={11} color={UI.textFnt} /></span>
                                          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{doc.fileName}</span>
                                        </a>
                                        <button onClick={() => removeArtistDoc(artist.id, doc.id)}
                                          style={{ background: "none", border: "none", cursor: "pointer", color: UI.textVFnt, padding: "0 2px", lineHeight: 1, display: "inline-flex", alignItems: "center" }}
                                          onMouseEnter={e => e.currentTarget.style.color = "var(--danger)"}
                                          onMouseLeave={e => e.currentTarget.style.color = UI.textVFnt}
                                          title="Remove"><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></button>
                                      </div>
                                    ))}
                                  </div>
                                )}
                                {catDocs.length === 0 && (
                                  <div style={{ fontFamily: FF4, fontSize: 11, color: UI.textVFnt, paddingLeft: 2 }}>
                                    {cat === "Photos" && "High-res press photos, artist portraits"}
                                    {cat === "Bios" && "Artist bio (short & long form), fact sheet"}
                                    {cat === "Admats" && "Posters, banners, show-specific artwork"}
                                    {cat === "Fonts" && "Brand fonts used in marketing materials"}
                                    {cat === "Promo Copy" && "Blurbs, taglines, social captions"}
                                    {cat === "Other" && "Anything else for the promoter's press team"}
                                  </div>
                                )}
                              </div>
                            );
                          })}

                          {artistDocUploadErr && <div style={{ fontSize: 11, color: "var(--danger)", marginTop: 6 }}>{artistDocUploadErr}</div>}
                        </div>
                      );
                    })()}
                  </>
                )}

              </div>
            </>
          )}

          {/* USER PROFILE */}
          {view === "profile" && (
            <>
              <div className="ph">
                <div><div className="ph-title">My Profile</div><div className="ph-sub">ACCOUNT SETTINGS</div></div>
              </div>

              <div style={{ padding: "26px 36px", display: "grid", gridTemplateColumns: "380px 1fr", gap: 28, maxWidth: 860 }}>
                {/* Left: editable fields */}
                <div>
                <div className="vcm-card" style={{ marginBottom: 16 }}>
                  <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, letterSpacing: "1.5px", textTransform: "uppercase", color: UI.textMut, marginBottom: 16, fontWeight: 600 }}>Account Info</div>

                  {/* Email — read-only */}
                  <div style={{ marginBottom: 14 }}>
                    <div className="prf-lbl">Email</div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <div style={{ fontSize: 14, color: UI.textMut, flex: 1 }}>{user?.email}</div>
                      <CopyBtn text={user?.email} />
                    </div>
                  </div>

                  {[
                    { k: "name",    l: "Your Name",    ph: "e.g. Martin Earley"   },
                    { k: "agency",  l: "Agency",        ph: "e.g. LR Artists"      },
                  ].map(f => (
                    <div key={f.k} style={{ marginBottom: 12 }}>
                      <div className="prf-lbl">{f.l}</div>
                      <input className="prf-inp" placeholder={f.ph}
                        value={data.profile?.[f.k] || ""}
                        onChange={e => updProfile(f.k, e.target.value)} />
                    </div>
                  ))}

                  <div style={{ marginBottom: 12 }}>
                    <div className="prf-lbl">Phone</div>
                    <PhoneInput
                      className="prf-inp"
                      value={data.profile?.phone || ""}
                      onChange={v => updProfile("phone", v)}
                    />
                  </div>

                  <div style={{ marginBottom: 12 }}>
                    <div className="prf-lbl">Website</div>
                    <input className="prf-inp" placeholder="https://lrartists.com"
                      value={data.profile?.website || ""}
                      onChange={e => updProfile("website", e.target.value)} />
                  </div>

                  <div style={{ marginTop: 6, fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: UI.green, opacity: saved ? 1 : 0, transition: "opacity 0.4s", letterSpacing: 1, display: "flex", alignItems: "center", gap: 5 }}>
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                    SAVED
                  </div>

                  {/* Change password — makes "Account Settings" actually contain account settings */}
                  <div style={{ marginTop: 16, paddingTop: 14, borderTop: `1px solid ${UI.panelBdr}` }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                      <div>
                        <div className="prf-lbl" style={{ marginBottom: 0 }}>Password</div>
                        <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: UI.textFnt, marginTop: 2 }}>Change your sign-in password</div>
                      </div>
                      <button onClick={() => { setPwOpen(o => !o); setPwMsg(null); setPwValue(""); }}
                        style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: UI.textMut, background: "none", border: `1px solid ${UI.fieldBdr}`, borderRadius: 4, padding: "5px 14px", cursor: "pointer", flexShrink: 0 }}>
                        {pwOpen ? "Close" : "Change"}
                      </button>
                    </div>
                    {pwOpen && (
                      <div style={{ marginTop: 10 }}>
                        <input className="prf-inp" type="password" placeholder="New password (min 8 characters)"
                          value={pwValue} onChange={e => setPwValue(e.target.value)} autoComplete="new-password" />
                        <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 8 }}>
                          <button className="abtn" disabled={pwSaving || pwValue.length < 8} style={{ opacity: pwSaving || pwValue.length < 8 ? 0.5 : 1 }}
                            onClick={async () => {
                              setPwSaving(true); setPwMsg(null);
                              const { error } = await supabase.auth.updateUser({ password: pwValue });
                              setPwSaving(false);
                              if (error) { setPwMsg(error.message || "Couldn't update password"); }
                              else { setPwMsg("saved"); setPwValue(""); setTimeout(() => { setPwOpen(false); setPwMsg(null); }, 1800); }
                            }}>
                            {pwSaving ? "Updating…" : "Update Password"}
                          </button>
                          {pwMsg === "saved" && <span style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: UI.success }}>Password updated</span>}
                          {pwMsg && pwMsg !== "saved" && <span style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: UI.danger }}>{pwMsg}</span>}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Team card */}
                <div className="vcm-card" style={{ cursor: "pointer" }} onClick={() => setMTeam(true)}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                    <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, letterSpacing: "1.5px", textTransform: "uppercase", color: UI.textMut, fontWeight: 600 }}>Team</div>
                    <span style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: UI.textFnt, letterSpacing: 1 }}>MANAGE →</span>
                  </div>
                  <div style={{ fontSize: 14, color: UI.textMid, fontWeight: 500, marginBottom: 8 }}>{teamName}</div>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {teamMembers.map(m => (
                      <div key={m.user_id} title={m.email}
                        style={{ width: 26, height: 26, borderRadius: "50%", background: UI.panelAlt, border: `1px solid ${UI.fieldBdr}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, color: UI.textMut }}>
                        {(m.email || "?")[0].toUpperCase()}
                      </div>
                    ))}
                  </div>
                  <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: UI.textFnt, marginTop: 8, letterSpacing: 1 }}>
                    {teamMembers.length} MEMBER{teamMembers.length !== 1 ? "S" : ""}
                  </div>
                </div>
                </div>

                {/* Right: roster summary */}
                <div className="vcm-card" style={{ alignSelf: "start" }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
                    <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, letterSpacing: "1.5px", textTransform: "uppercase", color: UI.textMut, fontWeight: 600 }}>Your Roster</div>
                    <button className="add-g" style={{ fontSize: 11, padding: "4px 10px" }} onClick={() => setMArtist(true)}>+ Add Artist</button>
                  </div>
                  {data.artists.map((a, ai) => {
                    const c = artistColor(a.colorIdx ?? ai);
                    const totalShows = a.tours.flatMap(t => t.shows).length;
                    return (
                      <div key={a.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: `1px solid ${UI.panelBdr}`, cursor: "pointer" }}
                        onClick={() => { setSelA(a.id); setSelT(null); setSelS(null); setView("artist"); }}>
                        <div style={{ width: 8, height: 8, borderRadius: 4, background: c.pill, flexShrink: 0 }} />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 14, color: UI.textMid, fontWeight: 500 }}>{a.name}</div>
                          <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: UI.textFnt, marginTop: 1 }}>
                            {a.tours.length} tour{a.tours.length !== 1 ? "s" : ""} · {totalShows} show{totalShows !== 1 ? "s" : ""}
                          </div>
                        </div>
                        <span style={{ color: UI.textFnt, fontSize: 16 }}>›</span>
                      </div>
                    );
                  })}
                  {data.artists.length === 0 && (
                    <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: UI.textVFnt }}>No artists on roster yet.</div>
                  )}
                </div>
              </div>

              {/* ── Network Settings — only shown when this user has a claimed touring profile ── */}
              {(() => {
                const FF = "'Helvetica Neue', Helvetica, Arial, sans-serif";
                const myProfile = profiles.find(p => p.user_id === user?.id);
                if (!myProfile) return null;
                const catColor = PROFILE_ROLE_COLORS[myProfile.role_category] || "#6a8faf";
                const allTours = data.artists.flatMap(a => a.tours.map(t => ({
                  ...t, shows: t.shows.map(s => ({ ...s, _city: venues[s.venueId]?.city || "" }))
                })));
                // ── Hoisted helpers — must live here to avoid esbuild TDZ from conditional IIFEs ──
                // All profile editing now goes through the shared <ProfileEditor> (controlled
                // inputs + debounced auto-save) — these are just its data callbacks.
                const uploadMyPhoto = async (file) => {
                  try {
                    const ext = file.name.split(".").pop();
                    const path = `profiles/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
                    const { error } = await supabase.storage.from("advance-files").upload(path, file, { upsert: true });
                    if (error) return { error };
                    const { data: urlData } = supabase.storage.from("advance-files").getPublicUrl(path);
                    const { error: updErr } = await supabase.from('people_profiles').update({ photo_url: urlData.publicUrl }).eq('id', myProfile.id);
                    if (updErr) return { error: updErr };
                    setProfiles(prev => prev.map(p => p.id === myProfile.id ? { ...p, photo_url: urlData.publicUrl } : p));
                    return {};
                  } catch (err) { return { error: err }; }
                };
                const saveMyProfilePatch = async (patch) => {
                  // RLS can block silently (no error, 0 rows) — only success if a row came back.
                  const { data: upd, error } = await supabase.from('people_profiles').update(patch).eq('id', myProfile.id).select();
                  if (error || !upd || upd.length === 0) return { error: error || "blocked" };
                  setProfiles(prev => prev.map(p => p.id === myProfile.id ? { ...p, ...patch } : p));
                  return {};
                };
                const saveMyPrivatePatch = async (patch) => {
                  const existing = profilePrivate[myProfile.id];
                  if (existing?.id) {
                    const { error } = await supabase.from('people_private').update(patch).eq('id', existing.id);
                    if (error) return { error };
                    setProfilePrivate(p => ({ ...p, [myProfile.id]: { ...existing, ...patch } }));
                  } else {
                    const row = { ...patch, profile_id: myProfile.id, team_id: myProfile.team_id };
                    const { data: ins, error } = await supabase.from('people_private').insert(row).select().single();
                    if (error) return { error };
                    if (ins) setProfilePrivate(p => ({ ...p, [myProfile.id]: ins }));
                  }
                  return {};
                };

                // ── Routing sync: source tour, staleness detection, re-sync ──
                const routingFromTour = (t) => (t.shows || []).map(s => ({ date: s.date, city: s._city || "" })).filter(r => r.date);
                const syncRoutingFromTour = async (t) => {
                  if (!t) return;
                  const patch = {
                    tour_routing: routingFromTour(t), routing_tour_name: t.name,
                    routing_tour_id: t.id, routing_synced_at: new Date().toISOString(),
                  };
                  const { error } = await supabase.from('people_profiles').update(patch).eq('id', myProfile.id);
                  if (!error) setProfiles(prev => prev.map(p => p.id === myProfile.id ? { ...p, ...patch } : p));
                };
                const routingSourceTour = myProfile.routing_tour_id
                  ? allTours.find(t => t.id === myProfile.routing_tour_id)
                  : allTours.find(t => t.name === myProfile.routing_tour_name);
                const routingStale = !!(routingSourceTour && (myProfile.tour_routing || []).length > 0 &&
                  JSON.stringify(routingFromTour(routingSourceTour)) !== JSON.stringify(myProfile.tour_routing));
                const routingSyncedLabel = myProfile.routing_synced_at
                  ? new Date(myProfile.routing_synced_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })
                  : null;

                // Unlink this touring profile from the current account. Non-destructive:
                // clears user_id + claimed_at so the profile reverts to an unclaimed team
                // profile (stays in the touring party, re-claimable later via its invite link).
                const handleUnlink = async () => {
                  const nm = myProfile.preferred_name || myProfile.name || "this profile";
                  if (!window.confirm(`Unlink "${nm}" from your account?\n\nThis disconnects the touring profile from your login. The profile stays in your touring party as unclaimed, and can be claimed again later via its invite link.`)) return;
                  const { data: upd, error } = await supabase.from('people_profiles').update({ user_id: null, claimed_at: null }).eq('id', myProfile.id).select();
                  // RLS can block silently (no error, 0 rows) — only treat as success if a row came back.
                  if (error || !upd || upd.length === 0) { window.alert("Couldn't unlink the profile — please try again."); return; }
                  setProfiles(prev => prev.map(p => p.id === myProfile.id ? { ...p, user_id: null, claimed_at: null } : p));
                };

                // ── Reusable settings row layout ──────────────────────────────────────────
                // Use this for every settings section: title + description on the left,
                // any action (button, toggle, etc.) on the right. Keeps the panel consistent.
                //
                // Usage:
                //   {renderSettingRow({
                //     title: "Section Title",
                //     description: "One-line explanation of what this controls.",
                //     action: <button>Edit</button>,        // optional
                //     border: true,                         // adds bottom divider (default true)
                //     children: myExpandedContent,          // optional content below the row
                //   })}
                const renderSettingRow = ({ title, description, action, border = true, children }) => (
                  <div style={{ paddingTop: 20, paddingBottom: children ? 0 : 20, ...(border ? { borderBottom: `1px solid ${UI.panelBdr}` } : {}) }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, marginBottom: children ? 16 : 0 }}>
                      <div>
                        <div style={{ fontFamily: FF, fontSize: 11, fontWeight: 600, color: UI.text, marginBottom: 3 }}>{title}</div>
                        {description && <div style={{ fontFamily: FF, fontSize: 11, color: UI.textMut, lineHeight: 1.5 }}>{description}</div>}
                      </div>
                      {action && <div style={{ flexShrink: 0 }}>{action}</div>}
                    </div>
                    {children && <div style={{ paddingBottom: 20 }}>{children}</div>}
                  </div>
                );

                return (
                  <div style={{ padding: "0 36px 36px", maxWidth: 860 }}>
                    <div style={{ background: UI.rowBg, border: `1px solid ${UI.panelBdr}`, borderRadius: 8, padding: "24px 28px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16, paddingBottom: 16, borderBottom: `1px solid ${UI.panelBdr}` }}>
                        <div style={{ width: 40, height: 40, borderRadius: "50%", background: `${catColor}22`, border: `2px solid ${catColor}44`, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", flexShrink: 0 }}>
                          {myProfile.photo_url
                            ? <img src={myProfile.photo_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                            : <span style={{ fontFamily: FF, fontSize: 16, fontWeight: 700, color: catColor }}>{(myProfile.preferred_name || myProfile.name || "?")[0].toUpperCase()}</span>
                          }
                        </div>
                        <div>
                          <div style={{ fontFamily: FF, fontSize: 14, fontWeight: 600, color: UI.text }}>{myProfile.preferred_name || myProfile.name}</div>
                          <div style={{ fontFamily: FF, fontSize: 11, color: catColor }}>{PROFILE_ROLE_LABELS[myProfile.role_category]}{myProfile.role ? ` · ${myProfile.role}` : ""}</div>
                        </div>
                        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8 }}>
                          <span style={{ fontFamily: FF, fontSize: 11, letterSpacing: "1px", background: UI.successBg, color: UI.success, border: `1px solid ${UI.successBdr}`, borderRadius: 4, padding: "2px 8px" }}>Profile Claimed</span>
                          <button onClick={handleUnlink} title="Disconnect this touring profile from your account"
                            style={{ fontFamily: FF, fontSize: 11, color: UI.textMut, background: "none", border: `1px solid ${UI.fieldBdr}`, borderRadius: 4, padding: "3px 10px", cursor: "pointer", transition: "all 0.12s" }}
                            onMouseEnter={e => { e.currentTarget.style.borderColor = "var(--danger)"; e.currentTarget.style.color = "var(--danger)"; }}
                            onMouseLeave={e => { e.currentTarget.style.borderColor = UI.fieldBdr; e.currentTarget.style.color = UI.textMut; }}>Unlink</button>
                        </div>
                      </div>

                      {/* Shared profile editor — identity, photo, skills, visibility, travel info.
                          Same component the mobile MemberProfileScreen uses; auto-saves as you type. */}
                      <div style={{ paddingTop: 20 }}>
                        <ProfileEditor
                          profile={myProfile}
                          privateData={profilePrivate[myProfile.id] || null}
                          onSaveProfile={saveMyProfilePatch}
                          onSavePrivate={saveMyPrivatePatch}
                          uploadPhoto={uploadMyPhoto}
                          UI={UI}
                          variant="desktop"
                        />
                      </div>

                      {/* Routing sync — desktop-only (TMs sync from their own tours) */}
                      {myProfile.visible_to_network && renderSettingRow({
                        title: "Share Routing",
                        description: "Let connections see when you're in the same city on tour.",
                        border: false,
                        children: (
                          <div>
                            {(myProfile.tour_routing || []).length > 0 && (
                              <div style={{ fontFamily: FF, fontSize: 11, marginBottom: 10, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                                <span style={{ color: UI.success, display: "inline-flex", alignItems: "center", gap: 5 }}>
                                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={UI.success} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                                  Sharing {myProfile.tour_routing.length} dates{myProfile.routing_tour_name ? ` from "${myProfile.routing_tour_name}"` : ""}{routingSyncedLabel ? ` · synced ${routingSyncedLabel}` : ""}
                                </span>
                                <button onClick={async () => {
                                  const cleared = { tour_routing: [], routing_tour_name: "", routing_tour_id: null, routing_synced_at: null };
                                  await supabase.from('people_profiles').update(cleared).eq('id', myProfile.id);
                                  setProfiles(prev => prev.map(p => p.id === myProfile.id ? { ...p, ...cleared } : p));
                                }} style={{ fontFamily: FF, fontSize: 11, color: UI.danger, background: "none", border: "none", cursor: "pointer" }}>Clear</button>
                              </div>
                            )}
                            {routingStale && (
                              <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 12px", marginBottom: 10, background: UI.warningBg, border: `1px solid ${UI.warningBdr}`, borderRadius: 6 }}>
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={UI.warning} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>
                                <span style={{ fontFamily: FF, fontSize: 11, color: UI.warning, flex: 1 }}>
                                  "{routingSourceTour?.name}" has changed since your last sync — shared routing may be out of date.
                                </span>
                                <button onClick={() => syncRoutingFromTour(routingSourceTour)}
                                  style={{ fontFamily: FF, fontSize: 11, fontWeight: 600, color: UI.warning, background: "none", border: `1px solid ${UI.warningBdr}`, borderRadius: 4, padding: "3px 10px", cursor: "pointer", flexShrink: 0 }}>
                                  Re-sync
                                </button>
                              </div>
                            )}
                            <div style={{ display: "flex", gap: 8 }}>
                              <select value={myRoutingTourSel} onChange={e => setMyRoutingTourSel(e.target.value)} style={{ fontFamily: FF, fontSize: 12, background: UI.rowBg, border: `1px solid ${UI.fieldBdr}`, borderRadius: 4, padding: "7px 10px", color: UI.text, flex: 1, cursor: "pointer" }}>
                                <option value="">Select a tour to sync…</option>
                                {allTours.map(t => <option key={t.id} value={t.id}>{t.name} ({t.shows?.length || 0} shows)</option>)}
                              </select>
                              <button className="abtn" onClick={() => syncRoutingFromTour(allTours.find(t => t.id === myRoutingTourSel))}>Sync</button>
                            </div>
                          </div>
                        )
                      })}

                    </div>
                  </div>
                );
              })()}
            </>
          )}

          {/* VENUE CRM */}
          {(() => { if (view !== "venues" || selVenue) return null;
            const vq = venueSearch.trim().toLowerCase();
            const allStates = [...new Set(Object.values(venues).map(v => v.addressState).filter(Boolean))].sort();
            const [vStateFilter, setVStateFilterInline] = [null, null]; // handled via separate state below
            const filteredVenues = Object.values(venues)
              .filter(v => v.kind !== "festival")
              .filter(v => {
                if (!vq) return true;
                return (v.name || "").toLowerCase().includes(vq)
                  || (v.city || "").toLowerCase().includes(vq)
                  || (v.addressState || "").toLowerCase().includes(vq)
                  || (v.promoterName || "").toLowerCase().includes(vq)
                  || (v.promoterCompany || "").toLowerCase().includes(vq)
                  || (v.promoterEmail || "").toLowerCase().includes(vq);
              })
              .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
            // Perf: cap rendered rows like the contacts list — search filters the full set.
            const VENUE_RENDER_CAP = 150;
            const venuesCapped = filteredVenues.length > VENUE_RENDER_CAP && !venuesShowAll;
            const shownVenues = venuesShowAll ? filteredVenues : filteredVenues.slice(0, VENUE_RENDER_CAP);
            const totalVenues = Object.values(venues).filter(v => v.kind !== "festival").length;
            return (
              <>
                <div className="ph">
                  <div><div className="ph-title">Directory</div><div className="ph-sub">{totalVenues} VENUE{totalVenues !== 1 ? "S" : ""} · {Object.values(venues).filter(v => v.kind === "festival").length} FESTIVAL{Object.values(venues).filter(v => v.kind === "festival").length !== 1 ? "S" : ""}</div></div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <div className="sb-dir-tab-wrap">
                      <button className={`sb-dir-tab${view === "venues" ? " on" : ""}`} onClick={() => { setView("venues"); setSelVenue(null); }}>Venues</button>
                      <button className={`sb-dir-tab${view === "festivals" ? " on" : ""}`} onClick={() => { setView("festivals"); setSelFestival(null); }}>Festivals</button>
                    </div>
                    <button className="add-g" onClick={() => { setDirModal("venue"); setDirQuery(""); setDirResults([]); }} style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg>Venue Database</button>
                    <button className="add-p" onClick={() => setMAddVenue({ kind: "venue" })}>+ Add Venue</button>
                  </div>
                </div>

                <div className="crm-bar">
                  <input
                    className="crm-inp"
                    placeholder="Search venues by name, city, state, promoter…"
                    value={venueSearch}
                    onChange={e => setVenueSearch(e.target.value)}
                  />
                  <span className="crm-count">{filteredVenues.length} of {totalVenues}</span>
                </div>

                <div className="crm-list">
                  <div className="crm-hdr" style={{ gridTemplateColumns: "2fr 1.2fr 60px 1.5fr 1.8fr 110px" }}>
                    <span>Venue</span><span>City / State</span><span>Cap</span><span>Promoter</span><span>Email</span><span></span>
                  </div>
                  {shownVenues.map(v => (
                    <div key={v.id} className="crm-row" style={{ gridTemplateColumns: "2fr 1.2fr 60px 1.5fr 1.8fr 110px" }} onClick={() => setSelVenue(v.id)}>
                      <div className="crm-row-name">{v.name}</div>
                      <div className="crm-row-sub">{[v.city, v.addressState].filter(Boolean).join(", ")}</div>
                      <div className="crm-row-tag">{v.capacity ? Number(v.capacity).toLocaleString() : "—"}</div>
                      <div className="crm-row-tag">{v.promoterName || (v.promoterCompany ? v.promoterCompany : "—")}</div>
                      <div className="crm-row-email">{v.promoterEmail || "—"}</div>
                      <div className="crm-row-acts" onClick={e => e.stopPropagation()}>
                        <button className="abtn" style={{ padding: "4px 10px", fontSize: 11 }} onClick={() => setMVenue({ mode: "edit", venueId: v.id })}>Edit</button>
                        <button className="abtn" style={{ padding: "4px 10px", fontSize: 11 }} onClick={() => setSelVenue(v.id)}>View →</button>
                      </div>
                    </div>
                  ))}
                  {venuesCapped && (
                    <div style={{ padding: "16px 14px", textAlign: "center", fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: "var(--text-faint)", letterSpacing: 0.3 }}>
                      Showing first {VENUE_RENDER_CAP} of {filteredVenues.length}. Search to narrow, or{" "}
                      <button onClick={() => setVenuesShowAll(true)} style={{ background: "none", border: "none", padding: 0, color: "var(--accent)", cursor: "pointer", font: "inherit", textDecoration: "underline" }}>show all {filteredVenues.length}</button>.
                    </div>
                  )}
                  {venuesShowAll && filteredVenues.length > VENUE_RENDER_CAP && (
                    <div style={{ padding: "16px 14px", textAlign: "center", fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: "var(--text-faint)", letterSpacing: 0.3 }}>
                      Showing all {filteredVenues.length}.{" "}
                      <button onClick={() => setVenuesShowAll(false)} style={{ background: "none", border: "none", padding: 0, color: "var(--accent)", cursor: "pointer", font: "inherit", textDecoration: "underline" }}>show fewer</button>
                    </div>
                  )}
                  {filteredVenues.length === 0 && (
                    venueSearch
                      ? <div className="empty">No venues match &ldquo;{venueSearch}&rdquo;</div>
                      : <div style={{ padding: "48px 36px", textAlign: "center" }}>
                          <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, letterSpacing: 3, color: "var(--text-faint)", textTransform: "uppercase", marginBottom: 16 }}>No venues yet</div>
                          <div style={{ fontSize: 14, color: "var(--text-mut)", lineHeight: 1.7, marginBottom: 28, maxWidth: 380, margin: "0 auto 28px" }}>Search our venue database to find and add venues instantly, or import your own list from a spreadsheet.</div>
                          <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
                            <button className="add-p" style={{ padding: "10px 22px", fontSize: 11, display: "inline-flex", alignItems: "center", gap: 5 }} onClick={() => { setDirModal("venue"); setDirQuery(""); setDirResults([]); }}><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg>Browse Venue Database</button>
                            <button className="add-g" style={{ padding: "10px 22px", fontSize: 11 }} onClick={() => setMAddVenue({ kind: "venue" })}>+ Add Venue</button>
                          </div>
                        </div>
                  )}
                </div>
              </>
            );
          })()}

          {/* VENUE PROFILE */}
          {(() => { if (view !== "venues" || !selVenue) return null;
            const vp = venues[selVenue];
            if (!vp) { setSelVenue(null); return null; }
            // Build show history: all shows at this venue across all artists/tours
            const history = [];
            data.artists.forEach(a => {
              a.tours.forEach(t => {
                t.shows.forEach(s => {
                  if (s.venueId === selVenue) history.push({ show: s, tour: t, artist: a });
                });
              });
            });
            history.sort((a, b) => a.show.date.localeCompare(b.show.date));
            const upcoming = history.filter(h => h.show.date >= new Date().toISOString().slice(0, 10));
            const past     = history.filter(h => h.show.date <  new Date().toISOString().slice(0, 10));
            return (
              <>
                <div className="ph">
                  <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                    <button className="btn-g" style={{ padding: "6px 12px", fontSize: 12 }} onClick={() => setSelVenue(null)}>← Back</button>
                    <div>
                      <div className="ph-title">{vp.name}</div>
                      <div className="ph-sub">{vp.city}{vp.capacity ? ` · CAP. ${Number(vp.capacity).toLocaleString()}` : ""} · {history.length} SHOW{history.length !== 1 ? "S" : ""}</div>
                    </div>
                  </div>
                  <button className="add-g" onClick={() => setMVenue({ mode: "edit", venueId: selVenue })} style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4z"/></svg>Edit Venue</button>
                </div>

                <div style={{ padding: "26px 36px" }}>
                  {/* Top row: Promoter | Venue Details | Logistics */}
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14, marginBottom: 14 }}>

                    {/* Promoter */}
                    <div className="vcm-card">
                      <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, letterSpacing: 2, color: "var(--text-faint)", textTransform: "uppercase", marginBottom: 14 }}>Promoter</div>
                      {(vp.promoterName || vp.promoterEmail || vp.promoterPhone) ? (
                        <>
                          {vp.promoterName    && <div style={{ fontSize: 14, fontWeight: 500, color: "var(--text)", marginBottom: 4 }}>{vp.promoterName}</div>}
                          {vp.promoterCompany && <div style={{ fontSize: 12, color: "var(--text-mut)", marginBottom: 10, fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif" }}>{vp.promoterCompany}</div>}
                          <ContactField label="Email" value={vp.promoterEmail} />
                          <ContactField label="Phone" value={vp.promoterPhone} />
                        </>
                      ) : <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: "var(--text-vfaint)" }}>Not set</div>}
                    </div>

                    {/* Venue Details */}
                    <div className="vcm-card">
                      <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, letterSpacing: 2, color: "var(--text-faint)", textTransform: "uppercase", marginBottom: 14 }}>Venue Details</div>
                      <ContactField label="Address"   value={fmtVenueAddress(vp)} />
                      <ContactField label="Website"   value={vp.website} link />
                      {vp.ages      && <ContactField label="Ages"      value={vp.ages} />}
                      {vp.gaSeated  && <ContactField label="Setup"     value={vp.gaSeated} />}
                      {vp.capacity  && <ContactField label="Capacity"  value={String(Number(vp.capacity).toLocaleString())} />}
                      {!fmtVenueAddress(vp) && !vp.website && !vp.ages && !vp.gaSeated && !vp.capacity &&
                        <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: "var(--text-vfaint)" }}>Not set</div>}
                    </div>

                    {/* Logistics */}
                    <div className="vcm-card">
                      <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, letterSpacing: 2, color: "var(--text-faint)", textTransform: "uppercase", marginBottom: 14 }}>Logistics</div>
                      <ContactField label="Load-in"    value={vp.loadIn} />
                      <ContactField label="Parking"    value={vp.parking} />
                      <ContactField label="House Gear" value={vp.houseGear} />
                      <ContactField label="Green Rooms" value={vp.greenRooms} />
                      <ContactField label="Bathrooms"   value={vp.bathrooms} />
                      <ContactField label="Merch Split" value={vp.merchSplit} />
                      {!vp.loadIn && !vp.parking && !vp.houseGear && !vp.greenRooms && !vp.bathrooms && !vp.merchSplit &&
                        <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: "var(--text-vfaint)" }}>Not set</div>}
                    </div>
                  </div>

                  {/* Notes */}
                  {vp.notes && (
                    <div className="vcm-card" style={{ marginBottom: 14 }}>
                      <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, letterSpacing: 2, color: "var(--text-faint)", textTransform: "uppercase", marginBottom: 10 }}>Internal Notes</div>
                      <div style={{ fontSize: 14, color: "var(--text-mid)", lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{vp.notes}</div>
                    </div>
                  )}

                  {/* Show History */}
                  <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, letterSpacing: 2, color: "var(--text-faint)", textTransform: "uppercase", marginBottom: 12 }}>
                    Show History · {history.length} total
                  </div>
                  {history.length === 0 && (
                    <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 12, color: "var(--text-vfaint)", padding: "16px 0" }}>No shows at this venue yet.</div>
                  )}
                  {upcoming.length > 0 && (
                    <>
                      <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: "var(--success)", letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 8 }}>Upcoming</div>
                      {upcoming.map(({ show, tour, artist }) => (
                        <div key={show.id} className="prf-tour-row" onClick={() => goToShow(artist.id, tour.id, show.id)}>
                          <div>
                            <div style={{ fontSize: 14, fontWeight: 500, color: "#ddd", marginBottom: 3 }}>{fmtDate(show.date)}</div>
                            <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: "var(--text-mut)", display: "flex", gap: 10 }}>
                              <span>{artist.name}</span><span>·</span><span>{tour.name}</span>
                            </div>
                          </div>
                          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <span style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: show.status === "confirmed" ? "var(--success)" : show.status === "advancing" ? "var(--accent)" : "#555", textTransform: "uppercase" }}>{show.status}</span>
                            <svg width="13" height="13" viewBox="0 0 12 12" fill="none" stroke="var(--text-faint)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><path d="M4.5 2l4 4-4 4"/></svg>
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                  {past.length > 0 && (
                    <>
                      <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: "var(--text-mut)", letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 8, marginTop: upcoming.length > 0 ? 16 : 0 }}>Past</div>
                      {[...past].reverse().map(({ show, tour, artist }) => (
                        <div key={show.id} className="prf-tour-row" style={{ opacity: 0.6 }} onClick={() => goToShow(artist.id, tour.id, show.id)}>
                          <div>
                            <div style={{ fontSize: 14, fontWeight: 500, color: "#ddd", marginBottom: 3 }}>{fmtDate(show.date)}</div>
                            <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: "var(--text-mut)", display: "flex", gap: 10 }}>
                              <span>{artist.name}</span><span>·</span><span>{tour.name}</span>
                            </div>
                          </div>
                          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <span style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: "var(--text-mut)", textTransform: "uppercase" }}>{show.status}</span>
                            <svg width="13" height="13" viewBox="0 0 12 12" fill="none" stroke="var(--text-faint)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><path d="M4.5 2l4 4-4 4"/></svg>
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              </>
            );
          })()}

          {/* FESTIVAL CRM — LIST */}
          {(() => { if (view !== "festivals" || selFestival) return null;
            const fq = festivalSearch.trim().toLowerCase();
            const allFestivals = Object.values(venues).filter(v => v.kind === "festival");
            const filteredFestivals = allFestivals
              .filter(v => {
                if (!fq) return true;
                return (v.name || "").toLowerCase().includes(fq)
                  || (v.city || "").toLowerCase().includes(fq)
                  || (v.addressState || "").toLowerCase().includes(fq)
                  || (v.promoterName || "").toLowerCase().includes(fq)
                  || (v.promoterCompany || "").toLowerCase().includes(fq)
                  || (v.promoterEmail || "").toLowerCase().includes(fq);
              })
              .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
            return (
              <>
                <div className="ph">
                  <div><div className="ph-title">Directory</div><div className="ph-sub">{allFestivals.length} FESTIVAL{allFestivals.length !== 1 ? "S" : ""} · {Object.values(venues).filter(v => v.kind !== "festival").length} VENUE{Object.values(venues).filter(v => v.kind !== "festival").length !== 1 ? "S" : ""}</div></div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <div className="sb-dir-tab-wrap">
                      <button className={`sb-dir-tab${view === "venues" ? " on" : ""}`} onClick={() => { setView("venues"); setSelVenue(null); }}>Venues</button>
                      <button className={`sb-dir-tab${view === "festivals" ? " on" : ""}`} onClick={() => { setView("festivals"); setSelFestival(null); }}>Festivals</button>
                    </div>
                    <button className="add-g" onClick={() => { setDirModal("festival"); setDirQuery(""); setDirResults([]); }} style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg>Festival Database</button>
                    <button className="add-p" onClick={() => setMAddVenue({ kind: "festival" })}>+ Add Festival</button>
                  </div>
                </div>

                <div className="crm-bar">
                  <input
                    className="crm-inp"
                    placeholder="Search festivals by name, city, state, promoter…"
                    value={festivalSearch}
                    onChange={e => setFestivalSearch(e.target.value)}
                  />
                  <span className="crm-count">{filteredFestivals.length} of {allFestivals.length}</span>
                </div>

                <div className="crm-list">
                  <div className="crm-hdr" style={{ gridTemplateColumns: "2fr 1.2fr 60px 1.5fr 1.8fr 110px" }}>
                    <span>Festival</span><span>City / State</span><span>Cap</span><span>Promoter</span><span>Email</span><span></span>
                  </div>
                  {filteredFestivals.map(v => (
                    <div key={v.id} className="crm-row" style={{ gridTemplateColumns: "2fr 1.2fr 60px 1.5fr 1.8fr 110px" }} onClick={() => setSelFestival(v.id)}>
                      <div className="crm-row-name">{v.name}</div>
                      <div className="crm-row-sub">{[v.city, v.addressState].filter(Boolean).join(", ")}</div>
                      <div className="crm-row-tag">{v.capacity ? Number(v.capacity).toLocaleString() : "—"}</div>
                      <div className="crm-row-tag">{v.promoterName || (v.promoterCompany ? v.promoterCompany : "—")}</div>
                      <div className="crm-row-email">{v.promoterEmail || "—"}</div>
                      <div className="crm-row-acts" onClick={e => e.stopPropagation()}>
                        <button className="abtn" style={{ padding: "4px 10px", fontSize: 11 }} onClick={() => setMVenue({ mode: "edit", venueId: v.id })}>Edit</button>
                        <button className="abtn" style={{ padding: "4px 10px", fontSize: 11 }} onClick={() => setSelFestival(v.id)}>View →</button>
                      </div>
                    </div>
                  ))}
                  {filteredFestivals.length === 0 && (
                    festivalSearch
                      ? <div className="empty">No festivals match &ldquo;{festivalSearch}&rdquo;</div>
                      : <div style={{ padding: "48px 36px", textAlign: "center" }}>
                          <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, letterSpacing: 3, color: "var(--text-faint)", textTransform: "uppercase", marginBottom: 16 }}>No festivals yet</div>
                          <div style={{ fontSize: 14, color: "var(--text-mut)", lineHeight: 1.7, marginBottom: 28, maxWidth: 380, margin: "0 auto 28px" }}>Browse our festival database or import your own list to get started.</div>
                          <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
                            <button className="add-p" style={{ padding: "10px 22px", fontSize: 11, display: "inline-flex", alignItems: "center", gap: 5 }} onClick={() => { setDirModal("festival"); setDirQuery(""); setDirResults([]); }}><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg>Browse Festival Directory</button>
                            <button className="add-g" style={{ padding: "10px 22px", fontSize: 11 }} onClick={() => setMAddVenue({ kind: "festival" })}>+ Add Festival</button>
                          </div>
                        </div>
                  )}
                </div>
              </>
            );
          })()}

          {/* FESTIVAL CRM — PROFILE */}
          {(() => { if (view !== "festivals" || !selFestival) return null;
            const fp = venues[selFestival];
            if (!fp) { setSelFestival(null); return null; }
            const history = [];
            data.artists.forEach(a => {
              a.tours.forEach(t => {
                t.shows.forEach(s => {
                  if (s.venueId === selFestival) history.push({ show: s, tour: t, artist: a });
                });
              });
            });
            history.sort((a, b) => a.show.date.localeCompare(b.show.date));
            const upcoming = history.filter(h => h.show.date >= new Date().toISOString().slice(0, 10));
            const past     = history.filter(h => h.show.date <  new Date().toISOString().slice(0, 10));
            return (
              <>
                <div className="ph">
                  <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                    <button className="btn-g" style={{ padding: "6px 12px", fontSize: 12 }} onClick={() => setSelFestival(null)}>← Back</button>
                    <div>
                      <div className="ph-title">{fp.name}</div>
                      <div className="ph-sub">{fp.city}{fp.capacity ? ` · CAP. ${Number(fp.capacity).toLocaleString()}` : ""} · {history.length} SHOW{history.length !== 1 ? "S" : ""}</div>
                    </div>
                  </div>
                  <button className="add-g" onClick={() => setMVenue({ mode: "edit", venueId: selFestival })} style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4z"/></svg>Edit Festival</button>
                </div>

                <div style={{ padding: "26px 36px" }}>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14, marginBottom: 14 }}>

                    {/* Promoter / Contact */}
                    <div className="vcm-card">
                      <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, letterSpacing: 2, color: "var(--text-faint)", textTransform: "uppercase", marginBottom: 14 }}>Promoter / Contact</div>
                      {(fp.promoterName || fp.promoterEmail || fp.promoterPhone) ? (
                        <>
                          {fp.promoterName    && <div style={{ fontSize: 14, fontWeight: 500, color: "var(--text)", marginBottom: 4 }}>{fp.promoterName}</div>}
                          {fp.promoterCompany && <div style={{ fontSize: 12, color: "var(--text-mut)", marginBottom: 10, fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif" }}>{fp.promoterCompany}</div>}
                          <ContactField label="Email" value={fp.promoterEmail} />
                          <ContactField label="Phone" value={fp.promoterPhone} />
                        </>
                      ) : <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: "var(--text-vfaint)" }}>Not set</div>}
                    </div>

                    {/* Festival Details */}
                    <div className="vcm-card">
                      <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, letterSpacing: 2, color: "var(--text-faint)", textTransform: "uppercase", marginBottom: 14 }}>Festival Details</div>
                      <ContactField label="Location"  value={fmtVenueAddress(fp)} />
                      <ContactField label="Website"   value={fp.website} link />
                      {fp.ages      && <ContactField label="Ages"      value={fp.ages} />}
                      {fp.gaSeated  && <ContactField label="Setup"     value={fp.gaSeated} />}
                      {fp.capacity  && <ContactField label="Capacity"  value={String(Number(fp.capacity).toLocaleString())} />}
                      {!fmtVenueAddress(fp) && !fp.website && !fp.ages && !fp.gaSeated && !fp.capacity &&
                        <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: "var(--text-vfaint)" }}>Not set</div>}
                    </div>

                    {/* Logistics */}
                    <div className="vcm-card">
                      <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, letterSpacing: 2, color: "var(--text-faint)", textTransform: "uppercase", marginBottom: 14 }}>Logistics</div>
                      <ContactField label="Load-in"    value={fp.loadIn} />
                      <ContactField label="Parking"    value={fp.parking} />
                      <ContactField label="House Gear" value={fp.houseGear} />
                      <ContactField label="Green Rooms" value={fp.greenRooms} />
                      <ContactField label="Bathrooms"   value={fp.bathrooms} />
                      <ContactField label="Merch Split" value={fp.merchSplit} />
                      {!fp.loadIn && !fp.parking && !fp.houseGear && !fp.greenRooms && !fp.bathrooms && !fp.merchSplit &&
                        <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: "var(--text-vfaint)" }}>Not set</div>}
                    </div>
                  </div>

                  {fp.notes && (
                    <div className="vcm-card" style={{ marginBottom: 14 }}>
                      <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, letterSpacing: 2, color: "var(--text-faint)", textTransform: "uppercase", marginBottom: 10 }}>Internal Notes</div>
                      <div style={{ fontSize: 14, color: "var(--text-mid)", lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{fp.notes}</div>
                    </div>
                  )}

                  <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, letterSpacing: 2, color: "var(--text-faint)", textTransform: "uppercase", marginBottom: 12 }}>
                    Show History · {history.length} total
                  </div>
                  {history.length === 0 && (
                    <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 12, color: "var(--text-vfaint)", padding: "16px 0" }}>No shows at this festival yet.</div>
                  )}
                  {upcoming.length > 0 && (
                    <>
                      <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: "var(--success)", letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 8 }}>Upcoming</div>
                      {upcoming.map(({ show, tour, artist }) => (
                        <div key={show.id} className="prf-tour-row" onClick={() => goToShow(artist.id, tour.id, show.id)}>
                          <div>
                            <div style={{ fontSize: 14, fontWeight: 500, color: "#ddd", marginBottom: 3 }}>{fmtDate(show.date)}</div>
                            <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: "var(--text-mut)", display: "flex", gap: 10 }}>
                              <span>{artist.name}</span><span>·</span><span>{tour.name}</span>
                            </div>
                          </div>
                          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <span style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: show.status === "confirmed" ? "var(--success)" : show.status === "advancing" ? "var(--accent)" : "#555", textTransform: "uppercase" }}>{show.status}</span>
                            <svg width="13" height="13" viewBox="0 0 12 12" fill="none" stroke="var(--text-faint)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><path d="M4.5 2l4 4-4 4"/></svg>
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                  {past.length > 0 && (
                    <>
                      <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: "var(--text-mut)", letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 8, marginTop: upcoming.length > 0 ? 16 : 0 }}>Past</div>
                      {[...past].reverse().map(({ show, tour, artist }) => (
                        <div key={show.id} className="prf-tour-row" style={{ opacity: 0.6 }} onClick={() => goToShow(artist.id, tour.id, show.id)}>
                          <div>
                            <div style={{ fontSize: 14, fontWeight: 500, color: "#ddd", marginBottom: 3 }}>{fmtDate(show.date)}</div>
                            <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: "var(--text-mut)", display: "flex", gap: 10 }}>
                              <span>{artist.name}</span><span>·</span><span>{tour.name}</span>
                            </div>
                          </div>
                          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <span style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: "var(--text-mut)", textTransform: "uppercase" }}>{show.status}</span>
                            <svg width="13" height="13" viewBox="0 0 12 12" fill="none" stroke="var(--text-faint)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><path d="M4.5 2l4 4-4 4"/></svg>
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              </>
            );
          })()}

          {/* CONTACTS */}
          {(() => { if (view !== "contacts") return null;
            const cq = contactSearch.trim().toLowerCase();
            const filteredContacts = (data.contacts || [])
              .filter(c => {
                if (!cq) return true;
                return (c.name || "").toLowerCase().includes(cq)
                  || (c.company || "").toLowerCase().includes(cq)
                  || (c.role || "").toLowerCase().includes(cq)
                  || (c.email || "").toLowerCase().includes(cq)
                  || (c.phone || "").toLowerCase().includes(cq)
                  || (c.notes || "").toLowerCase().includes(cq);
              })
              .slice().sort((a, b) => {
                const lastName = n => { const p = (n || "").trim().split(/\s+/); return p.length > 1 ? p[p.length - 1] : p[0]; };
                return lastName(a.name).localeCompare(lastName(b.name));
              });
            // Perf: large address books (hundreds+) are expensive to render in full
            // on every keystroke. Cap the DOM rows; search still filters the whole set.
            const CONTACT_RENDER_CAP = 150;
            const contactsCapped = filteredContacts.length > CONTACT_RENDER_CAP && !contactsShowAll;
            const shownContacts = contactsShowAll ? filteredContacts : filteredContacts.slice(0, CONTACT_RENDER_CAP);
            return (
              <>
                <div className="ph">
                  <div><div className="ph-title">Contacts</div><div className="ph-sub">{(data.contacts || []).length} CONTACTS</div></div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button className="add-p" onClick={() => setMAddContact(true)}>+ Add Contacts</button>
                  </div>
                </div>

                <div className="crm-bar">
                  <input
                    className="crm-inp"
                    placeholder="Search by name, company, role, email…"
                    value={contactSearch}
                    onChange={e => setContactSearch(e.target.value)}
                  />
                  <span className="crm-count">{filteredContacts.length} of {(data.contacts || []).length}</span>
                </div>

                <div className="crm-list">
                  <div className="crm-hdr" style={{ gridTemplateColumns: "1.8fr 1.4fr 1.2fr 2fr 1fr 80px" }}>
                    <span>Name</span><span>Company</span><span>Role</span><span>Email</span><span>Phone</span><span></span>
                  </div>
                  {shownContacts.map(c => (
                    <div key={c.id} className="crm-row" style={{ gridTemplateColumns: "1.8fr 1.4fr 1.2fr 2fr 1fr 80px" }}>
                      <div className="crm-row-name">{c.name}</div>
                      <div className="crm-row-tag">{c.company || "—"}</div>
                      <div className="crm-row-tag">{c.role || "—"}</div>
                      <div className="crm-row-email">{c.email
                        ? <span style={{ cursor: "pointer", color: "var(--text-mut)" }} onClick={() => navigator.clipboard.writeText(c.email)}>{c.email}</span>
                        : "—"}
                      </div>
                      <div className="crm-row-tag">{c.phone || "—"}</div>
                      <div className="crm-row-acts">
                        <button className="abtn" style={{ padding: "4px 10px", fontSize: 11 }} onClick={() => setMContact({ mode: "edit", contactId: c.id })}>Edit</button>
                      </div>
                    </div>
                  ))}
                  {contactsCapped && (
                    <div style={{ padding: "16px 14px", textAlign: "center", fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: "var(--text-faint)", letterSpacing: 0.3 }}>
                      Showing first {CONTACT_RENDER_CAP} of {filteredContacts.length}. Search to narrow, or{" "}
                      <button onClick={() => setContactsShowAll(true)} style={{ background: "none", border: "none", padding: 0, color: "var(--accent)", cursor: "pointer", font: "inherit", textDecoration: "underline" }}>show all {filteredContacts.length}</button>.
                    </div>
                  )}
                  {contactsShowAll && filteredContacts.length > CONTACT_RENDER_CAP && (
                    <div style={{ padding: "16px 14px", textAlign: "center", fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: "var(--text-faint)", letterSpacing: 0.3 }}>
                      Showing all {filteredContacts.length}.{" "}
                      <button onClick={() => setContactsShowAll(false)} style={{ background: "none", border: "none", padding: 0, color: "var(--accent)", cursor: "pointer", font: "inherit", textDecoration: "underline" }}>show fewer</button>
                    </div>
                  )}
                  {filteredContacts.length === 0 && (
                    contactSearch
                      ? <div className="empty">No contacts match &ldquo;{contactSearch}&rdquo;</div>
                      : <EmptyState
                          icon={<svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/></svg>}
                          title="No contacts yet"
                          hint="Build your address book — promoters, production contacts, vendors, and more, reusable across every tour."
                          action={<Button variant="primary" size="sm" onClick={() => setMAddContact(true)}>+ Add Contact</Button>}
                        />
                  )}
                </div>
              </>
            );
          })()}

          {/* PEOPLE / PROFILES DIRECTORY */}
          {(() => { if (view !== "people") return null;
            const FF = "'Helvetica Neue', Helvetica, Arial, sans-serif";

            // ── Shared profile card renderer ──────────────────────────────
            const ProfileRow = ({ p, onEdit, tourSlot, onPreview }) => {
              const catColor = PROFILE_ROLE_COLORS[p.role_category] || "#6a8faf";
              const initials = (p.preferred_name || p.name || "?").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
              const isClaimed = !!(p.claimed_at || p.user_id);
              return (
                <div
                  style={{
                    display: "flex", alignItems: "center", gap: 14,
                    background: UI.rowBg, border: `1px solid ${UI.panelBdr}`,
                    borderLeft: `4px solid ${catColor}`,
                    borderRadius: 8, padding: "10px 14px",
                    cursor: "pointer", transition: "background 0.12s, border-color 0.12s",
                    marginBottom: 3,
                  }}
                  onClick={onEdit}
                  onMouseEnter={e => { e.currentTarget.style.background = UI.rowHov; e.currentTarget.style.borderColor = `${catColor}88`; }}
                  onMouseLeave={e => { e.currentTarget.style.background = UI.rowBg; e.currentTarget.style.borderColor = UI.panelBdr; e.currentTarget.style.borderLeftColor = catColor; }}
                >
                  {/* Avatar */}
                  <div style={{ width: 36, height: 36, borderRadius: "50%", background: `${catColor}22`, border: `1.5px solid ${catColor}55`, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", flexShrink: 0 }}>
                    {p.photo_url
                      ? <img src={p.photo_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      : <span style={{ fontFamily: FF, fontSize: 14, fontWeight: 700, color: catColor }}>{initials}</span>
                    }
                  </div>

                  {/* Name + role */}
                  <div style={{ flex: "0 1 220px", minWidth: 0 }}>
                    <div style={{ display: "inline-flex", alignItems: "center", gap: 6, maxWidth: "100%" }}>
                      <span style={{ fontFamily: FF, fontSize: 14, fontWeight: 600, color: UI.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.name || "—"}</span>
                      <div title={isClaimed ? "Claimed" : "Invite not yet claimed"} style={{ width: 6, height: 6, borderRadius: "50%", background: isClaimed ? UI.green : UI.textFnt, opacity: isClaimed ? 1 : 0.3, flexShrink: 0 }} />
                    </div>
                    <div style={{ fontFamily: FF, fontSize: 11, color: catColor, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {PROFILE_ROLE_LABELS[p.role_category] || p.role_category}{p.role ? ` · ${p.role}` : ""}
                    </div>
                  </div>

                  {/* Skills */}
                  <div style={{ flex: 1, display: "flex", flexWrap: "wrap", gap: 4, overflow: "hidden" }}>
                    {(p.skills || []).slice(0, 3).map(s => (
                      <span key={s} style={{ fontFamily: FF, fontSize: 11, background: `${catColor}12`, color: catColor, borderRadius: 16, padding: "2px 7px", whiteSpace: "nowrap" }}>{s}</span>
                    ))}
                    {(p.skills || []).length > 3 && <span style={{ fontFamily: FF, fontSize: 11, color: UI.textVFnt }}>+{p.skills.length - 3}</span>}
                  </div>

                  {/* Contact */}
                  <div style={{ display: "flex", gap: 6, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                    {p.email && (
                      <a href={`mailto:${p.email}`} title={p.email}
                        style={{ display: "flex", alignItems: "center", gap: 4, fontFamily: FF, fontSize: 11, color: UI.textMut, background: UI.panelAlt, border: `1px solid ${UI.fieldBdr}`, borderRadius: 4, padding: "5px 10px", textDecoration: "none", transition: "color 0.12s, border-color 0.12s" }}
                        onMouseEnter={e => { e.currentTarget.style.borderColor = catColor; e.currentTarget.style.color = catColor; }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor = UI.fieldBdr; e.currentTarget.style.color = UI.textMut; }}>
                        <svg width="11" height="11" viewBox="0 0 15 15" fill="none" stroke="currentColor" strokeWidth="1.6"><rect x="1.5" y="3" width="12" height="9" rx="1"/><path d="M1.5 4l6 5 6-5"/></svg>
                        Email
                      </a>
                    )}
                    {p.phone && (
                      <a href={`tel:${p.phone}`} title={p.phone}
                        style={{ display: "flex", alignItems: "center", gap: 4, fontFamily: FF, fontSize: 11, color: UI.textMut, background: UI.panelAlt, border: `1px solid ${UI.fieldBdr}`, borderRadius: 4, padding: "5px 10px", textDecoration: "none", transition: "color 0.12s, border-color 0.12s" }}
                        onMouseEnter={e => { e.currentTarget.style.borderColor = catColor; e.currentTarget.style.color = catColor; }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor = UI.fieldBdr; e.currentTarget.style.color = UI.textMut; }}>
                        <svg width="11" height="11" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><path d="M4 2h3l1.5 4-2 1.5a12 12 0 006 6L14 11.5l4 1.5v3a1 1 0 01-1 1A17 17 0 013 3a1 1 0 011-1z"/></svg>
                        Call
                      </a>
                    )}
                  </div>

                  {/* Tour slot */}
                  {tourSlot && <div style={{ flexShrink: 0 }} onClick={e => e.stopPropagation()}>{tourSlot}</div>}

                  {/* Preview */}
                  <button onClick={e => { e.stopPropagation(); onPreview(); }}
                    style={{ display: "flex", alignItems: "center", gap: 4, fontFamily: FF, fontSize: 11, color: UI.textMut, background: "none", border: `1px solid ${UI.fieldBdr}`, borderRadius: 4, padding: "5px 10px", cursor: "pointer", flexShrink: 0, transition: "color 0.12s, border-color 0.12s" }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = UI.amber; e.currentTarget.style.color = UI.amber; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = UI.fieldBdr; e.currentTarget.style.color = UI.textMut; }}>
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                    Preview
                  </button>
                </div>
              );
            };

            // ── YOUR TEAM tab ─────────────────────────────────────────────
            const pq = profileSearch.trim().toLowerCase();
            const filteredProfiles = profiles.filter(p => {
              const catOk = profCatFilter === "all" || p.role_category === profCatFilter;
              if (!catOk) return false;
              if (!pq) return true;
              return (p.name || "").toLowerCase().includes(pq)
                || (p.preferred_name || "").toLowerCase().includes(pq)
                || (p.role || "").toLowerCase().includes(pq)
                || (p.email || "").toLowerCase().includes(pq)
                || (p.city || "").toLowerCase().includes(pq)
                || (p.skills || []).some(s => s.toLowerCase().includes(pq));
            });

            // ── NETWORK tab ───────────────────────────────────────────────
            const nq = networkSearch.trim().toLowerCase();
            const filteredNetwork = networkProfiles.filter(p => {
              const catOk = networkCatFilter === "all" || p.role_category === networkCatFilter;
              if (!catOk) return false;
              if (networkFilter === "connected" && !connections.has(p.id)) return false;
              if (!nq) return true;
              return (p.name || "").toLowerCase().includes(nq)
                || (p.preferred_name || "").toLowerCase().includes(nq)
                || (p.role || "").toLowerCase().includes(nq)
                || (p.city || "").toLowerCase().includes(nq)
                || (p.skills || []).some(s => s.toLowerCase().includes(nq))
                || (p.teams?.name || "").toLowerCase().includes(nq);
            });

            return (
              <>
                {/* Header */}
                <div className="ph">
                  <div>
                    <div className="ph-title">People</div>
                    <div className="ph-sub">
                      {peopleTab === "team"
                        ? `${profiles.length} PROFILE${profiles.length !== 1 ? "S" : ""} ON YOUR TEAM`
                        : `${networkProfiles.length} IN NETWORK · ${connections.size} CONNECTED`
                      }
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <div className="sb-dir-tab-wrap">
                      <button className={`sb-dir-tab${peopleTab === "team" ? " on" : ""}`} onClick={() => setPeopleTab("team")}>Your Team</button>
                      <button className={`sb-dir-tab${peopleTab === "network" ? " on" : ""}`} onClick={() => { setPeopleTab("network"); if (!networkLoaded) loadNetworkProfiles(); }}>
                        Network{connections.size > 0 && <span style={{ marginLeft: 5, background: UI.amber, color: "var(--accent-fg)", borderRadius: 8, padding: "1px 5px", fontSize: 11, fontWeight: 700 }}>{connections.size}</span>}
                      </button>
                    </div>
                    <button className="add-p" style={{ visibility: peopleTab === "team" ? "visible" : "hidden" }} onClick={() => {
                      setProfCatFilter("all");
                      setMProfile({ mode: "add", context: selT ? { tourId: selT } : {} });
                    }}>+ Add Profile</button>
                  </div>
                </div>

                {/* ── YOUR TEAM TAB ── */}
                {peopleTab === "team" && (
                  <>
                    {/* ── Add from Roster banner ── */}
                    {(() => {
                      if (!artist || !tour) return null;
                      const rosterNotOnTour = profiles.filter(p =>
                        p.artist_id === artist.id && p.current_tour_id !== tour.id
                      );
                      if (rosterNotOnTour.length === 0) return null;
                      return (
                        <div style={{ margin: "0 36px 20px", background: UI.panelAlt, border: `1px solid ${UI.amber}44`, borderRadius: 8, padding: "14px 18px" }}>
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                            <div>
                              <span style={{ fontFamily: FF, fontSize: 11, fontWeight: 600, color: UI.amber }}>
                                {artist.name} Roster — {rosterNotOnTour.length} available to add to {tour.name}
                              </span>
                              <div style={{ fontFamily: FF, fontSize: 11, color: UI.textMut, marginTop: 2 }}>
                                These members are on the permanent roster but not yet on this tour.
                              </div>
                            </div>
                          </div>
                          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                            {rosterNotOnTour.map(p => {
                              const catCol = PROFILE_ROLE_COLORS[p.role_category] || UI.textFnt;
                              return (
                                <div key={p.id} style={{ display: "flex", alignItems: "center", gap: 8, background: UI.rowBg, border: `1px solid ${UI.panelBdr}`, borderRadius: 8, padding: "7px 12px" }}>
                                  <div style={{ width: 26, height: 26, borderRadius: "50%", background: `${catCol}18`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: catCol, flexShrink: 0 }}>
                                    {(p.preferred_name || p.name || "?").charAt(0).toUpperCase()}
                                  </div>
                                  <div style={{ minWidth: 0 }}>
                                    <div style={{ fontFamily: FF, fontSize: 11, fontWeight: 600, color: UI.text, whiteSpace: "nowrap" }}>{p.preferred_name || p.name}</div>
                                    {p.role && <div style={{ fontFamily: FF, fontSize: 11, color: UI.textMut }}>{p.role}</div>}
                                  </div>
                                  <button
                                    onClick={() => addProfileToTour(p.id, tour.id)}
                                    style={{ fontFamily: FF, fontSize: 11, padding: "4px 10px", borderRadius: 4, border: `1px solid ${UI.amber}`, cursor: "pointer", background: `${UI.amber}18`, color: UI.amber, fontWeight: 700, whiteSpace: "nowrap", transition: "all 0.12s", flexShrink: 0 }}
                                    onMouseEnter={e => { e.currentTarget.style.background = UI.amber; e.currentTarget.style.color = "var(--accent-fg)"; }}
                                    onMouseLeave={e => { e.currentTarget.style.background = `${UI.amber}18`; e.currentTarget.style.color = UI.amber; }}>
                                    + Add
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })()}

                    {/* Search + filter row */}
                    <div style={{ padding: "16px 36px 18px", display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                      <input className="crm-inp" style={{ flex: 1, minWidth: 200 }} placeholder="Search by name, role, city, skills…" value={profileSearch} onChange={e => setProfileSearch(e.target.value)} />
                      <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                        {["all", ...PROFILE_ROLE_CATS].map(cat => {
                          const on = profCatFilter === cat;
                          const col = cat === "all" ? UI.amber : PROFILE_ROLE_COLORS[cat];
                          return <button key={cat} onClick={() => setProfCatFilter(cat)} style={{ fontFamily: FF, fontSize: 11, padding: "5px 10px", borderRadius: 4, border: `1px solid ${on ? col : UI.fieldBdr}`, cursor: "pointer", background: on ? `${col}22` : "transparent", color: on ? col : UI.textMut, fontWeight: on ? 700 : 400, transition: "all 0.12s" }}>{cat === "all" ? "All" : PROFILE_ROLE_LABELS[cat]}</button>;
                        })}
                      </div>
                    </div>

                    {/* Profile list */}
                    <div style={{ padding: "0 36px 36px" }}>
                      {filteredProfiles.map(p => {
                        const onCurrentTour = tour && p.current_tour_id === tour.id;
                        const tourSlot = tour ? (onCurrentTour ? (
                          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <span style={{ fontFamily: FF, fontSize: 11, letterSpacing: "0.8px", textTransform: "uppercase", color: UI.green, background: UI.ckOnBg, border: `1px solid ${UI.green}44`, borderRadius: 4, padding: "3px 8px", whiteSpace: "nowrap", display: "inline-flex", alignItems: "center", gap: 4 }}><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>On Tour</span>
                            <button onClick={() => removeProfileFromTour(p.id)}
                              style={{ fontFamily: FF, fontSize: 11, padding: "3px 8px", borderRadius: 4, border: `1px solid ${UI.fieldBdr}`, cursor: "pointer", background: "transparent", color: UI.textMut, whiteSpace: "nowrap", transition: "all 0.12s" }}
                              onMouseEnter={e => { e.currentTarget.style.borderColor = "var(--danger)"; e.currentTarget.style.color = "var(--danger)"; }}
                              onMouseLeave={e => { e.currentTarget.style.borderColor = UI.fieldBdr; e.currentTarget.style.color = UI.textMut; }}>Remove</button>
                          </div>
                        ) : (
                          <button onClick={() => addProfileToTour(p.id, tour.id)}
                            style={{ fontFamily: FF, fontSize: 11, padding: "4px 10px", borderRadius: 4, border: `1px solid ${UI.amber}44`, cursor: "pointer", background: `${UI.amber}12`, color: UI.amber, whiteSpace: "nowrap", transition: "all 0.12s" }}
                            onMouseEnter={e => { e.currentTarget.style.background = `${UI.amber}28`; }}
                            onMouseLeave={e => { e.currentTarget.style.background = `${UI.amber}12`; }}>+ Add to Tour</button>
                        )) : null;
                        return (
                          <ProfileRow key={p.id} p={p}
                            onEdit={async () => { const privData = await loadProfilePrivate(p.id); setMProfile({ mode: "edit", profile: p, privateData: privData, context: { artistId: p.artist_id || null } }); }}
                            onPreview={() => setPreviewMember(p)}
                            tourSlot={tourSlot}
                          />
                        );
                      })}
                      {filteredProfiles.length === 0 && (
                        (profileSearch || profCatFilter !== "all")
                          ? <div className="empty">No profiles match your search.</div>
                          : <EmptyState
                              icon={<svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/></svg>}
                              title="No profiles yet"
                              hint="Add your touring party — band members, crew, management, and more. Profiles carry across tours and members can claim their own."
                              action={<Button variant="primary" size="sm" onClick={() => setMProfile({ mode: "add", context: selT ? { tourId: selT } : {} })}>+ Add Profile</Button>}
                            />
                      )}
                    </div>
                  </>
                )}

                {/* ── NETWORK TAB ── */}
                {peopleTab === "network" && (
                  <>
                    {networkLoading && (
                      <div style={{ padding: "60px 36px", textAlign: "center", fontFamily: FF, fontSize: 11, color: UI.textMut, letterSpacing: "1px" }}>Loading network…</div>
                    )}
                    {!networkLoading && networkLoaded && (
                      <>
                        <div style={{ padding: "16px 36px 18px", display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                          <input className="crm-inp" style={{ flex: 1, minWidth: 200 }} placeholder="Search by name, role, city, agency, skills…" value={networkSearch} onChange={e => setNetworkSearch(e.target.value)} />
                          {/* Connected filter */}
                          <button onClick={() => setNetworkFilter(f => f === "connected" ? "all" : "connected")} style={{ fontFamily: FF, fontSize: 11, padding: "5px 12px", borderRadius: 4, border: `1px solid ${networkFilter === "connected" ? UI.amber : UI.fieldBdr}`, cursor: "pointer", background: networkFilter === "connected" ? `${UI.amber}22` : "transparent", color: networkFilter === "connected" ? UI.amber : UI.textMut, fontWeight: networkFilter === "connected" ? 700 : 400, transition: "all 0.12s", whiteSpace: "nowrap" }}>
                            Connected {connections.size > 0 ? `(${connections.size})` : ""}
                          </button>
                          {/* Role filter */}
                          <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                            {["all", ...PROFILE_ROLE_CATS].map(cat => {
                              const on = networkCatFilter === cat;
                              const col = cat === "all" ? UI.amber : PROFILE_ROLE_COLORS[cat];
                              return <button key={cat} onClick={() => setNetworkCatFilter(cat)} style={{ fontFamily: FF, fontSize: 11, padding: "5px 10px", borderRadius: 4, border: `1px solid ${on ? col : UI.fieldBdr}`, cursor: "pointer", background: on ? `${col}22` : "transparent", color: on ? col : UI.textMut, fontWeight: on ? 700 : 400, transition: "all 0.12s" }}>{cat === "all" ? "All" : PROFILE_ROLE_LABELS[cat]}</button>;
                            })}
                          </div>
                        </div>

                        <div style={{ padding: "0 36px 36px", display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 14 }}>
                          {filteredNetwork.map(p => {
                            const isConnected = connections.has(p.id);
                            const agencyName = p.teams?.name;
                            // Overlap: dates where their routing city matches any of your tour show cities
                            const yourShowLocations = data.artists.flatMap(a =>
                              a.tours.flatMap(t => t.shows.map(s => ({
                                date: s.date,
                                city: (venues[s.venueId]?.city || "").toLowerCase().trim(),
                              })))
                            );
                            const overlapDates = (p.tour_routing || []).filter(r =>
                              r.city && yourShowLocations.some(s => s.date === r.date && s.city && s.city === r.city.toLowerCase().trim())
                            );
                            return (
                              <div key={p.id} style={{ position: "relative" }}>
                                {/* Agency tag + overlap badge */}
                                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4, paddingLeft: 2, minHeight: 16 }}>
                                  {agencyName
                                    ? <span style={{ fontFamily: FF, fontSize: 11, color: UI.textFnt, letterSpacing: "0.5px" }}>{agencyName}</span>
                                    : <span />
                                  }
                                  {overlapDates.length > 0 && (
                                    <span title={overlapDates.map(r => `${r.date} · ${r.city}`).join("\n")} style={{ fontFamily: FF, fontSize: 11, background: "#3ecf6e22", color: "var(--success)", border: "1px solid #3ecf6e44", borderRadius: 4, padding: "1px 7px", letterSpacing: "0.3px", cursor: "help", display: "inline-flex", alignItems: "center", gap: 4 }}>
                                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>{overlapDates.length} {overlapDates.length === 1 ? "overlap" : "overlaps"}
                                    </span>
                                  )}
                                </div>
                                <ProfileCard
                                  p={p}
                                  onClick={() => setMNetworkProfile(p)}
                                  actionSlot={
                                    <button
                                      onClick={e => { e.stopPropagation(); toggleConnection(p.id); }}
                                      style={{ fontFamily: FF, fontSize: 11, padding: "5px 14px", borderRadius: 4, border: `1px solid ${isConnected ? UI.green : UI.fieldBdr}`, cursor: "pointer", background: isConnected ? `${UI.green}18` : "transparent", color: isConnected ? UI.green : UI.textMut, fontWeight: isConnected ? 700 : 400, transition: "all 0.15s", width: "100%", letterSpacing: "0.3px" }}>
                                      {isConnected ? <span style={{ display: "inline-flex", alignItems: "center", gap: 4, justifyContent: "center" }}><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>Connected</span> : "+ Connect"}
                                    </button>
                                  }
                                />
                              </div>
                            );
                          })}
                          {filteredNetwork.length === 0 && !networkLoading && (
                            <div style={{ gridColumn: "1/-1" }}>
                              <div className="empty">
                                {networkSearch || networkCatFilter !== "all" || networkFilter === "connected"
                                  ? "No profiles match your filters."
                                  : "No one in the network yet. Profiles marked as visible to network will appear here as your team grows."
                                }
                              </div>
                            </div>
                          )}
                        </div>
                      </>
                    )}
                    {!networkLoading && !networkLoaded && (
                      <div style={{ padding: "60px 36px", textAlign: "center" }}>
                        <button className="add-p" onClick={loadNetworkProfiles}>Load Network</button>
                      </div>
                    )}
                  </>
                )}

                {/* ── Network profile read-only overlay ── */}
                {(() => { if (!mNetworkProfile) return null;
                  const p = mNetworkProfile;
                  const catColor = PROFILE_ROLE_COLORS[p.role_category] || "#6a8faf";
                  const initials = (p.preferred_name || p.name || "?").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
                  const isConnected = connections.has(p.id);
                  const bdr = UI.panelBdr;
                  const modalOverlaps = (() => {
                    const yourLocs = data.artists.flatMap(a =>
                      a.tours.flatMap(t => t.shows.map(s => ({
                        date: s.date,
                        city: (venues[s.venueId]?.city || "").toLowerCase().trim(),
                        tourName: t.name,
                      })))
                    );
                    return (p.tour_routing || [])
                      .map(r => {
                        const match = yourLocs.find(s => s.date === r.date && s.city && s.city === (r.city || "").toLowerCase().trim());
                        return match ? { ...r, tourName: match.tourName } : null;
                      })
                      .filter(Boolean);
                  })();
                  return (
                    <div className="ov" onClick={() => setMNetworkProfile(null)}>
                      <div className="mo" style={{ width: 460, padding: 0, overflow: "hidden" }} onClick={e => e.stopPropagation()}>
                        {/* Header band */}
                        <div style={{ background: `${catColor}18`, borderBottom: `1px solid ${catColor}30`, padding: "24px 28px 20px" }}>
                          <div style={{ display: "flex", gap: 18, alignItems: "flex-start" }}>
                            <div style={{ width: 68, height: 68, borderRadius: "50%", background: `${catColor}30`, border: `2px solid ${catColor}60`, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", flexShrink: 0 }}>
                              {p.photo_url ? <img src={p.photo_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <span style={{ fontFamily: FF, fontSize: 28, fontWeight: 700, color: catColor }}>{initials}</span>}
                            </div>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontFamily: FF, fontSize: 20, fontWeight: 700, color: UI.text, marginBottom: 2 }}>{p.preferred_name || p.name}</div>
                              {p.preferred_name && p.preferred_name !== p.name && <div style={{ fontFamily: FF, fontSize: 11, color: UI.textMut, marginBottom: 4 }}>{p.name}</div>}
                              {p.role && <div style={{ fontFamily: FF, fontSize: 12, color: UI.textMid, marginBottom: 6 }}>{p.role}</div>}
                              <span style={{ fontFamily: FF, fontSize: 11, letterSpacing: "1.5px", textTransform: "uppercase", color: catColor, background: `${catColor}25`, border: `1px solid ${catColor}40`, borderRadius: 4, padding: "2px 8px" }}>{PROFILE_ROLE_LABELS[p.role_category]}</span>
                              {p.teams?.name && <span style={{ fontFamily: FF, fontSize: 11, color: UI.textFnt, marginLeft: 8 }}>{p.teams.name}</span>}
                            </div>
                          </div>
                        </div>
                        {/* Body */}
                        <div style={{ padding: "20px 28px" }}>
                          {p.bio && <div style={{ fontFamily: FF, fontSize: 12, color: UI.textMid, lineHeight: 1.6, marginBottom: 16 }}>{p.bio}</div>}
                          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
                            {p.email && (
                              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                <div style={{ fontFamily: FF, fontSize: 12, color: UI.textMut }}>{p.email}</div>
                                <button className="abtn" style={{ fontSize: 11, padding: "3px 10px" }} onClick={() => navigator.clipboard.writeText(p.email)}>Copy</button>
                              </div>
                            )}
                            {p.phone && <div style={{ fontFamily: FF, fontSize: 12, color: UI.textMut }}>{fmtPhone(p.phone)}</div>}
                            {p.city && <div style={{ fontFamily: FF, fontSize: 12, color: UI.textMut }}>{p.city}</div>}
                            {p.instagram && (
                              <a href={`https://instagram.com/${p.instagram.replace(/^@/, "")}`} target="_blank" rel="noreferrer" style={{ fontFamily: FF, fontSize: 12, color: catColor, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 4 }}>{p.instagram.startsWith("@") ? p.instagram : `@${p.instagram}`}<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg></a>
                            )}
                          </div>
                          {(p.skills || []).length > 0 && (
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginBottom: 16 }}>
                              {p.skills.map(s => <span key={s} style={{ fontFamily: FF, fontSize: 11, background: `${catColor}18`, color: catColor, borderRadius: 4, padding: "3px 9px" }}>{s}</span>)}
                            </div>
                          )}

                          {/* Overlap dates */}
                          {modalOverlaps.length > 0 && (
                            <div style={{ background: "#3ecf6e12", border: "1px solid #3ecf6e30", borderRadius: 8, padding: "12px 14px" }}>
                              <div style={{ fontFamily: FF, fontSize: 11, letterSpacing: "1.5px", textTransform: "uppercase", color: "var(--success)", marginBottom: 10, display: "flex", alignItems: "center", gap: 5 }}>
                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>{modalOverlaps.length} Date{modalOverlaps.length !== 1 ? "s" : ""} in Common
                              </div>
                              <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                                {modalOverlaps.map((r, i) => (
                                  <div key={r.date} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                    <div style={{ fontFamily: FF, fontSize: 11, color: UI.textMid }}>
                                      {new Date(r.date + "T12:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}
                                      <span style={{ color: UI.textMut, marginLeft: 8 }}>{r.city}</span>
                                    </div>
                                    <span style={{ fontFamily: FF, fontSize: 11, color: UI.textFnt, letterSpacing: "0.3px" }}>{r.tourName}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          {(p.tour_routing || []).length > 0 && modalOverlaps.length === 0 && (
                            <div style={{ fontFamily: FF, fontSize: 11, color: UI.textMut, fontStyle: "italic" }}>Sharing routing — no city overlaps with your current tours.</div>
                          )}
                        </div>
                        {/* Footer */}
                        <div style={{ padding: "12px 28px", borderTop: `1px solid ${bdr}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <button
                            onClick={() => toggleConnection(p.id)}
                            style={{ fontFamily: FF, fontSize: 11, padding: "6px 18px", borderRadius: 4, border: `1px solid ${isConnected ? UI.green : UI.fieldBdr}`, cursor: "pointer", background: isConnected ? `${UI.green}18` : "transparent", color: isConnected ? UI.green : UI.textMut, fontWeight: isConnected ? 700 : 400, transition: "all 0.15s", display: "inline-flex", alignItems: "center", gap: 4 }}>
                            {isConnected ? <><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>Connected</> : "+ Connect"}
                          </button>
                          <button className="abtn" onClick={() => setMNetworkProfile(null)}>Close</button>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </>
            );
          })()}

          {/* OVERVIEW */}
          {view === "overview" && (
            <>
              {data.artists.length === 0 ? (
                <>
                {/* ── Onboarding empty state ── */}
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "80vh", padding: "48px 36px 56px", textAlign: "center" }}>

                  {/* Wordmark */}
                  <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 40, letterSpacing: 3, lineHeight: 1, marginBottom: 12, background: "linear-gradient(90deg, var(--accent), #ffcc55)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>ADVANCD</div>
                  <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: UI.textMid, letterSpacing: 3, textTransform: "uppercase", marginBottom: 6 }}>Tour Management</div>
                  <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 12, color: UI.textMut, marginBottom: 44 }}>Follow these steps to set up your workspace.</div>

                  {/* Step cards */}
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, maxWidth: 800, width: "100%" }}>
                    {[
                      {
                        step: "01", icon: "◈", title: "Add an Artist",
                        desc: "Start by adding an artist to your roster. You can manage multiple artists and their full touring schedules.",
                        actions: [
                          { label: "Add Artist", primary: true, onClick: () => setMArtist(true) },
                        ],
                      },
                      {
                        step: "02", icon: "⊞", title: "Build Your Venue List",
                        desc: "Search our pre-loaded venue database to add venues instantly, or import your own list from a spreadsheet.",
                        actions: [
                          { label: "Browse Directory", primary: true,  onClick: () => { setView("venues"); setDirModal("venue"); setDirQuery(""); setDirResults([]); } },
                          { label: "Import CSV",       primary: false, onClick: () => setMDataImport(true) },
                        ],
                      },
                      {
                        step: "03", icon: "✆", title: "Import Contacts",
                        desc: "Bring in your promoter and production contact list. Works with any CSV or Excel export.",
                        actions: [
                          { label: "Import Contacts", primary: true, onClick: () => setMAddContact(true) },
                        ],
                      },
                    ].map((card, i) => (
                      <div key={card.step} style={{
                        background: UI.panelBg,
                        border: `1px solid ${i === 0 ? UI.amdBdr : UI.panelBdr}`,
                        borderRadius: 8, padding: "24px 22px",
                        textAlign: "left", display: "flex", flexDirection: "column",
                      }}>
                        {/* Step badge + icon */}
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
                          <div style={{
                            fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
                            fontSize: 11, letterSpacing: "2px", textTransform: "uppercase",
                            color: i === 0 ? UI.amber : UI.textMut,
                            background: i === 0 ? UI.amdBg : UI.panelAlt,
                            border: `1px solid ${i === 0 ? UI.amdBdr : UI.panelBdr}`,
                            padding: "3px 8px", borderRadius: 4,
                          }}>Step {card.step}</div>
                          <div style={{ fontSize: 20, color: i === 0 ? UI.amber : UI.textFnt, lineHeight: 1 }}>{card.icon}</div>
                        </div>

                        {/* Title + description */}
                        <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 14, fontWeight: 700, color: UI.text, marginBottom: 8 }}>{card.title}</div>
                        <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 12, color: UI.textMut, lineHeight: 1.7, flex: 1, marginBottom: 20 }}>{card.desc}</div>

                        {/* Actions — fixed-height area so all cards stay the same height */}
                        <div style={{ display: "flex", flexDirection: "column", gap: 7, minHeight: 82 }}>
                          {card.actions.map(a => (
                            <button key={a.label}
                              className={a.primary ? "btn-p" : "btn-g"}
                              style={{ width: "100%", padding: "10px 0", fontSize: 12 }}
                              onClick={a.onClick}>{a.label}</button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Profile setup + sample tour — same max-width as cards, visually grouped */}
                  <div style={{ marginTop: 12, maxWidth: 800, width: "100%", display: "flex", flexDirection: "column", gap: 12 }}>

                    {/* Profile banner */}
                    <div
                      style={{
                        padding: "11px 18px",
                        background: data.profile?.name ? UI.ckOnBg : UI.amdBg,
                        border: `1px solid ${data.profile?.name ? UI.ckOnBdr : UI.amdBdr}`,
                        borderRadius: 8, cursor: data.profile?.name ? "default" : "pointer",
                        display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
                      }}
                      onClick={() => { if (!data.profile?.name) setView("profile"); }}>
                      <span style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 12, color: data.profile?.name ? UI.green : UI.amber, display: "inline-flex", alignItems: "center", gap: 7 }}>
                        {data.profile?.name
                          ? <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><polyline points="20 6 9 17 4 12"/></svg>
                          : <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>}
                        {data.profile?.name
                          ? `Profile set up as ${data.profile.name}${data.profile.agency ? ` · ${data.profile.agency}` : ""}`
                          : "Set up your profile — your name and agency appear on every advance email you send."}
                      </span>
                      {!data.profile?.name && (
                        <span style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: UI.amber, whiteSpace: "nowrap", flexShrink: 0 }}>Set up →</span>
                      )}
                    </div>

                    {/* Sample tour card */}
                    <div style={{
                      padding: "16px 22px 18px",
                      background: UI.panelBg, border: `1px solid ${UI.amdBdr}`, borderRadius: 8,
                    }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                        <span style={{ fontSize: 11, letterSpacing: "2px", textTransform: "uppercase", color: UI.amber, background: UI.amdBg, border: `1px solid ${UI.amdBdr}`, padding: "3px 7px", borderRadius: 4 }}>Shortcut</span>
                        <span style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 14, fontWeight: 700, color: UI.text }}>Load a Sample Tour</span>
                      </div>
                      <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 12, color: UI.textMut, lineHeight: 1.6, marginBottom: 14 }}>
                        Explore every feature with a pre-built 24-day tour — 9 shows, lodging, flights, and a 6-person touring party.
                      </div>
                      <button
                        className="btn-p"
                        style={{ width: "100%", padding: "10px 0", fontSize: 12 }}
                        disabled={sampleTourState === "loading"}
                        onClick={async () => {
                          if (!teamId || !user) return;
                          setSampleTourState("loading");
                          const result = await createSampleTour(teamId, user.id);
                          if (result.success) {
                            setSampleTourState({ inviteLinks: result.inviteLinks, claimLinks: result.claimLinks });
                            // Reload team data so the new sample tour appears immediately.
                            // (Mirrors the initial load in the [user] effect — there is no standalone loadData fn.)
                            const { data: loaded, venues: loadedVenues, hasData } = await loadNormalized(user.id, teamId);
                            if (hasData) {
                              setData(loaded);
                              setVenues(loadedVenues);
                            }
                          } else {
                            setSampleTourState(null);
                            alert("Sample tour creation failed: " + result.error);
                          }
                        }}
                      >
                        {sampleTourState === "loading" ? "Creating sample tour…" : <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg>Load Sample Tour</span>}
                      </button>
                    </div>
                  </div>
                </div>

                {/* ── Sample tour result modal ── */}
                {sampleTourState && sampleTourState !== "loading" && (
                  <div style={{ position: "fixed", inset: 0, zIndex: 700, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}
                    onClick={e => { if (e.target === e.currentTarget) setSampleTourState(null); }}>
                    <div style={{ width: "100%", maxWidth: 560, background: UI.panelBg, border: `1px solid ${UI.panelBdr}`, borderRadius: 12, overflow: "hidden", maxHeight: "82vh", display: "flex", flexDirection: "column" }}>

                      {/* Header */}
                      <div style={{ padding: "20px 24px 16px", borderBottom: `1px solid ${UI.panelBdr}`, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
                        <div>
                          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3 }}>
                            <span style={{ fontSize: 11, letterSpacing: "2px", textTransform: "uppercase", color: UI.green, background: UI.ckOnBg, border: `1px solid ${UI.ckOnBdr}`, padding: "3px 8px", borderRadius: 4 }}>Created</span>
                          </div>
                          <div style={{ fontSize: 16, fontWeight: 700, color: UI.text }}>Sample Tour Ready</div>
                          <div style={{ fontSize: 11, color: UI.textMut, marginTop: 2 }}>The Meridian · Spring {new Date().getFullYear()} North America</div>
                        </div>
                        <button className="btn-p" style={{ padding: "9px 20px", fontSize: 12, flexShrink: 0 }} onClick={() => setSampleTourState(null)}>Done</button>
                      </div>

                      {/* Scrollable body */}
                      <div style={{ overflowY: "auto", padding: "20px 24px", display: "flex", flexDirection: "column", gap: 20 }}>

                        {/* Invite links section */}
                        <div>
                          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                            <span style={{ fontSize: 11, letterSpacing: "2px", textTransform: "uppercase", color: UI.amber, background: UI.amdBg, border: `1px solid ${UI.amdBdr}`, padding: "3px 8px", borderRadius: 4 }}>Invite Links</span>
                            <span style={{ fontSize: 11, color: UI.textMut }}>Mobile view for band &amp; crew</span>
                          </div>
                          <div style={{ fontSize: 11, color: UI.textMut, marginBottom: 10, lineHeight: 1.6 }}>
                            The full touring party is already in your workspace under the Touring Party tab. These links give each member access to their own mobile experience. To test one without losing your TM session, open the link in a private/incognito window.
                          </div>
                          <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
                            {(sampleTourState.inviteLinks || []).map(l => (
                              <div key={l.email} style={{ padding: "11px 14px", background: UI.rowBg, border: `1px solid ${UI.panelBdr}`, borderRadius: 8 }}>
                                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 5 }}>
                                  <span style={{ fontSize: 14, fontWeight: 600, color: UI.text }}>{l.name}</span>
                                  <span style={{
                                    fontSize: 11, letterSpacing: "1.5px", textTransform: "uppercase",
                                    color: l.role === "band" ? UI.amber : UI.textMut,
                                    background: l.role === "band" ? UI.amdBg : UI.panelAlt,
                                    border: `1px solid ${l.role === "band" ? UI.amdBdr : UI.panelBdr}`,
                                    padding: "2px 7px", borderRadius: 4,
                                  }}>{l.role}</span>
                                </div>
                                <div style={{ fontSize: 11, color: UI.textMut, marginBottom: 7 }}>{l.email}</div>
                                <div style={{ display: "flex", gap: 6 }}>
                                  <input readOnly value={l.url} style={{ flex: 1, background: UI.panelAlt, border: `1px solid ${UI.fieldBdr}`, borderRadius: 4, padding: "6px 9px", fontSize: 11, color: UI.textMid, fontFamily: "monospace" }} onClick={e => e.target.select()} />
                                  <button className="btn-g" style={{ padding: "6px 14px", fontSize: 11 }}
                                    onClick={() => { try { navigator.clipboard.writeText(l.url); } catch {} }}>Copy</button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Claim links section */}
                        <div>
                          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                            <span style={{ fontSize: 11, letterSpacing: "2px", textTransform: "uppercase", color: UI.textMut, background: UI.panelAlt, border: `1px solid ${UI.panelBdr}`, padding: "3px 8px", borderRadius: 4 }}>Claim Links</span>
                            <span style={{ fontSize: 11, color: UI.textMut }}>Network profile pages</span>
                          </div>
                          <div style={{ fontSize: 11, color: UI.textMut, marginBottom: 10, lineHeight: 1.6 }}>Each person can visit their claim link to set up and personalise their own profile page.</div>
                          <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
                            {(sampleTourState.claimLinks || []).map(l => (
                              <div key={l.name} style={{ padding: "11px 14px", background: UI.rowBg, border: `1px solid ${UI.panelBdr}`, borderRadius: 8 }}>
                                <div style={{ fontSize: 14, fontWeight: 600, color: UI.text, marginBottom: 7 }}>{l.name}</div>
                                <div style={{ display: "flex", gap: 6 }}>
                                  <input readOnly value={l.url} style={{ flex: 1, background: UI.panelAlt, border: `1px solid ${UI.fieldBdr}`, borderRadius: 4, padding: "6px 9px", fontSize: 11, color: UI.textMid, fontFamily: "monospace" }} onClick={e => e.target.select()} />
                                  <button className="btn-g" style={{ padding: "6px 14px", fontSize: 11 }}
                                    onClick={() => { try { navigator.clipboard.writeText(l.url); } catch {} }}>Copy</button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Success note */}
                        <div style={{ padding: "12px 16px", background: UI.ckOnBg, border: `1px solid ${UI.ckOnBdr}`, borderRadius: 8, fontSize: 11, color: UI.green, lineHeight: 1.7 }}>
                          The tour is live in your workspace. Go to The Meridian → Spring {new Date().getFullYear()} North America to explore — the tour is built around today as a mid-tour off day in Denver, so you can immediately see what a live, active tour looks like.
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                </>
              ) : (
                /* ── Normal overview ── */
                <div>
                  <div className="ph">
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="ph-title">Artists</div>
                      <div className="ph-sub">{data.profile?.agency ? `${data.profile.agency.toUpperCase()} · ` : ""}{data.artists.length} {data.artists.length === 1 ? "ARTIST" : "ARTISTS"} · {allS.length} {allS.length === 1 ? "SHOW" : "SHOWS"}</div>
                    </div>
                    <div className="stats">
                      <div><div className="stat-n" style={{ color: "var(--success)" }}>{allS.filter(s => s.status === "confirmed").length}</div><div className="stat-l" style={{ color: "var(--success)" }}>Confirmed</div></div>
                      <div><div className="stat-n" style={{ color: "var(--accent)" }}>{allS.filter(s => s.status === "advancing").length}</div><div className="stat-l" style={{ color: "var(--accent)" }}>Advancing</div></div>
                      <div><div className="stat-n" style={{ color: "var(--danger)" }}>{allS.filter(s => s.status === "unstarted").length}</div><div className="stat-l" style={{ color: "var(--danger)" }}>Unstarted</div></div>
                    </div>
                  </div>

                  {/* ── Reminders banner ── */}
                  {reminders.length > 0 && (
                    <div style={{ padding: "0 36px 20px" }}>
                      <div style={{ background: UI.panelBg, border: `1px solid ${UI.panelBdr}`, borderRadius: 8, overflow: "hidden" }}>
                        {/* Header */}
                        <div style={{ padding: "10px 16px", background: UI.rowBg, borderBottom: remindersOpen ? `1px solid ${UI.panelBdr}` : "none", display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer" }}
                          onClick={() => setRemindersOpen(o => !o)}>
                          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <span style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, letterSpacing: "1.5px", color: reminders.some(r => r.urgency === "high") ? "var(--danger)" : UI.amber, display: "inline-flex", alignItems: "center", gap: 5 }}>
                              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>{reminders.length} REMINDER{reminders.length !== 1 ? "S" : ""}
                            </span>
                            {!remindersOpen && (
                              <span style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: UI.textFnt }}>
                                {reminders.filter(r => r.urgency === "high").length > 0 && `${reminders.filter(r => r.urgency === "high").length} high priority`}
                              </span>
                            )}
                          </div>
                          <span style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: UI.textFnt }}>{remindersOpen ? "Hide" : "Show"}</span>
                        </div>
                        {/* Reminder rows */}
                        {remindersOpen && reminders.map((r, i) => {
                          const typeConfig = {
                            deposit:   { label: "DEPOSIT",   color: UI.amber,   bg: UI.amdBg  },
                            advance:   { label: "ADVANCE",   color: UI.amber,   bg: UI.amdBg  },
                            settle:    { label: "SETTLE UP", color: "var(--danger)",  bg: lightMode ? "#fdeaea" : "#1e0d0d" },
                            unstarted: { label: "UNSTARTED", color: UI.amber,   bg: UI.amdBg  },
                          }[r.type] || { label: r.type.toUpperCase(), color: UI.textMut, bg: UI.rowBg };
                          return (
                            <div key={r.sId + '_' + r.type} style={{ display: "flex", alignItems: "center", gap: 12, padding: "9px 16px", borderBottom: i < reminders.length - 1 ? `1px solid ${UI.panelBdr}` : "none", background: r.urgency === "high" ? (lightMode ? "#fff8f8" : "#110808") : "transparent" }}>
                              <span style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, letterSpacing: "1px", color: typeConfig.color, background: typeConfig.bg, padding: "2px 7px", borderRadius: 4, flexShrink: 0 }}>{typeConfig.label}</span>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 12, fontWeight: 600, color: UI.text }}>{r.label}</div>
                                <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: UI.textMut, marginTop: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.detail}</div>
                              </div>
                              <button onClick={() => { goTour(r.aId, r.tId); setTimeout(() => { setSelS(r.sId); setDetailTab("settlement"); }, 80); }}
                                style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: UI.textMut, background: "none", border: `1px solid ${UI.panelBdr}`, borderRadius: 4, padding: "4px 12px", cursor: "pointer", flexShrink: 0 }}>
                                Go →
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <div className="ov-grid">
                    {data.artists.map((a, ai) => {
                      const _FF = "'Helvetica Neue', Helvetica, Arial, sans-serif";
                      const c = artistColor(a.colorIdx ?? ai);
                      const shows = a.tours.flatMap(t => t.shows);
                      const sent  = shows.filter(s => s.advanceToken).length;
                      const portConf = shows.filter(s => advanceStatus(s).label === "CONFIRMED").length;
                      const sentPct = shows.length ? Math.round(sent / shows.length * 100) : 0;
                      const todayStr = new Date().toISOString().slice(0, 10);
                      // Next upcoming show across all tours
                      const nextShow = shows
                        .filter(s => s.date >= todayStr)
                        .sort((a, b) => a.date.localeCompare(b.date))[0];
                      const nextVenue = nextShow ? (venues[nextShow.venueId] || null) : null;
                      const activeTour = a.tours.find(t => t.shows.some(s => s.date >= todayStr)) || a.tours[a.tours.length - 1];
                      // Confirmed count
                      const confirmedShows = shows.filter(s => s.status === "confirmed").length;
                      return (
                        <div
                          key={a.id}
                          className="ac"
                          style={{ borderColor: lightMode ? `${c.pill}40` : `${c.pill}28`, display: "flex", alignItems: "stretch" }}
                          onClick={() => { setSelA(a.id); setSelT(null); setSelS(null); setView("artist"); }}
                        >
                          {/* Left color accent bar */}
                          <div style={{ width: 4, background: c.pill, flexShrink: 0, borderRadius: "7px 0 0 7px" }} />

                          {/* Main content */}
                          <div style={{ flex: 1, minWidth: 0, padding: "11px 14px 11px", display: "flex", flexDirection: "column", gap: 4 }}>
                            {/* Row 1: name + portals top-right */}
                            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8 }}>
                              <div className="av-tour-name" style={{ color: lightMode ? "#1a1917" : "#f0f0f0", lineHeight: 1.2, fontSize: 20 }}>{a.name}</div>
                              <div style={{ textAlign: "right", flexShrink: 0 }}>
                                <div style={{ fontFamily: _FF, fontSize: 16, fontWeight: 600, color: portConf > 0 ? "var(--success)" : sent > 0 ? c.pill : UI.textVFnt, lineHeight: 1 }}>{sent}/{shows.length}</div>
                                <div style={{ fontFamily: _FF, fontSize: 11, color: UI.textVFnt, marginTop: 2, letterSpacing: 0.5 }}>PORTALS</div>
                              </div>
                            </div>

                            {/* Row 2: meta */}
                            <div className="av-tour-meta">
                              <span>{a.tours.length} TOUR{a.tours.length !== 1 ? "S" : ""}</span>
                              <span>{shows.length} SHOW{shows.length !== 1 ? "S" : ""}</span>
                              {confirmedShows > 0 && <span style={{ color: "var(--success)", display: "inline-flex", alignItems: "center", gap: 4 }}><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>{confirmedShows} confirmed</span>}
                            </div>

                            {/* Row 3: progress bar */}
                            <div style={{ height: 3, background: lightMode ? "#dedad4" : "#1a1a1a", borderRadius: 8, overflow: "hidden", marginTop: 2 }}>
                              <div style={{ height: "100%", width: `${sentPct}%`, borderRadius: 8, background: portConf === sent && sent > 0 ? "var(--success)" : `linear-gradient(90deg, ${c.pill}, ${c.pill}bb)`, transition: "width 0.4s" }} />
                            </div>

                            {/* Row 4: next show */}
                            {nextShow && (
                              <div style={{ fontFamily: _FF, fontSize: 11, color: UI.textMut, display: "flex", alignItems: "center", gap: 5, marginTop: 1 }}>
                                <span>{new Date(nextShow.date + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })}</span>
                                {nextVenue?.name && <><span style={{ color: UI.textVFnt }}>·</span><span>{nextVenue.name}</span></>}
                                {(nextVenue?.city || nextVenue?.addressState) && <><span style={{ color: UI.textVFnt }}>·</span><span>{[nextVenue.city, nextVenue.addressState].filter(Boolean).join(", ")}</span></>}
                              </div>
                            )}
                          </div>

                          {/* Edit button — bottom right, on hover */}
                          <button className="ac-edit-btn" title="Edit artist" onClick={e => { e.stopPropagation(); setMEditArtist(a.id); }}>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}

          {/* ADVANCES — dedicated view, same data source as Overview */}
          {view === "dashboard" && (() => {
            const FF2 = "'Helvetica Neue', Helvetica, Arial, sans-serif";
            const today = new Date().toISOString().slice(0, 10);

            // Same data pattern as the working Overview advances section
            const allRich = data.artists.flatMap((a, ai) =>
              a.tours.flatMap(t =>
                t.shows.map(s => ({
                  ...s,
                  artistName: a.name, artistId: a.id, artistIdx: ai,
                  tourName: t.name, tourId: t.id,
                  tourSections: t.advanceConfig?.sections || ALL_SECTION_IDS,
                }))
              )
            ).sort((a, b) => (a.date || "").localeCompare(b.date || ""));

            const total        = allRich.length;
            const cntConfirmed = allRich.filter(s => s.status === "confirmed").length;
            const cntAdvancing = allRich.filter(s => s.status === "advancing").length;
            const cntUnstarted = allRich.filter(s => s.status === "unstarted").length;

            const dashShows = allRich.filter(s => {
              if (dashFilter === "upcoming")  return s.date >= today;
              if (dashFilter === "advancing") return s.status === "advancing";
              if (dashFilter === "needs")     return advanceStatus(s).label === "IN PROGRESS";
              if (dashFilter === "submitted") return s.status === "confirmed";
              return true;
            }).filter(s => {
              if (dashDateRange === "30") { const d = new Date(); d.setDate(d.getDate()+30); return s.date <= d.toISOString().slice(0,10); }
              if (dashDateRange === "90") { const d = new Date(); d.setDate(d.getDate()+90); return s.date <= d.toISOString().slice(0,10); }
              return true;
            });

            return (
              <>
                <div className="ph">
                  <div>
                    <div className="ph-title">Advances</div>
                    <div className="ph-sub">{total} {total === 1 ? "SHOW" : "SHOWS"} · {allRich.filter(s => s.date >= today).length} UPCOMING</div>
                  </div>
                  <div className="stats">
                    <div><div className="stat-n" style={{ color: "var(--success)" }}>{cntConfirmed}</div><div className="stat-l" style={{ color: "var(--success)" }}>Confirmed</div></div>
                    <div><div className="stat-n" style={{ color: "var(--accent)" }}>{cntAdvancing}</div><div className="stat-l" style={{ color: "var(--accent)" }}>Advancing</div></div>
                    <div><div className="stat-n" style={{ color: "var(--danger)" }}>{cntUnstarted}</div><div className="stat-l" style={{ color: "var(--danger)" }}>Unstarted</div></div>
                  </div>
                </div>

                {/* Filter pills — same as Overview */}
                <div style={{ padding: "16px 36px 14px", display: "flex", gap: 6, alignItems: "center", justifyContent: "space-between", flexWrap: "wrap" }}>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {[
                      { id: "upcoming",  label: "Upcoming" },
                      { id: "advancing", label: "Advancing" },
                      { id: "needs",     label: "Needs Action" },
                      { id: "submitted", label: "Confirmed" },
                      { id: "all",       label: "All Shows" },
                    ].map(f => (
                      <button key={f.id} className={`fp ${dashFilter === f.id ? "on" : ""}`} onClick={() => setDashFilter(f.id)}>{f.label}</button>
                    ))}
                  </div>
                  <div style={{ display: "flex", gap: 6 }}>
                    {[{ id: "30", label: "30 Days" }, { id: "90", label: "90 Days" }, { id: "all", label: "All Time" }].map(f => (
                      <button key={f.id} className={`fp ${dashDateRange === f.id ? "on" : ""}`} onClick={() => setDashDateRange(f.id)}>{f.label}</button>
                    ))}
                  </div>
                </div>

                <div className="s-list">
                  {dashShows.length === 0 && <div className="empty">No shows match this filter.</div>}
                  {dashShows.map(s => {
                    const v = venues[s.venueId];
                    const uni = unifiedStatus(s);
                    const daysUntil = Math.ceil((new Date(s.date + "T12:00:00") - new Date()) / 864e5);
                    const isPast = s.date < today;
                    const urgent = !isPast && daysUntil <= 7;
                    const advData = s.advanceData || {};
                    const filledSecs = s.tourSections.filter(secId => {
                      const sec = advData[secId];
                      return sec && typeof sec === "object" && Object.values(sec).some(v => v !== "" && v !== null && v !== undefined && v !== false);
                    }).length;
                    const fillPct = s.tourSections.length > 0 ? Math.round(filledSecs / s.tourSections.length * 100) : 0;
                    return (
                      <div key={s.id} className="s-row" onClick={() => { setSelA(s.artistId); setSelT(s.tourId); setSelS(s.id); setView("tour"); }}>
                        <div className="s-date">{fmtDate(s.date).split(",")[0]}<br />{fmtDate(s.date).split(",")[1]}</div>
                        <div style={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
                          <div className="s-vname" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{v?.name || "—"}</div>
                          <div style={{ fontFamily: FF2, fontSize: 11, color: UI.textMut, letterSpacing: 0.3, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {s.artistName} · {s.tourName}{(v?.city || v?.addressState) ? ` · ${[v.city, v.addressState].filter(Boolean).join(", ")}` : ""}
                          </div>
                          {/* Always reserve the progress-bar row's height so rows don't change
                              size when an advance is started and the bar appears. */}
                          <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 5, height: 16 }}>
                            {s.advanceToken && s.tourSections.length > 0 && (<>
                              <div style={{ width: 80, height: 3, background: UI.panelBdr, borderRadius: 4, overflow: "hidden" }}>
                                <div style={{ height: "100%", width: `${fillPct}%`, background: fillPct === 100 ? UI.green : UI.amber, borderRadius: 4 }} />
                              </div>
                              <span style={{ fontFamily: FF2, fontSize: 11, color: fillPct === 100 ? UI.green : UI.textMut }}>{fillPct}% advance filled</span>
                            </>)}
                          </div>
                        </div>
                        <div>
                          <span style={{ fontFamily: FF2, fontSize: 11, letterSpacing: "0.3px", color: uni.color, background: uni.bg, padding: "6px 13px", borderRadius: 4, whiteSpace: "nowrap" }}>{uni.label}</span>
                        </div>
                        <div>
                          {(() => {
                            const label = isPast ? `${Math.abs(daysUntil)}d ago` : daysUntil === 0 ? "Today" : daysUntil === 1 ? "Tomorrow" : `${daysUntil}d`;
                            const bg    = isPast ? (lightMode ? "#e8e4de" : "#1a1a1a") : urgent ? (lightMode ? "#fff3cc" : "#2a1f00") : (lightMode ? "#e8e4de" : "#1a1a1a");
                            const color = isPast ? UI.textVFnt : urgent ? UI.amber : UI.textMut;
                            return <span style={{ fontFamily: FF2, fontSize: 11, letterSpacing: "0.3px", color, background: bg, padding: "6px 13px", borderRadius: 4, whiteSpace: "nowrap" }}>{label}</span>;
                          })()}
                        </div>
                        <div className="s-acts" onClick={e => e.stopPropagation()}>
                          <button className="abtn" onClick={() => { setSelA(s.artistId); setSelT(s.tourId); setSelS(s.id); setView("tour"); }}>Open →</button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            );
          })()}

          {/* CALENDAR */}
          {view === "calendar" && (
            <>
              <div className="ph">
                <div>
                  <div className="ph-title">Tour Calendar</div>
                  <div className="ph-sub">{data.artists.length} {data.artists.length === 1 ? "ARTIST" : "ARTISTS"} · {data.artists.flatMap(a => a.tours.flatMap(t => t.shows)).length} {data.artists.flatMap(a => a.tours.flatMap(t => t.shows)).length === 1 ? "SHOW" : "SHOWS"}</div>
                </div>
              </div>
              <CalendarView data={data} venues={venues} onGoToShow={goToShow} onAddShow={iso => { setMAddShows({ defaultDate: iso }); }} lightMode={lightMode} />
            </>
          )}

          {/* MY RECS VIEW */}
          {view === "myrecs" && (() => {
            const FF = "'Helvetica Neue', Helvetica, Arial, sans-serif";

            // Use the shared REC_CAT_COLORS constant (defined alongside LOCAL_CATS)
            const REC_CATS = [
              { id: "all", label: "All" },
              ...Object.entries(REC_CAT_COLORS).map(([id]) => ({ id, label: id.charAt(0).toUpperCase() + id.slice(1) })),
            ];

            const allRecs   = myRecs.data || [];
            const searchLow = myRecsSearch.trim().toLowerCase();

            const catFiltered = myRecsCat === "all"
              ? allRecs
              : allRecs.filter(r => (r.category || r.cat) === myRecsCat);

            const filtered = searchLow
              ? catFiltered.filter(r =>
                  (r.city  || "").toLowerCase().includes(searchLow) ||
                  (r.state || "").toLowerCase().includes(searchLow) ||
                  (r.name  || "").toLowerCase().includes(searchLow)
                )
              : catFiltered;

            // Group by "City, State" — preserve alphabetical city order
            const grouped = {};
            for (const rec of filtered) {
              const cityLabel = [rec.city, rec.state].filter(Boolean).join(", ") || "Unknown Location";
              if (!grouped[cityLabel]) grouped[cityLabel] = [];
              grouped[cityLabel].push(rec);
            }
            const cities = Object.keys(grouped).sort();

            // Count per category for filter chip badges
            const catCounts = {};
            for (const r of allRecs) {
              const c = r.category || r.cat || "other";
              catCounts[c] = (catCounts[c] || 0) + 1;
            }

            return (
              <>
                <div className="ph">
                  <div>
                    <div className="ph-title">My Recommendations</div>
                    {(() => {
                      const totalCities = new Set(allRecs.map(r => [r.city, r.state].filter(Boolean).join(", "))).size;
                      return <div className="ph-sub">{allRecs.length} REC{allRecs.length !== 1 ? "S" : ""} · {totalCities} {totalCities !== 1 ? "CITIES" : "CITY"}</div>;
                    })()}
                  </div>
                  <button className="add-p" onClick={() => { setSingleRecForm({ name: "", city: "", state: "", cat: "food", note: "", url: "", placeId: null, confirmed: false }); setBulkText(""); setBulkCity(""); setBulkState(""); setBulkParsed(null); setMyRecsTab("multiple"); setMyRecsModal("open"); }}>
                    + Add Recs
                  </button>
                </div>

                <div className="crm-bar">
                  <input
                    className="crm-inp"
                    placeholder="Filter by city or name…"
                    value={myRecsSearch}
                    onChange={e => setMyRecsSearch(e.target.value)}
                  />
                  <span className="crm-count">{allRecs.length} rec{allRecs.length !== 1 ? "s" : ""}</span>
                </div>

                {/* ── Add Recs Modal (tabbed) ── */}
                {myRecsModal === "open" && (() => {
                  const iStyle = { fontFamily: FF, fontSize: 12, color: UI.text, background: UI.rowBg, border: `1px solid ${UI.panelBdr}`, borderRadius: 8, padding: "7px 11px", outline: "none", width: "100%", boxSizing: "border-box" };
                  const lStyle = { fontFamily: FF, fontSize: 11, letterSpacing: "1.2px", textTransform: "uppercase", color: UI.textFnt, marginBottom: 5, display: "block" };

                  // ── Single tab logic ──
                  const cats = Object.keys(REC_CAT_COLORS);
                  const placeId = singleRecForm.placeId || null;
                  const dupeRec = placeId ? (myRecs.data || []).find(r => r.url?.includes(placeId)) : null;
                  const canSave = singleRecForm.name.trim() && singleRecForm.city.trim() && !singleRecSaving;
                  const handleSingleSave = async () => {
                    if (!canSave) return;
                    setSingleRecSaving(true);
                    await addCommunityRec(
                      { name: singleRecForm.name.trim(), cat: singleRecForm.cat, note: singleRecForm.note.trim() || null, url: singleRecForm.url.trim() || null },
                      null,
                      singleRecForm.city.trim(),
                      singleRecForm.state.trim() || null,
                    );
                    setSingleRecSaving(false);
                    setMyRecsModal(null);
                  };

                  // ── Multiple tab logic ──
                  const handleParse = async () => {
                    if (!bulkText.trim()) return;
                    setBulkParsing(true);
                    try {
                      const parsed = await parseRecsWithAI(bulkText, bulkCity, bulkState);
                      setBulkParsed(parsed.map((r, i) => ({ ...r, _id: i, city: r.city || bulkCity || "", state: r.state || bulkState || "" })));
                    } catch (err) {
                      console.error("[ADVANCD] parseRecsWithAI failed:", err);
                      setBulkParsed([]);
                    }
                    setBulkParsing(false);
                  };
                  const handleSaveAll = async () => {
                    if (!bulkParsed?.length) return;
                    setBulkSaving(true);
                    await Promise.all(bulkParsed.map(r =>
                      addCommunityRec(
                        { name: r.name, cat: r.category || "other", note: r.note || null, url: r.url || null },
                        null, r.city || "", r.state || null,
                      )
                    ));
                    setBulkSaving(false);
                    setMyRecsModal(null);
                    setBulkParsed(null);
                  };
                  const updateParsed = (id, field, val) =>
                    setBulkParsed(prev => prev.map(r => r._id === id ? { ...r, [field]: val } : r));
                  const removeParsed = (id) =>
                    setBulkParsed(prev => prev.filter(r => r._id !== id));

                  const closeModal = () => { setMyRecsModal(null); setBulkParsed(null); };
                  const isMultiple = myRecsTab === "multiple";

                  return (
                    <div style={{ position: "fixed", inset: 0, zIndex: 4000, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center" }}
                      onClick={e => { if (e.target === e.currentTarget) closeModal(); }}>
                      <div style={{ background: UI.bg, border: `1px solid ${UI.panelBdr}`, borderRadius: 12, width: 520, maxWidth: "90vw", height: 520, display: "flex", flexDirection: "column", boxShadow: "0 8px 40px rgba(0,0,0,0.5)", overflow: "hidden" }}>

                        {/* Header */}
                        <div style={{ padding: "22px 28px 0" }}>
                          <div style={{ fontFamily: FF, fontSize: 16, fontWeight: 700, color: UI.text, marginBottom: 2 }}>Add Recs</div>
                          <div style={{ fontFamily: FF, fontSize: 11, color: UI.textMut, marginBottom: 16 }}>
                            {isMultiple
                              ? (bulkParsed ? "Edit or remove recs before saving" : "Paste any text — emails, messages, lists — and Claude will extract the recs")
                              : "Search by name — selecting a result links it to Google Maps and fills in city automatically."}
                          </div>
                          {/* Tab bar */}
                          <div style={{ display: "flex", gap: 0, borderBottom: `1px solid ${UI.panelBdr}` }}>
                            {[["multiple", "Multiple Recs"], ["single", "Single Rec"]].map(([id, label]) => {
                              const active = myRecsTab === id;
                              const spacer = <span style={{ visibility: "hidden", fontWeight: 700 }}>{label}</span>;
                              return (
                                <button key={id} onClick={() => { setMyRecsTab(id); setBulkParsed(null); }}
                                  style={{ fontFamily: FF, fontSize: 12, fontWeight: active ? 700 : 400, color: active ? UI.text : UI.textMut, background: "none", border: "none", borderBottom: active ? `2px solid ${UI.amber}` : "2px solid transparent", padding: "6px 16px", cursor: "pointer", marginBottom: -1, position: "relative" }}>
                                  {spacer}
                                  <span style={{ position: "absolute", left: 0, right: 0, textAlign: "center" }}>{label}</span>
                                </button>
                              );
                            })}
                            {isMultiple && bulkParsed && (
                              <button onClick={() => setBulkParsed(null)} style={{ fontFamily: FF, fontSize: 11, padding: "5px 12px", borderRadius: 4, border: `1px solid ${UI.panelBdr}`, background: "none", color: UI.textMut, cursor: "pointer", marginLeft: "auto", alignSelf: "center", marginBottom: 4 }}>← Back</button>
                            )}
                          </div>
                        </div>

                        {/* Body */}
                        <div style={{ flex: 1, overflowY: "auto", padding: "20px 28px" }}>

                          {/* ── Multiple Recs tab ── */}
                          {isMultiple && !bulkParsed && (
                            <div style={{ display: "flex", flexDirection: "column", gap: 14, height: "100%" }}>
                              <div style={{ display: "flex", gap: 10 }}>
                                <div style={{ flex: 2 }}>
                                  <label style={lStyle}>City Hint <span style={{ textTransform: "none", letterSpacing: 0, color: UI.textVFnt }}>(optional)</span></label>
                                  <input value={bulkCity} onChange={e => setBulkCity(e.target.value)} placeholder="e.g. Nashville" style={iStyle} />
                                </div>
                                <div style={{ flex: 1 }}>
                                  <label style={lStyle}>State</label>
                                  <input value={bulkState} onChange={e => setBulkState(e.target.value)} placeholder="TN" style={iStyle} maxLength={3} />
                                </div>
                              </div>
                              <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
                                <label style={lStyle}>Text to parse</label>
                                <textarea
                                  value={bulkText}
                                  onChange={e => setBulkText(e.target.value)}
                                  placeholder={"Paste an email, iMessage, notes — anything with recommendations.\n\ne.g. \"You have to check out Dino's for burgers — cash only, amazing. And The 5 Spot has great live music on weekdays.\""}
                                  style={{ ...iStyle, flex: 1, minHeight: 180, resize: "none", lineHeight: 1.6, padding: 12 }}
                                />
                              </div>
                            </div>
                          )}

                          {isMultiple && bulkParsed && (
                            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                              {bulkParsed.length === 0 && (
                                <div style={{ fontFamily: FF, fontSize: 12, color: UI.textMut, textAlign: "center", padding: "32px 0" }}>
                                  No recommendations found. Try adding more detail or go back and edit.
                                </div>
                              )}
                              {bulkParsed.map(r => {
                                const col = REC_CAT_COLORS[r.category] || "#888";
                                return (
                                  <div key={r._id} style={{ border: `1px solid ${UI.panelBdr}`, borderRadius: 8, padding: "12px 14px", background: UI.rowBg, display: "flex", gap: 12, alignItems: "flex-start" }}>
                                    <div style={{ width: 8, height: 8, borderRadius: "50%", background: col, flexShrink: 0, marginTop: 5 }} />
                                    <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8, minWidth: 0 }}>
                                      <input value={r.name} onChange={e => updateParsed(r._id, "name", e.target.value)}
                                        style={{ ...iStyle, fontWeight: 600, fontSize: 14, padding: "5px 9px" }} />
                                      <div style={{ display: "flex", gap: 8 }}>
                                        <select value={r.category || "other"} onChange={e => updateParsed(r._id, "category", e.target.value)}
                                          style={{ ...iStyle, width: "auto", flex: 1, padding: "4px 8px", fontSize: 11 }}>
                                          {Object.keys(REC_CAT_COLORS).map(c => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
                                        </select>
                                        <input value={r.city || ""} onChange={e => updateParsed(r._id, "city", e.target.value)}
                                          placeholder="City" style={{ ...iStyle, flex: 2, padding: "4px 9px", fontSize: 11 }} />
                                        <input value={r.state || ""} onChange={e => updateParsed(r._id, "state", e.target.value)}
                                          placeholder="ST" style={{ ...iStyle, width: 44, padding: "4px 8px", fontSize: 11 }} maxLength={3} />
                                      </div>
                                      <input value={r.note || ""} onChange={e => updateParsed(r._id, "note", e.target.value)}
                                        placeholder="Note (optional)" style={{ ...iStyle, fontSize: 11, padding: "5px 9px", color: UI.textMut }} maxLength={120} />
                                    </div>
                                    <button onClick={() => removeParsed(r._id)} title="Remove"
                                      style={{ background: "none", border: "none", color: UI.textVFnt, cursor: "pointer", lineHeight: 1, padding: "2px 4px", flexShrink: 0, display: "inline-flex", alignItems: "center" }}><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></button>
                                  </div>
                                );
                              })}
                            </div>
                          )}

                          {/* ── Single Rec tab ── */}
                          {!isMultiple && (
                            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                              <div>
                                <label style={lStyle}>Place Name *</label>
                                <PlaceSearchInput
                                  query={singleRecForm.name}
                                  onQueryChange={val => setSingleRecForm(p => ({ ...p, name: val, placeId: null, confirmed: false }))}
                                  onPlacePicked={({ name, placeId, city, state, mapsUrl }) => {
                                    setSingleRecForm(p => ({ ...p, name, placeId, confirmed: true, city: city || p.city, state: state || p.state, url: mapsUrl }));
                                  }}
                                  onClear={() => setSingleRecForm(p => ({ ...p, placeId: null, confirmed: false }))}
                                  confirmed={!!singleRecForm.confirmed}
                                  UI={UI}
                                  location={singleRecForm.city ? `${singleRecForm.city} ${singleRecForm.state}`.trim() : ""}
                                  placeholder="e.g. Zuni Café, Dino's Burgers…"
                                  autoFocus
                                />
                              </div>
                              {dupeRec && (
                                <div style={{ fontFamily: FF, fontSize: 11, color: UI.amber, background: UI.amdBg, border: `1px solid ${UI.amdBdr}`, borderRadius: 8, padding: "8px 12px" }}>
                                  You already have <strong>{dupeRec.name}</strong> in {[dupeRec.city, dupeRec.state].filter(Boolean).join(", ")}. Save anyway to add a duplicate, or close and skip.
                                </div>
                              )}
                              <div>
                                <label style={lStyle}>Category</label>
                                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                                  {cats.map(c => {
                                    const col = REC_CAT_COLORS[c];
                                    const active = singleRecForm.cat === c;
                                    return (
                                      <button key={c} onClick={() => setSingleRecForm(p => ({ ...p, cat: c }))}
                                        style={{ fontFamily: FF, fontSize: 11, padding: "4px 12px", borderRadius: 8, cursor: "pointer", border: `1px solid ${active ? col : UI.panelBdr}`, background: active ? `${col}20` : UI.rowBg, color: active ? col : UI.textMut, fontWeight: active ? 600 : 400 }}>
                                        {c.charAt(0).toUpperCase() + c.slice(1)}
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>
                              <div style={{ display: "flex", gap: 10 }}>
                                <div style={{ flex: 2 }}>
                                  <label style={lStyle}>City * {singleRecForm.confirmed && singleRecForm.city && <span style={{ textTransform: "none", letterSpacing: 0, color: "var(--success)", marginLeft: 6, fontSize: 11 }}>auto-filled</span>}</label>
                                  <input value={singleRecForm.city} onChange={e => setSingleRecForm(p => ({ ...p, city: e.target.value }))} placeholder="e.g. San Francisco" style={iStyle} />
                                </div>
                                <div style={{ flex: 1 }}>
                                  <label style={lStyle}>State</label>
                                  <input value={singleRecForm.state} onChange={e => setSingleRecForm(p => ({ ...p, state: e.target.value }))} placeholder="CA" style={iStyle} maxLength={3} />
                                </div>
                              </div>
                              <div>
                                <label style={lStyle}>Note <span style={{ textTransform: "none", letterSpacing: 0, color: UI.textVFnt }}>(optional)</span></label>
                                <input value={singleRecForm.note} onChange={e => setSingleRecForm(p => ({ ...p, note: e.target.value }))} placeholder="Why it's worth visiting…" style={iStyle} maxLength={120} />
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Footer — pinned */}
                        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, padding: "14px 28px", borderTop: `1px solid ${UI.panelBdr}`, flexShrink: 0 }}>
                          <button onClick={closeModal}
                            style={{ fontFamily: FF, fontSize: 12, padding: "8px 18px", borderRadius: 8, border: `1px solid ${UI.panelBdr}`, background: "none", color: UI.textMut, cursor: "pointer" }}>Cancel</button>
                          {isMultiple ? (
                            !bulkParsed ? (
                              <button onClick={handleParse} disabled={bulkParsing || !bulkText.trim()}
                                style={{ fontFamily: FF, fontSize: 12, fontWeight: 600, padding: "8px 20px", borderRadius: 8, border: "none", background: bulkParsing || !bulkText.trim() ? UI.panelBdr : UI.amber, color: bulkParsing || !bulkText.trim() ? UI.textVFnt : "var(--accent-fg)", cursor: "pointer", minWidth: 100 }}>
                                {bulkParsing ? "Parsing…" : "Parse →"}
                              </button>
                            ) : (
                              <button onClick={handleSaveAll} disabled={bulkSaving || bulkParsed.length === 0}
                                style={{ fontFamily: FF, fontSize: 12, fontWeight: 600, padding: "8px 20px", borderRadius: 8, border: "none", background: bulkSaving || bulkParsed.length === 0 ? UI.panelBdr : UI.amber, color: bulkSaving || bulkParsed.length === 0 ? UI.textVFnt : "var(--accent-fg)", cursor: "pointer", minWidth: 140 }}>
                                {bulkSaving ? "Saving…" : `Add ${bulkParsed.length} Rec${bulkParsed.length !== 1 ? "s" : ""}`}
                              </button>
                            )
                          ) : (
                            <button onClick={handleSingleSave} disabled={!canSave}
                              style={{ fontFamily: FF, fontSize: 12, fontWeight: 600, padding: "8px 20px", borderRadius: 8, border: "none", background: !canSave ? UI.panelBdr : UI.amber, color: !canSave ? UI.textVFnt : "var(--accent-fg)", cursor: !canSave ? "not-allowed" : "pointer" }}>
                              {singleRecSaving ? "Saving…" : "Add Rec"}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })()}

                <div style={{ padding: "16px 24px", display: "flex", flexDirection: "column", gap: 24, maxWidth: 720 }}>

                  {/* Category filter chips */}
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                    {REC_CATS.map(c => {
                      const count = c.id === "all" ? allRecs.length : (catCounts[c.id] || 0);
                      if (c.id !== "all" && count === 0) return null;
                      const active = myRecsCat === c.id;
                      const col    = REC_CAT_COLORS[c.id] || "#3ecf6e"; // literal — col feeds `${col}18` alpha tints
                      return (
                        <button key={c.id} onClick={() => setMyRecsCat(c.id)}
                          style={{ fontFamily: FF, fontSize: 11, letterSpacing: 0.3, padding: "4px 11px", borderRadius: 8, cursor: "pointer", lineHeight: 1.4, display: "flex", alignItems: "center", gap: 5,
                            border: `1px solid ${active ? col : UI.panelBdr}`,
                            background: active ? `${col}18` : UI.rowBg,
                            color: active ? col : UI.textMut }}>
                          {c.id !== "all" && <span style={{ width: 7, height: 7, borderRadius: "50%", background: col, flexShrink: 0, opacity: active ? 1 : 0.45 }} />}
                          {c.label}
                          {count > 0 && <span style={{ fontSize: 11, color: active ? col : UI.textVFnt, opacity: 0.8 }}>{count}</span>}
                        </button>
                      );
                    })}
                  </div>

                  {/* States */}
                  {myRecs.status === "loading" && (
                    <div style={{ fontFamily: FF, fontSize: 12, color: UI.textFnt, padding: "16px 0" }}>Loading your recommendations…</div>
                  )}
                  {myRecs.status === "error" && (
                    <div style={{ fontFamily: FF, fontSize: 12, color: "var(--danger)" }}>Failed to load — try refreshing the page.</div>
                  )}
                  {myRecs.status === "ready" && allRecs.length === 0 && (
                    <div style={{ fontFamily: FF, fontSize: 12, color: UI.textFnt, padding: "32px 0", textAlign: "center" }}>
                      <div style={{ marginBottom: 12, display: "flex", justifyContent: "center", opacity: 0.25 }}>
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/><circle cx="12" cy="9" r="2.5"/></svg>
                      </div>
                      <div style={{ fontWeight: 600, color: UI.text, marginBottom: 6 }}>No recommendations yet</div>
                      <div style={{ color: UI.textMut, lineHeight: 1.5 }}>Open a tour day's Local Discovery section and share a place — it'll appear here.</div>
                    </div>
                  )}
                  {myRecs.status === "ready" && allRecs.length > 0 && filtered.length === 0 && (
                    <div style={{ fontFamily: FF, fontSize: 12, color: UI.textFnt }}>No recs match your filters.</div>
                  )}

                  {/* ── City rolodex decks ── */}
                  {myRecs.status === "ready" && cities.map(cityLabel => {
                    const recs = grouped[cityLabel];

                    return (
                      <div key={cityLabel}>
                        {/* City header */}
                        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                          <span style={{ fontFamily: FF, fontSize: 12, fontWeight: 700, color: UI.text, letterSpacing: 0.3 }}>{cityLabel}</span>
                          <span style={{ fontFamily: FF, fontSize: 11, color: UI.textVFnt }}>{recs.length} rec{recs.length !== 1 ? "s" : ""}</span>
                          <div style={{ flex: 1, height: 1, background: UI.panelBdr }} />
                        </div>

                        {/* Card deck — flush stack, no gap between cards */}
                        <div style={{ borderRadius: 8, overflow: "hidden", border: `1px solid ${UI.panelBdr}`, boxShadow: "0 2px 12px rgba(0,0,0,0.18)" }}>
                          {recs.map((rec, idx) => {
                            const cat      = rec.category || rec.cat || "other";
                            const color    = REC_CAT_COLORS[cat] || "#888";
                            const isOpen   = expandedRec === rec.id;
                            const mapsSearch = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([rec.name, rec.city, rec.state].filter(Boolean).join(", "))}`;
                            const mapHref  = rec.url || mapsSearch;
                            const notFirst = idx > 0;

                            if (isOpen) {
                              // ── Expanded card ──
                              return (
                                <div key={rec.id}
                                  style={{
                                    borderTop: notFirst ? `1px solid ${UI.panelBdr}` : "none",
                                    borderLeft: `4px solid ${color}`,
                                    background: UI.bg,
                                    boxShadow: `inset 0 0 0 0 transparent, 0 0 0 0 transparent`,
                                    position: "relative",
                                    zIndex: 10,
                                  }}>
                                  {/* Expanded header row — click to collapse */}
                                  {(() => {
                                    const expSaveCount = recSaveCounts[rec.id] || 0;
                                    const expTopPick   = expSaveCount >= 2;
                                    return (
                                      <button onClick={() => setExpandedRec(null)}
                                        style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "11px 14px 8px", background: "none", border: "none", cursor: "pointer", textAlign: "left" }}>
                                        <span style={{ fontFamily: FF, fontSize: 14, fontWeight: 700, color: UI.text, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{rec.name}</span>
                                        {expTopPick && (
                                          <span style={{ fontFamily: FF, fontSize: 11, letterSpacing: 0.8, color: color, textTransform: "uppercase", flexShrink: 0, padding: "2px 5px", background: `${color}18`, borderRadius: 4, fontWeight: 700 }}>TOP PICK</span>
                                        )}
                                        <span style={{ fontFamily: FF, fontSize: 11, letterSpacing: 0.8, color: color, textTransform: "uppercase", flexShrink: 0, padding: "2px 7px", border: `1px solid ${color}40`, borderRadius: 4, background: `${color}10` }}>{cat}</span>
                                        <span style={{ fontFamily: FF, fontSize: 11, color: UI.textMut, flexShrink: 0 }}>−</span>
                                      </button>
                                    );
                                  })()}
                                  {/* Expanded body */}
                                  <div style={{ padding: "0 14px 12px", display: "flex", flexDirection: "column", gap: 6 }}>
                                    {rec.note && (
                                      <div style={{ fontFamily: FF, fontSize: 12, color: UI.textFnt, lineHeight: 1.5 }}>{rec.note}</div>
                                    )}
                                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                      <button onClick={() => openPlaceInfo(rec)}
                                        style={{ fontFamily: FF, fontSize: 11, color: color, background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex", alignItems: "center", gap: 4 }}>
                                        <svg width="11" height="11" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M6 1a3.5 3.5 0 0 1 3.5 3.5C9.5 7.5 6 11 6 11S2.5 7.5 2.5 4.5A3.5 3.5 0 0 1 6 1z"/><circle cx="6" cy="4.5" r="1.2"/></svg>
                                        View Details
                                      </button>
                                      <span style={{ fontFamily: FF, fontSize: 11, color: UI.textVFnt }}>
                                        {new Date(rec.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                                      </span>
                                      {(recSaveCounts[rec.id] || 0) > 0 && (
                                        <span style={{ fontFamily: FF, fontSize: 11, color: UI.textVFnt }}>· {recSaveCounts[rec.id]} saved</span>
                                      )}
                                      <button onClick={() => { removeCommunityRec(rec.id, rec.city, rec.state); setExpandedRec(null); }}
                                        style={{ marginLeft: "auto", background: "none", border: "none", color: UI.textVFnt, cursor: "pointer", fontSize: 11, padding: "2px 4px", borderRadius: 4, transition: "color 0.12s" }}
                                        onMouseEnter={e => e.currentTarget.style.color = "var(--danger)"}
                                        onMouseLeave={e => e.currentTarget.style.color = UI.textVFnt}>
                                        Remove
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              );
                            }

                            // ── Collapsed strip ──
                            const saveCount   = recSaveCounts[rec.id] || 0;
                            const isTopPick   = saveCount >= 2;
                            return (
                              <button key={rec.id} onClick={() => setExpandedRec(rec.id)}
                                style={{
                                  width: "100%", display: "flex", alignItems: "center", gap: 10,
                                  padding: "0 14px", height: 42, cursor: "pointer", textAlign: "left",
                                  background: isTopPick ? `${color}08` : UI.rowBg, border: "none",
                                  borderTop: notFirst ? `1px solid ${UI.panelBdr}` : "none",
                                  borderLeft: `4px solid ${color}`,
                                  transition: "background 0.12s",
                                }}
                                onMouseEnter={e => e.currentTarget.style.background = isTopPick ? `${color}14` : UI.bg}
                                onMouseLeave={e => e.currentTarget.style.background = isTopPick ? `${color}08` : UI.rowBg}>
                                <span style={{ fontFamily: FF, fontSize: 11, letterSpacing: 0.8, color: color, textTransform: "uppercase", flexShrink: 0, minWidth: 68 }}>{cat}</span>
                                <span style={{ fontFamily: FF, fontSize: 12, fontWeight: 600, color: UI.text, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{rec.name}</span>
                                {isTopPick && (
                                  <span style={{ fontFamily: FF, fontSize: 11, letterSpacing: 0.8, color: color, textTransform: "uppercase", flexShrink: 0, padding: "2px 5px", background: `${color}18`, borderRadius: 4, fontWeight: 700 }}>TOP PICK</span>
                                )}
                                {saveCount > 0 && (
                                  <span style={{ fontFamily: FF, fontSize: 11, color: UI.textVFnt, flexShrink: 0 }}>{saveCount} saved</span>
                                )}
                                <svg width="11" height="11" viewBox="0 0 12 12" fill="none" stroke={UI.textVFnt} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><path d="M4.5 2l4 4-4 4"/></svg>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}

                </div>
              </>
            );
          })()}

          {/* ACTIVITY LOG VIEW */}
          {view === "activity" && (() => {
            const FF = "'Helvetica Neue', Helvetica, Arial, sans-serif";
            const ACTION_CONFIG = {
              created: { label: "Created", color: "var(--success)",  bg: UI.successBg },
              updated: { label: "Updated", color: "var(--accent-2)", bg: UI.accent2Bg },
              deleted: { label: "Deleted", color: "var(--danger)",   bg: UI.dangerBg },
            };
            const ENTITY_ICONS = {
              show: "♪", tour: "○", artist: "◈", contact: "◇", venue: "▣",
            };
            const groupByDate = (entries) => {
              const groups = {};
              entries.forEach(e => {
                const day = e.created_at?.slice(0, 10) || "Unknown";
                if (!groups[day]) groups[day] = [];
                groups[day].push(e);
              });
              return Object.entries(groups).sort((a, b) => b[0].localeCompare(a[0]));
            };
            return (
              <>
                <div className="ph">
                  <div>
                    <div className="ph-title">Activity Log</div>
                    <div className="ph-sub">CHANGE HISTORY · LAST 200 ENTRIES</div>
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button className="add-p" onClick={loadActivityLog}>Refresh</button>
                  </div>
                </div>
                <div style={{ padding: "0 24px 40px", maxWidth: 720 }}>
                  {activityLogLoading && (
                    <div style={{ padding: "48px 0", textAlign: "center", fontFamily: FF, fontSize: 11, color: UI.textMut, letterSpacing: 2 }}>LOADING…</div>
                  )}
                  {!activityLogLoading && activityLog.length === 0 && (
                    <div style={{ padding: "64px 0", textAlign: "center" }}>
                      <div style={{ fontFamily: FF, fontSize: 14, color: UI.textMut, marginBottom: 8 }}>No activity recorded yet</div>
                      <div style={{ fontFamily: FF, fontSize: 11, color: UI.textVFnt }}>Changes to shows, tours, contacts, and venues will appear here.</div>
                    </div>
                  )}
                  {!activityLogLoading && groupByDate(activityLog).map(([day, entries]) => (
                    <div key={day} style={{ marginBottom: 24 }}>
                      <div style={{ fontFamily: FF, fontSize: 11, letterSpacing: 1.5, color: UI.textVFnt, textTransform: "uppercase", padding: "8px 0 6px", borderBottom: `1px solid ${UI.panelBdr}`, marginBottom: 6 }}>
                        {new Date(day + "T12:00:00").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}
                      </div>
                      {entries.map(e => {
                        const ac = ACTION_CONFIG[e.action] || ACTION_CONFIG.updated;
                        const icon = ENTITY_ICONS[e.entity_type] || "·";
                        const timeStr = e.created_at ? new Date(e.created_at).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }) : "";
                        return (
                          <div key={e.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "7px 10px", borderRadius: 4, background: UI.rowBg, border: `1px solid ${UI.panelBdr}`, marginBottom: 3 }}>
                            <span style={{ fontSize: 14, flexShrink: 0, opacity: 0.5 }}>{icon}</span>
                            <span style={{ fontFamily: FF, fontSize: 11, fontWeight: 600, color: UI.text, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{e.entity_label || e.entity_id || "–"}</span>
                            <span style={{ fontFamily: FF, fontSize: 11, letterSpacing: 1, color: ac.color, background: ac.bg, padding: "2px 7px", borderRadius: 4, flexShrink: 0, whiteSpace: "nowrap" }}>{ac.label.toUpperCase()}</span>
                            {e.entity_type && <span style={{ fontFamily: FF, fontSize: 11, color: UI.textVFnt, textTransform: "uppercase", letterSpacing: 0.5, flexShrink: 0 }}>{e.entity_type}</span>}
                            {e.actor_name && <span style={{ fontFamily: FF, fontSize: 11, color: UI.textMut, flexShrink: 0, whiteSpace: "nowrap" }}>{e.actor_name}</span>}
                            <span style={{ fontFamily: FF, fontSize: 11, color: UI.textVFnt, flexShrink: 0, whiteSpace: "nowrap" }}>{timeStr}</span>
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </>
            );
          })()}

          {/* TOUR VIEW */}
          {view === "tour" && tour && (
            <>
              <div style={{ padding: "26px 36px 18px", borderBottom: "1px solid #1a1a1a" }}>
                {/* Top row: back breadcrumb (left) + mode toggle + primary CTA (right) */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 12 }}>
                  {/* Back breadcrumb */}
                  <button onClick={() => { setSelT(null); setSelS(null); setView("artist"); }} style={{ background: "none", border: "none", cursor: "pointer", fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: UI.textMut, letterSpacing: "0.2px", padding: 0, display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M7.5 2L3.5 6l4 4"/></svg>
                    {artist.name}
                  </button>
                  {/* Right cluster: mode toggle + primary CTA */}
                  <div style={{ display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
                  {tour.shows.length > 0 && (
                    <div style={{ display: "flex", background: UI.rowBg, border: `1px solid ${UI.panelBdr}`, borderRadius: 8, padding: 2, gap: 2, flexShrink: 0 }}>
                      {[["advance","Advance"], ["tour","Tour"], ["financials","Financials"]].map(([m, label]) => {
                        const curMode = tour.advanceConfig?.mode || 'advance';
                        const isActive = curMode === m;
                        const modeColor = m === 'tour' ? UI.green : m === 'financials' ? '#3ecf6e' : UI.amber;
                        return (
                          <button key={m}
                            onClick={async () => {
                              if (curMode === m) return;
                              // Always update the mode directly — avoids the binary-flip bug in toggleTourMode
                              updTourAdvCfg(cfg => ({ ...cfg, mode: m }));
                              await supabase.from("tours").update({ advance_config: { ...(tour.advanceConfig || {}), mode: m } }).eq("id", selT);
                              if (m === 'tour') {
                                await ensureTourDays(tour);
                              } else if (curMode === 'tour') {
                                // Leaving tour mode — clean up day state
                                setTourDays([]); setSelectedDayId(null);
                                setDayFlights({}); setVehicles([]); setDayTransport({}); setTourDayFilter('all'); setPastDaysOpen(false);
                              }
                              if (m === 'financials') {
                                loadTourFinancials(selT);
                              }
                            }}
                            style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, letterSpacing: "0.5px", padding: "4px 12px", borderRadius: 4, border: "none", cursor: "pointer", transition: "all 0.15s",
                              background: isActive ? modeColor : "none",
                              color:      isActive ? "#0d0d0d" : UI.textMut,
                              fontWeight: isActive ? 700 : 400,
                            }}>
                            {label}
                          </button>
                        );
                      })}
                    </div>
                  )}
                  <button className="add-p" style={{ flexShrink: 0, visibility: (!isTourMode && !isFinMode) ? "visible" : "hidden" }} onClick={() => setMAddShows({})}>+ Add Shows</button>
                  </div>
                </div>
                {/* Tour name */}
                <div style={{ marginBottom: 8 }}>
                  <input className="tour-inp" style={{ width: "100%", minWidth: 0 }} value={tour.name} onChange={e => inlineTourName(e.target.value)} title="Click to rename" />
                </div>
                {/* Row 1b: secondary actions — Tour Health | Import Dates | Advance Links | Export | Delete Tour */}
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
                  {/* 1. Tour Health */}
                  {(() => { if (!tour.shows.length) return null;
                    const todayStr = new Date().toISOString().slice(0, 10);
                    const issueCount = tour.shows.filter(s => {
                      const v = venues[s.venueId];
                      const noEmail = !(s.settlement?.promoterEmail || v?.promoterEmail || "").trim();
                      const noToken = !s.advanceToken;
                      const depOvd  = s.settlement?.depositDue && !s.settlement?.depositReceived && s.settlement.depositDue < todayStr;
                      const notSett = s.date < todayStr && !s.settlement?.settled;
                      return noEmail || noToken || depOvd || notSett;
                    }).length;
                    return (
                      <button className="add-g" onClick={() => setMTourHealth(true)}
                        title="See a health overview of this tour — missing emails, advances, deposits, settlements"
                        >
                        <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 14, height: 14, border: "1px solid currentColor", borderRadius: 4, opacity: 0.5, marginRight: 6, verticalAlign: "middle", flexShrink: 0 }}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" style={{ width: 9, height: 9 }}><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                        </span>
                        {issueCount > 0 ? `Tour Health (${issueCount})` : "Tour Health"}
                      </button>
                    );
                  })()}
                  {/* 2. Import Dates — now part of Add Shows modal */}
                  {/* 3. Advance Links */}
                  {tour.shows.length > 0 && (() => {
                    const noToken = tour.shows.filter(s => !s.advanceToken).length;
                    const allLive = noToken === 0;
                    return (
                      <button className="add-g"
                        onClick={() => setMAdvLinks(true)}
                        title="View and manage portal links for all shows"
                        >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ width: 11, height: 11, marginRight: 5, verticalAlign: "middle" }}><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
                        Advance Links
                      </button>
                    );
                  })()}
                  {/* 4. Export dropdown */}
                  <div style={{ position: "relative" }} ref={exportMenuRef}>
                    <button className="add-g" onClick={() => setShowExportMenu(v => !v)} title="Export or copy routing">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ width: 11, height: 11, marginRight: 5, verticalAlign: "middle" }}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                      Export ▾
                    </button>
                    {showExportMenu && (
                      <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, zIndex: 120, background: UI.panelBg, border: `1px solid ${UI.panelBdr}`, borderRadius: 8, padding: "4px 0", minWidth: 260, boxShadow: "0 4px 16px rgba(0,0,0,0.4)" }}>
                        <button
                          style={{ display: "block", width: "100%", textAlign: "left", background: "none", border: "none", padding: "7px 14px", fontFamily: "'Helvetica Neue',Helvetica,Arial,sans-serif", fontSize: 11, color: UI.text, cursor: "pointer", letterSpacing: "0.3px" }}
                          onMouseEnter={e => e.currentTarget.style.background = UI.rowBg}
                          onMouseLeave={e => e.currentTarget.style.background = "none"}
                          onClick={() => { exportRouting(artist, tour); setShowExportMenu(false); }}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ width: 11, height: 11, marginRight: 7, verticalAlign: "middle" }}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/></svg>
                          PDF Routing Sheet
                        </button>
                        <button
                          style={{ display: "block", width: "100%", textAlign: "left", background: "none", border: "none", padding: "7px 14px", fontFamily: "'Helvetica Neue',Helvetica,Arial,sans-serif", fontSize: 11, color: UI.text, cursor: "pointer", letterSpacing: "0.3px" }}
                          onMouseEnter={e => e.currentTarget.style.background = UI.rowBg}
                          onMouseLeave={e => e.currentTarget.style.background = "none"}
                          onClick={() => { exportRoutingDocx(artist, tour); setShowExportMenu(false); }}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ width: 11, height: 11, marginRight: 7, verticalAlign: "middle" }}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/></svg>
                          Word Doc Routing Sheet
                        </button>
                        <button
                          style={{ display: "block", width: "100%", textAlign: "left", background: "none", border: "none", padding: "7px 14px", fontFamily: "'Helvetica Neue',Helvetica,Arial,sans-serif", fontSize: 11, color: UI.text, cursor: "pointer", letterSpacing: "0.3px" }}
                          onMouseEnter={e => e.currentTarget.style.background = UI.rowBg}
                          onMouseLeave={e => e.currentTarget.style.background = "none"}
                          onClick={() => { copyRoutingText(artist, tour); setShowExportMenu(false); }}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ width: 11, height: 11, marginRight: 7, verticalAlign: "middle" }}><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
                          Copy Routing Sheet as Plain Text
                        </button>
                        {isTourMode && (
                          <button
                            style={{ display: "block", width: "100%", textAlign: "left", background: "none", border: "none", padding: "7px 14px", fontFamily: "'Helvetica Neue',Helvetica,Arial,sans-serif", fontSize: 11, color: UI.text, cursor: "pointer", letterSpacing: "0.3px", borderTop: `1px solid ${UI.rowBg2}` }}
                            onMouseEnter={e => e.currentTarget.style.background = UI.rowBg}
                            onMouseLeave={e => e.currentTarget.style.background = "none"}
                            onClick={() => { setMItineraryExport(true); setShowExportMenu(false); }}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ width: 11, height: 11, marginRight: 7, verticalAlign: "middle" }}><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></svg>
                            Export Itinerary
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  {/* 5. More — overflow menu for rare / destructive actions */}
                  <div style={{ position: "relative" }} ref={tourMoreMenuRef}>
                    <button className="add-g" onClick={() => setTourMoreOpen(v => !v)} title="More actions" aria-label="More actions" style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", padding: "7px 9px" }}>
                      <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: 14, height: 14, display: "block" }}><circle cx="12" cy="5" r="1.6"/><circle cx="12" cy="12" r="1.6"/><circle cx="12" cy="19" r="1.6"/></svg>
                    </button>
                    {tourMoreOpen && (
                      <div style={{ position: "absolute", top: "calc(100% + 4px)", right: 0, zIndex: 120, background: UI.panelBg, border: `1px solid ${UI.panelBdr}`, borderRadius: 8, padding: "4px 0", minWidth: 180, boxShadow: "0 4px 16px rgba(0,0,0,0.4)" }}>
                        {isTourMode && (
                          <button
                            style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", textAlign: "left", background: "none", border: "none", padding: "8px 14px", fontFamily: "'Helvetica Neue',Helvetica,Arial,sans-serif", fontSize: 11, color: UI.text, cursor: "pointer", letterSpacing: "0.3px", borderBottom: `1px solid ${UI.rowBg2}` }}
                            onMouseEnter={e => e.currentTarget.style.background = UI.rowBg}
                            onMouseLeave={e => e.currentTarget.style.background = "none"}
                            onClick={() => { setMItinerary(true); setTourMoreOpen(false); }}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" style={{ width: 13, height: 13, flexShrink: 0 }}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                            Import Itinerary
                          </button>
                        )}
                        {confirmDelTour ? (
                          <button
                            style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", textAlign: "left", background: "none", border: "none", padding: "8px 14px", fontFamily: "'Helvetica Neue',Helvetica,Arial,sans-serif", fontSize: 11, color: "var(--danger)", fontWeight: 600, cursor: "pointer", letterSpacing: "0.3px" }}
                            onMouseEnter={e => e.currentTarget.style.background = UI.rowBg}
                            onMouseLeave={e => e.currentTarget.style.background = "none"}
                            onClick={() => { setConfirmDelTour(false); setTourMoreOpen(false); deleteTour(); }}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" style={{ width: 13, height: 13, flexShrink: 0 }}><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                            Confirm — delete tour
                          </button>
                        ) : (
                          <button
                            style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", textAlign: "left", background: "none", border: "none", padding: "8px 14px", fontFamily: "'Helvetica Neue',Helvetica,Arial,sans-serif", fontSize: 11, color: "var(--danger)", cursor: "pointer", letterSpacing: "0.3px" }}
                            onMouseEnter={e => e.currentTarget.style.background = UI.rowBg}
                            onMouseLeave={e => e.currentTarget.style.background = "none"}
                            onClick={() => { setConfirmDelTour(true); setTimeout(() => setConfirmDelTour(false), 4000); }}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" style={{ width: 13, height: 13, flexShrink: 0 }}><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                            Delete tour
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                {/* Row 2: show count · date range (filters + progress live with the show list below) */}
                <div className="ph-sub" style={{ margin: 0, whiteSpace: "nowrap" }}>
                  {tour.shows.length} SHOW{tour.shows.length !== 1 ? "S" : ""}{tour.shows.length > 0 ? ` · ${tourDateRange(tour)}` : ""}
                </div>

                {/* Next Up callout — shows within 14 days */}
                {(() => {
                  const todayStr = new Date().toISOString().slice(0, 10);
                  const next = [...tour.shows].sort((a, b) => a.date.localeCompare(b.date)).find(s => s.date >= todayStr);
                  if (!next) return null;
                  const dUntil = Math.ceil((new Date(next.date + "T12:00:00") - new Date()) / 864e5);
                  if (dUntil > 14) return null;
                  const nv = venues[next.venueId];
                  const urgent = dUntil <= 2;
                  return (
                    <div style={{ marginTop: 10, padding: "9px 14px", background: urgent ? UI.amdBg : UI.ckOnBg, border: `1px solid ${urgent ? UI.amdBdr : UI.ckOnBdr}`, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer" }}
                      onClick={() => { setSelS(next.id); setDetailTab("advance"); }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <span style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, letterSpacing: 1.5, color: urgent ? UI.amber : UI.green }}>
                          {dUntil === 0 ? "TODAY" : dUntil === 1 ? "TOMORROW" : `IN ${dUntil} DAYS`}
                        </span>
                        <span style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 14, fontWeight: 600, color: UI.text }}>{nv?.name || "?"}</span>
                        {nv?.city && <span style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: UI.textMut }}>{nv.city}</span>}
                      </div>
                      <span style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: UI.textFnt }}>Open →</span>
                    </div>
                  );
                })()}

              </div>

              {/* ── Tour Setup strip (A + C) ─────────────────────────────── */}
              {(() => {
                const crewCount   = profiles.filter(p => p.current_tour_id === selT).length;
                const fileCount   = tour?.advanceConfig?.files?.length || 0;
                const hasCustomSecs = tourSectionIds.length < ALL_SECTION_IDS.length || hasFieldCustom;
                const hasDietary  = !!(tour?.advanceConfig?.dietaryNotes);
                const hasVehicle  = !!(tour?.advanceConfig?.vehicleNotes);
                const configured  = crewCount > 0 || fileCount > 0 || hasCustomSecs || hasDietary || hasVehicle;
                const panelOpen   = !!cats["portal_secs_cfg"];
                const open = () => { if (!panelOpen) togCat("portal_secs_cfg"); setTourSetupTab("sections"); window.scrollTo({ top: 0, behavior: "smooth" }); };

                if (!configured) {
                  // C: unconfigured — same single-line strip footprint as the configured Tour Setup
                  // strip and the Tour Shows strip; amber-tinted to signal "needs setup". Click to open.
                  return (
                    <div onClick={open} style={{ margin: 0, padding: "7px 36px", background: lightMode ? "#fff8e8" : "#140f00", borderBottom: `1px solid ${lightMode ? "#e8cc80" : "#3a2800"}`, display: "flex", alignItems: "center", gap: 8, cursor: "pointer", userSelect: "none" }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={UI.amber} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
                      <span style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: UI.amber, letterSpacing: 1, textTransform: "uppercase", flexShrink: 0 }}>Tour Setup</span>
                      <span style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: lightMode ? "#8a6600" : "#aa8800", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>Step 1 — add crew, sections, and files for every show on this tour</span>
                      <span style={{ marginLeft: "auto", flexShrink: 0, fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: UI.amber, fontWeight: 600 }}>Configure →</span>
                    </div>
                  );
                }

                // A: configured summary strip
                const summaryParts = [
                  hasCustomSecs ? `${tourSectionIds.length}/${ALL_SECTION_IDS.length} sections` : `${ALL_SECTION_IDS.length} sections`,
                  ...(crewCount > 0 ? [`${crewCount} crew`] : []),
                  ...(fileCount > 0 ? [`${fileCount} file${fileCount !== 1 ? "s" : ""}`] : []),
                  ...((hasDietary || hasVehicle) ? ["defaults set"] : []),
                ];
                return (
                  <div onClick={() => togCat("portal_secs_cfg")} style={{ margin: "0", padding: "7px 36px", background: lightMode ? "#eae6e0" : "#0a0a0a", borderBottom: `1px solid ${lightMode ? "#d0ccc6" : "#161616"}`, display: "flex", alignItems: "center", gap: 8, cursor: "pointer", userSelect: "none" }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={UI.amber} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
                    <span style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: UI.amber, letterSpacing: 1, textTransform: "uppercase", flexShrink: 0 }}>Tour Setup</span>
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={UI.green} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><path d="M20 6 9 17l-5-5"/></svg>
                    <span style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: UI.textMut, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{summaryParts.join(" · ")}</span>
                    <span style={{ marginLeft: "auto", flexShrink: 0, fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: panelOpen ? UI.amber : UI.textFnt }}>
                      {panelOpen ? "▾ Close" : "Configure →"}
                    </span>
                  </div>
                );
              })()}

                {/* Row 3: Tour Setup (collapsible, tabbed) */}
                {cats["portal_secs_cfg"] && (
                  <div style={{ margin: "14px 36px 18px", background: UI.panelBg, border: `1px solid ${UI.panelBdr}`, borderRadius: 8, overflow: "hidden" }}>
                    {/* Tab bar */}
                    <div style={{ display: "flex", borderBottom: `1px solid ${UI.hdrBdr}` }}>
                      {[
                        { id: "sections", label: "Sections & Fields" },
                        { id: "roster",   label: "Crew Roster" },
                        { id: "files",    label: "Tour Files" },
                        { id: "vehicles", label: "Vehicles" },
                      ].map(tab => (
                        <button key={tab.id} onClick={() => { setTourSetupTab(tab.id); if (tab.id === "vehicles") loadVehicles(selT); }} style={{
                          flex: 1, padding: "10px 14px", background: tourSetupTab === tab.id ? UI.rowBg : "none",
                          border: "none", borderRight: `1px solid ${UI.hdrBdr}`,
                          fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, letterSpacing: 0.8, textTransform: "uppercase",
                          color: tourSetupTab === tab.id ? UI.amber : UI.textFnt, cursor: "pointer",
                          borderBottom: tourSetupTab === tab.id ? `2px solid ${UI.amber}` : "none",
                        }}>
                          {tab.label}
                          {tab.id === "roster" && (() => { const n = profiles.filter(p => p.current_tour_id === selT).length; return n > 0 ? <span style={{ marginLeft: 6, color: UI.green }}>✦ {n}</span> : null; })()}
                          {tab.id === "files" && (tour?.advanceConfig?.files?.length > 0) && (
                            <span style={{ marginLeft: 6, color: UI.green }}>✦ {tour.advanceConfig.files.length}</span>
                          )}
                          {tab.id === "vehicles" && vehicles.length > 0 && (
                            <span style={{ marginLeft: 6, color: UI.green }}>✦ {vehicles.length}</span>
                          )}
                        </button>
                      ))}
                      {/* TT-1: Save as Template button */}
                      {savingTourTpl ? (
                        <div style={{ display: "flex", alignItems: "center", gap: 5, padding: "4px 8px", flexShrink: 0 }}>
                          <input
                            autoFocus
                            placeholder="Template name…"
                            value={newTourTplName}
                            onChange={e => setNewTourTplName(e.target.value)}
                            onKeyDown={async e => {
                              if (e.key === "Enter" && newTourTplName.trim()) {
                                await saveTourTemplate(newTourTplName);
                                setNewTourTplName(""); setSavingTourTpl(false);
                              }
                              if (e.key === "Escape") { setNewTourTplName(""); setSavingTourTpl(false); }
                            }}
                            style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: UI.text, background: UI.rowBg, border: `1px solid ${UI.amber}`, borderRadius: 4, padding: "4px 8px", width: 140, outline: "none" }}
                          />
                          <button
                            onClick={async () => { if (newTourTplName.trim()) { await saveTourTemplate(newTourTplName); setNewTourTplName(""); setSavingTourTpl(false); } }}
                            style={{ background: "none", border: `1px solid ${UI.amber}`, borderRadius: 4, color: UI.amber, fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, padding: "4px 8px", cursor: "pointer" }}>
                            Save
                          </button>
                          <button
                            onClick={() => { setNewTourTplName(""); setSavingTourTpl(false); }}
                            style={{ background: "none", border: `1px solid ${UI.panelBdr}`, borderRadius: 4, color: UI.textMut, fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, padding: "4px 8px", cursor: "pointer" }}>
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setSavingTourTpl(true)}
                          title="Save this tour's portal config and roster as a reusable template"
                          style={{ padding: "10px 12px", background: "none", border: "none", fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: UI.textMut, cursor: "pointer", flexShrink: 0, letterSpacing: 0.3 }}>
                          ⊞ Template
                        </button>
                      )}
                      <button onClick={() => togCat("portal_secs_cfg")} style={{
                        padding: "10px 16px", background: "none", border: "none",
                        fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: UI.textFnt,
                        cursor: "pointer", flexShrink: 0,
                      }} title="Close tour setup">✕</button>
                    </div>

                    {/* TT-1: Saved templates row — shown across all tabs */}
                    {tourTemplates.length > 0 && !savingTourTpl && (
                      <div style={{ padding: "6px 16px", borderBottom: `1px solid ${UI.rowBg2}`, display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                        <span style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: UI.textVFnt, letterSpacing: 0.5, marginRight: 2 }}>TEMPLATES</span>
                        {tourTemplates.map(t => (
                          <div key={t.id} style={{ display: "inline-flex", alignItems: "center", gap: 4, background: UI.rowBg2, border: `1px solid ${UI.panelBdr}`, borderRadius: 4, padding: "2px 6px 2px 8px", fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: UI.textMut }}>
                            <span title={`${t.roster_profile_ids?.length || 0} roster member${t.roster_profile_ids?.length !== 1 ? "s" : ""} · saved ${t.created_at?.slice(0, 10) || ""}`}>{t.name}</span>
                            <button onClick={() => deleteTourTemplate(t.id)} style={{ background: "none", border: "none", color: UI.textFnt, cursor: "pointer", fontSize: 11, lineHeight: 1, padding: "0 1px", opacity: 0.5 }} title="Delete template">✕</button>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* TAB: Sections & Fields */}
                    {tourSetupTab === "sections" && (<>
                    {(() => {
                      const tplCtrl = { background: "none", border: `1px solid ${UI.panelBdr}`, borderRadius: 4, fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: UI.textMut, padding: "4px 9px", cursor: "pointer", outline: "none", lineHeight: 1.4 };
                      return (
                      <div style={{ padding: "9px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, borderBottom: `1px solid ${UI.rowBg2}` }}>
                        <span style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: UI.textVFnt, letterSpacing: 0.5, flex: 1 }}>
                          Uncheck sections to hide from promoters. Expand a section to toggle individual fields.
                        </span>
                        <div style={{ display: "flex", gap: 6, alignItems: "center", flexShrink: 0 }}>
                          {advTemplates.length > 0 && !savingTpl && (
                            tplLoaded
                              ? <span style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: UI.green, letterSpacing: "0.5px" }}>✓ Template loaded</span>
                              : <select style={{ ...tplCtrl, appearance: "none" }}
                                  defaultValue=""
                                  onChange={e => { if (e.target.value) { loadAdvTemplate(e.target.value); e.target.value = ""; } }}>
                                  <option value="" disabled>Load template…</option>
                                  {advTemplates.map(t => (
                                    <option key={t.id} value={t.id}>{t.name}</option>
                                  ))}
                                </select>
                          )}
                          {savingTpl ? (
                            <div style={{ display: "flex", gap: 5, alignItems: "center" }}>
                              <input autoFocus style={{ ...tplCtrl, width: 140, cursor: "text" }}
                                placeholder="Template name…"
                                value={newTplName}
                                onChange={e => setNewTplName(e.target.value)}
                                onKeyDown={e => {
                                  if (e.key === "Enter" && newTplName.trim()) { saveAdvTemplate(newTplName); setNewTplName(""); setSavingTpl(false); }
                                  if (e.key === "Escape") { setNewTplName(""); setSavingTpl(false); }
                                }} />
                              <button style={{ ...tplCtrl, color: UI.amber, borderColor: UI.amber }}
                                onClick={() => { if (newTplName.trim()) { saveAdvTemplate(newTplName); setNewTplName(""); setSavingTpl(false); } }}>Save</button>
                              <button style={tplCtrl} onClick={() => { setNewTplName(""); setSavingTpl(false); }}>Cancel</button>
                            </div>
                          ) : (
                            <button style={tplCtrl} onClick={() => setSavingTpl(true)}>Save as template</button>
                          )}
                          {(tourSectionIds.length < ALL_SECTION_IDS.length || hasFieldCustom) && !savingTpl && (
                            <button style={tplCtrl} onClick={() =>
                              updTourAdvCfg(() => ({ sections: ALL_SECTION_IDS, fields: {} }))
                            }>Reset</button>
                          )}
                        </div>
                      </div>
                      );
                    })()}
                    {advTemplates.length > 0 && !savingTpl && (
                      <div style={{ padding: "6px 16px", borderBottom: `1px solid ${UI.rowBg2}`, display: "flex", gap: 6, flexWrap: "wrap" }}>
                        {advTemplates.map(t => (
                          <div key={t.id} style={{ display: "inline-flex", alignItems: "center", gap: 4, background: UI.rowBg2, border: `1px solid ${UI.panelBdr}`, borderRadius: 4, padding: "2px 6px 2px 8px", fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: UI.textMut }}>
                            <span>{t.name}</span>
                            <button onClick={() => deleteAdvTemplate(t.id)} style={{ background: "none", border: "none", color: UI.textFnt, cursor: "pointer", fontSize: 11, lineHeight: 1, padding: "0 1px" }} title="Delete template">✕</button>
                          </div>
                        ))}
                      </div>
                    )}

                    {ADVANCE_PORTAL_SECTIONS.map(sec => {
                      const secOn = tourSectionIds.includes(sec.id);
                      const enabledFlds = tourEnabledFields(sec.id);
                      // tmOnly fields are TM-internal — exclude from portal field toggles and counts
                      const allFldIds = sec.fields.filter(f => !f.tmOnly).map(f => f.id);
                      const fieldsCustom = enabledFlds.length < allFldIds.length;
                      const fieldsOpen = cats["psc_fld_" + sec.id];
                      return (
                        <div key={sec.id} style={{ borderBottom: `1px solid ${UI.rowBg2}` }}>
                          {/* Section row */}
                          <div style={{ display: "grid", gridTemplateColumns: "32px 1fr 88px 24px", alignItems: "center", padding: "8px 16px", gap: 0, cursor: "pointer" }}
                            onClick={() => togCat("psc_fld_" + sec.id)}>
                            <input type="checkbox" style={{ accentColor: UI.amber, width: 13, height: 13, cursor: "pointer", justifySelf: "start" }}
                              checked={secOn}
                              onClick={e => e.stopPropagation()}
                              onChange={e => updTourAdvanceSections(sec.id, e.target.checked)} />
                            <span style={{ fontSize: 12, color: secOn ? UI.textMid : UI.textFnt, fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", letterSpacing: 0.3 }}>
                              {sec.label}
                            </span>
                            <span style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: UI.amber, textAlign: "right", paddingRight: 10 }}>
                              {fieldsCustom ? `${enabledFlds.length}/${allFldIds.length} fields` : ""}
                            </span>
                            <span style={{ fontSize: 11, color: fieldsOpen ? UI.amber : UI.textFnt, transition: "transform 0.18s", display: "inline-block", transform: fieldsOpen ? "rotate(90deg)" : "none", justifySelf: "end" }}>▶</span>
                          </div>

                          {/* Tour-level Run of Show pre-fill — schedule section only */}
                          {fieldsOpen && sec.id === "schedule" && (() => {
                            const FF4 = "'Helvetica Neue', Helvetica, Arial, sans-serif";
                            const dSched = tour?.advanceConfig?.defaultSchedule || {};
                            const defaultActs = [
                              { id: "def_a0", name: "1st of 3",         load_in: "", soundcheck: "", set_time: "", changeover: "" },
                              { id: "def_a1", name: "Direct Support",    load_in: "", soundcheck: "", set_time: "", changeover: "" },
                              { id: "def_a2", name: artist?.name || "Headliner", load_in: "", soundcheck: "", set_time: "" },
                            ];
                            const acts = dSched.acts && dSched.acts.length > 0 ? dSched.acts : defaultActs;
                            const headliner = acts[acts.length - 1];
                            const supports  = acts.slice(0, acts.length - 1); // opener to direct support, in playing order

                            const updSched = (patch) => updTourAdvCfg(cfg => ({
                              ...cfg,
                              defaultSchedule: { ...(cfg.defaultSchedule || {}), acts, ...patch }
                            }));
                            const updAct = (actId, field, val) => updTourAdvCfg(cfg => {
                              const sched = cfg.defaultSchedule || {};
                              const currentActs = sched.acts && sched.acts.length > 0 ? sched.acts : defaultActs;
                              return {
                                ...cfg,
                                defaultSchedule: {
                                  ...sched,
                                  acts: currentActs.map(a => a.id === actId ? { ...a, [field]: val } : a)
                                }
                              };
                            });
                            const addAct = () => updTourAdvCfg(cfg => {
                              const sched = cfg.defaultSchedule || {};
                              const currentActs = sched.acts && sched.acts.length > 0 ? sched.acts : defaultActs;
                              const newAct = { id: "a" + Date.now().toString(36), name: "Support Act", load_in: "", soundcheck: "", set_time: "", changeover: "" };
                              const withNew = [...currentActs.slice(0, currentActs.length - 1), newAct, currentActs[currentActs.length - 1]];
                              return { ...cfg, defaultSchedule: { ...sched, acts: withNew } };
                            });
                            const removeAct = (actId) => updTourAdvCfg(cfg => {
                              const sched = cfg.defaultSchedule || {};
                              const currentActs = (sched.acts || defaultActs).filter(a => a.id !== actId);
                              return { ...cfg, defaultSchedule: { ...sched, acts: currentActs } };
                            });

                            // TimeInput12 (AM/PM toggle, canonical "H:MM AM/PM"). Replaces the old
                            // native <input type="time">, which emitted 24h "HH:MM" strings — the
                            // source of military-format times leaking into every show's pre-filled
                            // bill. Legacy 24h values here are unambiguous and convert cleanly.
                            const timeInp = (val, onChange) => (
                              <TimeInput12 value={val || ""} onChange={onChange}
                                className="f-inp"
                                inputStyle={{ fontSize: 11, padding: "4px 6px" }}
                                toggleStyle={{ fontFamily: FF4, background: "var(--panel-alt)", color: "var(--text-mut)", border: "1px solid var(--field-border, var(--panel-border))", borderRadius: "0 6px 6px 0" }}
                              />
                            );

                            const rosRow = (label, inp, sub) => (
                              <div style={{ display: "grid", gridTemplateColumns: "200px 120px 1fr", alignItems: "center", gap: 8, padding: "4px 0", borderBottom: `1px solid ${UI.rowBg2}` }}>
                                <span style={{ fontFamily: FF4, fontSize: 11, color: UI.textMid }}>{label}</span>
                                <div>{inp}</div>
                                {sub && <span style={{ fontFamily: FF4, fontSize: 11, color: UI.textVFnt }}>{sub}</span>}
                              </div>
                            );

                            return (
                              <div style={{ margin: "0 0 0 0", borderTop: `1px solid ${UI.rowBg2}`, borderBottom: `1px solid ${UI.rowBg2}` }}>
                                {/* Header */}
                                <div style={{ padding: "10px 16px 6px 38px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                  <div>
                                    <span style={{ fontFamily: FF4, fontSize: 11, color: UI.amber, letterSpacing: 1, textTransform: "uppercase" }}>Tour Default Run of Show</span>
                                    <span style={{ fontFamily: FF4, fontSize: 11, color: UI.textVFnt, marginLeft: 10 }}>Pre-fills every show's advance — override per-show in each advance card</span>
                                  </div>
                                  <button className="sm-btn" onClick={addAct}>+ Add support act</button>
                                </div>

                                <div style={{ padding: "4px 16px 10px 38px" }}>
                                  {/* LOAD-IN PHASE */}
                                  <div style={{ fontFamily: FF4, fontSize: 11, color: UI.textFnt, letterSpacing: 1.5, textTransform: "uppercase", padding: "8px 0 4px" }}>Load-in &amp; Soundcheck</div>
                                  {rosRow("Venue access", timeInp(dSched.venue_access, v => updSched({ venue_access: v })))}
                                  {/* Headliner loads in first */}
                                  {rosRow(
                                    `${headliner.name} load-in`,
                                    timeInp(headliner.load_in, v => updAct(headliner.id, "load_in", v)),
                                    "Headliner"
                                  )}
                                  {rosRow(
                                    `${headliner.name} soundcheck`,
                                    timeInp(headliner.soundcheck, v => updAct(headliner.id, "soundcheck", v)),
                                    "Headliner"
                                  )}
                                  {/* Supports in reverse playing order for load-in (direct support → opener) */}
                                  {[...supports].reverse().map(act => (
                                    <div key={act.id}>
                                      {rosRow(
                                        `${act.name} load-in`,
                                        timeInp(act.load_in, v => updAct(act.id, "load_in", v)),
                                        <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                          {act.name !== "1st of 3" && act.name !== "Direct Support" && (
                                            <button onClick={() => removeAct(act.id)} style={{ background: "none", border: "none", color: UI.textFnt, cursor: "pointer", fontSize: 11, padding: 0 }} title="Remove act">✕</button>
                                          )}
                                          <input className="f-inp" style={{ fontSize: 11, padding: "2px 6px", width: 120 }}
                                            placeholder="Act name…"
                                            value={act.name}
                                            onChange={e => updAct(act.id, "name", e.target.value)} />
                                        </span>
                                      )}
                                      {rosRow(
                                        `${act.name} soundcheck`,
                                        timeInp(act.soundcheck, v => updAct(act.id, "soundcheck", v))
                                      )}
                                    </div>
                                  ))}

                                  {/* DOORS PHASE */}
                                  <div style={{ fontFamily: FF4, fontSize: 11, color: UI.textFnt, letterSpacing: 1.5, textTransform: "uppercase", padding: "10px 0 4px" }}>Doors</div>
                                  {rosRow("VIP doors", timeInp(dSched.vip_doors, v => updSched({ vip_doors: v })))}
                                  {rosRow("Doors (public)", timeInp(dSched.doors, v => updSched({ doors: v })))}

                                  {/* SET TIMES — playing order (opener first, headliner last) */}
                                  <div style={{ fontFamily: FF4, fontSize: 11, color: UI.textFnt, letterSpacing: 1.5, textTransform: "uppercase", padding: "10px 0 4px" }}>Set Times</div>
                                  {supports.map((act, idx) => (
                                    <div key={act.id}>
                                      {rosRow(
                                        `${act.name} set time`,
                                        timeInp(act.set_time, v => updAct(act.id, "set_time", v))
                                      )}
                                      {rosRow(
                                        "Changeover",
                                        timeInp(act.changeover, v => updAct(act.id, "changeover", v)),
                                        `After ${act.name}`
                                      )}
                                    </div>
                                  ))}
                                  {rosRow(
                                    `${headliner.name} set time`,
                                    timeInp(headliner.set_time, v => updAct(headliner.id, "set_time", v)),
                                    "Headliner"
                                  )}

                                  {/* END OF NIGHT */}
                                  <div style={{ fontFamily: FF4, fontSize: 11, color: UI.textFnt, letterSpacing: 1.5, textTransform: "uppercase", padding: "10px 0 4px" }}>End of Night</div>
                                  {rosRow("Load-out", timeInp(dSched.load_out, v => updSched({ load_out: v })))}
                                  {rosRow("Noise curfew", timeInp(dSched.noise_curfew, v => updSched({ noise_curfew: v })))}
                                  {rosRow("Green room curfew", timeInp(dSched.gr_curfew, v => updSched({ gr_curfew: v })))}
                                  {rosRow("Hard curfew", timeInp(dSched.hard_curfew, v => updSched({ hard_curfew: v })))}
                                </div>
                              </div>
                            );
                          })()}

                          {/* Field-level toggles — hidden for schedule since the ROS editor above covers all its fields */}
                          {fieldsOpen && sec.id !== "schedule" && (
                            <div style={{ padding: "6px 16px 12px 38px", display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))", gap: 5 }}>
                              {sec.fields.filter(f => !f.tmOnly).map(f => {
                                const fOn = enabledFlds.includes(f.id);
                                return (
                                  <label key={f.id} style={{ display: "flex", alignItems: "center", gap: 6, cursor: secOn ? "pointer" : "default", padding: "5px 8px", background: secOn && fOn ? UI.ckOnBg : UI.rowBg, border: `1px solid ${secOn && fOn ? UI.ckOnBdr : UI.subBdr}`, borderRadius: 4, opacity: secOn ? 1 : 0.45 }}>
                                    <input type="checkbox" style={{ accentColor: UI.amber, width: 12, height: 12, flexShrink: 0 }}
                                      checked={fOn}
                                      disabled={!secOn}
                                      onChange={e => updTourAdvanceField(sec.id, f.id, e.target.checked)} />
                                    <span style={{ fontSize: 11, color: secOn && fOn ? UI.textMid : UI.textFnt, fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif" }}>{f.label}</span>
                                  </label>
                                );
                              })}
                              {fieldsCustom && (
                                <button className="sm-btn" style={{ fontSize: 11 }}
                                  onClick={() => updTourAdvCfg(cfg => {
                                    const fields = { ...(cfg.fields || {}) };
                                    delete fields[sec.id];
                                    return { ...cfg, fields };
                                  })}>All fields on</button>
                              )}
                            </div>
                          )}

                          {/* Tour-level dietary notes — only for hospitality section */}
                          {fieldsOpen && sec.id === "hospitality" && (
                            <div style={{ padding: "0 16px 14px 38px" }}>
                              <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: UI.textMut, letterSpacing: 1, marginBottom: 6, textTransform: "uppercase" }}>Tour Dietary Notes / Allergies</div>
                              <textarea className="f-inp" rows={2} style={{ resize: "vertical", width: "100%", fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 12 }}
                                placeholder="e.g. 2 vegans, 1 nut allergy, no shellfish…"
                                value={tour?.advanceConfig?.dietaryNotes || ""}
                                onChange={e => updTourAdvCfg(cfg => ({ ...cfg, dietaryNotes: e.target.value }))} />
                              <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: UI.textVFnt, marginTop: 4, lineHeight: 1.5 }}>
                                Applies to every show on this tour — pre-fills the dietary field in each portal. TM can override per-show in the advance card.
                              </div>
                            </div>
                          )}

                          {/* Tour-level vehicle info — only for parking section */}
                          {fieldsOpen && sec.id === "parking" && (
                            <div style={{ padding: "0 16px 14px 38px" }}>
                              <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: UI.textMut, letterSpacing: 1, marginBottom: 6, textTransform: "uppercase" }}>Touring Vehicle / Bus Info</div>
                              <textarea className="f-inp" rows={2} style={{ resize: "vertical", width: "100%", fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 12 }}
                                placeholder="e.g. 2016 Sprinter van, black, plates TX-1234 · 48ft bus + 28ft trailer"
                                value={tour?.advanceConfig?.vehicleNotes || ""}
                                onChange={e => updTourAdvCfg(cfg => ({ ...cfg, vehicleNotes: e.target.value }))} />
                              <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: UI.textVFnt, marginTop: 4, lineHeight: 1.5 }}>
                                Pre-fills the "Touring vehicle / bus" field on every show's advance. Helps promoters prepare parking logistics.
                              </div>
                            </div>
                          )}

                          {/* Section notes — TM notes visible to promoter in this section */}
                          {fieldsOpen && (
                            <div style={{ padding: "4px 16px 14px 38px" }}>
                              <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: UI.textMut, letterSpacing: 1, marginBottom: 6, textTransform: "uppercase" }}>
                                TM Notes for promoter — {sec.label}
                              </div>
                              <textarea className="f-inp" rows={2} style={{ resize: "vertical", width: "100%", fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 12 }}
                                placeholder={`Any notes you want the promoter to see in the ${sec.label} section…`}
                                value={tour?.advanceConfig?.sectionNotes?.[sec.id] || ""}
                                onChange={e => {
                                  const val = e.target.value;
                                  updTourAdvCfg(cfg => ({
                                    ...cfg,
                                    sectionNotes: { ...(cfg.sectionNotes || {}), [sec.id]: val }
                                  }));
                                }} />
                              <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: UI.textVFnt, marginTop: 4, lineHeight: 1.5 }}>
                                Appears as a note to the promoter within this section of the advance portal.
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}

                    <div style={{ padding: "8px 16px", fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: UI.textVFnt, lineHeight: 1.6 }}>
                      Changes apply to all shows on this tour and save automatically.
                    </div>
                    </>)}

                    {/* TAB: Crew Roster — PP-1 Phase 1: people_profiles as single source of truth */}
                    {tourSetupTab === "roster" && (() => {
                      const FF4 = "'Helvetica Neue', Helvetica, Arial, sans-serif";
                      // Profiles currently on this tour
                      const onTour = profiles.filter(p => p.current_tour_id === selT);
                      // Artist roster profiles NOT yet on this tour (quick-add chips)
                      const rosterNotOnTour = profiles.filter(p => p.artist_id === artist?.id && p.current_tour_id !== selT);
                      const groupColor = { band: "var(--accent)", crew: "#6a8faf", tm: "var(--success)", other: UI.textFnt };

                      return (
                        <div>
                          <div style={{ padding: "10px 16px 6px", fontFamily: FF4, fontSize: 11, color: UI.textVFnt, letterSpacing: 0.5, borderBottom: `1px solid ${UI.rowBg2}` }}>
                            Add people from the artist roster or create new profiles. Everyone added here will appear as the touring party on advance portals.
                          </div>

                          {/* ── People currently on this tour ── */}
                          {onTour.length > 0 ? (
                            <div style={{ borderBottom: `1px solid ${UI.rowBg2}` }}>
                              <div style={{ padding: "8px 16px 4px", fontFamily: FF4, fontSize: 11, color: UI.textFnt, letterSpacing: 1.5, textTransform: "uppercase" }}>
                                On This Tour · {onTour.length}
                              </div>
                              {/* Permission legend header */}
                              <div style={{ display: "grid", gridTemplateColumns: "1fr 120px 60px 60px 60px 72px 72px 72px 28px", alignItems: "center", gap: 4, padding: "4px 16px 4px", borderBottom: `1px solid ${UI.rowBg2}` }}>
                                <span style={{ fontFamily: FF4, fontSize: 11, color: UI.textVFnt, letterSpacing: 0.5 }}>PERSON</span>
                                <span style={{ fontFamily: FF4, fontSize: 11, color: UI.textVFnt, letterSpacing: 0.5 }}>TOUR ROLE</span>
                                <span style={{ fontFamily: FF4, fontSize: 11, color: UI.textVFnt, letterSpacing: 0.5, textAlign: "center" }}>GUAR.</span>
                                <span style={{ fontFamily: FF4, fontSize: 11, color: UI.textVFnt, letterSpacing: 0.5, textAlign: "center" }}>SETTLE</span>
                                <span style={{ fontFamily: FF4, fontSize: 11, color: UI.textVFnt, letterSpacing: 0.5, textAlign: "center" }}>MERCH</span>
                                <span style={{ fontFamily: FF4, fontSize: 11, color: UI.textVFnt, letterSpacing: 0.5, textAlign: "center" }}>CONTACTS</span>
                                <span style={{ fontFamily: FF4, fontSize: 11, color: UI.textVFnt, letterSpacing: 0.5, textAlign: "center" }}>ADVANCE</span>
                                <span style={{ fontFamily: FF4, fontSize: 11, color: UI.textVFnt, letterSpacing: 0.5, textAlign: "center" }}>SETLIST</span>
                                <span/>
                              </div>
                              {onTour.map(p => {
                                const mem = tourMemberships.find(m => m.profile_id === p.id) || {};
                                const currentRole = mem.tour_role || "crew";
                                const togglePerm = async (field) => {
                                  const newVal = !mem[field];
                                  await saveTourMembership(p.id, { [field]: newVal });
                                };
                                const changeRole = async (newRole) => {
                                  const preset = TOUR_ROLE_PRESETS.find(r => r.id === newRole);
                                  if (preset) await saveTourMembership(p.id, {
                                    tour_role:          preset.id,
                                    can_see_guarantee:  preset.can_see_guarantee,
                                    can_see_settlement: preset.can_see_settlement,
                                    can_see_merch:      preset.can_see_merch,
                                    can_see_contacts:   preset.can_see_contacts,
                                    can_see_advance:    preset.can_see_advance,
                                    can_edit_setlist:   preset.can_edit_setlist,  // SL-1
                                  });
                                };
                                return (
                                  <div key={p.id} style={{
                                    display: "grid", gridTemplateColumns: "1fr 120px 60px 60px 60px 72px 72px 72px 28px",
                                    alignItems: "center", gap: 4,
                                    padding: "8px 16px",
                                    borderBottom: `1px solid ${UI.rowBg2}`,
                                  }}>
                                    {/* Person info */}
                                    <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                                      <div style={{
                                        width: 24, height: 24, borderRadius: "50%", flexShrink: 0, overflow: "hidden",
                                        background: UI.panelAlt, display: "flex", alignItems: "center", justifyContent: "center",
                                        fontSize: 11, fontWeight: 700, color: UI.textFnt,
                                      }}>
                                        {p.photo_url
                                          ? <img src={p.photo_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                          : (p.name || "?").charAt(0).toUpperCase()}
                                      </div>
                                      <div style={{ minWidth: 0 }}>
                                        <div style={{ fontFamily: FF4, fontSize: 11, fontWeight: 600, color: UI.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.name || "Unnamed"}</div>
                                        {p.role && <div style={{ fontFamily: FF4, fontSize: 11, color: groupColor[p.role_category] || UI.textVFnt, letterSpacing: 0.3 }}>{p.role}</div>}
                                      </div>
                                    </div>
                                    {/* Role selector */}
                                    <select
                                      value={currentRole}
                                      onChange={e => changeRole(e.target.value)}
                                      style={{
                                        fontFamily: FF4, fontSize: 11, color: UI.text,
                                        background: UI.panelAlt, border: `1px solid ${UI.panelBdr}`,
                                        borderRadius: 4, padding: "3px 6px", cursor: "pointer", width: "100%",
                                      }}>
                                      {TOUR_ROLE_PRESETS.map(r => (
                                        <option key={r.id} value={r.id}>{r.label}</option>
                                      ))}
                                    </select>
                                    {/* Guarantee toggle */}
                                    <div style={{ display: "flex", justifyContent: "center" }}>
                                      <button
                                        onClick={() => togglePerm("can_see_guarantee")}
                                        title="Can see guarantee amount and deposit"
                                        style={{
                                          width: 26, height: 16, borderRadius: 8, border: "none", cursor: "pointer",
                                          background: mem.can_see_guarantee ? "var(--success)" : UI.panelAlt,
                                          position: "relative", transition: "background 0.15s", flexShrink: 0,
                                        }}>
                                        <span style={{
                                          position: "absolute", top: 2, left: mem.can_see_guarantee ? 12 : 2,
                                          width: 12, height: 12, borderRadius: "50%", background: "#fff",
                                          transition: "left 0.15s", display: "block",
                                        }}/>
                                      </button>
                                    </div>
                                    {/* Settlement toggle */}
                                    <div style={{ display: "flex", justifyContent: "center" }}>
                                      <button
                                        onClick={() => togglePerm("can_see_settlement")}
                                        title="Can see VS deal terms, settlement breakdown, payment method"
                                        style={{
                                          width: 26, height: 16, borderRadius: 8, border: "none", cursor: "pointer",
                                          background: mem.can_see_settlement ? "var(--success)" : UI.panelAlt,
                                          position: "relative", transition: "background 0.15s", flexShrink: 0,
                                        }}>
                                        <span style={{
                                          position: "absolute", top: 2, left: mem.can_see_settlement ? 12 : 2,
                                          width: 12, height: 12, borderRadius: "50%", background: "#fff",
                                          transition: "left 0.15s", display: "block",
                                        }}/>
                                      </button>
                                    </div>
                                    {/* Merch toggle */}
                                    <div style={{ display: "flex", justifyContent: "center" }}>
                                      <button
                                        onClick={() => togglePerm("can_see_merch")}
                                        title="Can see merch gross/net figures"
                                        style={{
                                          width: 26, height: 16, borderRadius: 8, border: "none", cursor: "pointer",
                                          background: mem.can_see_merch ? "var(--success)" : UI.panelAlt,
                                          position: "relative", transition: "background 0.15s", flexShrink: 0,
                                        }}>
                                        <span style={{
                                          position: "absolute", top: 2, left: mem.can_see_merch ? 12 : 2,
                                          width: 12, height: 12, borderRadius: "50%", background: "#fff",
                                          transition: "left 0.15s", display: "block",
                                        }}/>
                                      </button>
                                    </div>
                                    {/* Contacts toggle */}
                                    <div style={{ display: "flex", justifyContent: "center" }}>
                                      <button
                                        onClick={() => togglePerm("can_see_contacts")}
                                        title="Can see promoter name and phone"
                                        style={{
                                          width: 26, height: 16, borderRadius: 8, border: "none", cursor: "pointer",
                                          background: mem.can_see_contacts ? "var(--success)" : UI.panelAlt,
                                          position: "relative", transition: "background 0.15s", flexShrink: 0,
                                        }}>
                                        <span style={{
                                          position: "absolute", top: 2, left: mem.can_see_contacts ? 12 : 2,
                                          width: 12, height: 12, borderRadius: "50%", background: "#fff",
                                          transition: "left 0.15s", display: "block",
                                        }}/>
                                      </button>
                                    </div>
                                    {/* Advance toggle */}
                                    <div style={{ display: "flex", justifyContent: "center" }}>
                                      <button
                                        onClick={() => togglePerm("can_see_advance")}
                                        title="Can see hospitality notes, rider, backline"
                                        style={{
                                          width: 26, height: 16, borderRadius: 8, border: "none", cursor: "pointer",
                                          background: mem.can_see_advance ? "var(--success)" : UI.panelAlt,
                                          position: "relative", transition: "background 0.15s", flexShrink: 0,
                                        }}>
                                        <span style={{
                                          position: "absolute", top: 2, left: mem.can_see_advance ? 12 : 2,
                                          width: 12, height: 12, borderRadius: "50%", background: "#fff",
                                          transition: "left 0.15s", display: "block",
                                        }}/>
                                      </button>
                                    </div>
                                    {/* Setlist edit toggle (SL-1) */}
                                    <div style={{ display: "flex", justifyContent: "center" }}>
                                      <button
                                        onClick={() => togglePerm("can_edit_setlist")}
                                        title="Can add/edit songs in the show setlist"
                                        style={{
                                          width: 26, height: 16, borderRadius: 8, border: "none", cursor: "pointer",
                                          background: mem.can_edit_setlist ? "var(--success)" : UI.panelAlt,
                                          position: "relative", transition: "background 0.15s", flexShrink: 0,
                                        }}>
                                        <span style={{
                                          position: "absolute", top: 2, left: mem.can_edit_setlist ? 12 : 2,
                                          width: 12, height: 12, borderRadius: "50%", background: "#fff",
                                          transition: "left 0.15s", display: "block",
                                        }}/>
                                      </button>
                                    </div>
                                    {/* Remove */}
                                    <button
                                      onClick={() => removeProfileFromTour(p.id)}
                                      title="Remove from tour"
                                      style={{ background: "none", border: "none", color: UI.textFnt, cursor: "pointer", fontSize: 14, lineHeight: 1, padding: 0, opacity: 0.4, textAlign: "center" }}>✕</button>
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <div style={{ padding: "10px 16px 8px", fontFamily: FF4, fontSize: 11, color: UI.textVFnt, borderBottom: `1px solid ${UI.rowBg2}` }}>
                              No one on this tour yet. Add from the roster below or create a new profile.
                            </div>
                          )}

                          {/* ── Artist roster — quick-add chips ── */}
                          {rosterNotOnTour.length > 0 && (
                            <div style={{ borderBottom: `1px solid ${UI.rowBg2}` }}>
                              <div style={{ padding: "8px 16px 6px", fontFamily: FF4, fontSize: 11, color: UI.textFnt, letterSpacing: 1.5, textTransform: "uppercase" }}>
                                Add from Roster · {artist?.name}
                              </div>
                              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, padding: "4px 16px 12px" }}>
                                {rosterNotOnTour.map(p => (
                                  <div key={p.id}
                                    onClick={() => addProfileToTour(p.id, selT)}
                                    style={{
                                      display: "flex", alignItems: "center", gap: 8,
                                      padding: "7px 12px", borderRadius: 8, cursor: "pointer",
                                      background: UI.rowBg, border: `1px solid ${UI.panelBdr}`,
                                      transition: "all 0.15s",
                                    }}>
                                    <div style={{
                                      width: 18, height: 18, borderRadius: "50%", flexShrink: 0,
                                      background: UI.panelAlt, display: "flex", alignItems: "center", justifyContent: "center",
                                      fontSize: 11, fontWeight: 700, color: UI.textFnt,
                                    }}>
                                      {(p.name || "?").charAt(0).toUpperCase()}
                                    </div>
                                    <div>
                                      <div style={{ fontFamily: FF4, fontSize: 11, fontWeight: 600, color: UI.textMut }}>{p.name || "Unnamed"}</div>
                                      {p.role && <div style={{ fontFamily: FF4, fontSize: 11, color: UI.textVFnt, letterSpacing: 0.3 }}>{p.role}</div>}
                                    </div>
                                    <span style={{ fontFamily: FF4, fontSize: 11, color: UI.amber, marginLeft: 2 }}>+ Add</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {rosterNotOnTour.length === 0 && onTour.filter(p => p.artist_id === artist?.id).length === 0 && (
                            <div style={{ padding: "10px 16px 8px", fontFamily: FF4, fontSize: 11, color: UI.textVFnt, borderBottom: `1px solid ${UI.rowBg2}` }}>
                              No artist roster profiles yet —{" "}
                              <span style={{ color: UI.amber, cursor: "pointer", textDecoration: "underline" }}
                                onClick={() => { togCat("portal_secs_cfg"); setView("artist"); }}>
                                set up the roster in the artist profile
                              </span>
                              {" "}and they'll appear here.
                            </div>
                          )}

                          {/* ── Create new profile ── */}
                          <div style={{ padding: "10px 16px 12px", display: "flex", alignItems: "center", gap: 10 }}>
                            <button className="sm-btn" onClick={() => setMProfile({ mode: "add", context: { tourId: selT } })}>+ Add new person</button>
                            <span style={{ fontFamily: FF4, fontSize: 11, color: UI.textVFnt }}>Creates a profile and adds them to this tour</span>
                          </div>
                        </div>
                      );
                    })()}

                    {/* TAB: Tour Files */}
                    {tourSetupTab === "files" && (() => {
                      const FF4 = "'Helvetica Neue', Helvetica, Arial, sans-serif";
                      const tourFiles = tour?.advanceConfig?.files || [];

                      // Build list of files available from artist profile
                      const allArtistDocs = artist?.documents || [];
                      const tourDocs  = allArtistDocs.filter(d => d.section !== "press");
                      const pressDocs = allArtistDocs.filter(d => d.section === "press");

                      const artistProfileFiles = [
                        ...tourDocs.map(d => ({ sourceKey: `doc_${d.id}`, url: d.fileUrl, name: d.fileName || "Document", label: d.label || "Document" })),
                      ].filter(Boolean);

                      const pressAssetFiles = pressDocs.map(d => ({
                        sourceKey: `doc_${d.id}`, url: d.fileUrl, name: d.fileName || "File",
                        label: d.category || d.label || "Press",
                        category: d.category,
                      }));

                      // Separate artist-sourced vs directly-uploaded files in current tour config
                      const uploadedFiles = tourFiles.filter(f => !f._artistSource);

                      const renderFileRow = (af) => {
                        const included = tourFiles.some(f => f._artistSource === af.sourceKey);
                        return (
                          <div key={af.sourceKey}
                            onClick={() => toggleArtistFile(af.sourceKey, af.url, af.name, af.label)}
                            style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", background: included ? `${UI.amber}12` : UI.rowBg2, border: `1px solid ${included ? UI.amber : UI.fieldBdr}`, borderRadius: 8, cursor: "pointer", transition: "all 0.12s" }}>
                            <div style={{ width: 16, height: 16, borderRadius: 4, border: `1.5px solid ${included ? UI.amber : UI.fieldBdr}`, background: included ? UI.amber : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "all 0.12s" }}>
                              {included && <span style={{ color: "#000", fontSize: 11, fontWeight: 700, lineHeight: 1 }}>✓</span>}
                            </div>
                            <span style={{ fontFamily: FF4, fontSize: 11, color: included ? UI.amber : UI.textFnt, background: included ? UI.amdBg : UI.rowHov, border: `1px solid ${included ? UI.amdBdr : UI.fieldBdr}`, borderRadius: 4, padding: "1px 5px", flexShrink: 0 }}>{af.label}</span>
                            <span style={{ fontFamily: FF4, fontSize: 11, color: included ? UI.text : UI.textMut, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{af.name}</span>
                            <a href={af.url} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} style={{ fontFamily: FF4, fontSize: 11, color: UI.textFnt, textDecoration: "none", flexShrink: 0 }} title="Preview file">↗</a>
                          </div>
                        );
                      };

                      return (
                        <div>
                          <div style={{ padding: "10px 16px 6px", fontFamily: FF4, fontSize: 11, color: UI.textVFnt, letterSpacing: 0.5, borderBottom: `1px solid ${UI.rowBg2}` }}>
                            Files included here appear on every show's advance portal — stage plots, riders, W9s, press kit assets, etc.
                          </div>

                          {/* ── Tour Documents ── */}
                          <div style={{ padding: "10px 16px 6px" }}>
                            <div style={{ fontFamily: FF4, fontSize: 11, letterSpacing: "1.2px", textTransform: "uppercase", color: UI.textFnt, marginBottom: 8 }}>Tour Documents</div>
                            {artistProfileFiles.length === 0 ? (
                              <div style={{ fontFamily: FF4, fontSize: 11, color: UI.textVFnt, fontStyle: "italic", paddingBottom: 8 }}>
                                No tour documents on artist profile yet. Upload a rider or stage plot in the artist's Documents tab.
                              </div>
                            ) : (
                              <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 6 }}>
                                {artistProfileFiles.map(renderFileRow)}
                              </div>
                            )}
                          </div>

                          {/* ── Press Kit ── */}
                          <div style={{ padding: "6px 16px 6px", borderTop: `1px solid ${UI.rowBg2}` }}>
                            <div style={{ fontFamily: FF4, fontSize: 11, letterSpacing: "1.2px", textTransform: "uppercase", color: UI.textFnt, margin: "8px 0 8px" }}>Press Kit</div>
                            {pressAssetFiles.length === 0 ? (
                              <div style={{ fontFamily: FF4, fontSize: 11, color: UI.textVFnt, fontStyle: "italic", paddingBottom: 8 }}>
                                No press assets yet. Upload photos, bios, admats, and promo copy in the artist's Documents tab.
                              </div>
                            ) : (
                              <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 6 }}>
                                {pressAssetFiles.map(renderFileRow)}
                              </div>
                            )}
                          </div>

                          {/* ── Directly Uploaded Files ── */}
                          {uploadedFiles.length > 0 && (
                            <div style={{ padding: "4px 16px 4px", borderTop: `1px solid ${UI.rowBg2}` }}>
                              <div style={{ fontFamily: FF4, fontSize: 11, letterSpacing: "1.2px", textTransform: "uppercase", color: UI.textFnt, margin: "8px 0 6px" }}>Uploaded Files</div>
                              <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                                {uploadedFiles.map(f => (
                                  <div key={f.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 8px", background: UI.rowBg2, border: `1px solid ${UI.fieldBdr}`, borderRadius: 4 }}>
                                    <span style={{ fontFamily: FF4, fontSize: 11, color: UI.amber, background: UI.amdBg, border: `1px solid ${UI.amdBdr}`, borderRadius: 4, padding: "1px 5px", flexShrink: 0 }}>{f.label}</span>
                                    <a href={f.url} target="_blank" rel="noreferrer" style={{ flex: 1, fontSize: 11, color: UI.textMut, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textDecoration: "none" }}>{f.name}</a>
                                    <button onClick={() => removeTourFile(f.id)} style={{ background: "none", border: "none", color: UI.textFnt, cursor: "pointer", fontSize: 14, lineHeight: 1, padding: "0 2px", flexShrink: 0 }} title="Remove">✕</button>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* ── Upload zone (drag-and-drop) ── */}
                          <div style={{ padding: "8px 16px 12px", borderTop: `1px solid ${UI.rowBg2}`, marginTop: 4 }}>
                            <div style={{ fontFamily: FF4, fontSize: 11, letterSpacing: "1.2px", textTransform: "uppercase", color: UI.textFnt, marginBottom: 8 }}>Upload Additional Files</div>
                            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                              <select value={tourFileLabel} onChange={e => setTourFileLabel(e.target.value)}
                                style={{ background: UI.rowBg2, border: `1px solid ${UI.fieldBdr}`, color: UI.textMid, borderRadius: 4, padding: "5px 8px", fontSize: 11, fontFamily: FF4, cursor: "pointer" }}>
                                {["Stage Plot","Input List","Tech Rider","Hospitality Rider","Press Kit","Contract","W9","Setlist","Other"].map(l => <option key={l} value={l}>{l}</option>)}
                              </select>
                              <label
                                style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "8px 10px", background: tourFileUploading ? UI.rowHov : UI.rowBg2, border: `2px dashed ${UI.fieldBdr}`, borderRadius: 4, cursor: tourFileUploading ? "default" : "pointer", fontSize: 11, color: UI.textMut, fontFamily: FF4, transition: "border-color 0.15s, background 0.15s" }}
                                onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = UI.amber; e.currentTarget.style.background = `${UI.amber}10`; }}
                                onDragLeave={e => { e.currentTarget.style.borderColor = UI.fieldBdr; e.currentTarget.style.background = UI.rowBg2; }}
                                onDrop={e => {
                                  e.preventDefault();
                                  e.currentTarget.style.borderColor = UI.fieldBdr;
                                  e.currentTarget.style.background = UI.rowBg2;
                                  const dropped = Array.from(e.dataTransfer.files);
                                  if (dropped.length) uploadTourFiles(dropped);
                                }}>
                                {tourFileUploading ? <><span className="spin" /> Uploading...</> : "⬆ Click or drag files here"}
                                <input type="file" multiple style={{ display: "none" }} disabled={tourFileUploading}
                                  onChange={e => { if (e.target.files.length) { uploadTourFiles(Array.from(e.target.files)); e.target.value = ""; } }} />
                              </label>
                            </div>
                          </div>
                        </div>
                      );
                    })()}

                    {/* TAB: Vehicles — buses, vans, trucks for this tour */}
                    {tourSetupTab === "vehicles" && (() => {
                      const FF4 = "'Helvetica Neue', Helvetica, Arial, sans-serif";
                      const vehicleIcon = {
                        bus:      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="4" width="22" height="13" rx="2"/><path d="M1 11h22M5 17v2M19 17v2"/><circle cx="6" cy="17" r="1" fill="currentColor"/><circle cx="18" cy="17" r="1" fill="currentColor"/></svg>,
                        van:      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M1 7h14l4 5v4H1V7z"/><path d="M15 7v9"/><circle cx="5" cy="17" r="1.5" fill="currentColor"/><circle cx="17" cy="17" r="1.5" fill="currentColor"/></svg>,
                        sprinter: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M1 7h14l4 5v4H1V7z"/><path d="M15 7v9"/><circle cx="5" cy="17" r="1.5" fill="currentColor"/><circle cx="17" cy="17" r="1.5" fill="currentColor"/></svg>,
                        truck:    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="6" width="13" height="11" rx="1"/><path d="M14 9h4l3 4v4h-7V9z"/><circle cx="5.5" cy="17.5" r="1.5" fill="currentColor"/><circle cx="18.5" cy="17.5" r="1.5" fill="currentColor"/></svg>,
                      };
                      return (
                        <div style={{ padding: "10px 16px 14px" }}>
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                            <span style={{ fontFamily: FF4, fontSize: 11, color: UI.textVFnt, letterSpacing: 0.5 }}>
                              Buses, vans, and trucks for this tour — used for driver info and ground logistics.
                            </span>
                            <button onClick={() => setMVehicle({ mode: "add" })}
                              style={{ fontFamily: FF4, fontSize: 11, color: UI.green, background: "none", border: `1px solid ${UI.ckOnBdr}`, borderRadius: 4, padding: "3px 10px", cursor: "pointer", flexShrink: 0 }}>
                              + Add Vehicle
                            </button>
                          </div>
                          {vehicles.length === 0 ? (
                            <div style={{ fontFamily: FF4, fontSize: 11, color: UI.textVFnt, padding: "4px 0 6px" }}>
                              No vehicles added yet.
                            </div>
                          ) : (
                            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                              {vehicles.map(v => (
                                <div key={v.id} onClick={() => setMVehicle({ mode: "edit", vehicle: v })}
                                  style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 14px", background: UI.rowBg, border: `1px solid ${UI.panelBdr}`, borderRadius: 8, cursor: "pointer" }}>
                                  <span style={{ color: UI.textMut, display: "flex", alignItems: "center" }}>{vehicleIcon[v.type] || vehicleIcon.bus}</span>
                                  <div>
                                    <div style={{ fontFamily: FF4, fontSize: 11, fontWeight: 600, color: UI.text }}>{v.label}</div>
                                    {v.driver_name && <div style={{ fontFamily: FF4, fontSize: 11, color: UI.textFnt, letterSpacing: "0.5px" }}>{v.driver_name}</div>}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                )}

              {/* ── TOUR MODE — Day Calendar ── */}
              {isTourMode && (
                <div style={{ padding: "24px 36px 40px" }}>

                  {/* ── COMM-1: Broadcasts ── */}
                  {(() => {
                    const FF4 = "'Helvetica Neue', Helvetica, Arial, sans-serif";
                    const tourParty = profiles.filter(p => p.current_tour_id === selT);
                    const tourBroadcasts = broadcasts.filter(b => b.tour_id === selT);
                    const audLabel = { all: "Everyone", band: "Band", crew: "Crew" };
                    const audColor = { all: UI.amber, band: "var(--accent)", crew: "#6a8faf" };
                    const today = new Date().toISOString().slice(0, 10);

                    // Default day tag to next upcoming tour day if not yet set
                    const defaultDayId = (() => {
                      if (broadcastDayTag !== "") return broadcastDayTag;
                      const next = tourDays.find(d => d.date >= today);
                      return next?.id || tourDays[tourDays.length - 1]?.id || "";
                    })();
                    // Sync default into state once on render (only when empty)
                    if (broadcastDayTag === "" && defaultDayId) {
                      // Use setTimeout to avoid setState-during-render
                      setTimeout(() => setBroadcastDayTag(defaultDayId), 0);
                    }

                    const fmtDayPill = (d) => {
                      const dt = new Date(d.date + "T12:00:00");
                      const label = dt.toLocaleDateString("en-US", { month: "short", day: "numeric" });
                      return d.city ? `${label} · ${d.city}` : label;
                    };
                    const fmtDayFull = (d) => {
                      if (!d) return null;
                      const dt = new Date(d.date + "T12:00:00");
                      const label = dt.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
                      return d.city ? `${label} · ${d.city}` : label;
                    };
                    const fmtTs = (iso) => {
                      const d = new Date(iso);
                      return d.toLocaleDateString("en-US", { month: "short", day: "numeric" }) + " · " +
                             d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
                    };

                    const selectedDay = tourDays.find(d => d.id === broadcastDayTag);

                    // Any active (non-past) broadcast not yet seen by the full party?
                    const anyUnseen = tourParty.length > 0 && tourBroadcasts.some(b => {
                      const taggedDay = b.tour_day_id ? tourDays.find(d => d.id === b.tour_day_id) : null;
                      if (taggedDay && taggedDay.date < today) return false;
                      return (broadcastReads[b.id] || new Set()).size < tourParty.length;
                    });

                    return (
                      <div style={{ marginBottom: 24, padding: "12px 20px", background: UI.panelBg, border: `1px solid ${UI.panelBdr}`, borderRadius: 8 }}>
                        {/* Collapsible header */}
                        <button onClick={() => setBroadcastCollapsed(c => !c)}
                          style={{ display: "flex", alignItems: "center", gap: 8, background: "none", border: "none", cursor: "pointer", padding: 0, marginBottom: broadcastCollapsed ? 0 : 14, width: "100%", WebkitTapHighlightColor: "transparent" }}>
                          <span style={{ fontFamily: FF4, fontSize: 11, letterSpacing: "2px", color: UI.textFnt }}>BROADCASTS</span>
                          {tourBroadcasts.length > 0 && (
                            <span style={{ fontFamily: FF4, fontSize: 11, background: UI.amber + "22", color: UI.amber, borderRadius: 8, padding: "1px 7px", fontWeight: 700 }}>{tourBroadcasts.length}</span>
                          )}
                          {anyUnseen && (
                            <span title="Not everyone has seen the latest broadcasts" style={{ width: 7, height: 7, borderRadius: 999, background: UI.amber, flexShrink: 0 }} />
                          )}
                          {broadcastCollapsed && (
                            <span style={{ fontFamily: FF4, fontSize: 11, color: UI.textVFnt, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {tourBroadcasts.length === 0 ? "Send an update to the touring party" : ""}
                            </span>
                          )}
                          <svg width="11" height="11" viewBox="0 0 12 12" fill="none" stroke={UI.textMut} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: "auto", flexShrink: 0, transform: broadcastCollapsed ? "rotate(-90deg)" : "rotate(0deg)", transition: "transform 0.15s" }}><path d="M2 4.5l4 4 4-4"/></svg>
                        </button>

                        {!broadcastCollapsed && (<>

                        {/* Day selector — scrollable pill strip */}
                        <div style={{ marginBottom: 10 }}>
                          <div style={{ fontFamily: FF4, fontSize: 11, color: UI.textMut, letterSpacing: "1px", marginBottom: 6 }}>TAG TO DAY</div>
                          <div style={{ display: "flex", gap: 6, overflowX: "auto", paddingBottom: 4, scrollbarWidth: "none" }}>
                            <button onClick={() => setBroadcastDayTag("general")}
                              style={{ fontFamily: FF4, fontSize: 11, whiteSpace: "nowrap", padding: "4px 10px", borderRadius: 4, cursor: "pointer", flexShrink: 0,
                                background: broadcastDayTag === "general" ? UI.panelBdr : "none",
                                border: `1px solid ${broadcastDayTag === "general" ? UI.textMut : UI.panelBdr}`,
                                color: broadcastDayTag === "general" ? UI.text : UI.textMut }}>
                              General
                            </button>
                            {tourDays.map(d => {
                              const sel = broadcastDayTag === d.id || (broadcastDayTag === "" && d.id === defaultDayId);
                              const isPast = d.date < today;
                              return (
                                <button key={d.id} onClick={() => setBroadcastDayTag(d.id)}
                                  style={{ fontFamily: FF4, fontSize: 11, whiteSpace: "nowrap", padding: "4px 10px", borderRadius: 4, cursor: "pointer", flexShrink: 0,
                                    background: sel ? UI.amber + "22" : "none",
                                    border: `1px solid ${sel ? UI.amber : UI.panelBdr}`,
                                    color: sel ? UI.amber : isPast ? UI.textVFnt : UI.textMut,
                                    opacity: isPast && !sel ? 0.5 : 1 }}>
                                  {fmtDayPill(d)}
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        {/* Compose area */}
                        {selectedDay && broadcastDayTag !== "general" && (
                          <div style={{ fontFamily: FF4, fontSize: 11, color: UI.amber, marginBottom: 6, letterSpacing: "0.3px" }}>
                            → {fmtDayFull(selectedDay)}
                          </div>
                        )}
                        <textarea
                          value={broadcastDraft}
                          onChange={e => setBroadcastDraft(e.target.value)}
                          onKeyDown={e => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) saveBroadcast(); }}
                          placeholder={broadcastDayTag === "general" ? "General message to touring party…" : `Message for ${selectedDay ? fmtDayFull(selectedDay) : "selected day"}…`}
                          style={{ width: "100%", fontFamily: FF4, fontSize: 14, color: UI.text, background: UI.inputBg || UI.rowBg, border: `1px solid ${UI.panelBdr}`, borderRadius: 8, padding: "10px 12px", resize: "none", height: 68, boxSizing: "border-box", outline: "none", marginBottom: 8 }}
                        />
                        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: tourBroadcasts.length ? 16 : 0 }}>
                          {["all", "band", "crew"].map(a => (
                            <button key={a} onClick={() => setBroadcastAudience(a)}
                              style={{ fontFamily: FF4, fontSize: 11, padding: "4px 10px", borderRadius: 4, cursor: "pointer",
                                background: broadcastAudience === a ? audColor[a] + "22" : "none",
                                border: `1px solid ${broadcastAudience === a ? audColor[a] : UI.panelBdr}`,
                                color: broadcastAudience === a ? audColor[a] : UI.textMut }}>
                              {audLabel[a]}
                            </button>
                          ))}
                          <div style={{ flex: 1 }} />
                          <button onClick={saveBroadcast} disabled={!broadcastDraft.trim() || broadcastSending}
                            style={{ fontFamily: FF4, fontSize: 11, padding: "6px 18px", borderRadius: 8, cursor: broadcastDraft.trim() ? "pointer" : "default",
                              background: broadcastDraft.trim() ? UI.amber : "none",
                              border: `1px solid ${broadcastDraft.trim() ? UI.amber : UI.panelBdr}`,
                              color: broadcastDraft.trim() ? "var(--accent-fg)" : UI.textMut,
                              fontWeight: 700, opacity: broadcastSending ? 0.6 : 1 }}>
                            {broadcastSending ? "Sending…" : "Send"}
                          </button>
                        </div>

                        {/* Broadcast log */}
                        {tourBroadcasts.length > 0 && (
                          <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 4 }}>
                            {tourBroadcasts.map(b => {
                              const reads = broadcastReads[b.id] || new Set();
                              const total = tourParty.length;
                              const noneRead = reads.size === 0;
                              const allRead  = total > 0 && reads.size >= total;
                              const someRead = !noneRead && !allRead;
                              const taggedDay = b.tour_day_id ? tourDays.find(d => d.id === b.tour_day_id) : null;
                              const isPast = taggedDay && taggedDay.date < today;
                              // Read-status color: red = unseen, amber = partial, green = all seen; muted if past
                              const statusColor = isPast ? UI.panelBdr
                                : allRead  ? "var(--success)"
                                : someRead ? UI.amber
                                :            "var(--danger)";
                              return (
                                <div key={b.id} style={{
                                  padding: "8px 10px",
                                  background: statusColor + "0d",
                                  border: `1px solid ${statusColor}44`,
                                  borderRadius: 8,
                                  opacity: isPast ? 0.6 : 1,
                                }}>
                                  {/* Top row: day tag + timestamp */}
                                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                                    <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                                      {taggedDay && (
                                        <span style={{ fontFamily: FF4, fontSize: 11, fontWeight: 700, color: isPast ? UI.textMut : UI.amber, letterSpacing: "0.4px", textTransform: "uppercase" }}>
                                          {fmtDayFull(taggedDay)}
                                        </span>
                                      )}
                                      {isPast && (
                                        <span style={{ fontFamily: FF4, fontSize: 11, letterSpacing: "1px", padding: "1px 4px", borderRadius: 4, background: UI.panelBdr, color: UI.textMut }}>PAST</span>
                                      )}
                                      {b.audience !== "all" && (
                                        <span style={{ fontFamily: FF4, fontSize: 11, letterSpacing: "1px", padding: "1px 5px", borderRadius: 4,
                                          color: audColor[b.audience], background: audColor[b.audience] + "18" }}>
                                          {audLabel[b.audience].toUpperCase()}
                                        </span>
                                      )}
                                    </div>
                                    <span style={{ fontFamily: FF4, fontSize: 11, color: UI.textMut, flexShrink: 0 }}>{fmtTs(b.created_at)}</span>
                                  </div>
                                  {/* Message body */}
                                  <div style={{ fontFamily: FF4, fontSize: 12, color: UI.text, lineHeight: 1.5, marginBottom: 5 }}>{b.body}</div>
                                  {/* Footer: sender + seen count */}
                                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                    <span style={{ fontFamily: FF4, fontSize: 11, color: UI.textMut }}>{b.sender_name || ""}</span>
                                    <span style={{ fontFamily: FF4, fontSize: 11, color: statusColor, fontWeight: 600 }}>
                                      Seen {reads.size}/{total}
                                    </span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                        {tourBroadcasts.length === 0 && (
                          <div style={{ fontFamily: FF4, fontSize: 11, color: UI.textVFnt, paddingTop: 4 }}>No broadcasts yet — tag a day and send a message to the touring party.</div>
                        )}

                        </>)}
                      </div>
                    );
                  })()}

                  {/* ── Touring Party — PP-1: sourced from people_profiles.current_tour_id ── */}
                  {(() => {
                    const FF4 = "'Helvetica Neue', Helvetica, Arial, sans-serif";
                    const groupColor = { band: "var(--accent)", crew: "#6a8faf", tm: "var(--success)", other: UI.textFnt };
                    const tourParty = profiles.filter(p => p.current_tour_id === selT);
                    return (
                      <div style={{ marginBottom: 20 }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                          <span style={{ fontFamily: FF4, fontSize: 11, letterSpacing: "2px", color: UI.textFnt }}>TOURING PARTY · {tourParty.length} {tourParty.length === 1 ? "MEMBER" : "MEMBERS"}</span>
                          <div style={{ display: "flex", gap: 6 }}>
                            <button onClick={() => setMTourInvite(true)}
                              style={{ fontFamily: FF4, fontSize: 11, color: UI.textMut, background: "none", border: `1px solid ${UI.panelBdr}`, borderRadius: 4, padding: "3px 10px", cursor: "pointer" }}>
                              Invite
                            </button>
                            <button onClick={() => { togCat("portal_secs_cfg"); setTourSetupTab("roster"); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                              style={{ fontFamily: FF4, fontSize: 11, color: UI.amber, background: "none", border: `1px solid ${UI.amdBdr}`, borderRadius: 4, padding: "3px 10px", cursor: "pointer" }}>
                              + Add Member
                            </button>
                          </div>
                        </div>
                        {tourParty.length === 0 ? (
                          <div style={{ fontFamily: FF4, fontSize: 11, color: UI.textVFnt, padding: "10px 0" }}>
                            No one on this tour yet —{" "}
                            <span style={{ color: UI.amber, cursor: "pointer", textDecoration: "underline" }}
                              onClick={() => { togCat("portal_secs_cfg"); setTourSetupTab("roster"); window.scrollTo({ top: 0, behavior: "smooth" }); }}>
                              add members in Tour Setup → Crew Roster
                            </span>.
                          </div>
                        ) : (
                          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                            {tourParty.map(p => (
                              <div key={p.id} onClick={() => setMProfile({ mode: "edit", profile: p })}
                                style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 12px", background: UI.rowBg, border: `1px solid ${UI.panelBdr}`, borderRadius: 8, cursor: "pointer" }}>
                                <div style={{ width: 24, height: 24, borderRadius: "50%", background: UI.panelBg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: groupColor[p.role_category] || UI.textMut, flexShrink: 0, overflow: "hidden" }}>
                                  {p.photo_url
                                    ? <img src={p.photo_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                    : (p.name || "?").charAt(0).toUpperCase()}
                                </div>
                                <div>
                                  <div style={{ fontFamily: FF4, fontSize: 11, fontWeight: 600, color: UI.text }}>{p.name}</div>
                                  <div style={{ fontFamily: FF4, fontSize: 11, color: groupColor[p.role_category] || UI.textFnt, letterSpacing: "0.5px" }}>{p.role || p.role_category}</div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* ── Member access & briefings — merged into Touring Party (2026-07-03), collapsed by default ── */}
                        {tourInvites.length > 0 && (() => {
                          const activeCount   = tourInvites.filter(i => i.invite_status === "accepted").length;
                          const pendingCount  = tourInvites.filter(i => i.invite_status === "pending").length;
                          const declinedCount = tourInvites.filter(i => i.invite_status === "declined").length;
                          return (
                            <details style={{ marginTop: 12 }}>
                              <summary style={{ cursor: "pointer", listStyle: "none", display: "flex", alignItems: "center", gap: 8, userSelect: "none" }}>
                                <span style={{ fontFamily: FF4, fontSize: 11, fontWeight: 600, color: UI.textFnt }}>Member access & briefings</span>
                                {activeCount > 0  && <span style={{ fontFamily: FF4, fontSize: 11, color: UI.green,   background: lightMode ? "#e8f5e9" : "#0d1f0d", border: `1px solid ${UI.ckOnBdr}`, borderRadius: 4, padding: "1px 6px", letterSpacing: "0.5px" }}>{activeCount} ACTIVE</span>}
                                {pendingCount > 0 && <span style={{ fontFamily: FF4, fontSize: 11, color: UI.amber,   background: UI.amdBg,                         border: `1px solid ${UI.amdBdr}`, borderRadius: 4, padding: "1px 6px", letterSpacing: "0.5px" }}>{pendingCount} PENDING</span>}
                                {declinedCount > 0 && <span style={{ fontFamily: FF4, fontSize: 11, color: "var(--danger)", background: lightMode ? "#fdecea" : "#1a0000",  border: "1px solid var(--danger)", borderRadius: 4, padding: "1px 6px", letterSpacing: "0.5px" }}>{declinedCount} DECLINED</span>}
                              </summary>
                              <div style={{ fontFamily: FF4, fontSize: 11, color: UI.textMut, margin: "8px 0 10px" }}>
                                Members invited to the mobile app — click a row to configure which modules they see.
                              </div>
                              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                          {tourInvites.map(inv => {
                            const isPending  = inv.invite_status === "pending";
                            const isDeclined = inv.invite_status === "declined";
                            const roleLabel  = inv.role === "band" ? "Band" : "Crew";
                            const enabledMods = (inv.modules || DEFAULT_MEMBER_MODULES).filter(m => m.enabled !== false);

                            return (
                              <div key={inv.id}
                                onClick={() => setMModuleConfig({ member: inv })}
                                style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 12px", background: UI.rowBg, border: `1px solid ${UI.panelBdr}`, borderRadius: 8, cursor: "pointer", transition: "border-color 0.15s, opacity 0.15s", opacity: isPending ? 0.65 : isDeclined ? 0.4 : 1 }}
                                onMouseEnter={e => e.currentTarget.style.borderColor = isPending ? UI.amber : isDeclined ? UI.panelBdr : UI.amber}
                                onMouseLeave={e => e.currentTarget.style.borderColor = UI.panelBdr}>

                                {/* Avatar */}
                                <div style={{ width: 30, height: 30, borderRadius: "50%", background: UI.panelAlt, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, color: inv.role === "band" ? UI.amber : "#6a8faf", flexShrink: 0, border: isPending ? `1px dashed ${UI.amber}` : "none" }}>
                                  {(inv.name || inv.email || "?").charAt(0).toUpperCase()}
                                </div>

                                {/* Name + role + status/icons row */}
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                                    <span style={{ fontFamily: FF4, fontSize: 11, fontWeight: 600, color: UI.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                      {inv.name || inv.email}
                                    </span>
                                    <span style={{ fontFamily: FF4, fontSize: 11, color: UI.textVFnt, letterSpacing: "0.5px", flexShrink: 0 }}>{roleLabel}</span>
                                    {isPending  && <span style={{ fontFamily: FF4, fontSize: 11, color: UI.amber,   background: UI.amdBg,   border: `1px solid ${UI.amdBdr}`,  borderRadius: 4, padding: "1px 5px", flexShrink: 0, letterSpacing: "0.3px" }}>INVITE SENT</span>}
                                    {isDeclined && <span style={{ fontFamily: FF4, fontSize: 11, color: "var(--danger)", background: lightMode ? "#fdecea" : "#1a0000", border: "1px solid var(--danger)", borderRadius: 4, padding: "1px 5px", flexShrink: 0, letterSpacing: "0.3px" }}>DECLINED</span>}
                                  </div>
                                  {/* Module icon chips */}
                                  <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                                    {enabledMods.map(m => (
                                      <ModuleIcon key={m.type} type={m.type} size={11} color={UI.textFnt} />
                                    ))}
                                  </div>
                                </div>

                                <svg width="13" height="13" viewBox="0 0 12 12" fill="none" stroke={UI.textVFnt} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginRight: 2 }}><path d="M4.5 2l4 4-4 4"/></svg>
                              </div>
                            );
                          })}
                              </div>
                            </details>
                          );
                        })()}
                      </div>
                    );
                  })()}

                  {/* ── Day filter bar ── */}
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12, gap: 8, flexWrap: "wrap" }}>
                    <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                      {[
                        { id: "all",    label: `All (${tourDays.length})` },
                        { id: "show",   label: `Shows (${tourDays.filter(d => d.type === "show").length})`   },
                        { id: "travel", label: `Travel (${tourDays.filter(d => d.type === "travel").length})` },
                        { id: "drive",  label: `Drive (${tourDays.filter(d => d.type === "drive").length})`  },
                        { id: "off",    label: `Off (${tourDays.filter(d => d.type === "off").length})`      },
                      ].map(f => (
                        <button key={f.id} onClick={() => setTourDayFilter(f.id)}
                          className={`fp ${tourDayFilter === f.id ? "on" : ""}`}>
                          {f.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {tourDaysLoading ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 8 }}>
                      {Array.from({ length: 6 }).map((_, i) => (
                        <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "11px 16px", background: UI.panelBg, border: `1px solid ${UI.panelBdr}`, borderRadius: 8 }}>
                          <div className="adv-skel" style={{ width: 44, height: 38, flexShrink: 0 }} />
                          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 7 }}>
                            <div className="adv-skel" style={{ width: `${55 - i * 4}%`, height: 11 }} />
                            <div className="adv-skel" style={{ width: `${32 - i * 2}%`, height: 9 }} />
                          </div>
                          <div className="adv-skel" style={{ width: 64, height: 18, flexShrink: 0, borderRadius: 999 }} />
                        </div>
                      ))}
                    </div>
                  ) : tourDays.length === 0 ? (
                    <EmptyState
                      icon={<svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></svg>}
                      title="No tour days yet"
                      hint="Add shows to this tour and the calendar will build itself — show days, travel days, and off days in between."
                      action={<Button variant="primary" size="sm" onClick={() => setMAddShows({})}>+ Add Shows</Button>}
                    />
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 8 }}>
                      {/* Past days — collapsed by default, revealed via strip */}
                      {(() => {
                        const todayISO = new Date().toISOString().slice(0, 10);
                        const pastCount = tourDays.filter(d => (tourDayFilter === "all" || d.type === tourDayFilter) && d.date < todayISO).length;
                        if (!pastCount) return null;
                        return (
                          <button onClick={() => setPastDaysOpen(o => !o)}
                            style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 16px", background: "none", border: `1px dashed ${UI.panelBdr}`, borderRadius: 8, cursor: "pointer", fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: UI.textMut, letterSpacing: "0.3px", transition: "border-color 0.15s, color 0.15s" }}
                            onMouseEnter={e => { e.currentTarget.style.borderColor = UI.textFnt; e.currentTarget.style.color = UI.text; }}
                            onMouseLeave={e => { e.currentTarget.style.borderColor = UI.panelBdr; e.currentTarget.style.color = UI.textMut; }}>
                            <svg width="10" height="10" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, transform: pastDaysOpen ? "rotate(90deg)" : "none", transition: "transform 0.2s" }}><path d="M4.5 2l4 4-4 4"/></svg>
                            {pastDaysOpen ? `Hide ${pastCount} past day${pastCount !== 1 ? "s" : ""}` : `Show ${pastCount} past day${pastCount !== 1 ? "s" : ""}`}
                          </button>
                        );
                      })()}
                      {tourDays.filter(d => (tourDayFilter === "all" || d.type === tourDayFilter) && (pastDaysOpen || d.date >= new Date().toISOString().slice(0, 10))).map(day => {
                        const isExpanded = selectedDayId === day.id;
                        const isPastDay  = day.date < new Date().toISOString().slice(0, 10);
                        const show = day.show_id ? tour.shows.find(s => s.id === day.show_id) : null;
                        const v    = show ? venues[show.venueId] : null;
                        const dayNum = tourDays.indexOf(day) + 1;
                        const typeConfig = {
                          show:    { label: "SHOW DAY",   color: UI.amber,   border: UI.amdBdr,  bg: UI.amdBg  },
                          travel:  { label: "TRAVEL DAY", color: "#6a8faf",  border: "#1a3a5a",  bg: "#0c1520" },
                          drive:   { label: "DRIVE DAY",  color: UI.green,   border: UI.ckOnBdr, bg: UI.ckOnBg },
                          off:     { label: "OFF DAY",    color: UI.textFnt, border: UI.panelBdr, bg: UI.rowBg },
                        }[day.type] || { label: "OFF DAY", color: UI.textFnt, border: UI.panelBdr, bg: UI.rowBg };
                        const FF3 = "'Helvetica Neue', Helvetica, Arial, sans-serif";
                        return (
                          <div key={day.id} style={{ background: UI.panelBg, border: `1px solid ${isExpanded ? typeConfig.border : UI.panelBdr}`, borderRadius: 8, overflow: "hidden", opacity: isPastDay && !isExpanded ? 0.55 : 1, transition: "border-color 0.15s, opacity 0.15s" }}>
                            {/* Card header — always visible */}
                            <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "11px 16px", cursor: "pointer" }}
                              onClick={() => {
                                const nextId = isExpanded ? null : day.id;
                                setSelectedDayId(nextId);
                                if (nextId) {
                                  const wxCity = (day.type === "show" && v) ? (v.city || day.city) : day.city;
                                  loadDayWeather(nextId, wxCity, day.date);
                                }
                              }}>
                              <div style={{ minWidth: 28, fontFamily: FF3, fontSize: 11, color: UI.textVFnt, letterSpacing: 1, flexShrink: 0 }}>D{dayNum}</div>
                              <div style={{ minWidth: 88, fontFamily: FF3, fontSize: 11, color: UI.textMut, flexShrink: 0 }}>{fmtDate(day.date).split(",").slice(0,2).join(",")}</div>
                              <span style={{ fontFamily: FF3, fontSize: 11, letterSpacing: "1px", color: typeConfig.color, background: typeConfig.bg, padding: "2px 8px", borderRadius: 4, flexShrink: 0 }}>{typeConfig.label}</span>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                {/* Primary line */}
                                <div style={{ fontFamily: FF3, fontSize: 14, fontWeight: 600, color: UI.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                  {day.type === "show" && v
                                    ? v.name
                                    : day.type === "travel"
                                      ? (() => {
                                          const from = [day.city, day.state].filter(Boolean).join(", ");
                                          const to   = [day.travel_to_city, day.travel_to_state].filter(Boolean).join(", ");
                                          if (!from && !to) return <span style={{ color: UI.textVFnt }}>No locations set</span>;
                                          return <span>{from || <span style={{ color: UI.textVFnt }}>Origin?</span>} → {to || <span style={{ color: UI.textVFnt }}>Destination?</span>}</span>;
                                        })()
                                      : (() => {
                                          const loc = [day.city, day.state].filter(Boolean).join(", ");
                                          return loc || <span style={{ color: UI.textVFnt }}>No city set</span>;
                                        })()
                                  }
                                </div>
                                {/* Secondary line: venue city/state for show days, or bus call / hotel */}
                                <div style={{ fontFamily: FF3, fontSize: 11, color: UI.textMut, marginTop: 2, display: "flex", gap: 10, flexWrap: "wrap" }}>
                                  {day.type === "show" && v && (v.city || v.addressState) && (
                                    <span>{[v.city, v.addressState].filter(Boolean).join(", ")}</span>
                                  )}
                                  {day.bus_call   && <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="4" width="22" height="13" rx="2"/><path d="M1 11h22M5 17v2M19 17v2"/></svg>{day.bus_call}</span>}
                                  {day.hotel_name && <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 22V8l9-6 9 6v14"/><path d="M9 22V12h6v10"/></svg>{day.hotel_name}</span>}
                                  {(() => { if (day.type !== "show" || !show) return null;
                                    const sch = show.advanceTmData?.schedule;
                                    return sch?.venue_access ? <span>Load-in {sch.venue_access}</span> : null;
                                  })()}
                                </div>
                              </div>
                              {(() => {
                                const wx = dayWeather[day.id];
                                if (!wx || wx.status !== 'ready') return null;
                                return (
                                  <span style={{ fontFamily: FF3, fontSize: 11, color: UI.textMut, flexShrink: 0 }}>
                                    {wx.high}° <span style={{ opacity: 0.5 }}>/</span> {wx.low}°
                                  </span>
                                );
                              })()}
                              {/* Who's in town indicator */}
                              {(() => {
                                const dayCity = ((day.type === "show" && v) ? v.city : day.city || "").toLowerCase().trim();
                                if (!dayCity) return null;
                                const inTown = networkProfiles.filter(p =>
                                  connections.has(p.id) &&
                                  (p.tour_routing || []).some(r => r.date === day.date && (r.city || "").toLowerCase().trim() === dayCity)
                                );
                                if (!inTown.length) return null;
                                return (
                                  <span title={inTown.map(p => p.preferred_name || p.name).join(", ")}
                                    style={{ fontFamily: FF3, fontSize: 11, color: "var(--success)", background: "#3ecf6e18", border: "1px solid #3ecf6e30", borderRadius: 4, padding: "2px 7px", flexShrink: 0, letterSpacing: "0.3px", cursor: "help", display: "inline-flex", alignItems: "center", gap: 4 }}>
                                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/></svg>{inTown.length} in town
                                  </span>
                                );
                              })()}
                              {/* LG-1: logistics summary icons */}
                              {(() => {
                                const ls = logisticsSummary[day.id];
                                if (!ls) return null;
                                const svgHotel    = <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 22V8l9-6 9 6v14"/><path d="M9 22V12h6v10"/></svg>;
                                const svgPlane    = <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.8 19.2L16 11l3.5-3.5A2 2 0 0 0 17 4.5L13.5 8 5.2 6.2a1 1 0 0 0-.9.3L3 8l4 3-1.2 2.1L2 14v2l4-1 .9 3z"/></svg>;
                                const svgVan      = <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 7h14l4 5v4H1V7z"/><path d="M15 7v9"/><circle cx="5" cy="17" r="1.5" fill="currentColor"/><circle cx="17" cy="17" r="1.5" fill="currentColor"/></svg>;
                                const icons = [];
                                if (ls.hasLodging)   icons.push({ svg: svgHotel, label: "Lodging booked",   color: "var(--success)" });
                                if (ls.hasFlights)   icons.push({ svg: svgPlane, label: "Flights added",   color: "#6a8faf" });
                                if (ls.hasTransport) icons.push({ svg: svgVan,   label: "Transport added", color: "#a78bfa" });
                                const isLogDay = day.type === "show" || day.type === "travel" || day.type === "drive";
                                if (isLogDay && !ls.hasLodging)   icons.push({ svg: svgHotel, label: "No lodging yet",   dim: true });
                                if (isLogDay && !ls.hasFlights && day.type !== "drive") icons.push({ svg: svgPlane, label: "No flights yet", dim: true });
                                if (!icons.length) return null;
                                return (
                                  <div style={{ display: "flex", gap: 4, flexShrink: 0, alignItems: "center" }}>
                                    {icons.map((ic, i) => (
                                      <span key={i} title={ic.label} style={{ color: ic.dim ? UI.panelBdr : ic.color, opacity: ic.dim ? 0.5 : 1, display: "flex", alignItems: "center", cursor: "default" }}>{ic.svg}</span>
                                    ))}
                                  </div>
                                );
                              })()}
                              <svg width="11" height="11" viewBox="0 0 12 12" fill="none" stroke={UI.textFnt} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, transition: "transform 0.2s", transform: isExpanded ? "rotate(90deg)" : "none" }}><path d="M4.5 2l4 4-4 4"/></svg>
                            </div>

                            {/* Expanded edit panel */}
                            {isExpanded && (
                              <div style={{ borderTop: `1px solid ${UI.panelBdr}`, padding: "16px 16px 20px", display: "flex", flexDirection: "column", gap: 14 }}>
                                {/* Weather strip — unobtrusive single line */}
                                {(() => {
                                  const wx = dayWeather[day.id];
                                  if (!wx) return null;
                                  if (wx.status === 'ready') {
                                    const parts = [
                                      wx.condition,
                                      `${wx.high}° / ${wx.low}°`,
                                      wx.wind   ? `Wind ${wx.wind} mph`  : null,
                                      wx.precip ? `${wx.precip}% precip` : null,
                                    ].filter(Boolean);
                                    return (
                                      <div style={{ fontFamily: FF3, fontSize: 11, color: UI.textMut, letterSpacing: 0.2 }}>
                                        {parts.map((p, i) => (
                                          <span key={i}>
                                            {i > 0 && <span style={{ opacity: 0.35, margin: "0 6px" }}>·</span>}
                                            {p}
                                          </span>
                                        ))}
                                      </div>
                                    );
                                  }
                                  if (wx.status === 'future') return (
                                    <div style={{ fontFamily: FF3, fontSize: 11, color: UI.textVFnt }}>
                                      Forecast available from {wx.available}
                                    </div>
                                  );
                                  if (wx.status === 'error') return (
                                    <div style={{ fontFamily: FF3, fontSize: 11, color: UI.textVFnt }}>
                                      Weather unavailable
                                    </div>
                                  );
                                  return null;
                                })()}

                                {/* Who's in town — expanded */}
                                {(() => {
                                  const dayCity = ((day.type === "show" && v) ? v.city : day.city || "").toLowerCase().trim();
                                  if (!dayCity) return null;
                                  const inTown = networkProfiles.filter(p =>
                                    connections.has(p.id) &&
                                    (p.tour_routing || []).some(r => r.date === day.date && (r.city || "").toLowerCase().trim() === dayCity)
                                  );
                                  if (!inTown.length) return null;
                                  const FF3 = "'Helvetica Neue', Helvetica, Arial, sans-serif";
                                  return (
                                    <div style={{ background: "#3ecf6e0e", border: "1px solid #3ecf6e28", borderRadius: 8, padding: "10px 14px" }}>
                                      <div style={{ fontFamily: FF3, fontSize: 11, letterSpacing: "1.5px", textTransform: "uppercase", color: "var(--success)", marginBottom: 8, display: "flex", alignItems: "center", gap: 5 }}><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/></svg>Also in town</div>
                                      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                                        {inTown.map(p => {
                                          const catColor = PROFILE_ROLE_COLORS[p.role_category] || "#6a8faf";
                                          const initials = (p.preferred_name || p.name || "?").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
                                          return (
                                            <div key={p.id} onClick={() => setMNetworkProfile(p)} style={{ display: "flex", alignItems: "center", gap: 7, background: UI.rowBg, border: `1px solid ${UI.panelBdr}`, borderRadius: 16, padding: "4px 12px 4px 4px", cursor: "pointer", transition: "border-color 0.15s" }}
                                              onMouseEnter={e => e.currentTarget.style.borderColor = catColor}
                                              onMouseLeave={e => e.currentTarget.style.borderColor = UI.panelBdr}>
                                              <div style={{ width: 22, height: 22, borderRadius: "50%", background: `${catColor}25`, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", flexShrink: 0 }}>
                                                {p.photo_url ? <img src={p.photo_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <span style={{ fontFamily: FF3, fontSize: 11, fontWeight: 700, color: catColor }}>{initials}</span>}
                                              </div>
                                              <div>
                                                <div style={{ fontFamily: FF3, fontSize: 11, fontWeight: 600, color: UI.text }}>{p.preferred_name || p.name}</div>
                                                {p.role && <div style={{ fontFamily: FF3, fontSize: 11, color: UI.textMut }}>{p.role}</div>}
                                              </div>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  );
                                })()}

                                {/* Day type + location fields */}
                                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                                  {/* Row 1: day type + bus call */}
                                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                                    <div style={{ flex: 1, minWidth: 160 }}>
                                      <div style={{ fontFamily: FF3, fontSize: 11, letterSpacing: 1.5, color: UI.textFnt, marginBottom: 6 }}>DAY TYPE</div>
                                      <div style={{ display: "flex", gap: 4 }}>
                                        {[["show","Show"],["travel","Travel"],["drive","Drive"],["off","Off"]].map(([t, lbl]) => (
                                          <button key={t} onClick={() => saveTourDay(day.id, { type: t })}
                                            style={{ fontFamily: FF3, fontSize: 11, padding: "4px 10px", borderRadius: 4, border: "1px solid", cursor: "pointer",
                                              background: day.type === t ? typeConfig.color : "none",
                                              color: day.type === t ? "#0d0d0d" : UI.textMut,
                                              borderColor: day.type === t ? typeConfig.color : UI.panelBdr,
                                              fontWeight: day.type === t ? 700 : 400,
                                            }}>{lbl}</button>
                                        ))}
                                      </div>
                                    </div>
                                    <div style={{ flex: 1, minWidth: 80 }}>
                                      <div style={{ fontFamily: FF3, fontSize: 11, letterSpacing: 1.5, color: UI.textFnt, marginBottom: 6 }}>BUS CALL</div>
                                      <input type="time" style={{ width: "100%", boxSizing: "border-box", background: UI.rowBg, border: `1px solid ${UI.panelBdr}`, borderRadius: 4, padding: "6px 10px", color: UI.text, fontFamily: FF3, fontSize: 12 }}
                                        value={day.bus_call}
                                        onChange={e => setTourDays(p => p.map(d => d.id === day.id ? { ...d, bus_call: e.target.value } : d))}
                                        onBlur={e => saveTourDay(day.id, { bus_call: e.target.value })} />
                                    </div>
                                  </div>

                                  {/* Row 2: location fields — show day pulls from venue (read-only); travel day has from/to; off/drive has city+state */}
                                  {day.type === "show" && v ? (
                                    <div style={{ fontFamily: FF3, fontSize: 11, color: UI.textMut, background: UI.rowBg, border: `1px solid ${UI.panelBdr}`, borderRadius: 4, padding: "7px 10px" }}>
                                      <span style={{ color: UI.textVFnt, marginRight: 6 }}>Venue location:</span>
                                      {[v.name, v.city, v.addressState].filter(Boolean).join(", ")}
                                    </div>
                                  ) : day.type === "travel" ? (
                                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
                                      <div style={{ flex: 2, minWidth: 140 }}>
                                        <div style={{ fontFamily: FF3, fontSize: 11, letterSpacing: 1.5, color: UI.textFnt, marginBottom: 6 }}>FROM CITY</div>
                                        <input style={{ width: "100%", boxSizing: "border-box", background: UI.rowBg, border: `1px solid ${UI.panelBdr}`, borderRadius: 4, padding: "6px 10px", color: UI.text, fontFamily: FF3, fontSize: 12 }}
                                          value={day.city || ""} placeholder="Departing city"
                                          onChange={e => setTourDays(p => p.map(d => d.id === day.id ? { ...d, city: e.target.value } : d))}
                                          onBlur={e => saveTourDay(day.id, { city: e.target.value })} />
                                      </div>
                                      <div style={{ flex: 1, minWidth: 80 }}>
                                        <div style={{ fontFamily: FF3, fontSize: 11, letterSpacing: 1.5, color: UI.textFnt, marginBottom: 6 }}>STATE</div>
                                        <input style={{ width: "100%", boxSizing: "border-box", background: UI.rowBg, border: `1px solid ${UI.panelBdr}`, borderRadius: 4, padding: "6px 10px", color: UI.text, fontFamily: FF3, fontSize: 12 }}
                                          value={day.state || ""} placeholder="CA"
                                          onChange={e => setTourDays(p => p.map(d => d.id === day.id ? { ...d, state: e.target.value } : d))}
                                          onBlur={e => saveTourDay(day.id, { state: e.target.value })} />
                                      </div>
                                      <div style={{ fontFamily: FF3, fontSize: 16, color: UI.textVFnt, paddingBottom: 6, flexShrink: 0 }}>→</div>
                                      <div style={{ flex: 2, minWidth: 140 }}>
                                        <div style={{ fontFamily: FF3, fontSize: 11, letterSpacing: 1.5, color: UI.textFnt, marginBottom: 6 }}>TO CITY</div>
                                        <input style={{ width: "100%", boxSizing: "border-box", background: UI.rowBg, border: `1px solid ${UI.panelBdr}`, borderRadius: 4, padding: "6px 10px", color: UI.text, fontFamily: FF3, fontSize: 12 }}
                                          value={day.travel_to_city || ""} placeholder="Arriving city"
                                          onChange={e => setTourDays(p => p.map(d => d.id === day.id ? { ...d, travel_to_city: e.target.value } : d))}
                                          onBlur={e => saveTourDay(day.id, { travel_to_city: e.target.value })} />
                                      </div>
                                      <div style={{ flex: 1, minWidth: 80 }}>
                                        <div style={{ fontFamily: FF3, fontSize: 11, letterSpacing: 1.5, color: UI.textFnt, marginBottom: 6 }}>STATE</div>
                                        <input style={{ width: "100%", boxSizing: "border-box", background: UI.rowBg, border: `1px solid ${UI.panelBdr}`, borderRadius: 4, padding: "6px 10px", color: UI.text, fontFamily: FF3, fontSize: 12 }}
                                          value={day.travel_to_state || ""} placeholder="NY"
                                          onChange={e => setTourDays(p => p.map(d => d.id === day.id ? { ...d, travel_to_state: e.target.value } : d))}
                                          onBlur={e => saveTourDay(day.id, { travel_to_state: e.target.value })} />
                                      </div>
                                    </div>
                                  ) : (
                                    <div style={{ display: "flex", gap: 10 }}>
                                      <div style={{ flex: 2, minWidth: 140 }}>
                                        <div style={{ fontFamily: FF3, fontSize: 11, letterSpacing: 1.5, color: UI.textFnt, marginBottom: 6 }}>CITY</div>
                                        <input style={{ width: "100%", boxSizing: "border-box", background: UI.rowBg, border: `1px solid ${UI.panelBdr}`, borderRadius: 4, padding: "6px 10px", color: UI.text, fontFamily: FF3, fontSize: 12 }}
                                          value={day.city || ""} placeholder="City"
                                          onChange={e => setTourDays(p => p.map(d => d.id === day.id ? { ...d, city: e.target.value } : d))}
                                          onBlur={e => saveTourDay(day.id, { city: e.target.value })} />
                                      </div>
                                      <div style={{ flex: 1, minWidth: 80 }}>
                                        <div style={{ fontFamily: FF3, fontSize: 11, letterSpacing: 1.5, color: UI.textFnt, marginBottom: 6 }}>STATE</div>
                                        <input style={{ width: "100%", boxSizing: "border-box", background: UI.rowBg, border: `1px solid ${UI.panelBdr}`, borderRadius: 4, padding: "6px 10px", color: UI.text, fontFamily: FF3, fontSize: 12 }}
                                          value={day.state || ""} placeholder="CA"
                                          onChange={e => setTourDays(p => p.map(d => d.id === day.id ? { ...d, state: e.target.value } : d))}
                                          onBlur={e => saveTourDay(day.id, { state: e.target.value })} />
                                      </div>
                                    </div>
                                  )}
                                </div>

                                {/* Lodging — collapsible */}
                                {(() => {
                                  const lodgingList  = dayLodging[day.id] || [];
                                  const lodgingCount = lodgingList.length;
                                  const lodgingOpen  = daySections[day.id]?.lodging ?? (lodgingCount > 0);
                                  const toggleLodging = () => {
                                    if (!dayLodging[day.id]) loadDayLodging(day.id);
                                    setDaySections(p => ({ ...p, [day.id]: { ...p[day.id], lodging: !lodgingOpen } }));
                                  };
                                  const totalRooms = lodgingList.reduce((acc, l) => acc + (l.rooms || []).reduce((s, r) => s + (r.count || 0), 0), 0);
                                  return (
                                    <div style={{ border: `1px solid ${UI.panelBdr}`, borderRadius: 8, overflow: "hidden" }}>
                                      <button onClick={toggleLodging} style={{ width: "100%", display: "flex", alignItems: "center", gap: 8, padding: "9px 12px", background: UI.rowBg, border: "none", cursor: "pointer", textAlign: "left" }}>
                                        <span style={{ fontFamily: FF3, fontSize: 11, letterSpacing: 1.5, color: lodgingOpen ? UI.amber : UI.textMut, textTransform: "uppercase", flex: 1 }}>Lodging</span>
                                        {lodgingCount > 0 && (
                                          <span style={{ fontFamily: FF3, fontSize: 11, color: UI.amber, background: UI.amdBg, border: `1px solid ${UI.amdBdr}`, borderRadius: 8, padding: "1px 7px" }}>
                                            {lodgingCount} {lodgingCount === 1 ? "property" : "properties"}{totalRooms > 0 ? ` · ${totalRooms} rooms` : ""}
                                          </span>
                                        )}
                                        <span style={{ fontFamily: FF3, fontSize: 11, color: UI.textMut, lineHeight: 1 }}>{lodgingOpen ? "−" : "+"}</span>
                                      </button>
                                      {lodgingOpen && (
                                        <div style={{ padding: "10px 12px", borderTop: `1px solid ${UI.panelBdr}`, display: "flex", flexDirection: "column", gap: 8 }}>
                                          {!dayLodging[day.id] && (() => { loadDayLodging(day.id); return null; })()}
                                          {lodgingCount === 0 ? (
                                            <div style={{ fontFamily: FF3, fontSize: 11, color: UI.textVFnt }}>No lodging added yet.</div>
                                          ) : (
                                            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                              {lodgingList.map(l => {
                                                const assigned = profiles.filter(p => (l.personnel_ids || []).includes(p.id));
                                                const roomSummary = (l.rooms || []).map(r => `${r.count}× ${r.type}`).join(", ");
                                                const isAirbnb = l.type === "airbnb";
                                                const isHome   = l.type === "home";
                                                const typeColor = isAirbnb ? "#c06faf" : isHome ? "var(--success)" : UI.amber;
                                                const typeBg    = isAirbnb ? "#2a0a1e"  : isHome ? "#0a1e12" : UI.amdBg;
                                                const typeBdr   = isAirbnb ? "#5a1a4a"  : isHome ? "#1a4a2a" : UI.amdBdr;
                                                return (
                                                  <div key={l.id} onClick={() => { loadDayLodging(day.id); setMLodging({ mode: "edit", dayId: day.id, lodging: l }); }}
                                                    style={{ padding: "10px 12px", background: UI.amdBg, border: `1px solid ${isAirbnb ? "#3a1a2a" : isHome ? "#1a3a28" : UI.amdBdr}`, borderRadius: 8, cursor: "pointer" }}>
                                                    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8, marginBottom: roomSummary || assigned.length ? 6 : 0 }}>
                                                      <div>
                                                        <span style={{ fontFamily: FF3, fontSize: 12, fontWeight: 600, color: UI.text }}>{l.name}</span>
                                                        {l.chain && <span style={{ fontFamily: FF3, fontSize: 11, color: UI.textMut, marginLeft: 8 }}>{l.chain}</span>}
                                                        {isHome && l.address && <span style={{ fontFamily: FF3, fontSize: 11, color: UI.textMut, marginLeft: 8 }}>{l.address}</span>}
                                                      </div>
                                                      <div style={{ display: "flex", gap: 8, flexShrink: 0, alignItems: "center" }}>
                                                        {l.check_in && <span style={{ fontFamily: FF3, fontSize: 11, color: UI.textMut }}>In {l.check_in}</span>}
                                                        {l.check_out && <span style={{ fontFamily: FF3, fontSize: 11, color: UI.textMut }}>Out {l.check_out}</span>}
                                                        <span style={{ fontFamily: FF3, fontSize: 11, letterSpacing: 1, color: typeColor, background: typeBg, border: `1px solid ${typeBdr}`, borderRadius: 4, padding: "2px 7px", textTransform: "uppercase" }}>
                                                          {LODGING_TYPE_LABELS[l.type] || l.type}
                                                        </span>
                                                      </div>
                                                    </div>
                                                    {roomSummary && <div style={{ fontFamily: FF3, fontSize: 11, color: UI.textMut }}>{roomSummary}</div>}
                                                    {assigned.length > 0 && (
                                                      <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 5 }}>
                                                        {assigned.map(c => (
                                                          <span key={c.id} style={{ fontFamily: FF3, fontSize: 11, color: UI.textFnt, background: UI.rowBg, border: `1px solid ${UI.panelBdr}`, borderRadius: 8, padding: "1px 8px" }}>{c.name}</span>
                                                        ))}
                                                      </div>
                                                    )}
                                                  </div>
                                                );
                                              })}
                                            </div>
                                          )}
                                          {/* Hotel recommendations */}
                                          {(() => {
                                            // Get destination for this day: venue or city
                                            const recDest = (day.type === "show" && v)
                                              ? [v.name, v.city, v.addressState].filter(Boolean).join(", ")
                                              : day.city || null;
                                            const lodgingPrefs = artist?.lodging_preferences || {};
                                            if (!recDest || !GMAP_KEY) return null;
                                            return (
                                              <div style={{ borderTop: `1px solid ${UI.panelBdr}`, paddingTop: 10 }}>
                                                <div style={{ fontFamily: FF3, fontSize: 11, letterSpacing: 1.5, color: UI.textMut, textTransform: "uppercase", marginBottom: 8 }}>Hotel Recommendations</div>
                                                <HotelRecommendations
                                                  destination={recDest}
                                                  routeWaypoints={dayRouteWaypoints[day.id] || null}
                                                  lodgingPrefs={lodgingPrefs}
                                                  onAddLodging={(prefill) => {
                                                    loadDayLodging(day.id);
                                                    setMLodging({ mode: "add", dayId: day.id, lodging: prefill });
                                                  }}
                                                />
                                              </div>
                                            );
                                          })()}

                                          <button onClick={() => { loadDayLodging(day.id); setMLodging({ mode: "add", dayId: day.id }); }}
                                            style={{ fontFamily: FF3, fontSize: 11, color: UI.amber, background: "none", border: `1px solid ${UI.amdBdr}`, borderRadius: 4, padding: "5px 12px", cursor: "pointer", alignSelf: "flex-start" }}>
                                            + Add Lodging
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })()}

                                {/* Travel Map — embedded Google Maps with live traffic */}
                                {(() => {
                                  const dayIdx  = tourDays.findIndex(d => d.id === day.id);
                                  const prevDay = dayIdx > 0 ? tourDays[dayIdx - 1] : null;
                                  if (!prevDay) return null;

                                  // ── Origin ───────────────────────────────────────────────
                                  // Priority: prev lodging table > prev legacy hotel fields > prev venue > prev city
                                  let originAddr, originLabel;
                                  const prevLodgingList = dayLodging[prevDay.id] || [];
                                  const prevLodging = prevLodgingList[0]; // use first lodging record if multiple
                                  if (prevLodging?.address) {
                                    originAddr  = prevLodging.address;
                                    originLabel = prevLodging.name || "Hotel";
                                  } else if (prevLodging?.name) {
                                    originAddr  = prevLodging.name;
                                    originLabel = prevLodging.name;
                                  } else if (prevDay.hotel_address) {
                                    originAddr  = prevDay.hotel_address;
                                    originLabel = prevDay.hotel_name || "Hotel";
                                  } else if (prevDay.hotel_name) {
                                    originAddr  = prevDay.hotel_name;
                                    originLabel = prevDay.hotel_name;
                                  } else {
                                    const prevShow  = prevDay.show_id ? tour.shows.find(s => s.id === prevDay.show_id) : null;
                                    const prevVenue = prevShow ? venues[prevShow.venueId] : null;
                                    originAddr  = prevVenue
                                      ? [prevVenue.name, prevVenue.address, prevVenue.city, prevVenue.addressState].filter(Boolean).join(", ")
                                      : prevDay.city;
                                    originLabel = prevVenue?.name || prevDay.city;
                                  }

                                  // ── Destination ──────────────────────────────────────────
                                  // Priority: this day lodging table > legacy hotel fields > venue > city
                                  let destAddr, destLabel;
                                  const todayLodgingList = dayLodging[day.id] || [];
                                  const todayLodging = todayLodgingList[0];
                                  if (todayLodging?.address) {
                                    destAddr  = todayLodging.address;
                                    destLabel = todayLodging.name || "Hotel";
                                  } else if (todayLodging?.name) {
                                    destAddr  = todayLodging.name;
                                    destLabel = todayLodging.name;
                                  } else if (day.hotel_address) {
                                    destAddr  = day.hotel_address;
                                    destLabel = day.hotel_name || "Hotel";
                                  } else if (day.hotel_name) {
                                    destAddr  = day.hotel_name;
                                    destLabel = day.hotel_name;
                                  } else if (day.type === "show" && v) {
                                    destAddr  = [v.name, v.address, v.city, v.addressState].filter(Boolean).join(", ");
                                    destLabel = v.name;
                                  } else {
                                    destAddr  = day.city;
                                    destLabel = day.city;
                                  }

                                  if (!originAddr || !destAddr) return null;

                                  // Arrival target: venue access for show days, lodging check-in otherwise
                                  const todayCheckIn  = todayLodging?.check_in || day.hotel_check_in || null;
                                  const arrivalTarget = (day.type === "show" && show?.advanceTmData?.schedule?.venue_access)
                                    ? show.advanceTmData.schedule.venue_access
                                    : todayCheckIn;
                                  const arrivalLabel  = (day.type === "show" && show?.advanceTmData?.schedule?.venue_access)
                                    ? "Venue Access"
                                    : todayCheckIn ? "Hotel Check-in" : null;

                                  const mapOpen   = daySections[day.id]?.map ?? false;
                                  const toggleMap = () => setDaySections(p => ({ ...p, [day.id]: { ...p[day.id], map: !mapOpen } }));
                                  return (
                                    <div style={{ border: `1px solid ${UI.panelBdr}`, borderRadius: 8, overflow: "hidden" }}>
                                      <button onClick={toggleMap} style={{ width: "100%", display: "flex", alignItems: "center", gap: 8, padding: "9px 12px", background: UI.rowBg, border: "none", cursor: "pointer", textAlign: "left" }}>
                                        <span style={{ fontFamily: FF3, fontSize: 11, letterSpacing: 1.5, color: mapOpen ? UI.textFnt : UI.textMut, textTransform: "uppercase", flex: 1 }}>Route</span>
                                        <span style={{ fontFamily: FF3, fontSize: 11, color: UI.textVFnt, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 200 }}>{originLabel} → {destLabel}</span>
                                        <span style={{ fontFamily: FF3, fontSize: 11, color: UI.textMut, lineHeight: 1, flexShrink: 0 }}>{mapOpen ? "−" : "+"}</span>
                                      </button>
                                      {mapOpen && (
                                        <div style={{ borderTop: `1px solid ${UI.panelBdr}`, padding: "12px 12px 14px" }}>
                                          <TravelMap
                                            origin={originAddr}
                                            destination={destAddr}
                                            originLabel={originLabel}
                                            destLabel={destLabel}
                                            arrivalTarget={arrivalTarget}
                                            arrivalLabel={arrivalLabel}
                                            darkMode={false}
                                            onRouteReady={wps => setDayRouteWaypoints(p => ({ ...p, [day.id]: wps }))}
                                          />
                                        </div>
                                      )}
                                    </div>
                                  );
                                })()}

                                {/* Show info (read-only, pulled from advance data) */}
                                {day.type === "show" && show && v && (
                                  <div style={{ background: UI.amdBg, border: `1px solid ${UI.amdBdr}`, borderRadius: 8, padding: "10px 14px" }}>
                                    <div style={{ fontFamily: FF3, fontSize: 11, letterSpacing: 1.5, color: UI.amber, marginBottom: 8 }}>SHOW INFO — FROM ADVANCE</div>
                                    <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
                                      <div><div style={{ fontFamily: FF3, fontSize: 11, color: UI.textMut }}>Venue</div><div style={{ fontFamily: FF3, fontSize: 12, color: UI.text }}>{v.name}</div></div>
                                      {show.advanceTmData?.schedule?.venue_access && <div><div style={{ fontFamily: FF3, fontSize: 11, color: UI.textMut }}>Load-in</div><div style={{ fontFamily: FF3, fontSize: 12, color: UI.text }}>{show.advanceTmData.schedule.venue_access}</div></div>}
                                      {(() => { const bill = show.advanceTmData?.schedule?.bill; const hl = bill?.length ? bill[bill.length - 1] : null; if (!hl?.set) return null; const dur = setDurMins(hl.set, hl.setEnd); return <div><div style={{ fontFamily: FF3, fontSize: 11, color: UI.textMut }}>Set Time</div><div style={{ fontFamily: FF3, fontSize: 12, color: UI.text }}>{hl.set}{dur ? <span style={{ fontSize: 11, color: UI.textMut, marginLeft: 4 }}>({fmtDur(dur)})</span> : null}</div></div>; })()}
                                      {(show.settlement?.promoterName || v.promoterName) && <div><div style={{ fontFamily: FF3, fontSize: 11, color: UI.textMut }}>Promoter</div><div style={{ fontFamily: FF3, fontSize: 12, color: UI.text }}>{show.settlement?.promoterName || v.promoterName}</div></div>}
                                      {show.settlement?.guarantee && <div><div style={{ fontFamily: FF3, fontSize: 11, color: UI.textMut }}>Guarantee</div><div style={{ fontFamily: FF3, fontSize: 12, color: UI.amber }}>{show.settlement.guarantee}</div></div>}
                                    </div>
                                  </div>
                                )}

                                {/* Flights — collapsible */}
                                {(() => { if (day.type !== "travel" && day.type !== "show") return null;
                                  const flightCount  = (dayFlights[day.id] || []).length;
                                  const flightsOpen  = daySections[day.id]?.flights ?? (flightCount > 0);
                                  const toggleFlights = () => {
                                    if (!dayFlights[day.id]) loadDayFlights(day.id);
                                    setDaySections(p => ({ ...p, [day.id]: { ...p[day.id], flights: !flightsOpen } }));
                                  };
                                  return (
                                    <div style={{ border: `1px solid ${UI.panelBdr}`, borderRadius: 8, overflow: "hidden" }}>
                                      {/* Toggle header */}
                                      <button onClick={toggleFlights} style={{ width: "100%", display: "flex", alignItems: "center", gap: 8, padding: "9px 12px", background: UI.rowBg, border: "none", cursor: "pointer", textAlign: "left" }}>
                                        <span style={{ fontFamily: FF3, fontSize: 11, letterSpacing: 1.5, color: flightsOpen ? "#6a8faf" : UI.textMut, textTransform: "uppercase", flex: 1 }}>Flights</span>
                                        {flightCount > 0 && (
                                          <span style={{ fontFamily: FF3, fontSize: 11, color: "#6a8faf", background: "#0c1520", border: "1px solid #1a3a5a", borderRadius: 8, padding: "1px 7px" }}>{flightCount}</span>
                                        )}
                                        <span style={{ fontFamily: FF3, fontSize: 11, color: UI.textMut, lineHeight: 1 }}>{flightsOpen ? "−" : "+"}</span>
                                      </button>
                                      {/* Expanded content */}
                                      {flightsOpen && (
                                        <div style={{ padding: "10px 12px", borderTop: `1px solid ${UI.panelBdr}`, display: "flex", flexDirection: "column", gap: 8 }}>
                                          {!dayFlights[day.id] && (() => { loadDayFlights(day.id); return null; })()}
                                          {flightCount === 0 ? (
                                            <div style={{ fontFamily: FF3, fontSize: 11, color: UI.textVFnt }}>No flights added yet.</div>
                                          ) : (
                                            <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                                              {(dayFlights[day.id] || []).map(fl => {
                                                const passengers = profiles.filter(p => (fl.passenger_ids || []).includes(p.id));
                                                const statusCfg = {
                                                  scheduled: { label: "SCHEDULED", color: UI.textFnt,  bg: UI.rowBg  },
                                                  "on-time": { label: "ON TIME",   color: "var(--success)",   bg: "#0d2818" },
                                                  delayed:   { label: fl.delay_mins > 0 ? `+${fl.delay_mins}MIN` : "DELAYED", color: "var(--accent)", bg: "#1a1200" },
                                                  cancelled: { label: "CANCELLED", color: "var(--danger)",   bg: "#1e0d0d" },
                                                  landed:    { label: "LANDED",    color: UI.textMut,  bg: UI.rowBg  },
                                                  diverted:  { label: "DIVERTED",  color: "var(--danger)",   bg: "#1e0d0d" },
                                                }[fl.status] || { label: fl.status?.toUpperCase() || "—", color: UI.textFnt, bg: UI.rowBg };
                                                const depTime = fl.actual_dep || fl.scheduled_dep;
                                                return (
                                                  <div key={fl.id} onClick={() => setMFlight({ mode: "edit", dayId: day.id, flight: fl })}
                                                    style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 12px", background: "#0c1520", border: `1px solid ${fl.status === 'cancelled' || fl.status === 'diverted' ? "#5a1a1a" : fl.status === 'on-time' ? "#1a3a1a" : "#1a3a5a"}`, borderRadius: 8, cursor: "pointer" }}>
                                                    <span style={{ fontFamily: FF3, fontSize: 12, fontWeight: 700, color: "#6a8faf", minWidth: 70, flexShrink: 0 }}>{fl.airline_code}{fl.flight_number}</span>
                                                    <span style={{ fontFamily: FF3, fontSize: 11, color: UI.text }}>{fl.origin} → {fl.destination}</span>
                                                    {depTime && <span style={{ fontFamily: FF3, fontSize: 11, color: UI.textMut }}>{new Date(depTime).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}</span>}
                                                    {fl.gate && <span style={{ fontFamily: FF3, fontSize: 11, color: UI.textFnt }}>Gate {fl.gate}</span>}
                                                    <span style={{ fontFamily: FF3, fontSize: 11, letterSpacing: "1px", color: statusCfg.color, background: statusCfg.bg, padding: "2px 7px", borderRadius: 4, marginLeft: "auto", flexShrink: 0 }}>{statusCfg.label}</span>
                                                    {passengers.length > 0 && <span style={{ fontFamily: FF3, fontSize: 11, color: UI.textFnt, flexShrink: 0 }}>{passengers.map(c => c.name.split(" ")[0]).join(", ")}</span>}
                                                  </div>
                                                );
                                              })}
                                            </div>
                                          )}
                                          <button onClick={() => { loadDayFlights(day.id); setMFlight({ mode: "add", dayId: day.id }); }}
                                            style={{ fontFamily: FF3, fontSize: 11, color: "#6a8faf", background: "none", border: "1px solid #1a3a5a", borderRadius: 4, padding: "5px 12px", cursor: "pointer", alignSelf: "flex-start" }}>
                                            + Add Flight
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })()}

                                {/* Ground Transport — collapsible */}
                                {(() => {
                                  const transportCount  = (dayTransport[day.id] || []).length;
                                  const transportOpen   = daySections[day.id]?.transport ?? (transportCount > 0);
                                  const toggleTransport = () => {
                                    if (!dayTransport[day.id]) loadDayTransport(day.id);
                                    setDaySections(p => ({ ...p, [day.id]: { ...p[day.id], transport: !transportOpen } }));
                                  };
                                  return (
                                    <div style={{ border: `1px solid ${UI.panelBdr}`, borderRadius: 8, overflow: "hidden" }}>
                                      {/* Toggle header */}
                                      <button onClick={toggleTransport} style={{ width: "100%", display: "flex", alignItems: "center", gap: 8, padding: "9px 12px", background: UI.rowBg, border: "none", cursor: "pointer", textAlign: "left" }}>
                                        <span style={{ fontFamily: FF3, fontSize: 11, letterSpacing: 1.5, color: transportOpen ? UI.green : UI.textMut, textTransform: "uppercase", flex: 1 }}>Ground Transport</span>
                                        {transportCount > 0 && (
                                          <span style={{ fontFamily: FF3, fontSize: 11, color: UI.green, background: UI.ckOnBg, border: `1px solid ${UI.ckOnBdr}`, borderRadius: 8, padding: "1px 7px" }}>{transportCount}</span>
                                        )}
                                        <span style={{ fontFamily: FF3, fontSize: 11, color: UI.textMut, lineHeight: 1 }}>{transportOpen ? "−" : "+"}</span>
                                      </button>
                                      {/* Expanded content */}
                                      {transportOpen && (
                                        <div style={{ padding: "10px 12px", borderTop: `1px solid ${UI.panelBdr}`, display: "flex", flexDirection: "column", gap: 8 }}>
                                          {!dayTransport[day.id] && (() => { loadDayTransport(day.id); return null; })()}
                                          {transportCount === 0 ? (
                                            <div style={{ fontFamily: FF3, fontSize: 11, color: UI.textVFnt }}>No ground transport added yet.</div>
                                          ) : (
                                            <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                                              {(dayTransport[day.id] || []).map(tr => {
                                                const trIconSvg = tr.type === "train" ? (
                                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                                    <rect x="4" y="3" width="16" height="14" rx="3"/>
                                                    <path d="M4 11h16"/>
                                                    <path d="M8 17l-2 4M16 17l2 4M8 17h8"/>
                                                    <circle cx="8.5" cy="7.5" r="1"/>
                                                    <circle cx="15.5" cy="7.5" r="1"/>
                                                  </svg>
                                                ) : tr.type === "rideshare" ? (
                                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M5 17H3a2 2 0 01-2-2V9a2 2 0 012-2h13l4 4v4a2 2 0 01-2 2h-2"/>
                                                    <circle cx="8.5" cy="17.5" r="2.5"/>
                                                    <circle cx="17.5" cy="17.5" r="2.5"/>
                                                    <path d="M12 7v4"/>
                                                  </svg>
                                                ) : (
                                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M5 17H3a2 2 0 01-2-2V9a2 2 0 012-2h13l4 4v4a2 2 0 01-2 2h-2"/>
                                                    <circle cx="8.5" cy="17.5" r="2.5"/>
                                                    <circle cx="17.5" cy="17.5" r="2.5"/>
                                                  </svg>
                                                );
                                                return (
                                                  <div key={tr.id} onClick={() => setMTransport({ mode: "edit", dayId: day.id, transport: tr })}
                                                    style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 12px", background: UI.rowBg, border: `1px solid ${UI.panelBdr}`, borderRadius: 8, cursor: "pointer" }}>
                                                    <span style={{ flexShrink: 0, color: UI.textMut, display: "flex", alignItems: "center" }}>{trIconSvg}</span>
                                                    <div style={{ flex: 1, minWidth: 0 }}>
                                                      <div style={{ fontFamily: FF3, fontSize: 11, fontWeight: 600, color: UI.text }}>
                                                        {tr.pickup_location && tr.dropoff_location
                                                          ? `${tr.pickup_location} → ${tr.dropoff_location}`
                                                          : tr.pickup_location || tr.dropoff_location || TRANSPORT_TYPE_LABELS[tr.type]}
                                                      </div>
                                                      <div style={{ fontFamily: FF3, fontSize: 11, color: UI.textMut, marginTop: 2, display: "flex", gap: 10 }}>
                                                        {tr.pickup_time && <span>{tr.pickup_time}</span>}
                                                        {tr.company && <span>{tr.company}</span>}
                                                        {tr.driver_name && <span>{tr.driver_name}</span>}
                                                        {tr.confirmation_number && <span style={{ color: UI.textFnt }}>#{tr.confirmation_number}</span>}
                                                        {tr.passenger_notes && <span style={{ color: UI.textFnt }}>{tr.passenger_notes}</span>}
                                                      </div>
                                                    </div>
                                                    <span style={{ fontFamily: FF3, fontSize: 11, letterSpacing: "1px", color: "var(--success)", background: UI.ckOnBg, padding: "2px 7px", borderRadius: 4, flexShrink: 0, textTransform: "uppercase" }}>{TRANSPORT_TYPE_LABELS[tr.type]}</span>
                                                  </div>
                                                );
                                              })}
                                            </div>
                                          )}
                                          <button onClick={() => { loadDayTransport(day.id); setMTransport({ mode: "add", dayId: day.id }); }}
                                            style={{ fontFamily: FF3, fontSize: 11, color: UI.green, background: "none", border: `1px solid ${UI.ckOnBdr}`, borderRadius: 4, padding: "5px 12px", cursor: "pointer", alignSelf: "flex-start" }}>
                                            + Add Transport
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })()}

                                {/* ── Local Discovery ── */}
                                {(() => {
                                  const localLocation = (day.type === "show" && v)
                                    ? [v.address, v.city, v.addressState].filter(Boolean).join(", ")
                                    : day.city || null;
                                  if (!localLocation) return null;

                                  const activeCat   = localFilter[day.id]     || "food";
                                  const openFilter  = localOpenFilter[day.id] || "";
                                  const cacheKey    = `${localLocation}|${activeCat}|${openFilter}`;
                                  const ld          = dayLocal[day.id]?.[cacheKey];
                                  const localOpen   = daySections[day.id]?.local ?? false;
                                  const sortMode    = localSort[day.id] || "distance";
                                  const venueId     = day.type === "show" ? show?.venueId : null;
                                  const recCity     = (day.type === "show" && v) ? v.city      : day.city;
                                  const recState    = (day.type === "show" && v) ? v.addressState : null;
                                  const cityKey     = normCityKey(recCity, recState);
                                  const allRecs     = cityRecs[cityKey]?.data || [];
                                  const isAddingRec = localRec?.dayId === day.id;
                                  const isEmergencyCat = activeCat === "hospital" || activeCat === "urgent";

                                  // Community recs: filter by active category
                                  const communityRecs = allRecs.filter(r => (r.cat || r.category) === activeCat);

                                  const toggleLocal = () => {
                                    const next = !localOpen;
                                    setDaySections(p => ({ ...p, [day.id]: { ...p[day.id], local: next } }));
                                    if (next) {
                                      if (!ld) loadDayLocal(day.id, localLocation, activeCat, openFilter, day.date);
                                      loadCommunityRecs(recCity, recState);
                                    }
                                  };

                                  const setCat = (cat) => {
                                    setLocalFilter(p => ({ ...p, [day.id]: cat }));
                                    setLocalShowMore(p => ({ ...p, [day.id]: false }));
                                    const key = `${cat}|${openFilter}`;
                                    if (!dayLocal[day.id]?.[key]?.status) {
                                      loadDayLocal(day.id, localLocation, cat, openFilter, day.date);
                                    }
                                  };

                                  // Toggle open-now / open-late filter (clicking active one turns it off)
                                  const toggleOpenFilter = (mode) => {
                                    const next = openFilter === mode ? "" : mode;
                                    setLocalOpenFilter(p => ({ ...p, [day.id]: next }));
                                    setLocalShowMore(p => ({ ...p, [day.id]: false }));
                                    const key = `${activeCat}|${next}`;
                                    if (!dayLocal[day.id]?.[key]?.status) {
                                      loadDayLocal(day.id, localLocation, activeCat, next, day.date);
                                    }
                                  };

                                  const fmtDist = (m) => m ? `${(m / 1609.34).toFixed(1)} mi` : "";
                                  const stars   = (r) => "★".repeat(Math.round(r || 0)) + "☆".repeat(5 - Math.round(r || 0));

                                  // Sort client-side
                                  const rawResults = ld?.results || [];
                                  const results = [...rawResults].sort((a, b) =>
                                    sortMode === "rating" ? (b.rating - a.rating) : (a.distance - b.distance)
                                  );
                                  const totalCount = results.length + communityRecs.length;
                                  const catLabel = LOCAL_CATS.find(c => c.id === activeCat)?.label || "Spots";

                                  return (
                                    <div style={{ border: `1px solid ${UI.panelBdr}`, borderRadius: 8, overflow: "hidden" }}>
                                      {/* Header */}
                                      <button onClick={toggleLocal} style={{ width: "100%", display: "flex", alignItems: "center", gap: 8, padding: "9px 12px", background: UI.rowBg, border: "none", cursor: "pointer", textAlign: "left" }}>
                                        <span style={{ fontFamily: FF3, fontSize: 11, letterSpacing: 1.5, color: localOpen ? "var(--success)" : UI.textMut, textTransform: "uppercase", flex: 1 }}>Local Discovery</span>
                                        {totalCount > 0 && (
                                          <span style={{ fontFamily: FF3, fontSize: 11, color: "var(--success)", background: "#0a1a0a", border: "1px solid #1e3a1e", borderRadius: 8, padding: "1px 7px" }}>
                                            {totalCount} spot{totalCount !== 1 ? "s" : ""}
                                          </span>
                                        )}
                                        <span style={{ fontFamily: FF3, fontSize: 11, color: UI.textMut, lineHeight: 1 }}>{localOpen ? "−" : "+"}</span>
                                      </button>

                                      {localOpen && (
                                        <div style={{ padding: "10px 12px", borderTop: `1px solid ${UI.panelBdr}`, display: "flex", flexDirection: "column", gap: 10 }}>

                                          {/* Category chips — row 1: Food & Drink */}
                                          <div style={{ display: "flex", flexWrap: "wrap", gap: 4, alignItems: "center" }}>
                                            <span style={{ fontFamily: FF3, fontSize: 11, letterSpacing: 0.8, color: UI.textVFnt, textTransform: "uppercase", marginRight: 2, whiteSpace: "nowrap" }}>Food & Drink</span>
                                            {LOCAL_CATS.filter(c => c.group === "food").map(cat => {
                                              const active = activeCat === cat.id;
                                              return (
                                                <button key={cat.id} onClick={() => setCat(cat.id)}
                                                  style={{ fontFamily: FF3, fontSize: 11, letterSpacing: 0.5, padding: "3px 9px", borderRadius: 8, cursor: "pointer", lineHeight: 1.4, whiteSpace: "nowrap",
                                                    border: `1px solid ${active ? "#1e3a1e" : UI.panelBdr}`,
                                                    background: active ? "#0a1a0a" : "none",
                                                    color: active ? "var(--success)" : UI.textMut }}>
                                                  {cat.label}
                                                </button>
                                              );
                                            })}
                                          </div>

                                          {/* Category chips — row 2: Specialty */}
                                          <div style={{ display: "flex", flexWrap: "wrap", gap: 4, alignItems: "center" }}>
                                            <span style={{ fontFamily: FF3, fontSize: 11, letterSpacing: 0.8, color: UI.textVFnt, textTransform: "uppercase", marginRight: 2, whiteSpace: "nowrap" }}>Specialty</span>
                                            {LOCAL_CATS.filter(c => c.group === "specialty").map(cat => {
                                              const active = activeCat === cat.id;
                                              const color  = REC_CAT_COLORS[cat.id] || "#f0a500";
                                              return (
                                                <button key={cat.id} onClick={() => setCat(cat.id)}
                                                  style={{ fontFamily: FF3, fontSize: 11, letterSpacing: 0.5, padding: "3px 9px", borderRadius: 8, cursor: "pointer", lineHeight: 1.4, whiteSpace: "nowrap",
                                                    border: `1px solid ${active ? `${color}60` : UI.panelBdr}`,
                                                    background: active ? `${color}18` : "none",
                                                    color: active ? color : UI.textMut }}>
                                                  {cat.label}
                                                </button>
                                              );
                                            })}
                                          </div>

                                          {/* Category chips — row 3: Health & Emergency */}
                                          <div style={{ display: "flex", flexWrap: "wrap", gap: 4, alignItems: "center" }}>
                                            <span style={{ fontFamily: FF3, fontSize: 11, letterSpacing: 0.8, color: UI.textVFnt, textTransform: "uppercase", marginRight: 2, whiteSpace: "nowrap" }}>Health</span>
                                            {LOCAL_CATS.filter(c => c.group === "health").map(cat => {
                                              const active = activeCat === cat.id;
                                              const isEmg  = cat.id === "hospital" || cat.id === "urgent";
                                              const activeColor = isEmg ? "var(--danger)" : "#6a9fcf";
                                              const activeBg    = isEmg ? "#1e0808"  : "#081828";
                                              const activeBdr   = isEmg ? "#5a1a1a"  : "#1a3a5a";
                                              return (
                                                <button key={cat.id} onClick={() => setCat(cat.id)}
                                                  style={{ fontFamily: FF3, fontSize: 11, letterSpacing: 0.5, padding: "3px 9px", borderRadius: 8, cursor: "pointer", lineHeight: 1.4, whiteSpace: "nowrap",
                                                    border: `1px solid ${active ? activeBdr : UI.panelBdr}`,
                                                    background: active ? activeBg : "none",
                                                    color: active ? activeColor : UI.textMut }}>
                                                  {cat.label}
                                                </button>
                                              );
                                            })}
                                          </div>

                                          {/* Hours filter — only for food/bar/coffee categories */}
                                          {["food","bars","coffee","catering"].includes(activeCat) && (
                                            <div style={{ display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap" }}>
                                              {[
                                                { mode: "now",  label: "Open Now",   hint: "Open right now" },
                                                { mode: "late", label: "Open Late",  hint: "Closes 10 pm or later" },
                                              ].map(({ mode, label, hint }) => {
                                                const active = openFilter === mode;
                                                return (
                                                  <button key={mode} onClick={() => toggleOpenFilter(mode)}
                                                    style={{ fontFamily: FF3, fontSize: 11, letterSpacing: 0.5, padding: "3px 10px", borderRadius: 8, cursor: "pointer", lineHeight: 1.4,
                                                      border: `1px solid ${active ? "var(--accent)" : UI.panelBdr}`,
                                                      background: active ? "#1a1000" : "none",
                                                      color: active ? "var(--accent)" : UI.textVFnt }}>
                                                    {active ? "● " : "○ "}{label}
                                                  </button>
                                                );
                                              })}
                                              {openFilter === "late" && (
                                                <span style={{ fontFamily: FF3, fontSize: 11, color: UI.textVFnt }}>
                                                  {ld?.status === "loading" ? "Fetching hours…" : "Closes 10 pm or later"}
                                                </span>
                                              )}
                                            </div>
                                          )}

                                          {/* Community recs (filtered to active cat) */}
                                          {communityRecs.length > 0 && (() => {
                                            // Sort: top picks (most saves) first, then recency
                                            const sortedRecs = [...communityRecs].sort((a, b) => (recSaveCounts[b.id] || 0) - (recSaveCounts[a.id] || 0));
                                            const topPickThreshold = 2;
                                            return (
                                              <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                                                <div style={{ fontFamily: FF3, fontSize: 11, letterSpacing: 1.2, color: UI.textMut, textTransform: "uppercase", marginBottom: 1 }}>★ Community picks · {communityRecs.length}</div>
                                                <div style={{ borderRadius: 8, overflow: "hidden", border: `1px solid ${UI.panelBdr}` }}>
                                                {sortedRecs.map((rec, ri) => {
                                                  const isOwner   = user?.id && rec.user_id === user.id;
                                                  const isSaved   = mySavedRecs.has(rec.id);
                                                  const saveCount = recSaveCounts[rec.id] || 0;
                                                  const isTopPick = saveCount >= topPickThreshold;
                                                  const addedBy   = rec.added_by_email ? rec.added_by_email.split("@")[0] : null;
                                                  const mapsSearch = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([rec.name, recCity, recState].filter(Boolean).join(", "))}`;
                                                  const recCat   = rec.category || rec.cat || "other";
                                                  const recColor = REC_CAT_COLORS[recCat] || "#f0a500";
                                                  return (
                                                    <div key={rec.id} style={{
                                                      padding: "8px 10px 8px 12px",
                                                      background: isTopPick ? `${recColor}18` : `${recColor}0f`,
                                                      borderTop: ri > 0 ? `1px solid ${recColor}28` : "none",
                                                      borderLeft: `3px solid ${isTopPick ? recColor : recColor + "88"}`,
                                                      display: "flex", gap: 8, alignItems: "flex-start",
                                                    }}>
                                                      <div style={{ flex: 1, minWidth: 0 }}>
                                                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                                          <button onClick={() => openPlaceInfo(rec)}
                                                            style={{ fontFamily: FF3, fontSize: 12, fontWeight: 600, color: UI.text, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", background: "none", border: "none", cursor: "pointer", padding: 0, textAlign: "left" }}>
                                                            {rec.name} ›
                                                          </button>
                                                          {isTopPick && <span style={{ fontFamily: FF3, fontSize: 11, color: recColor, background: `${recColor}22`, border: `1px solid ${recColor}44`, borderRadius: 4, padding: "1px 5px", letterSpacing: 0.5, flexShrink: 0 }}>TOP PICK</span>}
                                                          <span style={{ fontFamily: FF3, fontSize: 11, color: recColor, flexShrink: 0, letterSpacing: 0.5, textTransform: "uppercase" }}>{recCat}</span>
                                                        </div>
                                                        {rec.note && <div style={{ fontFamily: FF3, fontSize: 11, color: UI.textFnt, marginTop: 2, lineHeight: 1.4 }}>{rec.note}</div>}
                                                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 3 }}>
                                                          {addedBy && <span style={{ fontFamily: FF3, fontSize: 11, color: UI.textVFnt }}>by {addedBy}</span>}
                                                          {saveCount > 0 && <span style={{ fontFamily: FF3, fontSize: 11, color: isSaved ? recColor : UI.textVFnt }}>{saveCount} saved</span>}
                                                        </div>
                                                      </div>
                                                      <div style={{ display: "flex", gap: 4, flexShrink: 0, alignItems: "flex-start", marginTop: 1 }}>
                                                        {/* Save / unsave button */}
                                                        <button
                                                          onClick={(e) => { e.stopPropagation(); toggleRecSave(rec); }}
                                                          title={isSaved ? "Remove from saved" : "Save this rec"}
                                                          style={{ background: "none", border: "none", cursor: "pointer", padding: 2, color: isSaved ? recColor : UI.textVFnt, transition: "color 0.15s" }}>
                                                          <svg width="13" height="13" viewBox="0 0 24 24" fill={isSaved ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                            <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/>
                                                          </svg>
                                                        </button>
                                                        {isOwner && (
                                                          <button onClick={(e) => { e.stopPropagation(); removeCommunityRec(rec.id, recCity, recState); }}
                                                            style={{ background: "none", border: "none", color: UI.textVFnt, cursor: "pointer", fontSize: 11, padding: 2, lineHeight: 1 }}>✕</button>
                                                        )}
                                                      </div>
                                                    </div>
                                                  );
                                                })}
                                                </div>
                                              </div>
                                            );
                                          })()}

                                          {/* Results header: context label + sort toggle */}
                                          {ld?.status === "ready" && results.length > 0 && (
                                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                              <span style={{ fontFamily: FF3, fontSize: 11, color: UI.textFnt, flex: 1 }}>
                                                {results.length} {catLabel.toLowerCase()} near {(day.type === "show" && v?.city) ? v.city : (day.city || localLocation)}
                                              </span>
                                              <div style={{ display: "flex", gap: 2 }}>
                                                {["distance","rating"].map(s => (
                                                  <button key={s} onClick={() => setLocalSort(p => ({ ...p, [day.id]: s }))}
                                                    style={{ fontFamily: FF3, fontSize: 11, letterSpacing: 0.5, padding: "2px 7px", borderRadius: 4, cursor: "pointer",
                                                      border: `1px solid ${sortMode === s ? UI.textFnt : UI.panelBdr}`,
                                                      background: sortMode === s ? UI.rowBg : "none",
                                                      color: sortMode === s ? UI.text : UI.textVFnt,
                                                      textTransform: "capitalize" }}>
                                                    {s}
                                                  </button>
                                                ))}
                                              </div>
                                            </div>
                                          )}

                                          {/* Google Places results */}
                                          {ld?.status === "loading" && (
                                            <div style={{ fontFamily: FF3, fontSize: 11, color: UI.textFnt, padding: "8px 0" }}>Loading spots…</div>
                                          )}
                                          {ld?.status === "error" && (
                                            <div style={{ fontFamily: FF3, fontSize: 11, color: "var(--danger)" }}>{ld.errorMsg || "Failed to load"} — <button onClick={() => refreshDayLocal(day.id, localLocation, activeCat, openFilter, day.date)} style={{ background: "none", border: "none", color: UI.textFnt, cursor: "pointer", fontSize: 11, padding: 0, textDecoration: "underline" }}>Retry</button></div>
                                          )}
                                          {ld?.status === "ready" && results.length === 0 && (
                                            <div style={{ fontFamily: FF3, fontSize: 11, color: UI.textFnt }}>No results found nearby.</div>
                                          )}
                                          {ld?.status === "ready" && results.length > 0 && (() => {
                                            const PAGE = 10;
                                            const showMore   = localShowMore[day.id] || false;
                                            const visible    = showMore ? results : results.slice(0, PAGE);
                                            const hasMore    = results.length > PAGE && !showMore;
                                            const fmtClose = (t) => {
                                              if (!t) return null;
                                              const h = parseInt(t.slice(0, 2), 10);
                                              const m = t.slice(2);
                                              const suffix = h >= 12 ? "pm" : "am";
                                              const h12 = h > 12 ? h - 12 : h === 0 ? 12 : h;
                                              return m === "00" ? `${h12} ${suffix}` : `${h12}:${m} ${suffix}`;
                                            };
                                            return (
                                              <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                                                {visible.map(biz => {
                                                  const emgCard    = isEmergencyCat;
                                                  const closeLabel = openFilter === "late" && biz.closingTime ? fmtClose(biz.closingTime) : null;
                                                  return (
                                                    <a key={biz.id} href={biz.url} target="_blank" rel="noreferrer"
                                                      style={{ padding: "8px 10px", borderRadius: 8, textDecoration: "none", display: "flex", flexDirection: "column", gap: 3,
                                                        background: emgCard ? "#1a0808" : UI.rowBg,
                                                        border: `1px solid ${emgCard ? "#4a1a1a" : UI.panelBdr}` }}>
                                                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                                        <span style={{ fontFamily: FF3, fontSize: 12, fontWeight: 600, color: biz.is_closed ? UI.textFnt : (emgCard ? "#e08080" : UI.text), flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{biz.name}</span>
                                                        {biz.price && <span style={{ fontFamily: FF3, fontSize: 11, color: UI.green, flexShrink: 0 }}>{biz.price}</span>}
                                                        {closeLabel && <span style={{ fontFamily: FF3, fontSize: 11, color: "var(--success)", letterSpacing: 0.3, flexShrink: 0 }}>until {closeLabel}</span>}
                                                        {biz.is_closed && <span style={{ fontFamily: FF3, fontSize: 11, color: "var(--danger)", letterSpacing: 0.5, flexShrink: 0 }}>CLOSED</span>}
                                                      </div>
                                                      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                                                        {biz.rating > 0 && <span style={{ fontFamily: FF3, fontSize: 11, color: "var(--accent)", letterSpacing: -1 }}>{stars(biz.rating)} <span style={{ letterSpacing: 0, color: UI.textMut }}>{biz.rating}</span></span>}
                                                        {biz.categories?.length > 0 && <span style={{ fontFamily: FF3, fontSize: 11, color: emgCard ? "#c06060" : UI.textFnt }}>{biz.categories.slice(0, 2).join(" · ")}</span>}
                                                        {biz.distance > 0 && <span style={{ fontFamily: FF3, fontSize: 11, color: UI.textVFnt, marginLeft: "auto" }}>{fmtDist(biz.distance)}</span>}
                                                      </div>
                                                      {biz.address && <div style={{ fontFamily: FF3, fontSize: 11, color: UI.textVFnt }}>{biz.address}</div>}
                                                    </a>
                                                  );
                                                })}
                                                {hasMore && (
                                                  <button onClick={() => setLocalShowMore(p => ({ ...p, [day.id]: true }))}
                                                    style={{ fontFamily: FF3, fontSize: 11, color: UI.textFnt, background: "none", border: `1px solid ${UI.panelBdr}`, borderRadius: 4, padding: "6px 0", cursor: "pointer", textAlign: "center" }}>
                                                    Show {results.length - PAGE} more…
                                                  </button>
                                                )}
                                                {showMore && results.length > PAGE && (
                                                  <button onClick={() => setLocalShowMore(p => ({ ...p, [day.id]: false }))}
                                                    style={{ fontFamily: FF3, fontSize: 11, color: UI.textVFnt, background: "none", border: "none", cursor: "pointer", textAlign: "center", padding: "4px 0" }}>
                                                    Show less
                                                  </button>
                                                )}
                                              </div>
                                            );
                                          })()}

                                          {/* Cache timestamp + refresh */}
                                          {ld?.cachedAt && (
                                            <div style={{ fontFamily: FF3, fontSize: 11, color: UI.textVFnt, display: "flex", gap: 8, alignItems: "center" }}>
                                              <span>Updated {new Date(ld.cachedAt).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}</span>
                                              <button onClick={() => refreshDayLocal(day.id, localLocation, activeCat, openFilter, day.date)} style={{ background: "none", border: "none", color: UI.textFnt, cursor: "pointer", fontSize: 11, padding: 0, textDecoration: "underline" }}>Refresh</button>
                                            </div>
                                          )}

                                          {/* Add community rec */}
                                          {isAddingRec ? (
                                            <LocalRecForm
                                              onSave={(rec) => {
                                                addCommunityRec(rec, venueId, recCity, recState);
                                                setLocalRec(null);
                                              }}
                                              onCancel={() => setLocalRec(null)}
                                              UI={UI} FF={FF3}
                                              location={localLocation}
                                            />
                                          ) : recCity ? (
                                            <button onClick={() => setLocalRec({ dayId: day.id, venueId })}
                                              style={{ fontFamily: FF3, fontSize: 11, color: "var(--success)", background: "none", border: "1px solid #1e3a1e", borderRadius: 4, padding: "5px 12px", cursor: "pointer", alignSelf: "flex-start" }}>
                                              + Share a recommendation
                                            </button>
                                          ) : null}

                                        </div>
                                      )}
                                    </div>
                                  );
                                })()}

                                {/* Notes */}
                                <div>
                                  <div style={{ fontFamily: FF3, fontSize: 11, letterSpacing: 1.5, color: UI.textFnt, marginBottom: 6 }}>NOTES</div>
                                  <textarea rows={2} style={{ width: "100%", boxSizing: "border-box", background: UI.rowBg, border: `1px solid ${UI.panelBdr}`, borderRadius: 4, padding: "6px 10px", color: UI.text, fontFamily: FF3, fontSize: 12, resize: "vertical" }}
                                    value={day.notes} placeholder="Notes for this day…"
                                    onChange={e => setTourDays(p => p.map(d => d.id === day.id ? { ...d, notes: e.target.value } : d))}
                                    onBlur={e => saveTourDay(day.id, { notes: e.target.value })} />
                                </div>

                                {/* DS-1: Day Sheet export strip */}
                                {(() => {
                                  const FF5 = "'Helvetica Neue', Helvetica, Arial, sans-serif";
                                  const token = day.day_sheet_token;
                                  const appUrl = window.location.origin;
                                  const isInternal = dsInternalFlags[day.id] !== false; // default internal
                                  const sheetUrl = token ? `${appUrl}/api/day-sheet?token=${token}${isInternal ? "&internal=1" : ""}` : null;
                                  const isGenerating = dsGenerating === day.id;
                                  const isCopied = dsLinkCopied === day.id;
                                  return (
                                    <div style={{ borderTop: `1px solid ${UI.rowHov}`, paddingTop: 12, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                                      <span style={{ fontFamily: FF5, fontSize: 11, letterSpacing: "1.5px", textTransform: "uppercase", color: UI.textFnt, flexShrink: 0 }}>Day Sheet</span>
                                      {/* Internal / External toggle */}
                                      <div style={{ display: "flex", gap: 0, borderRadius: 4, overflow: "hidden", border: `1px solid ${UI.panelBdr}`, flexShrink: 0 }}>
                                        {[["Internal", true], ["External", false]].map(([label, val]) => (
                                          <button key={label} onClick={() => setDsInternalFlags(p => ({ ...p, [day.id]: val }))}
                                            style={{ fontFamily: FF5, fontSize: 11, padding: "3px 10px", cursor: "pointer", background: isInternal === val ? UI.rowBg : "none", color: isInternal === val ? UI.text : UI.textVFnt, border: "none", borderRight: label === "Internal" ? `1px solid ${UI.panelBdr}` : "none" }}>
                                            {label}
                                          </button>
                                        ))}
                                      </div>
                                      {/* Share link */}
                                      <button
                                        onClick={async () => {
                                          let t = token;
                                          if (!t) t = await generateDaySheetToken(day.id);
                                          if (!t) return;
                                          const url = `${appUrl}/api/day-sheet?token=${t}${isInternal ? "&internal=1" : ""}`;
                                          navigator.clipboard.writeText(url).then(() => {
                                            setDsLinkCopied(day.id);
                                            setTimeout(() => setDsLinkCopied(null), 2000);
                                          });
                                        }}
                                        disabled={isGenerating}
                                        style={{ fontFamily: FF5, fontSize: 11, lineHeight: "14px", color: isCopied ? UI.green : UI.textMut, background: "none", border: `1px solid ${isCopied ? UI.green : UI.panelBdr}`, borderRadius: 4, padding: "3px 10px", cursor: isGenerating ? "default" : "pointer", display: "flex", alignItems: "center", gap: 5, flexShrink: 0 }}>
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ width: 10, height: 10 }}><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>
                                        {isGenerating ? "Generating…" : isCopied ? "Copied!" : "Copy Link"}
                                      </button>
                                      {/* PDF */}
                                      <button
                                        onClick={async () => {
                                          let t = token;
                                          if (!t) t = await generateDaySheetToken(day.id);
                                          if (!t) return;
                                          window.open(`${appUrl}/api/day-sheet?token=${t}&format=pdf${isInternal ? "&internal=1" : ""}`, "_blank");
                                        }}
                                        disabled={isGenerating}
                                        style={{ fontFamily: FF5, fontSize: 11, lineHeight: "14px", color: UI.textMut, background: "none", border: `1px solid ${UI.panelBdr}`, borderRadius: 4, padding: "3px 10px", cursor: isGenerating ? "default" : "pointer", display: "flex", alignItems: "center", gap: 5, flexShrink: 0 }}>
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ width: 10, height: 10, flexShrink: 0 }}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                                        PDF
                                      </button>
                                      {/* DOCX — button instead of <a> so sizing matches exactly */}
                                      <button
                                        onClick={async () => {
                                          let t = token;
                                          if (!t) t = await generateDaySheetToken(day.id);
                                          if (!t) return;
                                          window.open(`${appUrl}/api/day-sheet?token=${t}&format=docx${isInternal ? "&internal=1" : ""}`, "_blank");
                                        }}
                                        disabled={isGenerating}
                                        style={{ fontFamily: FF5, fontSize: 11, lineHeight: "14px", color: UI.textMut, background: "none", border: `1px solid ${UI.panelBdr}`, borderRadius: 4, padding: "3px 10px", cursor: isGenerating ? "default" : "pointer", display: "flex", alignItems: "center", gap: 5, flexShrink: 0 }}>
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ width: 10, height: 10, flexShrink: 0 }}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
                                        Word
                                      </button>
                                    </div>
                                  );
                                })()}

                                {/* SC-1: Day Schedule / Calendar */}
                                {(() => {
                                  const manualItems = dayScheduleItems.filter(i => i.tour_day_id === day.id);
                                  // TM sees all items; show ROS preview strip from advance for context
                                  const advSch  = show?.advanceTmData?.schedule || {};
                                  const advBill = Array.isArray(advSch.bill) ? advSch.bill : [];
                                  // Quick count of advance time fields that haven't been imported yet
                                  const importedKeys = new Set(manualItems.filter(i => i.source_key).map(i => i.source_key));
                                  const hasUnimported = show && (() => {
                                    const check = (key, val) => val && !importedKeys.has(key);
                                    const advBus = show.advanceTmData?.bus || {};
                                    if (check("adv_schedule_venue_access", advSch.venue_access)) return true;
                                    if (check("adv_schedule_staging_build", advSch.staging_build)) return true;
                                    if (check("adv_schedule_doors_public", advSch.doors_public)) return true;
                                    if (check("adv_schedule_curfew", advSch.curfew)) return true;
                                    if (check("adv_bus_bus_arrival", advBus.bus_arrival)) return true;
                                    if (check("adv_bus_catering", advBus.catering_time)) return true;
                                    return advBill.some((act, i) =>
                                      (act.set && !importedKeys.has(`adv_bill_${i}_set`)) ||
                                      (act.load && !importedKeys.has(`adv_bill_${i}_load`))
                                    );
                                  })();
                                  const allItems = manualItems.sort((a, b) => a.start_minutes - b.start_minutes);
                                  const tourPartyForDay = profiles.filter(p => p.current_tour_id === selT);
                                  const isImporting = importingAdvSched === day.id;
                                  return (
                                    <div>
                                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                                        <div style={{ fontFamily: FF3, fontSize: 11, letterSpacing: 1.5, color: UI.textFnt }}>DAY SCHEDULE</div>
                                        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                                          {show && (
                                            <button
                                              onClick={() => importAdvanceToSchedule(day.id, show, day)}
                                              disabled={isImporting}
                                              style={{ fontFamily: FF3, fontSize: 11, color: hasUnimported ? UI.amber : UI.textMut, background: "none", border: `1px solid ${hasUnimported ? UI.amdBdr : UI.panelBdr}`, borderRadius: 4, padding: "3px 10px", cursor: isImporting ? "default" : "pointer", opacity: isImporting ? 0.5 : 1, display: "flex", alignItems: "center", gap: 5 }}>
                                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ width: 10, height: 10 }}><path d="M12 3v12m0 0l-4-4m4 4l4-4M3 17v2a2 2 0 002 2h14a2 2 0 002-2v-2"/></svg>
                                              {isImporting ? "Importing…" : "Import from Advance"}
                                            </button>
                                          )}
                                          <button
                                            onClick={() => setMSchedItem({ mode: "add", dayId: day.id })}
                                            style={{ fontFamily: FF3, fontSize: 11, color: UI.amber, background: "none", border: `1px solid ${UI.amdBdr}`, borderRadius: 4, padding: "3px 10px", cursor: "pointer" }}>
                                            + Add Event
                                          </button>
                                        </div>
                                      </div>
                                      {allItems.length === 0 ? (
                                        <div style={{ fontFamily: FF3, fontSize: 11, color: UI.textVFnt, padding: "10px 0" }}>
                                          {show && hasUnimported
                                            ? "Advance has times ready to import — click \"Import from Advance\" to populate the schedule."
                                            : "No events yet. Add custom items or import times from the advance."}
                                        </div>
                                      ) : (
                                        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                                          {allItems.map(item => {
                                            const typeInfo = SCHED_TYPES.find(t => t.id === item.type) || { color: "#6a8faf", label: item.type };
                                            const assignedPeople = tourPartyForDay.filter(p => (item.assigned_profile_ids || []).includes(p.id));
                                            const isImported = !!item.source_key;
                                            return (
                                              <div key={item.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "7px 10px", background: UI.rowBg, borderRadius: 8, border: `1px solid ${isImported ? UI.amdBdr + "44" : UI.panelBdr}` }}>
                                                {/* Time */}
                                                <div style={{ fontFamily: FF3, fontSize: 11, color: UI.textMut, width: 56, flexShrink: 0, textAlign: "left" }}>{fmtMins(item.start_minutes)}</div>
                                                {/* Duration — always rendered so title starts at a consistent position */}
                                                <div style={{ fontFamily: FF3, fontSize: 11, color: UI.textVFnt, width: 30, flexShrink: 0, textAlign: "left" }}>
                                                  {item.duration_minutes > 0 ? (item.duration_minutes < 60 ? `${item.duration_minutes}m` : `${Math.floor(item.duration_minutes / 60)}h${item.duration_minutes % 60 ? ` ${item.duration_minutes % 60}m` : ""}`) : ""}
                                                </div>
                                                {/* Title */}
                                                <div style={{ flex: 1, fontFamily: FF3, fontSize: 12, fontWeight: 500, color: UI.text, textAlign: "left" }}>{item.title}</div>
                                                {/* Type badge */}
                                                <span style={{ fontFamily: FF3, fontSize: 11, letterSpacing: 0.5, color: typeInfo.color, background: `${typeInfo.color}18`, padding: "2px 6px", borderRadius: 4, flexShrink: 0 }}>{typeInfo.label}</span>
                                                {/* Assigned people avatars */}
                                                {assignedPeople.length > 0 && (
                                                  <div style={{ display: "flex", flexShrink: 0 }}>
                                                    {assignedPeople.slice(0, 4).map(p => (
                                                      <div key={p.id} title={p.name} style={{ width: 18, height: 18, borderRadius: "50%", background: UI.panelAlt, border: `1px solid ${UI.bg}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: UI.textFnt, overflow: "hidden", marginLeft: -4 }}>
                                                        {p.photo_url ? <img src={p.photo_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : (p.name || "?")[0].toUpperCase()}
                                                      </div>
                                                    ))}
                                                    {assignedPeople.length > 4 && <div style={{ width: 18, height: 18, borderRadius: "50%", background: UI.panelAlt, border: `1px solid ${UI.bg}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, color: UI.textVFnt, marginLeft: -4 }}>+{assignedPeople.length - 4}</div>}
                                                  </div>
                                                )}
                                                {/* Edit / delete */}
                                                <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
                                                  <button onClick={() => setMSchedItem({ mode: "edit", dayId: day.id, item })}
                                                    style={{ background: "none", border: "none", color: UI.textMut, cursor: "pointer", fontSize: 11, padding: "0 2px" }} title="Edit">✎</button>
                                                  <button onClick={() => deleteDayScheduleItem(item.id)}
                                                    style={{ background: "none", border: "none", color: UI.textMut, cursor: "pointer", fontSize: 11, padding: "0 2px", opacity: 0.5 }} title="Delete">✕</button>
                                                </div>
                                              </div>
                                            );
                                          })}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })()}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* ── FINANCIALS MODE ── */}
              {isFinMode && (() => {
                const FF2 = "'Helvetica Neue', Helvetica, Arial, sans-serif";
                const todayStr = new Date().toISOString().slice(0, 10);

                // ── Income data derived from existing show.settlement fields ──
                const incomeRows = tour.shows.map(s => {
                  const gRaw = (s.settlement?.guarantee || "").replace(/[^0-9.]/g, "");
                  const dRaw = (s.settlement?.deposit   || "").replace(/[^0-9.]/g, "");
                  const g    = parseFloat(gRaw) || 0;
                  const d    = parseFloat(dRaw) || 0;
                  const v    = venues[s.venueId];
                  return { show: s, venue: v, guarantee: g, deposit: d };
                });

                const totalGuaranteed  = incomeRows.reduce((a, r) => a + r.guarantee, 0);
                const totalDepositsRx  = incomeRows.filter(r => r.show.settlement?.depositReceived).reduce((a, r) => a + r.deposit, 0);
                const totalSettled     = incomeRows.filter(r => r.show.settlement?.settled).reduce((a, r) => a + r.guarantee, 0);
                const outstandingCount = tour.shows.filter(s => s.date < todayStr && !s.settlement?.settled).length;

                // ── Expense aggregates ──
                const usdExpenses   = tourExpenses.filter(e => e.currency === "USD");
                const totalExpUSD   = usdExpenses.reduce((a, e) => a + Number(e.amount), 0);
                const netUSD        = totalSettled - totalExpUSD;
                // Merch totals — group by currency for multi-currency display
                const merchByCur   = {};
                showMerch.forEach(m => {
                  if (!merchByCur[m.currency]) merchByCur[m.currency] = { gross: 0, venueTake: 0, net: 0 };
                  merchByCur[m.currency].gross     += Number(m.gross);
                  merchByCur[m.currency].venueTake += Number(m.gross) * (Number(m.venue_cut_pct) / 100);
                  merchByCur[m.currency].net       += Number(m.net);
                });
                const totalMerchNetUSD = (merchByCur["USD"]?.net || 0);
                const expByCat     = {};
                tourExpenses.forEach(e => {
                  if (!expByCat[e.category]) expByCat[e.category] = {};
                  if (!expByCat[e.category][e.currency]) expByCat[e.category][e.currency] = 0;
                  expByCat[e.category][e.currency] += Number(e.amount);
                });
                const budByCat = {};
                tourBudget.forEach(b => { budByCat[b.category] = { amount: Number(b.budgeted_amount), currency: b.currency }; });

                const tabStyle = (id) => ({
                  fontFamily: FF2, fontSize: 11, letterSpacing: "0.5px", padding: "8px 16px",
                  border: "none", cursor: "pointer", transition: "all 0.15s",
                  borderBottom: tourFinTab === id ? `2px solid var(--success)` : `2px solid transparent`,
                  background: "none",
                  color: tourFinTab === id ? "var(--success)" : UI.textFnt,
                  fontWeight: tourFinTab === id ? 700 : 400,
                });
                const cardStyle = { background: UI.panelBg, border: `1px solid ${UI.panelBdr}`, borderRadius: 8, padding: "14px 16px" };
                const lblStyle2 = { fontFamily: FF2, fontSize: 11, letterSpacing: 1, color: UI.textVFnt, textTransform: "uppercase", marginBottom: 4 };
                const valStyle  = { fontFamily: FF2, fontSize: 20, fontWeight: 700, color: UI.text, letterSpacing: "-0.5px" };
                const subStyle  = { fontFamily: FF2, fontSize: 11, color: UI.textMut, marginTop: 2 };

                return (
                  <div style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
                    {/* Tab bar */}
                    <div style={{ display: "flex", borderBottom: `1px solid ${UI.panelBdr}`, padding: "0 36px", flexShrink: 0, background: UI.bg }}>
                      {[["overview","Overview"], ["income","Income"], ["expenses","Expenses"], ["budget","Budget"], ["merch","Merch"]].map(([id, label]) => (
                        <button key={id} style={tabStyle(id)} onClick={() => setTourFinTab(id)}>{label}</button>
                      ))}
                    </div>

                    <div style={{ flex: 1, overflowY: "auto", padding: "24px 36px 48px" }}>

                      {/* ── OVERVIEW TAB ── */}
                      {tourFinTab === "overview" && (
                        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                          {/* P&L summary strip */}
                          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12 }}>
                            {[
                              { label: "Gross Contracted", value: fmtMoney(totalGuaranteed), sub: `${tour.shows.length} show${tour.shows.length !== 1 ? "s" : ""}`, color: "var(--success)" },
                              { label: "Deposits Received", value: fmtMoney(totalDepositsRx), sub: `${incomeRows.filter(r => r.show.settlement?.depositReceived).length} of ${incomeRows.filter(r => r.deposit > 0).length} deposits`, color: "var(--accent)" },
                              { label: "Total Settled", value: fmtMoney(totalSettled), sub: outstandingCount > 0 ? `${outstandingCount} outstanding` : "All settled", color: outstandingCount > 0 ? "var(--danger)" : "var(--success)" },
                              { label: "Expenses (USD)", value: fmtMoney(totalExpUSD), sub: `${tourExpenses.filter(e => e.currency === "USD").length} line items`, color: "var(--danger)" },
                              { label: "Merch Net (USD)", value: fmtMoney(totalMerchNetUSD), sub: showMerch.length > 0 ? `${showMerch.length} show${showMerch.length !== 1 ? "s" : ""} logged` : "None logged yet", color: "#a78bfa" },
                            ].map(card => (
                              <div key={card.label} style={cardStyle}>
                                <div style={lblStyle2}>{card.label}</div>
                                <div style={{ ...valStyle, color: card.color }}>{card.value}</div>
                                <div style={subStyle}>{card.sub}</div>
                              </div>
                            ))}
                          </div>

                          {/* Net row */}
                          <div style={{ ...cardStyle, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                            <div>
                              <div style={lblStyle2}>Net (Settled − USD Expenses)</div>
                              <div style={{ ...valStyle, fontSize: 28, color: netUSD >= 0 ? "var(--success)" : "var(--danger)" }}>{fmtMoney(Math.abs(netUSD))}</div>
                              <div style={subStyle}>{netUSD >= 0 ? "Surplus" : "Deficit"}</div>
                            </div>
                            <div style={{ textAlign: "right" }}>
                              <div style={subStyle}>{fmtMoney(totalSettled)} settled</div>
                              <div style={subStyle}>− {fmtMoney(totalExpUSD)} expenses</div>
                            </div>
                          </div>

                          {/* Expense by category mini-chart */}
                          {tourExpenses.length > 0 && (
                            <div style={cardStyle}>
                              <div style={{ ...lblStyle2, marginBottom: 12 }}>Expenses by Category</div>
                              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                                {EXPENSE_CATEGORIES.filter(c => expByCat[c.id]).map(cat => {
                                  const catUSD    = expByCat[cat.id]?.["USD"] || 0;
                                  const budgeted  = budByCat[cat.id]?.amount || 0;
                                  const pct       = budgeted > 0 ? Math.min((catUSD / budgeted) * 100, 100) : null;
                                  const over      = budgeted > 0 && catUSD > budgeted;
                                  return (
                                    <div key={cat.id}>
                                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                                        <span style={{ fontFamily: FF2, fontSize: 11, color: UI.textFnt, display: "flex", alignItems: "center", gap: 6 }}><span style={{ display: "inline-block", width: 8, height: 8, borderRadius: 4, background: cat.color, flexShrink: 0 }} />{cat.label}</span>
                                        <span style={{ fontFamily: FF2, fontSize: 11, color: over ? "var(--danger)" : UI.text, fontWeight: over ? 600 : 400 }}>
                                          {fmtMoney(catUSD)}{budgeted > 0 ? ` / ${fmtMoney(budgeted)}` : ""}
                                          {over && <span style={{ marginLeft: 6, color: "var(--danger)", fontSize: 11 }}>OVER</span>}
                                        </span>
                                      </div>
                                      {pct !== null && (
                                        <div style={{ height: 3, borderRadius: 4, background: lightMode ? "#dedad4" : "#1e1e1e", overflow: "hidden" }}>
                                          <div style={{ height: "100%", width: `${pct}%`, background: over ? "var(--danger)" : "var(--success)", transition: "width 0.4s ease" }} />
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          {/* Claude AI insight */}
                          <div style={cardStyle}>
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                              <div style={{ fontFamily: FF2, fontSize: 12, fontWeight: 600, color: UI.text, display: "flex", alignItems: "center", gap: 6 }}><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--success)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3l1.9 5.8a2 2 0 0 0 1.3 1.3L21 12l-5.8 1.9a2 2 0 0 0-1.3 1.3L12 21l-1.9-5.8a2 2 0 0 0-1.3-1.3L3 12l5.8-1.9a2 2 0 0 0 1.3-1.3L12 3z"/></svg>Financial Health Summary</div>
                              <button
                                onClick={async () => {
                                  setFinAiLoading(true);
                                  try {
                                    const insight = await getTourFinancialInsight({
                                      tour, shows: tour.shows, expenses: tourExpenses,
                                      budget: tourBudget, venues
                                    });
                                    setFinAiResponse(insight);
                                  } catch { setFinAiResponse("Unable to generate insight right now."); }
                                  setFinAiLoading(false);
                                }}
                                disabled={finAiLoading}
                                style={{ fontFamily: FF2, fontSize: 11, padding: "5px 12px", borderRadius: 4, border: `1px solid #3ecf6e44`, background: "#3ecf6e18", color: "var(--success)", cursor: finAiLoading ? "default" : "pointer" }}>
                                {finAiLoading ? "Thinking…" : finAiResponse ? "Refresh" : "Generate Insight"}
                              </button>
                            </div>
                            {finAiResponse
                              ? <div style={{ fontFamily: FF2, fontSize: 12, color: UI.textFnt, lineHeight: 1.6 }}>{finAiResponse}</div>
                              : <div style={{ fontFamily: FF2, fontSize: 11, color: UI.textVFnt, fontStyle: "italic" }}>Click "Generate Insight" for a plain-English financial health summary from Claude.</div>
                            }
                          </div>
                        </div>
                      )}

                      {/* ── INCOME TAB ── */}
                      {tourFinTab === "income" && (
                        <div>
                          <div style={{ fontFamily: FF2, fontSize: 11, color: UI.textVFnt, marginBottom: 14 }}>
                            Income is sourced from the Settlement tab on each show. Edit deal terms there.
                          </div>
                          {incomeRows.length === 0
                            ? <EmptyState
                                icon={<svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>}
                                title="No shows added yet"
                                hint="Income pulls from each show's deal terms — add shows and set guarantees in the Settlement tab."
                                action={<Button variant="primary" size="sm" onClick={() => setMAddShows({})}>+ Add Shows</Button>}
                              />
                            : (
                            <table style={{ width: "100%", borderCollapse: "collapse" }}>
                              <thead>
                                <tr style={{ borderBottom: `1px solid ${UI.panelBdr}` }}>
                                  {["Date","Venue","Deal Type","Guarantee","Deposit","Rcvd","Settled"].map(h => (
                                    <th key={h} style={{ fontFamily: FF2, fontSize: 11, letterSpacing: 0.8, color: UI.textVFnt, textTransform: "uppercase", padding: "6px 10px", textAlign: "left", fontWeight: 500 }}>{h}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {incomeRows.map(({ show: s, venue: v, guarantee, deposit }) => (
                                  <tr key={s.id}
                                    style={{ borderBottom: `1px solid ${UI.rowBg2}`, cursor: "pointer" }}
                                    onClick={() => { setSelS(s.id); setDetailTab("settlement"); setData(p => ({ ...p, artists: p.artists.map(a => a.id !== selA ? a : { ...a, tours: a.tours.map(t => t.id !== selT ? t : { ...t, advanceConfig: { ...(t.advanceConfig || {}), mode: 'advance' } }) }) })); supabase.from("tours").update({ advance_config: { ...(tour.advanceConfig || {}), mode: 'advance' } }).eq("id", selT); }}>
                                    <td style={{ fontFamily: FF2, fontSize: 11, color: UI.textFnt, padding: "10px 10px" }}>{fmtDate(s.date)}</td>
                                    <td style={{ fontFamily: FF2, fontSize: 11, color: UI.text, padding: "10px 10px", fontWeight: 500 }}>{v?.name || "?"}<span style={{ fontFamily: FF2, fontSize: 11, color: UI.textMut, marginLeft: 6 }}>{v?.city || ""}</span></td>
                                    <td style={{ fontFamily: FF2, fontSize: 11, color: UI.textFnt, padding: "10px 10px" }}>{s.settlement?.dealType || "—"}</td>
                                    <td style={{ fontFamily: FF2, fontSize: 11, color: guarantee > 0 ? "var(--success)" : UI.textMut, padding: "10px 10px", fontWeight: guarantee > 0 ? 600 : 400 }}>{guarantee > 0 ? fmtMoney(guarantee) : "—"}</td>
                                    <td style={{ fontFamily: FF2, fontSize: 11, color: UI.textFnt, padding: "10px 10px" }}>{deposit > 0 ? fmtMoney(deposit) : "—"}</td>
                                    <td style={{ padding: "10px 10px", textAlign: "center" }}>
                                      {s.settlement?.depositReceived
                                        ? <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--success)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: "middle" }}><polyline points="20 6 9 17 4 12"/></svg>
                                        : <span style={{ fontSize: 12, color: UI.textVFnt }}>·</span>}
                                    </td>
                                    <td style={{ padding: "10px 10px", textAlign: "center" }}>
                                      {s.settlement?.settled
                                        ? <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--success)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: "middle" }}><polyline points="20 6 9 17 4 12"/></svg>
                                        : s.date < todayStr
                                          ? <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--danger)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: "middle" }}><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                                          : <span style={{ fontSize: 12, color: UI.textVFnt }}>·</span>}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                              <tfoot>
                                <tr style={{ borderTop: `1px solid ${UI.panelBdr}` }}>
                                  <td colSpan={3} style={{ fontFamily: FF2, fontSize: 11, color: UI.textVFnt, padding: "10px 10px" }}>Totals</td>
                                  <td style={{ fontFamily: FF2, fontSize: 12, fontWeight: 700, color: "var(--success)", padding: "10px 10px" }}>{fmtMoney(totalGuaranteed)}</td>
                                  <td style={{ fontFamily: FF2, fontSize: 12, fontWeight: 700, color: UI.text, padding: "10px 10px" }}>{fmtMoney(incomeRows.reduce((a, r) => a + r.deposit, 0))}</td>
                                  <td style={{ fontFamily: FF2, fontSize: 12, fontWeight: 700, color: "var(--success)", padding: "10px 10px", textAlign: "center" }}>{fmtMoney(totalDepositsRx)}</td>
                                  <td style={{ fontFamily: FF2, fontSize: 12, fontWeight: 700, color: "var(--success)", padding: "10px 10px", textAlign: "center" }}>{fmtMoney(totalSettled)}</td>
                                </tr>
                              </tfoot>
                            </table>
                          )}
                        </div>
                      )}

                      {/* ── EXPENSES TAB ── */}
                      {tourFinTab === "expenses" && (
                        <div>
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
                            <div style={{ fontFamily: FF2, fontSize: 20, fontWeight: 700, color: UI.text }}>
                              {fmtMoney(totalExpUSD)} <span style={{ fontSize: 12, fontWeight: 400, color: UI.textMut }}>total (USD)</span>
                            </div>
                            <button
                              onClick={() => setMExpense({ mode: "add" })}
                              style={{ fontFamily: FF2, fontSize: 11, fontWeight: 600, padding: "8px 16px", borderRadius: 8, border: "none", background: "var(--success)", color: "var(--accent-fg)", cursor: "pointer" }}>
                              + Add Expense
                            </button>
                          </div>

                          {finLoading && (
                            <div style={{ display: "flex", flexDirection: "column", gap: 8, padding: "8px 0" }}>
                              {Array.from({ length: 5 }).map((_, i) => (
                                <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", background: UI.panelBg, border: `1px solid ${UI.panelBdr}`, borderRadius: 8 }}>
                                  <div className="adv-skel" style={{ width: 34, height: 34, borderRadius: 999, flexShrink: 0 }} />
                                  <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 7 }}>
                                    <div className="adv-skel" style={{ width: `${50 - i * 4}%`, height: 11 }} />
                                    <div className="adv-skel" style={{ width: `${28 - i * 2}%`, height: 9 }} />
                                  </div>
                                  <div className="adv-skel" style={{ width: 70, height: 13, flexShrink: 0 }} />
                                </div>
                              ))}
                            </div>
                          )}

                          {!finLoading && tourExpenses.length === 0 && (
                            <EmptyState
                              icon={<svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2"/><rect x="9" y="3" width="6" height="4" rx="1"/><line x1="9" y1="12" x2="15" y2="12"/><line x1="9" y1="16" x2="13" y2="16"/></svg>}
                              title="No expenses logged yet"
                              hint="Track accommodation, travel, per diems, and more."
                              action={<Button variant="primary" size="sm" onClick={() => setMExpense({ mode: "add" })}>+ Add Expense</Button>}
                            />
                          )}

                          {!finLoading && EXPENSE_CATEGORIES.filter(c => tourExpenses.some(e => e.category === c.id)).map(cat => {
                            const catExpenses = tourExpenses.filter(e => e.category === cat.id);
                            const catTotals   = {};
                            catExpenses.forEach(e => {
                              catTotals[e.currency] = (catTotals[e.currency] || 0) + Number(e.amount);
                            });
                            const totalStr = Object.entries(catTotals).map(([cur, amt]) => fmtMoney(amt, cur)).join(" + ");
                            return (
                              <div key={cat.id} style={{ marginBottom: 20 }}>
                                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8, padding: "6px 0", borderBottom: `1px solid ${UI.panelBdr}` }}>
                                  <div style={{ fontFamily: FF2, fontSize: 11, fontWeight: 600, color: UI.textFnt, letterSpacing: 0.3, display: "flex", alignItems: "center", gap: 6 }}><span style={{ display: "inline-block", width: 8, height: 8, borderRadius: 4, background: cat.color, flexShrink: 0 }} />{cat.label}</div>
                                  <div style={{ fontFamily: FF2, fontSize: 12, fontWeight: 700, color: UI.text }}>{totalStr}</div>
                                </div>
                                {catExpenses.map(e => (
                                  <div key={e.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 6px", borderRadius: 4, borderBottom: `1px solid ${UI.rowBg2}` }}>
                                    <div style={{ flex: 1 }}>
                                      <div style={{ fontFamily: FF2, fontSize: 12, color: UI.text }}>{e.description}</div>
                                      {e.notes && <div style={{ fontFamily: FF2, fontSize: 11, color: UI.textMut, marginTop: 2 }}>{e.notes}</div>}
                                    </div>
                                    {e.date && <div style={{ fontFamily: FF2, fontSize: 11, color: UI.textVFnt, flexShrink: 0 }}>{fmtDate(e.date)}</div>}
                                    <div style={{ fontFamily: FF2, fontSize: 12, fontWeight: 600, color: UI.text, flexShrink: 0, minWidth: 80, textAlign: "right" }}>{fmtMoney(Number(e.amount), e.currency)}</div>
                                    <button onClick={() => setMExpense({ mode: "edit", expense: e })}
                                      style={{ background: "none", border: "none", color: UI.textVFnt, cursor: "pointer", padding: "2px 5px", borderRadius: 4, display: "inline-flex", alignItems: "center" }} title="Edit"
                                      onMouseEnter={ev => ev.currentTarget.style.color = UI.text}
                                      onMouseLeave={ev => ev.currentTarget.style.color = UI.textVFnt}>
                                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4z"/></svg>
                                    </button>
                                    {expDeleteConfirm === e.id
                                      ? <button onClick={() => deleteTourExpense(e.id)}
                                          style={{ background: "var(--danger)", border: "none", color: "#fff", cursor: "pointer", fontSize: 11, padding: "3px 8px", borderRadius: 4, fontFamily: FF2 }}>Delete?</button>
                                      : <button onClick={() => setExpDeleteConfirm(e.id)}
                                          style={{ background: "none", border: "none", color: UI.textVFnt, cursor: "pointer", padding: "2px 5px", borderRadius: 4, display: "inline-flex", alignItems: "center" }} title="Delete"
                                          onMouseEnter={ev => ev.currentTarget.style.color = "var(--danger)"}
                                          onMouseLeave={ev => ev.currentTarget.style.color = UI.textVFnt}>
                                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                                        </button>
                                    }
                                  </div>
                                ))}
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* ── BUDGET TAB ── */}
                      {tourFinTab === "budget" && (
                        <div>
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
                            <div style={{ fontFamily: FF2, fontSize: 14, color: UI.textFnt }}>
                              Plan spending by category, then track actuals against it.
                            </div>
                            <div style={{ display: "flex", gap: 8 }}>
                              {!budgetEditing && (
                                <button
                                  disabled={budgetAiLoading}
                                  onClick={async () => {
                                    setBudgetAiLoading(true);
                                    try {
                                      const crewSize = profiles.filter(p => p.current_tour_id === selT).length || tour.advanceConfig?.crewManifest?.length || 4;
                                      const suggestions = await getBudgetSuggestions({
                                        tourName: tour.name,
                                        showCount: tour.shows.length,
                                        tourDays: tourDays.filter(d => d.tour_id === selT).length || tour.shows.length * 1.5,
                                        crewCount: crewSize,
                                      });
                                      if (suggestions.length > 0) {
                                        const draft = {};
                                        suggestions.forEach(s => {
                                          if (s.category) draft[s.category] = { amount: String(s.amount || ""), currency: "USD", notes: s.notes || "" };
                                        });
                                        setBudgetDraft(draft);
                                        setBudgetEditing(true);
                                      }
                                    } catch {}
                                    setBudgetAiLoading(false);
                                  }}
                                  style={{ fontFamily: FF2, fontSize: 11, padding: "7px 14px", borderRadius: 8, border: `1px solid #3ecf6e44`, background: "#3ecf6e18", color: "var(--success)", cursor: budgetAiLoading ? "default" : "pointer", display: "inline-flex", alignItems: "center", gap: 5 }}>
                                  {budgetAiLoading ? "Building…" : <><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3l1.9 5.8a2 2 0 0 0 1.3 1.3L21 12l-5.8 1.9a2 2 0 0 0-1.3 1.3L12 21l-1.9-5.8a2 2 0 0 0-1.3-1.3L3 12l5.8-1.9a2 2 0 0 0 1.3-1.3L12 3z"/></svg>AI Budget Builder</>}
                                </button>
                              )}
                              {!budgetEditing
                                ? <button onClick={() => {
                                    const draft = {};
                                    tourBudget.forEach(b => { draft[b.category] = { amount: String(b.budgeted_amount), currency: b.currency, notes: b.notes || "" }; });
                                    EXPENSE_CATEGORIES.forEach(c => { if (!draft[c.id]) draft[c.id] = { amount: "", currency: "USD", notes: "" }; });
                                    setBudgetDraft(draft);
                                    setBudgetEditing(true);
                                  }}
                                  style={{ fontFamily: FF2, fontSize: 11, fontWeight: 600, padding: "7px 14px", borderRadius: 8, border: "none", background: "var(--accent)", color: "var(--accent-fg)", cursor: "pointer" }}>
                                  {tourBudget.length > 0 ? "Edit Budget" : "Set Budget"}
                                </button>
                                : <>
                                  <button onClick={() => setBudgetEditing(false)}
                                    style={{ fontFamily: FF2, fontSize: 11, padding: "7px 14px", borderRadius: 8, border: `1px solid ${UI.panelBdr}`, background: "none", color: UI.textMut, cursor: "pointer" }}>Cancel</button>
                                  <button onClick={async () => {
                                    const rows = EXPENSE_CATEGORIES
                                      .filter(c => budgetDraft[c.id]?.amount && Number(budgetDraft[c.id].amount) > 0)
                                      .map(c => ({
                                        tour_id: selT, team_id: teamId,
                                        category: c.id,
                                        budgeted_amount: parseFloat(budgetDraft[c.id].amount) || 0,
                                        currency: budgetDraft[c.id].currency || "USD",
                                        notes: budgetDraft[c.id].notes || null,
                                      }));
                                    await saveTourBudgetRows(rows);
                                    setBudgetEditing(false);
                                  }}
                                  style={{ fontFamily: FF2, fontSize: 11, fontWeight: 600, padding: "7px 14px", borderRadius: 8, border: "none", background: "var(--success)", color: "var(--accent-fg)", cursor: "pointer" }}>
                                    Save Budget
                                  </button>
                                </>
                              }
                            </div>
                          </div>

                          {/* Budget rows */}
                          <table style={{ width: "100%", borderCollapse: "collapse" }}>
                            <thead>
                              <tr style={{ borderBottom: `1px solid ${UI.panelBdr}` }}>
                                {["Category", "Budgeted", "Actual", "Variance", ""].map(h => (
                                  <th key={h} style={{ fontFamily: FF2, fontSize: 11, letterSpacing: 0.8, color: UI.textVFnt, textTransform: "uppercase", padding: "6px 10px", textAlign: h === "Budgeted" || h === "Actual" || h === "Variance" ? "right" : "left", fontWeight: 500 }}>{h}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {EXPENSE_CATEGORIES.map(cat => {
                                const budRow   = budByCat[cat.id];
                                const budAmt   = budRow?.amount || 0;
                                const actUSD   = expByCat[cat.id]?.["USD"] || 0;
                                const variance = budAmt > 0 ? actUSD - budAmt : null;
                                const hasData  = budAmt > 0 || actUSD > 0;
                                if (!budgetEditing && !hasData) return null;
                                return (
                                  <tr key={cat.id} style={{ borderBottom: `1px solid ${UI.rowBg2}` }}>
                                    <td style={{ fontFamily: FF2, fontSize: 12, color: UI.textFnt, padding: "10px 10px" }}><span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><span style={{ display: "inline-block", width: 8, height: 8, borderRadius: 4, background: cat.color, flexShrink: 0 }} />{cat.label}</span></td>
                                    <td style={{ padding: "10px 10px", textAlign: "right" }}>
                                      {budgetEditing
                                        ? <div style={{ display: "flex", gap: 4, justifyContent: "flex-end", alignItems: "center" }}>
                                            <select value={budgetDraft[cat.id]?.currency || "USD"}
                                              onChange={e => setBudgetDraft(p => ({ ...p, [cat.id]: { ...(p[cat.id] || {}), currency: e.target.value } }))}
                                              style={{ fontFamily: FF2, fontSize: 11, padding: "4px 6px", borderRadius: 4, border: `1px solid ${UI.panelBdr}`, background: lightMode ? "#fff" : "#0d0d0d", color: UI.textFnt, outline: "none", maxWidth: 60 }}>
                                              {CURRENCIES.map(c => <option key={c.code} value={c.code}>{c.code}</option>)}
                                            </select>
                                            <input type="number" min="0" step="1"
                                              value={budgetDraft[cat.id]?.amount || ""}
                                              onChange={e => setBudgetDraft(p => ({ ...p, [cat.id]: { ...(p[cat.id] || {}), amount: e.target.value } }))}
                                              placeholder="0"
                                              style={{ fontFamily: FF2, fontSize: 11, padding: "4px 8px", borderRadius: 4, border: `1px solid ${UI.panelBdr}`, background: lightMode ? "#fff" : "#0d0d0d", color: UI.text, outline: "none", width: 90, textAlign: "right" }} />
                                          </div>
                                        : <span style={{ fontFamily: FF2, fontSize: 12, color: budAmt > 0 ? UI.text : UI.textVFnt }}>
                                            {budAmt > 0 ? fmtMoney(budAmt, budRow?.currency || "USD") : "—"}
                                          </span>
                                      }
                                    </td>
                                    <td style={{ fontFamily: FF2, fontSize: 12, color: actUSD > 0 ? UI.text : UI.textVFnt, padding: "10px 10px", textAlign: "right" }}>
                                      {actUSD > 0 ? fmtMoney(actUSD) : "—"}
                                    </td>
                                    <td style={{ fontFamily: FF2, fontSize: 12, fontWeight: 600, padding: "10px 10px", textAlign: "right",
                                      color: variance === null ? UI.textVFnt : variance <= 0 ? "var(--success)" : "var(--danger)" }}>
                                      {variance === null ? "—" : `${variance > 0 ? "+" : ""}${fmtMoney(Math.abs(variance))}`}
                                    </td>
                                    <td style={{ padding: "10px 10px" }}>
                                      {variance !== null && variance > 0 && (
                                        <span style={{ fontFamily: FF2, fontSize: 11, letterSpacing: 0.5, color: "var(--danger)", background: UI.dangerBg, border: `1px solid ${UI.dangerBdr}`, padding: "2px 6px", borderRadius: 4, textTransform: "uppercase" }}>Over</span>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                              {/* Totals row */}
                              {!budgetEditing && (tourBudget.length > 0 || tourExpenses.length > 0) && (
                                <tr style={{ borderTop: `1px solid ${UI.panelBdr}` }}>
                                  <td style={{ fontFamily: FF2, fontSize: 11, fontWeight: 700, color: UI.textFnt, padding: "10px 10px" }}>Total</td>
                                  <td style={{ fontFamily: FF2, fontSize: 12, fontWeight: 700, color: UI.text, padding: "10px 10px", textAlign: "right" }}>
                                    {fmtMoney(tourBudget.filter(b => b.currency === "USD").reduce((a, b) => a + Number(b.budgeted_amount), 0))}
                                  </td>
                                  <td style={{ fontFamily: FF2, fontSize: 12, fontWeight: 700, color: UI.text, padding: "10px 10px", textAlign: "right" }}>{fmtMoney(totalExpUSD)}</td>
                                  <td colSpan={2} />
                                </tr>
                              )}
                            </tbody>
                          </table>

                          {!budgetEditing && tourBudget.length === 0 && tourExpenses.length === 0 && (
                            <EmptyState
                              icon={<svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="4" height="14" rx="1"/><rect x="10" y="8" width="4" height="9" rx="1"/><rect x="17" y="5" width="4" height="12" rx="1"/><line x1="3" y1="21" x2="21" y2="21"/></svg>}
                              title="No budget set yet"
                              hint={"Use “Set Budget” above to plan by category, or let the AI Budget Builder generate a starting point."}
                            />
                          )}
                        </div>
                      )}

                      {/* ── MERCH TAB ── */}
                      {tourFinTab === "merch" && (() => {
                        const showsWithMerch = tour.shows.map(s => ({
                          show: s,
                          merch: showMerch.find(m => m.show_id === s.id) || null,
                        }));
                        const hasMerch       = showMerch.length > 0;
                        const squareLoading  = squareConn === null;   // still fetching status
                        const isConnected    = squareConn?.connected === true;
                        const lastSynced     = squareConn?.last_synced_at;
                        const syncedNotes    = (m) => m?.notes?.startsWith("Synced from Square");

                        return (
                          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

                            {/* ── Square connection panel ── */}
                            <div style={{
                              background: isConnected ? (lightMode ? "#f0faf5" : "#0a1a10") : UI.panelBg,
                              border: `1px solid ${isConnected ? (lightMode ? "#b0dfc0" : "#1a3a28") : UI.panelBdr}`,
                              borderRadius: 8, padding: "14px 18px",
                              display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16,
                            }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                                {/* Square logo mark */}
                                <div style={{
                                  width: 34, height: 34, borderRadius: 8, flexShrink: 0,
                                  background: isConnected ? "var(--success)" : (lightMode ? "#dedad4" : "#222"),
                                  display: "flex", alignItems: "center", justifyContent: "center",
                                }}>
                                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                                    <rect x="2" y="2" width="20" height="20" rx="3" stroke={isConnected ? "#0d0d0d" : (lightMode ? "#888" : "#555")} strokeWidth="2.5" fill="none"/>
                                    <rect x="7" y="7" width="10" height="10" rx="1.5" fill={isConnected ? "#0d0d0d" : (lightMode ? "#888" : "#555")}/>
                                  </svg>
                                </div>
                                <div>
                                  {isConnected ? (
                                    <>
                                      <div style={{ fontFamily: FF2, fontSize: 12, fontWeight: 600, color: lightMode ? "#1a6a3a" : "var(--success)" }}>
                                        Square connected
                                      </div>
                                      <div style={{ fontFamily: FF2, fontSize: 11, color: UI.textMut, marginTop: 1 }}>
                                        {lastSynced
                                          ? `Last synced ${timeAgo(lastSynced)}`
                                          : "Not synced yet — click Sync to pull sales data"}
                                      </div>
                                    </>
                                  ) : (
                                    <>
                                      <div style={{ fontFamily: FF2, fontSize: 12, fontWeight: 600, color: UI.textFnt }}>
                                        Connect Square
                                      </div>
                                      <div style={{ fontFamily: FF2, fontSize: 11, color: UI.textMut, marginTop: 1 }}>
                                        Auto-pull nightly sales from your merch seller's Square account
                                      </div>
                                    </>
                                  )}
                                </div>
                              </div>
                              <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                                {squareLoading ? (
                                  <span style={{ fontFamily: FF2, fontSize: 11, color: UI.textVFnt }}>Checking…</span>
                                ) : isConnected ? (
                                  <>
                                    <button
                                      onClick={syncSquare}
                                      disabled={squareSyncing}
                                      style={{ fontFamily: FF2, fontSize: 11, fontWeight: 600, padding: "7px 14px", borderRadius: 8, border: "none", background: squareSyncing ? UI.textMut : "var(--success)", color: "var(--accent-fg)", cursor: squareSyncing ? "default" : "pointer", display: "inline-flex", alignItems: "center", gap: 5 }}>
                                      {squareSyncing ? "Syncing…" : <><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a9 9 0 1 1-2.64-6.36"/><polyline points="21 3 21 9 15 9"/></svg>Sync Now</>}
                                    </button>
                                    <button
                                      onClick={disconnectSquare}
                                      style={{ fontFamily: FF2, fontSize: 11, padding: "7px 12px", borderRadius: 8, border: `1px solid ${UI.dangerBdr}`, background: "none", color: "var(--danger)", cursor: "pointer" }}>
                                      Disconnect
                                    </button>
                                  </>
                                ) : (
                                  <button
                                    onClick={connectSquare}
                                    style={{ fontFamily: FF2, fontSize: 11, fontWeight: 600, padding: "7px 16px", borderRadius: 8, border: "none", background: "#a78bfa", color: "#0d0d0d", cursor: "pointer" }}>
                                    Connect Square →
                                  </button>
                                )}
                              </div>
                            </div>

                            {/* ── Summary cards (only when data exists) ── */}
                            {hasMerch && (
                              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                                {Object.entries(merchByCur).map(([cur, totals]) => {
                                  const sym = CURRENCIES.find(c => c.code === cur)?.symbol || cur;
                                  return (
                                    <div key={cur} style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
                                      {[
                                        { label: `Gross Sales (${cur})`,   value: `${sym}${totals.gross.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,     color: "#a78bfa" },
                                        { label: `Venue Take (${cur})`,    value: `${sym}${totals.venueTake.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, color: "var(--danger)" },
                                        { label: `Net to Artist (${cur})`, value: `${sym}${totals.net.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,     color: "var(--success)" },
                                      ].map(card => (
                                        <div key={card.label} style={{ background: UI.panelBg, border: `1px solid ${UI.panelBdr}`, borderRadius: 8, padding: "14px 16px" }}>
                                          <div style={{ fontFamily: FF2, fontSize: 11, letterSpacing: 1, color: UI.textVFnt, textTransform: "uppercase", marginBottom: 4 }}>{card.label}</div>
                                          <div style={{ fontFamily: FF2, fontSize: 20, fontWeight: 700, color: card.color, letterSpacing: "-0.5px" }}>{card.value}</div>
                                          <div style={{ fontFamily: FF2, fontSize: 11, color: UI.textMut, marginTop: 2 }}>
                                            {showMerch.filter(m => m.currency === cur).length} show{showMerch.filter(m => m.currency === cur).length !== 1 ? "s" : ""}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  );
                                })}
                              </div>
                            )}

                            {/* ── Per-show table ── */}
                            {tour.shows.length > 0 ? (
                              <div>
                                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                                  <div style={{ fontFamily: FF2, fontSize: 11, color: UI.textVFnt }}>
                                    {isConnected
                                      ? "Gross from Square · set venue cut % per show to calculate net."
                                      : "Connect Square above to auto-pull sales, or log manually per show."}
                                  </div>
                                </div>
                                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                  <thead>
                                    <tr style={{ borderBottom: `1px solid ${UI.panelBdr}` }}>
                                      {["Date","Venue","Gross","Venue Cut","Net to Artist","",""].map(h => (
                                        <th key={h} style={{ fontFamily: FF2, fontSize: 11, letterSpacing: 0.8, color: UI.textVFnt, textTransform: "uppercase", padding: "6px 10px", textAlign: ["Gross","Venue Cut","Net to Artist"].includes(h) ? "right" : "left", fontWeight: 500 }}>{h}</th>
                                      ))}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {showsWithMerch.map(({ show, merch }) => {
                                      const venueName = venues[show.venueId]?.name || "—";
                                      const cur = merch?.currency || "USD";
                                      const sym = CURRENCIES.find(c => c.code === cur)?.symbol || cur;
                                      const fromSquare = syncedNotes(merch);
                                      return (
                                        <tr key={show.id} style={{ borderBottom: `1px solid ${UI.rowBg2}`, opacity: merch ? 1 : 0.55 }}>
                                          <td style={{ fontFamily: FF2, fontSize: 12, color: UI.textFnt, padding: "10px 10px", whiteSpace: "nowrap" }}>{fmtDate(show.date)}</td>
                                          <td style={{ fontFamily: FF2, fontSize: 12, color: UI.textFnt, padding: "10px 10px", maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{venueName}</td>
                                          <td style={{ fontFamily: FF2, fontSize: 12, color: UI.text, padding: "10px 10px", textAlign: "right" }}>
                                            {merch
                                              ? <span>{sym}{Number(merch.gross).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                  {fromSquare && <span style={{ marginLeft: 5, fontFamily: FF2, fontSize: 11, color: "#a78bfa", letterSpacing: 0.3 }}>SQ</span>}
                                                </span>
                                              : "—"}
                                          </td>
                                          <td style={{ fontFamily: FF2, fontSize: 12, color: merch?.venue_cut_pct > 0 ? "var(--danger)" : UI.textMut, padding: "10px 10px", textAlign: "right" }}>
                                            {merch ? `${merch.venue_cut_pct}%` : "—"}
                                          </td>
                                          <td style={{ fontFamily: FF2, fontSize: 12, fontWeight: 600, color: merch ? "var(--success)" : UI.textMut, padding: "10px 10px", textAlign: "right" }}>
                                            {merch ? `${sym}${Number(merch.net).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "—"}
                                          </td>
                                          {/* Source badge */}
                                          <td style={{ padding: "10px 6px" }}>
                                            {fromSquare && (
                                              <span style={{ fontFamily: FF2, fontSize: 11, color: "#a78bfa", background: "#a78bfa18", border: "1px solid #a78bfa33", borderRadius: 4, padding: "2px 5px", letterSpacing: 0.3 }}>Square</span>
                                            )}
                                          </td>
                                          <td style={{ padding: "10px 10px", textAlign: "right", whiteSpace: "nowrap" }}>
                                            {merch ? (
                                              <>
                                                <button
                                                  onClick={() => setMMerch({ mode: "edit", showId: show.id, entry: merch })}
                                                  style={{ fontFamily: FF2, fontSize: 11, padding: "4px 10px", borderRadius: 4, border: `1px solid ${UI.panelBdr}`, background: "none", color: UI.textFnt, cursor: "pointer", marginRight: 6 }}>
                                                  {fromSquare ? "Edit Cut %" : "Edit"}
                                                </button>
                                                {merchDeleteConfirm === merch.id ? (
                                                  <>
                                                    <button onClick={() => deleteShowMerch(merch.id)}
                                                      style={{ fontFamily: FF2, fontSize: 11, padding: "4px 10px", borderRadius: 4, border: "none", background: "var(--danger)", color: "#fff", cursor: "pointer", marginRight: 4 }}>Confirm</button>
                                                    <button onClick={() => setMerchDeleteConfirm(null)}
                                                      style={{ fontFamily: FF2, fontSize: 11, padding: "4px 10px", borderRadius: 4, border: `1px solid ${UI.panelBdr}`, background: "none", color: UI.textMut, cursor: "pointer" }}>Cancel</button>
                                                  </>
                                                ) : (
                                                  <button onClick={() => setMerchDeleteConfirm(merch.id)} title="Delete"
                                                    style={{ fontFamily: FF2, fontSize: 11, padding: "4px 10px", borderRadius: 4, border: `1px solid ${UI.dangerBdr}`, background: "none", color: "var(--danger)", cursor: "pointer", display: "inline-flex", alignItems: "center" }}>
                                                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                                                  </button>
                                                )}
                                              </>
                                            ) : (
                                              <button
                                                onClick={() => setMMerch({ mode: "add", showId: show.id })}
                                                style={{ fontFamily: FF2, fontSize: 11, padding: "4px 10px", borderRadius: 4, border: `1px solid #a78bfa44`, background: "#a78bfa18", color: "#a78bfa", cursor: "pointer" }}>
                                                Log manually
                                              </button>
                                            )}
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            ) : (
                              <EmptyState
                                icon={<svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>}
                                title="No shows yet"
                                hint="Add shows to start tracking merch."
                                action={<Button variant="primary" size="sm" onClick={() => setMAddShows({})}>+ Add Shows</Button>}
                              />
                            )}
                          </div>
                        );
                      })()}

                    </div>
                  </div>
                );
              })()}

              {/* ── ADVANCE MODE — Show list + Detail panel ── */}
              {!isTourMode && !isFinMode && <>
              {/* Tour Shows strip — persistent header above the show list; collapsible once an advance is open */}
              {(() => {
                const FFs = "'Helvetica Neue', Helvetica, Arial, sans-serif";
                const curIdx = show ? filteredShows.findIndex(s => s.id === selS) : -1;
                const listVisible = !show || showListOpen;
                const goRel = (dir) => {
                  if (curIdx < 0 || !filteredShows.length) return;
                  const next = Math.min(Math.max(curIdx + dir, 0), filteredShows.length - 1);
                  if (next !== curIdx) { setSelS(filteredShows[next].id); setEDraft(""); }
                };
                const navBtn = (dir, glyph, disabled) => (
                  <button onClick={e => { e.stopPropagation(); goRel(dir); }} disabled={disabled}
                    style={{ fontFamily: FFs, fontSize: 12, lineHeight: 1, color: disabled ? UI.textVFnt : UI.textMut, background: "none", border: `1px solid ${UI.panelBdr}`, borderRadius: 4, padding: "3px 9px", cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.4 : 1 }}>
                    {glyph}
                  </button>
                );
                return (
                  <div ref={tourShowsStripRef} onClick={() => show && setShowListOpen(o => !o)}
                    style={{ margin: 0, padding: "7px 36px", background: lightMode ? "#eae6e0" : "#0a0a0a", borderBottom: `1px solid ${lightMode ? "#d0ccc6" : "#161616"}`, display: "flex", alignItems: "center", gap: 8, cursor: show ? "pointer" : "default", userSelect: "none" }}>
                    <span style={{ width: 12, flexShrink: 0, color: UI.textVFnt, display: "inline-flex", justifyContent: "center", transform: listVisible ? "rotate(90deg)" : "none", transition: "transform 0.28s cubic-bezier(0.4,0,0.2,1)", opacity: show ? 1 : 0.4 }}><svg width="10" height="10" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M4.5 2l4 4-4 4"/></svg></span>
                    <span style={{ fontFamily: FFs, fontSize: 11, color: UI.amber, letterSpacing: 1, textTransform: "uppercase" }}>Tour Shows</span>
                    {curIdx >= 0 ? <>
                      <span style={{ fontFamily: FFs, fontSize: 11, color: UI.textFnt }}>·</span>
                      <span style={{ fontFamily: FFs, fontSize: 11, color: UI.textMut, letterSpacing: 1, textTransform: "uppercase" }}>
                        Show {curIdx + 1} of {filteredShows.length}
                      </span>
                    </> : filteredShows.length > 0 && <>
                      <span style={{ fontFamily: FFs, fontSize: 11, color: UI.textFnt }}>·</span>
                      <span style={{ fontFamily: FFs, fontSize: 11, color: UI.textMut, letterSpacing: 1, textTransform: "uppercase" }}>
                        {filteredShows.length} {filteredShows.length === 1 ? "Show" : "Shows"}
                      </span>
                    </>}
                    {show && <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6 }}>
                      {navBtn(-1, "‹", curIdx <= 0)}
                      {navBtn(1, "›", curIdx < 0 || curIdx >= filteredShows.length - 1)}
                    </div>}
                  </div>
                );
              })()}
              {/* Tour-level progress — folded in as a hairline directly under the Tour Shows strip */}
              {tour.shows.length > 0 && (() => {
                const total = tour.shows.length;
                const nConf = tour.shows.filter(s => s.status === "confirmed" || s.status === "dayOf").length;
                const nAdv  = tour.shows.filter(s => s.status === "advancing").length;
                return (
                  <div style={{ display: "flex", height: 3, background: lightMode ? "#dedad4" : "#1e1e1e" }}>
                    <div style={{ width: `${(nConf / total) * 100}%`, background: "var(--success)", transition: "width 0.4s ease" }} />
                    <div style={{ width: `${(nAdv / total) * 100}%`,  background: "var(--accent)",  transition: "width 0.4s ease" }} />
                  </div>
                );
              })()}
              <div className={`tm-flds-wrap ${(!show || showListOpen) ? "open" : ""}`}>
              <div className="tm-flds-inner">
              {/* List controls — filters, bulk status, today + progress bar. These act on the
                  list, so they live inside the collapsible area and hide when an advance is open. */}
              {tour.shows.length > 0 && (
                <div style={{ padding: "10px 36px 8px", display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  {["all", "confirmed", "advancing", "unstarted"].map(f => (
                    <button key={f} className={`fp ${showFilter === f ? "on" : ""}`} onClick={() => setShowFilter(f)}>
                      {f === "all" ? "All" : statusConfig[f].label}
                    </button>
                  ))}
                  <select className="fp" style={{ cursor: "pointer", visibility: filteredShows.length > 0 ? "visible" : "hidden" }}
                    value=""
                    title={`Set status for all ${filteredShows.length} shown show${filteredShows.length !== 1 ? "s" : ""}`}
                    onChange={e => { if (e.target.value) { bulkSetStatus(e.target.value); e.target.value = ""; } }}>
                    <option value="" disabled>Mark all…</option>
                    <option value="unstarted">Unstarted</option>
                    <option value="advancing">Advancing</option>
                    <option value="confirmed">Confirmed</option>
                  </select>
                  {(() => {
                    const todayStr = new Date().toISOString().slice(0, 10);
                    const hasUpcoming = tour.shows.some(s => s.date >= todayStr);
                    return (
                      <button className="fp" title="Scroll to next upcoming show"
                        style={{ visibility: hasUpcoming ? "visible" : "hidden" }}
                        onClick={() => nextShowRef.current?.scrollIntoView({ block: "center", behavior: "smooth" })}>
                        ↑ Today
                      </button>
                    );
                  })()}
                  {(() => {
                    const total     = tour.shows.length;
                    const todayStr  = new Date().toISOString().slice(0, 10);
                    const nConf     = tour.shows.filter(s => s.status === "confirmed" || s.status === "dayOf").length;
                    const nAdv      = tour.shows.filter(s => s.status === "advancing").length;
                    const nUnst     = total - nConf - nAdv;
                    const nSettle   = tour.shows.filter(s => s.date < todayStr && !s.settlement?.settled).length;
                    return (
                      <div style={{ marginLeft: "auto", display: "flex", gap: 12, alignItems: "center", fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, letterSpacing: 0.3, flexWrap: "wrap" }}>
                        {nConf  > 0 && <span style={{ color: "var(--success)" }}>{nConf} confirmed</span>}
                        {nAdv   > 0 && <span style={{ color: "var(--accent)" }}>{nAdv} advancing</span>}
                        {nUnst  > 0 && <span style={{ color: lightMode ? "#9a9794" : "#555" }}>{nUnst} unstarted</span>}
                        {nSettle > 0 && <span style={{ color: "var(--danger)", display: "inline-flex", alignItems: "center", gap: 4 }}><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>{nSettle} settlement{nSettle !== 1 ? "s" : ""} outstanding</span>}
                      </div>
                    );
                  })()}
                </div>
              )}
              <div className="sg">
                <div className="sg-hdr"><div>Date</div><div>Venue</div><div>Status</div><div></div><div></div></div>
                {tour.shows.length === 0 && (
                  <div style={{ padding: "48px 36px 56px", textAlign: "center" }}>
                    <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, letterSpacing: 3, color: "var(--text-faint)", textTransform: "uppercase", marginBottom: 28 }}>Advance Workflow</div>
                    <div style={{ display: "flex", justifyContent: "center", maxWidth: 580, margin: "0 auto 36px" }}>
                      {[
                        { num: "01", icon: <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>, label: "Import Dates", sub: "Bulk import tour dates\nor add shows manually" },
                        { num: "02", icon: <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="9" y1="21" x2="9" y2="9"/></svg>, label: "Fill the Portal", sub: "Add TM details + upload\nstage plot, tech rider, etc." },
                        { num: "03", icon: <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22 6 12 13 2 6"/></svg>, label: "Send to Promoter", sub: "Share the advance link\nand draft your email" },
                      ].map((step, i) => (
                        <div key={i} style={{ flex: 1, padding: "20px 18px", position: "relative" }}>
                          {i > 0 && <div style={{ position: "absolute", left: -6, top: "44%", color: "var(--sub-border)", display: "flex" }}><svg width="14" height="14" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M4.5 2l4 4-4 4"/></svg></div>}
                          <div style={{ color: "var(--accent)", marginBottom: 8, display: "flex", justifyContent: "center" }}>{step.icon}</div>
                          <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: "var(--text-faint)", letterSpacing: 2, textTransform: "uppercase", marginBottom: 5 }}>{step.num}</div>
                          <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-mid)", marginBottom: 7 }}>{step.label}</div>
                          <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: "var(--text-mut)", lineHeight: 1.65, whiteSpace: "pre-line" }}>{step.sub}</div>
                        </div>
                      ))}
                    </div>
                    <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
                      <button className="add-p" style={{ padding: "10px 24px", fontSize: 11 }} onClick={() => setMAddShows({})}>+ Add Shows</button>
                    </div>
                  </div>
                )}
                {filteredShows.length === 0 && tour.shows.length > 0 && <div className="empty">No {statusConfig[showFilter]?.label || ""} shows.</div>}
                {(() => {
                  const todayStr2 = new Date().toISOString().slice(0, 10);
                  const nextShowId = filteredShows.find(s => s.date >= todayStr2)?.id ?? null;
                  return filteredShows.map((s, idx) => {
                    const v   = venues[s.venueId];
                    const uni = unifiedStatus(s);
                    const todayStr   = todayStr2;
                    const isPast     = s.date < todayStr;
                    const daysUntil  = Math.ceil((new Date(s.date + "T12:00:00") - new Date()) / 864e5);
                    const isUpcoming = !isPast && daysUntil <= 7;
                    const needsSettle  = isPast && !s.settlement?.settled;
                    const autoFlag     = isUpcoming && s.status === "unstarted";
                    const isNext       = s.id === nextShowId;
                    return (
                      <Fragment key={s.id}>
                        <div ref={isNext ? nextShowRef : null}
                          className={`s-row ${selS === s.id ? "sel" : ""} ${(s.flagged || autoFlag) ? "flagged" : ""} ${isNext && !selS ? "next-show" : ""} ${isPast ? (needsSettle ? "past unsettled" : "past") : ""}`}
                          onClick={() => { setSelS(s.id === selS ? null : s.id); setEDraft(""); setDetailTab("advance"); }}>
                          <div className="s-date">{fmtDate(s.date).split(",")[0]}<br />{fmtDate(s.date).split(",")[1]}</div>
                          <div>
                            <div className="s-vname">{v?.name || "Unknown"}</div>
                            <div className="s-city">{[v?.city, v?.addressState].filter(Boolean).join(", ")}{s.lodging?.name ? <span style={{ color: "#2a5a2a", marginLeft: 6, display: "inline-flex", alignItems: "center", gap: 3 }}><svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 22V8l9-6 9 6v14"/><rect x="9" y="14" width="6" height="8"/></svg>{s.lodging.name}</span> : null}</div>
                          </div>
                          <div><span className="s-badge" title="Click to cycle status" style={{ color: uni.color, background: uni.bg, cursor: "pointer", userSelect: "none" }} onClick={e => { e.stopPropagation(); cycleShowStatus(selA, selT, s.id, s.status); }}><span className="s-dot" style={{ background: uni.dot }} />{uni.label}</span></div>
                          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                            {needsSettle && (
                              <button onClick={e => { e.stopPropagation(); setSelS(s.id); setDetailTab("settlement"); }}
                                style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, letterSpacing: 1, color: "var(--danger)", background: "none", border: "none", padding: 0, cursor: "pointer", textDecoration: "underline", fontWeight: 600 }}>
                                SETTLE UP →
                              </button>
                            )}
                          </div>
                          <div className="s-acts" onClick={e => e.stopPropagation()}>
                            <button className={`flag-btn${(s.flagged || autoFlag) ? " on" : ""}`} title={s.flagged ? "Remove flag" : "Flag as urgent"} onClick={() => toggleFlag(selA, selT, s.id)}><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" y1="22" x2="4" y2="15"/></svg></button>
                            <button className="ibtn ed" title="Edit" onClick={() => setMShow({ mode: "edit", showId: s.id })}><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4z"/></svg></button>
                            <button className="abtn" onClick={() => { setSelS(s.id); setEDraft(""); setDetailTab("advance"); }}>Advance →</button>
                          </div>
                        </div>
                      </Fragment>
                    );
                  });
                })()}
              </div>
              </div>
              </div>{/* end collapsible show list wrap */}

              {/* DETAIL PANEL */}
              {show && (
                <div className="dp" ref={dpRef}>
                  <div className="dp-hdr">
                    <div>
                      <div className="dp-title">{venue?.name || "Unknown Venue"} — {fmtDate(show.date)}</div>
                      <div className="dp-meta">{[
                        [venue?.city, venue?.addressState].filter(Boolean).join(", "),
                        venue?.capacity ? `Cap. ${Number(venue.capacity).toLocaleString()}` : null,
                        venue?.ages || null,
                      ].filter(Boolean).join("  ·  ")}</div>
                      {(venue?.promoterName || venue?.promoterEmail) && (
                        <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: "var(--text-mut)", marginTop: 3 }}>
                          Promoter:{" "}
                          {venue?.promoterEmail ? (
                            <a href={`mailto:${venue.promoterEmail}`} title={venue.promoterEmail} style={{ color: "var(--accent)", textDecoration: "none" }}>{venue?.promoterName || venue.promoterEmail}</a>
                          ) : (
                            <span style={{ color: "var(--text-mid)" }}>{venue.promoterName}</span>
                          )}
                        </div>
                      )}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                      <div className="st-sel">
                        {/* Day-Of retired as a manual status (day-of banner is automatic) — still shown on legacy rows that have it */}
                        {Object.entries(SC).filter(([k]) => k !== "dayOf" || show.status === "dayOf").map(([k, sc]) => (
                          <button key={k} className={`st-opt ${show.status === k ? "on" : ""}`}
                            style={show.status === k ? { background: sc.dot, color: "var(--accent-fg)" } : {}}
                            onClick={() => { updShow("status", k); logActivity(`Status set to ${sc.label}`); }}>{sc.label}</button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Day-of-show banner */}
                  {(() => { if (show.date !== new Date().toISOString().slice(0,10)) return null;
                    const td = show.advanceTmData || {};
                    const sc = td.schedule || {};
                    const hosp = td.hospitality || {};
                    const gl = td.guestList || {};
                    const g = Number((show.settlement?.guarantee || "").replace(/[^0-9.]/g, ""));
                    const dep = Number((show.settlement?.deposit || "").replace(/[^0-9.]/g, ""));
                    const balance = g ? (dep ? g - dep : g) : null;
                    const phone = show.settlement?.promoterPhone || venue?.promoterPhone || "";
                    const tzSuffix = show.timezone ? ` ${show.timezone}` : "";
                    const items = [
                      sc.venue_access && { k: "Load-in", v: sc.venue_access + tzSuffix },
                      sc.doors_public && { k: "Doors", v: sc.doors_public + tzSuffix },
                      sc.hs_set       && { k: "Set Time", v: sc.hs_set + tzSuffix },
                      sc.curfew       && { k: "Curfew", v: sc.curfew + tzSuffix },
                      phone           && { k: "Promoter", v: phone },
                      balance         && { k: "Due at Settlement", v: `$${balance.toLocaleString()}`, hi: true },
                      hosp.buyout     && { k: "Buyout", v: hosp.buyout },
                      gl.gl_cap       && { k: "GL Cap", v: String(gl.gl_cap) },
                    ].filter(Boolean);
                    return (
                      <div className="day-of-bar">
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: showDayOfPanel && items.length ? 10 : 0 }}>
                          <span style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: "var(--success)", letterSpacing: 2, fontWeight: 600 }}>TODAY · DAY OF SHOW</span>
                          <button onClick={() => setShowDayOfPanel(p => !p)} style={{ background: "none", border: "none", color: "#2a5a2a", cursor: "pointer", fontSize: 11, fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif" }}>{showDayOfPanel ? "Hide" : "Show"}</button>
                        </div>
                        {showDayOfPanel && items.length > 0 && (
                          <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
                            {items.map(item => (
                              <div key={item.k} className="day-of-item">
                                <div className="day-of-k">{item.k}</div>
                                <div className="day-of-v" style={{ color: item.hi ? "var(--accent)" : undefined, fontWeight: item.hi ? 600 : 400 }}>{item.v}</div>
                              </div>
                            ))}
                          </div>
                        )}
                        {showDayOfPanel && items.length === 0 && (
                          <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: "#2a5a2a" }}>Fill in the Advance tab to see today's key details here.</div>
                        )}
                      </div>
                    );
                  })()}

                  {/* Tab bar */}
                  <div className="dp-tabs">
                    {(() => {
                      const todayStr = new Date().toISOString().slice(0, 10);
                      // Report is inherently post-show — only surface it once the show has
                      // happened (or if it's already the active tab, so ‹ › nav never orphans).
                      const showReportTab = show.date <= todayStr || detailTab === "report";
                      const mainTabs = [["advance","Advance"],["email","Email"],["info","Info"],["settlement","Settlement"],["files","Files"],...(showReportTab ? [["report","Report"]] : [])];
                      // Setlist + Activity are niche — tucked into an overflow menu.
                      const overflowTabs = [["setlist","Setlist"],["activity","Activity"]];
                      const activeOverflow = overflowTabs.find(([id]) => id === detailTab);
                      return (<>
                        {mainTabs.map(([id,label]) => (
                          <button key={id} className={`dp-tab ${detailTab === id ? "on" : ""}`} onClick={() => setDetailTab(id)}>{label}</button>
                        ))}
                        <div style={{ position: "relative", display: "inline-flex", alignItems: "flex-end" }} ref={dpMoreMenuRef}>
                          <button className={`dp-tab ${activeOverflow ? "on" : ""}`} title="More tabs" aria-label="More tabs"
                            onClick={() => setDpMoreOpen(v => !v)}
                            style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                            {activeOverflow ? activeOverflow[1] : null}
                            <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: 13, height: 13, display: "block" }}><circle cx="12" cy="5" r="1.6"/><circle cx="12" cy="12" r="1.6"/><circle cx="12" cy="19" r="1.6"/></svg>
                          </button>
                          {dpMoreOpen && (
                            <div style={{ position: "absolute", top: "calc(100% + 2px)", right: 0, zIndex: 120, background: UI.panelBg, border: `1px solid ${UI.panelBdr}`, borderRadius: 8, padding: "4px 0", minWidth: 150, boxShadow: "0 4px 16px rgba(0,0,0,0.4)" }}>
                              {overflowTabs.map(([id,label]) => (
                                <button key={id} onClick={() => { setDetailTab(id); setDpMoreOpen(false); }}
                                  style={{ display: "block", width: "100%", textAlign: "left", background: detailTab === id ? UI.rowBg : "none", border: "none", padding: "8px 14px", fontFamily: "'Helvetica Neue',Helvetica,Arial,sans-serif", fontSize: 11, letterSpacing: "1px", textTransform: "uppercase", color: detailTab === id ? "var(--accent)" : UI.text, cursor: "pointer" }}
                                  onMouseEnter={e => { if (detailTab !== id) e.currentTarget.style.background = UI.rowBg; }}
                                  onMouseLeave={e => { if (detailTab !== id) e.currentTarget.style.background = "none"; }}>
                                  {label}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      </>);
                    })()}
                  </div>

                  {/* ── INFO TAB ── */}
                  {detailTab === "info" && (
                  <div className="dp-body">
                      <div className="vc">
                        <div className="vc-lbl">Venue Info</div>
                        <div className="vc-name">{venue?.name || "Unknown Venue"}</div>
                        {venue ? (() => {
                          // Source-merged venue facts: the LIVE advance (promoter
                          // answer wins over TM pre-fill) → deal memo → venue CRM.
                          // A faint tag shows when a value comes from the advance
                          // or deal memo so CRM discrepancies stop being confusing.
                          const tmD3  = show?.advanceTmData || {};
                          const proD3 = show?.advanceData   || {};
                          const pick3 = (sec, f) => {
                            const pv = (proD3[sec] || {})[f];
                            if (pv !== undefined && pv !== null && pv !== "") return pv;
                            const tv = (tmD3[sec] || {})[f];
                            return (tv !== undefined && tv !== null && tv !== "") ? tv : null;
                          };
                          const dm3 = tmD3._dealMemo || {};
                          const join3 = (...parts) => parts.filter(Boolean).join(" · ") || null;
                          const yn3 = (v) => v === true ? "Yes" : v === false ? "No" : v;
                          // Each row: advance value → deal memo value → CRM value
                          const mergedRows = [
                            { k: "Address",     v: fmtVenueAddress(venue), src: null },
                            (() => { const a = pick3("schedule", "venue_access"); return { k: "Load-in", v: a || venue.loadIn, src: a ? "advance" : null }; })(),
                            (() => { const a = pick3("schedule", "doors_public"); return a ? { k: "Doors", v: a, src: "advance" } : null; })(),
                            (() => {
                              const a = join3(pick3("parking", "loading_zone"), pick3("parking", "bus_parking"), pick3("parking", "park_notes"));
                              return { k: "Parking", v: a || venue.parking, src: a ? "advance" : null };
                            })(),
                            (() => { const a = pick3("backline", "bl_notes"); return { k: "House Gear", v: a || venue.houseGear, src: a ? "advance" : null }; })(),
                            (() => { const v = dm3.ages || venue.ages; return v ? { k: "Ages", v, src: dm3.ages ? "deal memo" : null } : null; })(),
                            (() => { const v = dm3.capacity || venue.capacity; return v ? { k: "Capacity", v, src: dm3.capacity ? "deal memo" : null } : null; })(),
                            venue.gaSeated ? { k: "Setup", v: venue.gaSeated, src: null } : null,
                            (() => {
                              const a = join3(pick3("greenRoom", "rooms") && `${pick3("greenRoom", "rooms")} room(s)`, pick3("greenRoom", "gr_access"), yn3(pick3("greenRoom", "shower")) != null ? `Shower: ${yn3(pick3("greenRoom", "shower"))}` : null);
                              const v = a || venue.greenRooms;
                              return v ? { k: "Green Rooms", v, src: a ? "advance" : null } : null;
                            })(),
                            venue.bathrooms ? { k: "Bathrooms", v: venue.bathrooms, src: null } : null,
                            (() => {
                              const a = join3(pick3("merch", "merch_split_hard") && `Hard ${pick3("merch", "merch_split_hard")}`, pick3("merch", "merch_split_soft") && `Soft ${pick3("merch", "merch_split_soft")}`, pick3("merch", "merch_split"));
                              const v = a || venue.merchSplit;
                              return v ? { k: "Merch Split", v, src: a ? "advance" : null } : null;
                            })(),
                          ].filter(Boolean);
                          return (
                            <div className="vc-grid">
                              {mergedRows.map(r3 => (
                                <div key={r3.k}>
                                  <div className="vf-k">
                                    {r3.k}
                                    {r3.src && <span style={{ marginLeft: 5, fontSize: 11, fontWeight: 400, letterSpacing: 0.3, textTransform: "none", color: "var(--accent)", opacity: 0.8 }}>· {r3.src}</span>}
                                  </div>
                                  <div className="vf-v">{r3.v || "—"}</div>
                                </div>
                              ))}
                            </div>
                          );
                        })() : (
                          <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: "var(--text-vfaint)", marginTop: 8 }}>Venue data not found.</div>
                        )}
                      </div>

                      {/* Past Show History at this venue */}
                      {venue && (() => {
                        const _FF = "'Helvetica Neue', Helvetica, Arial, sans-serif";
                        const vh = venueHistoryCache[venue.id];
                        // Filter out the current show so TMs only see *previous* visits
                        const pastItems = (vh?.items || []).filter(item => item.show.id !== selS);
                        if (vh?.loading) return (
                          <div className="vc" style={{ marginBottom: 18 }}>
                            <div className="vc-lbl">Past Shows Here</div>
                            <div style={{ fontFamily: _FF, fontSize: 11, color: "var(--text-faint)", marginTop: 6 }}>Loading history…</div>
                          </div>
                        );
                        if (!pastItems.length) return null;
                        const recColor = (rec) => rec === 'Recommend' ? UI.green : rec === 'Avoid' ? 'var(--danger)' : UI.textMut;
                        return (
                          <div className="vc" style={{ marginBottom: 18 }}>
                            <div className="vc-lbl">Past Shows Here · {pastItems.length}</div>
                            {pastItems.slice(0, 6).map(({ show: ps, reports }) => {
                              const tmRpt   = reports.find(r => r.role_category === 'tm');
                              const ratings = reports.map(r => r.responses?.overall_rating).filter(Boolean);
                              const avg     = ratings.length ? (ratings.reduce((a, b) => a + Number(b), 0) / ratings.length).toFixed(1) : null;
                              const rec     = tmRpt?.responses?.venue_recommendation;
                              return (
                                <div key={ps.id} style={{ padding: "9px 10px", borderRadius: 8, background: UI.rowBg, border: `1px solid ${UI.panelBdr}`, marginBottom: 7 }}>
                                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: rec || tmRpt?.responses?.hospitality_notes || tmRpt?.responses?.venue_notes ? 5 : 0 }}>
                                    <span style={{ fontFamily: _FF, fontSize: 11, fontWeight: 600, color: UI.text }}>{fmtDate(ps.date)}</span>
                                    {avg && <span style={{ fontFamily: _FF, fontSize: 11, color: UI.amber }}>★ {avg}/5</span>}
                                    {rec && (
                                      <span style={{ fontFamily: _FF, fontSize: 11, fontWeight: 700, letterSpacing: 0.8, textTransform: "uppercase", color: recColor(rec),
                                        padding: "1px 6px", borderRadius: 4, background: rec === 'Recommend' ? `${UI.green}18` : rec === 'Avoid' ? '#e0555518' : `${UI.textMut}18`,
                                        border: `1px solid ${recColor(rec)}44` }}>
                                        {rec}
                                      </span>
                                    )}
                                    {/* Member ratings summary */}
                                    <span style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
                                      {reports.filter(r => r.role_category !== 'tm' && r.responses?.overall_rating).map(r => {
                                        const p = profiles.find(px => px.id === r.profile_id);
                                        return (
                                          <span key={r.id} style={{ fontFamily: _FF, fontSize: 11, color: UI.textFnt }}>
                                            {p?.name?.split(' ')[0] || (r.role_category === 'band' ? 'Band' : 'Crew')}: {r.responses.overall_rating}/5
                                          </span>
                                        );
                                      })}
                                    </span>
                                  </div>
                                  {tmRpt?.responses?.hospitality_notes && (
                                    <div style={{ fontFamily: _FF, fontSize: 11, color: UI.textMut, fontStyle: "italic", marginBottom: 2 }}>
                                      Hospitality: {tmRpt.responses.hospitality_notes}
                                    </div>
                                  )}
                                  {tmRpt?.responses?.venue_notes && (
                                    <div style={{ fontFamily: _FF, fontSize: 11, color: UI.textFnt }}>{tmRpt.responses.venue_notes}</div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        );
                      })()}

                      {/* Lodging */}
                      <div className="vc" style={{ marginBottom: 18 }}>
                        <div className="vc-lbl" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                          <span>Lodging</span>
                          <button className="sm-btn" style={{ fontSize: 11, padding: "2px 8px" }}
                            onClick={() => setMShow({ mode: "edit", showId: show.id })}>Edit</button>
                        </div>
                        {show.lodging?.name ? (
                          <div className="vc-grid">
                            <div><div className="vf-k">Hotel</div><div className="vf-v">{show.lodging.name}</div></div>
                            {show.lodging.address && (
                              <div><div className="vf-k">Address</div>
                                <div className="vf-v">
                                  <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(show.lodging.address)}`}
                                    target="_blank" rel="noreferrer"
                                    style={{ color: "var(--accent)", textDecoration: "none", fontSize: 11 }}>
                                    {show.lodging.address} ↗
                                  </a>
                                </div>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: "var(--text-vfaint)", marginTop: 6 }}>
                            No lodging set — <span style={{ color: "var(--accent)", cursor: "pointer" }} onClick={() => setMShow({ mode: "edit", showId: show.id })}>add hotel</span>
                          </div>
                        )}
                      </div>

                      {/* Contacts */}
                      <div className="vc" style={{ marginBottom: 18 }}>
                        <div className="vc-lbl" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                          <span>Contacts</span>
                          {!addingShowContact && !addingShowContactNew && (
                            <div style={{ display: "flex", gap: 5 }}>
                              <button className="sm-btn" style={{ fontSize: 11, padding: "2px 8px" }} onClick={() => { setAddingShowContact(true); setNewContactPick({ contactId: "", role: "" }); }}>+ Link</button>
                              <button className="sm-btn" style={{ fontSize: 11, padding: "2px 8px" }} onClick={() => { setAddingShowContactNew(true); setNewContactForm({ name: "", email: "", phone: "", role: "Promoter" }); }}>+ New</button>
                            </div>
                          )}
                        </div>
                        {/* Derived contacts — deal memo promoter + advance venue crew.
                            Read-only; sourced so the card is never empty when the data
                            exists elsewhere on the show. */}
                        {(() => {
                          const FFc = "'Helvetica Neue', Helvetica, Arial, sans-serif";
                          const st = show?.settlement || {};
                          const tmVc  = (show?.advanceTmData || {}).venueCrew || {};
                          const proVc = (show?.advanceData   || {}).venueCrew || {};
                          const vcPick = (f) => {
                            const pv = proVc[f], tv = tmVc[f];
                            const has = (c) => c && typeof c === "object" && (c.name || c.phone || c.email);
                            return has(pv) ? pv : has(tv) ? tv : null;
                          };
                          const raw = [];
                          const promoName  = st.promoterName  || venue?.promoterName;
                          const promoEmail = st.promoterEmail || venue?.promoterEmail;
                          const promoPhone = st.promoterPhone || venue?.promoterPhone;
                          if (promoName || promoEmail || promoPhone) {
                            raw.push({ role: "Promoter", name: promoName, email: promoEmail, phone: promoPhone, src: st.promoterName || st.promoterEmail ? "deal memo" : "venue" });
                          }
                          const VC_ROLES = [["vc_dos", "Day of Show"], ["vc_foh", "FOH"], ["vc_mon", "Monitors"], ["vc_ld", "Lighting"], ["vc_pm", "Production Mgr"], ["vc_sm", "Stage Mgr"], ["vc_sec", "Security"]];
                          for (const [fid, label] of VC_ROLES) {
                            const c = vcPick(fid);
                            if (c) raw.push({ role: label, name: c.name, email: c.email, phone: c.phone, src: "advance" });
                          }
                          // Dedupe the same person across roles (match by email, then
                          // phone digits, then name) — one row, roles combined.
                          const rows = [];
                          const keyed = {};
                          for (const r4 of raw) {
                            const key = (r4.email && r4.email.trim().toLowerCase()) ||
                                        (r4.phone && String(r4.phone).replace(/\D/g, "").replace(/^1(?=\d{10}$)/, "")) ||
                                        (r4.name && r4.name.trim().toLowerCase());
                            if (key && keyed[key]) {
                              const ex = keyed[key];
                              if (!ex.roles.includes(r4.role)) ex.roles.push(r4.role);
                              ex.name  = ex.name  || r4.name;
                              ex.email = ex.email || r4.email;
                              ex.phone = ex.phone || r4.phone;
                              if (!ex.srcs.includes(r4.src)) ex.srcs.push(r4.src);
                              continue;
                            }
                            const row = { roles: [r4.role], name: r4.name, email: r4.email, phone: r4.phone, srcs: [r4.src] };
                            if (key) keyed[key] = row;
                            rows.push(row);
                          }
                          if (rows.length === 0) return null;
                          // Source lives in the tooltip, not inline — the row already
                          // carries role + name + email + phone; tags were noise here.
                          return rows.map((r4, i4) => (
                            <div key={`drv_${i4}`} title={`From ${r4.srcs.join(" + ")}`} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 8px", borderRadius: 6, background: UI.rowBg, border: `1px solid ${UI.subBdr}`, marginBottom: 5 }}>
                              <span style={{ fontFamily: FFc, fontSize: 11, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase", color: UI.textMut, flexShrink: 0, minWidth: 92 }}>{r4.roles.join(" · ")}</span>
                              <span style={{ fontFamily: FFc, fontSize: 12, color: UI.text, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {[r4.name, r4.email, fmtPhone(r4.phone)].filter(Boolean).join(" · ") || "—"}
                              </span>
                              <span style={{ flex: 1 }} />
                              {r4.email && <a href={`mailto:${r4.email}`} style={{ fontFamily: FFc, fontSize: 11, color: UI.textMut, flexShrink: 0 }}>Email</a>}
                            </div>
                          ));
                        })()}
                        {/* Venue-level contacts (cascaded, read-only) */}
                        {(venue?.linkedContacts || []).map((lc, i) => {
                          const c = data.contacts.find(x => x.id === lc.contactId);
                          if (!c) return null;
                          return (
                            <div key={"v" + i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "7px 10px", background: UI.rowBg2, border: `1px solid ${UI.panelBdr}`, borderRadius: 8, marginBottom: 5 }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                                <span style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 12, fontWeight: 600, color: UI.text }}>{c.name}</span>
                                {lc.role && <span style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, letterSpacing: "0.8px", color: UI.amber, background: UI.amdBg, padding: "1px 6px", borderRadius: 4 }}>{lc.role.toUpperCase()}</span>}
                                <span style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, letterSpacing: "1px", color: UI.textFnt }}>FROM VENUE</span>
                                {c.email && <a href={`mailto:${c.email}`} style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: UI.textMut, textDecoration: "none" }}>{c.email}</a>}
                                {c.phone && <span style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: UI.textMut }}>{fmtPhone(c.phone)}</span>}
                              </div>
                            </div>
                          );
                        })}
                        {/* Show-level contacts (editable) */}
                        {(show.linkedContacts || []).map((lc, i) => {
                          const c = data.contacts.find(x => x.id === lc.contactId);
                          if (!c) return null;
                          return (
                            <div key={"s" + i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "7px 10px", background: UI.rowBg, border: `1px solid ${UI.panelBdr}`, borderRadius: 8, marginBottom: 5 }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                                <span style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 12, fontWeight: 600, color: UI.text }}>{c.name}</span>
                                {lc.role && <span style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, letterSpacing: "0.8px", color: UI.amber, background: UI.amdBg, padding: "1px 6px", borderRadius: 4 }}>{lc.role.toUpperCase()}</span>}
                                {c.email && <a href={`mailto:${c.email}`} style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: UI.textMut, textDecoration: "none" }}>{c.email}</a>}
                                {c.phone && <span style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: UI.textMut }}>{fmtPhone(c.phone)}</span>}
                              </div>
                              <button onClick={() => updShow("linkedContacts", (show.linkedContacts || []).filter((_, j) => j !== i))}
                                style={{ background: "none", border: "none", color: UI.textMut, cursor: "pointer", fontSize: 14, padding: "0 4px" }}>×</button>
                            </div>
                          );
                        })}
                        {/* Add contact picker */}
                        {addingShowContact && (
                          <div style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 6, flexWrap: "wrap" }}>
                            <select style={{ flex: 2, minWidth: 160, background: UI.rowBg, border: `1px solid ${UI.panelBdr}`, color: UI.text, borderRadius: 4, padding: "5px 8px", fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 12 }}
                              value={newContactPick.contactId} onChange={e => setNewContactPick(p => ({ ...p, contactId: e.target.value }))}>
                              <option value="">— Pick a contact —</option>
                              {data.contacts.map(c => <option key={c.id} value={c.id}>{c.name}{c.company ? ` · ${c.company}` : ""}</option>)}
                            </select>
                            <select style={{ flex: 1, minWidth: 130, background: UI.rowBg, border: `1px solid ${UI.panelBdr}`, color: UI.text, borderRadius: 4, padding: "5px 8px", fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 12 }}
                              value={newContactPick.role} onChange={e => setNewContactPick(p => ({ ...p, role: e.target.value }))}>
                              {CONTACT_ROLES.map(r => <option key={r} value={r}>{r || "— Role —"}</option>)}
                            </select>
                            <button className="btn-p" style={{ padding: "5px 12px", fontSize: 11, flexShrink: 0 }}
                              onClick={() => {
                                if (!newContactPick.contactId) return;
                                updShow("linkedContacts", [...(show.linkedContacts || []), { ...newContactPick }]);
                                setAddingShowContact(false);
                                setNewContactPick({ contactId: "", role: "" });
                              }}>Add</button>
                            <button className="btn-g" style={{ padding: "5px 10px", fontSize: 11, flexShrink: 0, display: "inline-flex", alignItems: "center" }} onClick={() => setAddingShowContact(false)}><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></button>
                          </div>
                        )}
                        {/* Inline new contact form */}
                        {addingShowContactNew && (
                          <div style={{ background: UI.rowBg, border: `1px solid ${UI.panelBdr}`, borderRadius: 8, padding: "10px 12px", marginTop: 6 }}>
                            <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, letterSpacing: "0.8px", color: UI.amber, marginBottom: 8 }}>NEW CONTACT</div>
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginBottom: 6 }}>
                              <div>
                                <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: UI.textMut, marginBottom: 3 }}>Name *</div>
                                <input style={{ width: "100%", boxSizing: "border-box", background: UI.panelAlt, border: `1px solid ${UI.panelBdr}`, color: UI.text, borderRadius: 4, padding: "5px 8px", fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 12 }}
                                  value={newContactForm.name} onChange={e => setNewContactForm(p => ({ ...p, name: e.target.value }))} placeholder="Full name" autoFocus />
                              </div>
                              <div>
                                <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: UI.textMut, marginBottom: 3 }}>Role</div>
                                <select style={{ width: "100%", background: UI.rowBg, border: `1px solid ${UI.panelBdr}`, color: UI.text, borderRadius: 4, padding: "5px 8px", fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 12 }}
                                  value={newContactForm.role} onChange={e => setNewContactForm(p => ({ ...p, role: e.target.value }))}>
                                  {CONTACT_ROLES.filter(r => r).map(r => <option key={r} value={r}>{r}</option>)}
                                </select>
                              </div>
                            </div>
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginBottom: 8 }}>
                              <div>
                                <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: UI.textMut, marginBottom: 3 }}>Email</div>
                                <input type="email" style={{ width: "100%", boxSizing: "border-box", background: UI.panelAlt, border: `1px solid ${UI.panelBdr}`, color: UI.text, borderRadius: 4, padding: "5px 8px", fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 12 }}
                                  value={newContactForm.email} onChange={e => setNewContactForm(p => ({ ...p, email: e.target.value }))} placeholder="email@example.com" />
                              </div>
                              <div>
                                <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: UI.textMut, marginBottom: 3 }}>Phone</div>
                                <input type="tel" style={{ width: "100%", boxSizing: "border-box", background: UI.panelAlt, border: `1px solid ${UI.panelBdr}`, color: UI.text, borderRadius: 4, padding: "5px 8px", fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 12 }}
                                  value={newContactForm.phone} onChange={e => setNewContactForm(p => ({ ...p, phone: e.target.value }))} placeholder="Phone number" />
                              </div>
                            </div>
                            <div style={{ display: "flex", gap: 6 }}>
                              <button className="btn-p" style={{ padding: "5px 14px", fontSize: 11 }} disabled={!newContactForm.name?.trim()}
                                onClick={() => {
                                  const cid = "c" + uid();
                                  const newC = { id: cid, name: newContactForm.name.trim(), email: newContactForm.email.trim(), phone: newContactForm.phone.trim(), role: newContactForm.role, company: "", notes: "" };
                                  setData(p => ({ ...p, contacts: [...(p.contacts || []), newC] }));
                                  supabase.from("contacts").insert({ id: cid, user_id: user?.id, team_id: teamId, name: newC.name, email: newC.email, phone: newC.phone, role: newC.role, company: "", notes: "" });
                                  updShow("linkedContacts", [...(show.linkedContacts || []), { contactId: cid, role: newContactForm.role }]);
                                  setAddingShowContactNew(false);
                                }}>Save & Link</button>
                              <button className="btn-g" style={{ padding: "5px 10px", fontSize: 11, display: "inline-flex", alignItems: "center" }} onClick={() => setAddingShowContactNew(false)}><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></button>
                            </div>
                          </div>
                        )}
                        {!(venue?.linkedContacts?.length) && !(show.linkedContacts?.length) && !addingShowContact && !addingShowContactNew &&
                          !(show?.settlement?.promoterName || show?.settlement?.promoterEmail || venue?.promoterName || venue?.promoterEmail) && (
                          <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: UI.textFnt, marginTop: 4 }}>
                            No contacts linked — click + Link to add from CRM, or + New to create one
                          </div>
                        )}
                      </div>

                      {/* ── City Intel — Who You Know ── */}
                      {(() => {
                        const venueCity = venue?.city?.trim();
                        if (!venueCity) return null;
                        const cityLower = venueCity.toLowerCase();

                        // Contacts whose city matches
                        const contactsInCity = (data.contacts || []).filter(c => {
                          if (!c.city) return false;
                          const cCity = c.city.toLowerCase().trim();
                          return cCity.includes(cityLower) || cityLower.includes(cCity);
                        });

                        // Network profiles routing through this city ±14 days of show
                        const showDateMs = show.date ? new Date(show.date + "T12:00:00").getTime() : null;
                        const networkInCity = networkLoaded ? networkProfiles.filter(p =>
                          (p.tour_routing || []).some(r => {
                            if (!r.city) return false;
                            const rCity = r.city.toLowerCase().trim();
                            if (!rCity.includes(cityLower) && !cityLower.includes(rCity)) return false;
                            if (!showDateMs || !r.date) return true;
                            return Math.abs(new Date(r.date + "T12:00:00").getTime() - showDateMs) <= 14 * 864e5;
                          })
                        ) : [];

                        const totalCount = contactsInCity.length + networkInCity.length;

                        return (
                          <div className="vc" style={{ marginBottom: 18 }}>
                            <div className="vc-lbl" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                              <span>Who You Know · {[venueCity, venue?.addressState].filter(Boolean).join(", ")}</span>
                              {totalCount > 0 && (
                                <span style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, background: `${UI.amber}22`, color: UI.amber, border: `1px solid ${UI.amber}44`, borderRadius: 8, padding: "1px 8px", fontWeight: 700, letterSpacing: 0.5 }}>{totalCount}</span>
                              )}
                            </div>

                            {/* Contacts from CRM */}
                            {contactsInCity.length > 0 && (
                              <div style={{ marginBottom: networkInCity.length > 0 || !networkLoaded ? 10 : 0 }}>
                                <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, letterSpacing: 1, color: UI.textFnt, fontWeight: 700, marginBottom: 5 }}>FROM YOUR CONTACTS</div>
                                {contactsInCity.map(c => (
                                  <div key={c.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "6px 10px", background: UI.rowBg2, border: `1px solid ${UI.panelBdr}`, borderRadius: 8, marginBottom: 4 }}>
                                    <div style={{ minWidth: 0, flex: 1 }}>
                                      <span style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 12, fontWeight: 600, color: UI.text }}>{c.name}</span>
                                      {c.company && <span style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: UI.textMut, marginLeft: 7 }}>{c.company}</span>}
                                    </div>
                                    <div style={{ display: "flex", gap: 5, flexShrink: 0 }}>
                                      {c.email && <a href={`mailto:${c.email}`} style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, padding: "3px 8px", borderRadius: 4, background: `${UI.amber}18`, color: UI.amber, border: `1px solid ${UI.amber}33`, textDecoration: "none" }}>Email</a>}
                                      {c.phone && <a href={`tel:${c.phone.replace(/\s/g, "")}`} style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, padding: "3px 8px", borderRadius: 4, background: "transparent", color: UI.textMut, border: `1px solid ${UI.panelBdr}`, textDecoration: "none" }}>Call</a>}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* Network profiles routing through */}
                            {networkLoaded && networkInCity.length > 0 && (
                              <div>
                                <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, letterSpacing: 1, color: UI.textFnt, fontWeight: 700, marginBottom: 5 }}>ROUTING THROUGH · ±2 WEEKS</div>
                                {networkInCity.map(p => {
                                  const isConnected = connections.has(p.id);
                                  const catColor = PROFILE_ROLE_COLORS?.[p.role_category] || "#6a8faf";
                                  const initials = (p.preferred_name || p.name || "?").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
                                  return (
                                    <div key={p.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", background: UI.rowBg2, border: `1px solid ${UI.panelBdr}`, borderRadius: 8, marginBottom: 4 }}>
                                      <div style={{ width: 28, height: 28, borderRadius: "50%", background: `${catColor}30`, border: `1.5px solid ${catColor}55`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, overflow: "hidden" }}>
                                        {p.photo_url
                                          ? <img src={p.photo_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                          : <span style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, fontWeight: 700, color: catColor }}>{initials}</span>}
                                      </div>
                                      <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 12, fontWeight: 600, color: UI.text }}>{p.preferred_name || p.name}</div>
                                        {(p.role || p.routing_tour_name) && (
                                          <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: UI.textMut }}>{[p.role, p.routing_tour_name].filter(Boolean).join(" · ")}</div>
                                        )}
                                      </div>
                                      <div style={{ display: "flex", gap: 5, alignItems: "center", flexShrink: 0 }}>
                                        {isConnected && <span style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: UI.green, border: `1px solid ${UI.green}44`, borderRadius: 4, padding: "1px 5px", letterSpacing: 0.5 }}>CONNECTED</span>}
                                        {p.email && <a href={`mailto:${p.email}`} style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, padding: "3px 8px", borderRadius: 4, background: `${UI.amber}18`, color: UI.amber, border: `1px solid ${UI.amber}33`, textDecoration: "none" }}>Email</a>}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}

                            {/* Prompt to load network if not yet loaded */}
                            {!networkLoaded && (
                              <div style={{ marginTop: contactsInCity.length > 0 ? 0 : 2 }}>
                                <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, letterSpacing: 1, color: UI.textFnt, fontWeight: 700, marginBottom: 5 }}>ROUTING THROUGH · ±2 WEEKS</div>
                                <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: UI.textFnt }}>
                                  <span style={{ color: UI.amber, cursor: "pointer" }} onClick={loadNetworkProfiles}>Load network</span> to see who's routing through {venueCity}.
                                </div>
                              </div>
                            )}

                            {/* Empty state */}
                            {contactsInCity.length === 0 && networkLoaded && networkInCity.length === 0 && (
                              <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: UI.textFnt, marginTop: 2 }}>No contacts or connections routing through {venueCity}.</div>
                            )}
                            {contactsInCity.length === 0 && !networkLoaded && (
                              <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: UI.textFnt, marginTop: 2 }}>No CRM contacts in {venueCity}. <span style={{ color: UI.amber, cursor: "pointer" }} onClick={loadNetworkProfiles}>Load network</span> to check connections.</div>
                            )}
                          </div>
                        );
                      })()}

                      {/* Show Notes */}
                      <div className="vc" style={{ marginBottom: 0 }}>
                        <div className="vc-lbl">Show Notes</div>
                        <textarea
                          className="show-notes-ta"
                          rows={4}
                          placeholder="General notes for this show..."
                          value={show.notes || ""}
                          onChange={e => updShow("notes", e.target.value)}
                        />
                      </div>

                  </div>
                  )} {/* end info tab */}

                  {/* ── SETLIST TAB (SL-1) ── */}
                  {detailTab === "setlist" && (() => {
                    const setsForShow = showSetlists
                      .filter(s => s.show_id === selS)
                      .sort((a, b) => (a.set_number || 0) - (b.set_number || 0));
                    const setNumbers = setsForShow.map(s => s.set_number);
                    const currentSetNum = (activeSetlistSetNum && setNumbers.includes(activeSetlistSetNum))
                      ? activeSetlistSetNum
                      : (setNumbers[0] || 1);
                    const currentSet = setsForShow.find(s => s.set_number === currentSetNum);
                    const songs = currentSet?.songs || [];

                    const writeSongs = (newSongs) => {
                      saveShowSetlist({ showId: selS, tourId: selT, setNumber: currentSetNum, songs: newSongs });
                    };
                    const addSet = () => {
                      const nextNum = setNumbers.length ? Math.max(...setNumbers) + 1 : 1;
                      saveShowSetlist({ showId: selS, tourId: selT, setNumber: nextNum, songs: [] });
                      setActiveSetlistSetNum(nextNum);
                    };
                    const removeSet = () => {
                      if (!currentSet?.id) return;
                      if (!window.confirm(`Delete Set ${currentSetNum}? This cannot be undone.`)) return;
                      deleteShowSetlist(currentSet.id);
                      const remaining = setNumbers.filter(n => n !== currentSetNum);
                      setActiveSetlistSetNum(remaining[0] || null);
                    };
                    const addSong = () => writeSongs([...songs, { id: uid(), title: "", song_key: "", tempo: null, tuning: "", notes: "" }]);
                    const updateSong = (idx, patch) => writeSongs(songs.map((s, i) => i === idx ? { ...s, ...patch } : s));
                    const removeSong = (idx) => writeSongs(songs.filter((_, i) => i !== idx));
                    const moveSong = (from, to) => {
                      if (to < 0 || to >= songs.length) return;
                      const arr = [...songs];
                      const [moved] = arr.splice(from, 1);
                      arr.splice(to, 0, moved);
                      writeSongs(arr);
                    };

                    const showDateStr = show?.date
                      ? new Date(show.date + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
                      : '';
                    const exportUrl = (format) => show?.advanceToken
                      ? `/api/setlist-export?token=${encodeURIComponent(show.advanceToken)}&format=${format}`
                      : null;
                    const canExport = !!show?.advanceToken && setsForShow.length > 0;
                    const FFhel = "'Helvetica Neue', Helvetica, Arial, sans-serif";
                    const arrowBtn = (disabled) => ({
                      width: 22, height: 22, border: "1px solid var(--field-border)", background: disabled ? "var(--panel-alt)" : "var(--panel)",
                      cursor: disabled ? "not-allowed" : "pointer", borderRadius: 4, fontSize: 11,
                      color: disabled ? "var(--text-faint)" : "var(--text-mid)", display: "flex", alignItems: "center", justifyContent: "center",
                      padding: 0,
                    });

                    // SL-2: Template helpers (computed at top so no nested IIFE is needed in the menu JSX)
                    const artistId = artist?.id;
                    const artistTemplates = artistId
                      ? setlistTemplates.filter(t => t.artist_id === artistId)
                      : [];
                    const canSaveTemplate = setsForShow.length > 0
                      && setsForShow.some(s => (s.songs || []).length > 0);
                    const submitSaveTemplate = () => {
                      const name = templateSaveName.trim();
                      if (!name || !artistId) return;
                      saveSetlistTemplate({ name, artistId, showId: selS });
                      setTemplateSaveName("");
                    };
                    const submitRenameTemplate = (id) => {
                      const name = templateRenameName.trim();
                      if (!name) return;
                      renameSetlistTemplate(id, name);
                      setTemplateRenameId(null);
                      setTemplateRenameName("");
                    };
                    const handleApplyTemplate = (template) => {
                      if (setsForShow.length === 0) {
                        applySetlistTemplate({ template, showId: selS, tourId: selT, mode: "replace" });
                        setTemplatesMenuOpen(false);
                      } else {
                        setTemplateApplyConfirm({ template });
                      }
                    };
                    const closeTemplatesMenu = () => {
                      setTemplatesMenuOpen(false);
                      setTemplateApplyConfirm(null);
                      setTemplateSaveName("");
                      setTemplateRenameId(null);
                      setTemplateRenameName("");
                    };
                    const iconBtn = {
                      width: 24, height: 24, padding: 0,
                      border: "none", background: "transparent",
                      borderRadius: 4, cursor: "pointer",
                      display: "inline-flex", alignItems: "center", justifyContent: "center",
                      color: "var(--text-mut)", flexShrink: 0,
                    };

                    return (
                      <div className="dp-body" style={{ padding: 16 }}>
                        {/* Header */}
                        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 16, gap: 12 }}>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontFamily: FFhel, fontSize: 11, letterSpacing: 1.8, color: "var(--text-mut)", marginBottom: 4 }}>SETLIST</div>
                            <div style={{ fontFamily: FFhel, fontSize: 14, fontWeight: 700, color: "var(--text-vfaint)", lineHeight: 1.4 }}>
                              {artist?.name || ""}{showDateStr ? ` · ${showDateStr}` : ""}{venue?.name ? ` · ${venue.name}` : ""}
                            </div>
                          </div>
                          <div style={{ display: "flex", gap: 8, flexShrink: 0, alignItems: "flex-start" }}>
                            {/* Bulk import a whole song list */}
                            <button
                              onClick={() => setSetlistImportOpen(true)}
                              title="Paste or drop a whole setlist at once"
                              style={{
                                fontFamily: FFhel, fontSize: 11, padding: "7px 12px",
                                border: "1px solid var(--panel-border)", borderRadius: 4,
                                background: "var(--panel)", color: "var(--text)",
                                cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6,
                              }}>
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                                <polyline points="17 8 12 3 7 8"/>
                                <line x1="12" y1="3" x2="12" y2="15"/>
                              </svg>
                              Import
                            </button>
                            {/* SL-2: Templates ▾ dropdown (per-artist) */}
                            <div style={{ position: "relative" }}>
                              <button
                                onClick={() => setTemplatesMenuOpen(o => !o)}
                                title={artistId ? "Saved setlists for this artist" : "Select an artist to access templates"}
                                disabled={!artistId}
                                style={{
                                  fontFamily: FFhel, fontSize: 11, padding: "7px 12px",
                                  border: "1px solid var(--panel-border)", borderRadius: 4,
                                  background: !artistId ? "var(--panel-alt)" : (templatesMenuOpen ? "var(--row-hover)" : "var(--panel)"),
                                  color: !artistId ? "var(--text-faint)" : "var(--text)",
                                  cursor: !artistId ? "not-allowed" : "pointer",
                                  display: "inline-flex", alignItems: "center", gap: 6,
                                }}>
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                                  <polyline points="14 2 14 8 20 8"/>
                                  <line x1="9" y1="13" x2="15" y2="13"/>
                                  <line x1="9" y1="17" x2="15" y2="17"/>
                                </svg>
                                Templates
                                <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: 2 }}>
                                  <polyline points="6 9 12 15 18 9"/>
                                </svg>
                              </button>
                              {templatesMenuOpen && artistId && (
                                <>
                                  {/* Backdrop */}
                                  <div onClick={closeTemplatesMenu} style={{ position: "fixed", inset: 0, zIndex: 9 }}/>
                                  {/* Menu */}
                                  <div style={{
                                    position: "absolute", right: 0, top: "calc(100% + 4px)",
                                    zIndex: 10, minWidth: 300, maxWidth: 380,
                                    background: "var(--panel)", border: "1px solid var(--panel-border)",
                                    borderRadius: 8, boxShadow: "0 6px 24px rgba(0,0,0,0.30)",
                                    padding: 4, overflow: "hidden",
                                  }}>
                                    {templateApplyConfirm ? (
                                      /* Apply confirm view (replaces menu body) */
                                      <div style={{ padding: "10px 12px" }}>
                                        <div style={{ fontFamily: FFhel, fontSize: 12, color: "var(--text)", marginBottom: 4 }}>
                                          Apply <strong>{templateApplyConfirm.template.name}</strong>?
                                        </div>
                                        <div style={{ fontFamily: FFhel, fontSize: 11, color: "var(--text-mut)", marginBottom: 12, lineHeight: 1.4 }}>
                                          This show already has {setsForShow.length} set{setsForShow.length === 1 ? "" : "s"}.
                                        </div>
                                        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                          <button onClick={() => {
                                            applySetlistTemplate({ template: templateApplyConfirm.template, showId: selS, tourId: selT, mode: "replace" });
                                            setTemplateApplyConfirm(null);
                                            setTemplatesMenuOpen(false);
                                          }} style={{
                                            padding: "8px 10px", fontFamily: FFhel, fontSize: 12,
                                            border: "1px solid var(--accent)", borderRadius: 4,
                                            background: "var(--accent)", color: "var(--accent-fg)",
                                            cursor: "pointer", fontWeight: 600, textAlign: "left",
                                          }}>
                                            Replace current setlist
                                          </button>
                                          <button onClick={() => {
                                            applySetlistTemplate({ template: templateApplyConfirm.template, showId: selS, tourId: selT, mode: "append" });
                                            setTemplateApplyConfirm(null);
                                            setTemplatesMenuOpen(false);
                                          }} style={{
                                            padding: "8px 10px", fontFamily: FFhel, fontSize: 12,
                                            border: "1px solid var(--panel-border)", borderRadius: 4,
                                            background: "var(--panel-alt)", color: "var(--text)",
                                            cursor: "pointer", fontWeight: 600, textAlign: "left",
                                          }}>
                                            Add as new sets after Set {Math.max(...setsForShow.map(s => s.set_number || 0))}
                                          </button>
                                          <button onClick={() => setTemplateApplyConfirm(null)} style={{
                                            padding: "6px 10px", fontFamily: FFhel, fontSize: 11,
                                            border: "none", background: "transparent",
                                            color: "var(--text-mut)", cursor: "pointer", textAlign: "left",
                                          }}>
                                            Cancel
                                          </button>
                                        </div>
                                      </div>
                                    ) : (
                                      <>
                                        {/* Save current as new template */}
                                        {canSaveTemplate && (
                                          <div style={{ padding: 4 }}>
                                            <div style={{ display: "flex", gap: 4 }}>
                                              <input
                                                value={templateSaveName}
                                                onChange={e => setTemplateSaveName(e.target.value)}
                                                onKeyDown={e => { if (e.key === 'Enter') submitSaveTemplate(); }}
                                                placeholder="Save current as new template…"
                                                style={{
                                                  flex: 1, padding: "7px 9px", fontSize: 12, fontFamily: FFhel,
                                                  border: "1px solid var(--field-border)", borderRadius: 4, outline: "none",
                                                  minWidth: 0, background: "var(--panel-alt)", color: "var(--text)",
                                                }}/>
                                              <button onClick={submitSaveTemplate} disabled={!templateSaveName.trim()} style={{
                                                padding: "7px 12px", fontSize: 11, fontFamily: FFhel,
                                                border: "1px solid", borderRadius: 4,
                                                background: templateSaveName.trim() ? "var(--accent)" : "var(--panel-alt)",
                                                borderColor: templateSaveName.trim() ? "var(--accent)" : "var(--panel-border)",
                                                color: templateSaveName.trim() ? "var(--accent-fg)" : "var(--text-faint)",
                                                cursor: templateSaveName.trim() ? "pointer" : "not-allowed",
                                                fontWeight: 600, flexShrink: 0,
                                              }}>Save</button>
                                            </div>
                                          </div>
                                        )}

                                        {/* Divider + section heading */}
                                        <div style={{ height: 1, background: "var(--panel-border)", margin: "4px 0" }}/>
                                        <div style={{
                                          padding: "6px 10px 4px", fontFamily: FFhel, fontSize: 11,
                                          letterSpacing: 1.5, color: "var(--text-mid)", textTransform: "uppercase",
                                        }}>
                                          Templates{artist?.name ? ` for ${artist.name}` : ""}
                                        </div>

                                        {/* Template list */}
                                        {artistTemplates.length === 0 && (
                                          <div style={{ padding: "8px 10px 12px", fontFamily: FFhel, fontSize: 11, color: "var(--text-mut)", fontStyle: "italic" }}>
                                            No templates yet{canSaveTemplate ? " — save the current setlist above." : "."}
                                          </div>
                                        )}
                                        {artistTemplates.map(t => {
                                          const isRenaming = templateRenameId === t.id;
                                          const setCount  = Array.isArray(t.sets) ? t.sets.length : 0;
                                          const songCount = (Array.isArray(t.sets) ? t.sets : []).reduce((sum, s) => sum + ((s.songs || []).length), 0);
                                          return (
                                            <div key={t.id} style={{ display: "flex", alignItems: "center", gap: 2, padding: "2px 4px 2px 10px" }}>
                                              {isRenaming ? (
                                                <>
                                                  <input
                                                    value={templateRenameName}
                                                    onChange={e => setTemplateRenameName(e.target.value)}
                                                    onKeyDown={e => {
                                                      if (e.key === 'Enter') submitRenameTemplate(t.id);
                                                      if (e.key === 'Escape') { setTemplateRenameId(null); setTemplateRenameName(""); }
                                                    }}
                                                    autoFocus
                                                    style={{
                                                      flex: 1, padding: "5px 7px", fontSize: 12, fontFamily: FFhel,
                                                      border: "1px solid var(--field-border)", borderRadius: 4, outline: "none",
                                                      minWidth: 0, background: "var(--panel-alt)", color: "var(--text)",
                                                    }}/>
                                                  <button onClick={() => submitRenameTemplate(t.id)} style={iconBtn} title="Save">
                                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                                                  </button>
                                                </>
                                              ) : (
                                                <>
                                                  <button onClick={() => handleApplyTemplate(t)} style={{
                                                    flex: 1, padding: "6px 6px", textAlign: "left",
                                                    border: "none", background: "transparent", cursor: "pointer",
                                                    borderRadius: 4, display: "flex", flexDirection: "column", alignItems: "flex-start",
                                                    minWidth: 0,
                                                  }}
                                                    onMouseEnter={e => e.currentTarget.style.background = "var(--row-hover)"}
                                                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                                                    <span style={{ fontFamily: FFhel, fontSize: 12, color: "var(--text)", fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "100%" }}>{t.name}</span>
                                                    <span style={{ fontFamily: FFhel, fontSize: 11, color: "var(--text-mut)", marginTop: 1 }}>
                                                      {setCount} set{setCount === 1 ? "" : "s"} · {songCount} song{songCount === 1 ? "" : "s"}
                                                    </span>
                                                  </button>
                                                  <button onClick={() => { setTemplateRenameId(t.id); setTemplateRenameName(t.name); }} style={iconBtn} title="Rename">
                                                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                                                  </button>
                                                  <button onClick={() => {
                                                    if (window.confirm(`Delete template "${t.name}"?`)) deleteSetlistTemplate(t.id);
                                                  }} style={{ ...iconBtn, color: "var(--danger)" }} title="Delete">
                                                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                                                  </button>
                                                </>
                                              )}
                                            </div>
                                          );
                                        })}
                                      </>
                                    )}
                                  </div>
                                </>
                              )}
                            </div>
                            {/* Export ▾ dropdown */}
                            <div style={{ position: "relative" }}>
                            <button
                              disabled={!canExport}
                              onClick={() => setExportMenuOpen(o => !o)}
                              title={!canExport ? "Generate an advance token (Advance tab) and add at least one set to enable export" : "Choose a format"}
                              style={{
                                fontFamily: FFhel, fontSize: 11, padding: "7px 12px",
                                border: "1px solid var(--panel-border)", borderRadius: 4,
                                background: !canExport ? "var(--panel-alt)" : (exportMenuOpen ? "var(--row-hover)" : "var(--panel)"),
                                color: !canExport ? "var(--text-faint)" : "var(--text)",
                                cursor: !canExport ? "not-allowed" : "pointer",
                                display: "inline-flex", alignItems: "center", gap: 6,
                              }}>
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                                <polyline points="7 10 12 15 17 10"/>
                                <line x1="12" y1="15" x2="12" y2="3"/>
                              </svg>
                              Export
                              <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: 2 }}>
                                <polyline points="6 9 12 15 18 9"/>
                              </svg>
                            </button>
                            {exportMenuOpen && canExport && (
                              <>
                                {/* Backdrop — click anywhere outside the menu to close */}
                                <div onClick={() => setExportMenuOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 9 }}/>
                                {/* Menu */}
                                <div style={{
                                  position: "absolute", right: 0, top: "calc(100% + 4px)",
                                  zIndex: 10, minWidth: 220,
                                  background: "var(--panel)", border: "1px solid var(--panel-border)",
                                  borderRadius: 8, boxShadow: "0 6px 24px rgba(0,0,0,0.30)",
                                  padding: 4, overflow: "hidden",
                                }}>
                                  {[
                                    { fmt: "pdf",  label: "PDF (.pdf)",   note: "Print-ready, opens in browser" },
                                    { fmt: "docx", label: "Word (.docx)", note: "Editable in Word, Pages, Docs" },
                                  ].map(opt => (
                                    <button key={opt.fmt}
                                      onClick={() => {
                                        const url = exportUrl(opt.fmt);
                                        if (url) window.open(url, '_blank');
                                        setExportMenuOpen(false);
                                      }}
                                      style={{
                                        display: "flex", flexDirection: "column", alignItems: "flex-start",
                                        width: "100%", padding: "8px 10px",
                                        background: "transparent", border: "none", borderRadius: 4,
                                        cursor: "pointer", textAlign: "left",
                                        fontFamily: FFhel,
                                      }}
                                      onMouseEnter={e => e.currentTarget.style.background = "var(--row-hover)"}
                                      onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                                      <span style={{ fontSize: 12, color: "var(--text)", fontWeight: 600 }}>{opt.label}</span>
                                      <span style={{ fontSize: 11, color: "var(--text-mut)", marginTop: 1 }}>{opt.note}</span>
                                    </button>
                                  ))}
                                </div>
                              </>
                            )}
                            </div>
                          </div>
                        </div>

                        {/* Empty state */}
                        {setsForShow.length === 0 && (
                          <div style={{
                            padding: "28px 20px", textAlign: "center",
                            border: "1px dashed var(--panel-border)", borderRadius: 8,
                            fontFamily: FFhel, fontSize: 12, color: "var(--text-mut)",
                          }}>
                            <div style={{ marginBottom: 12 }}>No setlist yet.</div>
                            <button onClick={addSet} style={{
                              fontFamily: FFhel, fontSize: 12, padding: "8px 16px",
                              border: "1px solid var(--panel-border)", borderRadius: 4, background: "var(--panel-alt)",
                              color: "var(--text)", cursor: "pointer",
                            }}>+ Create Set 1</button>
                          </div>
                        )}

                        {/* Set tabs */}
                        {setsForShow.length > 0 && (
                          <div style={{ display: "flex", alignItems: "center", gap: 2, borderBottom: "1px solid var(--panel-border)", marginBottom: 14 }}>
                            {setsForShow.map(set => (
                              <button key={set.set_number} onClick={() => setActiveSetlistSetNum(set.set_number)} style={{
                                fontFamily: FFhel, fontSize: 12, padding: "8px 14px",
                                border: "none", background: "transparent",
                                borderBottom: `2px solid ${currentSetNum === set.set_number ? "var(--accent)" : "transparent"}`,
                                color: currentSetNum === set.set_number ? "var(--text)" : "var(--text-mut)",
                                fontWeight: currentSetNum === set.set_number ? 700 : 500,
                                cursor: "pointer", marginBottom: -1,
                              }}>
                                Set {set.set_number}{set.songs?.length ? ` · ${set.songs.length}` : ""}
                              </button>
                            ))}
                            <button onClick={addSet} style={{
                              fontFamily: FFhel, fontSize: 11, padding: "8px 12px",
                              border: "none", background: "transparent", color: "var(--text-mut)",
                              cursor: "pointer",
                            }}>+ Add Set</button>
                          </div>
                        )}

                        {/* Active set */}
                        {currentSet && (
                          <>
                            {/* Setlist sheet — a contained, bounded card */}
                            <div style={{ border: "1px solid var(--panel-border)", borderRadius: 8, overflow: "hidden", background: "var(--panel)" }}>
                              {/* Column header band */}
                              <div style={{
                                display: "grid",
                                gridTemplateColumns: "22px minmax(120px, 1.6fr) 52px 52px 88px minmax(100px, 1.2fr) 50px 22px",
                                gap: 6, padding: "8px 10px",
                                fontFamily: FFhel, fontSize: 11, letterSpacing: 1.2, color: "var(--text-mid)", textTransform: "uppercase",
                                background: "var(--panel-alt)", borderBottom: "1px solid var(--panel-border)",
                              }}>
                                <span></span>
                                <span>Title</span>
                                <span>Key</span>
                                <span>BPM</span>
                                <span>Tuning</span>
                                <span>Notes</span>
                                <span></span>
                                <span></span>
                              </div>

                              {/* Song rows */}
                              {songs.length === 0 && (
                                <div style={{ padding: "22px 10px", fontFamily: FFhel, fontSize: 12, color: "var(--text-mut)", textAlign: "center" }}>
                                  No songs in this set yet.
                                </div>
                              )}
                              {songs.map((song, idx) => (
                                <div key={song.id || idx} style={{
                                  display: "grid",
                                  gridTemplateColumns: "22px minmax(120px, 1.6fr) 52px 52px 88px minmax(100px, 1.2fr) 50px 22px",
                                  gap: 6, padding: "3px 10px", alignItems: "center",
                                  borderBottom: idx === songs.length - 1 ? "none" : "1px solid var(--sub-border)",
                                }}>
                                  <span style={{ fontFamily: FFhel, fontSize: 11, color: "var(--text-mut)", textAlign: "right" }}>{idx + 1}</span>
                                  <input className="sl-inp" style={{ fontWeight: 600 }} value={song.title || ""}     onChange={e => updateSong(idx, { title: e.target.value })}    placeholder="Song title" />
                                  <input className="sl-inp" value={song.song_key || ""}  onChange={e => updateSong(idx, { song_key: e.target.value })} placeholder="" />
                                  <input className="sl-inp" value={song.tempo ?? ""}     onChange={e => updateSong(idx, { tempo: e.target.value === "" ? null : Number(e.target.value) })} placeholder="" type="number" min="0" />
                                  <input className="sl-inp" value={song.tuning || ""}    onChange={e => updateSong(idx, { tuning: e.target.value })}   placeholder="" />
                                  <input className="sl-inp" value={song.notes || ""}     onChange={e => updateSong(idx, { notes: e.target.value })}    placeholder="" />
                                  <div style={{ display: "flex", gap: 2 }}>
                                    <button onClick={() => moveSong(idx, idx - 1)} disabled={idx === 0} style={arrowBtn(idx === 0)} title="Move up">
                                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="18 15 12 9 6 15"/></svg>
                                    </button>
                                    <button onClick={() => moveSong(idx, idx + 1)} disabled={idx === songs.length - 1} style={arrowBtn(idx === songs.length - 1)} title="Move down">
                                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
                                    </button>
                                  </div>
                                  <button onClick={() => removeSong(idx)} title="Remove song" style={{
                                    width: 22, height: 22, border: "1px solid transparent", background: "transparent",
                                    cursor: "pointer", borderRadius: 4, color: "var(--text-faint)",
                                    display: "flex", alignItems: "center", justifyContent: "center", padding: 0,
                                  }}
                                    onMouseEnter={e => { e.currentTarget.style.color = "var(--danger)"; e.currentTarget.style.borderColor = "var(--field-border)"; e.currentTarget.style.background = "var(--panel-alt)"; }}
                                    onMouseLeave={e => { e.currentTarget.style.color = "var(--text-faint)"; e.currentTarget.style.borderColor = "transparent"; e.currentTarget.style.background = "transparent"; }}>
                                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                                  </button>
                                </div>
                              ))}
                            </div>

                            {/* Footer actions */}
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 14, gap: 10 }}>
                              <button onClick={addSong} style={{
                                fontFamily: FFhel, fontSize: 11, padding: "7px 14px",
                                border: "1px solid var(--panel-border)", borderRadius: 4, background: "var(--panel-alt)",
                                color: "var(--text)", cursor: "pointer",
                              }}>+ Add Song</button>
                              <button onClick={removeSet} style={{
                                fontFamily: FFhel, fontSize: 11, padding: "6px 10px",
                                border: "none", background: "transparent",
                                color: "var(--danger)", cursor: "pointer",
                              }}>Delete Set {currentSetNum}</button>
                            </div>
                          </>
                        )}
                        {setlistImportOpen && (
                          <SetlistImportModal
                            onClose={() => setSetlistImportOpen(false)}
                            onSave={(newSongs) => {
                              const withIds = newSongs.map(s => ({ id: uid(), title: s.title || "", song_key: s.song_key || "", tempo: s.tempo ?? null, tuning: s.tuning || "", notes: s.notes || "" }));
                              if (currentSet) {
                                writeSongs([...songs, ...withIds]);
                              } else {
                                saveShowSetlist({ showId: selS, tourId: selT, setNumber: 1, songs: withIds });
                                setActiveSetlistSetNum(1);
                              }
                            }}
                          />
                        )}
                      </div>
                    );
                  })()}

                  {/* ── FILES TAB ── */}
                  {detailTab === "files" && (
                  <div className="adv-wrap">
                    <div className="adv-3col">
                      <div className="ec">
                        <div className="ec-hdr">
                          <span className="ec-ttl">Advance Sheet</span>
                          <button className="pdf-btn" onClick={() => exportPDF(artist, show, venue)} style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>Export PDF</button>
                        </div>
                        <div style={{ padding: "12px 16px 14px", fontSize: 12, color: "var(--text-mut)", lineHeight: 1.6 }}>
                          Print-ready advance sheet for <strong style={{ color: "var(--text-mut)" }}>{venue?.name}</strong>. Use browser Print → Save as PDF.
                        </div>
                      </div>
                      <div className="ec">
                        <div className="ec-hdr">
                          <span className="ec-ttl">Day Sheet</span>
                          <button className="pdf-btn" onClick={() => exportDaySheet(artist, tour, show, venue)} style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>Export PDF</button>
                        </div>
                        <div style={{ padding: "12px 16px 14px", fontSize: 12, color: "var(--text-mut)", lineHeight: 1.6 }}>
                          Band-facing day-of sheet — schedule, venue, parking, hospitality, green room, merch. No deal terms.
                        </div>
                      </div>
                      {/* Stage Plot */}
                      <PlotCard show={show} onUrlChange={updStagePlot} lightMode={lightMode} />
                    </div>

                    {/* File uploads — reuse the same IIFE from advance portal */}
                    {(() => {
                      const tmD    = show.advanceTmData || {};
                      const proD   = show.advanceData   || {};
                      const tourFiles = tour?.advanceConfig?.files || [];
                      const tmFiles  = tmD._files  || [];
                      const proFiles = proD._files || [];
                      const totalCount = tourFiles.length + tmFiles.length + proFiles.length;
                      return (
                        <div style={{ marginTop: 10 }}>
                          <div className="ec" style={{ overflow: "hidden" }}>
                            <div className="ec-hdr">
                              <span className="ec-ttl">Uploaded Files</span>
                              {totalCount > 0 && (
                                <span style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: "var(--success)" }}>
                                  {totalCount} file{totalCount !== 1 ? "s" : ""}
                                </span>
                              )}
                            </div>
                            <div style={{ padding: "12px 16px 16px", display: "flex", flexDirection: "column", gap: 10 }}>
                              {/* Tour-level files (from tour configuration) */}
                              {tourFiles.length > 0 && (
                                <div>
                                  <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: UI.textFnt, letterSpacing: 1, marginBottom: 6 }}>TOUR FILES</div>
                                  <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                                    {tourFiles.map(f => (
                                      <div key={f.id || f.name} style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 8px", background: UI.rowBg2, border: `1px solid ${UI.fieldBdr}`, borderRadius: 4 }}>
                                        <span style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: UI.green, background: UI.ckOnBg, border: `1px solid ${UI.ckOnBdr}`, borderRadius: 4, padding: "1px 5px", flexShrink: 0 }}>{f.label || "File"}</span>
                                        <a href={f.url} target="_blank" rel="noreferrer" style={{ flex: 1, fontSize: 11, color: UI.textMut, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textDecoration: "none" }}>{f.name}</a>
                                        <span style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: UI.textFnt }}>TOUR</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                              <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: UI.textFnt, letterSpacing: 1, marginBottom: 2 }}>SHOW UPLOADS (visible to promoter)</div>
                              {tmFiles.length > 0 && (
                                <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                                  {tmFiles.map(f => (
                                    <div key={f.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 8px", background: UI.rowBg2, border: `1px solid ${UI.fieldBdr}`, borderRadius: 4 }}>
                                      <span style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: UI.amber, background: UI.amdBg, border: `1px solid ${UI.amdBdr}`, borderRadius: 4, padding: "1px 5px", flexShrink: 0 }}>{f.label}</span>
                                      <a href={f.url} target="_blank" rel="noreferrer" style={{ flex: 1, fontSize: 11, color: UI.textMut, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textDecoration: "none" }}>{f.name}</a>
                                      <button onClick={() => removeTmFile(f.id)} style={{ background: "none", border: "none", color: UI.textFnt, cursor: "pointer", lineHeight: 1, padding: "0 2px", flexShrink: 0, display: "inline-flex", alignItems: "center" }} title="Remove"><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></button>
                                    </div>
                                  ))}
                                </div>
                              )}
                              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                                <select value={tmFileLabel} onChange={e => setTmFileLabel(e.target.value)}
                                  style={{ background: UI.rowBg2, border: `1px solid ${UI.fieldBdr}`, color: UI.textMid, borderRadius: 4, padding: "5px 8px", fontSize: 11, fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", cursor: "pointer" }}>
                                  {["Stage Plot","Input List","Tech Rider","Hospitality Rider","W9","Setlist","Press Kit","Other"].map(l => <option key={l} value={l}>{l}</option>)}
                                </select>
                                <label style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "5px 10px", background: tmFileUploading ? UI.rowHov : UI.rowBg2, border: `1px dashed ${UI.fieldBdr}`, borderRadius: 4, cursor: tmFileUploading ? "default" : "pointer", fontSize: 11, color: UI.textMut, fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif" }}>
                                  {tmFileUploading ? <><span className="spin" /> Uploading...</> : <><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: "middle", marginRight: 5 }}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>Upload File</>}
                                  <input type="file" multiple style={{ display: "none" }} disabled={tmFileUploading}
                                    onChange={e => { if (e.target.files.length) { uploadTmFiles(Array.from(e.target.files)); e.target.value = ""; } }} />
                                </label>
                              </div>
                              {proFiles.length > 0 && (
                                <>
                                  <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: UI.textFnt, letterSpacing: 1, marginTop: 4 }}>PROMOTER UPLOADS</div>
                                  <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                                    {proFiles.map(f => (
                                      <div key={f.id || f.name} style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 8px", background: UI.rowBg2, border: `1px solid ${UI.fieldBdr}`, borderRadius: 4 }}>
                                        <span style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: UI.textMut, background: UI.rowHov, border: `1px solid ${UI.fieldBdr}`, borderRadius: 4, padding: "1px 5px", flexShrink: 0 }}>{f.label || "File"}</span>
                                        <a href={f.url} target="_blank" rel="noreferrer" style={{ flex: 1, fontSize: 11, color: UI.textMut, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textDecoration: "none" }}>{f.name}</a>
                                      </div>
                                    ))}
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                  )} {/* end files tab */}

                  {/* ── ADVANCE TAB ── */}
                  {detailTab === "advance" && (
                  <div className="adv-wrap">
                      {/* Advance Portal — TM view: pre-fill + share */}
                      {(() => {
                        const tmD  = show.advanceTmData || {};   // TM suggestions
                        const proD = show.advanceData   || {};   // Promoter responses

                        // Count promoter amendments across all visible sections
                        const amendCount = ADVANCE_PORTAL_SECTIONS
                          .filter(sec => tourSectionIds.includes(sec.id))
                          .reduce((n, sec) => {
                            const enabledFlds = tourEnabledFields(sec.id);
                            return n + sec.fields.filter(f => {
                              if (!enabledFlds.includes(f.id)) return false;
                              const tv = (tmD[sec.id] || {})[f.id];
                              const pv = (proD[sec.id] || {})[f.id];
                              return pv !== undefined && pv !== "" && pv !== tv;
                            }).length;
                          }, 0);
                        // Count promoter field-level comments across all sections
                        const proCommentTotal = ADVANCE_PORTAL_SECTIONS
                          .filter(sec => tourSectionIds.includes(sec.id))
                          .reduce((n, sec) => {
                            const comments = (proD[sec.id] || {})._comments || {};
                            return n + Object.keys(comments).length;
                          }, 0);

                        return (
                          <div>
                            {/* Control bar */}
                            <div className="adv-ctrl">
                              <div className="adv-ctrl-left">
                                {/* DF-3: draft an empty advance from prior venue answers + defaults */}
                                {Object.keys(show.advanceTmData || {}).filter(k => !k.startsWith("_")).length === 0 && (
                                  <button
                                    className="sm-btn"
                                    disabled={!!advDraft?.busy}
                                    style={{ color: UI.amber, borderColor: UI.amdBdr }}
                                    title="Pre-fill this advance from the last show at this venue, tour defaults, and the venue record"
                                    onClick={async () => {
                                      setAdvDraft({ busy: true });
                                      const res = await draftAdvanceForShow(show);
                                      setAdvDraft(res);
                                      setTimeout(() => setAdvDraft(null), 8000);
                                    }}>
                                    {advDraft?.busy ? <><span className="spin" /> Drafting…</> : "✦ Draft Advance"}
                                  </button>
                                )}
                                {advDraft?.msg && !advDraft.busy && (
                                  <span style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: advDraft.ok ? UI.green : "var(--danger)" }}>
                                    {advDraft.msg}
                                  </span>
                                )}
                                {show.advanceFinalized ? (
                                  <button
                                    className="sm-btn"
                                    onClick={async () => { setAdvanceFinalizing(true); await finalizeAdvance(selS, false); setAdvanceFinalizing(false); }}
                                    disabled={advanceFinalizing}
                                    style={{ color: UI.green, borderColor: UI.green, opacity: 0.8 }}
                                    title="Unlock to make edits">
                                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: "inline", verticalAlign: "middle", marginRight: 4 }}><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 9.9-1"/></svg>
                                    Unlock to Edit
                                  </button>
                                ) : (
                                  <button
                                    className="sm-btn"
                                    onClick={async () => { setAdvanceFinalizing(true); await finalizeAdvance(selS, true); setAdvanceFinalizing(false); }}
                                    disabled={advanceFinalizing}
                                    title="Lock the advance so it can't be accidentally edited">
                                    {advanceFinalizing ? <><span className="spin" /> Finalizing…</> : (
                                      <><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: "inline", verticalAlign: "middle", marginRight: 4 }}><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>Finalize Advance</>
                                    )}
                                  </button>
                                )}
                                {confirmResetAdv ? (
                                  <>
                                    <button className="sm-btn" style={{ color: "var(--danger)", borderColor: "var(--danger)" }}
                                      onClick={async () => { await resetAdvance(selS); setConfirmResetAdv(false); }}
                                      title="This will clear all promoter-filled data and section statuses">
                                      Confirm Reset
                                    </button>
                                    <button className="sm-btn" onClick={() => setConfirmResetAdv(false)}>Cancel</button>
                                  </>
                                ) : (
                                  <button className="sm-btn" style={{ color: UI.textMut }}
                                    onClick={() => { setConfirmResetAdv(true); setTimeout(() => setConfirmResetAdv(false), 5000); }}
                                    title="Clear all promoter-filled advance data and start fresh">
                                    ↺ Reset Advance
                                  </button>
                                )}
                              </div>
                              {tour && tour.shows.length > 1 && (
                                <button className="sm-btn" title="Copy this show's TM suggestions to all other shows on the tour" onClick={applyAdvanceToTour} style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg>Apply to Tour</button>
                              )}
                              {show.advanceToken && (
                                <button className="sm-btn" title="Preview what the promoter sees"
                                  onClick={() => window.open(`${window.location.origin}/advance/${show.advanceToken}?preview=1`, "_blank")}>
                                  ↗ Preview
                                </button>
                              )}
                              {Object.keys(proD).filter(k => !k.startsWith("_")).length > 0 && (
                                <button className="sm-btn" title="Export advance as PDF" onClick={exportAdvancePdf}>
                                  ↓ Export PDF
                                </button>
                              )}
                              <button className="sm-btn" onClick={shareAdvance} disabled={advGenLoading} title="Copy portal link to clipboard">
                                {advGenLoading ? <><span className="spin" /> Generating...</>
                                  : advLinkCopied ? <><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: "middle", marginRight: 5 }}><polyline points="20 6 9 17 4 12"/></svg>Copied!</>
                                  : <><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: "middle", marginRight: 5 }}><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>Copy Link</>}
                              </button>
                              <button className="draft-btn" onClick={sendAdvance} disabled={advGenLoading || eLoad} title="Generate portal link and open Email tab with a drafted intro email">
                                {advGenLoading ? <><span className="spin" /> Generating...</> : eLoad ? <><span className="spin" /> Drafting...</> : "✉ Draft Email"}
                              </button>
                            </div>


                            {/* ORG-1: Show link code — lets venue/festival find this show */}
                            {ORG_LINKING_UI && (() => {
                              const _FF = "'Helvetica Neue', Helvetica, Arial, sans-serif";
                              const code = show.linkCode;
                              return (
                                <div style={{ marginBottom: 6, display: "flex", alignItems: "center", gap: 8 }}>
                                  <span style={{ fontFamily: _FF, fontSize: 11, color: UI.textFnt, letterSpacing: 1, textTransform: "uppercase" }}>Show Code</span>
                                  {code ? (
                                    <>
                                      <span style={{ fontFamily: "monospace", fontSize: 14, fontWeight: 700, letterSpacing: 3, color: UI.amber, padding: "2px 8px", background: UI.amdBg, border: `1px solid ${UI.amdBdr}`, borderRadius: 4 }}>{code}</span>
                                      <button onClick={() => { navigator.clipboard.writeText(code); setLinkCodeCopied(true); setTimeout(() => setLinkCodeCopied(false), 2000); }}
                                        style={{ fontFamily: _FF, fontSize: 11, padding: "4px 10px", background: "none", border: `1px solid ${UI.subBdr}`, borderRadius: 4, color: linkCodeCopied ? UI.green : UI.textMut, cursor: "pointer" }}>
                                        {linkCodeCopied ? "✓ Copied" : "Copy"}
                                      </button>
                                    </>
                                  ) : (
                                    <button
                                      onClick={async () => {
                                        setLinkCodeGenerating(true);
                                        await generateShowLinkCode(selS);
                                        setLinkCodeGenerating(false);
                                      }}
                                      disabled={linkCodeGenerating}
                                      style={{ fontFamily: _FF, fontSize: 11, padding: "4px 10px", background: "none", border: `1px solid ${UI.subBdr}`, borderRadius: 4, color: UI.textMut, cursor: "pointer" }}>
                                      {linkCodeGenerating ? "Generating…" : "Generate Code"}
                                    </button>
                                  )}
                                  <span style={{ fontFamily: _FF, fontSize: 11, color: UI.textFnt }}>Share with venue/festival so they can find this show</span>
                                </div>
                              );
                            })()}

                            {/* V-1: Venue account link strip — hidden behind ORG_LINKING_UI,
                                but always shown if this show already has a link (data safety) */}
                            {(() => {
                              const _FF = "'Helvetica Neue', Helvetica, Arial, sans-serif";
                              const curLinks = showEventLinks[selS] || [];
                              const venueLink = curLinks.find(l => l.org_role === "promoter");
                              if (!ORG_LINKING_UI && !venueLink) return null;
                              const isConfirmed = venueLink?.status === "confirmed";
                              const isPending   = venueLink?.status === "pending";
                              const venueName   = venueLink?.metadata?.venue_name || venueLink?.metadata?.artist_name || "Venue";
                              const tmSettled    = !!venueLink?.tm_settlement_confirmed;
                              const venSettled   = !!venueLink?.venue_settlement_confirmed;
                              const bothSettled  = tmSettled && venSettled;
                              return (
                                <div style={{ marginBottom: 6 }}>
                                  {venueLink ? (
                                    <div style={{ background: UI.rowBg, border: `1px solid ${bothSettled ? UI.ckOnBdr : isConfirmed ? UI.ckOnBdr : UI.subBdr}`, borderRadius: 8, overflow: "hidden" }}>
                                      {/* Link header row */}
                                      <div style={{ padding: "8px 12px", display: "flex", alignItems: "center", gap: 10 }}>
                                        <span style={{ fontFamily: _FF, fontSize: 11, color: isConfirmed ? UI.green : UI.amber, letterSpacing: 0.5, flex: 1 }}>
                                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ display: "inline", verticalAlign: "middle", marginRight: 4 }}>{venueLink.metadata?.org_type === "festival" ? <><polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 12 12 17 22 12"/></> : <><path d="M3 22V8l9-6 9 6v14"/><rect x="9" y="14" width="6" height="8"/><rect x="6" y="10" width="3" height="3"/><rect x="15" y="10" width="3" height="3"/></>}</svg>{venueLink.metadata?.venue_name || (venueLink.metadata?.org_type === "festival" ? "Festival Account" : "Venue Account")}
                                          {" · "}
                                          {isConfirmed ? "Confirmed ✓" : "Awaiting confirmation"}
                                        </span>
                                        <button
                                          onClick={() => unlinkVenueOrg(venueLink.id)}
                                          style={{ fontFamily: _FF, fontSize: 11, color: UI.textMut, background: "none", border: "none", cursor: "pointer", padding: "2px 6px", borderRadius: 4 }}
                                        >Unlink</button>
                                      </div>
                                      {/* V-3: Settlement sign-off strip (only for confirmed links) */}
                                      {isConfirmed && show?.date && (() => {
                                        const isPastShow = show.date < new Date().toISOString().slice(0, 10);
                                        if (!isPastShow) return null; // Only show for past dates
                                        return (
                                          <div style={{ padding: "8px 12px", borderTop: `1px solid ${UI.subBdr}`, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                                            <span style={{ fontFamily: _FF, fontSize: 11, color: UI.textFnt, letterSpacing: 1, textTransform: "uppercase", flexShrink: 0 }}>Settlement</span>
                                            <span style={{ fontFamily: _FF, fontSize: 11, color: tmSettled ? UI.green : UI.amber }}>
                                              TM: {tmSettled ? "✓ Confirmed" : "Pending"}
                                            </span>
                                            <span style={{ fontFamily: _FF, fontSize: 11, color: venSettled ? UI.green : UI.textMut }}>
                                              Venue: {venSettled ? "✓ Confirmed" : "Pending"}
                                            </span>
                                            {bothSettled ? (
                                              <span style={{ fontFamily: _FF, fontSize: 11, fontWeight: 700, color: UI.green, marginLeft: "auto" }}>✅ Both Confirmed</span>
                                            ) : !tmSettled ? (
                                              <button
                                                onClick={() => confirmTmSettlement(venueLink.id)}
                                                style={{ fontFamily: _FF, fontSize: 11, fontWeight: 700, padding: "4px 12px", background: UI.accent, color: UI.btnTxt, border: "none", borderRadius: 4, cursor: "pointer", marginLeft: "auto" }}>
                                                ✓ Confirm Settlement
                                              </button>
                                            ) : (
                                              <span style={{ fontFamily: _FF, fontSize: 11, color: UI.textMut, marginLeft: "auto", fontStyle: "italic" }}>Awaiting venue…</span>
                                            )}
                                          </div>
                                        );
                                      })()}
                                    </div>
                                  ) : (
                                    <div style={{ position: "relative" }}>
                                      {!venueOrgSearchOpen ? (
                                        <button
                                          className="sm-btn"
                                          onClick={() => setVenueOrgSearchOpen(true)}
                                          style={{ fontFamily: _FF, fontSize: 11, padding: "5px 10px" }}
                                          title="Link this show to a venue account on ADVANCD"
                                        style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg>Link Venue / Festival</button>
                                      ) : (
                                        <div style={{ padding: "10px 12px", background: UI.rowBg, border: `1px solid ${UI.subBdr}`, borderRadius: 8 }}>
                                          <div style={{ fontFamily: _FF, fontSize: 11, color: UI.textMut, marginBottom: 6 }}>Search for a venue or festival with an ADVANCD account:</div>
                                          <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
                                            <input
                                              autoFocus
                                              value={venueOrgSearch}
                                              onChange={e => { setVenueOrgSearch(e.target.value); searchVenueOrgs(e.target.value); }}
                                              placeholder="Venue or festival name..."
                                              style={{ flex: 1, fontFamily: _FF, fontSize: 12, padding: "6px 10px", background: UI.inpBg, border: `1px solid ${UI.inpBdr}`, borderRadius: 4, color: UI.text, outline: "none" }}
                                            />
                                            <button onClick={() => { setVenueOrgSearchOpen(false); setVenueOrgSearch(""); setVenueOrgResults([]); }}
                                              style={{ fontFamily: _FF, fontSize: 11, color: UI.textMut, background: "none", border: `1px solid ${UI.panelBdr}`, borderRadius: 4, padding: "4px 10px", cursor: "pointer" }}>Cancel</button>
                                          </div>
                                          {venueOrgSearching && <div style={{ fontFamily: _FF, fontSize: 11, color: UI.textMut }}>Searching...</div>}
                                          {!venueOrgSearching && venueOrgResults.length === 0 && venueOrgSearch.trim() && (
                                            <div style={{ fontFamily: _FF, fontSize: 11, color: UI.textMut }}>No venues or festivals found. They may not have an account yet.</div>
                                          )}
                                          {venueOrgResults.map(org => (
                                            <div key={org.id}
                                              onClick={() => !linkingVenue && linkShowToVenueOrg(org.id, org.name, org.type)}
                                              style={{ padding: "7px 10px", borderRadius: 4, cursor: "pointer", display: "flex", alignItems: "center", gap: 8, marginTop: 3,
                                                background: UI.rowHov || UI.rowBg, border: `1px solid ${UI.subBdr}` }}
                                            >
                                              <span style={{ fontFamily: _FF, fontSize: 12, color: UI.text, display: "inline-flex", alignItems: "center", gap: 5 }}><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">{org.type === "festival" ? <><polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 12 12 17 22 12"/></> : <><path d="M3 22V8l9-6 9 6v14"/><rect x="9" y="14" width="6" height="8"/><rect x="6" y="10" width="3" height="3"/><rect x="15" y="10" width="3" height="3"/></>}</svg>{org.name}</span>
                                              <span style={{ fontFamily: _FF, fontSize: 11, color: UI.textMut, marginLeft: "auto", textTransform: "capitalize" }}>{org.type}</span>
                                              {linkingVenue && <span style={{ fontFamily: _FF, fontSize: 11, color: UI.textMut }}>Linking...</span>}
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              );
                            })()}

                            {/* Message thread with linked venue/festival */}
                            {(() => {
                              const _confirmedLink = (showEventLinks[selS] || []).find(l => l.org_role === "promoter" && l.status === "confirmed");
                              if (!_confirmedLink) return null;
                              const _myProfile = profiles.find(p => p.user_id === user?.id);
                              const _tmName = _myProfile?.name || user?.email || "Tour Manager";
                              const _msgT = {
                                btn: UI.amber, btnText: lightMode ? "#fff" : "#0d0d0d",
                                card: UI.rowBg, border: UI.subBdr, muted: UI.textMut,
                                text: UI.text, inp: UI.panelAlt, inpBorder: UI.fieldBdr,
                              };
                              return (
                                <AdvanceMessageThread
                                  linkId={_confirmedLink.id}
                                  senderType="tm"
                                  senderName={_tmName}
                                  T={_msgT}
                                  FF="'Helvetica Neue', Helvetica, Arial, sans-serif"
                                />
                              );
                            })()}

                            {/* Promoter activity card — last viewed + amendments + notes */}
                            {show.advanceToken && (
                              <div style={{ marginBottom: 6, padding: "8px 12px", background: UI.rowBg, border: `1px solid ${amendCount > 0 || proCommentTotal > 0 ? UI.amdBdr : UI.subBdr}`, borderRadius: 8, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                                {/* Last viewed */}
                                <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: show.advanceLastViewedAt ? UI.amber : UI.textVFnt, flex: 1, minWidth: 0 }}>
                                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                                  {show.advanceLastViewedAt
                                    ? `Viewed ${timeAgo(show.advanceLastViewedAt)} · ${new Date(show.advanceLastViewedAt).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}`
                                    : "Not yet viewed"}
                                </span>
                                {/* Amendments + notes badges */}
                                {amendCount > 0 && (
                                  <span style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, fontWeight: 700, color: UI.amber, letterSpacing: 0.5, flexShrink: 0 }}>
                                    ↩ {amendCount} amendment{amendCount !== 1 ? "s" : ""}
                                  </span>
                                )}
                                {proCommentTotal > 0 && (
                                  <span style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, fontWeight: 700, color: UI.amber, letterSpacing: 0.5, flexShrink: 0 }}>
                                    ◎ {proCommentTotal} note{proCommentTotal !== 1 ? "s" : ""}
                                  </span>
                                )}
                              </div>
                            )}

                            {/* Confirmed banner */}
                            {proD._submitted && (
                              <div style={{ margin: "6px 0 2px", padding: "7px 12px", background: UI.ckOnBg, border: `1px solid ${UI.ckOnBdr}`, borderRadius: 8, display: "flex", alignItems: "center", gap: 8 }}>
                                <span style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: UI.green, fontWeight: 600, letterSpacing: 0.5 }}>✓ ADVANCE CONFIRMED BY PROMOTER</span>
                                {proD._submittedAt && (
                                  <span style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: UI.green, opacity: 0.7 }}>
                                    {new Date(proD._submittedAt).toLocaleDateString("en-US", { month: "short", day: "numeric" })} at {new Date(proD._submittedAt).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}
                                  </span>
                                )}
                              </div>
                            )}

                            {/* AP-1: Section status overview strip */}
                            {show.advanceToken && (() => {
                              const secStatus = show.advanceSectionStatus || {};
                              const visSecIds = ADVANCE_PORTAL_SECTIONS.filter(s => tourSectionIds.includes(s.id));
                              const counts = { flagged: 0, submitted: 0, confirmed: 0, pending: 0 };
                              visSecIds.forEach(s => {
                                const st = secStatus[s.id]?.status || "pending";
                                counts[st] = (counts[st] || 0) + 1;
                              });
                              const _FF = "'Helvetica Neue', Helvetica, Arial, sans-serif";
                              return (
                                <div style={{ marginBottom: 6, padding: "7px 12px", background: UI.rowBg, border: `1px solid ${counts.flagged > 0 ? UI.amdBdr : UI.subBdr}`, borderRadius: 8, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                                  <span style={{ fontFamily: _FF, fontSize: 11, color: UI.textFnt, letterSpacing: 1, textTransform: "uppercase" }}>Sections</span>
                                  {counts.flagged > 0 && <span style={{ fontFamily: _FF, fontSize: 11, color: UI.amber, letterSpacing: 0.5 }}>⚡ {counts.flagged} flagged</span>}
                                  {counts.submitted > 0 && <span style={{ fontFamily: _FF, fontSize: 11, color: UI.green, letterSpacing: 0.5 }}>✓ {counts.submitted} submitted</span>}
                                  {counts.confirmed > 0 && <span style={{ fontFamily: _FF, fontSize: 11, color: UI.green, letterSpacing: 0.5, opacity: 0.7 }}>● {counts.confirmed} confirmed</span>}
                                  {counts.pending > 0 && <span style={{ fontFamily: _FF, fontSize: 11, color: UI.textFnt, letterSpacing: 0.5 }}>○ {counts.pending} pending</span>}
                                  <span style={{ marginLeft: "auto" }}>
                                    {visSecIds.map(s => {
                                      const st = secStatus[s.id]?.status || "pending";
                                      const dotColor = st === "flagged" ? UI.amber : (st === "submitted" || st === "confirmed") ? UI.green : UI.subBdr;
                                      return (
                                        <span key={s.id} title={`${s.label}: ${st}`}
                                          style={{ display: "inline-block", width: 7, height: 7, borderRadius: "50%", background: dotColor, margin: "0 2px", opacity: st === "pending" ? 0.3 : 1, verticalAlign: "middle" }} />
                                      );
                                    })}
                                  </span>
                                </div>
                              );
                            })()}

                            {/* V-2: Venue-filled info (read-only for TM) */}
                            {(() => {
                              const curLinks = showEventLinks[selS] || [];
                              const venueLink = curLinks.find(l => l.status === "confirmed");
                              const venueAdvData = venueLink?.venue_advance_data || {};
                              const hasVenueData = VENUE_ADVANCE_SECTIONS.some(s =>
                                Object.values(venueAdvData[s.id] || {}).some(v => v)
                              );
                              // Also show house inventory from org profile if available
                              // (fetched separately — stored in venueLink.metadata for now,
                              //  or we can show a note to check the venue profile tab)
                              if (!venueLink) return null;
                              const _FF = "'Helvetica Neue', Helvetica, Arial, sans-serif";
                              const isFestLink = venueLink.metadata?.org_type === "festival";
                              const advSections = isFestLink ? FESTIVAL_ADVANCE_SECTIONS : VENUE_ADVANCE_SECTIONS;
                              return (
                                <div style={{ marginBottom: 8 }}>
                                  <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, color: UI.textMut, fontFamily: _FF, textTransform: "uppercase", marginBottom: 6 }}>
                                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: "inline", verticalAlign: "middle", marginRight: 4 }}>{isFestLink ? <><polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 12 12 17 22 12"/></> : <><path d="M3 22V8l9-6 9 6v14"/><rect x="9" y="14" width="6" height="8"/><rect x="6" y="10" width="3" height="3"/><rect x="15" y="10" width="3" height="3"/></>}</svg>{isFestLink ? "Festival Info" : "Venue Info"} from {venueLink.metadata?.venue_name || (isFestLink ? "Festival" : "Venue")}
                                  </div>
                                  {!hasVenueData && (
                                    <div style={{ padding: "8px 12px", background: UI.rowBg, border: `1px solid ${UI.subBdr}`, borderRadius: 8, fontFamily: _FF, fontSize: 11, color: UI.textMut }}>
                                      {isFestLink ? "The festival hasn't filled in their sections yet." : "The venue hasn't filled in their sections yet."}
                                    </div>
                                  )}
                                  {hasVenueData && advSections.map(sec => {
                                    const secData = venueAdvData[sec.id] || {};
                                    const entries = sec.fields.filter(f => secData[f.key]);
                                    if (!entries.length) return null;
                                    return (
                                      <div key={sec.id} style={{ marginBottom: 6, padding: "10px 14px", background: UI.rowBg, border: `1px solid ${UI.subBdr}`, borderRadius: 8 }}>
                                        <div style={{ fontFamily: _FF, fontSize: 11, fontWeight: 700, color: UI.textMid, marginBottom: 8, letterSpacing: 0.5 }}>{sec.label}</div>
                                        {entries.map(f => (
                                          <div key={f.key} style={{ display: "flex", gap: 12, marginBottom: 5 }}>
                                            <div style={{ fontFamily: _FF, fontSize: 11, color: UI.textMut, minWidth: 130 }}>{f.label}</div>
                                            <div style={{ fontFamily: _FF, fontSize: 11, color: UI.text, flex: 1 }}>{secData[f.key]}</div>
                                          </div>
                                        ))}
                                      </div>
                                    );
                                  })}
                                </div>
                              );
                            })()}

                            {/* Venue Intel — past show history for this venue, visible while advancing */}
                            {venue && (() => {
                              const _FF = "'Helvetica Neue', Helvetica, Arial, sans-serif";
                              const vh = venueHistoryCache[venue.id];
                              const pastItems = (vh?.items || []).filter(item => item.show.id !== selS);
                              if (!vh || vh.loading || !pastItems.length) return null;
                              const recColor = (rec) => rec === 'Recommend' ? UI.green : rec === 'Avoid' ? 'var(--danger)' : UI.textMut;
                              return (
                                <div style={{ marginBottom: 8, padding: "10px 14px", background: UI.rowBg, border: `1px solid ${UI.subBdr}`, borderRadius: 8 }}>
                                  <div style={{ fontFamily: _FF, fontSize: 11, fontWeight: 700, letterSpacing: 1, color: UI.textFnt, textTransform: "uppercase", marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
                                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 22V8l9-6 9 6v14"/><rect x="9" y="14" width="6" height="8"/><rect x="6" y="10" width="3" height="3"/><rect x="15" y="10" width="3" height="3"/></svg>
                                    Venue Intel · {pastItems.length} past show{pastItems.length !== 1 ? "s" : ""}
                                  </div>
                                  {pastItems.slice(0, 3).map(({ show: ps, reports }) => {
                                    const tmRpt   = reports.find(r => r.role_category === 'tm');
                                    const ratings = reports.map(r => r.responses?.overall_rating).filter(Boolean);
                                    const avg     = ratings.length ? (ratings.reduce((a, b) => a + Number(b), 0) / ratings.length).toFixed(1) : null;
                                    const rec     = tmRpt?.responses?.venue_recommendation;
                                    const hNotes  = tmRpt?.responses?.hospitality_notes;
                                    const pvtNotes = tmRpt?.responses?.venue_notes;
                                    return (
                                      <div key={ps.id} style={{ marginBottom: 6, paddingBottom: 6, borderBottom: `1px solid ${UI.subBdr}` }}>
                                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                          <span style={{ fontFamily: _FF, fontSize: 11, fontWeight: 600, color: UI.textMid }}>{fmtDate(ps.date)}</span>
                                          {avg && <span style={{ fontFamily: _FF, fontSize: 11, color: UI.amber }}>★ {avg}/5</span>}
                                          {rec && (
                                            <span style={{ fontFamily: _FF, fontSize: 11, fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase",
                                              color: recColor(rec), marginLeft: 2 }}>{rec}</span>
                                          )}
                                        </div>
                                        {hNotes && <div style={{ fontFamily: _FF, fontSize: 11, color: UI.textMut, marginTop: 2, fontStyle: "italic" }}>Hospitality: {hNotes}</div>}
                                        {pvtNotes && <div style={{ fontFamily: _FF, fontSize: 11, color: UI.textFnt, marginTop: 1 }}>{pvtNotes}</div>}
                                      </div>
                                    );
                                  })}
                                  {pastItems.length > 3 && (
                                    <div style={{ fontFamily: _FF, fontSize: 11, color: UI.textFnt, marginTop: 2 }}>
                                      +{pastItems.length - 3} more — see Info tab for full history
                                    </div>
                                  )}
                                </div>
                              );
                            })()}

                            {/* Ticket count quick-view strip — shown when promoter has filled ticket data */}
                            {(() => {
                              const tix = proD.tickets || {};
                              const sold = tix.tickets_sold;
                              const cap  = tix.capacity;
                              const soldOut = tix.sellout;
                              const link = tix.ticket_link;
                              if (!sold && !cap && soldOut === undefined) return null;
                              const _FF = "'Helvetica Neue', Helvetica, Arial, sans-serif";
                              const pct = (sold && cap) ? Math.round((parseFloat(sold) / parseFloat(cap)) * 100) : null;
                              return (
                                <div style={{ marginBottom: 8, padding: "10px 14px", background: soldOut ? UI.ckOnBg : UI.rowBg, border: `1px solid ${soldOut ? UI.ckOnBdr : UI.subBdr}`, borderRadius: 8, display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
                                  <span style={{ fontFamily: _FF, fontSize: 11, color: UI.textFnt, letterSpacing: 1, textTransform: "uppercase", flexShrink: 0 }}>🎟 Tickets</span>
                                  {sold && (
                                    <span style={{ fontFamily: _FF, fontSize: 14, fontWeight: 700, color: soldOut ? UI.green : UI.text }}>
                                      {sold}{cap ? <span style={{ fontWeight: 400, fontSize: 11, color: UI.textMut }}>/{cap}</span> : null}
                                      {pct !== null && <span style={{ fontFamily: _FF, fontWeight: 400, fontSize: 11, color: UI.textMut, marginLeft: 4 }}>({pct}%)</span>}
                                    </span>
                                  )}
                                  {soldOut && <span style={{ fontFamily: _FF, fontSize: 11, fontWeight: 700, color: UI.green, letterSpacing: 0.5 }}>✓ SOLD OUT</span>}
                                  {!sold && cap && <span style={{ fontFamily: _FF, fontSize: 11, color: UI.textMut }}>Cap {cap}</span>}
                                  {link && (
                                    <a href={link.startsWith("http") ? link : "https://" + link} target="_blank" rel="noopener noreferrer"
                                      style={{ fontFamily: _FF, fontSize: 11, color: UI.accent, marginLeft: "auto", textDecoration: "none" }}>
                                      ↗ Ticket link
                                    </a>
                                  )}
                                </div>
                              );
                            })()}

                            {/* FA-1: Finalized banner */}
                            {show.advanceFinalized && (
                              <div style={{ margin: "0 0 8px", padding: "10px 14px", background: UI.ckOnBg, border: `1px solid ${UI.ckOnBdr}`, borderRadius: 8, display: "flex", alignItems: "center", gap: 10 }}>
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: UI.green, flexShrink: 0 }}><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                                <span style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, fontWeight: 700, color: UI.green, letterSpacing: 0.5, flex: 1 }}>ADVANCE FINALIZED</span>
                                {show.advanceFinalizedAt && (
                                  <span style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: UI.green, opacity: 0.7 }}>
                                    {new Date(show.advanceFinalizedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })} at {new Date(show.advanceFinalizedAt).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}
                                  </span>
                                )}
                              </div>
                            )}

                            {/* Sections */}
                            <div style={{ padding: "0", pointerEvents: show.advanceFinalized ? "none" : "auto", opacity: show.advanceFinalized ? 0.65 : 1, transition: "opacity 0.2s" }}>
                              {!show.advanceToken && (
                                <div className="adv-hint">
                                  Pre-fill suggestions below, then click <strong style={{ color: UI.textMid }}>Copy Link</strong> to share with the promoter, or <strong style={{ color: UI.textMid }}>Draft Email</strong> to do both at once. Promoter amendments appear in <span style={{ color: UI.amber }}>amber</span>.
                                </div>
                              )}
                              {/* DF-3: provenance note for auto-drafted advances */}
                              {show.advanceTmData?._draftSources?.sources && !show.advanceFinalized && (
                                <div className="adv-hint" style={{ color: UI.amber }}>
                                  Drafted automatically from {show.advanceTmData._draftSources.sources.join(" + ")} — review before sending.
                                </div>
                              )}
                            <div className="adv-sections">

                              {ADVANCE_PORTAL_SECTIONS.filter(sec => tourSectionIds.includes(sec.id)).map(sec => {
                                // For schedule: fall back to tour-level defaultSchedule when the show has no overrides yet
                                const tourDefaultSched = tour?.advanceConfig?.defaultSchedule;
                                const rawTmSd = tmD[sec.id] || {};
                                // Use tour defaults for the bill whenever the show hasn't saved one yet.
                                // Only gate on !rawTmSd.bill — NOT on !rawTmSd.venue_access.
                                // Previously gating on both meant: as soon as the TM typed anything
                                // into a time field, rawTmSd.venue_access became truthy, the whole
                                // condition flipped false, rawTmSd (which still had no bill) became
                                // tmSd, and BillEditorInline fell back to DEFAULT_BILL — re-inserting
                                // Direct Support even if the TM just deleted it.
                                const tmSd = (sec.id === "schedule" && !rawTmSd.bill && tourDefaultSched)
                                  ? {
                                      // Start with tour defaults for the times
                                      venue_access:  tourDefaultSched.venue_access  || "",
                                      vip_access:    tourDefaultSched.vip_doors     || "",
                                      doors_public:  tourDefaultSched.doors         || "",
                                      noise_curfew:  tourDefaultSched.noise_curfew  || "",
                                      gr_curfew:     tourDefaultSched.gr_curfew     || "",
                                      curfew:        tourDefaultSched.hard_curfew   || "",
                                      // Overlay any show-level overrides the TM has already typed
                                      ...rawTmSd,
                                      // Always keep the tour defaults bill until the show has its own
                                      bill: tourDefaultSched.acts
                                        ? tourDefaultSched.acts.map(a => ({ id: a.id, name: a.name, load: a.load_in || "", sc: a.soundcheck || "", set: a.set_time || "" }))
                                        : [],
                                      changeovers: tourDefaultSched.acts
                                        ? tourDefaultSched.acts.slice(0, -1).map(a => ({ time: a.changeover || "" }))
                                        : [],
                                    }
                                  : rawTmSd;
                                const proSd = proD[sec.id] || {};

                                // ── Promoter ROS proposal (portal bill edits) ──
                                // The portal stores a full proposed copy of the bill in
                                // advance_data.schedule.bill/.changeovers. Diff it against the
                                // TM's displayed bill so changes surface with Apply / Dismiss.
                                const rosDiffs = [];
                                let rosProposal = null;
                                if (sec.id === "schedule" && Array.isArray(proSd.bill) && proSd.bill.length > 0) {
                                  const tmBillShown = Array.isArray(tmSd.bill) && tmSd.bill.length ? tmSd.bill : DEFAULT_BILL;
                                  const tmCosShown  = Array.isArray(tmSd.changeovers) ? tmSd.changeovers : [];
                                  const proCosArr   = Array.isArray(proSd.changeovers) ? proSd.changeovers : tmCosShown;
                                  rosProposal = { bill: proSd.bill, changeovers: proCosArr };
                                  const ROS_LBL = { name: "NAME", load: "LOAD-IN", sc: "S/CHECK", set: "SET", setEnd: "SET END" };
                                  proSd.bill.forEach((b, i) => {
                                    const t = tmBillShown.find(x => x.id && x.id === b.id) || tmBillShown[i] || {};
                                    const chg = ["name", "load", "sc", "set", "setEnd"].filter(k => (b[k] || "") !== (t[k] || ""));
                                    if (chg.length) rosDiffs.push({
                                      label: t.name || b.name || `Act ${i + 1}`,
                                      parts: chg.map(k => `${ROS_LBL[k]}: ${t[k] || "—"} → ${b[k] || "—"}`),
                                    });
                                  });
                                  proCosArr.forEach((c, i) => {
                                    const t = tmCosShown[i] || {};
                                    const parts = [];
                                    if ((c.time || "") !== (t.time || "")) parts.push(`START: ${t.time || "—"} → ${c.time || "—"}`);
                                    if ((c.timeEnd || "") !== (t.timeEnd || "")) parts.push(`END: ${t.timeEnd || "—"} → ${c.timeEnd || "—"}`);
                                    if (!!c.isIntermission !== !!t.isIntermission) parts.push(c.isIntermission ? "→ INTERMISSION" : "→ CHANGEOVER");
                                    if (parts.length) rosDiffs.push({ label: `${c.isIntermission ? "Intermission" : "Changeover"} ${i + 1}`, parts });
                                  });
                                }

                                const enabledFlds = tourEnabledFields(sec.id);
                                // tmOnly fields are TM-internal (band/crew notes) — always visible in TM view,
                                // never sent to the promoter portal; they bypass the advanceConfig enable gate.
                                const visFields   = sec.fields.filter(f => f.tmOnly || enabledFlds.includes(f.id));
                                const timeFields    = visFields.filter(f => f.type === "time");
                                const ynFields      = visFields.filter(f => f.type === "yesno");
                                const textFields    = visFields.filter(f => f.type === "text" || f.type === "percent");
                                const contactFields = visFields.filter(f => f.type === "contact");
                                // Long-form fields render as full-width textareas; everything else sits in the 2-col grid
                                const isLongTextFld = f => f.id.endsWith("_notes") ||
                                  ["dietary","will_call","loading_zone","bus_parking","gr_access","gr_layout","gt_route"].includes(f.id);
                                const shortTextFields = textFields.filter(f => !isLongTextFld(f));
                                const longTextFields  = textFields.filter(isLongTextFld);

                                // Counts for header badge
                                const tmFilled = visFields.filter(f => {
                                  const v = tmSd[f.id];
                                  if (f.type === "yesno")   return v === true || v === false;
                                  if (f.type === "contact") { const c = (typeof v === "object" && v) ? v : {}; return !!(c.name || c.phone || c.email); }
                                  return v && String(v).trim();
                                }).length;
                                const secAmends = visFields.filter(f => {
                                  const tv = tmSd[f.id]; const pv = proSd[f.id];
                                  if (f.type === "contact") {
                                    const tmC = (typeof tv === "object" && tv) ? tv : {};
                                    const proC = (typeof pv === "object" && pv) ? pv : {};
                                    return ["name","phone","email"].some(sub => proC[sub] !== undefined && proC[sub] !== "" && proC[sub] !== tmC[sub]);
                                  }
                                  if (f.type === "wifi_networks") {
                                    return Array.isArray(pv) && pv.some(n => n && (n.network || n.password)) &&
                                      JSON.stringify(pv) !== JSON.stringify(Array.isArray(tv) ? tv : []);
                                  }
                                  if (f.type === "time") return pv !== undefined && pv !== "" && !timeEq(pv, tv);
                                  return pv !== undefined && pv !== "" && pv !== tv;
                                }).length;

                                const proCommentCount = visFields.filter(f => !!(proSd._comments || {})[f.id]).length;

                                const key = "tm_" + sec.id;
                                const isOpen = !!cats[key];

                                // Render a single field row (TM input + optional promoter amendment row + threading)
                                const renderField = (f, inputEl) => {
                                  const tv = tmSd[f.id];
                                  const pv = proSd[f.id];
                                  // Contact fields must use sub-field comparison (not object reference)
                                  // so that accepted values don't re-appear as "amended" after a re-render
                                  const amended = f.type === "contact"
                                    ? (() => {
                                        const tmC  = (typeof tv === "object" && tv)  ? tv  : {};
                                        const proC = (typeof pv === "object" && pv) ? pv : {};
                                        return ["name","phone","email"].some(sub => proC[sub] !== undefined && proC[sub] !== "" && proC[sub] !== tmC[sub]);
                                      })()
                                    : f.type === "wifi_networks"
                                    // Arrays: reference compare is always true — compare content
                                    ? Array.isArray(pv) && pv.some(n => n && (n.network || n.password)) &&
                                      JSON.stringify(pv) !== JSON.stringify(Array.isArray(tv) ? tv : [])
                                    : f.type === "time"
                                    // Times: semantic compare so "17:00" vs "5:00 PM" isn't flagged
                                    ? pv !== undefined && pv !== "" && !timeEq(pv, tv)
                                    : pv !== undefined && pv !== "" && pv !== tv;
                                  // Object/array-safe display of a promoter value — raw objects as
                                  // React children throw React #31 and crash the whole advance tab
                                  // (hit in prod: wifi_networks array of {network, password}).
                                  const fmtPv = (v) => {
                                    if (v === true) return "✓ Yes";
                                    if (v === false) return "✗ No";
                                    if (f.type === "wifi_networks" && Array.isArray(v))
                                      return v.map(n => [n?.network, n?.password].filter(Boolean).join(" · ")).join("   |   ") || "—";
                                    if (Array.isArray(v)) return v.map(x => (typeof x === "object" && x !== null) ? JSON.stringify(x) : String(x)).join(", ");
                                    if (typeof v === "object" && v !== null) return JSON.stringify(v);
                                    return v;
                                  };
                                  const tmNote  = (tmSd._comments  || {})[f.id];
                                  const proNote = (proSd._comments || {})[f.id];
                                  const noteKey = "fn_" + sec.id + "_" + f.id;
                                  const noteOpen = !!cats[noteKey];
                                  const hasNote = !!tmNote || !!proNote;
                                  const FF9 = "'Helvetica Neue', Helvetica, Arial, sans-serif";

                                  const saveTmNote = (text) => {
                                    const updated = { ...(tmSd._comments || {}), [f.id]: { text: text.trim(), ts: new Date().toISOString() } };
                                    updAdvanceTmField(sec.id, "_comments", updated);
                                    logActivity(`Added note on ${sec.label} / ${f.label}`);
                                  };
                                  const clearTmNote = () => {
                                    const updated = { ...(tmSd._comments || {}) };
                                    delete updated[f.id];
                                    updAdvanceTmField(sec.id, "_comments", updated);
                                    logActivity(`Removed note on ${sec.label} / ${f.label}`);
                                  };

                                  return (
                                    <div key={f.id}>
                                      <div style={{ fontFamily: FF9, fontSize: 11, color: amended ? UI.amber : UI.textFnt, marginBottom: 3, display: "flex", alignItems: "center", gap: 5 }}>
                                        <span style={{ flex: 1 }}>{f.label}</span>
                                        {amended && <span style={{ fontSize: 11, color: UI.amber, letterSpacing: 0.5 }}>↩ AMENDED</span>}
                                        <button
                                          title={noteOpen ? "Hide note" : hasNote ? "View / edit note" : "Add note"}
                                          onClick={() => togCat(noteKey)}
                                          style={{ background: "none", border: "none", cursor: "pointer", fontSize: 11, color: hasNote ? UI.green : noteOpen ? UI.textMut : UI.textVFnt, padding: 0, lineHeight: 1, flexShrink: 0, opacity: hasNote || noteOpen ? 1 : 0.45 }}>
                                          ◎
                                        </button>
                                      </div>
                                      {inputEl}
                                      {amended && (
                                        <div style={{ marginTop: 4, padding: "4px 8px", background: UI.amdBg, border: `1px solid ${UI.amdBdr}`, borderRadius: 4, display: "flex", alignItems: "center", gap: 6 }}>
                                          <span style={{ fontFamily: FF9, fontSize: 11, color: UI.amber, letterSpacing: 0.3, flexShrink: 0 }}>PROMOTER:</span>
                                          <span style={{ fontSize: 11, color: UI.amber, fontWeight: 500 }}>
                                            {f.type === "yesno" ? (pv === true ? "✓ Yes" : pv === false ? "✗ No" : "—") : f.type === "contact" ? [pv?.name, pv?.phone, pv?.email].filter(Boolean).join(" · ") : fmtPv(pv)}
                                          </span>
                                          <span style={{ flex: 1 }} />
                                          {(tv === true || tv === false || (tv !== undefined && tv !== "" && !(Array.isArray(tv) && tv.length === 0))) && (
                                            <span style={{ fontFamily: FF9, fontSize: 11, color: UI.textFnt }}>
                                              was: {f.type === "yesno" ? (tv === true ? "yes" : tv === false ? "no" : "—") : f.type === "contact" ? [tv?.name, tv?.phone, tv?.email].filter(Boolean).join(" · ") : fmtPv(tv)}
                                            </span>
                                          )}
                                          <button
                                            onClick={() => updAdvanceTmField(sec.id, f.id, pv)}
                                            title="Copy promoter's answer into your field"
                                            style={{ fontFamily: FF9, fontSize: 11, fontWeight: 700, letterSpacing: 0.5, color: UI.bg, background: UI.amber, border: "none", borderRadius: 4, padding: "2px 7px", cursor: "pointer", flexShrink: 0 }}>
                                            ← Accept
                                          </button>
                                        </div>
                                      )}
                                      {(noteOpen || proNote || tmNote) && (
                                        <div style={{ marginTop: 5, display: "flex", flexDirection: "column", gap: 4 }}>
                                          {proNote && (
                                            <div style={{ display: "flex", alignItems: "flex-start", gap: 7, padding: "5px 8px", background: UI.amdBg, border: `1px solid ${UI.amdBdr}`, borderRadius: 4, lineHeight: 1.45 }}>
                                              <span style={{ fontFamily: FF9, fontSize: 11, fontWeight: 700, letterSpacing: 0.8, textTransform: "uppercase", flexShrink: 0, paddingTop: 1, color: UI.amber }}>PRO</span>
                                              <span style={{ flex: 1, fontSize: 11, color: UI.textMid }}>{proNote.text}</span>
                                            </div>
                                          )}
                                          {tmNote ? (
                                            <div style={{ display: "flex", alignItems: "flex-start", gap: 7, padding: "5px 8px", background: UI.ckOnBg, border: `1px solid ${UI.ckOnBdr}`, borderRadius: 4, lineHeight: 1.45 }}>
                                              <span style={{ fontFamily: FF9, fontSize: 11, fontWeight: 700, letterSpacing: 0.8, textTransform: "uppercase", flexShrink: 0, paddingTop: 1, color: UI.green }}>TM</span>
                                              <span style={{ flex: 1, fontSize: 11, color: UI.textMid }}>{tmNote.text}</span>
                                              <button onClick={clearTmNote} title="Remove note" style={{ background: "none", border: "none", color: UI.textFnt, cursor: "pointer", padding: 0, marginLeft: "auto", flexShrink: 0, lineHeight: 1, display: "inline-flex", alignItems: "center" }}><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></button>
                                            </div>
                                          ) : (noteOpen || proNote) ? (
                                            <input
                                              className="f-inp"
                                              placeholder="Add TM note…"
                                              style={{ fontSize: 11 }}
                                              onBlur={e => { if (e.target.value.trim()) { saveTmNote(e.target.value.trim()); e.target.value = ""; if (noteOpen) togCat(noteKey); } }}
                                              onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); e.target.blur(); } }} />
                                          ) : null}
                                        </div>
                                      )}
                                    </div>
                                  );
                                };

                                // AP-1: per-section status (TM-side)
                                const secSt    = (show.advanceSectionStatus || {})[sec.id] || {};
                                const isSecFlagged   = secSt.status === "flagged";
                                const isSecSubmitted = secSt.status === "submitted";
                                const isSecConfirmed = secSt.status === "confirmed";
                                const flagNoteKey    = "flag_note_" + sec.id;
                                const flagNoteOpen   = !!cats[flagNoteKey];
                                const FF9 = "'Helvetica Neue', Helvetica, Arial, sans-serif";

                                return (
                                  <div key={sec.id} style={{ background: UI.rowBg, border: `1px solid ${isSecFlagged ? UI.amdBdr : secAmends > 0 ? UI.amdBdr : isSecSubmitted ? UI.ckOnBdr : UI.subBdr}`, borderRadius: 8, overflow: "hidden" }}>
                                    <div className="adv-sec-hdr-row" style={{ padding: "9px 12px", display: "flex", alignItems: "center", gap: 8, cursor: "pointer", userSelect: "none", background: isSecFlagged ? UI.amdBg : "transparent" }} onClick={() => togCat(key)}>
                                      <span style={{ color: UI.textVFnt, transform: isOpen ? "rotate(90deg)" : "none", transition: "transform 0.28s cubic-bezier(0.4,0,0.2,1)", display: "inline-flex", flexShrink: 0 }}><svg width="10" height="10" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M4.5 2l4 4-4 4"/></svg></span>
                                      <span style={{ fontFamily: FF9, fontSize: 11, color: isSecFlagged ? UI.amber : secAmends > 0 ? UI.amber : UI.textMut, textTransform: "uppercase", letterSpacing: 1.2 }}>{sec.label}</span>
                                      <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6 }}>
                                        {/* AP-1: section status badges */}
                                        {isSecFlagged && <span style={{ fontFamily: FF9, fontSize: 11, color: UI.amber, letterSpacing: 0.5, display: "inline-flex", alignItems: "center", gap: 4 }}><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" y1="22" x2="4" y2="15"/></svg>flagged</span>}
                                        {isSecSubmitted && !isSecFlagged && <span style={{ fontFamily: FF9, fontSize: 11, color: UI.green, letterSpacing: 0.5 }}>✓ submitted</span>}
                                        {isSecConfirmed && <span style={{ fontFamily: FF9, fontSize: 11, color: UI.green, letterSpacing: 0.5 }}>✓ confirmed</span>}
                                        {/* F-5d: Festival flag badge */}
                                        {(() => {
                                          const cl = showEventLinks[selS] || [];
                                          const fl = cl.find(l => l.status === "confirmed" && l.metadata?.org_type === "festival");
                                          const flg = fl?.venue_section_status?.tm_section_flags?.[sec.id];
                                          if (!flg || flg.status !== "flagged") return null;
                                          return <span style={{ fontFamily: FF9, fontSize: 11, color: "#c050c8", letterSpacing: 0.5, display: "inline-flex", alignItems: "center", gap: 4 }}><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" y1="22" x2="4" y2="15"/></svg>festival</span>;
                                        })()}
                                        {/* Existing badges — counts live in the variable zone; action
                                            buttons get fixed-width slots below so they align across cards */}
                                        {secAmends > 0 && <span style={{ fontFamily: FF9, fontSize: 11, color: UI.amber }}>{secAmends} ↩</span>}
                                        {proCommentCount > 0 && <span style={{ fontFamily: FF9, fontSize: 11, color: UI.amber }} title="Promoter has left notes on fields in this section">◎ {proCommentCount}</span>}
                                        {proSd._confirmed && secAmends === 0 && !isSecFlagged && !isSecSubmitted && !isSecConfirmed && <span style={{ fontFamily: FF9, fontSize: 11, color: UI.green, letterSpacing: 0.5 }}>✓ noted</span>}
                                        {tmFilled > 0 && secAmends === 0 && !proSd._confirmed && !isSecFlagged && !isSecSubmitted && !isSecConfirmed && <span style={{ fontFamily: FF9, fontSize: 11, color: UI.green, display: "inline-flex", alignItems: "center", gap: 3 }}>{tmFilled}<svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3l1.9 5.8a2 2 0 0 0 1.3 1.3L21 12l-5.8 1.9a2 2 0 0 0-1.3 1.3L12 21l-1.9-5.8a2 2 0 0 0-1.3-1.3L3 12l5.8-1.9a2 2 0 0 0 1.3-1.3L12 3z"/></svg></span>}
                                        {sec.id === "guestList" && (() => {
                                          const _glE = show?.advanceTmData?.guestListSheet?.entries || [];
                                          const _glP = glRequests.filter(r => r.status === "pending").length;
                                          if (!_glE.length && !_glP) return null;
                                          return (<>
                                            {_glE.length > 0 && <span style={{ fontFamily: FF9, fontSize: 11, color: UI.green }}>{_glE.length} guest{_glE.length !== 1 ? "s" : ""}</span>}
                                            {_glP > 0 && <span style={{ fontFamily: FF9, fontSize: 11, color: UI.amber }}>{_glP} pending</span>}
                                          </>);
                                        })()}
                                        {/* AP-1: Confirm button (when promoter submitted) */}
                                        {isSecSubmitted && !isSecConfirmed && (
                                          <button
                                            onClick={e => { e.stopPropagation(); confirmAdvanceSectionByTM(show.id, sec.id); }}
                                            title="Mark this section as confirmed"
                                            style={{ fontFamily: FF9, fontSize: 11, fontWeight: 700, letterSpacing: 0.5, color: "var(--accent-fg)", background: UI.green, border: "none", borderRadius: 4, padding: "2px 8px", cursor: "pointer", flexShrink: 0, display: "inline-flex", alignItems: "center", gap: 4 }}>
                                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>Confirm
                                          </button>
                                        )}
                                        {/* Fixed-width action slots — reserved even when empty so the
                                            buttons form clean columns across all section cards */}
                                        <span style={{ width: 92, display: "inline-flex", justifyContent: "flex-end", flexShrink: 0 }}>
                                          {secAmends > 0 && (
                                            <button
                                              onClick={e => {
                                                e.stopPropagation();
                                                visFields.forEach(f => {
                                                  const tv = tmSd[f.id];
                                                  const pv = proSd[f.id];
                                                  if (pv !== undefined && pv !== "" && pv !== tv) {
                                                    updAdvanceTmField(sec.id, f.id, pv);
                                                  }
                                                });
                                              }}
                                              title="Accept all promoter amendments in this section"
                                              style={{ fontFamily: FF9, fontSize: 11, fontWeight: 700, letterSpacing: 0.5, color: UI.bg, background: UI.amber, border: "none", borderRadius: 4, padding: "2px 8px", cursor: "pointer", flexShrink: 0 }}>
                                              ← Accept All
                                            </button>
                                          )}
                                        </span>
                                        {/* AP-1: Flag / Unflag button (fixed slot, hover-revealed) */}
                                        {show.advanceToken && (
                                          <span className={`adv-flag-slot${isSecFlagged || flagNoteOpen ? " on" : ""}`} style={{ width: 60, display: "inline-flex", justifyContent: "flex-end", flexShrink: 0 }}>
                                            {isSecFlagged ? (
                                              <button
                                                onClick={e => { e.stopPropagation(); unflagAdvanceSection(show.id, sec.id); }}
                                                title="Remove flag from this section"
                                                style={{ fontFamily: FF9, fontSize: 11, color: UI.textMut, background: "none", border: `1px solid ${UI.panelBdr}`, borderRadius: 4, padding: "2px 8px", cursor: "pointer", flexShrink: 0 }}>
                                                Unflag
                                              </button>
                                            ) : (
                                              <button
                                                onClick={e => { e.stopPropagation(); togCat(flagNoteKey); }}
                                                title="Flag this section and send a note to the promoter"
                                                style={{ fontFamily: FF9, fontSize: 11, color: UI.amber, background: "none", border: `1px solid ${UI.amdBdr}`, borderRadius: 4, padding: "2px 8px", cursor: "pointer", flexShrink: 0, opacity: flagNoteOpen ? 1 : 0.65, display: "inline-flex", alignItems: "center", gap: 4 }}>
                                                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" y1="22" x2="4" y2="15"/></svg>Flag
                                              </button>
                                            )}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                    {/* AP-1: inline flag note editor */}
                                    {flagNoteOpen && !isSecFlagged && (
                                      <div style={{ padding: "8px 12px", background: UI.amdBg, borderTop: `1px solid ${UI.amdBdr}`, display: "flex", gap: 8, alignItems: "flex-start" }} onClick={e => e.stopPropagation()}>
                                        <span style={{ fontFamily: FF9, fontSize: 11, color: UI.amber, fontWeight: 700, paddingTop: 6, flexShrink: 0, display: "inline-flex", alignItems: "center", gap: 4 }}><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" y1="22" x2="4" y2="15"/></svg>Note to promoter:</span>
                                        <input
                                          className="f-inp"
                                          placeholder="e.g. Please clarify the catering buyout amount"
                                          style={{ flex: 1, fontSize: 11 }}
                                          autoFocus
                                          onKeyDown={e => {
                                            if (e.key === "Enter") {
                                              e.preventDefault();
                                              flagAdvanceSection(show.id, sec.id, e.target.value.trim());
                                              togCat(flagNoteKey);
                                            }
                                            if (e.key === "Escape") togCat(flagNoteKey);
                                          }}
                                        />
                                        <button
                                          onClick={e => {
                                            const inp = e.target.closest("[style]").querySelector("input");
                                            flagAdvanceSection(show.id, sec.id, inp?.value?.trim() || "");
                                            togCat(flagNoteKey);
                                          }}
                                          style={{ fontFamily: FF9, fontSize: 11, fontWeight: 700, color: "var(--accent-fg)", background: UI.amber, border: "none", borderRadius: 4, padding: "4px 10px", cursor: "pointer", flexShrink: 0 }}>
                                          Send Flag
                                        </button>
                                      </div>
                                    )}
                                    {/* AP-1: existing flag note display */}
                                    {isSecFlagged && secSt.note && (
                                      <div style={{ padding: "7px 12px", background: UI.amdBg, borderTop: `1px solid ${UI.amdBdr}`, display: "flex", gap: 8, alignItems: "center" }}>
                                        <span style={{ fontFamily: FF9, fontSize: 11, color: UI.amber, fontWeight: 700, flexShrink: 0, display: "inline-flex", alignItems: "center", gap: 4 }}><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" y1="22" x2="4" y2="15"/></svg>TM note to promoter:</span>
                                        <span style={{ flex: 1, fontSize: 11, color: UI.amber }}>{secSt.note}</span>
                                      </div>
                                    )}
                                    {/* F-5d: Festival flag banner — visible to TM when festival has flagged this section */}
                                    {(() => {
                                      const curLinks = showEventLinks[selS] || [];
                                      const festLink = curLinks.find(l => l.status === "confirmed" && l.metadata?.org_type === "festival");
                                      if (!festLink) return null;
                                      const festFlags  = festLink.venue_section_status?.tm_section_flags || {};
                                      const festSecFlg = festFlags[sec.id] || {};
                                      if (!festSecFlg.status || festSecFlg.status === "confirmed") return null;
                                      const festName = festLink.metadata?.venue_name || "Festival";
                                      return (
                                        <div style={{ padding: "7px 12px", background: "#e06fdf12", borderTop: `1px solid #e06fdf33`, display: "flex", gap: 8, alignItems: "center" }}>
                                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#e06fdf" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                                            <polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/>
                                          </svg>
                                          <span style={{ fontFamily: FF9, fontSize: 11, color: "#c050c8", fontWeight: 700, flexShrink: 0 }}>
                                            {festName} flagged:
                                          </span>
                                          <span style={{ flex: 1, fontSize: 11, color: "#c050c8" }}>
                                            {festSecFlg.note || "Needs attention"}
                                          </span>
                                          <button
                                            onClick={e => { e.stopPropagation(); resolveFestFlag(show.id, sec.id); }}
                                            title="Mark this festival flag as resolved"
                                            style={{ fontFamily: FF9, fontSize: 11, fontWeight: 700, color: "#0d0d0d", background: "#c050c8", border: "none", borderRadius: 4, padding: "2px 8px", cursor: "pointer", flexShrink: 0 }}>
                                            Noted ✓
                                          </button>
                                        </div>
                                      );
                                    })()}
                                    <div className={`tm-flds-wrap ${isOpen ? "open" : ""}`}>
                                      <div className="tm-flds-inner">
                                    <div className="adv-flds" style={{ padding: "12px 14px 14px", borderTop: `1px solid ${UI.rowHov}`, display: "flex", flexDirection: "column", gap: 12 }}>

                                        {/* ── Bill Editor (schedule section only) ── */}
                                        {sec.id === "schedule" && (
                                          <div>
                                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                                              <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, letterSpacing: "1.5px", textTransform: "uppercase", color: UI.textFnt }}>Bill</div>
                                              {tourDefaultSched && rawTmSd.bill && (
                                                <button
                                                  onClick={() => {
                                                    if (!window.confirm("Reset this show's schedule to the tour defaults?")) return;
                                                    updAdvanceTmBatch("schedule", {
                                                      bill: tourDefaultSched.acts
                                                        ? tourDefaultSched.acts.map(a => ({ id: a.id, name: a.name, load: a.load_in || "", sc: a.soundcheck || "", set: a.set_time || "" }))
                                                        : [],
                                                      changeovers: tourDefaultSched.acts
                                                        ? tourDefaultSched.acts.slice(0, -1).map(a => ({ time: a.changeover || "" }))
                                                        : [],
                                                      venue_access: tourDefaultSched.venue_access || "",
                                                      vip_access:   tourDefaultSched.vip_doors    || "",
                                                      doors_public: tourDefaultSched.doors         || "",
                                                      noise_curfew: tourDefaultSched.noise_curfew  || "",
                                                      gr_curfew:    tourDefaultSched.gr_curfew     || "",
                                                      curfew:       tourDefaultSched.hard_curfew   || "",
                                                    });
                                                  }}
                                                  style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: UI.textMut, background: "none", border: `1px solid ${UI.panelBdr}`, borderRadius: 4, padding: "2px 8px", cursor: "pointer" }}>
                                                  ↺ Tour defaults
                                                </button>
                                              )}
                                            </div>
                                            <BillEditorInline
                                              schedule={tmSd}
                                              onUpdate={(update) => updAdvanceTmBatch("schedule", update)}
                                              UI={UI} lightMode={lightMode}
                                            />
                                            {/* Promoter's proposed ROS changes (from the portal bill editor) */}
                                            {rosDiffs.length > 0 && (
                                              <div style={{ marginTop: 10, padding: "8px 10px", background: UI.amdBg, border: `1px solid ${UI.amdBdr}`, borderRadius: 6 }}>
                                                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                                                  <span style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, fontWeight: 700, letterSpacing: 1, color: UI.amber }}>PROMOTER PROPOSED SCHEDULE CHANGES</span>
                                                  <span style={{ flex: 1 }} />
                                                  <button
                                                    title="Replace your Run of Show with the promoter's proposed version"
                                                    onClick={() => {
                                                      if (!rosProposal) return;
                                                      updAdvanceTmBatch("schedule", { bill: rosProposal.bill, changeovers: rosProposal.changeovers });
                                                    }}
                                                    style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, fontWeight: 700, letterSpacing: 0.5, color: UI.bg, background: UI.amber, border: "none", borderRadius: 4, padding: "2px 8px", cursor: "pointer", flexShrink: 0 }}>
                                                    ← Apply
                                                  </button>
                                                  <button
                                                    title="Clear the promoter's proposal and keep your schedule"
                                                    onClick={async () => {
                                                      selfAdvDataWriteRef.current = Date.now(); // suppress self-toast
                                                      const nextAdv = { ...(show.advanceData || {}) };
                                                      const schedNext = { ...(nextAdv.schedule || {}) };
                                                      delete schedNext.bill;
                                                      delete schedNext.changeovers;
                                                      nextAdv.schedule = schedNext;
                                                      const { error } = await supabase.from("shows").update({ advance_data: nextAdv }).eq("id", selS);
                                                      if (!error) setData(p => ({ ...p, artists: p.artists.map(a => ({ ...a,
                                                        tours: a.tours.map(t => ({ ...t,
                                                          shows: t.shows.map(s => s.id === selS ? { ...s, advanceData: nextAdv } : s)
                                                        }))
                                                      })) }));
                                                    }}
                                                    style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: UI.textMut, background: "none", border: `1px solid ${UI.panelBdr}`, borderRadius: 4, padding: "2px 8px", cursor: "pointer", flexShrink: 0 }}>
                                                    Dismiss
                                                  </button>
                                                </div>
                                                {rosDiffs.map((d, di) => (
                                                  <div key={di} style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: UI.textMid, lineHeight: 1.6 }}>
                                                    <span style={{ color: UI.amber, fontWeight: 600 }}>{d.label}</span>
                                                    <span style={{ color: UI.textFnt }}> — </span>
                                                    {d.parts.join(" · ")}
                                                  </div>
                                                ))}
                                              </div>
                                            )}
                                            {timeFields.length > 0 && (
                                              <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, letterSpacing: "1.5px", textTransform: "uppercase", color: UI.textFnt, marginTop: 16, marginBottom: 4 }}>Venue Times</div>
                                            )}
                                          </div>
                                        )}

                                        {timeFields.length > 0 && (
                                          <div className="adv-fld-grid">
                                            {timeFields.map(f => renderField(f,
                                              <TimeInput12
                                                value={tmSd[f.id] || ""}
                                                onChange={v => updAdvanceTmField(sec.id, f.id, v)}
                                                className="f-inp"
                                                inputStyle={{ padding: "4px 6px" }}
                                                toggleStyle={{ fontFamily: FF9, background: "var(--panel-alt)", color: "var(--text-mut)", border: "1px solid var(--field-border, var(--panel-border))", borderRadius: "0 6px 6px 0" }}
                                              />
                                            ))}
                                          </div>
                                        )}
                                        {ynFields.length > 0 && (
                                          <div className="adv-fld-grid">
                                            {ynFields.map(f => {
                                              const val = tmSd[f.id];
                                              const pv  = proSd[f.id];
                                              const amended = pv !== undefined && pv !== null && pv !== val;
                                              return renderField(f,
                                                <div style={{ display: "flex", gap: 6 }}>
                                                  <button style={{ flex: 1, padding: "6px 0", borderRadius: 4, border: `1px solid ${val === true ? UI.green : UI.fieldBdr}`, background: val === true ? UI.ckOnBg : UI.rowBg2, color: val === true ? UI.green : UI.textMut, fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, letterSpacing: 0.5, cursor: "pointer", fontWeight: val === true ? 600 : 400 }}
                                                    onClick={() => updAdvanceTmField(sec.id, f.id, true)}>Yes</button>
                                                  <button style={{ flex: 1, padding: "6px 0", borderRadius: 4, border: `1px solid ${val === false ? "var(--danger)" : UI.fieldBdr}`, background: val === false ? UI.amdBg : UI.rowBg2, color: val === false ? "var(--danger)" : UI.textMut, fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, letterSpacing: 0.5, cursor: "pointer", fontWeight: val === false ? 600 : 400 }}
                                                    onClick={() => updAdvanceTmField(sec.id, f.id, false)}>No</button>
                                                </div>
                                              );
                                            })}
                                          </div>
                                        )}
                                        {contactFields.map(f => {
                                          const tmC  = (typeof tmSd[f.id]  === "object" && tmSd[f.id])  ? tmSd[f.id]  : {};
                                          const proC = (typeof proSd[f.id] === "object" && proSd[f.id]) ? proSd[f.id] : {};
                                          return renderField(f,
                                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
                                              {[["name","Name"],["phone","Phone"],["email","Email"]].map(([sub, ph]) => {
                                                const amended = proC[sub] !== undefined && proC[sub] !== "" && proC[sub] !== tmC[sub];
                                                return (
                                                  <div key={sub}>
                                                    <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: UI.textVFnt, marginBottom: 2, letterSpacing: 0.5 }}>{ph}</div>
                                                    <input className="f-inp"
                                                      style={ amended ? { borderColor: UI.amdBdr, color: UI.amber } : {} }
                                                      placeholder={ph}
                                                      value={tmC[sub] || ""}
                                                      onChange={e => {
                                                        const cur = (typeof tmSd[f.id] === "object" && tmSd[f.id]) ? tmSd[f.id] : {};
                                                        updAdvanceTmField(sec.id, f.id, { ...cur, [sub]: e.target.value });
                                                      }} />
                                                    {amended && <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: UI.amber, marginTop: 2 }}>Promoter: {proC[sub]}</div>}
                                                  </div>
                                                );
                                              })}
                                            </div>
                                          );
                                        })}
                                        {shortTextFields.length > 0 && (
                                          <div className="adv-fld-grid">
                                            {shortTextFields.map(f => {
                                              const tourDefault =
                                                (sec.id === "hospitality" && f.id === "dietary")
                                                  ? (tour?.advanceConfig?.dietaryNotes || "")
                                                : (sec.id === "parking" && f.id === "vehicle")
                                                  ? (tour?.advanceConfig?.vehicleNotes || "")
                                                : "";
                                              const usingTourDefault = !!tourDefault && !tmSd[f.id];
                                              return renderField(f,
                                                <>
                                                  {f.type === "percent" ? (
                                                    <div style={{ position: "relative" }}>
                                                      <input className="f-inp"
                                                        placeholder="0"
                                                        inputMode="decimal"
                                                        value={String(tmSd[f.id] || "").replace(/%/g, "").trim()}
                                                        style={{ paddingRight: 26 }}
                                                        onChange={e => {
                                                          const v = e.target.value.replace(/[^0-9.]/g, "");
                                                          updAdvanceTmField(sec.id, f.id, v ? `${v}%` : "");
                                                        }} />
                                                      <span style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: UI.textFnt, pointerEvents: "none", userSelect: "none" }}>%</span>
                                                    </div>
                                                  ) : (
                                                    <input className="f-inp"
                                                      placeholder={tourDefault || "—"}
                                                      value={tmSd[f.id] || ""}
                                                      onChange={e => updAdvanceTmField(sec.id, f.id, e.target.value)} />
                                                  )}
                                                  {usingTourDefault && (
                                                    <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: UI.amber, marginTop: 3, letterSpacing: 0.3 }}>
                                                      ↑ Tour default active — type above to override for this show
                                                    </div>
                                                  )}
                                                </>
                                              );
                                            })}
                                          </div>
                                        )}
                                        {longTextFields.map(f => {
                                          const tourDefault =
                                            (sec.id === "hospitality" && f.id === "dietary")
                                              ? (tour?.advanceConfig?.dietaryNotes || "")
                                            : (sec.id === "parking" && f.id === "vehicle")
                                              ? (tour?.advanceConfig?.vehicleNotes || "")
                                            : "";
                                          const usingTourDefault = !!tourDefault && !tmSd[f.id];
                                          return renderField(f,
                                            <>
                                              <textarea className="f-inp"
                                                placeholder={tourDefault || "—"}
                                                value={tmSd[f.id] || ""}
                                                rows={2}
                                                style={{ resize: "vertical", lineHeight: 1.5, minHeight: 52 }}
                                                onChange={e => updAdvanceTmField(sec.id, f.id, e.target.value)} />
                                              {usingTourDefault && (
                                                <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: UI.amber, marginTop: 3, letterSpacing: 0.3 }}>
                                                  ↑ Tour default active — type above to override for this show
                                                </div>
                                              )}
                                            </>
                                          );
                                        })}

                                        {/* ── Wi-Fi Networks (TM side) ── */}
                                        {visFields.filter(f => f.type === "wifi_networks").map(f => {
                                          const FF5 = "'Helvetica Neue', Helvetica, Arial, sans-serif";
                                          const nets = Array.isArray(tmSd[f.id]) ? tmSd[f.id] : [];
                                          const updateNets = (next) => updAdvanceTmField(sec.id, f.id, next);
                                          return renderField(f,
                                            <div key={f.id}>
                                              {nets.map((net, i) => (
                                                <div key={i} style={{ display: "flex", gap: 6, marginBottom: 6, alignItems: "center" }}>
                                                  <input className="f-inp" placeholder="Network name" style={{ flex: 1 }}
                                                    value={net.network || ""}
                                                    onChange={e => updateNets(nets.map((n, j) => j === i ? { ...n, network: e.target.value } : n))} />
                                                  <input className="f-inp" placeholder="Password" style={{ flex: 1 }}
                                                    value={net.password || ""}
                                                    onChange={e => updateNets(nets.map((n, j) => j === i ? { ...n, password: e.target.value } : n))} />
                                                  <button onClick={() => updateNets(nets.filter((_, j) => j !== i))}
                                                    style={{ background: "none", border: "none", cursor: "pointer", color: UI.textFnt, fontSize: 14, lineHeight: 1, padding: "0 2px", flexShrink: 0 }}>×</button>
                                                </div>
                                              ))}
                                              <button onClick={() => updateNets([...nets, { network: "", password: "" }])}
                                                style={{ fontFamily: FF5, fontSize: 11, color: UI.amber, background: "none", border: `1px solid ${UI.amdBdr}`, borderRadius: 4, padding: "3px 10px", cursor: "pointer" }}>
                                                + Add Network
                                              </button>
                                            </div>
                                          );
                                        })}

                                        {/* ── Guest List Sheet (inline inside Guest List section) ── */}
                                        {sec.id === "guestList" && (() => {
                                          const FF5 = "'Helvetica Neue', Helvetica, Arial, sans-serif";
                                          const GL_ACCESS = ["GA","AA","Family","Media","VIP","Backstage"];
                                          const GL_ACCESS_COLOR = { GA: UI.textMut, AA: "var(--accent)", Family: "var(--success)", Media: "#6a8faf", VIP: "#8a6faf", Backstage: "var(--accent)" };
                                          const glEntries = show?.advanceTmData?.guestListSheet?.entries || [];
                                          const addGlEntry = () => {
                                            const next = [...glEntries, { id: crypto.randomUUID(), firstName: "", lastName: "", email: "", tickets: 1, access: [], note: "", confirmed: false }];
                                            updAdvanceTmField("guestListSheet", "entries", next);
                                          };
                                          const updGlEntry = (id, field, val) => {
                                            const next = glEntries.map(e => e.id === id ? { ...e, [field]: val } : e);
                                            updAdvanceTmField("guestListSheet", "entries", next);
                                          };
                                          const toggleGlAccess = (id, tag) => {
                                            const entry = glEntries.find(e => e.id === id);
                                            const cur = Array.isArray(entry?.access) ? entry.access : [];
                                            const next = cur.includes(tag) ? cur.filter(a => a !== tag) : [...cur, tag];
                                            updGlEntry(id, "access", next);
                                          };
                                          const removeGlEntry = (id) => {
                                            const next = glEntries.filter(e => e.id !== id);
                                            updAdvanceTmField("guestListSheet", "entries", next);
                                          };
                                          const totalTix = glEntries.reduce((sum, e) => sum + (Number(e.tickets) || 1), 0);
                                          const pendingReqs = glRequests.filter(r => r.status === "pending").length;
                                          return (
                                            <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${UI.rowHov}` }}>
                                              {/* Sub-header */}
                                              <div style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
                                                <span style={{ fontFamily: FF5, fontSize: 11, letterSpacing: "1.5px", textTransform: "uppercase", color: UI.textFnt }}>Guest List Sheet</span>
                                                {glEntries.length > 0 && <span style={{ fontFamily: FF5, fontSize: 11, color: UI.green, marginLeft: 8 }}>{glEntries.length} guest{glEntries.length !== 1 ? "s" : ""} · {totalTix} ticket{totalTix !== 1 ? "s" : ""} · {glEntries.filter(e => e.confirmed).length} confirmed</span>}
                                                {pendingReqs > 0 && <span style={{ fontFamily: FF5, fontSize: 11, color: UI.amber, marginLeft: 6 }}>· {pendingReqs} request{pendingReqs !== 1 ? "s" : ""} pending</span>}
                                                <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
                                                  <button onClick={() => { setGlBulkOpen(o => !o); setGlBulkParsed(null); setGlBulkText(""); }} style={{ fontFamily: FF5, fontSize: 11, color: UI.textMut, background: "none", border: `1px solid ${UI.panelBdr}`, borderRadius: 4, padding: "3px 10px", cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}>
                                                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                                                    Import
                                                  </button>
                                                  <button onClick={addGlEntry} style={{ fontFamily: FF5, fontSize: 11, color: UI.amber, background: "none", border: `1px solid ${UI.amdBdr}`, borderRadius: 4, padding: "3px 10px", cursor: "pointer" }}>+ Add Guest</button>
                                                </div>
                                              </div>
                                              {/* GL Bulk Import panel */}
                                              {glBulkOpen && (
                                                <div style={{ background: UI.panelBg, border: `1px solid ${UI.panelBdr}`, borderRadius: 8, padding: "12px 14px", marginBottom: 10 }}>
                                                  {!glBulkParsed ? (
                                                    <>
                                                      <div style={{ fontFamily: FF5, fontSize: 11, color: UI.text, fontWeight: 600, marginBottom: 4 }}>Paste guest list</div>
                                                      <div style={{ fontFamily: FF5, fontSize: 11, color: UI.textMut, marginBottom: 8 }}>Paste any format — promoter email, forwarded message, plain list. Claude will extract the names.</div>
                                                      <textarea
                                                        value={glBulkText}
                                                        onChange={e => setGlBulkText(e.target.value)}
                                                        placeholder={"John Smith +2\nMaria Garcia — VIP\nThe Rodriguez family x4\nPress: Alex Kim (Media)"}
                                                        style={{ width: "100%", minHeight: 90, fontFamily: FF5, fontSize: 11, color: UI.text, background: UI.rowBg, border: `1px solid ${UI.fieldBdr}`, borderRadius: 4, padding: "8px 10px", resize: "vertical", boxSizing: "border-box" }}
                                                      />
                                                      <div style={{ display: "flex", gap: 8, marginTop: 8, justifyContent: "flex-end" }}>
                                                        <button onClick={() => setGlBulkOpen(false)} style={{ fontFamily: FF5, fontSize: 11, color: UI.textMut, background: "none", border: `1px solid ${UI.panelBdr}`, borderRadius: 4, padding: "4px 12px", cursor: "pointer" }}>Cancel</button>
                                                        <button
                                                          disabled={!glBulkText.trim() || glBulkParsing}
                                                          onClick={async () => {
                                                            setGlBulkParsing(true);
                                                            try {
                                                              const parsed = await parseGuestListWithAI(glBulkText);
                                                              setGlBulkParsed(parsed.map((g, i) => ({ ...g, _id: i, firstName: g.firstName || "", lastName: g.lastName || "", tickets: g.tickets || 1, access: Array.isArray(g.access) ? g.access : ["GA"], note: g.note || "", _remove: false })));
                                                            } catch { setGlBulkParsed([]); }
                                                            setGlBulkParsing(false);
                                                          }}
                                                          style={{ fontFamily: FF5, fontSize: 11, color: glBulkParsing || !glBulkText.trim() ? UI.textVFnt : UI.amber, background: "none", border: `1px solid ${glBulkParsing || !glBulkText.trim() ? UI.panelBdr : UI.amdBdr}`, borderRadius: 4, padding: "4px 14px", cursor: glBulkParsing || !glBulkText.trim() ? "default" : "pointer" }}
                                                        >{glBulkParsing ? "Parsing…" : "Parse with AI"}</button>
                                                      </div>
                                                    </>
                                                  ) : (
                                                    <>
                                                      <div style={{ display: "flex", alignItems: "center", marginBottom: 10 }}>
                                                        <div>
                                                          <div style={{ fontFamily: FF5, fontSize: 11, color: UI.text, fontWeight: 600 }}>
                                                            {glBulkParsed.filter(g => !g._remove).length} guest{glBulkParsed.filter(g => !g._remove).length !== 1 ? "s" : ""} found
                                                          </div>
                                                          <div style={{ fontFamily: FF5, fontSize: 11, color: UI.textMut, marginTop: 1 }}>Review and edit before adding</div>
                                                        </div>
                                                        <button onClick={() => { setGlBulkParsed(null); }} style={{ marginLeft: "auto", fontFamily: FF5, fontSize: 11, color: UI.textMut, background: "none", border: `1px solid ${UI.panelBdr}`, borderRadius: 4, padding: "3px 10px", cursor: "pointer" }}>← Back</button>
                                                      </div>
                                                      {glBulkParsed.length === 0 && (
                                                        <div style={{ fontFamily: FF5, fontSize: 11, color: UI.textMut, textAlign: "center", padding: "12px 0" }}>No guests detected. Go back and try a different format.</div>
                                                      )}
                                                      <div style={{ display: "flex", flexDirection: "column", gap: 5, maxHeight: 260, overflowY: "auto" }}>
                                                        {glBulkParsed.map((g, idx) => (
                                                          <div key={g._id} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 44px 80px 20px", gap: 5, alignItems: "center", opacity: g._remove ? 0.35 : 1 }}>
                                                            <input className="f-inp" placeholder="Last name" value={g.lastName || ""} onChange={ev => setGlBulkParsed(p => p.map((x, i) => i === idx ? { ...x, lastName: ev.target.value } : x))} style={{ textDecoration: g._remove ? "line-through" : "none" }} />
                                                            <input className="f-inp" placeholder="First name" value={g.firstName || ""} onChange={ev => setGlBulkParsed(p => p.map((x, i) => i === idx ? { ...x, firstName: ev.target.value } : x))} />
                                                            <input className="f-inp" type="number" min="1" max="20" value={g.tickets} onChange={ev => setGlBulkParsed(p => p.map((x, i) => i === idx ? { ...x, tickets: Number(ev.target.value) || 1 } : x))} style={{ textAlign: "center" }} />
                                                            <div style={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                                                              {["GA","AA","VIP","Media","Family","Backstage"].map(tag => {
                                                                const GL_ACCESS_COLOR = { GA: UI.textMut, AA: "var(--accent)", Family: "var(--success)", Media: "#6a8faf", VIP: "#8a6faf", Backstage: "var(--accent)" };
                                                                const active = (g.access || []).includes(tag);
                                                                return (
                                                                  <button key={tag} onClick={() => setGlBulkParsed(p => p.map((x, i) => {
                                                                    if (i !== idx) return x;
                                                                    const cur = x.access || [];
                                                                    return { ...x, access: cur.includes(tag) ? cur.filter(a => a !== tag) : [...cur, tag] };
                                                                  }))} style={{ fontFamily: FF5, fontSize: 11, letterSpacing: 0.5, padding: "1px 5px", borderRadius: 4, border: `1px solid ${active ? GL_ACCESS_COLOR[tag] : UI.panelBdr}`, color: active ? GL_ACCESS_COLOR[tag] : UI.textVFnt, background: active ? `${GL_ACCESS_COLOR[tag]}18` : "none", cursor: "pointer" }}>{tag}</button>
                                                                );
                                                              })}
                                                            </div>
                                                            <button onClick={() => setGlBulkParsed(p => p.map((x, i) => i === idx ? { ...x, _remove: !x._remove } : x))} title={g._remove ? "Restore" : "Remove"} style={{ background: "none", border: "none", cursor: "pointer", color: UI.textVFnt, fontSize: 14, lineHeight: 1, padding: 0 }}>{g._remove ? "↩" : "×"}</button>
                                                          </div>
                                                        ))}
                                                      </div>
                                                      <div style={{ display: "flex", gap: 8, marginTop: 10, justifyContent: "flex-end" }}>
                                                        <button onClick={() => { setGlBulkOpen(false); setGlBulkParsed(null); setGlBulkText(""); }} style={{ fontFamily: FF5, fontSize: 11, color: UI.textMut, background: "none", border: `1px solid ${UI.panelBdr}`, borderRadius: 4, padding: "4px 12px", cursor: "pointer" }}>Cancel</button>
                                                        <button
                                                          disabled={!glBulkParsed.some(g => !g._remove)}
                                                          onClick={() => {
                                                            const toAdd = glBulkParsed.filter(g => !g._remove).map(g => ({ id: crypto.randomUUID(), firstName: g.firstName || "", lastName: g.lastName || "", email: "", tickets: g.tickets || 1, access: g.access || ["GA"], note: g.note || "", confirmed: false }));
                                                            const next = [...glEntries, ...toAdd];
                                                            updAdvanceTmField("guestListSheet", "entries", next);
                                                            setGlBulkOpen(false);
                                                            setGlBulkParsed(null);
                                                            setGlBulkText("");
                                                          }}
                                                          style={{ fontFamily: FF5, fontSize: 11, color: glBulkParsed.some(g => !g._remove) ? UI.amber : UI.textVFnt, background: "none", border: `1px solid ${glBulkParsed.some(g => !g._remove) ? UI.amdBdr : UI.panelBdr}`, borderRadius: 4, padding: "4px 14px", cursor: glBulkParsed.some(g => !g._remove) ? "pointer" : "default" }}
                                                        >Add {glBulkParsed.filter(g => !g._remove).length} Guest{glBulkParsed.filter(g => !g._remove).length !== 1 ? "s" : ""}</button>
                                                      </div>
                                                    </>
                                                  )}
                                                </div>
                                              )}
                                              {/* TM guest rows */}
                                              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                                {glEntries.map((e, idx) => {
                                                  const accessArr = Array.isArray(e.access) ? e.access : (e.access ? [e.access] : []);
                                                  return (
                                                    <div key={e.id} style={{ background: UI.panelBg, border: `1px solid ${e.confirmed ? UI.ckOnBdr : UI.fieldBdr}`, borderRadius: 8, padding: "8px 10px", display: "flex", flexDirection: "column", gap: 6 }}>
                                                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1.5fr 44px 1fr", gap: 5, alignItems: "end" }}>
                                                        <div>
                                                          {idx === 0 && <div style={{ fontFamily: FF5, fontSize: 11, color: UI.textVFnt, letterSpacing: 0.8, textTransform: "uppercase", marginBottom: 3 }}>Last Name</div>}
                                                          <input className="f-inp" placeholder="Last name" value={e.lastName} onChange={ev => updGlEntry(e.id, "lastName", ev.target.value)} />
                                                        </div>
                                                        <div>
                                                          {idx === 0 && <div style={{ fontFamily: FF5, fontSize: 11, color: UI.textVFnt, letterSpacing: 0.8, textTransform: "uppercase", marginBottom: 3 }}>First Name</div>}
                                                          <input className="f-inp" placeholder="First name" value={e.firstName} onChange={ev => updGlEntry(e.id, "firstName", ev.target.value)} />
                                                        </div>
                                                        <div>
                                                          {idx === 0 && <div style={{ fontFamily: FF5, fontSize: 11, color: UI.textVFnt, letterSpacing: 0.8, textTransform: "uppercase", marginBottom: 3 }}>Email</div>}
                                                          <input className="f-inp" placeholder="Email" value={e.email} onChange={ev => updGlEntry(e.id, "email", ev.target.value)} />
                                                        </div>
                                                        <div>
                                                          {idx === 0 && <div style={{ fontFamily: FF5, fontSize: 11, color: UI.textVFnt, letterSpacing: 0.8, textTransform: "uppercase", marginBottom: 3 }}>Tickets</div>}
                                                          <input className="f-inp" type="number" min="1" max="20" placeholder="1" value={e.tickets ?? 1} onChange={ev => updGlEntry(e.id, "tickets", Math.max(1, parseInt(ev.target.value, 10) || 1))} style={{ textAlign: "center" }} />
                                                        </div>
                                                        <div>
                                                          {idx === 0 && <div style={{ fontFamily: FF5, fontSize: 11, color: UI.textVFnt, letterSpacing: 0.8, textTransform: "uppercase", marginBottom: 3 }}>Note</div>}
                                                          <input className="f-inp" placeholder="Note" value={e.note} onChange={ev => updGlEntry(e.id, "note", ev.target.value)} />
                                                        </div>
                                                      </div>
                                                      <div style={{ display: "flex", alignItems: "center", gap: 5, flexWrap: "wrap" }}>
                                                        {idx === 0 && <span style={{ fontFamily: FF5, fontSize: 11, color: UI.textVFnt, letterSpacing: 0.8, textTransform: "uppercase", marginRight: 2 }}>Access:</span>}
                                                        {GL_ACCESS.map(tag => {
                                                          const on = accessArr.includes(tag);
                                                          return (
                                                            <button key={tag} onClick={() => toggleGlAccess(e.id, tag)}
                                                              style={{ fontFamily: FF5, fontSize: 11, padding: "2px 8px", borderRadius: 4, border: "1px solid", cursor: "pointer", letterSpacing: "0.3px",
                                                                background: on ? GL_ACCESS_COLOR[tag] : "none",
                                                                color: on ? "#0d0d0d" : UI.textVFnt,
                                                                borderColor: on ? GL_ACCESS_COLOR[tag] : UI.fieldBdr,
                                                                fontWeight: on ? 700 : 400,
                                                              }}>{tag}</button>
                                                          );
                                                        })}
                                                        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8 }}>
                                                          <label style={{ display: "flex", alignItems: "center", gap: 4, cursor: "pointer", fontFamily: FF5, fontSize: 11, color: e.confirmed ? UI.green : UI.textVFnt }}>
                                                            <input type="checkbox" checked={!!e.confirmed} onChange={ev => updGlEntry(e.id, "confirmed", ev.target.checked)} style={{ accentColor: UI.green, width: 12, height: 12 }} />
                                                            Confirmed
                                                          </label>
                                                          <button onClick={() => removeGlEntry(e.id)} title="Remove" style={{ background: "none", border: "none", color: UI.textVFnt, cursor: "pointer", padding: 0, lineHeight: 1, display: "inline-flex", alignItems: "center" }}><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></button>
                                                        </div>
                                                      </div>
                                                    </div>
                                                  );
                                                })}
                                              </div>
                                              {/* Member Requests */}
                                              {glRequests.length > 0 && (
                                                <div style={{ marginTop: 10, paddingTop: 8, borderTop: `1px solid ${UI.rowHov}` }}>
                                                  <div style={{ fontFamily: FF5, fontSize: 11, color: UI.textFnt, letterSpacing: "1.5px", textTransform: "uppercase", marginBottom: 6 }}>Member Requests</div>
                                                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                                                    {glRequests.map(r => {
                                                      const rAccess = Array.isArray(r.access) ? r.access : [];
                                                      return (
                                                        <div key={r.id} style={{ padding: "6px 8px", background: UI.panelBg, border: `1px solid ${r.status === "pending" ? UI.subBdr : r.status === "confirmed" ? UI.ckOnBdr : "#3a1010"}`, borderRadius: 4 }}>
                                                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                              <span style={{ fontFamily: FF5, fontSize: 11, color: UI.text, fontWeight: 500 }}>{r.first_name} {r.last_name}</span>
                                                              {(r.tickets || 1) > 1 && <span style={{ fontFamily: FF5, fontSize: 11, color: UI.textMut, marginLeft: 5 }}>×{r.tickets}</span>}
                                                              <span style={{ fontFamily: FF5, fontSize: 11, color: UI.textVFnt, marginLeft: 8 }}>from {r.submitted_by_name}</span>
                                                              {r.email && <span style={{ fontFamily: FF5, fontSize: 11, color: UI.textMut, marginLeft: 8 }}>{r.email}</span>}
                                                              {r.phone && <span style={{ fontFamily: FF5, fontSize: 11, color: UI.textMut, marginLeft: 8 }}>{fmtPhone(r.phone)}</span>}
                                                              {r.note && <span style={{ fontFamily: FF5, fontSize: 11, color: UI.textMut, marginLeft: 8 }}>— {r.note}</span>}
                                                            </div>
                                                            {r.status === "pending" ? (
                                                              <div style={{ display: "flex", gap: 5, flexShrink: 0 }}>
                                                                <button onClick={() => updGlRequestStatus(r.id, "confirmed")} style={{ fontFamily: FF5, fontSize: 11, padding: "2px 8px", borderRadius: 4, border: `1px solid ${UI.green}`, background: "none", color: UI.green, cursor: "pointer" }}>✓ Confirm</button>
                                                                <button onClick={() => updGlRequestStatus(r.id, "denied")} style={{ fontFamily: FF5, fontSize: 11, padding: "2px 8px", borderRadius: 4, border: "1px solid var(--danger)", background: "none", color: "var(--danger)", cursor: "pointer" }}>Deny</button>
                                                              </div>
                                                            ) : (
                                                              <span style={{ fontFamily: FF5, fontSize: 11, color: r.status === "confirmed" ? UI.green : "var(--danger)", fontWeight: 600, flexShrink: 0 }}>{r.status === "confirmed" ? "Confirmed" : "Denied"}</span>
                                                            )}
                                                          </div>
                                                          {rAccess.length > 0 && (
                                                            <div style={{ display: "flex", gap: 4, marginTop: 4, flexWrap: "wrap" }}>
                                                              {rAccess.map(tag => (
                                                                <span key={tag} style={{ fontFamily: FF5, fontSize: 11, padding: "1px 6px", borderRadius: 4, border: `1px solid ${GL_ACCESS_COLOR[tag] || UI.fieldBdr}`, color: GL_ACCESS_COLOR[tag] || UI.textVFnt, letterSpacing: "0.3px" }}>{tag}</span>
                                                              ))}
                                                            </div>
                                                          )}
                                                        </div>
                                                      );
                                                    })}
                                                  </div>
                                                </div>
                                              )}
                                              {glEntries.length === 0 && glRequests.length === 0 && (
                                                <div style={{ fontFamily: FF5, fontSize: 11, color: UI.textVFnt, padding: "4px 0 2px" }}>No guests added yet.</div>
                                              )}
                                            </div>
                                          );
                                        })()}

                                      </div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>{/* end adv-sections */}

                            {/* ── Touring Party (per-show) — PP-1: defaults from people_profiles ── */}
                            {(() => {
                              const FF5 = "'Helvetica Neue', Helvetica, Arial, sans-serif";
                              // PP-1: tour defaults from people_profiles; legacy crewManifest as fallback
                              const profileDefaults = profiles.filter(p => p.current_tour_id === selT).map(p => ({ id: p.id, name: p.name || "", role: p.role || "", phone: p.phone || "", email: p.email || "" }));
                              const tourDefaults = profileDefaults.length > 0 ? profileDefaults : (tour?.advanceConfig?.crewManifest || []);
                              // _showCrew.entries === array → show-level override active
                              // _showCrew.entries === null/absent → fall back to tour defaults
                              const hasOverride = Array.isArray(show?.advanceTmData?._showCrew?.entries);
                              const crew = hasOverride ? show.advanceTmData._showCrew.entries : tourDefaults;
                              const isOpen = !!cats["tm__showcrew"];

                              const addCrewRow = () => {
                                const base = hasOverride ? crew : tourDefaults.map(p => ({ ...p, id: p.id || crypto.randomUUID() }));
                                const next = [...base, { id: crypto.randomUUID(), name: "", role: "", phone: "", email: "" }];
                                updAdvanceTmBatch("_showCrew", { entries: next });
                              };
                              const updCrewRow = (id, field, val) => {
                                const next = crew.map(p => p.id === id ? { ...p, [field]: val } : p);
                                updAdvanceTmBatch("_showCrew", { entries: next });
                              };
                              const removeCrewRow = (id) => {
                                const next = crew.filter(p => p.id !== id);
                                updAdvanceTmBatch("_showCrew", { entries: next });
                              };
                              const resetToTourDefaults = () => {
                                updAdvanceTmBatch("_showCrew", { entries: null });
                              };

                              return (
                                <div style={{ background: UI.rowBg, border: `1px solid ${UI.subBdr}`, borderRadius: 8, overflow: "hidden", marginTop: 0, marginBottom: 6 }}>
                                  <div style={{ padding: "9px 12px", display: "flex", alignItems: "center", gap: 8, cursor: "pointer", userSelect: "none" }} onClick={() => togCat("tm__showcrew")}>
                                    <span style={{ color: UI.textVFnt, transform: isOpen ? "rotate(90deg)" : "none", transition: "transform 0.28s cubic-bezier(0.4,0,0.2,1)", display: "inline-flex", flexShrink: 0 }}><svg width="10" height="10" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M4.5 2l4 4-4 4"/></svg></span>
                                    <span style={{ fontFamily: FF5, fontSize: 11, color: UI.textMut, textTransform: "uppercase", letterSpacing: 1.2 }}>Touring Party</span>
                                    <div style={{ marginLeft: "auto" }}>
                                      {hasOverride
                                        ? <span style={{ fontFamily: FF5, fontSize: 11, color: UI.amber }}>Show override · {crew.length} member{crew.length !== 1 ? "s" : ""}</span>
                                        : tourDefaults.length > 0
                                          ? <span style={{ fontFamily: FF5, fontSize: 11, color: UI.textVFnt }}>Tour defaults · {tourDefaults.length} member{tourDefaults.length !== 1 ? "s" : ""}</span>
                                          : <span style={{ fontFamily: FF5, fontSize: 11, color: UI.textVFnt }}>No crew set</span>}
                                    </div>
                                  </div>
                                  <div className={`tm-flds-wrap ${isOpen ? "open" : ""}`}>
                                    <div className="tm-flds-inner">
                                      <div className="adv-flds" style={{ padding: "10px 14px 14px", borderTop: `1px solid ${UI.rowHov}` }}>
                                        {!hasOverride && tourDefaults.length > 0 && (
                                          <div style={{ fontFamily: FF5, fontSize: 11, color: UI.textMut, marginBottom: 10, padding: "8px 10px", background: UI.panelBg, borderRadius: 8, border: `1px solid ${UI.fieldBdr}` }}>
                                            Showing tour-wide crew. To customise for this show, click below.
                                            <br />
                                            <button onClick={() => updAdvanceTmBatch("_showCrew", { entries: tourDefaults.map(p => ({ ...p, id: p.id || crypto.randomUUID() })) })}
                                              style={{ marginTop: 6, fontFamily: FF5, fontSize: 11, color: UI.amber, background: "none", border: `1px solid ${UI.amdBdr}`, borderRadius: 4, padding: "3px 10px", cursor: "pointer" }}>
                                              Customise for this show
                                            </button>
                                          </div>
                                        )}
                                        {/* Crew rows */}
                                        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                                          {crew.map((p, idx) => (
                                            <div key={p.id || idx} style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr 1fr 1.5fr 20px", gap: 5, alignItems: "center" }}>
                                              {idx === 0 && ["Name", "Role", "Phone", "Email", ""].map((h, i) => (
                                                <div key={i} style={{ fontFamily: FF5, fontSize: 11, color: UI.textVFnt, letterSpacing: 0.8, textTransform: "uppercase", marginBottom: 2 }}>{h}</div>
                                              ))}
                                              {hasOverride ? (
                                                <>
                                                  <input className="f-inp" placeholder="Name" value={p.name || ""} onChange={ev => updCrewRow(p.id, "name", ev.target.value)} />
                                                  <input className="f-inp" placeholder="Role" value={p.role || ""} onChange={ev => updCrewRow(p.id, "role", ev.target.value)} />
                                                  <input className="f-inp" placeholder="Phone" value={p.phone || ""} onChange={ev => updCrewRow(p.id, "phone", ev.target.value)} />
                                                  <input className="f-inp" placeholder="Email" value={p.email || ""} onChange={ev => updCrewRow(p.id, "email", ev.target.value)} />
                                                  <button onClick={() => removeCrewRow(p.id)} title="Remove" style={{ background: "none", border: "none", color: UI.textVFnt, cursor: "pointer", padding: 0, lineHeight: 1, display: "inline-flex", alignItems: "center" }}><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></button>
                                                </>
                                              ) : (
                                                <>
                                                  <span style={{ fontFamily: FF5, fontSize: 11, color: UI.text, fontWeight: 500 }}>{p.name || "—"}</span>
                                                  <span style={{ fontFamily: FF5, fontSize: 11, color: UI.textMut }}>{p.role || "—"}</span>
                                                  <span style={{ fontFamily: FF5, fontSize: 11, color: UI.textMut }}>{p.phone || "—"}</span>
                                                  <span style={{ fontFamily: FF5, fontSize: 11, color: UI.textMut }}>{p.email || "—"}</span>
                                                  <span />
                                                </>
                                              )}
                                            </div>
                                          ))}
                                        </div>
                                        {crew.length === 0 && (
                                          <div style={{ fontFamily: FF5, fontSize: 11, color: UI.textVFnt, padding: "4px 0 8px" }}>No crew members. Add one below or set defaults in Tour Setup → Crew Roster.</div>
                                        )}
                                        {/* Actions */}
                                        <div style={{ display: "flex", gap: 8, marginTop: 10, alignItems: "center", flexWrap: "wrap" }}>
                                          {hasOverride && (
                                            <button onClick={addCrewRow}
                                              style={{ fontFamily: FF5, fontSize: 11, color: UI.amber, background: "none", border: `1px solid ${UI.amdBdr}`, borderRadius: 4, padding: "4px 12px", cursor: "pointer" }}>
                                              + Add Member
                                            </button>
                                          )}
                                          {hasOverride && (
                                            <button onClick={resetToTourDefaults}
                                              style={{ fontFamily: FF5, fontSize: 11, color: UI.textVFnt, background: "none", border: `1px solid ${UI.subBdr}`, borderRadius: 4, padding: "4px 12px", cursor: "pointer" }}>
                                              ↺ Reset to tour defaults
                                            </button>
                                          )}
                                          {!hasOverride && tourDefaults.length === 0 && (
                                            <button onClick={addCrewRow}
                                              style={{ fontFamily: FF5, fontSize: 11, color: UI.amber, background: "none", border: `1px solid ${UI.amdBdr}`, borderRadius: 4, padding: "4px 12px", cursor: "pointer" }}>
                                              + Add Member
                                            </button>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })()}

                            {/* Guest List Sheet is now inline inside the Guest List section above */}

                            <div className="adv-full">
                              {/* Additional notes */}
                              <div style={{ background: UI.rowBg, border: `1px solid ${UI.subBdr}`, borderRadius: 8, overflow: "hidden" }}>
                                <div style={{ padding: "9px 12px", display: "flex", alignItems: "center", gap: 8, cursor: "pointer", userSelect: "none" }} onClick={() => togCat("tm__notes")}>
                                  <span style={{ color: UI.textVFnt, transform: cats["tm__notes"] ? "rotate(90deg)" : "none", transition: "transform 0.28s cubic-bezier(0.4,0,0.2,1)", display: "inline-flex", flexShrink: 0 }}><svg width="10" height="10" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M4.5 2l4 4-4 4"/></svg></span>
                                  <span style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: UI.textMut, textTransform: "uppercase", letterSpacing: 1.2 }}>Additional Notes</span>
                                </div>
                                <div className={`tm-flds-wrap ${cats["tm__notes"] ? "open" : ""}`}>
                                  <div className="tm-flds-inner">
                                  <div className="adv-flds" style={{ padding: "10px 14px 14px", borderTop: `1px solid ${UI.rowHov}`, display: "flex", flexDirection: "column", gap: 6 }}>
                                    <textarea className="f-inp" rows={3} style={{ resize: "vertical", width: "100%", fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif" }}
                                      placeholder="Notes / context for the promoter..."
                                      value={tmD._notes || ""}
                                      onChange={e => updShow("advanceTmData", { ...tmD, _notes: e.target.value })} />
                                    {proD._notes && proD._notes !== tmD._notes && (
                                      <div style={{ padding: "6px 10px", background: UI.amdBg, border: `1px solid ${UI.amdBdr}`, borderRadius: 4 }}>
                                        <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: UI.amber, marginBottom: 3 }}>PROMOTER NOTES ↩</div>
                                        <div style={{ fontSize: 11, color: UI.amber, lineHeight: 1.5 }}>{proD._notes}</div>
                                      </div>
                                    )}
                                  </div>
                                  </div>
                                </div>
                              </div>

                            </div>
                            </div>
                          </div>
                        );
                      })()}
                  </div>
                  )} {/* end advance tab */}

                  {/* ── SETTLEMENT TAB ── */}
                  {/* ── EMAIL TAB ── */}
                  {detailTab === "email" && (
                  <div className="adv-wrap">
                    <div className="ec">
                      <div className="ec-hdr">
                        <span className="ec-ttl">AI Advance Email</span>
                        <button className="draft-btn" onClick={sendAdvance} disabled={eLoad || advGenLoading}>
                          {advGenLoading ? <><span className="spin" /> Generating link...</> : eLoad ? <><span className="spin" /> Drafting...</> : <><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: "middle", marginRight: 5 }}><path d="M12 3l1.9 5.8a2 2 0 0 0 1.3 1.3L21 12l-5.8 1.9a2 2 0 0 0-1.3 1.3L12 21l-1.9-5.8a2 2 0 0 0-1.3-1.3L3 12l5.8-1.9a2 2 0 0 0 1.3-1.3L12 3z"/></svg>Draft Email</>}
                        </button>
                      </div>
                      <div className="e-to">To: <span>{venue?.promoterName || venue?.contact || "—"}</span>{(venue?.promoterEmail || venue?.email) ? <> &lt;<span>{venue?.promoterEmail || venue?.email}</span>&gt;</> : ""}</div>
                      <div className="e-body" style={eDraft && !eLoad ? { padding: 0 } : {}}>
                        {eLoad
                          ? <div className="e-load"><div className="spin" /> Generating...</div>
                          : eDraft
                            ? <textarea
                                value={eDraft}
                                onChange={e => setEDraft(e.target.value)}
                                rows={Math.min(22, Math.max(10, eDraft.split("\n").length + 2))}
                                spellCheck={true}
                                style={{ display: "block", width: "100%", boxSizing: "border-box", background: "transparent", border: "none", outline: "none", resize: "vertical", padding: "14px 16px", fontSize: 14, color: "inherit", lineHeight: 1.65, fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif" }} />
                            : <span className="e-ph">Click Draft Email to generate a portal link and draft your intro email in one step. Pre-fill the Advance tab first to give the AI more context.</span>
                        }
                      </div>
                      {eDraft && !eLoad && (
                        <div style={{ padding: "0 16px 4px", fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: UI.textFnt, letterSpacing: 0.5 }}>
                          Editable — adjust anything before copying or saving to drafts. The portal link becomes a styled button in the saved draft.
                        </div>
                      )}
                      {eDraft && (
                        <div className="e-btns">
                          <button className="sm-btn" onClick={doCopy}>{copied ? "✓ Copied!" : "Copy"}</button>
                          <button className="sm-btn" onClick={doMailto}>Open in Email</button>
                          {[["microsoft", "Outlook"], ["google", "Gmail"]].map(([prov, label]) => {
                            if (!emailConn?.[prov]?.connected) return null;
                            const st = draftSaveState?.provider === prov ? draftSaveState.status : null;
                            return (
                              <button key={prov} className="sm-btn"
                                onClick={() => saveDraftToProvider(prov)}
                                disabled={st === "saving"}
                                style={{ display: "inline-flex", alignItems: "center", gap: 6, ...(st === "done" ? { color: "var(--success)", borderColor: "#1d5c40" } : st === "error" ? { color: "var(--danger)", borderColor: "#5c1d1d" } : {}) }}>
                                {st === "saving" && <span className="spin" style={{ width: 10, height: 10 }} />}
                                {st === "saving" ? `Saving to ${label}…` : st === "done" ? `✓ In ${label} Drafts` : st === "error" ? "Failed — retry" : `Save to ${label} Drafts`}
                              </button>
                            );
                          })}
                        </div>
                      )}
                      {/* EM-1: connect strip — shown until at least one provider is connected */}
                      {emailConn !== null && !emailConn?.microsoft?.connected && !emailConn?.google?.connected && (
                        <div style={{ padding: "10px 16px 13px", borderTop: `1px solid ${UI.rowHov}`, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                          <span style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: UI.textFnt }}>
                            Connect your mailbox to save drafts directly — no copy/paste:
                          </span>
                          <button className="sm-btn" disabled={!!emailConnecting} onClick={() => connectEmailProvider("microsoft")} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                            {emailConnecting === "microsoft" && <span className="spin" style={{ width: 10, height: 10 }} />}
                            {emailConnecting === "microsoft" ? "Opening Microsoft sign-in…" : "Connect Outlook"}
                          </button>
                          <button className="sm-btn" disabled={!!emailConnecting} onClick={() => connectEmailProvider("google")}>Connect Gmail</button>
                        </div>
                      )}
                      {(emailConn?.microsoft?.connected || emailConn?.google?.connected) && (
                        <div style={{ padding: "8px 16px 12px", borderTop: `1px solid ${UI.rowHov}`, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                          {[["microsoft", "Outlook"], ["google", "Gmail"]].map(([prov, label]) => emailConn?.[prov]?.connected ? (
                            <span key={prov} style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: UI.textFnt, display: "inline-flex", alignItems: "center", gap: 5 }}>
                              <span style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--success)", display: "inline-block" }} />
                              {label}: {emailConn[prov].email || "connected"}
                              <button onClick={() => disconnectEmailProvider(prov)} title={`Disconnect ${label}`}
                                style={{ background: "none", border: "none", color: UI.textFnt, cursor: "pointer", padding: 0, lineHeight: 1, display: "inline-flex", alignItems: "center" }}><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></button>
                            </span>
                          ) : (
                            <button key={prov} className="sm-btn" disabled={!!emailConnecting} style={{ padding: "3px 8px", fontSize: 11, display: "inline-flex", alignItems: "center", gap: 5 }} onClick={() => connectEmailProvider(prov)}>
                              {emailConnecting === prov && <span className="spin" style={{ width: 9, height: 9 }} />}
                              {emailConnecting === prov ? "Opening sign-in…" : `Connect ${label}`}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  )} {/* end email tab */}

                  {/* ── SETTLEMENT TAB ── */}
                  {detailTab === "settlement" && (
                  <div className="adv-wrap">
                      {/* Bottom line — the key figures up top */}
                      {(() => {
                        const g = Number((show.settlement?.guarantee || "").replace(/[^0-9.]/g, "")) || 0;
                        const dep = Number((show.settlement?.deposit || "").replace(/[^0-9.]/g, "")) || 0;
                        if (!g && !dep) return null;
                        const FFs = "'Helvetica Neue', Helvetica, Arial, sans-serif";
                        const depPaid = !!show.settlement?.depositReceived;
                        const settled = !!show.settlement?.settled;
                        const overdue = show.settlement?.depositDue && !depPaid && new Date(show.settlement.depositDue + "T00:00:00") < new Date();
                        const bal = g - dep;
                        const money = n => `$${Math.round(n).toLocaleString()}`;
                        const cell = (label, value, big, color, sub, subColor) => (
                          <div style={{ flex: 1, minWidth: 90, padding: "11px 16px" }}>
                            <div style={{ fontFamily: FFs, fontSize: 11, letterSpacing: 1, textTransform: "uppercase", color: UI.textFnt, marginBottom: 4 }}>{label}</div>
                            <div style={{ fontFamily: FFs, fontSize: big ? 22 : 15, fontWeight: 700, color: color || UI.text, lineHeight: 1.1 }}>{value}</div>
                            {sub && <div style={{ fontFamily: FFs, fontSize: 11, color: subColor || UI.textMut, marginTop: 3 }}>{sub}</div>}
                          </div>
                        );
                        return (
                          <div className="ec" style={{ marginBottom: 10 }}>
                            <div style={{ display: "flex", alignItems: "stretch", flexWrap: "wrap" }}>
                              {cell("Guarantee", g ? money(g) : "—")}
                              <div style={{ width: 1, background: UI.panelBdr, alignSelf: "stretch" }} />
                              {cell("Deposit", dep ? money(dep) : "—", false, undefined,
                                dep ? (overdue ? "Overdue" : depPaid ? "Received" : "Not received") : null,
                                dep ? (overdue ? UI.danger : depPaid ? UI.green : UI.amber) : UI.textMut)}
                              <div style={{ width: 1, background: UI.panelBdr, alignSelf: "stretch" }} />
                              {cell("Balance Due", money(bal > 0 ? bal : 0), true,
                                settled ? UI.green : (bal > 0 ? UI.amber : UI.green),
                                settled ? "Settled ✓" : null, UI.green)}
                            </div>
                          </div>
                        );
                      })()}

                      {/* Settlement */}
                      <div className="ec">
                        <div className="ec-hdr"><span className="ec-ttl">Settlement</span></div>
                        <div style={{ padding: "12px 16px 16px", display: "flex", flexDirection: "column", gap: 10 }}>
                          <div style={{ display: "flex", gap: 8 }}>
                            <div style={{ flex: 1 }}>
                              <div className="mo-lbl" style={{ marginBottom: 5 }}>Deal Type</div>
                              <select className="mo-sel" style={{ fontSize: 12 }}
                                value={show.settlement?.dealType || ""}
                                onChange={e => updShow("settlement", { ...(show.settlement||{}), dealType: e.target.value })}>
                                <option value="">—</option>
                                <option value="Flat">Flat Guarantee</option>
                                <option value="Plus">+ Deal (Guarantee + %)</option>
                                <option value="Vs">Vs. Deal</option>
                                <option value="Door">Door Deal</option>
                                <option value="Free">Free / Charity</option>
                              </select>
                            </div>
                            <div style={{ flex: 1 }}>
                              <div className="mo-lbl" style={{ marginBottom: 5 }}>Guarantee</div>
                              <input className="mo-inp" style={{ fontSize: 12 }} placeholder="$0"
                                value={show.settlement?.guarantee || ""}
                                onChange={e => updShow("settlement", { ...(show.settlement||{}), guarantee: e.target.value })} />
                            </div>
                          </div>
                          {(show.settlement?.dealType === "Vs" || show.settlement?.dealType === "Plus" || show.settlement?.dealType === "Door") && (
                            <div style={{ display: "flex", gap: 8 }}>
                              <div style={{ flex: 1 }}>
                                <div className="mo-lbl" style={{ marginBottom: 5 }}>
                                  {show.settlement?.dealType === "Plus" ? "+ % Terms" : "Vs. Amount / %"}
                                </div>
                                <input className="mo-inp" style={{ fontSize: 12 }}
                                  placeholder={show.settlement?.dealType === "Plus" ? "e.g. + 10% of net ticket sales over breakeven" : "e.g. 85% NBOR after expenses"}
                                  value={show.settlement?.vsTerms || ""}
                                  onChange={e => updShow("settlement", { ...(show.settlement||{}), vsTerms: e.target.value })} />
                              </div>
                            </div>
                          )}
                          <div style={{ display: "flex", gap: 8 }}>
                            <div style={{ flex: 1 }}>
                              <div className="mo-lbl" style={{ marginBottom: 5 }}>Deposit</div>
                              <input className="mo-inp" style={{ fontSize: 12 }} placeholder="$0"
                                value={show.settlement?.deposit || ""}
                                onChange={e => updShow("settlement", { ...(show.settlement||{}), deposit: e.target.value })} />
                            </div>
                            <div style={{ flex: 1 }}>
                              <div className="mo-lbl" style={{ marginBottom: 5 }}>Deposit Due</div>
                              <input type="date" className="mo-inp" style={{ fontSize: 12 }}
                                value={show.settlement?.depositDue || ""}
                                onChange={e => updShow("settlement", { ...(show.settlement||{}), depositDue: e.target.value })} />
                            </div>
                            <div style={{ flex: 1 }}>
                              <div className="mo-lbl" style={{ marginBottom: 5 }}>Payment Method</div>
                              <select className="mo-sel" style={{ fontSize: 12 }}
                                value={show.settlement?.paymentMethod || ""}
                                onChange={e => updShow("settlement", { ...(show.settlement||{}), paymentMethod: e.target.value })}>
                                <option value="">—</option>
                                <option value="Check">Check</option>
                                <option value="Wire">Wire Transfer</option>
                                <option value="ACH">ACH</option>
                                <option value="Cash">Cash</option>
                                <option value="Venmo">Venmo</option>
                                <option value="Zelle">Zelle</option>
                              </select>
                            </div>
                          </div>
                          {(() => { if (!show.settlement?.depositDue || show.settlement?.depositReceived) return null;
                            const overdue = new Date(show.settlement.depositDue + "T00:00:00") < new Date();
                            return (
                              <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: overdue ? "var(--danger)" : UI.amber, background: overdue ? UI.dangerBg : UI.amdBg, border: `1px solid ${overdue ? UI.dangerBdr : UI.amdBdr}`, borderRadius: 8, padding: "7px 12px", display: "flex", alignItems: "center", gap: 7 }}>
                                {overdue ? (
                                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                                ) : (
                                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><circle cx="12" cy="12" r="9"/><polyline points="12 7 12 12 15 14"/></svg>
                                )}
                                {overdue ? "Deposit overdue" : "Deposit due"} · {fmtDate(show.settlement.depositDue)}
                              </div>
                            );
                          })()}
                          <div style={{ display: "flex", gap: 20 }}>
                            <label style={{ display: "flex", alignItems: "center", gap: 7, cursor: "pointer", fontSize: 12, color: "var(--text-mut)", fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif" }}>
                              <input type="checkbox" style={{ accentColor: "var(--accent)", width: 13, height: 13, cursor: "pointer" }}
                                checked={!!show.settlement?.depositReceived}
                                onChange={e => updShow("settlement", { ...(show.settlement||{}), depositReceived: e.target.checked })} />
                              Deposit received
                            </label>
                            <label style={{ display: "flex", alignItems: "center", gap: 7, cursor: "pointer", fontSize: 12, color: show.settlement?.settled ? "var(--success)" : UI.textMut, fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif" }}>
                              <input type="checkbox" style={{ accentColor: "var(--success)", width: 13, height: 13, cursor: "pointer" }}
                                checked={!!show.settlement?.settled}
                                onChange={e => {
                                  updShow("settlement", { ...(show.settlement||{}), settled: e.target.checked });
                                  if (e.target.checked) { setSettledToast(true); setTimeout(() => setSettledToast(false), 2500); }
                                }} />
                              {settledToast && show.settlement?.settled ? "Settlement complete!" : "Settled"}
                            </label>
                          </div>
                          <div>
                            <label style={{ display: "flex", alignItems: "center", gap: 7, cursor: "pointer", fontSize: 12, color: show.settlement?.radiusClause ? "var(--accent)" : UI.textMut, fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", marginBottom: show.settlement?.radiusClause ? 8 : 0 }}>
                              <input type="checkbox" style={{ accentColor: "var(--accent)", width: 13, height: 13, cursor: "pointer" }}
                                checked={!!show.settlement?.radiusClause}
                                onChange={e => updShow("settlement", { ...(show.settlement||{}), radiusClause: e.target.checked })} />
                              Radius clause
                            </label>
                            {show.settlement?.radiusClause && (
                              <div style={{ display: "flex", gap: 8 }}>
                                <div style={{ flex: 1 }}>
                                  <div className="mo-lbl" style={{ marginBottom: 5 }}>Distance</div>
                                  <input className="mo-inp" style={{ fontSize: 12 }} placeholder="e.g. 90 miles"
                                    value={show.settlement?.radiusDistance || ""}
                                    onChange={e => updShow("settlement", { ...(show.settlement||{}), radiusDistance: e.target.value })} />
                                </div>
                                <div style={{ flex: 1 }}>
                                  <div className="mo-lbl" style={{ marginBottom: 5 }}>Duration</div>
                                  <input className="mo-inp" style={{ fontSize: 12 }} placeholder="e.g. 90 days before & after"
                                    value={show.settlement?.radiusDuration || ""}
                                    onChange={e => updShow("settlement", { ...(show.settlement||{}), radiusDuration: e.target.value })} />
                                </div>
                              </div>
                            )}
                          </div>
                          <div>
                            <div className="mo-lbl" style={{ marginBottom: 5 }}>Notes</div>
                            <textarea className="mo-inp" rows={2} style={{ resize: "vertical", fontSize: 12 }} placeholder="Deal notes, conditions..."
                              value={show.settlement?.notes || ""}
                              onChange={e => updShow("settlement", { ...(show.settlement||{}), notes: e.target.value })} />
                          </div>
                        </div>
                      </div>

                      {/* Settlement Calculator */}
                      {show.settlement?.dealType && show.settlement.dealType !== "Free" && (
                        <div className="ec" style={{ marginTop: 10 }}>
                          <div className="ec-hdr">
                            <span className="ec-ttl">Settlement Calculator</span>
                            <button onClick={() => {
                              const tiers = show.settlement?.ticketTiers || [];
                              const newTier = { id: Date.now().toString(36) + Math.random().toString(36).slice(2,5), name: "", count: "", price: "", serviceFee: "" };
                              updShow("settlement", { ...(show.settlement||{}), ticketTiers: [...tiers, newTier] });
                            }} style={{ fontFamily: "'Helvetica Neue',Helvetica,Arial,sans-serif", fontSize: 11, letterSpacing: 0.5, color: UI.textMut, background: "none", border: `1px solid ${UI.panelBdr}`, borderRadius: 4, padding: "3px 9px", cursor: "pointer" }}>
                              + Add Tier
                            </button>
                          </div>
                          <div style={{ padding: "12px 16px 16px", display: "flex", flexDirection: "column", gap: 10 }}>

                            {/* Tier table */}
                            {(show.settlement?.ticketTiers?.length > 0) && (
                              <>
                                <div style={{ display: "grid", gridTemplateColumns: "1fr 72px 80px 90px 72px 20px", gap: 4 }}>
                                  {["Tier", "Count", "Face $", "Svc fee/ea", "Gross", ""].map((h, i) => (
                                    <div key={i} className="mo-lbl" style={{ marginBottom: 0 }}>{h}</div>
                                  ))}
                                </div>
                                {show.settlement.ticketTiers.map((tier, idx) => {
                                  const rowGross = (Number(tier.count)||0) * (Number(tier.price)||0);
                                  return (
                                    <div key={tier.id} style={{ display: "grid", gridTemplateColumns: "1fr 72px 80px 90px 72px 20px", gap: 4, alignItems: "center" }}>
                                      <input className="mo-inp" style={{ fontSize: 12 }} placeholder="GA, VIP…" value={tier.name}
                                        onChange={e => { const t=[...(show.settlement.ticketTiers)]; t[idx]={...t[idx],name:e.target.value}; updShow("settlement",{...(show.settlement),ticketTiers:t}); }} />
                                      <input className="mo-inp" style={{ fontSize: 12 }} type="number" placeholder="0" value={tier.count}
                                        onChange={e => { const t=[...(show.settlement.ticketTiers)]; t[idx]={...t[idx],count:e.target.value}; updShow("settlement",{...(show.settlement),ticketTiers:t}); }} />
                                      <input className="mo-inp" style={{ fontSize: 12 }} type="number" placeholder="0.00" value={tier.price}
                                        onChange={e => { const t=[...(show.settlement.ticketTiers)]; t[idx]={...t[idx],price:e.target.value}; updShow("settlement",{...(show.settlement),ticketTiers:t}); }} />
                                      <input className="mo-inp" style={{ fontSize: 12 }} type="number" placeholder="0.00" value={tier.serviceFee}
                                        onChange={e => { const t=[...(show.settlement.ticketTiers)]; t[idx]={...t[idx],serviceFee:e.target.value}; updShow("settlement",{...(show.settlement),ticketTiers:t}); }} />
                                      <div style={{ fontFamily: "'Helvetica Neue',Helvetica,Arial,sans-serif", fontSize: 12, color: UI.textFnt, textAlign: "right" }}>
                                        {rowGross > 0 ? `$${Math.round(rowGross).toLocaleString()}` : "—"}
                                      </div>
                                      <button onClick={() => { const t=(show.settlement.ticketTiers).filter((_,i)=>i!==idx); updShow("settlement",{...(show.settlement),ticketTiers:t}); }}
                                        style={{ background:"none", border:"none", color: UI.textVFnt, cursor:"pointer", fontSize:16, padding:0, lineHeight:1, display:"flex", alignItems:"center", justifyContent:"center" }}>×</button>
                                    </div>
                                  );
                                })}
                              </>
                            )}

                            {/* Empty state with optional migration from old single-price fields */}
                            {(!show.settlement?.ticketTiers || show.settlement.ticketTiers.length === 0) && (
                              <div style={{ textAlign: "center", padding: "10px 0 4px", fontFamily: "'Helvetica Neue',Helvetica,Arial,sans-serif", fontSize: 11, color: UI.textVFnt }}>
                                Use "+ Add Tier" to enter ticket tiers — GA, VIP, Student, etc.
                                {show.settlement?.calcTickets && show.settlement?.calcTicketPrice && (
                                  <div style={{ marginTop: 6 }}>
                                    <button onClick={() => updShow("settlement", { ...(show.settlement||{}), ticketTiers: [
                                      { id: Date.now().toString(36), name: "GA", count: show.settlement.calcTickets, price: show.settlement.calcTicketPrice, serviceFee: "" }
                                    ]})} style={{ fontSize: 11, color: UI.amber, background: "none", border: "none", cursor: "pointer", textDecoration: "underline", fontFamily: "'Helvetica Neue',Helvetica,Arial,sans-serif" }}>
                                      Import existing data as GA tier
                                    </button>
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Modifier fields: expenses, facility fee, deal % */}
                            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                              <div style={{ flex: 1, minWidth: 100 }}>
                                <div className="mo-lbl" style={{ marginBottom: 5 }}>Venue Expenses</div>
                                <input className="mo-inp" style={{ fontSize: 12 }} type="number" placeholder="$0"
                                  value={show.settlement?.calcExpenses || ""}
                                  onChange={e => updShow("settlement", { ...(show.settlement||{}), calcExpenses: e.target.value })} />
                              </div>
                              <div style={{ flex: 1, minWidth: 80 }}>
                                <div className="mo-lbl" style={{ marginBottom: 5 }}>Facility / Tax %</div>
                                <input className="mo-inp" style={{ fontSize: 12 }} type="number" placeholder="0"
                                  value={show.settlement?.calcFacilityPct || ""}
                                  onChange={e => updShow("settlement", { ...(show.settlement||{}), calcFacilityPct: e.target.value })} />
                              </div>
                              {(show.settlement?.dealType === "Vs" || show.settlement?.dealType === "Plus" || show.settlement?.dealType === "Door") && (
                                <div style={{ flex: 1, minWidth: 80 }}>
                                  <div className="mo-lbl" style={{ marginBottom: 5 }}>Deal %</div>
                                  <input className="mo-inp" style={{ fontSize: 12 }} type="number" placeholder="85"
                                    value={show.settlement?.vsPercent || ""}
                                    onChange={e => updShow("settlement", { ...(show.settlement||{}), vsPercent: e.target.value })} />
                                </div>
                              )}
                              {show.settlement?.dealType === "Plus" && (
                                <div style={{ flex: 1, minWidth: 100 }}>
                                  <div className="mo-lbl" style={{ marginBottom: 5 }}>Breakeven</div>
                                  <input className="mo-inp" style={{ fontSize: 12 }} type="number" placeholder="$0"
                                    value={show.settlement?.vsBreakeven || ""}
                                    onChange={e => updShow("settlement", { ...(show.settlement||{}), vsBreakeven: e.target.value })} />
                                </div>
                              )}
                            </div>

                            {/* Results */}
                            {(() => {
                              const tiers      = show.settlement?.ticketTiers || [];
                              const expenses   = Number(show.settlement?.calcExpenses)   || 0;
                              const facPct     = Number(show.settlement?.calcFacilityPct) || 0;
                              const pct        = Number(show.settlement?.vsPercent)       || 0;
                              const brk        = Number(show.settlement?.vsBreakeven)     || 0;
                              const guar       = Number((show.settlement?.guarantee || "").replace(/[^0-9.]/g, "")) || 0;
                              const dep        = Number((show.settlement?.deposit   || "").replace(/[^0-9.]/g, "")) || 0;

                              let gross = 0, totalSvcFees = 0;
                              tiers.forEach(t => {
                                const cnt = Number(t.count) || 0, prc = Number(t.price) || 0, svc = Number(t.serviceFee) || 0;
                                gross        += cnt * prc;
                                totalSvcFees += cnt * svc;
                              });
                              // Backward compat: fall back to legacy single-tier fields if no tiers entered
                              if (tiers.length === 0) {
                                gross = (Number(show.settlement?.calcTickets)||0) * (Number(show.settlement?.calcTicketPrice)||0);
                              }
                              if (gross === 0 && expenses === 0) return null;

                              const facilityFee = gross * facPct / 100;
                              const nbor        = gross - totalSvcFees - facilityFee;
                              const net         = nbor - expenses;
                              const dtype       = show.settlement?.dealType;
                              let payout = guar, payNote = "";
                              if (dtype === "Flat")                { payout = guar; payNote = "Flat guarantee"; }
                              else if (dtype === "Vs"   && pct)   { const vp = net * pct / 100; payout = Math.max(guar, vp); payNote = payout === guar ? "Guarantee wins" : `${pct}% net wins`; }
                              else if (dtype === "Plus" && pct)   { const bonus = Math.max(0, (net - brk) * pct / 100); payout = guar + bonus; payNote = bonus > 0 ? `+$${Math.round(bonus).toLocaleString()} bonus` : "Below breakeven"; }
                              else if (dtype === "Door" && pct)   { payout = nbor * pct / 100; payNote = `${pct}% of NBOR`; }
                              const due = payout - dep;

                              const resultRows = [
                                { k: "Gross Box Office",                                    v: `$${Math.round(gross).toLocaleString()}` },
                                totalSvcFees > 0 && { k: "Service Fees",                   v: `−$${Math.round(totalSvcFees).toLocaleString()}`, dim: true },
                                facilityFee  > 0 && { k: `Facility / Tax (${facPct}%)`,    v: `−$${Math.round(facilityFee).toLocaleString()}`,  dim: true },
                                (totalSvcFees > 0 || facilityFee > 0) && { k: "NBOR",      v: `$${Math.round(nbor).toLocaleString()}` },
                                expenses > 0     && { k: "Venue Expenses",                 v: `−$${Math.round(expenses).toLocaleString()}`,     dim: true },
                                { k: "Net",                                                 v: `$${Math.round(net).toLocaleString()}` },
                                { k: `Artist Payout${payNote ? ` · ${payNote}` : ""}`,     v: `$${Math.round(payout).toLocaleString()}`,        hi: true },
                                dep > 0 && { k: "Due at Settlement",                       v: `$${Math.round(due).toLocaleString()}`,           hi: true },
                              ].filter(Boolean);

                              return (
                                <div className="calc-grid">
                                  {resultRows.map(item => (
                                    <div key={item.k} className="calc-cell">
                                      <div className="calc-k" style={{ color: item.dim ? undefined : undefined }}>{item.k}</div>
                                      <div className="calc-v" style={{ color: item.hi ? "var(--accent)" : item.dim ? UI.textFnt : undefined }}>{item.v}</div>
                                    </div>
                                  ))}
                                </div>
                              );
                            })()}
                          </div>
                        </div>
                      )}
                  </div>
                  )} {/* end settlement tab */}

                  {/* ── ACTIVITY TAB ── */}
                  {detailTab === "activity" && (() => {
                    const tmLog  = (show.activityLog || []);
                    const proLog = (show.advanceData?._activity || []);
                    const all = [...tmLog, ...proLog].sort((a, b) => b.ts.localeCompare(a.ts));
                    const FF = "'Helvetica Neue', Helvetica, Arial, sans-serif";
                    const fmtTs = (ts) => {
                      const d = new Date(ts);
                      const now = new Date();
                      const diffMs = now - d;
                      const diffMins = Math.floor(diffMs / 60000);
                      const diffHrs  = Math.floor(diffMs / 3600000);
                      const diffDays = Math.floor(diffMs / 86400000);
                      if (diffMins < 1)   return "just now";
                      if (diffMins < 60)  return `${diffMins}m ago`;
                      if (diffHrs  < 24)  return `${diffHrs}h ago`;
                      if (diffDays < 7)   return `${diffDays}d ago`;
                      return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
                    };
                    const actorStyle = {
                      tm:       { color: UI.green,  label: "TM"       },
                      promoter: { color: UI.amber,  label: "PROMOTER" },
                      system:   { color: UI.textMut, label: "SYSTEM"  },
                    };
                    return (
                      <div className="dp-body">
                        {all.length === 0 ? (
                          <div style={{ fontFamily: FF, fontSize: 11, color: UI.textFnt, padding: "20px 0", textAlign: "center" }}>
                            No activity yet — status changes, notes, and advance updates will appear here.
                          </div>
                        ) : (
                          <div style={{ display: "flex", flexDirection: "column" }}>
                            {all.map((entry, i) => {
                              const style = actorStyle[entry.actor] || actorStyle.system;
                              return (
                                <div key={entry.id || i} style={{ display: "flex", gap: 10, padding: "9px 0", borderBottom: `1px solid ${UI.subBdr}` }}>
                                  <div style={{ width: 6, height: 6, borderRadius: "50%", background: style.color, flexShrink: 0, marginTop: 5 }} />
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontFamily: FF, fontSize: 12, color: UI.textMid, lineHeight: 1.4 }}>{entry.label}</div>
                                    <div style={{ display: "flex", gap: 8, marginTop: 3, alignItems: "center" }}>
                                      <span style={{ fontFamily: FF, fontSize: 11, fontWeight: 700, letterSpacing: 0.8, textTransform: "uppercase", color: style.color }}>{style.label}</span>
                                      <span style={{ fontFamily: FF, fontSize: 11, color: UI.textFnt }}>{fmtTs(entry.ts)}</span>
                                      <span style={{ fontFamily: FF, fontSize: 11, color: UI.textVFnt }}>{new Date(entry.ts).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}</span>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* ── REPORT TAB ── */}
                  {detailTab === "report" && (
                    <ShowReportTMForm
                      key={selS}
                      showId={selS}
                      tourId={selT}
                      showReports={showReports}
                      profiles={profiles}
                      saveShowReport={saveShowReport}
                      show={show}
                      venues={venues}
                      fmtDate={fmtDate}
                      UI={UI}
                      lightMode={lightMode}
                    />
                  )}

                </div>
              )}
              </>} {/* end !isTourMode */}
            </>
          )}
        </div>

        {/* MODALS */}

        {/* ── Place Info Modal ── */}
        {placeInfoModal && (() => {
          const { rec } = placeInfoModal;
          const det     = placeInfoDetails[rec.id];
          const d       = det?.data;
          const recCat  = rec.category || rec.cat || "other";
          const color   = REC_CAT_COLORS[recCat] || "#f0a500";
          const FF      = "'Helvetica Neue', Helvetica, Arial, sans-serif";

          // Today's hours: weekday_text is Mon-Sun (index 0-6), JS getDay() is 0=Sun
          const todayIdx   = ((new Date().getDay() + 6) % 7); // convert to Mon=0
          const todayHours = d?.opening_hours?.weekday_text?.[todayIdx] || null;
          const isOpenNow  = d?.opening_hours?.isOpen?.() ?? null;

          const priceLabel = d?.price_level ? "$".repeat(d.price_level) : null;

          return (
            <div onClick={() => setPlaceInfoModal(null)}
              style={{ position: "fixed", inset: 0, zIndex: 2000,
                background: "rgba(0,0,0,0.55)", backdropFilter: "blur(6px)",
                display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
              <div onClick={e => e.stopPropagation()}
                style={{ background: UI.bg, borderRadius: 12, width: "100%", maxWidth: 420,
                  border: `1px solid ${UI.panelBdr}`, borderLeft: `4px solid ${color}`,
                  boxShadow: "0 20px 60px rgba(0,0,0,0.5)", overflow: "hidden" }}>

                {/* Header */}
                <div style={{ padding: "16px 18px 12px", borderBottom: `1px solid ${UI.panelBdr}`, display: "flex", flexDirection: "column", gap: 6 }}>
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontFamily: FF, fontSize: 16, fontWeight: 700, color: UI.text, lineHeight: 1.2 }}>{d?.name || rec.name}</div>
                    </div>
                    <button onClick={() => setPlaceInfoModal(null)}
                      style={{ background: "none", border: "none", color: UI.textMut, cursor: "pointer", fontSize: 20, lineHeight: 1, padding: "0 2px", flexShrink: 0, marginTop: -2 }}>×</button>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <span style={{ fontFamily: FF, fontSize: 11, letterSpacing: 0.8, color, textTransform: "uppercase",
                      padding: "2px 8px", border: `1px solid ${color}40`, borderRadius: 4, background: `${color}12` }}>{recCat}</span>
                    {priceLabel && <span style={{ fontFamily: FF, fontSize: 11, color: UI.green }}>{priceLabel}</span>}
                    {d?.rating && <span style={{ fontFamily: FF, fontSize: 11, color: "var(--accent)" }}>★ {d.rating} <span style={{ color: UI.textVFnt, fontSize: 11 }}>({(d.user_ratings_total || 0).toLocaleString()})</span></span>}
                    {isOpenNow !== null && <span style={{ fontFamily: FF, fontSize: 11, letterSpacing: 0.5, color: isOpenNow ? "var(--success)" : "var(--danger)", fontWeight: 600 }}>{isOpenNow ? "● OPEN" : "● CLOSED"}</span>}
                  </div>
                  {rec.note && <div style={{ fontFamily: FF, fontSize: 12, color: UI.textFnt, fontStyle: "italic", lineHeight: 1.5 }}>"{rec.note}"</div>}
                </div>

                {/* Details body */}
                <div style={{ padding: "13px 18px", display: "flex", flexDirection: "column", gap: 9 }}>
                  {det?.status === "loading" && (
                    <div style={{ fontFamily: FF, fontSize: 12, color: UI.textFnt }}>Fetching details…</div>
                  )}
                  {(det?.status === "error" || det?.status === "not_found") && (
                    <div style={{ fontFamily: FF, fontSize: 12, color: UI.textMut }}>
                      {det.status === "not_found" ? "Place details not found on Google Maps." : "Couldn't load details — check your connection."}
                    </div>
                  )}

                  {d && (<>
                    {d.formatted_address && (
                      <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                        <svg width="13" height="13" viewBox="0 0 12 12" fill="none" stroke={UI.textVFnt} strokeWidth="1.4" strokeLinecap="round" style={{ flexShrink: 0, marginTop: 2 }}><path d="M6 1a3.5 3.5 0 0 1 3.5 3.5C9.5 7.5 6 11 6 11S2.5 7.5 2.5 4.5A3.5 3.5 0 0 1 6 1z"/><circle cx="6" cy="4.5" r="1.2"/></svg>
                        <span style={{ fontFamily: FF, fontSize: 12, color: UI.textFnt, lineHeight: 1.45 }}>{d.formatted_address}</span>
                      </div>
                    )}
                    {d.formatted_phone_number && (
                      <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                        <svg width="13" height="13" viewBox="0 0 12 12" fill="none" stroke={UI.textVFnt} strokeWidth="1.4" strokeLinecap="round" style={{ flexShrink: 0 }}><path d="M2 2.5c0 4.5 3.5 8 8 8 0-.5-.5-2-1-2.5l-1.5.5C6.5 8 4 5.5 3.5 4.5L4 3c-.5-.5-1.5-1-2-1z"/></svg>
                        <a href={`tel:${d.formatted_phone_number}`}
                          style={{ fontFamily: FF, fontSize: 12, color, textDecoration: "none", fontWeight: 500 }}>{d.formatted_phone_number}</a>
                      </div>
                    )}
                    {todayHours && (
                      <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                        <svg width="13" height="13" viewBox="0 0 12 12" fill="none" stroke={UI.textVFnt} strokeWidth="1.4" strokeLinecap="round" style={{ flexShrink: 0, marginTop: 1 }}><circle cx="6" cy="6" r="4.5"/><path d="M6 3.5v2.7l1.5 1.5"/></svg>
                        <span style={{ fontFamily: FF, fontSize: 12, color: UI.textFnt, lineHeight: 1.4 }}>{todayHours}</span>
                      </div>
                    )}
                    {d.website && (
                      <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                        <svg width="13" height="13" viewBox="0 0 12 12" fill="none" stroke={UI.textVFnt} strokeWidth="1.4" strokeLinecap="round" style={{ flexShrink: 0 }}><circle cx="6" cy="6" r="4.5"/><path d="M6 1.5c-1.5 1.5-1.5 7.5 0 9M6 1.5c1.5 1.5 1.5 7.5 0 9M1.5 6h9"/></svg>
                        <a href={d.website} target="_blank" rel="noreferrer"
                          style={{ fontFamily: FF, fontSize: 12, color, textDecoration: "none", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 340 }}>
                          {d.website.replace(/^https?:\/\//, "").replace(/\/$/, "")}
                        </a>
                      </div>
                    )}
                  </>)}
                </div>

                {/* Reservation CTA */}
                <div style={{ padding: "10px 18px 16px", borderTop: `1px solid ${UI.panelBdr}`, display: "flex", flexDirection: "column", gap: 7 }}>
                  <div style={{ fontFamily: FF, fontSize: 11, letterSpacing: 0.9, color: UI.textVFnt, textTransform: "uppercase", marginBottom: 1 }}>Make a Reservation</div>
                  <div style={{ display: "flex", gap: 7 }}>
                    {/* OpenTable */}
                    <a href={`https://www.opentable.com/s/?term=${encodeURIComponent(d?.name || rec.name)}&covers=2&dateTime=${new Date().toISOString().slice(0,10)}T19%3A00`}
                      target="_blank" rel="noreferrer"
                      style={{ flex: 1, fontFamily: FF, fontSize: 11, fontWeight: 600, letterSpacing: 0.3,
                        padding: "9px 0", borderRadius: 8, cursor: "pointer", textDecoration: "none",
                        background: "#DA3743", border: "1px solid #b02a35", color: "#fff",
                        display: "flex", alignItems: "center", justifyContent: "center", gap: 5 }}>
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><rect x="1.5" y="3" width="9" height="7.5" rx="1"/><path d="M1.5 5.5h9M4 1.5v2M8 1.5v2"/></svg>
                      OpenTable
                    </a>
                    {/* Resy */}
                    <a href={`https://resy.com/cities/search?query=${encodeURIComponent(d?.name || rec.name)}`}
                      target="_blank" rel="noreferrer"
                      style={{ flex: 1, fontFamily: FF, fontSize: 11, fontWeight: 600, letterSpacing: 0.3,
                        padding: "9px 0", borderRadius: 8, cursor: "pointer", textDecoration: "none",
                        background: "#1F5BFF", border: "1px solid #1040cc", color: "#fff",
                        display: "flex", alignItems: "center", justifyContent: "center", gap: 5 }}>
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><rect x="1.5" y="3" width="9" height="7.5" rx="1"/><path d="M1.5 5.5h9M4 1.5v2M8 1.5v2"/></svg>
                      Resy
                    </a>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

        {mExpense && tour && (
          <ExpenseModal
            existing={mExpense.mode === "edit" ? mExpense.expense : null}
            tourId={selT} teamId={teamId}
            onSave={saveTourExpense}
            onClose={() => setMExpense(null)}
            lightMode={lightMode}
          />
        )}
        {mMerch && tour && (
          <MerchModal
            existing={mMerch.mode === "edit" ? mMerch.entry : null}
            showId={mMerch.showId || null}
            shows={tour.shows}
            venues={venues}
            tourId={selT}
            teamId={teamId}
            onSave={saveShowMerch}
            onClose={() => setMMerch(null)}
            lightMode={lightMode}
          />
        )}
        {previewMember && <MemberPreviewModal profile={previewMember} data={data} venues={venues} profiles={profiles} dayLodging={dayLodging} dayFlights={dayFlights} dayTransport={dayTransport} loadDayLodging={loadDayLodging} loadDayFlights={loadDayFlights} loadDayTransport={loadDayTransport} lightMode={lightMode} onClose={() => setPreviewMember(null)} broadcasts={broadcasts} />}
        {mArtist     && <ArtistModal onSave={saveArtist} onClose={() => setMArtist(false)} />}
        {mEditArtist && <EditArtistModal artist={data.artists.find(a => a.id === mEditArtist)} onSave={saveEditArtist} onClose={() => setMEditArtist(null)} />}
        {mTour       && <TourModal artistName={artist?.name} existing={mTour.mode === "edit" ? tour : null} onSave={saveTour} onClose={() => setMTour(null)} templates={tourTemplates} />}
        {mAddShows   && tour && <AddShowsModal tourName={tour.name} venues={venues} defaultDate={mAddShows.defaultDate} onSave={(payload) => { saveShow(payload); setMAddShows(null); }} onImport={(rows) => { handleBulkImport(rows); setMAddShows(null); }} onClose={() => setMAddShows(null)} onAddVenue={addVenueQuick} onAddVenueFromDir={addVenueFromDirForShow} />}
        {mShow       && tour && <ShowModal tourName={tour.name} existing={mShow.mode === "edit" ? tour.shows.find(s => s.id === mShow.showId) : null} defaultDate={mShow.defaultDate} venues={venues} onSave={saveShow} onClose={() => setMShow(null)} onAddVenue={addVenueQuick} onAddVenueFromDir={addVenueFromDirForShow} />}
        {mAddVenue   && <AddVenueModal kind={mAddVenue.kind || "venue"} onSave={saveVenue} onClose={() => setMAddVenue(null)} />}
        {mVenue      && <VenueModal existing={mVenue.mode === "edit" ? venues[mVenue.venueId] : null} kind={mVenue.kind || (mVenue.mode === "edit" && venues[mVenue.venueId]?.kind) || "venue"} contacts={data.contacts} onSave={saveVenue} onClose={() => setMVenue(null)} />}
        {mAddContact && <AddContactsModal onSave={(payload) => saveContact(payload)} onClose={() => setMAddContact(false)} />}
        {mContact    && <ContactModal existing={mContact.mode === "edit" ? data.contacts.find(c => c.id === mContact.contactId) : null} onSave={saveContact} onClose={() => setMContact(null)} />}
        {mDataImport    && <DataImportModal onClose={() => setMDataImport(false)} onImportVenues={handleImportVenues} onImportContacts={handleImportContacts} />}
        {mArtistSection && <EditArtistSectionModal artist={data.artists.find(a => a.id === mArtistSection.artistId)} section={mArtistSection.section} onSave={saveArtistSection} onClose={() => setMArtistSection(null)} />}
        {mImport     && tour && <BulkImportModal tourName={tour.name} venues={venues} onImport={handleBulkImport} onClose={() => setMImport(false)} />}
        {mTeam && <TeamModal user={user} teamId={teamId} teamName={teamName} teamMembers={teamMembers} onMembersChange={setTeamMembers} onTeamNameChange={setTeamName} onClose={() => setMTeam(false)} lightMode={lightMode} />}
        {mBatchAdv && <BatchAdvanceModal
          state={mBatchAdv}
          onEditDraft={(idx, val) => setMBatchAdv(prev => {
            if (!prev) return prev;
            const newItems = [...prev.items];
            newItems[idx] = { ...newItems[idx], draft: val };
            return { ...prev, items: newItems };
          })}
          onToggleSkip={(idx) => setMBatchAdv(prev => {
            if (!prev) return prev;
            const newItems = [...prev.items];
            newItems[idx] = { ...newItems[idx], skipped: !newItems[idx].skipped };
            return { ...prev, items: newItems };
          })}
          onClose={() => { setMBatchAdv(null); setBatchDraftState(null); }}
          lightMode={lightMode}
          emailConn={emailConn}
          batchDraftState={batchDraftState}
          onSaveDrafts={batchSaveDrafts}
          artistName={artist?.name || ""}
        />}
        {/* EM-1: Gmail coming-soon modal */}
        {gmailComingSoon && (
          <div className="ov" onClick={() => setGmailComingSoon(false)}>
            <div className="mo" style={{ width: 420, maxWidth: "92vw" }} onClick={e => e.stopPropagation()}>
              <div className="mo-title" style={{ display: "flex", alignItems: "center", gap: 9 }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
                </svg>
                Gmail Connection Coming Soon
              </div>
              <div className="mo-sub">OUTLOOK IS AVAILABLE NOW</div>
              <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 12, color: UI.textMid, lineHeight: 1.6, marginBottom: 18 }}>
                Saving drafts to Gmail is built and waiting on Google's app-approval process.
                In the meantime, you can connect an Outlook mailbox for one-click drafts, or
                use Copy / Open in Email with any client.
              </div>
              <div className="mo-acts">
                <button className="btn-g" onClick={() => setGmailComingSoon(false)}>Close</button>
              </div>
            </div>
          </div>
        )}
        {mAdvLinks && tour && <AdvanceLinksModal
          tour={tour}
          artist={artist}
          venues={venues}
          onGenerateLink={generateAdvanceLink}
          onClose={() => setMAdvLinks(false)}
          lightMode={lightMode}
        />}
        {mTourHealth && tour && <TourHealthModal
          tour={tour}
          artist={artist}
          venues={venues}
          onClose={() => setMTourHealth(false)}
          onGoToShow={(showId) => {
            // Ensure we're in advance mode so the show list is visible
            if (tour.advanceConfig?.mode !== undefined && tour.advanceConfig.mode !== 'advance') {
              const next = { ...(tour.advanceConfig || {}), mode: 'advance' };
              setData(p => ({ ...p, artists: p.artists.map(a => a.id !== selA ? a : { ...a, tours: a.tours.map(t => t.id !== selT ? t : { ...t, advanceConfig: next }) }) }));
              supabase.from("tours").update({ advance_config: next }).eq("id", selT);
            }
            setSelS(showId);
            setDetailTab("advance");
            setMTourHealth(false);
          }}
          lightMode={lightMode}
        />}
        {mProfile && <ProfileModal
          existing={mProfile.profile || null}
          existingPrivate={mProfile.privateData || null}
          allTours={data.artists.flatMap(a => a.tours.map(t => ({
            ...t,
            shows: t.shows.map(s => ({ ...s, _city: venues[s.venueId]?.city || "" }))
          })))}
          artistId={mProfile.context?.artistId || mProfile.profile?.artist_id || null}
          onSave={saveProfile}
          onClose={() => setMProfile(null)}
          lightMode={lightMode}
        />}
        {/* CrewMemberModal retired — PP-1 Phase 2 */}
        {mTourInvite && tour && <DesktopInviteModal
          tourId={tour.id} artistId={artist?.id} teamId={teamId}
          tourName={tour.name} artistName={artist?.name} inviterEmail={user?.email}
          onClose={() => setMTourInvite(false)} UI={UI} lightMode={lightMode}
        />}
        {mModuleConfig && <DesktopModuleConfigModal
          member={mModuleConfig.member}
          onClose={() => setMModuleConfig(null)}
          onSaved={(memberId, modules) => {
            setTourInvites(prev => prev.map(m => m.id === memberId ? { ...m, modules } : m));
            setMModuleConfig(null);
          }}
          UI={UI} lightMode={lightMode}
        />}
        {mVehicle && <VehicleModal existing={mVehicle.vehicle || null} onSave={saveVehicle} onClose={() => setMVehicle(null)} />}
        {mItinerary && <ItineraryImportModal tourParty={profiles.filter(p => p.current_tour_id === selT)} tourDays={tourDays} onConfirm={handleItineraryConfirm} onClose={() => setMItinerary(false)} />}

        {mItineraryExport && <ItineraryExportModal UI={UI} onClose={() => setMItineraryExport(false)} onExport={(sections) => { exportTourItinerary(sections); }} onShare={(sections) => shareItinerary(sections)} />}
        {mFlight && <FlightModal existing={mFlight.flight || null} dayId={mFlight.dayId} tourParty={profiles.filter(p => p.current_tour_id === selT)} onSave={saveFlight} onClose={() => setMFlight(null)} />}
        {mTransport && <GroundTransportModal existing={mTransport.transport || null} onSave={saveTransport} onClose={() => setMTransport(null)} />}
        {mSchedItem && <ScheduleItemModal existing={mSchedItem.item || null} dayId={mSchedItem.dayId} tourParty={profiles.filter(p => p.current_tour_id === selT)} onSave={saveDayScheduleItem} onClose={() => setMSchedItem(null)} lightMode={lightMode} />}
        {mLodging   && <LodgingModal existing={mLodging.lodging || null} tourParty={profiles.filter(p => p.current_tour_id === selT)} onSave={saveLodging} onClose={() => setMLodging(null)} artistHomeCity={data.artists.find(a => a.id === selA)?.homeCity || ""} artistHomeState={data.artists.find(a => a.id === selA)?.homeState || ""} />}
        {mBatchDeposit && tour && <BatchDepositModal
          shows={tour.shows}
          venues={venues}
          onSave={saveBatchDeposit}
          onClose={() => setMBatchDeposit(false)}
          lightMode={lightMode}
        />}

        {/* VENUE DIRECTORY MODAL */}
      {(() => { if (!dirModal) return null;
        const kind = dirModal;
        const label = kind === "festival" ? "Festival" : "Venue";
        const alreadyAdded = (pv) => Object.values(venues).some(v =>
          (v.name || "").toLowerCase() === (pv.name || "").toLowerCase() &&
          (v.city || "").toLowerCase() === (pv.city || "").toLowerCase()
        );
        return (
          <div className="ov" onClick={() => { setDirModal(null); setDirQuery(""); setDirResults([]); }}>
            <div className="mo" style={{ width: 680, padding: 0, overflow: "hidden" }} onClick={e => e.stopPropagation()}>
              {/* Header */}
              <div style={{ padding: "22px 28px 16px", borderBottom: `1px solid ${UI.panelBdr}` }}>
                <div className="mo-title">{label} Database</div>
                <div className="mo-sub">Browse or search our built-in {label.toLowerCase()} database and add to your list — you can fill in contact details after adding.</div>
                <input
                  className="crm-inp"
                  style={{ marginTop: 12, width: "100%", boxSizing: "border-box" }}
                  placeholder={`Search ${label.toLowerCase()}s by name…`}
                  value={dirQuery}
                  autoFocus
                  onChange={e => {
                    setDirQuery(e.target.value);
                    searchDirectory(e.target.value, kind);
                  }}
                  onFocus={() => { if (!dirResults.length) searchDirectory(dirQuery, kind); }}
                />
              </div>

              {/* Results */}
              <div style={{ maxHeight: 420, overflowY: "auto", padding: "8px 0" }}>
                {dirLoading && (
                  <div style={{ padding: "20px 28px", color: UI.textMut, fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11 }}>Searching…</div>
                )}
                {!dirLoading && dirResults.length === 0 && dirQuery.trim() && (
                  <div style={{ padding: "20px 28px", color: UI.textMut, fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11 }}>No results for "{dirQuery}"</div>
                )}
                {!dirLoading && dirResults.length === 0 && !dirQuery.trim() && (
                  <div style={{ padding: "20px 28px", color: UI.textMut, fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11 }}>Loading…</div>
                )}
                {!dirLoading && dirResults.map(pv => {
                  const added = alreadyAdded(pv);
                  return (
                    <div key={pv.id} style={{ display: "grid", gridTemplateColumns: "2fr 1.2fr 60px 100px", gap: 10, alignItems: "center", padding: "9px 28px", borderRadius: 0, transition: "background 0.1s", cursor: added ? "default" : "pointer", background: "transparent" }}
                      onMouseEnter={e => { if (!added) e.currentTarget.style.background = UI.rowHov; }}
                      onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}>
                      <div style={{ fontSize: 14, fontWeight: 500, color: added ? UI.textMut : UI.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{pv.name}</div>
                      <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: UI.textMut }}>{[pv.city, pv.address_state].filter(Boolean).join(", ") || "—"}</div>
                      <div style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: UI.textMut }}>{pv.capacity ? Number(pv.capacity).toLocaleString() : "—"}</div>
                      <div style={{ textAlign: "right" }}>
                        {added
                          ? <span style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: "var(--success)", letterSpacing: 0.5 }}>✓ Added</span>
                          : <button className="abtn" style={{ padding: "4px 12px", fontSize: 11 }} onClick={() => addFromDirectory(pv)}>+ Add</button>
                        }
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Footer */}
              <div style={{ padding: "12px 28px", borderTop: `1px solid ${UI.panelBdr}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 11, color: UI.textFnt }}>
                  {dirResults.length > 0 ? `${dirResults.length} result${dirResults.length !== 1 ? "s" : ""}` : ""}
                </span>
                <button className="abtn" onClick={() => { setDirModal(null); setDirQuery(""); setDirResults([]); }}>Close</button>
              </div>
            </div>
          </div>
        );
      })()}
      </div>
    </>
  );
}
